/**
 * Pema e Familjes — the entire frontend application.
 *
 * This is a single-file React app on purpose (see ARCHITECTURE.md §1 for
 * why). Start there for the big picture; this file is organized as:
 *   1. Module-level helper functions & small components (no state of their
 *      own) — theming, layout algorithm, kinship engine, tiny UI bits.
 *   2. The `App` component — all state, data fetching, the trunk-navigation
 *      logic, and the full render tree with ~25 modals.
 *
 * If you're looking for a specific thing:
 *   - Card layout / why the trunk line stays straight → computeLayout()
 *   - Who counts as "close cousin" vs "married out" → classifyRelation()
 *   - What's currently shown in "Trungu im" → the visiblePeople useMemo
 *   - The +/- buttons and their 3-state cycle → expandButtons useMemo
 *   - Login / roles / permissions → myRole, canEditFor, canAddChildFor.
 *     FAZA 3: identifikimi kryesor kaloi nga emër+datëlindje te Supabase
 *     Auth real (Google/Apple/Email) + person_account_links — shih
 *     seksionin "---------- auth ----------" dhe PHASE3_REAL_LOGIN.md.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  // Kontrolle funksionale — MBETEN gjithmonë ikona (edhe në 'emoji_all'), që
  // aplikacioni të mbetet i qartë (mbyllje, shigjeta, +, kërkim, zoom).
  Search, Plus, X, ZoomIn, ZoomOut, Maximize2, Crosshair, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  // Ikona "kuptimore" — aliasohen me `_` dhe mbështillen më poshtë, që në
  // mënyrën 'emoji_all' të kthehen automatikisht në emoji kudo në platformë.
  LogIn as _LogIn, LogOut as _LogOut, Edit2 as _Edit2, Trash2 as _Trash2, Heart as _Heart, Users as _Users,
  Camera as _Camera, UserCircle2 as _UserCircle2, BarChart3 as _BarChart3, Shield as _Shield, Bell as _Bell,
  Link2 as _Link2, KeyRound as _KeyRound, PlusCircle as _PlusCircle, Info as _Info, BookOpen as _BookOpen, AlertTriangle as _AlertTriangle,
  Megaphone as _Megaphone, Palette as _Palette, Mail as _Mail, ScrollText as _ScrollText, Download as _Download, MapPin as _MapPin,
  HelpCircle as _HelpCircle, Home as _Home, Undo2 as _Undo2, RefreshCw as _RefreshCw, Repeat as _Repeat,
  ArrowLeftRight as _ArrowLeftRight, Shuffle as _Shuffle, EyeOff as _EyeOff, MessageCircle as _MessageCircle, Menu as _Menu, ShoppingBag as _ShoppingBag,
  MessageSquareQuote as _MessageSquareQuote, Smile as _Smile, Mic as _Mic, FileText as _FileText,
  Cake as _Cake, CalendarDays as _CalendarDays, Flame as _Flame, Baby as _Baby, Feather as _Feather, Hand as _Hand,
} from "lucide-react";
import { supabase } from "./supabaseClient";
import { getPasswordPolicy, validatePassword, passwordErrorText, signupsDisabledMsg } from "./passwordPolicy";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar";
import { SAFE_TOP as ZONA_LART, SAFE_BOTTOM as ZONA_POSHTE, zonaLartPx } from "./zonaESigurt"; // kapaku 64/48 — shih shënimin atje
import { App as CapacitorApp } from "@capacitor/app";
// NJOFTIME1 — njoftime që i mban VETË telefoni. Asgjë nuk kalon nga rrjeti, dhe
// nuk kërkohet as Firebase as çelës Apple.
//
// ⚠ PSE `registerPlugin` DHE JO `import { LocalNotifications } from "…"`.
//
// 29 gusht 2026, ndërtimi te Cloudflare dështoi:
//
//   [vite]: Rollup failed to resolve import "@capacitor/local-notifications"
//
// Importi i drejtpërdrejtë e bën paketën varësi të NDËRTIMIT TË WEB-IT — dhe
// atë paketë e përdor vetëm aplikacioni nativ. Pra faqja në internet nuk
// ndërtohej dot për një gjë që s'e prek kurrë. (E njëjta formë gabimi si me
// `acorn`: një varësi e shtuar te kodi pa hyrë te `package-lock.json`.)
//
// `Capacitor.registerPlugin` lidhet me plugin-in NATIV me emër, përmes urës —
// pa asnjë import. Rezultati: ndërtimi i web-it nuk varet më nga kjo paketë
// fare, kurse te telefoni funksionon njësoj. Te shfletuesi thirrjet dështojnë
// dhe kapen nga `try/catch`; gjithsesi aty as nuk hyjmë, sepse çdo rrugë ruhet
// nga `Capacitor.isNativePlatform()`.
//
// Paketa mbetet e nevojshme për të NDËRTUAR aplikacionin (`npm i
// @capacitor/local-notifications` dhe `npx cap sync`) — por vetëm atje.
const LocalNotifications = registerPlugin("LocalNotifications");
import AppLock, { AppLockSetting } from "./AppLock.jsx"; // brava e aplikacionit (gjurmë/fytyrë/PIN), e ndezur nga Admin Center
import VersioniIRi from "./VersioniIRi.jsx"; // VERSIONI1 — «ka version të ri» / «kërkohet përditësim»
import { versioniIInstaluar, hapDyqaninNgaShiriti, ID_MESAZHI_PERDITESIM } from "./versioni.js";
import ServicesScreen from "./ServicesScreen.jsx"; // Faza 0 — vitrina e shërbimeve, e drejtuar nga regjistri
import { loadFeatures, featureGated, featureOn, featureShown, featureAllowed, onFeaturesChange } from "./features.js";
import { ndertoTregimin } from "./tregimi.js";  // TREGIMI1/2 — çfarë hyn te historia
import { ndertoRrefimin } from "./rrefimi.js";  // RRËFIMI1 — si dëgjohet  // TREGIMI1 — motori që zgjedh momentet, kapitujt dhe brezat  // Faza 0 — flag-et e reja (feature_catalog)
import { useTranslation } from "react-i18next";
import { setLanguage, getSwitcherOptions, getStoredChoice } from "./i18n";
import { tDyn } from "./serverText";
import { cacheRuaj, cacheLexo, cacheFshi, eshteGabimRrjeti } from "./cache.js";
import { hyrMeOAuth, degjoKthiminEOAuth } from "./oauth.js"; // OAUTH1 — Google/Apple brenda app-it (Apple, Guideline 4) // CACHE1 — leximi pa internet (kujtesa e telefonit)
import { shenoGjurmen, shenoHyrjen } from "./gjurma.js"; // PROVAT1 — gjurma e llogarisë pa familje // teksti i shkruar nga admini (shih serverText.js)
import { paletteForMode, onDesignChange, getDesign, getDesigns, getThemes, getAppearance, getCrestOn, setUserDesign, setUserTheme, setUserCrest } from "./designLoader"; // Design Studio — hierarkia design→temë→crest
import { fmtDate, fmtDateTime, fmtRelative, stampForFilename } from "./datetime"; // data/ora sipas pajisjes
import LoginScreen, { FALLBACK_LOGIN } from "./LoginScreen.jsx"; // ekrani i hyrjes, i konfigurueshëm nga Admin Center
import { prova, provaNga, EkraniProves } from "./prova.jsx"; // ⚠ I PËRKOHSHËM — çelësi i provës ndarëse (hiqet para lansimit)
import DateField from "./DateField"; // shkrim i lirë i datës + kalendari i sistemit
import SupportChat from "./SupportChat"; // asistenti AI për end-users (Faza 7)
import ChatFab from "./ChatFab"; // BALLINA3 — butoni pluskues, pesë sjellje
import { IKONAT_HEIRLOOM, StemaHeirloom, StemaMeNderrim, PellumbiHeirloom, ShenjaEKujtimitB, UnazaMeGur, UnazatEndethurura, NyjaShto, SHTEG_PELLUMBI_PLOT, SHTEG_UNAZA, SHTEG_PEMA, UnazatNderthurura, UnazatQePreken, UnazatENdara, UnazatMePellumb, Shkendija, Paralajmerimi } from "./ikonatHeirloom.jsx"; // seti i tretë i ikonave + stolitë e pemës
import AddPersonSheet from "./AddPersonSheet"; // Faza 2 — formulari i ri "Shto person"
import { countryOptions, displayCountry, resolveCountry, familyCountryCodes } from "./countries";
import { loadCities } from "./cities"; // qytetet: një skedar për shtet, me kërkesë // shtetet: kod ISO + emër nga Intl.DisplayNames (pa skedarë përkthimi)
// FIX (RISTRUKTURIM) — import "mammoth" u hoq — konvertimi .docx→HTML tani
// ndodh vetëm te Admin Center (uploadDocument u hoq krejtësisht këtu).
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";

// ════════════════════════════════════════════════════════════════════════════
// LIDHJET QË I DËRGOHEN DIKUJT TJETËR
//
// 31 gusht 2026 · defekt i gjetur para dorëzimit.
//
// Brenda aplikacionit native, `window.location.origin` NUK është adresa e faqes.
// Është `capacitor://localhost` te iOS dhe `http://localhost` te Android — një
// origjinë e brendshme, e vlefshme vetëm brenda asaj pajisjeje.
//
// Prandaj çdo ftesë e kopjuar nga telefoni dilte si
//     capacitor://localhost/invite?token=…
// dhe personi që e merrte nuk hapte dot asgjë. Pikërisht rruga nga e cila hyn
// një familje e re ishte e prishur — dhe vetëm në telefon, pra te desktopi nuk
// dukej kurrë.
//
// Kjo ndodh VETËM te lidhjet që dalin nga pajisja. Aty ku `origin` përdoret për
// lundrim brenda vetes (rikthimi pas OAuth-it, `location.replace`), ai është i
// saktë dhe mbetet ashtu — mos i «ndreq» ato.
// ════════════════════════════════════════════════════════════════════════════
// A MBËRRITI KJO FAQE ME NJË TOKEN AUTENTIKIMI NË ADRESË?
//
// Lexohet NJË HERË, te ngarkimi i modulit — dhe kjo është e gjithë poenta.
// `supabase-js` me `detectSessionInUrl: true` e përpunon fragmentin dhe e
// PASTRON adresën menjëherë, duke lënë vetëm `#`. Pra çdo kontroll i mëvonshëm
// brenda një komponenti do ta shihte adresën tashmë bosh dhe do të vendoste
// gabim.
//
// Përdoret nga blloku «mos më mbaj të kyçur»: një skedë që sapo u kyç nga një
// magic-link (impersonim, ftesë, rivendosje fjalëkalimi) nuk guxon të dalë
// vetvetiu në ngarkim — sepse `sessionStorage` i një skede të re është
// gjithmonë bosh, dhe pa këtë kontroll sesioni shuhej brenda së njëjtës çast
// që lindte.
const KA_TOKEN_NE_URL = (() => {
  try {
    const s = String(window.location.hash || "") + String(window.location.search || "");
    return /access_token=|refresh_token=|[?&#]code=|type=(magiclink|recovery|invite|signup|email_change)/.test(s);
  } catch (_e) {
    return false;   // pa `window` (SSR, teste) — sillu si mbërritje normale
  }
})();

const ORIGJINA_PUBLIKE = "https://pemaefamiljes.com";

function lidhjePublike(shtegu) {
  const origjina = Capacitor.isNativePlatform()
    ? ORIGJINA_PUBLIKE
    : (window.location.origin || ORIGJINA_PUBLIKE);
  return origjina.replace(/\/+$/, "") + shtegu;
}

// FAZA 23 — "Harta e Familjes". `birth_country` ruhet si TEKST I LIRË NË
// SHQIP (jo kod ISO — lista `countries` rritet vetë kur dikush shton vend
// të ri, shih useState(["Kosovë", "Shqipëri"]) më poshtë), kështu që
// hartës i duhet një hap konvertimi PARA se të mund të ngjyrosë vende mbi
// world-atlas (i cili identifikon çdo vend me kod numerik ISO 3166-1).
// Tabela këtu u GJENERUA PROGRAMATIKISHT (jo shkruar dorazi — i njëjti
// proces si te Admin Center-i, për të shmangur gabime transkriptimi) nga
// i18n-iso-countries (lokali "sq"), plus disa alias të zakonshëm që
// njerëzit i shkruajnë ndryshe (p.sh. "USA", "Zvicer" pa ë). Vende që
// s'përputhen (emra të pazakontë/gabime shtypi) THJESHT S'NGJYROSEN në
// hartë — mbeten të dukshme në listën tekstuale ekzistuese poshtë, e cila
// funksionon gjithsesi pavarësisht hartës.
const SQ_COUNTRY_NAME_TO_NUMERIC = {"afganistan":"004","shqipëri":"008","algjeri":"012","samoa amerikane":"016","andorrë":"020","angolë":"024","anguilë":"660","antarktidë":"010","antigua & barbuda":"028","argjentinë":"032","armeni":"051","arubë":"533","australi":"036","austri":"040","azerbajxhan":"031","bahamas":"044","bahrein":"048","bangladesh":"050","barbados":"052","bjellorusi":"112","belgjikë":"056","belize":"084","benin":"204","bermuda":"060","butan":"064","bolivi":"068","bosnjë & hercegovinë":"070","botsvanë":"072","ishulli buve":"074","brazil":"076","territori britanik i oqeanit indian":"086","brunej":"096","bullgari":"100","burkina faso":"854","burund":"108","kamboxhia":"116","kamerun":"120","kanada":"124","kepi i gjelbër":"132","ishujt kajman":"136","republika afrikano-qendrore":"140","çad":"148","kili":"152","kinë":"156","ishulli i krishtlindjes":"162","ishujt kokos (kiling)":"166","kolumbi":"170","komore":"174","kongo-brazavil":"178","kongo-kinshasa":"180","ishulli kuk":"184","kosta rikë":"188","bregu i fildishtë":"384","kroaci":"191","kubë":"192","qipro":"196","republika çeke":"203","danimarkë":"208","xhibut":"262","dominikë":"212","republika dominikane":"214","ekuador":"218","egjipt":"818","el salvador":"222","guineja ekuatoriale":"226","eritre":"232","estoni":"233","etiopi":"231","ishujt folkland":"238","ishujt faroe":"234","fixhi":"242","finlandë":"246","francë":"250","guajana frënge":"254","polinezia frënge":"258","territoret frënge jugore":"260","gabon":"266","gambia":"270","gjeorgji":"268","gjermani":"276","gana":"288","gjibraltar":"292","greqi":"300","groenlandë":"304","grenadë":"308","guadalup":"312","guam":"316","guatemalë":"320","guine":"324","guinea-bisau":"624","guajanë":"328","haiti":"332","ishujt hërd & mekdonald":"334","vatikan":"336","honduras":"340","rva i hong kongut kinë":"344","hungari":"348","islandë":"352","indi":"356","indonezi":"360","iran":"364","irak":"368","irlandë":"372","izrael":"376","itali":"380","xhamajkë":"388","japoni":"392","jordani":"400","kazakistan":"398","kenia":"404","kiribati":"296","koreja e veriut":"408","koreja e jugut":"410","kuvajt":"414","kirgizstan":"417","laos":"418","letoni":"428","liban":"422","lesoto":"426","liberi":"430","libi":"434","lihtenshtein":"438","lituani":"440","luksemburg":"442","rva i makaos kinë":"446","madagaskar":"450","malavi":"454","malajzi":"458","maldive":"462","mali":"466","maltë":"470","ishujt marshall":"584","martinikë":"474","mauritani":"478","mauritius":"480","majote":"175","meksikë":"484","mikronezi":"583","moldavi":"498","monako":"492","mongoli":"496","montserat":"500","marok":"504","mozambik":"508","mianmar (burma)":"104","namibi":"516","nauru":"520","nepal":"524","holandë":"528","kaledonia e re":"540","zelandë e re":"554","nikaragua":"558","niger":"562","nigeri":"566","niue":"570","ishujt norfolk":"574","ishujt veriorë mariana":"580","maqedoni":"807","norvegji":"578","oman":"512","pakistan":"586","palau":"585","territoret palestineze":"275","panama":"591","papua guineja e re":"598","paraguai":"600","peru":"604","filipine":"608","ishujt pitkern":"612","poloni":"616","portugali":"620","porto riko":"630","katar":"634","reunion":"638","rumani":"642","rusi":"643","ruandë":"646","shën helena":"654","shën kits e nevis":"659","shën luçia":"662","shën pier dhe mikëlon":"666","shën vinsent & grenadinet":"670","samoa":"882","san marino":"674","sao tome & prinsipe":"678","arabi saudite":"682","senegal":"686","sejshelle":"690","sierra leone":"694","singapor":"702","sllovaki":"703","slloveni":"705","ishujt solomon":"090","somali":"706","afrikë e jugut":"710","xhorxha jugore dhe ishujt sanduiç të jugut":"239","spanjë":"724","sri lankë":"144","sudan":"729","surinam":"740","svalbard & zhan majen":"744","svaziland":"748","suedi":"752","zvicër":"756","siri":"760","tajvan":"158","taxhikistan":"762","tanzani":"834","tajlandë":"764","timori lindor":"626","togo":"768","tokelau":"772","tonga":"776","trinidad & tobago":"780","tunizi":"788","turqi":"792","turkmenistan":"795","ishujt turk & kaikos":"796","tuvalu":"798","ugandë":"800","ukrainë":"804","emiratet e bashkuara arabe":"784","mbretëria e bashkuar":"826","shtetet e bashkuara të amerikës":"840","ishujt e përtejmë shba-së":"581","uruguai":"858","uzbekistan":"860","vanuatu":"548","venezuelë":"862","vietnam":"704","ishujt e virgjër britanikë":"092","ishujt e virgjër amerikanë":"850","uollis e futuna":"876","sahara perëndimore":"732","jemen":"887","zambia":"894","zimbabve":"716","ishujt aland":"248","karaibet holandeze":"535","kurasao":"531","gërnsi":"831","ishulli i robit":"833","xhërsi":"832","mali i zi":"499","shën bartolomeo":"652","shën martin":"534","serbi":"688","sudan i jugut":"728","kosovë":"983","usa":"840","shba":"840","amerike":"840","angli":"826","britani e madhe":"826","holande":"528","zvicer":"756"};

// Kod ISO alfa-2 → numerik (id-ja që përdor world-atlas). Përdoret për të
// ngjyrosur hartën duke u nisur nga KODI (resolveCountry njeh kod+emër në çdo
// gjuhë), jo nga emri shqip — kështu funksionon në çdo gjuhë të ndërfaqes dhe
// pavarësisht si është ruajtur vendi. "XK" (Kosova) → "983", i vizatuar me
// overlay veçmas (world-atlas s'e ka Kosovën si formë të veçantë).
const ALPHA2_TO_NUMERIC = {"AF":"004","AL":"008","DZ":"012","AS":"016","AD":"020","AO":"024","AI":"660","AQ":"010","AG":"028","AR":"032","AM":"051","AW":"533","AU":"036","AT":"040","AZ":"031","BS":"044","BH":"048","BD":"050","BB":"052","BY":"112","BE":"056","BZ":"084","BJ":"204","BM":"060","BT":"064","BO":"068","BA":"070","BW":"072","BV":"074","BR":"076","IO":"086","BN":"096","BG":"100","BF":"854","BI":"108","KH":"116","CM":"120","CA":"124","CV":"132","KY":"136","CF":"140","TD":"148","CL":"152","CN":"156","CX":"162","CC":"166","CO":"170","KM":"174","CG":"178","CD":"180","CK":"184","CR":"188","CI":"384","HR":"191","CU":"192","CY":"196","CZ":"203","DK":"208","DJ":"262","DM":"212","DO":"214","EC":"218","EG":"818","SV":"222","GQ":"226","ER":"232","EE":"233","ET":"231","FK":"238","FO":"234","FJ":"242","FI":"246","FR":"250","GF":"254","PF":"258","TF":"260","GA":"266","GM":"270","GE":"268","DE":"276","GH":"288","GI":"292","GR":"300","GL":"304","GD":"308","GP":"312","GU":"316","GT":"320","GN":"324","GW":"624","GY":"328","HT":"332","HM":"334","VA":"336","HN":"340","HK":"344","HU":"348","IS":"352","IN":"356","ID":"360","IR":"364","IQ":"368","IE":"372","IL":"376","IT":"380","JM":"388","JP":"392","JO":"400","KZ":"398","KE":"404","KI":"296","KP":"408","KR":"410","KW":"414","KG":"417","LA":"418","LV":"428","LB":"422","LS":"426","LR":"430","LY":"434","LI":"438","LT":"440","LU":"442","MO":"446","MG":"450","MW":"454","MY":"458","MV":"462","ML":"466","MT":"470","MH":"584","MQ":"474","MR":"478","MU":"480","YT":"175","MX":"484","FM":"583","MD":"498","MC":"492","MN":"496","MS":"500","MA":"504","MZ":"508","MM":"104","NA":"516","NR":"520","NP":"524","NL":"528","NC":"540","NZ":"554","NI":"558","NE":"562","NG":"566","NU":"570","NF":"574","MP":"580","MK":"807","NO":"578","OM":"512","PK":"586","PW":"585","PS":"275","PA":"591","PG":"598","PY":"600","PE":"604","PH":"608","PN":"612","PL":"616","PT":"620","PR":"630","QA":"634","RE":"638","RO":"642","RU":"643","RW":"646","SH":"654","KN":"659","LC":"662","PM":"666","VC":"670","WS":"882","SM":"674","ST":"678","SA":"682","SN":"686","SC":"690","SL":"694","SG":"702","SK":"703","SI":"705","SB":"090","SO":"706","ZA":"710","GS":"239","ES":"724","LK":"144","SD":"729","SR":"740","SJ":"744","SZ":"748","SE":"752","CH":"756","SY":"760","TW":"158","TJ":"762","TZ":"834","TH":"764","TL":"626","TG":"768","TK":"772","TO":"776","TT":"780","TN":"788","TR":"792","TM":"795","TC":"796","TV":"798","UG":"800","UA":"804","AE":"784","GB":"826","US":"840","UM":"581","UY":"858","UZ":"860","VU":"548","VE":"862","VN":"704","VG":"092","VI":"850","WF":"876","EH":"732","YE":"887","ZM":"894","ZW":"716","AX":"248","BQ":"535","CW":"531","GG":"831","IM":"833","JE":"832","ME":"499","BL":"652","MF":"663","RS":"688","SX":"534","SS":"728","XK":"983"};

// Normalizim për krahasim: pa diakritikë, i vogël, i prerë. Kështu "Zvicër" ==
// "zvicer", "Danimarkë" == "Danimark", "Belgjikë" == "belgjike" — vlerat e
// vjetra të ruajtura shqip njihen pa u dashur çdo variant i shkruar me dorë.
const normCountry = (s) => String(s || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Emër shqip (i normalizuar) → numerik i world-atlas. Ndërtuar nga tabela e
// plotë SQ (e gjeneruar nga i18n-iso-countries), pra mbulon ÇDO shtet në
// shqip, me ose pa diakritikë. Shërben si rrjetë sigurie kur `resolveCountry`
// (që mbështetet te Intl/alias) s'e njeh emrin shqip.
const SQ_NORM_TO_NUMERIC = Object.fromEntries(
  Object.entries(SQ_COUNTRY_NAME_TO_NUMERIC).map(([name, num]) => [normCountry(name), num])
);

// Nga çfarëdo vlere e ruajtur për vendin (kod, ose emër në çdo gjuhë, edhe me
// gabime të vogla diakritikësh) → id numerik i world-atlas, ose null.
// Zinxhir: (1) resolveCountry (kod + emër + alias), (2) tabela e plotë shqip e
// normalizuar, (3) përputhje fuzzy VETËM për klasën "ë e rënë" (p.sh.
// "Danimark" ~ "Danimarkë"), me kufij të rreptë gjatësie që të mos ketë
// përputhje të gabuara (p.sh. "Niger" nuk bëhet kurrë "Nigeri").
function countryToNumeric(raw, label) {
  const code = resolveCountry(raw);
  if (code && ALPHA2_TO_NUMERIC[code]) return ALPHA2_TO_NUMERIC[code];
  for (const c of [normCountry(raw), normCountry(label)]) {
    if (c && SQ_NORM_TO_NUMERIC[c]) return SQ_NORM_TO_NUMERIC[c];
  }
  const nr = normCountry(raw);
  if (nr.length >= 6) {
    for (const k of Object.keys(SQ_NORM_TO_NUMERIC)) {
      if (k.length >= 6 && Math.abs(k.length - nr.length) <= 1 && (k.startsWith(nr) || nr.startsWith(k))) {
        return SQ_NORM_TO_NUMERIC[k];
      }
    }
  }
  return null;
}

// FAZA 18 — "Versioni i app-it" (Admin Center). Rritet DORAZI me çdo
// deploy kuptimplotë (jo çdo commit të vogël) — thjeshtësi e qëllimshme,
// jo automatizim CI/CD (do të kërkonte git hash/build-time injection te
// vite.config.js, gjë e panevojshme për shkallën aktuale të projektit).
// Raportohet te rpc_heartbeat, që paneli i platformës të shohë shpërndarjen
// e versioneve (p.sh. "kush ende s'e ka rifreskuar faqen pas deploy-it").
const APP_VERSION = "1.0.0";

// FAZA 6 — i njëjti id fiks i familjes së parë si te schema.sql (Faza 1).
// Përdoret si family_id "parazgjedhje" kur App.jsx hapet pa `familySlug`
// (p.sh. te "/", deploy-i i instancës origjinale) — kështu çdo query i vjetër
// (settings, etj.) vazhdon të lexojë rreshtin e saktë pa asnjë ndryshim URL-je.

// Mbështjellës i ikonave për mënyrën 'emoji_all': kur dizajni aktiv e ka këtë
// mënyrë, ÇDO përdorim i këtyre ikonave në platformë kthehet automatikisht në
// emoji (pa prekur qindra vende një-nga-një). Përndryshe render-on ikonën reale
// të pandryshuar. Kontrollet funksionale (X, shigjeta, +, kërkim, zoom) s'janë
// mbështjellë me qëllim — mbeten ikona që UI-ja të mbetet e qartë.
// 3 shtator 2026 — ARGUMENTI I TRETË: `celesi`.
// Kur dizajni aktiv kërkon setin «Heirloom», ikona merret nga `ikonatHeirloom`.
// Nëse për atë emër s'ka vizatim, bie te ikona klasike — kurrë te bosh. Kjo
// është arsyeja pse çelësi është opsional: një set i ri mund të nisë me
// gjysmën e ikonave dhe platforma prapë punon, thjesht e përzier — dhe ne e
// shohim se cila mungon, në vend që të gjejmë një vend të zbrazët.
function _emj(Real, glyph, celesi) {
  return function IconW(props) {
    if (NOVA_ICON_MODE === "heirloom") {
      const H = celesi && IKONAT_HEIRLOOM[celesi];
      if (H) return <H {...props} />;
    }
    if (NOVA_ICON_MODE === "emoji_all") {
      const sz = (props && props.size) || 16;
      return <span aria-hidden style={{ fontSize: Math.round(sz * 0.92), lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", width: sz, height: sz, verticalAlign: "middle", flexShrink: 0 }}>{glyph}</span>;
    }
    return <Real {...props} />;
  };
}
const LogIn = _emj(_LogIn, "🔓", "hyrje"), LogOut = _emj(_LogOut, "🚪", "dalje"), Edit2 = _emj(_Edit2, "✏️", "laps"),
  Trash2 = _emj(_Trash2, "🗑️", "kosh"), Heart = _emj(_Heart, "❤️", "zemër"), Users = _emj(_Users, "👥", "njerëz"),
  Camera = _emj(_Camera, "📷", "kamera"), UserCircle2 = _emj(_UserCircle2, "👤", "personRreth"), BarChart3 = _emj(_BarChart3, "📊", "shtylla"),
  Shield = _emj(_Shield, "🛡️", "mburoja"), Bell = _emj(_Bell, "🔔", "zilia"), Link2 = _emj(_Link2, "🔗", "lidhja"),
  KeyRound = _emj(_KeyRound, "🔑", "çelësi"), PlusCircle = _emj(_PlusCircle, "➕", "shtoRreth"), Info = _emj(_Info, "ℹ️", "info"),
  AlertTriangle = _emj(_AlertTriangle, "⚠️", "kujdes"),
  BookOpen = _emj(_BookOpen, "📖", "libri"), Megaphone = _emj(_Megaphone, "📣", "megafoni"), Palette = _emj(_Palette, "🎨", "paleta"),
  Mail = _emj(_Mail, "✉️", "zarfi"), ScrollText = _emj(_ScrollText, "📜", "rrotull"), Download = _emj(_Download, "📥", "shkarko"),
  HelpCircle = _emj(_HelpCircle, "🛟", "ndihma"), Home = _emj(_Home, "🏠", "shtëpia"), Undo2 = _emj(_Undo2, "↩️", "kthimi"),
  RefreshCw = _emj(_RefreshCw, "🔄", "rifresko"), Repeat = _emj(_Repeat, "🔁", "përsërit"), ArrowLeftRight = _emj(_ArrowLeftRight, "🔀", "ndërro"),
  Shuffle = _emj(_Shuffle, "🔀", "përzie"), EyeOff = _emj(_EyeOff, "🙈", "syriMbyllur"), MessageCircle = _emj(_MessageCircle, "💬", "biseda"),
  Menu = _emj(_Menu, "📋", "menyja"), ShoppingBag = _emj(_ShoppingBag, "🛍️", "çanta"),
  // SISTEMI VIZUAL (2 shtator 2026) — ikonat e rubrikave të Odës, në vend të emoji-ve.
  MessageSquareQuote = _emj(_MessageSquareQuote, "🗣️", "thonjëzat"), Smile = _emj(_Smile, "😄", "buzëqeshje"),
  Mic = _emj(_Mic, "🎙️", "mikrofoni"), FileText = _emj(_FileText, "📄", "fleta"),
  Cake = _emj(_Cake, "🎂", "tortë"), CalendarDays = _emj(_CalendarDays, "📅", "kalendari"), Flame = _emj(_Flame, "🕯️", "qiri"),
  Baby = _emj(_Baby, "👶", "foshnjë"), Feather = _emj(_Feather, "🕊️", "pëllumbi"), Hand = _emj(_Hand, "👋", "dora");
// 4 shtator 2026 — SKEDA E PEMËS DUHET TË JETË PEMË.
// Pronari: «te butonat e menysë, te pema duhet ta ketë formën e pemës që
// diskutuam — të njëjtën si te ikona e switch family, po pa rrathë.»
// Deri sot ajo skedë mbante ikonën e NJERËZVE (👥) — trashëgim nga koha kur
// pema quhej «anëtarët».
//
// ⚠ PROVA E PARË ISHTE GABIM: e lidha me `_emj`, pra pema e vektorizuar dilte
// VETËM te dizajnet me `iconMode = heirloom`; kudo tjetër binte te ikona e
// Lucide-s, dhe ajo — me kurorën e saj të rrumbullakët — nuk lexohet si pemë.
// Pronari: «nuk po duket si pemë, por si ari i ngordhur». Kishte të drejtë.
// Prandaj kjo skedë NUK kalon nga çelësi: forma është gjithmonë e njëjta,
// sepse skeda e pemës nuk është çështje teme — është vetë emri i ekranit.
const PemaSkeda = ({ size = 20, color = "currentColor", strokeWidth: _sw, style, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" fill="none" aria-hidden="true"
       style={{ display: "block", flexShrink: 0, color, ...(style || {}) }} {...rest}>
    <path d={SHTEG_PEMA} fill="currentColor" fillRule="evenodd"
          stroke="currentColor" strokeWidth={Math.max(0, (0.95 - (5.5 / 200) * size) / size * 200)} />
  </svg>
);

// ---------- theme system ----------
const BASE_COLORS = {
  panel: "#F6F2E7", panelSoft: "#EFE8D6", ink: "#2A2118", inkLight: "#6B6152",
  // `onBg` = ngjyra e TEKSTIT/ikonave që rrinë DIREKT mbi sfondin imziv (bg/bgDeep):
  // te pamja klasike (sfond i kuq i errët) është krem i çelët. Kur aktivohet një
  // dizajn, llogaritet sipas ndriçimit të bg-së (i çelët→tekst i errët; i errët→
  // tekst i çelët), që guaska (header/sidebar/pema) të lexohet në ç'do temë.
  onBg: "#F6F2E7",
  // SISTEMI VIZUAL (2 shtator 2026) — dy çelësa që ura e dizajnit nuk i kishte:
  //   `onBgDeep`  = teksti mbi shiritin e kokës (bgDeep). Deri sot ai lexonte
  //                 `onBg`, që llogaritet nga `bg` — dhe kjo funksiononte vetëm
  //                 sa kohë bg dhe bgDeep ishin të dyja të errëta. Një dizajn me
  //                 dysheme letër dhe shirit burgundy e prishte: tekst i errët
  //                 mbi shirit të errët.
  //   `accentText` = theksi kur është TEKST (nav aktiv, «Shto»). Ari #C9A227 mbi
  //                 sfond të errët ka 4.9:1; mbi letër ka 2.4:1 dhe s'lexohet.
  // Te klasikja të dyja janë ato që ishin — asnjë ndryshim pa dizajn.
  onBgDeep: "#F6F2E7",
  accentText: "#C9A227",
  // `field` = sfondi i fushave (input, select, listë rënëse). Klasikja: e bardhë.
  // Deri sot ishte `"#fff"` e ngulitur në 16 vende, me tekst `ink` — që te një
  // dizajn i errët është i ÇELËT. Tekst i çelët mbi të bardhë: e padukshme.
  // Kjo ishte klasa e defektit që pronari e pa te «Kërko personin» dhe te kodi
  // i familjes. Tani sfondi i fushës ndjek dizajnin, si teksti i saj.
  field: "#FFFFFF",
  // `accentTint` = sfond gjysmë-tejdukshëm i theksit (butoni aktiv "Shiko", nav
  // aktiv, hover). Klasike = ar; Moderne = jeshil — llogaritet nga theksi.
  accentTint: "rgba(201,162,39,0.18)",
  danger: "#7A2430", dangerSoft: "#F1DED9",
  rose: "#B5657A", roseSoft: "#F0DCE1",
  branchCore: "#4C7A4A", branchCoreSoft: "#DEEBDC",
  branchMarriedIn: "#D98CA0", branchMarriedInSoft: "#F5E1E7",
  branchNearCousin: "#3E7EA6", branchNearCousinSoft: "#DCE9F0",
  branchFarCousin: "#C99A28", branchFarCousinSoft: "#F3E7C6",
};
const THEMES = {
  forest: { name: "The Forest", eagle: false, colors: { bg: "#1F2A1E", bgDeep: "#182119", gold: "#B8935A", goldSoft: "rgba(184,147,90,0.35)", moss: "#6B8F63", spouseLine: "#6B8F63" } },
  albania: { name: "Flag Red", eagle: true, colors: { bg: "#6E1423", bgDeep: "#3A0A13", gold: "#C9A227", goldSoft: "rgba(201,162,39,0.4)", moss: "#1C1310", spouseLine: "#E7CD82" } },
  midnight: { name: "Midnight", eagle: false, colors: { bg: "#152238", bgDeep: "#0C1524", gold: "#C7B78A", goldSoft: "rgba(199,183,138,0.35)", moss: "#3E5C76", spouseLine: "#DCE6F0" } },
  bronze: { name: "Bronze", eagle: false, colors: { bg: "#3B2A20", bgDeep: "#241811", gold: "#C88B3B", goldSoft: "rgba(200,139,59,0.35)", moss: "#5A4126", spouseLine: "#E0B88A" } },
  kosovo: { name: "Kosovo Motifs", kosovo: true, colors: { bg: "#244AA5", bgDeep: "#152C63", gold: "#E8B227", goldSoft: "rgba(232,178,39,0.4)", moss: "#0F1E42", spouseLine: "#F4D77C" } },
  crest: { name: "The Family Crest", crest: true, colors: { bg: "#2B2620", bgDeep: "#1A1712", gold: "#B89050", goldSoft: "rgba(184,144,80,0.35)", moss: "#4A4034", spouseLine: "#D9C8A8" } },
};
const COLORS = { ...BASE_COLORS, ...THEMES.albania.colors };

// ---------- Design Studio → COLORS (urë me hex REALE) ----------
// Design Studio publikon një dizajn (paletë me tokena). Këtu marrim paletën
// AKTIVE si HEX TË ZGJIDHUR (jo string-je `var(...)`, se ato s'zgjidhen te
// atributet SVG `fill=`/`stroke=` as te `color=` i ikonave) dhe e mbivendosim
// mbi çelësat e "kromit" të UI-t (sipërfaqe, tekst) — kështu KREJT aplikacioni
// (kartela, modale, menu, formularë, feed) adopton dizajnin e ri.
//
// Ndihmëse ngjyrash (hex → rgb, ndriçim, errësim) për të nxjerrë ngjyra
// dytësore (bgDeep, onBg) kur dizajni jep vetëm paletën e sheshtë.
function _hexToRgb(h) {
  try {
    let s = String(h || "").trim().replace("#", "");
    if (s.length === 3) s = s.split("").map((c) => c + c).join("");
    if (s.length < 6) return null;
    return { r: parseInt(s.slice(0, 2), 16), g: parseInt(s.slice(2, 4), 16), b: parseInt(s.slice(4, 6), 16) };
  } catch (_e) { return null; }
}
function _luma(h) { const c = _hexToRgb(h); return c ? (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255 : 0.5; }
function _darken(h, f = 0.32) {
  const c = _hexToRgb(h); if (!c) return h;
  const d = (v) => Math.max(0, Math.round(v * (1 - f)));
  const hx = (v) => d(v).toString(16).padStart(2, "0");
  return "#" + hx(c.r) + hx(c.g) + hx(c.b);
}
// NJOFTIME1 — id-ja e një njoftimi lokal duhet të jetë numër i plotë 32-bit
// (kërkesë e Android-it). Dhe duhet të jetë E QËNDRUESHME: i njëjti send në të
// njëjtën ditë duhet të japë të njëjtën id, përndryshe çdo hapje e app-it do të
// shtonte një kopje të dytë të së njëjtës ditëlindje.
// NJOFTIME2 — kush hyn te «ky person dhe pasardhësit e tij». Ndalimi te
// `dalja.has(p.id)` e bën ciklet të padëmshme: një person që tashmë është
// brenda nuk shtohet dy herë, pra një lidhje e gabuar prind-fëmijë nuk e ngrin
// aplikacionin — thjesht e mbaron rrethin.
function pasardhesitE(people, rrenja) {
  const dalja = new Set([rrenja]);
  const lista = Object.values(people || {});
  let shtuar = true, mbrojtje = 0;
  while (shtuar && mbrojtje++ < 60) {
    shtuar = false;
    for (const p of lista) {
      if (!p || dalja.has(p.id)) continue;
      if ((p.parent_ids || []).some((pid) => dalja.has(pid))) { dalja.add(p.id); shtuar = true; }
    }
  }
  return dalja;
}
// NJOFTIME5 (3 nëntor 2026) — DEGA për njoftimet: pasardhësit + bashkëshortët
// e tyre. «Dega e Imerit» pa gruan e Imerit dhe pa nuset s'është degë — dhe
// pikërisht ditëlindjet e tyre janë ato që njeriu do t'i urojë. `pasardhesitE`
// mbetet siç është (statistika numëron gjak), kjo përdoret vetëm për «nga kush».
function degaMeBashkeshortet(people, rrenja) {
  const dalja = pasardhesitE(people, rrenja);
  for (const id of [...dalja]) {
    const p = people && people[id];
    for (const sid of (p && p.spouse_ids) || []) if (people && people[sid]) dalja.add(sid);
  }
  return dalja;
}
// NJOFTIME5 — gjyshi nga i cili nis dega e parazgjedhur: babai i babait; nëse
// s'dihet, babai i nënës; nëse s'dihet as ai (p.sh. nuse pa prindër në këtë
// pemë), gjyshi i bashkëshortit. `null` = s'dihet → e gjithë familja, derisa
// pema të lidhet. Llogaritet gjallë, jo e ngulitur në çastin e bashkimit.
function gjyshiIDeges(people, meId) {
  const prinderit = (id) => ((people[id] && people[id].parent_ids) || []).filter((x) => people[x]);
  const babai = (id) => { const pr = prinderit(id); return pr.find((x) => people[x].gender === "M") || null; };
  const nena = (id) => { const pr = prinderit(id); return pr.find((x) => people[x].gender === "F") || null; };
  const gjyshiI = (id) => {
    if (!id) return null;
    const b = babai(id); const gb = b && babai(b); if (gb) return gb;
    const n = nena(id); const gn = n && babai(n); if (gn) return gn;
    // prindër pa gjini të shënuar: çdo prind i çdo prindi
    for (const pr of prinderit(id)) { const g = prinderit(pr)[0]; if (g) return g; }
    return null;
  };
  if (!people || !people[meId]) return null;
  const imi = gjyshiI(meId);
  if (imi) return imi;
  for (const sid of people[meId].spouse_ids || []) { const g = gjyshiI(sid); if (g) return g; }
  return null;
}
// NJOFTIME6 (3 nëntor 2026) — SA BREZA LART. Dega e gjyshit (dy breza lart) është
// parazgjedhja, po në një familje të madhe dega e vetë prindit mund të ketë
// tashmë dhjetëra vetë — dhe dega e gjyshit akoma më shumë: 40 ditëlindje në
// muaj janë zhurmë dhe njeriu i fik krejt. Rregulli (pronari):
//     dega e PRINDIT ka `pragu` persona ose më shumë → parazgjedhja është dega e prindit (një brez lart)
//     përndryshe                                      → dega e gjyshit (dy breza lart)
// `pragu` është çelës i platformës (Admin Center → Privatesia → Njoftimet,
// parazgjedhja 50). Gjithçka llogaritet gjallë nga pema, si te NJOFTIME5.
// Prindi është ai nëpër të cilin gjendet gjyshi (babai i babait → babai i
// nënës → …); pa gjysh të njohur, babai, pastaj nëna, pastaj çdo prind; dhe
// nëse vetë personi s'ka prindër në pemë, ata të bashkëshortit.
// SIGURIA2 (U5) — dëgjues «resume» që pastrohet saktë. `addListener` kthen
// premtim: nëse efekti pastrohej para se ai të zgjidhej, dëgjuesi mbetej për
// gjithë jetën e app-it dhe çdo rifreskim xhirohej disa herë.
function dëgjoResume(fn) {
  let doreza = null;
  let hequr = false;
  try {
    Promise.resolve(CapacitorApp.addListener("resume", fn))
      .then((h) => { if (hequr) { try { h?.remove?.(); } catch (_e) { /* asgjë */ } } else doreza = h; })
      .catch(() => {});
  } catch (_e) { /* jo native */ }
  return () => { hequr = true; try { doreza?.remove?.(); } catch (_e) { /* asgjë */ } };
}

// SIGURIA2 (M2) — lista e familjeve të llogarisë, me dy riprovime kur serveri
// kthen gabim të përkohshëm (jo-rrjeti: 5xx, timeout i bazës). Më parë një gabim
// i vetëm lexohej si «kjo llogari s'ka asnjë familje»: ekrani i hyrjes ofronte
// «krijo familje» dhe, në rrugën me slug, fshihej kujtesa e familjes së fundit.
async function merrListenELidhjeve() {
  let r = await supabase.rpc("rpc_my_linked_people");
  for (let i = 0; r.error && !eshteGabimRrjeti(r.error) && i < 2; i++) {
    await new Promise((res) => setTimeout(res, 700 * (i + 1)));
    r = await supabase.rpc("rpc_my_linked_people");
  }
  return r;
}

function prindiIDeges(people, meId, gjyshi) {
  const prinderit = (id) => ((people[id] && people[id].parent_ids) || []).filter((x) => people[x]);
  const kandidatet = (id) => {
    const pr = prinderit(id);
    const babai = pr.find((x) => people[x].gender === "M");
    const nena = pr.find((x) => people[x].gender === "F");
    return [babai, nena, ...pr].filter(Boolean);
  };
  const tePrinderit = [meId, ...(((people[meId] && people[meId].spouse_ids) || []).filter((x) => people[x]))];
  if (gjyshi) {
    for (const id of tePrinderit) for (const pr of kandidatet(id)) if (prinderit(pr).includes(gjyshi)) return pr;
  }
  for (const id of tePrinderit) { const k = kandidatet(id)[0]; if (k) return k; }
  return null;
}
function rrenjaEDeges(people, meId, pragu) {
  const bosh = { rrenja: null, niveli: null, dega: null };
  if (!people || !people[meId]) return bosh;
  const gjyshi = gjyshiIDeges(people, meId);
  const prindi = prindiIDeges(people, meId, gjyshi);
  const kufiri = Number.isFinite(Number(pragu)) && Number(pragu) >= 5 ? Number(pragu) : 50;
  if (prindi) {
    const degaP = degaMeBashkeshortet(people, prindi);
    if (degaP.size >= kufiri) return { rrenja: prindi, niveli: "prindi", dega: degaP };
  }
  if (gjyshi) return { rrenja: gjyshi, niveli: "gjyshi", dega: degaMeBashkeshortet(people, gjyshi) };
  return bosh;
}
// PUSH1 — CILA PAJISJE ËSHTË KJO.
//
// `push_subscriptions` mbante vetëm `endpoint` — një varg i gjatë pa kuptim për
// njeriun. Pra Admin Center-i tregonte një numër, dhe një numër nuk i përgjigjet
// pyetjes «cila prej tyre nuk po merr njoftime».
//
// Lexohet nga `userAgent`, që është e vetmja gjë që e di shfletuesi për veten.
// S'është identifikues: nuk ndan dot dy Chrome-a te i njëjti Windows, dhe as
// nuk synon. Synon vetëm t'i japë njeriut një emër që e njeh.
function etiketaEPajisjes() {
  try {
    const ua = String(navigator.userAgent || "");
    const sistemi = /iPhone|iPad|iPod/i.test(ua) ? "iPhone/iPad"
      : /Android/i.test(ua) ? "Android"
      : /Macintosh|Mac OS/i.test(ua) ? "Mac"
      : /Windows/i.test(ua) ? "Windows"
      : /Linux/i.test(ua) ? "Linux" : "—";
    // Rendi ka rëndësi: Edge dhe Opera e shkruajnë edhe «Chrome» te vargu i
    // tyre, dhe Chrome e shkruan «Safari». Kush pyet i pari, fiton.
    const shfletuesi = /Edg\//i.test(ua) ? "Edge"
      : /OPR\/|Opera/i.test(ua) ? "Opera"
      : /Firefox\//i.test(ua) ? "Firefox"
      : /Chrome\//i.test(ua) ? "Chrome"
      : /Safari\//i.test(ua) ? "Safari" : "—";
    let lloji = "Web";
    if (Capacitor.isNativePlatform()) lloji = "App";
    else if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) lloji = "PWA";
    return `${sistemi} · ${shfletuesi} · ${lloji}`;
  } catch (_e) { return "—"; }
}
function idENjoftimit(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return (Math.abs(h) % 2000000000) || 1;
}
function _rgba(h, a) { const c = _hexToRgb(h); return c ? `rgba(${c.r},${c.g},${c.b},${a})` : `rgba(201,162,39,${a})`; }

// ── ODA — NJË BISEDË LEXOHET SI BISEDË ──────────────────────────────────────
//
// 2 shtator 2026. Pronari: «pse ka standard të ndryshëm mes barcoletës dhe të
// tjerave?» Kishte të drejtë, dhe përgjigjja e ktheu këtë funksion nga tri
// rregulla në një.
//
// SI ISHTE. Barcoletat vinin si dialog me viza, anekdotat si rrëfim me
// thonjëza. Shfaqja përpiqej t'i njihte të dyja: një rregull për vizat, një
// për thonjëzat, një për titullin. Tri rregulla që hamendësonin se cila copë
// ishte rrëfim dhe cila fjalim — dhe secili prej tyre kishte një cep ku e
// merrte gabim.
//
// SI ËSHTË. Standardi u vendos te vetë teksti, jo këtu: titulli, pastaj çdo
// replikë me vizë, njësoj për anekdotat dhe barcoletat. Rrëfimi nuk u hoq —
// hyri brenda replikës, si në çdo libër shqip: «— A ka mushka mend? pyeti
// një burrë.»
//
// Atëherë shfaqjes i mbetet një punë e vetme: thyej rreshtin para çdo vize.
// Asnjë hamendje, asnjë cep. Fjalët e urta nuk kanë viza, ndaj nuk preken —
// një rregull i mirë nuk bën asgjë kur s'ka çfarë të bëjë.
//
// KUJDES kur ndryshohet përmbajtja: viza me hapësira në të dyja anët është
// tani shenjë strukture, jo shenjë pikësimi. Një vizë e vendosur mes fjalëve
// brenda një fjalie do të prodhojë rresht të ri.
// SISTEMI VIZUAL (2 shtator 2026) — një hije për të gjitha kartelat e
// përmbajtjes, që Oda, Së fundi dhe Historia të mos ndryshojnë mes tyre.
const KARTELA_HIJE = "0 1px 2px rgba(36,28,24,.04), 0 4px 12px rgba(36,28,24,.06)";
// Funksion, jo objekt: `FONTS` deklarohet më poshtë në këtë skedar (TDZ), dhe
// gjithsesi ndryshon në kohë xhirimi kur publikohet një dizajn. Një objekt i
// ngrirë këtu do të binte në ngarkim — e kapi `tools/tdz.cjs`, jo esbuild.
// `ngjyraRezerve` = ngjyra që përdorte ky mbishkrim para se dizajnet të kishin
// rol tipografik. E marrim si parametër DHE jo si mbivendosje te vendi i
// thirrjes: përndryshe `{...kartelaEtikete(), color: X}` do ta fshinte
// gjithmonë ngjyrën që zgjodhi dizajni — dhe te Heirloom pikërisht ajo ngjyrë
// (ari i errët) është gjysma e karakterit të mbishkrimit.
const kartelaEtikete = (ngjyraRezerve) => {
  const bazë = { fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" };
  if (ngjyraRezerve) bazë.color = ngjyraRezerve;
  return { ...tip("etiketa", bazë), display: "flex", alignItems: "center", gap: 7, margin: "0 0 6px" };
};


function odaMeRreshta(tekst) {
  const s = String(tekst || "").replace(/\s+/g, " ").trim();
  return s.replace(/\s+([—–])\s+/g, "\n$1 ");
}

// ── KARTELA1 — SIPËRFAQJA E KARTELAVE TË POSTIMIT ───────────────────────────
//
// 29 gusht 2026. Pronari: «çdo gjë duhet ta ndjekë temën. Oda është në rregull,
// por Së fundi është gabim. Do të doja të mund të bëj switch mes Odës dhe
// Historisë në të gjitha pjesët ku ka postim».
//
// ÇFARË KISHTE NDODHUR. Ballina, Historia, Statistikat dhe Shërbimet i
// vizatonin kartelat mbi `panel` — një sipërfaqe e ÇELËT — ndërsa sfondi i
// ekranit është i thellë. Pra mbi një të kuqe të errët notonin drejtkëndësha
// kremi: nuk janë të palexueshëm, por nuk i përkasin faqes. Oda, e shkruar më
// vonë, e kishte zgjidhur ndryshe: `accentTint` (i tejdukshëm mbi vetë sfondin)
// me tekst `onBg`. Prandaj Oda «dukej mirë» dhe Së fundi «palidhje» — nuk ishin
// dy shije, ishin dy zgjidhje të ndryshme të së njëjtës pyetje, të lëna të dyja.
//
// ZGJIDHJA. Një funksion i vetëm kthen ÇIFTIN e plotë sipërfaqe+bojë. Kush e
// merr sfondin nga këtu, e merr edhe tekstin nga këtu — pra rregulli i kontrastit
// (i shkruar te `kontrollo-kontrastin.mjs` pas katër raportimeve) nuk mbetet më
// gjë që duhet mbajtur mend: bëhet i pamundur për t'u thyer, sepse çifti është
// një objekt, jo dy zgjedhje të ndara.
//
// PSE KTHEN NJË PALETË TË PLOTË, JO KATËR NGJYRA. Ekranet i kanë tashmë të
// shkruara `COLORS.panel` / `COLORS.ink` në dhjetëra vende, dhe secili çift
// është tashmë i saktë. Duke i zëvendësuar VETËM ata katër çelësa dhe duke lënë
// të tjerët të pandryshuar, ekranet nuk kanë nevojë të rishkruhen: ata vazhdojnë
// të thonë «sipërfaqe» dhe «bojë», dhe stili vendos se çfarë do të thotë kjo.
// (Prandaj edhe `ServicesScreen.jsx` nuk preket fare — merr paletën e gatshme.)
//
// Dritaret që hapen (`Modal`) dhe menytë NUK e ndjekin: ato mbulojnë ekranin,
// pra sfondi i thellë s'është më poshtë tyre — kërkesë e shprehur e pronarit.
//
// ─────────────────────────────────────────────────────────────────────────────
// DY ZGJEDHJE, DHE KREMI JASHTË TË DYJAVE
//
// Versioni i parë i kësaj i quajti degët «oda» dhe «historia», ku «historia»
// thjesht kthente `COLORS` — pra sipërfaqen klasike `panel`, që është KREM
// (#F6F2E7). Pronari e sqaroi menjëherë, dhe sqarimi ishte i drejtë:
//
//   «ngjyra krem te ballina nuk më pëlqen, më shumë më pëlqen ngjyra e bardhë
//    siç ishte te historia… krem që ishte te Së fundi e urrej.»
//
// Pra zgjedhja s'ishte kurrë mes «kremit» dhe «Odës». Ishte mes **së bardhës**
// dhe **së kuqes së Odës**. Prandaj dega e çelët nuk e merr më `panel`; e bardha
// shkruhet hapur, dhe kremi nuk del më te asnjë kartelë postimi.
//
//   erret    e ngritur mbi sfondin e thellë, tekst i çelët
//   bardhe   e bardhë e pastër, tekst i errët
//
// EMRI. Fillimisht quhej «oda», sepse aty e pashë të parën. Por një ekran nuk
// është ngjyrë: Oda mund të ndryshojë nesër, dhe atëherë emri do të tregonte
// diçka që s'ekziston më. Tani thotë çfarë është — e errët, ose e bardhë.
//
// SIPËRFAQJA E ERRËT ËSHTË `accentTint` — DHE MBETET.
//
// E provova një herë ndryshe: e ngritur me vetë bojën e faqes, që hue-ja të
// mos rrëshqiste drejt kafesë. Teknikisht ishte më «e pastër». Pronari e pa dhe
// tha të kundërtën: «te e errëta më shumë po më pëlqen e kafta siç ishte».
//
// Kishte edhe një arsye të dytë, që e vuri re vetë nga një pamje ekrani: te
// «Së shpejti» rreshtat përdorin `accentTint` DREJTPËRDREJT, jo `panel` — pra
// ndryshimi im i kishte lënë jashtë, dhe kutitë e statistikave dilnin me një
// ngjyrë tjetër nga rreshtat pranë tyre. Dy ngjyra ku duhej një.
//
// Kthimi te `accentTint` i zgjidh të dyja njëherësh: ngjyra që i pëlqen, dhe
// e njëjta ngjyrë kudo. Shënimi rri këtu që të mos «rregullohet» sërish nga
// dikush që sheh vetëm matematikën e hue-s.
//
// ROJA E BOJËS. Te dega e bardhë boja NUK merret verbërisht nga dizajni aktiv:
// një dizajn me sfond të errët ka `ink` TË ÇELËT, dhe tekst i çelët mbi të
// bardhë do të thotë faqe bosh. Prandaj: nëse boja e dizajnit është e çelët,
// biem te boja klasike e errët. E njëjta arsye si te i gjithë rregulli i
// kontrastit — sipërfaqja dhe boja vendosen bashkë, ose nuk vendosen fare.
//
// `etiketa` — BOJA E ETIKETAVE TË VOGLA («Një lidhje e re», «💌 Trashëgimi»).
//
// Ari mbi letër të bardhë jep 2,2:1 — praktikisht i palexueshëm. E ndreqa një
// herë duke e errësuar arin (#8A6D12, 4,9:1), dhe ajo ishte përgjigje e gabuar
// ndaj pyetjes së duhur. Pronari e tha qartë:
//
//   «edhe në fushë të bardhë nuk duhet të ketë tekst ari por i zi — rregullo në
//    të gjithë platformën që kontrasti të përshtatet kur ndryshojnë ngjyrat.»
//
// RREGULLI, JO NGJYRA: etiketa nuk është ngjyrë e vetën, është funksion i
// SIPËRFAQES. Sipërfaqe e çelët → bojë e errët. Sipërfaqe e thellë → ar. Prandaj
// rri këtu, brenda çiftit, dhe jo e shkruar një nga një te njëzet vende.
//
// Ari nuk zhduket nga aplikacioni — mbetet aty ku i takon: mbi sfondin e thellë
// (skedat, pilulat, butonat), ku është i lexueshëm dhe është vetë tema.
//
// Vlerat e lejuara ruhen te kufizimi i tabelës (KARTELA1), jo te një listë e
// dytë këtu: një listë që s'e lexon askush është vetëm një vend më shumë ku
// mund të mos përputhet.
function kartelaPalete(COLORS, stili) {
  if (stili === "erret") {
    return {
      ...COLORS,
      panel: COLORS.accentTint,
      panelSoft: _rgba(COLORS.gold, 0.09),
      ink: COLORS.onBg,
      inkLight: _rgba(COLORS.onBg, 0.68),
      etiketa: COLORS.gold,
    };
  }
  return {
    ...COLORS,
    panel: "#FFFFFF",
    panelSoft: "#F4F3F1",
    ink: _luma(COLORS.ink) < 0.5 ? COLORS.ink : "#2A2118",
    inkLight: _luma(COLORS.inkLight) < 0.62 ? COLORS.inkLight : "#6B6152",
    // Mbi letër të bardhë etiketa është e zezë, jo ar — kërkesë e shprehur.
    // E ngulitur me qëllim: një dizajn mund ta ketë `ink` blu ose të gjelbër, dhe
    // atëherë etiketa do të merrte atë ngjyrë; ky rresht thotë «e zezë», dhe e
    // zeza mbi të bardhë është e njëjta, sido që të ndryshojë paleta.
    etiketa: "#2A2118",
  };
}
// Emblemat e temës (shqiponja / stema e Kosovës / stema e familjes) i përkasin
// pamjes KLASIKE (sfond i errët). Kur një dizajn i ÇELËT (p.sh. Moderne) e
// zotëron pamjen, i fshehim — kështu temat & dizajnet mbeten KOMPLET të ndara.
function emblemsVisible() { try { return _luma(COLORS.bg) < 0.5; } catch (_e) { return true; } }
// EMBLEMAT SI PRONË E DIZAJNIT (menaxhohen nga Design Studio, jo nga tema e
// familjes). Nëse dizajni aktiv ka `tokens.emblems`, ai vendos ç'shfaqet
// (p.sh. shqiponja te Moderne nëse e dëshiron admini). Pa këtë → bie te tema.
function designEmblems() {
  try { const d = getDesign(); return (d && d.id && d.tokens && d.tokens.emblems) ? d.tokens.emblems : null; }
  catch (_e) { return null; }
}
// A duhet shfaqur emblema `kind` (eagle/kosovo)? Dizajni komandon nëse e ka
// përcaktuar; përndryshe sjellja e vjetër (tema + vetëm në pamje të errët).
function showEmblem(kind, themeKey) {
  const em = designEmblems();
  if (em) return !!em[kind];
  // Nëse hierarkia është AKTIVE (ka design), MOS bie te tema e vjetër — përndryshe
  // shqiponja + Kosova + yjet mbivendosen ("lemsh"). Emblemat vijnë vetëm nga dizajni.
  try { if (getDesigns().length) return false; } catch (_e) {}
  try { return !!(THEMES[themeKey] && THEMES[themeKey][kind]) && emblemsVisible(); }
  catch (_e) { return false; }
}
// ── HOLLËSITË E SHIRITIT TË SIPËRM ────────────────────────────────────────
//
// 28 gusht 2026. Këto janë zgjedhje pamjeje për të cilat askush s'e di
// përgjigjen para se t'i shohë në telefon: a duhet ikona para emrit të
// familjes, sa e madhe fotoja, a duhet kornizë rreth saj. Prandaj nuk rrinë
// të ngurta te kodi — janë pronë e dizajnit, si emblemat, dhe ndryshohen te
// Admin Center → Design Studio, pa ndërtim të ri.
//
// Parazgjedhjet janë ato që kërkoi useri: te telefoni vetëm fotoja, e
// rrumbullakët dhe më e madhe, pa kutinë katërkëndore; te desktopi si më parë.
// BALLINA1 — skedat e ballinës. Këto rrinë JASHTË komponentit me qëllim: efekti
// që korrigjon skedën e zgjedhur duhet të xhirojë edhe kur `App` kthehet herët
// gjatë nisjes (shih `bootSettled`). Një funksion brenda komponentit do të ishte
// ende i painicializuar në atë çast, dhe efekti do të hidhte ReferenceError.
// BALLINA6 — TRI PYETJE, TRI SKEDA.
//
// Deri sot ballina kishte pesë skeda: Feed, Kujtime, Trashëgimia, Arkiva, Oda.
// Katër prej tyre ishin LLOJE GJËRASH («çfarë është ky send»), jo pyetje që bën
// një njeri kur hap aplikacionin. Prandaj askush s'e dinte te cila të shkonte:
// një foto e gjyshit është kujtim, është arkivë, dhe është histori — të tria.
//
// Tani ballina i përgjigjet tri pyetjeve, dhe vetëm atyre:
//
//   Së fundi   — çfarë ndodhi (e shkuara e afërt)
//   Së shpejti — çfarë vjen (e ardhmja e afërt)
//   Oda        — çfarë tregohet sot
//
// Kujtimet, Trashëgimia dhe Arkiva NUK u fshinë. Ato shkuan te Historia, ku
// bëhen një lumë i vetëm kohor me filtra — sepse aty pyetja është një e vetme:
// «çfarë ka ndodhur me këtë familje».
const HOME_TAB_ETIKETA = (t) => ({
  feed: t("Recently"),
  upcoming: t("Upcoming"),
  // BALLINA7 — statistikat mund të rrinë edhe si skedë e ballinës. Nuk është
  // parazgjedhje: zgjidhet te Admin Center, sepse aty vendoset çdo gjë tjetër
  // e pamjes. Trupi është i njëjti si te ekrani — një burim, dy vende.
  stats: t("Statistics"),
  // «Oda» — dhoma ku tregohen anekdotat, fjalët e urta dhe barcoletat.
  // Emri s'ka nevojë për shpjegim te asnjë familje shqiptare.
  oda: t("The Oda"),
});
// BALLINA2 — titulli i vogël mbi çdo seksion të ballinës. Një funksion i
// vetëm, që «SË SHPEJTI» dhe «RISITË NË FAMILJE» të mos jenë kurrë dy stile.
// SHKRONJAT E MËDHA JANË PËR RREZIKUN, JO PËR TITUJT (29 gusht 2026).
//
// Pronari: «a mund t'i kontrollosh capital letters në krejt platformën — vetëm
// diçka rrezik mund të shkruhet me capital letter».
//
// Kishte të drejtë, dhe arsyeja është më e thellë se shija: kur ÇDO titull i
// vogël bërtet, asnjë bërtitje nuk dëgjohet më. Fjala «FSHIJ» te dritarja e
// fshirjes duhet të dallohet nga gjithçka tjetër te ekrani — dhe nuk dallohet
// dot nëse pranë saj rrinë njëzet etiketa po aq të mëdha.
//
// Prandaj `textTransform: "uppercase"` u hoq nga i gjithë aplikacioni. Madhësia
// e vogël, fonti mono dhe hapësira mes shkronjave e thonë tashmë «kjo është
// etiketë, jo fjali» — pa ngritur zërin.
const SEKSION_TITULL = (COLORS, FONTS) => ({
  fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
  color: COLORS.onBg, opacity: 0.72, margin: 0,
});

const HOME_TAB_KATALOGU = ["feed", "upcoming", "oda", "stats"];
// Skedat e tërhequra. Nuk mjafton t'i heqësh nga katalogu: ka familje me
// `home_tabs` të ruajtur që i përmendin ende, dhe filtri do t'ua zbrazte
// ballinën. Lista rri e shkruar që rregulli të lexohet, jo të merret me mend.
const HOME_TAB_TE_TERHEQURA = ["memories", "legacy", "arkiva"]; // → Historia
const HOME_TAB_PARAZGJEDHJE = ["feed", "upcoming", "oda"];
// BALLINA4 — ikona për renditjen «ikona». Të gjitha nga grupi që aplikacioni
// tashmë importon; asnjë varësi e re për një zgjedhje pamjeje.
const HOME_TAB_IKONA = {
  feed: Megaphone, upcoming: Bell, oda: BookOpen, stats: BarChart3,
};
// `arkiva` shfaqet vetëm nëse arkivi është i ndezur — përndryshe do të ishte një
// skedë që hap një zbrazëti. Nëse gjithçka filtrohet, kthehu te Feed, jo te një
// ballinë pa asnjë skedë.
// MUZEU4 — ÇFARË NUK E TRASHËGON MUZEU.
//
// Kjo listë është më e rëndësishme se ajo e veçorive që muzeu i merr, sepse
// pikërisht këtu një muze do të dukej i prishur:
//
//   · Trashëgimia (Legacy) — mesazhe për të ardhmen s'kanë kuptim pa familje;
//   · Oda — anekdota e barcoleta te një muze janë ton i gabuar.
//
// Nuk fshihen me kusht anësor te ekrani: hiqen nga vetë lista e skedave, pra
// asnjë vend tjetër nuk ka nevojë ta dijë rregullin. Një muze me njoftime
// ditëlindjesh ose me barcoleta nuk është veçori e pambaruar; është defekt.
const HOME_TAB_JO_TE_MUZEUT = ["oda"];

// `archiveEnabled` nuk përdoret më këtu — arkivi nuk është skedë ballinë. E mban
// nënshkrimi sepse tri vende e thërrasin; heqja e parametrit do t'i zhvendoste
// argumentet dhe `eshteMuze` do të merrte vlerën e gabuar. Parametrat hiqen kur
// hiqen thirrjet, jo më parë.
function skedatENjohura(cfg, archiveEnabled, eshteMuze) {
  const nga = Array.isArray(cfg) && cfg.length ? cfg : HOME_TAB_PARAZGJEDHJE;
  // GABIMI QË E FSHEHU «SË SHPEJTI». Versioni i parë hiqte vetëm skedat e
  // tërhequra dhe mbante pjesën tjetër: një cilësim ["feed","memories","legacy",
  // "arkiva","oda"] mbetej ["feed","oda"] — listë krejt e vlefshme, pra
  // parazgjedhja nuk ndizej kurrë dhe «Së shpejti» s'shfaqej ASNJËHERË te asnjë
  // familje që kishte konfiguruar ballinën. Skeda ekzistonte dhe s'kishte si të
  // arrihej. Nëse cilësimi PËRMEND një skedë të tërhequr, ai u shkrua për formën
  // e vjetër: nuk pastrohet — zëvendësohet.
  const eVjeter = nga.some((k) => HOME_TAB_TE_TERHEQURA.includes(k));
  const gjalle = eVjeter ? [] : nga.filter((k) => HOME_TAB_KATALOGU.includes(k));
  const lista = (gjalle.length ? gjalle : HOME_TAB_PARAZGJEDHJE)
    .filter((k) => !eshteMuze || !HOME_TAB_JO_TE_MUZEUT.includes(k))
    .slice(0, 6);
  return lista.length ? lista : ["feed"];
}

const HEADER_PARAZGJEDHJE = {
  familyIcon: true, // ikona e njerëzve para emrit të familjes
  familyNameSize: 24, // përmasa e emrit të familjes te shiriti i sipërm (px). SISTEMI VIZUAL 2.9.2026: 20 → 24 (Fraunces nën 20 humb karakterin).
  avatar: 24,       // përmasa e fotos te desktopi (px)
  avatarM: 40,      // përmasa te telefoni (px)
  // KOKA1 (29 gusht 2026) — a lejohet fotoja ta rrisë kokën?
  //
  // Deri sot fotoja ishte elementi më i gjatë i rreshtit, pra ajo e caktonte
  // lartësinë: foto 50px do të thoshte kokë 50px.
  //
  // Provën e parë e bëra si numër — «sa px vend zë» — dhe ishte gabim: pronari
  // e rriti foton, koka u rrit, dhe zgjidhja ishte një fushë e dytë që duhej ta
  // gjente vetë. Një cilësim që kërkon një cilësim tjetër për të punuar nuk
  // është cilësim; është kurth.
  //
  // Tani është një vendim i vetëm. Kur ndizet, fotoja kufizohet te lartësia që
  // koka ka PA TË — e matur në çast, jo e hamendësuar — dhe del jashtë asaj
  // kutie njësoj nga të katër anët.
  // KOKA3 — NDEZUR ME PARAZGJEDHJE. Ishte `false`, pra duhej gjetur dhe ndezur
  // me dorë — dhe pronari e provoi katër herë pa e gjetur. Një mbrojtje që
  // duhet zbuluar nuk mbron askënd. Kush e do foton të rrisë kokën, e fik.
  avatarNoGrow: true,   // desktopi
  avatarNoGrowM: true,  // telefoni
  // KOKA2 (29 gusht 2026) — sa rreshta lejohet nën-rreshti.
  //
  // Kur fotoja u qendërzua ndaj të dy rreshtave, nën-rreshti humbi gjerësinë e
  // grupit të djathtë (te desktopi ai mban edhe emrin dhe shigjetën, pra është i
  // gjerë) dhe u këput në dy rreshta — dhe rreshti i dytë e rriti kokën sërish.
  //
  // Nuk ka zgjidhje ku teksti ka gjerësi të plotë DHE fotoja rri në qendër:
  // fotoja duhet ta zërë hapësirën në të dy rreshtat. Prandaj zgjedhja jepet,
  // jo hamendësohet — dhe të dyja anët e saj janë të vërteta:
  //
  //   2 = asnjë mesazh nuk pritet, por koka mund të rritet me një rresht
  //   1 = koka nuk rritet kurrë, por një mesazh i gjatë mbaron me «…»
  //
  // Një rrugë e tretë, pa kod: fike kornizën te desktopi. Atëherë grupi i
  // djathtë humb emrin dhe shigjetën, ngushtohet shumë, dhe teksti hyn vetë.
  subLines: 2,
  frame: true,      // korniza + shigjeta rreth avatarit te desktopi
  frameM: false,    // te telefoni
  padTop: 0,        // hapësirë SHTESË mbi shiritin e sipërm (px, mund të jetë negative)
  padBottom: 0,     // hapësirë SHTESË nën shiritin e poshtëm (px)
  sepGap: 6,        // hapësira majtas e djathtas ndarësit te rreshti i dytë
  subTracking: 0,   // hapësira mes shkronjave te rreshti i dytë (letter-spacing)
  sepChar: "•",     // vetë ndarësi — shenjë e lirë, ose bosh për asnjë
  sepSize: 0,       // përmasa e ndarësit në px; 0 = sa teksti rreth tij
  // SISTEMI VIZUAL (2 shtator 2026) — parazgjedhja ishte mono/11.5. Monospace
  // në 11px është shenja e terminalit, dhe ky rresht duket në çdo faqe. Tani
  // body/13. Vlerat e vendosura te Admin Center fitojnë mbi këto — nëse aty
  // është ende «mono», ndryshoje aty.
  subFont: "body",  // fonti i rreshtit të dytë: mono | body | display
  subSize: 13,      // përmasa e tij në px
  subWord: 0,       // hapësira mes FJALËVE (word-spacing) — kjo e ndan iOS-in nga Android-i
};

// Numër nga tokenat e kokës, me kufij — që një gabim shtypi te Admin Center të
// mos e prishë shiritin.
function headerNum(key, min, max) {
  const v = headerRaw(key);
  const fb = HEADER_PARAZGJEDHJE[key] || 0;
  if (v === undefined || v === "") return fb;
  const n = Number(v);
  if (!Number.isFinite(n)) return fb;
  return Math.max(min, Math.min(max, n));
}

// Hapësira mbi header-in dhe nën shiritin e tab-eve nis nga zona e sigurt e
// pajisjes — notch-i lart, shiriti i shtëpisë poshtë. Ajo zonë NUK preket:
// ajo është fizike, jo shije. Ky numër i shtohet asaj, ose i zbritet.
//
// KUFIJTË, dhe pse u zgjeruan. Fillimisht i vura -24 deri 48, me arsyetimin se
// më poshtë përmbajtja do të futej nën prerje. Arsyetimi qëndron, por vendimi
// ishte i imi dhe i heshtur: pronari shkroi -90, nuk ndodhi asgjë, dhe asgjë
// s'i tha pse. Një kufi që e gëlltit vlerën në heshtje është më keq se një kufi
// i gjerë — sepse e bën provën të duket e prishur.
//
// Tani -60 deri 96. Nën zonën e sigurt teksti fillon të hyjë nën prerje; ajo
// duket menjëherë me sy, dhe syri vendos më mirë se numri im.
// SECILI SISTEM E KA ZONËN E VET. iPhone-i me Dynamic Island rezervon rreth
// 59px lart dhe 34px poshtë; Android-i rreth 24px dhe 0; shfletuesi asgjë. Pra
// një numër i vetëm i lëviz të treja njësoj, por nuk i BARAZON — dhe nëse te
// njëri duket mirë, te tjetri s'ka pse.
//
// Prandaj çdo vlerë mund të ketë një variant për iOS dhe një për Android:
// `padTopIOS`, `padTopAndroid`. Kur ai variant mungon, përdoret numri i
// përgjithshëm. Kështu një vlerë e vetme mjafton derisa të mos mjaftojë.
function platformaTani() {
  try { return Capacitor.getPlatform(); } catch (_e) { return "web"; }
}

// LEXIMI I VETËM I TOKENAVE TË KOKËS.
//
// Çdo vlerë mund të ketë variantin e vet për iOS dhe për Android — `sepGap`,
// `sepGapIOS`, `sepGapAndroid`. Kjo nuk ishte tekë: i njëjti tekst me të njëjtin
// font zë gjerësi të ndryshme te dy sistemet, sepse i masin shkronjat ndryshe.
// Prandaj "pak më ngushtë, vetëm te Android-i" duhej të ishte e mundur pa e
// prishur iPhone-in.
//
// Kthen vlerën E PAPËRPUNUAR (edhe tekstin bosh), ose `undefined` kur s'ka.
function headerRaw(key) {
  try {
    const h = getDesign()?.tokens?.header;
    if (!h) return undefined;
    const p = platformaTani();
    const veçantë = p === "ios" ? key + "IOS" : p === "android" ? key + "Android" : null;
    const ka = (k) => k && h[k] !== undefined && h[k] !== null && h[k] !== "";
    if (ka(veçantë)) return h[veçantë];
    if (h[key] !== undefined && h[key] !== null) return h[key];
  } catch (_e) { /* pa dizajn */ }
  return undefined;
}
function headerPad(key) {
  return headerNum(key, -60, 96);
}

// GABIM I IMI, i gjetur më 28 gusht në mbrëmje: e ndërtoja hapësirën si
// `calc(<zona e sigurt> + -12px)`. Për një vlerë pozitive kjo punon; për një
// negative shfletuesit e trajtojnë të gjithë deklaratën si të pavlefshme dhe e
// heqin — pra pikërisht rasti që ky cilësim ekziston për të nuk bënte asgjë.
//
// `calc(A + 12px)` dhe `calc(A - 12px)` janë të dyja të vlefshme. Shenja duhet
// të jetë veprim, jo pjesë e numrit.
function hapesira(baza, px) {
  if (!px) return baza;
  if (px > 0) return `calc(${baza} + ${px}px)`;
  // `padding` NUK mund të jetë negative. Kur zona e sigurt është më e vogël se
  // sa zbresim — p.sh. te shfletuesi, ku ajo është 0 — `calc(0px - 20px)` del
  // negative, dhe atëherë shfletuesi e hedh poshtë TË GJITHË deklaratën. Pra
  // rezultati s'është "pak më lart": është "asgjë nuk ndodhi".
  //
  // `max(0px, …)` e kap atë rast: sa të ketë zonë, aq hiqet; poshtë saj ndalet.
  return `max(0px, calc(${baza} - ${Math.abs(px)}px))`;
}
function headerToken(key) {
  const v = headerRaw(key);
  return v === undefined || v === "" ? HEADER_PARAZGJEDHJE[key] : v;
}

// DHËMBËZIMI I RRESHTIT TË DYTË ("3 breza • 14 anëtarë • …").
//
// Ai rresht rri nën emrin e familjes, dhe emri nis pas ikonës. Prandaj kishte
// një dhëmbëzim prej 22px të shkruar me dorë — i saktë sa kohë që ikona ishte
// gjithmonë aty. Sapo ikona u bë e fikshme, ai numër mbeti varur në ajër: teksti
// dukej i shtyrë pa asnjë arsye të dukshme.
//
// Tani lidhet me shkakun e vet: me ikonë → nën emrin; pa ikonë → në fillim, në
// një vijë me gjithçka tjetër. Dhe admini mund ta mbishkruajë me një numër, po
// të dojë diçka tjetër.
function headerSubIndent() {
  try {
    const d = getDesign();
    const v = d && d.tokens && d.tokens.header ? d.tokens.header.subIndent : undefined;
    if (v !== undefined && v !== null && v !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) return Math.max(0, Math.min(80, n));
    }
  } catch (_e) { /* asgjë */ }
  return headerToken("familyIcon") !== false ? 22 : 0;
}

// Nuanca feste (opsionale) — vendoset nga Design Studio te tokens.emblems.holiday.
const HOLIDAY_MOTIF = { christmas: "❄️", newyear: "✨", bajram: "🌙", easter: "🌷" };
function designHoliday() { const em = designEmblems(); return em && em.holiday ? em.holiday : null; }
// Family Crest shfaqet nëse: useri s'e ka fikur (getCrestOn) DHE dizajni s'e ndalon.
function crestVisible() {
  try { if (!getCrestOn()) return false; const em = designEmblems(); if (em && em.crest === false) return false; return true; }
  catch (_e) { return true; }
}

// URË E PLOTË: kur ka dizajn AKTIV, KREJT app-i (guaska, header, sidebar, pema,
// kartela, modale, feed) adopton dizajnin. Sfondi imziv (bg/bgDeep) merret nga
// dizajni; teksti/ikonat MBI sfond (onBg) llogariten sipas ndriçimit të bg-së
// që të lexohen si te temat e çelëta (Moderne) ashtu te ato të errëta (Klasike).
// Hex REALE → edhe atributet SVG (`fill=`/`stroke=`) dhe `color=` i ikonave punojnë.
// Pa dizajn aktiv → {} → pamja KLASIKE e paprekur (dhe "Klasike" ruhet gjithnjë).
// A i ka tokeni fushat "e plota" (bgDeep/onBg/lineSoft/selection/spouseLine)?
// Nëse PO → dizajni është plotësisht token-driven (edhe Klasike editohet nga
// Admin Center). Nëse JO (token i vjetër) dhe temë e errët → mbajmë klasikun e
// ngulitur (migrim i sigurt: asgjë s'prishet derisa të ekzekutohet SQL-ja).
function _designHasFull(p) {
  return p && (p.bgDeep != null || p.onBg != null || p.lineSoft != null || p.selection != null || p.spouseLine != null);
}
function designUIColors() {
  try {
    const d = getDesign();
    // "follow system" = rezerva NEUTRALE kur s'ka rrjet DHE s'ka cache (offline
    // pa dizajn). Nuk ka `id`, por DUHET ta pikturojmë paletën (light/dark sipas
    // sistemit) mbi COLORS — që faqja e mirëmbajtjes dhe stilet inline të mos
    // mbeten te e kuqja klasike. Ndryshe nga klasiku, këtu NUK biem te albania.
    const followSystem = !!(d && d.followSystem);
    if ((!d || !d.id) && !followSystem) return {};
    const p = paletteForMode() || {};
    const bgDark = p.bg ? _luma(p.bg) < 0.5 : false;
    const full = _designHasFull(p);
    // Temë e ERRËT me token të VJETËR (pa fushat e plota) → klasiku i ngulitur,
    // ekzakt (i sigurt). Sapo Klasike të plotësohet me SQL, bëhet token-driven.
    // Rezerva "follow system" e kapërcen këtë portë (s'duam të kuqen klasike).
    if (bgDark && !full && !followSystem) return {};
    const out = {};
    if (p.surface) out.panel = p.surface;
    if (p.surface2) out.panelSoft = p.surface2;
    if (p.text) out.ink = p.text;
    if (p.text2) out.inkLight = p.text2;
    if (p.bg) out.bg = p.bg;
    const bgDeep = p.bgDeep || (p.bg ? _darken(p.bg, bgDark ? 0.45 : 0.06) : null);
    if (bgDeep) out.bgDeep = bgDeep;
    const onBg = p.onBg || (p.bg ? (bgDark ? (p.surface || "#F6F2E7") : (p.text || "#241f18")) : null);
    if (onBg) out.onBg = onBg;
    // SISTEMI VIZUAL — shiriti i kokës mund të jetë i errët edhe kur dyshemeja
    // është e çelët. Teksti mbi të ndjek shiritin, jo dyshemenë.
    //
    // RREGULLI (i ndrequr natën e 2 shtatorit, pasi Nova e mori emrin e familjes
    // me ngjyrën e sipërfaqes): kur dyshemeja dhe shiriti janë të dyja të errëta
    // ose të dyja të çelëta — pra çdo dizajn që ekzistonte para sonte — teksti
    // mbi shirit është saktësisht `onBg`, si gjithmonë. Vetëm kur ato ndahen
    // hyn logjika e re. Versioni i parë thoshte «shirit i errët → surface», që
    // te një dizajn i errët është vetë e errët. Gabim i imi.
    const bgDeepDark = bgDeep ? _luma(bgDeep) < 0.5 : bgDark;
    let onBgDeep = p.onBgDeep || null;
    if (!onBgDeep) {
      if (bgDeepDark === bgDark) onBgDeep = onBg;                 // si gjithmonë
      else if (bgDeepDark)       onBgDeep = "#F6F2E7";            // shirit i errët mbi dysheme të çelët
      else                       onBgDeep = p.text || "#241f18";  // shirit i çelët mbi dysheme të errët
    }
    if (onBgDeep) out.onBgDeep = onBgDeep;
    if (p.accent) { out.gold = p.accent; out.accentTint = _rgba(p.accent, 0.16); } // Klasike: ari; Moderne: jeshil
    // Theksi si TEKST: mbi dysheme të errët vetë theksi; mbi të çelët, i errësuar
    // aq sa të lexohet — ose ai që e ka shkruar dizajni vetë.
    if (p.accentText) out.accentText = p.accentText;
    else if (p.accent) out.accentText = bgDark ? p.accent : _darken(p.accent, 0.35);
    // Fushat dhe sipërfaqet «e buta» (kutitë e shënimeve, rreshtat e bllokuar).
    // Te një dizajn i errët teksti është i çelët, ndaj edhe këto duhet të jenë
    // të errëta — përndryshe tekst i çelët mbi të çelët. Të butat bëhen ngjyrime
    // të tejdukshme mbi sipërfaqen, që të funksionojnë në të dyja temat.
    out.field = bgDark ? (p.surface2 || p.surface || "#2A251C") : "#FFFFFF";
    if (bgDark) {
      out.dangerSoft = _rgba(BASE_COLORS.danger, 0.22);
      out.roseSoft = _rgba(BASE_COLORS.rose, 0.22);
    }
    const lineSoft = p.lineSoft || p.border;
    if (lineSoft) out.goldSoft = lineSoft;
    const sel = p.selection || p.accent;
    if (sel) out.moss = sel;
    const spouse = p.spouseLine || p.accent;
    if (spouse) out.spouseLine = spouse;
    if (p.branch) {
      if (p.branch.core) out.branchCore = p.branch.core;
      if (p.branch.married_in) out.branchMarriedIn = p.branch.married_in;
      if (p.branch.cousin) out.branchNearCousin = p.branch.cousin;
      if (p.branch.extended) out.branchFarCousin = p.branch.extended;
    }
    // (Hequr override-i "gri neutrale për çdo temë të çelët" — shkaktonte
    // "lemsh" te Classic në light/Follow-system, sepse panelet kremi klasik
    // bëheshin gri. Tani BESOJMË TOKENAT: Moderne ka surface2 të ftohtë në DB,
    // Classic-light ka krem klasik — secili sipas tokenit të vet.)
    return out;
  } catch (_e) { return {}; }
}
// Fontet plotësisht nga tokeni (display/body). Kështu Moderne ka fontin e vet
// (p.sh. Space Grotesk) dhe Klasike të vetin (Fraunces) — të dyja nga Admin Center.
function designUIFonts() {
  try {
    const d = getDesign();
    const followSystem = !!(d && d.followSystem);
    if ((!d || !d.id) && !followSystem) return {};
    const p = paletteForMode() || {};
    const bgDark = p.bg ? _luma(p.bg) < 0.5 : false;
    if (bgDark && !_designHasFull(p) && !followSystem) return {}; // Klasike i vjetër → fontet klasike të ngulitura
    const f = p.fonts || {};
    const out = {};
    if (f.display) out.display = f.display;
    if (f.body) out.body = f.body;
    return out;
  } catch (_e) { return {}; }
}
// Mënyra e ikonave e dizajnit AKTIV — vjen nga tokeni `layout.iconMode`:
//   'original'   = ikonat klasike, siç kanë qenë nga fillimi (DEFAULT nëse mungon)
//   'emoji_menu' = emoji VETËM te menyja e avatarit + veprimet e hub-it
//   'emoji_all'  = emoji në TË GJITHË platformën
//   'heirloom'   = seti i vizatuar për dizajnin «Heirloom» (`ikonatHeirloom.jsx`)
// Vendoset për SECILIN dizajn nga Design Studio. Ruhet si variabël modulare që
// lexohet në render; App e sinkronizon nga dizajni (onDesignChange → setState).
let NOVA_ICON_MODE = "original";

// ════════════════════════════════════════════════════════════════════════════
// PAMJA E EKRANEVE SIPAS DIZAJNIT — `layout.pamja`  ·  3 shtator 2026
//
// Pronari e emërtoi vetë problemin, dhe e emërtoi saktë:
//   «kur po bëjmë ndryshime një nga një, shpesh po prishim diçka tjetër. A ka
//    mundësi ekranet, fushat, me çelës — që kur ndërron temën, ndërrojnë fushat
//    edhe ekranet përshtaten temës?»
//
// PO, DHE KJO ËSHTË ZGJIDHJA E VËRTETË E ASAJ QË PO NA NDODH.
// Deri tani çdo hap ka qenë REDAKTIM i të njëjtit ekran: ndrysho një ngjyrë për
// Heirloom-in dhe e ndryshove edhe për tetë dizajnet e tjera; ndreq një madhësi
// këtu dhe prishe një rreshtim atje. Sa më shumë dizajne, aq më shumë kushte të
// fshehura brenda një kodi të vetëm — dhe pikërisht ashtu prishet diçka tjetër
// sa herë ndreqim një gjë.
//
// Me një ÇELËS PAMJEJE, e reja nuk e redakton të vjetrën: rri krah saj.
//     pamja("pema", "heirloom") ? <PemaHeirloom/> : <PemaKlasike/>
// Dega klasike mbetet e paprekur, fjalë për fjalë — pra kthimi te çdo dizajn
// tjetër e kthen aplikacionin saktësisht ashtu siç ishte, pa u mbështetur te
// kujtesa ime se çfarë preka. Dhe një pamje e re mund të ndërtohet e plotë, e
// pastër, pa frikën se po prish diçka që s'e ke parë prej javësh.
//
// Mungesa = "klasike". Pra asnjë dizajn ekzistues nuk lëviz.
// ⚠ NDRYSHIM PAS PYETJES SË PRONARIT (3 shtator 2026, natë):
//   «a i bëjmë çelësat e dritareve në mënyrë që të jenë theme-independent?»
//
// Kishte të drejtë, dhe versioni i parë i imi ishte i ngushtë: një çelës i
// VETËM për krejt dizajnin (`"pamja": "heirloom"`) do të thoshte se një temë i
// merr TË GJITHA ekranet përnjëherë, ose asnjë. Kjo e kthen çdo hap në një
// zgjedhje gjithçka-ose-asgjë, dhe pikërisht atë po përpiqemi të shmangim.
//
// Tani çelësi është PËR SECILIN EKRAN:
//     "pamja": { "pema": "heirloom", "feed": "klasike" }
// Domethënë:
//   · Ndërtojmë një ekran, e ndezim vetëm atë, dhe të tjerët nuk luajnë fare.
//   · Nëse Pema e re nuk të pëlqen, e kthen VETËM Pemën — pa u prekur asgjë
//     tjetër, dhe pa deploy.
//   · Dy dizajne mund të ndajnë të njëjtin ekran ose të kenë secili të vetin;
//     asnjëri s'e prek tjetrin. Ky është kuptimi i «të pavarura».
//
// Pranohet edhe një varg i vetëm (`"pamja": "heirloom"`) — do të thotë «kjo
// pamje për çdo ekran» — dhe çelësi `"*"` shërben si parazgjedhje brenda hartës.
// Mungesa = "klasike" kudo, pra asnjë dizajn ekzistues nuk lëviz.
let PAMJA_E_DIZAJNIT = null;
function pamjaEDizajnit() {
  try {
    const d = getDesign();
    const v = d && d.tokens && d.tokens.layout && d.tokens.layout.pamja;
    if (!v) return null;
    return (typeof v === "string") ? String(v) : (v && typeof v === "object" ? v : null);
  } catch (_e) { return null; }
}
/** Cila pamje i takon këtij ekrani. */
function pamjaE(ekrani) {
  const v = PAMJA_E_DIZAJNIT;
  if (!v) return "klasike";
  if (typeof v === "string") return v;
  return String(v[ekrani] || v["*"] || "klasike");
}
/** A vizatohet ky ekran me këtë pamje? `pamja("pema", "heirloom") ? … : …` */
function pamja(ekrani, emri) { return pamjaE(ekrani) === String(emri || "klasike"); }

// IKONAT1 (2 shtator 2026) — IKONAT E PËRMBAJTJES: Emoji ose Lucide.
//
// Një çelës i vetëm, për krejt platformën, te Admin Center. Ndryshe nga
// `NOVA_ICON_MODE` (që i takon dizajnit dhe mbulon kromin — navigimin dhe
// veprimet), ky mbulon ikonat e PËRMBAJTJES: rubrikat e Odës, llojet e
// ngjarjeve, etiketat e feed-it, datat e ardhshme.
//
// Parazgjedhja është 'emoji' — pra saktësisht siç ishin para 2 shtatorit.
// Lucide bëhet i dukshëm vetëm kur pronari e ndez vetë. Kështu ndryshimi i
// sotëm te kartelat nuk i ndryshon asgjë askujt pa vendim.
let IKONAT_PERMBAJTJE = "emoji";
function IkonaP({ Lucide, emoji, size = 14, color, strokeWidth = 1.75, style }) {
  if (IKONAT_PERMBAJTJE === "lucide" && Lucide) {
    return <Lucide size={size} strokeWidth={strokeWidth} color={color} style={style} />;
  }
  return (
    <span aria-hidden style={{ fontSize: Math.round(size * 0.95), lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0, ...(style || {}) }}>{emoji}</span>
  );
}
function designIconMode() {
  try {
    const d = getDesign();
    const m = d && d.tokens && d.tokens.layout && d.tokens.layout.iconMode;
    return (m === "emoji_menu" || m === "emoji_all" || m === "heirloom") ? m : "original";
  } catch (_e) { return "original"; }
}
// Zbato dizajnin aktiv menjëherë në ngarkim (para render-it të parë).
Object.assign(COLORS, designUIColors());
NOVA_ICON_MODE = designIconMode();
PAMJA_E_DIZAJNIT = pamjaEDizajnit();

// ── SHIRITI I SISTEMIT: ora, bateria, rrjeti ────────────────────────────────
//
// 29 gusht 2026. Pronari: «kur kam ngjyra të errëta, si t'ia bëj që nuk duket
// sa është ora, sa ka bateri — nuk duket shiriti origjinal i telefonit».
//
// SHKAKU ISHTE NJË FJALË E LEXUAR MBRAPSHT. Poshtë kishte:
//     StatusBar.setStyle({ style: StatusBarStyle.Light })
//     // ikona/tekst i çelët — header-i i app-it është sfond i errët
//
// Komenti thotë saktësisht të kundërtën e kodit. Te Capacitor, `Style` NUK e
// përshkruan ngjyrën e tekstit; e përshkruan MËNYRËN:
//     Style.Dark  = «modaliteti i errët»  → tekst i ÇELËT (për sfond të errët)
//     Style.Light = «modaliteti i çelët»  → tekst i ERRËT (për sfond të çelët)
// Pra `Light` mbi një header vishnje të errët vizaton orë e bateri të zeza mbi
// të errët. Ato nuk «zhduken» — janë aty, thjesht të padallueshme.
//
// DHE NUK MJAFTON THJESHT TA PËRMBYSËSH. App-i ka shtatë tema plus dizajnet e
// Design Studio-s, disa të errëta e disa të çelëta, dhe përdoruesi i ndërron
// gjatë punës. Një konstante — cilado qoftë — do të ishte e gabuar për gjysmën
// e tyre. Prandaj stili LLOGARITET nga ngjyra që ndodhet vërtet nën shirit:
// `COLORS.bgDeep`, sfondi i header-it. Ndriçim nën 0.5 → tekst i çelët; mbi →
// i errët. Kështu asnjë temë e ardhshme nuk kërkon rregullim këtu.
//
// `theme-color` përditësohet me të njëjtën ngjyrë. Te `index.html` ai rri i
// ngulitur `#1F2A1E` — jeshilja e temës «The Forest» — kurse parazgjedhja e
// app-it është vishnje. Ai meta prek shiritin te PWA-ja dhe te shfletuesi, pra
// duhet ta ndjekë temën ashtu si shiriti nativ.
// SHIRITI2 (29 gusht 2026) — «kur shkoj te menyja e avatarit, apo ndonjë meny
// tjetër që është light, atëherë shiriti i sistemit nuk duket». Te të dyja
// platformat, jo vetëm te iPhone-i.
//
// SHIRITI1 e lidhi stilin me `COLORS.bgDeep` — sfondin e header-it. Ai supozim
// vlen vetëm derisa header-i të jetë ai që ndodhet nën orë e bateri. Në telefon
// nuk është: `Modal` mbulon KREJT ekranin, dhe sipërfaqja e tij është krem e
// çelët. Pra nën shirit rri e çelëta, kurse stili mbeti ai i vishnjës — tekst i
// çelët mbi krem. I njëjti defekt si dje, thjesht një shtresë më lart.
//
// Prandaj ngjyra nuk merret më nga një ndryshore, por nga një PIRG: kush mbulon
// majën e ekranit, shtyn ngjyrën e vet dhe e heq kur mbyllet. Çdo dritare e
// aplikacionit kalon nga `Modal`, pra një rregullim i vetëm i mbulon të gjitha —
// menyja e avatarit, «Shiko», cilësimet, njoftimet, çdo panel i ardhshëm.
const PIRGU_I_SHIRITIT = [];
function shtyjNgjyreneShiritit(ngjyra) {
  const ze = { ngjyra };
  PIRGU_I_SHIRITIT.push(ze);
  try { sinkronizoShiritinESistemit(); } catch (_e) { /* pa efekt */ }
  return () => {
    const i = PIRGU_I_SHIRITIT.indexOf(ze);
    if (i >= 0) PIRGU_I_SHIRITIT.splice(i, 1);
    try { sinkronizoShiritinESistemit(); } catch (_e) { /* pa efekt */ }
  };
}
// Sipërfaqja e vërtetë lexohet me `getComputedStyle`, pra vjen si `rgb(...)`.
// Duhet edhe ajo formë, jo vetëm hex-i: dizajnet e Design Studio-s e japin
// sipërfaqen përmes `var(--surface)`, dhe vlera përfundimtare njihet vetëm pasi
// shfletuesi e ka zgjidhur.
function _ngjyraNeHex(c) {
  const s = String(c || "").trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return s;
  const m = s.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,/\s]+([\d.]+))?\s*\)$/i);
  if (!m) return null;
  // E tejdukshme = nuk e mbulon asgjë; le të vendosë shtresa poshtë saj.
  if (m[4] !== undefined && Number(m[4]) < 0.9) return null;
  const hx = (v) => Math.max(0, Math.min(255, Math.round(Number(v)))).toString(16).padStart(2, "0");
  return "#" + hx(m[1]) + hx(m[2]) + hx(m[3]);
}
function ngjyraNenShiritin() {
  // 640px = pika `sm` e Tailwind-it — e njëjta që e bën `Modal` dritare të vogël
  // në qendër. Mbi të, dritarja nuk e prek majën e ekranit, pra pirgu s'vlen.
  const mbulon = (() => { try { return window.innerWidth < 640; } catch (_e) { return true; } })();
  let nga_pirgu = null;
  for (let i = PIRGU_I_SHIRITIT.length - 1; mbulon && i >= 0 && !nga_pirgu; i--) {
    nga_pirgu = _ngjyraNeHex(PIRGU_I_SHIRITIT[i].ngjyra);
  }
  const c = nga_pirgu || _ngjyraNeHex(COLORS.bgDeep) || _ngjyraNeHex(COLORS.bg);
  return c || "#3A0A13";
}
export function sinkronizoShiritinESistemit() {
  const sfond = ngjyraNenShiritin();
  const eErret = _luma(sfond) < 0.5;
  try {
    let m = document.querySelector('meta[name="theme-color"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "theme-color");
      document.head.appendChild(m);
    }
    m.setAttribute("content", sfond);
  } catch (_e) { /* pa DOM (SSR/test) — s'ka çka të përditësohet */ }
  try {
    if (!Capacitor.isNativePlatform()) return;
    StatusBar.setStyle({ style: eErret ? StatusBarStyle.Dark : StatusBarStyle.Light }).catch(() => {});
    // Sfondi i shiritit: pa efekt kur WebView-i vizaton nën të (Android 15+ dhe
    // gjithnjë te ne, sepse `overlaysWebView` është `true`), dhe i papranuar te
    // iOS. E mbajmë sepse te pajisjet e vjetra Android ende ka rëndësi, dhe
    // dështimi kushton vetëm një premtim të kapur.
    StatusBar.setBackgroundColor({ color: sfond }).catch(() => {});
  } catch (_e) { /* jo nativ — mjaftoi `theme-color` */ }
}

function applyTheme(key) {
  const t = THEMES[key] || THEMES.albania;
  const ui = designUIColors();
  // RENDITJA KA RËNDËSI — dhe deri më 20 gusht ishte gjithmonë `t.colors` para
  // `ui`, pra ngjyrat e design-it e mbishkruanin PËRHERË temën e zgjedhur. Kjo
  // e bënte zgjedhësin e vjetër të temës një buton pa efekt sapo ekzistonte një
  // design: zgjedhja ruhej besnikërisht te baza dhe nuk dukej kurrë. Ankesa
  // "ndryshimet tek pamja nuk ruhen" ishte pikërisht ky heshtje.
  //
  // Tani komandon një sistem i vetëm, dhe cili është varet nga çka ka për të
  // ofruar hierarkia: nëse design-i aktiv ka tema, ai komandon (dhe zgjedhësi i
  // vjetër as nuk shfaqet). Nëse s'ka asnjë temë, zgjedhësi i vjetër është i
  // vetmi kontroll që i mbetet përdoruesit — pra ai duhet të fitojë.
  const legacyOnly = (() => { try { return getThemes().length === 0; } catch (_e) { return false; } })();
  if (legacyOnly) Object.assign(COLORS, BASE_COLORS, ui, t.colors);
  else Object.assign(COLORS, BASE_COLORS, t.colors, ui);
  sinkronizoShiritinESistemit(); // tema ndryshoi → edhe ora e bateria duhet të ndryshojnë ngjyrë
}
// Risinkronizim kur admini publikon një dizajn (ose ndërrohet light/dark):
// përditëso COLORS. Ri-render-i shkaktohet nga komponenti App (shih useEffect
// me onDesignChange → setState). Eksportuar këtu si funksion i thjeshtë.
let _colorsWereFollowSystem = false;
export function syncDesignColors() {
  const ui = designUIColors();
  const nowFollow = (() => { try { const d = getDesign(); return !!(d && d.followSystem); } catch (_e) { return false; } })();
  // Kalim NGA "follow system" (neutral) TE klasikja pa-dizajn (ku designUIColors
  // kthen {}): një Object.assign i {} s'do t'i pastronte ngjyrat neutrale, prandaj
  // rikthejmë bazën klasike shprehimisht përpara.
  if (_colorsWereFollowSystem && !nowFollow && Object.keys(ui).length === 0) {
    Object.assign(COLORS, BASE_COLORS, THEMES.albania.colors);
  }
  Object.assign(COLORS, ui);
  NOVA_ICON_MODE = designIconMode();
  _colorsWereFollowSystem = nowFollow;
  Object.assign(FONTS, designUIFonts());
  TIPI = tipografiaEDizajnit();          // rolet tipografike të dizajnit aktiv
  PAMJA_E_DIZAJNIT = pamjaEDizajnit();   // cila degë ekranesh vizatohet
  zbatoPermasatEKarteles();              // pamja e pemës e ndryshon kornizën
  // VETËM kur ka një dizajn aktiv. Pa këtë portë, `paletteForMode()` kthen
  // rezervën neutrale edhe te pamja klasike — dhe klasikja do të ndryshonte
  // ngjyra pa e prekur askush. Rregulli i vjetër i shtëpisë: asgjë nuk lëviz
  // derisa dikush zgjedh një dizajn.
  try {
    const _d = getDesign();
    PALETA_TIPI = (_d && (_d.id || _d.followSystem)) ? (paletteForMode() || {}) : {};
  } catch (_e) { PALETA_TIPI = {}; }
  // Dizajni u publikua, ose sistemi kaloi light↔dark. `bgDeep` sapo ndryshoi,
  // pra shiriti i sistemit duhet rillogaritur — përndryshe një dizajn i çelët
  // do të trashëgonte tekstin e çelët të vishnjës, dhe ora zhdukej sërish.
  try { sinkronizoShiritinESistemit(); } catch (_e) { /* nuk e bllokon ngjyrat */ }
}

// Paleta e "ikonave të gjalla" (moderne) + ndihmësi. `iconTint(role)` kthen
// ngjyrën e rolit VETËM kur dizajni aktiv i ka ndezur (COLORS.vividIcons);
// përndryshe kthen COLORS.ink (monokrom, si klasikja). Kështu i njëjti kod
// funksionon në çdo dizajn pa u bërë "ylber" te temat neutrale.
const ICON_TINTS = {
  profile: "#7C6FE0", security: "#3B82F6", appearance: "#EC4899", bell: "#F59E0B",
  help: "#14B8A6", info: "#0EA5E9", announce: "#FB7185", mail: "#F97316",
  members: "#6366F1", trash: "#EF4444", log: "#8B5CF6", export: "#10B981",
  family: "#22C55E", sync: "#0EA5E9", bring: "#6366F1", join: "#F59E0B", create: "#22C55E",
  tree: "#2E8B57", feed: "#F59E0B", stats: "#3B82F6", search: "#0EA5E9", add: "#22C55E",
  chat: "#7C6FE0", menu: "#6366F1", switch: "#0EA5E9", legacy: "#A855F7", back: "#0EA5E9",
  home: "#22C55E", memories: "#F59E0B", bellHeader: "#F59E0B",
};
function iconTint(role) {
  return COLORS.vividIcons ? (ICON_TINTS[role] || COLORS.ink) : COLORS.ink;
}
// Për ikonat e NAVIGIMIT (sidebar/tab/header) që kanë gjendje aktive: në
// dizajnin "vivid" ikona merr ngjyrën e vet moderne (plot); përndryshe ruan
// sjelljen ekzistuese (ari kur aktiv, onBg kur joaktiv).
function navIconColor(role, active) {
  return COLORS.vividIcons ? (ICON_TINTS[role] || COLORS.onBg) : (active ? COLORS.accentText : COLORS.onBg);
}
// Emoji për secilin "rol" ikone te menyja e avatarit + veprimet e hub-it.
const ICON_EMOJI = {
  profile: "👤", security: "🛡️", appearance: "🎨", bell: "🔔", help: "🛟", info: "ℹ️",
  announce: "📣", mail: "✉️", members: "👥", trash: "🗑️", log: "📜", export: "📤",
  family: "🏠", sync: "🔄", bring: "👨‍👩‍👧", join: "🔑", create: "✨",
};
// Ikona e menysë/hub-it: në mënyrat emoji shfaq emoji-n e rolit; përndryshe
// ikonën klasike (monokrome, siç ka qenë gjithmonë).
function NovaIcon({ role, Icon, size = 14 }) {
  if (NOVA_ICON_MODE === "emoji_menu" || NOVA_ICON_MODE === "emoji_all") {
    return <span aria-hidden style={{ width: size + 8, textAlign: "center", fontSize: size + 3, lineHeight: 1, flexShrink: 0 }}>{ICON_EMOJI[role] || "•"}</span>;
  }
  return <Icon size={size} color={COLORS.ink} />;
}
const FONTS = { display: "'Fraunces', Georgia, serif", body: "'Work Sans', system-ui, sans-serif", mono: "'IBM Plex Mono', monospace" };
// DYQANET1 — distinktivi i një dyqani (Google Play / App Store) te ekrani i
// hyrjes. Fotoja zyrtare nga /dyqani/<skedari> (public/); kur mungon, buton
// teksti. Në nivel moduli me qëllim: një komponent i deklaruar brenda trupit
// të App-it do të rimontohej në çdo render dhe do ta rikërkonte foton.
function Distinktivi({ href, skedari, teksti }) {
  const [paFoto, setPaFoto] = useState(false);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={teksti}
       style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", height: 40, borderRadius: 7, textDecoration: "none",
                background: paFoto ? "#000" : "transparent", color: "#fff", padding: paFoto ? "0 14px" : 0,
                fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, border: paFoto ? "1px solid #6b6b6b" : "none" }}>
      {paFoto ? teksti : <img src={`/dyqani/${skedari}`} alt={teksti} style={{ height: 40, display: "block" }} onError={() => setPaFoto(true)} />}
    </a>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TIPOGRAFIA E DIZAJNIT — `tokens.type`  ·  3 shtator 2026
//
// Pronari: «nuk jam tu kërku thjesht emoji, por identike, të njëjta si te
// mockup — stil i tekstit njësoj».
//
// KY ISHTE BOSHLLËKU I VËRTETË. Dizajni deri sot mbante NGJYRAT dhe FAMILJEN e
// shkronjave, po jo ROLET: sa i madh është një titull kartele, a është etiketa
// me shkronja kapitale dhe sa e hapur, çfarë ngjyre ka. Ato numra rrinë të
// ngulitur brenda ekraneve — prandaj dy dizajne me fonte krejt të ndryshme
// dilnin me të njëjtën peshë e të njëjtin ritëm, dhe asnjëri nuk dukej si vetja.
//
// Tani dizajni i mban edhe ato. `tokens.type` është një fjalor rolesh:
//   etiketa    mbishkrimi i vogël mbi titull (te Heirloom: kapitale ari)
//   titulli    titulli i kartelës
//   trupi      teksti i kartelës
//   nenshkrimi legjenda nën foto
//   faqja      titulli i një ekrani
//   nenfaqja   rreshti nën të
//   emri       emri i një personi
//   vitet      vitet nën emër
// Çdo rol pranon: font ('display'|'body'), size, weight, lh, ls, upper, italic,
// color (emër tokeni ngjyre, jo hex — që të ndjekë temën e çelët e të errët).
//
// MUNGESA NUK PRISH ASGJË: pa `type`, secili rol kthen atë që kishte ekrani më
// parë. Pra tetë dizajnet ekzistuese nuk lëvizin as një piksel.
let TIPI = {};
// Paleta e modalitetit aktual, e ruajtur në një çast — që `tip()` të mos e
// rindërtojë atë objekt për çdo kartelë në ekran. Rifreskohet te i njëjti vend
// ku rifreskohen ngjyrat, pra edhe kur sistemi kalon nga e çelëta te e errëta.
let PALETA_TIPI = {};
function tipografiaEDizajnit() {
  try { const d = getDesign(); return (d && d.tokens && d.tokens.type) || {}; }
  catch (_e) { return {}; }
}
// `rezerva` është stili i sotëm i atij roli. Kështu një dizajn mund ta
// përcaktojë vetëm etiketën dhe të lërë gjithçka tjetër ashtu siç është.
function tip(roli, rezerva) {
  const r = TIPI && TIPI[roli];
  if (!r) return rezerva || {};
  const out = { ...(rezerva || {}) };
  // `shkalla` — një numër i vetëm që i rrit ose i zvogëlon TË GJITHA rolet
  // bashkë, në përqindje. Ekziston sepse madhësia e duhur nuk lexohet dot nga
  // një CSS: varet nga vetë shkronja. Cormorant Garamond, për shembull, e ka
  // trupin e shkronjës së vogël dukshëm më të ulët se Fraunces — pra i njëjti
  // numër piksela jep tekst që SYRI e sheh më të vogël. Kjo ishte ankesa
  // «gjdo gje u zoom out». Rregullimi është një numër, jo tetë.
  const k = Number(TIPI.shkalla) > 0 ? Number(TIPI.shkalla) / 100 : 1;
  if (r.font) out.fontFamily = r.font === "display" ? FONTS.display : FONTS.body;
  if (r.size != null) out.fontSize = Math.round(r.size * k * 10) / 10;
  if (r.weight != null) out.fontWeight = r.weight;
  if (r.lh != null) out.lineHeight = r.lh;
  if (r.ls != null) out.letterSpacing = r.ls;
  if (r.upper != null) out.textTransform = r.upper ? "uppercase" : "none";
  if (r.italic != null) out.fontStyle = r.italic ? "italic" : "normal";
  // Ngjyra jepet si EMËR tokeni (`bronze`, `text2`, `accent`), jo si hex —
  // përndryshe do të ishte e saktë te tema e çelët dhe e palexueshme te e errëta.
  // Ngjyra kërkohet TE PALETA e dizajnit (`bronze`, `text`, `text2`, `accent`,
  // `muted`), jo te `COLORS`. Arsyeja është konkrete: te `COLORS`, çelësi
  // `gold` mban THEKSIN (terrakotën), jo arin — pra një etiketë e shkruar
  // «gold» do të dilte e kuqe. Paleta i ka emrat e vet, të pastër.
  if (r.color) {
    const c = PALETA_TIPI[r.color] || COLORS[r.color];
    if (c) out.color = c;
  }
  if (r.tnum) out.fontVariantNumeric = "tabular-nums";
  return out;
}

// ── PAMJA «HEIRLOOM»: RRESHTAT E NJOFTIMEVE TE BALLINA ──────────────────────
// 4 shtator 2026. Te mockup-i këto nuk janë rreshta mbi sipërfaqen e kartelës
// por shirita KREMI me qoshe të buta — «Me trishtim: … nuk është më në mesin
// tonë», «… tani janë çift». Dallimi është i qëllimshëm: ato nuk janë kartela
// përmbajtjeje, janë lajme të një fjalie, dhe kremi i ndan nga kartelat pa u
// dashur asnjë kryetitull.
// Marrin `KART` si argument sepse paleta e kartelave llogaritet brenda
// komponentit; këto rrinë jashtë që të mos rilindin te çdo render.
const njoftimiKutia = (KART) => (pamja("feed", "heirloom") ? {
  background: PALETA_TIPI.surface2 || KART.panel,
  borderRadius: 14, padding: "13px 15px", gap: 12,
} : { background: KART.panel });
const njoftimiTeksti = (KART) => (pamja("feed", "heirloom") ? {
  fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 500,
  color: PALETA_TIPI.text || KART.ink, lineHeight: 1.35,
} : { fontFamily: FONTS.body, fontSize: 15, color: KART.ink, lineHeight: 1.5 });

// ── PAMJA «HEIRLOOM»: GUACKA E KARTELËS SË PËRMBAJTJES ──────────────────────
// Kartela e mockup-it: e bardhë mbi letër, kufi ari flokëhollë (jo gri), qoshe
// 16, mbushje 17 dhe një hije që bie POSHTË e larg — `0 12px 30px -20px` — pra
// duket sikur letra rri mbi letër, jo sikur kartela pluskon. Kjo hije e vetme
// e ndryshon më shumë ndjesinë e faqes sesa çdo ngjyrë.
const kartelaGuacka = (KART, mbushja) => (pamja("feed", "heirloom") ? {
  background: PALETA_TIPI.surface || KART.panel,
  border: `1px solid ${PALETA_TIPI.accentBorder || KART.goldSoft}`,
  borderRadius: 16, padding: mbushja || 17,
  boxShadow: "0 12px 30px -20px rgba(44,38,37,.35)",
} : {
  background: KART.panel,
  border: `1px solid ${KART.goldSoft}`,
  borderRadius: 12, padding: mbushja || "14px 20px 16px",
  boxShadow: KARTELA_HIJE,
});



// SINKRON, NË NGARKIM TË MODULIT — dhe PIKËRISHT KËTU, jo më lart.
//
// `syncDesignColors()` shkruan te `COLORS` dhe te `FONTS`. `FONTS` deklarohet
// me `const` te rreshti sipër, ndaj një thirrje më herët do të binte me
// "Cannot access 'FONTS' before initialization" — pikërisht ajo racë gabimi që
// esbuild s'e sheh dhe që e kap vetëm `tools/tdz.cjs`. E provova dhe u kap aty.
//
// PSE FARE: `COLORS` nis nga tema klasike, sepse diçka duhet të jetë aty para se
// të dihet zgjedhja. Deri tani ajo "diçka" mbetej e dukshme — sinkronizimi bëhej
// te një `useEffect`, pra pas vizatimit të parë, dhe çdo rifreskim nxirrte një
// kornizë me të kuqen klasike mbi një faqe që tashmë kishte ngjyrën e duhur.
//
// `designLoader` tani e zbaton zgjedhjen e ruajtur sapo ngarkohet moduli, dhe
// modulet e importuara vlerësohen para importuesit — pra kur mbërrin ky rresht,
// paleta e përdoruesit është gati. Nuk ka më temë "të parazgjedhur" që dikush ta
// shohë; ka vetëm atë të tijën, dhe vendimin e serverit që vjen menjëherë pas
// dhe e mbishkruan.
try { syncDesignColors(); } catch (_e) { /* pa cache — mbeten vlerat bazë */ }
// Zbato fontet e dizajnit aktiv menjëherë në ngarkim (FONTS sapo u përkufizua).
Object.assign(FONTS, designUIFonts());
// `vizitor` rri veç: nuk është variant i «Shikues», është rol i një hapësire
// tjetër. Te një muze roli do të thotë çfarë mund të bësh, jo kush je.
// ⚠ 4 shtator 2026 — këto etiketa nuk kalonin nga `t()` në asnjë vend, pra
// roli i njeriut dilte SHQIP në çdo gjuhë. S'i kapte as kërkimi i zakonshëm:
// nuk kanë as `ë` as `ç`. Çelësat tani janë vetë këto fjalë, si kudo tjetër
// te aplikacioni — shih `Redaktor`, `Kontribues`, `Shikues`, `Vizitor` te
// `sq.json` dhe `en.json`.
const ROLE_LABELS = { admin: "Admin", editor: "Redaktor", contributor: "Kontribues", viewer: "Shikues", vizitor: "Vizitor" };

// ============================================================================
// RREGULLAT SIPAS SHTETIT (rule_sets) — ngjyrat/etiketat/pragjet/modeli tani vijnë
// nga databaza (rpc_get_effective_rules), JO hardcode. `ACTIVE_RULES` mbushet në
// ngarkim (shih useEffect-in te komponenti); këto vlera janë PARAZGJEDHJET (të
// njëjta me ç'ishin) — përdoren derisa të ngarkohen rregullat, ose si rezervë.
const DEFAULT_RULE_COLORS = { core: "#4C7A4A", married_in: "#D98CA0", married_out: "#B5657A", close_cousin: "#3E7EA6", extended: "#C99A28", divorced: "#C0392B", solo_ring: "#9CA3AF" };
const DEFAULT_RULE_LABELS = { core: "Close family", married_in: "Married into the family", married_out: "New branch in another lineage", close_cousin: "Close cousin", extended: "Distant cousin" };
const DEFAULT_RULE_THRESHOLDS = { close_cousin_max_up: 2, close_cousin_max_down: 2, extended_max_up: 4, extended_max_down: 4 };
const DEFAULT_RULE_STATUS_ICONS = { show_married: true, show_divorced: true, show_widowed: true, show_cohabiting: true, show_engaged: true };
const DEFAULT_RULE_BEHAVIORS = { mark_solo_child: true };
const ACTIVE_RULES = {
  colors: { ...DEFAULT_RULE_COLORS },
  labels: { ...DEFAULT_RULE_LABELS },
  thresholds: { ...DEFAULT_RULE_THRESHOLDS },
  kinship_model: "patrilineal",
  status_icons: { ...DEFAULT_RULE_STATUS_ICONS },
  behaviors: { ...DEFAULT_RULE_BEHAVIORS },
};
// Varianti "soft" (sfond i lehtë) derivohet nga vetë ngjyra e degës (përzihet me
// të bardhën), që të mbetet i pajtueshëm kur admini e ndryshon ngjyrën.
// ⚠ 4 shtator 2026 — GRI E VËRTETË, E LLOGARITUR.
// Dy herë radhazi «pa ngjyra» doli si tema, dhe të dyja herët për të njëjtën
// arsye: si «neutral» mora një token të paletës. Po te një temë e ngrohtë ÇDO
// token është i ngrohtë — `muted` i heirloom-it është #A08D79, dhe ari
// #C6A664. Mbi letër krem, me 55% tejdukshmëri, syri s'i dallon.
// Neutrali nuk mund të merret hua; duhet llogaritur. Këtu shkrimi dhe letra
// kthehen në ndriçimin e tyre (pa nuancë fare) dhe merret një ton mes tyre —
// pra një gri që ndjek temën e çelët/të errët po s'ka asnjë ngjyrë.
function toneGri(ngjyraETekstit, ngjyraELetres, nga = 0.45) {
  const t = _hexToRgb(ngjyraETekstit), l = _hexToRgb(ngjyraELetres);
  if (!t || !l) return "#8B8B8B";
  const nd = (c) => 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
  const v = Math.round(nd(t) + (nd(l) - nd(t)) * nga);
  const k = Math.max(0, Math.min(255, v));
  return `rgb(${k},${k},${k})`;
}
function lightenHex(hex, amt = 0.82) {
  try {
    const n = parseInt(String(hex).replace("#", ""), 16);
    if (Number.isNaN(n)) return hex;
    const r = (n >> 16) & 255, g = (n >> 8) & 255, bl = n & 255;
    const mix = (c) => Math.round(c + (255 - c) * amt);
    return `#${((1 << 24) + (mix(r) << 16) + (mix(g) << 8) + mix(bl)).toString(16).slice(1)}`;
  } catch { return hex; }
}
function branchColor(b) {
  const c = ACTIVE_RULES.colors || DEFAULT_RULE_COLORS;
  if (b === "married_out") return c.married_out || DEFAULT_RULE_COLORS.married_out;
  if (b === "married_in") return c.married_in || DEFAULT_RULE_COLORS.married_in;
  if (b === "close_cousin") return c.close_cousin || DEFAULT_RULE_COLORS.close_cousin;
  if (b === "extended") return c.extended || DEFAULT_RULE_COLORS.extended;
  return c.core || DEFAULT_RULE_COLORS.core;
}
function branchSoft(b) {
  return lightenHex(branchColor(b));
}
function branchLabel(b) {
  const l = ACTIVE_RULES.labels || DEFAULT_RULE_LABELS;
  if (b === "married_out") return l.married_out || DEFAULT_RULE_LABELS.married_out;
  if (b === "married_in") return l.married_in || DEFAULT_RULE_LABELS.married_in;
  if (b === "close_cousin") return l.close_cousin || DEFAULT_RULE_LABELS.close_cousin;
  if (b === "extended") return l.extended || DEFAULT_RULE_LABELS.extended;
  return l.core || DEFAULT_RULE_LABELS.core;
}

function BranchIcon({ branch, size = 16, color, gender }) {
  const s = size;
  const common = { width: s, height: s, viewBox: "0 0 40 40" };
  if (branch === "core") {
    return (
      <svg {...common}>
        <rect x="17" y="24" width="6" height="12" rx="1.5" fill={color} />
        <circle cx="20" cy="14" r="12" fill={color} opacity="0.95" />
        <circle cx="12" cy="18" r="7" fill={color} opacity="0.8" />
        <circle cx="28" cy="18" r="7" fill={color} opacity="0.8" />
      </svg>
    );
  }
  if (branch === "close_cousin") {
    return (
      <svg {...common}>
        <path d="M 6 34 C 16 26 24 18 34 6" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" />
        <ellipse cx="30" cy="10" rx="4.5" ry="7" fill={color} transform="rotate(35 30 10)" />
        <ellipse cx="18" cy="22" rx="3.5" ry="5.5" fill={color} opacity="0.85" transform="rotate(35 18 22)" />
        <ellipse cx="10" cy="30" rx="3" ry="4.5" fill={color} opacity="0.7" transform="rotate(35 10 30)" />
      </svg>
    );
  }
  if (branch === "extended") {
    return (
      <svg {...common}>
        <path d="M 8 34 C 16 26 24 18 32 8" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" />
        <ellipse cx="29" cy="11" rx="3" ry="5" fill={color} transform="rotate(35 29 11)" />
        <ellipse cx="14" cy="26" rx="2.2" ry="3.6" fill={color} opacity="0.75" transform="rotate(35 14 26)" />
      </svg>
    );
  }
  if (branch === "married_out") {
    return (
      <svg {...common}>
        <path d="M 20 6 C 30 12 32 22 20 34 C 8 22 10 12 20 6 Z" fill={color} />
        <line x1="20" y1="10" x2="20" y2="30" stroke="#fff" strokeWidth="1.2" opacity="0.6" />
      </svg>
    );
  }
  if (branch === "married_in") {
    if (gender === "M") {
      return (
        <svg {...common}>
          <circle cx="20" cy="22" r="12" fill="none" stroke={color} strokeWidth="5" />
        </svg>
      );
    }
    return (
      <svg {...common}>
        <circle cx="20" cy="24" r="11" fill="none" stroke={color} strokeWidth="5" />
        <path d="M 20 4 L 14 11 L 20 15 L 26 11 Z" fill={color} />
        <path d="M 20 4 L 14 11 L 17 11 Z" fill={color} opacity="0.7" />
        <path d="M 20 4 L 26 11 L 23 11 Z" fill="#fff" opacity="0.35" />
      </svg>
    );
  }
  return null;
}
// ── SHQIPONJA DYKRENARE ────────────────────────────────────────────────────
// 4 shtator 2026. Pronari: «a mundesh shqiponjën në background me e bë tamam
// shqiponjë». Deri sot ishte e vizatuar me dorë — dhjetë shtigje kurbash dhe
// vijash që SUGJERONIN një shqiponjë pa qenë ndonjëherë ajo.
//
// Tani NUK është vizatim. Është VEKTORIZIM MEKANIK i figurës që solli vetë
// pronari (potrace mbi foton, e normalizuar në kutinë `0 0 400 400`, e
// qendërzuar, me numra të plotë). E njëjta rrugë që e zgjidhi pëllumbin: kur
// figura ekziston, nuk e rivizatojmë — e gjurmojmë.
//
// Një shteg i vetëm, me `fill-rule="evenodd"` — vrimat (sytë, ndarjet e
// puplave) janë brenda tij, ndaj rregulli i mbushjes s'është zbukurim: pa të
// shqiponja bëhet një njollë e ngurtë.
const SHTEG_SHQIPONJA_SKENDERBEU =
  "M 131,1 C 128,3 128,4 127,7 C 126,10 126,10 121,10 C 111,10 103,23 110,29 C 112,31 112,31 109,32 C 106,32 102,32 101,30 C 97,26 95,29 98,33 C 103,38 109,39 118,35 C 122,32 124,32 127,32 C 131,32 130,34 126,34 C 119,35 120,38 128,39 C 131,40 131,40 137,38 C 145,36 148,37 151,44 C 151,45 151,46 152,46 C 154,48 155,58 154,65 C 153,68 154,68 157,67 C 161,66 162,66 161,72 C 161,75 161,76 159,78 C 156,81 156,82 159,83 C 162,85 162,86 159,89 C 159,90 158,91 158,91 C 158,91 158,92 157,93 C 157,94 157,96 156,96 C 156,98 155,99 150,98 C 144,96 135,97 131,100 C 118,106 109,98 112,83 C 113,81 113,78 114,76 C 114,74 115,72 115,71 C 121,60 114,47 100,45 C 99,45 97,44 96,44 C 93,43 83,42 80,43 C 64,47 54,54 48,65 C 47,68 45,70 45,70 C 45,70 44,71 44,73 C 44,74 43,76 43,76 C 40,80 39,90 39,111 C 39,133 39,136 37,143 C 36,146 36,148 36,148 C 36,149 35,150 35,151 C 34,151 34,153 33,153 C 33,154 32,156 31,157 C 28,161 28,162 32,162 C 36,162 41,159 44,152 C 45,149 46,148 47,150 C 47,151 46,161 45,163 C 45,164 44,168 44,171 C 43,174 43,178 42,179 C 42,181 41,185 40,190 C 39,194 38,199 37,201 C 37,203 36,205 36,206 C 36,207 35,209 35,210 C 34,211 34,213 33,213 C 33,214 33,216 32,216 C 32,218 31,219 27,232 C 25,236 24,240 24,240 C 23,241 23,242 23,243 C 23,245 22,246 21,246 C 20,247 20,248 20,251 C 20,259 23,257 35,242 C 38,238 44,228 44,225 C 45,224 45,223 46,222 C 46,221 47,220 47,219 C 47,218 48,216 49,215 C 49,214 50,212 50,212 C 50,211 50,209 51,207 C 51,205 52,202 52,200 C 53,197 54,194 54,193 C 54,191 55,189 55,188 C 55,187 56,184 56,182 C 57,180 58,177 58,175 C 58,173 58,170 59,169 C 60,168 61,161 61,159 C 61,159 61,159 62,160 C 62,160 62,182 61,186 C 60,187 60,190 60,192 C 60,194 59,198 59,200 C 58,201 57,204 57,206 C 57,208 56,210 56,211 C 53,216 56,219 59,215 C 62,212 68,199 68,195 C 69,193 70,192 71,192 C 71,192 71,198 70,200 C 69,201 68,203 68,205 C 68,206 67,209 66,211 C 66,213 64,217 64,221 C 60,231 57,241 56,242 C 55,243 53,249 53,250 C 53,255 62,249 66,242 C 66,241 67,239 69,238 C 70,236 71,234 72,233 C 74,229 75,227 76,226 C 76,225 77,223 82,214 C 85,209 90,195 90,192 C 90,192 90,190 91,189 C 92,188 92,185 92,184 C 93,183 93,181 94,179 C 94,178 95,176 95,174 C 95,169 96,168 97,169 C 98,170 97,187 95,194 C 95,196 95,199 94,201 C 94,203 93,207 93,208 C 92,210 92,212 92,212 C 92,213 91,214 91,215 C 90,216 89,217 89,217 C 89,219 85,228 85,228 C 84,229 84,232 85,232 C 87,233 97,224 105,213 C 109,208 111,203 112,194 C 112,191 112,188 113,186 C 113,184 113,179 114,176 C 114,165 115,163 116,167 C 118,172 119,184 117,191 C 117,192 117,195 116,198 C 116,201 115,204 114,205 C 110,214 117,215 122,206 C 122,205 124,203 125,201 C 126,199 127,198 127,197 C 127,196 128,195 128,194 C 129,194 129,192 130,190 C 130,188 130,186 131,185 C 131,184 132,181 132,176 C 133,165 134,164 134,174 C 135,181 135,180 132,193 C 131,200 131,201 132,202 C 134,203 139,197 141,193 C 141,192 141,191 142,191 C 142,190 143,189 143,187 C 144,182 145,181 145,186 C 145,194 153,190 156,181 C 158,175 159,175 161,182 C 161,183 162,185 163,187 C 166,190 166,191 164,191 C 157,193 149,201 148,207 C 148,208 147,209 147,210 C 146,211 146,213 145,215 C 143,232 142,233 136,238 C 135,239 135,241 137,242 C 140,244 138,245 132,246 C 129,247 127,248 127,249 C 127,251 134,258 139,259 C 143,260 143,260 139,264 C 135,269 134,270 128,274 C 127,274 125,275 125,276 C 123,276 122,277 117,277 C 112,277 111,277 110,278 C 107,279 104,279 102,278 C 101,277 99,277 98,277 C 96,276 93,273 92,271 C 92,268 84,264 80,265 C 78,266 76,267 75,268 C 75,268 74,268 73,269 C 69,270 66,274 67,277 C 68,280 70,280 72,278 C 75,276 79,279 77,281 C 76,281 75,283 73,284 C 71,286 69,287 69,287 C 66,288 63,294 64,296 C 65,297 66,297 68,296 C 70,296 71,295 73,296 C 77,296 77,296 76,299 C 76,300 75,302 75,303 C 75,305 74,306 74,307 C 73,309 73,312 74,315 C 77,318 79,318 81,315 C 82,312 83,311 85,311 C 87,311 91,308 92,306 C 95,299 101,295 108,294 C 111,294 115,293 116,293 C 120,292 120,292 122,293 C 124,293 126,293 128,293 C 130,293 132,293 133,293 C 133,293 135,295 137,297 C 141,301 142,302 145,302 C 147,302 148,303 149,306 C 150,307 152,308 153,306 C 161,298 154,285 142,285 C 136,285 135,284 140,280 C 144,278 153,271 157,266 C 164,258 165,257 170,260 C 171,260 173,261 175,261 C 182,262 182,262 180,269 C 180,272 179,276 178,278 C 178,279 177,282 177,283 C 177,284 176,285 176,285 C 175,286 175,287 174,288 C 174,289 173,290 172,291 C 170,294 171,296 176,296 C 181,295 180,297 173,302 C 171,303 168,304 168,305 C 163,307 161,308 161,308 C 161,308 160,309 159,310 C 158,310 157,311 157,311 C 156,311 155,311 154,312 C 148,315 142,316 139,314 C 136,312 135,312 133,313 C 130,317 135,326 140,328 C 143,329 144,330 143,333 C 142,341 143,351 147,357 C 152,364 154,363 154,353 C 154,350 155,347 155,346 C 155,345 156,344 156,342 C 158,332 164,324 175,317 C 177,315 180,313 182,312 C 188,306 193,305 192,310 C 192,311 191,314 191,316 C 189,323 187,327 181,334 C 175,340 176,342 184,344 C 189,345 188,345 183,351 C 177,356 175,360 174,365 C 174,367 173,368 173,369 C 172,372 172,379 172,380 C 174,381 178,379 178,377 C 178,377 183,371 184,370 C 185,370 186,371 186,372 C 186,375 190,388 192,391 C 197,399 205,402 206,398 C 206,398 207,397 208,396 C 211,393 212,391 213,389 C 215,385 218,376 218,374 C 218,368 223,371 227,379 C 231,386 233,374 229,363 C 227,358 226,356 221,351 C 216,345 215,345 219,344 C 227,342 228,340 224,335 C 220,330 215,323 215,320 C 215,320 214,318 213,317 C 211,311 212,305 216,308 C 217,308 218,309 218,309 C 218,309 220,310 222,312 C 224,313 228,316 230,317 C 234,320 242,327 243,330 C 244,331 244,332 245,332 C 247,334 250,348 250,355 C 250,363 252,363 258,354 C 261,350 263,340 261,334 C 260,331 261,329 264,328 C 270,326 274,317 271,313 C 270,311 269,312 265,314 C 261,316 250,314 245,310 C 244,309 243,309 243,308 C 242,308 240,307 239,306 C 237,305 235,304 234,303 C 226,299 223,295 228,296 C 233,296 234,295 231,290 C 229,287 227,282 225,276 C 225,275 224,272 224,271 C 222,264 223,262 227,261 C 230,261 232,261 234,260 C 238,258 239,258 246,265 C 253,273 261,279 264,281 C 269,284 268,285 261,285 C 251,285 244,296 249,304 C 252,308 254,309 255,305 C 256,303 257,302 259,302 C 262,302 263,301 267,297 C 271,293 271,293 275,293 C 278,294 279,294 281,293 C 284,292 284,292 287,293 C 289,294 293,294 295,294 C 303,295 306,297 311,305 C 312,307 313,309 314,309 C 314,309 315,309 315,310 C 316,311 318,311 319,312 C 322,312 323,313 323,315 C 324,319 326,319 329,315 C 332,312 332,310 330,306 C 330,305 329,304 329,303 C 329,301 329,299 328,298 C 326,296 334,295 336,297 C 341,300 342,293 338,289 C 337,288 336,287 336,287 C 336,287 334,286 333,284 C 331,283 329,282 329,281 C 327,280 328,277 331,277 C 332,277 333,278 333,278 C 336,280 336,280 338,277 C 340,274 337,270 332,268 C 331,268 330,268 330,267 C 324,262 315,265 311,272 C 309,275 308,276 306,276 C 305,277 303,277 302,278 C 299,279 297,279 295,278 C 293,277 292,277 287,277 C 282,277 281,276 279,275 C 277,274 275,273 272,271 C 269,269 264,264 263,262 C 262,260 263,259 265,259 C 275,256 280,247 272,246 C 266,245 264,244 267,242 C 270,241 269,239 266,236 C 262,233 261,229 260,222 C 260,219 259,216 259,216 C 258,215 258,213 258,211 C 257,210 257,208 256,207 C 255,206 255,205 255,204 C 255,204 255,203 254,202 C 254,201 253,200 252,199 C 251,198 248,195 246,194 C 244,193 243,193 243,192 C 242,192 241,192 240,191 C 239,191 239,189 240,187 C 242,185 244,181 244,177 C 245,171 245,171 247,177 C 249,188 257,195 260,189 C 260,187 262,187 262,191 C 263,193 268,201 270,202 C 274,203 274,200 272,192 C 271,189 270,179 270,176 C 271,174 271,174 271,176 C 273,184 274,185 274,187 C 274,188 274,190 274,191 C 275,192 275,193 276,194 C 276,199 286,212 289,213 C 292,213 293,210 291,207 C 290,206 290,205 290,205 C 290,205 289,203 289,201 C 287,194 286,179 287,171 C 288,165 288,165 289,170 C 289,172 290,174 290,175 C 291,176 291,180 291,183 C 292,187 292,191 292,193 C 293,194 293,197 293,199 C 294,205 297,212 301,216 C 302,216 303,218 304,219 C 317,234 325,237 319,224 C 317,221 316,218 316,217 C 316,216 315,215 315,215 C 314,213 312,209 310,197 C 308,187 307,172 308,171 C 309,170 310,172 310,176 C 311,178 311,180 312,181 C 312,182 312,184 313,185 C 313,186 313,188 314,189 C 314,189 315,191 315,192 C 316,194 317,197 318,200 C 319,203 320,206 321,207 C 321,208 322,210 323,211 C 323,213 324,214 324,214 C 324,214 324,215 324,215 C 325,216 326,217 326,218 C 329,225 333,231 341,242 C 343,246 348,251 350,252 C 353,253 354,250 352,247 C 351,246 351,245 351,244 C 351,244 350,243 350,242 C 349,241 348,240 348,238 C 348,237 347,235 346,234 C 346,232 343,222 340,216 C 340,214 339,212 339,211 C 338,209 337,207 337,205 C 337,204 336,202 336,201 C 335,200 334,197 335,197 C 336,197 337,198 337,199 C 337,200 338,201 338,202 C 339,203 340,205 340,206 C 341,208 342,210 342,211 C 343,212 344,214 344,214 C 345,216 347,217 348,216 C 348,216 348,212 347,210 C 346,208 346,206 345,199 C 344,195 343,190 343,188 C 341,179 340,163 341,163 C 342,163 342,164 343,169 C 343,171 343,173 344,174 C 344,175 345,178 345,180 C 345,182 346,185 346,186 C 347,187 347,190 347,191 C 348,192 348,195 349,196 C 349,198 350,200 350,202 C 351,203 352,207 352,210 C 355,219 357,225 359,228 C 359,228 360,230 360,231 C 361,232 361,233 361,234 C 362,234 363,236 364,238 C 368,246 376,257 379,257 C 380,257 380,244 379,242 C 378,241 376,236 375,232 C 374,230 374,229 373,228 C 373,227 372,226 372,225 C 372,223 371,222 371,221 C 371,221 370,219 370,218 C 369,217 369,214 368,212 C 367,209 366,207 366,205 C 366,204 365,202 365,200 C 364,199 364,197 364,197 C 364,196 364,194 363,192 C 362,191 362,188 361,186 C 361,185 361,183 360,181 C 360,180 359,178 359,175 C 358,173 358,170 358,169 C 357,167 357,164 356,161 C 355,152 356,152 359,156 C 363,161 366,163 370,163 C 374,163 374,162 372,159 C 369,156 366,150 364,143 C 364,142 364,140 363,138 C 361,132 360,120 361,105 C 361,93 361,93 360,87 C 359,83 359,80 359,79 C 359,78 358,77 358,76 C 358,75 357,72 356,71 C 355,66 353,62 349,57 C 341,49 337,46 323,43 C 320,43 312,43 310,44 C 310,44 307,45 305,45 C 292,47 284,56 288,67 C 288,68 289,70 289,71 C 289,72 290,74 290,75 C 291,76 291,78 292,80 C 292,82 292,84 293,86 C 295,98 285,106 274,100 C 269,97 261,96 254,98 C 248,99 248,99 247,95 C 247,93 244,89 243,87 C 242,86 243,85 245,84 C 248,82 248,81 246,78 C 242,74 240,65 243,66 C 250,69 251,69 250,62 C 248,47 255,35 265,38 C 272,40 272,40 276,39 C 284,38 285,35 278,34 C 274,34 273,33 274,32 C 276,31 280,32 285,34 C 295,39 300,39 306,33 C 310,29 307,27 303,30 C 301,32 296,33 293,31 C 292,31 292,31 293,30 C 301,26 294,11 283,10 C 277,10 276,9 276,7 C 276,2 268,-2 264,1 C 258,4 232,6 229,4 C 224,1 222,4 224,10 C 225,11 226,12 227,13 C 230,15 229,16 226,19 C 223,21 221,22 219,19 C 216,17 213,18 214,22 C 214,24 214,25 217,28 C 217,29 218,31 218,31 C 217,32 216,34 215,34 C 213,34 212,35 212,37 C 212,39 211,40 209,39 C 206,37 205,38 204,40 C 203,42 202,42 200,40 C 198,37 197,37 195,38 C 195,39 194,40 194,40 C 193,40 192,39 192,37 C 192,36 191,35 190,34 C 185,32 185,31 187,28 C 193,21 191,15 185,20 C 182,22 180,21 177,18 C 175,16 175,15 177,13 C 183,9 182,0 176,4 C 172,6 155,5 143,2 C 136,0 134,0 131,1 M 143,15 C 143,16 140,18 139,18 C 138,17 138,16 138,15 C 139,15 143,15 143,15 M 266,16 C 266,17 263,18 262,17 C 260,15 261,15 264,15 C 266,15 266,15 266,16 M 124,25 C 124,26 119,29 116,29 C 115,29 115,29 115,28 C 115,27 122,24 124,25 M 284,26 C 289,27 290,30 286,29 C 285,29 280,26 280,26 C 280,25 282,25 284,26 M 204,48 C 205,49 205,50 206,51 C 208,54 203,62 201,59 C 197,55 197,51 201,47 C 203,45 202,45 204,48 M 259,178 C 260,185 260,185 259,185 C 259,185 257,174 258,172 C 258,169 259,171 259,178 M 73,187 C 73,188 72,189 72,189 C 71,190 71,188 72,186 C 72,184 73,185 73,187";

// Shqiponja e FLAMURIT — e njëjta rrugë: gjurmim mekanik i figurës zyrtare
// që solli pronari (me degët e ullirit). Kjo është parazgjedhja.
const SHTEG_SHQIPONJA_FLAMURI =
  "M 124,0 C 124,0 124,1 124,1 C 122,1 118,3 118,5 C 117,6 117,6 116,6 C 113,5 108,5 107,5 C 106,6 105,6 105,6 C 101,7 99,10 99,14 C 99,16 99,17 99,16 C 101,12 108,11 113,16 C 115,17 118,20 118,20 C 115,23 97,19 92,15 C 86,8 83,6 82,10 C 80,15 87,21 95,23 C 105,24 109,25 115,24 C 118,24 121,24 121,24 C 121,25 117,27 115,28 C 113,28 109,28 107,27 C 105,26 100,25 99,25 C 98,26 102,30 105,31 C 106,31 107,31 107,31 C 108,32 111,32 113,33 C 114,33 115,33 115,33 C 116,33 118,34 120,34 C 122,35 125,35 126,36 C 132,37 136,38 136,39 C 136,39 137,39 138,39 C 140,40 145,42 146,43 C 150,46 157,52 157,53 C 157,53 157,54 157,54 C 158,54 160,56 161,58 C 165,65 166,72 164,80 C 162,89 151,93 143,88 C 139,85 136,80 136,73 C 136,70 136,70 138,64 C 140,59 139,57 136,56 C 135,55 133,54 132,53 C 131,53 129,52 129,52 C 128,52 125,50 122,49 C 119,47 116,46 115,45 C 114,45 113,44 111,43 C 109,42 107,41 106,41 C 105,40 103,40 103,40 C 102,39 101,39 100,39 C 100,38 99,38 98,38 C 98,37 97,37 96,37 C 95,36 94,36 94,36 C 92,35 89,36 87,39 C 86,41 86,41 85,41 C 83,41 74,38 71,37 C 70,37 69,36 68,36 C 59,32 48,24 43,17 C 42,16 41,15 41,15 C 41,14 39,12 37,9 C 33,2 27,3 24,11 C 22,14 23,19 24,23 C 25,23 25,24 25,24 C 25,26 32,35 36,38 C 40,41 51,47 56,49 C 62,51 67,53 70,53 C 75,54 77,54 79,54 C 88,54 88,65 79,66 C 75,67 62,66 59,65 C 57,65 54,64 53,64 C 53,64 52,63 51,63 C 49,62 47,62 45,61 C 45,60 44,60 43,59 C 41,58 40,57 37,55 C 32,50 27,49 25,54 C 24,55 24,58 24,59 C 26,68 31,74 41,78 C 42,78 43,79 46,79 C 51,80 53,81 58,81 C 61,81 70,81 73,80 C 75,80 76,80 77,80 C 77,80 78,80 78,79 C 80,78 84,78 86,80 C 87,81 85,88 82,88 C 82,88 81,88 81,89 C 76,92 72,94 67,95 C 62,97 52,97 49,96 C 49,96 48,96 48,95 C 46,95 45,95 43,94 C 42,93 39,91 38,90 C 37,90 35,89 34,88 C 27,82 21,87 25,96 C 29,108 41,114 58,111 C 60,110 61,110 63,110 C 63,109 64,109 64,109 C 65,109 65,109 66,109 C 66,109 67,108 68,108 C 68,108 69,108 70,107 C 71,107 72,107 73,106 C 74,106 77,104 79,103 C 88,99 91,106 83,112 C 81,114 78,116 77,116 C 77,116 76,117 74,118 C 71,120 67,121 65,122 C 65,123 64,123 63,123 C 54,127 46,127 37,122 C 26,117 19,128 28,136 C 31,139 39,140 46,139 C 51,138 52,138 55,138 C 63,136 71,133 78,128 C 83,125 85,125 85,128 C 85,131 84,133 80,136 C 79,137 77,138 77,139 C 75,141 66,147 63,149 C 56,153 52,154 45,154 C 43,154 40,154 38,154 C 28,153 26,154 25,158 C 23,167 31,174 41,173 C 44,172 51,171 53,170 C 58,168 61,166 62,165 C 65,164 70,161 70,161 C 70,160 70,160 70,160 C 71,160 73,158 79,153 C 84,150 86,150 88,153 C 88,153 88,156 88,157 C 87,158 87,159 87,160 C 86,163 76,174 69,179 C 57,188 44,191 32,188 C 24,185 21,190 26,199 C 29,206 44,206 57,200 C 62,197 63,197 68,193 C 73,190 78,185 82,180 C 88,172 92,173 90,181 C 89,184 89,184 85,189 C 77,202 69,210 60,215 C 56,218 48,221 46,221 C 45,221 45,221 45,221 C 45,221 42,221 37,221 C 31,221 29,221 28,221 C 28,222 28,222 28,222 C 25,222 24,227 26,230 C 30,238 45,242 53,239 C 54,238 55,238 56,238 C 60,236 67,230 75,222 C 75,221 78,218 80,216 C 87,209 89,206 91,205 C 92,203 95,200 100,195 C 108,186 112,188 110,200 C 109,208 104,215 94,220 C 89,222 88,223 87,224 C 85,228 86,231 92,233 C 101,237 116,222 121,205 C 121,204 121,203 122,200 C 123,198 123,196 123,196 C 123,194 129,192 132,193 C 135,196 135,198 132,206 C 130,211 128,215 123,218 C 119,222 118,223 120,225 C 122,227 127,227 130,225 C 137,220 142,214 147,202 C 149,198 150,197 152,198 C 155,199 156,203 155,206 C 155,206 154,208 154,209 C 153,211 152,212 152,213 C 152,214 151,215 151,215 C 151,215 148,222 146,224 C 140,233 130,240 118,243 C 118,243 117,244 116,244 C 116,244 113,245 109,245 C 104,247 104,248 110,255 C 116,260 115,260 114,260 C 112,261 97,265 96,265 C 95,265 95,265 94,265 C 94,265 93,266 92,266 C 91,266 91,266 90,266 C 90,267 90,267 89,267 C 89,267 86,267 83,268 C 79,269 76,270 75,270 C 74,271 74,271 71,269 C 70,268 68,267 65,265 C 63,264 60,262 59,261 C 58,260 57,259 57,259 C 56,259 54,258 54,257 C 53,257 53,256 53,255 C 51,251 47,248 45,249 C 45,249 44,250 44,250 C 43,250 42,250 42,250 C 40,251 40,251 37,250 C 35,249 30,249 28,250 C 28,250 28,251 28,251 C 27,251 26,252 26,252 C 26,252 26,252 27,252 C 28,252 29,252 30,253 C 30,253 31,253 32,253 C 33,253 36,256 37,258 C 40,264 43,265 52,263 C 53,263 63,272 64,273 C 64,273 62,273 59,273 C 57,273 52,273 48,273 C 41,273 42,273 39,270 C 36,267 31,266 29,266 C 28,267 28,267 27,267 C 26,267 24,269 22,270 C 21,271 21,271 20,272 C 16,272 15,273 13,276 C 12,278 12,278 13,277 C 17,276 19,275 22,277 C 28,281 35,281 39,278 C 40,277 41,277 43,277 C 44,277 48,278 53,278 C 62,278 64,279 64,279 C 64,279 61,281 59,282 C 58,282 57,283 56,284 C 54,285 54,285 52,285 C 43,282 35,286 33,296 C 33,296 32,298 32,300 C 31,302 30,309 31,309 C 31,309 32,308 32,308 C 33,304 37,299 39,300 C 47,302 52,299 54,293 C 54,291 55,290 59,288 C 60,288 61,287 62,287 C 63,287 63,286 64,286 C 65,286 70,283 71,282 C 72,282 72,282 73,282 C 73,282 77,283 78,283 C 78,284 79,284 80,284 C 80,284 81,284 81,285 C 82,285 84,286 86,286 C 88,287 88,287 89,289 C 91,294 95,296 101,295 C 109,294 112,296 112,301 C 112,302 113,301 114,299 C 115,295 113,292 109,289 C 109,289 109,288 108,287 C 106,283 101,281 97,283 C 95,284 94,284 87,280 C 86,279 84,278 83,278 C 82,278 82,278 83,277 C 85,277 86,276 87,276 C 88,276 100,273 103,272 C 103,272 104,272 104,272 C 107,271 116,269 118,268 C 119,268 119,268 120,268 C 121,268 121,268 122,267 C 122,267 123,267 125,269 C 126,270 127,271 128,272 C 129,272 130,273 131,274 C 134,277 136,278 138,277 C 138,277 141,274 141,273 C 141,273 141,272 141,272 C 142,271 142,270 142,270 C 142,270 143,269 144,268 C 144,267 146,265 147,263 C 150,256 155,248 156,247 C 157,246 158,246 158,245 C 169,232 175,229 180,233 C 186,239 182,257 173,268 C 172,268 171,270 170,271 C 167,275 164,277 161,280 C 155,285 148,288 147,288 C 147,288 146,289 146,289 C 146,289 139,289 137,289 C 136,288 132,288 131,289 C 131,289 130,289 129,289 C 125,291 124,294 128,298 C 132,302 141,304 146,303 C 150,302 152,301 159,295 C 162,291 164,291 166,293 C 169,296 167,300 157,308 C 153,312 146,315 141,315 C 134,315 131,319 134,322 C 136,324 137,325 141,326 C 149,329 158,324 167,315 C 172,310 175,310 175,315 C 174,324 166,334 159,336 C 158,336 157,336 157,336 C 156,336 155,336 154,336 C 146,336 146,343 155,347 C 163,352 171,346 178,332 C 182,326 182,326 184,326 C 186,326 188,330 188,335 C 187,344 184,349 175,357 L 173,359 L 174,361 C 175,363 176,365 177,367 C 179,369 180,371 181,372 C 181,373 183,375 184,376 C 185,378 185,379 186,379 C 186,380 188,383 191,388 C 197,396 199,399 199,400 C 199,400 199,400 200,400 C 201,400 201,400 201,400 C 201,399 204,394 207,390 C 209,388 210,385 211,384 C 212,382 215,378 217,375 C 225,363 227,360 227,359 C 227,359 226,358 225,357 C 217,350 213,343 212,334 C 211,327 217,322 220,329 C 225,342 235,350 243,348 C 253,346 255,336 246,336 C 245,336 243,336 243,336 C 243,336 243,336 242,336 C 240,336 235,333 233,331 C 231,329 227,322 227,321 C 227,321 227,320 227,320 C 226,319 225,314 226,313 C 227,310 229,311 233,315 C 239,322 243,324 250,326 C 257,328 267,324 267,320 C 267,317 265,316 258,315 C 245,314 228,297 234,293 C 237,291 237,291 243,296 C 244,297 248,300 249,301 C 251,302 251,302 253,302 C 257,303 263,303 266,301 C 267,301 268,301 268,300 C 271,299 274,296 274,293 C 274,290 269,288 261,289 C 258,289 254,289 254,289 C 254,288 253,288 253,288 C 252,288 245,284 239,280 C 234,276 229,270 224,264 C 213,247 215,227 228,232 C 231,234 238,240 242,246 C 243,246 244,247 244,248 C 245,249 247,252 248,254 C 249,255 250,257 251,258 C 251,260 253,262 253,263 C 256,268 261,276 262,277 C 264,278 266,277 269,275 C 270,274 272,272 273,271 C 273,271 275,269 276,269 C 277,267 278,267 278,267 C 279,268 279,268 280,268 C 281,268 282,269 283,269 C 284,269 287,270 289,270 C 289,270 291,271 292,271 C 294,272 296,272 298,273 C 299,273 301,273 302,273 C 303,274 304,274 304,274 C 305,274 305,274 305,274 C 306,274 307,275 308,275 C 311,276 311,276 314,276 C 319,278 318,278 317,278 C 315,279 315,279 311,281 C 306,283 305,284 303,283 C 302,282 298,282 297,282 C 297,283 297,283 296,283 C 295,283 292,286 292,288 C 291,289 291,289 291,289 C 290,289 288,292 287,293 C 286,295 286,298 287,300 C 287,302 288,302 288,301 C 288,300 288,299 289,297 C 289,297 291,295 292,295 C 292,295 293,295 293,295 C 293,295 297,295 300,295 C 305,296 309,293 312,288 C 312,287 312,287 317,285 C 319,284 322,283 322,283 C 323,283 327,282 327,282 C 327,282 328,282 329,283 C 330,284 333,285 335,286 C 336,286 337,287 338,287 C 339,287 340,288 341,288 C 345,290 347,291 347,294 C 347,299 353,301 361,300 C 362,300 363,300 363,300 C 364,301 364,301 364,301 C 365,301 368,306 368,308 C 368,309 369,309 369,308 C 369,308 369,302 369,301 C 369,301 368,299 368,298 C 367,297 367,295 367,294 C 366,293 365,291 364,290 C 364,289 364,289 364,289 C 364,288 360,285 358,285 C 355,284 351,284 348,285 C 346,285 336,280 336,279 C 336,279 343,278 353,278 C 356,277 359,277 359,277 C 359,277 360,277 360,278 C 366,282 372,281 378,277 C 380,276 383,275 384,276 C 385,276 388,278 388,277 C 388,277 386,274 385,273 C 384,273 383,272 380,272 C 379,271 378,271 377,270 C 372,265 366,265 360,271 C 358,273 358,273 353,273 C 350,273 345,273 342,273 C 336,273 335,273 336,273 C 337,272 339,271 340,269 C 346,263 347,263 348,263 C 356,265 360,263 363,258 C 365,255 369,252 373,252 C 374,252 374,252 373,251 C 369,249 363,248 361,250 C 360,251 359,251 358,250 C 354,249 354,249 352,249 C 350,250 348,252 347,256 C 347,257 346,258 345,258 C 345,258 345,258 345,258 C 345,259 339,262 337,264 C 336,264 333,266 331,268 C 328,270 326,271 326,271 C 325,271 323,270 322,270 C 322,269 320,269 319,269 C 318,269 317,268 317,268 C 316,268 315,268 315,268 C 314,268 313,267 313,267 C 312,267 311,267 311,267 C 311,267 310,267 310,266 C 309,266 308,266 307,266 C 307,266 304,265 303,265 C 301,264 299,264 299,264 C 297,263 297,263 294,262 C 292,262 291,262 289,261 C 287,261 285,260 285,260 C 285,260 287,258 290,255 C 295,250 295,250 295,249 C 295,247 293,245 291,245 C 290,245 284,244 283,243 C 282,243 281,243 280,243 C 271,240 261,233 256,228 C 256,227 255,226 255,226 C 255,225 252,222 251,220 C 251,219 249,215 249,215 C 249,215 248,214 248,213 C 248,212 247,211 246,209 C 246,208 245,206 245,206 C 245,205 245,201 245,200 C 247,196 251,197 253,202 C 259,217 268,227 275,227 C 281,227 282,223 278,220 C 272,215 270,212 269,208 C 268,208 268,207 268,206 C 267,205 266,202 266,200 C 265,196 268,193 271,193 C 275,193 276,195 279,204 C 279,204 279,205 280,206 C 280,207 280,208 280,208 C 280,218 299,236 308,233 C 314,232 316,227 312,224 C 311,222 311,222 307,220 C 295,214 289,207 289,196 C 289,191 292,187 294,189 C 294,190 294,190 294,190 C 295,190 297,191 298,192 C 298,192 300,194 302,196 C 312,207 325,222 332,228 C 340,237 345,239 354,240 C 363,240 371,236 374,230 C 375,227 375,222 373,222 C 373,222 372,222 372,222 C 371,221 371,221 364,221 C 356,221 356,221 351,220 C 338,217 327,208 315,190 C 307,178 308,168 316,178 C 321,185 329,191 337,197 C 342,200 349,203 354,204 C 355,204 356,204 357,204 C 361,205 368,204 371,203 C 376,200 378,190 374,187 C 373,187 368,187 368,188 C 367,188 366,188 363,189 C 361,189 354,189 353,189 C 352,189 351,189 350,188 C 346,187 341,186 337,184 C 334,182 335,182 332,180 C 324,174 314,163 313,159 C 313,159 312,158 312,157 C 312,157 312,154 312,153 C 313,150 317,150 320,153 C 323,155 330,161 332,162 C 332,162 334,163 336,164 C 339,166 342,168 345,169 C 345,169 346,169 346,169 C 347,170 351,171 353,172 C 354,172 355,172 356,172 C 358,173 365,173 366,172 C 376,169 379,159 372,154 C 371,154 368,153 362,154 C 354,155 347,154 342,151 C 339,150 338,150 337,149 C 336,148 334,147 333,146 C 332,146 331,145 330,144 C 329,143 324,139 323,139 C 323,138 322,138 321,137 C 318,135 315,132 315,131 C 314,126 316,125 319,127 C 326,131 334,135 343,137 C 361,143 376,139 376,128 C 376,121 370,119 361,123 C 354,127 345,127 337,123 C 337,123 336,123 336,123 C 333,122 330,120 329,119 C 328,119 327,118 327,118 C 327,118 326,118 325,117 C 324,116 323,116 323,116 C 321,116 314,109 313,108 C 311,103 315,100 321,103 C 323,104 326,106 326,106 C 327,106 327,106 328,106 C 330,108 336,109 340,110 C 341,110 342,111 343,111 C 350,112 359,111 364,109 C 370,106 374,101 375,96 C 376,95 376,94 376,94 C 378,86 372,83 365,88 C 362,90 361,91 361,91 C 361,91 360,92 359,92 C 359,93 357,93 357,94 C 356,94 355,94 355,95 C 354,95 353,95 353,95 C 353,95 352,95 352,95 C 348,97 335,97 330,94 C 330,94 330,94 329,94 C 329,94 322,91 319,89 C 319,88 318,88 318,88 C 316,88 314,84 314,82 C 314,79 316,78 321,79 C 332,81 341,82 350,80 C 353,80 359,78 359,78 C 359,78 359,78 360,78 C 362,78 370,72 372,68 C 375,65 376,59 376,57 C 374,50 369,49 362,55 C 359,58 353,61 349,63 C 348,63 347,64 347,64 C 345,64 344,65 341,65 C 335,66 321,67 319,66 C 312,62 313,55 322,54 C 326,54 328,54 333,53 C 336,52 344,49 344,49 C 344,49 345,49 345,49 C 346,49 357,43 358,42 C 358,42 359,42 359,42 C 359,42 359,41 359,41 C 359,41 361,40 362,39 C 375,29 381,17 375,8 C 371,3 366,3 363,8 C 355,23 337,36 320,40 C 315,41 314,41 313,40 C 311,36 308,35 304,37 C 302,38 300,39 298,39 C 297,40 295,40 294,41 C 293,41 292,42 292,42 C 292,42 291,42 289,43 C 288,44 286,45 285,45 C 263,55 260,57 261,60 C 261,62 262,63 262,64 C 264,70 264,70 264,73 C 264,84 256,92 246,90 C 246,90 245,90 244,90 C 237,88 233,76 236,65 C 238,57 249,45 258,41 C 259,41 260,40 260,40 C 262,39 268,37 268,37 C 268,37 269,37 270,37 C 272,36 273,36 277,35 C 279,35 282,34 283,34 C 284,33 285,33 286,33 C 286,33 287,33 288,33 C 290,32 292,32 293,31 C 298,29 299,28 300,26 C 301,25 301,25 299,25 C 298,26 294,27 293,27 C 293,27 292,27 292,28 C 287,29 285,28 280,25 C 278,24 279,24 287,24 C 295,24 296,24 301,23 C 302,23 303,23 304,23 C 305,23 305,23 306,22 C 306,22 306,22 306,22 C 312,22 319,15 318,10 C 317,6 312,8 307,15 C 306,17 301,19 297,20 C 296,20 294,20 294,20 C 291,21 282,21 282,20 C 282,20 286,16 288,15 C 293,12 297,12 300,16 C 301,17 301,17 301,14 C 301,10 298,6 294,6 C 294,6 293,6 293,5 C 293,5 288,5 285,6 C 283,6 282,6 282,5 C 282,4 278,1 276,1 C 276,1 276,0 276,0 C 275,0 272,0 267,0 C 258,0 257,0 254,2 C 253,2 253,2 252,2 C 252,2 251,3 251,3 C 249,4 249,4 247,4 C 243,6 240,6 237,7 C 235,7 234,7 233,8 C 232,8 232,8 231,8 C 231,8 230,8 229,8 C 229,8 227,8 225,9 C 223,9 222,9 223,9 C 223,9 224,10 224,10 C 227,12 229,14 232,16 C 235,18 235,18 231,20 C 227,22 221,26 218,30 C 216,32 216,32 222,32 C 225,32 228,32 228,32 C 229,33 229,33 228,33 C 228,33 222,35 220,36 C 220,36 219,36 219,36 C 214,38 207,43 210,44 C 215,44 216,45 213,47 C 213,48 211,49 210,51 C 209,52 206,55 205,57 C 203,58 201,60 201,61 C 200,62 201,62 193,55 C 191,52 189,50 188,49 C 185,46 185,46 185,45 C 185,45 189,43 191,43 C 193,43 188,39 183,37 C 180,36 175,34 172,33 C 169,33 172,32 178,32 C 184,32 184,32 181,30 C 177,25 173,22 167,19 C 165,18 165,18 168,16 C 169,15 172,13 173,12 C 175,11 176,10 176,10 C 177,9 177,9 177,9 C 176,9 175,9 174,9 C 174,8 172,8 171,8 C 164,7 154,5 151,4 C 149,3 146,2 145,1 C 144,1 142,1 142,0 C 140,0 125,0 124,0 M 137,11 C 138,11 140,11 140,11 C 140,11 141,11 141,12 C 146,13 143,18 137,18 C 129,19 123,14 129,12 C 129,11 130,11 130,11 C 131,11 133,10 134,10 C 135,10 136,10 137,11 M 268,11 C 271,11 273,13 273,14 C 272,20 256,20 256,14 C 256,12 261,10 265,11 C 266,11 267,11 268,11";

// ÇELËSI I SHQIPONJËS — «skenderbeu» (e gjurmuar, parazgjedhje) ose
// «artistike» (vizatimi i vjetër, i ruajtur i plotë më poshtë). Pronari:
// «këtë që kemi e kam dyfishuar, nuk e humbasim». Pra e vjetra nuk fshihet:
// rri krah të resë dhe kthehet duke ndërruar VETËM këtë rresht.
//   "flamuri"    = shqiponja e flamurit (parazgjedhje)
//   "skenderbeu" = shqiponja heraldike e gjurmuar më 4 shtator
//   "artistike"  = vizatimi i vjetër, i ruajtur i plotë më poshtë
const SHQIPONJA_STILI = "flamuri";
// Sa e MADHE del filigrani, si përqindje e brinjës më të vogël të kornizës.
// Ishte 100% — e zinte krejt ekranin. Pronari: «bëje pak më të vogël».
// 46% ishte tepër e vogël, 100% e zinte ekranin. Pronari: «e duhura është në
// mes të asaj të parës dhe kësaj».
const SHQIPONJA_MADHESIA = "70%";
// Sa e DUKSHME. 0.10 → 0.15: «pak më e dukshme, jo shumë».
const SHQIPONJA_DUKSHMERIA = 0.15;
const SHTEG_SHQIPONJA = SHQIPONJA_STILI === "skenderbeu" ? SHTEG_SHQIPONJA_SKENDERBEU : SHTEG_SHQIPONJA_FLAMURI;

// Vizatimi i vjetër, fjalë për fjalë siç ka qenë — dhjetë shtigje.
function ShqiponjaArtistike({ color }) {
  return (
    <>
      <g fill={color}>
        <path d="M 208,185 C 220,165 240,148 262,136 C 258,150 250,162 240,172 C 258,158 278,140 292,116 C 286,134 276,150 262,164 C 280,152 300,132 312,106 C 306,128 294,148 278,164 C 292,156 306,142 314,124 C 306,148 290,168 270,180 C 282,178 294,170 302,158 C 290,178 270,192 248,196 C 258,196 268,192 276,184 C 260,200 236,206 216,200 C 210,196 208,190 208,185 Z" />
        <path d="M 192,185 C 180,165 160,148 138,136 C 142,150 150,162 160,172 C 142,158 122,140 108,116 C 114,134 124,150 138,164 C 120,152 100,132 88,106 C 94,128 106,148 122,164 C 108,156 94,142 86,124 C 94,148 110,168 130,180 C 118,178 106,170 98,158 C 110,178 130,192 152,196 C 142,196 132,192 124,184 C 140,200 164,206 184,200 C 190,196 192,190 192,185 Z" />
        <path d="M 200,160 C 189,170 182,188 182,208 C 182,236 189,262 200,286 C 211,262 218,236 218,208 C 218,188 211,170 200,160 Z" />
        <path d="M 200,286 L 160,320 L 182,304 L 176,336 L 200,308 L 224,336 L 218,304 L 240,320 Z" />
        <path d="M 188,278 L 178,308 M 212,278 L 222,308" stroke={color} strokeWidth="7" fill="none" />
        <path d="M 168,310 L 178,308 L 187,315 M 232,310 L 222,308 L 213,315" stroke={color} strokeWidth="7" fill="none" strokeLinecap="round" />
        <path d="M 194,166 C 176,148 154,130 128,118" stroke={color} strokeWidth="18" fill="none" strokeLinecap="round" />
        <path d="M 206,166 C 224,148 246,130 272,118" stroke={color} strokeWidth="18" fill="none" strokeLinecap="round" />
        <path d="M 128,118 C 110,108 92,106 74,112 C 84,100 100,94 116,95 C 106,86 92,84 78,88 C 96,74 122,74 138,88 C 150,98 152,114 144,126 C 138,134 128,138 118,134 C 108,130 104,120 108,112 C 114,113 122,115 128,118 Z M 74,112 C 62,113 50,118 40,127 C 52,124 62,123 71,124 C 62,129 54,135 48,144 C 60,136 70,131 78,128 Z" />
        <path d="M 272,118 C 290,108 308,106 326,112 C 316,100 300,94 284,95 C 294,86 308,84 322,88 C 304,74 278,74 262,88 C 250,98 248,114 256,126 C 262,134 272,138 282,134 C 292,130 296,120 292,112 C 286,113 278,115 272,118 Z M 326,112 C 338,113 350,118 360,127 C 348,124 338,123 329,124 C 338,129 346,135 352,144 C 340,136 330,131 322,128 Z" />
      </g>
    </>
  );
}

function EagleWatermark({ opacity = SHQIPONJA_DUKSHMERIA, madhesia = SHQIPONJA_MADHESIA }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", opacity, zIndex: 0, overflow: "hidden" }}>
      <svg viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet" style={{ width: madhesia, height: madhesia, maxWidth: 420, maxHeight: 420 }}>
        {SHQIPONJA_STILI === "artistike"
          ? <ShqiponjaArtistike color={COLORS.moss} />
          : <path d={SHTEG_SHQIPONJA} fill={COLORS.moss} fillRule="evenodd" />}
      </svg>
    </div>
  );
}
// Simplified silhouette of Kosovo's borders, with the 6 stars from the flag
// arranged above it in an arc — same idea as the eagle watermark, but for
// the "Kosovo Motifs" theme.
// The real Kosovo map silhouette, traced from the actual flag (not hand-
// drawn approximation) — extracted as a transparent PNG in the theme's own
// gold accent color, stored at public/kosovo-map.png.
function KosovoWatermark({ opacity = 0.16 }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", opacity, zIndex: 0, overflow: "hidden" }}>
      <img src="/kosovo-map.png" alt="" style={{ width: "55%", maxWidth: 340, objectFit: "contain" }} />
    </div>
  );
}
// Renders the family's uploaded coat-of-arms as a faint watermark, for the
// "Stema e Familjes" theme. Falls back to nothing if no crest is uploaded
// yet — the theme still applies its color palette either way.
function CrestWatermark({ crestUrl, opacity = 0.14 }) {
  if (!crestUrl) return null;
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", opacity, zIndex: 0, overflow: "hidden" }}>
      <img src={crestUrl} alt="" style={{ width: "55%", maxWidth: 360, objectFit: "contain", filter: "grayscale(1)" }} />
    </div>
  );
}

// ============================================================================
// FAZA 3 — KATALOGU I STILEVE TË POSTERIT (sfonde + korniza artistike + titull).
// Krejt SVG i pastër (vektorial → pa humbje në çdo madhësi), i pavarur nga tema
// e app-it që pamja të jetë e parashikueshme. `buildPosterDecor` kthen tri copa
// SVG: `defs` (gradientë), `back` (sfond + motive PAS pemës) dhe `front`
// (korniza + titull MBI/rreth pemës). Përdoret VETËM te eksporti i posterit —
// shtesë e pastër, s'prek asgjë nga pamja normale e pemës.
// ============================================================================

// Shqiponja dykrenare (e njëjta siluetë si EagleWatermark) si motiv i ripërdorshëm.
// I njëjti shteg si te `EagleWatermark` — një burim i vetëm për figurën.
const EAGLE_PATHS_ARTISTIKE =
  '<path d="M 208,185 C 220,165 240,148 262,136 C 258,150 250,162 240,172 C 258,158 278,140 292,116 C 286,134 276,150 262,164 C 280,152 300,132 312,106 C 306,128 294,148 278,164 C 292,156 306,142 314,124 C 306,148 290,168 270,180 C 282,178 294,170 302,158 C 290,178 270,192 248,196 C 258,196 268,192 276,184 C 260,200 236,206 216,200 C 210,196 208,190 208,185 Z"/>' +
  '<path d="M 192,185 C 180,165 160,148 138,136 C 142,150 150,162 160,172 C 142,158 122,140 108,116 C 114,134 124,150 138,164 C 120,152 100,132 88,106 C 94,128 106,148 122,164 C 108,156 94,142 86,124 C 94,148 110,168 130,180 C 118,178 106,170 98,158 C 110,178 130,192 152,196 C 142,196 132,192 124,184 C 140,200 164,206 184,200 C 190,196 192,190 192,185 Z"/>' +
  '<path d="M 200,160 C 189,170 182,188 182,208 C 182,236 189,262 200,286 C 211,262 218,236 218,208 C 218,188 211,170 200,160 Z"/>' +
  '<path d="M 200,286 L 160,320 L 182,304 L 176,336 L 200,308 L 224,336 L 218,304 L 240,320 Z"/>' +
  '<path d="M 128,118 C 110,108 92,106 74,112 C 84,100 100,94 116,95 C 106,86 92,84 78,88 C 96,74 122,74 138,88 C 150,98 152,114 144,126 C 138,134 128,138 118,134 C 108,130 104,120 108,112 C 114,113 122,115 128,118 Z M 74,112 C 62,113 50,118 40,127 C 52,124 62,123 71,124 C 62,129 54,135 48,144 C 60,136 70,131 78,128 Z"/>' +
  '<path d="M 272,118 C 290,108 308,106 326,112 C 316,100 300,94 284,95 C 294,86 308,84 322,88 C 304,74 278,74 262,88 C 250,98 248,114 256,126 C 262,134 272,138 282,134 C 292,130 296,120 292,112 C 286,113 278,115 272,118 Z M 326,112 C 338,113 350,118 360,127 C 348,124 338,123 329,124 C 338,129 346,135 352,144 C 340,136 330,131 322,128 Z"/>' +
  '<path d="M 188,278 L 178,308 M 212,278 L 222,308" stroke-width="7" fill="none"/>' +
  '<path d="M 168,310 L 178,308 L 187,315 M 232,310 L 222,308 L 213,315" stroke-width="7" fill="none" stroke-linecap="round"/>' +
  '<path d="M 194,166 C 176,148 154,130 128,118" stroke-width="18" fill="none" stroke-linecap="round"/>' +
  '<path d="M 206,166 C 224,148 246,130 272,118" stroke-width="18" fill="none" stroke-linecap="round"/>';
// I njëjti shteg si te `EagleWatermark` — një burim i vetëm për figurën.
const EAGLE_PATHS = SHQIPONJA_STILI === "artistike"
  ? EAGLE_PATHS_ARTISTIKE
  : `<path d="${SHTEG_SHQIPONJA}" fill-rule="evenodd"/>`;

function escapeXml(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" }[c]));
}

function eagleMotif(cx, cy, size, color, opacity) {
  const s = size / 400;
  const art = SHQIPONJA_STILI === "artistike";
  const dy = art ? 215 : 200;            // i vjetri ishte i qendërzuar te 215
  const vi = art ? ` stroke="${color}" stroke-linejoin="round"` : "";  // i vjetri kishte vija
  return `<g transform="translate(${(cx - 200 * s).toFixed(1)},${(cy - dy * s).toFixed(1)}) scale(${s.toFixed(4)})" fill="${color}"${vi} opacity="${opacity}">${EAGLE_PATHS}</g>`;
}

// Yll me 5 cepa (poligon) — për motivin e Kosovës.
function starPoly(cx, cy, R, color) {
  let pts = "";
  for (let i = 0; i < 10; i++) {
    const r = i % 2 ? R * 0.42 : R;
    const a = (-90 + i * 36) * Math.PI / 180;
    pts += `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)} `;
  }
  return `<polygon points="${pts.trim()}" fill="${color}"/>`;
}
function starsArc(cx, cy, spread, R, color) {
  let out = "";
  for (let i = 0; i < 6; i++) {
    const t = i / 5 - 0.5;
    out += starPoly(cx + t * spread, cy + Math.abs(t) * spread * 0.16, R, color);
  }
  return out;
}

// Ornament këndi "spirale" (scroll) — vendoset në 4 qoshet me rrotullim.
function scrollGlyph(color, sw) {
  return `<path d="M0,0 C40,2 60,6 80,18" fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round"/>` +
    `<path d="M0,0 C2,40 6,60 18,80" fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round"/>` +
    `<path d="M22,22 C42,24 54,34 56,54 C52,40 42,34 32,38 C40,32 42,24 36,16" fill="none" stroke="${color}" stroke-width="${(sw * 0.8).toFixed(2)}" stroke-linecap="round"/>` +
    `<circle cx="32" cy="32" r="4.5" fill="${color}"/>`;
}
// Sprigë me gjethe — për stilin "Emerald".
function leafGlyph(color) {
  let g = `<path d="M0,0 C24,-4 52,-2 76,10" fill="none" stroke="${color}" stroke-width="3.2"/>`;
  [12, 30, 48, 66].forEach((d, i) => { g += `<ellipse cx="${d}" cy="${(i % 2 ? 2 : -2)}" rx="8" ry="3.8" fill="${color}" transform="rotate(${i % 2 ? 26 : -26} ${d} 0)"/>`; });
  return g;
}
// Zemër e vogël — për stilin "Trëndafil".
function heartGlyph(color) {
  return `<path d="M20,10 C20,2 8,2 8,12 C8,20 20,28 20,28 C20,28 32,20 32,12 C32,2 20,2 20,10 Z" fill="${color}"/>`;
}
function cornerGlyphs(bx, by, bw, bh, s, glyph) {
  const P = (px, py, rot) => `<g transform="translate(${px.toFixed(1)},${py.toFixed(1)}) rotate(${rot}) scale(${s.toFixed(4)})">${glyph}</g>`;
  return P(bx, by, 0) + P(bx + bw, by, 90) + P(bx + bw, by + bh, 180) + P(bx, by + bh, 270);
}
// Kurorë dafine rreth titullit — për stilin "Kurorë dafine".
function laurelWreath(cx, cy, spread, color) {
  const branch = (dir) => {
    let b = `<path d="M0,0 C${28 * dir},-10 ${52 * dir},-6 ${70 * dir},14" fill="none" stroke="${color}" stroke-width="3.4"/>`;
    [14, 30, 46, 60].forEach((d, i) => { b += `<ellipse cx="${d * dir}" cy="${-4 + i * 2}" rx="8" ry="3.6" fill="${color}" transform="rotate(${(i % 2 ? 24 : 40) * -dir} ${d * dir} ${-4 + i * 2})"/>`; });
    return `<g transform="translate(${cx},${cy})">${b}</g>`;
  };
  return branch(-1) + branch(1);
}

// `POSTER_STYLES` u hoq (19 gusht 2026) — lista e stileve të posterit vjen
// tani nga biblioteka e Admin Center; kjo kopje e ngulitur s'lexohej më.
const STYLE_CFG = {
  classicGold:  { bg: { t: "vig", a: ["#241f18", "#141009"] }, ink: "#C9A227", sub: "#a98a2e", frame: "scroll", fc: "#C9A227", motif: null, minimal: false },
  royalRed:     { bg: { t: "rad", a: ["#7d1a2b", "#3A0A13"] }, ink: "#E7CD82", sub: "#d9b45f", frame: "scroll", fc: "#C9A227", motif: { t: "eagle", c: "#C9A227", o: 0.07 }, minimal: false },
  eagleGold:    { bg: { t: "vig", a: ["#2B2620", "#15110b"] }, ink: "#C9A227", sub: "#a98a2e", frame: "double", fc: "#C9A227", motif: { t: "eagle", c: "#C9A227", o: 0.13 }, minimal: false },
  kosovoBlue:   { bg: { t: "rad", a: ["#2a54b8", "#152C63"] }, ink: "#E8B227", sub: "#d7c98a", frame: "double", fc: "#E8B227", motif: { t: "stars", c: "#E8B227" }, minimal: false },
  parchment:    { bg: { t: "vig", a: ["#f1e8d2", "#ddcba6"] }, ink: "#5b432a", sub: "#7a5f3a", frame: "square", fc: "#8a6d3b", motif: null, minimal: false },
  midnightDeco: { bg: { t: "lin", a: ["#1b2c48", "#0C1524"] }, ink: "#E7CD82", sub: "#c9b478", frame: "deco", fc: "#C9A227", motif: null, minimal: false },
  emerald:      { bg: { t: "rad", a: ["#1c4a37", "#0c2119"] }, ink: "#D9C79A", sub: "#b8a26f", frame: "leaf", fc: "#C9A227", motif: null, minimal: false },
  rose:         { bg: { t: "rad", a: ["#f7ecef", "#e7c9d2"] }, ink: "#9c3f57", sub: "#b5657a", frame: "heart", fc: "#B5657A", motif: null, minimal: false },
  laurel:       { bg: { t: "vig", a: ["#f7f0e0", "#e9dcc2"] }, ink: "#3a2f1c", sub: "#6b4f2a", frame: "laurel", fc: "#9c7b34", motif: null, minimal: true },
  minimalLight: { bg: { t: "solid", a: ["#faf7f0"] }, ink: "#2A2118", sub: "#6B6152", frame: "single", fc: "#2A2118", motif: null, minimal: true },
  onyxGold:     { bg: { t: "vig", a: ["#141210", "#050505"] }, ink: "#C9A227", sub: "#9d8130", frame: "scroll", fc: "#C9A227", motif: null, minimal: false },
};

function buildPosterDecor(styleKey, g, title, subtitle, unit) {
  const cfg = STYLE_CFG[styleKey];
  if (!cfg) return null;
  const { x, y, w, h } = g;
  const cx = x + w / 2;
  // ---- defs + sfond ----
  let defs = "";
  let bgFill;
  if (cfg.bg.t === "solid") {
    bgFill = cfg.bg.a[0];
  } else if (cfg.bg.t === "lin") {
    defs += `<linearGradient id="pefPosterBg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${cfg.bg.a[0]}"/><stop offset="100%" stop-color="${cfg.bg.a[1]}"/></linearGradient>`;
    bgFill = "url(#pefPosterBg)";
  } else {
    defs += `<radialGradient id="pefPosterBg" cx="50%" cy="42%" r="78%"><stop offset="0%" stop-color="${cfg.bg.a[0]}"/><stop offset="100%" stop-color="${cfg.bg.a[1]}"/></radialGradient>`;
    bgFill = "url(#pefPosterBg)";
  }
  let back = `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${bgFill}"/>`;
  if (cfg.motif && cfg.motif.t === "eagle") back += eagleMotif(cx, y + h * 0.52, Math.min(w, h) * 0.92, cfg.motif.c, cfg.motif.o);
  // ---- korniza (front) ----
  const inset = unit * 0.5;
  const sw = Math.max(2, unit * 0.05);
  const fc = cfg.fc;
  const bx = x + inset, by = y + inset, bw = w - 2 * inset, bh = h - 2 * inset;
  const rect = (rx, ry, rw, rh, w2) => `<rect x="${rx.toFixed(1)}" y="${ry.toFixed(1)}" width="${rw.toFixed(1)}" height="${rh.toFixed(1)}" fill="none" stroke="${fc}" stroke-width="${w2.toFixed(2)}"/>`;
  const dbl = () => rect(bx, by, bw, bh, sw) + rect(bx + sw * 2.4, by + sw * 2.4, bw - sw * 4.8, bh - sw * 4.8, sw * 0.5);
  let front = "";
  switch (cfg.frame) {
    case "single":
      front += rect(bx, by, bw, bh, sw * 0.7);
      break;
    case "double":
      front += dbl();
      break;
    case "scroll":
      front += dbl() + cornerGlyphs(bx + sw * 2.4, by + sw * 2.4, bw - sw * 4.8, bh - sw * 4.8, unit / 90, scrollGlyph(fc, 6));
      break;
    case "leaf":
      front += dbl() + cornerGlyphs(bx + sw * 3, by + sw * 3, bw - sw * 6, bh - sw * 6, unit / 90, leafGlyph(fc));
      break;
    case "heart":
      front += dbl() + cornerGlyphs(bx + sw * 3, by + sw * 3, bw - sw * 6, bh - sw * 6, unit / 60, heartGlyph(fc));
      break;
    case "square": {
      front += dbl();
      const q = unit * 0.12;
      [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]].forEach(([px, py]) => { front += `<rect x="${(px - q / 2).toFixed(1)}" y="${(py - q / 2).toFixed(1)}" width="${q.toFixed(1)}" height="${q.toFixed(1)}" fill="${fc}"/>`; });
      break;
    }
    case "deco": {
      front += rect(bx, by, bw, bh, sw);
      front += `<rect x="${(bx + sw * 3).toFixed(1)}" y="${(by + sw * 3).toFixed(1)}" width="${(bw - sw * 6).toFixed(1)}" height="${(bh - sw * 6).toFixed(1)}" fill="none" stroke="${fc}" stroke-width="${(sw * 0.5).toFixed(2)}" stroke-dasharray="${(unit * 0.14).toFixed(1)} ${(unit * 0.09).toFixed(1)}"/>`;
      const L = unit * 0.5;
      [[bx, by, 1, 1], [bx + bw, by, -1, 1], [bx + bw, by + bh, -1, -1], [bx, by + bh, 1, -1]].forEach(([px, py, sx, sy]) => {
        front += `<path d="M${px.toFixed(1)},${(py + sy * L).toFixed(1)} L${px.toFixed(1)},${py.toFixed(1)} L${(px + sx * L).toFixed(1)},${py.toFixed(1)}" fill="none" stroke="${fc}" stroke-width="${(sw * 1.6).toFixed(2)}"/>`;
      });
      break;
    }
    case "laurel":
      front += rect(bx, by, bw, bh, sw * 0.6);
      break;
    default:
      front += dbl();
  }
  if (cfg.motif && cfg.motif.t === "stars") front += starsArc(cx, y + unit * 1.5, w * 0.5, unit * 0.17, cfg.motif.c);
  // ---- titulli ----
  const fs = unit * 0.62;
  const ty = y + unit * 1.45; // ulur që titulli të mos prekë kornizën lart
  if (cfg.frame === "laurel") front += laurelWreath(cx, ty - fs * 0.32, unit * 1.6, fc);
  front += `<text x="${cx.toFixed(1)}" y="${ty.toFixed(1)}" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-weight="700" font-size="${fs.toFixed(1)}" fill="${cfg.ink}" letter-spacing="${(fs * 0.02).toFixed(1)}">${escapeXml(title)}</text>`;
  if (subtitle) front += `<text x="${cx.toFixed(1)}" y="${(ty + fs * 0.82).toFixed(1)}" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-size="${(fs * 0.32).toFixed(1)}" fill="${cfg.sub}" letter-spacing="${(fs * 0.09).toFixed(1)}">${escapeXml(subtitle)}</text>`;
  return { defs, back, front };
}

// ---------- helpers ----------
function uid() {
  return "tmp_" + Math.random().toString(36).slice(2, 10);
}
function lifespan(p) {
  const b = p.birth_date ? p.birth_date.slice(0, 4) : "?";
  const d = p.death_date ? p.death_date.slice(0, 4) : "";
  return d ? `${b}–${d}` : `l. ${b}`;
}
// Kërkesë e userit — traditë shqiptare: nëse personi (ose admini në emër
// të tij) ka zgjedhur ta shfaqë, mbiemri i vajzërisë vjen NË MES të emrit
// dhe mbiemrit (p.sh. "Merita Orlishta Krasniqi"), jo vetëm si fushë e
// fshehur brenda profilit.
// FIX (raportuar nga useri — "cilësimet e pemës s'ndryshojnë në Android,
// edhe pas build-i të ri") — disa thirrje RPC për cilësime platformë-gjerë
// (zoom-i i fokusit, zoom-out-i i zgjerimit, madhësia/skema e kartelës)
// dështonin NË HESHTJE (asnjë kontroll `error`, asnjë riprovim) — Android
// duket më i prirur ndaj kësaj (mundësisht garë me thirrje të tjera
// fillestare, ose rrjet më i ngadaltë). Helper i përbashkët — riprovon
// deri në 5 herë (300ms mes tyre) para se të bjerë mbrapsht te vlerat
// parazgjedhje të ruajtura në vetë komponentin thirrës.
async function fetchSettingRpcWithRetry(rpcName, attempt = 1) {
  const { data, error } = await supabase.rpc(rpcName);
  if (!error && data != null) return data;
  if (error) console.warn(rpcName + " failed (attempt " + attempt + "):", error.message);
  if (attempt < 5) {
    await new Promise((r) => setTimeout(r, 300));
    return fetchSettingRpcWithRetry(rpcName, attempt + 1);
  }
  return null;
}
function displayName(p) {
  if (p?.maiden_name_in_display && p?.maiden_name) {
    return `${p?.first_name || ""} ${p.maiden_name} ${p?.last_name || ""}`.trim() || "No name";
  }
  return `${p?.first_name || ""} ${p?.last_name || ""}`.trim() || "No name";
}

// ─────────────────────────────────────────────────────────────────────────────
// EMRA TË NJËJTË — DALLUESI
//
// Kërkesë e pronarit (5 shtator 2026): «kur ka disa persona me emër të njëjtë
// dhe bëj kërkim — për t'i ndryshuar rolin, për t'i vendosur prindër, çfarëdo —
// nuk e di cili është cili. Problemi është i njëjti në të gjitha vendet ku
// përdoret kërkimi.»
//
// Dalluesi shfaqet VETËM te personat që kanë emër e mbiemër identik me dikë
// tjetër në pemë. Kështu listat mbeten të pastra, dhe vetë prania e një
// dalluesi të thotë se aty ka dy veta me atë emër.
//
// Radha: emri i babait → viti i lindjes → asgjë. Babai vjen i pari sepse pemët
// e vjetra shpesh s'kanë datëlindje — dhe pikërisht ato janë pemët ku emrat
// përsëriten brez pas brezi.
//
// ⚠ ATËSIA SHKRUHET NË EMËRORE, BRENDA KLLAPAVE: «Arben (Ramiz) Krasniqi» —
// forma e dokumenteve zyrtare. Trajta e shquar shqipe («Arben i Ramizit
// Krasniqi») do të kërkonte lakim të emrit (Ramiz→Ramizit, Ukë→Ukës,
// Hysni→Hysniut), i cili gabon lehtë, dhe do të ishte gabim gramatikor brenda
// versionit anglisht e në 21 gjuhët e tjera. Kllapat lexohen njësoj kudo.
function babaiI(p, people) {
  const ids = Array.isArray(p?.parent_ids) ? p.parent_ids : [];
  if (!ids.length) return "";
  const prinderit = ids.map((id) => (people ? people[id] : null)).filter(Boolean);
  // Prindi mashkull; nëse gjinia s'dihet po prindi është një i vetëm, ai.
  const babai = prinderit.find((x) => x.gender === "M") || (prinderit.length === 1 ? prinderit[0] : null);
  return babai ? (babai.first_name || "").trim() : "";
}

// Kthen një Map(id → etiketë). Llogaritet një herë për listë, jo një herë për
// rresht — ndryshe çdo rresht do të numëronte krejt pemën.
function ndertoEtiketatDalluese(people) {
  const harta = new Map();
  const lista = Object.values(people || {});
  const numri = new Map();
  for (const p of lista) {
    const k = displayName(p).trim().toLowerCase();
    if (k) numri.set(k, (numri.get(k) || 0) + 1);
  }
  for (const p of lista) {
    const emri = displayName(p);
    if ((numri.get(emri.trim().toLowerCase()) || 0) < 2) { harta.set(p.id, emri); continue; }
    const babai = babaiI(p, people);
    const first = (p.first_name || "").trim();
    const last = (p.last_name || "").trim();
    if (babai && first && last) harta.set(p.id, `${first} (${babai}) ${last}`);
    else if (babai) harta.set(p.id, `${emri} (${babai})`);
    else {
      const viti = String(p.birth_date || "").slice(0, 4);
      harta.set(p.id, viti ? `${emri} (${viti})` : emri);
    }
  }
  // Nëse dalluesi s'ka dalluar — dy vëllezër me të njëjtin emër, gjë që te
  // pemët e vjetra ndodh kur i pari ka vdekur foshnjë — shtohet edhe viti.
  const perseri = new Map();
  for (const v of harta.values()) perseri.set(v, (perseri.get(v) || 0) + 1);
  for (const p of lista) {
    const v = harta.get(p.id);
    if ((perseri.get(v) || 0) < 2) continue;
    const viti = String(p.birth_date || "").slice(0, 4);
    if (viti) harta.set(p.id, `${v} ${viti}`);
  }
  return harta;
}

// Përdoret kudo ku njeriu ZGJEDH një person nga një listë. NUK përdoret te
// fjalitë e rrjedhës («X iu bashkua familjes») — atje emri është tregim, jo
// zgjedhje, dhe kllapat do të ishin zhurmë.
function emriDallues(p, etiketat) {
  return (etiketat && p && etiketat.get(p.id)) || displayName(p);
}
// ─────────────────────────────────────────────────────────────────────────────
// BALLINA6 — KOHA E NJË RRESHTI TË HISTORISË
//
// Historia përzien dy burime me dy formate date: ngjarjet e familjes shkruhen
// me dorë («1898», «qershor 1969», «1969-06-14») dhe skedarët kanë timestamp të
// plotë. Për t'i renditur bashkë duhet një numër i vetëm.
//
// Rregulli: provo datën e plotë; nëse s'lexohet, merr vitin e parë katërshifror
// dhe vendose te 1 janari. Një ngjarje e vitit 1898 rri te 1898 — jo te viti
// zero, ku do të zhytej nën çdo gjë tjetër dhe do të dukej e humbur.
function kohaEHistorise(s) {
  if (!s) return 0;
  const d = Date.parse(s);
  if (!Number.isNaN(d)) return d;
  const m = String(s).match(/(\d{4})/);
  return m ? Date.UTC(Number(m[1]), 0, 1) : 0;
}
function vitiEHistorise(s) {
  const m = s ? String(s).match(/(\d{4})/) : null;
  return m ? m[1] : null;
}
// HISTORIA1 — SA E DI VETEN NJË DATË.
//
// Për 1898 dihet vetëm viti. Po të kërkoja datë të plotë, dikush do të shkruante
// 1 janar — dhe një datë e trilluar te regjistri i një familjeje është gënjeshtër
// e vogël që pas njëzet vjetësh lexohet si e vërtetë. Prandaj data e mban edhe
// saktësinë e vet, dhe ekrani e shkruan vetëm aq sa dihet.
const SAKTESITE_E_DATES = ["dite", "muaj", "vit", "dekade"];
function dataELexueshme(s, saktesi, gjuha, t) {
  const v = String(s || "").trim();
  if (!v) return "";
  const m = v.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/);
  if (!m) return v;          // tekst i lirë («verë e vonshme») — thuhet siç është
  const vit = m[1], muaj = m[2], dite = m[3];
  if (saktesi === "dekade") return t("the {{year}}s", { year: vit });
  if (saktesi === "vit" || !muaj) return vit;
  try {
    const d = new Date(Number(vit), Number(muaj) - 1, dite ? Number(dite) : 1);
    if (saktesi === "muaj" || !dite) return d.toLocaleDateString(gjuha || "sq", { month: "long", year: "numeric" });
    return d.toLocaleDateString(gjuha || "sq", { day: "numeric", month: "long", year: "numeric" });
  } catch (_e) { return v; }
}
// Data e sotme si `YYYY-MM-DD` në orën LOKALE. `toISOString()` do të jepte UTC,
// dhe pas orës 01:00 në Prishtinë do të shkruante datën e djeshme — një gabim
// që del vetëm natën dhe prandaj nuk kapet kurrë duke provuar ditën.
function sotSiTekst() {
  const d = new Date();
  const p2 = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}
// ─────────────────────────────────────────────────────────────────────────────
// STATISTIKA — NDIHMËSIT
//
// Brezi i secilit person: 1 + maksimumi i brezave të prindërve që ende
// ekzistojnë te pema. Maksimumi (jo i pari) e rregullon rastin e bashkëshortit
// të ardhur nga jashtë, ku dy prindërit rrinë te nivele të ndryshme. I mbrojtur
// nga ciklet, sepse një pemë e importuar gabim mund të ketë të tillë dhe një
// rekurs pa mbrojtje do të ngrinte ekranin, jo të jepte gabim.
function nivelePerPerson(people) {
  const level = {};
  const visiting = new Set();
  const getLevel = (id) => {
    if (id in level) return level[id];
    if (visiting.has(id)) return 0;
    const p = people[id];
    if (!p) return 0;
    const parents = (p.parent_ids || []).filter((pid) => people[pid]);
    if (parents.length === 0) { level[id] = 0; return 0; }
    visiting.add(id);
    let mx = 0;
    for (const pid of parents) mx = Math.max(mx, getLevel(pid) + 1);
    visiting.delete(id);
    level[id] = mx;
    return mx;
  };
  Object.keys(people).forEach((id) => getLevel(id));

  // ⚠ 5 shtator 2026 — «brezi i parë e ka vetëm një person, po këtu shfaqen 29».
  //
  // Kishte të drejtë, dhe shkaku është te vetë përkufizimi më sipër: kush nuk ka
  // asnjë prind TE PEMA merr nivelin 0. Kjo është e saktë për paraardhësin e
  // parë — dhe e gabuar për çdo bashkëshort/e që ka ardhur me martesë, sepse
  // prindërit e tij nuk janë te kjo pemë. Pra nusja e një djali të brezit të
  // pestë ulej te brezi i parë, krah stërgjyshit. Me 131 veta, 29 sosh.
  //
  // Rregulli: kush s'ka prindër PO ka bashkëshort me nivel të njohur, merr
  // nivelin e bashkëshortit — martesa është brenda brezit, jo mes brezash.
  // Përsëritet derisa të qetësohet, sepse një bashkëshort mund të varet nga një
  // tjetër (dy të ardhur me martesë, të lidhur mes tyre). Kufiri i përsëritjeve
  // e mbron nga një zinxhir i mbyllur në vetvete.
  const paPrinder = Object.keys(people).filter(
    (id) => ((people[id] && people[id].parent_ids) || []).filter((pid) => people[pid]).length === 0
  );
  for (let raundi = 0; raundi < 30; raundi += 1) {
    let ndryshoi = false;
    for (const id of paPrinder) {
      const bashke = ((people[id] && people[id].spouse_ids) || []).filter((sid) => people[sid]);
      if (!bashke.length) continue;
      let mx = level[id] || 0;
      for (const sid of bashke) mx = Math.max(mx, level[sid] || 0);
      if (mx > (level[id] || 0)) { level[id] = mx; ndryshoi = true; }
    }
    if (!ndryshoi) break;
  }
  return level;
}
// DITA1 — «viti mund të hamendësohet dhe merret si e dhënë e mirëqenë; muaji
// dhe dita janë problem». Prandaj një pyetje e vetme, e thirrur kudo ku
// numërohet një DITË ose një MUAJ. Viti nuk e pyet kurrë këtë funksion.
function ditaENjohur(p) {
  return !!p && !p.birth_date_approx && /^\d{4}-\d{2}-\d{2}$/.test(String(p.birth_date || ""));
}
function vitiIDates(s) {
  const m = s ? String(s).match(/(\d{4})/) : null;
  const v = m ? Number(m[1]) : null;
  // Një vit jashtë kufijve njerëzorë është gabim shtypi, jo e dhënë. Po ta
  // lija, një «19999» do ta prishte mesataren e një brezi të tërë pa u parë.
  return v && v > 1000 && v < 2200 ? v : null;
}
function moshaNeVdekje(p) {
  const a = vitiIDates(p?.birth_date), b = vitiIDates(p?.death_date);
  if (!a || !b) return null;
  const d = b - a;
  return d >= 0 && d <= 120 ? d : null;
}
// VENDLINDJA ËSHTË NJË VEND, JO NJË ADRESË.
//
// Pronari: «këtu e marrim vetëm vendlindjen që është fshati apo qyteti, jo
// adresa e gjatë». Fusha është tekst i lirë, pra dikush shkruan «Pejë» dhe
// dikush «Rr. Dëshmorët e Kombit nr. 12, Pejë, Kosovë» — dhe si dy vende të
// ndryshme, ato e ndajnë të njëjtin qytet në dy copa.
//
// Rregulli: hidhen copat që janë rrugë a numër, dhe merret e PARA që mbetet.
// E para, jo e fundit: te shqipja vendi shkruhet para shtetit («Pejë, Kosovë»),
// dhe shteti e ka fushën e vet te pema.
const COPA_ADRESE = /(^|\s)(rr\.?|rruga|rrugës|str\.?|street|nr\.?|no\.?|p\.?n\.?|lagjja|lagja|blloku|banesa|kati|hyrja)(\s|$)/i;
function vendlindjaEShkurter(s) {
  const v = String(s || "").trim();
  if (!v) return "";
  const copat = v.split(/[,/;]/).map((x) => x.trim()).filter(Boolean)
    .filter((x) => !/\d/.test(x) && !COPA_ADRESE.test(x));
  const zgjedhur = (copat[0] || v).replace(/\s+/g, " ").trim();
  // Shkronja e parë e madhe, që «pejë» dhe «Pejë» të mos jenë dy vende.
  return zgjedhur.charAt(0).toLocaleUpperCase("sq") + zgjedhur.slice(1);
}


// ═════════════════════════════════════════════════════════════════════════════
// STATISTIKA3 — NJË PAMJE E VETME (2 shtator 2026)
//
// Tri pamjet (Kartela / Portret / Rrëfim) u bënë NJË. Pronari: «nuk kemi pse
// t'i kemi disa kartela, mjafton vetëm një» — dhe forma që zgjodhi është ajo e
// portretit: kartela të bardha, një pyetje për kartelë, numri i madh në serif.
//
// TRI RREGULLA QË MBAN KJO PAMJE
//   1. Çdo kartelë ka një ÇELËS (`hero`, `brezat`, …). Titulli, nëntitulli,
//      dukshmëria dhe radha vijnë nga Admin Center (`rpc_statistika_cilesimet`),
//      me parazgjedhjen e platformës dhe përjashtimin e familjes të bashkuara
//      në server. Pa derë (SQL i paxhiruar) → çdo kartelë e dukshme, me
//      titullin e vet, në radhën e ngulitur këtu.
//   2. FILTRI I DEGËS = NJË PARAARDHËS. Pronari: «shkruaj emrin e personit dhe
//      automatikisht dalin pasardhësit e tij». Zgjedh një person, dhe çdo numër
//      i faqes flet vetëm për të dhe pasardhësit e tij (jo bashkëshortët e
//      tyre — ata janë degë tjetër). Brezi llogaritet gjithmonë mbi pemën e
//      plotë — struktura s'filtrohet, vetëm grupi.
//   3. Asnjë listë e prerë pa «trego më shumë». Pesë vendet e para janë
//      përmbledhje, jo e vërteta e plotë; e plota është një klik larg.
//
// Ngjyrat: një seri e vetme = burgund (mbi panel të errët: trëndafil, që të
// lexohet). Dy seri (burra/gra) = burgund + ar. Degët = ngjyrat e pemës, që
// «me martesë» të jetë e njëjta rozë këtu dhe te kartela e personit.
// ═════════════════════════════════════════════════════════════════════════════

const STATISTIKA_RADHA_PARAZGJEDHUR = [
  "hero", "perberja", "brezat", "plotesia", "sot", "emri_brez", "mosha_prinder",
  "sa_femije", "muajt", "vendlindja", "harta", "migrimi", "jeta",
  // Dy të reja, 5 shtator 2026. Vendosen pas «jeta» sepse të dyja flasin për të
  // njëjtën gjë me të: kohën e një jete dhe çastin e lindjes.
  "jetegjatet", "binjaket",
  "arkivi", "profesioni",
  // 6 shtator 2026 — në fund, një përmbledhje në formë tregimi të shkurtër,
  // e nxjerrë nga numrat më lart.
  "permbledhja",
];
// Çelësat e muajve — përkthehen me `t`, jo me `toLocaleDateString`: në disa
// pajisje lokali «sq» s'ekziston dhe muaji dilte «October» në mes të shqipes.
const STATISTIKA_MUAJT = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const STATISTIKA_DEGET = ["core", "married_in", "married_out", "close_cousin", "extended"];

// «Trego më shumë» — një listë e prerë thotë vetë sa i mungon.
function TregoMeShume({ sa, gjithsej, hapur, onNdrysho, FONTS, KART, t }) {
  if (gjithsej <= sa) return null;
  return (
    <button onClick={() => onNdrysho(!hapur)}
      style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, color: KART.accentText || KART.gold,
               background: "transparent", border: "none", padding: "4px 0", cursor: "pointer", alignSelf: "flex-start" }}>
      {hapur ? t("Show less") : t("Show more ({{sa}})", { sa: gjithsej - sa })}
    </button>
  );
}

// Kartela e statistikës — sipërfaqe e bardhë, një pyetje, radius 12, hija e
// përbashkët e kartelave. `nenAdmin` (nga Admin Center) fiton mbi `nen`.
function StatKarta({ st, eyebrow, titulli, nen, nenAdmin, children }) {
  const { KART, FONTS, paneliErret } = st;
  const nenT = nenAdmin || nen;
  // ── PAMJA «HEIRLOOM» ──────────────────────────────────────────────────────
  // Te mockup-i statistikat NUK janë kartela: janë një faqe e vetme që rrjedh,
  // e ndarë me mbishkrime të vogla ari me kapitale. Kartelat i ndajnë numrat
  // në kuti dhe e bëjnë faqen të duket si panel; mbishkrimet i lidhin si
  // kapituj të një teksti. Prandaj këtu heqim guackën, jo ta zbukurojmë atë.
  // Titulli merr serifin e madh, nëntitulli kursivin — ashtu si «Burrat majtas,
  // gratë djathtas» me rreshtin e tij poshtë.
  if (pamja("statistika", "heirloom")) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 20 }}>
        {eyebrow && (
          <span style={tip("etiketa", { fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, letterSpacing: "0.16em",
                         textTransform: "uppercase", color: PALETA_TIPI.bronze || KART.gold })}>{eyebrow}</span>
        )}
        {titulli && <p style={tip("titulli", { fontFamily: FONTS.display, fontSize: 25, fontWeight: 600, color: PALETA_TIPI.text || KART.ink, margin: 0, lineHeight: 1.15, textWrap: "balance" })}>{titulli}</p>}
        {nenT && <p style={tip("trupi", { fontFamily: FONTS.body, fontSize: 15.5, fontStyle: "italic", color: PALETA_TIPI.text2 || KART.inkLight, margin: 0, maxWidth: "60ch", lineHeight: 1.6 })}>{nenT}</p>}
        {children}
      </section>
    );
  }
  return (
    <section style={{ background: KART.panel, borderRadius: 12, padding: "20px 22px", boxShadow: KARTELA_HIJE,
                      border: `1px solid ${paneliErret ? _rgba(KART.onBg, 0.08) : "#E6DFD3"}`, display: "flex", flexDirection: "column", gap: 10 }}>
      {/* ⚠ 5 shtator 2026 — TË TRI RRESHTAT U RRITËN DHE KALUAN TE `tip()`.
          Pronari: «tekstet janë shumë të vogla, sidomos ky nëntitull — e
          vështirë e lexueshme».
          Kishte të drejtë, dhe matja e thotë pse: 13.5px është madhësi
          LEGJENDE, kurse aty shkruhet një fjali e plotë. Trupi i tekstit te
          iOS është 17px, te Android 16 — ne po shkruanim me madhësinë që i
          jepet një shënimi nën fotografi.
              mbishkrimi 12.5 → 13 · titulli 18 → 21 · nëntitulli 13.5 → 15.5
          Mbishkrimi u ngrit vetëm gjysmë piksel me qëllim: është kapitale me
          hapësirë mes shkronjave, dhe aty çdo piksel duket dy.
          Kalimi te `tip()` është pjesa më e rëndësishme: deri sot këto tri
          madhësi ishin të ngulitura, pra `tokens.type` dhe `shkalla` — sistemi
          që u shkrua pikërisht për ankesën «çdo gjë u zoom out» — nuk i preknin
          fare statistikat. Tani i prekin. Rezerva mbetet ajo më poshtë, ndaj
          asnjë dizajn nuk lëviz derisa dikush ta ndryshojë me dorë. */}
      <span style={tip("etiketa", { fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: KART.inkLight })}>{eyebrow}</span>
      {titulli && <p style={tip("titulli", { fontFamily: FONTS.display, fontSize: 21, fontWeight: 600, color: KART.ink, margin: 0, lineHeight: 1.25, textWrap: "balance" })}>{titulli}</p>}
      {nenT && <p style={tip("trupi", { fontFamily: FONTS.body, fontSize: 15.5, color: KART.inkLight, margin: 0, maxWidth: "60ch", lineHeight: 1.6 })}>{nenT}</p>}
      {children}
    </section>
  );
}
// Një person, jo një numër: foto, emër, mosha. Klikimi e hap te pema.
function StatPersoni({ st, p, m, teksti, onHapPerson }) {
  const { KART, FONTS, NGJ, NGJ_SOFT, t } = st;
  const foto = photoUrl(p.photo_path);
  const vjeç = m >= 1 ? t("{{n}} years old", { n: m }) : t("under a year old");
  return (
    <button onClick={() => onHapPerson(p.id)} style={{ display: "flex", gap: 12, alignItems: "center", background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}>
      <span style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, overflow: "hidden", background: NGJ_SOFT, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {foto ? <img src={foto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontFamily: FONTS.display, fontSize: 18, color: NGJ }}>{(p.first_name || "?").charAt(0)}</span>}
      </span>
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 600, color: KART.ink }}>{displayName(p)}</span>
        <span style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.inkLight }}>{vjeç} · {teksti}</span>
      </span>
    </button>
  );
}

// LINDJET E SHUMËFISHTA — «palë», jo «çifte» (pronari, 6 shtator 2026: «ata
// nuk janë çifte, janë binjakë»). Grupet ndahen sipas madhësisë: «3 palë
// binjakë», «1 palë trinjakë», … Kthen një rresht për madhësi, binjakët të parët.
function palet(grupet, t) {
  const sipasMadhesise = {};
  grupet.forEach((g) => { sipasMadhesise[g.length] = (sipasMadhesise[g.length] || 0) + 1; });
  return Object.entries(sipasMadhesise).map(([k, n]) => [Number(k), n]).sort((a, b) => a[0] - b[0]).map(([k, n]) => {
    if (k === 2) return n === 1 ? t("One set of twins") : t("{{n}} sets of twins", { n });
    if (k === 3) return n === 1 ? t("One set of triplets") : t("{{n}} sets of triplets", { n });
    if (k === 4) return n === 1 ? t("One set of quadruplets") : t("{{n}} sets of quadruplets", { n });
    if (k === 5) return n === 1 ? t("One set of quintuplets") : t("{{n}} sets of quintuplets", { n });
    return t("{{n}} births of {{k}} children at once", { n, k });
  });
}
// «2 palë binjakë dhe 1 palë trinjakë» — për rrëfimin.
function listaEPaleve(grupet, t) {
  const p = palet(grupet, t);
  if (p.length <= 1) return p[0] || "";
  return p.slice(0, -1).join(", ") + " " + t("and") + " " + p[p.length - 1];
}
function EkraniIStatistikave({ people, marriages, ngjarjeHistorie, cilesimet, KART, FONTS, t, lang, meTitull, onHapPerson, generationsCount, degaEfektive, degaFamiljare, degaVersioni, emriIFamiljes, personiIReferimit }) {
  const [paraardhesi, setParaardhesi] = useState(null); // id-ja e personit → dega e tij
  // PIKËPAMJA (6 shtator 2026). Pronari: «statistikat janë të fokusuara nga
  // pozicioni im si anëtar — s'është keq, po si parazgjedhje duhet të jenë të
  // familjes». Degët («trungu», «me martesë», «kushërinj») maten nga dikush:
  //   familja → nga paraardhësi i parë: gjaku, të ardhurit me martesë, degët
  //             e reja në fis tjetër — pa «kushërinj», sepse ata janë relativë
  //   une     → nga pozicioni i atij që shikon, si te pema
  // RIGJENERIMI ME AI (6 shtator 2026): si te ditëlindjet — teksti standard
  // i dërgohet `family-suggestion` (kind «stats_summary») që ta rishkruajë me
  // stil tjetër pa prekur asnjë numër a emër. Ruhet vetëm sa është hapur ekrani.
  const [permbledhjaAI, setPermbledhjaAI] = useState(null);
  const [permbledhjaDuke, setPermbledhjaDuke] = useState(false);
  const [permbledhjaGabim, setPermbledhjaGabim] = useState("");
  const rigjeneroPermbledhjen = async (teksti) => {
    setPermbledhjaDuke(true); setPermbledhjaGabim("");
    try {
      const { data, error } = await supabase.functions.invoke("family-suggestion", { body: { kind: "stats_summary", text: teksti, lang } });
      const txt = data && data.text ? String(data.text).trim() : "";
      if (error || !txt) setPermbledhjaGabim((data && data.error === "rate_limited") ? t("Too many requests — try again in a minute.") : t("Could not generate right now. Try again in a moment."));
      else setPermbledhjaAI(txt);
    } catch (_e) { setPermbledhjaGabim(t("Could not generate right now. Try again in a moment.")); }
    setPermbledhjaDuke(false);
  };
  const [pikepamja, setPikepamja] = useState(() => {
    try { return localStorage.getItem("statistika.pikepamja") === "une" ? "une" : "familja"; } catch (_e) { return "familja"; }
  });
  const ndryshoPikepamjen = (v) => { setPikepamja(v); setPermbledhjaAI(null); setPermbledhjaGabim(""); try { localStorage.setItem("statistika.pikepamja", v); } catch (_e) { /* pa ruajtje */ } };
  const versioni = degaVersioni + ":" + pikepamja;
  const [hartaBaza, setHartaBaza] = useState("lindja"); // lindja | banimi
  const [hapur, setHapur] = useState({});               // «trego më shumë» për çelës
  const [vendiIHapur, setVendiIHapur] = useState(null);
  const hapja = (k) => !!hapur[k];
  const ndryshoHapjen = (k) => (v) => setHapur((h) => ({ ...h, [k]: v }));

  // NGJYRAT E SERIVE — dy botë, jo një.
  // Mbi kartelë të bardhë: burgund + ar, siç u miratua te mockup-i.
  // Mbi kartelë të errët (stili «e errët» i kartelave, edhe mbi temën e kuqe):
  // burgundi humbet — pronari e pa: «te e kuqja kontrasti shumë i dobët, te e
  // errëta shumë i fortë». Aty seria kryesore është THEKSI i dizajnit (ari,
  // ose ç'ka zgjedhur dizajni), dhe seria e dytë një krem i zbutur — jo një
  // ngjyrë e dytë e fortë mbi të zezë. Numri i madh e ndjek theksin.
  // Pse nga BOJA e jo nga paneli: te stili «e errët» paneli është
  // `accentTint`, një rgba gjysmë-transparente mbi sfondin — `_luma` s'e
  // lexon dot dhe kthente 0.5, pra «i çelët», dhe burgundi dilte mbi të
  // kuqen e errët (pronari e pa dy herë). Boja e tekstit është gjithmonë
  // hex dhe thotë të vërtetën: tekst i çelët = panel i errët.
  const paneliErret = _luma(KART.ink) > 0.5;
  // ⚠ 3 shtator 2026 — DEFEKT I VËRTETË, I RAPORTUAR NGA PRONARI:
  // «nuk dihet sa janë femra sa janë meshkuj» te piramida e brezave.
  //
  // Shkaku: te paneli i çelët seria e parë ishte burgundi i ngulitur #6E1423
  // dhe e dyta `KART.gold` — dhe te `COLORS`, `gold` mban THEKSIN e dizajnit.
  // Te pamja klasike theksi ishte ar, pra dy ngjyra të ndryshme. Te Heirloom
  // theksi është terrakotë: burgundi dhe terrakota janë të dyja të kuqe të
  // errëta, dhe dy seritë u bënë praktikisht një. Një grafik ku dy sasi kanë
  // të njëjtën ngjyrë nuk është grafik i shëmtuar — është grafik i gabuar.
  //
  // Tani, kur ka dizajn: seria e parë merr THEKSIN, e dyta merr ARIN e vërtetë
  // (`bronze`) — saktësisht si te mockup-i, vishnje majtas dhe ar djathtas.
  // Pa dizajn, mbeten numrat klasikë, të paprekur.
  const NGJ = paneliErret ? KART.gold : (PALETA_TIPI.accent || "#6E1423");
  const NGJ2 = paneliErret ? _rgba(KART.onBg, 0.55) : (PALETA_TIPI.bronze || KART.gold);
  const NGJ_ZBEHTE = paneliErret ? _rgba(KART.gold, 0.45) : (PALETA_TIPI.accentBorder || "#C7A2AA");
  const NGJ_SOFT = paneliErret ? _rgba(KART.gold, 0.16) : (PALETA_TIPI.accentSoft || "#F5E6E9");
  const SHINA = paneliErret ? _rgba(KART.onBg, 0.12) : (PALETA_TIPI.lineSoft || PALETA_TIPI.border || "#EEE8DE");
  const DEGA_NGJYRA = {
    // `core` ishte jeshile e ngulitur, dhe e ngulitur do të thotë se dizajni
    // s'kishte zë: Heirloom-i e ka degën vishnje (#7A2E2A) te tokenat e vet dhe
    // prapë dilte jeshile. Tani dizajni fiton; pa dizajn, jeshilja klasike.
    core: KART.branchCore || "#3B7F49", married_in: KART.branchMarriedIn, married_out: KART.rose,
    close_cousin: KART.branchNearCousin, extended: KART.branchFarCousin,
  };
  const st = { KART, FONTS, NGJ, NGJ_SOFT, SHINA, paneliErret, t };
  const DEGA_EMRI = {
    core: t("The trunk"), married_in: t("By Marriage"), married_out: t("New branch in another lineage"),
    close_cousin: t("stats.closeCousins"), extended: t("stats.farCousins"),
  };

  // ── PERSONAT ─────────────────────────────────────────────────────────────
  const gjithe = useMemo(() => Object.values(people), [people]);
  const nivelet = useMemo(() => nivelePerPerson(people), [people]);
  const brezi = (p) => (nivelet[p.id] || 0) + 1;
  // Dega e një personi VJEN NGA I NJËJTI VEND si te pema: `effectiveBranch`,
  // që merr parasysh anulimin e ngjyrës, tri rastet e veçanta, origjinën «e
  // ardhur me martesë» dhe farefisninë e llogaritur. Kolona `p.branch` mbetet
  // vetëm si rezervë për rastin kur prop-i s'vjen (p.sh. te një thirrje e vjetër).
  const degaE = (p) => {
    const f = pikepamja === "une" ? degaEfektive : (degaFamiljare || degaEfektive);
    return f ? f(p) : (p.branch || "core");
  };
  // Dega e paraardhësit: ai vetë + të gjithë pasardhësit, brez pas brezi.
  const all = useMemo(() => {
    if (!paraardhesi || !people[paraardhesi]) return gjithe;
    const brenda = new Set([paraardhesi]);
    let radha = [paraardhesi];
    while (radha.length) {
      const id = radha.shift();
      gjithe.forEach((p) => { if ((p.parent_ids || []).includes(id) && !brenda.has(p.id)) { brenda.add(p.id); radha.push(p.id); } });
    }
    return gjithe.filter((p) => brenda.has(p.id));
  }, [gjithe, people, paraardhesi]);
  const dega = paraardhesi || "all"; // çelës i memo-ve më poshtë

  const sot = new Date();
  const vitiSot = sot.getFullYear();
  const moshaSot = (p) => {
    if (!p.birth_date || p.death_date) return null;
    const d = new Date(p.birth_date);
    if (isNaN(d)) return null;
    let m = vitiSot - d.getFullYear();
    if (sot < new Date(vitiSot, d.getMonth(), d.getDate())) m -= 1;
    return m >= 0 && m <= 125 ? m : null;
  };

  // ── LLOGARITË ────────────────────────────────────────────────────────────
  const S = useMemo(() => {
    const neGrup = new Set(all.map((p) => p.id));
    const men = all.filter((p) => p.gender === "M").length;
    const women = all.filter((p) => p.gender === "F").length;
    const living = all.filter((p) => !p.death_date);
    const brezaLista = [...new Set(all.map(brezi))].sort((a, b) => a - b);

    // Përbërja sipas degës
    const perberja = STATISTIKA_DEGET.map((d) => [d, all.filter((p) => degaE(p) === d).length]).filter(([, n]) => n > 0);

    // Piramida
    const gjiniPerBrez = {};
    all.forEach((p) => {
      const b = brezi(p);
      if (!gjiniPerBrez[b]) gjiniPerBrez[b] = { M: 0, F: 0 };
      if (p.gender === "M") gjiniPerBrez[b].M += 1; else if (p.gender === "F") gjiniPerBrez[b].F += 1;
    });
    const maksAnesor = Math.max(1, ...Object.values(gjiniPerBrez).map((x) => Math.max(x.M, x.F)));

    // Plotësia
    const meP = all.filter((p) => (p.parent_ids || []).some((x) => people[x]));
    const pjesa = (sa, nga) => (nga > 0 ? Math.round((sa / nga) * 100) : 0);
    const plotesia = [
      [t("Birth date"), all.filter((p) => p.birth_date).length, all.length],
      [t("Photo"), all.filter((p) => p.photo_path).length, all.length],
      [t("Birthplace"), all.filter((p) => (p.birth_place || "").trim()).length, all.length],
      [t("Both parents"), meP.filter((p) => (p.parent_ids || []).filter((x) => people[x]).length >= 2).length, meP.length],
    ];
    const plotesiaMes = all.length ? Math.round(plotesia.reduce((n, [, sa, nga]) => n + pjesa(sa, nga), 0) / plotesia.length) : 0;

    // Sot — të gjallët sipas moshës
    const moshat = living.map(moshaSot).filter((m) => m !== null);
    const brezaMosha = [0, 10, 20, 30, 40, 50, 60, 70, 80].map((nga, i, arr) => {
      const deri = arr[i + 1] !== undefined ? arr[i + 1] - 1 : 200;
      return { etiketa: arr[i + 1] !== undefined ? `${nga}–${deri}` : `${nga}+`, sa: moshat.filter((m) => m >= nga && m <= deri).length };
    });
    const maksMosha = Math.max(1, ...brezaMosha.map((x) => x.sa));
    const brezaTeGjalle = new Set(living.map(brezi)).size;
    const moshaMes = moshat.length ? Math.round(moshat.reduce((a, b) => a + b, 0) / moshat.length) : null;

    // Emri që kalon brezin — gjysh/gjyshe → nip/mbesë me të njëjtin emër
    // Krahasimi: vetëm fjala e parë e emrit (pa shënime si «(vdiq)» a emër të
    // dytë), pa shkronja të mëdha dhe pa theksa — «Fazile» = «Fazilë».
    const norm = (s) => String(s || "").trim().toLocaleLowerCase("sq").normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/[\s(]/)[0] || "";
    const trashegimEmri = [];
    all.forEach((p) => {
      const e = norm(p.first_name);
      if (!e) return;
      // 6 shtator 2026 — kërkoheshin vetëm prindërit e prindërve. Gjyshja
      // shpesh s'është te `parent_ids` e prindit (është vetëm bashkëshortja e
      // gjyshit), dhe emri mund të vijë edhe nga stërgjyshi. Tani: çdo
      // paraardhës dy a më shumë breza lart, bashkë me bashkëshortët e tij.
      // Kandidatët, me radhë përparësie: paraardhësit (gjysh, stërgjysh…) dhe
      // bashkëshortët e tyre; pastaj — 6 shtator 2026, «ka edhe ndër breza të
      // tjerë emra të ripërtërirë» — vëllezërit e motrat e prindërve dhe të
      // paraardhësve (xhaxhai, dajë, hallë, teze; xhaxhai i babait…). Prindërit
      // vetë jo: fëmija me emrin e babait s'është «emër që kalon brezin».
      const prinderit = (p.parent_ids || []).map((x) => people[x]).filter(Boolean);
      const kandidatet = [];
      const anesoret = [];
      const femijetENjeriut = (id) => all.filter((q) => (q.parent_ids || []).includes(id));
      let shtresa = prinderit, roje = 0;
      while (shtresa.length && roje++ < 12) {
        const meLart = shtresa.flatMap((pr) => (pr.parent_ids || []).map((x) => people[x]).filter(Boolean));
        meLart.forEach((g) => {
          kandidatet.push(g);
          (g.spouse_ids || []).forEach((sid) => { if (people[sid]) kandidatet.push(people[sid]); });
          // vëllezërit e motrat e shtresës poshtë (fëmijët e këtij paraardhësi që s'janë vetë prindi/paraardhësi)
          femijetENjeriut(g.id).forEach((f) => { if (!shtresa.some((x) => x.id === f.id)) anesoret.push(f); });
        });
        shtresa = meLart;
      }
      const gj = kandidatet.find((g) => g.id !== p.id && norm(g.first_name) === e)
        || anesoret.find((g) => g.id !== p.id && norm(g.first_name) === e);
      if (gj) trashegimEmri.push({ nga: gj, te: p });
    });
    // 6 shtator 2026 — edhe brenda brezit: vëllai a motra që mori emrin e
    // vëllait/motrës më të madh (zakonisht të ndjerë foshnjë). Të njëjtët
    // prindër, i njëjti emër, i lindur më vonë.
    const trashegimVellezer = [];
    const sipasPrinderve = {};
    all.forEach((p) => {
      const e = norm(p.first_name);
      const pr = (p.parent_ids || []).filter((x) => people[x]).sort().join("|");
      if (!e || !pr) return;
      (sipasPrinderve[`${pr}@${e}`] = sipasPrinderve[`${pr}@${e}`] || []).push(p);
    });
    Object.values(sipasPrinderve).filter((g) => g.length > 1).forEach((g) => {
      g.sort((a, b) => String(a.birth_date || "9999").localeCompare(String(b.birth_date || "9999")));
      // Trashëgim është VETËM kur i madhi kishte vdekur para se të lindte i
      // vogli — dy vëllezër me të njëjtin emër, gjallë njëkohësisht, nuk janë
      // trashëgim (pronari). Pa data të sigurta nuk numërohet.
      for (let i = 1; i < g.length; i++) {
        const iMadhi = g[i - 1], iVogli = g[i];
        if (iMadhi.death_date && iVogli.birth_date && String(iMadhi.death_date) < String(iVogli.birth_date)) {
          trashegimVellezer.push({ nga: iMadhi, te: iVogli });
        }
      }
    });
    // 8 shtator 2026 — brezi 1 i pari, si te pema; më i riu më poshtë.
    trashegimEmri.sort((a, b) => brezi(a.te) - brezi(b.te));
    trashegimVellezer.sort((a, b) => brezi(a.te) - brezi(b.te));
    const trashegimtaret = new Set(trashegimEmri.map((x) => x.te.id)).size;
    const emrat = {};
    all.forEach((p) => { const e = (p.first_name || "").trim(); if (e) emrat[e] = (emrat[e] || 0) + 1; });
    const emratQeKthehen = Object.entries(emrat).filter(([, n]) => n > 1).sort((a, b) => b[1] - a[1]);

    // Mosha e prindit te fëmija i parë, sipas brezit të prindit
    const femijetE = {};
    all.forEach((c) => (c.parent_ids || []).forEach((pid) => { if (people[pid]) (femijetE[pid] = femijetE[pid] || []).push(c); }));
    const moshaPrinderPerBrez = {};
    const hendeqet = [];
    Object.entries(femijetE).forEach(([pid, femijet]) => {
      const pr = people[pid]; if (!pr || !neGrup.has(pid)) return;
      const vp = vitiIDates(pr.birth_date); if (!vp) return;
      const vitet = femijet.map((c) => vitiIDates(c.birth_date)).filter(Boolean);
      if (!vitet.length) return;
      const iPari = Math.min(...vitet) - vp;
      if (iPari < 14 || iPari > 60) return;
      const b = brezi(pr);
      if (!moshaPrinderPerBrez[b]) moshaPrinderPerBrez[b] = { shuma: 0, sa: 0 };
      moshaPrinderPerBrez[b].shuma += iPari; moshaPrinderPerBrez[b].sa += 1;
      vitet.forEach((v) => { const h = v - vp; if (h >= 14 && h <= 60) hendeqet.push(h); });
    });
    const hendekuMes = hendeqet.length >= 3 ? Math.round(hendeqet.reduce((a, b) => a + b, 0) / hendeqet.length) : null;
    // Mosha e martesës — nga `marriages.married_on` + datëlindjet
    const moshatMartese = [];
    Object.values(marriages || {}).forEach((m) => {
      const vm = vitiIDates(m.married_on); if (!vm) return;
      [m.person_a, m.person_b].forEach((id) => {
        const p = people[id]; if (!p || !neGrup.has(id)) return;
        const vp = vitiIDates(p.birth_date); if (!vp) return;
        const a = vm - vp; if (a >= 14 && a <= 70) moshatMartese.push(a);
      });
    });
    const martesaMes = moshatMartese.length >= 3 ? Math.round(moshatMartese.reduce((a, b) => a + b, 0) / moshatMartese.length) : null;

    // Sa fëmijë — shpërndarja për çift
    const cifte = {};
    all.forEach((c) => {
      const pr = (c.parent_ids || []).filter((x) => people[x]).sort();
      if (!pr.length) return;
      cifte[pr.join("|")] = (cifte[pr.join("|")] || 0) + 1;
    });
    const shperndarja = {};
    Object.values(cifte).forEach((n) => { shperndarja[n] = (shperndarja[n] || 0) + 1; });
    const shperndarjaLista = Object.entries(shperndarja).map(([n, sa]) => [Number(n), sa]).sort((a, b) => a[0] - b[0]);
    const maksShperndarje = Math.max(1, ...shperndarjaLista.map(([, sa]) => sa));
    const maksFemije = shperndarjaLista.length ? shperndarjaLista[shperndarjaLista.length - 1][0] : 0;

    // Muajt
    const muajt = new Array(12).fill(0);
    // Vetëm datat e sakta: një «1948-01-01» i shkruar për të mbushur fushën do
    // ta ngrinte janarin pa asnjë lindje të vërtetë atje.
    all.forEach((p) => { if (!ditaENjohur(p)) return; const m = (p.birth_date || "").match(/^\d{4}-(\d{2})/); if (m) { const i = Number(m[1]) - 1; if (i >= 0 && i < 12) muajt[i] += 1; } });
    const maksMuaj = Math.max(1, ...muajt);
    const muajiKryesor = muajt.indexOf(Math.max(...muajt));

    // Vendlindjet
    const vendet = {};
    all.forEach((p) => { const v = vendlindjaEShkurter(p.birth_place); if (v) vendet[v] = (vendet[v] || 0) + 1; });
    const vendetRenditur = Object.entries(vendet).sort((a, b) => b[1] - a[1]);
    const vendetGjithsej = vendetRenditur.reduce((n, [, c]) => n + c, 0);
    const vendiIBrezit = (b) => {
      const gj = {};
      all.filter((p) => brezi(p) === b).forEach((p) => { const v = vendlindjaEShkurter(p.birth_place); if (v) gj[v] = (gj[v] || 0) + 1; });
      const r = Object.entries(gj).sort((x, y) => y[1] - x[1])[0];
      return r ? r[0] : null;
    };
    const brezaMeVend = brezaLista.filter((b) => vendiIBrezit(b));

    // Harta — vendlindja dhe banimi, të dyja në të njëjtën formë
    const numeroShtetet = (fusha) => {
      const counts = {}, byNumeric = {}, label = {}, unmatched = [];
      all.forEach((p) => {
        const raw = (p[fusha] || "").trim(); if (!raw) return;
        const lbl = displayCountry(raw, lang) || raw;
        counts[lbl] = (counts[lbl] || 0) + 1;
        const num = countryToNumeric(raw, lbl);
        if (num) { byNumeric[num] = (byNumeric[num] || 0) + 1; label[String(num)] = lbl; }
        else if (!unmatched.includes(lbl)) unmatched.push(lbl);
      });
      return { lista: Object.entries(counts).sort((a, b) => b[1] - a[1]), byNumeric, label, unmatched };
    };
    const hartaLindja = numeroShtetet("birth_country");
    const hartaBanimi = numeroShtetet("residence_country");
    const kaBanim = hartaBanimi.lista.length > 0;
    // Migrimi — jeton në shtet tjetër nga ai ku lindi
    const rrjedhat = {};
    let migruar = 0;
    all.forEach((p) => {
      const a = (p.birth_country || "").trim(), b = (p.residence_country || "").trim();
      if (!a || !b) return;
      const la = displayCountry(a, lang) || a, lb = displayCountry(b, lang) || b;
      if (la === lb) return;
      migruar += 1;
      const k = `${la} → ${lb}`; rrjedhat[k] = (rrjedhat[k] || 0) + 1;
    });
    const rrjedhatLista = Object.entries(rrjedhat).sort((a, b) => b[1] - a[1]);

    // Jeta
    const jetaPerBrez = {}, jetaGjini = { M: [], F: [] };
    all.forEach((p) => {
      const m = moshaNeVdekje(p); if (m === null) return;
      const b = brezi(p);
      if (!jetaPerBrez[b]) jetaPerBrez[b] = { shuma: 0, sa: 0 };
      jetaPerBrez[b].shuma += m; jetaPerBrez[b].sa += 1;
      if (p.gender === "M" || p.gender === "F") jetaGjini[p.gender].push(m);
    });
    const mesJete = (arr) => (arr.length >= 3 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null);
    const gjalleMeMoshe = living.map((p) => ({ p, m: moshaSot(p) })).filter((x) => x.m !== null);
    const meEMoshuara = gjalleMeMoshe.length ? gjalleMeMoshe.reduce((a, b) => (b.m > a.m ? b : a)) : null;
    const meIRiu = gjalleMeMoshe.length ? gjalleMeMoshe.reduce((a, b) => (b.m < a.m ? b : a)) : null;

    // Arkivi — sa persona u shtuan, muaj pas muaji (6 të fundit)
    const muajtArkivi = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(vitiSot, sot.getMonth() - i, 1);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      muajtArkivi.push({ k, muaji: d.getMonth(), viti: d.getFullYear(), sa: 0 });
    }
    all.forEach((p) => { const k = (p.created_at || "").slice(0, 7); const r = muajtArkivi.find((x) => x.k === k); if (r) r.sa += 1; });
    const maksArkivi = Math.max(1, ...muajtArkivi.map((x) => x.sa));
    const shtuarGjithsej6 = muajtArkivi.reduce((n, x) => n + x.sa, 0);
    const brezatEVjeter = brezaLista.slice(0, Math.max(1, Math.ceil(brezaLista.length / 2)));
    const teVjetrit = all.filter((p) => brezatEVjeter.includes(brezi(p)));
    const fotoPerPersonVjeter = teVjetrit.length ? (teVjetrit.filter((p) => p.photo_path).length / teVjetrit.length) : null;

    return { men, women, living, brezaLista, perberja, gjiniPerBrez, maksAnesor, plotesia, plotesiaMes, pjesa,
      brezaMosha, maksMosha, brezaTeGjalle, moshaMes, moshat, trashegimEmri, trashegimVellezer, trashegimtaret, emratQeKthehen,
      moshaPrinderPerBrez, hendekuMes, martesaMes, moshatMartese, shperndarjaLista, maksShperndarje, maksFemije, cifteSa: Object.keys(cifte).length,
      muajt, maksMuaj, muajiKryesor, vendetRenditur, vendetGjithsej, vendiIBrezit, brezaMeVend,
      hartaLindja, hartaBanimi, kaBanim, migruar, rrjedhatLista, jetaPerBrez, jetaGjini, mesJete, meEMoshuara, meIRiu,
      muajtArkivi, maksArkivi, shtuarGjithsej6, brezatEVjeter, fotoPerPersonVjeter };
  }, [all, people, marriages, dega, lang, versioni]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── CILËSIMET NGA ADMIN CENTER ───────────────────────────────────────────
  const cil = (k) => (cilesimet && cilesimet[k]) || null;
  const radha = useMemo(() => {
    const lista = STATISTIKA_RADHA_PARAZGJEDHUR.map((k, i) => ({ k, r: cil(k) && cil(k).radha != null ? cil(k).radha : (i + 1) * 10 }));
    return lista.sort((a, b) => a.r - b.r).map((x) => x.k);
  }, [cilesimet]); // eslint-disable-line react-hooks/exhaustive-deps
  const dukshem = (k) => (cil(k) ? cil(k).i_dukshem !== false : k !== "profesioni");

  // ── COPAT E PAMJES ───────────────────────────────────────────────────────
  const Eyebrow = ({ children }) => (
    <span style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: KART.inkLight }}>{children}</span>
  );
  const Nen = ({ children }) => (
    <p style={{ fontFamily: FONTS.body, fontSize: 14.5, color: KART.inkLight, margin: 0, maxWidth: "60ch", lineHeight: 1.5 }}>{children}</p>
  );
  // Një kartelë = një pyetje. Titulli i Admin Center fiton mbi të llogariturin.
  // `StatKarta` rri në nivel moduli (jo e përkufizuar këtu) që React të mos e
  // rimontojë çdo herë që ndryshon një filtër — brenda saj jeton harta, dhe
  // një hartë e rimontuar harron zoom-in.
  const kk = (celesi) => {
    const c = cil(celesi);
    return { st, eyebrow: (c && c.titulli) || t(`stat.${celesi}`), nenAdmin: c && c.nentitulli ? c.nentitulli : null };
  };

  // ── KUSH QËNDRON PAS NUMRIT ───────────────────────────────────────────────
  // Kërkesë e pronarit (5 shtator 2026): «jam kurioz ta di kush është ai një
  // kushëri i largët, po si ta gjej. Do doja që te statistikat, kur prek
  // statistikën, të shfaqen personat për të cilët po flet — pastaj po e preke
  // prapë, fshehen.»
  //
  // Kishte të drejtë për diçka më të thellë se sa dukej: një numër «1» pa
  // mënyrë për ta arritur atë person është një enigmë, jo një statistikë. Dhe
  // te «Sa e plotë është pema» kjo është veçanërisht e rëndë — ajo kartelë të
  // thotë ÇFARË mungon dhe deri sot s'të thoshte KE KË.
  //
  // `S` mban numra; kjo hartë mban NJERËZIT. E ndarë me qëllim: shtimi i
  // listave brenda `S` do të kërkonte prekjen e çdo kartele që e lexon atë.
  const KUSH = useMemo(() => {
    const m = {};
    m["hero:gjithsej"] = all;
    m["hero:gjalle"] = all.filter((p) => !p.death_date);
    m["hero:M"] = all.filter((p) => p.gender === "M");
    m["hero:F"] = all.filter((p) => p.gender === "F");
    STATISTIKA_DEGET.forEach((d) => { m[`dega:${d}`] = all.filter((p) => degaE(p) === d); });
    all.forEach((p) => {
      const b = brezi(p);
      const g = p.gender === "M" ? "M" : p.gender === "F" ? "F" : null;
      if (!g) return;
      (m[`brez:${b}:${g}`] = m[`brez:${b}:${g}`] || []).push(p);
    });
    // Plotësia lexohet MBRAPSHT: kartela tregon sa e kanë, po puna që të mbetet
    // është te ata që NUK e kanë. Prandaj këtu ruhen mungesat.
    m["mungon:birth_date"] = all.filter((p) => !p.birth_date);
    m["mungon:photo"] = all.filter((p) => !p.photo_path);
    m["mungon:birth_place"] = all.filter((p) => !(p.birth_place || "").trim());
    m["mungon:prinder"] = all.filter((p) => {
      const gjalle = (p.parent_ids || []).filter((x) => people[x]);
      return gjalle.length === 1; // një prind i njohur — i dyti mungon
    });

    // «Sa fëmijë» numëron ÇIFTE, jo persona. Prandaj klikimi tregon prindërit e
    // atyre çifteve — ata janë subjekti i rreshtit, jo fëmijët.
    const ciftetE = {};
    all.forEach((c) => {
      const pr = (c.parent_ids || []).filter((x) => people[x]).sort();
      if (!pr.length) return;
      (ciftetE[pr.join("|")] = ciftetE[pr.join("|")] || []).push(c);
    });
    Object.entries(ciftetE).forEach(([celesi, femijet]) => {
      const k = `femije:${femijet.length}`;
      const prinderit = celesi.split("|").map((id) => people[id]).filter(Boolean);
      m[k] = (m[k] || []).concat(prinderit);
    });

    all.forEach((p) => {
      const mm = ditaENjohur(p) ? (p.birth_date || "").match(/^\d{4}-(\d{2})/) : null;
      if (mm) { const i = Number(mm[1]) - 1; if (i >= 0 && i < 12) (m[`muaj:${i}`] = m[`muaj:${i}`] || []).push(p); }
      const v = vendlindjaEShkurter(p.birth_place);
      if (v) (m[`vend:${v}`] = m[`vend:${v}`] || []).push(p);
      const a = (p.birth_country || "").trim(), b = (p.residence_country || "").trim();
      if (a && b) {
        const la = displayCountry(a, lang) || a, lb = displayCountry(b, lang) || b;
        if (la !== lb) {
          (m["migrim:gjithe"] = m["migrim:gjithe"] || []).push(p);
          const k = `rrjedha:${la} → ${lb}`;
          (m[k] = m[k] || []).push(p);
        }
      }
    });
    return m;
  }, [all, people, versioni, lang]);

  // ── KUR NJË LISTË E RRAFSHTË NUK MJAFTON ──────────────────────────────────
  // Pronari (5 shtator 2026): «te Sa fëmijë do doja që çiftet të jenë të
  // kapura — emri i dy personave të çiftit në një rubrikë, se pastaj nuk dihet
  // kush janë çiftet.» Kishte të drejtë: gjashtë emra në një radhë nuk thonë se
  // cilët dy shkojnë bashkë. Dhe: «te muajt, nëse dy veta e kanë ditëlindjen të
  // njëjtën ditë, le ta kenë ngjyrën ndryshe nga standardi.»
  //
  // Të dyja janë e njëjta nevojë: grupim. Prandaj një hartë e dytë, `GRUPET`,
  // që ku ekziston zë vendin e listës së rrafshët.
  const GRUPET = useMemo(() => {
    const g = {};

    // «Sa fëmijë» — një grup për ÇDO ÇIFT, jo një listë prindërish.
    const ciftetE = {};
    all.forEach((c) => {
      const pr = (c.parent_ids || []).filter((x) => people[x]).sort();
      if (!pr.length) return;
      (ciftetE[pr.join("|")] = ciftetE[pr.join("|")] || []).push(c);
    });
    Object.entries(ciftetE).forEach(([celesi, femijet]) => {
      const k = `femije:${femijet.length}`;
      const prinderit = celesi.split("|").map((id) => people[id]).filter(Boolean);
      (g[k] = g[k] || []).push({ persona: prinderit, bashke: true });
    });

    // «Lindjet sipas muajve» — një grup për ÇDO DITË. Dita që e ndajnë dy a më
    // shumë veta shënohet: ajo është gjetja, jo dita bosh.
    const ditet = {};
    all.forEach((p) => {
      if (!ditaENjohur(p)) return;
      const mm = String(p.birth_date || "").match(/^\d{4}-(\d{2})-(\d{2})/);
      if (!mm) return;
      const muaji = Number(mm[1]) - 1, dita = Number(mm[2]);
      if (muaji < 0 || muaji > 11) return;
      (ditet[`${muaji}:${dita}`] = ditet[`${muaji}:${dita}`] || []).push(p);
    });
    Object.entries(ditet).forEach(([celesi, lista]) => {
      const [muaji, dita] = celesi.split(":").map(Number);
      (g[`muaj:${muaji}`] = g[`muaj:${muaji}`] || []).push({
        titulli: `${dita}`, persona: lista, theksuar: lista.length > 1, rendi: dita,
      });
    });
    Object.keys(g).forEach((k) => {
      if (k.startsWith("muaj:")) g[k].sort((a, b) => a.rendi - b.rendi);
    });
    return g;
  }, [all, people]);

  // Muajt që përmbajnë të paktën një ditë të përbashkët — për shenjën mbi shufër.
  const muajtMeDitePerbashketa = useMemo(() => {
    const set = new Set();
    Object.entries(GRUPET).forEach(([k, grupet]) => {
      if (!k.startsWith("muaj:")) return;
      if (grupet.some((x) => x.theksuar)) set.add(Number(k.slice(5)));
    });
    return set;
  }, [GRUPET]);

  // ── DY STATISTIKA TË REJA (kërkesë e pronarit, 5 shtator 2026) ────────────
  //
  // 1 · «Personi që ka jetuar më së gjati në familje — edhe këtu mund të jetë
  //     lista top 10.» Renditen VETËM ata që kanë të dyja datat: një jetë e
  //     matur pa datëvdekje nuk është jetë e mbaruar, dhe përzierja e të
  //     gjallëve me të ndjerët do të krahasonte dy gjëra të ndryshme. Të
  //     gjallët e kanë kartelën e vet («më e moshuara sot»).
  //
  // 2 · «Kur dy a më shumë persona kanë datën e njëjtë të lindjes dhe janë nga
  //     dy prindër të njëjtë, ata janë binjakë apo trinjakë.»
  //     Të dyja kushtet duhen bashkë: e njëjta datë pa të njëjtët prindër është
  //     rastësi (te 131 veta ndodh shpesh), dhe të njëjtët prindër pa të
  //     njëjtën datë janë thjesht vëllezër. Kërkohet data e PLOTË — dy persona
  //     me vetëm vitin «1948» nuk janë binjakë, janë dy të panjohura.
  const EKSTRA = useMemo(() => {
    const jetegjatet = all
      .map((p) => ({ p, m: moshaNeVdekje(p) }))
      .filter((x) => x.m !== null)
      .sort((a, b) => b.m - a.m)
      .slice(0, 10);

    const grupet = {};
    all.forEach((p) => {
      const d = String(p.birth_date || "");
      // Data e plotë DHE e saktë. Dy paraardhës me «1-1-1900» të shkruar për të
      // mbushur fushën do të dilnin binjakë — pikërisht gabimi që s'duhet.
      if (!ditaENjohur(p)) return;
      const pr = (p.parent_ids || []).filter((x) => people[x]).sort();
      if (!pr.length) return;                           // pa prindër s'ka si të vërtetohet
      (grupet[`${pr.join("|")}@${d}`] = grupet[`${pr.join("|")}@${d}`] || []).push(p);
    });
    const binjaket = Object.values(grupet).filter((g) => g.length >= 2)
      .sort((a, b) => (b.length - a.length) || String(b[0].birth_date).localeCompare(String(a[0].birth_date)));
    return { jetegjatet, binjaket };
  }, [all, people]);

  const [zbuluar, setZbuluar] = useState(null); // { celesi, titulli }
  const prekja = (celesi, titulli) => ({
    onClick: () => setZbuluar((z) => (z && z.celesi === celesi ? null : { celesi, titulli })),
    title: titulli,
    style: { background: "transparent", border: "none", padding: 0, textAlign: "inherit", cursor: "pointer", font: "inherit", color: "inherit" },
  });
  const etiketatStat = useMemo(() => ndertoEtiketatDalluese(people), [people]);

  // Lista shfaqet BRENDA kartelës që u prek, jo si dritare: pyetja «kush?» ka
  // kuptim vetëm ngjitur me numrin që e ngriti.
  const KushZbuluar = ({ prefiksi }) => {
    if (!zbuluar || !String(zbuluar.celesi).startsWith(prefiksi)) return null;
    const lista = KUSH[zbuluar.celesi] || [];
    const grupet = GRUPET[zbuluar.celesi] || null;
    const sa = grupet ? grupet.reduce((n, g) => n + g.persona.length, 0) : lista.length;
    return (
      <div style={{ marginTop: 12, borderTop: `1px solid ${paneliErret ? _rgba(KART.onBg, 0.1) : "#E6DFD3"}`, paddingTop: 10 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: KART.inkLight }}>
            {zbuluar.titulli}
          </span>
          <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: KART.inkLight, fontVariantNumeric: "tabular-nums" }}>{sa}</span>
          <button type="button" onClick={() => setZbuluar(null)}
            style={{ marginLeft: "auto", background: "transparent", border: "none", cursor: "pointer", fontFamily: FONTS.body, fontSize: 13.5, color: KART.inkLight, padding: 0 }}>
            {t("Hide")}
          </button>
        </div>
        {grupet ? (
          /* Grupimi: çiftet lidhen me «×», ditët me numrin e ditës. Dita që e
             ndajnë dy a më shumë veta merr ngjyrën e theksit — pikërisht ajo
             është gjetja, jo dita ku është vetëm një person. */
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto", overflowX: "hidden" }}>
            {grupet.map((gr, gi) => (
              <div key={gi} style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                {gr.titulli && (
                  <span style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, minWidth: 22, textAlign: "right",
                                 color: gr.theksuar ? NGJ : KART.inkLight, fontVariantNumeric: "tabular-nums" }}>{gr.titulli}</span>
                )}
                {gr.persona.map((p, pi) => (
                  <React.Fragment key={p.id}>
                    {gr.bashke && pi > 0 && <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: KART.inkLight }}>×</span>}
                    <button type="button" onClick={() => onHapPerson && onHapPerson(p.id)}
                      style={{ fontFamily: FONTS.body, fontSize: 13.5,
                               color: gr.theksuar ? KART.ink : KART.ink,
                               background: gr.theksuar ? NGJ_SOFT : "transparent",
                               border: `1px solid ${gr.theksuar ? NGJ : SHINA}`,
                               borderRadius: 999, padding: "4px 10px", cursor: "pointer" }}>
                      {emriDallues(p, etiketatStat)}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        ) : lista.length === 0 ? (
          <p style={{ fontFamily: FONTS.body, fontSize: 14, color: KART.inkLight, margin: 0 }}>{t("Nobody here.")}</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 240, overflowY: "auto", overflowX: "hidden" }}>
            {lista.slice(0, 400).map((p) => (
              <button key={p.id} type="button" onClick={() => onHapPerson && onHapPerson(p.id)}
                style={{ fontFamily: FONTS.body, fontSize: 13.5, color: KART.ink, background: NGJ_SOFT,
                         border: `1px solid ${SHINA}`, borderRadius: 999, padding: "4px 10px", cursor: "pointer" }}>
                {emriDallues(p, etiketatStat)}
              </button>
            ))}
            {lista.length > 400 && (
              <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: KART.inkLight, alignSelf: "center" }}>
                {t("+{{n}} more", { n: lista.length - 400 })}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };
  const Numri = ({ children, i_vogel }) => (
    <span style={{ fontFamily: FONTS.display, fontSize: i_vogel ? 26 : 42, fontWeight: 600, lineHeight: 1, color: NGJ, fontVariantNumeric: "tabular-nums" }}>{children}</span>
  );
  const Pika = ({ vlera, teksti }) => (
    <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
      <Numri>{vlera}</Numri>
      <span style={{ fontFamily: FONTS.body, fontSize: 16, color: KART.inkLight, maxWidth: "22ch", lineHeight: 1.4 }}>{teksti}</span>
    </div>
  );
  const Dy = ({ children }) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 4 }}>{children}</div>
  );
  // Rreshti me shirit: etiketë · shirit · vlerë (+ shënim i vogël).
  const Rreshti = ({ etiketa, pjesa, vlera, shenim, zbehte }) => (
    <>
      <span style={{ fontFamily: FONTS.body, fontSize: 16, color: KART.inkLight, whiteSpace: "nowrap", fontStyle: zbehte ? "italic" : "normal" }}>{etiketa}</span>
      <div style={{ height: 10, background: SHINA, borderRadius: 5, overflow: "hidden" }}>
        <div style={{ width: `${Math.max(2, Math.min(100, pjesa))}%`, height: "100%", borderRadius: 5, background: NGJ, opacity: zbehte ? 0.35 : 1 }} />
      </div>
      <span style={{ fontFamily: FONTS.body, fontSize: 16, fontWeight: 500, color: KART.ink, whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {vlera}{shenim && <span style={{ color: KART.inkLight, fontWeight: 400, marginLeft: 6 }}>{shenim}</span>}
      </span>
    </>
  );
  const Rreshtat = ({ children }) => (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "8px 12px", alignItems: "center", marginTop: 4 }}>{children}</div>
  );
  const Shenimi = ({ children }) => (
    <p style={{ fontFamily: FONTS.body, fontSize: 14.5, color: KART.inkLight, margin: 0, borderTop: `1px solid ${paneliErret ? _rgba(KART.onBg, 0.1) : "#E6DFD3"}`, paddingTop: 10 }}>{children}</p>
  );
  const emriMuajit = (i) => t(STATISTIKA_MUAJT[i]);
  const emriMuajitShkurt = (i) => emriMuajit(i).slice(0, 3);
  const meBrez = (n) => t("Generation {{n}}", { n });

  // ── KARTELAT ─────────────────────────────────────────────────────────────
  const kartelat = {
    hero: () => (
      <StatKarta {...kk("hero")}>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "16px 28px", alignItems: "end" }}>
          <div>
            {/* Vija ari nën numrin e madh — nga mockup-i. E vetmja gjë që e
                dallon një numër nga një titull kur të dy janë serif e të mëdhenj. */}
            <button type="button" {...prekja("hero:gjithsej", t("All members"))}>
              <span style={{ display: "inline-block", borderBottom: pamja("statistika", "heirloom") ? `3px solid ${PALETA_TIPI.spouseLine || NGJ}` : "none", paddingBottom: pamja("statistika", "heirloom") ? 4 : 0 }}>
                <span style={{ fontFamily: FONTS.display, fontSize: 60, lineHeight: 0.95, fontWeight: 600, color: NGJ, fontVariantNumeric: "tabular-nums" }}>{all.length}</span>
              </span>
            </button>
            <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 15.5, fontWeight: 500, color: KART.inkLight, marginTop: 8 }}>
              {t("members in {{n}} generations", { n: S.brezaLista.length })}
            </span>
          </div>
          {/* ⚠ 4 shtator 2026 — «si numrat, sa gra edhe sa burra».
              Meshkujt dhe femrat shfaqeshin këtu si dy numra gri pranë njëri-
              tjetrit, dhe pikërisht të njëjtat dy sasi vizatoheshin më poshtë
              te piramida me dy ngjyra. Pra e njëjta gjë kishte dy gjuhë në të
              njëjtën faqe: lexuesi duhej ta mësonte dy herë cila është cila.
              Tani numri mban SHENJËN e vet — pikën me ngjyrën e serisë — dhe
              legjenda e piramidës nuk shpjegon më diçka të re, thjesht e
              përsërit të njëjtën. Ngjyra nuk mbetet kurrë e vetme: etiketa
              është aty gjithmonë, prandaj kjo lexohet edhe pa dalluar ngjyrat. */}
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {[[S.living.length, t("stats.living"), null, "hero:gjalle"],
              [S.men, t("stats.men"), NGJ, "hero:M"],
              [S.women, t("stats.women"), NGJ2, "hero:F"],
              [`${S.plotesiaMes}%`, t("complete"), null, null]].map(([v, e, ngjyra, celesi]) => {
              const trupi = (
                <>
                  <span style={{ fontFamily: FONTS.display, fontSize: 24, fontWeight: 600, lineHeight: 1.1, color: KART.ink, fontVariantNumeric: "tabular-nums" }}>{v}</span>
                  <span style={{ fontFamily: FONTS.body, fontSize: 14, color: KART.inkLight, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {ngjyra && <i style={{ width: 8, height: 8, borderRadius: 3, background: ngjyra, display: "inline-block", flexShrink: 0 }} />}
                    {e}
                  </span>
                </>
              );
              /* «e plotë» është përqindje, jo grup njerëzish — nuk ka kë të
                 tregojë, ndaj mbetet tekst i thjeshtë. Kartela «Sa e plotë»
                 më poshtë e mban atë pyetje, rresht për rresht. */
              if (!celesi) return <div key={e} style={{ display: "flex", flexDirection: "column" }}>{trupi}</div>;
              return (
                <button key={e} type="button" {...prekja(celesi, e)}
                  style={{ ...prekja(celesi, e).style, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  {trupi}
                </button>
              );
            })}
          </div>
        </div>
        <KushZbuluar prefiksi="hero:" />
      </StatKarta>
    ),
    perberja: () => S.perberja.length > 0 && (
      <StatKarta {...kk("perberja")}>
        <div style={{ display: "flex", height: 22, borderRadius: 6, overflow: "hidden", gap: 2, background: SHINA }}
             title={S.perberja.map(([d, n]) => `${DEGA_EMRI[d]} ${n}`).join(" · ")}>
          {S.perberja.map(([d, n]) => <i key={d} style={{ display: "block", height: "100%", width: `${(n / all.length) * 100}%`, background: DEGA_NGJYRA[d] }} />)}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
          {S.perberja.map(([d, n]) => (
            <button key={d} type="button" {...prekja(`dega:${d}`, DEGA_EMRI[d])}
              style={{ ...prekja(`dega:${d}`, DEGA_EMRI[d]).style, fontFamily: FONTS.body, fontSize: 14, color: KART.inkLight, display: "inline-flex", alignItems: "center", gap: 7 }}>
              <i style={{ width: 10, height: 10, borderRadius: 3, background: DEGA_NGJYRA[d], display: "inline-block" }} />
              {DEGA_EMRI[d]}<b style={{ color: KART.ink, fontWeight: 600 }}>{n}</b>
            </button>
          ))}
        </div>
        <KushZbuluar prefiksi="dega:" />
      </StatKarta>
    ),
    brezat: () => S.brezaLista.length > 0 && (
      <StatKarta {...kk("brezat")} titulli={t("Men on the left, women on the right")} nen={t("Where the shape widens, the family grew.")}>
        {/* ⚠ 4 shtator 2026 — «nuk ka numra sa gra sa burra janë në brezin e 6
            edhe te 5. Ndoshta kish me qenë një fjalë Brezi mbi këta numra, edhe
            të ndahet o me vijë o me pikë sa meshkuj sa femra.»
            Kishte të drejtë dhe mangësia ishte e rëndë: një piramidë pa numra
            tregon FORMËN po jo SASINË — sheh që një brez është më i gjerë, po
            s'e di me sa. Dhe kolona e mesit mbante numra pa thënë kurrë se ç'janë.
            Tri gjëra u shtuan: fjala «Brezi» mbi atë kolonë, numri te fundi i
            SECILËS shufër, dhe dy vija flokëhollë që e ndajnë mesin nga anët.
            Numrat rrinë te skaji i JASHTËM i shufrës, jo te qendra: aty ku
            mbaron shufra, syri tashmë është. */}
        <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 38px 1fr", gap: "6px 10px", alignItems: "center", marginTop: 4 }}>
          {/* Boshti — dy vija të vazhdueshme, jo kufij për rresht (ata do të
              këputeshin te çdo hapësirë mes rreshtave). */}
          <span aria-hidden style={{ position: "absolute", top: 0, bottom: 0, left: "calc(50% - 19px)", width: 1, background: SHINA }} />
          <span aria-hidden style={{ position: "absolute", top: 0, bottom: 0, left: "calc(50% + 19px)", width: 1, background: SHINA }} />
          <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NGJ, textAlign: "left" }}>{t("stats.men")}</span>
          <span style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: KART.inkLight, textAlign: "center" }}>{t("Generation")}</span>
          <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NGJ2, textAlign: "right" }}>{t("stats.women")}</span>
          {S.brezaLista.map((b) => {   // brezi 1 sipër, si te pema (8 shtator 2026)
            const x = S.gjiniPerBrez[b] || { M: 0, F: 0 };
            const numri = (v, ngjyra) => (
              <span style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, color: v > 0 ? ngjyra : KART.inkLight,
                             opacity: v > 0 ? 1 : 0.5, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{v}</span>
            );
            return (
              <React.Fragment key={b}>
                {/* Numri rri te një kolonë e vetën, jo ngjitur me majën e
                    shufrës. Dy arsye: shufra më e gjatë zë 100% të qelizës,
                    pra një numër pranë saj do të dilte jashtë; dhe numrat e
                    rreshtuar në kolonë lexohen poshtë-lart si listë, gjë që
                    një numër që kërcen sipas gjatësisë s'e lejon. */}
                <button type="button" {...prekja(`brez:${b}:M`, `${meBrez(b)} · ${t("stats.men")}`)}
                  style={{ ...prekja(`brez:${b}:M`, "").style, display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                  <span style={{ width: 22, textAlign: "right", flexShrink: 0 }}>{numri(x.M, NGJ)}</span>
                  <span style={{ flex: 1, display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                    <span style={{ height: 12, borderRadius: 6, background: NGJ, width: `${(x.M / S.maksAnesor) * 100}%` }} title={`${meBrez(b)} · ${t("stats.men")} ${x.M}`} />
                  </span>
                </button>
                <span style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: KART.inkLight, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{b}</span>
                <button type="button" {...prekja(`brez:${b}:F`, `${meBrez(b)} · ${t("stats.women")}`)}
                  style={{ ...prekja(`brez:${b}:F`, "").style, display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                  <span style={{ flex: 1, display: "flex", minWidth: 0 }}>
                    <span style={{ height: 12, borderRadius: 6, background: NGJ2, width: `${(x.F / S.maksAnesor) * 100}%` }} title={`${meBrez(b)} · ${t("stats.women")} ${x.F}`} />
                  </span>
                  <span style={{ width: 22, textAlign: "left", flexShrink: 0 }}>{numri(x.F, NGJ2)}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 18, fontFamily: FONTS.body, fontSize: 14, color: KART.inkLight }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: NGJ }} />{t("stats.men")}<b style={{ color: KART.ink }}>{S.men}</b></span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: NGJ2 }} />{t("stats.women")}<b style={{ color: KART.ink }}>{S.women}</b></span>
        </div>
        <KushZbuluar prefiksi="brez:" />
      </StatKarta>
    ),
    plotesia: () => all.length > 0 && (
      <StatKarta {...kk("plotesia")} titulli={t("The only statistic that tells you what to do next")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center", marginTop: 4 }}>
          <div style={{ position: "relative", width: 96, height: 96, flexShrink: 0 }}>
            <svg viewBox="0 0 96 96" width="96" height="96" role="img" aria-label={`${S.plotesiaMes}%`}>
              <circle cx="48" cy="48" r="40" fill="none" stroke={SHINA} strokeWidth="10" />
              <circle cx="48" cy="48" r="40" fill="none" stroke={NGJ} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${(S.plotesiaMes / 100) * 251.3} 251.3`} transform="rotate(-90 48 48)" />
            </svg>
            <b style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.display, fontSize: 24, fontWeight: 600, color: KART.ink }}>{S.plotesiaMes}%</b>
          </div>
          <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <Rreshtat>
            {/* Rreshti mban ÇFARË mungon; klikimi tregon KE KË. Kjo kartela e
                thotë vetë se «tregon çfarë të bësh më pas» — pa emrat, ajo
                fjali ishte gjysmë e mbajtur. */}
            {S.plotesia.map(([e, sa, nga], i) => {
              const celesi = ["mungon:birth_date", "mungon:photo", "mungon:birth_place", "mungon:prinder"][i];
              const munguar = nga - sa;
              return (
                <Rreshti key={e} etiketa={e} pjesa={S.pjesa(sa, nga)} vlera={`${S.pjesa(sa, nga)}%`}
                  shenim={munguar > 0
                    ? (<button type="button" {...prekja(celesi, `${e} — ${t("{{sa}} missing", { sa: munguar })}`)}
                         style={{ ...prekja(celesi, "").style, textDecoration: "underline", textUnderlineOffset: 3 }}>
                         {t("{{sa}} missing", { sa: munguar })}
                       </button>)
                    : t("complete")} />
              );
            })}
          </Rreshtat>
          </div>
        </div>
        <KushZbuluar prefiksi="mungon:" />
      </StatKarta>
    ),
    sot: () => S.moshat.length > 0 && (
      <StatKarta {...kk("sot")} titulli={t("The shape of the family as it is, not as it was")} nen={t("The generation pyramid shows history. This shows the living, by age.")}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(9, 1fr)", gap: 5, alignItems: "end", height: 120, marginTop: 6 }}>
          {S.brezaMosha.map((x) => (
            <div key={x.etiketa} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", gap: 5 }} title={`${x.etiketa}: ${x.sa}`}>
              <span style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, color: KART.ink, opacity: x.sa ? 1 : 0.35, fontVariantNumeric: "tabular-nums" }}>{x.sa}</span>
              <i style={{ display: "block", width: "100%", height: `${Math.max(2, (x.sa / S.maksMosha) * 72)}px`, background: NGJ, opacity: 0.85, borderRadius: "4px 4px 0 0" }} />
              <span style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.inkLight, whiteSpace: "nowrap" }}>{x.etiketa}</span>
            </div>
          ))}
        </div>
        <Dy>
          <Pika vlera={S.brezaTeGjalle} teksti={t("generations alive at the same time")} />
          {S.moshaMes !== null && <Pika vlera={S.moshaMes} teksti={t("average age of the living")} />}
        </Dy>
        <Shenimi>{t("Counted from the birth dates of {{sa}} living people.", { sa: S.moshat.length })}</Shenimi>
      </StatKarta>
    ),
    emri_brez: () => (S.trashegimEmri.length > 0 || S.trashegimVellezer.length > 0 || S.emratQeKthehen.length > 0) && (
      <StatKarta {...kk("emri_brez")}
        titulli={S.trashegimEmri.length > 0 ? t("{{sa}} children carry a grandparent's name", { sa: S.trashegimtaret }) : t("Names that come back")}
        nen={S.trashegimEmri.length > 0 ? t("A written tradition: one generation's name in the next, two steps down.") : t("In an Albanian family a repeated name is not a coincidence — it is a written tradition.")}>
        {S.trashegimEmri.length > 0 ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "6px 10px", alignItems: "center", marginTop: 4 }}>
              {(hapja("emri") ? S.trashegimEmri : S.trashegimEmri.slice(0, 6)).map(({ nga, te }) => (
                <React.Fragment key={nga.id + ">" + te.id}>
                  <button onClick={() => onHapPerson(nga.id)} style={{ textAlign: "right", fontFamily: FONTS.body, fontSize: 15, color: KART.inkLight, background: "transparent", border: "none", padding: 0, cursor: "pointer" }}>
                    {nga.first_name} · {meBrez(brezi(nga))}
                    {lifespan(nga) && <span style={{ display: "block", fontSize: 13, opacity: 0.8, fontVariantNumeric: "tabular-nums" }}>{lifespan(nga)}</span>}
                  </button>
                  <span style={{ color: KART.inkLight }}>→</span>
                  {/* 6 shtator 2026: vitet lindje–vdekje nën emër — dy motra me të
                      njëjtin emër (njëra vdiq foshnjë) ndryshe s'dallohen. */}
                  <button onClick={() => onHapPerson(te.id)} style={{ textAlign: "left", fontFamily: FONTS.body, fontSize: 15, fontWeight: 600, color: KART.ink, background: "transparent", border: "none", padding: 0, cursor: "pointer" }}>
                    {te.first_name} · {meBrez(brezi(te))}
                    {lifespan(te) && <span style={{ display: "block", fontSize: 13, fontWeight: 400, color: KART.inkLight, fontVariantNumeric: "tabular-nums" }}>{lifespan(te)}</span>}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <TregoMeShume sa={6} gjithsej={S.trashegimEmri.length} hapur={hapja("emri")} onNdrysho={ndryshoHapjen("emri")} FONTS={FONTS} KART={KART} t={t} />
          </>
        ) : null}
        {/* 6 shtator 2026 — listë MË VETE për brezin e njëjtë: emri i vëllait a
            motrës që vdiq foshnjë i vihet fëmijës që lindi pas. Ndarë nga lista
            e paraardhësve, që të mos pështillet kush kujt ia përtëriu emrin. */}
        {S.trashegimVellezer.length > 0 && (
          <>
            <Eyebrow>{t("Within the same generation: {{sa}} took a late sibling's name", { sa: S.trashegimVellezer.length })}</Eyebrow>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", columnGap: 12, rowGap: 8, alignItems: "center", marginTop: 6 }}>
              {(hapja("vellezer") ? S.trashegimVellezer : S.trashegimVellezer.slice(0, 6)).map(({ nga, te }) => (
                <React.Fragment key={nga.id + ">" + te.id}>
                  <button onClick={() => onHapPerson(nga.id)} style={{ textAlign: "right", fontFamily: FONTS.body, fontSize: 15, color: KART.inkLight, background: "transparent", border: "none", padding: 0, cursor: "pointer" }}>
                    {nga.first_name} · {meBrez(brezi(nga))}
                    <span style={{ display: "block", fontSize: 13, opacity: 0.8, fontVariantNumeric: "tabular-nums" }}>{lifespan(nga)}</span>
                  </button>
                  <span style={{ color: KART.inkLight }}>→</span>
                  <button onClick={() => onHapPerson(te.id)} style={{ textAlign: "left", fontFamily: FONTS.body, fontSize: 15, fontWeight: 600, color: KART.ink, background: "transparent", border: "none", padding: 0, cursor: "pointer" }}>
                    {te.first_name} · {meBrez(brezi(te))}
                    <span style={{ display: "block", fontSize: 13, fontWeight: 400, color: KART.inkLight, fontVariantNumeric: "tabular-nums" }}>{lifespan(te)}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
            <TregoMeShume sa={6} gjithsej={S.trashegimVellezer.length} hapur={hapja("vellezer")} onNdrysho={ndryshoHapjen("vellezer")} FONTS={FONTS} KART={KART} t={t} />
          </>
        )}
        {S.emratQeKthehen.length > 0 && (
          <>
            {S.trashegimEmri.length > 0 && <Eyebrow>{t("Names that come back")}</Eyebrow>}
            <Rreshtat>
              {(hapja("emrat") ? S.emratQeKthehen : S.emratQeKthehen.slice(0, 5)).map(([e, n]) => (
                <Rreshti key={e} etiketa={e} pjesa={(n / S.emratQeKthehen[0][1]) * 100} vlera={t("{{sa}}×", { sa: n })} />
              ))}
            </Rreshtat>
            <TregoMeShume sa={5} gjithsej={S.emratQeKthehen.length} hapur={hapja("emrat")} onNdrysho={ndryshoHapjen("emrat")} FONTS={FONTS} KART={KART} t={t} />
          </>
        )}
      </StatKarta>
    ),
    mosha_prinder: () => Object.keys(S.moshaPrinderPerBrez).length > 0 && (
      <StatKarta {...kk("mosha_prinder")} titulli={t("Parents' age at their first child, generation by generation")} nen={t("The clearest change in a family's way of life — without a word.")}>
        <Rreshtat>
          {S.brezaLista.filter((b) => S.moshaPrinderPerBrez[b]).map((b) => {
            const x = S.moshaPrinderPerBrez[b]; const mes = Math.round(x.shuma / x.sa);
            return <Rreshti key={b} etiketa={meBrez(b)} pjesa={(mes / 40) * 100} vlera={t("{{n}} years old", { n: mes })} shenim={t("{{sa}} parents", { sa: x.sa })} />;
          })}
        </Rreshtat>
        {(S.hendekuMes !== null || S.martesaMes !== null) && (
          <Dy>
            {S.hendekuMes !== null && <Pika vlera={S.hendekuMes} teksti={t("years between one generation and the next")} />}
            {S.martesaMes !== null && <Pika vlera={S.martesaMes} teksti={t("average age at marriage · {{sa}} people", { sa: S.moshatMartese.length })} />}
          </Dy>
        )}
        <Shenimi>{t("From the parent's and the first child's birth dates. Marriage from the wedding date, where it is recorded.")}</Shenimi>
      </StatKarta>
    ),
    sa_femije: () => S.shperndarjaLista.length > 0 && (
      <StatKarta {...kk("sa_femije")} titulli={t("The family with {{n}} children happened once", { n: S.maksFemije })} nen={t("Not the average — the spread. How many couples with one child, with two, with many.")}>
        <Rreshtat>
          {S.shperndarjaLista.map(([n, sa]) => (
            <Rreshti key={n} etiketa={n === 1 ? t("1 child") : t("{{n}} children", { n })} pjesa={(sa / S.maksShperndarje) * 100}
              vlera={(<button type="button" {...prekja(`femije:${n}`, n === 1 ? t("1 child") : t("{{n}} children", { n }))}
                        style={{ ...prekja(`femije:${n}`, "").style, textDecoration: "underline", textUnderlineOffset: 3 }}>{sa}</button>)}
              shenim={t("couples")} />
          ))}
        </Rreshtat>
        <KushZbuluar prefiksi="femije:" />
        <Shenimi>{t("Counted per couple, not per person — a child has two parents but belongs to one household.")} {t("{{sa}} couples", { sa: S.cifteSa })}.</Shenimi>
      </StatKarta>
    ),
    muajt: () => S.muajt.some((n) => n > 0) && (
      <StatKarta {...kk("muajt")} titulli={t("Most birthdays are in {{month}}", { month: emriMuajit(S.muajiKryesor) })}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 5, alignItems: "end", height: 120, marginTop: 6 }}>
          {S.muajt.map((n, i) => (
            <button key={i} type="button" {...prekja(`muaj:${i}`, emriMuajit(i))}
              style={{ ...prekja(`muaj:${i}`, "").style, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", gap: 5 }} title={`${emriMuajit(i)}: ${n}`}>
              <span style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, color: KART.ink, opacity: n ? 1 : 0.35, fontVariantNumeric: "tabular-nums" }}>{n}</span>
              <i style={{ display: "block", width: "100%", height: `${Math.max(2, (n / S.maksMuaj) * 70)}px`, background: i === S.muajiKryesor ? NGJ : NGJ_ZBEHTE, borderRadius: "4px 4px 0 0" }} />
              {/* Shenja e ditës së përbashkët. NUK i ndërrohet ngjyra shufrës:
                  ajo ngjyrë mban tashmë një kuptim tjetër (muaji kryesor), dhe
                  një kanal që mban dy kuptime nuk lexohet dot. Shenja rri veç,
                  dhe legjenda e shpjegon — ngjyra s'mbetet kurrë e vetme. */}
              <i aria-hidden style={{ display: "block", width: 6, height: 6, borderRadius: 999, marginTop: -3,
                                      background: muajtMeDitePerbashketa.has(i) ? NGJ : "transparent" }} />
              <span style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.inkLight }}>{emriMuajitShkurt(i)}</span>
            </button>
          ))}
        </div>
        {muajtMeDitePerbashketa.size > 0 && (
          <p style={{ display: "flex", alignItems: "center", gap: 7, margin: "6px 0 0", fontFamily: FONTS.body, fontSize: 13.5, color: KART.inkLight }}>
            <i style={{ width: 6, height: 6, borderRadius: 999, background: NGJ, display: "inline-block", flexShrink: 0 }} />
            {t("A dot marks a month where two or more people share the same day.")}
          </p>
        )}
        <KushZbuluar prefiksi="muaj:" />
      </StatKarta>
    ),
    vendlindja: () => S.vendetGjithsej > 0 && (() => {
      const kryesore = S.vendetRenditur.slice(0, 5);
      const tjera = S.vendetRenditur.slice(5);
      const tjeraSa = tjera.reduce((n, [, c]) => n + c, 0);
      const maks = S.vendetRenditur[0][1];
      const dyTeParat = kryesore.slice(0, 2).reduce((n, [, c]) => n + c, 0);
      return (
        <StatKarta {...kk("vendlindja")}
          titulli={kryesore.length >= 2 && dyTeParat / S.vendetGjithsej >= 0.4
            ? t("{{n}} places, most of them {{a}} and {{b}}", { n: S.vendetRenditur.length, a: kryesore[0][0], b: kryesore[1][0] })
            : t("{{n}} places", { n: S.vendetRenditur.length })}
          nen={t("Only the village or town — the street and the number are left out.")}>
          <Rreshtat>
            {/* Numri klikohet: «Ku ka lindur familja» pa emrat është një listë
                vendesh, jo një listë njerëzish. */}
            {(() => {
              const rreshtiVendit = ([e, c]) => (
                <Rreshti key={e} etiketa={e} pjesa={(c / maks) * 100}
                  vlera={(<button type="button" {...prekja(`vend:${e}`, e)}
                            style={{ ...prekja(`vend:${e}`, "").style, textDecoration: "underline", textUnderlineOffset: 3 }}>{c}</button>)}
                  shenim={`${Math.round((c / S.vendetGjithsej) * 100)}%`} />
              );
              return (<>
                {kryesore.map(rreshtiVendit)}
                {hapja("vendet") && tjera.map(rreshtiVendit)}
              </>);
            })()}
            {!hapja("vendet") && tjeraSa > 0 && <Rreshti etiketa={t("{{sa}} others", { sa: tjera.length })} pjesa={100} zbehte vlera={tjeraSa} shenim={`${Math.round((tjeraSa / S.vendetGjithsej) * 100)}%`} />}
          </Rreshtat>
          <KushZbuluar prefiksi="vend:" />
          <TregoMeShume sa={5} gjithsej={S.vendetRenditur.length} hapur={hapja("vendet")} onNdrysho={ndryshoHapjen("vendet")} FONTS={FONTS} KART={KART} t={t} />
          {S.brezaMeVend.length > 1 && (
            <Nen>{meBrez(S.brezaMeVend[0])}: {S.vendiIBrezit(S.brezaMeVend[0])} → {meBrez(S.brezaMeVend[S.brezaMeVend.length - 1])}: {S.vendiIBrezit(S.brezaMeVend[S.brezaMeVend.length - 1])}</Nen>
          )}
        </StatKarta>
      );
    })(),
    harta: () => (() => {
      const baza = hartaBaza === "banimi" && S.kaBanim ? S.hartaBanimi : S.hartaLindja;
      const fusha = hartaBaza === "banimi" && S.kaBanim ? "residence_country" : "birth_country";
      const lista = hapja("shtetet") ? baza.lista : baza.lista.slice(0, 6);
      const cip = (k, e, aktiv, mundshem) => (
        <button key={k} onClick={() => mundshem && setHartaBaza(k)} disabled={!mundshem}
          title={mundshem ? undefined : t("Appears once current residences are filled in.")}
          style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, padding: "5px 11px", borderRadius: 999, cursor: mundshem ? "pointer" : "default",
                   border: `1px solid ${aktiv ? NGJ_SOFT : (paneliErret ? _rgba(KART.onBg, 0.15) : "#E6DFD3")}`,
                   background: aktiv ? NGJ_SOFT : "transparent", color: aktiv ? NGJ : KART.inkLight, opacity: mundshem ? 1 : 0.5 }}>{e}</button>
      );
      return (
        <StatKarta {...kk("harta")} titulli={t("From where, and where today")}>
          <div style={{ display: "flex", gap: 6, marginTop: -2 }}>
            {cip("lindja", t("Birthplace"), fusha === "birth_country", true)}
            {cip("banimi", t("Current residence"), fusha === "residence_country", S.kaBanim)}
          </div>
          <FamilyMapMini countsByNumeric={baza.byNumeric} unmatched={baza.unmatched} t={t}
            onPick={(id) => { const lbl = baza.label[String(id)]; if (lbl) setVendiIHapur((c) => (c === lbl ? null : lbl)); }} />
          {baza.lista.length === 0 ? (
            <Nen>{t("stats.noCountriesYet")}</Nen>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              {lista.map(([shteti, sa]) => (
                <li key={shteti}>
                  <button onClick={() => setVendiIHapur((c) => (c === shteti ? null : shteti))}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 4px", borderRadius: 6,
                             background: vendiIHapur === shteti ? NGJ_SOFT : "transparent", border: "none", cursor: "pointer",
                             fontFamily: FONTS.body, fontSize: 14.5, color: KART.ink }}>
                    <span>{shteti}</span>
                    <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", color: NGJ }}>{sa}</span>
                  </button>
                  {vendiIHapur === shteti && (
                    <ul style={{ listStyle: "none", margin: "2px 0 6px 8px", padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                      {all.filter((p) => (displayCountry((p[fusha] || "").trim(), lang) || (p[fusha] || "").trim()) === shteti).map((p) => (
                        <li key={p.id}>
                          <button onClick={() => onHapPerson(p.id)}
                            style={{ width: "100%", textAlign: "left", padding: "5px 8px", borderRadius: 6, border: "none", cursor: "pointer",
                                     background: KART.panelSoft, fontFamily: FONTS.body, fontSize: 13.5, color: KART.inkLight }}>
                            {displayName(p)} <span style={{ fontVariantNumeric: "tabular-nums" }}>({lifespan(p)})</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
          <TregoMeShume sa={6} gjithsej={baza.lista.length} hapur={hapja("shtetet")} onNdrysho={ndryshoHapjen("shtetet")} FONTS={FONTS} KART={KART} t={t} />
        </StatKarta>
      );
    })(),
    migrimi: () => (
      <StatKarta {...kk("migrimi")}>
        {S.kaBanim ? (
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 16, alignItems: "center", background: NGJ_SOFT, borderRadius: 10, padding: "14px 16px" }}>
            <button type="button" {...prekja("migrim:gjithe", t("live in a different country from the one they were born in"))}
              style={{ ...prekja("migrim:gjithe", "").style, fontFamily: FONTS.display, fontSize: 32, color: NGJ, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{S.migruar}</button>
            <p style={{ margin: 0, fontFamily: FONTS.body, fontSize: 14.5, color: KART.inkLight, lineHeight: 1.5 }}>
              <strong style={{ color: KART.ink, fontWeight: 600 }}>{t("live in a different country from the one they were born in")}</strong>
              {S.rrjedhatLista.length > 0 && (
                <><br />{(hapja("rrjedhat") ? S.rrjedhatLista : S.rrjedhatLista.slice(0, 3)).map(([k, n], i) => (
                  <React.Fragment key={k}>
                    {i > 0 && " · "}
                    <button type="button" {...prekja(`rrjedha:${k}`, k)}
                      style={{ ...prekja(`rrjedha:${k}`, "").style, textDecoration: "underline", textUnderlineOffset: 3 }}>{k} {n}</button>
                  </React.Fragment>
                ))}</>
              )}
            </p>
          </div>
        ) : (
          <Nen>{t("Appears once current residences are filled in — the profile has a field for it, next to the birthplace.")}</Nen>
        )}
        <KushZbuluar prefiksi="migrim:" />
        <KushZbuluar prefiksi="rrjedha:" />
        {S.kaBanim && <TregoMeShume sa={3} gjithsej={S.rrjedhatLista.length} hapur={hapja("rrjedhat")} onNdrysho={ndryshoHapjen("rrjedhat")} FONTS={FONTS} KART={KART} t={t} />}
      </StatKarta>
    ),
    // ── JETËT MË TË GJATA ───────────────────────────────────────────────────
    // Renditen vetëm ata që kanë TË DYJA datat: një jetë pa datëvdekje nuk është
    // jetë e mbaruar, dhe përzierja e të gjallëve me të ndjerët do të krahasonte
    // dy gjëra të ndryshme. Të gjallët e kanë rreshtin e vet te «Jeta».
    jetegjatet: () => EKSTRA.jetegjatet.length > 0 && (
      <StatKarta {...kk("jetegjatet")}
        titulli={t("{{name}} lived the longest — {{n}} years", { name: displayName(EKSTRA.jetegjatet[0].p), n: EKSTRA.jetegjatet[0].m })}
        nen={t("Counted only for those with both dates recorded.")}>
        <Rreshtat>
          {EKSTRA.jetegjatet.map(({ p, m }, i) => (
            <Rreshti key={p.id}
              etiketa={(<button type="button" onClick={() => onHapPerson && onHapPerson(p.id)}
                          style={{ background: "transparent", border: "none", padding: 0, font: "inherit", color: "inherit", cursor: "pointer", textAlign: "left" }}>
                          {i + 1}. {emriDallues(p, etiketatStat)}
                        </button>)}
              pjesa={(m / EKSTRA.jetegjatet[0].m) * 100}
              vlera={t("{{n}} years", { n: m })}
              shenim={vitiIDates(p.birth_date) && vitiIDates(p.death_date) ? `${vitiIDates(p.birth_date)}–${vitiIDates(p.death_date)}` : null} />
          ))}
        </Rreshtat>
      </StatKarta>
    ),
    // ── BINJAKË DHE TRINJAKË ────────────────────────────────────────────────
    // Dy kushte bashkë: E NJËJTA DATË E PLOTË dhe TË NJËJTËT PRINDËR. E njëjta
    // datë pa prindërit e njëjtë është rastësi — te 131 veta ndodh shpesh. Dhe
    // kërkohet data e plotë: dy persona me vetëm vitin «1948» nuk janë binjakë,
    // janë dy të panjohura.
    binjaket: () => EKSTRA.binjaket.length > 0 && (
      <StatKarta {...kk("binjaket")}
        titulli={palet(EKSTRA.binjaket, t)[0]}
        nen={[...palet(EKSTRA.binjaket, t).slice(1), t("Same day of birth, same parents.")].join(" · ")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
          {EKSTRA.binjaket.map((grupi) => (
            <div key={grupi.map((x) => x.id).join("|")} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: KART.inkLight }}>
                {grupi.length === 2 ? t("Twins") : grupi.length === 3 ? t("Triplets") : grupi.length === 4 ? t("Quadruplets") : grupi.length === 5 ? t("Quintuplets") : t("{{n}} born the same day", { n: grupi.length })}
                {" · "}{grupi[0].birth_date}
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {grupi.map((p) => (
                  <button key={p.id} type="button" onClick={() => onHapPerson && onHapPerson(p.id)}
                    style={{ fontFamily: FONTS.body, fontSize: 13.5, color: KART.ink, background: NGJ_SOFT,
                             border: `1px solid ${SHINA}`, borderRadius: 999, padding: "4px 10px", cursor: "pointer" }}>
                    {emriDallues(p, etiketatStat)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </StatKarta>
    ),
    jeta: () => (Object.keys(S.jetaPerBrez).length > 0 || S.meEMoshuara) && (
      <StatKarta {...kk("jeta")} titulli={t("Lifespan, and who holds the tree today")}>
        {Object.keys(S.jetaPerBrez).length > 0 && (
          <Rreshtat>
            {S.brezaLista.filter((b) => S.jetaPerBrez[b]).map((b) => {
              const x = S.jetaPerBrez[b]; const mes = Math.round(x.shuma / x.sa);
              return <Rreshti key={b} etiketa={meBrez(b)} pjesa={mes} vlera={`${mes} ${t("years")}`} shenim={t("{{sa}} people", { sa: x.sa })} />;
            })}
            {S.mesJete(S.jetaGjini.F) !== null && <Rreshti etiketa={t("Women")} pjesa={S.mesJete(S.jetaGjini.F)} vlera={`${S.mesJete(S.jetaGjini.F)} ${t("years")}`} shenim={t("{{sa}} people", { sa: S.jetaGjini.F.length })} />}
            {S.mesJete(S.jetaGjini.M) !== null && <Rreshti etiketa={t("Men")} pjesa={S.mesJete(S.jetaGjini.M)} vlera={`${S.mesJete(S.jetaGjini.M)} ${t("years")}`} shenim={t("{{sa}} people", { sa: S.jetaGjini.M.length })} />}
          </Rreshtat>
        )}
        {Object.keys(S.jetaPerBrez).length > 0 && <Shenimi>{t("Only those with both dates recorded. Where a generation is thin, the number says little.")}</Shenimi>}
        {S.meEMoshuara && (
          <Dy>
            <StatPersoni st={st} onHapPerson={onHapPerson} p={S.meEMoshuara.p} m={S.meEMoshuara.m} teksti={t("the oldest living")} />
            {S.meIRiu && S.meIRiu.p.id !== S.meEMoshuara.p.id && <StatPersoni st={st} onHapPerson={onHapPerson} p={S.meIRiu.p} m={S.meIRiu.m} teksti={t("the youngest")} />}
          </Dy>
        )}
      </StatKarta>
    ),
    arkivi: () => all.length > 0 && (
      <StatKarta {...kk("arkivi")} titulli={S.shtuarGjithsej6 > 0 ? t("The tree grows by {{n}} people a month", { n: Math.round((S.shtuarGjithsej6 / 6) * 10) / 10 }) : t("The tree is still, for now")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          {S.muajtArkivi.map((x, i) => (
            <div key={x.k} style={{ display: "grid", gridTemplateColumns: "96px 1fr 40px", gap: 10, alignItems: "center", fontFamily: FONTS.body, fontSize: 14.5, color: KART.inkLight }}>
              <span>{emriMuajitShkurt(x.muaji)}{i === 0 || x.muaji === 0 ? ` ${x.viti}` : ""}</span>
              <div style={{ height: 12, borderRadius: 6, background: `linear-gradient(90deg, ${NGJ_SOFT}, ${NGJ})`, width: `${Math.max(3, (x.sa / S.maksArkivi) * 100)}%`, opacity: x.sa ? 1 : 0.25 }} />
              <span style={{ color: KART.ink, fontWeight: 500, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{x.sa}</span>
            </div>
          ))}
        </div>
        <Dy>
          {S.fotoPerPersonVjeter !== null && (
            <Pika vlera={S.fotoPerPersonVjeter.toFixed(1)} teksti={t("photos per person in generations {{a}}–{{b}} — an invitation to scan", { a: S.brezatEVjeter[0], b: S.brezatEVjeter[S.brezatEVjeter.length - 1] })} />
          )}
          {(ngjarjeHistorie || 0) > 0 && <Pika vlera={ngjarjeHistorie} teksti={t("events in the family's story")} />}
        </Dy>
      </StatKarta>
    ),
    profesioni: () => (
      <StatKarta {...kk("profesioni")}>
        <Nen>{t("Needs a profession field on the person — not there yet.")}</Nen>
      </StatKarta>
    ),
    // PËRMBLEDHJA (6 shtator 2026). Pronari: «krejt në fund do doja një
    // përmbledhje tekstuale, formë tregimi i shkurtër, e nxjerrë nga
    // statistikat». Çdo fjali del vetëm kur numri pas saj ekziston — pa numra
    // të shpikur, pa fjali boshe. Fjalitë janë çelësa përkthimi me vlera.
    permbledhja: () => {
      // RRËFIMI (6 shtator 2026, sipas shembullit të pronarit): paragrafë, jo
      // listë numrash. Çdo fjali del vetëm kur numri pas saj ekziston, dhe
      // gjinia e personit vendos trajtën («më e moshuara» / «më i moshuari»).
      const emriI = (p) => (p ? displayName(p) : "");
      const gj = (p, M, F) => (p && p.gender === "F" ? F : M);
      const paragrafet = [];
      const une = pikepamja === "une" && personiIReferimit && people[personiIReferimit] ? people[personiIReferimit] : null;

      // 0 · NGA POZICIONI IM (6 shtator 2026) — kur pikëpamja është «nga unë»,
      //     rrëfimi nis nga personi: brezi i tij, vija e gjakut deri te rrënja,
      //     vëllezërit e motrat, fëmijët e pasardhësit, dhe farefisi rreth tij
      //     (i njëjti motor si ngjyrat e pemës). Pastaj vazhdon me familjen.
      if (une) {
        const brezaIm = brezi(une);
        paragrafet.push(t("story.me.p1", { emri: emriI(une), b: brezaIm, n: all.length }));
        // Vija e gjakut: prindi me gjak (jo i ardhur me martesë), brez pas brezi.
        const vija = [];
        let k = une, roje = 0;
        while (k && roje++ < 40) {
          const prinderit = (k.parent_ids || []).map((x) => people[x]).filter(Boolean);
          if (!prinderit.length) break;
          const meGjak = prinderit.find((pr) => degaE(pr) === "core") || prinderit[0];
          vija.push(meGjak); k = meGjak;
        }
        if (vija.length >= 2) {
          paragrafet.push(t("story.me.p2", { k: vija.length, lista: vija.slice(0, -1).map(emriI).join(", "), rrenja: emriI(vija[vija.length - 1]) }));
        } else if (vija.length === 1) {
          paragrafet.push(t("story.me.p2One", { prindi: emriI(vija[0]) }));
        }
        const prinderitEMi = new Set(une.parent_ids || []);
        const vellezer = prinderitEMi.size ? all.filter((q) => q.id !== une.id && (q.parent_ids || []).some((x) => prinderitEMi.has(x))).length : 0;
        const femijet = all.filter((q) => (q.parent_ids || []).includes(une.id)).length;
        const pasardhesit = pasardhesitE(people, une.id).size - 1;
        const copat = [];
        if (vellezer > 0) copat.push(t("story.me.siblings", { n: vellezer }));
        if (femijet > 0) copat.push(t("story.me.children", { n: femijet }));
        if (pasardhesit > femijet) copat.push(t("story.me.descendants", { n: pasardhesit }));
        if (copat.length) paragrafet.push(t("story.me.p3", { lista: copat.join(", ") }));
        const perbI = Object.fromEntries(S.perberja);
        const rreth = [];
        if (perbI.close_cousin) rreth.push(t("story.me.closeCousins", { n: perbI.close_cousin }));
        if (perbI.extended) rreth.push(t("story.me.farCousins", { n: perbI.extended }));
        if (perbI.married_in) rreth.push(t("story.me.marriedIn", { n: perbI.married_in }));
        if (perbI.married_out) rreth.push(t("story.me.newBranches", { n: perbI.married_out }));
        if (rreth.length) paragrafet.push(t("story.me.p4", { lista: rreth.join(", ") }));
      }

      // 1 · Sa jemi
      if (!une) paragrafet.push([
        (S.men > 0 && S.women > 0)
          ? t("story.p1Gender", { n: all.length, m: S.men, f: S.women, breza: S.brezaLista.length })
          : t("story.p1", { n: all.length, breza: S.brezaLista.length }),
        t("story.p1Living", { gjalle: S.living.length }),
      ].join(" "));

      // 2 · Stacionet e familjes — jo vetëm brezi i parë dhe «shumica»
      //     (pronari: «stacioni i dytë ku u rrit familja nuk përmendet fare»).
      //     Për çdo brez merret vendlindja mbizotëruese; brezat me të njëjtin
      //     vend bashkohen në një stacion. Një stacion vlen kur ka të paktën 2
      //     veta — një vend i vetmuar është rastësi, jo vendbanim i familjes.
      const stacionet = [];
      S.brezaLista.forEach((b) => {
        const gj = {};
        // Kur te vendlindja është shkruar vetëm shteti («Deutschland»), emri
        // kalon nga tabela e shteteve që të dalë në gjuhën e ndërfaqes.
        const emriIVendit = (q) => { const v = vendlindjaEShkurter(q.birth_place); if (!v) return ""; return resolveCountry(v) ? (displayCountry(v, lang) || v) : v; };
        all.filter((q) => brezi(q) === b).forEach((q) => { const v = emriIVendit(q); if (v) gj[v] = (gj[v] || 0) + 1; });
        const kryesori = Object.entries(gj).sort((x, y) => y[1] - x[1])[0];
        if (!kryesori) return;
        const i = stacionet.length - 1;
        if (i >= 0 && stacionet[i].vendi === kryesori[0]) { stacionet[i].sa += kryesori[1]; stacionet[i].deri = b; }
        else stacionet.push({ vendi: kryesori[0], sa: kryesori[1], nga: b, deri: b });
      });
      // Stacionet e vogla (1 person) shkrihen te fqinji paraardhës, që zinxhiri
      // të mos këputet nga një lindje e vetmuar diku tjetër.
      const teMedhenj = [];
      stacionet.forEach((st) => {
        const i = teMedhenj.length - 1;
        if (st.sa < 2 && i >= 0) { teMedhenj[i].sa += st.sa; teMedhenj[i].deri = st.deri; return; }
        if (i >= 0 && teMedhenj[i].vendi === st.vendi) { teMedhenj[i].sa += st.sa; teMedhenj[i].deri = st.deri; return; }
        teMedhenj.push({ ...st });
      });
      const rrenja = teMedhenj.length ? teMedhenj[0].vendi : null;
      // «Përqendrimi sot» = vendi ku ka MË SHUMË të gjallë (pronari: «aty ku
      // ka banorë më shumë»), jo stacioni i brezit të fundit — ai mund të jetë
      // i vogël (pak lindje të reja në Gjermani nuk e zhvendosin familjen).
      // Matet me vendlindjen e të gjallëve, me të njëjtën shkallë si stacionet.
      const teGjalletSipasVendit = {};
      S.living.forEach((q) => { const v = vendlindjaEShkurter(q.birth_place); if (!v) return; const e = resolveCountry(v) ? (displayCountry(v, lang) || v) : v; teGjalletSipasVendit[e] = (teGjalletSipasVendit[e] || 0) + 1; });
      const sot = (Object.entries(teGjalletSipasVendit).sort((a, b) => b[1] - a[1])[0] || [null])[0];
      const brezMax = Object.entries(S.gjiniPerBrez).map(([b, x]) => [Number(b), (x.M || 0) + (x.F || 0)]).sort((a, b) => b[1] - a[1])[0];
      if (rrenja) {
        let p2 = t("story.p2Roots", { rrenja });
        const tjeret = teMedhenj.slice(1).map((st) => st.vendi);
        if (tjeret.length === 1) p2 += " " + t("story.p2Then", { vendi: tjeret[0] });
        else if (tjeret.length > 1) p2 += " " + t("story.p2ThenMany", { vendi: tjeret[0], lista: tjeret.slice(1).join(", ") });
        if (sot && sot !== rrenja && sot !== tjeret[tjeret.length - 1]) p2 += " " + t("story.p2Today", { sot });
        else if (sot) p2 += " " + t("story.p2StillThere", { sot });
        paragrafet.push(p2);
      }
      // 2b · Nga erdhën dhe ku shkuan — migrimi (shtet i lindjes → shtet i banimit)
      if (S.migruar > 0) {
        const destinacionet = {};
        S.rrjedhatLista.forEach(([rruga, sa]) => { const ku = String(rruga).split(" → ")[1]; if (ku) destinacionet[ku] = (destinacionet[ku] || 0) + sa; });
        const kryesori = Object.entries(destinacionet).sort((a, b) => b[1] - a[1])[0];
        paragrafet.push(kryesori
          ? t("story.p2bMigrationRoute", { migruar: S.migruar, ku: kryesori[0], sa: kryesori[1] })
          : t("story.p2bMigration", { migruar: S.migruar }));
      }

      // 3 · Brezi më i madh, martesa, fëmija i parë
      {
        const copat = [];
        if (brezMax && brezMax[1] > 1) copat.push(t("story.p3Generation", { b: brezMax[0], n: brezMax[1] }));
        if (S.martesaMes != null && S.hendekuMes != null) copat.push(t("story.p3MarriageAndChild", { mm: S.martesaMes, mf: S.hendekuMes }));
        else if (S.hendekuMes != null) copat.push(t("story.p3Child", { mf: S.hendekuMes }));
        if (copat.length) paragrafet.push(copat.join(" "));
      }

      // 4 · Fëmijët dhe emrat
      {
        const copat = [];
        if (S.shperndarjaLista.length > 0 && S.cifteSa > 0) {
          const gjithsej = S.shperndarjaLista.reduce((n, [k, sa]) => n + k * sa, 0);
          copat.push(t("story.p4Children", { mes: Math.round(gjithsej / S.cifteSa), maks: S.maksFemije }));
        }
        if (S.emratQeKthehen.length > 0) copat.push(t("story.p4Name", { emri: S.emratQeKthehen[0][0], n: S.emratQeKthehen[0][1] }));
        if (copat.length) paragrafet.push(copat.join(" "));
      }

      // 5 · Jeta më e gjatë, më e moshuara / më i riu sot, lindjet e shumëfishta
      {
        const copat = [];
        // Jeta më e gjatë përmendet vetëm kur e kalon moshën e çdo të gjalli —
        // ndryshe «jetoi 73 vjet» pranë «më i moshuari sot, 98 vjeç» s'ka kuptim.
        if (EKSTRA.jetegjatet.length > 0) {
          const j = EKSTRA.jetegjatet[0];
          const moshaMaksEGjalle = S.meEMoshuara ? S.meEMoshuara.m : -1;
          if (j.m > moshaMaksEGjalle) copat.push(t(gj(j.p, "story.p5LongestM", "story.p5LongestF"), { emri: emriI(j.p), m: j.m }));
        }
        if (S.meEMoshuara && S.meIRiu && S.meEMoshuara.p.id !== S.meIRiu.p.id) {
          const plaku = t(gj(S.meEMoshuara.p, "story.oldestM", "story.oldestF"), { emri: emriI(S.meEMoshuara.p), m: S.meEMoshuara.m });
          const iRiu = S.meIRiu.m > 0
            ? t(gj(S.meIRiu.p, "story.youngestM", "story.youngestF"), { emri: emriI(S.meIRiu.p), m: S.meIRiu.m })
            : t(gj(S.meIRiu.p, "story.youngestBabyM", "story.youngestBabyF"), { emri: emriI(S.meIRiu.p) });
          copat.push(t("story.oldestYoungest", { plaku, iRiu }));
        }
        if (EKSTRA.binjaket.length > 0) copat.push(t("story.p5Twins", { lista: listaEPaleve(EKSTRA.binjaket, t) }));
        if (copat.length) paragrafet.push(copat.join(" "));
      }

      // 6 · Plotësia dhe mbyllja
      paragrafet.push(t("story.p6Completeness", { p: S.plotesiaMes }));
      paragrafet.push(t("story.p6Closing"));

      const tekstiStandard = paragrafet.join("\n\n");
      const teShfaqura = permbledhjaAI ? permbledhjaAI.split(/\n+/).map((x) => x.trim()).filter(Boolean) : paragrafet;
      const butoni = (e, onClick, kryesor) => (
        <button type="button" onClick={onClick} disabled={permbledhjaDuke}
          style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, padding: "6px 12px", borderRadius: 999, cursor: permbledhjaDuke ? "wait" : "pointer",
                   border: `1px solid ${kryesor ? NGJ : SHINA}`, background: kryesor ? NGJ_SOFT : "transparent", color: kryesor ? NGJ : KART.inkLight, opacity: permbledhjaDuke ? 0.6 : 1 }}>{e}</button>
      );
      return (
        <StatKarta {...kk("permbledhja")} titulli={emriIFamiljes ? t("{{family}}, in numbers", { family: emriIFamiljes }) : t("The family, in numbers")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6, maxWidth: "62ch" }}>
            {teShfaqura.map((tx, i) => (
              <p key={i} style={{ fontFamily: FONTS.display, fontSize: 16.5, lineHeight: 1.65, color: KART.ink, margin: 0,
                                  fontStyle: !permbledhjaAI && i === teShfaqura.length - 1 ? "italic" : "normal" }}>{tx}</p>
            ))}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
              {butoni(permbledhjaDuke ? t("Writing…") : "✨ " + (permbledhjaAI ? t("Write it differently") : t("Rewrite with AI")), () => rigjeneroPermbledhjen(tekstiStandard), true)}
              {permbledhjaAI && butoni(t("Back to the standard text"), () => { setPermbledhjaAI(null); setPermbledhjaGabim(""); }, false)}
              {permbledhjaGabim && <span style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.inkLight }}>{permbledhjaGabim}</span>}
            </div>
            {permbledhjaAI && <Nen>{t("Rewritten by AI from the same numbers — read it before sharing.")}</Nen>}
          </div>
        </StatKarta>
      );
    },
  };

  return (
    <div className="max-w-2xl mx-auto" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        {meTitull
          ? <p style={{ fontFamily: FONTS.display, fontSize: 22, fontWeight: 600, color: KART.onBg, margin: 0 }}>{t("stats.title")}</p>
          : <span />}
        {/* FILTRI I DEGËS — një person; numrat flasin për të dhe pasardhësit. */}
        <div style={{ flex: "1 1 240px", maxWidth: 360 }}>
          <SearchSelect people={people} value={paraardhesi} onChange={setParaardhesi} placeholder={t("A person's branch — type the name…")} />
        </div>
      </div>
      {/* Pikëpamja: e familjes (parazgjedhje) ose nga pozicioni im. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {[["familja", t("stats.viewFamily")], ["une", t("stats.viewMe")]].map(([k, e]) => {
          const aktiv = pikepamja === k;
          return (
            <button key={k} onClick={() => ndryshoPikepamjen(k)}
              style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, padding: "5px 12px", borderRadius: 999, cursor: "pointer",
                       border: `1px solid ${aktiv ? KART.gold : _rgba(KART.onBg, 0.25)}`,
                       background: aktiv ? _rgba(KART.gold, 0.18) : "transparent", color: KART.onBg, opacity: aktiv ? 1 : 0.75 }}>{e}</button>
          );
        })}
        <span style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.onBg, opacity: 0.65 }}>
          {pikepamja === "une" ? t("stats.viewMeHint") : t("stats.viewFamilyHint")}
        </span>
      </div>
      {paraardhesi && people[paraardhesi] && (
        <p style={{ fontFamily: FONTS.body, fontSize: 14.5, color: KART.onBg, opacity: 0.85, margin: "-4px 0 0" }}>
          {t("The branch of {{name}}: {{sa}} descendants", { name: displayName(people[paraardhesi]), sa: all.length - 1 })}
        </p>
      )}
      {all.length === 0 ? (
        <StatKarta {...kk("hero")}><Nen>{t("No people in this selection yet.")}</Nen></StatKarta>
      ) : radha.filter(dukshem).map((k) => {
        const f = kartelat[k]; if (!f) return null;
        const el = f();
        return el ? <React.Fragment key={k}>{el}</React.Fragment> : null;
      })}
    </div>
  );
}
function truncateCard(str, maxChars) {
  if (!str) return "";
  return str.length > maxChars ? str.slice(0, maxChars - 1) + "…" : str;
}
// ── EMRI TE KARTELA E PEMËS (SKEMA1, 6 shtator 2026) ────────────────────────
// Çdo skemë vizatonte tri `<text>` të vetat: emri, mbiemri, vitet — me
// madhësi të ngulitura. Tani të gjitha kalojnë këtu, dhe skema aktive mund
// t'i ndryshojë nga Admin Center (kutia `emri`): madhësinë e emrit, të
// mbiemrit dhe të viteve; rreshtat («dy» = emri / mbiemri / vitet, «nje» =
// «Emri Mbiemri» / vitet); dhe mbiemrin e vajzërisë si rresht kursiv i vetin.
// Pa asnjë cilësim, del FJALË PËR FJALË ç'dilte më parë: stilet dhe kufijtë
// e prerjes i jep vetë skema thirrëse.
//   yEmri/yMbiemri/yVitet — rreshtat e skemës; hapi mes tyre përdoret për
//                            rreshtin shtesë të vajzërisë
//   gjeresia              — sa piksela ka teksti në dispozicion (për prerjen
//                            kur admini e rrit shkronjën)
//   ngjesh                — kur s'ka mbiemër, vitet ngjiten lart (heirloom)
function emriNeKartele({ p, x, anchor = "middle", yEmri, yMbiemri, yVitet, emri, mbiemri, vitet,
  maxEmri = 10, maxMbiemri = 12, gjeresia = 96, ngjesh = false, mbiemriTransform = null }) {
  const s = stiliIEmrit();
  const stEmri = { ...emri, fontSize: s.emri_px || emri.fontSize };
  const stMbiemri = { ...mbiemri, fontSize: s.mbiemri_px || mbiemri.fontSize };
  const stVitet = { ...vitet, fontSize: s.vitet_px || vitet.fontSize };
  const hapi = Math.max(10, yVitet - yMbiemri);
  // Kufiri i prerjes: ai i skemës kur madhësia s'është prekur; ndryshe nga
  // gjerësia e vërtetë (≈0.55 em për shkronjë te seriferat e ngushta).
  const kufi = (px, parazgjedhja, iPrekur) => (iPrekur ? Math.max(6, Math.floor(gjeresia / (px * 0.55))) : parazgjedhja);
  const kE = kufi(stEmri.fontSize, maxEmri, !!s.emri_px);
  const kM = kufi(stMbiemri.fontSize, maxMbiemri, !!s.mbiemri_px);
  const mb = (str) => (mbiemriTransform ? mbiemriTransform(str) : str);
  const out = [];
  let y;
  if (s.rreshtat === "nje") {
    const iPlote = [p.first_name, p.last_name].filter(Boolean).join(" ");
    out.push(<text key="e" x={x} y={yEmri} textAnchor={anchor} style={stEmri}>{truncateCard(iPlote, Math.max(kE, Math.floor(gjeresia / (stEmri.fontSize * 0.55))))}</text>);
    y = yMbiemri;
  } else {
    out.push(<text key="e" x={x} y={yEmri} textAnchor={anchor} style={stEmri}>{truncateCard(p.first_name, kE)}</text>);
    if (p.last_name || !ngjesh) {
      out.push(<text key="m" x={x} y={yMbiemri} textAnchor={anchor} style={stMbiemri}>{truncateCard(mb(p.last_name || ""), kM)}</text>);
      y = yVitet;
    } else {
      y = yMbiemri;
    }
  }
  if (s.vajzeria && p.maiden_name) {
    out.push(<text key="v" x={x} y={y} textAnchor={anchor} style={{ ...stMbiemri, fontStyle: "italic", fontWeight: 400, letterSpacing: 0 }}>({truncateCard(p.maiden_name, Math.max(6, kM - 2))})</text>);
    y += hapi;
  }
  out.push(<text key="y" x={x} y={y} textAnchor={anchor} style={stVitet}>{lifespan(p)}</text>);
  return out;
}
// FAZA 3 — POSTER DY-FAMILJESH. Bashkon dy pemë te "ura": `personAId` (rreshti
// në familjen A, zakonisht married_in) dhe `personBId` (rreshti core në B). I jep
// personA prejardhjen e B-së (prindërit e nënës) dhe rishkruan çdo referencë
// personB → personA. Kthen NJË hartë të vetme personash, gati për computeLayout
// (del një grafik i lidhur → layout hourglass: dy prejardhje takohen te çifti).
function mergeBridgedFamilies(peopleMap, personAId, personBId, mode = "full", primaryFamilyId = null) {
  // Kopje e thellë (që të mos mutohet hyrja).
  // FIX (analiza deep — "nuk po kuptoj pse familja e Meritës kalon në anën
  // e majtë"): rpc_two_family_poster s'ka ORDER BY, ndaj rendi i rreshtave
  // (→ rendi i çelësave të hartës → rendi i rrënjëve në computeLayout →
  // KUSH del MAJTAS) ishte i RASTËSISHËM. Tani: njerëzit e familjes AKTIVE
  // (ajo nga po printon) futen TË PARËT në hartë → rrënja e saj vendoset e
  // para → familja jote gjithmonë majtas, familja tjetër vjen në krahun e
  // personit që i lidh.
  const merged = {};
  const rows = Object.values(peopleMap);
  const ordered = primaryFamilyId
    ? [...rows.filter((p) => p.family_id === primaryFamilyId), ...rows.filter((p) => p.family_id !== primaryFamilyId)]
    : rows;
  ordered.forEach((p) => {
    merged[p.id] = {
      ...p,
      parent_ids: [...(p.parent_ids || [])],
      spouse_ids: [...(p.spouse_ids || [])],
      engaged_ids: [...(p.engaged_ids || [])],
      cohabiting_ids: [...(p.cohabiting_ids || [])],
      divorced_from_ids: [...(p.divorced_from_ids || [])],
    };
  });

  // FIX (analiza deep — "foto 2: s'dihet kush është pika e bashkimit; motra
  // ime dhe vëllau i Meritës s'kanë asgjë të përbashkët"): "sidebyside" më
  // parë anashkalonte VETËM bashkimin e urës (rregulli 3), por rregullat 1
  // (synced_from) e 2 (emër+datë) VAZHDONIN t'i shkrinin dublikatat (Merita,
  // ti, fëmijët ekzistojnë në TË DYJA familjet nga "Sill familjen") — pra
  // grafet mbeteshin TË NGJITUR në pika arbitrare, jo dy pemë të veçanta.
  // Tani: krah-për-krah = ASNJË bashkim fare. Secila familje mbetet graf më
  // vete, e plotë me kopjet e veta lokale (ti dukesh te pema jote, kopja
  // jote te pema e saj — pikërisht si dy postera klasikë krah-për-krah).
  // Lidhjen e vetme e vizaton rendering-u: vijë e ndërprerë "i njëjti
  // person" mes dy kopjeve të personit urë (shih bridgeLine te pema).
  if (mode === "sidebyside") {
    // Higjienë referencash (si rw() më poshtë): hiq id-të që s'janë në hartë
    // (persona të bllokuar/fshirë që RPC-ja s'i ktheu) — njësoj si pema normale.
    const clean = (arr, selfId) => (arr || []).filter((x) => x !== selfId && merged[x]);
    Object.values(merged).forEach((p) => {
      p.parent_ids = clean(p.parent_ids, p.id);
      p.spouse_ids = clean(p.spouse_ids, p.id);
      p.engaged_ids = clean(p.engaged_ids, p.id);
      p.cohabiting_ids = clean(p.cohabiting_ids, p.id);
      p.divorced_from_ids = clean(p.divorced_from_ids, p.id);
    });
    // SHËNIM I QËNDRUESHËM I ÇIFTIT URË (shih layoutOpts te memoja e
    // positions): kopja në pemën A renditet e fundit, kopja në B e para.
    // FIX (diagnostika [2FAM]: canon=null — id-të e urës në person_bridge_links
    // tregonin rreshta që RPC-ja s'i kthen më, me gjasë të fshirë e të
    // rikrijuar nga sinkronizimi PAS krijimit të urës): mos u mbështet FARE
    // te id-të — çdo person që merr pjesë në një çift kopjesh NDËR-familjare
    // (lidhja synced_from mes dy familjeve) shënohet sipas anës së vet:
    // ana e familjes aktive → e fundit; ana tjetër → e para.
    const primFam = primaryFamilyId || merged[personAId]?.family_id || null;
    Object.values(merged).forEach((p) => {
      const o = p.synced_from_person_id && merged[p.synced_from_person_id];
      if (!o || o.family_id === p.family_id) return;
      [p, o].forEach((x) => {
        if (primFam && x.family_id === primFam) x._twofam_last = true;
        else x._twofam_first = true;
      });
    });
    if (merged[personAId]) merged[personAId]._twofam_last = true;
    if (merged[personBId]) merged[personBId]._twofam_first = true;
    return merged;
  }

  // ---- HARTA KANONIKE: një përfaqësues i vetëm për çdo NJERI REAL. ----
  // Bug-u i mëparshëm: dedup-onte VETËM personin e urës (Merita), ndaj Gazmendi
  // dhe fëmijët — të futur në të dyja pemët me "Sill familjen bërthamë" — dilnin
  // dy herë. Tani bashkojmë çdo dublikatë, jo vetëm urën. Union-find mbi "rrënjë"
  // (pa cikle): `from` bie te grupi i `to` (to = nyja që mbahet).
  const canon = {};
  const resolve = (id) => { let c = id, g = 0; while (canon[c] && canon[c] !== c && g++ < 5000) c = canon[c]; return c; };
  const union = (from, to) => {
    const rf = resolve(from), rt = resolve(to);
    if (rf && rt && rf !== rt && merged[rf] && merged[rt]) canon[rf] = rt;
  };

  // (1) Dublikatat nga "sjell familjen bërthamë": kopja → origjinali (synced_from_person_id).
  Object.values(merged).forEach((p) => {
    if (p.synced_from_person_id && merged[p.synced_from_person_id]) union(p.id, p.synced_from_person_id);
  });

  // (2) Rezervë: i njëjti emër + datëlindje, por në FAMILJE TË NDRYSHME (pasqyron
  //     dedup-in e vetë rpc_import_nuclear_family). Kërkon datëlindje → shmang
  //     bashkime false të paraardhësve pa datë e me emër të përbashkët.
  const norm = (p) => `${(p.first_name || "").trim().toLowerCase()} ${(p.last_name || "").trim().toLowerCase()}`.trim();
  const seen = {};
  Object.values(merged).forEach((p) => {
    if (!norm(p) || !p.birth_date) return;
    const key = `${norm(p)}|${p.birth_date}`;
    if (seen[key] && merged[seen[key]] && merged[seen[key]].family_id !== p.family_id) union(p.id, seen[key]);
    else if (!seen[key]) seen[key] = p.id;
  });

  // (3) URA (ka përparësi): personB → personA.
  //   • "sidebyside" = NUK e bashkojmë urën → dy pemët mbeten të veçanta krah-për-krah.
  if (mode !== "sidebyside" && merged[personAId] && merged[personBId]) union(personBId, personAId);

  // ---- Bashko fushat te përfaqësuesi (union lidhjesh; mbush vetëm bosh). ----
  const uni = (x, y) => Array.from(new Set([...(x || []), ...(y || [])]));
  Object.values(merged).forEach((p) => {
    const rep = resolve(p.id);
    if (rep === p.id) return;
    const r = merged[rep];
    if (!r) return;
    r.parent_ids = uni(r.parent_ids, p.parent_ids);
    r.spouse_ids = uni(r.spouse_ids, p.spouse_ids);
    r.engaged_ids = uni(r.engaged_ids, p.engaged_ids);
    r.cohabiting_ids = uni(r.cohabiting_ids, p.cohabiting_ids);
    r.divorced_from_ids = uni(r.divorced_from_ids, p.divorced_from_ids);
    if (!r.maiden_name && p.maiden_name) r.maiden_name = p.maiden_name;
    if (!r.photo_path && p.photo_path) r.photo_path = p.photo_path;
    if (!r.birth_date && p.birth_date) r.birth_date = p.birth_date;
    if (!r.death_date && p.death_date) r.death_date = p.death_date;
    if (!r.gender && p.gender) r.gender = p.gender;
  });

  // Hiq jo-përfaqësuesit.
  Object.keys(merged).forEach((id) => { if (resolve(id) !== id) delete merged[id]; });

  // Rishkruaj çdo referencë te id-ja kanonike; hiq vetë-referenca & të fshirat.
  const rw = (arr, selfId) => Array.from(new Set((arr || []).map((id) => resolve(id)))).filter((id) => id !== selfId && merged[id]);
  Object.values(merged).forEach((p) => {
    p.parent_ids = rw(p.parent_ids, p.id);
    p.spouse_ids = rw(p.spouse_ids, p.id);
    p.engaged_ids = rw(p.engaged_ids, p.id);
    p.cohabiting_ids = rw(p.cohabiting_ids, p.id);
    p.divorced_from_ids = rw(p.divorced_from_ids, p.id);
  });

  // FIX ROBUSTËSIE (raportuar nga useri — "prap nuk është rregulluar,
  // njerëzit ruajnë pozicionet edhe pas bashkimit"): memoja e layout-it e
  // gjente çiftin urë me people[bridge.person_a/person_b] — por pas
  // bashkimeve zinxhir (synced_from → emër+datë → ura) id-ja KANONIKE mund
  // të mos jetë ASNJËRA nga dy id-të e urës (mbijeton një kopje e tretë) —
  // atëherë kërkimi dështonte NË HESHTJE dhe renditja e detyruar s'aplikohej
  // fare. Tani vetë shkrirja, që e DI se ku përfundoi çdo id (resolve),
  // e shënon personin kanonik të urës drejtpërdrejt — pa asnjë hamendje.
  const canonBridge = resolve(personAId);
  const cb = merged[canonBridge] ? canonBridge : (merged[resolve(personBId)] ? resolve(personBId) : null);
  if (cb) merged[cb]._twofam_last = true;
  // FIX THEMELOR (diagnostika [2FAM]: canon=null, sortLast bosh — id-të e
  // urës në person_bridge_links tregonin rreshta që RPC-ja NUK i kthen më;
  // me gjasë kopja u fshi e u rikrijua nga sinkronizimi PAS krijimit të
  // urës, ndaj vetë shkrirja punonte — përmes synced_from — por shënimi i
  // çiftit dështonte në heshtje): identifikimi tani NUK varet fare nga
  // id-të e urës. Kushdo përfaqësues kanonik që ka PËRTHITHUR kopje nga TË
  // DYJA familjet (pra ekziston realisht në të dyja pemët — Gazmendi,
  // Merita, dhe fëmijët e sjellë me "Sill familjen") shënohet _twofam_last:
  // pikërisht bërthama që i lidh dy familjet, e cila duhet të rreshtohet e
  // fundit që të bëhet epiqendra e bashkimit. (Për fëmijët është pa efekt —
  // grupi i tyre i vëllezërve përbëhet krejt nga të shënuar, ndaj mbetet
  // renditja sipas datëlindjes.)
  const famsOf = {};
  Object.values(peopleMap).forEach((p) => {
    const rep = resolve(p.id);
    if (!merged[rep]) return;
    if (!famsOf[rep]) famsOf[rep] = new Set();
    famsOf[rep].add(p.family_id);
  });
  Object.entries(famsOf).forEach(([rep, fams]) => { if (fams.size >= 2) merged[rep]._twofam_last = true; });

  return merged;
}
// SHTESË (kërkesë e userit — "kur dikush ka dy mbiemra, duhet dy rreshta,
// jo '...' ") — SVG s'ka "word-wrap" vetjak, ndaj ndahet me dorë: nëse emri
// i plotë s'hyn në një rresht, provo ta ndash te fjala që e bën më të
// BALANCUAR (jo domosdo në mes saktë — p.sh. "Gazmend" / "Gazmendi Krasniqi"
// në vend të "Gazmend Gazmendi" / "Krasniqi", nëse kjo e fundit është më e
// çekuilibruar). Kthen gjithmonë 1 ose 2 rreshta (kurrë më shumë — rreshti
// i dytë prehet me "…" nëse ende s'hyn).
// ── EMRI NË DY RRESHTA GJITHMONË — emri sipër, mbiemri poshtë ──────────────
// 4 shtator 2026. Pronari: «te Trashëgimia emri, mbiemri, e për fundi vitet
// duket më bukur edhe nuk shkelin emrat mbi njëri-tjetrin. A mund ta bësh të
// njëjtën edhe te Rrënjët?»
//
// `wrapNameTwoLines` ndan sipas GJERËSISË: nëse emri i plotë hyn brenda buxhetit
// të shkronjave, mbetet një rresht i vetëm. Por buxheti llogaritet mbi gjerësinë
// e KARTELËS (96px), ndërsa dy bashkëshortë rrinë vetëm ~134px larg qendër-me-
// qendër — pra një rresht «i lejuar» prapë e prek fqinjin.
//
// Këtu ndarja nuk është më sipas gjerësisë por sipas KUPTIMIT: emri një rresht,
// mbiemri një rresht. Kjo është saktësisht ajo që bën medaljoni i Trashëgimisë,
// dhe arsyeja pse aty s'mbivendoset asgjë.
// 6 shtator 2026 (SKEMA1): `rreshtatEEmrit` u shkri te `emriNeKartele` — i
// njëjti rregull (emri / mbiemri), tani për të gjitha skemat dhe me çelësat e
// Admin Center-it («një rresht» / «dy rreshta», vajzëria, madhësitë).

function wrapNameTwoLines(fullName, maxCharsPerLine) {
  const name = fullName || "";
  if (name.length <= maxCharsPerLine) return [name];
  const words = name.split(" ").filter(Boolean);
  if (words.length < 2) return [truncateCard(name, maxCharsPerLine)];
  let bestSplit = Math.ceil(words.length / 2), bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const line1 = words.slice(0, i).join(" ");
    const line2 = words.slice(i).join(" ");
    const diff = Math.abs(line1.length - line2.length);
    if (diff < bestDiff) { bestDiff = diff; bestSplit = i; }
  }
  const line1 = words.slice(0, bestSplit).join(" ");
  const line2 = words.slice(bestSplit).join(" ");
  return [truncateCard(line1, maxCharsPerLine), truncateCard(line2, maxCharsPerLine)];
}
// SHTESË (kërkesë e userit) — gjeneron vetë shtegun "d" të linjës lidhëse
// prind→fëmijë, sipas 3 stileve të zgjedhshme nga Admin Center:
// - "straight" (origjinali) — cepa 90° të mprehtë, e paprekur.
// - "half_curved" — i njëjti shteg, por cepat e rrumbullakosur (harkore
//   VETËM te 2 thyerjet, jo lakim i plotë) — kërkesë e userit: "vetëm ku ka
//   thyerje 45gradë e bëjmë harkore".
// - "curved" — kurbë e butë bezier, pa cepa fare.
// Hapësira e shiritit të statusit dhe e shiritit të navigimit, si një varg i
// vetëm CSS. Përdoret kudo ku diçka prek buzët e ekranit — shih shënimin e
// gjatë te useEffect-i i StatusBar-it për arsyen pse ka tri shtresa rezerve.
// Zona e sigurt jeton te `zonaESigurt.js` (shih shënimin e gjatë atje: kapaku
// 64/48 dhe pse u desh). Ri-eksportohet këtu me të njëjtat emra që përdor
// gjithë ky skedar, që asnjë thirrje ekzistuese të mos ndryshojë.
const SAFE_TOP = ZONA_LART;
const SAFE_BOTTOM = ZONA_POSHTE;

// Data dhe ora e ndërtimit, të futura nga `vite.config.js`. Rezerva vlen te
// `vite dev` dhe te testet, ku `define` nuk xhiron — pa të, aplikacioni do të
// rrëzohej me «__PEF_BUILD__ is not defined» pikërisht atje ku duhet të punojë.
const VULA_E_NDERTIMIT = (() => {
  try { return typeof __PEF_BUILD__ === "string" ? __PEF_BUILD__ : "dev"; }
  catch (_e) { return "dev"; }
})();

function buildConnectorPath(x1, y1, x2, y2, style, forceMidY) {
  // FIX (kërkesë e userit — "gabim, e ke zhvendosur TË GJITHA vijat
  // horizontale, duhet vetëm te rasti specifik") — kthimi mbrapsht: bend-i
  // "afër prindit" (15%) duhet VETËM kur lidhja KALON 2 breza njëherësh
  // (baba→fëmijë solo, kur mes tyre ndodhet rreshti i nuseve — pikërisht
  // rasti që krijonte konfuzion). Lidhjet NORMALE (1 brez, shumica
  // dërrmuese e pemës) mbeten me thyerje NË MES (50%), siç kanë qenë
  // GJITHMONË — s'duhej ndryshuar fare për to.
  // SHTESË (kërkesë e userit) — `forceMidY`, kur jepet, e anashkalon
  // llogaritjen automatike — përdoret nga rasti "baba→fëmijë solo" (shih
  // poshtë) që lidhja të "bashkohet" vizualisht me vijën baba→nuse deri
  // te lartësia e degëzimit, e të shfaqet si degë shtesë E PO ASAJ shufre,
  // jo si vijë krejt e veçantë.
  // HEQJE (20 gusht 2026) — këtu kishte edhe një `bendOffsetPct` nga Admin
  // Center-i. Ai zhvendoste pikën standarde 50%/15%, POR vetëm kur `forceMidY`
  // mungonte — dhe ajo sot jepet gjithmonë. Pra vlera llogaritej dhe hidhej
  // poshtë në të njëjtin rresht: pikërisht pse admini e pa si "jo funksionale".
  // Lartësinë e vijave horizontale e administron `solo_bend_offset`.
  const spansExtraTier = Math.abs(y2 - y1) > V_SPACING * 1.6;
  const basePct = spansExtraTier ? 0.15 : 0.5;
  const midY = forceMidY ?? (y1 + (y2 - y1) * basePct);
  if (style === "curved") {
    return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
  }
  if (style === "half_curved" && x1 !== x2) {
    const dir = x2 > x1 ? 1 : -1;
    const r = Math.max(2, Math.min(14, Math.abs(midY - y1) * 0.6, Math.abs(x2 - x1) * 0.4));
    return `M ${x1} ${y1} L ${x1} ${midY - r} Q ${x1} ${midY} ${x1 + r * dir} ${midY} L ${x2 - r * dir} ${midY} Q ${x2} ${midY} ${x2} ${midY + r} L ${x2} ${y2}`;
  }
  // "straight" (ose half_curved kur x1===x2, s'ka thyerje për t'u rrumbullakosur)
  return `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
}
// FIX — i njëjti defekt si te admin/src/AdminApp.jsx: supabase-js
// (functions.invoke) NUK e mbush "data" kur ka gabim (jo status 2xx) —
// vetëm "error", dhe error.message në atë rast është përherë teksti
// gjenerik "Edge Function returned a non-2xx status code", JO mesazhi i
// vërtetë që vetë Edge Function-i e ktheu (p.sh. "This person has no email
// on file", "not permitted", etj.) — ai gjendet te error.context (Response
// objekti bruto), i palexuar. Kjo funksion e rilexon trupin real kur
// mundet, dhe bie mbrapa te error.message VETËM nëse s'ka trup të lexueshëm.
async function extractFunctionErrorMessage(error, fallbackData) {
  if (fallbackData?.error) return fallbackData.error;
  if (!error) return "gabim i panjohur";
  try {
    if (error.context && typeof error.context.json === "function") {
      const body = await error.context.clone().json();
      if (body?.error) return body.error;
    }
  } catch (_e) {
    // trupi s'ishte JSON i lexueshëm — bie mbrapa te error.message
  }
  return error.message || String(error);
}

// #1 (signed URLs): cache i URL-ve të nënshkruara me FALLBACK te URL publike.
// Derisa bucket-et janë publike, fallback-u i mban fotot të ngarkuara; kur
// bëhen private, cache-i (mbushur nga efekti më poshtë) shërben signed URLs.
const _signedMedia = { photos: {}, documents: {} };
// mediaProxy — fsheh host-in e Supabase-it nga DOM-i. Rishkruan çdo URL
// storage (`/storage/v1/object/sign/...` ose `/object/public/...`) në një URL
// të NJËJT-ORIGJINË `/_m/...` që e shërben Worker-i (shih worker.js). Kështu
// `...supabase.co` s'del kurrë te "copy image address". Proxy-ja aplikohet
// VETËM në domain-in e prodhimit ku ekziston Worker-i (pemaefamiljes.com); në
// dev ose në Capacitor kthehet URL-ja origjinale (signed URL punon kudo).
function mediaProxy(url) {
  if (!url || typeof url !== "string") return url;
  const i = url.indexOf("/storage/v1/");
  if (i === -1) return url; // s'është URL storage-i i Supabase (p.sh. blob:, data:)
  try {
    if (!/(^|\.)pemaefamiljes\.com$/i.test(window.location.hostname)) return url;
  } catch (e) { return url; }
  return "/_m/" + url.slice(i + "/storage/v1/".length);
}
function photoUrl(path) {
  if (!path) return null;
  const signed = _signedMedia.photos[path];
  // HEQJE E REZERVËS PUBLIKE (20 gusht 2026). Bucket-i "photos" është privat
  // që prej 19 gushtit, pra URL-ja publike NUK mund të punojë më kurrë — ajo
  // ishte rezervë e epokës publike, dhe tani prodhonte vetëm një kërkesë të
  // garantuar 400 për çdo foto: kalimthi për të vërtetat (deri sa mbërrijnë
  // signed URL-të dhe `signedTick` rivizaton), përgjithmonë për rrugët e të
  // dhënave të provës që s'kanë objekt fare. Konsola mbushej me gabime që
  // dukeshin si defekt dhe s'ishin gjë. Pa signed URL: null — asnjë kërkesë.
  return signed ? mediaProxy(signed) : null;
}

// CroppedImg — shfaq një foto të "kropuar" me fokus (x,y) + zoom, njësoj si
// coverImageProps në pemë por në DOM. E ngarkon vetë përmasën natyrale; nëse
// s'e ka ende, bie te CSS object-position (pa zoom) — asgjë s'prishet.
// `downloadable=false` çaktivizon right-click/drag (rregulli i shkarkimit).
function CroppedImg({ src, focusX = 0.5, focusY = 0.5, zoom = 1, size = 72, round = false, downloadable = true, className, style }) {
  const [nat, setNat] = useState(null);
  useEffect(() => {
    if (!src) { setNat(null); return; }
    let ok = true;
    const img = new window.Image();
    img.onload = () => { if (ok) setNat({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.src = src;
    return () => { ok = false; };
  }, [src]);
  if (!src) return null;
  const restrict = !downloadable;
  const z = Math.max(1, Math.min(5, zoom || 1));
  const guard = restrict ? { onContextMenu: (e) => e.preventDefault(), draggable: false } : {};
  const imgBase = { pointerEvents: restrict ? "none" : "auto", userSelect: restrict ? "none" : "auto", WebkitTouchCallout: restrict ? "none" : "default" };
  let inner;
  if (nat && nat.w && nat.h) {
    const scale = Math.max(size / nat.w, size / nat.h) * z;
    const dispW = nat.w * scale, dispH = nat.h * scale;
    inner = <img src={src} alt="" {...guard} style={{ position: "absolute", width: dispW, height: dispH, left: -(dispW - size) * focusX, top: -(dispH - size) * focusY, maxWidth: "none", ...imgBase }} />;
  } else {
    inner = <img src={src} alt="" {...guard} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: `${focusX * 100}% ${focusY * 100}%`, ...imgBase }} />;
  }
  return (
    <div className={className} {...(restrict ? { onContextMenu: (e) => e.preventDefault() } : {})} style={{ position: "relative", width: size, height: size, overflow: "hidden", borderRadius: round ? "50%" : 8, ...style }}>
      {inner}
    </div>
  );
}

// ZoomableImage — foto në "pamjen e plotë" (lightbox): hapet fit; një klik e
// zmadhon (fit ↔ 2.5x), klik prapë e kthen; zvarritje kur është e zmadhuar;
// pinch-to-zoom në mobile. Mbyllja bëhet nga backdrop-i/X (klik mbi foton NUK
// mbyll — ai bën zoom). `restricted` çaktivizon right-click/drag.
function ZoomableImage({ src, restricted = false, maxW = "94vw", maxH = "88vh" }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const drag = useRef(null);
  const pinch = useRef(null);
  const dragging = useRef(false);
  const reset = () => { setScale(1); setTx(0); setTy(0); };
  const toggle = (e) => { e.stopPropagation(); if (scale > 1) reset(); else setScale(2.5); };
  const onMouseDown = (e) => { if (scale <= 1) return; e.stopPropagation(); dragging.current = true; drag.current = { x: e.clientX, y: e.clientY, tx, ty }; };
  const onMouseMove = (e) => { if (!dragging.current || !drag.current) return; setTx(drag.current.tx + (e.clientX - drag.current.x)); setTy(drag.current.ty + (e.clientY - drag.current.y)); };
  const onMouseUp = () => { dragging.current = false; drag.current = null; };
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) pinch.current = { d: dist(e.touches), s: scale };
    else if (e.touches.length === 1 && scale > 1) drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx, ty };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      e.stopPropagation();
      const ns = Math.max(1, Math.min(4, pinch.current.s * (dist(e.touches) / pinch.current.d)));
      setScale(ns); if (ns <= 1.01) { setTx(0); setTy(0); }
    } else if (e.touches.length === 1 && drag.current) {
      e.stopPropagation();
      setTx(drag.current.tx + (e.touches[0].clientX - drag.current.x));
      setTy(drag.current.ty + (e.touches[0].clientY - drag.current.y));
    }
  };
  const onTouchEnd = (e) => { if (e.touches.length === 0) { drag.current = null; pinch.current = null; } };
  const guard = restricted ? { onContextMenu: (e) => e.preventDefault(), draggable: false } : {};
  return (
    <img
      src={src} alt="" {...guard}
      onClick={toggle}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      style={{ maxWidth: maxW, maxHeight: maxH, objectFit: "contain", borderRadius: 8, transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transition: dragging.current || pinch.current ? "none" : "transform 0.18s ease", cursor: scale > 1 ? "grab" : "zoom-in", touchAction: "none", userSelect: restricted ? "none" : "auto", WebkitTouchCallout: restricted ? "none" : "default" }}
    />
  );
}

// CropEditor — editor i kropit (pan + zoom + katror). I gjithë viewport-i
// katror ËSHTË kropi (çka del në pemë). Përdoruesi lëviz foton (drag / një
// gisht) dhe zmadhon (slider / pinch). Thërret onChange({x,y,zoom}) live.
function CropEditor({ src, focusX = 0.5, focusY = 0.5, zoom = 1, onChange }) {
  const [nat, setNat] = useState(null);
  const [box, setBox] = useState(300);
  const drag = useRef(null);
  const pinch = useRef(null);
  const setRef = useCallback((el) => { if (el && el.clientWidth) setBox(el.clientWidth); }, []);
  useEffect(() => {
    if (!src) { setNat(null); return; }
    let ok = true;
    const img = new window.Image();
    img.onload = () => { if (ok) setNat({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.src = src;
    return () => { ok = false; };
  }, [src]);
  const z = Math.max(1, Math.min(5, zoom || 1));
  const scale = nat ? Math.max(box / nat.w, box / nat.h) * z : 1;
  const dispW = nat ? nat.w * scale : box, dispH = nat ? nat.h * scale : box;
  const left = -(dispW - box) * focusX, top = -(dispH - box) * focusY;
  const applyPan = (nl, nt) => {
    if (!nat) return;
    const fx = dispW > box ? -nl / (dispW - box) : 0.5;
    const fy = dispH > box ? -nt / (dispH - box) : 0.5;
    onChange({ x: Math.min(1, Math.max(0, fx)), y: Math.min(1, Math.max(0, fy)), zoom: z });
  };
  const onMouseDown = (e) => { if (!nat) return; drag.current = { x: e.clientX, y: e.clientY, left, top }; };
  const onMouseMove = (e) => { if (!drag.current) return; applyPan(drag.current.left + (e.clientX - drag.current.x), drag.current.top + (e.clientY - drag.current.y)); };
  const onMouseUp = () => { drag.current = null; };
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) pinch.current = { d: dist(e.touches), z };
    else if (e.touches.length === 1) drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, left, top };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      const nz = Math.max(1, Math.min(5, pinch.current.z * (dist(e.touches) / pinch.current.d)));
      onChange({ x: focusX, y: focusY, zoom: nz });
    } else if (e.touches.length === 1 && drag.current) {
      applyPan(drag.current.left + (e.touches[0].clientX - drag.current.x), drag.current.top + (e.touches[0].clientY - drag.current.y));
    }
  };
  const onTouchEnd = (e) => { if (e.touches.length === 0) { drag.current = null; pinch.current = null; } };
  return (
    <div>
      <div
        ref={setRef}
        style={{ position: "relative", width: "min(320px, 100%)", height: box, margin: "0 auto", background: "#000", borderRadius: 8, overflow: "hidden", cursor: "grab", userSelect: "none", touchAction: "none" }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      >
        {src && nat ? (
          <img src={src} alt="" draggable={false} style={{ position: "absolute", width: dispW, height: dispH, left, top, maxWidth: "none", pointerEvents: "none" }} />
        ) : src ? (
          <img src={src} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} />
        ) : null}
        <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.65)", borderRadius: 8, pointerEvents: "none" }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
        <span style={{ fontSize: 15, opacity: 0.7 }}>−</span>
        <input type="range" min="1" max="5" step="0.02" value={z} onChange={(e) => onChange({ x: focusX, y: focusY, zoom: parseFloat(e.target.value) })} style={{ flex: 1 }} />
        <span style={{ fontSize: 17, opacity: 0.7 }}>+</span>
      </div>
    </div>
  );
}

// ReactionBar (Faza 6) — shirit i vogël reagimesh (like/emoji) nën një postim
// të feed-it (kujtim). `reactions` = lista për këtë event [{emoji, reactor_account_id}].
const REACTION_EMOJIS = ["❤️", "👍", "🙏", "😊", "😢"];
// RAPORTIMI1 (31 gusht 2026) — pse butoni «Raporto» hyri PIKËRISHT këtu.
//
// Apple, rregulli 1.2: përmbajtja e shkruar nga njerëzit duhet të ketë buton
// raportimi TE VETË PËRMBAJTJA. Kujtimi vizatohet në dy vende — te ballina
// (rr. ~17850) dhe te «Historia» (rr. ~18105) — dhe të dyja e mbyllin kartelën
// me këtë shirit. Pra një ndryshim këtu i mbulon të dyja, dhe s'ka rrezik që
// njëra kartelë ta ketë butonin e tjetra jo.
//
// Shiriti është gjithashtu i vetmi rresht veprimesh që NUK rri pas `canManage`.
// Kjo s'është rastësi që e shfrytëzojmë: raportimi i takon pikërisht anëtarit
// që s'ka leje të fshijë asgjë vetë.
function ReactionBar({ eventId, reactions, myAccountId, onToggle, colors, raporto = null }) {
  // `t` këtu NUK vjen nga App — ky komponent rri në nivel moduli, jashtë çdo
  // komponenti tjetër, prandaj e merr vetë përkthimin.
  const { t } = useTranslation();
  const counts = {}, mine = {};
  (reactions || []).forEach((r) => {
    counts[r.emoji] = (counts[r.emoji] || 0) + 1;
    if (myAccountId && r.reactor_account_id === myAccountId) mine[r.emoji] = true;
  });
  // KARTELA1 — `etiketa` kur ekziston: reagimet rrinë BRENDA kartelës, dhe
  // ari mbi letër të bardhë jep 2,2:1. Rezerva mbetet ari, për thirrjet e vjetra.
  const gold = (colors && (colors.etiketa || colors.gold)) || "#B8935A";
  const muted = (colors && colors.inkLight) || "#8a7f6f";
  const eh = pamja("feed", "heirloom");

  // ── 4 shtator 2026 — REAGIMET HYJNË NË NJË DROPDOWN ───────────────────────
  // Pronari: «te emojit në postime për reaction do doja të shfaqen me dropdown
  // dhe vetëm emojit që bëjnë reaction të shfaqen karshi dropdownit».
  //
  // Arsyeja pse ka kuptim: pesë pilula bosh nën çdo postim janë pesë ftesa që
  // askush s'i ka pranuar. Ato zënë të njëjtën hapësirë sa reagimet e vërteta
  // dhe e bëjnë një kujtim pa asnjë reagim të duket i shkretë. Me dropdown,
  // rreshti tregon VETËM atë që ka ndodhur — dhe kur s'ka ndodhur asgjë, rri
  // vetëm një buton i qetë.
  const [hapur, setHapur] = useState(false);
  const kutia = useRef(null);
  useEffect(() => {
    if (!hapur) return;
    const jashtë = (e) => { if (kutia.current && !kutia.current.contains(e.target)) setHapur(false); };
    const esc = (e) => { if (e.key === "Escape") setHapur(false); };
    document.addEventListener("mousedown", jashtë);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", jashtë); document.removeEventListener("keydown", esc); };
  }, [hapur]);

  const stili = (active) => eh
    ? { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
        borderRadius: 16, fontSize: 13, fontWeight: 600, lineHeight: 1, cursor: "pointer",
        background: active ? (PALETA_TIPI.surface2 || COLORS.accentTint) : (PALETA_TIPI.surface || "#fdfaf4"),
        border: `1px solid ${active ? (PALETA_TIPI.bronze || gold) : (PALETA_TIPI.accentBorder || "rgba(0,0,0,0.08)")}`,
        color: PALETA_TIPI.text || "inherit" }
    : { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px",
        borderRadius: 999, fontSize: 13, lineHeight: 1.4, cursor: "pointer",
        background: active ? COLORS.accentTint : "rgba(0,0,0,0.04)",
        border: `1px solid ${active ? gold : "rgba(0,0,0,0.08)"}` };

  // Vetëm ato që kanë të paktën një reagim. Rendi mbetet ai i `REACTION_EMOJIS`,
  // që dy postime me të njëjtat reagime të duken njësoj — jo sipas kush erdhi i pari.
  const meReagime = REACTION_EMOJIS.filter((em) => (counts[em] || 0) > 0);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: eh ? 7 : 6, marginTop: eh ? 13 : 8, alignItems: "center" }}>
      <div ref={kutia} style={{ position: "relative", display: "inline-flex" }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setHapur((v) => !v); }}
          aria-haspopup="true" aria-expanded={hapur}
          title={t("React")}
          style={{ ...stili(hapur), gap: eh ? 5 : 4 }}
        >
          <Smile size={eh ? 16 : 14} strokeWidth={1.75} />
          <ChevronDown size={eh ? 13 : 12} strokeWidth={2}
            style={{ transform: hapur ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
        </button>
        {hapur && (
          <div
            role="menu"
            style={{
              position: "absolute", bottom: "calc(100% + 6px)", left: 0, zIndex: 40,
              display: "flex", gap: 2, padding: 4, borderRadius: 999,
              background: PALETA_TIPI.surface || COLORS.panel || "#fff",
              border: `1px solid ${PALETA_TIPI.accentBorder || "rgba(0,0,0,0.10)"}`,
              boxShadow: "0 8px 24px -10px rgba(0,0,0,.35)",
            }}
          >
            {REACTION_EMOJIS.map((em) => (
              <button
                key={em} type="button" role="menuitem"
                onClick={(e) => { e.stopPropagation(); onToggle && onToggle(eventId, em); setHapur(false); }}
                title={em}
                style={{
                  width: 32, height: 32, borderRadius: "50%", border: "none", cursor: "pointer",
                  background: mine[em] ? (PALETA_TIPI.surface2 || COLORS.accentTint) : "transparent",
                  fontSize: 17, lineHeight: 1, padding: 0,
                }}
              >{em}</button>
            ))}
          </div>
        )}
      </div>
      {meReagime.map((em) => (
        <button
          key={em} type="button"
          onClick={(e) => { e.stopPropagation(); onToggle && onToggle(eventId, em); }}
          style={stili(!!mine[em])}
        >
          <span style={eh ? { fontSize: 16, lineHeight: 1 } : undefined}>{em}</span>
          <span style={{ fontSize: eh ? 13 : 11, color: eh ? (PALETA_TIPI.text || muted) : muted }}>{counts[em]}</span>
        </button>
      ))}
      {raporto && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); raporto.hap(); }}
          title={raporto.titulli}
          style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 9px", borderRadius: 999, fontSize: 12, lineHeight: 1.4,
            cursor: "pointer", background: "transparent", border: "1px solid rgba(0,0,0,0.08)",
            color: muted,
          }}
        >
          ⚑ {raporto.etiketa}
        </button>
      )}
    </div>
  );
}
// MODULI 27 — "kohë relative" (p.sh. "3 orë më parë") për Qendrën e
// Ndihmës — s'ekzistonte më parë këtu (vetëm te Admin Center).
// "3 orë më parë" — teksti e jep vetë shfletuesi (Intl.RelativeTimeFormat), pra
// del në gjuhën e pajisjes pa asnjë çelës përkthimi. Shih src/datetime.js.
function timeAgo(dateStr) {
  return fmtRelative(dateStr);
}
function isBirthdayToday(dateStr) {
  if (!dateStr) return false;
  const [, m, d] = dateStr.split("-");
  const now = new Date();
  return Number(m) === now.getMonth() + 1 && Number(d) === now.getDate();
}
function birthdayDaysUntil(dateStr) {
  if (!dateStr) return null;
  const [, m, d] = dateStr.split("-").map(Number);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let next = new Date(now.getFullYear(), m - 1, d);
  next.setHours(0, 0, 0, 0);
  if (next < now) next.setFullYear(next.getFullYear() + 1);
  return Math.round((next - now) / 86400000);
}
// SA VJEÇ BËHET NË DITËLINDJEN E ARDHSHME.
//
// Këtu ishte një gabim që dukej vetëm gjysmën e vitit. Llogaritej si
// `viti i sotëm − viti i lindjes + 1`, me shënimin «përafërt» — dhe përafërt do
// të thoshte: i saktë vetëm PAS ditëlindjes së këtij viti, dhe një vit më shumë
// para saj. Dikush i lindur më 1990, i parë në janar 2026, dilte se «mbush 37»
// kur në të vërtetë mbush 36.
//
// Rregulli i vërtetë s'ka nevojë për përafrim: gjej vitin e ditëlindjes së
// ARDHSHME — po ajo llogari që bën `birthdayDaysUntil` — dhe zbrit vitin e
// lindjes. Data lexohet nga vargu, jo me `new Date(...)`, sepse ajo e fundit e
// merr mesnatën si UTC dhe te brezat perëndimorë mund ta zhvendosë vitin.
function birthdayNextAge(dateStr) {
  if (!dateStr) return null;
  const [by, m, d] = String(dateStr).split("-").map(Number);
  if (!by || !m || !d) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let next = new Date(now.getFullYear(), m - 1, d);
  next.setHours(0, 0, 0, 0);
  if (next < now) next.setFullYear(next.getFullYear() + 1);   // kaloi këtë vit
  const years = next.getFullYear() - by;
  return years >= 0 ? years : null;
}
function deathAnniversaryInfo(dateStr) {
  if (!dateStr) return null;
  const [dy, dm, dd] = dateStr.split("-").map(Number);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let next = new Date(now.getFullYear(), dm - 1, dd);
  next.setHours(0, 0, 0, 0);
  let years = next.getFullYear() - dy;
  if (next < now) { next.setFullYear(next.getFullYear() + 1); years += 1; }
  const daysUntil = Math.round((next - now) / 86400000);
  return { daysUntil, years };
}
function compressToBlob(file, size = 480, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const minSide = Math.min(img.width, img.height);
        const sx = (img.width - minSide) / 2;
        const sy = (img.height - minSide) / 2;
        ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, size, size);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Compress an image WITHOUT cropping — keeps the entire photo, just scales it
// down to fit within maxSize on its longest side. Use this anywhere the full
// picture must remain visible (e.g. family history events); compressToBlob
// above intentionally center-crops to a square and should stay reserved for
// circular/square avatars.
function compressToBlobFit(file, maxSize = 1000, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ---------- layout ----------
// FIX/SHTESË (kërkesë e userit) — këto ishin `const` (të pandryshueshme).
// Tani `let`, sepse Admin Center → Cilësimet lejon rregullimin e hapësirës
// mes vëllezërve/motrave dhe çifteve, DHE zgjedhjen e skemës së kartelës
// (horizontal/vertikal — kjo e dyta ka nevojë për kartelë më të ngushtë).
// BASE_* mbeten fikse, referencë për shumëzuesit (1.0 = origjinali, i
// pandryshuar). Ndryshohen VETËM nga applyTreeDisplaySettings() (poshtë),
// e thirrur një herë kur cilësimet ngarkohen nga databaza — çdo leximi
// tjetër i H_SPACING/V_SPACING/SPOUSE_OFFSET/CARD_W/CARD_H në kod e sheh
// automatikisht vlerën e fundit (janë ndryshore të thjeshta module-scope,
// jo const), pa pasur nevojë të kalohen si parametra kudo.
const BASE_H_SPACING = 172, BASE_V_SPACING = 168, BASE_SPOUSE_OFFSET = 0.78;
const CARD_W_HORIZONTAL = 132, CARD_H_HORIZONTAL = 58;
const CARD_W_VERTICAL = 96, CARD_H_VERTICAL = 112;
let H_SPACING = BASE_H_SPACING, V_SPACING = BASE_V_SPACING, SPOUSE_OFFSET = BASE_SPOUSE_OFFSET;
let CARD_W = CARD_W_HORIZONTAL, CARD_H = CARD_H_HORIZONTAL;
// Rreshti i fundit i cilësimeve të pemës, i mbajtur që përmasat të mund të
// rillogariten kur ndryshon dizajni — jo vetëm kur rilexohen cilësimet.
let RRESHTI_I_PEMES = null;
const SKEMAT_FOTO_LART = ["vertical", "elegant", "elegantoriginal", "modern3d", "modernphoto", "story", "glass", "heirloom"];
// ── CILËSIMET PËR SKEMË (SKEMA1, 6 shtator 2026) ────────────────────────────
// Deri sot çdo cilësim nën «The tree» ishte global — një numër për të gjitha
// skemat. Po shkronja të ndryshme kanë standard të ndryshëm, dhe rregullimi i
// një skeme e prishte tjetrën. Tani baza mban një rresht MBIVENDOSJESH për
// skemë (`tree_scheme_settings`): katër kuti JSON — display, lines, advanced,
// emri. Klienti i lexon të gjitha me një thirrje dhe i hedh mbi vlerat
// globale të skemës AKTIVE. Skema pa rresht = globalja = si sot.
let SKEMAT_CILESIMET = {};   // { dizajni: { display, lines, advanced, emri } }
let SKEMAT_PROMISE = null;
let RRESHTI_VIJAVE = null;     // rreshti global i vijave (rezerva kur dizajni ndërron)
let RRESHTI_AVANCUAR = null;   // rreshti global i gjeometrisë
function ngarkoSkemat() {
  if (!SKEMAT_PROMISE) {
    SKEMAT_PROMISE = fetchSettingRpcWithRetry("rpc_get_tree_scheme_settings").then((d) => {
      SKEMAT_CILESIMET = (d && typeof d === "object" && !Array.isArray(d)) ? d : {};
      return SKEMAT_CILESIMET;
    });
  }
  return SKEMAT_PROMISE;
}
// Çelësi (SKEMA2): DIZAJNI aktiv — «Trashëgimia», «Rrënjët»… — ose «klasike»
// kur s'ka dizajn. Pronari: «zgjedh dizajnin në kokë dhe dalin të gjitha
// cilësimet për të; çdo ndryshim ka efekt vetëm te ai dizajn».
function skemaAktive() {
  try { const d = getDesign(); if (d && d.id) return String(d.id); } catch (_e) { /* pa dizajn */ }
  return "klasike";
}
function mbivendosjaESkemes(pjesa) {
  const s = SKEMAT_CILESIMET[skemaAktive()];
  const k = s && s[pjesa];
  return (k && typeof k === "object" && !Array.isArray(k)) ? k : {};
}
// A vizatohet pema si MEDALJON heirloom? (SKEMA2, 6 shtator 2026)
// Deri sot e vendoste vetëm tokeni i dizajnit `layout.pamja.pema`. Tani, kur
// admini ka zgjedhur një skemë kartele PËR KËTË DIZAJN te The tree, ajo fiton:
// «heirloom» = medaljoni, çdo skemë tjetër = kartela klasike e asaj skeme.
// Pa zgjedhje të tillë, vendos tokeni si më parë.
function pemaHeirloom() {
  const m = mbivendosjaESkemes("display");
  if (m.card_layout) return m.card_layout === "heirloom";
  return pamja("pema", "heirloom");
}
// Numrat vijnë si TEKST nga JSON-i i adminit ose nga kolonat `numeric` —
// kthehen në numra, dhe ç'nuk është numër hiqet (asnjë NaN në gjeometri).
function pastroNumrat(obj, celesat) {
  const out = { ...obj };
  celesat.forEach((k) => {
    if (out[k] !== undefined && out[k] !== null && out[k] !== "") {
      const n = Number(out[k]);
      if (Number.isFinite(n)) out[k] = n; else delete out[k];
    } else delete out[k];
  });
  return out;
}
const CELESAT_NUMERIKE_DISPLAY = ["sibling_spacing", "couple_spacing", "card_width_h", "card_height_h",
  "card_width_v", "card_height_v", "generation_spacing", "photo_ring_width", "photo_ring_gap", "expand_btn_size"];
const CELESAT_NUMERIKE_ADVANCED = ["multi_spouse_gap_mult", "solo_child_first_offset", "solo_child_gap",
  "line_thickness", "expand_btn_offset_x", "expand_btn_offset_y", "spouse_line_offset_y", "solo_bend_offset"];
// Rreshti global + mbivendosja e dizajnit aktiv — përfshirë `card_layout`
// (SKEMA2: edhe skema e kartelës është cilësim i dizajnit).
function rreshtiIPemesEfektiv() {
  const row = RRESHTI_I_PEMES;
  if (!row) return null;
  const m = pastroNumrat(mbivendosjaESkemes("display"), CELESAT_NUMERIKE_DISPLAY);
  return { ...row, ...m, card_layout: m.card_layout || row.card_layout };
}
// Emri te kartela — çelësat e kutisë `emri`, me parazgjedhje = si sot.
function stiliIEmrit() {
  const e = mbivendosjaESkemes("emri");
  const px = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null; };
  return {
    emri_px: px(e.emri_px), mbiemri_px: px(e.mbiemri_px), vitet_px: px(e.vitet_px),
    rreshtat: e.rreshtat === "nje" ? "nje" : "dy",
    vajzeria: e.vajzeria === true || e.vajzeria === "true",
  };
}

function zbatoPermasatEKarteles() {
  const row = rreshtiIPemesEfektiv();
  if (!row) return;
  // «heirloom» e ndezur për ekranin e pemës e detyron çiftin vertikal, sepse
  // ajo pamje është gjithmonë foto lart — pavarësisht ç'ka zgjedhur admini.
  const fotoLart = SKEMAT_FOTO_LART.includes(row.card_layout) || pemaHeirloom();
  if (fotoLart) {
    CARD_W = row.card_width_v || CARD_W_VERTICAL;
    CARD_H = row.card_height_v || CARD_H_VERTICAL;
  } else {
    CARD_W = row.card_width_h || CARD_W_HORIZONTAL;
    CARD_H = row.card_height_h || CARD_H_HORIZONTAL;
  }
}
// Lartësia e VËRTETË e përmbajtjes te medaljoni «heirloom»: shiriti, fotoja,
// dy rreshtat e emrit dhe vitet. NUK varet nga CARD_H — kutia që cakton admini
// — sepse medaljoni s'ka kuti fare: nëse CARD_H ulet, teksti mbetet aty ku
// është dhe butoni «+» i bie sipër viteve. Pikërisht kjo ndodhi:
// «kur një njeri ka vit lindje edhe vdekje po fshehet nga plus-minusi».
function lartesiaEMedaljonit() {
  const rFoto = Math.max(14, Math.min(30, CARD_W * 0.21));
  // SKEMA1: një rresht më pak kur emri e mbiemri rrinë bashkë, një më shumë
  // kur shfaqet mbiemri i vajzërisë.
  const e = stiliIEmrit();
  const rreshta = (e.rreshtat === "nje" ? -13 : 0) + (e.vajzeria ? 13 : 0);
  return 10 + 6 + 2 * rFoto + 14 + 27 + 12 + rreshta;
}

// SHTESË (kërkesë e userit — "+/- është shumë poshtë te foto pezull") —
// te stilet "foto pezull" (Elegant-oval, Modern Foto Pezull), butoni "+"
// të zbulimit të fëmijëve ishte gjithmonë i pozicionuar te fundi i
// CARD_H (kutia admin-konfigurueshme), edhe pse ato stile s'kanë FARE
// kuti/sfond — përmbajtja e vërtetë (foto+emër+vit) mbaron shumë më lart.
// `ROW_CONTENT_H` mban lartësinë E VËRTETË të përmbajtjes për stilin
// AKTUAL (njësoj si `rowContentHeight` në llogaritjen e V_SPACING —
// shih loadTreeDisplaySettings) — përdoret nga expandButtons (jo CARD_H
// direkt), që butoni "+" të "ngjitet" pas fotos/tekstit real, jo pas
// kutisë boshe.
let ROW_CONTENT_H = CARD_H_HORIZONTAL;
/**
 * Computes an {x, y} grid position (not pixels — multiply by H_SPACING/
 * V_SPACING when drawing) for every person in `people`.
 *
 * y = generation depth from whichever root(s) the tree has.
 * x for a leaf (no children) comes from a left-to-right cursor.
 * x for a parent = the average of their CHILDREN'S COUPLE CENTERS (not the
 * children's raw individual x). This is the key trick that keeps the
 * connector line straight through many generations: if we averaged the
 * children's own x instead, every generation with a spouse would nudge the
 * line sideways (spouses are placed to one side of their partner), and a
 * long single-child ancestry chain would drift into a diagonal instead of
 * staying vertical. See ARCHITECTURE.md §7 for the full story.
 *
 * Couples are placed symmetrically around that same center (partner at
 * center - offset/2, spouse at center + offset/2) for the same reason —
 * asymmetric placement (primary at center, spouse further right) would
 * reintroduce the drift one generation up.
 */
// ==========================================================================
// computeLayout — RISHKRUAR (v2): "blloqe me kontura" (contour packing).
// Zëvendëson versionin e vjetër me 5 degë të veçanta vendosjeje + `cursor`
// global. Parimet:
//   1) Çdo person kthen një BLLOK ku kartela e vet është në x=0, bashkë me
//      KONTURËN e nën-pemës (min/max x për ÇDO rresht).
//   2) Vëllezërit/grupet ndahen sipas konturës REALE — jo sipas numërimit
//      të fëmijëve. Kjo heq njëkohësisht mbivendosjet DHE boshllëqet e
//      tepërta (defekti "hapësirë e madhe para Anisës").
//   3) Hapësira e çiftit s'bie kurrë nën gjerësinë e kartelës + vendin e
//      ikonës së statusit — ndaj ikona ka gjithmonë ku të futet.
//   4) Grupet e fëmijëve qendërzohen te QENDRA E ÇIFTIT të fëmijës (jo te
//      kartela), që zinxhirët me një fëmijë të mbeten vertikalë.
// Rregullat ekzistuese ruhen: renditja sipas datëlindjes, i ndjerë/i
// divorcuar majtas & aktivja djathtas, fëmijët solo anash, 3+ nuse në
// nën-rresht (multiWifeTiers).
// ==========================================================================
// SHIRITI I PEMËS — konfigurimi i parazgjedhur (Admin Center mund ta
// mbishkruajë: cilat ikona duken, renditja, orientimi dhe pozicioni).
// Adresa e vetme publike e kontaktit (shfaqet te ekrani i hyrjes).
const SUPPORT_EMAIL = "kontakt@pemaefamiljes.com";

const TREEBAR_DEFAULT = {
  items: [
    { key: "home", on: true }, { key: "search", on: true }, { key: "view", on: true },
    { key: "poster", on: true }, { key: "settings", on: true }, { key: "zoomin", on: true },
    { key: "zoomout", on: true }, { key: "fit", on: true }, { key: "add", on: true },
  ],
  orientation: "horizontal",
  position: "bottom",
};

// ============================================================================
// KOLEKSIONI — vendosja kur figurat nuk kanë lidhje mes tyre
//
// 29 gusht 2026, MUZEU3. Pronari: *«te muzeu mund të ketë njerëz që nuk kanë
// lidhje familjare biologjike… nuk do ketë ngjyra, nuk do ketë persona që
// lidhen me personazhet historike»*.
//
// `computeLayout` e ndërton pemën nga prind–fëmijë–bashkëshort. Pa asnjë lidhje
// ai nuk ka çfarë të ndërtojë: çdo figurë do të ishte rrënjë e vetme, dhe do të
// dilte një rresht i gjatë pa kuptim.
//
// PYETJA E VËRTETË: çfarë e zëvendëson gjakun si bosht renditjeje?
// Përgjigjja e vetë muzeut: **salla**, dhe brenda saj **koha**. Kjo nuk është
// term i shpikur — është fjala që përdor çdo muze për të njëjtën punë.
//
// Renditja brenda sallës është ajo e `familyBook.js`: sipas datëlindjes, kush
// s'ka datë shkon i fundit. Një rregull, dy vende — jo dy rregulla.
//
// Kthen të njëjtën formë si `computeLayout`, me `childrenMap` bosh: pa lidhje
// nuk ka vija, dhe vizatuesi nuk ka nevojë të dijë se cila paraqitje e thirri.
const KOLEKSION_PER_RRESHT = 5;   // sa figura në një rresht brenda një salle
const KOLEKSION_HAP_SALLASH = 1.1; // hapësira mes sallave, në rreshta

function radhaEKoleksionit(a, b) {
  // Pa datë → i fundit. Dy pa datë → alfabetikisht, që radha të mos ndryshojë
  // sa herë rifreskohet faqja.
  const da = a?.birth_date || "";
  const db = b?.birth_date || "";
  if (da && db) return da < db ? -1 : da > db ? 1 : 0;
  if (da) return -1;
  if (db) return 1;
  const na = `${a?.first_name || ""} ${a?.last_name || ""}`.trim().toLowerCase();
  const nb = `${b?.first_name || ""} ${b?.last_name || ""}`.trim().toLowerCase();
  return na < nb ? -1 : na > nb ? 1 : 0;
}

function paraqitKoleksionin(people, opts) {
  // `paSalla` = pamja «Sipas kohës»: të gjithë në një radhë të vetme kronologjike,
  // sallat lihen mënjanë. Nuk është layout i dytë — është i njëjti, me një grup.
  const paSalla = !!(opts && opts.paSalla);
  const positions = {};
  const salla = {};   // emri i sallës → [id]
  const ids = Object.keys(people || {});

  ids.forEach((id) => {
    const p = people[id];
    if (!p) return;
    // Pa sallë të caktuar figurat mblidhen bashkë. Nuk u shpikim emër: një
    // etiketë e trilluar do të dukej si e dhënë e vërtetë te ekspozita.
    const emri = paSalla ? "" : ((p.salla || "").trim() || "");
    (salla[emri] = salla[emri] || []).push(id);
  });

  // Sallat me emër alfabetikisht; ajo pa emër shkon e fundit — figurat ende të
  // pavendosura nuk duhet ta hapin ekspozitën.
  const emrat = Object.keys(salla).sort((a, b) => {
    if (a === "") return 1;
    if (b === "") return -1;
    return a.localeCompare(b, "sq");
  });

  const bandat = [];
  let y = 0;
  emrat.forEach((emri) => {
    const lista = salla[emri].slice().sort((a, b) => radhaEKoleksionit(people[a], people[b]));
    const rreshta = Math.max(1, Math.ceil(lista.length / KOLEKSION_PER_RRESHT));
    lista.forEach((id, i) => {
      positions[id] = {
        x: i % KOLEKSION_PER_RRESHT,
        y: y + Math.floor(i / KOLEKSION_PER_RRESHT),
      };
    });
    bandat.push({ emri, nga: y, deri: y + rreshta - 1, sa: lista.length });
    y += rreshta + KOLEKSION_HAP_SALLASH;
  });

  return { positions, childrenMap: {}, multiWifeTiers: {}, multiMarriageParents: {}, bandat };
}

// ---- rrënjët ----
// FIX (posteri dy-familjesh, modaliteti KONVERGJENT — "prap e njëjta
// anomali", ndërsa pema e plotë dhe krah-për-krah u rregulluan): pamja
// konvergjente është E VETMJA që FILTRON njerëz (posterExcludeIds, "vetëm
// pasardhësit e dy paraardhësve"). Paraardhësi i zgjedhur (p.sh. Xhemajli)
// VAZHDON të ketë parent_ids drejt prindërve të vet TË FSHEHUR nga filtri
// — dhe filtri i vjetër i rrënjëve kontrollonte vetëm GJATËSINË e
// parent_ids, jo praninë REALE në hartën e dukshme. Pasoja: familja
// aktive mbetej PA asnjë rrënjë të njohur, vendosja binte te rezerva
// ids.forEach (rend arbitrar), dhe rrënja e familjes tjetër (p.sh. Shaban
// Orlishta, që s'ka fare prindër të regjistruar) dilte E PARA → familja e
// saj majtas, vija jote e zgjatur — pikërisht anomalia e raportuar. Tani
// "prind" numërohet VETËM nëse është realisht i dukshëm në këtë pamje —
// kështu paraardhësit e zgjedhur bëhen rrënjë të mirëfillta, familja
// aktive vendoset e para (majtas) dhe ankorimi i bllokut tjetër te çifti
// funksionon njësoj si te pema e plotë.
// ── MYSAFIRI (dikur «honorary») ─────────────────────────────────────────
//
// 29 gusht 2026, pronari: «fjala honorary nuk po më pëlqen … guest, mysafir
// është fjala e duhur». Teksti që sheh njeriu e thoshte prej kohësh
// «Mysafir»; vetëm vlera e ruajtur mbeti anglisht.
//
// PSE NJË FUNKSION DHE JO NJË KRAHASIM. Krahasimi `member_kind === "…"` ishte
// shkruar në gjashtë vende. Gjatë kalimit baza mban të dyja vlerat — dhe
// gjashtë krahasime do të thonin gjashtë raste ku dikush e harron të dytën.
// Këtu është një.
//
// «honorary» pranohet vetëm derisa të xhirohet MYSAFIRI2; pastaj mbetet i
// padëmshëm, sepse asnjë rresht s'e ka më atë vlerë.
// VENDI: NIVEL MODULI, JO BRENDA `computeLayout`.
// E pata shkruar brenda tij, dhe `computeLayout` punonte — por katër thirrjet e
// tjera janë te vetë komponenti (pamja e dukshme, kartela, dy butonat e
// mysafirit). Ato s'e shihnin dot: `ReferenceError: eshteMysafir is not defined`,
// ekran i bardhë. esbuild-i nuk e kap — një identifikues i pandeklaruar është
// gabim vetëm në xhirim.
function eshteMysafir(p) { return p?.member_kind === "mysafir" || p?.member_kind === "honorary"; }

function computeLayout(people, advLayout, layoutOpts) {
  const adv = advLayout || {};
  // SHTESË (posteri dy-familjesh — "ne me Meritën llogaritemi si më të rinjtë
  // dhe listohemi të fundit, që të jemi epiqendra ku bashkohen dy familjet"):
  // renditje e detyruar brenda grupit të vëllezërve/motrave, PAVARËSISHT
  // datëlindjes — sortLast i çon në fund (skaji i djathtë i familjes A, ku
  // ankorohet familja B → s'ka vija të gjata), sortFirst në fillim (kopja e
  // personit urë në pemën B, krah-për-krah, të rrijë ngjitur me pemën A).
  // Boshe në pemën normale — layout-i i përditshëm i paprekur.
  const sortFirstSet = (layoutOpts && layoutOpts.sortFirst) || new Set();
  const sortLastSet = (layoutOpts && layoutOpts.sortLast) || new Set();
  const multiSpouseGapMult = adv.multi_spouse_gap_mult ?? 1.0;
  const soloChildFirstOffset = adv.solo_child_first_offset ?? 1.2;
  const soloChildGap = adv.solo_child_gap ?? 0.9;

  const ids = Object.keys(people);

  // ---- distancat minimale ----
  // FIX (raportuar nga useri — "ndryshimi i vlerës nën 0.5 s'ka asnjë efekt")
  // — SIB_SEP/SPOUSE_SEP përdornin `cardUnits = CARD_W / H_SPACING`, i cili
  // RRITET kur H_SPACING zvogëlohet (çelësi i vëllezërve/motrave/çifteve) —
  // ky "kompensim automatik" ANULONTE efektivisht uljen, duke e mbajtur
  // distancën në piksela përafërsisht = gjerësia e kartelës, PAVARËSISHT sa
  // poshtë e ul admini shumëzuesin. Hequr — tani SIB_SEP/SPOUSE_SEP janë
  // konstante NË NJËSI RRJETE (jo të varura nga H_SPACING), kështu që
  // distanca në piksela (SIB_SEP × H_SPACING) ndjek me të vërtetë,
  // proporcionalisht, çelësin e admin-it, në krejt intervalin (0.1×–2×).
  // Kujdes: në vlera shumë të ulëta, kartelat DO të mbivendosen pjesërisht —
  // kjo është zgjedhje e qëllimshme e admin-it, jo defekt.
  const hSp = H_SPACING > 0 ? H_SPACING : BASE_H_SPACING;
  const ICON_CLEARANCE = 30 / hSp;               // vend për ikonën e statusit (mbetet i vogël, jo problematik)
  const SIB_SEP = 1.0;
  const SPOUSE_SEP = SPOUSE_OFFSET;
  const MULTI_SEP = SPOUSE_SEP * multiSpouseGapMult;

  // ---- harta e fëmijëve ----
  const childrenMap = {};
  ids.forEach((id) => {
    (people[id].parent_ids || []).forEach((pid) => {
      if (!childrenMap[pid]) childrenMap[pid] = [];
      if (!childrenMap[pid].includes(id)) childrenMap[pid].push(id);
    });
  });
  Object.keys(childrenMap).forEach((pid) => {
    const ord = (id) => (sortFirstSet.has(id) ? -1 : sortLastSet.has(id) ? 1 : 0);
    childrenMap[pid].sort((a, b) => {
      const oa = ord(a), ob = ord(b);
      if (oa !== ob) return oa - ob; // çifti urë: i detyruar në fund (ose në fillim), pastaj datëlindja
      const da = people[a]?.birth_date || "9999-99-99";
      const db = people[b]?.birth_date || "9999-99-99";
      return da < db ? -1 : da > db ? 1 : 0;
    });
  });

  // ---- blloqe ----
  const newBlock = () => ({ nodes: [], contour: new Map() });
  const addNode = (b, id, x, y) => {
    b.nodes.push({ id, x, y });
    const c = b.contour.get(y);
    if (!c) b.contour.set(y, { min: x, max: x });
    else { c.min = Math.min(c.min, x); c.max = Math.max(c.max, x); }
  };
  const shiftBlock = (b, dx) => {
    if (!dx) return;
    b.nodes.forEach((n) => { n.x += dx; });
    b.contour.forEach((c) => { c.min += dx; c.max += dx; });
  };
  // SHTESË (analiza e posterit dy-familjesh — "familja e saj duhet të ketë
  // vendin në krah të Meritës") — zhvendosje VERTIKALE e një blloku të tërë:
  // rindërton çelësat e konturës (rreshtat), jo vetëm koordinatat e nyjeve.
  const shiftBlockY = (b, dy) => {
    if (!dy) return;
    b.nodes.forEach((n) => { n.y += dy; });
    const nc = new Map();
    b.contour.forEach((c, row) => nc.set(row + dy, c));
    b.contour = nc;
  };
  const mergeBlock = (t, s) => {
    s.nodes.forEach((n) => t.nodes.push(n));
    s.contour.forEach((c, row) => {
      const e = t.contour.get(row);
      if (!e) t.contour.set(row, { min: c.min, max: c.max });
      else { e.min = Math.min(e.min, c.min); e.max = Math.max(e.max, c.max); }
    });
  };
  // sa duhet zhvendosur `src` djathtas që të mos prekë `target` në asnjë rresht
  const requiredShift = (target, src, sep) => {
    let dx = -Infinity;
    src.contour.forEach((c, row) => {
      const t = target.contour.get(row);
      if (t) dx = Math.max(dx, t.max + sep - c.min);
    });
    return dx === -Infinity ? 0 : Math.max(0, dx);
  };

  const visited = new Set();
  const multiWifeTiers = {};
  const multiMarriageParents = new Set();

  // ------------------------------------------------------------------
  // layoutPerson: kthen një bllok ku KARTELA E VETË PERSONIT është në x=0
  // ------------------------------------------------------------------
  function layoutPerson(id, gen) {
    visited.add(id);
    const person = people[id];
    const block = newBlock();
    if (!person) { addNode(block, id, 0, gen); return { block, coupleCenter: 0 }; }

    const spouses = (person.spouse_ids || []).filter((s) => people[s] && !visited.has(s));

    const statusRank = (sId) => {
      if (people[sId]?.death_date) return 0;
      if ((person.divorced_from_ids || []).includes(sId)) return 1;
      return 2;
    };
    // FIX v3 (kërkesë e userit — "gabova: divorcuar s'shkon GJITHMONË majtas.
    // Rregulli është SIMETRIK: divorcuar DHE i/e ndjerë qëndrojnë DJATHTAS
    // përveç kur ekziston bashkëshort/e tjetër VËRTET aktiv/e") — versioni i
    // mëparshëm e trajtonte divorcin si përjashtim të pakushtëzuar (gjithmonë
    // majtas); e sakta: e vetmja gjë që dikë e shtyn majtas ËSHTË prania e
    // një bashkëshorti/eje TJETËR vërtet aktiv/e (jo divorcuar, jo i/e
    // ndjerë) — divorcuari/a dhe i/e ndjeri/a trajtohen NJËSOJ, të dy
    // qëndrojnë djathtas kur s'ka "aktiv të vërtetë" për t'u lënë vend.
    const hasTrulyActiveSpouse = spouses.some((s) => !people[s]?.death_date && !(person.divorced_from_ids || []).includes(s));
    const positionRank = (sId) => {
      const isTrulyActive = !people[sId]?.death_date && !(person.divorced_from_ids || []).includes(sId);
      if (isTrulyActive) return 2; // vërtet aktiv/e → gjithmonë djathtas
      return hasTrulyActiveSpouse ? 1 : 2; // divorcuar OSE i/e ndjerë → majtas VETËM nëse ka dikë tjetër vërtet aktiv/e
    };

    // grupet e fëmijëve
    const childGroups = {};
    (childrenMap[id] || []).forEach((cid) => {
      const cp = people[cid]?.parent_ids || [];
      const other = cp.find((p2) => p2 !== id && spouses.includes(p2));
      const key = other || "__solo__";
      if (!childGroups[key]) childGroups[key] = [];
      childGroups[key].push(cid);
    });
    const ownExtra = {};
    spouses.forEach((sId) => {
      const shared = new Set(childGroups[sId] || []);
      ownExtra[sId] = (childrenMap[sId] || []).filter((cid) => !shared.has(cid) && !visited.has(cid));
    });

    const spousesWithKids = spouses.filter((s) => (childGroups[s] || []).length > 0);
    if (spousesWithKids.length >= 2) multiMarriageParents.add(id);

    // ---------- vendos një grup fëmijësh, kthen {block, centerX} ----------
    const layoutGroup = (childIds, gen2) => {
      let acc = null;
      const anchors = [];
      childIds.forEach((cid) => {
        if (visited.has(cid)) return;
        const { block: cb, coupleCenter } = layoutPerson(cid, gen2);
        if (!acc) { acc = cb; anchors.push(coupleCenter); }
        else {
          const dx = requiredShift(acc, cb, SIB_SEP);
          shiftBlock(cb, dx);
          anchors.push(dx + coupleCenter);
          mergeBlock(acc, cb);
        }
      });
      if (!acc) return null;
      return { block: acc, centerX: (Math.min(...anchors) + Math.max(...anchors)) / 2 };
    };

    // ================= RASTI 3+ NUSE ME FËMIJË: nuset një rresht poshtë =====
    if (spousesWithKids.length >= 3) {
      const ordered = [...spouses].sort((a, b) => statusRank(a) - statusRank(b));
      let acc = null;
      const wifeXs = [];
      const tierIds = [];
      ordered.forEach((sId) => {
        const wb = newBlock();
        addNode(wb, sId, 0, gen + 1);
        visited.add(sId);
        const kids = [...(childGroups[sId] || []), ...(ownExtra[sId] || [])];
        const g = layoutGroup(kids, gen + 2);
        if (g) { shiftBlock(g.block, -g.centerX); mergeBlock(wb, g.block); }
        if (!acc) { acc = wb; wifeXs.push(0); }
        else {
          const dx = requiredShift(acc, wb, SIB_SEP);
          shiftBlock(wb, dx);
          wifeXs.push(dx);
          mergeBlock(acc, wb);
        }
        tierIds.push(sId);
      });
      const soloG = layoutGroup(childGroups["__solo__"] || [], gen + 2);
      if (soloG) {
        const dx = acc ? requiredShift(acc, soloG.block, SIB_SEP) : 0;
        shiftBlock(soloG.block, dx);
        if (!acc) acc = soloG.block; else mergeBlock(acc, soloG.block);
      }
      const centre = (Math.min(...wifeXs) + Math.max(...wifeXs)) / 2;
      if (acc) { shiftBlock(acc, -centre); mergeBlock(block, acc); }
      addNode(block, id, 0, gen);
      multiWifeTiers[id] = tierIds;
      return { block, coupleCenter: 0 };
    }

    // ================= RASTI I ZAKONSHËM (0, 1 ose 2 bashkëshortë) =========
    const inactive = spouses.filter((s) => positionRank(s) < 2).sort((a, b) => positionRank(a) - positionRank(b));
    const active = spouses.filter((s) => positionRank(s) === 2);
    const sep = spouses.length > 1 ? MULTI_SEP : SPOUSE_SEP;

    const rowX = { [id]: 0 };
    // FIX v2 (kërkesë e userit, pas testit të tij vetjak — "divorci VETËM,
    // pa rimartesë, s'duhet të lëvizë askënd; vetëm pasi martohet person i
    // dytë ka kuptim të dallohet aktivi nga ish-i") — versioni i parë e bënte
    // pozicionin GJITHMONË të qëndrueshëm, çka do të thoshte se edhe pas
    // rimartese, ish-i dhe bashkëshorti/ja e re do të renditeshin thjesht
    // sipas radhës së regjistrimit, jo sipas "kush është aktiv tani" — jo
    // saktësisht ç'kërkoi useri. Tani: me VETËM 1 bashkëshort/e (i/e
    // divorcuar apo aktiv/e, s'ka rëndësi), pozicioni ËSHTË GJITHMONË
    // djathtas, i qëndrueshëm — s'ka "të dytin" për të cilin të dallohet,
    // kështu që divorci i vetëm s'e lëviz askund. Me 2+ bashkëshortë (domethënë
    // KA ndodhur rimartesë), rikthehet dallimi aktiv/joaktiv (active=djathtas
    // më afër, inactive=majtas) — pikërisht siç e kërkoi useri.
    if (spouses.length <= 1) {
      spouses.forEach((s) => { rowX[s] = sep; });
    } else {
      inactive.forEach((s, i) => { rowX[s] = -sep * (i + 1); });
      active.forEach((s, i) => { rowX[s] = sep * (i + 1); });
    }

    // grupet e fëmijëve me pozicionin e dëshiruar (nyja e bashkimit)
    const groups = [];
    inactive.slice().reverse().forEach((sId) => {
      if ((ownExtra[sId] || []).length) groups.push({ ids: ownExtra[sId], target: rowX[sId] - soloChildFirstOffset });
      if ((childGroups[sId] || []).length) groups.push({ ids: childGroups[sId], target: rowX[sId] / 2 });
    });
    if ((childGroups["__solo__"] || []).length) groups.push({ ids: childGroups["__solo__"], target: 0 });
    active.forEach((sId) => {
      if ((childGroups[sId] || []).length) groups.push({ ids: childGroups[sId], target: rowX[sId] / 2 });
      if ((ownExtra[sId] || []).length) groups.push({ ids: ownExtra[sId], target: rowX[sId] + soloChildFirstOffset });
    });
    groups.sort((a, b) => a.target - b.target);

    // FIX (kërkesë e userit — "hapësira mes të gjithë fëmijëve nën degën e
    // një personi duhet të jetë e barabartë"): më parë çdo grup fëmijësh
    // vendosej i qendërzuar nën nyjen E VET, dhe kur dy nyje ishin larg
    // njëra-tjetrës, mes dy grupeve mbetej boshllëk më i madh se hapësira
    // normale mes vëllezërve. Tani të gjitha grupet paketohen NJË PAS TJETRIT
    // me të njëjtën hapësirë si mes vëllezërve, dhe i gjithë seti zhvendoset
    // më pas që të qendërzohet nën prindërit — pra hapësira është kudo e
    // njëjtë, dhe familja mbetet e qendërzuar.
    let kidsBlock = null;
    const placed = [];
    groups.forEach((g) => {
      const laid = layoutGroup(g.ids, gen + 1);
      if (!laid) return;
      shiftBlock(laid.block, -laid.centerX);
      let center = 0;
      if (kidsBlock) {
        center = requiredShift(kidsBlock, laid.block, SIB_SEP);
        shiftBlock(laid.block, center);
        mergeBlock(kidsBlock, laid.block);
      } else {
        kidsBlock = laid.block;
      }
      placed.push({ g, center });
    });
    if (placed.length) {
      const ts = groups.map((g) => g.target);
      const targetCenter = (Math.min(...ts) + Math.max(...ts)) / 2;
      const actualCenter = (placed[0].center + placed[placed.length - 1].center) / 2;
      const d = targetCenter - actualCenter;
      if (d) { shiftBlock(kidsBlock, d); placed.forEach((p) => { p.center += d; }); }
    }
    const devs = placed.map((p) => p.center - p.g.target);

    // ribalanco: rreshti i prindërve qendërzohet mbi masën e fëmijëve
    const rowShift = devs.length ? devs.reduce((a, b) => a + b, 0) / devs.length : 0;
    if (kidsBlock && rowShift) shiftBlock(kidsBlock, -rowShift);
    if (kidsBlock) mergeBlock(block, kidsBlock);

    Object.entries(rowX).forEach(([pid, x]) => {
      if (pid !== id) visited.add(pid);
      addNode(block, pid, x, gen);
    });
    const xsRow = Object.values(rowX);
    return { block, coupleCenter: (Math.min(...xsRow) + Math.max(...xsRow)) / 2 };
  }


  const hasVisibleParent = (p) => (p.parent_ids || []).some((pid) => people[pid]);
  const roots = ids.filter((id) => {
    const p = people[id];
    if (eshteMysafir(p)) return false; // FAZA 5 — mysafiri vendoset veç (zonë e dedikuar), jo si rrënjë
    if (hasVisibleParent(p)) return false;
    const marriesIn = (p.spouse_ids || []).some((sid) => people[sid] && hasVisibleParent(people[sid]));
    return !marriesIn;
  });

  let world = null;
  const placeRoot = (id) => {
    if (visited.has(id)) return;
    if (eshteMysafir(people[id])) return; // FAZA 5 — nuk hyn te trungu; vendoset poshtë, veç
    const { block: b } = layoutPerson(id, 0);
    if (!world) { world = b; return; }
    // FIX THEMELOR (analiza "deep" e posterit dy-familjesh — "familja e saj
    // shkon krah paraardhësit tim; ajo duhet të ketë vendin në krah të
    // Meritës, aty është pika e bashkimit"): më parë ÇDO bllok rrënjë i ri
    // paketohej thjesht DJATHTAS radhazi, në KRYE (gen 0) — pra paraardhësit
    // e një bashkëshorteje të ardhur (p.sh. familja Orlishta e Meritës)
    // përfundonin lart, ngjitur me paraardhësit e familjes tjetër, me një
    // vijë të gjatë prind→fëmijë që përshkonte gjithë pemën (forma "V").
    // Tani: nëse blloku LIDHET me dikë tashmë të vendosur (ka një skaj
    // prind-në-bllok → fëmijë-në-botë), blloku ANKOROHET te ai person:
    //   1) vertikalisht — prindi ulet saktësisht NJË rresht mbi fëmijën
    //      (brezat përputhen, s'ka vijë që kapërcen 5 breza);
    //   2) horizontalisht — synohet pikërisht MBI fëmijën (çifti = pika e
    //      bashkimit), dhe pranohet zhvendosja minimale djathtas që s'krijon
    //      mbivendosje (requiredShift garanton se çdo dx ≥ min është pa
    //      përplasje). Kjo vlen NJËSOJ për këdo që e bën posterin — pika e
    //      bashkimit është gjithmonë çifti i tij, pa asnjë rast special.
    const inWorld = new Map();
    world.nodes.forEach((n) => inWorld.set(n.id, n));
    const edges = [];
    b.nodes.forEach((n) => {
      (childrenMap[n.id] || []).forEach((cid) => {
        const wn = inWorld.get(cid);
        if (wn) edges.push({ dx: wn.x - n.x, dy: wn.y - 1 - n.y });
      });
    });
    if (edges.length) {
      const dy = edges[0].dy;
      const same = edges.filter((e) => e.dy === dy);
      const wantDx = same.reduce((s, e) => s + e.dx, 0) / same.length;
      shiftBlockY(b, dy);
      shiftBlock(b, Math.max(requiredShift(world, b, SIB_SEP), wantDx));
    } else {
      shiftBlock(b, requiredShift(world, b, SIB_SEP));
    }
    mergeBlock(world, b);
  };
  roots.forEach(placeRoot);
  ids.forEach(placeRoot);

  const positions = {};
  if (world) world.nodes.forEach((n) => { positions[n.id] = { x: n.x, y: n.y }; });

  // ---- rrjeta e sigurisë: asnjë kartelë s'mbivendoset kurrë ----
  // FIX (i njëjti problem si SIB_SEP/SPOUSE_SEP sipër) — `cardUnits` (hequr
  // tani) rritej kur H_SPACING zvogëlohej, duke e bërë këtë "rrjetë sigurie"
  // të anulonte vetë fix-in e mësipërm (do t'i shtynte kartelat mbrapsht në
  // ~gjerësinë e kartelës, pavarësisht cilësimit të admin-it). Tani konstante
  // fikse, e vogël, në njësi rrjete — mbetet mbrojtje minimale kundër
  // mbivendosjes SË PLOTË (pozicione identike), jo kundër çdo afrimi.
  const MIN_SEP = 0.55;
  const byRow = {};
  Object.entries(positions).forEach(([pid, pos]) => {
    if (!byRow[pos.y]) byRow[pos.y] = [];
    byRow[pos.y].push(pid);
  });
  Object.values(byRow).forEach((rowIds) => {
    rowIds.sort((a, b) => positions[a].x - positions[b].x);
    for (let i = 1; i < rowIds.length; i++) {
      const prev = positions[rowIds[i - 1]], cur = positions[rowIds[i]];
      if (cur.x - prev.x < MIN_SEP) cur.x = prev.x + MIN_SEP;
    }
  });

  // ---- FAZA 5 — MYSAFIRËT: zonë e dedikuar LART-DJATHTAS, jashtë pemës ----
  // Nuk kanë prind/bashkëshort (s'lidhen me vija gjaku); i grumbullojmë në një
  // rrjetë të vogël sipër-djathtas majës së pemës, që të mos përzihen me trungun.
  const mysafiretIds = ids.filter((id) => eshteMysafir(people[id]) && !positions[id]);
  if (mysafiretIds.length) {
    let minX = Infinity, maxX = -Infinity, minY = Infinity;
    Object.values(positions).forEach((p) => { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); });
    if (!isFinite(minX)) { minX = 0; maxX = 0; minY = 0; }
    const startX = maxX + 2.2;   // djathtas skajit të pemës
    const startY = minY - 1.5;   // pak sipër majës
    const perRow = 4;
    mysafiretIds.sort((a, b) => {
      const na = `${people[a]?.first_name || ""} ${people[a]?.last_name || ""}`;
      const nb = `${people[b]?.first_name || ""} ${people[b]?.last_name || ""}`;
      return na < nb ? -1 : na > nb ? 1 : 0;
    });
    mysafiretIds.forEach((id, i) => {
      positions[id] = { x: startX + (i % perRow) * 1.6, y: startY + Math.floor(i / perRow) * 1.2 };
    });
  }

  return { positions, childrenMap, multiWifeTiers, multiMarriageParents };
}

// ---------- kinship engine ----------
// Computes each person's relationship category RELATIVE TO THE LOGGED-IN
// VIEWER (used when "Ngjyros bazuar në farefisninë time" is on): core /
// close_cousin / extended / married_in / married_out. This is NOT the same
// as a person's stored `branch` field — that's a manual fallback used when
// this automatic classification is switched off. See ARCHITECTURE.md §8.
//
// ancestorDistances: BFS up the parent chain from `startId`, returns how
// many generations back each ancestor is (used to find the "common
// ancestor" between viewer and target, and how far up).
function ancestorDistances(startId, people) {
  const dist = new Map();
  const queue = [[startId, 0]];
  while (queue.length) {
    const [cur, d] = queue.shift();
    if (dist.has(cur) && dist.get(cur) <= d) continue;
    dist.set(cur, d);
    const p = people[cur];
    if (!p) continue;
    (p.parent_ids || []).forEach((pid) => { if (people[pid]) queue.push([pid, d + 1]); });
  }
  return dist;
}
// pathDown: BFS down from a common ancestor to find the exact child-of-
// child-of-child chain leading to the target — used by patrilinealOverride
// to check whether that path passes through a woman (meaning "married
// out" per the patrilineal tradition — see ARCHITECTURE.md §8).
function pathDown(fromId, toId, childrenMap) {
  if (fromId === toId) return [fromId];
  const visited = new Set([fromId]);
  const queue = [[fromId]];
  while (queue.length) {
    const path = queue.shift();
    const last = path[path.length - 1];
    (childrenMap[last] || []).forEach((c) => {
      if (visited.has(c)) return;
      visited.add(c);
      const nextPath = [...path, c];
      if (c === toId) queue.unshift(nextPath);
      else queue.push(nextPath);
    });
    if (last === toId) return path;
  }
  return null;
}
// HAPI 6 — modeli farefisnor (rule_sets): patrilineal (vajza "del" nga fisi),
// matrilineal (djali "del"), bilateral (askush s'del sipas gjinisë).
function patrilinealOverride(commonId, targetId, people, childrenMap) {
  const km = (ACTIVE_RULES && ACTIVE_RULES.kinship_model) || "patrilineal";
  if (km === "bilateral") return null; // pa përjashtim sipas gjinisë
  const outGender = km === "matrilineal" ? "M" : "F";
  const path = pathDown(commonId, targetId, childrenMap);
  if (!path || path.length < 3) return null;
  for (let i = 1; i < path.length - 1; i++) {
    const link = people[path[i]];
    if (link && link.gender === outGender) return "married_out";
  }
  return null;
}
function bloodRelation(viewerId, targetId, people) {
  if (!people[viewerId] || !people[targetId]) return null;
  if (viewerId === targetId) return { up: 0, down: 0, common: viewerId };
  const distV = ancestorDistances(viewerId, people);
  const distT = ancestorDistances(targetId, people);
  let common = null, bestSum = Infinity;
  distV.forEach((dv, anc) => {
    if (distT.has(anc)) {
      const sum = dv + distT.get(anc);
      if (sum < bestSum) { bestSum = sum; common = anc; }
    }
  });
  if (!common) return null;
  return { up: distV.get(common), down: distT.get(common), common };
}
function classifyRelation(viewerId, targetId, people, childrenMap, bashkeprinderit) {
  const rel = bloodRelation(viewerId, targetId, people);
  if (rel) {
    const { up, down, common } = rel;
    if (down === 0) return "core"; // target is an ancestor of the viewer — always core, regardless of path
    if (up === 0) {
      // target is a descendant of the viewer — still check whether the path crosses a daughter's marriage
      return patrilinealOverride(common, targetId, people, childrenMap) || "core";
    }
    const override = patrilinealOverride(common, targetId, people, childrenMap);
    if (override) return override;
    // FIX (raportuar nga useri — "a e kam gabim, mendoja se vëllai është
    // familje e ngushtë?") — JO, e kishte të drejtë: KJO ishte defekt real.
    // Vëllezër/motra (up=1, down=1 — një brez lart te prindi i përbashkët,
    // pastaj një brez poshtë) s'kapeshin nga asnjë kusht më sipër
    // ("down===0"/"up===0" janë vetëm për paraardhës/pasardhës TË
    // DREJTPËRDREJTË), prandaj binin te "up<=2 && down<=2" më poshtë —
    // KLASIFIKOHESHIN SI "KUSHËRI I AFËRT", jo "Familja e ngushtë". Vëllai/
    // motra ËSHTË familje e ngushtë, jo kushëri — shtuar kontroll eksplicit.
    if (up === 1 && down === 1) return "core";
    // Pragjet vijnë nga rule_sets: dy kufij (i afërt, i largët). Përtej "i largët"
    // → "degë e re në fis tjetër" (very distant). Të gjitha konfigurueshme per-shtet.
    const _th = (ACTIVE_RULES && ACTIVE_RULES.thresholds) || {};
    const _ccUp = Number(_th.close_cousin_max_up) || 2;
    const _ccDown = Number(_th.close_cousin_max_down) || 2;
    const _exUp = Number(_th.extended_max_up) || 4;
    const _exDown = Number(_th.extended_max_down) || 4;
    if (up <= _ccUp && down <= _ccDown) return "close_cousin";
    if (up <= _exUp && down <= _exDown) return "extended";
    return "married_out";
  }
  // Pa gjak të përbashkët: a është bashkëshort/e i dikujt me gjak?
  // 6 shtator 2026 (pronari): «të ardhurit me martesë te personat që janë
  // degë në fis tjetër hynin te trungu». Dy ndreqje:
  //   · bashkëshorti i një pasardhësi NGA VAJZA është pjesë e DEGËS SË RE
  //     («married_out»), jo «me martesë» te trungu — ai është kryet e fisit
  //     tjetër, jo i ardhur në këtë;
  //   · kush s'ka lidhje martese të shënuar po ka FËMIJË TË PËRBASHKËT me
  //     dikë me gjak (të dhënat e vjetra) trajtohet si bashkëshort/e — më
  //     parë binte te kolona e vjetër `branch`, shpesh «core».
  const target = people[targetId];
  if (!target) return null;
  const lidhjet = new Set(target.spouse_ids || []);
  if (bashkeprinderit && bashkeprinderit[targetId]) bashkeprinderit[targetId].forEach((x) => lidhjet.add(x));
  let rezultati = null;
  for (const sId of lidhjet) {
    if (sId === viewerId) return "married_in";
    const r = bloodRelation(viewerId, sId, people);
    if (!r) continue;
    const klasa = r.down === 0 ? "core"
      : (patrilinealOverride(r.common, sId, people, childrenMap) || "core");
    if (klasa === "married_out") rezultati = "married_out";
    else if (!rezultati) rezultati = "married_in";
  }
  return rezultati;
}
function computeRelationMap(viewerId, people) {
  const childrenMap = {};
  // Bashkëprindërit: kush ndan fëmijë me kë — një herë për gjithë hartën.
  const bashkeprinderit = {};
  Object.values(people).forEach((p) => (p.parent_ids || []).forEach((pid) => {
    if (!childrenMap[pid]) childrenMap[pid] = [];
    childrenMap[pid].push(p.id);
    (p.parent_ids || []).forEach((tjetri) => {
      if (tjetri === pid) return;
      if (!bashkeprinderit[pid]) bashkeprinderit[pid] = new Set();
      bashkeprinderit[pid].add(tjetri);
    });
  }));
  const map = {};
  Object.keys(people).forEach((id) => { map[id] = classifyRelation(viewerId, id, people, childrenMap, bashkeprinderit); });
  return map;
}

// ---------- small components ----------

// Combobox — fushë me shkrim + sugjerime. Zëvendëson listat rënëse kudo ku
// opsionet janë të shumta: shkruan disa shkronja, sugjerimet ngushtohen, klikon.
// `allowFree` (parazgjedhje: true) lejon edhe një vlerë që s'është në listë —
// e domosdoshme për fshatra që s'i ka asnjë bazë e botës.
function Combobox({ value, onChange, options, placeholder, allowFree = true, disabled, maxShown = 50, emptyText }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  // Opsionet pranohen si vargje ose si {value,label}.
  const norm = useMemo(
    () => (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return norm.slice(0, maxShown);
    const starts = [], has = [];
    for (const o of norm) {
      const l = String(o.label).toLowerCase();
      if (l.startsWith(s)) starts.push(o);
      else if (l.includes(s)) has.push(o);
      if (starts.length >= maxShown) break;
    }
    return [...starts, ...has].slice(0, maxShown);
  }, [q, norm, maxShown]);

  // Teksti i shfaqur: ajo qe po shkruan, ose etiketa e vleres se zgjedhur.
  const selectedLabel = useMemo(() => {
    const f = norm.find((o) => o.value === value);
    return f ? f.label : value || "";
  }, [norm, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (o) => { onChange(o.value); setQ(""); setOpen(false); };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (matches[hi]) pick(matches[hi]);
      else if (allowFree && q.trim()) { onChange(q.trim()); setQ(""); setOpen(false); }
    } else if (e.key === "Escape") { setOpen(false); setQ(""); }
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        disabled={disabled}
        value={open ? q : selectedLabel}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQ(""); setHi(0); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onBlur={() => { if (allowFree && q.trim()) onChange(q.trim()); }}
        onKeyDown={onKey}
        className="w-full rounded-md px-3 py-2 outline-none"
        style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, fontSize: 14 }}
      />
      {open && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 rounded-md overflow-auto"
          style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, maxHeight: 240, boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}
        >
          {matches.map((o, i) => (
            <button
              key={o.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHi(i)}
              className="block w-full text-left px-3 py-2"
              style={{
                background: i === hi ? "rgba(0,0,0,0.05)" : "transparent",
                fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink,
              }}
            >
              {o.label}
            </button>
          ))}
          {!matches.length && (
            <div style={{ padding: "8px 12px", fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
              {q.trim() && allowFree
                ? t('Press Enter to use "{{text}}"', { text: q.trim() })
                : emptyText || t("No results.")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// Vendlindja në tri nivele: fshati/vendi i saktë (shkruhet lirisht) → qyteti
// (listë sipas shtetit, e ngarkuar me kërkesë) → shteti (250, të përkthyera).
// Të treja janë combobox: shkruan, ngushtohen sugjerimet, klikon.
// BANIMI1 — i njëjti komponent shërben edhe për VENDBANIMIN AKTUAL: `celesat`
// thotë cilat fusha të formularit lexon/shkruan, `meFshat` heq rreshtin e
// fshatit (banimi ka vetëm shtet + qytet, të dyja opsionale).
function BirthPlaceFields({ form, setForm, people, countries, canManage, addCountry, lang,
                            celesat = { village: "birthVillage", city: "birthCity", country: "birthCountry" }, meFshat = true }) {
  const { t } = useTranslation();
  const [cityList, setCityList] = useState([]);
  const K = celesat;
  const code = resolveCountry(form[K.country]);

  useEffect(() => {
    let alive = true;
    if (!code) { setCityList([]); return; }
    loadCities(code).then((list) => { if (alive) setCityList(list); });
    return () => { alive = false; };
  }, [code]);

  // ⚠ 4 shtator 2026 — «kemi bërë një sistem: vetëm kur zgjedh shtetin shfaqen
  // të gjitha qytetet e atij shteti. Tash njeriu mund të shkruajë çka të dojë
  // edhe ruhen qytetet e fundit pa marrë parasysh ç'shtet ke zgjedhur.»
  //
  // Kishte të drejtë. Lista botërore (`cityList`) VINTE sipas shtetit, po mbi
  // të shtoheshin QYTETET E FAMILJES pa asnjë kusht shteti — pra me «Gjermani»
  // të zgjedhur sugjerohej «Prishtinë», sepse dikush tjetër në pemë e ka. Dhe
  // fusha pranonte tekst të lirë edhe pa asnjë shtet, ndaj qyteti ruhej me
  // `birth_country` bosh.
  //
  // Tani qytetet e familjes filtrohen sipas shtetit të vet të atij personi, dhe
  // fusha rri e mbyllur derisa të zgjidhet një shtet.
  const njejtiShtet = (v) => {
    const zgjedhur = form[K.country];
    if (!zgjedhur || !v) return false;
    const a = resolveCountry(v);
    const b = code;
    if (a && b) return a === b;                      // të dy njihen si kod
    return String(v).trim().toLowerCase() === String(zgjedhur).trim().toLowerCase();
  };
  // Qytetet e vetë familjes shtohen — edhe kur s'janë në bazën botërore — po
  // VETËM ato që i takojnë shtetit të zgjedhur.
  const cityOptions = useMemo(() => {
    const own = Object.values(people || {})
      .filter((p) => p && njejtiShtet(K.city === "birthCity" ? p.birth_country : p.residence_country))
      .map((p) => (K.city === "birthCity" ? p.birth_city : p.residence_city))
      .filter(Boolean);
    return Array.from(new Set([...own, ...cityList]));
  }, [people, cityList, form[K.country], code]);

  const countryOpts = useMemo(() => {
    const base = countryOptions(lang, familyCountryCodes(people)).map((c) => ({ value: c.code, label: c.name }));
    const custom = (countries || []).filter((c) => !resolveCountry(c)).map((c) => ({ value: c, label: c }));
    return [...base, ...custom];
  }, [lang, people, countries]);

  // RADHA: shteti → qyteti → fshati (2 shtator 2026). Pronari: «po e shkrove
  // shtetin të parin, të dalin emrat e qyteteve të atij shteti». Lista e
  // qyteteve ngarkohet sipas shtetit, prandaj shteti duhet zgjedhur i pari —
  // radha e fushave ndjek radhën e varësisë, jo atë të adresës.
  return (
    <>
      <div>
        <FieldLabel>{t("person.country")}</FieldLabel>
        <Combobox
          value={form[K.country]}
          onChange={(v) => {
            if (v === "__add_new__") {
              const name = window.prompt(t("person.newCountryPrompt"));
              if (name && name.trim()) { addCountry(name.trim()); setForm((f) => ({ ...f, [K.country]: name.trim() })); }
              return;
            }
            setForm((f) => ({ ...f, [K.country]: v, [K.city]: "" }));
          }}
          options={canManage ? [...countryOpts, { value: "__add_new__", label: t("person.addNewCountry") }] : countryOpts}
          allowFree={false}
          placeholder={t("Type to search…")}
        />
      </div>
      <div>
        <FieldLabel>{t("person.cityVillage")}</FieldLabel>
        <Combobox
          value={form[K.city]}
          onChange={(v) => setForm((f) => ({ ...f, [K.city]: v }))}
          options={cityOptions}
          /* Pa shtet nuk ka qytet: fusha rri e mbyllur, ndryshe qyteti ruhet
             me shtetin bosh dhe harta s'e sheh kurrë. */
          disabled={!form[K.country]}
          allowFree={!!form[K.country]}
          placeholder={form[K.country] ? t("Type to search…") : t("Choose the country first")}
          emptyText={t("No city found — you can write it yourself.")}
        />
      </div>
      {meFshat && (
        <div>
          <FieldLabel>{t("Village / exact place (optional)")}</FieldLabel>
          <input
            className="w-full rounded-md px-3 py-2 outline-none"
            style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, fontSize: 14 }}
            placeholder={t("e.g. Vajnik")}
            value={form[K.village] || ""}
            onChange={(e) => setForm((f) => ({ ...f, [K.village]: e.target.value }))}
          />
        </div>
      )}
    </>
  );
}

// Nxjerr fshatin/vendin e saktë nga `birth_place` ("Fshat, Qytet, Shtet") duke
// hequr pjesët që përputhen me qytetin dhe shtetin e ruajtur veçmas.
//
// ⚠ 4 shtator 2026 — DEFEKT QË RRITEJ ME ÇDO RUAJTJE.
// Ruajtja e shkruan shtetin në GJUHËN AKTUALE (`displayCountry(kodi,
// i18n.language)` → «Kosovë»), kurse ky funksion e kërkonte VETËM anglisht
// («Kosovo»). Pra pjesa e shtetit nuk hiqej dot: fusha «Fshati» mbushej me
// «Vajnik, Kosovë», ruajtja e radhës shkruante «Vajnik, Kosovë, Kosovë», dhe
// ashtu rritej pa fund.
//
// Tani hiqet çdo formë e njohur e shtetit: kodi vetë, emri anglisht, emri
// shqip, dhe emri në gjuhën që po përdoret.
function villageFromPlace(place, city, country, gjuha) {
  if (!place) return "";
  const parts = String(place).split(",").map((s) => s.trim()).filter(Boolean);
  const drop = new Set();
  const shto = (v) => { if (v) drop.add(String(v).trim().toLowerCase()); };
  shto(city);
  if (country) {
    shto(country);
    shto(displayCountry(country, "en"));
    shto(displayCountry(country, "sq"));
    if (gjuha) shto(displayCountry(country, gjuha));
  }
  const rest = parts.filter((p) => !drop.has(p.toLowerCase()));
  // Nëse mbeten të gjitha pjesët, teksti s'ishte i strukturuar → mos hamendëso.
  return rest.length && rest.length < parts.length ? rest.join(", ") : "";
}

function SearchSelect({ people, value, onChange, placeholder, excludeId }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = value ? people[value] : null;
  const etiketat = useMemo(() => ndertoEtiketatDalluese(people), [people]);
  const matches = useMemo(() => {
    if (!q.trim()) return [];
    const l = q.toLowerCase();
    // Kërkimi bëhet edhe mbi etiketën dalluese — kush shkruan «Ramiz» duke
    // kërkuar djalin e Ramizit e gjen atë, jo asgjë.
    return Object.values(people).filter((p) => p.id !== excludeId
      && (displayName(p).toLowerCase().includes(l) || emriDallues(p, etiketat).toLowerCase().includes(l))).slice(0, 8);
  }, [q, people, excludeId, etiketat]);
  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-md px-3 py-2" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
        <span style={{ fontFamily: FONTS.body, color: COLORS.ink }}>{emriDallues(selected, etiketat)}</span>
        <button type="button" onClick={() => onChange(null)} aria-label={t("Clear")} className="rounded hover:bg-black/5 flex items-center justify-center shrink-0" style={{ width: 44, height: 44, margin: "-11px -13px -11px 0", background: "transparent", border: "none", padding: 0 }}><X size={14} color={COLORS.inkLight} /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder={placeholder}
        className="w-full rounded-md px-3 py-2 outline-none" style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, color: COLORS.ink }} />
      {open && matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md shadow-lg overflow-hidden" style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}` }}>
          {matches.map((m) => (
            <button key={m.id} onClick={() => { onChange(m.id); setQ(""); setOpen(false); }} className="block w-full text-left px-3 py-2 hover:bg-black/5" style={{ fontFamily: FONTS.body, color: COLORS.ink, fontSize: 14 }}>
              {emriDallues(m, etiketat)} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{lifespan(m)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
// Multi-select for picking specific contacts (email recipients). Unlike a
// typical "type to search, pick one, closes" pattern, this stays open
// across multiple picks — click several people in a row without it
// collapsing each time — while still offering a search box to filter when
// the contact list is long. Toggled open/closed explicitly, similar in
// spirit to the single-person "Shto Person" search but built for picking
// many people at once.
function ContactMultiSelect({ contacts, selectedIds, onChange, placeholder }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const visible = useMemo(() => {
    const unselected = contacts.filter((c) => !selectedIds.has(c.person_id));
    if (!q.trim()) return unselected;
    const l = q.toLowerCase();
    return unselected.filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(l) || (c.email || "").toLowerCase().includes(l));
  }, [q, contacts, selectedIds]);
  const selectedContacts = contacts.filter((c) => selectedIds.has(c.person_id));
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between rounded-md px-3 py-2"
        style={{ background: COLORS.field, border: "1px solid rgba(0,0,0,0.15)", fontFamily: FONTS.body, color: COLORS.ink }}
      >
        <span>{selectedContacts.length > 0 ? t("{{n}} selected", { n: selectedContacts.length }) : (placeholder || t("Select people…"))}</span>
        <span style={{ color: COLORS.inkLight, fontSize: 12 }}>{open ? t("▲ Collapse") : t("▼ Expand")}</span>
      </button>
      {open && (
        <div className="mt-1.5 rounded-md" style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}` }}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search…")}
            className="w-full px-3 py-2 outline-none text-sm"
            style={{ borderBottom: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, color: COLORS.ink }}
          />
          <div className="max-h-56 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="px-3 py-2" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Nobody found.")}</p>
            ) : (
              visible.map((c) => (
                <button
                  key={c.person_id}
                  type="button"
                  onClick={() => { const next = new Set(selectedIds); next.add(c.person_id); onChange(next); }}
                  className="block w-full text-left px-3 py-2 hover:bg-black/5"
                  style={{ fontFamily: FONTS.body, color: COLORS.ink, fontSize: 14, borderTop: `1px solid ${COLORS.goldSoft}22` }}
                >
                  {c.first_name} {c.last_name} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>({c.email})</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
      {selectedContacts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedContacts.map((c) => (
            <span key={c.person_id} className="flex items-center gap-1 rounded-full px-2.5 py-1" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
              <span style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.ink }}>{c.first_name} {c.last_name}</span>
              <button onClick={() => { const next = new Set(selectedIds); next.delete(c.person_id); onChange(next); }} style={{ color: COLORS.inkLight, fontSize: 12, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
// FIX (kërkesë e userit) — dritaret popup dukeshin "si dizajn i viteve '90",
// teksti i vogël kërkonte zoom për t'u lexuar në telefon. Meqë KY komponent
// përdoret nga ÇDO dritare popup në krejt aplikacionin (njoftimet, anëtarët,
// eksporti, çdo panel admin...), rregullimi këtu përmirëson automatikisht
// GJITHÇKA njëherësh, pa pasur nevojë të ndryshohet çdo modal veç e veç.
//
// Në TELEFON (nën breakpoint-in "sm" të Tailwind, 640px): sillet si FAQE E
// RE, jo më "kuti e vogël pezull" — hapet me rrëshqitje nga e djathta (shih
// @keyframes modalSlideInMobile te index.css), mbulon krejt ekranin, dhe ka
// shigjetë "mbrapa" (ChevronLeft) në vend të një X të vogël — pikërisht
// modeli i aplikacioneve native. Swipe me gisht nga e majta në të djathtë
// (kudo mbi vetë panelin, jo vetëm nga buza) e mbyll, njësoj si "kthehu
// mbrapa" i telefonave — prek panelin, ndjek gishtin gjatë tërheqjes
// (feedback i gjallë), lëshohet nëse ka kaluar ~90px, përndryshe kthehet
// vetë në vend.
//
// Në DESKTOP: mbetet dialog i centruar, thjesht pak më modern (skaje më të
// rrumbullakosura, hije më e butë, sfond i turbullt pas tij).
// FIX THEMELOR (raportuar nga useri — "Sinkronizo profilin te Qendra e
// familjes në telefon nuk punon"): ÇDO Modal kishte të njëjtin z-index (z-50),
// ndaj kur një dritare hapej NGA një tjetër, se cila dilte sipër vendosej nga
// RENDI NË KOD, jo nga rendi i hapjes. Dritarja e sinkronizimit renderohet
// PARA "Qendrës së familjeve" në JSX → hub-i (full-screen në telefon) e
// mbulonte plotësisht: dukej sikur butoni s'bën asgjë. ("Sill familjen"
// punonte pikërisht sepse renderohet PAS hub-it — e njëjta çështje, anasjelltas.)
// Tani çdo Modal merr një z-index sipas RENDIT TË HAPJES, kështu e fundit e
// hapur del gjithmonë sipër — pavarësisht se ku ndodhet në kod. E kufizuar
// nën 60 (lightbox-i i fotos) që të mos prishet asnjë shtresë ekzistuese.
// ── ZONA E PREKJES ─────────────────────────────────────────────────────────
// 4 shtator 2026. Pronari: «back edhe X butoni te çdo dritare duhet zgjeruar
// hapësira e tuçit, sepse tani është vetëm te butoni edhe është e vështirë i
// navigueshëm».
//
// Kishte të drejtë, dhe defekti ishte i imi: zona e prekjes ishte saktësisht
// sa rrethi i dukshëm (~32px). Apple kërkon 44×44pt, Google 48dp — dhe s'është
// kapriço: maja e gishtit mbulon rreth 10 mm, pra një cak 32px kërkon një
// saktësi që në lëvizje nuk e ka askush.
//
// Zgjidhja NUK e ndryshon pamjen: butoni bëhet 44×44 i tejdukshëm, kurse
// rrethi i dukshëm rri BRENDA tij si një `<span>`. Syri sheh njësoj; gishti
// gjen dy herë më shumë vend.
//
// `display` NUK vendoset këtu me qëllim — disa nga këta butona shfaqen vetëm
// në telefon (`sm:hidden`) e disa vetëm në desktop (`hidden sm:flex`), dhe një
// `display` inline do t'i mbyste ato klasa (stili inline fiton gjithmonë).
const ZONA_PREKJES = 44;
function ButonIkone({ onClick, className = "", stil, rrethi, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`items-center justify-center shrink-0 ${className}`}
      style={{ width: ZONA_PREKJES, height: ZONA_PREKJES, background: "transparent", border: "none", padding: 0, cursor: "pointer", ...(stil || {}) }}>
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 999, ...(rrethi || {}) }}>{children}</span>
    </button>
  );
}
let MODAL_DEPTH = 0;
function Modal({ title, onClose, children, wide, fullBleed, titullMadh }) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const touchStart = useRef(null);
  const [zTop] = useState(() => 50 + Math.min(8, (MODAL_DEPTH += 1)));
  const [, setRrotullimi] = useState(0); // vetëm për ri-render pas rrotullimit
  const panelRef = useRef(null);
  useEffect(() => () => { MODAL_DEPTH = Math.max(0, MODAL_DEPTH - 1); }, []);

  // SHIRITI2 — në telefon kjo dritare mbulon krejt ekranin, pra ajo, jo header-i
  // i app-it, është ajo që ndodhet nën orën dhe baterinë. Ngjyra lexohet nga
  // vetë elementi i pikturuar (jo nga `COLORS.panel`), sepse kur një dizajn e
  // vendos sipërfaqen përmes `var(--surface)`, vlera e vërtetë njihet vetëm pasi
  // shfletuesi e ka zgjidhur.
  useEffect(() => {
    let hiqNgaPirgu = null;
    try {
      const el = panelRef.current;
      hiqNgaPirgu = shtyjNgjyreneShiritit(el ? getComputedStyle(el).backgroundColor : null);
    } catch (_e) { hiqNgaPirgu = null; }
    // Rrotullimi i ekranit mund ta kalojë kufirin 640px në të dyja drejtimet:
    // dritarja bëhet e vogël në qendër, ose përsëri e plotë. Prek edhe hapësirën
    // e kokës, e cila tani është numër i llogaritur në render — pra duhet një
    // ri-render, jo vetëm një ri-matje.
    const rifresko = () => {
      setRrotullimi((n) => n + 1);
      try { sinkronizoShiritinESistemit(); } catch (_e) { /* pa efekt */ }
    };
    window.addEventListener("resize", rifresko);
    window.addEventListener("orientationchange", rifresko);
    return () => {
      window.removeEventListener("resize", rifresko);
      window.removeEventListener("orientationchange", rifresko);
      if (hiqNgaPirgu) hiqNgaPirgu();
    };
  }, []);

  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchMove = (e) => {
    if (!touchStart.current) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    // Nëse lëvizja është kryesisht VERTIKALE, s'është gjest mbylljeje —
    // lëri të kalojë si scroll normal i përmbajtjes, mos e "rrëmbe".
    if (Math.abs(dy) > Math.abs(dx) || dx < 0) return;
    setDragging(true);
    setDragX(dx);
  };
  const onTouchEnd = () => {
    if (dragX > 90) { onClose(); return; }
    setDragX(0);
    setDragging(false);
    touchStart.current = null;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-0 sm:p-4" style={{ zIndex: zTop, background: "rgba(15,18,13,0.6)", backdropFilter: "blur(2px)" }} onClick={onClose}>
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={`modal-panel w-full h-full sm:h-auto rounded-none ${fullBleed ? "sm:max-w-4xl" : wide ? "sm:max-w-lg" : "sm:max-w-sm"} shadow-2xl flex flex-col`}
        style={{
          background: `var(--surface, ${COLORS.panel})`,
          border: `1px solid var(--border, ${COLORS.goldSoft})`,
          maxHeight: "100dvh",
          transform: dragX ? `translateX(${dragX}px)` : undefined,
          opacity: dragX ? Math.max(0.35, 1 - dragX / 280) : 1,
          transition: dragging ? "none" : "transform 0.22s ease, opacity 0.22s ease",
        }}
      >
        <div className="flex items-center gap-2 px-4 sm:px-5 py-4 shrink-0 sticky top-0 z-10" style={{ borderBottom: `1px solid var(--border, ${COLORS.goldSoft})`, background: `var(--surface, ${COLORS.panel})`, paddingTop: Math.max(16, zonaLartPx()) }}>
          {/* Shigjeta mbrapa — VETËM telefon, zëvendëson X-in aty */}
          <ButonIkone onClick={onClose} className="flex sm:hidden -ml-2.5"
            rrethi={{ background: `var(--surface2, ${COLORS.panelSoft})`, color: `var(--text, ${COLORS.ink})` }}>
            <ChevronLeft size={20} color="currentColor" />
          </ButonIkone>
          {/* `titullMadh` — vetëm aty ku dritarja ËSHTË një faqe më vete, si
               «Çfarë do të shtosh?». Te mockup-i ai titull është serif i
               madh; te një dritare e vogël cilësimesh i njëjti numër do të
               ishte i zhurmshëm. Prandaj është veti e thirrjes, jo e Modal-it. */}
           <h3 className="flex-1" style={{ fontFamily: `var(--font-display, ${FONTS.display})`, color: `var(--text, ${COLORS.ink})`, fontSize: (titullMadh ? 27 : 19), fontWeight: 600, lineHeight: titullMadh ? 1.1 : undefined }}>{title}</h3>
          {/* X — VETËM desktop (telefoni e ka shigjetën sipër) */}
          <ButonIkone onClick={onClose} className="hidden sm:flex -mr-2.5" stil={{ color: `var(--text2, ${COLORS.inkLight})` }}><X size={18} color="currentColor" /></ButonIkone>
        </div>
        <div className="modal-content p-4 sm:p-5" style={{ overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", flex: 1, paddingBottom: `max(1rem, ${SAFE_BOTTOM})` }}>{children}</div>
      </div>
    </div>
  );
}
// ETIKETAT E FUSHAVE — pa shkronja të mëdha.
//
// Ishin `text-transform: uppercase` me font monospace: «DATA E LINDJES»,
// «VENDBANIMI». Shkronjat e mëdha e heqin formën e fjalës — atë profilin lart e
// poshtë që syri e njeh pa e lexuar shkronjë për shkronjë — dhe në shqip, me ë
// dhe ç, bëhet edhe më e vështirë. Për dy fjalë mbi një kuti teksti nuk fitohet
// asgjë; humbet vetëm shpejtësia e leximit.
function FieldLabel({ children }) {
  return <label className="block mb-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, letterSpacing: 0.1, color: `var(--text2, ${COLORS.inkLight})` }}>{children}</label>;
}
// Switch modern (ndez/fik) — zëvendëson checkbox-et te panelet e reja. Rresht i
// plotë me tekst + përshkrim opsional majtas dhe switch djathtas.
function Toggle({ on, onChange, label, desc, icon }) {
  return (
    <div className="flex items-center gap-3 py-2">
      {icon != null && <span className="shrink-0">{icon}</span>}
      <div className="flex-1 min-w-0">
        <div style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>{label}</div>
        {desc && <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, lineHeight: 1.35 }}>{desc}</div>}
      </div>
      <button type="button" role="switch" aria-checked={!!on} onClick={() => onChange(!on)}
        style={{ width: 40, height: 23, borderRadius: 999, background: on ? (COLORS.moss || COLORS.gold) : "rgba(120,110,90,0.28)", position: "relative", flexShrink: 0, border: "none", cursor: "pointer", transition: "background .15s", padding: 0 }}>
        <span style={{ position: "absolute", top: 2, left: on ? 19 : 2, width: 19, height: 19, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.3)", transition: "left .15s" }} />
      </button>
    </div>
  );
}
// Kontroll segmentesh (pill) — një zgjedhje nga disa opsione, modern.
function Segmented({ value, onChange, options, size = 11.5 }) {
  return (
    <div className="flex rounded-xl" style={{ background: COLORS.panelSoft, padding: 3, gap: 2 }}>
      {options.map(([val, label]) => (
        <button key={val} type="button" onClick={() => onChange(val)} className="flex-1"
          style={{ border: "none", background: value === val ? (COLORS.panel) : "transparent", boxShadow: value === val ? "0 1px 3px rgba(0,0,0,.14)" : "none", color: COLORS.ink, fontWeight: value === val ? 700 : 500, fontFamily: FONTS.body, fontSize: size, padding: "7px 4px", borderRadius: 9, cursor: "pointer" }}>
          {label}
        </button>
      ))}
    </div>
  );
}
// FAZA 23 — "Harta e Familjes" (choropleth mini, brenda modalit "Statistika").
// Ripërdor `countryList` që ekziston tashmë (llogaritur nga vetë `people`,
// asnjë RPC/thirrje shtesë). `world-atlas` merret nga CDN në kohë
// ekzekutimi — jo i paketuar, e mban app-in të lehtë.
// `onPick` (24 gusht): në telefon s'ka hover — trokitja mbi një vend te harta
// duhet të bëjë atë që në desktop e bën rreshti i listës: t'i hapë emrat.
// Kufijtë (lon/lat) e një grupi formash GeoJSON, pa asnjë varësi të re.
// `d3-geo` do ta bënte këtë me një rresht, por ai vjen te projekti vetëm si
// varësi e tërthortë e `react-simple-maps` — dhe një import që mbështetet te
// varësia e dikujt tjetër prishet ditën që ai e ndryshon. Dhjetë rreshta këtu
// janë më të lira se një ndërtim i dështuar te Cloudflare.
function kufijtëEFormave(geos) {
  let minX = 180, minY = 90, maxX = -180, maxY = -90, gjeti = false;
  const pikë = (c) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === "number" && typeof c[1] === "number") {
      gjeti = true;
      if (c[0] < minX) minX = c[0];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[1] > maxY) maxY = c[1];
      return;
    }
    for (const x of c) pikë(x);
  };
  for (const g of geos) pikë(g?.geometry?.coordinates);
  return gjeti ? [minX, minY, maxX, maxY] : null;
}

function FamilyMapMini({ countsByNumeric, unmatched = [], t, onPick }) {
  const [hovered, setHovered] = useState(null);
  // HARTA HAPET ATY KU ËSHTË FAMILJA (kërkesë e pronarit: «shumë zoom out, tepër
  // shumë white space»). Bota e tërë ka kuptim vetëm nëse familja është e
  // shpërndarë në botë; për një familje kosovare ajo është 95% oqean.
  //
  // Pamja fillestare llogaritet nga vetë vendet me të dhëna, pasi harta ngarkohet
  // — jo nga një numër i ngulitur, sepse një familje e shpërndarë në tri
  // kontinente do ta kishte po aq të gabuar një zoom fiks te Ballkani.
  const [mapPos, setMapPos] = useState({ coordinates: [16, 44], zoom: 4 });
  const uPershtat = useRef(false);
  const zoomBy = (f) => setMapPos((p) => ({ ...p, zoom: Math.min(40, Math.max(1, p.zoom * f)) }));
  const fillestarja = useRef({ coordinates: [16, 44], zoom: 4 });
  const resetZoom = () => setMapPos(fillestarja.current);

  // Thirret nga `Geographies` sapo format mbërrijnë. Vendosja e gjendjes shtyhet
  // me `queueMicrotask`, sepse brenda render-it do të ishte gjendje e vendosur
  // gjatë vizatimit — React-i ankohet, dhe me të drejtë.
  const pershtatPamjen = (geographies) => {
    if (uPershtat.current) return;
    // ⚠ 5 shtator 2026 — «a mund ta fiksojmë hartën këtu».
    //
    // Harta hapej e gjerë sepse përshtatej te ÇDO shtet me qoftë edhe një
    // person. Një kushëri i vetëm në Kanada e tërhiqte kornizën në gjysmë
    // globi, dhe pjesa tjetër bëhej ujë — pikërisht ajo që dukej «e trishtë».
    //
    // Tani përshtatja bëhet te shtetet që mbajnë ~90% të njerëzve. Kanadaja
    // mbetet e ngjyrosur dhe e arritshme me rrëshqitje; thjesht nuk e vendos
    // ajo se ku hapet harta. Vetrregullohet: nesër, kur atje të jenë dhjetë
    // veta, ata hyjnë vetë brenda 90%-shit dhe korniza hapet.
    const PJESA = 0.9;
    const gjithsej = Object.values(countsByNumeric).reduce((a, b) => a + b, 0);
    const radhitur = Object.entries(countsByNumeric).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
    const bërthama = new Set();
    let mbledhur = 0;
    for (const [id, n] of radhitur) {
      bërthama.add(String(id));
      mbledhur += n;
      if (gjithsej > 0 && mbledhur / gjithsej >= PJESA) break;
    }
    const meTeDhena = geographies.filter((g) => bërthama.has(String(g.id)));
    // Kosova nuk ekziston si formë te `world-atlas` (është brenda Serbisë),
    // pra pa këtë rresht një familje krejt kosovare do të kishte ZERO forma për
    // t'u përshtatur — dhe harta do të mbetej te parazgjedhja, rastësisht e
    // saktë. Kutia është e përafërt me qëllim: përdoret vetëm për kornizën.
    const format = meTeDhena.slice();
    if (bërthama.has("983")) {
      format.push({ geometry: { coordinates: [[[20.01, 41.85], [21.79, 41.85], [21.79, 43.27], [20.01, 43.27]]] } });
    }
    if (format.length === 0) return;
    const k = kufijtëEFormave(format);
    if (!k) return;
    uPershtat.current = true;
    const [minX, minY, maxX, maxY] = k;
    // Rusia dhe Franca kanë territore që shtrihen gjysmën e globit; një kufi i
    // gjerë pa kufizim do ta kthente pamjen te bota, pra pikërisht atje nga ku
    // po ikim. Prandaj gjerësia kufizohet.
    const gjeresia = Math.min(200, Math.max(6, maxX - minX));
    const lartesia = Math.min(120, Math.max(4, maxY - minY));
    // Zoom-i i `ZoomableGroup` është shumëzues mbi projeksionin: te 1 duket
    // gjithë bota (360°). Mbushja 1.6 lë ajër përreth, që vendi të mos prekë
    // buzët e kornizës.
    const zoom = Math.max(1, Math.min(12, Math.min(360 / (gjeresia * 1.6), 180 / (lartesia * 1.6))));
    const pamja = { coordinates: [(minX + maxX) / 2, (minY + maxY) / 2], zoom };
    fillestarja.current = pamja;
    queueMicrotask(() => setMapPos(pamja));
  };

  const maxCount = Math.max(1, ...Object.values(countsByNumeric));
  // Fill i vendeve me të dhëna — gjithmonë ari, me intensitet sipas numrit.
  //
  // ⚠ NDREQUR 24 gusht: vendet pa të dhëna vizatoheshin me `COLORS.onBg` —
  // ngjyra "mbi SFONDIN e temës". Po harta nuk rri mbi sfond; rri mbi PANEL.
  // Pas PAMJA2-shit red_and_black-u i çelët i mori panelet e bardha të Modernes
  // dhe mbajti `onBg`-në e vet të çelët (ashtu duhej — atë e përdor pema mbi
  // sfondin e kuq të errët) → e çelët mbi të çelët, hartë e padukshme.
  //
  // Ngjyra e duhur mbi panel është `ink` — teksti i panelit, që ÇDO temë e
  // garanton të lexueshëm mbi `panel`, përndryshe s'do të lexohej asnjë fjalë.
  const dataOpacity = (c) => 0.4 + 0.6 * (c / maxCount);

  // ⚠ 5 shtator 2026 — «diçka është pak e trishtë si hartë».
  //
  // Kishte të drejtë, dhe shkaku ishte te ngjyra, jo te rezolucioni: toka
  // vizatohej `ink` @0.16 dhe kufiri `ink` @0.4 — gri e ftohtë me vija thuajse
  // të zeza, pra pamja e parazgjedhur e bibliotekës, jo e temës.
  //
  // Tani është atlas letre: deti është vetë paneli, toka një hije e butë e
  // tekstit mbi të, dhe KUFIJTË JANË NË NGJYRË TË DETIT — pra shtetet ndahen
  // si copa letre të prera, pa asnjë vijë të errët.
  //
  // Të tria vijnë nga tokenat e temës, jo nga hex — ndaj punon njësoj te tema
  // e errët, ku `panel` bëhet i errët dhe `ink` i çelët: toka mbetet një hije e
  // ndriçuar mbi panel, dhe kufijtë mbeten deti. Kjo është arsyeja pse s'e
  // shkrova paletën e ngrohtë me kod ngjyre.
  const DETI = COLORS.panel;
  const TOKA = COLORS.ink;
  const TOKA_OP = 0.13;

  if (Object.keys(countsByNumeric).length === 0 && unmatched.length === 0) return null;

  return (
    <div style={{ position: "relative", marginBottom: 12 }}>
      <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panel, padding: 6 }}>
        <ComposableMap projectionConfig={{ scale: 130 }} style={{ width: "100%", height: "auto", background: DETI, display: "block" }}>
          <ZoomableGroup zoom={mapPos.zoom} center={mapPos.coordinates} onMoveEnd={setMapPos} maxZoom={40} minZoom={1}>
          <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json">
            {({ geographies }) => {
              pershtatPamjen(geographies);
              return geographies.map((geo) => {
                const count = countsByNumeric[geo.id] || 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={count > 0 ? COLORS.gold : TOKA}
                    fillOpacity={count > 0 ? dataOpacity(count) : TOKA_OP}
                    stroke={DETI}
                    strokeOpacity={1}
                    strokeWidth={0.7}
                    /* Pa këtë, `ZoomableGroup` e shumëzon edhe trashësinë e
                       vijës: te zoom 8 kufijtë bëhen brezа. */
                    vectorEffect="non-scaling-stroke"
                    onMouseEnter={(evt) => setHovered({ id: geo.id, name: geo.properties?.name || "—", count, x: evt.clientX, y: evt.clientY })}
                    onMouseMove={(evt) => setHovered((h) => (h ? { ...h, x: evt.clientX, y: evt.clientY } : h))}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => { if (count > 0 && onPick) onPick(String(geo.id)); }}
                    style={{ default: { outline: "none" }, hover: { outline: "none", fillOpacity: Math.min(1, (count > 0 ? dataOpacity(count) : 0.14) + 0.2) }, pressed: { outline: "none" } }}
                  />
                );
              });
            }}
          </Geographies>
          {/* Kosova NUK ekziston si formë e veçantë te world-atlas (është
              bashkuar me Serbinë). Prandaj e vizatojmë me një GeoJSON të veçantë
              SIPËR hartës bazë, me numrat/ngjyrën e VETË Kosovës (kodi "983").
              I njëjti trajtim si te Admin Center-i. */}
          <Geographies geography="https://raw.githubusercontent.com/sdimunzio/countries-geojson/master/kosovo.json">
            {({ geographies }) =>
              geographies.map((geo) => {
                const count = countsByNumeric["983"] || 0;
                // Vetëm MBUSHJE — pa vijë. Vija e Kosovës vjen nga harta bazë
                // (kufiri i "Serbisë" e përfshin), kështu duket natyrale si
                // vendet e tjera dhe nuk dyfishohet në vijë të zezë të trashë.
                return (
                  <Geography
                    key="kosovo-overlay"
                    geography={geo}
                    fill={count > 0 ? COLORS.gold : TOKA}
                    fillOpacity={count > 0 ? dataOpacity(count) : TOKA_OP}
                    stroke="none"
                    strokeWidth={0}
                    onMouseEnter={(evt) => setHovered({ id: "983", name: "Kosovo", count, x: evt.clientX, y: evt.clientY })}
                    onMouseMove={(evt) => setHovered((h) => (h ? { ...h, x: evt.clientX, y: evt.clientY } : h))}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => { if (count > 0 && onPick) onPick("983"); }}
                    style={{ default: { outline: "none" }, hover: { outline: "none", fillOpacity: Math.min(1, (count > 0 ? dataOpacity(count) : 0.14) + 0.2) }, pressed: { outline: "none" } }}
                  />
                );
              })
            }
          </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        {/* Kontrollet e zoom-it */}
        <div style={{ position: "absolute", top: 10, right: 10, display: "flex", flexDirection: "column", gap: 4 }}>
          <button onClick={() => zoomBy(1.5)} title={t("Zoom in")} style={{ width: 30, height: 30, borderRadius: 6, background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, fontSize: 18, lineHeight: 1, cursor: "pointer" }}>＋</button>
          <button onClick={() => zoomBy(1 / 1.5)} title={t("Zoom out")} style={{ width: 30, height: 30, borderRadius: 6, background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, fontSize: 18, lineHeight: 1, cursor: "pointer" }}>－</button>
          <button onClick={resetZoom} title={t ? t("Reset") : "Reset"} style={{ width: 30, height: 30, borderRadius: 6, background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, fontSize: 13, lineHeight: 1, cursor: "pointer" }}>⤢</button>
        </div>
      </div>
      {hovered && hovered.count > 0 && (
        <div style={{ position: "fixed", left: hovered.x + 12, top: hovered.y + 12, background: COLORS.ink, color: "#F6F2E7", fontSize: 12, padding: "4px 8px", borderRadius: 6, pointerEvents: "none", zIndex: 50 }}>
          {hovered.name}: {hovered.count}
        </div>
      )}
      {unmatched.length > 0 && (
        <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 6 }}>
          ⓘ {t
            ? t("{{list}} — shown in the list below only; the map can’t place these.", { list: unmatched.join(", ") })
            : `${unmatched.join(", ")} — shown in the list below only.`}
        </p>
      )}
    </div>
  );
}

// Numërator që tik-tak për faqen e Maintenance Mode. `until` = timestamptz.
// Parse kohëzgjatjen e lirë ("1h", "1 orë", "2 hours", "90 min", "1 ditë") në ms.
// Përdoret për të fshehur VETË njoftimin kur skadon, edhe kur admini s'ka vendosur
// një "ends_at" të qartë (vetëm kohëzgjatjen).
function parseDurationMs(txt) {
  if (!txt) return null;
  const s = String(txt).toLowerCase().replace(",", ".");
  let ms = 0, found = false;
  const re = /(\d+(?:\.\d+)?)\s*(dit|dat|day|d|or|hour|hr|h|min|m)/g;
  let mm;
  while ((mm = re.exec(s))) {
    const n = parseFloat(mm[1]); const u = mm[2];
    if (u.startsWith("dit") || u.startsWith("dat") || u.startsWith("day") || u === "d") ms += n * 86400000;
    else if (u.startsWith("or") || u.startsWith("hour") || u.startsWith("hr") || u === "h") ms += n * 3600000;
    else if (u.startsWith("min") || u === "m") ms += n * 60000;
    found = true;
  }
  return found ? ms : null;
}
// A ka skaduar një njoftim mirëmbajtjeje? ends_at ka përparësi; përndryshe
// llogaritet nga starts_at + kohëzgjatja. Pa asnjërën → s'skadon vetë.
function maintNoticeExpired(n) {
  try {
    if (!n) return false;
    if (n.ends_at) return new Date(n.ends_at).getTime() <= Date.now();
    const dur = parseDurationMs(n.duration_text);
    if (dur && n.starts_at) return new Date(n.starts_at).getTime() + dur <= Date.now();
    return false;
  } catch (_e) { return false; }
}

function MaintCountdown({ until, labelPrefix, onDone }) {
  const [now, setNow] = useState(Date.now());
  const firedRef = useRef(false);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Kur ora mbërrin te zero, rikontrollo statusin menjëherë (mos prit polling-un
  // 30s) që dalja nga mirëmbajtja të ndodhë sa më shpejt.
  useEffect(() => {
    if (!until) return;
    if (new Date(until).getTime() - now <= 0 && !firedRef.current) {
      firedRef.current = true;
      if (typeof onDone === "function") onDone();
    }
  }, [until, now, onDone]);
  if (!until) return null;
  const ms = new Date(until).getTime() - now;
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.gold, marginTop: 10 }}>
      {labelPrefix} {h > 0 ? `${h}h ` : ""}{pad(m)}:{pad(sec)}
    </div>
  );
}

function DocHtmlContent({ row, docUrl }) {
  const { t } = useTranslation();
  if (row?.content_html) {
    return (
      <div
        className="doc-content"
        style={{ fontFamily: FONTS.body, fontSize: 14.5, color: COLORS.ink, lineHeight: 1.65 }}
        dangerouslySetInnerHTML={{ __html: row.content_html }}
      />
    );
  }
  if (row) {
    // Older document uploaded before the Word→HTML conversion existed —
    // there's no processed version to render, only the original file.
    return (
      <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, textAlign: "center", padding: "24px 0" }}>
        {t("This older version of the document was not converted to a page.")} <a href={docUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.gold, textDecoration: "underline" }}>{t("Download the original file")}</a>.
      </p>
    );
  }
  return null;
}


// `SimpleMarkdown` u hoq (20 gusht 2026) — ekzistonte vetëm për të
// përpunuar tekstet e ngulitura te `docFull.js`/`docShort.js`. Pas heqjes
// së tyre asnjë vend s'e thërriste; dokumentet e vërteta janë HTML dhe
// shfaqen nga `DocHtmlContent`.
function TermsContent({ termsDoc }) {
  if (!termsDoc) return null;
  return <DocHtmlContent row={termsDoc.row} docUrl={termsDoc.publicUrl} />;
}
// Inputet përdorin tokena dizajni (var()) — funksionon te `style={}` i DOM-it
// dhe përditësohet LIVE kur publikohet një dizajn. Fallback = pamja klasike.
const inputStyle = { background: "var(--surface, #fff)", border: `1px solid var(--border, ${COLORS.goldSoft})`, fontFamily: `var(--font-body, ${FONTS.body})`, color: `var(--text, ${COLORS.ink})` };
// `MONTHS_SQ` u hoq (19 gusht 2026) — datat formatohen nga `datetime.js`
// sipas gjuhës aktive; kjo listë e ngulitur s'përdorej askund.

// DateSelect — mbahet emri i vjetër që thirrjet ekzistuese të mos ndryshojnë.
// Brendia u zëvendësua: në vend të tri listave rënëse, tani shkruhet lirshëm
// ("29061984" → 29.06.1984) ose zgjidhet nga kalendari i sistemit. Renditja e
// pjesëve merret nga rajoni i pajisjes. Shih src/DateField.jsx.
function DateSelect({ value, onChange }) {
  return <DateField value={value} onChange={onChange} inputStyle={{ ...inputStyle, padding: "8px 10px", fontSize: 13 }} />;
}

export default function App({ familySlug = null }) {
  // FAZA 10 — shih PHASE10_I18N.md. `t()` përdoret vetëm te pjesët e
  // migruara ende (modali i login-it, disa etiketa navigimi) — pjesa tjetër
  // e madhe e App.jsx mbetet ende me tekst shqip të ngulitur direkt, siç
  // ishte — shih dokumentin për shtrirjen e plotë dhe planin e migrimit.
  const { t, i18n } = useTranslation();
  // Zgjedhja e gjuhës: "system" ose kodi. Opsionet vijnë nga `getSwitcherOptions`:
  // të preferuarat e Admin Center-it të parat, pastaj çdo gjuhë tjetër që ka
  // skedar përkthimi. Të dytat rrinë të mbyllura derisa dikush t'i kërkojë.
  const [langChoice, setLangChoice] = useState(() => getStoredChoice());
  const [gjuhetETjeraHapur, setGjuhetETjeraHapur] = useState(false);
  // MUZEU1 — kujdestarët mbahen VEÇ nga figurat. Shih `fetchPeople`.
  const [kujdestaret, setKujdestaret] = useState({});
  // EMRI: `llojiHapesires`, JO `hapesira`.
  //
  // E pata quajtur `hapesira` — dhe te ky skedar ekziston prej muajsh një
  // funksion i nivelit të modulit me po atë emër (`function hapesira(baza, px)`,
  // ndihmësja e hapësirës së kokës dhe e shiritit). Gjendja e komponentit e
  // MBULOI atë brenda gjithë komponentit, dhe `hapesira(SAFE_TOP, …)` u bë
  // thirrje mbi një objekt:
  //
  //     TypeError: hapesira is not a function
  //
  // Ekran i bardhë, dhe te konsola vetëm `c is not a function` — sepse pas
  // minifikimit emri nuk ekziston më. Asnjë nga dy veglat s'e kapte: të dy
  // emrat JANË të deklaruar; problemi ishte se njëri e mbulonte tjetrin.
  // Prandaj `kontrollo-emrat.mjs` mësoi sot edhe këtë.
  //
  // Lloji i hapësirës: 'familje' (parazgjedhje) ose 'muze', dhe paraqitja
  // 'peme' ose 'koleksion'. Lexohet para se të vizatohet çfarëdo, sepse te një
  // muze bien veçori të tëra — jo fshihen me kusht anësor, por nuk ekzistojnë.
  const [llojiHapesires, setLlojiHapesires] = useState({ lloji: "familje", paraqitja: "peme", kiosku: false, kiosk_pauza: 40, kiosk_lake: 12 });
  const eshteMuze = llojiHapesires.lloji === "muze";
  // Kioska ndizet vetëm te një muze — rojtari te baza e bën atë të pamundur, po
  // klienti nuk mbështetet te ai: një ekran në dhomën e ndenjes që tregon të
  // vdekurit në lak do të ishte gabim shumë i madh për t'u lënë në një vend.
  const kiosku = eshteMuze && !!llojiHapesires.kiosku;
  // MUZEU3 — salla e zgjedhur te pamja «Sipas sallës». Bosh = ende pa zgjedhur.
  // «Shto histori» premtonte një formular dhe dorëzonte një vend: të çonte te
  // ekrani i Historisë, ku formularin duhej ta gjeje vetë — dhe te telefoni ai
  // rri nën titullin e skedave. Folja duhet ta dorëzojë atë që premton.
  // BALLINA8 — DY VEPRIME, DY FAQE.
  //
  // Pronari, duke parë listën e «Shto»: «vetëm se ndryshimi është që të dyja të
  // dërgojnë te i njëjti vend». Kishte të drejtë — «Shto histori» dhe «Shto
  // foto» të çonin te Historia dhe vetëm lëviznin kursorin te një fushë tjetër.
  // Dy zëra që premtojnë dy gjëra dhe dorëzojnë të njëjtin ekran nuk janë dy
  // zgjedhje; janë një zgjedhje e vizatuar dy herë.
  //
  // Poshtë janë vërtet dy gjëra të ndryshme: `family_history_events` (ngjarje
  // me datë, titull, rrëfim, foto) dhe `archive_items` (skedar me lloj,
  // madhësi, persona). Prandaj secila merr faqen e vet.
  const [shtoHistoriOpen, setShtoHistoriOpen] = useState(false);
  const [shtoSkedarOpen, setShtoSkedarOpen] = useState(false);
  // KERKIMI1 — një kërkim, te koka, në çdo faqe.
  //
  // Tri kërkime te tri faqe do të ishin tri zbatime, dhe brenda muajit do të
  // silleshin ndryshe — po ai gabim me veshje të re. Prandaj një dritare, dhe
  // ajo që ndryshon është VETËM fusha ku kërkon.
  //
  // Nis te faqja ku je, sepse aty je duke kërkuar diçka; por «Kudo» rri një
  // prekje larg, sepse ndonjëherë s'e mban mend te cila faqe e pe.
  const [kerkimiOpen, setKerkimiOpen] = useState(false);
  const [kerkimiTeksti, setKerkimiTeksti] = useState("");
  const [kerkimiKudo, setKerkimiKudo] = useState(false);
  const [sallaAktive, setSallaAktive] = useState("");
  const [gjuhaPanelOpen, setGjuhaPanelOpen] = useState(false); // faqja e gjuhëve (shih menynë e avatarit)
  // Emri i gjuhës së zgjedhur, për ta treguar te rreshti pa e hapur faqen.
  const emriIGjuhes = useCallback((vlera) => {
    if (vlera === "system") return t("System (phone language)");
    const o = getSwitcherOptions().find((x) => x.value === vlera);
    return o ? o.label : vlera;
  }, [t]);
  // Design Studio LIVE — kur admini publikon një dizajn (ose ndërrohet
  // light/dark), risinkronizo COLORS dhe ri-rendero KREJT app-in. Pa këtë,
  // "Publish" te Design Studio s'e prekte pamjen (COLORS ishte i ngulitur).
  const [designTick, setDesignTick] = useState(0);
  useEffect(() => {
    syncDesignColors(); // apliko dizajnin aktiv sapo App montohet
    const off = onDesignChange(() => { syncDesignColors(); setDesignTick((n) => n + 1); });
    return off;
  }, []);
  // ==========================================================================
  // STATE. Roughly grouped as: core data (people/roles/settings), auth flow,
  // which modal/panel is open, form drafts, and view/navigation state (see
  // ARCHITECTURE.md §3 for the full breakdown). There's a lot of it because
  // this single component IS the whole app — see ARCHITECTURE.md §3 for why
  // that's a deliberate choice rather than an oversight.
  // ==========================================================================
  const [loading, setLoading] = useState(true);
  // CACHE1 — a jemi pa internet? Ngrihet nga ngjarjet e shfletuesit dhe nga
  // dështimet e rrjetit te ngarkimet; ulet sapo një kërkesë kalon prapë.
  const [jeOffline, setJeOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  useEffect(() => {
    const ra = () => setJeOffline(false);
    const bie = () => setJeOffline(true);
    window.addEventListener("online", ra);
    window.addEventListener("offline", bie);
    return () => { window.removeEventListener("online", ra); window.removeEventListener("offline", bie); };
  }, []);
  const [people, setPeople] = useState({});
  // Kërkesë e userit — skema e kartelës (horizontal/vertikal) dhe hapësira
  // mes vëllezërve/motrave e çifteve, të gjitha të rregullueshme nga Admin
  // Center → Cilësimet (shih rpc_get_tree_display_settings te schema.sql).
  // `layoutVersion` "detyron" rillogaritjen e positions/basePositions kur
  // H_SPACING/SPOUSE_OFFSET/CARD_W/CARD_H ndryshojnë (janë ndryshore module
  // -scope, jo state — vetë ndryshimi i tyre s'do ta shkaktonte këtë
  // rillogaritje pa këtë numërues shtesë të varësive).
  const [treeCardLayout, setTreeCardLayout] = useState("horizontal");
  // Kur dizajni aktiv e ka ndezur pamjen «heirloom» PËR EKRANIN E PEMËS, skema
  // e kartelës vjen nga ai, jo nga cilësimi i Admin Center-it. Cilësimi nuk
  // fshihet — thjesht nuk zbatohet sa kohë ai ekran ka pamjen e vet. Fikja e
  // çelësit e kthen menjëherë atë që kishte zgjedhur admini.
  const treeCardLayoutEf = pemaHeirloom() ? "heirloom" : treeCardLayout;
  const [layoutVersion, setLayoutVersion] = useState(0);
  // SHTESË (kërkesë e userit) — "korniza e fotos" (unaza rreth fotos në
  // kartelat foto-lart) tani ka 3 mënyra, të rregullueshme nga Admin
  // Center: "theme" (ngjyra e artë e temës aktuale, njësoj për të gjithë),
  // "branch" (siç ishte — ngjyra e degës familjare), "none" (asnjë ngjyrë,
  // stil neutral gri/krem). Parazgjedhje "branch", për të mos ndryshuar
  // asgjë pa e prekur vetë admini.
  // «dizajni» = ngjyrat i jep tema (siç ishte); «familja» = ngjyrat i jep
  // pozicioni në pemë (Rregullat & Ngjyrat) dhe cilësimet e vijave.
  // Parazgjedhja është «dizajni», që asnjë familje ekzistuese të mos ndryshojë
  // pamje pa e kërkuar vetë.
  const [burimiINgjyrave, setBurimiINgjyrave] = useState("dizajni");
  // Zgjedhja E PËRDORUESIT, mbi parazgjedhjen e platformës — i njëjti model
  // si `userNavVariant`: bosh = ndiq platformën; ruhet vetëm në këtë pajisje.
  // Pronari: «unë po dua që useri të mundet te pema të zgjedhë pamjen e temës,
  // psh mundet me zgjedh ngjyrat e degës, ngjyrat e temës, edhe asnjë ngjyrë».
  const [userBurimiNgjyrave, setUserBurimiNgjyrave] = useState(() => {
    try { return localStorage.getItem("pef_tree_colors") || ""; } catch { return ""; }
  });
  const zgjidhBurimineNgjyrave = (val) => {
    try { if (val) localStorage.setItem("pef_tree_colors", val); else localStorage.removeItem("pef_tree_colors"); } catch { /* private mode */ }
    setUserBurimiNgjyrave(val);
  };
  // Lexohet me një thirrje të vetën, jo brenda `rpc_get_tree_display_settings`:
  // ai funksion ka leje të dhëna dhe thirrje të vjetra, dhe ndryshimi i tabelës
  // që kthen do të kërkonte `drop` — pra humbje lejesh. Një RPC e re është më
  // e lirë se një rrezik. Nëse thirrja dështon (skedari s'është xhiruar ende),
  // mbetet «dizajni» — sjellja e mëparshme.
  // ── ZMADHIMI I APLIKACIONIT (ZOOM1) ─────────────────────────────────────
  // Një përqindje e vetme nga Admin Center që shkallëzon GJITHÇKA bashkë —
  // shkronjat, butonat, hapësirat, ikonat — pra proporcionet mbeten saktësisht
  // si janë. Nuk është «fonte më të mëdha»: ajo do t'i shtynte tekstet jashtë
  // kutive që rrinë me piksela.
  //
  // Zbatohet me `zoom` te rrënja e dokumentit, jo me `transform: scale()`:
  // `transform` e lë faqen me përmasat e vjetra dhe krijon shirita rrëshqitës
  // të gabuar, ndërsa `zoom` e rillogarit vetë kutinë e faqes.
  // 100 (ose thirrje e dështuar, kur ZOOM1 s'është xhiruar ende) = pamja e
  // sotme, e paprekur.
  useEffect(() => {
    let anuluar = false;
    supabase.rpc("rpc_get_app_zoom").then(({ data, error }) => {
      if (anuluar || error || data == null) return;
      const z = Number(data);
      if (!Number.isFinite(z) || z < 80 || z > 160) return;
      try { document.documentElement.style.zoom = z === 100 ? "" : String(z / 100); } catch (_e) {}
    }).catch(() => {});
    return () => { anuluar = true; };
  }, []);
  useEffect(() => {
    let anuluar = false;
    supabase.rpc("rpc_get_tree_color_source").then(({ data, error }) => {
      if (anuluar || error) return;
      setBurimiINgjyrave(data === "familja" ? "familja" : "dizajni");
    }).catch(() => {});
    return () => { anuluar = true; };
  }, []);
  const [photoRingMode, setPhotoRingMode] = useState("branch");
  // Unaza rreth fotos — trashësia si SHUMËZUES (secila skemë ka bazën e vet:
  // 1.75 te ovali, 3 te Modern; një numër i ngurtë do t'i barazonte të gjitha)
  // dhe hendeku në piksela. 1.0/2 = pamja e deritanishme; 0 = pa unazë.
  const [photoRingWidth, setPhotoRingWidth] = useState(1);
  const [photoRingGap, setPhotoRingGap] = useState(2);
  // SHTESË (kërkesë e userit) — "identifikimi i origjinës jo-plotë":
  // ngjyrë e VEÇANTË (#8B5E3C, VETËM te unaza/rrethi i fotos, jo te vija
  // lidhëse — u konfirmua shprehimisht) për 2 kategori, të dokumentuara
  // këtu qartë siç u kërkua ("memorizo tek dokumentacioni"):
  //
  // 1) "Fëmijë i adoptuar" — shënuar EKSPLICITISHT nga admin/editor përmes
  //    dropdown-it "Rast i veçantë" (te formulari i personit),
  //    `p.branch_special_case === "adopted"`.
  //
  // 2) "Fëmijë solo" — ÇDO person me VETËM 1 prind të regjistruar
  //    (`p.parent_ids.length === 1`), PAVARËSISHT cili prind (baba OSE
  //    nënë) — u konfirmua shprehimisht nga useri: "është e pamundur që
  //    babai të ketë fëmijë me dikë e të mos e dijë origjinën; ose është i
  //    adoptuar, ose ka nënë [të panjohur/paregjistruar] — bie vetvetiu
  //    nën të njëjtën kategori vizuale". Kjo mbulon VETVETIU edhe rastin
  //    "prind i panjohur" (kategoria e 3-të e dropdown-it "Rast i
  //    veçantë") — s'nevojitet kontroll shtesë i veçantë për të, thjesht
  //    duke qenë solo (1 prind) e vë tashmë në këtë kategori.
  //
  // KJO NGJYRË KA PËRPARËSI mbi cilësimin e zakonshëm "Ngjyra e unazës"
  // (dega/tema/asnjë, Admin Center) — për këta persona specifikë,
  // mbivendos GJITHMONË, pavarësisht çfarë ka zgjedhur admini atje
  // (sinjal më specifik/i rëndësishëm — origjina familjare — se ngjyra e
  // përgjithshme e degës).
  const SOLO_OR_ADOPTED_RING_COLOR = "#9CA3AF"; // gri neutral, i zgjedhur nga useri (më i dukshëm mbi sfonde të errëta se #8B5E3C fillestare)
  // SHTESË (kërkesë e userit — "Ngjyrat e Pemës", çdo ngjyrë dege +
  // statusi i shkurorëzuar, e personalizueshme nga Admin Center) — harton
  // çdo degë te fusha përkatëse e `treeAdvLayout`, duke rënë mbrapa te
  // `branchColor()` origjinale (COLORS.branchXxx) nëse admini s'e ka
  // ndryshuar ende asgjë.
  // BURIMI I VETËM I NGJYRAVE: "Rregullat & Ngjyrat" (rule_sets). Editori i vjetër
  // "Ngjyrat e Pemës" te Cilësimet u hoq — tani gjithçka vjen nga branchColor().
  const adminBranchColor = (branch) => branchColor(branch);
  const ringColorFor = (p) => {
    // FIX (kërkesë e userit — "unaza mbetet edhe kur e ndryshoj rastin e
    // veçantë") — hide_solo_ring lejon ta çaktivizosh EKSPLICITISHT për
    // këtë person specifik, pavarësisht se ka vetëm 1 prind të regjistruar
    // (p.sh. prindi tjetër është vërtet i panjohur, por familja s'e sheh
    // si "rast i veçantë" — thjesht person normal, pa shenjë vizuale).
    // Sjellja per-shtet: `mark_solo_child` kontrollon VETËM shenjimin e fëmijës
    // me një prind. Disa kultura e trajtojnë fëmijën me një prind plotësisht si
    // fëmijë të familjes (asnjë unazë e veçantë) → mark_solo_child=false. Adoptimi
    // është koncept i ndarë dhe shenjohet gjithmonë.
    const _beh = (ACTIVE_RULES && ACTIVE_RULES.behaviors) || {};
    const _markSolo = _beh.mark_solo_child !== false;
    if (p.hide_solo_ring) { /* kaloje kontrollin e mëposhtëm — trajtohet normalisht */ }
    else if (p.branch_special_case === "adopted" || (_markSolo && (p.parent_ids || []).length === 1)) return (ACTIVE_RULES.colors && ACTIVE_RULES.colors.solo_ring) || SOLO_OR_ADOPTED_RING_COLOR;
    // FIX v5 (kërkesë e userit — PARIM I PËRGJITHSHËM, jo kusht i lidhur me
    // rastin specifik të testuar: "ngjyra e kuqe e divorcit VLEN VETËM për
    // 'të ardhurit' — kush erdhi në familje me martesë. Fëmijët e trungut të
    // gjakut KURRË s'ndryshojnë ngjyrë pse divorcohen — identiteti i tyre
    // në pemë s'varet nga statusi martesor, pavarësisht rrethanave (edhe në
    // ardhmen, për cilindo rast tjetër që mund të shfaqet).") — kushti i
    // mëparshëm ("a ka bashkëshort/e tjetër aktiv/e") ishte HEURISTIKË e
    // lidhur me rastin specifik të testuar (Gazmend+test), jo parim i
    // qëndrueshëm. Tani: e kuqja aplikohet VETËM nëse dega efektive e
    // personit është "married_in" (erdhi në familje me martesë) — asnjëherë
    // për core/close_cousin/extended/married_out (të gjitha janë GJAK,
    // thjesht në distanca të ndryshme ose degë të reja). Filtri `people[sid]`
    // mbetet, si mbrojtje shtesë kundër referencave te persona të fshirë.
    const isMarriedIn = effectiveBranch(p) === "married_in";
    const hasValidDivorce = (p.divorced_from_ids || []).some((sid) => people[sid]); // vetëm referenca ende ekzistuese
    if (isMarriedIn && hasValidDivorce) return (ACTIVE_RULES.colors && ACTIVE_RULES.colors.divorced) || "#C0392B";
    if (paNgjyra()) return toniNeutral();
    // ⚠ 4 shtator 2026 — «te Rrënjët, tema duhet me pas gold; kurse asgjë,
    // fotografia me mbulu rrethin».
    //
    // Kishte të drejtë dhe shkakun e kisha lënë unë: unaza pyeste
    // `photoRingMode`, që vjen NGA BAZA (cilësimi i platformës). Kur njeriu e
    // ndërron burimin e ngjyrave PËR VETE te pema (ruajtur vetëm në pajisjen e
    // vet), ai cilësim nuk lëviz — pra zgjidhje «Ngjyrat e temës» dhe unaza
    // mbetej e degës. Te Trashëgimia s'dukej, sepse medaljoni i saj e kishte
    // shkruar veçmas rregullin e duhur; jashtë saj askush s'e kishte.
    //
    // Tani e pyet burimin efektiv — i njëjti që vendos vijat, kartelat dhe
    // gjithçka tjetër. Një burim i vetëm, për krejt pemën.
    if (ngjyratENdjekinDizajnin()) return PALETA_TIPI.spouseLine || COLORS.gold;
    if (burimiEfektiv() === "familja") return adminBranchColor(effectiveBranch(p));
    return photoRingMode === "theme" ? COLORS.gold : photoRingMode === "none" ? COLORS.goldSoft : adminBranchColor(effectiveBranch(p));
  };
  // Trashësia përfundimtare e unazës për një skemë të dhënë. Kthen 0 kur
  // admini e ka fikur — dhe atëherë unaza nuk vizatohet fare.
  const ringW = (base) => Math.round(base * photoRingWidth * 100) / 100;
  // SHTESË (kërkesë e userit) — pamja e butonit "+/-": "classic" (tekst
  // +/- si më parë), "chevron" (shigjetë poshtë/sipër, më modern),
  // "outline" (vija gjeometrike, jo font — më minimalist). Madhësia
  // (rrezja e rrethit) gjithashtu e rregullueshme, 12 = origjinali.
  const [expandBtnStyle, setExpandBtnStyle] = useState("classic");
  const [expandBtnSize, setExpandBtnSize] = useState(12);
  // SHTESË (kërkesë e userit) — seksion i RI, i ndarë, për "vizat" e pemës.
  // "original" (parazgjedhje) sillet EKZAKTËSISHT si sot; "parent_child"
  // rregullon lidhjen te fëmija i gjakut (jo mesi i çiftit) + heq linjat e
  // dyfishta kur të dy prindërit janë bashkëshortë të njëri-tjetrit. Stili
  // vizual (drejta/gjysmë-lakuar/lakuar) zbatohet mbi çdo topologji.
  const [treeLineTopology, setTreeLineTopology] = useState("original");
  const [treeLineStyle, setTreeLineStyle] = useState("straight");
  useEffect(() => {
    let cancelled = false;
    fetchSettingRpcWithRetry("rpc_get_tree_line_settings").then((data) => {
      if (cancelled) return;
      const rowGlobal = Array.isArray(data) ? data[0] : data;
      RRESHTI_VIJAVE = rowGlobal || null;
      // SKEMA1: mbivendosja e skemës — vetëm kur skema aktive dihet tashmë;
      // përndryshe e shtron ngarkuesi i kartelës kur të mbarojë.
      const row = RRESHTI_I_PEMES ? { ...(rowGlobal || {}), ...mbivendosjaESkemes("lines") } : rowGlobal;
      if (row?.line_topology) setTreeLineTopology(row.line_topology);
      if (row?.line_style) setTreeLineStyle(row.line_style);
    });
    return () => { cancelled = true; };
  }, []);
  // SHTESË (kërkesë e userit — "Pema Familjare" te Admin Center, kontroll
  // i plotë gjeometrie pa prekur kod) — vlerat parazgjedhje PËRPUTHEN
  // SAKTËSISHT me sjelljen aktuale të koduar (asnjë ndryshim i padukshëm
  // derisa admini t'i ndryshojë vetë nga UI).
  const [treeAdvLayout, setTreeAdvLayout] = useState({
    multi_spouse_gap_mult: 1.0, solo_child_first_offset: 1.2, solo_child_gap: 0.9,
    line_thickness: 1.6, line_color: "#C9A227", line_dash: "solid", spouse_line_color: "#E7CD82",
    // HEQJE (20 gusht 2026) — shtatë ngjyrat e degëve rrinin këtu si
    // parazgjedhje, por asnjë rresht s'i lexonte: burimi i tyre është
    // "Rules & Colors" (`rule_sets`) qëkur ai u bë i vetmi.
    expand_btn_offset_x: 0, expand_btn_offset_y: 0,
    spouse_line_offset_y: 0, solo_bend_offset: 0,
  });
  useEffect(() => {
    let cancelled = false;
    fetchSettingRpcWithRetry("rpc_get_tree_advanced_layout").then((data) => {
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row) {
        // FIX — Postgres i kthen kolonat `numeric` si TEKST (p.sh. "-22").
        // Pa këtë kthim, çdo mbledhje në kod bëhet bashkim teksti
        // ("289" + "-22" = "289-22") dhe llogaritja del NaN. Prandaj disa
        // cilësime dukeshin sikur "nuk funksiononin" ose sillnin kërcime.
        const NUM = ["multi_spouse_gap_mult", "solo_child_first_offset", "solo_child_gap",
          "line_thickness", "expand_btn_offset_x", "expand_btn_offset_y",
          "spouse_line_offset_y", "solo_bend_offset"];
        const clean = { ...row };
        NUM.forEach((k) => {
          if (clean[k] !== undefined && clean[k] !== null && clean[k] !== "") {
            const n = Number(clean[k]);
            if (Number.isFinite(n)) clean[k] = n; else delete clean[k];
          } else delete clean[k];
        });
        RRESHTI_AVANCUAR = clean;
        // SKEMA1: mbivendosja e skemës aktive, kur ajo dihet tashmë.
        const m = RRESHTI_I_PEMES ? pastroNumrat(mbivendosjaESkemes("advanced"), CELESAT_NUMERIKE_ADVANCED) : {};
        setTreeAdvLayout((prev) => ({ ...prev, ...clean, ...m }));
      }
    });
    return () => { cancelled = true; };
  }, []);
  // SHTESË (kërkesë e userit) — "fokusimi i fotografive": disa foto priteshin
  // automatikisht në qendër (preserveAspectRatio="xMidYMid slice"), duke
  // lënë fytyrën jashtë kornizës rrethore/ovale. Tani çdo person ka
  // photo_focus_x/photo_focus_y (0..1, ku ndodhet "qendra e interesit" në
  // foto — vendoset nga vetë useri në formularin e fotos, shih
  // PhotoFocusPicker). Për ta zbatuar SAKTË (jo thjesht përafrim), na duhet
  // përmasa E VËRTETË e fotos (jo vetëm ajo e kornizës) — ngarkohet një herë
  // për foto (jo për person, disa mund ta ndajnë të njëjtën shih rrallë) me
  // një <img> të padukshme, dhe ruhet këtu.
  const [photoNaturalSizes, setPhotoNaturalSizes] = useState({});
  useEffect(() => {
    Object.values(people).forEach((p) => {
      if (!p.photo_path || photoNaturalSizes[p.photo_path]) return;
      const img = new window.Image();
      img.onload = () => setPhotoNaturalSizes((prev) => (prev[p.photo_path] ? prev : { ...prev, [p.photo_path]: { w: img.naturalWidth, h: img.naturalHeight } }));
      img.src = photoUrl(p.photo_path);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people]);
  // Njësoj si "background-size: cover" + "object-position: focusX focusY"
  // në CSS — por SVG s'e ka këtë, ndaj llogaritet me dorë: shkallëzo foton
  // (natW×natH) që të MBULOJË plotësisht kutinë (boxW×boxH — zakonisht
  // katrori/drejtkëndëshi që rrethon rrethin/elipsin e clip-path-it), pastaj
  // zhvendose sipas focusX/focusY (0=skaji majtas/sipër, 1=djathtas/poshtë,
  // 0.5=qendra — parazgjedhje, sillet si më parë nëse s'është vendosur ende).
  const coverImageProps = (p, boxX, boxY, boxW, boxH) => {
    const nat = p.photo_path ? photoNaturalSizes[p.photo_path] : null;
    const focusX = p.photo_focus_x ?? 0.5, focusY = p.photo_focus_y ?? 0.5;
    const zoom = Math.max(1, Math.min(5, p.photo_zoom ?? 1));
    if (!nat || !nat.w || !nat.h) return { x: boxX, y: boxY, width: boxW, height: boxH };
    const scale = Math.max(boxW / nat.w, boxH / nat.h) * zoom;
    const scaledW = nat.w * scale, scaledH = nat.h * scale;
    return {
      x: boxX - (scaledW - boxW) * focusX,
      y: boxY - (scaledH - boxH) * focusY,
      width: scaledW, height: scaledH,
    };
  };
  // Ngarkuesi i cilësimeve mbahet si funksion i qëndrueshëm (useCallback) që
  // të mund të thirret DY herë: një herë kur hapet app-i (mount, më poshtë),
  // dhe sërish sa herë app-i kthehet në plan të parë (Android `resume` + web
  // `visibilitychange`) — kështu ndryshimet e Admin Center zbatohen pa u
  // dashur cold-start / riinstalim i APK-së.
  const treeSettingsMounted = useRef(true);
  const loadTreeDisplaySettings = useCallback(async (attempt = 1) => {
    // FIX (raportuar nga useri — "madhësia e kartelave s'ndryshon në
    // Android, edhe pas build-i të ri të APK-së" + "viza mes çifteve
    // mungon, pjesërisht") — kërkesa e mëparshme dështonte NË HESHTJE nëse
    // RPC-ja s'kthente vlerë (asnjë kontroll `error`, asnjë riprovim) —
    // Android duket se e prek këtë RPC specifike më shpesh se desktopi
    // (garë e mundshme me thirrje të tjera fillestare, ose kusht rrjeti më
    // i ngadaltë). Tani riprovohet deri në 5 herë (300ms mes tyre) para se
    // të bjerë mbrapsht te vlerat parazgjedhje — dhe REGJISTRON gabimin në
    // konsolë në vend që ta "gëlltisë" plotësisht, që problemet e ngjashme
    // të ardhshme të jenë të dukshme, jo të heshtura.
      const { data, error } = await supabase.rpc("rpc_get_tree_display_settings");
      if (!treeSettingsMounted.current) return;
      const rowGlobal = Array.isArray(data) ? data[0] : data;
      if (error || !rowGlobal) {
        if (error) console.warn("rpc_get_tree_display_settings failed (attempt " + attempt + "):", error.message);
        if (attempt < 5) { setTimeout(() => loadTreeDisplaySettings(attempt + 1), 300); }
        return;
      }
      // SKEMA1 (6 shtator 2026): mbivendosjet për skemë — një thirrje, pastaj
      // rreshti global bëhet rreshti i skemës AKTIVE. Pa SKEMA1 të xhiruar,
      // thirrja kthen null dhe `row` mbetet globalja: si sot.
      await ngarkoSkemat();
      if (!treeSettingsMounted.current) return;
      RRESHTI_I_PEMES = rowGlobal;
      const row = rreshtiIPemesEfektiv();
      // Vijat dhe gjeometria e skemës aktive — ngarkuesit e tyre mund të kenë
      // mbaruar para këtij (dhe pa e ditur skemën); këtu shtrohen mbi to.
      // Globalja + mbivendosja: kështu, kur dizajni ndërron nga një me
      // cilësime te një pa to, vlerat kthehen te globalja, jo te të vjetrat.
      {
        const l = { ...(RRESHTI_VIJAVE || {}), ...mbivendosjaESkemes("lines") };
        if (l.line_topology) setTreeLineTopology(l.line_topology);
        if (l.line_style) setTreeLineStyle(l.line_style);
        const a = { ...(RRESHTI_AVANCUAR || {}), ...pastroNumrat(mbivendosjaESkemes("advanced"), CELESAT_NUMERIKE_ADVANCED) };
        if (Object.keys(a).length) setTreeAdvLayout((prev) => ({ ...prev, ...a }));
      }
      setTreeCardLayout(row.card_layout || "horizontal");
      // SHTESË (kërkesë e userit) — gjerësia/gjatësia e kartelës tani
      // vjen nga databaza (veç e veç për secilën skemë), jo më konstante
      // fikse — CARD_W_HORIZONTAL etj. mbeten VETËM si vlera fallback nëse
      // rreshti/kolona mungon papritur (p.sh. RPC-ja dështoi pjesërisht).
      // FIX (kërkesë e userit) — 3 skemat e reja (elegant/modern3d/story)
      // janë të gjitha kompozicion "foto lart, tekst poshtë", njësoj si
      // "vertical" — ndajnë të njëjtin çift përmasash (card_width_v/
      // card_height_v), jo një kolonë të re më vete për secilën.
      // ⚠ 4 shtator 2026 — KURTHI QË E KAPA PARA SE TA SHIHTE NDONJËRI.
      // Pamja «heirloom» e pemës është kompozim «foto lart, emri poshtë», si
      // «vertical». Po përmasat e kartelës zgjidheshin KËTU, nga cilësimi i
      // ruajtur — pra një familje me skemën «horizontal» do të merrte
      // medaljonin e ri brenda një kartele të gjerë e të ulët, dhe emri me
      // vitet do të binin jashtë saj. Skema e re nuk mjafton të vizatohet;
      // duhet edhe korniza ku vizatohet.
      // Përmasat i llogarit tani një funksion i vetëm, që e thërret edhe
      // sinkronizimi i dizajnit — kështu ndezja e çelësit pa rifreskuar faqen
      // nuk të lë me kartelë të gabuar.
      zbatoPermasatEKarteles();
      H_SPACING = BASE_H_SPACING * (row.sibling_spacing || 1);
      // SHTESË (kërkesë e userit) — "kompaktësia e pemës": largësia mes
      // BREZAVE (prind→fëmijë, vertikale), krejt e ndarë nga largësia mes
      // vëllezërve/motrave (horizontale, sipër). "Standard" = 1.0 (siç
      // ishte gjithmonë); "Kompakt" ~0.72, zgjedhur nga Admin Center.
      // FIX (raportuar nga useri — "vetëm 0.72 funksionon, ndoshta lidhet
      // me skemën aktuale") — shumëzuesi VEPRONTE mbi V_SPACING TOTALE
      // (168px bazë), jo mbi HENDEKUN real mes kartelave (V_SPACING -
      // CARD_H). Kur CARD_H ishte i madh (p.sh. admini e kishte zmadhuar
      // te "Skema e kartelës", ose një skemë "foto-lart" me kartelë të
      // gjatë), hendeku bëhej i papërfillshëm për shumëzues të butë (0.85,
      // 0.9…) — vetëm një shumëzues shumë agresiv (0.72) prodhonte
      // MBIVENDOSJE të dukshme (që dukej si "funksionon", por në fakt
      // ishte defekt, jo qëllim). Tani shumëzuesi vepron mbi VETË
      // HENDEKUN (56px = 168-112, hendeku origjinal i skemës vertikale),
      // i shtuar mbi CARD_H AKTUALE — kështu çdo vlerë prodhon GJITHMONË
      // ndryshim proporcional të dukshëm, pavarësisht madhësisë së
      // kartelës aktuale.
      const BASE_ROW_GAP = BASE_V_SPACING - CARD_H_VERTICAL; // 168 - 112 = 56
      // FIX (raportuar nga useri — "me kartelat me kufi funksionon shumë
      // mirë, por me foto pezull ndryshimi është pothuajse i pavërejtshëm")
      // — te stilet "foto pezull" (Elegant-oval, Modern Foto Pezull), s'ka
      // FARE rect/sfond që të mbulojë CARD_H — përmbajtja e VËRTETË
      // vizuale (foto + emër + vit) është ~85px, ndjeshëm më e shkurtër se
      // CARD_H parazgjedhje (112px, ose edhe më e madhe nëse admini e ka
      // zmadhuar te "Skema e kartelës"). Ai "tepricë" (CARD_H minus
      // përmbajtja e vërtetë) është hapësirë E PADUKSHME brenda kutisë —
      // reduktimi i V_SPACING e "gëlltiste" atë tepricë të padukshme PARA
      // se të bëhej fare i dukshëm ndryshimi real mes kartelave. Tani, VETËM
      // për këto stile, gjatësia e përdorur si bazë për hendekun s'është
      // CARD_H (kutia admin-konfigurueshme), por një përafrim i
      // përmbajtjes SË VËRTETË vizuale (~95px) — kështu çdo ndryshim i
      // shumëzuesit prek DREJTPËRDREJT hapësirën që shihet me sy.
      const FLOATING_PHOTO_STYLES = ["elegant", "modernphoto"];
      const rowContentHeight = FLOATING_PHOTO_STYLES.includes(row.card_layout) ? Math.min(CARD_H, 95) : CARD_H;
      V_SPACING = rowContentHeight + BASE_ROW_GAP * (row.generation_spacing || 1);
      ROW_CONTENT_H = rowContentHeight;
      setPhotoRingMode(row.photo_ring_mode || "branch");
      setPhotoRingWidth(row.photo_ring_width == null ? 1 : Number(row.photo_ring_width));
      setPhotoRingGap(row.photo_ring_gap == null ? 2 : Number(row.photo_ring_gap));
      setExpandBtnStyle(row.expand_btn_style || "classic");
      setExpandBtnSize(row.expand_btn_size || 12);
      // FIX (raportuar nga useri — "ndryshimi i largësisë vëllezër/motra
      // ndryshon edhe largësinë e çifteve") — SPOUSE_OFFSET është në
      // "njësi rrjete", dhe gapi i VËRTETË në piksela mes çifteve llogaritet
      // gjithmonë si SPOUSE_OFFSET × H_SPACING (shih computeLayout). Nëse
      // thjesht shumëzohej BASE_SPOUSE_OFFSET me faktorin e çifteve (siç
      // ishte më parë), ndryshimi i H_SPACING (nga çelësi i VËLLEZËRVE/
      // MOTRAVE) e ndryshonte gapin real edhe pa prekur fare çelësin e
      // çifteve — pikërisht defekti i raportuar. Tani synohet një GAP NË
      // PIKSELA i qëndrueshëm (bazuar te H_SPACING BAZË, jo aktuale),
      // dhe SPOUSE_OFFSET rillogaritet mbrapsht që, shumëzuar me H_SPACING
      // AKTUALE, të japë saktësisht atë gap — kështu çelësi i çifteve
      // prodhon GJITHMONË të njëjtin rezultat vizual, pavarësisht vlerës
      // së çelësit të vëllezërve/motrave.
      const targetCouplePixelGap = BASE_SPOUSE_OFFSET * BASE_H_SPACING * (row.couple_spacing || 1);
      SPOUSE_OFFSET = targetCouplePixelGap / H_SPACING;
      setLayoutVersion((v) => v + 1);
  }, []);

  // Ngarko cilësimet një herë kur hapet app-i.
  useEffect(() => {
    treeSettingsMounted.current = true;
    loadTreeDisplaySettings();
    return () => { treeSettingsMounted.current = false; };
  }, [loadTreeDisplaySettings]);
  // SKEMA2: cilësimet e pemës janë PËR DIZAJN — kur dizajni ndërron (publikim
  // te Design Studio, ose familja me dizajn të vetin), rilexohen për të riun.
  useEffect(() => {
    if (designTick > 0) loadTreeDisplaySettings();
  }, [designTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rifresko cilësimet sa herë app-i kthehet në plan të parë. Në Android
  // (Capacitor) procesi rri gjallë në memorie, ndaj pa këtë një ndryshim i
  // bërë në Admin Center s'do të dukej derisa app-i të mbyllej plotësisht dhe
  // të rihapej. `resume` = Capacitor (native), `visibilitychange` = web/PWA.
  // Kjo ndihmon edhe rikuperimin nëse ngarkimi i parë (RPC) dështoi në Android.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") loadTreeDisplaySettings(); };
    document.addEventListener("visibilitychange", onVisible);
    const hiqResume = dëgjoResume(() => { loadTreeDisplaySettings(); });
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      hiqResume();
    };
  }, [loadTreeDisplaySettings]);
  const [roles, setRoles] = useState({});
  // REDAKTOR1 (6 shtator 2026) — shkalla e redaktorit: 1 = fushëveprimi nis
  // nga prindërit (si gjithmonë), 2 = «Redaktor +», nis nga gjyshërit. Roli
  // mbetet `editor` kudo; vetëm fushëveprimi ndryshon.
  const [rolesShkalla, setRolesShkalla] = useState({});
  // NDREQJE, 31 gusht 2026: vlera fillestare ishte "Krasniqi" — trashëgim i kohës
  // kur platforma mbante një familje të vetme. Në çdo çast ku asnjë familje
  // nuk është ngarkuar (llogari e re, e palidhur me asnjë familje; ose gjatë
  // ngarkimit) app-i shfaqte emrin e një familjeje të huaj në krye. Tani nis
  // bosh, dhe titulli bie te një emërtim asnjanës derisa familja të dihet.
  const [treeName, setTreeName] = useState("");
  // FAZA 38 — treeName mban VETËM mbiemrin (p.sh. "Gashi", "Krasniqi"); fjala
  // "Familja"/"Family" gjenerohet automatikisht këtu, kurrë e ruajtur në
  // databazë — kështu admini/platform admini vendos vetëm mbiemrin.
  // SHTESË (kërkesë e userit — "kur dy familje bashkohen me pemë, s'mund të
  // quhet 'Krasniqi Pema familjare'"): në pamjen dy-familjesh titulli bëhet
  // automatikisht kombinimi "Krasniqi & Orlishta" (twoFamName, vendoset te
  // enterTwoFamily, pastrohet te exitTwoFamily), me mundësi mbishkrimi të
  // lirë nga useri (twoFamTitleCustom — fusha "Titulli i posterit" te
  // opsionet e posterit dy-familjesh). Vlen për header-in E POSTERIN.
  const [twoFamName, setTwoFamName] = useState(null);
  const [twoFamTitleCustom, setTwoFamTitleCustom] = useState("");
  const effTreeName = twoFamName ? (twoFamTitleCustom.trim() || twoFamName) : treeName;
  const familyDisplayName = twoFamName
    ? (twoFamTitleCustom.trim() || t("family.label", { surname: twoFamName }))
    // Pa mbiemër s'ka «Familja X» — do të dilte «Familja » me një hapësirë të
    // varur. Emërtimi asnjanës përdoret vetëm në çastet ku familja s'dihet ende.
    : (treeName ? t("family.label", { surname: treeName }) : t("The family"));
  // FAZA 35 — editingTitle u hoq (s'ka më redaktim inline të titullit).
  // PA TEMË TË PARAZGJEDHUR PËR PLATFORMËN — kërkesë e userit, dhe shkaku i
  // vërtetë i "pulsimit të klasikes së kuqe".
  //
  // Kjo gjendje nisej gjithmonë me "albania". Dhe meqë `applyTheme(themeKey)`
  // thirret GJATË render-it, çdo vizatim para se të mbërrinin cilësimet e
  // rishkruante paletën me të kuqen klasike — duke e prishur edhe sinkronizimin
  // që bëhet në ngarkim të modulit. Prandaj arnimet e sipërfaqeve nuk mjaftuan:
  // burimi ishte këtu, dhe xhironte sërish në çdo render.
  //
  // Tani nis nga ajo që ka zgjedhur vetë përdoruesi, e ruajtur në këtë pajisje.
  // Çelësi i fundit i përdorur mbahet edhe pa familje, sepse te render-i i parë
  // familja aktive ende s'dihet. Serveri vjen menjëherë pas dhe e mbishkruan —
  // e para vizaton, e dyta vendos.
  const [themeKey, setThemeKey] = useState(() => {
    try {
      const last = localStorage.getItem("pef.theme.last");
      if (last && THEMES[last]) return last;
    } catch (_e) { /* modaliteti privat */ }
    return "albania";
  });
  const [crestPath, setCrestPath] = useState(null);
  const crestUrl = crestPath ? photoUrl(crestPath) : null;
  // RIKTHIM (19 gusht 2026) — gjatë pastrimit të kodit të vdekur kjo gjendje u
  // hoq gabimisht si "e palexuar". Në të vërtetë lexohet te `BirthPlaceFields`
  // (lista e shteteve te "Vendlindja") dhe shkruhet nga `fetchSettings`. Heqja
  // e saj e ktheu `setCountries` në referencë të pavlefshme; meqë `fetchSettings`
  // xhiron brenda një `Promise.all` në nisje, ai gabim e rrëzonte GJITHË
  // zinxhirin fillestar: pa familje aktive → pa persona → pa role → gjithçka
  // dukej si "viewer", pema bosh, zgjedhësi i familjeve i fshehur.
  const [countries, setCountries] = useState([]);
  const [themePanelOpen, setThemePanelOpen] = useState(false);
  // FIX — `personalizedView` u hoq (motori automatik i farefisnisë tani
  // është GJITHMONË aktiv, jo më opsion — shih relationMap më poshtë).
  const [rolesPanelOpen, setRolesPanelOpen] = useState(false);
  // KOSHI — persona të fshirë (butë) që admini mund t'i rikthejë.
  const [trashOpen, setTrashOpen] = useState(false);
  const [deletedPeople, setDeletedPeople] = useState([]);
  // Sinkronizim profili mes familjeve — modal me zgjedhje fushash.
  const [syncModalFor, setSyncModalFor] = useState(null); // truthy = modali i sinkronizimit i hapur
  const [syncSource, setSyncSource] = useState(null);     // person_id i familjes-BURIM
  const [syncTargets, setSyncTargets] = useState(() => new Set()); // person_ids DESTINACION
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncFields, setSyncFields] = useState({ photo: true, bio: true, birth: true, contact: true, legacy: true });
  const [importOpen, setImportOpen] = useState(false); // modali "Sill familjen"
  const [familiesHubOpen, setFamiliesHubOpen] = useState(false); // hub-i "Familjet"
  // "Sill familjen" i rindërtuar me model dropdown (si sinkronizimi): burim =
  // familja që KA bashkëshortin+fëmijët; destinacione = VETËM ato familje ku
  // ende s'ekziston (preview>0), që të mos ketë dyfishime.
  const [importSource, setImportSource] = useState(null);        // person_id BURIM
  const [importTargets, setImportTargets] = useState(() => new Set()); // person_ids DESTINACION
  const [importBusy, setImportBusy] = useState(false);
  const [importPreview, setImportPreview] = useState({});         // { person_id(dest): count }
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  // Ri-render kur ndryshon mënyra e ikonave (sinkron me dizajnin aktiv).
  const [, setIconTick] = useState(0);
  const [membersSearch, setMembersSearch] = useState("");
  const [membersRoleFilter, setMembersRoleFilter] = useState("all");
  // "Kodi i Familjes" — mekanizmi i tretë i bashkimit (shih schema.sql,
  // seksioni "FAZA — KODI I FAMILJES"). familyJoinCode = { code, expires_at,
  // use_count, created_at } | null. joinCodeClaims = regjistri i bashkimeve
  // të fundit me kod (dyfishon si njoftim admini, shih seen_at).
  const [familyJoinCode, setFamilyJoinCode] = useState(null);
  const [kodetPersonale, setKodetPersonale] = useState({}); // KODI7 — person_id → { expires_at }
  const [rolesMeDore, setRolesMeDore] = useState({});       // ROLI1 — person_id → true kur admini e ka vendosur me dorë
  const [llogaritEAnetareve, setLlogaritEAnetareve] = useState({}); // LLOGARIA1 — person_id → { email_masked, linked_at, last_seen_at }
  const [joinCodeBusy, setJoinCodeBusy] = useState(false);
  const [joinCodeClaims, setJoinCodeClaims] = useState([]);
  const [joinCodeDurationHours, setJoinCodeDurationHours] = useState(720); // parazgjedhje: 1 muaj
  const [logPanelOpen, setLogPanelOpen] = useState(false);
  const [termsGateOpen, setTermsGateOpen] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  // FAZA 14 — "Kontakto Adminin": formë e thjeshtë brenda platformës që
  // krijon rresht te support_tickets (rpc_create_support_ticket, ekziston
  // tashmë në backend që kur u ndërtua moduli i ticketing-ut, thjesht i
  // mungonte kjo formë). Punon edhe pa qenë i loguar (p_person_id/account
  // opsionalë brenda vetë RPC-së).
  // FAZA 14/MODULI 27 — "Qendra e Ndihmës" (ish "Kontakto Adminin"): tani
  // jo më formë e vetme "dërgo e harro" — listë e tiketave të tua, secila
  // me bisedë (thread) të vërtetë, jo "1 mesazh + 1 përgjigje".
  const [contactAdminOpen, setContactAdminOpen] = useState(false);
  const [contactAdminCategory, setContactAdminCategory] = useState("other");
  const [contactAdminMessage, setContactAdminMessage] = useState("");
  const [contactAdminBusy, setContactAdminBusy] = useState(false);
  const [supportView, setSupportView] = useState("list"); // 'list' | 'new' | 'thread' | 'faq'
  // FAQ1 (13 shtator 2026) — pyetjet e shpeshta: rreshtat e bazës së njohurive
  // të asistentit me etiketën «faq» (Admin Center → AI Assistant). null = s'janë
  // lexuar ende. Tekstet kalojnë nga tDyn → editori i përkthimeve.
  const [faq, setFaq] = useState(null);
  const [faqHapur, setFaqHapur] = useState(null);
  const loadFaq = async () => {
    try {
      const { data, error } = await supabase.rpc("rpc_faq_lexo");
      const lista = error ? [] : (data || []);
      setFaq(lista);
      return lista.length;
    } catch (_e) { setFaq([]); return 0; }
  };
  const [myTickets, setMyTickets] = useState([]);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [ticketThread, setTicketThread] = useState(null);
  const [ticketReplyText, setTicketReplyText] = useState("");
  const [ticketReplyBusy, setTicketReplyBusy] = useState(false);
  const [ticketAttUrls, setTicketAttUrls] = useState({}); // path → signed URL
  const [ticketPendingAtts, setTicketPendingAtts] = useState([]); // imazhe të ngarkuara, para dërgimit
  const [ticketThreadError, setTicketThreadError] = useState(null);
  const [ticketDownloadBusy, setTicketDownloadBusy] = useState(false);
  // Mban TIPIN e dokumentit që po lexohet ("info" | "terms" | "privacy" |
  // "tracking" | "us_notice" | "license"), jo thjesht hapur/mbyllur. Më parë
  // ishte boolean dhe hapte gjithmonë vetëm `info` — prandaj katër llojet e
  // tjera ngarkoheshin nga serveri e nuk shfaqeshin kurrë askund.
  const [docModalOpen, setDocModalOpen] = useState(null);
  // Çelësat e dukshmërisë (`platform_document_visibility`) caktohen te Admin
  // Center, por klienti s'i lexonte fare. Pa ta, fshehja e një dokumenti nuk
  // kishte asnjë efekt.
  const [docVisibility, setDocVisibility] = useState({});
  const DOC_LABELS = {
    info: "Full documentation", terms: "Terms of Use", privacy: "Privacy Policy",
    tracking: "Cookies and tracking", us_notice: "Notice for users in the USA", license: "Licence",
  };
  const [docs, setDocs] = useState({ info: null, terms: null });
  // Pa dokument kushtesh nuk ka version për t'u pranuar. Më parë binte te një
  // konstante e ngulitur ("2.0"), pra dikush "pranonte" versionin 2.0 të një
  // dokumenti që s'ekzistonte.
  // Versioni i kushteve te gjuha BURIM (shqip) — identiteti i vërtetë i tyre.
  const [termsSourceVersion, setTermsSourceVersion] = useState(null);
  // PSE JO `docs.terms.version` (31 gusht 2026)
  //
  // Pronari: «te iPhone-i kushtet dalin çdo herë që ndryshon gjuhën».
  //
  // Shkaku: versionet numërohen VEÇ PËR ÇDO GJUHË — shqipja mund të jetë te v3,
  // anglishtja te v2 — kurse pranimi ruan vetëm numrin, pa gjuhë. Pra dikush që
  // pranon shqipen v3 dhe pastaj kalon në anglisht, krahasohet me v2, nuk
  // përputhet, dhe porta hapet sërish. Pranon, kthehet te shqipja, hapet prapë.
  // Një lak pa fund, dhe çdo hap i tij mbishkruan dëshminë e mëparshme.
  //
  // Gabimi ishte konceptual: numri i një përkthimi nuk është identiteti i
  // kushteve. Kushtet janë një dokument; përkthimet janë e njëjta gjë e thënë
  // ndryshe. Prandaj identiteti merret nga BURIMI — shqipja — dhe njeriu që
  // pranoi «v3» ka pranuar v3, në cilëndo gjuhë që e lexoi.
  //
  // `docs.terms` mbetet ai që SHFAQET (gjuha e lexuesit); ndryshon vetëm çfarë
  // numri regjistrohet.
  const currentTermsVersion = termsSourceVersion || (docs.terms ? String(docs.terms.version) : null);
  const [docHistory, setDocHistory] = useState({ info: [], terms: [] });
  const [docHistoryOpenType, setDocHistoryOpenType] = useState(null);
  // DOKUMENTET1 (28 gusht 2026) — dokumentet nuk janë tekst i `i18n`; janë
  // skedarë të ngarkuar. Deri sot kishte vetëm një kopje për çdo lloj, dhe ajo
  // ishte shqip: telefoni në anglisht, aplikacioni në anglisht, dokumenti
  // shqip. Tani çdo rresht ka `lang`, dhe këtu zgjedhet kopja që i takon
  // gjuhës së përdoruesit. Kur përkthimi mungon, shfaqet shqipja — por e thënë
  // hapur në dritare, se një kusht juridik i palexuar nuk pranohet në heshtje.
  const [docFallback, setDocFallback] = useState({});
  // FIX (RISTRUKTURIM — "Rreth Platformës" tani qendrore) — s'lexon më
  // dokumentet e VETË familjes (activeFamilyId), lexon gjithmonë nga
  // familja "sentinel" e platformës (shih schema.sql) — e njëjta përmbajtje
  // për ÇDO familje, e menaxhuar VETËM nga Admin Center tani.
  const PLATFORM_DOCS_FAMILY_ID = "00000000-0000-0000-0000-0000000000ff";
  // Zgjedh gjuhën e një dokumenti: përputhje e plotë (`pt-BR`), pastaj rrënja
  // (`pt`), pastaj shqipja. Rreshtat pa `lang` (para migrimit) janë shqip.
  const zgjidhGjuhenEDokumentit = (rreshtat) => {
    const lista = rreshtat || [];
    if (lista.length === 0) return { rreshtat: [], eRezervuar: false };
    const plote = String(i18n.language || "sq").toLowerCase();
    const rrenja = plote.split("-")[0];
    const gj = (r) => String(r.lang || "sq").toLowerCase();
    const provat = [
      (r) => gj(r) === plote,
      (r) => gj(r).split("-")[0] === rrenja,
      (r) => gj(r) === "sq",
    ];
    for (let i = 0; i < provat.length; i++) {
      const gjetur = lista.filter(provat[i]);
      // I fundit është shqipja: nëse arritëm deri këtu ndërsa përdoruesi
      // kërkonte tjetër gjuhë, kjo është kopje rezervë, jo përkthim.
      if (gjetur.length > 0) return { rreshtat: gjetur, eRezervuar: i === 2 && rrenja !== "sq" };
    }
    return { rreshtat: lista, eRezervuar: rrenja !== "sq" };
  };
  const fetchDocuments = async () => {
    const { data, error } = await supabase.from("documents").select("*").eq("family_id", PLATFORM_DOCS_FAMILY_ID).order("version", { ascending: false });
    if (error || !data) return;
    const latest = { info: null, terms: null, privacy: null, tracking: null, us_notice: null, license: null };
    const history = { info: [], terms: [], privacy: [], tracking: [], us_notice: [], license: [] };
    const rezerva = {};
    const teGjitha = { info: [], terms: [], privacy: [], tracking: [], us_notice: [], license: [] };
    data.forEach((row) => {
      if (teGjitha[row.doc_type]) teGjitha[row.doc_type].push(row);
    });
    Object.keys(teGjitha).forEach((lloj) => {
      const z = zgjidhGjuhenEDokumentit(teGjitha[lloj]);
      // Versionet numërohen për çdo gjuhë veç — pra renditja bëhet brenda
      // gjuhës së zgjedhur, jo mes gjuhëve.
      history[lloj] = z.rreshtat.slice().sort((a, b) => b.version - a.version);
      latest[lloj] = history[lloj][0] || null;
      rezerva[lloj] = z.eRezervuar && !!latest[lloj];
    });
    setDocs(latest);
    setDocHistory(history);
    setDocFallback(rezerva);
    // Numri më i madh i kushteve te gjuha burim. Rreshtat pa `lang` janë nga
    // para migrimit, pra edhe ata janë shqip. Nëse s'ka fare shqip — familje e
    // ngritur vetëm në anglisht — mbetet `null` dhe vlen rezerva më lart.
    const burimi = (teGjitha.terms || [])
      .filter((r) => !r.lang || String(r.lang).toLowerCase() === "sq")
      .map((r) => Number(r.version))
      .filter((n) => Number.isFinite(n));
    setTermsSourceVersion(burimi.length ? String(Math.max(...burimi)) : null);
    // Leximi është publik (RLS e vetë tabelës) — njësoj si te Admin Center.
    try {
      const { data: vis } = await supabase.from("platform_document_visibility").select("*");
      const map = {};
      (vis || []).forEach((r) => { map[r.doc_type] = r.enabled; });
      setDocVisibility(map);
    } catch (e) { /* pa rresht → parazgjedhja poshtë vendos */ }
  };
  const documentUrl = (row) => (row ? supabase.storage.from("documents").getPublicUrl(row.storage_path).data.publicUrl : null);
  // Kur ndërrohet gjuha me app-in hapur, dokumentet duhet të ndërrohen bashkë
  // me të. Rendja e parë kapërcehet — atë e bën ngarkimi normal më lart.
  const gjuhaEDokumenteve = useRef(null);
  useEffect(() => {
    const tani = i18n.language || "sq";
    if (gjuhaEDokumenteve.current === null) { gjuhaEDokumenteve.current = tani; return; }
    if (gjuhaEDokumenteve.current === tani) return;
    gjuhaEDokumenteve.current = tani;
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
  // FIX (RISTRUKTURIM) — uploadDocument() u hoq — "Rreth Platformës" tani
  // menaxhohet VETËM nga Admin Center (shih rpc_platform_upload_document),
  // jo më nga admini i familjes këtu.
  // THJESHTIM — "menuOpen" (menu e veçantë "Më shumë") u hoq; gjithçka
  // tani përdor "avatarMenuOpen" (shih renderAccountMenuItems më poshtë).
  // FAZA 40 — pull-to-refresh: pasi u hoq ikona ⟳, s'kishte më asnjë mënyrë
  // manuale rifreskimi në telefon. Tërheqja poshtë duke filluar nga shiriti
  // lart e zëvendëson — njësoj si sjellja e mëparshme e butonit.
  const [pullDistance, setPullDistance] = useState(0);
  const pullRef = useRef(0);                 // vlera e fundit, pa mbetur e ngecur në closure
  const PULL_THRESHOLD = 64;

  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  // Kërkesë e userit — veprimet "Sinkronizo profilin" / "Sillë familjen" për një
  // familje tjetër mbahen TË FSHEHURA nën një shigjetë poshtë (dropdown), për të
  // kursyer hapësirë te menyja. Mban person_id-në e familjes së hapur (një në një
  // kohë), ose null nëse asnjë.
  const [famActionsOpenFor, setFamActionsOpenFor] = useState(null);
  // FAZA 3 (2FA OPSIONALE) — ndryshe nga Admin Center (2FA E DETYRUESHME për
  // platform admin), këtu është vetëm një OPSION që çdo anëtar mund ta
  // aktivizojë vetë nëse do siguri shtesë — s'gaton kurrë login-in.
  const [securityPanelOpen, setSecurityPanelOpen] = useState(false);
  const [mfaFactors, setMfaFactors] = useState(null); // null = ende s'u ngarkua; [] = s'ka asnjë; [factor] = aktive
  const [mfaEnrollData, setMfaEnrollData] = useState(null); // { factorId, qr, secret } gjatë regjistrimit
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [activityLog, setActivityLog] = useState([]);
  const [noteEditing, setNoteEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [subBasket, setSubBasket] = useState([]); // [{personId, watchType}]
  const [chatOpen, setChatOpen] = useState(false); // Asistenti AI — hapet nga butoni te menyja
  const [fabOpen, setFabOpen] = useState(false);        // butoni pluskues (Ndërro familjen / Asistenti / Posta)
  const [switchFamOpen, setSwitchFamOpen] = useState(false); // paneli i shpejtë "Ndërro familjen"
  const [familyHistoryEvents, setFamilyHistoryEvents] = useState([]);
  const [familyHistoryForm, setFamilyHistoryForm] = useState({ id: null, eventDate: "", title: "", description: "", saktesia: "dite" });
  const [familyHistoryPhotoFiles, setFamilyHistoryPhotoFiles] = useState([]);
  const [familyHistoryPhotoPreviews, setFamilyHistoryPhotoPreviews] = useState([]);
  const [familyHistorySaving, setFamilyHistorySaving] = useState(false);
  // FAZA 24 — "Legacy". `unlockedLegacyMessages` mbahet globalisht (ngarkohet
  // një herë, jo për çdo profil) sepse rpc_list_unlocked_legacy_messages
  // kthen TË GJITHA mesazhet e zbuluara të familjes njëherësh — filtrohet
  // më pas sipas personit të shfaqur, shmang thirrje N+1 për çdo klikim profili.
  const [unlockedLegacyMessages, setUnlockedLegacyMessages] = useState([]);
  const [myLegacyOpen, setMyLegacyOpen] = useState(false);
  const [myLegacyForPersonId, setMyLegacyForPersonId] = useState(null);
  // NIVELI I TRASHËGIMISË (TRASH1/TRASH2). Ekrani duhet ta dijë çfarë ka blerë
  // ky njeri — përndryshe kutia e tekstit është e njëjta si dje dhe ligji te
  // baza mbetet kod i fjetur.
  const [legacyTier, setLegacyTier] = useState(null);
  const [legacyMediaBusy, setLegacyMediaBusy] = useState(false);
  const [myLegacyMessages, setMyLegacyMessages] = useState([]);
  const [legacyForm, setLegacyForm] = useState({ id: null, title: "", messageText: "" });
  const [legacySaving, setLegacySaving] = useState(false);
  // Zbulimi me verifikim: statusi i personit të zgjedhur + modali i kërkesës.
  const [selectedLegacyStatus, setSelectedLegacyStatus] = useState(null);
  const [legacyViewers, setLegacyViewers] = useState([]);
  const [legacyReqOpen, setLegacyReqOpen] = useState(false);
  const [legacyReqNote, setLegacyReqNote] = useState("");
  const [legacyReqFile, setLegacyReqFile] = useState(null);
  const [legacyReqBusy, setLegacyReqBusy] = useState(false);
  // feedOpen ("Ç'ka të re") u hoq — ishte modal i pa-arritshëm (askund
  // s'thirrej setFeedOpen(true)), dhe përmbajtja e tij mbivendosej me
  // skedën e re "Ndryshime" te modali "Njoftimet".
  // FAZA 27 — "Archive". `archiveEnabled` vjen nga rpc_is_archive_enabled —
  // nëse false, artikulli i menusë s'shfaqet FARE (jo gri/i kyçur, siç u
  // kërkua nga admini — "e fshehur nga platforma, vetëm me kërkesë").
  const [archiveEnabled, setArchiveEnabled] = useState(false);
  // BASHKIM (kërkesë e userit) — "Kujtime" (historia me ngjyra) dhe "Archive"
  // (magazina e sheshtë e skedarëve) tani janë NJË ekran i vetëm me 2 skeda,
  // në vend të dy artikujve të veçantë në navigim që dukeshin njësoj për
  // dikë nga jashtë. TË DHËNAT mbeten krejt të pandryshuara (dy tabela të
  // veçanta, dy grupe rpc_*) — bashkohet VETËM paraqitja/navigimi.
  const [memoriesTab, setMemoriesTab] = useState("events"); // "events" | "files"
  const [archiveItems, setArchiveItems] = useState([]);
  const [archiveUploading, setArchiveUploading] = useState(false);
  const [archiveCaptionDraft, setArchiveCaptionDraft] = useState("");
  const [archiveLightbox, setArchiveLightbox] = useState(null);
  // BALLINA6 / ARKIVA2 — Historia është një lumë me filtra, jo skeda.
  //
  // Një filtër dhe një skedë duken njësoj, por bëjnë të kundërtën: skeda të çon
  // diku tjetër dhe të humb vendin ku ishe; filtri e ngushton atë që sheh dhe
  // të lë aty. Historia e ka gjithmonë vetëm një pyetje — «çfarë ka ndodhur» —
  // prandaj një ekran, gjashtë filtra.
  const [historiaFiltri, setHistoriaFiltri] = useState("gjitha"); // gjitha|ngjarje|foto|zera|dokumente|person
  const [historiaPersoni, setHistoriaPersoni] = useState(null);   // id kur filtri = "person"
  // HAPESIRA5 · 3 shtator 2026 — KOSHI I SKEDARËVE.
  //
  // `rpc_delete_archive_item` shënonte `deleted_at` që nga fillimi, po asgjë —
  // as te aplikacioni, as te Admin Center — nuk i tregonte ata rreshta dhe as
  // s'i kthente. Pra «fshirje e butë» ishte e butë vetëm në emër.
  //
  // Tani hapësira lirohet sapo diçka shkon te koshi, dhe kthimi është vendi ku
  // ajo hapësirë ripaguhet: nëse ndërkohë vendi u zu, kthimi refuzohet me fjali,
  // jo me heshtje.
  const [koshiOpen, setKoshiOpen] = useState(false);
  const [koshiItems, setKoshiItems] = useState(null);   // null = ende s'e dimë
  const [koshiBusy, setKoshiBusy] = useState(null);     // id-ja që po kthehet
  const ngarkoKoshin = async () => {
    if (!currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_list_archive_trash", { p_requester_id: currentPersonId });
    setKoshiItems(error ? [] : (data || []));
  };
  const ktheNgaKoshi = async (id) => {
    setKoshiBusy(id);
    const { error } = await supabase.rpc("rpc_restore_archive_item", { p_id: id, p_requester_id: currentPersonId });
    setKoshiBusy(null);
    if (error) {
      const m = error.message || "";
      // Dy dështime të pritshme, dy fjali. Çdo gjë tjetër shkon ashtu siç vjen.
      if (m.includes("hapesira_plot")) showToast(t("space.restoreNoRoom"));
      else if (m.includes("skedari_mungon")) showToast(t("space.restoreGone"));
      else showToast(t("toast.failed", { error: m }));
      return;
    }
    showToast(t("space.restored"));
    await ngarkoKoshin();
    await fetchArchiveItems();
  };
  const [historiaPersoniHapur, setHistoriaPersoniHapur] = useState(false);
  // Kush etiketohet te një skedar. `null` = asnjë kuti e hapur.
  const [etiketaPer, setEtiketaPer] = useState(null);
  const [etiketaZgjedhur, setEtiketaZgjedhur] = useState([]);
  // {sa, bajte, limit_mb, max_file_mb, pa_limit} + (HAPESIRA1) arkiv/platformë/
  // total, mënyra dhe kufijtë — nga `rpc_arkiva_gjendja`.
  const [arkivaGjendja, setArkivaGjendja] = useState(null);
  // FAZA 28 — "Navigimi + Home". S'ka pasur më parë fare koncept ekrani
  // kryesor të ndryshueshëm (gjithçka ishte Pema + modale mbi të). "home" =
  // Feed-i (ky bëhet ekrani i parë që sheh dikush, jo Pema — pikërisht
  // ndryshimi themelor i diskutuar: "pse do ta hapte sot app-in"). "tree"
  // = kanavacë ekzistuese e pemës, e paprekur, thjesht tani e arritshme
  // përmes tab-it "Pema", jo automatikisht default.
  const [activeScreen, setActiveScreen] = useState(() => {
    try { return sessionStorage.getItem("family_active_screen") || "home"; } catch { return "home"; }
  });

  // ─── «PAMJA E APLIKACIONIT» (GUASKA1) — E HEQUR ─────────────────────────
  //
  // 29 gusht 2026. Pronari: «këtë tani dua ta heq komplet — nuk do përdoret as
  // nga Admin Center, as nga klienti».
  //
  // Ishin pesë shabllone të ngulitura (classic / hub / pese / intim / koncept),
  // të shkruara më 26 gusht për t'i krahasuar variantet krah për krah. Dy ditë
  // më vonë BALLINA1 solli listën e lirë: te Admin Center zgjidhet HYRJE PËR
  // HYRJE cili shirit do, në ç'radhë. Që nga ai çast shabllonet nuk shtonin
  // asgjë — ishin pesë kombinime të ngrira të një mekanizmi që i bën të gjitha.
  //
  // Dy emra për një gjë, dhe kjo është e treta herë që na kushton: e mbanim një
  // zgjedhës te Pamja e klientit, një kartelë te Admin Center, një kolonë, dy
  // RPC dhe një `localStorage` — për një sjellje që tashmë e vendos një tjetër.
  //
  // Nuk humbet asnjë veçori: shiriti vendoset te Admin Center → Shiriti dhe
  // ballina. Pa cilësim, `hyrjetEShiritit()` kthen `null` dhe mbetet blloku
  // klasik i vjetër — pikërisht ai që ishte «classic».
  //
  // Çelësi `pef_app_shell` mund të ketë mbetur te disa pajisje. Nuk e lexon më
  // askush; fshihet një herë, që të mos rrijë si mbetje pa pronar.
  useEffect(() => {
    try { localStorage.removeItem("pef_app_shell"); } catch (_e) { /* modalitet privat */ }
  }, []);

  // HAPËSIRA LART/POSHTË vendoset VETËM te Admin Center → Design Studio →
  // App header (tokens.header.padTop / padBottom).
  //
  // Këtu pati për pak kohë edhe një çift butonash + / − brenda «Pamja», për ta
  // provuar shpejt. U hoqën me vendim të pronarit, dhe vendimi është i drejtë:
  // sa më shumë çelësa t'i japësh familjes, aq më shumë gjëra ka ajo për të
  // prishur dhe ne për të mbështetur. Pamja e aplikacionit vendoset një herë,
  // nga një vend, për të gjithë.

  // KARTELA1 — SIPËRFAQJA E KARTELAVE TË POSTIMIT.
  //
  // Po ai rregull si te statistikat, sepse është po ajo lloj gjëje: platforma
  // cakton parazgjedhjen, pajisja mban zgjedhjen e vet. Kërkesë e pronarit:
  // «të mund të zgjedh mes të bardhës dhe të kuqes si te Oda, në Admin Center
  // apo edhe tek Pamja si klient».
  //
  // (Rregulli i 28 gushtit — «nuk dua të jenë te Pamja këto» — vlen për çelësat
  // që PRISHIN diçka nëse ngatërrohen. Kjo është ngjyra e një kartele: e keqja
  // më e madhe që mund të bëjë dikush është ta kthejë prapa.)
  //
  // Parazgjedhja është `bardhe`. Një ndryshim pamjeje që ndodh vetvetiu me një
  // deploy nuk është veçori; është befasi.
  const [kartelaPlatform, setKartelaPlatform] = useState("bardhe");
  const [kartelaImja, setKartelaImja] = useState(() => {
    try { return localStorage.getItem("pef_kartela") || null; } catch { return null; }
  });
  const kartelaStili = kartelaImja || kartelaPlatform;
  const zgjidhKartelen = useCallback((v) => {
    setKartelaImja(v);
    try { if (v) localStorage.setItem("pef_kartela", v); else localStorage.removeItem("pef_kartela"); }
    catch (_e) { /* modalitet privat — zgjedhja vlen vetëm për këtë seancë */ }
  }, []);
  useEffect(() => {
    let gjallë = true;
    supabase.rpc("rpc_get_card_style").then(({ data, error }) => {
      // Pa derë (KARTELA1 e paxhiruar) ose pa rrjet → `bardhe`.
      if (!gjallë || error || !data) return;
      setKartelaPlatform(String(data));
    });
    return () => { gjallë = false; };
  }, []);
  // Paleta e kartelave: e njëjta si `COLORS`, me katër çelësa të rishkruar.
  // Ekranet e postimit (Ballina, Historia, Statistikat, Shërbimet) e përdorin
  // KËTË në vend të `COLORS` — dhe pikërisht sepse çdo çelës tjetër mbetet i
  // pandryshuar, zëvendësimi është i sigurt fjalë për fjalë.
  //
  // PA `useMemo` ME QËLLIM. `COLORS` është objekt modular që Design Studio e
  // NDRYSHON NË VEND (`Object.assign`) — pra referenca s'ndërron kurrë, dhe një
  // memo mbi të do të mbante paletën e vjetër pas çdo ndryshimi dizajni. Një
  // shpërndarje prej tridhjetë çelësash për render nuk kushton sa ai gabim.
  const KART = kartelaPalete(COLORS, kartelaStili);

  // IKONAT1 — çelësi Emoji/Lucide i platformës. Lexohet një herë në hyrje;
  // `setIkonatP` ekziston vetëm që pema të rivizatohet pasi vlera arrin.
  const [, setIkonatP] = useState(IKONAT_PERMBAJTJE);
  useEffect(() => {
    let gjallë = true;
    supabase.rpc("rpc_get_ikonat_e_permbajtjes").then(({ data, error }) => {
      // Pa derë (IKONAT1 e paxhiruar) → 'emoji', siç ishin. Asnjë ndryshim nga gabimi.
      if (!gjallë || error || (data !== "emoji" && data !== "lucide")) return;
      IKONAT_PERMBAJTJE = data;
      setIkonatP(data);
    });
    return () => { gjallë = false; };
  }, []);

  // BALLINA1 (28 gusht 2026) — shiriti poshtë dhe skedat e ballinës vijnë nga
  // cilësimi, jo nga listat e ngulitura. Deri sot, për të provuar një radhë të
  // re duhej kod i ri dhe një deploy; tani zgjidhen te Admin Center.
  //
  // `null` do të thotë «askush s'ka vendosur ende» — dhe atëherë sillemi
  // saktësisht si më parë. Pa këtë dallim, çdo gabim rrjeti do të kthehej në
  // «u zhduk shiriti».
  const [navEntries, setNavEntries] = useState(null);
  const [homeTabsCfg, setHomeTabsCfg] = useState(null);
  // BALLINA2 — ku jeton asistenti: "pluskues" | "meny" | "te_dyja".
  //
  // U desh sepse butoni pluskues ishte `hidden lg:block` — vetëm kompjuter — dhe
  // në telefon e vetmja derë e tij ishte hamburgeri i shiritit KLASIK. Sapo
  // shiriti u ndërrua, asistenti mbeti pa asnjë hyrje në telefon.
  const [chatLauncher, setChatLauncher] = useState("pluskues");
  // BALLINA3 — sjellja e butonit pluskues: fiks | terhiqes | fshihet | buze | mini.
  // Vetëm pamje; të pesta hapin të njëjtën bisedë.
  const [chatFabStyle, setChatFabStyle] = useState("fiks");
  // BALLINA4 — pilula «＋ Shto» te ballina, dhe renditja e skedave.
  // KOKA1 — lartësia e matur e kolonës së majtë të kokës; kufiri i fotos.
  const kokaMajtasRef = useRef(null);
  const [kokaKufi, setKokaKufi] = useState(0);
  const [homeAddButton, setHomeAddButton] = useState(true);
  const [homeTabsStyle, setHomeTabsStyle] = useState("pilula");
  // BALLINA5 — pamja e hyrjes «Shto» te shiriti: njesoj | unaze | ngritur.
  const [navAddStyle, setNavAddStyle] = useState("njesoj");
  useEffect(() => {
    let gjallë = true;
    supabase.rpc("rpc_get_nav_layout").then(({ data, error }) => {
      if (!gjallë || error || !data) return;
      // BALLINA5 — `rpc_get_nav_layout` kthen tani një objekt `jsonb`. Forma e
      // vjetër (rresht tabele) mbetet e mbuluar, që një bazë e pa-përditësuar
      // të mos e lërë shiritin pa cilësim.
      const rreshti = Array.isArray(data) ? data[0] : data;
      if (!rreshti) return;
      const list = (v) => (Array.isArray(v) && v.length ? v.map(String) : null);
      setNavEntries(list(rreshti.nav_entries));
      setHomeTabsCfg(list(rreshti.home_tabs));
      // BALLINA2 — pa këtë kolonë (SQL i pa xhiruar) mbetet parazgjedhja
      // «pluskues», pra asistenti ka gjithmonë të paktën një derë.
      if (rreshti.chat_launcher) setChatLauncher(String(rreshti.chat_launcher));
      if (rreshti.chat_fab_style) setChatFabStyle(String(rreshti.chat_fab_style));
      if (typeof rreshti.home_add_button === "boolean") setHomeAddButton(rreshti.home_add_button);
      if (rreshti.home_tabs_style) setHomeTabsStyle(String(rreshti.home_tabs_style));
      if (rreshti.nav_add_style) setNavAddStyle(String(rreshti.nav_add_style));
    });
    return () => { gjallë = false; };
  }, []);

  // Tërheq-për-rifreskim KUDO, jo vetëm nga shiriti i sipërm.
  //
  // Tri kushte, të gjitha të domosdoshme, përndryshe gjesti do të hante
  // lëvizjet normale:
  //   1. te pema, VETËM nga shiriti i sipërm — mbi kanavacë tërheqja poshtë do
  //      të thotë zhvendosje e pemës, dhe një gjest s'mund të ketë dy kuptime;
  //   2. zona që rrëshqet duhet të jetë TASHMË në krye (scrollTop 0);
  //   3. lëvizja duhet të jetë kryesisht vertikale — përndryshe do të prishte
  //      rrëshqitjet anash (galeri, tabela të gjera).
  //
  // Kushti i parë ishte më parë "jo te pema fare", dhe kjo e hiqte rifreskimin
  // edhe atje ku s'kishte asnjë konflikt: shiriti i sipërm nuk zhvendos gjë.
  // Kush tërheq nga koka pret rifreskim, te pema si kudo tjetër.
  useEffect(() => {
    let startY = null, startX = null, pulling = false;

    // Gjen zonën që vërtet rrëshqet nën gishtin, dhe thotë a është në krye.
    const atTop = (el) => {
      let n = el;
      while (n && n !== document.body && n.nodeType === 1) {
        const st = window.getComputedStyle(n);
        if (/(auto|scroll)/.test(st.overflowY) && n.scrollHeight > n.clientHeight + 1) {
          return n.scrollTop <= 0;
        }
        n = n.parentElement;
      }
      const doc = document.scrollingElement || document.documentElement;
      return (doc.scrollTop || 0) <= 0;
    };

    const onStart = (e) => {
      if (!e.touches || e.touches.length !== 1) { startY = null; return; }
      // Te pema gjesti nis vetëm nga shiriti i sipërm — kudo tjetër mbi
      // kanavacë ai i takon zhvendosjes së pemës.
      if (activeScreen === "tree") {
        const koka = headerBarRef.current;
        if (!koka || !e.target || !koka.contains(e.target)) { startY = null; return; }
      }
      if (!atTop(e.target)) { startY = null; return; }
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      pulling = false;
    };
    const onMove = (e) => {
      if (startY == null || !e.touches || e.touches.length !== 1) return;
      const dy = e.touches[0].clientY - startY;
      const dx = e.touches[0].clientX - startX;
      if (Math.abs(dx) > Math.abs(dy)) { startY = null; return; }   // lëvizje anash
      if (dy > 8) {
        pulling = true;
        const d = Math.min(dy, 110);
        pullRef.current = d;
        setPullDistance(d);
        if (e.cancelable) e.preventDefault();   // ndal kërcimin elastik të faqes
      } else if (dy < -4) {
        startY = null;
        pullRef.current = 0;
        setPullDistance(0);
      }
    };
    const onEnd = () => {
      if (pulling && pullRef.current > PULL_THRESHOLD) window.location.reload();
      startY = null; pulling = false;
      pullRef.current = 0;
      setPullDistance(0);
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [activeScreen]);
  useEffect(() => {
    // 6 shtator 2026 — pronari: «kur bëj refresh te Shto, më kthen te pema —
    // sikur Shto të mos jetë faqe në vetvete». Tani ruhet edhe «shto», bashkë
    // me faqen nga erdhe, që ← pas rifreskimit të të çojë prapa aty.
    try {
      sessionStorage.setItem("family_active_screen", activeScreen);
      if (activeScreen === "shto" && ekraniPara.current) sessionStorage.setItem("family_screen_before_shto", ekraniPara.current);
    } catch {}
  }, [activeScreen]);
  const [homeTab, setHomeTab] = useState("feed");
  // SESHPEJTI1 — ÇFARË DUA TË SHOH TE «SË SHPEJTI».
  //
  // Kërkesë e pronarit: kutiza, që secili të zgjedhë vetë. Ruhet te pajisja —
  // është pamje, jo e dhënë e familjes, dhe një rrugëtim te serveri për katër
  // kutiza do të ishte tabelë e re për një preferencë.
  const [seShpejtiFiltrat, setSeShpejtiFiltrat] = useState(() => {
    try {
      const raw = localStorage.getItem("pef_seshpejti");
      if (raw) {
        const v = JSON.parse(raw);
        if (v && typeof v === "object") return v;
      }
    } catch (_e) { /* modaliteti privat — parazgjedhjet */ }
    return { birthday: true, wedding: true, anniversary: true, important: true };
  });
  // Filtrat rrinë të MBLEDHUR. Katër kutiza gjithmonë të hapura mbi një listë
  // prej tre rreshtash janë më shumë ndërfaqe se përmbajtje — dhe pjesa e
  // sipërme e ekranit i takon asaj që vjen, jo mjeteve për ta parë.
  const [filtratEHapur, setFiltratEHapur] = useState(false);
  const ndryshoFiltrinESeShpejtit = (celesi) => {
    setSeShpejtiFiltrat((v) => {
      const i_ri = { ...v, [celesi]: !v[celesi] };
      // Të gjitha të fikura do të ishte një faqe bosh pa shkak të dukshëm —
      // dhe kush e fik të fundit nuk e kupton se e fiku vetë. Prandaj e fundit
      // nuk fiket: kthehet e ndezur.
      if (!Object.values(i_ri).some(Boolean)) return v;
      try { localStorage.setItem("pef_seshpejti", JSON.stringify(i_ri)); } catch (_e) { /* privat */ }
      return i_ri;
    });
  };
  // FAZA 34 — rolePasswordDrafts u hoq bashkë me fjalëkalimin shtesë të adminit.
  const [blockReasonDrafts, setBlockReasonDrafts] = useState({});
  applyTheme(themeKey);

  const [currentPersonId, setCurrentPersonId] = useState(null);
  // Media Access Policy (kush mund të SHKARKOJË një profile photo) — lexohet
  // nga rpc_get_media_policy; e konfigurueshme nga Admin Center. Parazgjedhjet
  // pasqyrojnë vendimin: vetë personi + prindër + partner + fëmijë.
  const [mediaPolicy, setMediaPolicy] = useState({
    allow_self: true, allow_parents: true, allow_partners: true,
    allow_children: true, allow_grandparents: false, allow_siblings: false,
  });
  // Faza 6 — Feed: rregullat (key→enabled; null = pa ngarkuar → të gjitha aktive)
  // dhe reagimet (event_id → [{emoji, reactor_account_id}]).
  const [feedRules, setFeedRules] = useState(null);
  const [feedReactions, setFeedReactions] = useState({});
  const [feedSettings, setFeedSettings] = useState({ retention_days: 7 }); // sa ditë rrinë artikujt para se të fshihen
  const [showOlderFeed, setShowOlderFeed] = useState(false);
  const [feedPosts, setFeedPosts] = useState([]); // Faza 6 — postimet e aprovuara (branch_joined)
  // FEED1 — pastrimi i rrjedhës nga admini. Prek VETËM postimet e ruajtura
  // (`feed_posts`): dega e re, martesa, anëtari i ri. Lindjet dhe vdekjet
  // llogariten nga `people` në çast — s'janë rreshta, ndaj s'ka çfarë fshihet.
  const [feedToolsOpen, setFeedToolsOpen] = useState(false); // veglat rrinë të fshehura pas titullit
  const [feedSelectMode, setFeedSelectMode] = useState(false);
  const [feedSelect, setFeedSelect] = useState(() => new Set());
  const [feedHidden, setFeedHidden] = useState(null); // null = dritarja e mbyllur
  const [feedWisdom, setFeedWisdom] = useState([]); // "Fjala e ditës" — anekdotë/fjalë e urtë/barcoletë
  // ODA1 (29 gusht 2026, kërkesë e pronarit) — «tani që e kemi faqen e dedikuar
  // për Oda, s'ka pse të jenë të mbyllura; duhet të jenë të hapura by default».
  //
  // Kishte të drejtë, dhe arsyeja pse ishin të mbyllura vlente vetëm në vendin e
  // vjetër: te feed-i ato tri karta janë mysafire mes ditëlindjeve, kujtimeve dhe
  // lajmeve, prandaj rrinin të mbledhura që të mos e zinin faqen. Te Oda ato NUK
  // janë mysafire — ato janë faqja. Një faqe që hapet me tri kuti të mbyllura i
  // kërkon njeriut tri prekje për të parë atë që erdhi të shohë.
  //
  // Prandaj gjendja nuk mban më «cilat janë hapur», por «cilat i preku njeriu».
  // Parazgjedhja e vendos vendi: te Oda hapur, te feed-i mbyllur. Kështu një
  // klikim do të thotë gjithmonë «e kundërta e asaj që pashë», pa u dashur dy
  // grupe gjendjeje për të njëjtat tri karta.
  const [urtesiaEPrekur, setUrtesiaEPrekur] = useState(() => new Set());
  const toggleWisdom = useCallback((key) => setUrtesiaEPrekur((prev) => {
    const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n;
  }), []);
  const [importantDates, setImportantDates] = useState([]); // Faza 9 — data të rëndësishme të familjes
  // Përvjetorët e martesës. Rregullat (lidhje aktive, të dy gjallë, çifti s'e ka
  // fikur) zbatohen te serveri — kështu një build i vjetër i klientit s'mund të
  // nxjerrë një përvjetor që s'duhej dalë.
  const [marriageAnnivs, setMarriageAnnivs] = useState([]);
  // Data e martesës për çdo çift, e çelësuar `${idMëIVogël}|${idMëIMadh}` — po
  // ai rend si te tabela `marriages`, ku çifti ruhet një herë të vetme
  // (`person_a < person_b`). Prandaj data futet te njëri profil dhe duket te të
  // dy: s'ka dy rreshta që mund të dalin nga sinkronizimi.
  const [marriages, setMarriages] = useState({});
  const [marriageSaving, setMarriageSaving] = useState(null);
  // ASISTENTI — a është i ndezur nga admini i platformës. Butonat e tij
  // shfaqeshin gjithmonë; nëse asistenti ishte i fikur, `SupportChat` kthente
  // null dhe klikimi s'bënte ASGJË, pa asnjë shpjegim. Tani pyetet i njëjti
  // konfigurim publik që lexon vetë SupportChat, dhe butonat fshihen fare.
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc("rpc_chatbot_public_config").then(({ data }) => {
      if (cancelled) return;
      const cfg = Array.isArray(data) ? data[0] : data;
      setAssistantEnabled(!!(cfg && cfg.enabled));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Mesazhet e header-it (nën emrin e familjes) — të konfigurueshme nga Admin
  // Center (global ose per-familje). Aktivizo 1 → statik; disa → rrotullohen.
  const [headerMessages, setHeaderMessages] = useState([]);
  const [headerMsgIdx, setHeaderMsgIdx] = useState(0);
  const [signedTick, setSignedTick] = useState(0); // #1: re-render kur signed URLs bëhen gati
  // SHTESË (kërkesë e userit) — lista personale e njerëzve që VETË ky
  // përdorues ka zgjedhur të mos i shohë në pemën e vet (tipik: ish-
  // bashkëshort/e). Krejt personale — s'prek fare çfarë shohin të tjerët.
  const [myHiddenPeople, setMyHiddenPeople] = useState(() => new Set());
  useEffect(() => {
    if (!currentPersonId) { setMyHiddenPeople(new Set()); return; }
    let cancelled = false;
    supabase.rpc("rpc_get_my_hidden_people", { p_requester_id: currentPersonId }).then(({ data }) => {
      if (cancelled || !data) return;
      setMyHiddenPeople(new Set(data.map((r) => r.hidden_person_id)));
    });
    return () => { cancelled = true; };
  }, [currentPersonId]);
  // FIX (kërkesë e userit) — "Fshihe për mua" u kufizua rreptësisht:
  // VETËM (a) dy partnerë të divorcuar mund ta fshehin njëri-tjetrin, ose
  // (b) bashkëshorti/ja i/e mbetur gjallë (dhe/ose bashkëshorti/ja e RE e
  // tij/saj) mund ta fshehë ish-in e ndjerë. ASNJË person tjetër (fëmijë,
  // nipër, vëllezër, etj.) s'e ka këtë opsion fare — më parë ("çdokush
  // fsheh kë të dojë") ishte shumë e gjerë, prishte integritetin e pemës.
  const canHideForMe = (viewerId, targetId) => {
    if (!viewerId || !targetId || viewerId === targetId) return false;
    const viewer = people[viewerId], target = people[targetId];
    if (!viewer || !target) return false;
    // (a) të divorcuar nga njëri-tjetri
    if ((viewer.divorced_from_ids || []).includes(targetId)) return true;
    // (b) ish i ndjerë — kërkon që target të jetë i/e ndjerë
    if (!target.death_date) return false;
    // (b1) vetë viewer-i ishte bashkëshort/e i/e target-it (jo i divorcuar)
    if ((viewer.spouse_ids || []).includes(targetId) && !(viewer.divorced_from_ids || []).includes(targetId)) return true;
    // (b2) viewer-i është bashkëshorti/ja i/e RE i dikujt që ishte i/e
    // martuar (jo i divorcuar) me target-in e ndjerë
    return (target.spouse_ids || []).some((otherId) => {
      if (otherId === viewerId) return false;
      const other = people[otherId];
      if (!other) return false;
      return (other.spouse_ids || []).includes(viewerId) && !(other.divorced_from_ids || []).includes(viewerId);
    });
  };
  const [authOpen, setAuthOpen] = useState(false);
  const [authForm, setAuthForm] = useState({ firstName: "", lastName: "", birthDate: "", parentFirstName: "", email: "", password: "" });
  // FAZA 34 — pendingAdminId (hapi i fjalëkalimit shtesë) u hoq krejtësisht.
  const [authError, setAuthError] = useState("");
  // FAZA 3 — identifikimi real (shih PHASE3_REAL_LOGIN.md). `authSession`
  // është sesioni i Supabase Auth (jo më emër+datëlindje); `linkedPersonChecked`
  // bëhet true sapo të kemi provuar t'i gjejmë personin e lidhur (suksesshëm
  // ose jo) — përdoret për të mos treguar "s'je lidhur" për një çast para se
  // kontrolli të mbarojë. `notLinkedYet` do të thotë: ka sesion Auth të
  // vlefshëm, por asnjë person i kësaj peme s'është lidhur ende me të.
  const [authSession, setAuthSession] = useState(null);
  const [authSessionLoading, setAuthSessionLoading] = useState(true);
  // TAVANI I NISJES. Sfondi i qetë më poshtë pret derisa të dihet a ka sesion
  // dhe cili person është. Por një nga ata sinjale mund të mos mbërrijë kurrë —
  // p.sh. një llogari pa familje aktive s'e nis fare kontrollin e lidhjes — dhe
  // atëherë pritja do të bëhej ekran bosh i përhershëm. Më mirë një pulsim i
  // vetëm pas dy sekondash e gjysmë sesa një derë e mbyllur.
  // A e dimë tashmë temën e këtij personi? Derisa ta dimë, nuk vizatohet asnjë
  // sipërfaqe e temuar — sepse çdo gjë që vizatohet para saj është me detyrim
  // një hamendje, dhe hamendja e kodit ishte klasikja e kuqe.
  //
  // Fara te `localStorage` nuk mjafton në telefon: Android-i e pastron atë
  // ruajtje periodikisht (arsyeja pse sesioni jeton te `@capacitor/preferences`),
  // pra pas çdo pastrimi hapja e parë do të pulsonte sërish. Pritja e shkurtër
  // është e vetmja garanci.
  const [themeReady, setThemeReady] = useState(false);
  const [bootGraceOver, setBootGraceOver] = useState(false);
  useEffect(() => {
    // Tavani ra nga 2.5s te 1.5s sepse pritja tani mbulon vetëm leximin e
    // sesionit nga ruajtja vendore. Nëse as ai s'përgjigjet brenda një sekonde
    // e gjysmë, diçka nuk shkon, dhe një ekran i mbyllur është më keq se një
    // vendim i marrë me atë që dihet.
    const id = setTimeout(() => setBootGraceOver(true), 1500);
    return () => clearTimeout(id);
  }, []);
  // FIX (raportuar nga përdoruesi): "U identifikove si..." shfaqej te ÇDO
  // rifreskim faqeje, jo vetëm te login-i i vërtetë — sepse completeLogin()
  // thirret edhe kur sesioni ekzistues thjesht rikonfirmohet (restore).
  // Ky ref mbetet `true` VETËM kur Supabase raporton një event 'SIGNED_IN' të
  // vërtetë (submit i formës ose kthimi nga OAuth), jo për 'INITIAL_SESSION'/
  // 'TOKEN_REFRESHED' të restaurimit të heshtur të sesionit në rifreskim.
  const justSignedInRef = useRef(false);
  const [linkedPersonChecked, setLinkedPersonChecked] = useState(false);
  // NDRYSHE NGA `linkedPersonChecked`, i cili ngrihet në MES të zgjidhjes (është
  // roje kundër ri-hyrjes te efekti), ky ngrihet vetëm kur dihet përfundimisht
  // se kush je — ose u gjet personi, ose u vërtetua se s'ka. Mes të dyve ka disa
  // thirrje rrjeti, dhe pikërisht në atë hendek ekrani i hyrjes pulsonte mbi pemë.
  const [personResolved, setPersonResolved] = useState(false);
  const [notLinkedYet, setNotLinkedYet] = useState(false);
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  // FAZA 14 — Magic Link: hyrje pa fjalëkalim, e dobishme veçanërisht për
  // persona jo-teknikë (s'ka fjalëkalim për t'u mbajtur mend/humbur).
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkBusy, setMagicLinkBusy] = useState(false);
  // FAZA 14 — buton "S'mund të hyj?": krijon kërkesë të dukshme te paneli i
  // platformës (rpc_request_password_help), që admini ta shohë dhe të
  // vendosë fjalëkalim të ri, pa e detyruar personin të bëjë vetë asgjë
  // teknike përveç klikimit të një butoni.
  const [helpRequestSent, setHelpRequestSent] = useState(false);
  const [helpRequestBusy, setHelpRequestBusy] = useState(false);
  // FAZA 4 — shih shënimin te useEffect-i i mount-it: null = ende s'dihet,
  // true/false pasi rpc_any_people_exist të përgjigjet.
  const [treeHasAnyPeople, setTreeHasAnyPeople] = useState(null);
  // FAZA 5/6 — kur App.jsx hapet te /f/{slug}, zgjidh familjen PARA
  // identifikimit (rpc_resolve_family_by_slug, publike, jo e dhënë personi).
  // `slugFamilyStatus`: "n/a" (s'ka familySlug fare — instanca parazgjedhje,
  // p.sh. deploy-i i sotëm i Krasniqit te "/"), "loading", "found", "not_found".
  // `activeFamilyId` (FAZA 6): family_id i përdorur për të lexuar `settings`
  // e SAKTË (jo më id=1 global) — Krasniqi (konstante fikse) kur s'ka slug,
  // ose id-ja e zgjidhur nga slug-u. Zgjidhet te useEffect-i kryesor i
  // mount-it më poshtë, PARA se fetchSettings() të thirret, në mënyrë që të
  // mos ketë race condition mes këtij dhe fetch-eve fillestare.
  const [slugFamilyStatus, setSlugFamilyStatus] = useState(familySlug ? "loading" : "n/a");
  const [slugFamilyName, setSlugFamilyName] = useState(null);
  // FAZA 37 (rishikuar) — emri i familjes/slug editohen nga Admin Center
  // (platform admin), JO nga admini i vetë familjes — slug është shumë i
  // rrezikshëm për t'u lënë në duart e admin-it të familjes (thyen linqe).
  const [activeFamilyId, setActiveFamilyId] = useState(null);
  // HAPESIRA · 3 shtator 2026 — gjendja lexohet me familjen, jo me arkivin.
  //
  // Deri sot ky numër vinte VETËM bashkë me listën e skedarëve të arkivit, pra
  // ekzistonte vetëm nëse dikush kishte hapur atë ekran. Tani hapësira shfaqet
  // te Shërbimet dhe paralajmëron kudo kur afrohet kufiri — pra duhet ditur
  // që në nisje, pa pritur askënd të klikojë diku.
  useEffect(() => {
    if (!activeFamilyId) { setArkivaGjendja(null); return; }
    let gjalle = true;
    supabase.rpc("rpc_arkiva_gjendja", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (!gjalle) return;
      setArkivaGjendja(error ? null : (data && typeof data === "object" && "bajte" in data ? data : null));
    }).catch(() => {});
    return () => { gjalle = false; };
  }, [activeFamilyId]);

  // STATISTIKA3 — cilësimet e kartelave (tituj, dukshmëri, radhë) nga Admin
  // Center, të bashkuara në server për këtë familje. Pa derë (STATISTIKA2 e
  // paxhiruar) → `null`, dhe ekrani përdor titujt e vet, të gjitha të dukshme.
  const [statistikaCilesimet, setStatistikaCilesimet] = useState(null);
  useEffect(() => {
    let gjallë = true;
    if (!activeFamilyId) { setStatistikaCilesimet(null); return; }
    supabase.rpc("rpc_statistika_cilesimet", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (!gjallë || error || !Array.isArray(data)) return;
      const m = {};
      data.forEach((r) => { m[r.key] = r; });
      setStatistikaCilesimet(m);
    });
    return () => { gjallë = false; };
  }, [activeFamilyId]);

  // ─── PERKTHIMI1 — POSTIMET E FAMILJES, TË LEXUARA NË GJUHË TJETËR ────────
  //
  // 29 gusht 2026. Kërkesa e pronarit, fjalë për fjalë:
  //
  //   «tek feeds apo historia shkruhet historia shqip. por kam një familjar në
  //    Kanada që nuk e flet më shqipen, dhe nëse ai e ka gjuhën anglisht in app
  //    teksti duhet të përkthehet.»
  //
  // KUR NUK NDODH ASGJË. Nëse gjuha e lexuesit është e njëjta me gjuhën e
  // familjes, kjo pjesë nuk bën as edhe një thirrje të vetme. Kjo nuk është
  // optimizim; është kushti që e bën veçorinë të pranueshme fare — përndryshe
  // çdo familje shqipfolëse me app shqip do të dërgonte tekstin e vet te
  // Microsoft-i pa asnjë arsye.
  //
  // PSE THIRRET FUNKSIONI EDGE EDHE KUR PËRKTHIMI ËSHTË RUAJTUR. Sepse dy gjëra
  // duhen kontrolluar bashkë: a e kemi përkthimin, DHE a i përgjigjet ende
  // origjinalit (hash-i). Të dyja i di funksioni. Një rrugë e dytë këtu, që
  // lexon vetëm cache-n, do të thoshte dy vende ku vendoset e njëjta gjë — dhe
  // ai është pikërisht gabimi që kemi paguar tri herë këtë javë. Një kërkesë
  // për ekran, jo një për rresht.
  const [gjuhaEFamiljes, setGjuhaEFamiljes] = useState(null);
  const [perkthimet, setPerkthimet] = useState({});      // "id|fusha" → { teksti, src_lang }
  const [origjinaliHapur, setOrigjinaliHapur] = useState({}); // id → true
  const [perkthimiGabim, setPerkthimiGabim] = useState(null);

  useEffect(() => {
    if (!activeFamilyId) { setGjuhaEFamiljes(null); return undefined; }
    let gjallë = true;
    supabase.rpc("rpc_get_content_lang", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      // Pa derë (PERKTHIMI1 e paxhiruar) → `null`, dhe atëherë përkthimi rri
      // krejt i fikur. Një veçori që s'është instaluar duhet të mos ekzistojë,
      // jo të dështojë.
      if (!gjallë || error || !data) return;
      setGjuhaEFamiljes(String(data));
    });
    return () => { gjallë = false; };
  }, [activeFamilyId]);

  // Gjuha e lexuesit, e prerë te rrënja: `en-CA` dhe `en` janë e njëjta gjuhë
  // për këtë qëllim, dhe një kod rajonal do të bënte dy cache për një tekst.
  // Kinezçja është përjashtimi i vetëm — aty rrënja `zh` nuk mjafton, sepse dy
  // shkrimet nuk janë e njëjta gjuhë për sytë e askujt.
  const gjuhaIme = (() => {
    const l = String(i18n.language || "en");
    if (/^zh/i.test(l)) return "zh-Hans";
    return l.split("-")[0];
  })();
  const perkthimiVlen = !!(gjuhaEFamiljes && gjuhaIme && gjuhaIme !== gjuhaEFamiljes);

  // ── «Edge Function returned a non-2xx status code» ──────────────────────
  //
  // 29 gusht 2026. Pronari raportoi pikërisht këtë varg, dhe ai varg është vetë
  // problemi: `supabase-js` e mbështjell ÇDO përgjigje jo-2xx me të njëjtin
  // mesazh gjenerik. Kodi i vërtetë — `needs_perkthimi2`, `azure_401`,
  // `oda_save: …` — rri te trupi i përgjigjes, te `error.context`, dhe atje nuk
  // shkon askush nëse nuk e di.
  //
  // Pra unë kisha ndërtuar një ndërfaqe që e shfaqte me kujdes mesazhin e
  // gabimit, dhe mesazhi nuk thoshte asgjë. Një diagnozë që s'diagnostikon është
  // më keq se asnjë: të bind se ke parë.
  //
  // `clone()` para leximit sepse trupi i një `Response` lexohet NJË herë; pa të,
  // prova e dytë (tekst i thjeshtë) do të dështonte gjithmonë.
  async function gabimiIFunksionit(error, data) {
    if (!error) return data?.error ? String(data.error) : null;
    const ctx = error.context;
    if (ctx && typeof ctx.clone === "function") {
      try {
        const trupi = await ctx.clone().json();
        if (trupi?.error) {
          return String(trupi.error) + (trupi.detail ? ` — ${String(trupi.detail).slice(0, 200)}` : "");
        }
      } catch (_e) { /* jo JSON — provojmë tekstin */ }
      try {
        const teksti = await ctx.clone().text();
        if (teksti && teksti.trim()) return teksti.trim().slice(0, 300);
      } catch (_e) { /* pa trup */ }
    }
    return String(error.message || error);
  }

  // ── PËRKTHEHET KUR KËRKOHET, JO KUR HAPET EKRANI ────────────────────────
  //
  // Versioni i parë e përkthente vetvetiu çdo postim të dukshëm sapo gjuha e
  // lexuesit ndryshonte. Pronari e ndaloi, dhe arsyeja e tij është më e mirë se
  // e imja:
  //
  //   «të jetë një buton te titulli i tekstit, p.sh. EN, për me përkthy — në
  //    mënyrë që mos me hargju kotën e përkthimit kot.»
  //
  // Dikush që hap Historinë dhe rrëshqet poshtë do të kishte përkthyer
  // pesëdhjetë postime pa lexuar asnjërin. Kuota falas është e madhe, por nuk
  // është e pafundme, dhe ajo që shpenzohet pa u lexuar nuk kthehet.
  //
  // Prandaj: një prekje, një postim. Dhe përderisa përkthimi ruhet përgjithmonë
  // te baza, prekja e dytë e askujt nuk kushton më asgjë.
  const [perkthimiDuke, setPerkthimiDuke] = useState({});   // id → true, sa zgjat kërkesa

  function kerkoPerkthimin(id, fushat, lloji = "histori") {
    if (!perkthimiVlen || !activeFamilyId || perkthimiDuke[id]) return;
    const njesite = Object.entries(fushat)
      .filter(([, teksti]) => teksti && String(teksti).trim())
      .map(([fusha, teksti]) => ({ id, fusha, teksti: String(teksti) }));
    if (njesite.length === 0) return;

    setPerkthimiDuke((v) => ({ ...v, [id]: true }));
    supabase.functions
      .invoke("perkthe", { body: { family_id: activeFamilyId, gjuha: gjuhaIme, njesite, lloji } })
      .then(async ({ data, error }) => {
        setPerkthimiDuke((v) => { const n = { ...v }; delete n[id]; return n; });
        // ÇFARË I THUHET LEXUESIT KUR DËSHTON. Asgjë — dhe kjo është e
        // qëllimshme. Teksti origjinal është aty, i plotë; një kuti e kuqe mbi
        // të do t'i thoshte se diçka u prish, kur në të vërtetë ai po lexon
        // pikërisht atë që ka shkruar familja. E vetmja gjendje që e meriton
        // një fjalë është kuota, sepse ajo zgjat gjithë muajin.
        //
        // Për ne mbetet konsola: aty shkruhet kodi i saktë, që një «nuk po
        // përkthen» të mos jetë kërkim në errësirë.
        const g = await gabimiIFunksionit(error, data);
        if (g) console.warn("perkthe:", g);
        setPerkthimiGabim(g === "quota_month" ? "quota_month" : null);
        const t2 = {};
        for (const p of data?.perkthimet || []) t2[`${p.id}|${p.fusha}`] = { teksti: p.teksti, src_lang: p.src_lang };
        // EDHE KUR AZURE THA «kjo është tashmë në gjuhën tënde», rezultati
        // ruhet — përndryshe butoni do të rrinte aty përgjithmonë dhe secili që
        // e shtyp do të paguante sërish për të njëjtën përgjigje.
        if (Object.keys(t2).length) setPerkthimet((v) => ({ ...v, ...t2 }));
        else setPerkthimet((v) => ({ ...v, [`${id}|_provuar`]: { teksti: "", src_lang: null } }));
      });
  }

  // Teksti që duhet parë për një fushë. Rregulli: origjinali fiton kur lexuesi e
  // ka kërkuar, dhe kur përkthimi është identik me origjinalin (Azure e zbuloi
  // se teksti ishte tashmë në gjuhën e lexuesit) — atëherë nuk ka çfarë të
  // shfaqet si «përkthim», dhe një etiketë e tillë do të ishte thjesht e rreme.
  function tekstiIDukshem(id, fusha, origjinali) {
    if (!perkthimiVlen || origjinaliHapur[id]) return { teksti: origjinali, perkthyer: false };
    const p = perkthimet[`${id}|${fusha}`];
    if (!p || !p.teksti || p.teksti === origjinali) return { teksti: origjinali, perkthyer: false };
    return { teksti: p.teksti, perkthyer: true };
  }
  // A ekziston një përkthim që VËRTET ndryshon nga origjinali? Kjo pyetje nuk i
  // shikon `origjinaliHapur`: përgjigjja duhet të mbetet e njëjta kur lexuesi e
  // ka kthyer origjinalin, përndryshe butoni i kthimit do të zhdukej sapo të
  // shtypej — dhe rruga mbrapa do të mbyllej pas vetes.
  function kaPerkthim(id, fushat) {
    if (!perkthimiVlen) return false;
    return Object.entries(fushat).some(([f, orig]) => {
      const p = perkthimet[`${id}|${f}`];
      return !!(p && p.teksti && orig && p.teksti !== orig);
    });
  }
  // A është pyetur ndonjëherë për këtë postim? Nëse po, butoni «EN» hiqet —
  // edhe kur përgjigja ishte «kjo është tashmë anglisht». Një buton që e jep të
  // njëjtën përgjigje sa herë shtypet nuk është buton; është faturë.
  function ePyetur(id) {
    // `body` hyri me 31 gusht 2026, bashkë me përkthimin e bisedave te Qendra e
    // Ndihmës. Pa të, butoni te një mesazh tikete s'do të zhdukej kurrë pas
    // përkthimit — sepse pyetja «a u pyet ndonjëherë?» s'do ta njihte fushën ku
    // erdhi përgjigjja, dhe do të ftonte një faturë të dytë për të njëjtin tekst.
    return ["title", "description", "body", "_provuar"].some((f) => perkthimet[`${id}|${f}`] !== undefined);
  }
  // Butoni te titulli: dy shkronjat e gjuhës së lexuesit. «EN» e thotë vetë se
  // çfarë do të ndodhë, pa asnjë fjalë — dhe pikërisht atij që i duhet, sepse
  // shfaqet VETËM kur gjuha e tij ndryshon nga ajo e familjes.
  function butoniIPerkthimit(id, fushat, ngjyra, titulli = null, lloji = "histori") {
    if (!perkthimiVlen || ePyetur(id)) return null;
    const duke = !!perkthimiDuke[id];
    return (
      <button
        onClick={(e) => { e.stopPropagation(); kerkoPerkthimin(id, fushat, lloji); }}
        disabled={duke}
        title={titulli || t("Translate this post")}
        className="shrink-0 px-1.5 rounded"
        style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em", lineHeight: "18px",
                 color: ngjyra, background: "transparent", border: `1px solid ${COLORS.goldSoft}`,
                 cursor: duke ? "default" : "pointer", opacity: duke ? 0.5 : 1 }}>
        {duke ? "…" : gjuhaIme.slice(0, 2).toUpperCase()}
      </button>
    );
  }
  // Rreshti nën tekstin e përkthyer. E shkruan hapur se është makinë, dhe e mban
  // origjinalin një prekje larg. Fjalët e një gjysheje, të rishkruara nga një
  // makinë dhe të paraqitura si të sajat, do të ishin një gënjeshtër e heshtur.
  function rreshtiIPerkthimit(id, ngjyra) {
    const hapur = !!origjinaliHapur[id];
    return (
      <button
        onClick={() => setOrigjinaliHapur((v) => ({ ...v, [id]: !hapur }))}
        className="mt-1.5 inline-flex items-center gap-1.5"
        style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer",
                 fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 500, color: ngjyra, opacity: 0.9 }}>
        <span>🌐</span>
        <span style={{ textDecoration: "underline" }}>
          {hapur ? t("Show the translation") : t("Machine translated · Show the original")}
        </span>
      </button>
    );
  }

  // FAZA 0 — sa shërbime ka për të blerë kjo familje. Vendos NËSE hyrja
  // "Shërbimet" shfaqet fare në navigim: me katalog bosh (ose RPC ende të
  // paaplikuar) menuja mbetet saktësisht siç ishte — kjo është gjithë ideja e
  // regjistrit, asgjë nuk zë vend derisa admini të ndezë diçka.
  const [servicesCount, setServicesCount] = useState(0);
  // RRAHJA E GJALLË — "pa rifreskim" (kërkesë e pronarit, 20 gusht).
  //
  // Tri gjëra ndryshojnë JASHTË kësaj skede dhe duhen parë pa e ringarkuar
  // faqen: bashkimi me një familje të dytë (ura), shënimi "i paguar" nga stafi
  // (veçoria e blerë), dhe heqja a bllokimi në një familje (ura që shkëputet).
  //
  // Serveri e llogarit të vërtetën në momentin e thirrjes — `rpc_my_bridges`
  // ndërtohet nga `my_family_ids()`, jo nga një tabelë e ruajtur. Klientit i
  // mbetet vetëm ta pyesë në kohën e duhur, dhe koha e duhur është kur faqja
  // rikthehet në plan të parë: pikërisht atëherë kur dikush ka bërë diçka diku
  // tjetër dhe kthehet të shohë rezultatin.
  const [liveTick, setLiveTick] = useState(0);
  useEffect(() => {
    if (!authSession) return undefined;
    const bump = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      setLiveTick((n) => n + 1);
    };
    document.addEventListener("visibilitychange", bump);
    window.addEventListener("focus", bump);
    return () => {
      document.removeEventListener("visibilitychange", bump);
      window.removeEventListener("focus", bump);
    };
  }, [authSession]);
  useEffect(() => {
    if (!activeFamilyId) { setServicesCount(0); return; }
    let alive = true;
    loadFeatures(activeFamilyId);   // flag-et e reja — gati për FeatureGate-t e Fazës 2
    supabase.rpc("rpc_services_for_me", { p_family_id: activeFamilyId })
      .then(({ data, error }) => { if (alive && !error) setServicesCount((data || []).length); })
      .catch(() => { /* RPC mungon → 0, hyrja s'shfaqet */ });
    return () => { alive = false; };
  }, [activeFamilyId, liveTick]);
  // Nëse sesioni u mbyll te "Shërbimet" dhe në rikthim katalogu del bosh
  // (admini e fiku ndërkohë), ekrani do të mbetej i zbrazët pa asnjë hyrje për
  // të dalë. Rikthim i qetë te ballina.
  //
  // POST-MORTEM (20 gusht, ora e deploy-it): ky efekt fillimisht qëndronte MBI
  // deklarimin e `servicesCount`, dhe vargu i varësive e lexon gjatë render-it —
  // pra "Cannot access before initialization" në ngarkim, aplikacioni i rrëzuar
  // që në nisje. esbuild s'e sheh (sintaksë e vlefshme), tdz.cjs s'e pa (kontrollon
  // vetëm nivelin e modulit). Rregulli që mbetet: çdo hook që lexon një gjendje
  // rri POSHTË deklarimit të saj, gjithmonë.
  useEffect(() => {
    if (activeScreen === "services" && activeFamilyId && servicesCount === 0) {
      const id = setTimeout(() => {
        setServicesCount((c) => { if (c === 0) setActiveScreen("home"); return c; });
      }, 4000);   // i lë kohë përgjigjes së parë të RPC-së
      return () => clearTimeout(id);
    }
  }, [activeScreen, activeFamilyId, servicesCount]);
  // `features.js` nuk është React — flag-et mbërrijnë pas render-it të parë dhe
  // askush s'e di. Pa këtë abonim, posteri do të ngrinte në gjendjen e çastit të
  // ngarkimit: i hapur për një familje që s'e ka blerë, ose i kyçur për një që e
  // ka. Një rrotë e vogël rivizatimi, e vetmja lidhje mes dy botëve.
  const [, setFeaturesTick] = useState(0);
  useEffect(() => onFeaturesChange(() => setFeaturesTick((n) => n + 1)), []);
  // Ngarko mesazhet e header-it për këtë familje (global + specifike).
  useEffect(() => {
    if (!activeFamilyId) { setHeaderMessages([]); return; }
    let alive = true;
    supabase.rpc("rpc_list_header_messages", { p_family_id: activeFamilyId })
      .then(({ data }) => { if (alive) { setHeaderMessages(Array.isArray(data) ? data : []); setHeaderMsgIdx(0); } })
      .catch(() => { if (alive) setHeaderMessages([]); });
    return () => { alive = false; };
  }, [activeFamilyId]);

  // FLAG-ET GLOBALE TË PLATFORMËS (Admin Center → Veçoritë & Njoftimet).
  // Parazgjedhjet përputhen me PLATFORM_features.sql; nëse RPC s'ekziston ende
  // (SQL i pa-aplikuar), mbeten këto (Posta OFF, email OFF, push ON).
  const [platformFeatures, setPlatformFeatures] = useState({
    family_mail_enabled: false, notify_push_enabled: true, notify_email_enabled: false, notify_channels_visible: true,
  });
  useEffect(() => {
    // Flag-et EFEKTIVE për familjen aktive (mbivendosja per-familje mbi
    // parazgjedhjen e platformës). Nëse s'ka familje aktive ende, ose RPC-ja
    // per-familje s'ekziston, bie te flag-et globale.
    const applyRow = (data) => { const r = Array.isArray(data) ? data[0] : data; if (r) setPlatformFeatures(r); };
    const fallbackGlobal = () => supabase.rpc("rpc_platform_features").then(({ data }) => applyRow(data)).catch(() => {});
    if (activeFamilyId) {
      supabase.rpc("rpc_family_features_effective", { p_family_id: activeFamilyId })
        .then(({ data, error }) => { if (error) fallbackGlobal(); else applyRow(data); })
        .catch(fallbackGlobal);
    } else {
      fallbackGlobal();
    }
  }, [activeFamilyId]);

  // Rrotullimi: nëse ka >1 mesazh, ndërro çdo 6s.
  useEffect(() => {
    if (headerMessages.length <= 1) return;
    const cur = headerMessages[headerMsgIdx % headerMessages.length];
    const secs = Math.min(120, Math.max(2, Number(cur && cur.duration_seconds) || 6));
    const id = setTimeout(() => setHeaderMsgIdx((i) => (i + 1) % headerMessages.length), secs * 1000);
    return () => clearTimeout(id);
  }, [headerMessages, headerMsgIdx]);
  // A është ky përdorues Super Admin i PLATFORMËS? Nevojitet që vetëm ai të mund
  // të fshijë adminin e VETËM të një familjeje (të tjerët as butonin s'e shohin).
  const [platformIsSuper, setPlatformIsSuper] = useState(false);
  // A lejohem të shkarkoj çdo foto? Vendimi merret te serveri
  // (rpc_can_download_any_photo): çelësi i politikës DHE leja `media_access`.
  // Fail-closed — pa përgjigje, mbetet false.
  const [canDownloadAnyPhoto, setCanDownloadAnyPhoto] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("rpc_can_download_any_photo");
        if (!alive || error) return;
        setCanDownloadAnyPhoto(data === true);
      } catch (e) { /* pa migrimin ende → mbetet false */ }
    })();
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("rpc_am_i_platform_staff");
        if (!alive || error) return;
        const row = Array.isArray(data) ? data[0] : data;
        if (row && (row.is_super === true || (Array.isArray(row.permissions) && row.permissions.includes("*")))) {
          setPlatformIsSuper(true);
        }
      } catch (e) { /* pa RBAC ende / pa rrjet → mbetet false (fail-closed) */ }
    })();
    return () => { alive = false; };
  }, []);
  // ── Maintenance Mode + njoftimet e planifikuara ─────────────────────────
  const [maintStatus, setMaintStatus] = useState(null);   // {maintenance_on, reason, info, until}
  const [maintCanBypass, setMaintCanBypass] = useState(false);
  const [maintNotices, setMaintNotices] = useState([]);
  const [maintPeek, setMaintPeek] = useState(false);       // "Kyçu" nga faqja e mirëmbajtjes
  const [maintDismissed, setMaintDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("pef_dismissed_maint") || "[]")); } catch (e) { return new Set(); }
  });
  // Rifreskim automatik i statusit + njoftimeve, që aplikacioni të HYJË dhe të
  // DALË vetë nga mirëmbajtja PA u mbyllur/rihapur. Pa këtë, kur admini e ndez
  // ose e fik mirëmbajtjen (ose skadon ora), pajisja mbetet e "ngrirë" derisa
  // përdoruesi e mbyll aplikacionin. Rifreskohet: në montim, çdo 30s, dhe sa
  // herë aplikacioni kthehet në plan të parë (visibilitychange/focus).
  const refreshMaint = useCallback(() => {
    supabase.rpc("rpc_platform_status").then(({ data }) => setMaintStatus(Array.isArray(data) ? data[0] : data)).catch(() => {});
    supabase.rpc("rpc_maintenance_notices_active").then(({ data }) => setMaintNotices(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    refreshMaint();
    const id = setInterval(refreshMaint, 30000);
    const onVis = () => { if (typeof document !== "undefined" && document.visibilityState === "visible") refreshMaint(); };
    try {
      document.addEventListener("visibilitychange", onVis);
      window.addEventListener("focus", refreshMaint);
    } catch (e) { /* pa DOM — pa rëndësi */ }
    // Native (Capacitor): sa herë aplikacioni kthehet nga sfondi.
    const hiqResume = dëgjoResume(refreshMaint);
    return () => {
      clearInterval(id);
      try {
        document.removeEventListener("visibilitychange", onVis);
        window.removeEventListener("focus", refreshMaint);
      } catch (e) { /* pa rëndësi */ }
      hiqResume();
    };
  }, [refreshMaint]);
  // Bypass rilexohet sa herë ndryshon sesioni (pas login-it).
  useEffect(() => {
    if (!authSession) { setMaintCanBypass(false); return; }
    supabase.rpc("rpc_can_bypass_maintenance").then(({ data }) => setMaintCanBypass(data === true)).catch(() => setMaintCanBypass(false));
  }, [authSession]);
  // Nëse një përdorues jo-i-lejuar hyn gjatë "peek", riktheje bllokimin.
  useEffect(() => {
    if (maintPeek && authSession && maintStatus?.maintenance_on && !maintCanBypass) setMaintPeek(false);
  }, [maintPeek, authSession, maintCanBypass, maintStatus]);
  const dismissNotice = (id) => {
    setMaintDismissed((prev) => {
      const next = new Set(prev); next.add(id);
      try { localStorage.setItem("pef_dismissed_maint", JSON.stringify([...next])); } catch (e) { /* storage plot */ }
      return next;
    });
  };
  // Kur admini ka caktuar një orë mbarimi ("Expected end"/until), ajo është
  // AUTORITATIVE: sapo numëratori mbërri te 00, bllokimi lëshohet VETË — pa
  // pritur që admini të fikë çelësin. (Nëse "until" lihet bosh → s'ka numërator
  // dhe bllokimi rri derisa admini ta fikë manualisht.)
  const [maintTick, setMaintTick] = useState(0); // eslint-disable-line no-unused-vars
  const maintExpired = !!(maintStatus?.until && new Date(maintStatus.until).getTime() <= Date.now());
  useEffect(() => {
    if (!maintStatus?.maintenance_on || !maintStatus?.until) return;
    const ms = new Date(maintStatus.until).getTime() - Date.now();
    if (ms <= 0) return; // tashmë skaduar → `maintExpired` e mbulon
    // Zgjohu SAKTËSISHT në orën e mbarimit që faqja të hapet vetë (edhe pa rrjet).
    const id = setTimeout(() => { setMaintTick((n) => n + 1); refreshMaint(); }, Math.min(ms + 500, 2147483000));
    return () => clearTimeout(id);
  }, [maintStatus, refreshMaint]);
  const maintenanceBlocked = !!(maintStatus && maintStatus.maintenance_on) && !maintExpired && !maintCanBypass && !maintPeek;

  // Njoftimet AKTIVE = ato jo të skaduara (sipas ends_at ose starts_at+kohëzgjatje).
  // Kur njoftimi skadon, zhduket vetë kudo (banderolë, zile, skeda Platforma).
  // `maintTick` (numëratori i mësipërm) e rifreskon këtë llogaritje me kalimin e kohës.
  const activeNotices = (maintNotices || []).filter((n) => !maintNoticeExpired(n));
  // Njoftime AKTIVE që përdoruesi s'i ka parë ende (s'e ka mbyllur banderolën) —
  // vetëm këto e ndezin numrin e KUQ te zilja. Pasi mbyllet banderola, numri
  // fiket, POR zilja mbetet (sepse njoftimi është ende në fuqi) derisa të skadojë.
  const unseenNotices = activeNotices.filter((n) => !maintDismissed.has(n.id));

  // RREGULLAT SIPAS SHTETIT — ngarko rregullat efektive (ngjyra/etiketa/pragje/
  // model) për këtë familje sapo dihet activeFamilyId; mbushi te ACTIVE_RULES dhe
  // rrit `rulesVersion` që branchColor/branchLabel të rillogariten. Parazgjedhjet
  // janë të njëjta me ç'ishin, kështu s'ka ndryshim pamjeje derisa admini të ndryshojë.
  const [rulesVersion, setRulesVersion] = useState(0);
  // HAPI 4 — zgjedhësi i shtetit te familja: a lejohet, cila është e zgjedhura, dhe lista.
  const [rulesCanChoose, setRulesCanChoose] = useState(false);
  const [familyRuleSetId, setFamilyRuleSetId] = useState("");
  const [ruleSetOptions, setRuleSetOptions] = useState([]);
  const loadRules = React.useCallback(() => {
    if (!activeFamilyId) return;
    supabase.rpc("rpc_get_effective_rules", { p_family_id: activeFamilyId }).then(
      ({ data }) => {
        const r = Array.isArray(data) ? data[0] : data;
        if (!r) return;
        ACTIVE_RULES.colors = { ...DEFAULT_RULE_COLORS, ...(r.colors || {}) };
        ACTIVE_RULES.labels = { ...DEFAULT_RULE_LABELS, ...(r.labels || {}) };
        ACTIVE_RULES.thresholds = { ...DEFAULT_RULE_THRESHOLDS, ...(r.thresholds || {}) };
        ACTIVE_RULES.kinship_model = r.kinship_model || "patrilineal";
        ACTIVE_RULES.status_icons = { ...DEFAULT_RULE_STATUS_ICONS, ...(r.status_icons || {}) };
        ACTIVE_RULES.behaviors = { ...DEFAULT_RULE_BEHAVIORS, ...(r.behaviors || {}) };
        setRulesVersion((v) => v + 1);
      },
      () => {}
    );
  }, [activeFamilyId]);
  useEffect(() => {
    if (!activeFamilyId) return;
    loadRules();
    supabase.rpc("rpc_rules_choice_context", { p_family_id: activeFamilyId }).then(
      ({ data }) => {
        const c = Array.isArray(data) ? data[0] : data;
        setRulesCanChoose(!!c?.can_choose);
        setFamilyRuleSetId(c?.current_rule_set_id || "");
      }, () => {}
    );
    supabase.rpc("rpc_list_rule_sets_public").then(
      ({ data }) => setRuleSetOptions(data || []), () => {}
    );
  }, [activeFamilyId, loadRules]);
  // Familja (admini) zgjedh shtetin → ruaj dhe ringarko rregullat.
  const setFamilyCountry = async (rid) => {
    const val = rid || null;
    const { error } = await supabase.rpc("rpc_set_family_rule_set", { p_family_id: activeFamilyId, p_rule_set_id: val });
    if (error) { showToast("Gabim: " + error.message); return; }
    setFamilyRuleSetId(rid || "");
    loadRules();
    showToast("Modaliteti u ndryshua.");
  };
  // FAZA 12 — zgjedhësi i familjeve: krejt lista e (person, familje) me të
  // cilat kjo llogari është e lidhur, jo vetëm përputhja për instancën
  // aktuale. E dobishme kur dikush i përket më shumë se një familjeje (p.sh.
  // trungu i vet + familja ku ka hyrë me martesë).
  const [myLinkedFamilies, setMyLinkedFamilies] = useState([]);
  // Familja e PREFERUAR e llogarisë — hapet si parazgjedhje në hyrje (shih
  // efektin "resolve family from account"). Yll ★ te "Familjet e mia".
  const [favoriteFamilyId, setFavoriteFamilyId] = useState(null);
  // VETË-BASHKIM (self-join) — familjet ku email-i i kësaj llogarie (Google/
  // Apple) përputhet me email-in e kontaktit të një personi ende TË PALIDHUR.
  // Përdoruesi i ri i sheh si tabelë dhe zgjedh vetë të bashkohet, pa ftesë.
  const [matchableFamilies, setMatchableFamilies] = useState([]);
  const [selfJoinBusy, setSelfJoinBusy] = useState(null); // person_id që po bashkohet
  const [authBusy, setAuthBusy] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  // Kërkesë e userit — paneli i profilit tani hapet EKRAN I PLOTË në
  // telefon (jo më gjysma e ekranit), me swipe-mbylljeje si dritaret popup.
  const [profileDragX, setProfileDragX] = useState(0);
  const [profileDragging, setProfileDragging] = useState(false);
  const profileTouchStart = useRef(null);
  const onProfileTouchStart = (e) => { profileTouchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onProfileTouchMove = (e) => {
    if (!profileTouchStart.current) return;
    const dx = e.touches[0].clientX - profileTouchStart.current.x;
    const dy = e.touches[0].clientY - profileTouchStart.current.y;
    if (Math.abs(dy) > Math.abs(dx) || dx < 0) return; // vertikal (scroll) — lëre normal
    setProfileDragging(true);
    setProfileDragX(dx);
  };
  const onProfileTouchEnd = () => {
    if (profileDragX > 90) { setSelectedId(null); }
    setProfileDragX(0);
    setProfileDragging(false);
    profileTouchStart.current = null;
  };
  useEffect(() => { setNoteEditing(false); }, [selectedId]);
  // 6 shtator 2026 — «herën tjetër profili nuk hapet komplet». Gjesti «prapa»
  // i sistemit (rrëshqitje nga skaji) nis si prekje mbi panel: `touchmove` e
  // zhvendos panelin, po sistemi e merr gjestin dhe dërgon `touchcancel`, jo
  // `touchend` — pra `profileDragX` mbetej i mbushur dhe paneli i radhës
  // hapej i zhvendosur e gjysmë i tejdukshëm. Pastrim edhe te anulimi, edhe
  // sa herë ndërron personi.
  useEffect(() => { setProfileDragX(0); setProfileDragging(false); profileTouchStart.current = null; }, [selectedId]);
  const [photoLightbox, setPhotoLightbox] = useState(null);
  // BASHKIM (kërkesë e userit) — "Njoftimet për ditëlindje", banner-i
  // "u shtua/n në pemë" (sipër), dhe "Njoftimet e platformës" tani janë NJË
  // faqe/modal i vetëm ("Njoftimet"), me 3 skeda. `announcementsOpen`
  // mbetet flamuri që hap TË GJITHË modalin (jo vetëm skedën e platformës) —
  // ruajtur me këtë emër për të mos e prishur listën e overlay-ve/prapa-
  // butonit që tashmë e referojnë. `notifPanelOpen` hiqet (ishte modal i
  // veçantë, tani është skeda "birthdays" këtu poshtë).
  const [announcementsOpen, setAnnouncementsOpen] = useState(false);
  // QENDRA1 (3 nëntor 2026) — dy skeda: «Preferencat» (parazgjedhje) dhe
  // «Lajmërimet» (ish-«Platforma»). «Ndryshimet» u hoq: Rrjedha i tregon tashmë
  // anëtarët e rinj dhe vdekjet, me rregullat e Admin Center.
  const [notificationsTab, setNotificationsTab] = useState("birthdays"); // "birthdays" | "platform"
  // NJOFTIME5 — mënyra e zgjedhur te ekrani («dega» | «familja» | «persona»);
  // null = ajo që rrjedh nga rreshtat e ruajtura. Rikthehet në çdo hapje.
  const [menyraEZgjedhur, setMenyraEZgjedhur] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [announceManageOpen, setAnnounceManageOpen] = useState(false);
  const [emailRepliesOpen, setEmailRepliesOpen] = useState(false);
  const [emailReplies, setEmailReplies] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [mailFolder, setMailFolder] = useState("inbox"); // 'inbox' | 'sent' | 'favorites'
  const [mailView, setMailView] = useState("list"); // 'list' | 'detail' | 'compose'
  const [selectedMailId, setSelectedMailId] = useState(null); // {type:'inbox'|'sent', id}
  const [replyDraftFor, setReplyDraftFor] = useState(null); // reply id currently composing
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [mailFolders, setMailFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // when set, mailFolder acts as "viewing this custom folder"
  const [newFolderName, setNewFolderName] = useState("");
  const fetchMailFolders = async () => {
    if (!canManage) return;
    const { data, error } = await supabase.rpc("rpc_list_mail_folders", { p_requester_id: currentPersonId });
    if (!error) setMailFolders(data || []);
  };
  const createMailFolder = async () => {
    if (!newFolderName.trim()) return;
    const { error } = await supabase.rpc("rpc_create_mail_folder", { p_name: newFolderName.trim(), p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    setNewFolderName("");
    await fetchMailFolders();
  };
  // POSTA — pesë veprime që deri më 21 gusht e ndryshonin ekranin PARA se të
  // dinin nëse serveri i pranoi.
  //
  // Forma ishte gjithmonë e njëjtë: thirre, pastaj përditëso gjendjen lokale,
  // pastaj thuaj "u fshi". Nëse RLS-ja e refuzonte, mesazhi zhdukej nga ekrani
  // dhe kthehej te rifreskimi i radhës. Përdoruesi mendon se ka çmendur.
  //
  // Tani rendi është i kundërt: prit përgjigjen, dhe prek ekranin vetëm nëse
  // serveri e bëri vërtet.
  const deleteMailFolder = async (id) => {
    const { error } = await supabase.rpc("rpc_delete_mail_folder", { p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    if (selectedFolderId === id) { setSelectedFolderId(null); setMailFolder("inbox"); }
    await fetchMailFolders();
    await fetchEmailReplies();
    await fetchSentEmails();
  };
  const moveToFolder = async (table, id, folderId) => {
    const { error } = await supabase.rpc("rpc_move_to_folder", { p_table: table, p_id: id, p_folder_id: folderId, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    if (table === "email_replies") setEmailReplies((prev) => prev.map((r) => (r.id === id ? { ...r, folder_id: folderId } : r)));
    else setSentEmails((prev) => prev.map((s) => (s.id === id ? { ...s, folder_id: folderId } : s)));
    showToast(folderId ? t("toast.movedToFolder") : t("toast.removedFromFolder"));
  };
  const deleteEmail = async (table, id) => {
    const { error } = await supabase.rpc("rpc_delete_email", { p_table: table, p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    if (table === "email_replies") setEmailReplies((prev) => prev.filter((r) => r.id !== id));
    else setSentEmails((prev) => prev.filter((s) => s.id !== id));
    setMailView("list");
    showToast(t("toast.deleted"));
  };
  // Funeral details for mourning notifications — the sender fills these in,
  // they get woven into the auto-generated body as a line about the funeral.
  const [funeralDate, setFuneralDate] = useState("");
  const [funeralPlace, setFuneralPlace] = useState("");
  const [mourningForId, setMourningForId] = useState(null); // deceased person id, used for the server-side permission check
  // For non-admin close relatives: a lightweight standalone mourning-only
  // composer, entirely separate from Posta Familjare (which they can't see).
  const [simpleMourningOpen, setSimpleMourningOpen] = useState(false);
  const fetchEmailReplies = async () => {
    if (!canManage) return;
    const { data, error } = await supabase.rpc("rpc_list_email_replies", { p_requester_id: currentPersonId });
    if (!error) setEmailReplies(data || []);
  };
  const fetchSentEmails = async () => {
    if (!canManage) return;
    const { data, error } = await supabase.rpc("rpc_list_sent_emails", { p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.sentLoadFailed", { error: error.message })); return; }
    setSentEmails(data || []);
  };
  const openEmailReplies = async () => {
    setEmailRepliesOpen(true);
    setMailFolder("inbox");
    setSelectedFolderId(null);
    setMailView("list");
    setSelectedMailId(null);
    await fetchEmailReplies();
    await fetchSentEmails();
    await fetchMailFolders();
  };
  const toggleStar = async (table, id, starred) => {
    const { error } = await supabase.rpc("rpc_toggle_star", { p_table: table, p_id: id, p_starred: starred, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    if (table === "email_replies") setEmailReplies((prev) => prev.map((r) => (r.id === id ? { ...r, starred } : r)));
    else setSentEmails((prev) => prev.map((s) => (s.id === id ? { ...s, starred } : s)));
  };
  const markReplyRead = async (id) => {
    // Shenja e leximit është e vetmja nga të pesta që NUK e ndal punën e askujt
    // kur dështon — prandaj pa toast. Por shkruhet te konsola: ndryshe "numri i
    // palexuarve nuk bie kurrë" do të kërkohej te ndërfaqja, kurse shkaku do të
    // ishte këtu.
    const { error } = await supabase.rpc("rpc_mark_reply_read", { p_reply_id: id, p_requester_id: currentPersonId });
    if (error) { console.error("rpc_mark_reply_read:", error.message); return; }
    setEmailReplies((prev) => prev.map((r) => (r.id === id ? { ...r, is_read: true } : r)));
  };
  const unreadReplyCount = emailReplies.filter((r) => !r.is_read).length;
  const sendReplyTo = async (reply) => {
    if (!replyText.trim()) { showToast(t("toast.writeAReply")); return; }
    setReplySending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-notification", {
        body: {
          subject: `Re: ${reply.original_subject || ""}`,
          body: replyText.trim(),
          senderName: t("family.label", { surname: treeName }),
          recipientEmails: [reply.from_email],
          requesterId: currentPersonId,
        },
      });
      if (error || data?.error) { showToast(t("toast.failed", { error: await extractFunctionErrorMessage(error, data) })); return; }
      showToast(t("toast.replySent"));
      setReplyDraftFor(null);
      setReplyText("");
      await fetchSentEmails();
    } catch (e) {
      showToast(t("toast.failed", { error: e?.message || String(e) }));
    } finally {
      setReplySending(false);
    }
  };
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  const [announceForm, setAnnounceForm] = useState({ title: "", body: "", startsAt: "", endsAt: "", targetGroup: "all", durationPreset: "manual" });
  const [mySubscriptions, setMySubscriptions] = useState([]);
  // SIGURIA2 (L2) — `true` vetëm pasi lista e abonimeve të personit është ngarkuar
  // (nga serveri ose nga kopja në pajisje). Para kësaj planifikuesi i njoftimeve
  // të telefonit nuk guxon të vendosë asgjë: me listën bosh do të llogariste
  // «dega» edhe kur personi ka zgjedhur një listë të vetën.
  const [subsGati, setSubsGati] = useState(false);
  const subsPersonRef = useRef(null);
  const [subForm, setSubForm] = useState({ channel: "push", contact: "", watchType: "person", watchPersonId: null });
  // Sigurohu që kanali i zgjedhur është gjithmonë një kanal I AKTIVIZUAR nga
  // Admin Center; nëse admini fik një kanal, kalo automatikisht te tjetri.
  useEffect(() => {
    const chPush = platformFeatures.notify_push_enabled, chEmail = platformFeatures.notify_email_enabled;
    setSubForm((sf) => {
      if (sf.channel === "push" && !chPush && chEmail) return { ...sf, channel: "email" };
      if (sf.channel === "email" && !chEmail && chPush) return { ...sf, channel: "push" };
      return sf;
    });
  }, [platformFeatures.notify_push_enabled, platformFeatures.notify_email_enabled]);
  const [selectedContact, setSelectedContact] = useState(null); // {email, phone} for the currently selected person, admin-only
  const [allContacts, setAllContacts] = useState([]);
  const [notifySenderName, setNotifySenderName] = useState(t("family.label", { surname: treeName }));
  const [notifySubject, setNotifySubject] = useState("");
  const [notifyBody, setNotifyBody] = useState("");
  const [notifyRecipientMode, setNotifyRecipientMode] = useState("all");
  const [notifyPicked, setNotifyPicked] = useState(new Set());
  const [notifyBranchAnchorId, setNotifyBranchAnchorId] = useState(null);
  const [hasSavedDraft, setHasSavedDraft] = useState(() => {
    try { return !!localStorage.getItem("ft_mail_draft"); } catch (e) { return false; }
  });
  // Auto-save the compose draft as the admin types — protects against an
  // accidental click closing the modal, a browser crash, or anything else
  // unpredictable losing an in-progress message. Saved locally on this
  // device only (not synced), cleared once the message actually sends.
  useEffect(() => {
    if (!notifySubject.trim() && !notifyBody.trim()) return;
    try {
      localStorage.setItem("ft_mail_draft", JSON.stringify({ subject: notifySubject, body: notifyBody, senderName: notifySenderName, savedAt: Date.now() }));
      setHasSavedDraft(true);
    } catch (e) { /* storage full/unavailable — draft protection just won't work this time */ }
  }, [notifySubject, notifyBody, notifySenderName]);
  const clearMailDraft = () => {
    try { localStorage.removeItem("ft_mail_draft"); } catch (e) { /* ignore */ }
    setHasSavedDraft(false);
  };
  const restoreMailDraft = () => {
    try {
      const raw = localStorage.getItem("ft_mail_draft");
      if (!raw) return;
      const draft = JSON.parse(raw);
      setNotifySubject(draft.subject || "");
      setNotifyBody(draft.body || "");
      setNotifySenderName(draft.senderName || t("family.label", { surname: treeName }));
      setNotifyRecipientMode("all");
      setEmailRepliesOpen(true);
      setMailView("compose");
    } catch (e) { /* corrupted draft — nothing to restore */ }
  };
  // bannerDismissed (ish-flamuri i mbylljes së banner-it lundrues) u hoq —
  // banner-i vetë tani është skeda "Ndryshime" brenda modalit "Njoftimet",
  // jo më element lundrues që kërkonte "mbyllje" të veçantë.
  // AUDITI 2026-08 — `bannerEnabled` dhe `toggleBanner` u hoqën: shiriti i
  // sipërm nuk ekziston më për familjet (e publikon vetëm platforma, për
  // mirëmbajtje), dhe pa të, flamuri s'kishte çfarë të kontrollonte.
  const [pushEnabled, setPushEnabled] = useState(false); // admin-wide switch, from settings
  const [myPushSubscribed, setMyPushSubscribed] = useState(false); // this device's own subscription status
  // AUDITI 2026-08 — u hoq krejt grupi i veglave të testimit që rrinte këtu:
  // numërimi i pajisjeve, lista e abonuesve, dërgimi i push-it provë, testi i
  // ditëlindjes (Email + Push) dhe çelësi kryesor i push-it. Ishin vegla
  // mbështetjeje në duart e adminit të familjes; tani i takojnë Admin Center-it,
  // ku zgjidhet familja dhe shihen pajisjet e saj. `pushEnabled` mbetet — lexohet
  // ende për të vendosur nëse abonohet kjo pajisje dhe a shfaqet çelësi personal.
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
  };
  // ── A MUND TË KETË FARE NJOFTIME KJO PAJISJE ────────────────────────────
  //
  // 29 gusht 2026. Pronari: «te telefoni, kur dua ta aktivizoj, më thotë se në
  // këtë shfletues apo pajisje nuk mund të aktivizohen njoftimet».
  //
  // Mesazhi ishte i vërtetë dhe njëkohësisht mashtrues: fajin nuk e ka pajisja.
  // Këtu është ndërtuar WEB PUSH — `serviceWorker` + `PushManager` + VAPID — dhe
  // ai nuk ekziston brenda një WebView-i. As te WKWebView-i i iOS-it, as te
  // WebView-i i Android-it. Pra brenda aplikacionit të instaluar `PushManager`
  // është `undefined`, dhe butoni «Aktivizo» s'ka si të punojë KURRË.
  //
  // Njoftimet e aplikacionit kërkojnë rrugën native (FCM / APNs) përmes
  // `@capacitor/push-notifications` — që sot nuk është te projekti.
  //
  // Derisa të ndërtohet, rreshti FSHIHET brenda app-it. Një buton që nuk mund të
  // punojë kurrë është më keq se mungesa e tij: e bind njeriun se diçka është
  // prishur te telefoni i tij, dhe e dërgon të kërkojë te cilësimet e sistemit
  // një leje që askush s'do t'ia kërkojë. Te shfletuesi mbetet, sepse aty punon.
  const pushIMundshem = (() => {
    try {
      if (Capacitor.isNativePlatform()) return false;
      return ("serviceWorker" in navigator) && ("PushManager" in window);
    } catch (_e) { return false; }
  })();

  const checkMyPushSubscription = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      setMyPushSubscribed(!!sub);
    } catch (e) { /* ignore */ }
  };
  // Parametri `silent` (kërkesë e userit): kur push-i kërkohet AUTOMATIKISHT
  // (shih autoRequestPushIfNeeded më poshtë), s'duhet të shfaqë toast-e —
  // sillet si çdo aplikacion tjetër, ku leja e njoftimeve trajtohet nga vetë
  // OS-i/shfletuesi, jo si veprim i dukshëm "u aktivizua!" brenda app-it.
  // Butoni manual "Aktivizo" (te paneli i njoftimeve) e thërret PA silent,
  // aty ende ka kuptim konfirmimi eksplicit.
  const enablePushForMe = async (silent = false) => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { if (!silent) showToast(t("toast.pushNotSupported")); return; }
    const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (!vapidKey) { if (!silent) showToast(t("toast.pushNotConfigured")); return; }
    // ── PA PERSON, ASNJË ABONIM ────────────────────────────────────────────
    //
    // 29 gusht 2026. Pronari: «e instalova PWA-në, i aktivizova njoftimet, dhe
    // prapë nuk shfaqem te lista, as nuk më vjen njoftim».
    //
    // Ky rresht mungonte, dhe mungesa e tij e shpjegon SAKTËSISHT atë çift
    // simptomash. `p_person_id` shkonte `null` nëse personi ende s'ishte
    // zgjidhur — dhe një abonim pa person është i padukshëm nga të dyja anët:
    //
    //   · lista e Admin Center-it lidh `push_subscriptions` me `people` për të
    //     nxjerrë familjen; me person `null`, rreshti nuk del kurrë;
    //   · `send-push` i zgjedh abonimet me `in("person_id", famIds)`, dhe
    //     `null` nuk përputhet me asnjë id — pra nuk i dërgohet kurrë.
    //
    // Rezultati: shfletuesi thoshte «u aktivizua», baza kishte një rresht, dhe
    // askush nuk e shihte e askush s'i dërgonte. Dështim i heshtur në të dy
    // drejtimet — dhe pikërisht i tillë sa të duket si «push-i s'punon».
    //
    // Tani ndalohet para se të krijohet jetimi, dhe e thotë pse.
    if (!currentPersonId) { if (!silent) showToast(t("Sign in as a family member first — the device is linked to a person.")); return; }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { if (!silent) showToast(t("toast.pushPermissionDenied")); return; }
      // register() can resolve before the worker is actually "active" — on iOS
      // in particular, subscribing too early here is a common cause of a
      // false "failed" the first time even though the browser-level
      // subscription itself goes through fine. Waiting for `.ready` avoids that.
      await navigator.serviceWorker.register("/sw.js");
      const reg = await navigator.serviceWorker.ready;
      // Reuse an existing subscription instead of subscribing again — on iOS,
      // calling subscribe() a second time while one already exists can throw.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidKey) });
      }
      const subJson = sub.toJSON();
      const { error } = await supabase.rpc("rpc_save_push_subscription", {
        p_person_id: currentPersonId,
        p_endpoint: subJson.endpoint,
        p_p256dh: subJson.keys.p256dh,
        p_auth: subJson.keys.auth,
        p_pajisja: etiketaEPajisjes(),
        p_agjenti: String(navigator.userAgent || "").slice(0, 400),
      });
      if (error) { if (!silent) showToast(t("toast.serverSaveFailed", { error: error.message })); return; }
      setMyPushSubscribed(true);
      if (!silent) showToast(t("toast.devicePushEnabled"));
    } catch (e) {
      // Self-heal: even though something above threw, the browser may have
      // still created a real subscription (this is exactly the iPhone case
      // where a "failed" toast was followed by the toggle showing active on
      // the next check) — if so, save it now instead of leaving it orphaned
      // client-side only, which is what silently breaks delivery.
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const subJson = sub.toJSON();
          // Nëse KJO dështon, shfletuesi mbetet i abonuar por serveri s'e di —
          // pra njoftimet nuk mbërrijnë kurrë, dhe ekrani thotë "u aktivizuan".
          // Është saktësisht defekti që komenti sipër përshkruan si "client-side
          // only, which is what silently breaks delivery".
          const { error: pushErr } = await supabase.rpc("rpc_save_push_subscription", {
            p_person_id: currentPersonId,
            p_endpoint: subJson.endpoint,
            p_p256dh: subJson.keys.p256dh,
            p_auth: subJson.keys.auth,
            p_pajisja: etiketaEPajisjes(),
            p_agjenti: String(navigator.userAgent || "").slice(0, 400),
          });
          if (pushErr) { showToast(t("toast.failed", { error: pushErr.message })); return; }
          setMyPushSubscribed(true);
          if (!silent) showToast(t("toast.devicePushEnabledRecovered"));
          return;
        }
      } catch (_e2) { /* fall through to the error toast below */ }
      if (!silent) showToast(t("toast.activationFailed", { error: e?.message || String(e) }));
    }
  };
  // Kërkesë e userit — leja e push-it duhet të trajtohet SI ÇDO APLIKACION
  // TJETËR: kërkohet automatikisht (jo pas një butoni të fshehur brenda
  // menusë), OS-i/shfletuesi e ruan vendimin, dhe APP-I KURRË s'e pyet
  // sërish dikë që e ka refuzuar tashmë — as çdo hapje, as çdo sesion.
  //   • Notification.permission === "granted" → tashmë e lejuar te OS-i;
  //     thjesht sigurohemi që PAJISJA konkrete ka pajtim aktiv (silent,
  //     s'ka nevojë leje shtesë — mund të ndodhë p.sh. pas fshirjes së
  //     pajtimit të vjetër nga vetë shfletuesi).
  //   • "denied" → OS-i tashmë ka thënë JO — s'e prekim FARE, kurrë.
  //   • "default" (ende s'ka vendosur) → e pyesim VETËM NJËHERË për këtë
  //     pajisje (flamuri lokalStorage "pushAsked:<personId>", vendosur PARA
  //     vetë thirrjes — edhe nëse dialogu mbyllet pa zgjedhje, s'rikthehemi
  //     ta pyesim sërish herën tjetër që hap app-in).
  const autoRequestPushIfNeeded = async () => {
    if (!pushEnabled || !currentPersonId) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "denied") return;
    if (Notification.permission === "granted") {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg && (await reg.pushManager.getSubscription());
        if (!sub) await enablePushForMe(true);
      } catch (e) { /* e padëmshme — thjesht provo sërish herën tjetër */ }
      return;
    }
    const askedKey = `pushAsked:${currentPersonId}`;
    if (localStorage.getItem(askedKey)) return;
    localStorage.setItem(askedKey, "1");
    await enablePushForMe(true);
  };
  // Efekt i VEÇANTË (jo brenda efektit të identifikimit sipër) — pushEnabled
  // vjen nga fetchSettings, që mund të mbarojë PAS që currentPersonId të
  // vendoset; nëse thirrja do të ishte brenda atij efekti, do të rrezikonte
  // të ekzekutohej me pushEnabled ende `false` (vlera fillestare), para se
  // cilësimi i vërtetë të mbërrijë. Duke e vënë vetë pushEnabled si
  // varësi këtu, autoRequestPushIfNeeded rithirret sapo vlera reale mbërrin.
  useEffect(() => {
    autoRequestPushIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPersonId, pushEnabled]);
  const disablePushForMe = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        // Rendi ka rëndësi: hiqe së pari te serveri. Nëse `unsubscribe()` te
        // shfletuesi bëhet i pari dhe serveri dështon, mbetet një abonim
        // "fantazmë" te baza që dërgon drejt një endpoint-i të vdekur.
        const { error: rmErr } = await supabase.rpc("rpc_remove_push_subscription", { p_endpoint: sub.endpoint });
        if (rmErr) { showToast(t("toast.failed", { error: rmErr.message })); return; }
        await sub.unsubscribe();
      }
      setMyPushSubscribed(false);
      showToast(t("toast.devicePushDisabled"));
    } catch (e) {
      showToast(t("toast.deactivationFailed"));
    }
  };
  const [hoverId, setHoverId] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [personModal, setPersonModal] = useState(null);
  const [form, setForm] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  // SHTESË (kërkesë e userit) — "fokusimi i fotografive". `photoFocus`
  // mban vlerën AKTUALE (0..1, 0..1) të formularit të hapur — fillon nga
  // 0.5/0.5 (qendra, sillet si më parë) te "Shto", ose nga vlera e ruajtur
  // e personit te "Redakto". `focusPickerOpen` hap dritaren interaktive.
  const [photoFocus, setPhotoFocus] = useState({ x: 0.5, y: 0.5 });
  const [photoZoom, setPhotoZoom] = useState(1); // kropi me zoom (1 = "cover")
  // Faza 11 — template-i aktiv i fushave custom + vlerat e tyre për personin.
  const [customTemplate, setCustomTemplate] = useState(null);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [personTypeOptions, setPersonTypeOptions] = useState(null); // Faza 2 — opsionet e "Shto person" (config)
  // Faza 2 — formulari i ri "Shto person" (AddPersonSheet). Për pemë jo-bosh;
  // personi i parë (bootstrap) vazhdon me modalin e detajuar.
  const [addSheet, setAddSheet] = useState(null);
  const addSheetBackRef = useRef(null); // hapi prapa brenda fletës (shih closeTopOverlay)
  // Lista «Çfarë do të shtosh?» — shih `hapShtimin()`.
  // ⚠ 4 shtator 2026 — «Shto» NUK ËSHTË MË DRITARE.
  //
  // Pronari: «Shto të trajtohet njësoj si të gjitha dritaret e tjera të menysë
  // së poshtme. Pra nuk duhet të jetë popup window por një faqe si gjithë të
  // tjerat në meny.»
  //
  // Kishte të drejtë, dhe ishte mospërputhje e vjetër: pesë hyrjet e shiritit
  // çojnë te një EKRAN, e gjashta hapte një dritare mbi ekranin ku ishe. Pra
  // ajo ishte e vetmja hyrje e menysë që nuk të çonte askund — dhe kjo ndihet
  // edhe kur nuk dihet të thuhet me fjalë.
  //
  // Tani `activeScreen` merr vlerën e gjashtë, `"shto"`. Ekranet e panjohura
  // te pjesët e tjera bien vetvetiu te rezerva (kërkimi thotë «Kjo faqe»,
  // pemën s'e ndërton, etj.), prandaj shtimi i një vlere nuk kërkoi asnjë
  // ndryshim tjetër përveç kthimit prapa.
  //
  // `ekraniPara` mban se nga erdhe, që back-i të të kthejë atje e jo gjithmonë
  // te ballina. Nuk ruhet te sessionStorage: një rifreskim nuk duhet të të
  // hapë përsëri listën e shtimit.
  const ekraniPara = useRef((() => {
    try { return sessionStorage.getItem("family_screen_before_shto") || null; } catch { return null; }
  })());
  const [focusPickerOpen, setFocusPickerOpen] = useState(false);
  const [spouseModalFor, setSpouseModalFor] = useState(null);
  const [spousePick, setSpousePick] = useState(null);
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const [scale, setScale] = useState(1);
  // Ref gjithmonë e freskët e `scale` — përdoret brenda closure-ve që s'e
  // dëgjojnë `scale` si varësi (p.sh. dëgjuesi i "resize"/rrotullimit
  // poshtë), që të mos lexojnë vlerë të vjetëruar.
  const scaleRef = useRef(1);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  const [pan, setPan] = useState({ x: 60, y: 60 });
  // Kërkesë e userit — shkallë shtesë zoom-i kur pema qendërzohet mbi
  // personin e identifikuar (jo mbi krejt pemën), e rregullueshme nga
  // platform admin te Admin Center → Cilësimet (shih
  // rpc_get_tree_focus_zoom / rpc_platform_set_tree_focus_zoom te
  // schema.sql). Parazgjedhje 1.6 derisa të mbërrijë vlera e vërtetë nga
  // databaza — mjafton për shembull, nuk shkakton "fluturim" të dukshëm.
  const [treeFocusZoom, setTreeFocusZoom] = useState({ enabled: true, multiplier: 1.6 });
  useEffect(() => {
    let cancelled = false;
    fetchSettingRpcWithRetry("rpc_get_tree_focus_zoom").then((data) => {
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row) setTreeFocusZoom({ enabled: row.enabled, multiplier: row.multiplier });
    });
    return () => { cancelled = true; };
  }, []);
  // Kërkesë e userit — zoom-out i lehtë kur zgjerohet (jo mbyllet) një degë
  // e pemës, që nyjet e reja të zbuluara mos të dalin jashtë ekranit. Të dyja
  // (aktiv/joaktiv + sa shumë) të rregullueshme nga Admin Center → Cilësimet.
  const [expandZoomOut, setExpandZoomOut] = useState({ enabled: true, factor: 0.85 });
  useEffect(() => {
    let cancelled = false;
    fetchSettingRpcWithRetry("rpc_get_expand_zoom_out").then((data) => {
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row) setExpandZoomOut({ enabled: row.enabled, factor: row.factor });
    });
    return () => { cancelled = true; };
  }, []);
  // Kërkesë e userit — variant i lundrimit poshtë kur je te Pema
  // ("classic" = si sot: 4 tab-e; "swap" = buton qendror që ndërron mes
  // klasik/mjete, ish-"sheet"; "fab" = speed-dial mbi ikonën Pema;
  // "contextual" = rresht mjetesh, me buton kthimi te klasik; "drawer" =
  // gjuhëz anësore me ikona; "hybrid" = klasik + buton "+" mbi ikonën
  // Pema). E rregullueshme nga Admin Center → Cilësimet (shih
  // rpc_get_tree_nav_variant / rpc_platform_set_tree_nav_variant).
  const [treeNavVariantPhone, setTreeNavVariant] = useState("classic");
  // PAMJA1 — SHIRITI I TELEFONIT NUK I TAKON DESKTOPIT.
  //
  // Pronari pa dy shirita te pema në desktop. Shkaku ishte i vogël dhe i saktë:
  // nga gjashtë variantet, VETËM `classic` mbante `lg:hidden`. `drawer`, `sheet`,
  // `fab`, `hybrid` dhe `contextual` s'e mbanin — pra dilnin edhe te desktopi,
  // mbi mjetet që desktopi i ka tashmë të vetat.
  //
  // Rregullimi NUK bëhet duke shtuar `lg:hidden` në pesë vende: pesë vende janë
  // pesë shanse për ta harruar të gjashtin nesër (rregulli 25). Bëhet me NJË
  // vlerë të derivuar — `treeNavVariant` — që të gjitha blloqet e krahasojnë
  // tashmë. Nëse desktopi s'ka variant, ajo del `"none"` dhe çdo krahasim bie
  // vetvetiu, sot dhe për çdo variant që shtohet më vonë.
  // Shiriti i sipërm si element — që tërheqja për rifreskim ta njohë atë edhe
  // kur je te pema, ku gjesti nuk lejohet mbi kanavacë.
  const headerBarRef = useRef(null);
  const [treeNavVariantDesktop, setTreeNavVariantDesktop] = useState(null);
  const [treeBarConfigDesktop, setTreeBarConfigDesktop] = useState(null);
  const [isWideScreen, setIsWideScreen] = useState(() => {
    try { return window.matchMedia("(min-width: 1024px)").matches; } catch { return false; }
  });
  // 1024px = pika `lg` e Tailwind-it, e njëjta që fsheh shiritin klasik. Dy
  // pragje të ndryshme do të linin një brez ekranesh me të dyja ose me asnjë.
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(min-width: 1024px)"); } catch { return; }
    const on = (e) => setIsWideScreen(e.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  // PEMA2 (29 gusht 2026) — TE DESKTOPI VETËM DY VARIANTE VIZATOHEN VËRTET.
  //
  // `sheet`, `fab`, `hybrid` dhe `contextual` jetojnë të gjitha brenda shiritit
  // të poshtëm, i cili është `lg:hidden` — pra te desktopi zgjidheshin, ruheshin,
  // dhe nuk shfaqej asgjë. Pronari e pa saktë: «shumica e shiritave lundrues nuk
  // funksionojnë». Nuk ishin të prishur; s'ishin fare aty.
  //
  // Ata janë gjeste telefoni — fletë nga poshtë, buton qendror, rresht që del me
  // prekje. Te një ekran me sidebar fiks nuk kanë kuptim. Prandaj kufizohen këtu,
  // dhe zgjedhja bie te `none`: shiriti i sipërm dhe kolona e zoom-it mbeten,
  // dhe gjithçka punon. Më mirë një zgjedhje që s'zbatohet sesa një që zbatohet
  // përgjysmë.
  const VARIANTET_E_DESKTOPIT = ["classic", "drawer", "none"];
  const treeNavVariant = isWideScreen
    ? (VARIANTET_E_DESKTOPIT.includes(treeNavVariantDesktop) ? treeNavVariantDesktop : "none")
    : treeNavVariantPhone;
  // SHTESË (kërkesë e userit — "përdoruesit të mund ta zgjedhin menynë që u
  // pëlqen"): parazgjedhja vjen nga Admin Center (adminNavVariant), por çdo
  // përdorues mund ta mbishkruajë PËR VETE te ⚙️ Cilësimet e pamjes — ruhet
  // në pajisje (localStorage), jo në server.
  const [adminNavVariant, setAdminNavVariant] = useState("classic");
  const [userNavVariant, setUserNavVariant] = useState(() => { try { return localStorage.getItem("pef_tree_nav_variant") || ""; } catch { return ""; } });
  const chooseNavVariant = (val) => {
    try { if (val) localStorage.setItem("pef_tree_nav_variant", val); else localStorage.removeItem("pef_tree_nav_variant"); } catch { /* private mode */ }
    setUserNavVariant(val);
  };
  // SHTESË (kërkesë e userit — "swap" duhet ikonë të zgjedhshme) — cila
  // ikonë përdoret te butoni qendror i variantit "swap": "refresh"
  // (RefreshCw), "repeat" (Repeat — dy shigjeta ovale rreth njëra-tjetrës,
  // pikërisht siç e përshkroi useri), "arrows" (ArrowLeftRight), "shuffle".
  const [treeSwapIcon, setTreeSwapIcon] = useState("repeat");
  // SHTESË (kërkesë e userit) — shiriti i konfigurueshëm: cilat ikona duken,
  // renditja, orientimi (horizontal/vertikal) dhe pozicioni (lart/poshtë/
  // majtas/djathtas). Vlen "on top" për ÇDO variant — të gjithë marrin
  // ikonat nga i njëjti burim (renderTreeBarIcons).
  const [treeBarConfigPhone, setTreeBarConfig] = useState(TREEBAR_DEFAULT);
  // Bosh te desktopi = ndjek telefonin. Vetëm `variant` e vendos NËSE ka shirit;
  // konfigurimi thotë vetëm SI duket, dhe një kopje e dytë e tij pa nevojë do të
  // ndahej nga origjinali brenda muajit.
  const treeBarConfig = (isWideScreen && treeBarConfigDesktop)
    ? { ...treeBarConfigPhone, ...treeBarConfigDesktop }
    : treeBarConfigPhone;
  // PEMA2 — «kur ta zgjedh një tjetër, të zhduken mbetjet e së vjetrës».
  //
  // Te desktopi shiriti klasik i mban vetë kërkimin, pamjen, zoom-in dhe shtimin.
  // Kontrollet e vjetra rrinin poshtë tij, pra i njëjti veprim kishte dy butona
  // në të njëjtin ekran — dhe njëri prej tyre gjysmë i mbuluar.
  //
  // Fshehja bëhet VETËM kur shiriti i përmban vërtet ato vegla. Ikonat e tij
  // zgjidhen te Admin Center; po t'i fshihja kontrollet pa e kontrolluar këtë,
  // një ikonë e hequr atje do të zhdukte veprimin nga i gjithë ekrani.
  function shiritiIPemesKa(...celesat) {
    if (!isWideScreen || treeNavVariant !== "classic") return false;
    const ikonat = (treeBarConfig.items || TREEBAR_DEFAULT.items)
      .filter((it) => it && it.on !== false).map((it) => it.key);
    return celesat.every((k) => ikonat.includes(k));
  }
  const [treeBarPanel, setTreeBarPanel] = useState(null); // "search" | "view" — panele FIKSE (kurrë brenda kolonash me overflow)
  // Preferenca e përdoruesit fiton mbi parazgjedhjen e platformës.
  // Zgjedhja e përdoruesit vlen për TELEFONIN — aty ai e ndien ndryshimin dhe aty
  // e bëri zgjedhjen. Desktopi mbetet siç e ka vendosur Admin Center-i.
  useEffect(() => { setTreeNavVariant(userNavVariant || adminNavVariant); }, [userNavVariant, adminNavVariant]);
  useEffect(() => {
    let cancelled = false;
    fetchSettingRpcWithRetry("rpc_get_tree_nav_variant").then((data) => {
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.variant) { setAdminNavVariant(row.variant); setTreeNavVariant(row.variant); }
      if (row?.swap_icon) setTreeSwapIcon(row.swap_icon);
      if (row?.bar_config && typeof row.bar_config === "object" && Object.keys(row.bar_config).length) {
        setTreeBarConfig((d) => ({ ...d, ...row.bar_config }));
      }
      // PAMJA1 — bosh do të thotë "desktopi mban mjetet e veta lart", dhe kjo
      // është parazgjedhja. Një kolonë e re s'duhet të ndezë asgjë vetvetiu.
      setTreeNavVariantDesktop(row?.variant_desktop || null);
      setTreeBarConfigDesktop(
        row?.bar_config_desktop && typeof row.bar_config_desktop === "object"
          && Object.keys(row.bar_config_desktop).length ? row.bar_config_desktop : null);
    });
    return () => { cancelled = true; };
  }, []);
  // SHTESË (kërkesë e userit — bisedë e gjatë rreth variantet e navigimit) —
  // rindërtim i plotë i disa prej variantave, pas provës reale:
  // - "sheet" u zëvendësua tërësisht me "swap": buton QENDROR (mbi bar-in,
  //   në mes) që ndërron mes bar-it klasik dhe rreshtit të mjeteve —
  //   ikona e vetë butonit zgjidhet nga Admin Center (shih treeSwapIcon).
  // - "hybrid"/"contextual" tani ndajnë të njëjtin mekanizëm togglimi
  //   (bar klasik ⇄ rresht mjetesh), duke ndryshuar VETËM gjendjen
  //   fillestare kur hyn te Pema: hybrid → fillon klasik; contextual →
  //   fillon me mjetet e hapura. Që të mos ngatërrohen, mbahen si dy
  //   ndryshore më vete (jo e njëjta, me parazgjedhje të ndryshme).
  // - "drawer"/"fab" tani e MBAJNË GJITHMONË bar-in klasik të plotë (4
  //   tab-e) — vetëm shtojnë mjetet e Pemës si shtresë E DYTË (buton "+"
  //   mbi ikonën Pema, ose gjuhëza anësore), jo më zëvendësim të bar-it.
  const [treeSwapOpen, setTreeSwapOpen] = useState(false); // "swap" (ish-"sheet")
  const [treeFabOpen, setTreeFabOpen] = useState(false); // "fab" — tani vetëm speed-dial, bar-i mbetet klasik
  const [treeDrawerOpen, setTreeDrawerOpen] = useState(false); // "drawer" — tani vetëm rresht ikonash, jo faqe e madhe
  const [treeHybridOpen, setTreeHybridOpen] = useState(false); // "hybrid" — fillon MBYLLUR (klasik)
  const [treeContextualCollapsed, setTreeContextualCollapsed] = useState(false); // "contextual" — fillon E HAPUR (mjetet), butoni i ri e MBYLL (klasik)
  const drawerTouchStartX = useRef(null);
  const handleTouchX = useRef(null); // gjesti mbi vetë gjuhëzën (i izoluar nga back-u i faqes)
  // Ngjyra e gjuhëzës anësore = ngjyra e VIJAVE që lidhin personat në pemë.
  // ⚠ 4 shtator 2026 — RREGULLI I PËRPARËSISË, i shkruar pasi e provova
  // gabim një herë:
  //
  //     DIZAJNI vendos NGJYRAT.  ADMINI vendos GJEOMETRINË.
  //
  // Provova ta bëja adminin zot mbi gjithçka, sepse pronari vuri re me të
  // drejtë se rrëshqitësit nuk vepronin. Po ai kufi ishte i gabuar: sapo u
  // ndez, ngjyrat e heirloom-it u zëvendësuan nga ato të temës së mëparshme
  // — ngjyrat e degëve dhe të vijave. Pronari: «ky rregullimi i yt ndreqi
  // diçka por prishi diçka tjetër».
  //
  // Arsyeja pse ndarja është e drejtë: një temë ËSHTË paleta e vet. Nëse
  // cilësimi i vjetër i ngjyrës e mbivendos temën e re, atëherë tema nuk
  // ndryshon asgjë — dhe pikërisht kjo ndodhi. Përmasat, hapësirat dhe
  // stilet, përkundrazi, s'i takojnë temës: ato janë zgjedhje e familjes.
  // 4 shtator 2026 — dhe vendimi përfundimtar i pronarit: NUK e zgjedh unë.
  // «du që të mund ta vendos useri se cilën output ngjyrash në pemë e do —
  // atë që ndjek linjën familjare, apo të templatit siç ishte.»
  // Prandaj ky është një ÇELËS, jo një rregull i imi. Vlera vjen nga
  // cilësimet e pemës (`tree_color_source`); pa të, mbetet «dizajni» —
  // pra saktësisht sjellja që kishte para se ta prekja.
  // Tri gjendje, një burim i vetëm i së vërtetës:
  //   "dizajni" → ngjyrat i jep tema
  //   "familja" → ngjyrat i jep pozicioni në pemë (Rregullat & Ngjyrat)
  //   "asnje"   → pa ngjyra: një ton i vetëm, i qetë
  // Zgjedhja e përdoruesit fiton; pa të, vlen ajo e platformës.
  const burimiEfektiv = () => userBurimiNgjyrave || burimiINgjyrave || "dizajni";
  const paNgjyra = () => burimiEfektiv() === "asnje";
  const ngjyratENdjekinDizajnin = () => {
    if (burimiEfektiv() !== "dizajni") return false;
    try { const d = getDesign(); return !!(d && d.id); } catch (_e) { return false; }
  };
  // ⚠ 4 shtator 2026 — «pa ngjyra» dukej njësoj si «ngjyrat e temës».
  // Shkaku ishte i imi dhe elementar: si ton neutral kisha marrë `goldSoft`,
  // që te kjo temë NUK është gri — është ar i zbutur. Pra «pa ngjyra» jepte
  // pikërisht ngjyrën e temës, vetëm më të zbehtë.
  // Neutral do të thotë PA NUANCË: merret `muted`/`text2` e paletës — toni i
  // shkrimit dytësor — që ndryshon vetë mes temës së çelët dhe të errët, po
  // s'ka asnjë lidhje me arin.
  const toniNeutral = () => toneGri(PALETA_TIPI.text || COLORS.ink || "#3B2F26",
                                    PALETA_TIPI.bg || COLORS.bg || "#FBF7EF");
  const toniNeutralISoft = () => PALETA_TIPI.surface2 || COLORS.panelSoft || COLORS.panel;
  // 4 shtator 2026 — vendim i pronarit pas krahasimit me sy: «vijat mbajnë
  // tonin e temës». Vijat nuk kodojnë asgjë (ngjyra e degës rri te njerëzit,
  // vija ka një ngjyrë të vetme për të gjitha), prandaj gri apo ar nuk ndryshon
  // asnjë kuptim — po gri i ftohtë mbi letër të ngrohtë e bën pemën të duket
  // skicë e papërfunduar. «Pa ngjyra» mbetet e vërtetë aty ku ka rëndësi:
  // askush nuk ngjyroset më sipas degës.
  const ngjyraEVijes = () => (ngjyratENdjekinDizajnin() || paNgjyra() ? COLORS.gold : (treeAdvLayout.line_color || COLORS.gold));
  const ngjyraEVijesSeCiftit = () => (ngjyratENdjekinDizajnin() || paNgjyra() ? COLORS.spouseLine : (treeAdvLayout.spouse_line_color || COLORS.spouseLine));
  const treeHandleColor = ngjyraEVijes();
  // Kur largohesh nga "Pema" (Ballina/Kujtime/Statistika), rikthe çdo
  // variant te gjendja e vet FILLESTARE — përndryshe kur kthehesh te Pema
  // do të gjeje diçka "të hapur"/"të mbyllur" gabimisht nga hera e fundit.
  useEffect(() => {
    if (activeScreen !== "tree") {
      setTreeSwapOpen(false);
      setTreeFabOpen(false);
      setTreeDrawerOpen(false);
      setTreeHybridOpen(false);
      setTreeContextualCollapsed(false);
    }
  }, [activeScreen]);
  const dragging = useRef(null);
  const focusDraggingRef = useRef(false);
  const pinch = useRef(null);
  const containerRef = useRef(null);


  // PARAZGJEDHJE: "trung" — personi nis te trungu i vet, jo e gjithë pema (atë
  // e sheh VETËM kur e zgjedh vetë). Trungu i ANËS SË GJAKUT dhe i PAVARUR nga
  // renditja e prindërve (shih climbBloodParent + effectiveBranch).
  const [viewMode, setViewMode] = useState("trunk");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [viewAnchor, setViewAnchor] = useState(null);
  const [showCore, setShowCore] = useState(true);
  const [showMarriedIn, setShowMarriedIn] = useState(true);
  const [showCloseCousin, setShowCloseCousin] = useState(true);
  const [showMarriedOut, setShowMarriedOut] = useState(true);
  const [showExtended, setShowExtended] = useState(true);
  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const headerControlsRef = useRef(null);
  const sidebarMenuRef = useRef(null);
  const treeControlsRef = useRef(null);
  const treeBottomBarRef = useRef(null);
  const treeDrawerRef = useRef(null);
  useEffect(() => {
    const handleOutside = (e) => {
      // Prekjet BRENDA një Modal-i (menyja e avatarit në telefon, ose çdo panel
      // që hapet mbi të) NUK duhet t'i mbyllin dropdown-et poshtë. Pa këtë,
      // kur hapej një panel mbi menynë, prekja e parë brenda panelit e mbyllte
      // menynë — dhe back-i të çonte te pema, jo te menyja. Modal-et e menaxhojnë
      // vetë mbylljen; ky dëgjues vlen VETËM për prekjet mbi pemën/desktop.
      if (e.target && e.target.closest && e.target.closest(".modal-panel")) return;
      const insideHeader = headerControlsRef.current && headerControlsRef.current.contains(e.target);
      const insideSidebarMenu = sidebarMenuRef.current && sidebarMenuRef.current.contains(e.target);
      const insideTreeControls = treeControlsRef.current && treeControlsRef.current.contains(e.target);
      const insideTreeBottomBar = treeBottomBarRef.current && treeBottomBarRef.current.contains(e.target);
      const insideTreeDrawer = treeDrawerRef.current && treeDrawerRef.current.contains(e.target);
      if (!insideHeader && !insideSidebarMenu && !insideTreeControls && !insideTreeBottomBar && !insideTreeDrawer) {
        setAvatarMenuOpen(false);
        setThemePanelOpen(false);
        setViewPanelOpen(false);
        setSearchOpen(false);
        setSearchExpanded(false);
        setTreeFabOpen(false);
        setTreeDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2800); };
  // ---- FSHIRJA E LLOGARISË (kërkesë e userit) ----------------------------
  // Nyja në pemë MBETET; hiqet vetëm ajo që e bën "llogari" (lidhja, kontakti,
  // trashëgimia, abonimet, vetë hyrja). Admini i fundit i një familjeje
  // bllokohet nga serveri. Konfirmimi këtu: fjala "FSHIJ" GJITHMONË, plus
  //   • llogari me fjalëkalim  → fjalëkalimi (verifikohet me rihyrje),
  //   • Google/Apple (pa fjalëkalim) → shkruaj email-in e saktë të llogarisë.
  // ---- QASJA E MYSAFIRIT (admini i familjes) ---------------------------
  // Rregullat ruhen te guest_view_rules / guest_field_rules; RLS-ja i lejon
  // adminit të SË NJËJTËS familje (shih GUEST_view_scope.sql).
  // KATALOGU I MYSAFIRIT — një burim i vetëm (19 gusht 2026).
  //
  // Këto lista ishin të ngulitura NË DY VENDE: këtu dhe te `GuestAccessTab.jsx`
  // i Admin Center-it. Kishin rrëshqitur në të dy drejtimet — `include_spouses`
  // ekzistonte vetëm atje, zonat dhe trashëgimia vetëm këtu. Dy ndërfaqe që
  // vendosin rregulla SIGURIE me lista të ndryshme opsionesh nuk janë çështje
  // rehatie: admini beson se e mbylli diçka nga një derë, kur dera tjetër e la
  // hapur. Tani lista vjen nga baza (`rpc_guest_catalog`), e njëjta për të dyja.
  // Vlerat më poshtë mbeten VETËM si rrjetë sigurie nëse migrimi s'është aplikuar.
  const GUEST_FALLBACK = {
    scopes: [["person", "Vetëm ky person"], ["parents", "Prindërit e tij"], ["children", "Fëmijët e tij"], ["ancestors", "Paraardhësit (lart)"], ["descendants", "Pasardhësit (poshtë)"], ["subtree", "Ai + gjithë pasardhësit"], ["family", "E gjithë familja"]],
    fields: [["first_name", "Emri"], ["last_name", "Mbiemri"], ["photo", "Fotoja"], ["gender", "Gjinia"], ["birth_date", "Datëlindja"], ["death_date", "Data e vdekjes"], ["birth_place", "Vendlindja"], ["bio", "Biografia"], ["contact", "Kontakti"], ["legacy", "Mesazhet e trashëgimisë"]],
    areas: [["memories", "Kujtime — Ngjarje"], ["archive", "Kujtime — Skedarë"]],
    defaults: ["first_name", "last_name", "photo", "gender", "birth_date", "death_date", "birth_place", "bio"],
  };
  const [guestCatalog, setGuestCatalog] = useState(GUEST_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc("rpc_guest_catalog").then(({ data }) => {
      if (cancelled || !Array.isArray(data) || data.length === 0) return;
      const pick = (k) => data.filter((r) => r.kind === k).map((r) => [r.key, r.label]);
      setGuestCatalog({
        scopes: pick("scope"), fields: pick("field"), areas: pick("area"),
        defaults: data.filter((r) => r.kind === "field" && r.is_default).map((r) => r.key),
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const GUEST_FIELDS = guestCatalog.fields;
  const GUEST_DEFAULT_FIELDS = guestCatalog.defaults;
  const GUEST_SCOPES = guestCatalog.scopes;
  const [guestAccessFor, setGuestAccessFor] = useState(null);
  const [guestRules, setGuestRules] = useState([]);
  const [guestFields, setGuestFields] = useState(null);   // rreshti i përgjithshëm ose null
  // ZONAT E FAMILJES (19 gusht 2026) — Kujtimet dhe Arkivi nuk janë fusha të një
  // personi, janë zona të tëra. Ruhen te I NJËJTI rresht i përgjithshëm
  // (`person_id is null`), si çelësa më vete brenda `fields` — pa tabelë të dytë
  // lejeje që mund të dalë nga sinkronizimi me të parën. Pa çelës → nuk sheh.
  const GUEST_AREAS = guestCatalog.areas;
  // Rregullat PËR PERSON (person_id jo null). Sot përdoren vetëm për
  // trashëgiminë: edhe kur mysafirit i lejohet personi te pema, mesazhet e
  // vulosura mbeten të mbyllura derisa të lejohen shprehimisht për atë person.
  const [guestPersonRules, setGuestPersonRules] = useState([]);
  const [guestLegacyDraft, setGuestLegacyDraft] = useState("");
  // ARSYEJA DHE DRITARJA E AKSESIT. `rpc_set_honorary` u shkrua shprehimisht
  // "edhe për redaktime", por asnjë buton s'e thërriste kurrë pas krijimit —
  // pra arsyeja e një mysafiri s'ndryshohej dot më. Tani redaktohet këtu.
  const [guestMeta, setGuestMeta] = useState({ reason: "", start: "", end: "" });
  const saveGuestMeta = async () => {
    const gid = guestAccessFor; if (!gid) return;
    if (!guestMeta.reason.trim()) { showToast(t("An Honorary member requires a reason")); return; }
    setGuestBusy(true);
    const { error } = await supabase.rpc("rpc_set_honorary", {
      p_person_id: gid, p_reason: guestMeta.reason.trim(),
      p_access_start: guestMeta.start || null, p_access_end: guestMeta.end || null,
      p_requester_id: currentPersonId,
    });
    setGuestBusy(false);
    if (error) { showToast(error.message); return; }
    await fetchPeople();
    showToast(t("toast.savedPeriod"));
  };
  const [guestPreview, setGuestPreview] = useState(null);
  const [guestDraft, setGuestDraft] = useState({ effect: "allow", scope: "person", target_person_ids: [], depth: "", include_spouses: true });
  const [guestBusy, setGuestBusy] = useState(false);
  const loadGuestAccess = async (gid) => {
    const [{ data: r }, { data: f }, { data: pv }, { data: pr }] = await Promise.all([
      supabase.from("guest_view_rules").select("*").eq("guest_person_id", gid).order("created_at"),
      supabase.from("guest_field_rules").select("*").eq("guest_person_id", gid).is("person_id", null).maybeSingle(),
      supabase.rpc("rpc_guest_preview", { p_guest: gid }),
      supabase.from("guest_field_rules").select("*").eq("guest_person_id", gid).not("person_id", "is", null),
    ]);
    setGuestRules(r || []);
    setGuestFields(f || null);
    setGuestPreview(pv || []);
    setGuestPersonRules(pr || []);
  };
  const openGuestAccess = async (gid) => {
    setGuestAccessFor(gid);
    setGuestDraft({ effect: "allow", scope: "person", target_person_ids: [gid], depth: "", include_spouses: true });
    const g = people[gid] || {};
    setGuestMeta({
      reason: g.honorary_reason || "",
      start: g.honorary_access_start || "",
      end: g.honorary_access_end || "",
    });
    await loadGuestAccess(gid);
  };
  // "Zgjedhje e shpejtë" u hoq (19 gusht 2026). Ato pesë butonat i FSHININ të
  // gjitha rregullat ekzistuese dhe vendosnin një të vetëm — edhe pse "+" linte
  // të kuptohej se shtonin mbi ato që kishe. Pra ishin njëkohësisht të pasakta
  // dhe të rrezikshme. Katër prej tyre janë tani një plotësim formulari larg
  // (formulari pranon disa persona njëherësh), kurse i pesti — kthimi te "sheh
  // vetëm veten" — është e vetmja gjë që formulari s'e bën dot, prandaj mbetet
  // si veprim më vete, me konfirmim.
  const guestResetRules = async () => {
    const gid = guestAccessFor; if (!gid) return;
    if (!window.confirm(t("Remove all rules? The guest will see only themselves."))) return;
    setGuestBusy(true);
    const { error } = await supabase.from("guest_view_rules").delete().eq("guest_person_id", gid);
    setGuestBusy(false);
    if (error) { showToast(error.message); return; }
    await loadGuestAccess(gid);
  };
  const addGuestRule = async () => {
    const gid = guestAccessFor; if (!gid) return;
    const targets = guestDraft.scope === "family" ? [null] : (guestDraft.target_person_ids || []);
    if (guestDraft.scope !== "family" && targets.length === 0) { showToast(t("Choose a person first.")); return; }
    setGuestBusy(true);
    const rows = targets.map((pid) => ({
      guest_person_id: gid, effect: guestDraft.effect, scope: guestDraft.scope,
      target_person_id: pid,
      depth: guestDraft.depth === "" ? null : Number(guestDraft.depth),
      // Barazi me Admin Center: më parë ky çelës mungonte këtu, pra rregullat e
      // krijuara nga aplikacioni merrnin heshtazi parazgjedhjen e bazës dhe
      // dukeshin ndryshe nga ato të krijuara te Admin Center.
      include_spouses: !!guestDraft.include_spouses,
    }));
    const { error } = await supabase.from("guest_view_rules").insert(rows);
    setGuestBusy(false);
    if (error) { showToast(error.message); return; }
    setGuestDraft({ ...guestDraft, target_person_ids: [] });
    await loadGuestAccess(gid);
  };
  const delGuestRule = async (id) => {
    // KY ËSHTË PIKËRISHT RASTI QË KOMENTI POSHTË PËRSHKRUAN, dhe që mbeti i
    // pandrequr: shkrim i drejtpërdrejtë te tabela, pra përmes politikës RLS.
    //
    // Këtu `error` i vetëm nuk mjafton. Një `delete` që RLS-ja e filtron NUK
    // kthen gabim — kthen zero rreshta të prekur, dhe kjo lexohet si sukses.
    // Prandaj kërkohet `.select()`: nëse s'u fshi asnjë rresht, atëherë ose
    // rregulli s'ekziston më, ose ne s'kemi të drejtë ta fshijmë — dhe të dyja
    // duhen thënë, jo mbuluar me një rifreskim që tregon të njëjtën listë.
    const { data, error } = await supabase
      .from("guest_view_rules").delete().eq("id", id).select("id");
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    if (!data || data.length === 0) {
      showToast(t("Nothing was deleted — you may not have permission for this rule."));
      await loadGuestAccess(guestAccessFor);
      return;
    }
    await loadGuestAccess(guestAccessFor);
  };
  // FIX (19 gusht 2026) — më parë kjo shkruante DREJTPËRDREJT te tabela, pra
  // kalonte nga politika RLS. Kur ndonjë hallkë e asaj politike s'përputhej,
  // shkrimi nuk hidhej poshtë me gabim: RLS-ja thjesht s'shkruante asnjë rresht,
  // ndërfaqja rilexonte të njëjtën gjendje, dhe klikimi dukej sikur "s'bënte
  // asgjë" — pikërisht ankesa për "Kontakti" dhe zonat e Kujtimeve. Tani kalon
  // nga i njëjti funksion `security definer` si mbivendosjet për person: leja
  // kontrollohet shprehimisht dhe një refuzim vjen si gabim i dukshëm.
  const toggleGuestField = async (fieldKey, on) => {
    const gid = guestAccessFor; if (!gid) return;
    const cur = guestFields ? guestFields.fields : GUEST_DEFAULT_FIELDS;
    const next = on ? Array.from(new Set([...cur, fieldKey])) : cur.filter((x) => x !== fieldKey);
    setGuestBusy(true);
    const { error } = await supabase.rpc("rpc_set_guest_person_fields", {
      p_guest_person_id: gid, p_person_id: null, p_fields: next, p_requester_id: currentPersonId,
    });
    setGuestBusy(false);
    if (error) { showToast(error.message); return; }
    await loadGuestAccess(gid);
  };
  // RREGULL PËR NJË PERSON — çdo fushë e katalogut, përfshi `legacy`.
  //
  // Më parë kjo ishte vetëm për trashëgiminë; Admin Center-i ndërkohë kishte
  // një redaktor të plotë fushash për person. Tani të dyja ndërfaqet kanë të
  // njëjtin model: rregull i përgjithshëm + mbivendosje për person.
  //
  // Kalon nga `rpc_set_guest_person_fields`, jo nga shkrimi i drejtpërdrejtë:
  // politika e tabelës lejon vetëm adminin, kurse serveri aty lejon edhe vetë
  // personin dhe redaktorin brenda fushëveprimit — pra ata që e kanë vetë
  // aksesin te të dhënat që po japin.
  const setGuestPersonField = async (personId, fieldKey, on) => {
    const gid = guestAccessFor; if (!gid || !personId) return;
    const cur = guestPersonRules.find((x) => x.person_id === personId);
    const base = cur ? cur.fields || [] : [];
    const next = on ? Array.from(new Set([...base, fieldKey])) : base.filter((x) => x !== fieldKey);
    setGuestBusy(true);
    const { error } = await supabase.rpc("rpc_set_guest_person_fields", {
      p_guest_person_id: gid, p_person_id: personId, p_fields: next, p_requester_id: currentPersonId,
    });
    setGuestBusy(false);
    if (error) { showToast(error.message); return; }
    await loadGuestAccess(gid);
  };
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [delWord, setDelWord] = useState("");
  const [delSecret, setDelSecret] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delInfo, setDelInfo] = useState(null); // {blocking_families:[...]}
  const authProviders = (() => {
    const u = authSession?.user;
    const ids = (u?.identities || []).map((i) => i.provider);
    if (ids.length) return ids;
    return u?.app_metadata?.provider ? [u.app_metadata.provider] : [];
  })();
  const hasPasswordLogin = authProviders.includes("email") || authProviders.length === 0;
  const openDeleteAccount = async () => {
    setDelWord(""); setDelSecret(""); setDelInfo(null);
    setDeleteAccountOpen(true);
    try {
      const { data } = await supabase.rpc("rpc_my_account_delete_check");
      const row = Array.isArray(data) ? data[0] : data;
      if (row) setDelInfo(row);
    } catch (_e) { /* paralajmërimi është opsional */ }
  };
  const runDeleteAccount = async () => {
    const email = (authSession?.user?.email || "").trim().toLowerCase();
    if (delWord.trim().toUpperCase() !== "FSHIJ") { showToast(t('Type FSHIJ to confirm.')); return; }
    setDelBusy(true);
    try {
      if (hasPasswordLogin) {
        const { error: pErr } = await supabase.auth.signInWithPassword({ email, password: delSecret });
        if (pErr) { showToast(t("The password is not correct.")); return; }
      } else if (delSecret.trim().toLowerCase() !== email) {
        showToast(t("Type your account email exactly.")); return;
      }
      const { error } = await supabase.rpc("rpc_delete_my_account");
      shenoHyrjen("account_delete", { email, ok: !error, reason: error ? error.message : null }); // HYRJET1 — para se të ikë sesioni
      if (error) { showToast(error.message); return; }
      setDeleteAccountOpen(false);
      try { await supabase.auth.signOut(); } catch (_e) { /* llogaria s'ekziston më */ }
      try { localStorage.clear(); sessionStorage.clear(); } catch (_e) { /* asgjë */ }
      window.location.href = "/";
    } finally { setDelBusy(false); }
  };
  // FIX (raportuar nga useri — "back te Krijo familje / Bashkohu me kod të
  // çon te faqja kryesore, jo te faqja paraprake"): shkaku i vërtetë NUK ishte
  // dyfishimi i gjestit. `/join` dhe `/start` janë NGARKIME TË PLOTA faqeje,
  // ndërsa dritaret e app-it (Qendra e familjeve) jetojnë si hyrje historie të
  // TË NJËJTIT dokument. Një hap back nga `/join` e ringarkon "/" nga e para —
  // pra app-i niset i pastër te ballina, dhe dritarja ku ishe humbet.
  // Zgjidhja: para se të dalim, ruajmë shtegun DHE dritaren për t'u rihapur;
  // faqja e jashtme kthehet pikërisht aty, dhe app-i e rihap atë dritare.
  const leaveToPage = (url, reopen) => {
    try {
      sessionStorage.setItem("pef_return", JSON.stringify({ path: window.location.pathname + window.location.search, reopen: reopen || "" }));
    } catch (_e) { /* private mode — thjesht s'ka rikthim */ }
    window.location.href = url;
  };

  // FIX (RRJEDHJE MES FAMILJESH — serioze) — më parë këto DY funksione
  // s'kishin ASNJË filtër family_id, mbështeteshin PLOTËSISHT te RLS. Kjo
  // dukej e sigurt (RLS "family read people" lejon vetëm family_id brenda
  // my_family_ids()) — POR my_family_ids() kthen ÇDO familje ku LLOGARIA
  // ka person të lidhur, PA DIJENI se cilën familje po shikon FRONTEND-i
  // TANI (aktivizmi_familjes/URL-ja është koncept VETËM i klientit, RLS-ja
  // s'e sheh fare). Pra dikush i lidhur me DY familje (p.sh. Krasniqi + Gashi)
  // duke shikuar Krasniqin merrte MBRAPSHT persona/role NGA TË DYJA familjet
  // të përziera në të njëjtën pemë/feed — pikërisht defekti i raportuar.
  // Rregullimi: filtër EKSPLICIT family_id, jo më vetëm mbështetje te RLS.
  const fetchPeople = async (familyIdOverride) => {
    // Mos e mbishkruaj grupin e bashkuar kur je në modalitetin dy-familjesh.
    if (twoFamilyRef.current) return people;
    const fid = familyIdOverride || activeFamilyId;
    const { data, error } = await supabase.from("people").select("*").eq("family_id", fid).is("deleted_at", null).order("created_at", { ascending: true });
    // CACHE1 — pa rrjet: mos e zbraz pemën. Lexo fotografinë e fundit nga
    // telefoni; nëse s'ka, lëre gjendjen siç është.
    if (error && eshteGabimRrjeti(error)) {
      setJeOffline(true);
      const c = await cacheLexo(`fam:${fid}:people`);
      if (c && c.map) { setPeople(c.map); setKujdestaret(c.kujd || {}); return c.map; }
      return people;
    }
    // SIGURIA2 (3 nëntor 2026) — NJË GABIM, ASNJË GJENDJE. Më parë vetëm gabimi
    // «s'ka rrjet» kapej më sipër; çdo gabim tjetër (JWT i skaduar, 5xx nga
    // Cloudflare/PostgREST, përgjigje HTML) binte poshtë si `rows = data || []`
    // = «familja është bosh» → pema zbrazej, DHE fotografia offline (CACHE1)
    // mbishkruhej me një hartë bosh — pra nisja e radhës pa internet tregonte
    // pemë bosh. Tani gabimi i çfarëdo lloji e lë gjendjen dhe cache-in siç
    // janë; rifreskimi i radhës (realtime, kthimi nga sfondi) provon sërish.
    if (error) {
      console.warn("fetchPeople:", error.message || error);
      return people;
    }
    let rows = data || [];
    // MYSAFIRËT (member_kind='mysafir'): RLS-ja e re ua mbyll leximin e
    // drejtpërdrejtë të `people` (shih GUEST_view_scope.sql) — e vetmja derë
    // është rpc_guest_visible_people, e cila kthen VETËM personat e lejuar,
    // me fushat e palejuara të zbrazura që në server. Anëtarët e zakonshëm
    // s'e prekin kurrë këtë rrugë (lista e tyre s'del bosh).
    if (!rows.length) {
      const { data: g } = await supabase.rpc("rpc_guest_visible_people", { p_family_id: fid });
      if (Array.isArray(g) && g.length) rows = g;
    }
    // ── DY HARTA, JO NJË ME FILTËR ──────────────────────────────────────
    //
    // 29 gusht 2026, MUZEU1. `kujdestar` është person që ekziston vetëm që një
    // llogari të mbajë rol (`roles` çelësohet me `person_id` — pa person s'ka
    // rol). Ai NUK është figurë dhe nuk duhet parë askund përveç listës së
    // anëtarëve.
    //
    // PSE NDAHET KËTU DHE JO TE RENDERI. Ky është i vetmi vend ku personat
    // hyjnë në aplikacion. Nëse ndarja bëhet këtu, atëherë pema, trungu,
    // ngjyrat, `peopleCount`, brezat, statistikat, kërkimi, zgjedhësit e
    // prindit dhe bashkëshortit, posteri, turneu, libri dhe ditëlindjet e
    // trashëgojnë saktësinë PA E DITUR RREGULLIN — dhe pa u prekur asnjëri.
    //
    // Një filtër i vetëm te renderi do t'i linte të gjitha ato si detyra për
    // t'u mbajtur mend, dhe e njëzet-e-njëta do të shkruhej nga dikush që s'e
    // di rregullin. Kujdestari do të dilte te numri i anëtarëve, dhe një muze
    // me 12 figura do të shkruante 13.
    const map = {};
    const kujd = {};
    rows.forEach((p) => {
      if (p.member_kind === "kujdestar") kujd[p.id] = p;
      else map[p.id] = p;
    });
    setPeople(map);
    setKujdestaret(kujd);
    setJeOffline(false);
    cacheRuaj(`fam:${fid}:people`, { map, kujd });   // CACHE1
    return map;
  };
  // Hyr/dil nga posteri dy-familjesh (hourglass). RPC-ja `rpc_two_family_poster`
  // e zbaton vetë invariantin e privatësisë (vetëm anëtari i të dyjave merr të dhëna).
  const enterTwoFamily = async (bridge, modeOverride) => {
    // Roja e dytë: kartela mund të jetë e kyçur, por rruga hyn edhe nga
    // rikthimi i layout-it. Një veçori e shitur mbrohet në çdo derë.
    if (featureGated("poster_two_family")) { setActiveScreen("services"); return; }
    if (!featureAllowed("poster_two_family", true)) return;   // e fshehur nga admini
    const mode = modeOverride || twoFamLayout;
    try {
      const { data, error } = await supabase.rpc("rpc_two_family_poster", { p_bridge_id: bridge.id });
      if (error || !Array.isArray(data)) { showToast(t("Could not load the data for the two families.")); return; }
      const map = {};
      data.forEach((p) => (map[p.id] = p));
      // Familja AKTIVE (nga po printon) hyn e para → del gjithmonë MAJTAS.
      const merged = mergeBridgedFamilies(map, bridge.person_a, bridge.person_b, mode, activeFamilyId);
      twoFamilyRef.current = bridge.id;
      setTwoFamilyActive(bridge.id);
      setTwoFamBridge(bridge);
      setPosterSecOpen((prev) => { const n = new Set(prev); n.add("twofam"); return n; }); // mbaje hapur seksionin sa je në modalitet
      setPeople(merged);
      setPosterExcludeIds(new Set());
      setViewMode("full");
      // Titulli i kombinuar "Krasniqi & Orlishta" — merr emrin e familjes
      // tjetër nga `families` (i lexueshëm se je anëtar i të dyjave).
      try {
        const otherFamId = bridge.family_a === activeFamilyId ? bridge.family_b : bridge.family_a;
        const { data: famRow } = await supabase.from("families").select("name").eq("id", otherFamId).maybeSingle();
        const otherName = (famRow?.name || "").trim();
        setTwoFamName(otherName && otherName !== treeName ? `${treeName} & ${otherName}` : treeName);
      } catch { setTwoFamName(treeName); }
      showToast(t("The two-family tree is ready — choose a format and download it."));
    } catch { showToast("Gabim te posteri dy-familjesh."); }
  };
  // Ndrysho mënyrën e layout-it dy-familjesh dhe ri-aplikoje menjëherë.
  const changeTwoFamLayout = (mode) => {
    setTwoFamLayout(mode);
    if (twoFamilyActive && twoFamBridge) enterTwoFamily(twoFamBridge, mode);
  };
  const exitTwoFamily = async () => {
    twoFamilyRef.current = null;
    setTwoFamilyActive(null);
    setTwoFamRootA(null); setTwoFamRootB(null);
    setTwoFamName(null); setTwoFamTitleCustom("");
    setPosterExcludeIds(new Set());
    await fetchPeople();
    showToast("U ktheve te pema jote.");
  };
  // MUZEU1 — lloji i hapësirës vjen para çdo vizatimi. Nëse dështon (rrjet,
  // ose baza pa MUZEU1 ende), mbetet «familje» — pra sjellja e sotme, e
  // paprekur. Një veçori e re s'guxon ta prishë atë që punon.
  useEffect(() => {
    if (!activeFamilyId) return;
    let anuluar = false;
    supabase.rpc("rpc_get_lloji_hapesires", { p_family_id: activeFamilyId })
      .then(({ data }) => {
        if (anuluar || !data) return;
        const d = Array.isArray(data) ? data[0] : data;
        if (d && d.lloji) setLlojiHapesires({
          lloji: d.lloji,
          paraqitja: d.paraqitja || "peme",
          // Çelësat e kioskës mund të mungojnë nëse MUZEU5 s'është xhiruar —
          // atëherë kioska mbetet e fikur, pra sjellja e sotme e paprekur.
          kiosku: !!d.kiosku,
          kiosk_pauza: Number(d.kiosk_pauza) > 0 ? Number(d.kiosk_pauza) : 40,
          kiosk_lake: Number(d.kiosk_lake) > 0 ? Number(d.kiosk_lake) : 12,
        });
      })
      .catch(() => { /* baza pa MUZEU1 — mbetet familje */ });
    return () => { anuluar = true; };
  }, [activeFamilyId]);

  const fetchRoles = async (familyIdOverride) => {
    const fid = familyIdOverride || activeFamilyId;
    // `roles` s'ka vetë family_id (skopohet vetëm via person_id → people.
    // family_id) — përdor "embedding" e PostgREST-it (people!inner) për të
    // bashkuar dhe filtruar në një query të vetme, në vend të RLS-së së
    // vetme (i njëjti arsyetim si te fetchPeople sipër).
    const { data, error } = await supabase.from("roles").select("*, people!inner(family_id)").eq("people.family_id", fid);
    if (error && eshteGabimRrjeti(error)) {                 // CACHE1
      const c = await cacheLexo(`fam:${fid}:roles`);
      if (c && c.map) { setRoles(c.map); setRolesShkalla(c.shkalla || {}); setRolesMeDore(c.meDore || {}); return c.map; }
      return roles;
    }
    // SIGURIA2 — i njëjti rregull si te fetchPeople: një gabim jo-rrjeti nuk e
    // kthen përdoruesin në «vëzhgues» (roles = {}) dhe nuk e shkatërron cache-in.
    if (error) {
      console.warn("fetchRoles:", error.message || error);
      return roles;
    }
    const map = {};
    const shkalla = {};
    const meDore = {};
    if (data) data.forEach((r) => { map[r.person_id] = r.role; shkalla[r.person_id] = Number(r.scope_up) === 2 ? 2 : 1; meDore[r.person_id] = !!r.manual; });
    setRoles(map);
    setRolesShkalla(shkalla);
    setRolesMeDore(meDore);                                  // ROLI1
    cacheRuaj(`fam:${fid}:roles`, { map, shkalla, meDore });  // CACHE1
    return map;
  };
  const fetchSettings = async (familyId) => {
    if (!familyId) { setThemeReady(true); return; }
    const { data } = await supabase.from("settings").select("*").eq("family_id", familyId).single();
    if (data) {
      // FAZA 35 — tree_name NUK lexohet më këtu. Emri i shfaqur vjen
      // vetëm nga families.name (shih useEffect-in "zgjidh family_id"
      // më poshtë) — një burim i vetëm i së vërtetës, jo dy fusha që
      // mund të dalin nga sinkronizimi (settings.tree_name vs families.name).
      // Ky është vendimi i serverit — ai që e mbishkruan atë të pajisjes, sipas
      // modelit: zgjedhja lokale vizaton kornizën e parë, serveri vendos pastaj.
      let effective = null;
      if (data.theme_key && THEMES[data.theme_key]) effective = data.theme_key;
      // Zgjedhja personale e temës (jo-admin) e mbivendos atë të familjes —
      // vetëm në këtë pajisje. Shih `persistTheme`.
      try {
        const mine = localStorage.getItem(`pef.theme.${familyId || "default"}`);
        if (mine && THEMES[mine]) effective = mine;
      } catch (_e) { /* modaliteti privat */ }
      if (effective) {
        setThemeKey(effective);
        // Shkruaje si "e fundit e përdorur", që hapja e radhës të nisë prej saj
        // dhe të mos pulsojë asnjë temë e panjohur për këtë person.
        try { localStorage.setItem("pef.theme.last", effective); } catch (_e) { /* asgjë */ }
      }
      // `banner_enabled` nuk lexohet më — shiriti u hoq (shih shënimin te gjendja).
      if (typeof data.push_enabled === "boolean") setPushEnabled(data.push_enabled);
      if (data.crest_path) setCrestPath(data.crest_path);
      if (Array.isArray(data.countries)) setCountries(data.countries);
    }
    setThemeReady(true);
  };

  useEffect(() => {
    (async () => {
      // FAZA 6: zgjidh family_id PARA fetchSettings — nëse ka `familySlug`
      // (rruga /f/{slug}), merr id-në e vërtetë të asaj familjeje; përndryshe
      // (rruga "/", instanca parazgjedhje e sotme) përdor Krasniqin fiks.
      // Zgjidhet këtu (jo në një useEffect të veçantë) pikërisht për të
      // shmangur një race condition mes zgjidhjes së slug-ut dhe fetch-eve
      // fillestare (fetchSettings do të lexonte rreshtin e gabuar nëse do
      // të vraponin paralelisht).
      // Multi-tenant: NO family loads at "/" until login. Only a slug route
      // (/f/{slug}) loads a family up-front; the root path waits for the
      // account — see the "resolve family from account" effect below.
      if (!familySlug) { setLoading(false); return; }
      // Njësoj si te efekti poshtë: nëse ky bllok këputet në mes, `loading`
      // mbetet true përjetë dhe aplikacioni ngec te një ekran ngarkimi.
      try {
      const { data: slugRow, error: slugErr } = await supabase.rpc("rpc_resolve_family_by_slug", { p_slug: familySlug });
      let row = Array.isArray(slugRow) ? slugRow[0] : slugRow;
      // CACHE1 — pa rrjet, slug-u zgjidhet nga kujtesa e telefonit (u ruajt
      // herën e fundit që u hap me internet). Pa këtë, telefoni pa lidhje
      // shihte «familja s'u gjet» — një gënjeshtër.
      if (slugErr && eshteGabimRrjeti(slugErr)) {
        setJeOffline(true);
        row = await cacheLexo(`slug:${familySlug}`);
      } else if (row && row.id) {
        cacheRuaj(`slug:${familySlug}`, { id: row.id, name: row.name });
      }
      if (!row) {
        // SLUG1 (13 shtator 2026) — slug-u s'gjendet as te historia. Mos e lër
        // njeriun te «s'u gjet»: harroje slug-un e ruajtur dhe, nëse është i
        // identifikuar, çoje te familja e tij nga llogaria. Pa rrjet — kurrë.
        try { if (!jeOffline && localStorage.getItem("pef_family_slug") === familySlug) localStorage.removeItem("pef_family_slug"); } catch (_e) { /* modalitet privat */ }
        if (!slugErr) {
          try {
            const { data: sesioni } = await supabase.auth.getSession();
            if (sesioni && sesioni.session) {
              const { data: tetMijat } = await supabase.rpc("rpc_my_linked_people");
              const e_para = (tetMijat || []).find((r) => r.family_slug);
              if (e_para) {
                try { localStorage.setItem("pef_family_slug", e_para.family_slug); } catch (_e) { /* asgjë */ }
                window.location.replace(`/f/${e_para.family_slug}`);
                return;
              }
            }
          } catch (_e) { /* bie te ekrani «s'u gjet» */ }
        }
        setSlugFamilyStatus("not_found");
        setLoading(false);
        return;
      }
      // SLUG1 — adresa mbante slug-un e vjetër; serveri ktheu familjen me të
      // riun. Ruaje dhe ridrejto, që çdo lidhje tjetër (ftesa, kodet, poster-i)
      // të ndërtohet me adresën e saktë.
      if (row.slug && row.slug !== familySlug && !slugErr) {
        try { localStorage.setItem("pef_family_slug", row.slug); } catch (_e) { /* asgjë */ }
        window.location.replace(`/f/${row.slug}${window.location.search || ""}${window.location.hash || ""}`);
        return;
      }
      const familyId = row.id;
      setSlugFamilyName(row.name);
      if (row.name) setTreeName(row.name);
      setSlugFamilyStatus("found");
      setActiveFamilyId(familyId);
      await Promise.all([fetchPeople(familyId), fetchRoles(familyId), fetchSettings(familyId), fetchDocuments()]);
      // FAZA 3: identifikimi tani vjen nga sesioni real i Supabase Auth (shih
      // useEffect-in e ri "---------- auth (FAZA 3) ----------" më poshtë),
      // jo më nga një ID e ruajtur vetë në localStorage — Supabase Auth e
      // menaxhon vetë ruajtjen/rikthimin e sesionit.
      // FAZA 4: `people` tani është i kufizuar sipas familjes (RLS), kështu
      // që `peopleCount` (Object.keys(people).length) është 0 për këdo të
      // pa-identifikuar, edhe kur pema ka qindra anëtarë. `treeHasAnyPeople`
      // vjen nga një RPC e veçantë, e lexueshme nga të gjithë (vetëm boolean,
      // asnjë të dhënë personi) — përdoret VETËM për të dalluar "pema ka
      // njerëz, identifikohu" nga "pema është ende bosh, bëhu admini i parë".
      const { data: anyPeople } = await supabase.rpc("rpc_any_people_exist");
      setTreeHasAnyPeople(!!anyPeople);
      } catch (e) {
        console.error("zgjidhja e familjes nga slug-u:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Multi-tenant root: when there is no slug, resolve the family from the
  // logged-in account (login determines the family). Fires once authSession is
  // known (first load) and again on login-after-landing.
  useEffect(() => {
    if (familySlug || activeFamilyId || !authSession) return;
    (async () => {
      setLoading(true);
      // Çdo dalje e këtij bllloku duhet ta lërë `loading` false. Nëse një RPC
      // hidhet (rrjeti ende jo gati pas rihapjes së aplikacionit), pa këtë
      // `catch` blloku ndërpritet në mes: `loading` mbetet true përgjithmonë
      // dhe asnjë flamur s'ngrihet. Ekrani i hyrjes atëherë s'di ç'të thotë.
      try {
      try { await supabase.rpc("rpc_auto_join_my_email_matches"); } catch (e) { /* jo-kritike */ }
      let { data: linked, error: linkedErr } = await merrListenELidhjeve();
      // CACHE1 — pa rrjet: lista e familjeve të llogarisë nga kujtesa e telefonit.
      // SIGURIA2 (M2) — njësoj edhe kur serveri vazhdon të dështojë: gabimi nuk
      // është përgjigje «jo», prandaj përdoret lista e fundit e njohur.
      const uid = authSession?.user?.id || "";
      let paRrjet = false;
      if (linkedErr) {
        paRrjet = true; if (eshteGabimRrjeti(linkedErr)) setJeOffline(true);
        linked = (await cacheLexo(`acc:${uid}:linked`)) || [];
      } else if (!linkedErr) {
        cacheRuaj(`acc:${uid}:linked`, linked || []);
      }
      setMyLinkedFamilies(linked || []);
      // Familja e preferuar (nëse është caktuar DHE je ende anëtar) hapet e
      // para; përndryshe biem te e para e listës.
      let fav = null;
      if (paRrjet) fav = await cacheLexo(`acc:${uid}:fav`);
      else try { const { data: f } = await supabase.rpc("rpc_get_favorite_family"); fav = f || null; cacheRuaj(`acc:${uid}:fav`, fav); } catch (_e) { /* jo-kritike */ }
      setFavoriteFamilyId(fav);
      const linkedList = linked || [];
      const favValid = fav && linkedList.some((r) => r.family_id === fav) ? fav : null;
      const fam = favValid || linkedList[0]?.family_id || null;
      if (!fam) {
        // ══════════════════════════════════════════════════════════════════
        // I KYÇUR, POR I PALIDHUR ME ASNJË FAMILJE
        //
        // NDREQJE, 31 gusht 2026 — raportuar nga pronari: një llogari krejt e
        // re hyri dhe në vend të ekranit «Bashkohu / Krijo familje» pa
        // «Familja Krasniqi», me skedat e saj dhe me «Shto person» të hapur.
        //
        // SHKAKU: gjendja «i palidhur» ngrihej VETËM te efekti i identifikimit
        // më poshtë, dhe ai efekt nis me `if (!activeFamilyId) return`. Pra
        // pikërisht në rastin ku s'ka familje, ai s'xhironte KURRË —
        // `notLinkedYet` dhe `personResolved` mbeteshin false, dhe të tria
        // kushtet e `locked` dilnin false. Rezultati: aplikacioni i hapur për
        // dikë që s'i përket asnjë familjeje.
        //
        // Emri i huaj vinte nga një gjë e dytë, e ndrequr veçmas: `treeName`
        // e kishte «Krasniqi» si vlerë fillestare, trashëgim i kohës kur
        // platforma ishte e një familjeje të vetme. Kur asnjë familje nuk
        // ngarkohet, ajo vlerë mbetet — dhe app-i shfaq emrin e një familjeje
        // të huaj. Prandaj këtu pastrohet shprehimisht.
        //
        // `linkedPersonChecked` ngrihet gjithashtu: pa të, ekrani i hyrjes
        // bie te dega e parë («duke identifikuar…») dhe rri përgjithmonë duke
        // u ngarkuar, në vend që të tregojë rrugëdaljet.
        // ══════════════════════════════════════════════════════════════════
        setTreeName("");
        setMatchableFamilies([]);
        setLinkedPersonChecked(true);
        setNotLinkedYet(true);
        setPersonResolved(true);
        // VETË-BASHKIM: nëse email-i i kësaj llogarie përputhet me një person
        // ende të palidhur në ndonjë familje, ekrani i hyrjes ia ofron atë.
        try {
          const { data: matchable } = await supabase.rpc("rpc_my_matchable_families");
          setMatchableFamilies(matchable || []);
        } catch (e) { /* jo-kritike */ }
        setLoading(false);
        return;
      }
      setActiveFamilyId(fam);
      try {
        const { data: famRow } = await supabase.from("families").select("name, slug").eq("id", fam).single();
        if (famRow?.name) { setTreeName(famRow.name); cacheRuaj(`fam:${fam}:emri`, famRow.name); }   // CACHE1
        else if (paRrjet) { const e = await cacheLexo(`fam:${fam}:emri`); if (e) setTreeName(e); }
      } catch (e) { /* jo-kritike */ }
      await Promise.all([fetchPeople(fam), fetchRoles(fam), fetchSettings(fam), fetchDocuments()]);
      const { data: anyPeople2 } = await supabase.rpc("rpc_any_people_exist");
      setTreeHasAnyPeople(!!anyPeople2);
      } catch (e) {
        // S'e dimë dot nëse ke familje apo jo. Të mos e hapim aplikacionin mbi
        // një pyetje pa përgjigje — ekrani i hyrjes është dalja e sigurt, dhe
        // ai ka të tria rrugët (kod, krijo, dil).
        console.error("zgjidhja e familjes nga llogaria:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [authSession, familySlug, activeFamilyId]);

  // Mban `favoriteFamilyId` në sinkron për yllin ★ te "Familjet e mia" në ÇDO
  // rrugë (edhe te /f/{slug}, ku efekti i mësipërm s'e merr atë).
  useEffect(() => {
    if (!authSession) { setFavoriteFamilyId(null); return; }
    supabase.rpc("rpc_get_favorite_family").then(({ data }) => setFavoriteFamilyId(data || null)).catch(() => {});
  }, [authSession]);

  // FAZA 9 — sugjerim AI për një rast familjar (kontekst MINIMAL sipas V6:
  // emri i parë + lloji + vitet; asgjë tjetër). Rezultati shfaqet në një modal
  // për ta kopjuar/dërguar përdoruesi.
  const [occasionAI, setOccasionAI] = useState(null); // { busy, text, error, name }
  // Faza 9 — modali i menaxhimit të datave të rëndësishme (admin/editor).
  const [impModalOpen, setImpModalOpen] = useState(false);
  const [impLabel, setImpLabel] = useState("");
  const [impMonth, setImpMonth] = useState("");
  const [impDay, setImpDay] = useState("");
  const [impBusy, setImpBusy] = useState(false);
  // SESHPEJTI1 — kujt i takon data që po shtohet: "familja" ose "une".
  const [impKujt, setImpKujt] = useState("une");
  const addImportantDate = async () => {
    const m = parseInt(impMonth, 10), d = parseInt(impDay, 10);
    if (!impLabel.trim() || !(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) { showToast(t("Add a name and a valid date.")); return; }
    setImpBusy(true);
    const { error } = await supabase.rpc("rpc_add_important_date", {
      p_label: impLabel.trim(), p_month: m, p_day: d, p_note: null, p_requester_id: currentPersonId,
      // NULL = e familjes. Serveri e kërkon admin/editor për atë rast; këtu
      // vetëm e themi se për kë është, nuk vendosim ne se kush lejohet.
      p_person_id: impKujt === "familja" ? null : currentPersonId,
    });
    setImpBusy(false);
    if (error) { showToast(t("toast.saveFailed", { error: error.message })); return; }
    setImpLabel(""); setImpMonth(""); setImpDay("");
    fetchImportantDates();
    fetchMarriageAnnivs();
  };
  const deleteImportantDate = async (id) => {
    const { error } = await supabase.rpc("rpc_delete_important_date", { p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.saveFailed", { error: error.message })); return; }
    fetchImportantDates();
    fetchMarriageAnnivs();
  };
  const suggestForOccasion = async (item) => {
    if (item.type === "important") {
      setOccasionAI({ busy: true, text: "", error: "", name: item.label, item });
      try {
        const { data, error } = await supabase.functions.invoke("family-suggestion", { body: { kind: "important", name: (item.label || "").slice(0, 60), years: null, lang: (i18n?.language || "sq") } });
        if (error || !data?.text) {
          const msg = data?.error === "rate_limited" ? t("Too many requests — please wait a bit.") : (data?.error || error?.message || t("Saving failed. Please try again."));
          setOccasionAI({ busy: false, text: "", error: msg, name: item.label, item });
        } else {
          setOccasionAI({ busy: false, text: data.text, error: "", name: item.label, item });
        }
      } catch (e) { setOccasionAI({ busy: false, text: "", error: (e && e.message) || "error", name: item.label, item }); }
      return;
    }
    const p = people[item.personId];
    if (!p) return;
    // FIX (19 gusht 2026) — te lista e ngjarjeve përvjetori i martesës ka
    // `type: "wedding"`, kurse edge function-i `family-suggestion` pranon vetëm
    // "birthday" | "death_anniversary" | "marriage_anniversary" | "important".
    // Pa këtë përkthim çdo klikim mbi ✨ te një martesë kthente 400.
    const kind = item.type === "birthday" ? "birthday"
      : item.type === "anniversary" ? "death_anniversary"
      : item.type === "wedding" ? "marriage_anniversary"
      : item.type;
    let years = null;
    if (kind === "death_anniversary" || kind === "marriage_anniversary") years = item.years ?? null;
    else if (kind === "birthday" && p.birth_date) {
      years = birthdayNextAge(p.birth_date);
    }
    let nm = (p.first_name || "").trim();
    // Martesa është ngjarje e ÇIFTIT — dërgo të dy emrat, që sugjerimi t'u
    // drejtohet të dyve e jo vetëm bashkëshortit të parë.
    if (kind === "marriage_anniversary") {
      const other = people[(item.memberIds || [])[1]];
      const otherName = (other?.first_name || "").trim();
      if (otherName) nm = (nm ? `${nm} & ${otherName}` : otherName);
    }
    setOccasionAI({ busy: true, text: "", error: "", name: nm || displayName(p), item });
    try {
      const { data, error } = await supabase.functions.invoke("family-suggestion", {
        body: { kind, name: nm.slice(0, 60), years, lang: (i18n?.language || "sq") },
      });
      if (error || !data?.text) {
        const msg = data?.error === "rate_limited" ? t("Too many requests — please wait a bit.") : (data?.error || error?.message || t("Saving failed. Please try again."));
        setOccasionAI({ busy: false, text: "", error: msg, name: nm || displayName(p), item });
        return;
      }
      setOccasionAI({ busy: false, text: data.text, error: "", name: nm || displayName(p), item });
    } catch (e) {
      setOccasionAI({ busy: false, text: "", error: (e && e.message) || "error", name: nm || displayName(p), item });
    }
  };

  // Cakto/hiq familjen e preferuar (toggle). Optimist: përditëso UI-në menjëherë.
  const toggleFavoriteFamily = async (familyId) => {
    const next = favoriteFamilyId === familyId ? null : familyId;
    setFavoriteFamilyId(next);
    const { error } = await supabase.rpc("rpc_set_favorite_family", { p_family_id: next });
    if (error) { setFavoriteFamilyId(favoriteFamilyId); showToast(t("toast.saveFailed", { error: error.message })); return; }
    showToast(next ? t("Set as your default family") : t("Default family cleared"));
  };

  // FAZA 6 — "Fshih nga lista ime" (e kthyeshme, per-pajisje me localStorage) dhe
  // "Dil nga familja" (largim i vërtetë, rpc_leave_family).
  const [hiddenFamilies, setHiddenFamilies] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("pef_hidden_families") || "[]")); } catch (e) { return new Set(); }
  });
  const [showHiddenFamilies, setShowHiddenFamilies] = useState(false);
  const toggleHiddenFamily = (familyId) => {
    setHiddenFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(familyId)) next.delete(familyId); else next.add(familyId);
      try { localStorage.setItem("pef_hidden_families", JSON.stringify([...next])); } catch (e) { /* pa rëndësi */ }
      return next;
    });
  };
  // Log audit-i te "Changes" — veprime që i nis vetë klienti (eksport, poster,
  // sinkronizim, sjellje familjeje, largim). Ripërdor rpc_log_activity ekzistuese
  // (insert-only). S'duhet ta prishë veprimin nëse dështon.
  const logActivity = (action, targetId, detail) => {
    if (!currentPersonId) return;
    try { supabase.rpc("rpc_log_activity", { p_actor_id: currentPersonId, p_action: action, p_target_id: targetId || null, p_detail: detail || null }); } catch (_e) { /* jo-kritik */ }
  };
  const leaveFamily = async (f) => {
    if (!window.confirm(t("Are you sure you don't want your account to be linked to the \"{{family}}\" family?\n\nYou will be removed from this family — it stays for the others.", { family: f.family_name }))) return;
    const { error } = await supabase.rpc("rpc_leave_family", { p_person_id: f.person_id });
    if (error) { showToast(error.message); return; }
    logActivity("leave_family", null, f.family_name);
    showToast(t("You have left the family."));
    setTimeout(() => window.location.reload(), 600);
  };

  // Realtime i ndarë nga fetch-i fillestar (FAZA 6): varet nga `activeFamilyId`
  // që të mos e mbyllë (stale closure) vlerën `null` fillestare të tij — pa
  // këtë ndarje, callback-u i settings do të përdorte përgjithmonë family_id-në
  // e gabuar (null), sepse useEffect-i i fetch-it fillestar xhiron vetëm një
  // herë ([] deps), para se activeFamilyId të zgjidhet realisht.
  useEffect(() => {
    const channel = supabase
      .channel("tree-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "people" }, fetchPeople)
      .on("postgres_changes", { event: "*", schema: "public", table: "roles" }, fetchRoles)
      .on("postgres_changes", { event: "*", schema: "public", table: "settings" }, () => fetchSettings(activeFamilyId))
      // Kujtimet/feed-i (family_history_events) tani LIVE — kur dikush shton
      // një kujtim, personi që e ka app-in hapur e sheh pa refresh, njësoj
      // si personat/rolet. Përdoret `() => fetchFamilyHistory()` që të mos i
      // kalohet payload-i i ndryshimit si argument.
      .on("postgres_changes", { event: "*", schema: "public", table: "family_history_events" }, () => fetchFamilyHistory())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [activeFamilyId]);

  // #1 (signed URLs): gjenero URL të nënshkruara për fotot (bucket 'photos')
  // kur ndryshojnë personat/stema; rifreskohen para skadimit. Fallback-u te
  // photoUrl siguron që asgjë s'prishet ndërkohë (edhe nëse kjo dështon).
  useEffect(() => {
    let cancelled = false;
    async function run() {
      const paths = Array.from(new Set([
        ...Object.values(people || {}).map((pp) => pp && pp.photo_path).filter(Boolean),
        ...(crestPath ? [crestPath] : []),
        // #1 (media privat): përfshi edhe fotot e Ngjarjeve/Feed dhe Arkivit,
        // që kur bucket-i bëhet privat të gjitha të kenë signed URL në cache
        // (përndryshe fallback-u te URL publike do të dështonte për to).
        ...(familyHistoryEvents || []).flatMap((ev) => [ev.photo_path, ...((ev.photo_paths) || [])]).filter(Boolean),
        ...(archiveItems || []).map((it) => it && it.file_path).filter(Boolean),
      ]));
      if (!paths.length) return;
      try {
        const { data } = await supabase.storage.from("photos").createSignedUrls(paths, 3600);
        if (cancelled || !data) return;
        let changed = false;
        for (const it of data) { if (it && it.signedUrl && it.path) { _signedMedia.photos[it.path] = it.signedUrl; changed = true; } }
        if (changed) setSignedTick((t) => t + 1);
      } catch (e) { /* fallback te URL publike */ }
    }
    run();
    const iv = setInterval(run, 55 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [people, crestPath, familyHistoryEvents, archiveItems]);

  // Lexo Media Access Policy një herë (rregulli i shkarkimit të profile photos).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase.rpc("rpc_get_media_policy");
        const row = Array.isArray(data) ? data[0] : data;
        if (alive && row) setMediaPolicy((prev) => ({ ...prev, ...row }));
      } catch (e) { /* mbaje parazgjedhjen */ }
    })();
    return () => { alive = false; };
  }, []);

  // ---------- auth (FAZA 3 — sesion real Supabase Auth) ----------
  // Ndjek sesionin e Auth-it (Google/Apple/Email) në kohë reale. Kjo është e
  // pavarur nga `people`/`roles` më sipër dhe xhiron gjithmonë, pavarësisht
  // nëse pema ka mbaruar së ngarkuari — kështu ekrani i login-it mund të
  // reagojë menjëherë sapo dikush të hyjë, pa pritur tjetër gjë.
  // FAZA 16 — "Histori login-esh": raporton çdo hyrje REALE (jo çdo hapje
  // app-i me sesion ekzistues) te login_history, që platform admini të
  // mund të shohë "a u hyri realisht dikush, dhe kur/nga cila platformë".
  // I ndarë qëllimisht nga heartbeat-i (Faza 13, më poshtë) — ai raporton
  // "jam aktiv tani" periodikisht, ky këtu raporton "sapo hyra" një herë.
  // Heshtazi, fire-and-forget — s'duhet kurrë të prishë ose të vonojë login-in.
  // 6 shtator 2026 — «pas çdo refresh-i regjistrohet si login». Supabase e
  // rikthen sesionin në çdo ringarkim dhe ngjarja SIGNED_IN vjen sërish, po
  // hyrja është e njëjta. Hyrje e RE do të thotë `last_sign_in_at` i ri te
  // llogaria (ndryshon vetëm me fjalëkalim/Google/Apple, jo me refresh) —
  // vetëm ajo shënohet, një herë. Aktiviteti («kur ka qenë aktiv») nuk varet
  // nga kjo: atë e mban heartbeat-i çdo 90 s.
  async function hyrjeEPashenuar(pjesa) {
    try {
      const { data } = await supabase.auth.getSession();
      const u = data && data.session && data.session.user;
      if (!u) return true;
      const emri = `pef_hyrja_e_shenuar_${pjesa}`;
      const celesi = `${u.id}:${u.last_sign_in_at || ""}`;
      if (localStorage.getItem(emri) === celesi) return false;
      localStorage.setItem(emri, celesi);
      return true;
    } catch (_e) { return true; }
  }
  async function logLoginEvent() {
    if (!(await hyrjeEPashenuar("llogaria"))) return;
    const platform = Capacitor?.getPlatform ? Capacitor.getPlatform() : "web";
    let country = null;
    try {
      const res = await fetch("https://pemaefamiljes.com/api/geo", { cache: "no-store" });
      const data = await res.json();
      country = data?.country || null;
    } catch (e) {
      // e padëmshme
    }
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
    supabase.rpc("rpc_log_login", { p_country: country, p_platform: platform, p_user_agent: userAgent }).then(
      () => {},
      () => {} // heshtazi — s'duhet kurrë të shqetësojë përdoruesin
    );
  }

  useEffect(() => {
    // SIGURI (telefon): mos e lër `authSessionLoading` të ngecë. getSession()
    // ndonjëherë vonon/dështon në WebView native — pa këtë, ekrani i hyrjes
    // s'shfaqej kurrë. Timeout 4s + .catch e detyrojnë përfundimin.
    let done = false;
    const finish = (session) => { if (done) return; done = true; setAuthSession(session || null); setAuthSessionLoading(false); };
    supabase.auth.getSession().then(({ data }) => finish(data && data.session)).catch(() => finish(null));
    const _t = setTimeout(() => finish(null), 4000);
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      clearTimeout(_t); done = true; setAuthSessionLoading(false);
      if (_event === "SIGNED_IN") { justSignedInRef.current = true; logLoginEvent(); }
      setAuthSession(newSession);
      // SIGURIA2 (M3) — rikontrollo lidhjen vetëm kur ndryshon vërtet kush është
      // brenda (hyrje, dalje, ndryshim i përdoruesit). TOKEN_REFRESHED ndodh çdo
      // orë dhe sa herë hapet app-i nga sfondi: rivendosja e flamujve atëherë e
      // kthente ekranin në «duke identifikuar…» dhe e ngarkonte rishtas pemën.
      if (_event === "SIGNED_IN" || _event === "SIGNED_OUT" || _event === "USER_UPDATED") {
        setLinkedPersonChecked(false); // sesion i ri (ose dalje) → rikontrollo lidhjen
        setNotLinkedYet(false);
      }
    });
    return () => { clearTimeout(_t); listener.subscription.unsubscribe(); };
  }, []);

  // FAZA 21 — "Kohëzgjatja mesatare e sesionit" (Admin Center). Thirret te
  // "visibilitychange" (jo "beforeunload"/"pagehide" — JOBESUESHME në
  // mobile, WebView-i shpesh s'i ekzekuton fare para se procesi të vritet).
  // "hidden" ndodh edhe kur përdoruesi thjesht ndërron tab/aplikacion, jo
  // domosdoshmërisht mbyllje faqeje — e konsiderojmë "fundi i sesionit" për
  // qëllime matjeje, edhe nëse teknikisht faqja mbetet e hapur në sfond.
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "hidden" && authSession?.user?.id) {
        supabase.rpc("rpc_end_session").then(() => {}, () => {}); // heshtazi
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [authSession?.user?.id]);

  // Sapo të kemi DHE sesion Auth DHE pemën e ngarkuar, provo të gjejmë
  // personin e lidhur me këtë llogari. Rendi:
  //   1. rpc_try_auto_link_invite() — nëse email-i i verifikuar i llogarisë
  //      përputhet me një ftesë ende të papërdorur, lidh tani (shih FAZA 2).
  //      E padëmshme nëse s'ka ftesë përkatëse — thjesht kthen null.
  //   2. rpc_my_linked_people() — çdo person i lidhur tashmë me këtë llogari
  //      (nga ky hap ose nga një pranim i mëparshëm ftese te /invite).
  //   3. Filtrohet vetëm te personat që EKZISTOJNË te kjo instancë e pemës
  //      (`people` lokale) — kjo instancë e App.jsx është për NJË familje;
  //      nëse llogaria është lidhur edhe me familje të tjera diku tjetër, ato
  //      injorohen këtu.
  useEffect(() => {
    if (!authSession || linkedPersonChecked || loading || !activeFamilyId) return;
    (async () => {
      // OPTIMIZIM (pas ankesës për vonesë te çdo rifreskim): më parë
      // thirrej GJITHMONË rpc_try_auto_link_invite() PARA se të kontrollohej
      // fare nëse llogaria është tashmë e lidhur — një udhëtim rrjeti i
      // panevojshëm për dikë që tashmë ka llogari të lidhur (rasti i
      // zakonshëm, çdo rifreskim). Tani kontrollohet SË PARI nëse llogaria
      // është tashmë e lidhur (rpc_my_linked_people) — vetëm nëse S'ËSHTË,
      // provohet auto-lidhja me ftesë, dhe rikontrollohet një herë.
      // AUTO-JOIN AUTOMATIK — thirret GJITHMONË në fillim të zgjidhjes, PA kusht.
      // FIX KRITIK (raportuar: "as një ndryshim, familja e importuar s'shfaqet"):
      // më parë kjo thirrje ishte BRENDA if-it "s'je i lidhur me familjen aktive".
      // Por kur hap faqen je duke parë trungun TËND — familje me të cilën je
      // TASHMË i lidhur — kështu if-i ishte false dhe auto-join-i s'thirrej KURRË,
      // pra familjet e reja (të importuara, si Orlishta) s'zbuloheshin dot. Tani
      // thirret para se të marrim listën, pavarësisht familjes që po shihet.
      // Idempotent (on conflict do nothing) dhe i padëmshëm nëse s'ka përputhje.
      try { await supabase.rpc("rpc_auto_join_my_email_matches"); } catch (e) { /* jo-kritike */ }
      let { data: linked, error: linkedErr } = await merrListenELidhjeve();
      // CACHE1 — pa rrjet s'mund të thuhet «s'je i lidhur»: përgjigja mungon,
      // nuk është «jo». Merret lista e fundit e njohur; nëse as ajo s'ka,
      // mbetemi duke pritur rrjetin (ekrani i hyrjes, pa e fshirë kujtesën).
      // SIGURIA2 (M2) — i njëjti rregull për çdo gabim që mbetet pas riprovimeve.
      const paRrjet = !!linkedErr;
      const uid = authSession?.user?.id || "";
      if (paRrjet) { if (eshteGabimRrjeti(linkedErr)) setJeOffline(true); linked = (await cacheLexo(`acc:${uid}:linked`)) || []; }
      else if (!linkedErr) cacheRuaj(`acc:${uid}:linked`, linked || []);
      if (paRrjet && !linked.length) return;   // pa rrjet, pa kujtesë — provohet sërish kur të kthehet interneti
      if (!paRrjet && !(linked || []).some((row) => row.family_id === activeFamilyId)) {
        // FIX: `.rpc(...).catch(...)` direkt HIDHTE "TypeError: ...catch is
        // not a function" (ndërtuesi i Supabase-it është "thenable", jo
        // Promise i vërtetë) — kjo e ndalte krejt zgjidhjen e identifikimit
        // në heshtje, ishte pikërisht arsyeja pse identifikimi "ngecte" pa
        // reaguar fare.
        try {
          // Përpjekje me përpjekje të mirë: nëse s'ka ftesë për t'u lidhur, s'ka gjë
    // për të thënë. Por një dështim i vërtetë duhet të lërë gjurmë.
    { const { error } = await supabase.rpc("rpc_try_auto_link_invite");
      if (error) console.error("rpc_try_auto_link_invite:", error.message); }
          const retry = await supabase.rpc("rpc_my_linked_people");
          linked = retry.data;
        } catch (e) {
          // e padëmshme nëse s'ka ftesë përkatëse — thjesht vazhdo më poshtë
        }
      }
      // FAZA 12: ruaj krejt listën (jo vetëm përputhjen për këtë instancë) —
      // e nevojshme për "zgjedhësin e familjeve", kur llogaria është e
      // lidhur me persona në më shumë se një familje (p.sh. dikush që i
      // përket trungut të vet DHE familjes ku ka hyrë me martesë).
      setMyLinkedFamilies(linked || []);
      // FIX: më parë krahasohej kundrejt `people` lokale (`people[row.person_id]`)
      // — por `people` mund të ishte marrë PARA se sesioni Auth të ishte gati
      // (RLS e kufizon si "anon" derisa sesioni të njihet plotësisht nga
      // kërkesat, kështu që fetch-i i parë kthente bosh). Kjo bënte që një
      // lidhje krejt e saktë te person_account_links të shfaqej si "s'je
      // lidhur" — pikërisht defekti që u zbulua. Tani krahasohet sipas
      // family_id (të cilën e njohim tashmë, e pavarur nga `people`), dhe
      // rifreskohen vetë të dhënat (autentikuara tani) para se të vazhdojmë.
      const match = (linked || []).find((row) => row.family_id === activeFamilyId);
      setLinkedPersonChecked(true);
      if (!match) {
        setNotLinkedYet(true);
        setPersonResolved(true);
        // Kujtesa e pajisjes u tregua e gabuar: kjo llogari s'i përket familjes
        // që `pef_family_slug` e çoi këtu. Fshije, që rinisja e radhës të mos
        // e përsërisë të njëjtin gabim. Fshihet vetëm kur ajo ishte burimi —
        // një adresë e shprehur `/f/<slug>` (ftesë, lidhje e dërguar) mbetet
        // e vlefshme dhe s'ka pse të prekim asgjë për të.
        //
        // Pse s'mjafton fshirja te dalja: llogaria mund të ndërrohet edhe pa
        // dalje të rregullt — sesioni skadon, aplikacioni mbyllet me forcë,
        // ose telefoni jepet dikujt tjetër. Kjo është rrjeta poshtë asaj.
        try {
          if (!paRrjet && localStorage.getItem("pef_family_slug") === familySlug) {   // CACHE1: kurrë pa rrjet
            localStorage.removeItem("pef_family_slug");
          }
        } catch (_e) { /* private mode */ }
        // VETË-BASHKIM: kjo llogari s'është e lidhur me këtë familje. Nëse
        // është Google/Apple dhe email-i i saj përputhet me persona në familje
        // të tjera (ende të palidhur), i marrim ato që t'ia shfaqim si tabelë
        // "këto familje kanë email-in tënd — dëshiron të bashkohesh?".
        try {
          const { data: matchable } = await supabase.rpc("rpc_my_matchable_families");
          setMatchableFamilies(matchable || []);
        } catch (e) { /* jo-kritike */ }
        return;
      }
      // OPTIMIZIM: fetchPeople/fetchRoles tani KTHEJNË direkt hartën e
      // freskët — përdorim atë menjëherë, në vend të bëjmë edhe dy query
      // shtesë (freshPeople/freshRole) të mëparshme, thjesht sepse `people`/
      // `roles` (state) do të ishin ende "të mbyllura" (stale closure) me
      // vlerën e vjetër. Kjo heq 2 udhëtime rrjeti nga çdo identifikim.
      const [freshPeopleMap, freshRolesMap] = await Promise.all([fetchPeople(), fetchRoles()]);
      const matchedPerson = freshPeopleMap[match.person_id];
      // Rregulla e ruajtur nga identifikimi i vjetër: i ndjerët kurrë s'mund
      // të identifikohen; të bllokuarit refuzohen me shpjegim, jo heshtazi.
      if (matchedPerson?.death_date) {
        setNotLinkedYet(true);
        setPersonResolved(true);
        setAuthError(t("auth.profileMarkedDeceased"));
        return;
      }
      if (matchedPerson?.blocked) {
        setNotLinkedYet(true);
        setPersonResolved(true);
        setAuthError(t("auth.accessRestricted"));
        return;
      }
      // FAZA 14/15 — pezullimi i familjes (nga platform admin, p.sh.
      // mospagesë): njësoj si bllokimi individual më lart, kontrollohet këtu
      // te momenti i identifikimit. `families` ka "public read" (schema.sql),
      // kështu që një `select` i thjeshtë mjafton, pa RPC shtesë.
      const { data: familyRow } = await supabase.from("families").select("suspended, suspended_reason").eq("id", activeFamilyId).single();
      if (familyRow?.suspended) {
        setNotLinkedYet(true);
        setPersonResolved(true);
        setAuthError(t("auth.familySuspended", { reason: familyRow.suspended_reason ? ` (${familyRow.suspended_reason})` : "" }));
        return;
      }
      setNotLinkedYet(false);
      // FAZA 34 — hoqëm fjalëkalimin shtesë të adminit: ishte i nevojshëm
      // kur "hyrja" ishte thjesht zgjedhje emri+datëlindje pa vërtetim real.
      // Tani që ka Auth të vërtetë (email+password/Google/Apple), identiteti
      // është tashmë i vërtetuar te authSession — s'ka pse admini të ketë
      // edhe një fjalëkalim ekstra sipër tij.
      completeLogin(match.person_id);
      setPersonResolved(true);
    })();
  }, [authSession, linkedPersonChecked, loading, people, roles, activeFamilyId]);

  const myPersonId = currentPersonId;
  const myAccountId = authSession?.user?.id || null;
  // Faza 6 — a është rregulli i feed-it aktiv (default true derisa të ngarkohet)
  const ruleOn = (k) => !feedRules || feedRules[k] !== false;
  const fetchFeedRules = async () => {
    const { data, error } = await supabase.from("feed_rules").select("key, enabled");
    if (!error && data) { const m = {}; data.forEach((r) => { m[r.key] = r.enabled; }); setFeedRules(m); }
  };
  const fetchFeedSettings = async () => {
    const { data, error } = await supabase.from("feed_settings").select("retention_days").limit(1).maybeSingle();
    if (!error && data) setFeedSettings({ retention_days: data.retention_days ?? 7 });
  };
  // "Fjala e ditës" — postet ditore (get-or-create në server, njësoj për të gjithë).
  //
  // PERKTHIMI2 — Oda kërkohet NË GJUHËN E LEXUESIT. Nëse dera kthen tekst në një
  // gjuhë tjetër nga e imja, kartela e Odës shfaq një buton «EN» pranë emrit të
  // rubrikës, dhe një prekje i përkthen të TRIA njëherësh.
  //
  // PSE JO VETVETIU, SIÇ E KISHA MENDUAR. E ndërtova që të përkthehej vetë në
  // ngarkim — dhe nuk punoi dy herë me radhë, sepse në atë çast sesioni ende nuk
  // ekziston dhe thirrja e funksionit kthen 401. Pronari e propozoi zgjidhjen
  // vetë: «e vendosim EN përkthimin pas anekdotës dhe përkthen çdo gjë në atë
  // rubrikë».
  //
  // Ai propozim është më i mirë se ideja ime edhe përtej gabimit: një veprim që
  // e nis njeriu ka gjithmonë sesion, dhe kur dështon, dështimi DUKET — kurse
  // një provë e heshtur në ngarkim dështon në heshtje, dhe pastaj kërkohet me
  // orë të tëra. Dhe mbetet një mekanizëm i vetëm për Odën, jo dy.
  //
  // Kursimi nuk humbet: përkthimi ruhet për krejt platformën, pra e shtyp vetëm
  // i pari lexues i asaj gjuhe atë ditë.
  const [odaDuke, setOdaDuke] = useState(false);
  const [odaGabim, setOdaGabim] = useState(null);
  const fetchFeedWisdom = async (gjuhaK) => {
    const g = gjuhaK || gjuhaIme;
    const { data, error } = await supabase.rpc("rpc_feed_wisdom_today", { p_lang: g });
    if (error || !Array.isArray(data)) {
      // Pa këtë, PERKTHIMI2 i paxhiruar do ta linte Odën BOSH pa asnjë shpjegim
      // — dhe një faqe bosh lexohet si veçori e prishur, jo si SQL që mungon.
      if (error) console.warn("oda:", String(error.message || error));
      return;
    }
    setFeedWisdom(data);
  };
  async function perktheOden() {
    if (odaDuke) return;
    const g = gjuhaIme;
    setOdaDuke(true); setOdaGabim(null);
    const { data: res, error: gab } = await supabase.functions
      .invoke("perkthe", { body: { oda: true, gjuha: g } });
    const kod = await gabimiIFunksionit(gab, res);
    if (kod) {
      // GABIMI DUKET. Te postimet heshtja ishte e drejtë — teksti origjinal rri
      // aty i plotë. Këtu njeriu SAPO shtypi një buton: një buton që nuk bën
      // asgjë dhe nuk thotë asgjë është më keq se mungesa e tij.
      console.warn("perkthe/oda:", kod);
      setOdaGabim(kod);
      // Te Oda gabimi thuhet me zë: njeriu SAPO shtypi një buton, dhe një
      // tooltip që duhet gjetur me miun nuk lexohet kurrë te telefoni.
      showToast(t("Translation failed: {{error}}", { error: kod }));
      setOdaDuke(false);
      return;
    }
    const { data: rip } = await supabase.rpc("rpc_feed_wisdom_today", { p_lang: g });
    if (Array.isArray(rip)) setFeedWisdom(rip);
    setOdaDuke(false);
  }
  const fetchFeedReactions = async () => {
    if (!activeFamilyId) { setFeedReactions({}); return; }
    const { data, error } = await supabase.from("feed_reactions").select("event_id, emoji, reactor_account_id");
    if (error) return;
    const map = {};
    (data || []).forEach((r) => { (map[r.event_id] = map[r.event_id] || []).push({ emoji: r.emoji, reactor_account_id: r.reactor_account_id }); });
    setFeedReactions(map);
  };
  const fetchFeedPosts = async () => {
    if (!activeFamilyId) { setFeedPosts([]); return; }
    // RAPORTIMI1 — `.is("deleted_at", null)` si te `fetchPeople` dhe te historia.
    // Pa këtë, «hiq përmbajtjen» te Admin Center do të shkruante te baza dhe
    // kartela do të vazhdonte të dukej te telefoni.
    const { data, error } = await supabase.from("feed_posts").select("*").is("deleted_at", null).order("created_at", { ascending: false });
    if (!error) { setFeedPosts(data || []); cacheRuaj(`fam:${activeFamilyId}:feed`, data || []); return; }   // CACHE1
    if (eshteGabimRrjeti(error)) {
      const c = await cacheLexo(`fam:${activeFamilyId}:feed`);
      if (Array.isArray(c)) setFeedPosts(c);
    }
  };
  // FEED1 — fsheh ose kthe postime. Kufiri i familjes dhe kërkesa për rolin
  // `admin` janë te vetë funksioni i bazës; kjo është vetëm dera e ndërfaqes.
  const fshihPostimet = async (ids, fshih) => {
    const lista = Array.from(ids || []);
    if (!lista.length) return;
    const { data, error } = await supabase.rpc("rpc_feed_posts_set_hidden", {
      p_ids: lista, p_hidden: !!fshih, p_requester_id: currentPersonId,
    });
    if (error) { showToast(tDyn(error.message)); return; }
    const sa = typeof data === "number" ? data : lista.length;
    setFeedSelect(new Set());
    setFeedSelectMode(false);
    await fetchFeedPosts();
    showToast(fshih
      ? t("{{count}} posts removed from the feed.", { count: sa })
      : t("{{count}} posts are back in the feed.", { count: sa }));
  };
  const hapTeFshehurat = async () => {
    const { data, error } = await supabase.rpc("rpc_feed_posts_hidden", { p_requester_id: currentPersonId });
    if (error) { showToast(tDyn(error.message)); return; }
    setFeedHidden(data || []);
  };
  const kthePostimin = async (id) => {
    const { error } = await supabase.rpc("rpc_feed_posts_set_hidden", { p_ids: [id], p_hidden: false, p_requester_id: currentPersonId });
    if (error) { showToast(tDyn(error.message)); return; }
    setFeedHidden((v) => (v || []).filter((x) => x.id !== id));
    await fetchFeedPosts();
    showToast(t("The post is back in the feed."));
  };
  const fetchImportantDates = async () => {
    if (!activeFamilyId) { setImportantDates([]); return; }
    try {
      const { data } = await supabase.rpc("rpc_list_important_dates", { p_family_id: activeFamilyId, p_person_id: currentPersonId });
      setImportantDates(data || []);
    } catch (e) { /* tabela mund të mos jetë aplikuar ende */ }
  };
  const fetchMarriageAnnivs = async () => {
    if (!activeFamilyId) { setMarriageAnnivs([]); return; }
    try {
      const { data } = await supabase.rpc("rpc_marriage_anniversaries", { p_family_id: activeFamilyId, p_within_days: 7 });
      setMarriageAnnivs(data || []);
    } catch (e) { /* migrimi mund të mos jetë aplikuar ende */ }
  };
  // Çelësi i çiftit — gjithmonë i renditur, si te kufizimi `person_a < person_b`
  // i tabelës. Kështu (a,b) dhe (b,a) japin të njëjtin çelës.
  const pairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const fetchMarriages = async () => {
    if (!activeFamilyId) { setMarriages({}); return; }
    try {
      const { data, error } = await supabase.from("marriages")
        .select("person_a,person_b,married_on,show_in_feed").eq("family_id", activeFamilyId);
      if (error && eshteGabimRrjeti(error)) {                // CACHE1
        const c = await cacheLexo(`fam:${activeFamilyId}:marriages`);
        if (c && typeof c === "object") setMarriages(c);
        return;
      }
      const map = {};
      (data || []).forEach((m) => { map[pairKey(m.person_a, m.person_b)] = m; });
      setMarriages(map);
      cacheRuaj(`fam:${activeFamilyId}:marriages`, map);     // CACHE1
    } catch (e) { /* migrimi mund të mos jetë aplikuar ende */ }
  };
  // Ruajtja e datës / e zgjedhjes së çiftit. Serveri (`rpc_set_marriage`) e
  // lejon adminin, redaktorin, ose vetë njërin nga të dy — dhe mjafton njëri
  // ta fikë feed-in, sepse përvjetori u takon të dyve njësoj.
  const saveMarriage = async (a, b, marriedOn, showInFeed) => {
    const key = pairKey(a, b);
    setMarriageSaving(key);
    try {
      const { error } = await supabase.rpc("rpc_set_marriage", {
        p_a: a, p_b: b, p_married_on: marriedOn || null,
        p_show_in_feed: showInFeed, p_requester_id: currentPersonId,
      });
      if (error) { showToast(t("toast.failed", { error: error.message })); return; }
      setMarriages((prev) => ({ ...prev, [key]: { person_a: a < b ? a : b, person_b: a < b ? b : a, married_on: marriedOn || null, show_in_feed: showInFeed } }));
      fetchMarriageAnnivs();
      showToast(t("toast.savedPeriod"));
    } finally { setMarriageSaving(null); }
  };
  const toggleReaction = async (eventId, emoji) => {
    // Optimistik: përditëso menjëherë lokalisht, pastaj sinkronizo.
    setFeedReactions((prev) => {
      const cur = (prev[eventId] || []).slice();
      const idx = cur.findIndex((r) => r.emoji === emoji && r.reactor_account_id === myAccountId);
      if (idx >= 0) cur.splice(idx, 1); else cur.push({ emoji, reactor_account_id: myAccountId });
      return { ...prev, [eventId]: cur };
    });
    // Pa toast me qëllim: `fetchFeedReactions()` poshtë e rikthen gjendjen e
    // vërtetë brenda një çasti, pra emoji-ja thjesht kthehet mbrapsht dhe kjo
    // është e kuptueshme pa fjalë. Por `try/catch` u hoq — s'kapte kurrë asgjë,
    // sepse `supabase-js` nuk hedh përjashtim.
    const { error } = await supabase.rpc("rpc_toggle_feed_reaction", { p_event_id: eventId, p_emoji: emoji, p_requester_id: currentPersonId });
    if (error) console.error("rpc_toggle_feed_reaction:", error.message);
    fetchFeedReactions();
  };
  useEffect(() => { fetchFeedRules(); fetchFeedSettings(); }, []);
  // Oda varet nga gjuha: kur lexuesi e ndërron, teksti duhet të vijë sërish.
  // Deri sot ky efekt xhironte një herë me `[]` — dhe atëherë ndërrimi i gjuhës
  // e linte Odën në gjuhën e nisjes, gjë që dukej si veçori e prishur.
  // Oda varet nga gjuha: kur lexuesi e ndërron, teksti duhet të vijë sërish.
  useEffect(() => { fetchFeedWisdom(gjuhaIme); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [gjuhaIme]);
  // IKONAT E RUBRIKAVE — nga baza, jo nga kodi (FJALET1, 31 gusht 2026).
  //
  // Deri sot rrinin si hartë e ngulitur pak më poshtë. Pra tri rubrikat e para
  // kishin ikonën e tyre sepse dikush i shkroi me dorë, kurse çdo rubrikë e re
  // që krijonte pronari merrte «✨» — dhe s'kishte asnjë vend ku kjo të
  // ndryshohej, përveç një deploy-i.
  //
  // Harta e vjetër mbetet si rezervë te vizatimi: pa FJALET1 të xhiruar, kjo
  // derë s'ekziston, dhe atëherë asgjë nuk duhet të ndryshojë pamje.
  const [urtesiaIkonat, setUrtesiaIkonat] = useState({});
  useEffect(() => {
    let gjallë = true;
    supabase.rpc("rpc_fw_emoji").then(({ data, error }) => {
      if (!gjallë || error || !Array.isArray(data)) return;
      const harta = {};
      for (const r of data) if (r?.key && r?.emoji) harta[r.key] = r.emoji;
      setUrtesiaIkonat(harta);
    });
    return () => { gjallë = false; };
  }, []);
  // Faza 11 — ngarko template-in aktiv (published) të fushave custom një herë.
  useEffect(() => {
    (async () => {
      try {
        // Kërkesa e parë, pa rol: kthen shabllonin e përgjithshëm. Roli brenda
        // familjes njihet më vonë (myRole deklarohet poshtë), prandaj rafinimi
        // për adminët e familjes bëhet nga një efekt i dytë — jo këtu, që të
        // mos referohet një ndryshore para se të ekzistojë.
        const { data } = await supabase.rpc("rpc_get_template_for_me", { p_scope: null });
        if (data) setCustomTemplate(data);
      } catch (e) { /* pa template → asnjë fushë custom */ }
      try {
        const { data: opts } = await supabase.rpc("rpc_list_person_type_options");
        if (opts && opts.length) setPersonTypeOptions(opts);
      } catch (e) { /* pa config → opsionet e paracaktuara */ }
    })();
  }, []);
  useEffect(() => {
    fetchFeedReactions();
    fetchFeedPosts();
    fetchImportantDates();
    fetchMarriageAnnivs();
    fetchMarriages();
    if (!activeFamilyId) return;
    const ch = supabase
      .channel("feed-reactions-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_reactions" }, () => fetchFeedReactions())
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_posts" }, () => fetchFeedPosts())
      .subscribe();
    return () => supabase.removeChannel(ch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFamilyId]);
  // CACHE1 — rifreskimi nga rrjeti: kur interneti kthehet, kur app-i vjen në
  // plan të parë (resume/visibilitychange). Jo më shpesh se një herë në 60 s —
  // ndryshimet e çastit i sjell tashmë kanali i feed-it më lart. Personat
  // ruhen vetëm nga kanali i personave (`people` s'ka kanal: ndryshimet e
  // pemës vijnë me hapjen e radhës, siç vijnë edhe sot).
  const rifreskimiIFunditRef = useRef(0);
  useEffect(() => {
    if (!activeFamilyId) return;
    const rifresko = (meForce) => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const tani = Date.now();
      if (!meForce && tani - rifreskimiIFunditRef.current < 60000) return;
      rifreskimiIFunditRef.current = tani;
      Promise.all([fetchPeople(), fetchRoles(), fetchMarriages(), fetchFeedPosts()]).catch(() => {});
    };
    rifreskimiIFunditRef.current = Date.now();
    const kurRa = () => rifresko(true);
    const kurDuket = () => { if (document.visibilityState === "visible") rifresko(false); };
    window.addEventListener("online", kurRa);
    document.addEventListener("visibilitychange", kurDuket);
    const hiqResume = dëgjoResume(() => rifresko(false));
    return () => {
      window.removeEventListener("online", kurRa);
      document.removeEventListener("visibilitychange", kurDuket);
      hiqResume();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFamilyId]);
  // SHTESË (kërkesë e userit) — kush hyn me martesë/fejesë/bashkëjetesë e sheh
  // pemën NGA KËNDVËSHTRIMI I PARTNERIT TË VET TË GJAKUT, jo nga i veti — kështu
  // i ardhuri sheh identik ç'ka sheh partneri (ngjyra DHE trungu). Identifikimi:
  // "i ardhur" = dega e ruajtur `married_in` (etiketë që app-i e vë kur dikë e
  // shton si bashkëshort/e); partneri i gjakut = bashkëshorti/fejuari/bashkë-
  // jetuesi që NUK është `married_in`. Nëse s'gjendet (të dhëna të vjetra, ose
  // të dy të ardhur), bie mbrapsht te vetja — sjellja e mëparshme, pa u prekur.
  // Përdoret VETËM te pika e referimit të PAMJES (relationMap/trung/lisi), jo
  // te lejet/qendrimi — ato mbeten mbi vetë personin e loguar.
  const resolveViewerAnchor = (id) => {
    const p = id ? people[id] : null;
    if (!p || p.branch !== "married_in") return id;
    const partnerIds = [...(p.spouse_ids || []), ...(p.engaged_ids || []), ...(p.cohabiting_ids || [])];
    const bloodPartner = partnerIds.find((sid) => people[sid] && people[sid].branch !== "married_in");
    return bloodPartner || id;
  };
  // VIEW AS — kap `?viewAs` NJË HERË në render-in e parë (para se auth ta pastrojë
  // URL-në). E mbajmë te një ref që MBIJETON login-in: kur "Shiko si" hyn përmes
  // një llogarie host, login-i i host-it e rivendos focus-in në null te
  // useEffect-i i resetimit poshtë — pa këtë ref-guard, pamja binte te host-i.
  const viewAsRef = useRef(undefined);
  if (viewAsRef.current === undefined) {
    try { viewAsRef.current = new URLSearchParams(window.location.search).get("viewAs") || null; } catch { viewAsRef.current = null; }
  }
  useEffect(() => {
    setViewMode("trunk"); // parazgjedhje: trungu im (shih shënimin te useState viewMode)
    setExpandedIds(new Set());
    if (!viewAsRef.current) setFocusPersonId(null); // mos e pastro focus-in kur ka viewAs (host login)
    setChainStopId(null);
    // FIX (raportuar nga përdoruesi): karta e personit dilte e zhvendosur
    // majtas-lart pas çdo rifreskimi, në krahasim me qendrimin e saktë të
    // butonit "focus". Shkaku: fitView() ekzekutohej NJË herë të vetme (e
    // mbrojtur nga didFitRef) PARA se identiteti (myPersonId) të zgjidhej
    // nga sesioni — pozicionet e pemës ndryshojnë pastaj rreth "trungut tim"
    // të vërtetë, por qendrimi s'rikalkulohej më kurrë. Duke e zhbllokuar
    // didFitRef këtu, efekti i fitView() (më poshtë, që dëgjon `positions`)
    // rikalkulon automatikisht qendrimin sapo pozicionet reale të vendosen.
    didFitRef.current = false;
    if (currentPersonId) {
      fetchAnnouncements();
      checkMyPushSubscription();
      fetchEmailReplies();
      const params = new URLSearchParams(window.location.search);
      if (params.get("openAnnouncements") === "1") {
        openAnnouncements();
        params.delete("openAnnouncements");
        const cleanUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", cleanUrl);
      }
    }
  }, [currentPersonId]);

  // VIEW AS (kërkesë e userit — "të mund t'i impersonoj edhe personat, jo vetëm
  // llogaritë, nga Admin Center, për të parë si shihet pamja nga ai person").
  // Aplikon focusPersonId te personi i `viewAs` sapo ai të jetë ngarkuar te
  // `people` → trungu dhe ngjyrat dalin nga KËNDVËSHTRIMI i tij. Varet EDHE nga
  // currentPersonId që të RI-APLIKOHET pas login-it të host-it (i cili ndodh pas
  // ngarkimit të personave) — kështu pamja qëndron te personi, jo te host-i.
  // VETËM PAMJE: s'prek leje/të dhëna (mbeten sipas sesionit dhe RLS-së).
  const viewAsDoneRef = useRef(false);
  useEffect(() => {
    if (viewAsDoneRef.current) return;
    if (!viewAsRef.current || !people[viewAsRef.current]) return;
    setFocusPersonId(viewAsRef.current);
    setViewMode("trunk");        // pamja si personi → TRUNGU i tij, jo pema e plotë
    setActiveScreen("tree");     // hap DIREKT pemën — përndryshe mbetesh te "Ballina"
    viewAsDoneRef.current = true;
  }, [people, currentPersonId]);

  // FAZA 13 — heartbeat i thjeshtë për panelin e platformës (admin.pemaefamiljes.com):
  // raporton "jam aktiv" + platformë (web/android/ios) + shtet, çdo herë që
  // identifikohet dikush dhe pastaj periodikisht ndërsa app-i mbetet i hapur.
  // I gjithi opsional/i heshtur — nëse dështon (p.sh. offline), s'ndikon në
  // asgjë tjetër te platforma, thjesht s'përditësohet statistika.
  useEffect(() => {
    if (!currentPersonId) return;
    let cancelled = false;
    const platform = Capacitor?.getPlatform ? Capacitor.getPlatform() : "web";
    const sendHeartbeat = async () => {
      let country = null, city = null, region = null;
      try {
        // URL absolute (jo relative) sepse te Android/iOS, WebView-i s'e
        // ngarkon domenin real — kërkesa duhet të shkojë drejt e te
        // pemaefamiljes.com që të kalojë nëpër Cloudflare (i vetmi vend ku
        // ekziston header-i CF-IPCountry, e tani edhe cf.city/cf.region —
        // shih Faza 20 te functions/api/geo.js).
        const res = await fetch("https://pemaefamiljes.com/api/geo", { cache: "no-store" });
        const data = await res.json();
        country = data?.country || null;
        city = data?.city || null;
        region = data?.region || null;
      } catch (e) {
        // e padëmshme — thjesht s'e dimë vendndodhjen këtë herë
      }
      if (cancelled) return;
      // VERSIONI1 — te telefoni raportohet versioni i INSTALUAR (më parë gjithmonë «1.0.0»,
      // ndaj paneli s'dallonte kush e ka përditësuar); te shfletuesi mbetet APP_VERSION.
      const versioniRaportuar = (await versioniIInstaluar()) || APP_VERSION;
      if (cancelled) return;
      supabase.rpc("rpc_heartbeat", { p_country: country, p_platform: platform, p_app_version: versioniRaportuar, p_city: city, p_region: region }).then(
        () => {},
        () => {} // heshtazi — heartbeat s'duhet kurrë të shqetësojë përdoruesin
      );
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 90000); // çdo 90 sek ndërsa app-i është i hapur
    return () => { cancelled = true; clearInterval(interval); };
  }, [currentPersonId]);
  // SHËNIM (Faza 17 → 17b): fillimisht këtu ishte shtuar edhe një kanal
  // Supabase Realtime Presence (çdo klient .track() vetveten) për "prezencë
  // live" te paneli i platformës. U hoq — skalonte keq: presence bën
  // fan-out O(n) (çdo hyrje/dalje e njërit dërgohet te TË GJITHË të tjerët
  // e lidhur në kanal), pra me mijëra familjarë njëherësh do të krijonte
  // ngarkesë reale servieri/bandwidth. Zgjidhja e re (vetëm te AdminApp.jsx)
  // përdor Postgres Changes mbi account_activity — VETËM paneli i platformës
  // dëgjon (1 lidhje gjithsej, jo O(n)), heartbeat-i më sipër mbetet i
  // vetmi mekanizëm nga ana e klientit, pa ndryshim.
  const myRole = myPersonId ? roles[myPersonId] || "viewer" : null;
  // SHABLLONI SIPAS ROLIT — hierarkia që kërkoi auditi. Admini i familjes merr
  // shabllonin `family_admins` nëse ka një të publikuar; përndryshe serveri bie
  // vetë te `all_families`. Rri KËTU, pas myRole, dhe jo te efekti i mësipërm,
  // sepse një varësi e përmendur para deklarimit e rrëzon faqen.
  useEffect(() => {
    if (myRole !== "admin") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase.rpc("rpc_get_template_for_me", { p_scope: "family_admins" });
        if (alive && data) setCustomTemplate(data);
      } catch (e) { /* pa shabllon të vetin → mbetet ai i përgjithshëm */ }
    })();
    return () => { alive = false; };
  }, [myRole]);
  // canDownloadPhoto — a lejohet përdoruesi aktual të SHKARKOJË profile photo-n
  // e `person`. Shikimi lejohet për të gjithë familjen (RLS); ky është vetëm
  // rregulli i shkarkimit (buton/right-click), i konfigurueshëm nga Admin
  // Center. Parazgjedhje: vetë personi + prindër + partner + fëmijë.
  const canDownloadPhoto = (person) => {
    if (!person || !currentPersonId) return false;
    if (person.id === currentPersonId) return true;                 // vetë personi
    const pol = mediaPolicy || {};
    // AUDITI 2026-08 — më parë këtu krahasohej `myRole`, që është roli BRENDA
    // familjes; pra "admin" te politika u jepte akses fotosh të gjithë adminëve
    // të familjeve. Tani përgjigjen e jep serveri, nga RBAC-ja e platformës.
    if (canDownloadAnyPhoto) return true;
    const viewer = people[currentPersonId];
    if (!viewer) return false;
    // prind i person-it (viewer është prind → person.parent_ids përmban viewer)
    if (pol.allow_parents !== false && (person.parent_ids || []).includes(currentPersonId)) return true;
    // fëmijë i person-it (viewer është fëmijë → viewer.parent_ids përmban person)
    if (pol.allow_children !== false && (viewer.parent_ids || []).includes(person.id)) return true;
    // partner aktual (jo i divorcuar)
    if (pol.allow_partners !== false && (person.spouse_ids || []).includes(currentPersonId)
        && !((person.divorced_from_ids || []).includes(currentPersonId))) return true;
    // gjysh/gjyshe (opsionale)
    if (pol.allow_grandparents) {
      for (const pid of (person.parent_ids || [])) {
        const par = people[pid];
        if (par && (par.parent_ids || []).includes(currentPersonId)) return true;
      }
    }
    // vëlla/motër (opsionale) — ndajnë të paktën një prind
    if (pol.allow_siblings) {
      const ps = new Set(person.parent_ids || []);
      if ((viewer.parent_ids || []).some((x) => ps.has(x))) return true;
    }
    return false;
  };
  // A është kjo rrugë një profile photo që përdoruesi aktual S'mund ta
  // shkarkojë? (Fotot e Ngjarjeve/Arkivit s'i përkasin asnjë personi → jo të
  // kufizuara: i shkarkon çdo anëtar familjeje.)
  const isProfilePhotoRestricted = (path) => {
    if (!path) return false;
    const owner = Object.values(people).find((pp) => pp && pp.photo_path === path);
    return owner ? !canDownloadPhoto(owner) : false;
  };
  const canManage = myRole === "admin";
  // Faza 6 — zbulimi i "degës së re": grupe të lidhura personash të saposhtuar
  // (çift + fëmijë; 3+ brenda 1 dite OSE 5+ brenda 7 ditësh, me ≥2 pasardhës ose
  // 5+ gjithsej) → krijon kërkesa aprovimi për admin-in. Vetëm family-admin;
  // dedup me signature (on conflict do nothing në DB).
  useEffect(() => {
    if (!canManage || !activeFamilyId) return;
    const now = Date.now(), DAY = 86400000, WEEK = 7 * DAY;
    const recent = Object.values(people).filter((p) => p && p.created_at && !p.deleted_at && (now - new Date(p.created_at).getTime()) <= WEEK);
    if (recent.length < 3) return;
    const recentSet = new Set(recent.map((p) => p.id));
    const childrenOf = {};
    recent.forEach((c) => (c.parent_ids || []).forEach((pid) => { if (recentSet.has(pid)) (childrenOf[pid] = childrenOf[pid] || []).push(c.id); }));
    const visited = new Set(); const cands = [];
    for (const start of recent) {
      if (visited.has(start.id)) continue;
      const comp = []; const stack = [start.id];
      while (stack.length) {
        const id = stack.pop();
        if (visited.has(id)) continue;
        visited.add(id); comp.push(id);
        const p = people[id]; if (!p) continue;
        [...(p.parent_ids || []), ...(p.spouse_ids || []), ...(childrenOf[id] || [])].forEach((n) => { if (recentSet.has(n) && !visited.has(n)) stack.push(n); });
      }
      if (comp.length < 3) continue;
      const members = comp.map((id) => people[id]).filter(Boolean);
      const times = members.map((m) => new Date(m.created_at).getTime());
      const spanDays = (Math.max(...times) - Math.min(...times)) / DAY;
      let topCouple = null;
      for (const m of members) {
        for (const s of (m.spouse_ids || [])) {
          if (!comp.includes(s)) continue;
          if (members.some((c) => (c.parent_ids || []).some((pp) => pp === m.id || pp === s))) { topCouple = [m.id, s]; break; }
        }
        if (topCouple) break;
      }
      if (!topCouple) continue;
      const qualifies = (comp.length >= 3 && spanDays <= 1) || (comp.length >= 5 && spanDays <= 7);
      if (!qualifies) continue;
      const descCount = members.filter((c) => (c.parent_ids || []).some((pp) => topCouple.includes(pp))).length;
      if (descCount < 2 && comp.length < 5) continue;
      const headId = topCouple.reduce((a, b) => {
        const da = people[a] && people[a].birth_date ? new Date(people[a].birth_date).getTime() : Infinity;
        const db = people[b] && people[b].birth_date ? new Date(people[b].birth_date).getTime() : Infinity;
        return da <= db ? a : b;
      });
      const memberIds = comp.slice().sort();
      cands.push({ p_family_id: activeFamilyId, p_head: headId, p_head_name: displayName(people[headId]), p_members: memberIds, p_span_days: Math.round(spanDays), p_signature: memberIds.join(",") });
    }
    // FIX (kritik): `supabase.rpc(...).catch(...)` HIDHTE "TypeError: ...catch
    // is not a function" — ndërtuesi i Supabase-it është "thenable", JO Promise
    // i vërtetë (s'ka `.catch`). Ky gabim rrëzonte KREJT app-in sa herë
    // ndryshohej/ngarkohej familja (ky effect varet nga people/activeFamilyId),
    // dhe në telefon jepte "ekran të bardhë" në nisje (auto-ngarkon familjen e
    // fundit). Mbështillim me Promise.resolve(...) + try/catch → i padëmshëm.
    cands.forEach((c) => {
      try { Promise.resolve(supabase.rpc("rpc_upsert_branch_request", c)).catch(() => {}); } catch (_e) { /* jo-kritike */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, canManage, activeFamilyId]);
  // Content-protection deterrents for non-admins. IMPORTANT: this cannot stop
  // screenshots (that happens at the OS level, entirely outside any website's
  // control) or a technically capable person (DevTools, view-source, etc.).
  // It only removes the easy, casual paths — right-click "Save As", drag-to-
  // save, and text selection — for everyone except admins.
  useEffect(() => {
    if (!currentPersonId || myRole === "admin") return;
    const blockContextMenu = (e) => e.preventDefault();
    const blockDragStart = (e) => e.preventDefault();
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("dragstart", blockDragStart);
    document.body.classList.add("content-protected");
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("dragstart", blockDragStart);
      document.body.classList.remove("content-protected");
    };
  }, [currentPersonId, myRole]);
  const canDelete = myRole === "admin";
  // Admini i VETËM i familjes: s'mund të fshihet nga askush përveç Super Adminit
  // të platformës (server-side e njëjta rregull te rpc_delete_person). Nëse
  // personi i zgjedhur është i vetmi admin dhe shikuesi s'është super → fshihet
  // butoni "Hiq".
  const isSoleFamilyAdmin = (personId) => {
    if (!personId || roles[personId] !== "admin") return false;
    const adminIds = Object.keys(roles).filter((pid) => roles[pid] === "admin" && people[pid]);
    return adminIds.length === 1 && adminIds[0] === personId;
  };
  const canDeletePerson = (personId) =>
    canDelete && Object.keys(people).length > 1 &&
    (!isSoleFamilyAdmin(personId) || platformIsSuper);
  const editorScope = useMemo(() => {
    if (myRole !== "editor" || !myPersonId || !people[myPersonId]) return new Set();
    const me = people[myPersonId];
    const mySpouseIds = me.spouse_ids || [];
    // Start from my own parents (my blood line) — but someone who married
    // into the family has no recorded parents here, so also pull in my
    // spouse's parents, mirroring is_in_editor_scope() server-side.
    let rootParentIds = [...(me.parent_ids || [])];
    mySpouseIds.forEach((sid) => { (people[sid]?.parent_ids || []).forEach((pid) => { if (!rootParentIds.includes(pid)) rootParentIds.push(pid); }); });
    // REDAKTOR1: «Redaktor +» — rrënja ngjitet te gjyshërit (prindërit e
    // rrënjës), si te is_in_editor_scope. Pa gjyshër të shënuar mbetet rrënja
    // e zakonshme — «plus» s'jep kurrë më pak.
    if (rolesShkalla[myPersonId] === 2 && rootParentIds.length > 0) {
      const gjysherit = [];
      rootParentIds.forEach((pid) => { (people[pid]?.parent_ids || []).forEach((g) => { if (!gjysherit.includes(g)) gjysherit.push(g); }); });
      if (gjysherit.length > 0) rootParentIds = gjysherit;
    }
    if (rootParentIds.length === 0) {
      // No blood-line parents at all, mine or my spouse's — still let me
      // manage my own spouse and my own children directly.
      const scope = new Set(mySpouseIds);
      Object.values(people).forEach((p) => { if ((p.parent_ids || []).includes(myPersonId)) scope.add(p.id); });
      return scope;
    }
    const childrenMapAll = {};
    Object.values(people).forEach((p) => (p.parent_ids || []).forEach((pid) => {
      if (!childrenMapAll[pid]) childrenMapAll[pid] = [];
      childrenMapAll[pid].push(p.id);
    }));
    const scope = new Set(rootParentIds);
    const stack = [...rootParentIds];
    while (stack.length) {
      const id = stack.pop();
      (childrenMapAll[id] || []).forEach((cid) => { if (!scope.has(cid)) { scope.add(cid); stack.push(cid); } });
    }
    Array.from(scope).forEach((id) => { (people[id]?.spouse_ids || []).forEach((s) => scope.add(s)); });
    return scope;
  }, [myRole, myPersonId, people, rolesShkalla]);
  const canAddChildFor = (personId) => {
    if (myRole === "admin") return true;
    if (myRole === "editor") return personId === myPersonId || editorScope.has(personId);
    if (myRole === "contributor") return personId === myPersonId;
    return false;
  };
  const canEditFor = (personId) => {
    if (myRole === "admin") return true;
    if (myRole === "editor") return editorScope.has(personId);
    return false;
  };
  const canLinkSpouseFor = canAddChildFor;
  // REDAKTOR1 — kush mund t'i dërgojë kod personal këtij personi (pasqyrë e
  // rregullit të serverit te rpc_create_person_join_code). Jo vetes, jo
  // mysafirëve (ata kanë kodin e vet), jo të ndjerëve.
  const kodPersonalILejuar = (personId) => {
    const p = people[personId];
    if (!p || personId === myPersonId || p.death_date || eshteMysafir(p)) return false;
    if (myRole === "admin") return true;
    if (myRole === "editor") return editorScope.has(personId);
    if (myRole === "contributor") {
      const une = people[myPersonId];
      return (p.parent_ids || []).includes(myPersonId) || ((une && une.spouse_ids) || []).includes(personId);
    }
    return false;
  };
  // Kërkesë e userit — kur redaktori/kontribuesi zgjedh prindin ose
  // bashkëshortin/en për një person të ri, lista e SearchSelect duhet t'u
  // shfaqë VETËM personat që kanë të drejtë t'i redaktojnë/shtojnë nën ta —
  // jo krejt pemën. Admini vazhdon të shohë gjithë familjen (null = pa
  // kufizim). Për redaktorin përdoret e njëjta `editorScope` (+ vetë ai),
  // për kontribuesin vetëm vetja (i vetmi "prind"/"bashkëshort" i lejuar).
  const relationScopeIds = useMemo(() => {
    if (myRole === "admin") return null;
    if (myRole === "editor") return new Set([myPersonId, ...editorScope]);
    if (myRole === "contributor") return myPersonId ? new Set([myPersonId]) : new Set();
    return new Set();
  }, [myRole, myPersonId, editorScope]);
  const relationPeopleOptions = useMemo(() => {
    if (!relationScopeIds) return people;
    const filtered = {};
    Object.keys(people).forEach((id) => { if (relationScopeIds.has(id)) filtered[id] = people[id]; });
    return filtered;
  }, [relationScopeIds, people]);
  useEffect(() => {
    if (selectedId) fetchContactFor(selectedId);
    else setSelectedContact(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, canManage, currentPersonId]);

  // ── PORTA E KUSHTEVE ────────────────────────────────────────────────────
  //
  // 31 gusht 2026. Pronari: «te desktopi del, te iPhone-i jo».
  //
  // Nuk ishte as gjuha, as platforma — ishte RADHA. Ky efekt lexon `docs.terms`,
  // po vargu i varësive kishte vetëm `currentPersonId`. Pra ai xhironte një herë
  // të vetme: në çastin kur mësohej kush je.
  //
  // Nëse te ai çast dokumentet s'kishin mbërritur ende, `docs.terms` ishte null,
  // porta mbyllej — dhe s'kishte kush ta rihapte më, sepse ardhja e dokumenteve
  // nuk e nis efektin.
  //
  // Te desktopi rrjeti është i shpejtë dhe dokumentet mbërrijnë të parat, pra
  // rastësisht punonte. Te telefoni nisja është më e ngadaltë dhe radha
  // përmbyset. Një defekt që varet nga shpejtësia e rrjetit duket si «defekt i
  // iPhone-it» derisa e shikon vargun e varësive.
  //
  // Tani efekti pret edhe dokumentin. Pas pranimit s'rihapet: varësitë nuk
  // ndryshojnë më, dhe edhe po të ndryshonin, serveri do të thoshte se versioni
  // është pranuar.
  useEffect(() => {
    // Pa dokument kushtesh të publikuar, porta nuk ka çfarë të tregojë — dhe
    // s'ka kuptim t'i kërkosh dikujt të pranojë diçka që s'ekziston. Më parë
    // kjo mbulohej nga një tekst i ngulitur në kod; ai u hoq.
    if (!currentPersonId || !docs.terms) { setTermsGateOpen(false); return; }
    let gjallë = true;
    (async () => {
      try {
        const { data } = await supabase.rpc("rpc_get_terms_status", { p_person_id: currentPersonId });
        if (!gjallë) return;
        const row = data && data[0];
        if (!row || row.version !== currentTermsVersion) {
          setTermsChecked(false);
          setTermsGateOpen(true);
        }
      } catch (e) {}
    })();
    return () => { gjallë = false; };
  }, [currentPersonId, currentTermsVersion]);

  const acceptTerms = async () => {
    if (!termsChecked) { showToast(t("toast.checkBoxToConfirm")); return; }
    // Pranimi i kushteve nuk është preferencë — është regjistrim. Nëse shkrimi
    // dështon dhe porta mbyllet gjithsesi, kemi të dyja anët e humbura: ne s'kemi
    // provë se ai pranoi, dhe ai do të pyetet sërish në hyrjen e ardhshme pa e
    // ditur pse. Prandaj porta rri e hapur derisa serveri ta shkruajë.
    const { error } = await supabase.rpc("rpc_accept_terms", { p_person_id: currentPersonId, p_version: currentTermsVersion });
    if (error) { showToast(t("Not saved: ") + error.message); return; }
    setTermsGateOpen(false);
  };
  const refuseTerms = async () => {
    setTermsGateOpen(false);
    await handleSignOut();
    showToast(t("toast.signedOutTermsRefused"));
  };
  // Eksporti «backup» i familjes nga menuja e adminit u hoq (2 shtator 2026):
  // platforma ka soft-delete dhe kopje rezervë të veta, dhe një shkarkim me
  // një klik i krejt personave ishte vetëm një derë për keqpërdorim të të
  // dhënave. Eksporti i një familjeje bëhet vetëm nga stafi, në Admin Center,
  // me gjurmë. Etiketa «exported a backup» te historiku mbetet për rreshtat e vjetër.

  // FAZA 3 — eksport i pemës si POSTER (SVG vektorial → i mprehtë në çdo madhësi).
  // `sizeKey` cakton përmasat FIZIKE (mm) të skedës (A2…4A0) duke ruajtur
  // përpjesëtimin e pemës (pa shtrembërim) dhe duke zgjedhur orientimin që e
  // mbush më shumë fletën. Meqë SVG-ja është vektoriale, s'ka humbje cilësie në
  // asnjë përmasë — përmasa vetëm i thotë printerit sa i madh të dalë.
  // Fotot NGULITEN si base64 me rezolucionin e plotë ORIGJINAL (getPublicUrl
  // kthen objektin origjinal, jo miniaturë), kështu posteri është i pavarur nga
  // interneti dhe pa humbje. Kufizim: fontet mund të bien te fallback nëse
  // s'janë të instaluara aty ku hapet skeda. Korniza artistike / PDF vijnë më vonë.
  const eurFmt = (c) => (c / 100).toFixed(2).replace(".", ",") + " \u20AC";
  const PAPER_MM = { A2: [420, 594], A1: [594, 841], A0: [841, 1189], "2A0": [1189, 1682], "4A0": [1682, 2378] };

  // ---------------------------------------------------------------------------
  // PËRTEJ 4A0 — PËRMASË E LIRË, PA KOD TË RI
  //
  // Seria A mbaron te 4A0 (1682×2378 mm). Një pemë me katërqind veta është rreth
  // dymbëdhjetë herë më e gjerë se e lartë; e shtrënguar në një fletë √2 ajo zë
  // një brez të hollë në mes, dhe emrat bien nën dy milimetra. Fleta s'është e
  // vogël — është e formës së gabuar.
  //
  // Shtypshkronjat printojnë në RUL: gjerësia deri ~1,5 m, gjatësia praktikisht e
  // pakufizuar. Pra zgjidhja ekziston jashtë serisë A, dhe s'ka nevojë për asnjë
  // format të ri të ngulitur në kod.
  //
  // Formati lexohet nga EMRI i variantit të çmimit. Admini shton një rresht çmimi
  // me variant `Rul 300×90 cm` dhe gjeometria e ndjek. E njëjta gjë si kudo te ky
  // projekt: një gjë e re është një rresht te baza, jo një deploy.
  //
  // Pa këtë, një variant i tillë do të binte në heshtje te A1 — dhe klienti do të
  // paguante për rul e do të merrte një skedar A1. Pikërisht lloji i dështimit që
  // s'ankohet kurrë.
  //
  // Njësia parazgjedhje është cm; `mm` dhe `m` njihen shprehimisht. Jashtë
  // kufijve 10 cm – 6 m kthehemi te seria A, sepse një numër i shkruar gabim
  // s'duhet të prodhojë një fletë gjashtëdhjetë metra.
  // ---------------------------------------------------------------------------
  const paperMm = useCallback((sizeKey) => {
    const m = /(\d+(?:[.,]\d+)?)\s*[x×X]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm|m)?/.exec(String(sizeKey || ""));
    if (m) {
      const k = m[3] === "mm" ? 1 : m[3] === "m" ? 1000 : 10;   // pa njësi → cm
      const a = parseFloat(m[1].replace(",", ".")) * k;
      const b = parseFloat(m[2].replace(",", ".")) * k;
      // KUFIRI ËSHTË DY-SHKALLËSH, dhe kjo ka arsye.
      //
      // Deri 6 m pranohet edhe pa njësi, sepse `300×90` do të thotë centimetra
      // për këdo. Mbi 6 m kërkohet njësia E SHKRUAR — sepse aty s'jemi më te
      // poster: jemi te murali, dhe një mural nuk porositet aksidentalisht.
      //
      // Pikërisht kjo e kap gabimin e shtypit. Kush shkruan `3000x900` pa njësi
      // ka dashur `300x90`; pa këtë rregull ai do të merrte një skedar 30 metra.
      const njesiEShkruar = !!m[3];
      const tavani = njesiEShkruar ? 30000 : 6000;   // 30 m: përtej saj s'ka mur
      if ([a, b].every((v) => Number.isFinite(v) && v >= 100 && v <= tavani)) {
        return [Math.min(a, b), Math.max(a, b)];
      }
    }
    return PAPER_MM[sizeKey] || PAPER_MM.A1;
  }, []);
  // `task` (opsional) = rreshti i një porosie që pret veprën. Kur jepet, skedari
  // NUK shkarkohet — ngarkohet te porosia. Kjo është hallka që kërkoi pronari:
  // "personi blen formatin, gjeneron posterin, dhe ai poster shkon te
  // shtypshkronja." Pa të, skedari mbetej te telefoni dhe dikush duhej ta
  // dërgonte me dorë — duke shpresuar se është vërtet formati që u pagua.
  // POROSIT DHE BASHKËNGJIT, NË NJË HAP.
  //
  // Rrjedha e vjetër kërkonte që njeriu të kthehej pas pagesës dhe ta rikompozonte
  // posterin — të rigjente të njëjtat filtra, të njëjtin dizajn, të njëjtat
  // përjashtime. Askush s'e mban mend atë, dhe e dyta s'do të ishte kurrë e njëjta
  // me të parën. Prandaj vepra kapet NË ÇASTIN kur njeriu thotë "këtë dua".
  const placePosterOrder = async () => {
    if (!orderPick || !activeFamilyId) return;
    const offer = posterOffers.find((o) => o.service_key === orderPick.service_key);
    const price = (offer?.prices || []).find((p) => p.price_id === orderPick.price_id);
    if (!offer || !price) { setOrderErr(t("This service has no price yet — try again later.")); return; }
    const missing = (offer.fields || []).find((f) => f.required
      && (orderFields[f.key] == null || String(orderFields[f.key]).trim() === ""));
    if (missing) { setOrderErr(`${t("Still missing")}: ${missing.label || missing.key}`); return; }

    setOrderBusy(true); setOrderErr("");
    const input = {};
    (offer.fields || []).forEach((f) => {
      const v = orderFields[f.key];
      if (v != null && String(v).trim() !== "") input[f.key] = v;
    });
    const { data, error } = await supabase.rpc("rpc_place_single_order", {
      p_family_id: activeFamilyId, p_price_id: price.price_id, p_qty: 1, p_input: input,
    });
    if (error) { setOrderBusy(false); setOrderErr(error.message); return; }
    const row = Array.isArray(data) ? data[0] : data;
    // Formati vjen nga çmimi — jo nga zgjedhësi. `default` do të thotë se ky
    // shërbim s'ka formate (digjitali), ndaj mbetet ai që po shihet.
    const size = price.variant && price.variant !== "default" ? price.variant : posterSize;
    await exportTreeSVG(size, posterDesignId, {
      order_id: row.order_id, order_no: row.order_no, item_id: row.item_id, silent: true,
    });
    setOrderBusy(false); setOrderPick(null); setOrderFields({});
    setPosterModalOpen(false);
    showToast(t("Order {{no}} received — we prepare it and send it to you.", { no: row.order_no }));
    setLiveTick((n) => n + 1);   // rrahja e gjallë e rilexon vetë listën e porosive
  };

  // ---------------------------------------------------------------------------
  // CILI FORMAT I PËRSHTATET KËSAJ PEME
  //
  // Pyetja e pronarit ishte për "një sistem AI që e rekomandon madhësinë". Nuk
  // duhet: kjo është aritmetikë, dhe aritmetika e di përgjigjen saktë ndërsa një
  // model do ta hamendësonte.
  //
  // Zinxhiri është i shkurtër. Kanavaca e posterit ka një anë të gjatë në njësi;
  // fleta ka një anë të gjatë në milimetra. Raporti mes tyre e kthen çdo përmasë
  // nga njësi në milimetra — përfshirë shkronjën më të vogël te pema. Nëse ai
  // emër del 1,8 mm, posteri është i palexueshëm sado i shtrenjtë të jetë.
  //
  // Prandaj nuk numërojmë persona. Numri i personave është vetëm shkaku; pasoja
  // — dhe e vetmja gjë që i intereson blerësit — është sa i madh del emri.
  // ---------------------------------------------------------------------------

  // Kanavaca e fletës. E NJËJTA llogari që bën `exportTreeSVG` kur s'ka dizajn.
  // Rri këtu jashtë që të dyja ta thërrasin: po ta kopjoja, rekomandimi dhe
  // skedari do të binin nga sinkroni pa u vënë re nga askush.
  const sheetCanvas = useCallback((sizeKey, cw, ch) => {
    const [sS, sL] = paperMm(sizeKey);
    const portrait = (cw / Math.max(1, ch)) <= (sS / sL);
    const sheetR = portrait ? (sS / sL) : (sL / sS);
    const margin = Math.max(cw, ch) * 0.06;
    let W = cw + margin * 2, H = ch + margin * 2;
    if (W / H > sheetR) H = W / sheetR; else W = H * sheetR;
    return { W, H, portrait, longMm: sL };
  }, [paperMm]);

  // Pragjet janë për një poster të parë nga rreth gjysmë metri. Nën 2,1 mm
  // emri lexohet vetëm duke u afruar; mbi 3 mm lexohet rehat nga këmbë larg.
  const MM_MIRE = 3.0, MM_KUFI = 2.1;


  const exportTreeSVG = async (sizeKey = "A1", designId = "", task = null, preview = false, mural = null) => {
    // KUJDES: kontejneri i pemës përmban edhe SVG dekorative (EagleWatermark /
    // KosovoWatermark / CrestWatermark) PARA SVG-së së pemës. Prandaj marrim
    // shprehimisht SVG-në e pemës me id (jo querySelector("svg") që kthen filigranin).
    // ── ROJA E SKEDARIT (23 gusht 2026 — defekt i raportuar) ─────────────────
    //
    // Pronari: *"kur shërbimi ndryshohet me pagesë prapë opsioni download është
    // i mundshëm. Nuk duhet të mund të bëhet download, vetëm shiko demo."*
    //
    // Ka të drejtë, dhe faji ishte i vendit ku e vura rojën: e vura te BUTONI.
    // Po `exportTreeSVG` thirret nga tre vende — butoni i shkarkimit, "Shkarko
    // panelet" te murali, dhe bashkëngjitja te porosia — dhe unë kisha mbyllur
    // vetëm të parin. Murali nxjerr të njëjtën pemë me të njëjtën cilësi.
    //
    // Roja i takon FUNKSIONIT, jo butonave: tre butona janë tre shanse për ta
    // harruar të katërtin (rregulli 25). Dhe këtu ka rëndësi vërtet — një skedar
    // i lëshuar s'kthehet mbrapsht.
    //
    // Dy përjashtime, të dyja të ligjshme:
    //   `preview`  mostra e vogël me vulë — pikërisht "shiko demo"
    //   `task`     bashkëngjitja te një porosi TASHMË E PAGUAR
    if (!preview && !task && !featureAllowed("poster_pro", posterExportAllowed)) {
      showToast(t("The poster is bought first — here you can only see the sample."));
      return;
    }
    const svgEl = containerRef.current?.querySelector("#pef-tree-svg") || containerRef.current?.querySelector("svg");
    const posIds = Object.keys(positions);
    if (!svgEl || !posIds.length) { showToast(t("The tree is not ready to export.")); return; }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    posIds.forEach((id) => {
      const p = positions[id];
      minX = Math.min(minX, p.x * H_SPACING);
      minY = Math.min(minY, p.y * V_SPACING);
      maxX = Math.max(maxX, p.x * H_SPACING + CARD_W);
      maxY = Math.max(maxY, p.y * V_SPACING + CARD_H);
    });
    if (![minX, minY, maxX, maxY].every(Number.isFinite)) { showToast(t("The tree is not ready to export.")); return; }
    const cw = maxX - minX, ch = maxY - minY;
    const SVGNS = "http://www.w3.org/2000/svg";
    // Roja e fundit para bojës: një dizajn i pablerë nuk hyn te skedari, edhe
    // sikur `designId` të vinte nga gjendje e mbetur a nga një klik i vjetër.
    const design = (designId && !featureAllowed("poster_designs", true))
      ? null
      : posterDesigns.find((d) => d.id === designId);
    const clone = svgEl.cloneNode(true);
    clone.setAttribute("xmlns", SVGNS);
    clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    // Opsione printimi (portret galerie): heqja selektive PARA kompozimit.
    if (removeStatusIcons) {
      clone.querySelectorAll('[data-status-icon="1"]').forEach((el) => el.remove());
      // ⚠ 4 shtator 2026 — këtu ishte `[title="I/e ndjerë"]`: një selektor DOM
      // që kërkonte TEKSTIN E PËRKTHYER. Titulli vjen nga `t("Deceased")`, pra
      // sapo dikush e përdor aplikacionin anglisht, ky rresht nuk përputh asgjë
      // dhe pëllumbi mbetet te posteri edhe kur njeriu kërkoi ta heqë. Asnjë
      // gabim, asnjë shenjë — thjesht opsioni nuk vepron.
      //
      // Rreshti mbi këtë e mbulon tashmë rastin: pëllumbi vizatohet me
      // `data-status-icon="1"`. Selektorin sipas tekstit e heqim; një shenjë
      // strukturore nuk ndryshon me gjuhën.
    }
    // Hiq figurat/fotot e personave: fotot + rrathët/unazat e avatarit të çdo kartele.
    if (removePhotos) clone.querySelectorAll('g[data-pid] image, g[data-pid] circle, g[data-pid] ellipse').forEach((el) => el.remove());
    // Hiq ngjyrën rreth kartelave (konturat/unazat me ngjyrë dege) — për poster
    // vetëm-foto. Prek rrathët/elipset (unazat rrethore) DHE drejtkëndëshat.
    if (removeCardColor) clone.querySelectorAll('g[data-pid] rect, g[data-pid] circle, g[data-pid] ellipse').forEach((r) => r.setAttribute("stroke", "none"));
    // Ngjyra e shkronjave (opsionale) + hija/kontura DINAMIKE (opsionale). Halo
    // llogaritet nga ngjyra përfundimtare e tekstit → lexohet mbi çdo sfond.
    const parseColor = (c) => {
      if (!c) return null;
      c = c.trim();
      if (c[0] === "#") { let h = c.slice(1); if (h.length === 3) h = h.split("").map((x) => x + x).join(""); const n = parseInt(h, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
      const m = c.match(/rgba?\(([^)]+)\)/);
      if (m) { const p = m[1].split(",").map((x) => parseFloat(x)); return [p[0], p[1], p[2]]; }
      return null;
    };
    const textIsLight = (c) => { const rgb = parseColor(c); if (!rgb) return false; return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255 > 0.6; };
    const COLOR_MAP = { white: "#F6F2E7", black: "#2A2118", gold: "#C9A227" };
    clone.querySelectorAll('g[data-pid] text').forEach((t) => {
      let fill = t.style.fill || t.getAttribute("fill") || "";
      if (textColor !== "auto" && COLOR_MAP[textColor]) { fill = COLOR_MAP[textColor]; t.style.fill = fill; t.setAttribute("fill", fill); }
      if (textHalo) {
        t.setAttribute("paint-order", "stroke");
        t.setAttribute("stroke", textIsLight(fill) ? "rgba(0,0,0,0.62)" : "rgba(255,255,255,0.78)");
        t.setAttribute("stroke-width", "0.7");
        t.setAttribute("stroke-linejoin", "round");
      }
    });
    const g = Array.from(clone.children).find((c) => c.tagName && c.tagName.toLowerCase() === "g");
    const blobToDataUrl = (b) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b); });
    let vbX, vbY, vbW, vbH;
    let designName = "thjeshte";

    if (!design) {
      // PA DIZAJN — FLETË E VËRTETË, jo kuti sa pema.
      //
      // Deri sot këtu merrej thjesht kutia kufizuese e pemës plus 90 njësi marzh.
      // Rezultati kishte përpjesëtimin e pemës — zakonisht i gjerë e i ulët — dhe
      // pastaj shtypej "brenda" fletës A1. Pra skedari NUK ishte A1; ishte diçka
      // që hyn në A1 dhe lë brezat bosh anash.
      //
      // Kjo s'u vu re derisa doli mostra, sepse mostra e tregon pikërisht atë që
      // do të printohet — dhe pronari e pa menjëherë: "nuk i përngjan aspak
      // panoramës, nuk janë kornizat". Kishte të drejtë: pa dizajn, s'kishte fletë.
      //
      // Tani kanavaca ËSHTË fleta, me përpjesëtimin e formatit të zgjedhur (√2), në
      // atë orientim që e mbush më mirë pema. Pema qendërzohet brenda saj me marzhe
      // të përpjesëtuara. Kështu mostra dhe skedari tregojnë të njëjtën gjë, dhe ajo
      // gjë është një poster.
      // Llogaria rri te `sheetCanvas` sepse edhe rekomandimi i formatit e përdor.
      // Dy kopje të së njëjtës gjeometri do të thoshin se një ditë shiriti të
      // premton 3,7 mm dhe skedari të nxjerr 2,9 — pa u ankuar asnjë gabim.
      const { W, H } = sheetCanvas(sizeKey, cw, ch);
      vbX = minX + cw / 2 - W / 2;
      vbY = minY + ch / 2 - H / 2;
      vbW = W; vbH = H;
      if (g) g.setAttribute("transform", "translate(0,0) scale(1)");
      const bg = document.createElementNS(SVGNS, "rect");
      bg.setAttribute("x", vbX); bg.setAttribute("y", vbY);
      bg.setAttribute("width", vbW); bg.setAttribute("height", vbH);
      bg.setAttribute("fill", COLORS.bg);
      clone.insertBefore(bg, g || clone.firstChild);
      // Një kornizë e hollë ari, sa për ta bërë fletë e jo prerje ekrani. Nuk është
      // "dizajn" — ata blihen veçmas; është vija që i jep skedarit fund.
      const fr = document.createElementNS(SVGNS, "rect");
      const inset = Math.min(vbW, vbH) * 0.028;
      fr.setAttribute("x", vbX + inset); fr.setAttribute("y", vbY + inset);
      fr.setAttribute("width", vbW - inset * 2); fr.setAttribute("height", vbH - inset * 2);
      fr.setAttribute("fill", "none");
      fr.setAttribute("stroke", COLORS.goldSoft || "rgba(184,147,90,0.35)");
      fr.setAttribute("stroke-width", Math.max(1, Math.min(vbW, vbH) * 0.0022));
      clone.appendChild(fr);
    } else if (design.builtin_key && STYLE_CFG[design.builtin_key]) {
      // STIL I INTEGRUAR — renderohet me kod, PËRSHTATSHËM me pemën: orientimi
      // (horizontal për pema të gjera), marzhe të përpjesëtuara, dhe TITULLI me
      // emrin e familjes. (Zgjidh problemin e hapësirës boshe të imazhit statik.)
      designName = design.builtin_key || "stil";
      const cfg = STYLE_CFG[design.builtin_key];
      const unit = Math.max(140, Math.min(460, Math.max(cw, ch) * 0.05));
      const mSide = cfg.minimal ? unit * 0.85 : unit;
      const mTop = showFamilyTitle ? (cfg.minimal ? unit * 1.95 : unit * 2.45) : (cfg.minimal ? unit * 0.85 : unit);
      const mBot = cfg.minimal ? unit * 0.85 : unit;
      vbX = minX - mSide; vbY = minY - mTop; vbW = cw + mSide * 2; vbH = ch + mTop + mBot;
      if (g) g.setAttribute("transform", "translate(0,0) scale(1)");
      const parseSvg = (str) => { const doc = new DOMParser().parseFromString(`<svg xmlns="${SVGNS}">${str}</svg>`, "image/svg+xml"); return Array.from(doc.documentElement.childNodes); };
      const decor = buildPosterDecor(design.builtin_key, { x: vbX, y: vbY, w: vbW, h: vbH }, showFamilyTitle ? (effTreeName || t("Family Tree")).trim() : "", showFamilyTitle ? t("Family tree") : "", unit);
      if (decor) {
        parseSvg(`<defs>${decor.defs}</defs>${decor.back}`).forEach((n) => clone.insertBefore(document.importNode(n, true), g || null));
        parseSvg(decor.front).forEach((n) => clone.appendChild(document.importNode(n, true)));
      }
    } else {
      // ME DIZAJN TË NGARKUAR (imazh) — kanavaca = përmasat e dizajnit; pema
      // shkallëzohet e qendërzohet brenda "qendrës së sigurt" (~84%×78%).
      showToast(t("Loading the design…"));
      designName = (design.name || "dizajn").replace(/[^a-z0-9]+/gi, "-").slice(0, 40);
      const durl = supabase.storage.from("poster-designs").getPublicUrl(design.storage_path).data.publicUrl;
      let CW = 1680, CH = 2376, designDataUrl = null;
      try {
        const r = await fetch(durl, { mode: "cors" });
        const b = await r.blob();
        designDataUrl = await blobToDataUrl(b);
        const isSvg = (design.mime || "").includes("svg") || (design.storage_path || "").toLowerCase().endsWith(".svg");
        if (isSvg) {
          const txt = await b.text();
          const m = txt.match(/viewBox\s*=\s*["']([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)/);
          if (m) { CW = parseFloat(m[3]); CH = parseFloat(m[4]); }
          else { const mw = txt.match(/width\s*=\s*["']([\d.]+)/), mh = txt.match(/height\s*=\s*["']([\d.]+)/); if (mw && mh) { CW = parseFloat(mw[1]); CH = parseFloat(mh[1]); } }
        } else {
          const dim = await new Promise((res) => { const im = new Image(); im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight }); im.onerror = () => res(null); im.src = designDataUrl; });
          if (dim && dim.w && dim.h) { CW = dim.w; CH = dim.h; }
        }
      } catch { /* nëse dështon ngarkimi, vazhdojmë me dimensione parazgjedhje pa imazh */ }
      // Rezervo një brez lart për titullin (emrin e familjes) kur është ndezur.
      const titleBand = showFamilyTitle ? CH * 0.08 : 0;
      const sx = CW * 0.08, sy = CH * 0.14 + titleBand, sw = CW * 0.84, sh = CH * 0.78 - titleBand;
      const k = Math.min(sw / cw, sh / ch);
      const tx = sx + (sw - cw * k) / 2 - minX * k;
      const ty = sy + (sh - ch * k) / 2 - minY * k;
      if (g) g.setAttribute("transform", `translate(${tx.toFixed(2)},${ty.toFixed(2)}) scale(${k.toFixed(4)})`);
      vbX = 0; vbY = 0; vbW = CW; vbH = CH;
      if (designDataUrl) {
        const img = document.createElementNS(SVGNS, "image");
        img.setAttribute("href", designDataUrl);
        img.setAttribute("x", 0); img.setAttribute("y", 0);
        img.setAttribute("width", CW); img.setAttribute("height", CH);
        img.setAttribute("preserveAspectRatio", "none");
        if (design.kind === "frame") clone.appendChild(img);           // kornizë → MBI pemë
        else clone.insertBefore(img, g || clone.firstChild);           // sfond → PAS pemës
      }
      // Titulli (emri i familjes) MBI dizajnin e ngarkuar — ar me halo të errët
      // që të lexohet mbi sfonde të çelëta e të errëta njësoj.
      if (showFamilyTitle) {
        const tfs = CH * 0.04;
        const titleY = CH * 0.14 + titleBand * 0.58;
        const title = (effTreeName || t("Family Tree")).trim();
        const tSvg = `<text x="${(CW / 2).toFixed(1)}" y="${titleY.toFixed(1)}" text-anchor="middle" font-family="Georgia,'Times New Roman',serif" font-weight="700" font-size="${tfs.toFixed(1)}" fill="#C9A227" paint-order="stroke" stroke="rgba(0,0,0,0.55)" stroke-width="${(tfs * 0.06).toFixed(1)}" stroke-linejoin="round">${escapeXml(title)}</text><text x="${(CW / 2).toFixed(1)}" y="${(titleY + tfs * 0.72).toFixed(1)}" text-anchor="middle" font-family="Georgia,serif" font-size="${(tfs * 0.34).toFixed(1)}" fill="#C9A227" opacity="0.85" paint-order="stroke" stroke="rgba(0,0,0,0.5)" stroke-width="${(tfs * 0.03).toFixed(1)}" stroke-linejoin="round">${t("Family tree")}</text>`;
        const doc = new DOMParser().parseFromString(`<svg xmlns="${SVGNS}">${tSvg}</svg>`, "image/svg+xml");
        Array.from(doc.documentElement.childNodes).forEach((n) => clone.appendChild(document.importNode(n, true)));
      }
    }
    clone.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);
    // Përmasat fizike (mm): përshtat posterin brenda fletës së zgjedhur duke ruajtur
    // përpjesëtimin; zgjedh portret ose peizazh — atë që e mbush më shumë fletën.
    const [pShort, pLong] = paperMm(sizeKey);
    const scale = Math.max(Math.min(pShort / vbW, pLong / vbH), Math.min(pLong / vbW, pShort / vbH));
    const physW = Math.round(vbW * scale * 10) / 10, physH = Math.round(vbH * scale * 10) / 10;
    clone.setAttribute("width", `${physW}mm`);
    clone.setAttribute("height", `${physH}mm`);
    // Ngul fotot si base64 (rezolucion i plotë) → poster i pavarur, pa humbje.
    showToast(t("Preparing the {{size}} poster… (embedding photos)", { size: sizeKey }));
    const imgEls = Array.from(clone.querySelectorAll("image"));
    const hrefOf = (el) => el.getAttribute("href") || el.getAttribute("xlink:href");
    const uniqueUrls = Array.from(new Set(imgEls.map(hrefOf).filter((u) => u && !u.startsWith("data:"))));
    const dataMap = {};
    await Promise.all(uniqueUrls.map(async (u) => {
      try {
        const resp = await fetch(u, { mode: "cors" });
        if (!resp.ok) return;
        const b = await resp.blob();
        dataMap[u] = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.onerror = rej;
          fr.readAsDataURL(b);
        });
      } catch { /* nëse dështon, lëre referencën URL origjinale */ }
    }));
    imgEls.forEach((el) => {
      const u = hrefOf(el);
      if (u && dataMap[u]) { el.setAttribute("href", dataMap[u]); el.removeAttribute("xlink:href"); }
    });
    const svgStr = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${svgStr}`], { type: "image/svg+xml;charset=utf-8" });
    // ---------------------------------------------------------------------
    // MOSTRA (Faza 1f) — çfarë sheh njeriu PARA se të paguajë.
    //
    // I njëjti poster, i njëjti dizajn, i njëjti format — por i rasterizuar në
    // më së shumti 1000px dhe me vulën e ujit diagonale. Pra: mjaft për të parë
    // se ç'po blen, dhe qartësisht jo mjaft për ta shtypur.
    //
    // NDERSHMËRI: kjo E VËSHTIRËSON kopjimin, nuk e ndalon. Kushdo mund të bëjë
    // një pamje ekrani. Por ajo pamje do të jetë e vogël dhe e vulosur — dhe kjo
    // është e gjithë ajo që bën çdo dyqan fotografish në botë. Mbrojtja e vërtetë
    // është se skedari vektorial me foto full-res NUK i shkon kurrë shfletuesit
    // derisa porosia të paguhet.
    if (preview) {
      const url = URL.createObjectURL(blob);
      try {
        const img = await new Promise((res, rej) => {
          const im = new Image();
          im.onload = () => res(im); im.onerror = rej; im.src = url;
        });
        const MAXW = 1000;
        const scale = Math.min(1, MAXW / (img.naturalWidth || MAXW));
        const cnv = document.createElement("canvas");
        cnv.width = Math.max(1, Math.round((img.naturalWidth || MAXW) * scale));
        cnv.height = Math.max(1, Math.round((img.naturalHeight || MAXW) * scale));
        const ctx = cnv.getContext("2d");
        ctx.drawImage(img, 0, 0, cnv.width, cnv.height);
        // Vula: e pjerrët, e përsëritur, gjysmë e tejdukshme. E lexueshme sa duhet
        // për ta bërë të papërdorshme si produkt, jo aq sa ta fshehë pemën.
        const mark = `${(effTreeName || "Pema e Familjes")} · ${t("SAMPLE")}`;
        ctx.save();
        ctx.translate(cnv.width / 2, cnv.height / 2);
        ctx.rotate(-Math.PI / 6);
        ctx.font = `${Math.round(cnv.width * 0.045)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.fillStyle = "rgba(255,255,255,0.22)";
        ctx.strokeStyle = "rgba(0,0,0,0.14)";
        ctx.lineWidth = 1;
        const step = Math.round(cnv.width * 0.16);
        for (let yy = -cnv.height; yy < cnv.height; yy += step) {
          for (let xx = -cnv.width; xx < cnv.width * 1.2; xx += Math.round(cnv.width * 0.62)) {
            ctx.fillText(mark, xx, yy);
            ctx.strokeText(mark, xx, yy);
          }
        }
        ctx.restore();
        return cnv.toDataURL("image/jpeg", 0.72);
      } catch (_e) {
        return null;
      } finally { URL.revokeObjectURL(url); }
    }

    if (task) {
      // Rruga NIS me order_id sepse polica e storage-it e lexon pikërisht atë
      // segment për të vendosur nëse lejohet shkrimi. Çdo formë tjetër emri do
      // të refuzohej te serveri — dhe mirë që refuzohet.
      const path = `${task.order_id}/poster-${sizeKey}-${stampForFilename()}.svg`;
      const up = await supabase.storage.from("order-artwork")
        .upload(path, blob, { contentType: "image/svg+xml", upsert: true });
      if (up.error) { showToast(t("The poster could not be uploaded") + ": " + up.error.message); return; }
      const { error: attErr } = await supabase.rpc("rpc_attach_order_artwork", {
        p_item_id: task.item_id, p_path: path,
        p_meta: { size: sizeKey, mm: `${physW}x${physH}`, design: designName },
      });
      if (attErr) { showToast(t("The poster could not be attached") + ": " + attErr.message); return; }
      setArtworkTasks((xs) => xs.filter((x) => x.item_id !== task.item_id));
      if (!task.silent) {
        setPosterModalOpen(false);
        showToast(t("Poster {{size}} attached to order {{no}} — it goes to print with it.", { size: sizeKey, no: task.order_no }));
      }
      logActivity("artwork_attached", null, `${task.order_no} · ${sizeKey}`);
      return;
    }
    // -------------------------------------------------------------------
    // MURALI — I NJËJTI SKEDAR, I PRERË NË PANELE
    //
    // Çelësi i gjithë kësaj: pema NUK rikompozohet. Skedari është ndërtuar
    // tashmë, me foto e me gjithçka; paneli është vetëm një `viewBox` tjetër mbi
    // po atë përmbajtje. Pra asnjë rresht i algoritmit të pemës nuk preket, dhe
    // paneli 3 s'ka si të mos përputhet me panelin 4 — janë e njëjta vizatim.
    //
    // MBIVENDOSJA është për montuesin, jo për ne: çdo panel merr disa milimetra
    // shtesë nga fqinji, që prerja në mur të bëhet brenda zonës së përbashkët.
    // Pa të, një gabim prej një milimetri lë vijë të bardhë përgjatë gjithë murit.
    if (mural && mural.cols >= 1 && mural.rows >= 1 && (mural.cols > 1 || mural.rows > 1)) {
      const njMm = vbW / Math.max(1e-6, physW);          // njësi kanavace për mm
      const ov = Math.max(0, mural.overlapMm || 5) * njMm;
      const pW = vbW / mural.cols, pH = vbH / mural.rows;
      let n = 0;
      for (let r = 0; r < mural.rows; r++) {
        for (let c = 0; c < mural.cols; c++) {
          n += 1;
          const x0 = vbX + c * pW - (c > 0 ? ov : 0);
          const y0 = vbY + r * pH - (r > 0 ? ov : 0);
          const w0 = pW + (c > 0 ? ov : 0) + (c < mural.cols - 1 ? ov : 0);
          const h0 = pH + (r > 0 ? ov : 0) + (r < mural.rows - 1 ? ov : 0);
          const pc = clone.cloneNode(true);
          pc.setAttribute("viewBox", `${x0} ${y0} ${w0} ${h0}`);
          pc.setAttribute("width", `${Math.round(w0 / njMm * 10) / 10}mm`);
          pc.setAttribute("height", `${Math.round(h0 / njMm * 10) / 10}mm`);
          // Shenjat e prerjes + numri i panelit. Numri shkon BRENDA zonës së
          // mbivendosjes: pritet bashkë me të, pra nuk mbetet i dukshëm në mur.
          const mk = 8 * njMm, tk = Math.max(0.3 * njMm, w0 * 0.0004);
          const line = (xa, ya, xb, yb) =>
            `<line x1="${xa}" y1="${ya}" x2="${xb}" y2="${yb}" stroke="#000" stroke-width="${tk}" opacity="0.85"/>`;
          let marks = "";
          [[x0 + (c > 0 ? ov : 0), y0 + (r > 0 ? ov : 0)],
           [x0 + w0 - (c < mural.cols - 1 ? ov : 0), y0 + (r > 0 ? ov : 0)],
           [x0 + (c > 0 ? ov : 0), y0 + h0 - (r < mural.rows - 1 ? ov : 0)],
           [x0 + w0 - (c < mural.cols - 1 ? ov : 0), y0 + h0 - (r < mural.rows - 1 ? ov : 0)]]
            .forEach(([mx, my]) => { marks += line(mx - mk, my, mx + mk, my) + line(mx, my - mk, mx, my + mk); });
          marks += `<text x="${x0 + w0 / 2}" y="${y0 + 14 * njMm}" text-anchor="middle" font-family="monospace" font-size="${10 * njMm}" fill="#000" opacity="0.8">${r + 1}-${c + 1}  ·  ${n}/${mural.cols * mural.rows}</text>`;
          const gm = document.createElementNS(SVGNS, "g");
          gm.setAttribute("data-mural-marks", "1");
          gm.innerHTML = marks;
          pc.appendChild(gm);
          const pb = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(pc)}`],
                              { type: "image/svg+xml;charset=utf-8" });
          const pu = URL.createObjectURL(pb);
          const pa = document.createElement("a");
          pa.href = pu;
          pa.download = `mural-${(effTreeName || "familjare").replace(/\s+/g, "-")}-r${r + 1}c${c + 1}-${stampForFilename()}.svg`;
          document.body.appendChild(pa); pa.click(); document.body.removeChild(pa);
          URL.revokeObjectURL(pu);
          // Shfletuesit i bllokojnë shkarkimet e njëpasnjëshme; një frymë mes tyre.
          await new Promise((res) => setTimeout(res, 250));
        }
      }
      showToast(t("{{n}} mural panels downloaded — {{w}}×{{h}} mm each, {{o}} mm overlap.",
        { n: mural.cols * mural.rows,
          w: Math.round(physW / mural.cols), h: Math.round(physH / mural.rows),
          o: mural.overlapMm || 5 }));
      return;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pema-${(effTreeName || "familjare").replace(/\s+/g, "-")}-${designName}-${sizeKey}-${stampForFilename()}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    // Matësi i popullaritetit — rrit numëruesin e printimit për dizajnin e zgjedhur.
    if (design) { try { supabase.rpc("rpc_poster_design_printed", { p_id: design.id }); } catch { /* metrike jo-kritike */ } }
    logActivity("export_poster", null, `${sizeKey}${design ? " · " + (design.name || "dizajn") : ""}`);
    showToast(`Posteri ${sizeKey} u shkarkua (${physW}×${physH} mm) — vektorial, foto full-res.`);
  };

  const peopleCount = Object.keys(people).length;
  // FAZA 4: `locked` përdor `treeHasAnyPeople` (jo `peopleCount`), sepse
  // `people` tani është i kufizuar sipas familjes (RLS) — peopleCount është
  // gjithmonë 0 për këdo të pa-identifikuar, edhe kur pema ka anëtarë.
  // FIX (raportuar: "në telefon nuk shfaqet fare login"): pa sesion identifikimi,
  // pema ngarkohet BOSH (RLS → 0 anëtarë), pra `treeHasAnyPeople` është false dhe
  // ekrani i hyrjes NUK shfaqej — përdoruesi mbetej te një app bosh pa asnjë
  // mënyrë për t'u identifikuar. Tani: nëse ka MBARUAR ngarkimi i sesionit dhe
  // NUK ka sesion → detyro ekranin e hyrjes (kudo, edhe kur pema duket bosh).
  // FIX (raportuar nga useri — "jam login por nuk shfaqet asgjë, as meny"):
  // një llogari E KYÇUR por E PALIDHUR me asnjë person nuk sheh dot asnjë
  // rresht `people` (RLS), ndaj `treeHasAnyPeople` dilte false dhe app-i e
  // trajtonte si "pemë boshe" — faqe normale, pa avatar, pa meny, pa asnjë
  // rrugëdalje: përdoruesi mbetej i ngecur, s'dilte dot as nga llogaria.
  // Tani kjo gjendje e çon te ekrani i identifikimit, ku shpjegohet çfarë
  // ndodh dhe jepen hapat (bashkohu me kod / krijo familje / dil).
  // `linkedPersonChecked` te kushti i dytë është shtesa e 20 gushtit, dhe pa të
  // ekrani i hyrjes pulsonte mbi pemë në çdo rifreskim. Kushti do të thotë "ka
  // pemë me anëtarë, por ti s'je asnjëri prej tyre" — një gjendje e vërtetë
  // bllokimi. Por gjatë ngarkimit ai lexohej edhe kur përgjigjja thjesht s'kishte
  // ardhur ende: `treeHasAnyPeople` mbërrin para `currentPersonId`, dhe për një
  // çast dukej sikur je i huaj në familjen tënde. Tani pyetja bëhet vetëm pasi
  // zgjidhja e personit ka përfunduar.
  // ══════════════════════════════════════════════════════════════════════════
  // KUSHTI I KATËRT  (31 gusht 2026)
  //
  // Raportuar: ndonjëherë ekrani «llogaria nuk është lidhur» del si duhet, po
  // nëse e mbyll aplikacionin dhe e hap sërish pa prekur gjë, ai s'del më —
  // hapet aplikacioni, me titullin thjesht «Familja».
  //
  // Tri kushtet e mësipërme mbështeten te FLAMUJ (`notLinkedYet`,
  // `personResolved`, `treeHasAnyPeople`) që i ngre një zinxhir asinkron. Nëse
  // ai zinxhir këputet diku — një RPC që s'kthen, një slug i ruajtur që s'gjendet
  // më, një nisje ku rrjeti ende s'është gati — asnjë flamur s'ngrihet dhe
  // porta mbetet e hapur. Pikërisht kështu duket: herën e parë zinxhiri mbaroi,
  // herën e dytë jo.
  //
  // Ky kusht s'pyet asnjë flamur. Pyet gjendjen që shihet me sy: je i kyçur,
  // ngarkimi ka mbaruar, nuk ka asnjë familje të zgjedhur dhe asnjë person.
  // S'ekziston asnjë rast i ligjshëm ku aplikacioni duhet të vizatohet ashtu.
  // Përjashtohet vetëm slug-u që s'u gjet — ai e ka ekranin e vet me shpjegim.
  const paFamilje = !!authSession && !authSessionLoading && !loading
    && !activeFamilyId && !currentPersonId && slugFamilyStatus !== "not_found";
  // PROVAT1 — «dalin te orfanët pa asnjë shenjë ç'ka ndodhur». Sapo dikush
  // sheh ekranin «pa familje», shënohet (një herë për sesion): admini e di që
  // hyri dhe u ndal aty, edhe kur s'shtypi asgjë më.
  useEffect(() => {
    if (!paFamilje) return;
    try { if (sessionStorage.getItem("pef_gjurma_pa_familje")) return; sessionStorage.setItem("pef_gjurma_pa_familje", "1"); } catch (_e) { /* asgjë */ }
    shenoGjurmen("no_family_screen", null, (myLinkedFamilies || []).length ? `${myLinkedFamilies.length} linked families` : null, true, null);
  }, [paFamilje]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = (!authSessionLoading && !authSession)
    || (personResolved && !currentPersonId && treeHasAnyPeople === true)
    || (!!authSession && notLinkedYet && !currentPersonId)
    || paFamilje;

  const recentEvents = useMemo(() => {
    const now = Date.now();
    const windowMs = 5 * 24 * 60 * 60 * 1000; // 5 days
    const additions = [];
    const deaths = [];
    // ⚠ 5 shtator 2026 — VDEKJET LLOGARITESHIN NGA `updated_at`, JO NGA DATA E
    // VDEKJES. Pra çdo paraardhës i shtuar ose i redaktuar sot dilte te rrjedha
    // si «u nda nga jeta», edhe kur kishte vdekur më 1948. Kur një familje
    // sillte një degë të tërë, rrjedha mbushej me dhjetëra njoftime zie për
    // njerëz të vdekur para tre brezash — pikërisht ankesa e pronarit.
    //
    // Tani kushti është vetë ngjarja: a ka NDODHUR vdekja së fundmi. Dritarja
    // është më e gjerë se 5 ditët e shtimeve, sepse një vdekje shpesh
    // regjistrohet me vonesë — po mbetet e lidhur me ngjarjen, jo me çastin kur
    // dikush e preku rreshtin. Kjo vetrregullohet: asnjë pastrim me dorë.
    const DRITARJA_E_VDEKJES = 45 * 24 * 60 * 60 * 1000;
    Object.values(people).forEach((p) => {
      if (p.created_at && now - new Date(p.created_at).getTime() < windowMs) additions.push(p);
      if (p.death_date) {
        const kur = new Date(p.death_date).getTime();
        if (!Number.isNaN(kur) && now - kur >= 0 && now - kur < DRITARJA_E_VDEKJES) deaths.push(p);
      }
    });
    return { additions, deaths };
  }, [people]);

  // FAZA 25 — "Family Feed". BASHKON burime TASHMË EKZISTUESE (asnjë RPC e
  // re): family_history_events (kujtime, publike për krejt familjen),
  // unlockedLegacyMessages (Legacy të zbuluara), recentEvents (persona të
  // shtuar/ndarë nga jeta, tashmë llogaritur më sipër), dhe
  // ditëlindje/përvjetorë (të njëjtat funksione si te "Statistika"). Ndarë
  // në dy seksione: "Së shpejti" (parashikuese — brenda 7 ditëve), dhe
  // "Kujtime & Legacy" (retrospektive — çka u shtua/zbulua së fundmi).
  // FAZA 29 — nxjerrë si vlerë e ripërdorshme (më parë llogaritej vetëm
  // brenda modalit "Statistika") — tani i duhet edhe dashboard-it të ri të
  // desktopit. Vetë llogaritja e pandryshuar, thjesht jo më e kyçur brenda
  // një IIFE-je lokale.
  // Numërimi i brezave — llogaritet GJITHMONË nga rrënja aktuale poshtë:
  // niveli i çdo personi = 1 + MAKS(nivelet e prindërve që ekzistojnë ende te
  // pema). Kush s'ka prind (ose prindi u fshua/është në kosh) është rrënjë
  // (nivel 0). Marrja e MAKS-it mbi prindërit e rregullon rastin e bashkëshortit
  // "të ardhur në familje" (dy prindër në nivele të ndryshme) që më parë
  // nën-numërohej. I mbrojtur nga ciklet.
  // Një zbatim i vetëm i brezave: `nivelePerPerson` te moduli. Më parë e njëjta
  // ecje kishte dy kopje — këtu dhe te statistikat — dhe dy kopje të një
  // llogarie janë dy përgjigje që një ditë ndahen.
  const generationsCount = useMemo(() => {
    const all = Object.values(people);
    if (all.length === 0) return 0;
    const nivelet = nivelePerPerson(people);
    let maxLevel = 0;
    for (const p of all) maxLevel = Math.max(maxLevel, nivelet[p.id] || 0);
    return maxLevel + 1;
  }, [people]);

  const feedUpcomingAll = useMemo(() => {
    // MUZEU4 — te një muze nuk ka ditëlindje për t'u uruar. Figurat e një
    // ekspozite nuk presin urime, dhe një listë «së shpejti» me përvjetorë
    // vdekjesh historike do të ishte ton i gabuar, jo veçori e pambaruar.
    if (eshteMuze) return [];
    const all = Object.values(people);
    const birthdays = all
      // DITA1 — një datë e përafërt nuk urohet. Një njoftim «Sot është
      // ditëlindja e X» mbi një datë të hamendësuar është më keq se asnjë.
      .filter(ditaENjohur)
      .map((p) => ({ p, daysUntil: birthdayDaysUntil(p.birth_date) }))
      .filter((x) => x.daysUntil !== null && x.daysUntil >= 0 && x.daysUntil <= 7)
      // NJOFTIME4 — te «Së shpejti» ditëlindja e një të ndjeri lexohej si e
      // çdo të gjalli («Ditëlindja e X»). Dita e tij te Rrjedha është
      // përkujtim që nga 5 shtatori; etiketa këtu e thotë tani po atë.
      .map((x) => ({ type: "birthday", sortKey: x.daysUntil, personId: x.p.id, daysUntil: x.daysUntil,
        label: x.p.death_date
          ? t("{{name}}'s birthday (in memoriam)", { name: displayName(x.p) })
          : t("{{name}}'s birthday", { name: displayName(x.p) }) }));
    const anniversaries = all
      .filter((p) => p.death_date)
      .map((p) => ({ p, info: deathAnniversaryInfo(p.death_date) }))
      .filter((x) => x.info && x.info.daysUntil >= 0 && x.info.daysUntil <= 7 && x.info.years >= 1)
      .map((x) => ({ type: "anniversary", sortKey: x.info.daysUntil, personId: x.p.id, label: `${x.info.years} vjet nga ndarja e ${displayName(x.p)}`, daysUntil: x.info.daysUntil, years: x.info.years }));
    // Faza 9 — data të rëndësishme (rikthehen çdo vit muaj/ditë), brenda 14 ditëve.
    const nextOccurrenceDays = (m, d) => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      let nx = new Date(today.getFullYear(), m - 1, d);
      if (nx < today) nx = new Date(today.getFullYear() + 1, m - 1, d);
      return Math.round((nx - today) / 86400000);
    };
    const important = (importantDates || [])
      .map((r) => ({ r, daysUntil: nextOccurrenceDays(r.event_month, r.event_day) }))
      .filter((x) => x.daysUntil >= 0 && x.daysUntil <= 14)
      .map((x) => ({ type: "important", sortKey: x.daysUntil, id: x.r.id, label: x.r.label, note: x.r.note, daysUntil: x.daysUntil, imja: !!x.r.person_id }));
    // Përvjetori i martesës — çelësi është po ai i martesës, sepse është i njëjti
    // lloj ngjarjeje: një herë kur ndodh, pastaj çdo vit.
    const weddings = (marriageAnnivs || []).map((m) => {
      const a = people[m.person_a], b = people[m.person_b];
      // Një çift i shkurorëzuar nuk ka përvjetor martese. Kontrollohet edhe
      // këtu, jo vetëm te serveri: rreshti i martesës mbetet te `marriages`
      // pas divorcit (statusi shënohet veç, te `divorced_from_ids`), dhe një
      // urim «25 vjet martesë» për dy të ndarë do të ishte gabimi më i keq që
      // mund të bëjë kjo listë.
      if (a && b && ((a.divorced_from_ids || []).includes(b.id) || (b.divorced_from_ids || []).includes(a.id))) return null;
      return {
        type: "wedding", sortKey: m.days_until, id: `wa-${m.person_a}-${m.person_b}`,
        personId: m.person_a, memberIds: [m.person_a, m.person_b], years: m.years, daysUntil: m.days_until,
        label: t("{{years}} years of marriage — {{a}} and {{b}}", {
          years: m.years, a: a ? displayName(a) : "", b: b ? displayName(b) : "",
        }),
      };
    }).filter((x) => x && x.label);
    return [
      ...(ruleOn("birthday") ? birthdays : []),
      ...(ruleOn("anniversary") ? anniversaries : []),
      ...(ruleOn("marriage") ? weddings : []),
      ...important,
    ].sort((a, b) => a.sortKey - b.sortKey);
  }, [people, feedRules, importantDates, marriageAnnivs, eshteMuze]);
  // SOT NUK ËSHTË «SË SHPEJTI» (2 shtator 2026). Pronari: «kur e kanë
  // ditëlindjen po atë ditë duhet të zhvendoset nga Së shpejti te Së fundi».
  // Një ditëlindje që është sot nuk «vjen» — po ndodh. Prandaj lista «Së
  // shpejti» mban vetëm ≥1 ditë, dhe ajo që bie sot shkon te «Së fundi» si
  // postim i ditës (shih `feedRecent`). Njoftimet lokale vazhdojnë të lexojnë
  // listën e plotë, se kujtesa e orës 9 e ditës së sotme duhet ende.
  const feedUpcoming = useMemo(() => feedUpcomingAll.filter((x) => x.daysUntil >= 1), [feedUpcomingAll]);
  const feedSot = useMemo(() => feedUpcomingAll.filter((x) => x.daysUntil === 0), [feedUpcomingAll]);

  // ─── NJOFTIME1 — NJOFTIMET LOKALE TË DATAVE ─────────────────────────────
  //
  // 29 gusht 2026. Kërkesa: «a mund ta bëjmë të njëjtën në app — kur të
  // instalohet, të shfaqet pyetja a lejon njoftimet, po/jo».
  //
  // PSE LOKALE DHE JO NGA SERVERI. Push-i i serverit (FCM/APNs) duhet për atë
  // që NUK dihet paraprakisht: dikush postoi një kujtim, u bashkua një anëtar.
  // Por ajo që kjo platformë ka më të vlefshme për të kujtuar — ditëlindjet,
  // përvjetorët, përkujtimet — dihet me muaj përpara. Për to serveri është
  // rrugë e gjatë pa nevojë: telefoni i mban vetë.
  //
  // Kjo do të thotë: pa Firebase, pa çelës Apple, pa tabelë token-ash, dhe
  // punon edhe pa internet. Push-i i serverit mbetet për fazën tjetër.
  //
  // KUR PYETET NJERIU. Jo në sekondën e parë — ai është momenti me më shumë
  // «Jo», dhe një «Jo» te njoftimet nuk kthehet dot me kod: njeriu duhet të
  // shkojë vetë te cilësimet e telefonit. Prandaj pyetja del vetëm kur ka
  // VËRTET diçka për të kujtuar (`feedUpcoming` jo bosh) — pra kur përgjigjja
  // «Po» ka një arsye të dukshme në ekran.
  //
  // Flamuri ruhet PARA se të hapet dialogu: edhe nëse mbyllet pa zgjedhje, nuk
  // e bezdisim sërish. I njëjti rregull si te push-i i web-it.
  // NJOFTIME2 — PREFERENCAT E NJOFTIMEVE, të pajisjes.
  //
  // Kërkesë e pronarit: «tabin e ditëlindjeve e bëjmë preferencat — këtu
  // klienti mund të ndalë të gjitha njoftimet edhe të filtrojë se nga kush do
  // njoftime… dhe vetëm nga këta persona vijnë njoftime, jo nga e gjithë
  // familja».
  //
  // Ruhen te pajisja, jo te serveri: janë preferencë e një telefoni, dhe vetë
  // njoftimet i mban ai telefon. Një tabelë e re për katër kutiza do të ishte
  // rrugë e gjatë pa nevojë — i njëjti arsyetim si te kutizat e «Së shpejti».
  //
  // LISTA E PERSONAVE NUK DUBLOHET. Ajo ekziston tashmë (`mySubscriptions`) dhe
  // deri sot ushqente vetëm njoftimet me email/push nga serveri. Tani e njëjta
  // listë filtron edhe njoftimet e pajisjes: një vend ku zgjidhet «nga kush»,
  // jo dy. Bosh do të thotë e gjithë familja — sepse dikush që nuk ka zgjedhur
  // askënd nuk ka thënë «askush», ka thënë «s'e kam menduar».
  const NJOFTIME_LLOJET = ["birthday", "wedding", "anniversary", "important"];
  const [njoftimetPref, setNjoftimetPref] = useState(() => {
    const parazgjedhje = { ndezur: true, llojet: {} };
    try {
      const raw = localStorage.getItem("pef_njoftimet_pref");
      if (raw) {
        const v = JSON.parse(raw);
        if (v && typeof v === "object") return { ndezur: v.ndezur !== false, llojet: v.llojet || {} };
      }
    } catch (_e) { /* modalitet privat */ }
    return parazgjedhje;
  });
  // NJOFTIME3 (3 nëntor 2026) — LLOJET rrinë te LLOGARIA, jo te pajisja.
  // Pronari: «mund të kem 10 pajisje; filtri duhet të vlejë për të gjitha».
  // `account_activity.njoftimet.llojet` lexohet sapo njihet llogaria;
  // localStorage mbetet si kopje për nisjen pa rrjet (njoftimet lokale
  // planifikohen edhe offline). Vetëm «ndezur/fikur» mbetet i pajisjes —
  // ai është çelësi i këtij telefoni, si te çdo aplikacion.
  const uidPerNjoftime = (authSession && authSession.user && authSession.user.id) || "";
  useEffect(() => {
    if (!uidPerNjoftime) return undefined;
    let gjallë = true;
    supabase.rpc("rpc_njoftimet_lexo").then(({ data, error }) => {
      if (!gjallë || error || !data || typeof data !== "object") return;
      const llojet = data.llojet && typeof data.llojet === "object" ? data.llojet : null;
      if (!llojet) return;
      setNjoftimetPref((prev) => {
        const i_ri = { ...prev, llojet };
        try { localStorage.setItem("pef_njoftimet_pref", JSON.stringify(i_ri)); } catch (_e) { /* privat */ }
        return i_ri;
      });
    }, () => {});
    return () => { gjallë = false; };
  }, [uidPerNjoftime]);
  const ruajNjoftimetPref = useCallback((v, prejLlojeve = false) => {
    setNjoftimetPref(v);
    try { localStorage.setItem("pef_njoftimet_pref", JSON.stringify(v)); }
    catch (_e) { /* modalitet privat — vlen vetëm për këtë seancë */ }
    // Llojet shkojnë te serveri; «ndezur» jo (është i pajisjes).
    if (prejLlojeve) {
      supabase.rpc("rpc_njoftimet_shen_llojet", { p_llojet: v.llojet || {} }).then(({ error }) => {
        if (error) showToast(t("The notification types could not be saved for your account: ") + error.message);
      }, () => {});
    }
  }, []);
  const llojiNdezur = (k) => njoftimetPref.llojet[k] !== false;

  // Nga kush vijnë njoftime. `null` = nga e gjithë familja.
  // NJOFTIME5 — tri mënyra, për person në familje, në çdo pajisje:
  //   «dega»    = asgjë e ruajtur → dega e gjyshit (parazgjedhje; gjysh i
  //               panjohur → e gjithë familja derisa pema të lidhet)
  //   «familja» = një rresht `watch_type = 'family'` (kanali app)
  //   «persona» = rreshta personash (si më parë; «+ pasardhësit» tani me
  //               bashkëshortët)
  // NJOFTIME6 — pragu nga Admin Center. Pa derë (SQL i paxhiruar) mbetet 50.
  const [pragaEDeges, setPragaEDeges] = useState(50);
  useEffect(() => {
    let gjallë = true;
    supabase.rpc("rpc_get_njoftimet_pragu").then(({ data, error }) => {
      if (!gjallë || error) return;
      const n = Number(data);
      if (Number.isFinite(n) && n >= 5) setPragaEDeges(n);
    }, () => {});
    return () => { gjallë = false; };
  }, []);
  const njoftimetNgaKush = useMemo(() => {
    // gjyshi dhe dega llogariten gjithmonë: ekrani i tregon edhe kur
    // mënyra aktive është tjetër («Dega e Imerit — 23 persona»).
    const { rrenja: gjyshi, niveli, dega } = currentPersonId
      ? rrenjaEDeges(people, currentPersonId, pragaEDeges)
      : { rrenja: null, niveli: null, dega: null };
    const rreshtat = (mySubscriptions || []).filter((x) => x && x.watch_person_id);
    if (rreshtat.some((x) => x.watch_type === "family")) return { menyra: "familja", set: null, gjyshi, niveli, dega };
    const persona = rreshtat.filter((x) => x.watch_type !== "family");
    if (persona.length) {
      const set = new Set();
      for (const x of persona) {
        if (x.watch_type === "descendants") for (const id of degaMeBashkeshortet(people, x.watch_person_id)) set.add(id);
        else set.add(x.watch_person_id);
      }
      return { menyra: "persona", set, gjyshi, niveli, dega };
    }
    return { menyra: "dega", set: dega, gjyshi, niveli, dega };
  }, [mySubscriptions, people, currentPersonId, pragaEDeges]);
  // Nga kush vijnë njoftime. `null` = nga e gjithë familja.
  const personatENjoftimeve = njoftimetNgaKush.set;

  const [njoftimetLokale, setNjoftimetLokale] = useState(false);
  useEffect(() => {
    let gjallë = true;
    supabase.rpc("rpc_get_local_notifications").then(({ data, error }) => {
      // Pa derë (NJOFTIME1 e paxhiruar) → e fikur. Një veçori që nuk është
      // instaluar duhet të mos ekzistojë, jo të dështojë.
      if (!gjallë || error || data == null) return;
      setNjoftimetLokale(!!data);
    });
    return () => { gjallë = false; };
  }, []);

  useEffect(() => {
    if (!njoftimetLokale || !currentPersonId) return undefined;
    if (!Capacitor.isNativePlatform()) return undefined;   // te shfletuesi vlen Web Push
    if (!subsGati) return undefined;                        // SIGURIA2 — pa listën e abonimeve s'vendos asgjë

    // NJOFTIME2 — filtri: çfarë lloji, dhe nga kush. Zbatohet PARA se të
    // kërkohet leja: nuk ka kuptim ta pyesësh dikë për njoftime nëse pas
    // filtrave të tij nuk mbetet asnjë.
    const perTuKujtuar = feedUpcomingAll.filter((x) => {
      if (!llojiNdezur(x.type)) return false;
      if (personatENjoftimeve && x.personId && !personatENjoftimeve.has(x.personId)) return false;
      // NJOFTIME4 (3 nëntor 2026) — DITËLINDJA E NJË TË NDJERI NUK NJOFTOHET.
      // Një përdorues mori «🎂 Sot është ditëlindja e X» për dikë me datë
      // vdekjeje. `feedUpcomingAll` i mban ditëlindjet e të ndjerëve ME QËLLIM:
      // te Rrjedha dita e tyre del si përkujtim («84 vjet nga lindja»), jo si
      // urim. Por njoftimi i telefonit s'ka ton — thotë «Ditëlindja», dhe kaq.
      // Skripti i serverit i përjashton që nga fillimi (`!p.death_date`);
      // telefoni duhet të bëjë po ashtu. Përkujtimi i vdekjes (type
      // «anniversary») mbetet — ai ËSHTË njoftimi i duhur për të ndjerët.
      if (x.type === "birthday" && x.personId && people[x.personId] && people[x.personId].death_date) return false;
      return true;
    });
    // SIGURIA2 (U1) — nëse klienti i ka ndalur njoftimet ose filtrat s'lënë asgjë,
    // të planifikuarat e vjetra duhen FSHIRË (më parë mbeteshin dhe binin prapë),
    // por pa kërkuar leje: s'ka çfarë t'i kërkosh.
    const bosh = !njoftimetPref.ndezur || !perTuKujtuar.length;

    let gjallë = true;
    // Debounce 400ms: kur feed-i e filtrat përditësohen disa herë radhazi, vetëm
    // e fundit planifikon — pa u bërë cancel/schedule për secilën.
    const kohëmatësi = setTimeout(() => { (async () => {
      try {
        let leja = false;
        const gjendja = await LocalNotifications.checkPermissions();
        if (gjendja.display === "granted") {
          leja = true;
        } else if (!bosh && (gjendja.display === "prompt" || gjendja.display === "prompt-with-rationale")) {
          const çelësi = `pef_njoftimet_pyetur:${currentPersonId}`;
          if (localStorage.getItem(çelësi)) return;
          localStorage.setItem(çelësi, "1");
          const p = await LocalNotifications.requestPermissions();
          leja = p.display === "granted";
        }
        if (!leja || !gjallë) return;

        // FSHIHEN TË PLANIFIKUARAT PARA SE TË SHTOHEN TË REJAT. Pa këtë, çdo
        // hapje e app-it do të shtonte një kopje të dytë të së njëjtës
        // ditëlindje — dhe brenda javës telefoni do të bibte pesë herë për një
        // gjë. Id-të janë të qëndrueshme, por `schedule` me të njëjtën id te
        // iOS-i e zëvendëson, kurse te Android-i sillet ndryshe; fshirja e
        // qartë e heq atë ndryshim mes platformave.
        const pezull = await LocalNotifications.getPending();
        if (!gjallë) return;   // SIGURIA2 (U1) — një ekzekutim më i ri e ka zëvendësuar këtë
        const vjetrat = (pezull && pezull.notifications) || [];
        if (vjetrat.length) {
          await LocalNotifications.cancel({ notifications: vjetrat.map((n) => ({ id: n.id })) });
        }
        if (bosh || !gjallë) return;

        const TITUJT = {
          birthday: `🎂 ${t("Birthday")}`,
          wedding: `💍 ${t("Wedding anniversary")}`,
          anniversary: `🕯️ ${t("Remembrance")}`,
          important: `📅 ${t("Important date")}`,
        };
        const tani = new Date();
        const lista = perTuKujtuar
          .slice(0, 30)   // iOS-i mban maksimum 64 të planifikuara për app
          .map((x) => {
            // Ora 9:00 e mëngjesit, koha LOKALE e telefonit. `new Date(y, m, d…)`
            // e ndërton pikërisht te zona e pajisjes — një `toISOString()` këtu
            // do ta zhvendoste ditën për gjysmën e botës.
            const kur = new Date(tani.getFullYear(), tani.getMonth(), tani.getDate() + (Number(x.daysUntil) || 0), 9, 0, 0);
            return {
              id: idENjoftimit(`${x.type}|${x.label}|${kur.toDateString()}`),
              title: TITUJT[x.type] || `📅 ${t("Important date")}`,
              body: x.label,
              schedule: { at: kur, allowWhileIdle: true },
            };
          })
          // Një njoftim për një orë që ka kaluar nuk planifikohet dot; te disa
          // pajisje ai bie MENJËHERË, dhe atëherë ditëlindja e sotme të zgjon
          // sapo hap app-in.
          .filter((n) => n.schedule.at.getTime() > Date.now() + 60_000);

        if (lista.length && gjallë) await LocalNotifications.schedule({ notifications: lista });
      } catch (e) {
        // Heshtje për njeriun, emër për ne: njoftimet janë shtesë, dhe një
        // dështim i tyre nuk ka pse ta ndalë asgjë tjetër te ekrani.
        console.warn("njoftimet lokale:", String(e?.message || e));
      }
    })(); }, 400);
    return () => { gjallë = false; clearTimeout(kohëmatësi); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [njoftimetLokale, currentPersonId, feedUpcomingAll, njoftimetPref, personatENjoftimeve, subsGati]);

  const feedRecent = useMemo(() => {
    const memories = familyHistoryEvents.map((ev) => ({
      type: "memory", id: ev.id, sortTime: ev.created_at ? new Date(ev.created_at).getTime() : 0,
      title: ev.title, description: ev.description,
      // BALLINA2 — data e ngjarjes dhe kush e shtoi. Të dyja ekzistonin te
      // tabela dhe asnjëra s'shfaqej: karta thoshte vetëm «Kujtim i ri», pra
      // një kujtim familjeje pa vit dhe pa emër. Kolona `created_by` tregon te
      // `people`, prandaj emri merret nga harta që e kemi tashmë.
      eventDate: ev.event_date || null,
      createdBy: ev.created_by || null,
      photoPaths: [...(ev.photo_path ? [ev.photo_path] : []), ...(ev.photo_paths || [])],
    }));
    const legacies = unlockedLegacyMessages.map((m) => ({
      type: "legacy", id: m.id, sortTime: m.created_at ? new Date(m.created_at).getTime() : 0,
      title: m.title, personId: m.person_id, personName: m.person_name, messageText: m.message_text,
    }));
    // Faza 6 — VETËM lindjet e reja (<1 vit) dalin veç në feed. Shtimi i
    // personave të rritur (mbi 1 vjeç) NUK postohet individualisht — një degë
    // e re (disa persona brenda pak ditësh) trajtohet me aprovim admini
    // (shih feed_post_requests / "branch_joined"), jo si postime të veçanta.
    const isBabyBd = (bd) => {
      if (!bd) return false;
      const ms = Date.now() - new Date(bd).getTime();
      return ms >= 0 && ms < 366 * 24 * 3600 * 1000;
    };
    const babies = recentEvents.additions
      .filter((p) => isBabyBd(p.birth_date))
      .map((p) => ({ type: "new_baby", id: `add-${p.id}`, sortTime: p.created_at ? new Date(p.created_at).getTime() : 0, personId: p.id }));
    const deaths = recentEvents.deaths.map((p) => ({
      type: "death", id: `death-${p.id}`, sortTime: p.updated_at ? new Date(p.updated_at).getTime() : 0, personId: p.id,
    }));
    // Filtro sipas rregullave të aktivizuara të feed-it.
    const branchPosts = (feedPosts || []).filter((fp) => fp.kind === "branch_joined").map((fp) => ({
      type: "branch_joined", id: `fp-${fp.id}`, postId: fp.id, sortTime: fp.created_at ? new Date(fp.created_at).getTime() : 0,
      headPersonId: fp.head_person_id, memberIds: fp.member_ids || [],
    }));
    // Faza 6 — auto-post i martesës (nga feed_posts kind='marriage', shih
    // rpc_post_marriage). Gated nga rregulli 'marriage'.
    // Një çift që është ndarë nuk del më si lajm — as martesa e tyre, as
    // «sot do ta kishin përvjetorin». Historia mbetet te pema dhe te profili;
    // feed-i është për atë që gëzohet sot. (Pronari, 2 shtator 2026.)
    const teNdare = (ids) => {
      const [a, b] = (ids || []).map((id) => people[id]);
      return !!(a && b && ((a.divorced_from_ids || []).includes(b.id) || (b.divorced_from_ids || []).includes(a.id)));
    };
    const marriagePosts = (feedPosts || []).filter((fp) => fp.kind === "marriage" && !teNdare(fp.member_ids)).map((fp) => ({
      type: "marriage", id: `mp-${fp.id}`, postId: fp.id, sortTime: fp.created_at ? new Date(fp.created_at).getTime() : 0,
      memberIds: fp.member_ids || [],
    }));
    // Anëtari i ri — postohet vetëm kur zgjidhet te formulari (shih rpc_post_new_member).
    const newMembers = (feedPosts || []).filter((fp) => fp.kind === "new_member").map((fp) => ({
      type: "new_member", id: `nm-${fp.id}`, postId: fp.id, sortTime: fp.created_at ? new Date(fp.created_at).getTime() : 0,
      personId: fp.head_person_id, memberIds: fp.member_ids || [],
    }));
    const gated = [
      ...(ruleOn("memory") ? memories : []),
      ...(ruleOn("legacy") ? legacies : []),
      ...(ruleOn("new_baby") ? babies : []),
      ...(ruleOn("death") ? deaths : []),
      ...(ruleOn("new_branch") ? branchPosts : []),
      ...(ruleOn("marriage") ? marriagePosts : []),
      ...(ruleOn("new_member") ? newMembers : []),
    ];
    // Ajo që bie SOT — ditëlindje, përkujtim, përvjetor martese, datë e
    // rëndësishme — hyn si postim i ditës, mbi gjithçka tjetër të sotme.
    // Rregullat e feed-it janë zbatuar tashmë te `feedUpcomingAll`.
    const fundiIDites = new Date(); fundiIDites.setHours(23, 59, 59, 0);
    const sot = feedSot.map((x) => ({
      type: "sot", lloji: x.type, id: `sot-${x.type}-${x.id || x.personId}`, sortTime: fundiIDites.getTime(),
      personId: x.personId || null, memberIds: x.memberIds || null, label: x.label, years: x.years,
    }));
    return [...sot, ...gated].sort((a, b) => b.sortTime - a.sortTime);
  }, [familyHistoryEvents, unlockedLegacyMessages, recentEvents, feedRules, feedPosts, feedSot, people]);

  // MUZEU3 — te një koleksion nuk ka lidhje për të ndërtuar pemë; boshti është
  // salla dhe koha. Zgjedhja bëhet KËTU, te i vetmi vend ku llogaritet vendosja,
  // që asnjë ekran të mos ketë nevojë ta dijë cila paraqitje e thirri.
  const eshteKoleksion = llojiHapesires.paraqitja === "koleksion";
  // Te një koleksion parazgjedhja «trunk» s'ka kuptim — nuk ka trung. Kalohet te
  // e gjithë ekspozita një herë, kur mësohet paraqitja.
  useEffect(() => {
    if (!eshteKoleksion) return;
    setViewMode((v) => (["full", "salla", "koha"].includes(v) ? v : "full"));
  }, [eshteKoleksion]);
  // Sallat vijnë nga vetë figurat, jo nga një listë e mbajtur veç: një listë e
  // dytë do të dilte jashtë hapi sapo dikush ta riemërtonte një sallë.
  const sallatEKoleksionit = useMemo(() => {
    if (!eshteKoleksion) return [];
    const set = new Set();
    Object.values(people).forEach((p) => set.add((p?.salla || "").trim()));
    return [...set].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b, "sq")));
  }, [people, eshteKoleksion]);
  const { positions: basePositions, childrenMap: baseChildrenMap } = useMemo(
    () => (eshteKoleksion ? paraqitKoleksionin(people) : computeLayout(people, treeAdvLayout)),
    [people, layoutVersion, treeAdvLayout, eshteKoleksion]
  );
  const [focusPersonId, setFocusPersonId] = useState(null);
  // FIX (kërkesë e userit — "e heqim fushën manuale komplet, motori
  // automatik bëhet i vetmi") — `personalizedView` (checkbox-i "Ngjyros
  // sipas farefisnisë time") u hoq — llogaritja automatike ËSHTË TANI
  // GJITHMONË aktive, s'është më opsion. Pika e referimit (kush është
  // "unë" për llogaritjen) është `focusPersonId || myPersonId` — kështu
  // musafirët (pa `myPersonId`, s'janë anëtarë) marrin ngjyrosje të
  // personalizuar VETËM pasi të përdorin "Shpirt i Lirë" (që vendos
  // focusPersonId); përpara asaj, bie mbrapa te "core" (asnjë referim).
  const relationMap = useMemo(() => {
    const refId = focusPersonId || resolveViewerAnchor(myPersonId);
    if (!refId || !people[refId]) return null;
    return computeRelationMap(refId, people);
  }, [focusPersonId, myPersonId, people, rulesVersion]);
  // PIKËPAMJA E FAMILJES (6 shtator 2026) — për statistikat: farefisnia matet
  // nga PARAARDHËSI I PARË, jo nga ai që shikon. Paraardhësi: `is_root` kur
  // vjen nga baza; ndryshe personi pa prindër, jo mysafir, jo i ardhur me
  // martesë, me më shumë pasardhës.
  const rrenjaEFamiljes = useMemo(() => {
    const lista = Object.values(people);
    const iShenuar = lista.find((p) => p.is_root);
    if (iShenuar) return iShenuar.id;
    let mePasardhes = null, max = -1;
    lista.forEach((p) => {
      if ((p.parent_ids || []).length > 0 || eshteMysafir(p) || p.branch === "married_in") return;
      const n = pasardhesitE(people, p.id).size;
      if (n > max) { max = n; mePasardhes = p.id; }
    });
    return mePasardhes;
  }, [people]);
  const relationMapFamiljar = useMemo(() => {
    if (!rrenjaEFamiljes || !people[rrenjaEFamiljes]) return null;
    return computeRelationMap(rrenjaEFamiljes, people);
  }, [rrenjaEFamiljes, people, rulesVersion]);
  // SHTESË (kërkesë e userit) — "rast i veçantë" (fëmijë i adoptuar / mik
  // nderi / prind i panjohur) ANULON llogaritjen automatike VETËM për atë
  // person specifik — kudo tjetër vendos motori automatik. `p.branch`
  // (fusha e vjetër MANUALE, hequr nga formulari i zakonshëm) mbetet si
  // "mbajtësi" i vlerës së anulimit — jo më zgjedhje e lirë çdo herë, por
  // VETËM 3 vlerat e special-rasteve (shih SPECIAL_CASE_BRANCH poshtë).
  const SPECIAL_CASE_BRANCH = { adopted: "core", honorary: "married_in", unknown_parent: "core" };
  // FIX (kërkesë e userit — "liri e plotë e ngjyrës per një person specifik")
  // — branch_color_override (nëse është vendosur) ka PËRPARËSI mbi GJITHÇKA
  // tjetër: mbi branch_special_case (3 rastet fikse), mbi relationMap
  // automatik, edhe mbi kolonën e vjetër `branch`.
  // FIX (kërkesë e userit): origjina "e ardhur me martesë" është INTRINSEKE dhe
  // s'ndryshon sipas vëzhguesit — "Merita nuk guxon ta ndryshojë ngjyrën në
  // familjen ku ka ardhur si e martuar." Prandaj `branch === "married_in"` ka
  // përparësi MBI relationMap: pa këtë, kur e sheh një FËMIJË, nëna e ardhur me
  // martesë del si paraardhëse → relationMap e klasifikonte "core" (gabim). Kjo
  // s'ndikon te kush ka origjinë gjaku — ata mbeten siç i llogarit relationMap.
  // Ngjyra e degës kalon gjithmonë nga këtu, që «pa ngjyra» të mos ketë nevojë
  // të preket në 17 vende veç e veç — dhe që të mos harrohet asnjë.
  // Tri role, jo një — sepse «pa ngjyra» do të thotë gjëra të ndryshme sipas
  // asaj që ngjyra po mban:
  //
  //   ngjyraEDeges   → VIJA, kufij, tekst. Këto s'mund të zhduken: pa to
  //                     kartela nuk ekziston më dhe pema bëhet e palexueshme.
  //                     Marrin tonin e shkrimit dytësor.
  //   mbushjaEDeges  → SHIRITAT dhe pllakëzat me ngjyrë dege. Këto janë
  //                     dekor i pastër — nuk mbajnë asnjë kuptim veç ngjyrës,
  //                     prandaj zhduken fare.
  //   ngjyraEDegesSoft → SFONDI i lehtë i kartelës. Edhe ky zhduket: pronari
  //                     e kërkoi shprehimisht — «as si hijëzim, mos të
  //                     ekzistojë». Kartela mbetet letra vetë.
  // ⚠ ÇELËS I RI — 4 shtator 2026: `shenjat`.
  // Pronari mban dy dizajne, Klasiken dhe Heirloom-in, po «emojit ikonat nuk
  // më pëlqejnë te klasikja, sidomos te pema — dua t'i kemi të njëjta me
  // heirloomin».
  // Deri sot figurat e pemës (pëllumbi, unazat) vareshin nga `pamja.pema`,
  // po ai çelës ndërron NJËKOHËSISHT edhe skemën e kartelës — medaljonin.
  // Pra Klasikja nuk kishte si t'i merrte figurat pa marrë edhe kartelën.
  // Tani janë dy çelësa të ndarë:
  //   pamja.pema    = "heirloom" → kartela-medaljon
  //   pamja.shenjat = "heirloom" → pëllumbi dhe unazat, mbi ÇDO kartelë
  // Heirloom-i i ka të dy; Klasikja mund të marrë vetëm të dytin.
  // Shenjat (pëllumbi, unazat) ndjekin DIZAJNIN, jo skemën e kartelës: edhe kur
  // te Trashëgimia zgjidhet një kartelë klasike, zemrat e vjetra nuk kthehen.
  // SKEMA2: admini mund ta caktojë për dizajn te The tree («Figurat te pema»):
  // «heirloom» = unazat & pëllumbi, «klasike» = emoji-t; bosh = sipas dizajnit.
  const shenjatHeirloom = () => {
    const m = mbivendosjaESkemes("display");
    if (m.shenjat === "heirloom") return true;
    if (m.shenjat === "klasike") return false;
    return pamja("shenjat", "heirloom") || pamja("pema", "heirloom") || pemaHeirloom();
  };

  // Shenja e të ndjerit — një vend i vetëm, që të mos ndryshojë ndryshe te
  // secila skemë kartele. Pozicioni dhe përmasa mbeten ato të skemës.
  const shenjaEEndjeres = (x, y, mad) => (shenjatHeirloom() ? (
    <svg x={x - mad / 2} y={y - mad * 0.9} width={mad} height={mad} viewBox="0 0 200 200" data-status-icon="1">
      <title>{t("Deceased")}</title>
      <path d={SHTEG_PELLUMBI_PLOT} fill={PALETA_TIPI.bronze || COLORS.gold} fillRule="evenodd" />
    </svg>
  ) : (
    <text x={x} y={y} textAnchor="middle" style={{ fontSize: mad * 0.92 }} title={t("Deceased")}>{"\ud83d\udd4a\ufe0f"}</text>
  ));

  const ngjyraEDeges = (b) => (paNgjyra() ? toniNeutral() : branchColor(b));
  const mbushjaEDeges = (b) => (paNgjyra() ? "transparent" : branchColor(b));
  const ngjyraEDegesSoft = (b) => (paNgjyra() ? "transparent" : branchSoft(b));
  const effectiveBranch = (p) => p.branch_color_override || (p.branch_special_case && SPECIAL_CASE_BRANCH[p.branch_special_case]) || (p.branch === "married_in" ? "married_in" : null) || (relationMap && relationMap[p.id]) || p.branch || "core";
  // E njëjta shkallë përparësish, po farefisnia nga paraardhësi i parë.
  // Ndryshim i vetëm nga pema: kush ka hyrë me martesë te një DEGË E RE (fëmija
  // i një vajze të shtëpisë) i takon asaj dege, jo «me martesë» te trungu —
  // prandaj «married_out» i llogaritur fiton mbi kolonën `married_in`.
  const degaFamiljare = (p) => {
    const nl = relationMapFamiljar && relationMapFamiljar[p.id];
    return p.branch_color_override || (p.branch_special_case && SPECIAL_CASE_BRANCH[p.branch_special_case])
      || (nl === "married_out" ? "married_out" : null)
      || (p.branch === "married_in" ? "married_in" : null) || nl || p.branch || "core";
  };
  // NDEZUR SI PARAZGJEDHJE (kërkesë e pronarit, 29 gusht 2026).
  //
  // I fikur, çdo «+» e mbyllte degën e hapur më parë — pra ai që hap tri degë
  // për t'i krahasuar, e humb të parën kur hap të tretën, dhe kjo lexohet si
  // defekt, jo si zgjedhje. Krahasimi është arsyeja kryesore pse dikush hap një
  // degë. Kush e do sjelljen e vjetër (një degë njëherësh) e fik vetë te
  // «Cilësimet e pamjes së pemës».
  const [parallelBranches, setParallelBranches] = useState(true);
  const [freeSpiritMode, setFreeSpiritMode] = useState(false);
  const [chainStopId, setChainStopId] = useState(null);
  // SHTESË (kërkesë e userit) — çelës personal për "brezat" (banda + etiketa),
  // i fikur si parazgjedhje; pamja mbetet identike derisa ta ndezë vetë useri.
  const [showGenerationBands, setShowGenerationBands] = useState(false);
  // FAZA 2 (+ për-familje) — "brezat" lejohen VETËM nëse admini i ka aktivizuar.
  // Vlera EFEKTIVE vjen nga RPC: override i familjes (activeFamilyId) OSE
  // parazgjedhja platformë-gjerë. Varet nga activeFamilyId (xhiron kur zgjidhet).
  // E fikur si parazgjedhje; nëse RPC-ja dështon/s'ekziston, mbetet false pa dëm.
  const [generationBandsAllowed, setGenerationBandsAllowed] = useState(false);
  useEffect(() => {
    if (!activeFamilyId) return;
    let cancelled = false;
    supabase.rpc("rpc_get_generation_bands_enabled", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (cancelled || error) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row == null) return;
      setGenerationBandsAllowed(!!(typeof row === "object" ? row.enabled : row));
    });
    return () => { cancelled = true; };
  }, [activeFamilyId]);

  // FAZA 3 (SHËRBIM SHTESË) — "Poster për printim" lejohet VETËM nëse admini e ka
  // aktivizuar (platformë-gjerë OSE për familjen). Identik në sjellje me brezat:
  // vlera efektive vjen nga RPC-ja, varet nga activeFamilyId, e fikur si
  // parazgjedhje. `posterSize` mban formatin e zgjedhur në dropdown (A2…4A0).
  const [posterExportAllowed, setPosterExportAllowed] = useState(false);
  const [posterSize, setPosterSize] = useState("A1");
  // MURALI (Faza 1j) — i fikur derisa ta ndezësh te Admin Center. Gjendja rri
  // këtu edhe kur veçoria s'është e ndezur: një `useState` i pashfaqur s'kushton
  // asgjë, kurse një hook i kushtëzuar do të thyente rendin e hook-eve.
  const [muralOpen, setMuralOpen] = useState(false);
  const [mural, setMural] = useState({ cols: 3, rows: 1, overlapMm: 5 });
  // Dizajnet e posterit vijnë nga biblioteka e Admin Center (poster_designs).
  // "" = pa dizajn (i thjeshtë, vetëm sfond ngjyre). Ngarkohen një herë.
  const [posterDesignId, setPosterDesignId] = useState("");
  const [posterDesigns, setPosterDesigns] = useState([]);
  const [posterModalOpen, setPosterModalOpen] = useState(false);
  // POROSITË QË PRESIN VEPRËN (Faza 1c). Rilexohen bashkë me urat dhe veçoritë:
  // stafi mund ta ketë shënuar "i paguar" ndërkohë, dhe atëherë detyra shfaqet
  // pa e ringarkuar faqen — e njëjta rrahje si te ura.
  const [artworkTasks, setArtworkTasks] = useState([]);
  const [artworkTask, setArtworkTask] = useState(null);   // ajo që po plotësohet tani
  // POROSIA NGA VETË PEMA (Faza 1e). Posteri nuk shkarkohet më; kompozohet dhe
  // porositet. `posterOffers` janë shërbimet e posterit me çmimet e tyre, të
  // lexuara nga i njëjti burim si vitrina — pra çmimi këtu s'ka si të ndryshojë
  // nga ai te Shërbimet.
  const [posterOffers, setPosterOffers] = useState([]);
  // NJOFTIMI PARA PAGESËS (Faza 1g). Vetëm veçoritë që kjo familje i ka VËRTET
  // të ndezura sot — serveri e bën atë filtër, jo ne. Një njoftim për diçka që
  // s'e përdor është zhurmë, dhe zhurma është arsyeja pse njerëzit ndalojnë së
  // lexuari njoftimet fare.
  const [priceNotices, setPriceNotices] = useState([]);
  // SA TË REJA (Faza 2d). Pa këtë numër, biseda që ndërtuam sot do të rrinte e
  // pahapur: klienti s'ka pse ta hapë një faqe për të parë nëse ka diçka të re.
  const [orderAlerts, setOrderAlerts] = useState({ te_reja: 0, ne_pritje: 0 });
  // "E kuptova" shkon te serveri (është prova e mbulimit që sheh admini). Ky
  // çelës lokal është veç për kujtesën e javës së fundit, që ta mbyllësh dot pa
  // e fshirë provën.
  const PRICE_NOTICE_HUSH = "pef.pricenotice.hush";
  const [noticeHush, setNoticeHush] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(PRICE_NOTICE_HUSH) || "[]")); }
    catch { return new Set(); }
  });
  const [orderPick, setOrderPick] = useState(null);      // {service_key, price_id}
  const [orderFields, setOrderFields] = useState({});
  const [orderBusy, setOrderBusy] = useState(false);
  const [orderErr, setOrderErr] = useState("");
  // MOSTRA — çfarë shihet para pagesës. Rigjenerohet sa herë ndryshon dizajni a
  // formati, sepse pikërisht ato po i zgjedh njeriu.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(false);
  // KOMERCIALIZIMI I POSTERIT (Faza 1). Nëse admini e ka shtuar çelësin
  // `poster_pro` te regjistri dhe familja s'e zotëron, klikimi mbi Poster çon
  // te vitrina — aty e pret karta me çmimin. Modeli i dyqanit modern: veçoria
  // shihet, zbulohet, dhe zhbllokohet aty ku shitet. Pa çelës në
  // katalog, posteri mbetet i lirë si gjithmonë — asgjë s'kyçet nga vetja.
  const openPoster = () => {
    // FAZA1e: posteri NUK e nxjerr më njeriun te Shërbimet. Vitrina erdhi te ai —
    // kompozon këtu, zgjedh digjital ose të shtypur këtu, dhe porosit këtu. Ta
    // dërgoje jashtë do të thoshte ta humbte pikërisht pemën që sapo rregulloi.
    // `featureShown`, JO `featureAllowed`: e para pyet "a duhet të ekzistojë kjo
    // derë", e dyta "a e zotëron". Deri te FAZA1e ishin e njëjta gjë, sepse
    // dritarja ishte vegla e shkarkimit — s'kishte kuptim ta hapte kush s'e kishte
    // blerë. Tani dritarja ËSHTË dyqani: brenda saj shihet mostra dhe bëhet
    // porosia. Ta mbyllja me `featureAllowed` do të thoshte se sapo veçoria kalon
    // te "Me pagesë", butoni pushon së punuari pa asnjë fjalë — pikërisht ajo që
    // ndodhi.
    if (!featureShown("poster_pro", posterExportAllowed)) return;   // e fshehur nga admini — s'ka as derë
    setPosterModalOpen(true);
  }; // dritarja "Poster" (e ndarë nga "Pamja")
  // Dy porta të ndryshme, të bashkuara këtu:
  //   · `posterExportAllowed` — porta e vjetër administrative (a lejohet fare).
  //   · `poster_pro` te katalogu — porta komerciale (a shitet / a zotërohet).
  // `featureShown` zgjedh mes tyre: derisa pronari të mos e prekë dialin te Admin
  // Center (gjendja 'off'), fjalën e ka porta e vjetër dhe asgjë s'ndryshon. Sapo
  // ta prekë, regjistri vendos — dhe "e fshehur" fsheh vërtet, ndërsa "me pagesë"
  // e mban të dukshëm edhe për atë që s'e ka blerë: përndryshe s'ka ku ta shohë çmimin.
  const posterVisible = featureShown("poster_pro", posterExportAllowed);
  // A DUHET VITRINA, APO SHKARKIMI? (23 gusht 2026 — defekt i raportuar)
  //
  // Kushti ishte `posterOffers.length > 0`: "a ka ndonjë shërbim posteri me çmim
  // te katalogu". Po ajo s'është pyetja. Sapo ekzistonte një çmim diku, butoni i
  // shkarkimit zhdukej **për të gjithë** — edhe për familjet që e kanë veçorinë
  // falas. Komenti pranë tij thoshte "s'ekziston më për këdo që s'e ka blerë",
  // dhe pikërisht ajo fjali është ajo që kodi nuk e bënte.
  //
  // Pyetja e vërtetë është: **a e zotëron këtë veçori kjo familje?**
  // `featureAllowed` i përgjigjet asaj, dhe e bën me rregullin e pronarit:
  //   · veçori e pa-komercializuar  → vendos porta e vjetër, si gjithmonë
  //   · veçori FALAS dhe e ndezur   → `fn_feature_on` kthen true → shkarkim
  //   · veçori ME PAGESË, e pablerë → vitrina
  //
  // Pra "në rastin falas duhet të jetë si më parë; vetëm kur është me pagesë
  // zbatohen rregullat e reja" — fjalë për fjalë ajo që u kërkua.
  const posterShop = !featureAllowed("poster_pro", posterExportAllowed) && posterOffers.length > 0;
  const [treeSettingsOpen, setTreeSettingsOpen] = useState(false); // ⚙️ cilësimet e avancuara të pamjes — dritare e vet (Modal)
  const [legendOpen, setLegendOpen] = useState(false);           // legjenda e ngjyrave e palosshme
  // Opsione printimi — për një portret galerie: hiq fotot e personave, hiq ikonat e statusit.
  const [removePhotos, setRemovePhotos] = useState(false);
  const [removeStatusIcons, setRemoveStatusIcons] = useState(false);
  const [showFamilyTitle, setShowFamilyTitle] = useState(true);
  // Përjashtim i përkohshëm nga posteri (dhe nga pamja derisa të pastrohet) — për
  // të printuar vetëm një pjesë (p.sh. familja e ngushtë). Nuk ruhet në DB.
  const [posterExcludeIds, setPosterExcludeIds] = useState(() => new Set());
  // "Kush shfaqet te posteri" filtron NJËKOHËSISHT edhe pemën e gjallë — kjo
  // është e qëllimshme sa kohë modali është hapur (shërben si parapamje), por
  // më parë MBETEJ aktiv edhe pas mbylljes. Rrjedhoja: dikush "humbte" persona
  // nga pema e vet pa e kuptuar pse, duke besuar se preku vetëm posterin.
  // Tani mbyllja e pastron gjithmonë zgjedhjen.
  const closePosterModal = () => {
    setPosterModalOpen(false);
    setPosterExcludeIds(new Set());
    setPosterIncludeIds(new Set());
  };
  const [posterExcludeSearch, setPosterExcludeSearch] = useState("");
  const [posterExcludeDesc, setPosterExcludeDesc] = useState(false);
  const [textHalo, setTextHalo] = useState(true);              // hija/kontura e shkronjave
  const [textColor, setTextColor] = useState("auto");          // auto | white | black | gold
  const [removeCardColor, setRemoveCardColor] = useState(false); // hiq ngjyrën rreth kartelave
  // "Printo vetëm një familje": ripërdor modalitetin e testuar "Pasardhësit"
  // (viewMode="descendants" + viewAnchor) — jo filtër i personalizuar, që
  // layout-i të mos deformohet. Kjo fushë vetëm e drejton atë modalitet.
  const [posterIncludeSearch, setPosterIncludeSearch] = useState("");
  // SHTESË (kërkesë e userit — "filtri i posterit të jetë multi-select"):
  // sa persona të duash; për SECILIN shfaqet trungu i tij (vetë personi +
  // pasardhësit + çiftet), kurse degët anësore që s'lidhen me asnjërin
  // fshihen. Bosh = pa filtër (sjellja e mëparshme).
  const [posterIncludeIds, setPosterIncludeIds] = useState(() => new Set());
  // POSTER DY-FAMILJESH (hourglass) — urat që i sheh ky përdorues (anëtar i të
  // dyjave). Kur aktivizohet, `people` zëvendësohet me grupin e bashkuar; një
  // ref e mbron nga mbishkrimi i fetchPeople/realtime derisa të dilet.
  const [twoFamilyBridges, setTwoFamilyBridges] = useState([]);
  const [twoFamilyActive, setTwoFamilyActive] = useState(null);
  const twoFamilyRef = useRef(null);
  // Poster dy-familjesh me DY paraardhës të zgjedhur — shfaqen VETËM pasardhësit
  // e tyre (+ çiftet), duke u takuar te personi lidhës.
  const [twoFamRootA, setTwoFamRootA] = useState(null);
  const [twoFamRootB, setTwoFamRootB] = useState(null);
  // Mënyra e layout-it dy-familjesh: 'full' (pema e plotë e bashkuar),
  // 'converge' (dy paraardhës → pasardhësit, që takohen te ura),
  // 'sidebyside' (dy pema të veçanta krah-për-krah).
  const [twoFamLayout, setTwoFamLayout] = useState("full");
  const [twoFamBridge, setTwoFamBridge] = useState(null); // ura aktive (për ri-aplikim me mënyrë tjetër)
  // SHTESË (kërkesë e userit — "menyja e posterit është klasike, modernizoje"):
  // seksione të palosshme si te panelet e tjera moderne. "style" hapur si
  // parazgjedhje; "excludedList" kontrollon listën e të përjashtuarve
  // (Shfaq/Fshih — "nëse janë 500 veta, s'kanë pse të shfaqen të gjithë").
  const [posterSecOpen, setPosterSecOpen] = useState(() => new Set(["style"]));
  // URAT NDJEKIN "NDËRRO FAMILJEN" (FAZA0i). Kyçi i varësisë është lista e
  // familjeve të lidhura — i njëjti burim që mbush Family Hub-in — dhe jo
  // vetëm familja aktive. Kështu bashkimi me familjen e dytë e sjell urën pa
  // ringarkim, dhe heqja a bllokimi e heq atë po ashtu: `rpc_my_bridges` te
  // serveri ndërtohet nga `my_family_ids()` në momentin e thirrjes, ndaj
  // përgjigjja s'ka si të jetë e vjetëruar.
  const linkedFamKey = useMemo(
    () => (myLinkedFamilies || []).map((f) => f.family_id).sort().join(","),
    [myLinkedFamilies]
  );
  useEffect(() => {
    let cancelled = false;
    supabase.rpc("rpc_my_bridges").then(({ data, error }) => {
      if (cancelled || error || !Array.isArray(data)) return;
      setTwoFamilyBridges(data);
    });
    return () => { cancelled = true; };
  }, [activeFamilyId, linkedFamKey, liveTick]);
  useEffect(() => { setPreviewUrl(null); }, [posterDesignId, posterSize, posterModalOpen]);
  useEffect(() => {
    if (!activeFamilyId) { setArtworkTasks([]); return undefined; }
    let cancelled = false;
    supabase.rpc("rpc_my_artwork_tasks", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (cancelled || error || !Array.isArray(data)) return;
      setArtworkTasks(data);
    });
    supabase.rpc("rpc_services_for_me", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (cancelled || error || !Array.isArray(data)) return;
      setPosterOffers(data.filter((x) => String(x.service_key || "").startsWith("poster")));
    });
    // Nëse FAZA1g s'është xhiruar ende, kjo bie me "function not found" — dhe
    // lista mbetet bosh, pra shiriti nuk shfaqet. Sjellja e saktë: mungesa e
    // njoftimit s'duhet ta prishë aplikacionin.
    supabase.rpc("rpc_my_pricing_notices", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (cancelled || error || !Array.isArray(data)) return;
      setPriceNotices(data);
    });
    supabase.rpc("rpc_my_order_alerts", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (cancelled || error) return;
      const a = Array.isArray(data) ? data[0] : data;
      if (a) setOrderAlerts({ te_reja: a.te_reja || 0, ne_pritje: a.ne_pritje || 0 });
    });
    return () => { cancelled = true; };
  }, [activeFamilyId, liveTick]);

  // "E kuptova": e regjistron te serveri (mbulimi që sheh admini) dhe e heq
  // shiritin menjëherë, pa pritur rrahjen e radhës.
  const ackPriceNotice = useCallback(async (changeId, isFinalWeek) => {
    setPriceNotices((xs) => xs.map((x) => (x.change_id === changeId ? { ...x, seen: true } : x)));
    // Vetëm pranimi i javës së fundit e hesht përfundimisht. Po ta shtonim edhe
    // atë të parin, kujtesa e javës së fundit s'do të vinte kurrë — dhe pikërisht
    // ajo është e vetmja që e kap dikë para se t'i ndalojë veçoria.
    if (isFinalWeek) {
      setNoticeHush((prev) => {
        const next = new Set(prev); next.add(changeId);
        try { localStorage.setItem(PRICE_NOTICE_HUSH, JSON.stringify([...next])); } catch { /* private mode */ }
        return next;
      });
    }
    const { error } = await supabase.rpc("rpc_mark_pricing_notice_seen", {
      p_change_id: changeId, p_family_id: activeFamilyId,
    });
    if (error) console.warn("[pricing notice]", error.message);
  }, [activeFamilyId]);

  // Shfaqet derisa ta pranojë; pastaj hesht — por rikthehet në javën e fundit,
  // sepse tridhjetë ditë më parë njeriu e lexoi dhe e harroi.
  const noticeToShow = useMemo(
    () => priceNotices.find((n) => !n.seen || (n.days_left <= 7 && !noticeHush.has(n.change_id))) || null,
    [priceNotices, noticeHush]
  );
  // Ura mund të shkëputet ndërsa je BRENDA saj — dikush u hoq nga familja, ose
  // admini e heshti. Pa këtë, pema do të mbetej e bashkuar mbi të dhëna që
  // serveri s'do t'i jepte më, dhe veprimi i parë do të dilte gabim pa shpjegim.
  // Dalja e vetvetishme është më e ndershme se një ekran që duket i saktë.
  useEffect(() => {
    if (!twoFamilyActive) return;
    if (twoFamilyBridges.some((b) => b.id === twoFamilyActive)) return;
    exitTwoFamily();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoFamilyBridges, twoFamilyActive]);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc("rpc_list_poster_designs").then(({ data, error }) => {
      if (cancelled || error || !Array.isArray(data)) return;
      setPosterDesigns(data);
    });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!activeFamilyId) return;
    let cancelled = false;
    supabase.rpc("rpc_get_poster_export_enabled", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (cancelled || error) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row == null) return;
      setPosterExportAllowed(!!(typeof row === "object" ? row.enabled : row));
    });
    return () => { cancelled = true; };
  }, [activeFamilyId]);

  // Opsionet e posterit (të përbashkëta për mobile + desktop, që të mos dyfishohen).
  const posterDescendantsOf = (id) => {
    const acc = new Set(); const stack = [id];
    while (stack.length) { const cur = stack.pop(); Object.values(people).forEach((p) => { if ((p.parent_ids || []).includes(cur) && !acc.has(p.id)) { acc.add(p.id); stack.push(p.id); } }); }
    return acc;
  };
  const addPosterExclude = (id) => { setPosterExcludeIds((prev) => { const n = new Set(prev); n.add(id); if (posterExcludeDesc) posterDescendantsOf(id).forEach((d) => n.add(d)); return n; }); setPosterExcludeSearch(""); };
  // Poster dy-familjesh: mban VETËM pasardhësit e dy paraardhësve të zgjedhur
  // (+ vetë ata + bashkëshortët e secilit të mbajtur, që të duken çiftet). Të
  // tjerët i vë te posterExcludeIds (fshihen nga pema e nga posteri). Dy prejardhjet
  // takohen automatikisht te personi lidhës (pasardhës i të dyve).
  const applyTwoFamilyDescendants = (aId, bId) => {
    if (!aId || !bId || !people[aId] || !people[bId]) { showToast(t("Choose an ancestor from each family.")); return; }
    const keep = new Set([aId, bId]);
    [aId, bId].forEach((r) => posterDescendantsOf(r).forEach((d) => keep.add(d)));
    // shto bashkëshortët e të mbajturve (çiftet)
    Array.from(keep).forEach((id) => (people[id]?.spouse_ids || []).forEach((s) => { if (people[s]) keep.add(s); }));
    const exclude = new Set(Object.keys(people).filter((id) => !keep.has(id)));
    setPosterExcludeIds(exclude);
    setViewMode("full");
    showToast(t("Showing only the descendants of the two ancestors."));
  };
  // "E gjithë pema" nuk është gjendje e ruajtur: ËSHTË mungesa e çdo filtri.
  // Po ta mbaja si `useState` do të ishte një e vërtetë e dytë mbi të njëjtin
  // fakt, dhe do të ndahej nga e para në çastin që dikush shton një përjashtim.
  // ⚠ ÇKYÇJA (23 gusht 2026 — defekt i raportuar: "kur e selektoj the whole tree
  //   nuk mund ta çselektoj").
  //
  //   Faji ishte i formës, jo i klikimit: `gjithePema` është gjendje e DERIVUAR
  //   — do të thotë "asnjë filtër" — dhe një kutizë premton një gjendje që
  //   ndizet e fiket. Kur s'ka filtra, s'ka çfarë të hiqet për ta bërë false;
  //   pra kutiza mbetej e shënuar përgjithmonë.
  //
  //   Zgjidhja s'është ta bëj `useState`: atëherë do të kisha dy të vërteta për
  //   të njëjtin fakt, dhe do të ndaheshin sapo dikush shton një përjashtim.
  //   Zgjidhja është ta pranoj se ç'kyçja NUK ndryshon të dhënat — ndryshon
  //   VETËM nëse duan të shihen kontrollet e ngushtimit. Pra kutiza thotë:
  //   "e gjithë pema, dhe s'më duhen kontrollet".
  const [zgjedhVete, setZgjedhVete] = useState(false);
  const paFiltra = viewMode === "full"
    && posterIncludeIds.size === 0 && posterExcludeIds.size === 0;
  const gjithePema = paFiltra && !zgjedhVete;
  const cbLabel = { fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.ink };
  const renderPosterOptions = () => {
    const q = posterExcludeSearch.trim().toLowerCase();
    const matches = q ? Object.values(people).filter((p) => (displayName(p).toLowerCase().includes(q) || emriDallues(p, etiketatDalluese).toLowerCase().includes(q)) && !posterExcludeIds.has(p.id)).slice(0, 6) : [];
    const excluded = Object.values(people).filter((p) => posterExcludeIds.has(p.id));
    const iq = posterIncludeSearch.trim().toLowerCase();
    const includeMatches = iq ? Object.values(people).filter((p) => (displayName(p).toLowerCase().includes(iq) || emriDallues(p, etiketatDalluese).toLowerCase().includes(iq)) && !posterIncludeIds.has(p.id)).slice(0, 6) : [];
    const includedRoots = Object.values(people).filter((p) => posterIncludeIds.has(p.id));
    const secToggle = (k) => setPosterSecOpen((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
    const sec = (k, emoji, title, badge, body) => {
      const open = posterSecOpen.has(k);
      return (
        <div className="mb-1.5 rounded-lg overflow-hidden" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panel }}>
          <button onClick={() => secToggle(k)} className="w-full flex items-center gap-2 px-2.5 py-2 text-left" style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <span style={{ fontSize: 13 }}>{emoji}</span>
            <span style={{ ...cbLabel, fontWeight: 600, flex: 1 }}>{title}</span>
            {badge != null && <span style={{ background: COLORS.accentTint, color: COLORS.ink, borderRadius: 999, fontSize: 10, fontFamily: FONTS.body, padding: "1px 7px", fontWeight: 700 }}>{badge}</span>}
            {open ? <ChevronUp size={14} color={COLORS.inkLight} /> : <ChevronDown size={14} color={COLORS.inkLight} />}
          </button>
          {open && <div className="px-2.5 pb-2.5">{body}</div>}
        </div>
      );
    };
    return (
      <>
        {sec("style", "🎨", t("Style & look"), null, (
          <>
        <div className="mb-2">
          <Toggle on={showFamilyTitle} onChange={setShowFamilyTitle} label={t("Add the family name at the top")} />
          <Toggle on={textHalo} onChange={setTextHalo} label={t("Letter shadow/outline")} />
          <Toggle on={removePhotos} onChange={setRemovePhotos} label={t("Remove people's photos")} />
          <Toggle on={removeStatusIcons} onChange={setRemoveStatusIcons} label={t("Remove status icons")} />
          <Toggle on={removeCardColor} onChange={setRemoveCardColor} label={t("Remove the color around cards")} />
        </div>
        <div>
          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("Letter color:")}</p>
          <Segmented value={textColor} onChange={setTextColor}
            options={[["auto", "Auto"], ["white", t("White")], ["black", t("Black")], ["gold", t("Gold")]]} />
        </div>
          </>
        ))}
        {sec("who", "👥", t("Who appears on the poster"), excluded.length > 0 ? String(excluded.length) : null, (
          <>
        <p style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight, margin: "0 0 6px" }}>
          {t("On the poster:")} <b style={{ color: COLORS.ink }}>{Object.keys(visiblePeople).length}</b>{excluded.length > 0 ? <span> · {t("Excluded:")} {excluded.length}</span> : null}
        </p>
        {/* E GJITHË PEMA (kërkesë e pronarit, 23 gusht 2026):
            "nëse dikush don ta shtypë të gjithë pemën nuk lodhet me instalime
            manuale."
            Ai që do gjithçka nuk duhet ta ndërtojë "gjithçka" nga përjashtimet.
            Një kutizë e vetme e bën punën e tri kontrolleve: pamja shkon te
            `full`, lista e përfshirjeve zbrazet, dhe përjashtimet po ashtu.
            Kur është e shënuar, kontrollet poshtë s'kanë çfarë të bëjnë — pra
            fshihen: një fushë kërkimi që s'ndryshon asgjë është ftesë për
            konfuzion. */}
        <label style={{ ...cbLabel, display: "flex", alignItems: "center", gap: 7, margin: "0 0 8px",
                        padding: "7px 9px", borderRadius: 8, cursor: "pointer",
                        border: `1px solid ${gjithePema ? COLORS.gold : COLORS.goldSoft}`,
                        background: gjithePema ? COLORS.accentTint : "transparent" }}>
          <input type="checkbox" checked={gjithePema}
            onChange={(e) => {
              if (e.target.checked) {
                setPosterIncludeIds(new Set());
                setPosterExcludeIds(new Set());
                setViewMode("full");
                setZgjedhVete(false);
              } else {
                // Asgjë s'hiqet nga posteri — thjesht hapen kontrollet, që ai
                // të zgjedhë vetë kë të heqë. Të zbrazja pemën këtu do të ishte
                // një klikim që fshin njerëz pa e kërkuar askush.
                setZgjedhVete(true);
              }
            }} />
          <span style={{ fontWeight: gjithePema ? 700 : 400 }}>{t("The whole tree")}</span>
          <span style={{ color: COLORS.inkLight, fontSize: 10.5, marginLeft: "auto" }}>
            {Object.keys(people).length} {t("people")}
          </span>
        </label>
        {/* MULTI-SELECT (kërkesë e userit): zgjidh sa persona të duash — për
            secilin shfaqet trungu i tij (personi + pasardhësit + çiftet), dhe
            çdo degë anësore që s'lidhet me asnjërin fshihet. */}
        {!gjithePema && paFiltra && (
          <p style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight, margin: "-4px 0 7px" }}>
            {t("Nothing is removed yet — everyone is still on the poster.")}
          </p>
        )}
        <div className="mb-1.5" style={{ display: gjithePema ? "none" : "block" }}>
          <input value={posterIncludeSearch} onChange={(e) => setPosterIncludeSearch(e.target.value)} placeholder={t("Show only these people and their branches (search a name)")} className="w-full rounded-md text-xs px-2 py-1.5" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }} />
          {includeMatches.length > 0 && (
            <div className="mt-1 rounded-md overflow-hidden" style={{ border: `1px solid ${COLORS.goldSoft}` }}>
              {includeMatches.map((m) => (
                <button key={m.id} onClick={() => { setPosterIncludeIds((prev) => { const n = new Set(prev); n.add(m.id); return n; }); setPosterIncludeSearch(""); setViewMode("full"); }} className="block w-full text-left px-2 py-1.5 text-xs hover:bg-black/5" style={{ color: COLORS.ink, fontFamily: FONTS.body }}>{displayName(m)} <span style={{ color: COLORS.inkLight }}>{lifespan(m)}</span></button>
              ))}
            </div>
          )}
          {includedRoots.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mt-1.5">
              {includedRoots.map((m) => (
                <span key={m.id} className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: COLORS.accentTint, color: COLORS.ink, fontSize: 10.5, fontFamily: FONTS.body }}>
                  🌳 {truncateCard(displayName(m), 14)}
                  <button onClick={() => setPosterIncludeIds((prev) => { const n = new Set(prev); n.delete(m.id); return n; })} style={{ color: COLORS.rose }}>✕</button>
                </span>
              ))}
              <button onClick={() => setPosterIncludeIds(new Set())} className="px-1.5 py-0.5 rounded text-xs" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontFamily: FONTS.body }}>{t("Clear")}</button>
            </div>
          )}
        </div>
        <div className="mb-1.5">
          <label className="flex items-center gap-1.5 mb-1" style={cbLabel}><input type="checkbox" checked={posterExcludeDesc} onChange={(e) => setPosterExcludeDesc(e.target.checked)} /> {t("Exclude descendants too")}</label>
          <input value={posterExcludeSearch} onChange={(e) => setPosterExcludeSearch(e.target.value)} placeholder={t("Exclude people from the poster (search name)")} className="w-full rounded-md text-xs px-2 py-1.5" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }} />
          {matches.length > 0 && (
            <div className="mt-1 rounded-md overflow-hidden" style={{ border: `1px solid ${COLORS.goldSoft}` }}>
              {matches.map((m) => (
                <button key={m.id} onClick={() => addPosterExclude(m.id)} className="block w-full text-left px-2 py-1.5 text-xs hover:bg-black/5" style={{ color: COLORS.ink, fontFamily: FONTS.body }}>{displayName(m)} <span style={{ color: COLORS.inkLight }}>{lifespan(m)}</span></button>
              ))}
            </div>
          )}
          {excluded.length > 0 && (
            <div className="mt-1.5">
              <div className="flex items-center gap-1.5">
                <span style={{ ...cbLabel, color: COLORS.inkLight, flex: 1 }}>{t("Excluded:")} <b style={{ color: COLORS.ink }}>{excluded.length}</b></span>
                <button onClick={() => secToggle("excludedList")} className="px-2 py-0.5 rounded text-xs" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontFamily: FONTS.body }}>{posterSecOpen.has("excludedList") ? t("Hide") : t("Show")}</button>
                <button onClick={() => setPosterExcludeIds(new Set())} className="px-2 py-0.5 rounded text-xs" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontFamily: FONTS.body }}>{t("Clear")}</button>
              </div>
              {posterSecOpen.has("excludedList") && (
                <div className="flex flex-wrap items-center gap-1 mt-1.5" style={{ maxHeight: 150, overflowY: "auto", overflowX: "hidden", overscrollBehavior: "contain" }}>
                  {excluded.map((m) => (
                    <span key={m.id} className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontSize: 10.5, fontFamily: FONTS.body }}>
                      {truncateCard(m.first_name, 10)} <button onClick={() => setPosterExcludeIds((prev) => { const n = new Set(prev); n.delete(m.id); return n; })} style={{ color: COLORS.rose }}>✕</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
          </>
        ))}
        {/* Posteri dy-familjesh — shërbim i veçantë. I kyçur NUK fshihet: kartela
            mbetet aty, me bravën dhe me rrugën drejt çmimit. Fshehja e mëson
            përdoruesin se s'ekziston; brava e mëson se ekziston dhe blihet. */}
        {twoFamilyBridges.length > 0 && featureShown("poster_two_family", true) && sec("twofam", "🌉", t("Two-family poster"),
          featureGated("poster_two_family") ? "🔒" : (twoFamilyActive ? "✓" : String(twoFamilyBridges.length)),
          featureGated("poster_two_family") ? (
          <div>
            <p style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight, marginBottom: 6, lineHeight: 1.5 }}>
              {t("Merge two of your families into one poster (both descend to the person who links them).")}
            </p>
            <button onClick={() => { setActiveScreen("services"); setViewPanelOpen(false); }}
              className="w-full px-2 py-1.5 rounded text-xs"
              style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body, fontWeight: 600 }}>
              {t("Unlock — see the price")}
            </button>
          </div>
          ) : (
          <>
            <div style={{ marginBottom: 6 }}>
              <div style={{ ...cbLabel, marginBottom: 3 }}>{t("Layout")}</div>
              <select value={twoFamLayout} onChange={(e) => changeTwoFamLayout(e.target.value)} className="w-full rounded-md text-xs px-2 py-1.5" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }}>
                <option value="full">{t("Full merged tree")}</option>
                <option value="converge">{t("Converging (two ancestors → meet at the link)")}</option>
                <option value="sidebyside">{t("Side by side (two separate trees)")}</option>
              </select>
            </div>
            {!twoFamilyActive ? (
              <>
                <p style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight, marginBottom: 4 }}>{t("Merge two of your families into one poster (both descend to the person who links them).")}</p>
                {twoFamilyBridges.map((br) => (
                  <button key={br.id} onClick={() => enterTwoFamily(br)} className="block w-full text-left px-2 py-1.5 text-xs rounded hover:bg-black/5" style={{ color: COLORS.ink, fontFamily: FONTS.body }}>{t("Merge:")} {br.note || "dy familjet"} →</button>
                ))}
              </>
            ) : (() => {
              const famA = {}; const famB = {};
              Object.values(people).forEach((p) => { (p.family_id === activeFamilyId ? famA : famB)[p.id] = p; });
              return (
                <div className="space-y-2">
                  <div>
                    <div style={{ ...cbLabel, marginBottom: 3 }}>{t("Poster title")}</div>
                    <input value={twoFamTitleCustom} onChange={(e) => setTwoFamTitleCustom(e.target.value)} placeholder={twoFamName || ""} className="w-full rounded-md text-xs px-2 py-1.5" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }} />
                  </div>
                  {twoFamLayout === "converge" && (
                    <>
                      <div>
                        <div style={{ ...cbLabel, marginBottom: 3 }}>{t("Ancestor — your family")}</div>
                        <SearchSelect people={famA} value={twoFamRootA} onChange={setTwoFamRootA} placeholder={t("Search a person…")} />
                      </div>
                      <div>
                        <div style={{ ...cbLabel, marginBottom: 3 }}>{t("Ancestor — the other family")}</div>
                        <SearchSelect people={famB} value={twoFamRootB} onChange={setTwoFamRootB} placeholder={t("Search a person…")} />
                      </div>
                      <button onClick={() => applyTwoFamilyDescendants(twoFamRootA, twoFamRootB)} disabled={!twoFamRootA || !twoFamRootB} className="w-full px-2 py-1.5 rounded text-xs" style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body, opacity: (!twoFamRootA || !twoFamRootB) ? 0.5 : 1 }}>{t("Only their descendants")}</button>
                    </>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setPosterExcludeIds(new Set())} className="px-2 py-1 rounded text-xs" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontFamily: FONTS.body }}>{t("Show everyone")}</button>
                    <button onClick={exitTwoFamily} className="px-2 py-1 rounded text-xs" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontFamily: FONTS.body }}>{t("Exit — back to your tree")}</button>
                  </div>
                </div>
              );
            })()}
          </>
          )
        )}
      </>
    );
  };

  // RIORGANIZIM (kërkesë e userit — "shiriti të ketë funksione të qarta e të
  // ndara"): përmbajtja e vjetër monolite "Shiko" u nda në TRE pjesë, të
  // aplikuara "on top" për ÇDO variant shiriti (classic/sheet/fab/contextual/
  // drawer/hybrid), sepse të gjithë kalojnë nga po këto funksione:
  //   1. renderViewPickerContent — VETËM zgjedhja e pamjes (5 pamjet, listë
  //      e pastër) — hapet nga ikona e pemës 🌳.
  //   2. renderTreeSettingsContent — TË GJITHA cilësimet e avancuara të
  //      pamjes (navigimi i trungut, degët paralele, ngjyrat e degëve,
  //      brezat) — hapen nga ikona ⚙️ në një dritare të vet (Modal), JO më
  //      popover brenda kolonash me overflow (shkaku i "Settings në
  //      background": popover-i pritej nga scroll-container-i i drawer-it).
  //   3. Posteri 🖼️ — ikonë/buton më vete → setPosterModalOpen(true).
  // PEMA1 — `mbyll` mbërriti bashkë me dritaren e plotë. Si kuti e vogël
  // pluskuese, panelin që rrinte hapur pas zgjedhjes s'e vinte re askush; si
  // dritare mbi krejt ekranin, ai do të thoshte «zgjodha pamjen dhe pema u
  // fsheh». Dy mënyra kërkojnë të njëjtën përmbajtje me sjellje të ndryshme,
  // prandaj mbyllja jepet nga jashtë, jo e ngulitur brenda.
  //
  // «Familja e ngushtë» dhe «Pasardhësit» NUK e mbyllin menjëherë: ato kërkojnë
  // edhe një person, dhe zgjedhësi i tij del pikërisht poshtë. Mbyllja vjen kur
  // personi zgjidhet.
  // PEMA2 (29 gusht 2026, kërkesë e pronarit) — «vetëm dritarja u rrit në krejt
  // dritaren, por nuk është organizuar si një dritare settingu e re; vetëm pjesa
  // e bardhë është rritur».
  //
  // Kishte të drejtë, dhe kjo është gjysma që PEMA1 e la pa bërë. PEMA1 e ndryshoi
  // KUTINË — nga popover 320px në dritare mbi krejt ekranin — por përmbajtja
  // brenda mbeti ajo e shkruar për popover-in: etiketë 10px me shkronja të mëdha,
  // rreshta 13.5px pa asnjë shpjegim, të ngjeshur lart. E zgjeruar në ekran të
  // plotë, ajo nuk bëhet faqe; bëhet një popover i vogël me shumë bardhësi poshtë.
  //
  // Një dritare e plotë ka nevojë për strukturë që popover-i s'e kishte:
  //   · rreshta të lartë sa gishti (≈62px), jo sa kursori;
  //   · një përshkrim nën secilin — te popover-i s'kishte vend, këtu ka, dhe
  //     pikërisht ai i thotë njeriut ç'ndryshim sjell «Lisi i Gjakut»;
  //   · grupe me kufi, si te çdo faqe cilësimesh, që syri të kapë ku mbaron një
  //     temë dhe nis tjetra;
  //   · kërkuesi i personit të dalë POSHTË rreshtit që e kërkon, jo në fund të
  //     listës — lidhja shkak-pasojë duhet parë, jo mbajtur mend.
  // MUZEU3 — te një koleksion katër nga pesë pamjet ishin pamje GJAKU: trungu
  // im, lisi i gjakut, familja e ngushtë, pasardhësit. Aty ato do të ishin katër
  // dyer që hapin zbrazëti.
  //
  // Kjo ishte prova që vendosa vetë kur shkruam idenë: nëse kjo dritare nuk
  // ndryshon te koleksioni, atëherë muzeu është familje e maskuar.
  const PAMJET_E_KOLEKSIONIT = [
    ["full", "🏛️", t("The whole exhibition"), t("Every figure, laid out hall by hall.")],
    ["salla", "🚪", t("By hall"), t("Only one hall at a time.")],
    ["koha", "🕰️", t("By time"), t("Everyone in one line, oldest first — halls set aside.")],
  ];
  const PAMJET_E_PEMES = [
    ["trunk", "🌱", t("My trunk"), t("You, your ancestors, and everyone descended from them.")],
    ["blood_lisi", "🩸", t("The Blood Line"), t("Blood relatives only — those who joined by marriage stay out.")],
    ["close", "👨‍👩‍👧", t("Close family"), t("One person with their parents, partner and children.")],
    ["full", "🌳", t("Full tree"), t("Everyone in the family, with nothing filtered out.")],
    ["descendants", "⬇️", t("Descendants"), t("One person and everyone who comes after them.")],
  ];
  const grupiIPamjes = { borderRadius: 14, overflow: "hidden", border: `1px solid ${COLORS.goldSoft}`, background: `var(--surface2, ${COLORS.panelSoft})` };
  const etiketaESeksionit = { fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, margin: "0 4px 8px", display: "block" };
  const renderViewPickerContent = (mbyll) => (
    <>
      <span style={etiketaESeksionit}>{t("View mode")}</span>
      <div style={grupiIPamjes}>
        {(eshteKoleksion ? PAMJET_E_KOLEKSIONIT : PAMJET_E_PEMES).map(([key, emoji, label, pershkrimi], i) => {
          const aktiv = viewMode === key;
          const kerkonPerson = !eshteKoleksion && (key === "close" || key === "descendants");
          const kerkonSalle = eshteKoleksion && key === "salla";
          return (
            <React.Fragment key={key}>
              <button
                onClick={() => { setViewMode(key); if (!kerkonPerson && !kerkonSalle) mbyll?.(); }}
                className="w-full flex items-center gap-3 text-left"
                style={{
                  padding: "12px 14px",
                  background: aktiv ? COLORS.accentTint : "transparent",
                  borderTop: i ? `1px solid ${COLORS.goldSoft}` : "none",
                  fontFamily: FONTS.body,
                }}
              >
                <span className="shrink-0 flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 999, background: aktiv ? COLORS.gold : `var(--surface, ${COLORS.panel})`, border: `1px solid ${COLORS.goldSoft}`, fontSize: 18 }}>{emoji}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 15, fontWeight: aktiv ? 700 : 600, color: COLORS.ink }}>{label}</span>
                  <span style={{ display: "block", fontSize: 12.5, lineHeight: 1.35, color: COLORS.inkLight, marginTop: 2 }}>{pershkrimi}</span>
                </span>
                {aktiv && <span className="shrink-0" style={{ color: COLORS.gold, fontWeight: 700, fontSize: 16 }}>✓</span>}
              </button>
              {aktiv && kerkonSalle && (
                <div style={{ padding: "0 14px 12px", background: COLORS.accentTint }}>
                  <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginBottom: 6 }}>{t("Which hall?")}</span>
                  <select value={sallaAktive || ""} onChange={(ev) => { setSallaAktive(ev.target.value); mbyll?.(); }}
                    className="w-full rounded-md px-3 py-2 text-sm"
                    style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }}>
                    {sallatEKoleksionit.map((em) => (
                      <option key={em} value={em}>{em || t("Not placed yet")}</option>
                    ))}
                  </select>
                </div>
              )}
              {aktiv && kerkonPerson && (
                <div style={{ padding: "0 14px 12px", background: COLORS.accentTint }}>
                  <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginBottom: 6 }}>{t("Around whom?")}</span>
                  <SearchSelect people={people} value={viewAnchor} onChange={(v) => { setViewAnchor(v); if (v) mbyll?.(); }} placeholder={t("Search a person…")} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
  const renderTreeSettingsContent = () => (
    <>
      {/* SHTESË (kërkesë e userit): çdo përdorues zgjedh PËR VETE menynë e
          shiritit të pemës — parazgjedhja vjen nga platforma, zgjedhja ruhet
          vetëm në këtë pajisje. */}
      {/* FIX (kërkesë e userit — "po zë shumë hapësirë"): nga 7 butona të
          renditur vertikalisht → një dropdown i vetëm, kompakt. */}
      {/* NGJYRAT E PEMËS — zgjedhje e përdoruesit, jo vetëm e adminit.
          Kërkesë e pronarit, 4 shtator 2026. Parazgjedhja vjen nga platforma;
          zgjedhja ruhet vetëm në këtë pajisje, si ajo e shiritit më poshtë. */}
      <div className="mb-3 pb-2 flex items-center gap-2" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, whiteSpace: "nowrap" }}>{t("Tree colors")}</span>
        <select
          value={userBurimiNgjyrave}
          onChange={(e) => zgjidhBurimineNgjyrave(e.target.value)}
          className="flex-1 rounded-md text-xs px-2 py-1.5"
          style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, minWidth: 0 }}
        >
          <option value="">{`${t("Platform default")} (${burimiINgjyrave === "familja" ? t("Branch colors") : burimiINgjyrave === "asnje" ? t("No colors") : t("Theme colors")})`}</option>
          <option value="dizajni">{t("Theme colors")}</option>
          <option value="familja">{t("Branch colors")}</option>
          <option value="asnje">{t("No colors")}</option>
        </select>
      </div>
      {/* Rreshti i gjendjes — shtuar më 4 shtator 2026 sepse pyetja «a po
          funksionon?» s'kishte asnjë përgjigje të dukshme. Tani aplikacioni e
          thotë vetë se ç'po përdor DHE nga e ka marrë. */}
      <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, margin: "-6px 2px 10px" }}>
        {t("Active now:")}{" "}
        <strong style={{ color: COLORS.ink }}>
          {burimiEfektiv() === "asnje" ? t("No colors") : burimiEfektiv() === "familja" ? t("Branch colors") : t("Theme colors")}
        </strong>{" "}
        <span>({userBurimiNgjyrave ? t("your choice") : t("platform default")})</span>
      </p>
      <div className="mb-3 pb-2 flex items-center gap-2" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, whiteSpace: "nowrap" }}>{t("Tree bar menu")}</span>
        <select
          value={userNavVariant}
          onChange={(e) => chooseNavVariant(e.target.value)}
          className="flex-1 rounded-md text-xs px-2 py-1.5"
          style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, minWidth: 0 }}
        >
          <option value="">{`${t("Platform default")} (${adminNavVariant === "sheet" ? "Swap" : adminNavVariant})`}</option>
          <option value="classic">{t("Classic (static bar)")}</option>
          <option value="sheet">{t("Swap")}</option>
          <option value="fab">{t("FAB (speed-dial)")}</option>
          <option value="contextual">{t("Contextual")}</option>
          <option value="drawer">{t("Side drawer")}</option>
          <option value="hybrid">{t("Hybrid")}</option>
        </select>
      </div>
      {viewMode === "trunk" && (
        <div className="mb-3 pb-1">
          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 4px" }}>{t("Navigation")}</p>
          {focusPersonId && focusPersonId !== myPersonId ? (
            <>
              <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }}>
                {t("Showing the trunk of:")} <strong>{displayName(people[focusPersonId] || {})}</strong>
              </p>
              <button onClick={() => { setFocusPersonId(null); setExpandedIds(new Set()); setChainStopId(null); }} className="w-full py-2 rounded-lg text-xs mb-1" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>
                {t("← Back to my trunk")}
              </button>
            </>
          ) : (
            <p className="mb-1" style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, lineHeight: 1.4 }}>
              {t('Your ancestors are always visible. Click "+" on any person to see their descendants, branch by branch.')}
            </p>
          )}
          <Toggle on={parallelBranches} onChange={setParallelBranches}
            label={t("Allow several branches open at once (comparison)")}
            desc={parallelBranches ? t('Each "+" is added without closing the other branches.') : t('Each "+" closes the previously opened branch (one branch at a time).')} />
          <Toggle on={freeSpiritMode} onChange={setFreeSpiritMode}
            label={t("Adaptive navigation — tap any person to centre the tree on them")}
            desc={t("Affects navigation only, not editing rights.")} />
        </div>
      )}
      {/* Ngjyrat e degëve — legjendë/filtra e palosshme (rrallë ndryshohet). */}
      <div className="surface-radius mb-2" style={{ border: `1px solid ${COLORS.goldSoft}`, overflow: "hidden" }}>
        <button onClick={() => setLegendOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-black/5" style={{ background: "transparent" }}>
          <span style={{ fontSize: 15 }}>🎨</span>
          <span className="flex-1" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Branch colors")}</span>
          <ChevronDown size={16} color={COLORS.gold} style={{ transform: legendOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
        </button>
        {legendOpen && (
          <div className="px-3 pb-2" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            {[
              [showCore, setShowCore, branchColor("core"), t(branchLabel("core")), "core"],
              [showMarriedIn, setShowMarriedIn, branchColor("married_in"), t(branchLabel("married_in")), "married_in"],
              [showCloseCousin, setShowCloseCousin, branchColor("close_cousin"), t(branchLabel("close_cousin")), "close_cousin"],
              [showExtended, setShowExtended, branchColor("extended"), t(branchLabel("extended")), "extended"],
              [showMarriedOut, setShowMarriedOut, branchColor("married_out"), t(branchLabel("married_out")), "married_out"],
            ].map(([checked, setter, color, label, key]) => (
              <Toggle key={label} on={checked} onChange={setter} label={label} icon={<BranchIcon branch={key} size={15} color={color} />} />
            ))}
          </div>
        )}
      </div>
      {/* "Brezat": banda + etiketa te pema (vetëm nëse admini e ka lejuar). */}
      {generationBandsAllowed && (
        <Toggle on={showGenerationBands} onChange={setShowGenerationBands}
          label={t("Show generations (bands + labels)")} desc={t("Bands + labels")} />
      )}
    </>
  );
  // Paneli "Shiko" (i përdorur nga çdo variant shiriti): zgjedhja e pamjes +
  // dy veprimet e ndara — posteri 🖼️ dhe cilësimet ⚙️ (dritare e vet).
  const renderShikoPanelContent = (mbyll) => {
    // Veprimet ndjekin të njëjtin ritëm si pamjet: ikonë në rreth, titull,
    // përshkrim, shigjetë. Më parë ishin dy drejtkëndësha me kufi që dukeshin
    // si butona formulari të ngecur në fund të një liste.
    const veprimet = [
      posterVisible && ["poster", "🖼️", t("Create a poster for printing"), t("A printable sheet of the tree as you see it now."), () => { mbyll?.(); openPoster(); }, featureGated("poster_pro")],
      ["settings", "⚙️", t("Tree view settings"), t("Trunk navigation, branch colours, generations, and the tree bar."), () => { mbyll?.(); setTreeSettingsOpen(true); }, false],
    ].filter(Boolean);
    return (
      <>
        {renderViewPickerContent(mbyll)}
        <span style={{ ...etiketaESeksionit, marginTop: 20 }}>{t("More")}</span>
        <div style={grupiIPamjes}>
          {veprimet.map(([key, emoji, label, pershkrimi, veproj, kyçur], i) => (
            <button key={key} onClick={veproj} className="w-full flex items-center gap-3 text-left"
              style={{ padding: "12px 14px", background: "transparent", borderTop: i ? `1px solid ${COLORS.goldSoft}` : "none", fontFamily: FONTS.body }}>
              <span className="shrink-0 flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 999, background: `var(--surface, ${COLORS.panel})`, border: `1px solid ${COLORS.goldSoft}`, fontSize: 18, opacity: kyçur ? 0.55 : 1 }}>{emoji}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 15, fontWeight: 600, color: COLORS.ink }}>{label}{kyçur && <span style={{ marginLeft: 6, fontSize: 12, color: COLORS.gold }}>🔒</span>}</span>
                <span style={{ display: "block", fontSize: 12.5, lineHeight: 1.35, color: COLORS.inkLight, marginTop: 2 }}>{pershkrimi}</span>
              </span>
              <ChevronRight size={17} color={COLORS.inkLight} className="shrink-0" />
            </button>
          ))}
        </div>
      </>
    );
  };

  // SHTESË (kërkesë e userit — redizajnim i variantave "hybrid"/"contextual"/
  // "swap") — rreshti i mjeteve të Pemës (Kërko/Shiko · buton kthimi ·
  // Shto/Fit), i NDARË në funksion të vetëm sepse e përdorin tri variante
  // ndryshe (secili me gjendjen/togglimin e vet). `onClose` = çfarë ndodh
  // kur klikohet butoni i kthimit (shigjeta e lakuar) — çdo variant e kalon
  // callback-un e vet (mbyll hybrid / kolapso contextual / mbyll swap).
  // FIX (kërkesë e userit — "te swap-i s'ka nevojë për shigjetën majtas,
  // hiqe krejt, vendos Home aty") — te "swap" butoni qendror-lart MBETET
  // gjithmonë i dukshëm/prekshëm edhe kur rreshti i mjeteve është hapur
  // (vetë ai bën togglimin), kështu shigjeta e kthimit brenda rreshtit
  // bëhet e panevojshme — VETËM për "swap" hiqet krejt, dhe Home zë vendin
  // e saj majtas (jo më në qendër). Hybrid/contextual s'e kanë këtë buton
  // të dytë "gjithmonë të dukshëm", ndaj mbajnë shigjetën e kthimit siç
  // ishte.
  // ================== SHIRITI I PEMËS — BURIM I VETËM IKONASH ==================
  // (kërkesë e userit — "ndryshimi të implementohet në TË GJITHË baret + opsione
  // në Admin Center: cilat elemente duken, renditja, orientimi, pozicioni").
  // Çdo variant (classic statik / swap / fab / contextual / drawer / hybrid)
  // rendit PIKËRISHT këto ikona, sipas konfigurimit treeBarConfig. Kërkimi dhe
  // Pamja hapen si PANELE FIKSE mbi gjithçka (renderTreeBarPanels) — kurrë më
  // brenda kolonash me overflow (shkaku i "hapet në background").
  const treeBarBtnStyle = { background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` };
  const renderTreeBarIcons = (after, compact) => {
    // `compact` (kërkesë e userit — "të gjitha ikonat në NJË linjë, si te
    // Swap"): ikona më të vogla + padding më i ngushtë, që edhe 9 ikonat të
    // zënë vend në një rresht të vetëm në ekranet e telefonit.
    const done = (fn) => () => { fn(); if (after) after(); };
    const pad = compact ? "p-1.5" : "p-2.5";
    const sz = compact ? 14 : 16;
    const em = compact ? 13 : 15;
    const items = (treeBarConfig.items || TREEBAR_DEFAULT.items).filter((it) => it && it.on !== false);
    return items.map((it) => {
      switch (it.key) {
        case "home": return <button key="home" onClick={done(() => setActiveScreen("home"))} className={`${pad} rounded-full shrink-0`} style={{ background: COLORS.accentTint }} title={t("Family")}><Home size={compact ? 15 : 17} color={COLORS.gold} /></button>;
        case "search": return <button key="search" onClick={() => setTreeBarPanel((v) => (v === "search" ? null : "search"))} className={`${pad} rounded-full shrink-0`} style={{ ...treeBarBtnStyle, background: treeBarPanel === "search" ? COLORS.accentTint : COLORS.panel }} title={t("Search")}><Search size={sz} color={COLORS.ink} /></button>;
        case "view": return <button key="view" onClick={() => setTreeBarPanel((v) => (v === "view" ? null : "view"))} className={`${pad} rounded-full shrink-0`} style={{ ...treeBarBtnStyle, background: (viewMode !== "full" || treeBarPanel === "view") ? COLORS.accentTint : COLORS.panel }} title={t("View")}><span style={{ fontSize: em }}>🌳</span></button>;
        case "poster": return posterVisible ? <button key="poster" onClick={done(() => openPoster())} className={`${pad} rounded-full shrink-0`} style={treeBarBtnStyle} title={t("Poster for printing")}><span style={{ fontSize: em }}>🖼️</span></button> : null;
        case "settings": return <button key="settings" onClick={done(() => setTreeSettingsOpen(true))} className={`${pad} rounded-full shrink-0`} style={treeBarBtnStyle} title={t("Tree view settings")}><span style={{ fontSize: em }}>⚙️</span></button>;
        case "zoomin": return <button key="zoomin" onClick={() => { userAdjustedViewRef.current = true; setScale((s) => Math.min(2.2, s + 0.15)); }} className={`${pad} rounded-full shrink-0`} style={treeBarBtnStyle} title={t("Zoom in")}><ZoomIn size={sz} color={COLORS.ink} /></button>;
        case "zoomout": return <button key="zoomout" onClick={() => { userAdjustedViewRef.current = true; setScale((s) => Math.max(0.3, s - 0.15)); }} className={`${pad} rounded-full shrink-0`} style={treeBarBtnStyle} title={t("Zoom out")}><ZoomOut size={sz} color={COLORS.ink} /></button>;
        // Emri "Fit" premtonte "shiko krejt pemën", kurse veprimi qendërzon dhe
        // zmadhon mbi TY. Sjellja mbetet — është e dobishme — por etiketa tani
        // e thotë atë që bën.
        case "fit": return <button key="fit" onClick={done(() => fitView(currentPersonId))} className={`${pad} rounded-full shrink-0`} style={treeBarBtnStyle} title={t("Center on me")}><Crosshair size={sz} color={COLORS.ink} /></button>;
        case "add": return ["admin", "editor", "contributor"].includes(myRole) ? <button key="add" onClick={done(() => hapShtimin())} className={`${pad} rounded-full shrink-0`} style={{ background: COLORS.gold, color: COLORS.bgDeep }} title={t("Add")}><Plus size={sz} /></button> : null;
        default: return null;
      }
    }).filter(Boolean);
  };
  // PEMA1 (29 gusht 2026, kërkesë e pronarit) — «dua që dritarja të hapet në
  // gjithë faqen e telefonit edhe të ketë back butonin lart».
  //
  // Këto ishin kuti pluskuese: 320px të gjera, 55% të lartësisë, pa titull dhe
  // pa shigjetë kthimi. Pjesa tjetër e aplikacionit — «Pamja», «⚙️ Cilësimet
  // e pemës», «Ndërro familjen» — përdor `Modal`, i cili në telefon mbulon
  // krejt ekranin dhe ka shigjetën lart, kurse te kompjuteri rri dritare e
  // vogël në qendër. Këto të dyja ishin të vetmet që mbetën jashtë asaj rregulle.
  //
  // Tani është një rrugë e vetme, jo dy: e njëjta përmbajtje, i njëjti kufi.
  const renderTreeBarPanels = () => {
    if (!treeBarPanel || activeScreen !== "tree") return null;
    if (treeBarPanel === "search") {
      return (
        <Modal title={t("Search")} onClose={() => setTreeBarPanel(null)}>
          <div className="flex items-center rounded-md px-2 py-1.5 mb-2" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
            <Search size={15} color={COLORS.gold} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Search")} className="bg-transparent outline-none px-2 text-sm flex-1" style={{ color: COLORS.ink, fontFamily: FONTS.body }} autoFocus />
            {search && <button type="button" onClick={() => setSearch("")} aria-label={t("Clear")} className="flex items-center justify-center shrink-0" style={{ width: ZONA_PREKJES, height: ZONA_PREKJES, margin: "-12px -10px -12px 0", background: "transparent", border: "none", padding: 0 }}><X size={14} color={COLORS.gold} /></button>}
          </div>
          {searchMatches.map((m) => (
            <button key={m.id} onClick={() => { focusOn(m.id); setTreeBarPanel(null); }} className="block w-full text-left px-2 py-2.5 rounded hover:bg-black/5" style={{ color: COLORS.ink, fontSize: 14, fontFamily: FONTS.body }}>
              {emriDallues(m, etiketatDalluese)} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{lifespan(m)}</span>
            </button>
          ))}
        </Modal>
      );
    }
    return (
      <Modal title={t("View")} onClose={() => setTreeBarPanel(null)}>
        {renderShikoPanelContent(() => setTreeBarPanel(null))}
      </Modal>
    );
  };

  const renderToolsRow = (onClose, hideBackButton) => {
    // RIORGANIZIM (kërkesë e userit): rreshti i mjeteve merr ikonat nga
    // burimi i vetëm i konfigurueshëm (renderTreeBarIcons) — swap pa buton
    // kthimi (e ka vetë butonin qendror), hybrid/contextual me shigjetën.
    if (hideBackButton) {
      return <div className="flex items-center justify-around gap-1 px-1 py-2" style={{ flexWrap: "nowrap" }}>{renderTreeBarIcons(undefined, true)}</div>;
    }
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={onClose} className="p-2.5 rounded-full shadow-md shrink-0" style={{ background: COLORS.gold, color: COLORS.bgDeep }} title={t("Back to the menu")}>
          <Undo2 size={18} />
        </button>
        <div className="flex-1 flex items-center justify-around gap-1" style={{ flexWrap: "nowrap" }}>{renderTreeBarIcons(undefined, true)}</div>
      </div>
    );
  };

  // SHTESË (kërkesë e userit) — vetë ikona e "badge"-it "+" mbi ikonën
  // "Pema" te bar-i klasik — e ndarë, e ripërdorur nga hybrid/fab/
  // contextual (kur i kolapsuar). `onOpen` = çfarë hap (hybrid → hap
  // rreshtin e mjeteve; fab → hap speed-dial-in; contextual → hiq
  // kolapsimin).
  const renderPemaBadge = (onOpen, rotated) => (
    <button
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      aria-label={t("More tools")}
      className="absolute rounded-full flex items-center justify-center"
      style={{ right: -10, top: -6, width: 16, height: 16, background: COLORS.gold, border: `1.5px solid ${COLORS.bgDeep}` }}
    >
      <Plus size={10} color={COLORS.bgDeep} style={{ transform: rotated ? "rotate(45deg)" : "none", transition: "transform 0.2s" }} />
    </button>
  );

  // NGJITJA E TRUNGUT — ndjek anën e GJAKUT sipas ORIGJINËS, jo renditjes së
  // prindërve dhe jo gjinisë. FIX (kërkesë e userit): "gjaku im nuk përcaktohet
  // nga renditja prindi-1/prindi-2"; kur ngjitemi te paraardhësit kalojmë nga
  // prindi që i PËRKET kësaj familjeje me gjak (origjina ≠ `married_in`), sepse
  // ana e ardhur me martesë e ka linjën e vet të gjakut në një FAMILJE TJETËR —
  // ndjekja e saj do ta priste trungun. Rregulli:
  //   1) prefero prindërit e gjakut (origjina e ruajtur `branch` ≠ married_in);
  //   2) ndër prindërit e gjakut, babai (patrilineal) si tiebreak;
  //   3) vetëm nëse asnjë prind s'është i gjakut, bie mbrapsht te babai/renditja.
  // KUJDES: përdorim `p.branch` (ORIGJINA intrinseke, si e vë importi/shtimi), JO
  // `effectiveBranch` — sepse effectiveBranch është relativ ndaj vëzhguesit dhe
  // te pamja e një FËMIJE të dy prindërit dalin "core" (paraardhës), pra do ta
  // humbte dallimin i-ardhur/gjak pikërisht kur na duhet.
  const climbBloodParent = (personId) => {
    const pids = people[personId]?.parent_ids || [];
    const parents = pids.map((pid) => people[pid]).filter(Boolean);
    if (!parents.length) return null;
    const blood = parents.filter((p) => p.branch !== "married_in");
    // HAPI 6 — tiebreak-u sipas modelit farefisnor: patrilineal → babai, matrilineal
    // → nëna, bilateral → i pari i gjakut (pa preferencë gjinie). Gjithmonë prefero
    // anën e GJAKUT (jo `married_in`), pavarësisht renditjes.
    const km = (ACTIVE_RULES && ACTIVE_RULES.kinship_model) || "patrilineal";
    const pref = km === "matrilineal" ? "F" : km === "bilateral" ? null : "M";
    const byPref = (arr) => (pref ? arr.find((p) => p.gender === pref) : null);
    const next =
      byPref(blood) || blood[0] ||
      byPref(parents) || parents[0];
    return next ? next.id : null;
  };

  const defaultChainArray = useMemo(() => {
    const anchorId = focusPersonId || resolveViewerAnchor(myPersonId);
    if (viewMode !== "trunk" || !anchorId || !people[anchorId]) return [];
    const anchor = people[anchorId];
    let startId = anchorId;
    if (!(anchor.parent_ids || []).length) {
      const spouseWithParents = (anchor.spouse_ids || []).find((sid) => people[sid] && (people[sid].parent_ids || []).length > 0);
      if (spouseWithParents) startId = spouseWithParents;
    }
    const chainUp = [];
    const seenChain = new Set();
    let cur = startId;
    while (cur && people[cur] && !seenChain.has(cur)) {
      seenChain.add(cur);
      chainUp.push(cur);
      // FIX v2 (raportuar nga useri — "prap s'u rregullua, Ajete ka prindër
      // vetë, ndoshta Mehmeti jo") — heuristika e parë ("preferoje prindin
      // që VETË ka prindër") doli E KUNDËRTA e saktë për këtë rast, sepse
      // ana e gjakut mund të mos ketë prindër të mëparshëm të regjistruar
      // (rekord i humbur), ndërsa bashkëshortja e ardhur me martesë mund të
      // ketë. Zëvendësuar me konventën PATRILINEALE që vetë App.jsx e
      // përdor tashmë gjetkë (shih patrilinealOverride më sipër, që
      // kontrollon `gender === "F"` për të njëjtin qëllim) — preferohet
      // GJITHMONË prindi MASHKULL (babai) kur ngjitesh, meqë ky është
      // konventa e krejt pjesës tjetër të aplikacionit. Bie mbrapsht te
      // parent_ids[0] VETËM nëse s'ka fare prind mashkull.
      cur = climbBloodParent(cur);
    }
    if (!chainUp.includes(anchorId)) chainUp.unshift(anchorId);
    return chainUp.reverse(); // root first, anchor last
  }, [viewMode, myPersonId, focusPersonId, people, rulesVersion]);
  const defaultChainSet = useMemo(() => new Set(defaultChainArray), [defaultChainArray]);

  // ========================================================================
  // visiblePeople: decides WHO is currently shown, one branch per viewMode.
  // This is the single most important piece of navigation logic in the app.
  // See ARCHITECTURE.md §9-10 for the reasoning behind the "trunk" branch
  // (default chain + chainStopId + expandedIds) and the "blood_lisi" branch
  // (pure parent/child connectivity, no marriage recursion).
  // ========================================================================
  const visiblePeopleRaw = useMemo(() => {
    const allowedByBranch = (p) => {
      const b = effectiveBranch(p);
      if (b === "married_out") return showMarriedOut;
      if (b === "married_in") return showMarriedIn;
      if (b === "close_cousin") return showCloseCousin;
      if (b === "extended") return showExtended;
      return showCore;
    };
    if (viewMode === "blood_lisi") {
      const anchorId = focusPersonId || resolveViewerAnchor(myPersonId);
      if (!anchorId || !people[anchorId]) return {};
      // pure blood connectivity: parent/child chains ONLY (no marriage edges) —
      // this stops a bride's or groom's own unrelated family from ever being
      // pulled in, since we never traverse into a spouse's own parents.
      // We also never cross a "married out" boundary — once someone's branch is
      // married_out, their descendants belong to the other family's tree, so we
      // stop walking there entirely instead of filtering them out one-by-one later
      // (which used to leave disconnected fragments floating in the layout).
      const bloodComponent = new Set([anchorId]);
      const stack = [anchorId];
      while (stack.length) {
        const id = stack.pop();
        const p = people[id];
        if (!p) continue;
        if (id !== anchorId && effectiveBranch(p) === "married_out") continue;
        const neighbors = [...(p.parent_ids || []), ...(baseChildrenMap[id] || [])];
        neighbors.forEach((nid) => { if (people[nid] && !bloodComponent.has(nid)) { bloodComponent.add(nid); stack.push(nid); } });
      }
      const out = {};
      bloodComponent.forEach((id) => {
        const p = people[id];
        const b = effectiveBranch(p);
        if (b === "married_out") return; // hide the "new branch elsewhere" category
        if (!allowedByBranch(p)) return;
        out[id] = p;
      });
      // attach spouses directly (brides only, one level — never recurse into their own family)
      bloodComponent.forEach((id) => {
        const p = people[id];
        (p.spouse_ids || []).forEach((sid) => {
          const spouse = people[sid];
          if (!spouse || out[sid]) return;
          if (spouse.gender === "M") return; // hide grooms, keep brides
          if (!allowedByBranch(spouse)) return;
          out[sid] = spouse;
        });
      });
      // final safety net: whatever subtle path let anyone else slip through above,
      // only keep the connected component of `out` that actually contains the
      // anchor (checked within the filtered set itself, so nothing disconnected
      // can ever survive to be drawn floating beside the tree)
      const finalKeep = new Set([anchorId]);
      const finalStack = [anchorId];
      while (finalStack.length) {
        const id = finalStack.pop();
        const p = out[id];
        if (!p) continue;
        const neighbors = [...(p.parent_ids || []), ...(p.spouse_ids || []), ...(baseChildrenMap[id] || [])];
        neighbors.forEach((nid) => { if (out[nid] && !finalKeep.has(nid)) { finalKeep.add(nid); finalStack.push(nid); } });
      }
      Object.keys(out).forEach((id) => { if (!finalKeep.has(id)) delete out[id]; });
      return out;
    }
    if (viewMode === "trunk") {
      if (!(focusPersonId || myPersonId) || !people[focusPersonId || myPersonId]) return people;
      const keep = new Set();
      for (const id of defaultChainArray) {
        keep.add(id);
        (people[id]?.spouse_ids || []).forEach((s) => { if (people[s]) keep.add(s); });
        if (id === chainStopId) break;
      }
      // Only cascade a reveal from an expandedIds entry if that entry is itself
      // currently visible — otherwise a branch cut off higher up (e.g. by a chain
      // stop) would still "leak" its previously-expanded descendants as floating,
      // disconnected fragments. Iterate to a fixed point since revealing a child
      // can itself be in expandedIds and cascade further down.
      let changed = true;
      while (changed) {
        changed = false;
        expandedIds.forEach((id) => {
          if (!keep.has(id)) return;
          (baseChildrenMap[id] || []).forEach((cid) => {
            if (!people[cid] || keep.has(cid)) return;
            keep.add(cid);
            (people[cid].spouse_ids || []).forEach((s) => { if (people[s]) keep.add(s); });
            changed = true;
          });
        });
      }
      const out = {};
      keep.forEach((id) => {
        const p = people[id];
        if (defaultChainSet.has(id) || allowedByBranch(p)) out[id] = p;
      });
      return out;
    }
    if (viewMode === "close") {
      if (!viewAnchor || !basePositions[viewAnchor]) return people;
      const anchorGen = basePositions[viewAnchor].y;
      const out = {};
      Object.values(people).forEach((p) => {
        const pos = basePositions[p.id];
        if (!pos) return;
        if (Math.abs(pos.y - anchorGen) <= 2 && (allowedByBranch(p) || p.id === viewAnchor)) out[p.id] = p;
      });
      return out;
    }
    if (viewMode === "descendants") {
      if (!viewAnchor || !people[viewAnchor]) return people;
      const raw = {};
      const seen = new Set();
      const stack = [viewAnchor, ...(people[viewAnchor].spouse_ids || [])];
      while (stack.length) {
        const id = stack.pop();
        if (seen.has(id)) continue;
        seen.add(id);
        const p = people[id];
        if (!p) continue;
        raw[id] = p;
        (baseChildrenMap[id] || []).forEach((c) => stack.push(c));
        (p.spouse_ids || []).forEach((s) => stack.push(s));
      }
      const out = {};
      const anchorCircle = new Set([viewAnchor, ...(people[viewAnchor].spouse_ids || [])]);
      Object.values(raw).forEach((p) => { if (anchorCircle.has(p.id) || allowedByBranch(p)) out[p.id] = p; });
      return out;
    }
    const out = {};
    Object.values(people).forEach((p) => { if (allowedByBranch(p)) out[p.id] = p; });
    return out;
  }, [people, viewMode, viewAnchor, showCore, showMarriedIn, showCloseCousin, showMarriedOut, showExtended, basePositions, baseChildrenMap, relationMap, myPersonId, focusPersonId, expandedIds, defaultChainArray, defaultChainSet, chainStopId]);

  // SHTESË (kërkesë e userit — "divorc/martesa të shumta: personi mund të
  // zgjedhë të mos e shohë [ish-bashkëshortin/en] në pemë, por personi
  // qëndron aty ku ishte për të tjerët") — çdo person mund të fshehë KËDO
  // nga PAMJA E VET personale (jo leje mbi njëri-tjetrin — çdokush vendos
  // vetëm për veten, siç u konfirmua). Shtresë filtrimi E VEÇANTË, mbi
  // gjithçka që `visiblePeopleRaw` (logjika ekzistuese e viewMode/degëve)
  // tashmë ka vendosur të tregojë — kështu funksionon njësoj, pavarësisht
  // viewMode, pa dashur të përsëritet filtri brenda çdo dege more sipër.
  const visiblePeople = useMemo(() => {
    const out = {};
    // MUZEU3 — te një koleksion nuk ka trung, gjak, as pasardhës për të ndjekur.
    // Pra e gjithë logjika poshtë (që ndërton pamjen nga lidhjet) nuk ka çfarë
    // të bëjë: dilet këtu, me tri pamjet e vetme që kanë kuptim aty.
    if (eshteKoleksion) {
      Object.values(people).forEach((p) => {
        if (!p || myHiddenPeople.has(p.id) || posterExcludeIds.has(p.id)) return;
        if (viewMode === "salla" && (p.salla || "").trim() !== (sallaAktive || "").trim()) return;
        out[p.id] = p;
      });
      return out;
    }
    // Filtri multi-select i posterit: mbaj VETËM bashkimin e trungjeve të
    // personave të zgjedhur (secili + pasardhësit e tij), plus çiftet e tyre.
    let includeKeep = null;
    if (posterIncludeIds.size > 0) {
      includeKeep = new Set();
      posterIncludeIds.forEach((rootId) => {
        if (!people[rootId]) return;
        includeKeep.add(rootId);
        const stack = [rootId];
        while (stack.length) {
          const cur = stack.pop();
          Object.values(people).forEach((p) => {
            if ((p.parent_ids || []).includes(cur) && !includeKeep.has(p.id)) { includeKeep.add(p.id); stack.push(p.id); }
          });
        }
      });
      // shto bashkëshortët e të mbajturve, që çiftet të mos dalin gjysmake
      Array.from(includeKeep).forEach((id) => (people[id]?.spouse_ids || []).forEach((s) => { if (people[s]) includeKeep.add(s); }));
    }
    Object.keys(visiblePeopleRaw).forEach((id) => { if (!myHiddenPeople.has(id) && !posterExcludeIds.has(id) && (!includeKeep || includeKeep.has(id))) out[id] = visiblePeopleRaw[id]; });
    // FIX (kërkesë e userit — "mysafiri s'po gjendet në hartë") — mysafirët
    // (member_kind='mysafir') janë TË PALIDHUR (pa prind/fëmijë/bashkëshort),
    // ndaj asnjë pamje me zinxhir (trung/gjak/afër/pasardhës) s'i përfshin
    // kurrë. I shtojmë GJITHMONË (përveç atyre që i ke fshehur për vete), që
    // të shfaqen në zonën e dedikuar lart-djathtas në çdo pamje.
    Object.values(people).forEach((p) => {
      if (p && eshteMysafir(p) && !myHiddenPeople.has(p.id) && !posterExcludeIds.has(p.id)) out[p.id] = p;
    });
    return out;
  }, [visiblePeopleRaw, myHiddenPeople, posterExcludeIds, posterIncludeIds, people, eshteKoleksion, sallaAktive, viewMode]);

  const layoutRes = useMemo(() => {
    // SHTESË (kërkesë e userit — "posterët janë personalë; kur bashkohen dy
    // familje, unë me Meritën llogaritemi si më të rinjtë dhe listohemi të
    // fundit, që të jemi epiqendra"): VETËM në modalitetin dy-familjesh,
    // çifti urë (personi urë + bashkëshortët e tij) renditet i FUNDIT në
    // grupin e vëllezërve/motrave — pavarësisht datëlindjes reale — kështu
    // përfundon në skajin e djathtë të familjes A, pikërisht aty ku
    // ankorohet familja B → pa vija të gjata, pa konfuzion. Krah-për-krah:
    // kopja e personit urë në pemën B renditet e PARA, që dy kopjet të
    // qëndrojnë ballë-për-ballë përmes hendekut.
    let layoutOpts;
    if (twoFamilyActive && twoFamBridge) {
      // FIX ROBUSTËSIE: mos u mbështet te id-të e urës (pas bashkimeve
      // zinxhir kanonikja mund të jetë kopje e tretë) — lexo shënimet
      // _twofam_last/_twofam_first që i vendos VETË mergeBridgedFamilies te
      // personi kanonik. Bashkëshortët kapen në TË DY drejtimet (edhe kur
      // lidhja spouse_ids është një-drejtimshe në të dhëna).
      const sortFirst = new Set(), sortLast = new Set();
      Object.values(people).forEach((p) => {
        if (p._twofam_last) { sortLast.add(p.id); (p.spouse_ids || []).forEach((s) => sortLast.add(s)); }
        if (p._twofam_first) { sortFirst.add(p.id); (p.spouse_ids || []).forEach((s) => sortFirst.add(s)); }
      });
      Object.values(people).forEach((p) => {
        if (!sortLast.has(p.id) && (p.spouse_ids || []).some((s) => sortLast.has(s))) sortLast.add(p.id);
        if (!sortFirst.has(p.id) && (p.spouse_ids || []).some((s) => sortFirst.has(s))) sortFirst.add(p.id);
      });
      layoutOpts = { sortFirst, sortLast };
    }
    const res = eshteKoleksion
      ? paraqitKoleksionin(visiblePeople, { paSalla: viewMode === "koha" })
      : computeLayout(visiblePeople, treeAdvLayout, layoutOpts);
    // SHTESË (posteri dy-familjesh, "krah-për-krah"): dy grafet janë TË
    // PALIDHUR (asnjë dedup — shih mergeBridgedFamilies), ndaj paketohen
    // ngjitur si çdo dy blloqe. Për t'i lexuar si DY pemë të veçanta, e
    // gjithë familja B zhvendoset djathtas me një hendek të qartë. family_id
    // është i paprekur (s'ka bashkime), ndaj ndarja është e saktë.
    if (twoFamilyActive && twoFamLayout === "sidebyside" && twoFamBridge) {
      // FIX (id-të e urës mund të jenë të vdekura — shih [2FAM] më lart):
      // familja B gjendet nga shënimet e shkrirjes, jo nga person_b.
      const famB = people[twoFamBridge.person_b]?.family_id
        || Object.values(people).find((x) => x._twofam_first)?.family_id
        || Object.values(people).find((x) => activeFamilyId && x.family_id !== activeFamilyId)?.family_id;
      if (famB) {
        let maxA = -Infinity, minB = Infinity;
        Object.entries(res.positions).forEach(([pid, pos]) => {
          if (people[pid]?.family_id === famB) minB = Math.min(minB, pos.x);
          else maxA = Math.max(maxA, pos.x);
        });
        if (Number.isFinite(maxA) && Number.isFinite(minB)) {
          const d = maxA + 2.6 - minB; // ~2.6 njësi rrjete hendek mes dy pemëve
          if (d > 0) Object.keys(res.positions).forEach((pid) => { if (people[pid]?.family_id === famB) res.positions[pid].x += d; });
        }
      }
    }
    return res;
  }, [visiblePeople, layoutVersion, treeAdvLayout, twoFamilyActive, twoFamLayout, twoFamBridge, people, eshteKoleksion, viewMode]);

  // Bandat e sallave dalin nga e njëjta llogaritje si pozicionet — jo nga një e
  // dytë. Dy llogaritje për të njëjtin vendosje do të thoshin dy të vërteta sapo
  // njëra të ndryshonte.
  const { positions, multiWifeTiers, multiMarriageParents } = layoutRes;
  const koleksionBandat = layoutRes?.bandat || null;

  // KJO RRI KËTU POSHTË, JO LART TE POSTERI — dhe qëllimisht.
  //
  // Fillimisht e vendosa pranë `exportTreeSVG`, ku i takon nga kuptimi. Vegla e
  // TDZ-së e kapi: vargu i varësive `[posterModalOpen, positions, …]` vlerësohet
  // në çastin që `useMemo` thirret gjatë renderit — dhe atje lart të dyja ato
  // ende s'janë inicializuar. Do të ishte `ReferenceError` në ngarkim: ekran i
  // bardhë, jo një defekt i vogël diku thellë.
  //
  // Prandaj llogaria rri pas `positions`. `sheetCanvas` mbetet lart, sepse atë e
  // përdor edhe eksporti dhe s'varet nga asnjë gjendje.
  const sizeAdvice = useMemo(() => {
    if (!posterModalOpen) return null;
    const svgEl = containerRef.current?.querySelector("#pef-tree-svg");
    const posIds = Object.keys(positions || {});
    if (!svgEl || !posIds.length) return null;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    posIds.forEach((id) => {
      const pp = positions[id];
      minX = Math.min(minX, pp.x * H_SPACING);
      minY = Math.min(minY, pp.y * V_SPACING);
      maxX = Math.max(maxX, pp.x * H_SPACING + CARD_W);
      maxY = Math.max(maxY, pp.y * V_SPACING + CARD_H);
    });
    if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
    const cw = maxX - minX, ch = maxY - minY;

    // Shkronja më e vogël MATET, nuk supozohet. Kartelat ndryshojnë me temën,
    // me orientimin dhe me dizajnin; një konstante e shkruar këtu do të vjetërohej
    // në heshtje sapo dikush ta ndryshojë stilin e kartelës.
    let minFont = Infinity;
    svgEl.querySelectorAll("g[data-pid] text").forEach((el) => {
      const fs = parseFloat(window.getComputedStyle(el).fontSize);
      if (Number.isFinite(fs) && fs > 0) minFont = Math.min(minFont, fs);
    });
    if (!Number.isFinite(minFont)) return null;

    const rows = ["A2", "A1", "A0", "2A0", "4A0"].map((key) => {
      const { W, H, longMm } = sheetCanvas(key, cw, ch);
      const mm = minFont * (longMm / Math.max(W, H));
      return { key, mm, ok: mm >= MM_MIRE, kufi: mm >= MM_KUFI && mm < MM_MIRE };
    });
    const best = rows.find((r) => r.ok) || rows[rows.length - 1];

    // KUR ASNJË FLETË S'MJAFTON, THUAJ SA DUHET — mos ngri supet.
    // Marrëdhënia është lineare: mm i emrit rritet drejt me anën e gjatë të
    // fletës. Pra ana e nevojshme del me një pjesëtim, jo me provë-gabim.
    //     mm_i_emrit = font × (anë_e_gjatë_mm / anë_e_gjatë_kanavace)
    // → anë_e_gjatë_mm = MM_MIRE × anë_e_gjatë_kanavace / font
    // KUJDES: kanavaca merret me PËRPJESËTIMIN E VETË PEMËS, jo me √2.
    // Së pari e llogarita mbi 4A0 dhe më doli "3,4 × 2,37 m" — një fletë sa një
    // dhomë, e cila do të ishte kryesisht bosh. Gabimi ishte konceptual, jo
    // aritmetik: po kërkoja një fletë √2 MË TË MADHE për një pemë që s'është √2.
    // Pema me katërqind veta është një brez i gjatë; asaj i duhet një fletë brez,
    // dhe atëherë e njëjta gjerësi shtypjeje mjafton.
    const margin = Math.max(cw, ch) * 0.06;
    const natW = cw + margin * 2, natH = ch + margin * 2;
    const canvasLong = Math.max(natW, natH), canvasShort = Math.min(natW, natH);
    const needLongMm = (MM_MIRE * canvasLong) / minFont;
    const nevoja = {
      gjate: needLongMm / 1000,                               // metra
      shkurt: (needLongMm * (canvasShort / canvasLong)) / 1000,
    };
    // Kufiri i rulit: ~1,5 m gjerësi, dhe 6 m gjatësi — po aq sa pranon edhe
    // `paperMm`. E lidha me atë numër me qëllim: po ta thoshte shiriti "duhet
    // 10 m" dhe pastaj varianti `1000×140 cm` të binte te A1, do të premtonim
    // diçka që vetë kodi e refuzon dy hapa më tutje.
    //
    // Mbi këto, përgjigjja e ndershme nuk është një fletë edhe më e madhe. Një
    // poster dhjetëmetërsh nuk varet në asnjë mur; aty pema kërkon libër, ose
    // shtypje dege më dege.
    nevoja.brendaRulit = nevoja.shkurt <= 1.5 && nevoja.gjate <= 6;

    return { rows, best, persons: posIds.length, arrihet: rows.some((r) => r.ok), nevoja };
  }, [posterModalOpen, positions, sheetCanvas]);
  // SHTESË (kërkesë e userit) — "brezat": banda horizontale të lehta pas çdo
  // rreshti brezi + etiketë "Brezi N" majtas. KANAL VIZUAL I VEÇANTË (sfond i
  // zbehtë), JO ngjyra e kartelës/linjës — ato mbeten për gjak/të ardhur.
  // Vizatohen si shtresa E PARË (pas gjithçkaje), të fikura si parazgjedhje —
  // kur showGenerationBands=false kthen null, pamja mbetet identike.
  // MUZEU3 — bandat e SALLAVE. I njëjti mekanizëm si bandat e brezave, dhe me
  // qëllim i njëjti: një kanal vizual i zbehtë pas rreshtave, me emrin majtas.
  //
  // Ndryshimi i vetëm është se këto NUK janë zgjedhje. Te një koleksion salla
  // është i vetmi bosht që ka mbetur — pa etiketë, «sipas sallës» nuk do të
  // thoshte asgjë, dhe ekspozita do të dukej si figura të shpërndara rastësisht.
  const sallaBands = useMemo(() => {
    if (!eshteKoleksion) return null;
    const b = koleksionBandat;
    if (!b || !b.length) return null;
    const ids = Object.keys(positions);
    if (!ids.length) return null;
    let minX = Infinity, maxX = -Infinity;
    ids.forEach((id) => {
      const pp = positions[id];
      minX = Math.min(minX, pp.x * H_SPACING);
      maxX = Math.max(maxX, pp.x * H_SPACING + CARD_W);
    });
    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
    const gap = Math.max(0, V_SPACING - CARD_H);
    const padL = 90;
    const bandX = minX - padL, bandW = (maxX - minX) + padL + 24;
    return b.map((sal, i) => {
      const top = sal.nga * V_SPACING - gap / 2;
      const height = (sal.deri - sal.nga + 1) * V_SPACING - (V_SPACING - CARD_H) + gap;
      // Salla pa emër nuk merr etiketë të trilluar — merr atë që është.
      const etiketa = sal.emri || t("Not placed yet");
      return (
        <g key={`salla-${i}`}>
          <rect x={bandX} y={top} width={bandW} height={height} fill={COLORS.accentTint} opacity={sal.emri ? 1 : 0.5} />
          <text x={minX - 16} y={top + 18} textAnchor="end" dominantBaseline="middle"
                style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, fill: COLORS.gold, opacity: 0.7 }}>{etiketa}</text>
        </g>
      );
    });
  }, [eshteKoleksion, koleksionBandat, positions, layoutVersion, t]);

  const generationBands = useMemo(() => {
    if (!showGenerationBands || !generationBandsAllowed) return null;
    const ids = Object.keys(positions);
    if (!ids.length) return null;
    let minX = Infinity, maxX = -Infinity;
    const genYs = new Set();
    ids.forEach((id) => {
      const pp = positions[id];
      minX = Math.min(minX, pp.x * H_SPACING);
      maxX = Math.max(maxX, pp.x * H_SPACING + CARD_W);
      genYs.add(pp.y);
    });
    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
    const gens = [...genYs].sort((a, b) => a - b);
    const gap = Math.max(0, V_SPACING - CARD_H);
    const padL = 90;
    const bandX = minX - padL, bandW = (maxX - minX) + padL + 24;
    return gens.map((gy, i) => {
      const top = gy * V_SPACING - gap / 2;
      const height = CARD_H + gap;
      const tint = i % 2 === 0 ? COLORS.accentTint : COLORS.accentTint;
      return (
        <g key={`genband-${gy}`}>
          <rect x={bandX} y={top} width={bandW} height={height} fill={tint} />
          <text x={minX - 16} y={top + height / 2} textAnchor="end" dominantBaseline="middle" style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, fill: COLORS.gold, opacity: 0.55 }}>{t("Generation {{n}}", { n: i + 1 })}</text>
        </g>
      );
    });
  }, [showGenerationBands, generationBandsAllowed, positions, layoutVersion]);

  // Builds the +/-/− controls shown on trunk cards. Three distinct kinds:
  //   "chain"     — sits on an ancestor between the root and the focused
  //                 person. Three-state cycle: stopped → single → revealed
  //                 → stopped (see ARCHITECTURE.md §10 for why this needed
  //                 several redesigns before it matched the requested UX).
  //   "normal"    — sits on anyone NOT part of the default chain (a sibling,
  //                 cousin, etc. already revealed some other way). Uses the
  //                 exclusive-path-replace or additive logic depending on
  //                 `parallelBranches`.
  // hasHiddenKids/isExpanded checks here are deliberately STATIC (based on
  // baseChildrenMap / defaultChainSet / expandedIds membership) rather than
  // checking the current visiblePeople — an earlier version checked against
  // visiblePeople and had a bug where the button would vanish the instant
  // its own click made the children visible, since the check flipped in the
  // same render. Keep it static.
  const expandButtons = useMemo(() => {
    if (viewMode !== "trunk") return [];
    const out = [];
    const seenCouple = new Set();
    Object.values(visiblePeople).forEach((p) => {
      if (seenCouple.has(p.id) || !positions[p.id]) return;
      const coupleIds = [p.id, ...(p.spouse_ids || [])].filter((id) => positions[id]);
      coupleIds.forEach((id) => seenCouple.add(id));
      // FIX (raportuar nga useri — "ikona +/- gjithherë duhet të jetë tek
      // trungu, jo zhvendosur nga nuse që s'kanë fëmijë") — më parë `midX`
      // mesatarizonte ME TË GJITHË bashkëshortët (edhe ata pa fëmijë, që
      // mund të jenë shumë larg, siç ndodh te martesa e shumëfishtë) — kjo
      // e zhvendoste butonin larg trungut real. Te "Prind-Fëmijë" me disa
      // marrëdhënie, babai/nëna kanë TASHMË pozicion të llogaritur saktë
      // (qendra e VËRTETË e trungut, nga computeLayout) — përdore
      // DREJTPËRDREJT, mos e rillogarit duke përfshirë çdo bashkëshort.
      const multiMarriageAnchorId = treeLineTopology === "parent_child" ? coupleIds.find((id) => multiMarriageParents.has(id)) : null;
      const midXBase = multiMarriageAnchorId
        ? positions[multiMarriageAnchorId].x * H_SPACING + CARD_W / 2
        : ((Math.min(...coupleIds.map((id) => positions[id].x)) + Math.max(...coupleIds.map((id) => positions[id].x))) / 2) * H_SPACING + CARD_W / 2;
      // SHTESË (kërkesë e userit — Admin Center: "Zhvendosja horizontale/
      // vertikale e plusit") — offset shtesë, në piksel, mbi pozicionin e
      // llogaritur automatikisht.
      const midX = midXBase + (treeAdvLayout.expand_btn_offset_x || 0);
      // Te «heirloom» baza është përmbajtja reale, jo kutia — ndryshe butoni
      // ulet mbi vitet. Kudo tjetër mbetet ashtu siç ka qenë.
      const bazaY = pemaHeirloom() ? Math.max(ROW_CONTENT_H, lartesiaEMedaljonit()) : ROW_CONTENT_H;
      const y = positions[p.id].y * V_SPACING + bazaY + (treeAdvLayout.expand_btn_offset_y || 0);

      const totalChildren = new Set();
      coupleIds.forEach((id) => (baseChildrenMap[id] || []).forEach((cid) => totalChildren.add(cid)));
      const isChainNode = coupleIds.some((id) => defaultChainSet.has(id));

      if (isChainNode) {
        const chainMemberId = coupleIds.find((id) => defaultChainSet.has(id));
        const isLastInChain = coupleIds.includes(defaultChainArray[defaultChainArray.length - 1]);
        if (totalChildren.size > 0) {
          const revealed = coupleIds.some((id) => expandedIds.has(id));
          let state, icon;
          if (isLastInChain) {
            // the anchor themselves ("me") — there's no separate "default descendant"
            // to protect here, just a plain reveal/collapse of my own children
            state = revealed ? "revealed" : "single";
            icon = revealed ? "−" : "+";
          } else {
            const stopped = chainStopId === chainMemberId;
            // one button, three states: stopped (nothing shows) → single (just the
            // direct line shows) → revealed (all children/siblings show) → back to stopped
            if (stopped) state = "stopped";
            else if (totalChildren.size > 1 && revealed) state = "revealed";
            else if (totalChildren.size > 1) state = "single";
            else state = "flowing"; // only one child exists at all — plain collapse/expand
            icon = state === "revealed" ? "−" : state === "flowing" ? "−" : "+";
          }
          out.push({ key: `chain-${coupleIds.join("-")}`, kind: "chain", state, icon, x: midX, y, coupleIds, chainId: chainMemberId, isLastInChain });
        }
      } else {
        if (totalChildren.size === 0) return;
        const isExpanded = coupleIds.some((id) => expandedIds.has(id));
        out.push({ key: coupleIds.join("-"), kind: "normal", icon: isExpanded ? "−" : "+", x: midX, y, isExpanded, coupleIds });
      }
    });
    return out;
  }, [viewMode, visiblePeople, positions, baseChildrenMap, expandedIds, defaultChainSet, defaultChainArray, chainStopId, treeAdvLayout, treeLineTopology, multiMarriageParents]);

  // ---------- auth ----------
  const resetAuthForm = () => {
    setAuthForm({ firstName: "", lastName: "", birthDate: "", parentFirstName: "", email: "", password: "" });
    // FIX (bug rrëzimi): `setPendingAdminId` u hoq bashkë me hapin e fjalëkalimit
    // shtesë të adminit (FAZA 34), por thirrja kishte mbetur → "setPendingAdminId
    // is not defined" rrëzonte app-in gjatë login-it. Hequr.
    setAuthError("");
    setNeedsEmailConfirm(false);
    setMagicLinkSent(false);
  };
  const completeLogin = (personId) => {
    setCurrentPersonId(personId);
    // FAZA 3: s'ka më nevojë të ruajmë asgjë vetë në localStorage — Supabase
    // Auth e ruan/rikthen vetë sesionin; useEffect-i "auth (FAZA 3)" më lart
    // e rizgjidh currentPersonId nga sesioni i vërtetë në çdo ringarkim.
    // FIX: forma .then().catch() varet nga fakti që .then() i Supabase-it
    // kthen Promise të vërtetë (ndërtuesi vetë është vetëm "thenable", jo
    // Promise) — për siguri, përdorim formën me dy argumente të .then(),
    // që s'varet fare nga kjo sjellje.
    hyrjeEPashenuar(`personi_${personId}`).then((eRe) => {
      if (!eRe) return; // refresh i thjeshtë — s'është hyrje e re
      supabase.rpc("rpc_log_login", { p_person_id: personId }).then(
        ({ error }) => {
          if (error) showToast(t("toast.loginLogErrorHarmless", { error: error.message }));
        },
        (e) => showToast(t("toast.loginLogError", { error: e?.message || "i panjohur" }))
      );
    });
    setAuthOpen(false);
    resetAuthForm();
    // Vetëm te login-i i vërtetë (shih justSignedInRef më lart) — jo te çdo
    // rikonfirmim i heshtur i sesionit ekzistues gjatë rifreskimit të faqes.
    if (justSignedInRef.current) {
      justSignedInRef.current = false;
      showToast(t("toast.identifiedAs", { name: displayName(people[personId]) }));
    }
  };
  // FAZA 34 — handleAuthSubmit (hapi i fjalëkalimit shtesë të adminit) u hoq
  // krejtësisht: identiteti tashmë vërtetohet nga Auth i vërtetë.

  // FAZA 3 — hyrja reale. Google/Apple hapin flow-in OAuth të Supabase (kthen
  // vetë te kjo faqe); email+password provon hyrjen, dhe nëse llogaria s'ekziston
  // provon regjistrimin (njësoj si te PlatformAdmin.jsx/AcceptInvite.jsx —
  // shih ato skedarë për të njëjtin model). Rezultati (currentPersonId) nuk
  // vendoset këtu drejtpërdrejt — useEffect-i "auth (FAZA 3)" e vëren
  // ndryshimin e sesionit dhe e zgjidh personin e lidhur automatikisht.
  // FIX (raportuar nga useri): në telefon (app-i nativ Capacitor), "Vazhdo
  // me Google/Apple" hapte Chrome, por pas konfirmimit të hyrjes personi
  // mbetej BLLOKUAR në vetë Chrome — s'kthehej kurrë te app-i, dhe kur
  // hapej manualisht app-i, s'ishte i identifikuar.
  //
  // SHKAKU: `redirectTo: window.location.href` — brenda WebView-it të
  // Capacitor-it, `window.location.href` ËSHTË adresa e BRENDSHME e app-it
  // (p.sh. "https://localhost/" ose "capacitor://localhost/"), JO një adresë
  // që OS-i i telefonit e njeh si "kjo i përket app-it tim, rihape atë".
  // Google e KËRKON hyrjen të ndodhë në një shfletues të vërtetë (jo brenda
  // një WebView të fshehur — kjo është kërkesë sigurie e vetë Google-it, jo
  // gabim yni), kështu që hapja e Chrome-it ËSHTË E DUHUR — problemi ishte
  // VETËM se s'kishte rrugë kthimi pas asaj.
  //
  // RREGULLIMI: në native, redirect-i shkon te skema e VETË app-it
  // ("com.pemaefamiljes.app://auth-callback" — appId-ja e vendosur te
  // capacitor.config.json, e cila Capacitor-i e regjistron VETË si skemë
  // URL-je kur gjenerohen projektet native android/ios). OS-i e njeh këtë
  // skemë dhe e rihap VETË app-in, jo Chrome-in — dëgjuesi "appUrlOpen"
  // (shih useEffect-in e ri poshtë, pranë "backButton") e kap thirrjen e
  // rikthimit dhe ia jep Supabase-it për ta mbyllur sesionin.
  //
  // HAP SHTESË I DOMOSDOSHËM (jashtë këtij skedari, s'mund ta bëj unë vetë
  // nga këtu): te Supabase Dashboard → Authentication → URL Configuration
  // → Redirect URLs, shto SAKTËSISHT:
  //   com.pemaefamiljes.app://auth-callback
  // Pa këtë hap, Supabase do ta REFUZOJË vetë redirect-in si "jo i lejuar",
  // edhe pse kodi këtu është i saktë.
  // FIX (login flaky "më kthen disa herë te faqja e loginit para se të
  // funksionojë"): më parë përdorej `window.location.href` DIREKT si redirect.
  // Problemi: kur kthimi i parë nga Google dështonte për ndonjë arsye, URL-ja
  // mbetej me `?code=...` të vjetër në shirit. Klikimi i dytë e kapte ATË URL
  // (me `?code=` të mbetur) si redirect → Google kthente `?code=vjetër&code=ri`
  // → Supabase s'e shkëmbente dot kodin e dyfishtë → kthim te login. Pas disa
  // tentativave, param-i i vjetër pastrohej rastësisht dhe "funksiononte".
  // Zgjidhja: redirect GJITHMONË te një URL E PASTËR (origin + pathname — slug-u
  // `/f/orlishta` ruhet te pathname), pa asnjë `?query`/`#hash` të mbetur, që
  // çdo tentativë të fillojë nga zero.
  // FIX (viewAs humbte pas login-it): mbaj parametrat legjitimë (p.sh. `viewAs`)
  // por HIQ vetëm ata të auth-it (`code`/`state`/`error…`) që shkaktonin
  // grumbullimin. Kështu "Shiko si" → login → kthehesh te i njëjti /f/slug?viewAs=…
  const oauthCleanQuery = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      ["code", "state", "error", "error_description", "error_code"].forEach((k) => p.delete(k));
      const q = p.toString();
      return q ? `?${q}` : "";
    } catch { return ""; }
  })();
  const oauthRedirectUrl = Capacitor.isNativePlatform()
    ? "com.pemaefamiljes.app://auth-callback"
    : window.location.origin + window.location.pathname + oauthCleanQuery;
  // HYRJET1 — kthimi i dështuar i Google/Apple në WEB vjen si `?error=…` ose
  // `#error_description=…` te adresa. Shënohet një herë, sa hapet faqja.
  useEffect(() => {
    try {
      const nga = new URLSearchParams(window.location.search);
      const ngaThyerja = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
      const arsyeja = nga.get("error_description") || ngaThyerja.get("error_description") || nga.get("error") || ngaThyerja.get("error");
      if (arsyeja) shenoHyrjen("oauth_fail", { ok: false, reason: String(arsyeja) });
    } catch (_e) { /* asgjë */ }
  }, []);
  // OAUTH1 (8 shtator 2026) — në native adresa e Google/Apple hapet me
  // SFSafariViewController / Custom Tabs BRENDA app-it (shih oauth.js), jo në
  // Safari-n e sistemit: Apple e refuzoi build-in për këtë (Guideline 4).
  const handleGoogleSignIn = async () => {
    setAuthError("");
    const error = await hyrMeOAuth("google", oauthRedirectUrl);
    shenoHyrjen("oauth_start", { ok: !error, reason: error ? error.message : "google" }); // HYRJET1
    if (error) setAuthError(error.message);
  };
  const handleAppleSignIn = async () => {
    setAuthError("");
    const error = await hyrMeOAuth("apple", oauthRedirectUrl);
    shenoHyrjen("oauth_start", { ok: !error, reason: error ? error.message : "apple" }); // HYRJET1
    if (error) setAuthError(error.message);
  };
  // VETË-BASHKIM: përdoruesi zgjedh një familje nga tabela e përputhjeve dhe
  // bashkohet direkt. Backend-i (rpc_self_join_family) e verifikon që email-i
  // i personit përkon me email-in e verifikuar Google/Apple të llogarisë PARA
  // se ta lejojë — s'ka rrezik të bashkohesh me dikë që s'je ti. Trigger-i
  // link_account_all_same_person e shtrin lidhjen te i gjithë "pool"-i i të
  // njëjtit njeri (nyjet e sinkronizuara në familje të tjera).
  const handleSelfJoin = async (personId) => {
    setAuthError("");
    setSelfJoinBusy(personId);
    try {
      const { data, error } = await supabase.rpc("rpc_self_join_family", { p_person_id: personId });
      if (error) { setAuthError(error.message); return; }
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.family_slug) shkoTeFamilja(row.family_slug);
      else window.location.reload();
    } finally {
      setSelfJoinBusy(null);
    }
  };
  // FAZA 14 — hyrje me Magic Link (pa fjalëkalim). E njëjta rrugë si
  // handleEmailAuth për zgjidhjen e personit pas login-it (useEffect "auth
  // (FAZA 3)" e vëren sesionin e ri automatikisht kur personi klikon linkun
  // te email-i).
  const handleMagicLink = async () => {
    setAuthError("");
    if (!authForm.email) { setAuthError(t("auth.enterEmailAndPassword")); return; }
    setMagicLinkBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: authForm.email,
        options: { emailRedirectTo: window.location.href },
      });
      shenoHyrjen("magic_link", { email: authForm.email, ok: !error, reason: error ? error.message : null }); // HYRJET1
      if (error) { setAuthError(error.message); return; }
      setMagicLinkSent(true);
    } finally {
      setMagicLinkBusy(false);
    }
  };
  // FAZA 14 — "S'mund të hyj?": e sigurt kundër "email enumeration" (rpc_
  // request_password_help hesht njësoj në sukses edhe nëse email-i s'ekziston
  // fare), kështu që gjithmonë tregojmë të njëjtin mesazh konfirmimi.
  const handleRequestPasswordHelp = async () => {
    setAuthError("");
    if (!authForm.email) { setAuthError(t("auth.enterEmailAndPassword")); return; }
    setHelpRequestBusy(true);
    try {
      // KËTU heshtja është e QËLLIMSHME dhe mbetet: mesazhi te ekrani duhet të
      // jetë i njëjti pavarësisht nëse email-i ekziston, përndryshe kjo faqe
      // bëhet mjet për të zbuluar se cilat adresa janë të regjistruara.
      //
      // Por heshtja ndaj përdoruesit nuk do të thotë heshtje ndaj nesh. Nëse
      // vetë RPC-ja është e prishur, askush s'do ta merrte vesh kurrë — kërkesat
      // thjesht nuk do të mbërrinin, dhe ne do të mendonim se askush s'ka pyetur.
      const { error } = await supabase.rpc("rpc_request_password_help", { p_email: authForm.email });
      shenoHyrjen("password_help", { email: authForm.email, ok: !error, reason: error ? error.message : null }); // HYRJET1
      if (error) console.error("rpc_request_password_help:", error.message);
    } finally {
      setHelpRequestBusy(false);
      setHelpRequestSent(true);
    }
  };
  // FAZA 14/MODULI 27 — "Qendra e Ndihmës": listë e tiketave TË MIA, bisedë
  // e plotë (thread) për secilën, jo më "1 mesazh + 1 përgjigje".
  const loadMyTickets = async () => {
    const { data } = await supabase.rpc("rpc_list_my_tickets", { p_requester_id: currentPersonId });
    setMyTickets(data || []);
  };
  const resolveTicketAtts = async (msgs) => {
    const paths = [];
    (msgs || []).forEach((m) => (m.attachments || []).forEach((p) => { if (p) paths.push(p); }));
    if (!paths.length) return;
    const map = {};
    await Promise.all(paths.map(async (p) => {
      const { data } = await supabase.storage.from("ticket-attachments").createSignedUrl(p, 3600);
      if (data?.signedUrl) map[p] = data.signedUrl;
    }));
    setTicketAttUrls((prev) => ({ ...prev, ...map }));
  };
  const openTicketThread = async (ticketId) => {
    setActiveTicketId(ticketId);
    setSupportView("thread");
    setTicketThread(null);
    setTicketPendingAtts([]);
    setTicketReplyText("");
    setTicketThreadError(null);
    const { data, error } = await supabase.rpc("rpc_get_ticket_thread", { p_ticket_id: ticketId, p_requester_id: currentPersonId });
    if (error) setTicketThreadError(error.message);
    setTicketThread(data || []);
    resolveTicketAtts(data || []);
    // pastro numrin "të reja" menjëherë (optimist) + shëno të lexuar në server.
    setMyTickets((prev) => (prev || []).map((tk) => (tk.id === ticketId ? { ...tk, unread: 0 } : tk)));
    // Shenja e leximit nuk e ndal punën e askujt kur dështon — prandaj pa dialog.
    // Por pa këtë rresht, "numri i të rejave nuk bie kurrë" do të kërkohej te
    // ndërfaqja, kurse shkaku do të ishte këtu.
    { const { error } = await supabase.rpc("rpc_mark_ticket_read", { p_ticket_id: ticketId, p_requester_id: currentPersonId });
      if (error) console.error("rpc_mark_ticket_read:", error.message); }
  };
  // KOHË REALE — kur biseda është e hapur, rifresko vetë çdo 4s (pa refresh
  // faqeje) dhe shënoje si të lexuar (useri po e sheh).
  useEffect(() => {
    if (!contactAdminOpen || supportView !== "thread" || !activeTicketId) return;
    let gjallë = true;   // SIGURIA2 (U4) — përgjigja e vonuar s'e mbishkruan bisedën tjetër
    const iv = setInterval(async () => {
      const { data } = await supabase.rpc("rpc_get_ticket_thread", { p_ticket_id: activeTicketId, p_requester_id: currentPersonId });
      if (!gjallë) return;
      if (data) { setTicketThread(data); resolveTicketAtts(data); }
      // Këtu QËLLIMISHT pa dialog: bie çdo 4 sekonda, dhe një kuti dialogu çdo
      // 4 sekonda e bën faqen të papërdorshme.
      supabase.rpc("rpc_mark_ticket_read", { p_ticket_id: activeTicketId, p_requester_id: currentPersonId })
        .then(({ error }) => { if (error) console.error("mark_ticket_read:", error.message); });
    }, 4000);
    return () => { gjallë = false; clearInterval(iv); };
  }, [contactAdminOpen, supportView, activeTicketId, currentPersonId]);
  // Numërues i pa-lexuarave për distinktivin te avatari — ngarkohet periodikisht.
  useEffect(() => {
    if (!currentPersonId) return;
    loadMyTickets();
    const iv = setInterval(() => loadMyTickets(), 20000);
    return () => clearInterval(iv);
  }, [currentPersonId]);
  // Shkarko krejt bisedën si HTML i pavarur (imazhet të ngulitura si data-URL) —
  // për dëshmi. S'varet nga signed-URL që skadojnë.
  const downloadTicketTranscript = async () => {
    setTicketDownloadBusy(true);
    try {
      const tk = (myTickets || []).find((t) => t.id === activeTicketId) || {};
      const msgs = ticketThread || [];
      const esc = (x) => String(x == null ? "" : x).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const toDataUrl = async (path) => {
        try {
          let url = ticketAttUrls[path];
          if (!url) { const { data } = await supabase.storage.from("ticket-attachments").createSignedUrl(path, 3600); url = data?.signedUrl; }
          if (!url) return null;
          const resp = await fetch(url); const blob = await resp.blob();
          return await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(blob); });
        } catch { return null; }
      };
      let rows = "";
      for (const m of msgs) {
        const who = m.sender_role === "user" ? t("User") : (tDyn(m.sender_label) || t("Support"));
        let imgs = "";
        for (const p of (m.attachments || [])) { const d = await toDataUrl(p); if (d) imgs += `<div><img src="${d}" style="max-width:520px;border-radius:8px;margin-top:6px"/></div>`; }
        rows += `<div style="margin:10px 0;padding:10px 12px;border:1px solid #e5ddcf;border-radius:8px"><div style="font:12px monospace;color:#8a7a5c">${esc(who)} · ${esc(fmtDateTime(m.created_at))}</div>${m.body ? `<div style="white-space:pre-wrap;margin-top:4px">${esc(m.body)}</div>` : ""}${imgs}</div>`;
      }
      const html = `<!doctype html><html lang="${document.documentElement.lang || "en"}"><head><meta charset="utf-8"><title>${esc(tk.ref || t("Ticket"))}</title></head><body style="font-family:system-ui,Segoe UI,Arial;max-width:680px;margin:24px auto;color:#2a2a2a;padding:0 16px"><h2 style="margin-bottom:2px">${esc(tk.ref || t("Ticket"))} — ${esc(t("Help Center"))}</h2><div style="color:#666;font-size:13px">${esc(t("Downloaded:"))} ${esc(fmtDateTime(new Date()))}</div><hr style="margin:12px 0"/>${rows || "<p>No messages.</p>"}</body></html>`;
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `bisede-${String(tk.ref || "tikete").replace(/[^A-Za-z0-9_-]/g, "")}.html`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally {
      setTicketDownloadBusy(false);
    }
  };
  const uploadTicketAtt = async (file) => {
    if (!file || !activeTicketId) return;
    if (!file.type || !file.type.startsWith("image/")) { alert(t("Images only.")); return; }
    const ext = (file.name && file.name.includes(".")) ? file.name.split(".").pop() : (file.type.split("/")[1] || "png");
    const path = `${activeTicketId}/${crypto.randomUUID()}.${ext}`;
    setTicketReplyBusy(true);
    const { error } = await supabase.storage.from("ticket-attachments").upload(path, file, { contentType: file.type, upsert: false });
    setTicketReplyBusy(false);
    if (error) { alert(t("Image not uploaded:") + error.message); return; }
    setTicketPendingAtts((prev) => [...prev, path]);
  };
  const onTicketReplyPaste = (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (const it of items) { if (it.type && it.type.startsWith("image/")) { const f = it.getAsFile(); if (f) { e.preventDefault(); uploadTicketAtt(f); } } }
  };
  // Fshi bisedën VETËM nga pamja ime — te platforma ruhet e paprekur.
  const hideTicketFromView = async () => {
    if (!activeTicketId) return;
    if (!window.confirm(t("Delete this conversation from your list?\n\nIt is permanently removed from your view and won't reappear. On the platform it is kept whole and untouched (for record/audit)."))) return;
    const { error } = await supabase.rpc("rpc_hide_my_ticket", { p_ticket_id: activeTicketId, p_requester_id: currentPersonId });
    if (error) { alert(t("Not deleted:") + error.message); return; }
    setSupportView("list");
    await loadMyTickets();
  };
  const handleSendTicketReply = async () => {
    if ((!ticketReplyText.trim() && ticketPendingAtts.length === 0) || !activeTicketId) return;
    setTicketReplyBusy(true);
    try {
      // Rasti më i shtrenjtë nga të gjithë: dikush shkruan një ankesë, shtyp
      // "Dërgo", kutia zbrazet — dhe te ana tjetër s'mbërrin kurrë asgjë. Ai
      // pret përgjigje për ditë të tëra, dhe ne s'dimë as që na foli.
      //
      // Kutia NUK zbrazet nëse dërgimi dështoi: teksti i tij është e vetmja
      // kopje që ekziston.
      const { error } = await supabase.rpc("rpc_add_ticket_message", { p_ticket_id: activeTicketId, p_requester_id: currentPersonId, p_body: ticketReplyText.trim(), p_attachments: ticketPendingAtts });
      if (error) { showToast(t("Not sent: ") + error.message); return; }
      setTicketReplyText("");
      setTicketPendingAtts([]);
      const { data } = await supabase.rpc("rpc_get_ticket_thread", { p_ticket_id: activeTicketId, p_requester_id: currentPersonId });
      setTicketThread(data || []);
      resolveTicketAtts(data || []);
      await loadMyTickets();
    } finally {
      setTicketReplyBusy(false);
    }
  };
  // FAZA 14 — "Kontakto Adminin": çdo mesazh krijon një ticket të dukshëm te
  // admin.pemaefamiljes.com (tab "Ndihmë/Tickets"), pavarësisht kategorisë
  // ("s'mund të hyj" ka rrugën e vet të dedikuar më lart — kjo është për
  // gjithçka tjetër: gabime, kërkesa ndryshimi, pyetje).
  const handleSubmitTicket = async () => {
    if (!contactAdminMessage.trim()) return;
    setContactAdminBusy(true);
    try {
      // Kutia zbrazej para se të dihej nëse tiketa u krijua. Nëse RPC-ja
      // refuzohej, teksti i njeriut zhdukej dhe asnjë tiketë s'ekzistonte —
      // pra ai priste përgjigje për diçka që s'na mbërriti kurrë.
      const { data: newTicketId, error } = await supabase.rpc("rpc_create_support_ticket", {
        p_category: contactAdminCategory,
        p_message: contactAdminMessage.trim(),
      });
      if (error) { showToast(t("Not sent: ") + error.message); return; }
      setContactAdminMessage("");
      await loadMyTickets();
      if (newTicketId) await openTicketThread(newTicketId);
      else setSupportView("list");
    } finally {
      setContactAdminBusy(false);
    }
  };
  // ── RAPORTIMI1 · «Raporto përmbajtjen» ────────────────────────────────
  // Një raportim ËSHTË tiketë (shih RAPORTIMI1_raporto.sql). Prandaj këtu s'ka
  // ekran të ri dhe as listë «raportimet e mia»: pas dërgimit, raportimi del
  // vetvetiu te «Tiketat e mia» si çdo bisedë tjetër, dhe përgjigjja e ekipit
  // vjen po aty. Konfirmimi që kërkon Apple s'u shkrua — e jep struktura.
  const [raportoObj, setRaportoObj] = useState(null);   // {tipi, id, titulli}
  const [raportoArsye, setRaportoArsye] = useState("");
  const [raportoShenim, setRaportoShenim] = useState("");
  const [raportoDuke, setRaportoDuke] = useState(false);

  const hapRaportimin = (tipi, id, titulli) => {
    setRaportoObj({ tipi, id, titulli: titulli || "" });
    setRaportoArsye("");
    setRaportoShenim("");
  };

  const dergoRaportimin = async () => {
    if (!raportoObj || !raportoArsye) return;
    setRaportoDuke(true);
    try {
      const { data, error } = await supabase.rpc("rpc_raporto", {
        p_target_type: raportoObj.tipi,
        p_target_id: raportoObj.id,
        p_arsyeja: raportoArsye,
        p_shenim: raportoShenim.trim() || null,
      });
      if (error) { showToast(t("Not sent: ") + error.message); return; }
      setRaportoObj(null);
      // Dublikata s'është gabim — është përgjigje. Njeriu duhet ta dijë se
      // raportimi i tij i mëparshëm është ende i hapur, jo se s'punoi butoni.
      showToast(data?.i_dubluar
        ? t("You have already reported this — {{ref}} is still open.", { ref: data.ref })
        : t("Thank you. The report was registered: {{ref}}", { ref: data?.ref || "" }));
      loadMyTickets();
    } finally {
      setRaportoDuke(false);
    }
  };

  // FLUKSI I RI I FJALËKALIMIT (kërkesë e userit — "kur e konfirmojnë email-in,
  // atëherë të pyeten për fjalëkalimin dhe ta konfirmojnë"): regjistrimi tani
  // kërkon VETËM email-in. Supabase dërgon linkun; pas klikimit përdoruesi ka
  // sesion dhe app-i i shfaq një ekran TË DETYRUESHËM "Vendos fjalëkalimin"
  // me dy fusha. Shënimi `needs_password` te metadata e dallon këtë rrjedhë —
  // përdoruesit ekzistues (që kanë fjalëkalim) nuk e shohin kurrë atë ekran.
  const [signupEmailOnly, setSignupEmailOnly] = useState(false);
  const [setPwd, setSetPwd] = useState({ a: "", b: "" });
  const [setPwdBusy, setSetPwdBusy] = useState(false);
  const [setPwdErr, setSetPwdErr] = useState("");
  // RIVENDOSJA VETË-SHËRBIM E FJALËKALIMIT (kërkesë e userit): deri tani
  // "S'mund të hyj?" i dërgonte kërkesë platform-adminit, i cili duhej ta
  // ndryshonte fjalëkalimin me dorë — punë e panevojshme dhe pritje për
  // përdoruesin. Tani Supabase dërgon linkun; me kthimin nga ai link app-i
  // hap TË NJËJTIN ekran "Vendos fjalëkalimin" (me përsëritje).
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const sendPasswordReset = async () => {
    setAuthError("");
    if (!authForm.email) { setAuthError(t("Write your email first.")); return; }
    setAuthBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(authForm.email.trim(), { redirectTo: lidhjePublike("/") });
      shenoHyrjen("password_reset", { email: authForm.email, ok: !error, reason: error ? error.message : null }); // HYRJET1
      if (error) { setAuthError(error.message); return; }
      setResetSent(true);
    } finally { setAuthBusy(false); }
  };
  useEffect(() => {
    // Kthimi nga linku i rivendosjes: URL-ja mban `type=recovery`, dhe
    // Supabase lëshon eventin PASSWORD_RECOVERY. Mbulojmë të dyja.
    try {
      const u = window.location.href;
      if (/[?#&]type=recovery/.test(u)) setRecoveryMode(true);
    } catch (_e) { /* asgjë */ }
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
    });
    return () => { try { sub.subscription.unsubscribe(); } catch (_e) { /* asgjë */ } };
  }, []);
  const needsPasswordSetup = !!(authSession && (authSession.user?.user_metadata?.needs_password === true || recoveryMode));
  const handleSignUpEmailOnly = async () => {
    setAuthError("");
    if (!authForm.email) { setAuthError(t("auth.enterEmailAndPassword")); return; }
    setAuthBusy(true);
    try {
      const _pol = await getPasswordPolicy();
      if (!_pol.allow_signups) { shenoHyrjen("signup_email", { email: authForm.email, ok: false, reason: "signups disabled" }); setAuthError(signupsDisabledMsg()); return; }
      const { error } = await supabase.auth.signInWithOtp({
        email: authForm.email,
        options: { shouldCreateUser: true, data: { lang: (i18n.language || "en").split("-")[0], needs_password: true } },
      });
      shenoHyrjen("signup_email", { email: authForm.email, ok: !error, reason: error ? error.message : null }); // HYRJET1
      if (error) { setAuthError(error.message); return; }
      setNeedsEmailConfirm(true);
    } finally { setAuthBusy(false); }
  };
  const submitNewPassword = async () => {
    setSetPwdErr("");
    if (setPwd.a !== setPwd.b) { setSetPwdErr(t("The two passwords do not match.")); return; }
    const pol = await getPasswordPolicy();
    const chk = validatePassword(setPwd.a, pol);
    if (!chk.ok) { setSetPwdErr(passwordErrorText(chk.errors)); return; }
    setSetPwdBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: setPwd.a, data: { needs_password: false } });
      if (error) { setSetPwdErr(error.message); return; }
      setSetPwd({ a: "", b: "" });
      setRecoveryMode(false);
      try { window.history.replaceState({}, "", window.location.pathname); } catch (_e) { /* asgjë */ }
      showToast(t("Password set. You can sign in with it from now on."));
    } finally { setSetPwdBusy(false); }
  };
  const handleEmailAuth = async () => {
    setAuthError("");
    if (!authForm.email || !authForm.password) { setAuthError(t("auth.enterEmailAndPassword")); return; }
    setAuthBusy(true);
    try {
      // Vetëm HYRJE. Regjistrimi i ri ka rrugën e vet (handleSignUpEmailOnly):
      // email → link konfirmimi → fjalëkalimi vendoset pas konfirmimit. Kështu
      // askush s'mbetet duke pyetur "ku e vendos fjalëkalimin".
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: authForm.email, password: authForm.password });
      if (signInError) { shenoHyrjen("login_fail", { email: authForm.email, ok: false, reason: signInError.message }); setAuthError(t("auth.wrongEmailOrPassword", { defaultValue: signInError.message })); return; }
    } finally {
      setAuthBusy(false);
    }
  };
  // Dalje e plotë: mbyll sesionin real të Supabase Auth (jo vetëm gjendjen
  // lokale) — pa këtë, ringarkimi i faqes do të rikyçte automatikisht të
  // njëjtin person përmes useEffect-it "auth (FAZA 3)".
  const handleSignOut = async () => {
    try { sessionStorage.removeItem("family_active_screen"); } catch (e) {}
    // NDREQJE, 31 gusht 2026 — «bëj login me një llogari të re dhe futem te
    // familja Krasniqi».
    //
    // `pef_family_slug` është kujtesa e PAJISJES për familjen e fundit të hapur
    // (shkruhet te `shkoTeFamilja`, lexohet te `main.jsx`). Ajo shkruhej, por
    // s'fshihej kurrë. Pra pas daljes ajo mbetej aty, dhe llogaria e RADHËS —
    // kushdo qoftë ajo — nisej te rruga `/f/<slug>` e njeriut të mëparshëm.
    //
    // Kujtesa i përket llogarisë, jo aparatit. Kur llogaria ikën, ikën me të.
    try { localStorage.removeItem("pef_family_slug"); } catch (_e) { /* private mode */ }
    setActiveScreen("home");
    // `scope: "local"` — DALJA VLEN VETËM PËR KËTË SHFLETUES (1 shtator 2026).
    //
    // Parazgjedhja e Supabase-it është `global`: shfuqizon ÇDO sesion të asaj
    // llogarie te serveri. Rrjedhoja që raportoi useri: dalja nga
    // pemaefamiljes.com e nxirrte njëkohësisht edhe nga Admin Center-i — dhe
    // do ta nxirrte edhe nga telefoni e tableti, pa e kërkuar askush.
    //
    // «Dil» do të thotë «mbaro këtu», jo «mbylli të gjitha pajisjet e mia».
    // Dalja nga të gjitha ekziston veçmas dhe me qëllim: shfuqizimi i sesioneve
    // te Admin Center → Llogaritë.
    shenoHyrjen("sign_out"); // HYRJET1 — para se të ikë sesioni (email-in e merr serveri nga auth.uid())
    await supabase.auth.signOut({ scope: "local" });
    cacheFshi();   // CACHE1 — pema e kësaj llogarie s'mbetet në një telefon të përbashkët
    setCurrentPersonId(null);
    setNotLinkedYet(false);
    setLinkedPersonChecked(false);
    // SIGURIA2 (M1) — pema e llogarisë që iku nuk mbetet në memorie. Efekti i
    // rrënjës (`if (familySlug || activeFamilyId || !authSession) return`) e
    // zgjidh familjen vetëm kur `activeFamilyId` është bosh: pa këtë rivendosje,
    // llogaria e radhës në të njëjtën skedë shihte pemën e së mëparshmes.
    // Në rrugë me slug (`/f/<slug>`) familja vjen nga adresa, ndaj nuk preket.
    if (!familySlug) {
      setActiveFamilyId(null);
      setPeople({});
      setRoles({});
      setRolesShkalla({});
      setRolesMeDore({});
      setTreeName("");
      setMyLinkedFamilies([]);
      setSelectedId(null);
      setMarriages({});
      setFeedPosts([]);
    }
    setMySubscriptions([]);
    setSubsGati(false);
    showToast(t("toast.signedOut"));
  };

  // ---------- person CRUD ----------
  const resetPhotoState = () => { setPhotoFile(null); setPhotoPreview(null); setPhotoRemoved(false); setPhotoFocus({ x: 0.5, y: 0.5 }); setPhotoZoom(1); };
  // Kërkesë e userit — te "Shto Person" (thjeshtuar tashmë), data e vdekjes
  // s'kishte fare vend. Në vend ta shtojmë prapë si fushë gjithmonë e
  // dukshme (do ta rëndonte sërish formularin), një checkbox e zbulon vetëm
  // kur nevojitet — me togfjalësh të butë (t("has passed away")), jo "ka
  // vdekur" (i vrazhdë). Vetëm UI — s'ka fushë të veçantë në databazë; nëse
  // checkbox-i shënohet por data lihet bosh, personi thjesht s'shënohet
  // ende i ndjerë (death_date mbetet bosh, si më parë).
  const [markDeceased, setMarkDeceased] = useState(false);
  // Kërkesë e userit — njësoj si markDeceased sipër, checkbox që zbulon
  // fushën e mbiemrit të vajzërisë vetëm kur nevojitet, te "Shto Person".
  const [hasMaidenName, setHasMaidenName] = useState(false);
  // Kërkesë e userit — "para se të shtosh një person, duhet gjithsesi të
  // zgjedhësh ose PRIND ose BASHKËSHORT/E që është brenda zonës tënde të
  // të drejtave" — kjo e bën këtë zgjedhje eksplicite në formular, në vend
  // që redaktori/kontribuesi thjesht të mos e dinin pse dështonte "Shto".
  // "child" = si më parë (prind1/prind2); "spouse" = fushë e re, e vetme
  // (spouseOf) — krijon personin DHE e lidh si çift në një hap.
  const [addRelationType, setAddRelationType] = useState("child"); // "child" | "spouse"
  // SHTESË (kërkesë e userit) — kur shtohet dikush "i ardhur në familje",
  // lloji i marrëdhënies: martuar (parazgjedhje)/bashkëjetesë/fejuar —
  // e njëjta ide si "Rast i veçantë", vetëm për vetë llojin e lidhjes.
  const [addSpouseRelType, setAddSpouseRelType] = useState("married"); // "married" | "cohabiting" | "engaged"
  // NJË DERË PËR GJITHÇKA QË SHTOHET (kërkesë e pronarit, 28 gusht 2026):
  // «gjdo gje qe eshte per te shtuar diqka eshte ne nje vend».
  //
  // Butoni «+» nuk hap më drejtpërdrejt formularin e personit; hap një listë me
  // atë që mund të shtohet. Përparësia nuk është kursimi i një butoni te
  // ballina — është që kur nesër shtohet një lloj i tretë (një dokument, një
  // datë e rëndësishme), ai ka një vend të vetëm ku shkon, dhe askush nuk
  // kërkon «ku ishte ai butoni».
  //
  // Dy përjashtime, të dyja të qëllimshme:
  //   • pema bosh — atje ka vetëm një gjë për të bërë, dhe një listë me një
  //     zë është një buton më i keq;
  //   • «Shto fëmijë» / «Shto prind» te një person i zgjedhur — ato e dinë
  //     tashmë se çfarë po shtojnë, dhe një pyetje e ndërmjetme do të ishte
  //     një klikim i humbur.
  // NJË DERË, PAVARËSISHT ROLIT — 29 gusht 2026.
  //
  // Pronari: «kur jam në llogarinë e Meritës, butoni Shto ka pamjen e vjetër,
  // vetëm anëtar; kurse te llogaria ime është dizajni i ri që bëmë».
  //
  // Nuk ishte pamje e vjetër dhe nuk ishte ndërtim i vjetër. Ishte ky rresht:
  // `mundHistori = canManage`, dhe `canManage` do të thotë VETËM admin. Formulari
  // i historisë te «Kujtimet» është po ashtu vetëm për admin-in. Pra për këdo
  // tjetër njëra nga dy dyert ishte e mbyllur, dhe kodi vendosi ta kapërcente
  // listën fare — duke e çuar drejt te formulari i personit.
  //
  // Logjika ishte e ndershme; sjellja jo. Njeriu shtyp të njëjtin buton dhe merr
  // dy aplikacione të ndryshme, pa e ditur pse — dhe kjo lexohet si defekt ose si
  // version i vjetër, saktësisht siç e lexoi pronari.
  //
  // Tani dera është një. Kush s'e ka lejen e sheh rreshtin e historisë, të kyçur,
  // me arsyen e shkruar. Një derë e kyçur të thotë ku je; një derë që zhduket të
  // thotë se je diku tjetër.
  //
  // (Kush lejohet të shkruajë histori mbetet i pandryshuar — kjo prek vetëm
  // atë që sheh njeriu, jo atë që mund të bëjë.)
  // Pritet një kuadër: ekrani i Historisë sapo është montuar kur kjo shkrepet,
  // dhe një `focus()` mbi diçka që s'është ende në DOM nuk bën asgjë — pa gabim,
  // pa efekt, pikërisht lloji i defektit që s'e sheh dot duke lexuar kodin.
  // Efektet «shko te formulari dhe fokusoje» u hoqën: formularët nuk rrinë më
  // brenda Historisë, kanë faqet e veta. Një rrugëzgjatje që s'ka më rrugë.

  // Dalja nga faqja e shtimit — te ekrani nga erdhe, jo gjithmonë te ballina.
  const mbyllShtimin = () => setActiveScreen(ekraniPara.current || "home");

  const hapShtimin = () => {
    if (treeHasAnyPeople === false) { openAdd(); return; }
    if (activeScreen !== "shto") ekraniPara.current = activeScreen;
    setActiveScreen("shto");
  };

  const openAdd = (prefill) => {
    // Faza 2 — formulari i ri "Shto person" për çdo shtim NORMAL. Personi i
    // parë (pemë bosh / bootstrap) vazhdon me modalin e detajuar poshtë,
    // sepse kërkon rrjedhën e veçantë të admin-bootstrap-it.
    if (treeHasAnyPeople !== false) {
      const initial = prefill?.childOf ? { kind: "parent", sel: [prefill.childOf] }
        : prefill?.parent1 ? { kind: "child", sel: [prefill.parent1] } : null;
      setAddSheet({ initial });
      return;
    }
    const parent = prefill?.parent1 ? people[prefill.parent1] : null;
    const suggestedBranch = parent && parent.gender === "F" && parent.branch !== "married_in" ? "married_out" : "core";
    setForm({ firstName: "", lastName: "", maidenName: "", maidenNameInDisplay: false, birthDate: "", deathDate: "", birthPlace: "", birthVillage: "", birthCity: "", birthCountry: "", residenceCountry: "", residenceCity: "", bio: "", parent1: prefill?.parent1 || null, parent2: null, spouseOf: null, childOf: prefill?.childOf || null, branch: suggestedBranch, gender: "", externalTreeUrl: "", contactEmail: "", contactPhone: "", birthDateApprox: false });
    setAddSpouseRelType("married");
    setMarkDeceased(false);
    setHasMaidenName(false);
    // "parent" = po shtohet një PRIND për një person ekzistues (ndërtim poshtë-lart).
    setAddRelationType(prefill?.childOf ? "parent" : "child");
    resetPhotoState();
    setCustomFieldValues({});
    setPersonModal({ mode: "add" });
  };
  // ⚠ 4 shtator 2026 — GARË (race) TE KONTAKTI.
  // `openEdit` është `async`: e hap modalin, pastaj PRET `rpc_get_contact` dhe
  // e shkruan përgjigjen te forma pa pyetur nëse modali është ende i njëjti
  // person. Hap A → mbylle → hap B, dhe përgjigja e vonuar e A-së mbush
  // email/telefonin e B-së — pastaj «Ruaj» ia shkruan B-së të dhënat e A-së.
  // Nuk bie asnjë gabim; thjesht dy njerëz shkëmbejnë kontaktin.
  //
  // Kjo referencë mban personin që është hapur TANI. Përgjigja që vjen për
  // dikë tjetër hidhet poshtë.
  const personiIHapurRef = useRef(null);
  const openEdit = async (id) => {
    const p = people[id];
    setForm({
      firstName: p.first_name || "", lastName: p.last_name || "", maidenName: p.maiden_name || "", maidenNameInDisplay: !!p.maiden_name_in_display,
      birthDate: p.birth_date || "", birthDateApprox: !!p.birth_date_approx, deathDate: p.death_date || "", birthPlace: p.birth_place || "", birthVillage: (p.birth_city || p.birth_country) ? villageFromPlace(p.birth_place, p.birth_city, p.birth_country, i18n.language) : (p.birth_place || ""), birthCity: p.birth_city || "", birthCountry: p.birth_country || "", residenceCountry: p.residence_country || "", residenceCity: p.residence_city || "", bio: p.bio || "",
      parent1: (p.parent_ids || [])[0] || null, parent2: (p.parent_ids || [])[1] || null,
      branch: p.branch || "core", gender: p.gender || "", externalTreeUrl: p.external_tree_url || "",
      branchSpecialCase: p.branch_special_case || null,
      branchColorOverride: p.branch_color_override || null,
      hideSoloRing: !!p.hide_solo_ring,
      contactEmail: "", contactPhone: "",
    });
    setMarkDeceased(!!p.death_date);
    setHasMaidenName(!!p.maiden_name);
    setCustomFieldValues(p.custom_fields || {});
    resetPhotoState();
    // Kërkesë e userit — kur redaktohet një person me foto EKZISTUESE, fokusi
    // fillon nga vlera E RUAJTUR e tij (jo gjithmonë 0.5/0.5) — përndryshe
    // "Rregullo fokusin" do t'i dukej gjithnjë e "harruar" te redaktimi.
    setPhotoFocus({ x: p.photo_focus_x ?? 0.5, y: p.photo_focus_y ?? 0.5 });
    setPhotoZoom(p.photo_zoom ?? 1);
    personiIHapurRef.current = id;
    setPersonModal({ mode: "edit", id });
    if (canManage) {
      try {
        const { data } = await supabase.rpc("rpc_get_contact", { p_person_id: id, p_requester_id: currentPersonId });
        if (personiIHapurRef.current !== id) return;   // ndërkohë u hap dikush tjetër
        const c = (data && data[0]) || {};
        setForm((f) => ({ ...f, contactEmail: c.email || "", contactPhone: c.phone || "" }));
      } catch (e) {}
    }
  };
  // Email-i i llogarisë me të cilën u hy. Vjen nga sesioni, jo nga profili —
  // pra është i vërtetuar nga Google/Apple/email, jo i shkruar me dorë.
  const emailILlogarise = () => {
    try { return (authSession && authSession.user && authSession.user.email) || ""; }
    catch (_e) { return ""; }
  };

  // ── EMAIL-I VENDOSET VETË, NJË HERË ──────────────────────────────────────
  //
  // Kërkesë e pronarit: profili i personit duhet ta ketë email-in e llogarisë me
  // të cilën u hy, pa e shkruar askush.
  //
  // Bëhet vetëm kur fusha është BOSH. Nëse dikush e ka shkruar tashmë një email
  // — të shtëpisë, të punës, të dikujt që ia mban llogarinë — ai nuk preket
  // kurrë. Një veçori që të mbishkruan atë që ke shkruar vetë nuk është ndihmë.
  //
  // Një herë për çdo hapje të aplikacionit; `ref`-i e ndal përsëritjen edhe kur
  // efekti rixhirohet.
  // APPLE-RELAY (12 shtator 2026) — «Hide My Email»: llogaria ka
  // `…@privaterelay.appleid.com`, një adresë ndërmjetëse e Apple-it. Ajo NUK
  // shkruhet te kontakti i personit (familja do të shihte një adresë të
  // pakuptueshme); në vend të saj personit i tregohet një herë si ta rregullojë.
  const emailIFshehurNgaApple = (e) => /@privaterelay\.appleid\.com$/i.test(String(e || ""));
  const [njoftimiRelay, setNjoftimiRelay] = useState(false);
  const uidPerUdhezime = (authSession && authSession.user && authSession.user.id) || "";
  // UDHEZIME4 (18 shtator 2026) — gjendja «e parë» rri te SERVERI, te
  // LLOGARIA (`account_activity.udhezimet`), jo te lidhja llogari×person dhe
  // jo te localStorage. Pronari: «një llogari mund të jetë në disa familje —
  // udhëzimi del vetëm një herë, kur krijon llogarinë dhe hyn për herë të
  // parë; asnjëherë tjetër, sado familje të bashkohen». Lexohet një herë për
  // sesion. null = ende s'është lexuar → mos gjyko; false = s'ka bazë.
  const [udhezimetEPara, setUdhezimetEPara] = useState(null);
  useEffect(() => {
    setUdhezimetEPara(null);
    if (!uidPerUdhezime) return;
    let cancelled = false;
    supabase.rpc("rpc_udhezimet_lexo").then(({ data, error }) => {
      if (cancelled) return;
      setUdhezimetEPara(error ? false : (data && typeof data === "object" ? data : {}));
    }, () => { if (!cancelled) setUdhezimetEPara(false); });
    return () => { cancelled = true; };
  }, [uidPerUdhezime]);
  const shenoUdhezimin = (celesi) => {
    setUdhezimetEPara((u) => ({ ...(u || {}), [celesi]: new Date().toISOString() }));
    supabase.rpc("rpc_udhezimet_shen", { p_celesi: celesi }).then(() => {}, () => {});
  };
  useEffect(() => {
    const im = emailILlogarise();
    if (!currentPersonId || !emailIFshehurNgaApple(im)) { setNjoftimiRelay(false); return; }
    // UDHEZIME3 — një herë për lidhje, te serveri (jo localStorage: riinstalimi e rikthente)
    if (!udhezimetEPara || udhezimetEPara.relay) { setNjoftimiRelay(false); return; }
    setNjoftimiRelay(true);
  }, [currentPersonId, authSession, udhezimetEPara]);
  const mbyllNjoftiminRelay = () => {
    setNjoftimiRelay(false);
    shenoUdhezimin("relay");
  };

  // UDHEZIME1 (13 shtator 2026) — tri udhëzime të vogla në hyrjen e parë
  // (pema · shto · kujtimet), një herë për llogari; dhe një e katërt herën e
  // parë që personi ka dy familje («prek ikonën para emrit të familjes»).
  // Pronari: «jo tur i gjatë — tri kartela me Kuptova». Rrinë poshtë, si
  // njoftimi i relay-t; s'dalin bashkë me të.
  const [udhezimi, setUdhezimi] = useState(null); // null | 0 | 1 | 2 | "familje2"
  // UDHEZIME2 — çelësi i platformës (Admin Center → Veçoritë → «Udhëzimet e
  // hyrjes së parë»). Parazgjedhje ndezur: pa përgjigje nga serveri sillet si
  // më parë. Tekstet redaktohen te editori i përkthimeve (çelësat udhezim.*).
  const [udhezimetAktive, setUdhezimetAktive] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetchSettingRpcWithRetry("rpc_get_udhezimet_enabled").then((data) => {
      if (cancelled || data == null) return;
      const v = Array.isArray(data) ? data[0] : data;
      setUdhezimetAktive(typeof v === "object" && v !== null ? v.enabled !== false : v !== false);
    });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!currentPersonId || !activeFamilyId || !uidPerUdhezime || !udhezimetAktive) return;
    if (udhezimi !== null) return; // një udhëzim është hapur tashmë — mos e rinis
    if (!udhezimetEPara) return;   // null = duke lexuar; false = s'ka bazë
    // UDHEZIME4 (18 shtator 2026) — pronari: «udhëzimi dilte mbi kushtet e
    // përdorimit». Derisa personi t'i pranojë kushtet, asnjë udhëzim.
    if (termsGateOpen) return;
    // THEMELUESI1 — familje e re: admini i vetëm (≤ 3 persona). Tri kartela SI
    // ndërtohet, në vend të tri kartelave të zakonshme (ato i sheh kush hyn në
    // një pemë të gatshme). Lidhja është për person, pra edhe për familje.
    const nPersona = Object.keys(people).length; // 0 = ende s'është ngarkuar — mos gjyko
    if (roles[currentPersonId] === "admin" && nPersona >= 1 && nPersona <= 3 && !udhezimetEPara.themelues && !udhezimetEPara.baze) {
      // UDHEZIME4 — shënohet te serveri në çastin që SHFAQET, jo kur mbaron:
      // «vetëm një herë — as me refresh, as me version të ri, as në pajisje tjetër».
      shenoUdhezimin("themelues"); shenoUdhezimin("baze");
      setUdhezimi("themelues:0"); return;
    }
    if (!udhezimetEPara.baze && !udhezimetEPara.themelues) { shenoUdhezimin("baze"); setUdhezimi(0); return; }
    if ((myLinkedFamilies || []).length >= 2 && !udhezimetEPara.familje2) { shenoUdhezimin("familje2"); setUdhezimi("familje2"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPersonId, activeFamilyId, uidPerUdhezime, myLinkedFamilies, udhezimetAktive, roles, people, udhezimetEPara, termsGateOpen]);
  const udhezimiTjeter = () => {
    if (typeof udhezimi === "string" && udhezimi.startsWith("themelues:")) {
      const i = Number(udhezimi.slice(10));
      if (i < 2) { setUdhezimi("themelues:" + (i + 1)); return; }
      setUdhezimi(null); return;
    }
    if (udhezimi === "familje2") { setUdhezimi(null); return; }
    if (udhezimi < 2) { setUdhezimi(udhezimi + 1); return; }
    setUdhezimi(null);
  };

  useEffect(() => { fetchLlogaritEAnetareve(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [currentPersonId]);

  const emailUProvua = useRef(false);
  useEffect(() => {
    if (emailUProvua.current) return;
    const im = emailILlogarise();
    if (!currentPersonId || !im) return;
    if (emailIFshehurNgaApple(im)) { emailUProvua.current = true; return; } // adresa relay s'është kontakt
    emailUProvua.current = true;
    let gjallë = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("rpc_get_contact", { p_person_id: currentPersonId, p_requester_id: currentPersonId });
        if (!gjallë || error) return;
        const c = (data && data[0]) || {};
        if ((c.email || "").trim()) return;            // ka tashmë — mos e prek
        await supabase.rpc("rpc_set_contact", {
          p_person_id: currentPersonId,
          p_email: im,
          p_phone: (c.phone || "").trim(),
          p_requester_id: currentPersonId,
        });
      } catch (_e) { /* pa email të vendosur — asgjë nuk prishet */ }
    })();
    return () => { gjallë = false; };
  }, [currentPersonId, authSession]);

  const openSelfEdit = async () => {
    if (!currentPersonId) return;
    // Kërkesë e userit — preferenca "shfaqe mbiemrin e vajzërisë në mes"
    // fillon nga vlera EKZISTUESE e personit (jo gjithmonë e pashenjuar),
    // që dikush që e ka aktivizuar tashmë ta gjejë sërish të shenjuar.
    // PROFILI1 (13 shtator 2026) — «Profili im» redakton kartelën e vet: emri,
    // datëlindja, vendlindja, banimi, bio — me çdo rol (rpc_update_my_person).
    const _self = people[currentPersonId];
    setForm({
      contactEmail: "", contactPhone: "", maidenNameInDisplay: !!_self?.maiden_name_in_display,
      firstName: _self?.first_name || "", lastName: _self?.last_name || "", maidenName: _self?.maiden_name || "",
      gender: _self?.gender || "", birthDate: _self?.birth_date || "",
      birthPlace: _self?.birth_place || "",
      birthVillage: (_self?.birth_city || _self?.birth_country) ? villageFromPlace(_self.birth_place, _self.birth_city, _self.birth_country, i18n.language) : (_self?.birth_place || ""),
      birthCity: _self?.birth_city || "", birthCountry: _self?.birth_country || "",
      residenceCountry: _self?.residence_country || "", residenceCity: _self?.residence_city || "", bio: _self?.bio || "",
    });
    resetPhotoState();
    // Ngarko fokusin+zoom EKZISTUES të vetes (që self-save të mos i rivendosë).
    if (_self) { setPhotoFocus({ x: _self.photo_focus_x ?? 0.5, y: _self.photo_focus_y ?? 0.5 }); setPhotoZoom(_self.photo_zoom ?? 1); }
    setCustomFieldValues((_self && _self.custom_fields) || {});
    personiIHapurRef.current = currentPersonId;
    setPersonModal({ mode: "self", id: currentPersonId });
    try {
      const { data } = await supabase.rpc("rpc_get_contact", { p_person_id: currentPersonId, p_requester_id: currentPersonId });
      if (personiIHapurRef.current !== currentPersonId) return;
      const c = (data && data[0]) || {};
      // Nëse s'ka email të ruajtur, fillo nga ai me të cilin hyri — ai është i
      // vërtetuar dhe pothuajse gjithmonë i saktë. Mbetet i ndryshueshëm:
      // dikush hyn me llogarinë e punës dhe do të shkruajë atë të shtëpisë.
      setForm((f) => ({ ...f, contactEmail: c.email || emailILlogarise() || "", contactPhone: c.phone || "" }));
    } catch (e) {
      setForm((f) => ({ ...f, contactEmail: emailILlogarise() || "" }));
    }
  };
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoRemoved(false);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };
  // ══════════════════════════════════════════════════════════════════════
  // HAPESIRA2 · 3 shtator 2026 — NJË PYETJE PARA ÇDO NGARKIMI
  //
  // Gardhi i vërtetë rri te baza (politikë restriktive mbi `storage.objects`),
  // sepse fotot shkojnë DIREKT te storage-i dhe asnjë funksion i yni s'i sheh.
  // Por ai gardh flet gjuhën e Postgres-it: «new row violates row-level
  // security policy». Askush s'e meriton atë fjali.
  //
  // Prandaj kjo: e njëjta llogari, e bërë PARA se skedari të niset, me numrin e
  // vërtetë të bajtave në dorë — dhe me një fjali që thotë çfarë ndodhi dhe
  // çfarë të bësh. Serveri mbetet rrjeti; kjo është ndërfaqja.
  //
  // Kthen `null` kur ka vend, ose tekstin e gabimit. Nëse dera s'ekziston ende
  // (HAPESIRA2 i paxhiruar), kthen `null` — kurrë s'ndal një ngarkim sepse një
  // kontroll dështoi.
  // ══════════════════════════════════════════════════════════════════════
  const sipasMb = (bajte) => {
    const m = Number(bajte || 0) / 1048576;
    return m < 1024 ? `${m.toFixed(m < 10 ? 1 : 0)} MB` : `${(m / 1024).toFixed(2)} GB`;
  };
  const kontrolloHapesiren = async (bajte, eshteArkiv = false) => {
    if (!activeFamilyId) return null;
    try {
      const { data, error } = await supabase.rpc("rpc_hapesira_ka_vend", {
        p_family_id: activeFamilyId, p_bajte: Math.max(0, Number(bajte) || 0), p_arkiv: !!eshteArkiv,
      });
      if (error || !data || typeof data !== "object") return null;   // dera s'ekziston → mos ndal
      const maxFileMb = Number(data.max_file_mb || 0);
      if (maxFileMb > 0 && Number(bajte) > maxFileMb * 1048576) {
        return t("space.fileTooBig", { mb: maxFileMb });
      }
      if (data.ka_vend) return null;
      // Dy gjendje të ndryshme, dy fjali të ndryshme: «s'ka mbetur asgjë» dhe
      // «ka mbetur pak, po jo aq sa duhet ky skedar».
      return data.plot
        ? t("space.full")
        : t("space.notEnough", { mbetur: sipasMb(data.mbetur_bajte) });
    } catch (_e) {
      return null;
    }
  };
  // Rrjeti i fundit: kur gardhi i bazës e ndal ngarkimin, Supabase kthen
  // fjalinë e vet për RLS. Ajo përkthehet këtu në atë që njeriu duhet të dijë.
  const gabimiIHapesires = (err) => {
    const m = (err && err.message) || "";
    return /row-level security|violates row-level/i.test(m) ? t("space.full") : null;
  };

  const uploadPhotoFor = async (personId) => {
    let blob;
    try { blob = await compressToBlob(photoFile); }
    catch (e) { window.alert(t("The photo wasn't processed:") + "\n\n" + (e?.message || e)); return null; }
    // Foto e personit ZËVENDËSON atë të mëparshmen (`upsert: true`), pra shpesh
    // s'shton asnjë bajt të ri. Prapë pyetet: kur familja është mbi kufi, edhe
    // një zëvendësim i madh e rrit diskun.
    const _sh = await kontrolloHapesiren(blob?.size || 0, false);
    if (_sh) { window.alert(_sh); return null; }
    const path = `${activeFamilyId}/${personId}.jpg`;
    const { error } = await supabase.storage.from("photos").upload(path, blob, { upsert: true, contentType: "image/jpeg" });
    // Përdorim window.alert (jo toast) që gabimi TË MOS zhduket para se ta lexosh,
    // dhe të mos mbulohet nga mesazhi "u ruajt". Detajet e plota + te konsola.
    if (error) {
      console.error("Photo upload error:", error);
      const _hp = gabimiIHapesires(error);
      if (_hp) { window.alert(_hp); return null; }
      window.alert(
        t("PHOTO UPLOAD FAILED") + "\n\n" +
        "Bucket: photos\nPath: " + path + "\n\n" +
        t("Message: ") + (error.message || "(bosh)") + "\n" +
        "Detaje: " + JSON.stringify(error)
      );
      return null;
    }
    return path;
  };
  const uploadCrest = async (file) => {
    const _sh = await kontrolloHapesiren(file?.size || 0, false);
    if (_sh) { showToast(_sh); return; }
    const path = `${activeFamilyId}/family-crest-${Date.now()}.${(file.type.split("/")[1] || "png")}`;
    const { error: upErr } = await supabase.storage.from("photos").upload(path, file, { contentType: file.type });
    if (upErr) { showToast(gabimiIHapesires(upErr) || t("toast.crestUploadFailed", { error: upErr.message })); return; }
    const { error } = await supabase.rpc("rpc_set_crest", { p_crest_path: path, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.crestSaveFailed", { error: error.message })); return; }
    setCrestPath(path);
    showToast(t("toast.crestUploaded"));
  };

  // RIKTHIM (19 gusht 2026) — u hoq gabimisht bashkë me `countries`. E thërret
  // butoni "+ Shto shtet" te fusha e vendlindjes; serveri (`rpc_add_country`)
  // e lejon vetëm adminin e familjes dhe e shkruan te `settings.countries`.
  const addCountry = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    const { error } = await supabase.rpc("rpc_add_country", { p_country: clean, p_requester_id: currentPersonId });
    if (error) { showToast(error.message); return; }
    setCountries((prev) => (prev.includes(clean) ? prev : [...prev, clean]));
  };

  const removeCrest = async () => {
    const { error } = await supabase.rpc("rpc_set_crest", { p_crest_path: null, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.crestSaveFailed", { error: error.message })); return; }
    setCrestPath(null);
    showToast(t("Crest removed"));
  };

  const saveSelfEdit = async () => {
    const id = currentPersonId;
    // NJË RUAJTJE, KATËR SHKRIME.
    //
    // Deri më 21 gusht vetëm i treti prej tyre e lexonte `error`. Tre të tjerët
    // — fotoja, kuadrimi, mbiemri i vajzërisë — mund të refuzoheshin nga RLS-ja
    // dhe funksioni vazhdonte njësoj, deri te `toast.profileSaved`.
    //
    // Kjo është më keq se një dështim i heshtur: është një dështim që PËRGËNJESHTROHET.
    // Përdoruesi lexon "u ruajt", mbyll dritaren, dhe fotoja e vjetër kthehet
    // pas rifreskimit të radhës — kur ai s'ka më asnjë arsye ta lidhë me atë
    // klikim.
    //
    // Prandaj gabimet mblidhen, dhe fjala "u ruajt" thuhet vetëm nëse s'ka asnjë.
    const deshtimet = [];
    const provo = async (etiketa, thirrja) => {
      const { error } = await thirrja;
      if (error) deshtimet.push(`${etiketa}: ${error.message}`);
      return !error;
    };

    // PROFILI1 — të dhënat e kartelës së vet (jo lidhjet, jo vdekja)
    if (!(form.firstName || "").trim()) { showToast(t("toast.giveAtLeastOneName")); return; }
    {
      const villageTrim = (form.birthVillage || "").trim();
      const cityTrim = (form.birthCity || "").trim();
      const countryTrim = (form.birthCountry || "").trim();
      const countryLabel = displayCountry(countryTrim, i18n.language) || countryTrim;
      const combinedPlace = [villageTrim, cityTrim, countryLabel].filter(Boolean).join(", ");
      await provo(t("person.myProfile"), supabase.rpc("rpc_update_my_person", {
        p_requester_id: id,
        p_first_name: form.firstName.trim(),
        p_last_name: (form.lastName || "").trim(),
        p_maiden_name: (form.maidenName || "").trim(),
        p_gender: form.gender || null,
        p_birth_date: form.birthDate || null,
        p_birth_country: countryTrim || null,
        p_birth_city: cityTrim || null,
        p_birth_place: combinedPlace,
        p_residence_country: (form.residenceCountry || "").trim() || null,
        p_residence_city: (form.residenceCity || "").trim() || null,
        p_bio: (form.bio || "").trim(),
      }));
    }

    if (photoFile) {
      const path = await uploadPhotoFor(id);
      if (path) await provo(t("Photo"),
        supabase.rpc("rpc_set_photo", { p_person_id: id, p_photo_path: path, p_requester_id: id }));
    } else if (photoRemoved) {
      await provo(t("Photo"),
        supabase.rpc("rpc_set_photo", { p_person_id: id, p_photo_path: null, p_requester_id: id }));
    }
    // Kërkesë e userit — ruaj fokusin e fotos VETËM kur ka ende foto (e re
    // ose ekzistuese, jo e hequr) — s'ka kuptim të ruash fokus për foto
    // që s'ekziston më.
    if ((photoFile || (people[id]?.photo_path && !photoRemoved))) {
      await provo(t("Photo"),
        supabase.rpc("rpc_set_photo_crop", { p_person_id: id, p_focus_x: photoFocus.x, p_focus_y: photoFocus.y, p_zoom: photoZoom, p_requester_id: id }));
    }
    const { error } = await supabase.rpc("rpc_set_contact", {
      p_person_id: id,
      p_email: (form.contactEmail || "").trim(),
      p_phone: (form.contactPhone || "").trim(),
      p_requester_id: id,
    });
    if (error) {
      showToast(t("toast.contactSaveFailed", { error: error.message }));
    } else {
      setSelectedContact({ email: form.contactEmail || "", phone: form.contactPhone || "" });
    }
    // Kërkesë e userit — "në profilin e vet, gjithsecili mund të vendosë"
    // nëse mbiemri i vajzërisë shfaqet në mes të emrit — vetëm nëse ka
    // vërtet mbiemër vajzërie të regjistruar (s'ka kuptim ndryshe).
    if (people[id]?.maiden_name) {
      await provo(t("Maiden name"),
        supabase.rpc("rpc_set_maiden_name_display", { p_person_id: id, p_show: !!form.maidenNameInDisplay, p_requester_id: id }));
    }

    if (deshtimet.length) {
      // Vetëm i pari tregohet: një dialog me katër rreshta gabimi nuk lexohet,
      // dhe i pari është pothuajse gjithmonë shkaku i të tjerëve.
      showToast(t("Not saved: ") + deshtimet[0]);
    } else if (!error) {
      showToast(t("toast.profileSaved"));
    }
    await fetchPeople();
    // Dritarja mbetet e hapur nëse diçka dështoi — përndryshe përdoruesi humbet
    // edhe atë çka shkroi, dhe gabimi i vetëm që sheh është një toast që ikën.
    if (!deshtimet.length && !error) {
      setPersonModal(null);
      resetPhotoState();
    }
  };

  // Faza 2 — ruajtja nga formulari i ri (AddPersonSheet). Përkthen payload-in
  // në TË NJËJTAT RPC ekzistuese (rpc_upsert_person + lidhjet), pa ndryshuar
  // algoritmin. Rregullat më të forta (V2 "të dy prindërit", First Known
  // Ancestor, Honorary server-side) mbeten patch-e të veçanta për review.
  const handleAddSheetSubmit = async (payload, closeAfter) => {
    const { kind, person, links, customFields } = payload;
    // I njëjti kontroll si te redaktimi — përndryshe rruga e shtimit e lejon
    // atë që redaktimi e ndalon, dhe rregulli varet nga dera që hape.
    if (person && person.birth_date && person.death_date && person.death_date < person.birth_date) {
      showToast(t("The date of death cannot be earlier than the date of birth."));
      return;
    }
    // Vlera e formularit është e brendshme e klientit, pra u riemërtua lirisht.
    // Vlera e RUAJTUR te baza vendoset brenda `rpc_add_honorary_person` — prandaj
    // ajo kalon te «mysafir» vetëm me MYSAFIRI2, kur ndryshohet vetë funksioni.
    if (kind === "mysafir") {
      // FIX (constraint people_honorary_reason_chk) — krijim ATOMIK: personi
      // honorary + arsyeja + dritarja e aksesit futen me NJË RPC të vetme
      // (rpc_add_honorary_person), që CHECK-u i arsyes të kënaqet që në INSERT.
      // Rrjedha e vjetër dy-hapëshe (upsert pa arsye → set_honorary) e shkelte
      // constraint-in para hapit të dytë. rpc_set_honorary mbetet për editime.
      const h = payload.honorary || {};
      const reason = (h.reason || "").trim();
      if (!reason) throw new Error(t("A guest requires a reason"));
      const { data: hid, error: herr } = await supabase.rpc("rpc_add_honorary_person", {
        p_first_name: (person.first_name || "").trim(),
        p_last_name: (person.last_name || "").trim(),
        p_maiden_name: person.maiden_name || "",
        p_birth_date: person.birth_date || null,
        p_death_date: person.death_date || null,
        p_gender: person.gender || null,
        p_external_tree_url: person.external_tree_url || "",
        p_reason: reason,
        p_access_start: h.accessStart || null,
        p_access_end: h.accessEnd || null,
        p_requester_id: currentPersonId,
      });
      if (herr) throw new Error(herr.message); // kapet nga AddPersonSheet → shfaqet mbi formular, të dhënat mbeten
      const hidId = hid;
      if (customTemplate && Array.isArray(customTemplate.fields) && customTemplate.fields.some((f) => !f.is_core)) {
        try { await supabase.rpc("rpc_set_custom_fields", { p_person_id: hidId, p_custom_fields: customFields || {}, p_requester_id: currentPersonId }); } catch (e) { /* injoro */ }
      }
      await fetchPeople();
      showToast(t("toast.added"));
      if (closeAfter) { setAddSheet(null); setSelectedId(hidId); }
      return;
    }
    const parent_ids = kind === "child" ? (links.parentIds || []) : [];
    const isSpouseAdd = kind === "partner";
    const { data: newId, error } = await supabase.rpc("rpc_upsert_person", {
      p_id: null,
      p_first_name: (person.first_name || "").trim(),
      p_last_name: (person.last_name || "").trim(),
      p_maiden_name: person.maiden_name || "",
      p_birth_date: person.birth_date || null,
      p_death_date: person.death_date || null,
      p_birth_place: person.birth_place || "",
      p_bio: "",
      p_gender: person.gender || null,
      p_branch: "core",
      p_parent_ids: parent_ids,
      p_external_tree_url: person.external_tree_url || "",
      p_requester_id: currentPersonId,
      // ⚠ Këtu qëndronin `null, null` të ngulitura. Personi i shtuar dilte pa
      // qytet e pa shtet: harta bosh, kërkimi sipas qytetit i verbër, dhe
      // redaktimi i parë — që `birth_place` e çmonton duke u nisur nga këto dy
      // kolona — e gjente boshin dhe e fshinte vendlindjen pa pyetur.
      p_birth_city: (person.birth_city || "").trim() || null,
      p_birth_country: (person.birth_country || "").trim() || null,
      // BANIMI1 — dërgohet GJITHMONË vlera ose `''`. `null` te kjo derë do të
      // thotë «mos e prek» (mbrojtje për build-in e vjetër iOS), pra `''` është
      // mënyra e vetme për të thënë «bosh».
      p_residence_country: (person.residence_country || "").trim(),
      p_residence_city: (person.residence_city || "").trim(),
      p_maiden_name_in_display: false,
      p_spouse_of: isSpouseAdd ? links.partnerId : null,
      p_member_kind: kind === "partner" ? "partner" : "blood",
      p_is_root: kind === "root", // RRENJA1 — paraardhësi i parë, i krijuar nga admini
      // PRIND1 — «Shto prind»: fëmijët ekzistues kalojnë KËTU, jo me një thirrje
      // të dytë pas krijimit. Rregulli i FAZËS 3 («asnjë person i palidhur») e
      // rrëzonte hapin e parë para se të vinte i dyti, ndaj kjo rrugë s'kishte
      // punuar kurrë për askënd. Tani personi dhe lidhja bëhen së bashku, në
      // një transaksion — pra bie edhe personi pezull kur lidhja dështonte.
      p_child_ids: kind === "parent" ? (links.childIds || []) : null,
    });
    if (error) throw new Error(error.message); // kapet nga AddPersonSheet → shfaqet mbi formular, të dhënat mbeten
    const id = newId;
    if (isSpouseAdd && links.partnerId && links.relationshipType && links.relationshipType !== "marriage") {
      const { error: relErr } = await supabase.rpc("rpc_set_relationship_status", { p_a: id, p_b: links.partnerId, p_status: links.relationshipType, p_active: true, p_requester_id: currentPersonId });
      if (relErr) console.error("rpc_set_relationship_status:", relErr.message);
    }
    // Faza 6 — auto-post i martesës në feed (opsional, s'thyen krijimin).
    //
    // KUJDES me `try/catch` këtu: `supabase-js` NUK hedh përjashtim. Blloku i
    // mëparshëm dukej se e trajtonte gabimin, por `catch` s'aktivizohej kurrë —
    // ishte teatër. "Opsionale" del e vërtetë vetëm rastësisht: dështimi
    // shpërfillej sepse askush s'e lexonte, jo sepse kështu ishte vendosur.
    // Tani vendimi është i shkruar: shpërfillet, po shkruhet te konsola.
    if (isSpouseAdd && links.partnerId) {
      const { error: pmErr } = await supabase.rpc("rpc_post_marriage", { p_a: id, p_b: links.partnerId, p_requester_id: currentPersonId });
      if (pmErr) console.error("rpc_post_marriage (opsionale):", pmErr.message);
      // AUDITI 2026-08 — "Data e lidhjes" mblidhej te formulari qëkur, por asnjë
      // rresht kodi s'e ruante: `links.relationshipDate` shkonte drejt e në
      // harresë. Tani ruhet te çifti, dhe prej saj feed-i nxjerr përvjetorin.
      if (links.relationshipDate) {
        // E njëjta datë që "shkonte drejt e në harresë" para auditit të gushtit
        // mund të shkojë atje sërish nëse ky shkrim refuzohet — dhe `try/catch`
        // s'do ta kapte, sepse s'ka çfarë të kapë.
        const { error: mErr } = await supabase.rpc("rpc_set_marriage", {
          p_a: id, p_b: links.partnerId, p_married_on: links.relationshipDate,
          p_show_in_feed: true, p_requester_id: currentPersonId,
        });
        if (mErr) showToast(t("The person was created, but the date was not saved: ") + tDyn(mErr.message));
      }
    }
    // PRIND1 — këtu rrinte një cikël `rpc_add_parent_link` pas krijimit. U hoq:
    // lidhja tani bëhet brenda `rpc_upsert_person` (me `p_child_ids`), që të mos
    // ekzistojë më gjendja «personi u krijua, po lidhja dështoi» — një person
    // pezull që s'ndreqej dot nga aplikacioni.
    // DITA1 — flamuri shkruhet vetëm kur është i ndezur: te krijimi vlera
    // parazgjedhur te baza është tashmë `false`, pra një thirrje e dytë për të
    // shkruar `false` do të ishte kot.
    if (person && person.birth_date_approx) {
      const { error: dtErr } = await supabase.rpc("rpc_set_birth_date_approx", { p_person_id: id, p_approx: true, p_requester_id: currentPersonId });
      if (dtErr) showToast(t("The person was saved, but the “date is not exact” mark was not: ") + tDyn(dtErr.message));
    }
    if (customTemplate && Array.isArray(customTemplate.fields) && customTemplate.fields.some((f) => !f.is_core)) {
      try { await supabase.rpc("rpc_set_custom_fields", { p_person_id: id, p_custom_fields: customFields || {}, p_requester_id: currentPersonId }); } catch (e) { /* injoro */ }
    }
    // "Posto në feed" — zgjedhje e shprehur nga ai që e shtoi personin.
    // Parazgjedhja është JO; importi masiv s'kalon nga këtu, pra s'poston kurrë.
    // Thirrje opsionale me try/catch: një dështim s'guxon ta prishë shtimin.
    if (payload.postToFeed) {
      try { await supabase.rpc("rpc_post_new_member", { p_person: id, p_requester_id: currentPersonId }); } catch (_e) { /* opsionale */ }
    }
    await fetchPeople();
    showToast(t("toast.added"));
    if (closeAfter) { setAddSheet(null); setSelectedId(id); }
  };

  const saveForm = async (closeAfter = false) => {
    if (!form.firstName.trim()) { showToast(t("toast.giveAtLeastOneName")); return; }
    // ⚠ 4 shtator 2026 — DATA E VDEKJES PARA DATËLINDJES NUK KONTROLLOHEJ ASKUND.
    // As këtu, as te rruga e shtimit, as te baza. Prandaj te pema demo rrinte
    // «Mira Demo 9316–2010»: një shtypje e vetme, e ruajtur pa pyetur, dhe
    // `lifespan()` nxjerr moshë negative te kartela. Datat janë ISO
    // («VVVV-MM-DD»), pra krahasimi si tekst është i saktë.
    if (form.birthDate && form.deathDate && form.deathDate < form.birthDate) {
      showToast(t("The date of death cannot be earlier than the date of birth."));
      return;
    }
    // ⚠ 4 shtator 2026 — FUSHAT `required` SHFAQNIN YLLIN PA E MBAJTUR FJALËN.
    // Ylli vizatohej te etiketa, po asnjë rrugë nuk e kontrollonte: as ruajtja
    // këtu, as ajo e shtimit. Pra «e detyrueshme» ishte thjesht një shenjë.
    // Modeli i saktë ekzistonte tashmë te porositë — shih `Still missing`.
    {
      const flds = (customTemplate && Array.isArray(customTemplate.fields) ? customTemplate.fields : [])
        .filter((f) => !f.is_core && f.required
          && (!f.visible_for || f.visible_for.length === 0 || personModal.mode !== "add" || f.visible_for.includes(addRelationType)));
      const mungon = flds.find((f) => {
        if (f.type === "boolean") return false;
        const v = customFieldValues[f.key];
        return v == null || String(v).trim() === "";
      });
      if (mungon) { showToast(`${t("Still missing")}: ${mungon.label || mungon.key}`); return; }
    }
    // Kërkesë e userit — kur "Shto si: Bashkëshort/e", personi i ri s'ka
    // FARE prindër (parent_ids bosh) — lidhet drejtpërdrejt si çift me
    // personin e zgjedhur (p_spouse_of), jo si fëmijë i tij.
    const isSpouseAdd = personModal.mode === "add" && addRelationType === "spouse";
    // "parent" = personi i ri është PRINDI i një personi ekzistues (form.childOf).
    // Vetë personi i ri s'ka prindër ende (parent_ids bosh) — lidhet SIPËR fëmijës.
    const isParentAdd = personModal.mode === "add" && addRelationType === "parent";
    const parent_ids = (isSpouseAdd || isParentAdd) ? [] : [form.parent1, form.parent2].filter(Boolean);
    // FIX (kërkesë e userit) — "duhet gjithsesi të zgjedhësh ose prind ose
    // bashkëshort/e" — kontroll në FRONT-END, PARA se të thirret RPC-ja, që
    // dikush të mos hasë thjesht "not permitted" pa kuptuar pse. Backend-i
    // (rpc_upsert_person) mbetet kufiri i vërtetë i sigurisë — ky është
    // vetëm UX më i mirë, jo zëvendësim i kontrollit real.
    if (personModal.mode === "add" && treeHasAnyPeople !== false) {
      if (isSpouseAdd && !form.spouseOf) { showToast(t("toast.mustPickSpouse")); return; }
      if (isParentAdd && !form.childOf) { showToast(t("The person the parent is being added to is missing.")); return; }
      if (!isSpouseAdd && !isParentAdd && parent_ids.length === 0) { showToast(t("toast.mustPickParentOrSpouse")); return; }
    }
    // FAZA 4: `treeHasAnyPeople === false`, jo `peopleCount === 0` (shih locked më lart për arsyen).
    const isBootstrap = treeHasAnyPeople === false && personModal.mode === "add";
    const villageTrim = (form.birthVillage || "").trim();
    const cityTrim = (form.birthCity || "").trim();
    const countryTrim = (form.birthCountry || "").trim();
    // Ruhet emri i plotë i shtetit (jo kodi) që `birth_place` të mbetet i
    // lexueshëm; kodi ruhet veçmas te `birth_country`.
    const countryLabel = displayCountry(countryTrim, i18n.language) || countryTrim;
    const combinedPlace = [villageTrim, cityTrim, countryLabel].filter(Boolean).join(", ");
    const { data: newId, error } = await supabase.rpc("rpc_upsert_person", {
      p_id: personModal.mode === "edit" ? personModal.id : null,
      p_first_name: form.firstName.trim(),
      p_last_name: form.lastName.trim(),
      p_maiden_name: form.maidenName ? form.maidenName.trim() : "",
      p_birth_date: form.birthDate || null,
      p_death_date: form.deathDate || null,
      p_birth_place: combinedPlace,
      p_bio: form.bio.trim(),
      p_gender: form.gender || null,
      p_branch: form.branch || "core",
      p_parent_ids: parent_ids,
      // Fusha u hoq nga formulari, po vlera e vjetër nuk guxon të zhduket me
      // ruajtjen e parë: dërgohet ashtu siç është te baza.
      p_external_tree_url: personModal.mode === "edit"
        ? ((people[personModal.id] && people[personModal.id].external_tree_url) || "")
        : "",
      p_requester_id: currentPersonId,
      p_birth_city: cityTrim || null,
      p_birth_country: countryTrim || null,
      // BANIMI1 — vendbanimi aktual. Dërgohet GJITHMONË: vlera ose `''` (zbraze).
      // `null` te kjo derë do të thotë «mos e prek» — mbrojtje për build-in e
      // vjetër iOS që s'i njeh këto fusha dhe s'duhet t'i fshijë pa dashje.
      p_residence_country: (form.residenceCountry || "").trim(),
      p_residence_city: (form.residenceCity || "").trim(),
      // Kërkesë e userit — a shfaqet mbiemri i vajzërisë NË MES te emri i
      // shfaqur (traditë shqiptare: Emri MbiemriVajzërisë Mbiemri, p.sh.
      // "Merita Orlishta Krasniqi"). Vetëm kur ka mbiemër vajzërie fare.
      p_maiden_name_in_display: hasMaidenName ? !!form.maidenNameInDisplay : false,
      p_spouse_of: isSpouseAdd ? form.spouseOf : null,
      // Faza 3 — personi i parë (bootstrap) është First Known Ancestor.
      p_member_kind: "blood",
      p_is_root: isBootstrap,
      // PRIND1 — i njëjti defekt ishte edhe në këtë rrugë (modali i vjetër):
      // «Shto prind» krijonte një person pa prind e pa partner, dhe rregulli i
      // FAZËS 3 e rrëzonte para se të vinte lidhja. Tani fëmija kalon që në
      // krijim dhe lidhja bëhet brenda së njëjtës thirrje.
      p_child_ids: isParentAdd && form.childOf ? [form.childOf] : null,
    });
    if (error) { showToast(t("toast.saveFailed", { error: error.message })); return; }
    const id = personModal.mode === "edit" ? personModal.id : newId;
    // SHTESË (kërkesë e userit) — nëse personi u shtua si "i ardhur në
    // familje" me lloj tjetër nga martesa (bashkëjetesë/fejuar), shënohet
    // VEÇMAS pas krijimit — përmes së njëjtës mekanikë si "Shkurorëzo"
    // (rpc_set_relationship_status). "Bashkëshort/e" (parazgjedhja) s'kërkon
    // asgjë shtesë — lidhja martesore krijohet tashmë nga p_spouse_of sipër.
    if (isSpouseAdd && form.spouseOf && addSpouseRelType !== "married") {
      // Personi tashmë u krijua. Nëse KJO dështon, lidhja mbetet "martesë" —
      // parazgjedhja — dhe dikush që u shënua "i/e fejuar" del i martuar te
      // pema. Prandaj mesazhi e thotë saktë çfarë u ruajt dhe çfarë jo.
      const { error: relErr } = await supabase.rpc("rpc_set_relationship_status", { p_a: id, p_b: form.spouseOf, p_status: addSpouseRelType, p_active: true, p_requester_id: currentPersonId });
      if (relErr) showToast(t("The person was created, but the relationship type was not saved: ") + relErr.message);
    }
    // PRIND1 — «Shto prind» lidhet tani brenda `rpc_upsert_person` (`p_child_ids`),
    // në të njëjtin transaksion me krijimin. Thirrja e dytë u hoq: ajo ishte
    // burimi i personit pezull kur lidhja dështonte pas krijimit.
    // DITA1 — flamuri i datës së përafërt. Derë e vet, jo parametër i
    // `rpc_upsert_person`: ai nënshkrim është ndryshuar dy herë këto ditë dhe
    // çdo parametër i ri kërkon `drop` + `create` me listën e plotë të tipave.
    // Gabimi LEXOHET — një shkrim i heshtur këtu do të thoshte se kutiza
    // shënohet, formulari mbyllet, dhe asgjë s'ka ndryshuar te baza.
    {
      const { error: dtErr } = await supabase.rpc("rpc_set_birth_date_approx", {
        p_person_id: id, p_approx: !!form.birthDateApprox, p_requester_id: currentPersonId,
      });
      if (dtErr) showToast(t("The person was saved, but the “date is not exact” mark was not: ") + tDyn(dtErr.message));
    }
    // SHTESË (kërkesë e userit — "rast i veçantë": fëmijë i adoptuar/mik
    // nderi/prind i panjohur, DHE checkbox-i i ri "mos e trajto si fëmijë
    // me 1 prind") — backend-i (rpc_set_branch_special_case,
    // rpc_set_branch_override) TASHMË e lejonte edhe redaktorin
    // (current_role_of ... not in ('admin','editor')) — vetëm kushti këtu
    // në front-end ishte gabimisht VETËM admin (`canManage`). Rregulluar.
    if (["admin", "editor"].includes(myRole)) {
      // 21 gusht: ky rresht ishte pa kontroll gabimi — një rresht MBI atë ku
      // komenti poshtë feston ndreqjen e së njëjtës gjë. Auditi i mëparshëm e
      // pa `rpc_set_branch_override` dhe nuk e pa fqinjin e tij.
      const { error: scErr } = await supabase.rpc("rpc_set_branch_special_case", { p_person_id: id, p_case: form.branchSpecialCase || null, p_requester_id: currentPersonId });
      if (scErr) showToast(t("Saving failed: ") + scErr.message);
      // Kërkesë e userit — liri e plotë e ngjyrës së degës + fshehje e
      // unazës "solo", VETËM për këtë person. E njëjta leje (admin/
      // editor) si rasti i veçantë sipër.
      // FIX (auditim): më parë kjo thirrje bëhej PA kontroll gabimi, ndaj
      // mungesa e funksionit në server (shih BRANCH_override.sql) kalonte
      // krejt në heshtje — ngjyra e degës/unaza thjesht s'ruheshin kurrë.
      const { error: brErr } = await supabase.rpc("rpc_set_branch_override", { p_person_id: id, p_override: form.branchColorOverride || null, p_hide_ring: !!form.hideSoloRing, p_requester_id: currentPersonId });
      if (brErr) showToast(t("Saving failed: ") + brErr.message);
    }
    if (photoFile) {
      const path = await uploadPhotoFor(id);
      if (path) {
        const { error: phErr } = await supabase.rpc("rpc_set_photo", { p_person_id: id, p_photo_path: path, p_requester_id: currentPersonId || id });
        if (phErr) showToast(t("Saving failed: ") + phErr.message);
      }
    } else if (photoRemoved && personModal.mode === "edit" && people[id]?.photo_path) {
      // 6 shtator 2026 — «Hiq foton» te «Redakto detajet» s'bënte asgjë: butoni
      // e fshinte paraparjen, po ruajtja e lexonte `photoRemoved` vetëm te
      // profili im (mode "self"). Admini e shihte «u ruajt» dhe fotoja kthehej.
      const { error: phErr } = await supabase.rpc("rpc_set_photo", { p_person_id: id, p_photo_path: null, p_requester_id: currentPersonId || id });
      if (phErr) showToast(t("Saving failed: ") + phErr.message);
    }
    // Kërkesë e userit — ruaj fokusin e fotos VETËM kur ka ende foto (e re
    // ose ekzistuese, jo e hequr) pas shtimit/redaktimit.
    if (photoFile || (personModal.mode === "edit" && people[id]?.photo_path && !photoRemoved)) {
      const { error: crErr } = await supabase.rpc("rpc_set_photo_crop", { p_person_id: id, p_focus_x: photoFocus.x, p_focus_y: photoFocus.y, p_zoom: photoZoom, p_requester_id: currentPersonId || id });
      if (crErr) showToast(t("Saving failed: ") + crErr.message);
    }
    // Faza 11 — ruaj fushat custom (jsonb) nëse template-i i publikuar ka fusha.
    //
    // `try/catch` u hoq: nuk kapte kurrë asgjë. Fushat custom janë të dhëna që
    // i shkroi njeriu me dorë — nëse humbasin, ai s'ka nga t'i marrë prapë.
    if (customTemplate && Array.isArray(customTemplate.fields) && customTemplate.fields.length) {
      const { error: cfErr } = await supabase.rpc("rpc_set_custom_fields", { p_person_id: id, p_custom_fields: customFieldValues || {}, p_requester_id: currentPersonId || id });
      if (cfErr) showToast(t("The extra fields were not saved: ") + cfErr.message);
    }
    let contactWarning = "";
    if (canManage) {
      try {
        const { error: contactErr } = await supabase.rpc("rpc_set_contact", {
          p_person_id: id,
          p_email: (form.contactEmail || "").trim(),
          p_phone: (form.contactPhone || "").trim(),
          p_requester_id: currentPersonId,
        });
        if (contactErr) contactWarning = contactErr.message;
        else if (selectedId === id) setSelectedContact({ email: form.contactEmail || "", phone: form.contactPhone || "" });
      } catch (e) {
        contactWarning = e?.message || "gabim i panjohur";
      }
    }
    await fetchPeople();
    if (isBootstrap) {
      // FAZA 4: rpc_bootstrap_admin (këtu poshtë) është momenti kur
      // person_account_links krijohet — para kësaj, RLS ("family read
      // people") ende s'e lejon këtë llogari ta lexojë personin e sapokrijuar,
      // kështu që fetchPeople() e mësipërme ktheu bosh. Rifresko sërish PAS
      // bootstrap-it, dhe vendos treeHasAnyPeople=true direkt (e dimë me
      // siguri, s'ka nevojë të rithirret rpc_any_people_exist).
      // KY ËSHTË SHKRIMI MË I RREZIKSHËM I GJITHË APLIKACIONIT.
      //
      // Këtu krijohet `person_account_links` — pra lidhja mes llogarisë dhe
      // personit të parë. Nëse dështon në heshtje, familja krijohet, personi
      // krijohet, dhe pastaj RLS-ja nuk e lejon këtë llogari të lexojë asgjë:
      // pema del bosh. Përdoruesi sheh një aplikacion që "harroi" gjithçka që
      // sapo shkroi, dhe asnjë gabim askund.
      //
      // Prandaj ky është i vetmi vend ku përdor `alert` dhe jo `showToast`:
      // toast-i ikën pas tri sekondash, dhe ky mesazh është i vetmi që e lidh
      // ekranin bosh me shkakun.
      const { error: bootErr } = await supabase.rpc("rpc_bootstrap_admin", { p_person_id: id });
      if (bootErr) {
        alert(t("The first administrator could not be created: ") + bootErr.message
              + "\n\n" + t("Reload the page and try again — do not create the person a second time."));
        return;
      }
      await fetchPeople();
      setTreeHasAnyPeople(true);
      await fetchRoles();
      // FIX (bug rrëzimi): hapi i vjetër vendoste `pendingAdminId` për të kërkuar
      // fjalëkalim shtesë admini — ai hap u hoq (FAZA 34), por thirrja kishte
      // mbetur dhe rrëzonte app-in. Tani thjesht e kompletojmë login-in si admini
      // i sapokrijuar.
      completeLogin(id);
      setSelectedId(id);
      setPersonModal(null);
      resetPhotoState();
      return;
    }
    if (contactWarning) {
      showToast(t("toast.savedButContactFailed", { action: personModal.mode === "add" ? t("toast.added") : t("toast.saved2"), error: contactWarning }));
    } else {
      showToast(personModal.mode === "add" ? t("toast.added") : t("toast.saved2"));
    }
    // FIX (kërkesë e userit) — kur SHTOHET një person i ri (jo kur redaktohet
    // një ekzistues), formulari QËNDRON i hapur, i rikthyer bosh, gati për
    // të njëpasnjëshëm — që admini të shtojë krejt familjen (p.sh. disa
    // vëllezër/motra/fëmijë të njëpasnjëshëm) pa u larguar nga faqja çdo
    // herë. `parent1` MBETET (zakonisht shtohen disa nën të njëjtin prind
    // njëra pas tjetrës) — gjithçka tjetër kthehet bosh, njësoj si openAdd().
    if (personModal.mode === "add" && !isParentAdd && !closeAfter) {
      const parent = form.parent1 ? people[form.parent1] : null;
      const suggestedBranch = parent && parent.gender === "F" && parent.branch !== "married_in" ? "married_out" : "core";
      setForm({ firstName: "", lastName: "", maidenName: "", maidenNameInDisplay: false, birthDate: "", deathDate: "", birthPlace: "", birthVillage: "", birthCity: "", birthCountry: "", residenceCountry: "", residenceCity: "", bio: "", parent1: form.parent1, parent2: null, spouseOf: null, childOf: null, branch: suggestedBranch, gender: "", externalTreeUrl: "", contactEmail: "", contactPhone: "", birthDateApprox: false });
      setAddSpouseRelType("married");
      setMarkDeceased(false);
      setHasMaidenName(false);
      setAddRelationType("child");
      setCustomFieldValues({});
      resetPhotoState();
      return;
    }
    setSelectedId(id);
    setPersonModal(null);
    resetPhotoState();
  };

  // SHTESË (kërkesë e userit — statusi i marrëdhënies duhet të MBAJË
  // lidhjen vizuale, jo ta fshijë) — FIX: më parë "Shkurorëzo" thërriste
  // rpc_set_spouses(p_link=false), duke hequr `spouse_ids` KOMPLET — kjo e
  // zhdukte çiftin krejt nga pozicionimi (siç raportoi useri: "mungon
  // viza, trungu bie mbi njërin, jo mes tyre"). Tani: statusi shënohet
  // VEÇMAS (divorced_from_ids), spouse_ids MBETET — çifti vazhdon të
  // pozicionohet/lidhet bashkë siç ishte, thjesht me ikonë tjetër te pika
  // e bashkimit (shih coupleStatusIcons më poshtë).
  const toggleDivorceStatus = async (personId, spouseId, makeDivorced) => {
    const { error } = await supabase.rpc("rpc_set_divorce_status", { p_a: personId, p_b: spouseId, p_divorced: makeDivorced, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.saveFailed", { error: error.message })); return; }
    await fetchPeople();
    showToast(makeDivorced ? t("Marked as divorced.") : t("The divorce status was cancelled."));
  };

  // FIX (kërkesë e userit) — HIQ plotësisht një martesë të futur GABIM. Ndryshe
  // nga "Shkurorëzo" (që e mban lidhjen me "Anulo divorcin"), kjo e heq krejt:
  // të dy mbeten në pemë por s'janë më të lidhur. Vetëm admin, me konfirmim.
  const removeSpouseLink = async (personId, spouseId) => {
    if (!window.confirm(t("Remove the marital link COMPLETELY between {{a}} and {{b}}?\n\nBoth stay in the tree, but will no longer be linked. Use this when the marriage was entered by mistake (this is not a \"divorce\").", { a: displayName(people[personId] || {}), b: displayName(people[spouseId] || {}) }))) return;
    const { error } = await supabase.rpc("rpc_unlink_spouse", { p_a: personId, p_b: spouseId, p_requester_id: currentPersonId });
    if (error) { showToast(t("Unlinking failed: ") + error.message); return; }
    await fetchPeople();
    showToast(t("The marriage link was removed."));
  };

  // Një dispecer i vetëm për veprimet e bashkëshortit — përdoret nga dropdown-i
  // te paneli i personit (në vend të një liste të gjatë butonash inline).
  const handleSpouseAction = (personId, spouseId, action) => {
    switch (action) {
      case "divorce":   toggleDivorceStatus(personId, spouseId, true); break;
      case "undivorce": toggleDivorceStatus(personId, spouseId, false); break;
      case "cohabit":   toggleRelationshipStatus(personId, spouseId, "cohabiting", true); break;
      case "uncohabit": toggleRelationshipStatus(personId, spouseId, "cohabiting", false); break;
      case "engage":    toggleRelationshipStatus(personId, spouseId, "engaged", true); break;
      case "unengage":  toggleRelationshipStatus(personId, spouseId, "engaged", false); break;
      case "unlink":    removeSpouseLink(personId, spouseId); break;
      case "hide":      hidePersonForMe(spouseId); break;
      case "unhide":    unhidePersonForMe(spouseId); break;
      default: break;
    }
  };

  // SHTESË (kërkesë e userit) — statuse të reja marrëdhënieje: t("Cohabitation")
  // dhe t("Engaged") — e njëjta mekanikë si divorci, thjesht 2 statuse më vete.
  const toggleRelationshipStatus = async (personId, spouseId, status, active) => {
    const { error } = await supabase.rpc("rpc_set_relationship_status", { p_a: personId, p_b: spouseId, p_status: status, p_active: active, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.saveFailed", { error: error.message })); return; }
    await fetchPeople();
    const label = status === "cohabiting" ? t("cohabitation") : t("engagement");
    showToast(active ? t("Marked as {{label}}.", { label }) : t("The {{label}} status was cancelled.", { label }));
  };

  // SHTESË (kërkesë e userit) — "fshehja për vete" — krejt personale (siç u
  // konfirmua: "secili vendos vetëm për veten"), s'kërkon leje nga askush.
  const hidePersonForMe = async (hiddenId) => {
    if (!currentPersonId) return;
    setMyHiddenPeople((prev) => new Set(prev).add(hiddenId)); // optimist, kthehet mbrapsht nëse dështon
    const { error } = await supabase.rpc("rpc_hide_person_for_viewer", { p_hidden_person_id: hiddenId, p_requester_id: currentPersonId });
    if (error) {
      setMyHiddenPeople((prev) => { const next = new Set(prev); next.delete(hiddenId); return next; });
      showToast(t("toast.saveFailed", { error: error.message }));
    }
  };
  const unhidePersonForMe = async (hiddenId) => {
    if (!currentPersonId) return;
    setMyHiddenPeople((prev) => { const next = new Set(prev); next.delete(hiddenId); return next; });
    const { error } = await supabase.rpc("rpc_unhide_person_for_viewer", { p_hidden_person_id: hiddenId, p_requester_id: currentPersonId });
    if (error) {
      setMyHiddenPeople((prev) => new Set(prev).add(hiddenId));
      showToast(t("toast.saveFailed", { error: error.message }));
    }
  };

  const deletePerson = async (id) => {
    if (!window.confirm(t("toast.confirmDeletePerson", { name: displayName(people[id]) }))) return;
    const { error } = await supabase.rpc("rpc_delete_person", { p_target_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.removeFailed", { error: error.message })); return; }
    // FSHIRJE E BUTË: fotoja NUK hiqet — ruhet për rikthim. Fshirja përfundimtare
    // (bashkë me foton) bëhet vetëm nga Admin Center-i i platformës.
    if (currentPersonId === id) setCurrentPersonId(null);
    await Promise.all([fetchPeople(), fetchRoles()]);
    setSelectedId(null);
    showToast(t("Moved to the Trash — you can restore it from the admin menu → Trash."));
  };

  // KOSHI — lexon personat e fshirë (butë) të familjes dhe i rikthen.
  const fetchDeletedPeople = async () => {
    if (!currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_list_deleted_people", { p_requester_id: currentPersonId });
    if (error) { showToast("S'u ngarkua Koshi: " + error.message); return; }
    setDeletedPeople(Array.isArray(data) ? data : []);
  };
  const restorePerson = async (id) => {
    const p = deletedPeople.find((x) => x.id === id);
    const nm = p ? `${p.first_name || ""} ${p.last_name || ""}`.trim() : "";
    if (!window.confirm(t("Bring {{name}} back to the tree?", { name: nm || t("this person") }))) return;
    const { error } = await supabase.rpc("rpc_restore_person", { p_target_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("Restoring failed: ") + error.message); return; }
    await Promise.all([fetchDeletedPeople(), fetchPeople(), fetchRoles()]);
    showToast(t("The person was restored to the tree."));
  };

  // Sinkronizon profilin e përdoruesit mes familjes aktive dhe një familjeje
  // tjetër ku ai është i njëjti person — mbush VETËM fushat bosh (pa mbishkrim).
  // SHTESË (kërkesë e userit — "me vetëm 2 familje, klikimi duhet të bëjë
  // direkt switch; dritarja vetëm me shtypje të gjatë, ose kur ka >2 familje").
  const switchableFamilies = () => myLinkedFamilies.filter((f) => f.person_id && f.family_slug && !hiddenFamilies.has(f.family_id));

  // KALIMI TE NJË FAMILJE TJETËR — një rrugë e vetme për të tria mjediset.
  //
  // Deri tani bëhej me `window.location.href = "/f/<slug>"`. Te shfletuesi ajo
  // punon, sepse Cloudflare-i çdo rrugë të panjohur ia kthen `index.html`-së.
  // Brenda aplikacionit native NUK punon: aty faqet vijnë nga paketa lokale,
  // dhe `/f/orlishta` s'është skedar — pra klikimi nuk çonte askund, dhe butoni
  // dukej i vdekur.
  //
  // Tani slug-u ruhet dhe app-i rinis nga rrënja; `main.jsx` e lexon atje.
  const shkoTeFamilja = (slug) => {
    if (!slug) return;
    try { localStorage.setItem("pef_family_slug", slug); } catch (_e) { /* private mode */ }
    if (Capacitor.isNativePlatform()) window.location.replace(window.location.origin + "/");
    else window.location.href = `/f/${slug}`;
  };
  const quickSwitchFamily = () => {
    // 6 shtator 2026, pronari: «nëse dikush është anëtar në më shumë se dy
    // familje, mos u shfaq lista — ndërro me radhë alfabetike: 1→2, 2→3,
    // 3→4, dhe nga e fundit te e para». Lista mbetet te shtypja e gjatë.
    const list = switchableFamilies()
      .slice()
      .sort((a, b) => String(a.family_name || a.family_slug || "").localeCompare(String(b.family_name || b.family_slug || ""), "sq"));
    if (list.length >= 2) {
      const i = list.findIndex((f) => f.family_id === activeFamilyId);
      const tjetra = list[(i + 1) % list.length];
      if (tjetra && tjetra.family_id !== activeFamilyId) { shkoTeFamilja(tjetra.family_slug); return; }
    }
    setSwitchFamOpen(true);
  };
  // Prop-e të gatshme: klikim = switch i shpejtë, shtypje e gjatë (500ms) =
  // dritarja e plotë. Përdoret te të dy vendet (sidebar-i, menyja e telefonit).
  // Ora dhe flamuri rrinë te `ref`, jo brenda kësaj funksioni.
  //
  // Më parë ishin `let` brenda saj — dhe kjo funksion thirret PËRSËRI në çdo
  // rivizatim, sepse shkruhet drejt te JSX-i. Pra `onPointerDown` e vendoste
  // orën te një mbyllje, dhe `onClick`, që vjen pas rivizatimit, e kërkonte te
  // një tjetër ku ajo ishte `null`: anulimi s'anulonte, dhe dritarja hapej edhe
  // kur klikimi ishte i shkurtër. Një `ref` mbijeton rivizatimet.
  const shtypjaEGjate = useRef(null);
  const shtypjaIshteEGjate = useRef(false);
  const switchFamPressProps = (onBefore) => {
    const ndalOren = () => { if (shtypjaEGjate.current) { clearTimeout(shtypjaEGjate.current); shtypjaEGjate.current = null; } };
    return {
      onPointerDown: () => {
        shtypjaIshteEGjate.current = false;
        ndalOren();
        shtypjaEGjate.current = setTimeout(() => {
          shtypjaIshteEGjate.current = true;
          shtypjaEGjate.current = null;
          if (onBefore) onBefore();
          setSwitchFamOpen(true);
        }, 500);
      },
      onPointerUp: ndalOren,
      onPointerLeave: ndalOren,
      onPointerCancel: ndalOren,
      onContextMenu: (e) => e.preventDefault(),
      onClick: () => {
        ndalOren();
        if (shtypjaIshteEGjate.current) { shtypjaIshteEGjate.current = false; return; }
        if (onBefore) onBefore();
        quickSwitchFamily();
      },
    };
  };
  const openSyncModal = (f) => {
    setSyncFields({ photo: true, bio: true, birth: true, contact: true, legacy: true });
    setSyncSource(currentPersonId); // parazgjedhje: familja aktive si burim
    setSyncTargets(new Set(f?.person_id ? [f.person_id] : []));
    setSyncModalFor(true);
  };
  // `openSyncModalAll()` u hoq (19 gusht 2026) — asnjë buton s'e thërriste;
  // sinkronizimi bëhet gjithmonë nga `openSyncModal(...)` me zgjedhje burimi.
  // Sill bashkëshortin dhe fëmijët nga familja-BURIM te një ose disa familje
  // të tjera. Modeli i njëjtë me sinkronizimin (burim → destinacione), por
  // destinacionet filtrohen: shfaqen VETËM ato ku familja ende s'ekziston
  // (rpc_import_preview > 0), që të mos krijohen dyfishime.
  const openImportModal = () => {
    setImportSource(currentPersonId);
    setImportTargets(new Set());
    setImportPreview({});
    setImportOpen(true);
  };
  const runImport = async () => {
    if (!importSource) { showToast(t("Choose a source family.")); return; }
    const targets = [...importTargets].filter((id) => id && id !== importSource && (importPreview[id] || 0) > 0);
    if (targets.length === 0) { showToast(t("Choose at least one destination family.")); return; }
    setImportBusy(true);
    let total = 0;
    for (const target of targets) {
      // p_a = DESTINACION (këtu krijohen), p_b = BURIM (ka bashkëshortin+fëmijët)
      const { data, error } = await supabase.rpc("rpc_import_nuclear_family", { p_a: target, p_b: importSource });
      if (error) { setImportBusy(false); showToast(t("The family was not brought over: ") + error.message); return; }
      total += (data && data.created) || 0;
    }
    setImportBusy(false);
    await fetchPeople();
    setImportOpen(false);
    if (total > 0) logActivity("import_family", null, `${total} persona`);
    showToast(total > 0 ? t("{{count}} people were added (spouse + children). You can send them invitations now.", { count: total }) : t("Nothing new to bring — they already exist here."));
  };
  const runSync = async () => {
    const fields = Object.entries(syncFields).filter(([, v]) => v).map(([k]) => k);
    if (fields.length === 0) { showToast(t("Choose at least one field.")); return; }
    if (!syncSource) { showToast(t("Choose a source family.")); return; }
    const targets = [...syncTargets].filter((id) => id && id !== syncSource);
    if (targets.length === 0) { showToast(t("Choose at least one destination family.")); return; }
    // Shtytje me DREJTIM: burimi mbishkron çdo destinacion të zgjedhur.
    setSyncBusy(true);
    for (const target of targets) {
      // FIX (raportuar nga useri — "Sinkronizo profilin nuk po funksionon"):
      // rpc_push_profile (shtytje me drejtim) MUNGONTE në databazë, ndaj çdo
      // thirrje dështonte. Tani ekziston (SYNC_push_profile.sql), por mbahet
      // edhe një rezervë: nëse serveri s'e ka ende funksionin e ri, biem te
      // rpc_sync_person_across_families (bidirektional, mbush bosh) që
      // sinkronizimi të punojë menjëherë, pa pritur ekzekutimin e SQL-së.
      let { error } = await supabase.rpc("rpc_push_profile", { p_from: syncSource, p_to: target, p_fields: fields });
      if (error && /(does not exist|not find|schema cache|404)/i.test(error.message || "")) {
        ({ error } = await supabase.rpc("rpc_sync_person_across_families", { p_a: syncSource, p_b: target, p_fields: fields }));
      }
      if (error) { setSyncBusy(false); showToast(t("Syncing failed: ") + error.message); return; }
    }
    setSyncBusy(false);
    await fetchPeople();
    setSyncModalFor(null);
    logActivity("sync_profile", null, `${targets.length} familje · ${fields.join(", ")}`);
    showToast(t("Profile pushed to the selected families."));
  };

  // Kur hapet "Sill familjen" ose ndryshon burimi, llogarit sa persona do të
  // krijoheshin te secila familje-kandidate; mban në listë vetëm ato me >0.
  useEffect(() => {
    if (!importOpen || !importSource) return;
    let cancelled = false;
    (async () => {
      setImportPreviewLoading(true);
      const candidates = myLinkedFamilies.filter((f) => f.person_id && f.person_id !== importSource);
      const result = {};
      for (const f of candidates) {
        const { data } = await supabase.rpc("rpc_import_preview", { p_a: f.person_id, p_b: importSource });
        if (cancelled) return;
        result[f.person_id] = data || 0;
      }
      if (cancelled) return;
      setImportPreview(result);
      setImportTargets((prev) => new Set([...prev].filter((id) => (result[id] || 0) > 0)));
      setImportPreviewLoading(false);
    })();
    return () => { cancelled = true; };
  }, [importOpen, importSource, myLinkedFamilies]);

  // Mënyra e ikonave sinkronohet nga dizajni aktiv (designIconMode e vendos
  // NOVA_ICON_MODE te syncDesignColors). Ky efekt siguron një ri-render kur
  // ndryshon dizajni/tema, që NovaIcon dhe mbështjellësit e emoji-t të rifreskohen.
  useEffect(() => {
    const off = onDesignChange(() => setIconTick((n) => n + 1));
    return off;
  }, []);

  const linkSpouse = async () => {
    if (!spousePick || !spouseModalFor) return;
    const { error } = await supabase.rpc("rpc_set_spouses", { p_a: spouseModalFor, p_b: spousePick, p_link: true, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.linkFailed", { error: error.message })); return; }
    // Faza 6 — auto-post i martesës në feed (opsional, s'thyen lidhjen).
    try { await supabase.rpc("rpc_post_marriage", { p_a: spouseModalFor, p_b: spousePick, p_requester_id: currentPersonId }); } catch (_e) { /* auto-post opsional */ }
    await fetchPeople();
    setSpouseModalFor(null);
    setSpousePick(null);
    showToast(t("toast.linkedAsSpouses"));
  };

  // "Kodi i Familjes" — ngarkohet vetëm kur admini hap panelin "Menaxho
  // Anëtarët" (jo në çdo ngarkim faqeje), dhe pas 1.5s shënon njoftimet si
  // "të para" — kohë e mjaftueshme që badge-i "e re" të vërehet fillimisht.
  useEffect(() => {
    if (!rolesPanelOpen || !canManage) return;
    fetchFamilyJoinCode();
    fetchJoinCodeClaims();
    fetchKodetPersonale();
    fetchLlogaritEAnetareve();
    const timer = setTimeout(() => markJoinCodeClaimsSeen(), 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesPanelOpen, canManage]);

  const setRole = async (personId, role) => {
    const { error } = await supabase.rpc("rpc_set_role", { p_target_id: personId, p_role: role, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.roleSetFailed", { error: error.message })); return; }
    await fetchRoles();
    showToast(t("toast.roleSet", { role: t(`members.${role}`) }));
  };
  // FAZA 2 — krijon një ftesë (rpc_create_invite, shtuar te FAZA 1 e
  // schema.sql) dhe i thërret Edge Function-it send-invite ta dërgojë email
  // real te personi. Kërkon që personi të ketë email të regjistruar te
  // people_contact (kontrollohet vetë RPC-ja send-invite, jo këtu). Kjo
  // NUK e ndryshon identifikimin kryesor të App.jsx (emër+datëlindje) —
  // vetëm i jep një anëtari mundësinë të lidhë opsionalisht një llogari
  // reale (Google/Apple/Email), për t'u përgatitur për Fazën e ardhshme.
  const sendInviteTo = async (personId) => {
    const { data: token, error: createError } = await supabase.rpc("rpc_create_invite", {
      p_person_id: personId,
      p_requester_id: currentPersonId,
    });
    if (createError) { showToast(t("toast.inviteFailed", { error: createError.message })); return; }
    const { data, error: sendError } = await supabase.functions.invoke("send-invite", {
      body: { token, requesterId: currentPersonId },
    });
    if (sendError || data?.error) {
      showToast(t("toast.inviteCreatedEmailFailed", { error: await extractFunctionErrorMessage(sendError, data) }));
      return;
    }
    showToast(t("toast.inviteSentTo", { email: data.to }));
  };
  // HEQUR më 2 shtator 2026 — `copyInviteLink` (FAZA 12) rrinte pikërisht
  // këtu. Ai krijonte një ftesë PA adresë (`p_bind_email: false`) dhe e
  // kopjonte linkun, që admini ta dërgonte vetë me WhatsApp a SMS.
  //
  // Pse ra: ai link hyn te pema pa e ditur adresën e askujt. Kush ia përcjell
  // dikujt tjetër, i dha edhe hyrjen — dhe një link përcillet me një prekje,
  // ruhet te historiku i shfletuesit, del te parapamja e bisedës. Ishte i
  // vetmi vend ku pëlqimi i familjes nuk kishte emër.
  //
  // Arsyeja që e mbajti gjallë deri sot ishte "po personi që s'ka email?".
  // Por nuk ekziston llogari pa email: as fjalëkalimi, as magic link-u, as
  // Google-i e Apple-i nuk hyjnë dot pa adresë. Pra personi e ka gjithmonë
  // një — familja thjesht nuk e ka pyetur ende. Dhe ajo pyetje ËSHTË pëlqimi.
  //
  // Familjes i mbeten dy rrugë, të dyja me pëlqim të shkruar: ftesa me email
  // (`sendInviteTo`) dhe kodi personal (`kopjoKodinPersonal`, KODI2).
  //
  // Mekanizmi nuk u zhduk — kaloi te Admin Center, si
  // `rpc_platform_person_magic_link`: vetëm staf platforme, me arsye të
  // detyrueshme, një orë afat, dhe gjurmë te `platform_audit_log`.
  // Baza e refuzon `p_bind_email: false` që nga `PELQIMI7`, ndaj edhe
  // versionet e vjetra në telefona marrin një mesazh të kuptueshëm shqip.

  // KODI2 (1 shtator 2026) — KOD PERSONAL për një person të vetëm.
  //
  // Pse ekziston: kodi i familjes kërkon që personi ta gjejë VETEN në pemë me
  // emër. Kur nuk e gjente, shkruante një emër tjetër — dhe deri më sot kjo
  // krijonte një person pa asnjë lidhje, të padukshëm përgjithmonë. Ajo rrugë
  // u mbyll (`KODI1`); ky është zëvendësimi i saj.
  //
  // Me kodin personal nuk ka çfarë të kërkojë: kodi e di kush është. Personi
  // sheh «Ti je X te Familja Y» dhe konfirmon.
  //
  // Kufijtë rrinë te serveri, jo këtu: një përdorim, 7 ditë, refuzohet nëse
  // personi ka tashmë llogari, dhe i vjetri revokohet kur krijohet i riu.
  // Kjo funksion vetëm e kërkon kodin dhe e kopjon.
  //
  // Kopjohen të dyja — kodi dhe linku: linku për ata që klikojnë, kodi për ata
  // që preferojnë ta shkruajnë me dorë te ekrani i bashkimit.
  const kopjoKodinPersonal = async (personId) => {
    const { data, error } = await supabase.rpc("rpc_create_person_join_code", {
      p_person_id: personId,
      p_requester_id: currentPersonId,
    });
    // Mesazhet e serverit janë shqip dhe për njeriun (p.sh. «Ky person e ka
    // tashmë një llogari të lidhur…»), ndaj shfaqen ashtu siç vijnë.
    if (error) { showToast(error.message); return; }
    const rreshti = Array.isArray(data) ? data[0] : data;
    if (!rreshti || !rreshti.kodi) { showToast(t("members.personCodeFailed")); return; }

    // Kopjohet VETËM linku (pronari, 2 shtator 2026): kodi rri brenda tij, dhe
    // një rresht i dytë me kodin lakuriq ishte vetëm ngatërresë për atë që e
    // merr — s'e di ku ta shkruajë. Kodi mbetet i dukshëm te vetë profili.
    const url = lidhjePublike(`/join?code=${encodeURIComponent(rreshti.kodi)}`);
    // MESAZHI1 (13 shtator 2026) — kopjohet një mesazh i gatshëm për WhatsApp,
    // me linkun brenda: kush e merr vetëm klikon. Pronari: «admini s'ka pse ta
    // shpjegojë vetë ç'të bëjë personi».
    const mesazhi = t("members.personCodeMessage", { name: displayName(people[personId]), family: familyDisplayName || "", url });
    try {
      await navigator.clipboard.writeText(mesazhi);
      showToast(t("members.personCodeCopied", { code: rreshti.kodi }));
    } catch (e) {
      showToast(mesazhi);
    }
    fetchKodetPersonale();
  };
  // FAZA 5 — kod bashkimi për MYSAFIR (honorary). Ndryshe nga kodi i familjes
  // (6-shifror, i gjithë familjes), ky lidhet me NJË mysafir specifik, fillon me
  // "H-", dhe shfaq VETËM profilin e vet. Admini e gjeneron nga paneli i profilit
  // të mysafirit; linku kopjohet gati për t'u dërguar (WhatsApp/SMS/email).
  const generateHonoraryCode = async (personId) => {
    const { data: code, error } = await supabase.rpc("rpc_generate_honorary_code", {
      p_person_id: personId,
      p_requester_id: currentPersonId,
    });
    if (error) { showToast(t("toast.saveFailed", { error: error.message })); return; }
    const url = lidhjePublike(`/join?code=${encodeURIComponent(code)}`);
    try {
      await navigator.clipboard.writeText(url);
      showToast(t("Guest link copied — {{code}}", { code }));
    } catch (e) {
      showToast(url);
    }
  };
  // "Kodi i Familjes" — shih schema.sql. rpc_get_family_join_code kthen
  // rresht bosh (data=[]) nëse s'ka kod aktiv ende — normalizojmë në null.
  const fetchFamilyJoinCode = async () => {
    if (!canManage || !currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_get_family_join_code", { p_requester_id: currentPersonId });
    if (error) return;
    const row = Array.isArray(data) ? data[0] : data;
    setFamilyJoinCode(row || null);
  };
  // KODI7 — cilët persona kanë kod personal aktiv (pa vetë kodin). Pa derë
  // (SQL i paxhiruar) → hartë bosh, dhe rreshtat duken si më parë.
  const fetchKodetPersonale = async () => {
    if (!canManage || !currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_list_person_join_codes", { p_requester_id: currentPersonId });
    if (error) return;
    const m = {};
    (data || []).forEach((r) => { m[r.person_id] = r; });
    setKodetPersonale(m);
  };
  // LLOGARIA1 (12 shtator 2026) — cilët anëtarë kanë llogari të lidhur, dhe
  // shkëputja e saj nga admini i familjes. Rasti: dikush e ndërroi email-in ose
  // e humbi qasjen te i vjetri — llogaria e vjetër s'hapet më, personi mbetet
  // «i zënë». Admini e shkëput; personi hyn me llogari të re dhe merr veten me
  // kod personal (çelësi ngjitur) ose ftesë me email. Pa derë (SQL i
  // paxhiruar) → hartë bosh, rreshtat duken si më parë.
  const fetchLlogaritEAnetareve = async () => {
    if (!currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_list_family_account_links", { p_requester_id: currentPersonId });
    if (error) return;
    const m = {};
    (data || []).forEach((r) => { m[r.person_id] = r; });
    setLlogaritEAnetareve(m);
  };
  // 12 shtator 2026, mbrëmje — pronari: «kopjo kod personal» vetëm te të
  // palidhurit; te të lidhurit del trekëndëshi i kujdesit «Shkëput llogarinë»,
  // me konfirmim; pas shkëputjes shfaqet çelësi. Vlen edhe te kartela e
  // personit, për këdo që mund të dërgojë kod personal (kodPersonalILejuar).
  const shkeputLlogarine = async (personId) => {
    const l = llogaritEAnetareve[personId];
    const emri = displayName(people[personId]);
    if (!window.confirm(t("members.unlinkConfirm", { name: emri, email: l && l.email_masked ? ` (${l.email_masked})` : "" }))) return;
    const { error } = await supabase.rpc("rpc_unlink_person_account", { p_requester_id: currentPersonId, p_person_id: personId, p_reason: null });
    if (error) { showToast(error.message); return; }
    showToast(t("members.unlinkDone", { name: emri }));
    await Promise.all([fetchLlogaritEAnetareve(), fetchKodetPersonale(), fetchPeople()]);
  };
  const revokoKodinPersonal = async (personId) => {
    const { error } = await supabase.rpc("rpc_revoke_person_join_code", { p_person_id: personId, p_requester_id: currentPersonId });
    if (error) { showToast(error.message); return; }
    showToast(t("members.personCodeRevoked"));
    fetchKodetPersonale();
  };
  const fetchJoinCodeClaims = async () => {
    if (!canManage || !currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_list_join_code_claims", { p_requester_id: currentPersonId });
    if (!error) setJoinCodeClaims(data || []);
  };
  const generateFamilyJoinCode = async () => {
    setJoinCodeBusy(true);
    const { data, error } = await supabase.rpc("rpc_set_family_join_code", { p_requester_id: currentPersonId, p_expires_in_hours: joinCodeDurationHours });
    setJoinCodeBusy(false);
    if (error) { showToast(t("members.joinCodeFailed", { error: error.message })); return; }
    const row = Array.isArray(data) ? data[0] : data;
    setFamilyJoinCode({ code: row.code, expires_at: row.expires_at, use_count: 0, created_at: new Date().toISOString() });
    showToast(t("members.joinCodeGenerated"));
  };
  const revokeFamilyJoinCode = async () => {
    if (!window.confirm(t("members.joinCodeRevokeConfirm"))) return;
    setJoinCodeBusy(true);
    const { error } = await supabase.rpc("rpc_revoke_family_join_code", { p_requester_id: currentPersonId });
    setJoinCodeBusy(false);
    if (error) { showToast(t("members.joinCodeFailed", { error: error.message })); return; }
    setFamilyJoinCode(null);
    showToast(t("members.joinCodeRevoked"));
  };
  const copyJoinCodeLink = async () => {
    if (!familyJoinCode?.code) return;
    const url = lidhjePublike(`/join?code=${familyJoinCode.code}`);
    const mesazhi = t("members.joinCodeMessage", { family: familyDisplayName || "", url }); // MESAZHI1
    try {
      await navigator.clipboard.writeText(mesazhi);
      showToast(t("members.joinCodeLinkCopied"));
    } catch (e) {
      showToast(mesazhi);
    }
  };
  const markJoinCodeClaimsSeen = async () => {
    if (!currentPersonId || joinCodeClaims.every((c) => c.seen_at)) return;
    { const { error } = await supabase.rpc("rpc_mark_join_code_claims_seen", { p_requester_id: currentPersonId });
      if (error) console.error("rpc_mark_join_code_claims_seen:", error.message); }
    await fetchJoinCodeClaims();
  };
  const unseenJoinCodeClaimsCount = joinCodeClaims.filter((c) => !c.seen_at).length;

  // ROLI1 (13 shtator 2026) — roli vendoset vetë (mosha + fëmijët); admini e
  // ndryshon kur do dhe atëherë shënohet «me dorë». Ky buton e kthen te automatiku.
  const riktheRolinAutomatik = async (personId) => {
    const { data, error } = await supabase.rpc("rpc_roli_automatik_rikthe", { p_requester_id: currentPersonId, p_person_id: personId });
    if (error) { showToast(error.message); return; }
    await fetchRoles();
    showToast(t("members.roleAutoRestored", { role: t(`members.${data}`, { defaultValue: data }) }));
  };
  const setBlocked = async (personId, blocked, reason) => {
    const { error } = await supabase.rpc("rpc_set_blocked", { p_target_id: personId, p_blocked: blocked, p_reason: reason || null, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    await fetchPeople();
    showToast(blocked ? t("toast.personBlocked", { name: displayName(people[personId]) }) : t("toast.personUnblocked", { name: displayName(people[personId]) }));
  };
  // FAZA 34 — setAdminPasswordFor u hoq (fjalëkalimi shtesë i adminit s'ekziston më).
  const fetchContactFor = async (personId) => {
    if (!personId || (!canManage && personId !== currentPersonId)) { setSelectedContact(null); return; }
    try {
      const { data, error } = await supabase.rpc("rpc_get_contact", { p_person_id: personId, p_requester_id: currentPersonId });
      if (error) { setSelectedContact({ email: "", phone: "" }); return; }
      setSelectedContact((data && data[0]) || { email: "", phone: "" });
    } catch (e) {
      setSelectedContact({ email: "", phone: "" });
    }
  };
  // I AFËRM I NGUSHTË — pasqyrë e `fn_is_close_relative` te serveri: prind,
  // fëmijë, bashkëshort/e, ose motër/vëlla (të paktën një prind i përbashkët).
  // Serveri mbetet kufiri i vërtetë — `rpc_list_contacts_for_mourning` e
  // kontrollon vetë; kjo shërben vetëm që butoni të mos u shfaqet atyre që do
  // të merrnin refuzim.
  const isCloseRelativeOf = (viewerId, deceasedId) => {
    if (!viewerId || !deceasedId || viewerId === deceasedId) return false;
    const me = people[viewerId], dead = people[deceasedId];
    if (!me || !dead) return false;
    const myParents = me.parent_ids || [], deadParents = dead.parent_ids || [];
    if (myParents.includes(deceasedId)) return true;          // i ndjeri është prindi im
    if (deadParents.includes(viewerId)) return true;          // i ndjeri është fëmija im
    if ((me.spouse_ids || []).includes(deceasedId)) return true;
    return myParents.some((p) => deadParents.includes(p));    // motër/vëlla
  };
  const openMourning = async (deceasedPerson) => {
    setFuneralDate("");
    setFuneralPlace("");
    setMourningForId(deceasedPerson.id);
    const subject = t("Sad news — {{name}}", { name: displayName(deceasedPerson) });
    const body = t("With deep sadness we let you know that {{name}} is no longer with us.", { name: displayName(deceasedPerson) });
    if (canManage) {
      await openFamilyNotify({ subject, body, excludePersonId: deceasedPerson.id });
    } else {
      // Close relative, not admin — skip Posta Familjare entirely; they
      // shouldn't see the rest of the family's mail. Uses a separate RPC
      // that checks close-relative/editor-scope permission instead of
      // admin-only (rpc_list_all_contacts would reject them).
      const { data, error } = await supabase.rpc("rpc_list_contacts_for_mourning", { p_requester_id: currentPersonId, p_deceased_id: deceasedPerson.id });
      if (error) { showToast(t("toast.noPermissionToSend")); return; }
      const list = (data || []).filter((c) => c.email);
      setAllContacts(list);
      setNotifyPicked(new Set(list.map((c) => c.person_id)));
      setNotifyRecipientMode("all");
      setNotifySubject(subject);
      setNotifyBody(body);
      setSimpleMourningOpen(true);
    }
  };

  const fetchAnnouncements = async () => {
    if (!currentPersonId) return;
    // FAZA1h — `rpc_my_announcements` kthen edhe `kind` (risi / ndryshim /
    // njoftim), që lista të mos jetë tetë rreshta të njëjtë ku njeriu s'dallon
    // dot cili kërkon vendim dhe cili thjesht informon.
    const nu = await supabase.rpc("rpc_my_announcements", { p_person_id: currentPersonId });
    if (!nu.error) { setAnnouncements(nu.data || []); return; }
    // Derisa FAZA1h të jetë xhiruar, e vjetra punon njësoj — thjesht pa etiketa.
    const { data, error } = await supabase.rpc("rpc_get_active_announcements", { p_person_id: currentPersonId });
    if (!error) setAnnouncements(data || []);
  };
  const unreadAnnouncementCount = announcements.filter((a) => !a.is_read).length;
  const openAnnouncements = async () => {
    setAnnouncementsOpen(true);
    // QENDRA1 — hapet te Preferencat; vetëm kur zilja ka diçka të palexuar
    // shkon drejt te Lajmërimet, sepse atë e premton shenja te zilja.
    setNotificationsTab(unreadAnnouncementCount > 0 ? "platform" : "birthdays");
    setMenyraEZgjedhur(null);
    // mark everything currently active as read once the person opens the panel
    for (const a of announcements.filter((a) => !a.is_read)) {
      const { error } = await supabase.rpc("rpc_mark_announcement_read", { p_announcement_id: a.id, p_person_id: currentPersonId });
      if (error) { console.error("rpc_mark_announcement_read:", error.message); break; }
    }
    await fetchAnnouncements();
  };
  const fetchAllAnnouncements = async () => {
    if (!canManage) return;
    const { data, error } = await supabase.rpc("rpc_list_all_announcements", { p_requester_id: currentPersonId });
    if (!error) setAllAnnouncements(data || []);
  };
  const DURATION_PRESET_MS = { "1w": 7 * 86400000, "1m": 30 * 86400000, "6m": 182 * 86400000, "1y": 365 * 86400000 };
  const createAnnouncement = async () => {
    if (!announceForm.title.trim() || !announceForm.body.trim()) { showToast(t("toast.giveTitleAndBody")); return; }
    let endsAtIso = null;
    if (announceForm.durationPreset === "custom") {
      endsAtIso = announceForm.endsAt ? new Date(announceForm.endsAt).toISOString() : null;
    } else if (DURATION_PRESET_MS[announceForm.durationPreset]) {
      endsAtIso = new Date(Date.now() + DURATION_PRESET_MS[announceForm.durationPreset]).toISOString();
    } // "manual" (default) leaves endsAtIso as null — stays active until deleted by hand
    const { error } = await supabase.rpc("rpc_create_announcement", {
      p_title: announceForm.title.trim(),
      p_body: announceForm.body.trim(),
      p_starts_at: announceForm.startsAt ? new Date(announceForm.startsAt).toISOString() : null,
      p_ends_at: endsAtIso,
      p_target_group: announceForm.targetGroup,
      p_requester_id: currentPersonId,
    });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    const titleForPush = announceForm.title.trim();
    const bodyForPush = announceForm.body.trim();
    setAnnounceForm({ title: "", body: "", startsAt: "", endsAt: "", targetGroup: "all", durationPreset: "manual" });
    await fetchAllAnnouncements();
    await fetchAnnouncements();
    showToast(t("toast.announcementPublished"));
    if (pushEnabled) {
      try {
        const { data: pushData, error: pushErr } = await supabase.functions.invoke("send-push", {
          body: { title: titleForPush, body: bodyForPush, url: "/", requesterId: currentPersonId },
        });
        if (pushErr) { showToast(t("toast.pushCallFailed", { error: await extractFunctionErrorMessage(pushErr, pushData) })); }
        else if (pushData?.error) { showToast(t("toast.pushGenericError", { error: pushData.error })); }
        else if (pushData?.skipped) { showToast(t("toast.pushDisabledShort")); }
        else { showToast(t("toast.pushSentSummary", { sent: pushData?.sent ?? 0, total: pushData?.total ?? 0 })); }
      } catch (e) {
        showToast(t("toast.pushGenericError", { error: e?.message || String(e) }));
      }
    }
  };
  const deleteAnnouncement = async (id) => {
    const { error } = await supabase.rpc("rpc_delete_announcement", { p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    await fetchAllAnnouncements();
    await fetchAnnouncements();
  };

  const openFamilyNotify = async (prefill) => {
    const { data } = await supabase.rpc("rpc_list_all_contacts", { p_requester_id: currentPersonId });
    let list = (data || []).filter((c) => c.email);
    if (prefill?.excludePersonId) list = list.filter((c) => c.person_id !== prefill.excludePersonId);
    setAllContacts(list);
    setNotifyPicked(new Set(list.map((c) => c.person_id)));
    setNotifyRecipientMode("all");
    setNotifyBranchAnchorId(null);
    setNotifySubject(prefill?.subject || "");
    setNotifyBody(prefill?.body || "");
    setEmailRepliesOpen(true);
    setMailView("compose");
  };
  const notifyBranchRecipients = useMemo(() => {
    if (!notifyBranchAnchorId) return [];
    const branch = new Set([notifyBranchAnchorId]);
    const stack = [notifyBranchAnchorId];
    while (stack.length) {
      const id = stack.pop();
      (baseChildrenMap[id] || []).forEach((cid) => { if (!branch.has(cid)) { branch.add(cid); stack.push(cid); } });
    }
    return allContacts.filter((c) => branch.has(c.person_id));
  }, [notifyBranchAnchorId, baseChildrenMap, allContacts]);
  const [notifySending, setNotifySending] = useState(false);
  const getNotifyRecipientEmails = () => {
    const recipients =
      notifyRecipientMode === "all" ? allContacts :
      notifyRecipientMode === "branch" ? notifyBranchRecipients :
      allContacts.filter((c) => notifyPicked.has(c.person_id));
    return recipients.map((c) => c.email);
  };
  const sendFamilyNotifyViaDevice = () => {
    if (!notifySubject.trim()) { showToast(t("toast.writeASubject")); return; }
    if (!notifyBody.trim()) { showToast(t("toast.writeTheMessageBody")); return; }
    const emails = getNotifyRecipientEmails();
    if (emails.length === 0) { showToast(t("toast.chooseAtLeastOneRecipient")); return; }
    const adminName = displayName(people[currentPersonId] || {});
    const fullBody = `${notifySenderName}\n\n${notifyBody.trim()}\n\nMe respekt,\n${adminName}`;
    const subject = encodeURIComponent(notifySubject.trim());
    const body = encodeURIComponent(fullBody);
    window.location.href = `mailto:?bcc=${emails.join(",")}&subject=${subject}&body=${body}`;
    setMailView("list");
  };
  const sendFamilyNotify = async () => {
    if (!notifySubject.trim()) { showToast(t("toast.writeASubject")); return; }
    if (!notifyBody.trim()) { showToast(t("toast.writeTheMessageBody")); return; }
    const emails = getNotifyRecipientEmails();
    if (emails.length === 0) { showToast(t("toast.chooseAtLeastOneRecipient")); return; }
    setNotifySending(true);
    try {
      let fullBody = notifyBody.trim();
      if (funeralDate.trim() || funeralPlace.trim()) {
        fullBody += "\n\n" + (funeralPlace.trim()
          ? t("The funeral will be held on {{date}}, at {{place}}.", { date: funeralDate.trim() || "___", place: funeralPlace.trim() })
          : t("The funeral will be held on {{date}}.", { date: funeralDate.trim() || "___" }));
      }
      const { data, error } = await supabase.functions.invoke("send-notification", {
        body: {
          subject: notifySubject.trim(),
          body: fullBody,
          senderName: notifySenderName,
          recipientEmails: emails,
          requesterId: currentPersonId,
          mourningFor: mourningForId || undefined,
        },
      });
      if (error) { showToast(t("toast.sendFailed", { error: await extractFunctionErrorMessage(error, data) })); return; }
      if (data?.error) { showToast(t("toast.sendFailed", { error: data.error })); return; }
      const failedCount = data?.failed || 0;
      // `skipped` = marrës që e kanë FIKUR njoftimin me email. Serveri i heq
      // vetë, por heshtja do të ishte mashtruese: admini do të besonte se
      // njoftimi shkoi te të gjithë ata që zgjodhi.
      const skipped = data?.skipped || 0;
      const base = failedCount > 0
        ? t("toast.sentWithSomeFailed", { sent: data.sent, failed: failedCount, error: (data.firstError || "").slice(0, 150) })
        : t("toast.sentToCount", { count: data.sent });
      showToast(skipped > 0 ? `${base} · ${t("{{count}} did not want e-mail", { count: skipped })}` : base);
      clearMailDraft();
      setMourningForId(null);
      setFuneralDate("");
      setFuneralPlace("");
      setSimpleMourningOpen(false);
      if (canManage) {
        setMailView("list");
        setMailFolder("sent");
        await fetchSentEmails();
      }
    } catch (e) {
      showToast(t("toast.sendFailedTryAgain"));
    } finally {
      setNotifySending(false);
    }
  };

  // FAZA 24 — "Legacy". Ngarkon TË GJITHA mesazhet e zbuluara (persona
  // tashmë të ndjerë) të familjes njëherësh — RPC-ja vetë filtron sipas
  // family_id të kërkuesit, kështu s'ka nevojë filtrimi shtesë këtu përveç
  // sipas person_id kur shfaqet një profil specifik.
  const fetchUnlockedLegacy = async () => {
    if (!currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_list_unlocked_legacy_messages", { p_requester_id: currentPersonId });
    if (!error) setUnlockedLegacyMessages(data || []);
  };
  // Zbulimi me verifikim — statusi i personit të zgjedhur (drejton UI-në:
  // kërko / pret aprovim / kontrollet e kujdestarit).
  const fetchLegacyStatusFor = async (personId) => {
    if (!personId || !currentPersonId) { setSelectedLegacyStatus(null); setLegacyViewers([]); return; }
    const { data } = await supabase.rpc("rpc_legacy_unlock_status", { p_person_id: personId, p_requester_id: currentPersonId });
    const st = Array.isArray(data) ? data[0] : data;
    setSelectedLegacyStatus(st);
    if (st && st.am_i_unlocked_viewer) fetchLegacyViewers(personId); else setLegacyViewers([]);
  };
  const fetchLegacyViewers = async (personId) => {
    const { data } = await supabase.rpc("rpc_list_legacy_viewers", { p_person_id: personId, p_requester_id: currentPersonId });
    setLegacyViewers(data || []);
  };
  const addLegacyViewer = async (viewerId) => {
    if (!viewerId) return;
    const { error } = await supabase.rpc("rpc_add_legacy_viewer", { p_person_id: selectedId, p_viewer_person_id: viewerId, p_requester_id: currentPersonId });
    if (error) { showToast("Gabim: " + error.message); return; }
    fetchLegacyViewers(selectedId); fetchUnlockedLegacy();
  };
  const removeLegacyViewer = async (viewerId) => {
    const { error } = await supabase.rpc("rpc_remove_legacy_viewer", { p_person_id: selectedId, p_viewer_person_id: viewerId, p_requester_id: currentPersonId });
    if (error) { showToast("Gabim: " + error.message); return; }
    fetchLegacyViewers(selectedId); fetchUnlockedLegacy();
  };
  const submitLegacyRequest = async () => {
    if (!legacyReqFile) { showToast(t("Upload the death certificate.")); return; }
    setLegacyReqBusy(true);
    try {
      // `legacy-certificates` është bucket-i i dytë i familjes (shih
      // `pef_hapesira_bucket_at`), dhe skedarët e tij janë më të rëndët në
      // platformë — pra pikërisht ai që s'duhet të kalojë pa u pyetur.
      const _sh = await kontrolloHapesiren(legacyReqFile?.size || 0, false);
      if (_sh) { showToast(_sh); return; }
      const ext = (legacyReqFile.name.split(".").pop() || "bin").toLowerCase();
      const path = `${activeFamilyId}/${selectedId}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("legacy-certificates").upload(path, legacyReqFile, { contentType: legacyReqFile.type || "application/octet-stream" });
      if (upErr) { showToast(gabimiIHapesires(upErr) || ("Gabim ngarkimi: " + upErr.message)); return; }
      const { error } = await supabase.rpc("rpc_request_legacy_unlock", { p_person_id: selectedId, p_certificate_path: path, p_note: legacyReqNote || null, p_requester_id: currentPersonId });
      if (error) { showToast("Gabim: " + error.message); return; }
      setLegacyReqOpen(false); setLegacyReqNote(""); setLegacyReqFile(null);
      showToast(t("The request was sent — it is awaiting approval."));
      fetchLegacyStatusFor(selectedId);
    } finally { setLegacyReqBusy(false); }
  };
  const setLegacyVisibility = async (scope) => {
    const { error } = await supabase.rpc("rpc_set_legacy_visibility", { p_person_id: selectedId, p_scope: scope, p_requester_id: currentPersonId });
    if (error) { showToast("Gabim: " + error.message); return; }
    showToast(t("Visibility updated."));
    fetchLegacyStatusFor(selectedId); fetchUnlockedLegacy();
  };
  // FAZA 27 — "Archive". `fetchArchiveEnabled` thirret proaktivisht (jo vetëm
  // kur hapet modali) — që artikulli i menusë të dijë menjëherë a ta shfaqë
  // veten fare.
  const fetchArchiveEnabled = async () => {
    if (!currentPersonId) return;
    const { data } = await supabase.rpc("rpc_is_archive_enabled", { p_requester_id: currentPersonId });
    setArchiveEnabled(!!data);
  };
  const fetchArchiveItems = async () => {
    const { data, error } = await supabase.rpc("rpc_list_archive_items", { p_requester_id: currentPersonId });
    if (!error) setArchiveItems(data || []);
    // Hapësira lexohet bashkë me listën: janë e njëjta e vërtetë parë nga dy
    // anë, dhe po t'i ndaja do të kishte çaste kur matësi tregon një gjendje që
    // lista e mohon. Nëse ARKIVA2 s'është xhiruar ende, kjo derë s'ekziston →
    // `error` → matësi thjesht nuk shfaqet, dhe asgjë tjetër nuk prishet.
    if (activeFamilyId) {
      const { data: gj, error: gjErr } = await supabase.rpc("rpc_arkiva_gjendja", { p_family_id: activeFamilyId });
      setArkivaGjendja(gjErr ? null : (gj && typeof gj === "object" && "bajte" in gj ? gj : null));
    }
  };

  // Etiketimi: cilët persona janë te ky skedar. Zëvendëson listën e plotë (RPC-ja
  // e ka vendosjen, jo shtimin) — prandaj kutia niset e mbushur me atë që është.
  const hapEtiketat = (item) => {
    setEtiketaPer(item.id);
    setEtiketaZgjedhur(Array.isArray(item.person_ids) ? item.person_ids : []);
  };
  const ruajEtiketat = async () => {
    const id = etiketaPer;
    if (!id) return;
    const { error } = await supabase.rpc("rpc_vendos_personat_e_arkives", {
      p_item_id: id, p_person_ids: etiketaZgjedhur, p_requester_id: currentPersonId,
    });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    setEtiketaPer(null);
    await fetchArchiveItems();
  };
  // openArchive (i veçantë) u zëvendësua nga openMemories (poshtë) — Archive
  // tani është vetëm një skedë brenda ekranit "memories", jo ekran/artikull
  // navigimi i vetin.
  const guessArchiveFileType = (file) => {
    if (file.type.startsWith("image/")) return "photo";
    if (file.type.startsWith("video/")) return "video";
    // ARKIVA1 e shtoi «audio» te baza, por kjo funksion s'e njihte: çdo
    // regjistrim zëri hynte si «document» dhe filtri «Zëra» mbetej bosh
    // përgjithmonë. Një lloj i shtuar te serveri dhe i harruar te klienti është
    // veçori që ekziston vetëm te migrimi.
    if (file.type.startsWith("audio/")) return "audio";
    return "document";
  };
  const uploadArchiveFiles = async (files) => {
    setArchiveUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fileType = guessArchiveFileType(file);
        let blobToUpload = file;
        let ext = "jpg";
        if (fileType === "photo") {
          blobToUpload = await compressToBlobFit(file);
          ext = "jpg";
        } else {
          ext = (file.name.split(".").pop() || "bin").toLowerCase();
        }
        // HAPESIRA2 — pyetja PARA ngarkimit. Deri sot ky rresht e ngarkonte
        // skedarin dhe pastaj `rpc_add_archive_item` e refuzonte kur kuota
        // ishte plot: mesazhi vinte, por skedari mbetej te disku përgjithmonë.
        // Tani pyetet i pari, dhe nëse prapë refuzohet, fshihet (poshtë).
        const _sh = await kontrolloHapesiren(
          (blobToUpload && typeof blobToUpload.size === "number") ? blobToUpload.size : (file.size || 0),
          true);
        if (_sh) { showToast(_sh); continue; }
        const path = `${activeFamilyId}/archive-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("photos").upload(path, blobToUpload, { contentType: fileType === "photo" ? "image/jpeg" : file.type || "application/octet-stream" });
        if (upErr) { showToast(gabimiIHapesires(upErr) || t("toast.photoUploadFailed", { error: upErr.message })); continue; }
        // ARKIVA2 — madhësia e asaj që U NGARKUA, jo e asaj që u zgjodh. Fotot
        // shtypen para ngarkimit: një foto 6 MB nga telefoni bëhet rreth 400 KB,
        // dhe po të dërgoja 6 MB do t'i hante kuotës pesëmbëdhjetë herë më
        // shumë se ç'zë vërtet te disku.
        const madhesia = (blobToUpload && typeof blobToUpload.size === "number") ? blobToUpload.size : (file.size || 0);
        const { error: rpcErr } = await supabase.rpc("rpc_add_archive_item", {
          p_file_path: path, p_file_type: fileType, p_caption: archiveCaptionDraft, p_requester_id: currentPersonId,
          p_size_bytes: madhesia,
        });
        // Serveri i thotë të dyja arsyet me emër. Përkthehen këtu në fjali që
        // thonë çfarë të bësh — «arkiva_plot: 512.4 MB nga 500 MB» është e saktë
        // për mua dhe e pakuptueshme për këdo tjetër.
        if (rpcErr) {
          // SKEDARI I REFUZUAR NUK RRI TE DISKU (HAPESIRA2).
          //
          // Ngarkimi ndodh para se dera të pyetet — kështu e do rrjedha, sepse
          // dera e lexon madhësinë e vërtetë nga disku. Por kur ajo refuzon,
          // skedari mbetej aty: i padukshëm te lista, i pafshirë, dhe — që kur
          // hapësira matet nga disku — i numëruar kundër vetë familjes. Një
          // ngarkim i dështuar nuk guxon ta zvogëlojë kuotën e askujt.
          supabase.storage.from("photos").remove([path]).catch(() => {});
          const m = rpcErr.message || "";
          if (m.includes("arkiva_skedar_madh")) {
            showToast(t("«{{file}}» is larger than a single file may be. Split it or send a smaller version.", { file: file.name }));
          } else if (m.includes("arkiva_plot")) {
            showToast(t("space.full"));
          } else {
            showToast(gabimiIHapesires(rpcErr) || t("toast.failed", { error: rpcErr.message }));
          }
        }
      }
      setArchiveCaptionDraft("");
      await fetchArchiveItems();
    } finally {
      setArchiveUploading(false);
    }
  };
  const deleteArchiveItem = async (id) => {
    const { error } = await supabase.rpc("rpc_delete_archive_item", { p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    await fetchArchiveItems();
  };

  const openMyLegacy = async (personId) => {
    // KYÇJA (TRASH3). Kur mesazhi është vënë në shitje dhe ky person s'e ka
    // blerë, kliku nuk hap një kuti bosh që pastaj refuzohet nga baza — të çon
    // aty ku blihet. Një derë e mbyllur duhet të tregojë ku është çelësi.
    const { data: tierData } = await supabase.rpc("rpc_legacy_my_tier", { p_person_id: personId });
    const tier = Array.isArray(tierData) ? tierData[0] : tierData;
    setLegacyTier(tier || null);
    if (tier && tier.i_hapur === false) {
      setActiveScreen("services");
      showToast(t("The legacy message is bought first — you'll find it under Services."));
      return;
    }
    setMyLegacyForPersonId(personId);
    setLegacyForm({ id: null, title: "", messageText: "" });
    setMyLegacyOpen(true);
    const { data } = await supabase.rpc("rpc_list_my_legacy_messages", { p_person_id: personId, p_requester_id: currentPersonId });
    setMyLegacyMessages(data || []);
  };

  // Një thirrje e vetme për "çfarë ka dhe çfarë shitet". Nëse RPC-ja s'ekziston
  // ende (TRASH2 e pa xhiruar), ekrani sillet saktësisht si dje — pa gabim, pa
  // ofertë, vetëm teksti.
  // Niveli lexohet kur hapet paneli i personit, jo vetëm kur klikohet —
  // përndryshe dryni do të shfaqej pasi njeriu ka klikuar tashmë, dhe do të
  // dukej si gabim e jo si gjendje.
  useEffect(() => {
    if (!selectedId || !people[selectedId] || people[selectedId].death_date) { setLegacyTier(null); return; }
    loadLegacyTier(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const loadLegacyTier = async (personId) => {
    if (!personId) { setLegacyTier(null); return; }
    const { data, error } = await supabase.rpc("rpc_legacy_my_tier", { p_person_id: personId });
    if (error) { setLegacyTier(null); return; }
    setLegacyTier(Array.isArray(data) ? data[0] : data);
  };

  // Ngarkimi shkon te Storage; te baza shkon vetëm RRUGA. Një video brenda
  // tabelës do të frynte çdo kopje rezervë dhe çdo eksport.
  const attachLegacyMedia = async (messageId, kind, file) => {
    if (!file || !messageId) return;
    setLegacyMediaBusy(true);
    try {
      const ext = (file.name.split(".").pop() || "bin").toLowerCase();
      const path = `${myLegacyForPersonId}/${messageId}.${ext}`;
      const up = await supabase.storage.from("legacy-media")
        .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: true });
      if (up.error) { showToast(up.error.message); return; }
      const { error } = await supabase.rpc("rpc_legacy_media_set", {
        p_message_id: messageId, p_kind: kind, p_path: path, p_bytes: file.size,
      });
      if (error) { showToast(error.message); return; }
      const { data } = await supabase.rpc("rpc_list_my_legacy_messages", { p_person_id: myLegacyForPersonId, p_requester_id: currentPersonId });
      setMyLegacyMessages(data || []);
      showToast(t("Attached."));
    } finally { setLegacyMediaBusy(false); }
  };

  const removeLegacyMedia = async (messageId) => {
    const { error } = await supabase.rpc("rpc_legacy_media_set", {
      p_message_id: messageId, p_kind: "asnje", p_path: null, p_bytes: null,
    });
    if (error) { showToast(error.message); return; }
    const { data } = await supabase.rpc("rpc_list_my_legacy_messages", { p_person_id: myLegacyForPersonId, p_requester_id: currentPersonId });
    setMyLegacyMessages(data || []);
  };
  // SHTESË (kërkesë e userit — "trashëgimi e kontrolluar"): ruajtja (edhe
  // krijimi i ri) dhe fshirja kalojnë GJITHMONË përmes një konfirmimi, me
  // checkbox "apliko edhe në familjet e tjera ku jam anëtar" (kur llogaria
  // është e lidhur me >1 familje). Kërkon LEGACY_multi_family.sql (p_apply_all).
  const [legacyConfirm, setLegacyConfirm] = useState(null); // {action:'save'|'delete', id?, applyAll}
  const saveLegacyMessage = async (applyAll = false) => {
    if (!legacyForm.messageText.trim()) { showToast(t("toast.writeATitle")); return; }
    setLegacySaving(true);
    try {
      const { error } = await supabase.rpc("rpc_save_legacy_message", {
        p_person_id: myLegacyForPersonId, p_title: legacyForm.title, p_message_text: legacyForm.messageText,
        p_requester_id: currentPersonId, p_id: legacyForm.id, p_apply_all: applyAll,
      });
      if (error) { showToast(t("toast.failed", { error: error.message })); return; }
      setLegacyForm({ id: null, title: "", messageText: "" });
      const { data } = await supabase.rpc("rpc_list_my_legacy_messages", { p_person_id: myLegacyForPersonId, p_requester_id: currentPersonId });
      setMyLegacyMessages(data || []);
      showToast(t("toast.savedPeriod"));
    } finally {
      setLegacySaving(false);
    }
  };
  const deleteLegacyMessage = async (id, applyAll = false) => {
    const { error } = await supabase.rpc("rpc_delete_legacy_message", { p_id: id, p_requester_id: currentPersonId, p_apply_all: applyAll });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    const { data } = await supabase.rpc("rpc_list_my_legacy_messages", { p_person_id: myLegacyForPersonId, p_requester_id: currentPersonId });
    setMyLegacyMessages(data || []);
  };
  const confirmLegacyAction = async () => {
    const c = legacyConfirm;
    if (!c) return;
    setLegacyConfirm(null);
    if (c.action === "delete") await deleteLegacyMessage(c.id, c.applyAll);
    else await saveLegacyMessage(c.applyAll);
  };

  // FIX (RRJEDHJE MES FAMILJESH) — i njëjti defekt: pa filtër eksplicit,
  // dikush i lidhur me disa familje merrte "Kujtimet" e TË GJITHA familjeve
  // të veta, të përziera (arsyeja e vërtetë pse "Feed" tregonte risi nga
  // familja Gashi ndërkohë që shikohej Krasniqi).
  const fetchFamilyHistory = async () => {
    // HISTORIA2 — `.is("deleted_at", null)` si te `fetchPeople`. Fshirja tani
    // është e butë: pa këtë filtër, ngjarja e fshirë do të vazhdonte të dukej
    // sikur asgjë s'kishte ndodhur.
    const { data, error } = await supabase.from("family_history_events").select("*").eq("family_id", activeFamilyId).is("deleted_at", null).order("event_date", { ascending: true, nullsFirst: false });
    if (!error) setFamilyHistoryEvents(data || []);
  };
  // openFamilyHistory (i veçantë) u zëvendësua nga openMemories (poshtë) —
  // "Ngjarje" tani është vetëm skeda parazgjedhje e ekranit "memories".
  // BASHKIM — hyrja e VETME nga navigimi tani ("Kujtime"). Vendos ekranin
  // "memories" (jo më "archive"/"history" të ndara) dhe zgjedh skedën fillestare
  // (parazgjedhje "events" — Ngjarje). Ngarkon TË DYJA burimet e të dhënave
  // paraprakisht, që kalimi mes skedave brenda ekranit të jetë i menjëhershëm,
  // pa pritje shtesë rrjeti.
  const openMemories = async (initialTab = "events") => {
    setActiveScreen("memories");
    setMemoriesTab(initialTab);
    setFamilyHistoryForm({ id: null, eventDate: sotSiTekst(), title: "", description: "", saktesia: "dite" });
    setFamilyHistoryPhotoFiles([]);
    setFamilyHistoryPhotoPreviews([]);
    await fetchFamilyHistory();
    if (archiveEnabled) await fetchArchiveItems();
  };
  const editFamilyHistoryEvent = (ev) => {
    // Redaktimi hap TË NJËJTËN faqe si shtimi. Një formular i dytë për të
    // njëjtat fusha do të thoshte dy të vërteta për një ngjarje.
    setShtoHistoriOpen(true);
    setFamilyHistoryForm({ id: ev.id, eventDate: ev.event_date || "", title: ev.title, description: ev.description || "", saktesia: ev.date_precision || "dite" });
    setFamilyHistoryPhotoFiles([]);
    // FAZA 26 — bashkon photo_path (i vjetër, njëjës) + photo_paths (array i
    // ri) si lista fillestare e paraqitjeve — album, jo 1 foto e vetme.
    const existingPaths = [...(ev.photo_path ? [ev.photo_path] : []), ...(ev.photo_paths || [])];
    setFamilyHistoryPhotoPreviews(existingPaths.map((p) => ({ kind: "existing", path: p, url: photoUrl(p) })));
  };
  const onFamilyHistoryPhotoChange = (files) => {
    const fileArray = Array.from(files || []);
    setFamilyHistoryPhotoFiles((prev) => [...prev, ...fileArray]);
    fileArray.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setFamilyHistoryPhotoPreviews((prev) => [...prev, { kind: "new", url: reader.result }]);
      reader.readAsDataURL(file);
    });
  };
  const removeFamilyHistoryPreview = (index) => {
    setFamilyHistoryPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
    // Shënim: nëse ishte foto "e re" (jo ende e ngarkuar), s'e heqim nga
    // familyHistoryPhotoFiles individualisht këtu — thjeshtësi e qëllimshme
    // për v1 (albumi rrallë ka shumë foto njëherësh); ngarkimi filtron vetë
    // sipas previews aktuale te saveFamilyHistoryEvent më poshtë.
  };
  const saveFamilyHistoryEvent = async () => {
    // POSTIMI I SHPEJTË. Pronari e do një derë të vetme, por një postim i
    // shpejtë me dy fusha të detyrueshme nuk është i shpejtë. Nëse titulli
    // mungon dhe rrëfimi ka tekst, titulli merret nga rreshti i parë — pra
    // shkruan një gjë dhe ruan. Rregulli te baza (`title not null`) mbetet.
    const titulliAuto = (familyHistoryForm.title || "").trim()
      || (familyHistoryForm.description || "").trim().split("\n")[0].slice(0, 70);
    if (!titulliAuto) { showToast(t("toast.writeATitle")); return; }
    if (!(familyHistoryForm.eventDate || "").trim()) { showToast(t("Write when it happened — the date is what gives it a place in History.")); return; }
    setFamilyHistorySaving(true);
    try {
      // Ngarko VETËM foto TË REJA (kind: "new") — ato ekzistuese (kind:
      // "existing") tashmë kanë path në storage, thjesht i ripërdorim.
      const existingPaths = familyHistoryPhotoPreviews.filter((p) => p.kind === "existing").map((p) => p.path);
      const newFiles = familyHistoryPhotoFiles;
      const uploadedPaths = [];
      for (const file of newFiles) {
        const blob = await compressToBlobFit(file);
        // Pyetja bëhet për ÇDO skedar, jo një herë për grupin: pesë foto mund
        // ta kalojnë kufirin bashkë edhe kur secila veç e veç nxë.
        const _sh = await kontrolloHapesiren(blob?.size || 0, false);
        if (_sh) { showToast(_sh); break; }
        const path = `${activeFamilyId}/history-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
        const { error: upErr } = await supabase.storage.from("photos").upload(path, blob, { contentType: "image/jpeg" });
        if (upErr) { showToast(gabimiIHapesires(upErr) || t("toast.photoUploadFailed", { error: upErr.message })); }
        else uploadedPaths.push(path);
      }
      const allPaths = [...existingPaths, ...uploadedPaths];
      if (familyHistoryForm.id) {
        const { error } = await supabase.rpc("rpc_update_family_history_event", {
          p_id: familyHistoryForm.id, p_event_date: familyHistoryForm.eventDate, p_title: titulliAuto,
          p_description: familyHistoryForm.description, p_photo_path: null, p_requester_id: currentPersonId, p_photo_paths: allPaths,
          p_date_precision: familyHistoryForm.saktesia || "dite",
        });
        if (error) { showToast((error.message || "").includes("data_mungon") ? t("Write when it happened — the date is what gives it a place in History.") : t("toast.failed", { error: error.message })); return; }
      } else {
        const { error } = await supabase.rpc("rpc_add_family_history_event", {
          p_event_date: familyHistoryForm.eventDate, p_title: titulliAuto,
          p_description: familyHistoryForm.description, p_photo_path: null, p_requester_id: currentPersonId, p_photo_paths: allPaths,
          p_date_precision: familyHistoryForm.saktesia || "dite",
        });
        if (error) { showToast((error.message || "").includes("data_mungon") ? t("Write when it happened — the date is what gives it a place in History.") : t("toast.failed", { error: error.message })); return; }
      }
      setFamilyHistoryForm({ id: null, eventDate: sotSiTekst(), title: "", description: "", saktesia: "dite" });
      setFamilyHistoryPhotoFiles([]);
      setFamilyHistoryPhotoPreviews([]);
      setShtoHistoriOpen(false);
      await fetchFamilyHistory();
      showToast(t("toast.savedPeriod"));
    } finally {
      setFamilyHistorySaving(false);
    }
  };
  // HISTORIA2 — PYET PARA, DHE THUAJ KU SHKOI.
  //
  // Deri sot ikona 🗑️ e thërriste këtë funksion drejtpërdrejt, dhe funksioni
  // bënte një `delete` të vërtetë. Pra një prekje e gabuar te telefoni fshinte
  // përgjithmonë një rrëfim që mund të ishte shkruar një herë të vetme, nga
  // dikush që s'jeton më.
  //
  // Konfirmimi e përmend TITULLIN. «A je i sigurt?» nuk është pyetje: askush
  // nuk e di se cilën ngjarje po fshin nëse emri i saj nuk shkruhet aty.
  const deleteFamilyHistoryEvent = async (ev) => {
    const id = typeof ev === "string" ? ev : ev?.id;
    if (!id) return;
    const titulli = (typeof ev === "object" && ev?.title) ? ev.title : "";
    const pyetja = titulli
      ? t("Delete “{{title}}”? It goes to the Trash and an admin can bring it back.", { title: titulli })
      : t("Delete this event? It goes to the Trash and an admin can bring it back.");
    if (!window.confirm(pyetja)) return;

    const { error } = await supabase.rpc("rpc_delete_family_history_event", { p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    await fetchFamilyHistory();
    showToast(t("Moved to the Trash — you can restore it from the admin menu → Trash."));
  };

  // KOSHI I HISTORISË — po ai mekanizëm si te personat, prandaj po ata emra.
  const [deletedHistory, setDeletedHistory] = useState([]);
  const fetchDeletedHistory = async () => {
    if (!currentPersonId) return;
    const { data, error } = await supabase.rpc("rpc_list_deleted_history", { p_requester_id: currentPersonId });
    // Pa HISTORIA2 dera s'ekziston — dhe atëherë Koshi thjesht nuk e ka atë
    // seksion, në vend që të shfaqë një gabim që s'i thotë asgjë askujt.
    if (error) { setDeletedHistory([]); return; }
    setDeletedHistory(Array.isArray(data) ? data : []);
  };
  const restoreHistoryEvent = async (id) => {
    const e = deletedHistory.find((x) => x.id === id);
    if (!window.confirm(t("Bring “{{title}}” back to the history?", { title: e?.title || "—" }))) return;
    const { error } = await supabase.rpc("rpc_restore_history_event", { p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("Restoring failed: ") + error.message); return; }
    await Promise.all([fetchDeletedHistory(), fetchFamilyHistory()]);
    showToast(t("The event was restored to the history."));
  };

  // REGJISTRI4 — kufiri zbatohej PARA filtrit: serveri kthente 150 rreshtat e
  // fundit të çfarëdo lloji, dhe ekrani i hidhte poshtë hyrjet. Me 140 hyrje
  // mes tyre, te «Ndryshimet» mbeteshin 10 — prandaj regjistri dukej sikur
  // «nuk ruhet gjatë». Tani hyrjet hiqen te baza, ndaj kufiri numëron
  // NDRYSHIME. `logLimit` rritet me butonin «Shfaq më shumë».
  const [logLimit, setLogLimit] = useState(300);
  const fetchActivityLog = async (limit) => {
    if (!canManage) return;
    const n = limit || logLimit;
    const { data, error } = await supabase.rpc("rpc_get_activity_log", {
      p_requester_id: currentPersonId,
      p_limit: n,
      p_exclude_actions: ["login"], // hyrjet kanë skedën e vet («Identifikimet»)
    });
    if (!error) setActivityLog(data || []);
  };
  const [loginActivity, setLoginActivity] = useState([]);
  const [logTab, setLogTab] = useState("changes");
  const [logSearch, setLogSearch] = useState("");
  const [checksOpen, setChecksOpen] = useState(() => new Set()); // seksionet e palosshme te "Checks"
  const [loginFilter, setLoginFilter] = useState("all");
  const fetchLoginActivity = async () => {
    if (!canManage) return;
    const { data, error } = await supabase.rpc("rpc_get_login_activity", { p_requester_id: currentPersonId });
    if (!error) setLoginActivity(data || []);
  };
  // `fetchTermsAcceptanceList()` u hoq nga këtu (19 gusht 2026): e mbushte
  // `termsAcceptanceList`, të cilën asnjë JSX s'e lexonte — pra çdo hapje e
  // regjistrit bënte një thirrje RPC vetëm për ta hedhur rezultatin.
  useEffect(() => { if (logPanelOpen) { fetchActivityLog(); fetchLoginActivity(); } }, [logPanelOpen]);

  const saveUnregisteredNote = async () => {
    if (!canManage || !selectedId) return;
    const { error } = await supabase.rpc("rpc_set_unregistered_note", { p_person_id: selectedId, p_note: noteDraft.trim(), p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.noteSaveFailed", { error: error.message })); return; }
    await fetchPeople();
    setNoteEditing(false);
    showToast(t("toast.noteSaved"));
  };

  const fetchMySubscriptions = async () => {
    const pid = currentPersonId;
    subsPersonRef.current = pid;
    if (!pid) { setMySubscriptions([]); setSubsGati(false); return; }
    const { data, error } = await supabase.rpc("rpc_list_my_subscriptions", { p_requester_id: pid });
    if (subsPersonRef.current !== pid) return;   // personi ndërkohë ndryshoi
    // SIGURIA2 — një gabim NUK është «s'ka abonime»: më parë `data` i padefinuar
    // e boshatiste listën, dhe planifikuesi e lexonte si «zgjedhja u fshi».
    if (error) {
      if (eshteGabimRrjeti(error)) {
        const c = await cacheLexo(`subs:${pid}`);
        if (Array.isArray(c) && subsPersonRef.current === pid) { setMySubscriptions(c); setSubsGati(true); }
      } else {
        console.warn("fetchMySubscriptions:", error.message || error);
      }
      return;
    }
    setMySubscriptions(data || []);
    cacheRuaj(`subs:${pid}`, data || []);
    setSubsGati(true);
  };
  const addToSubBasket = (personId, watchType) => {
    if (!personId) return;
    setSubBasket((prev) => {
      if (prev.some((it) => it.personId === personId)) return prev; // already added
      return [...prev, { personId, watchType }];
    });
  };
  const removeFromSubBasket = (personId) => setSubBasket((prev) => prev.filter((it) => it.personId !== personId));
  // NJOFTIME3 (3 nëntor 2026) — «Aktivizo» s'bënte asgjë brenda aplikacionit.
  // Butoni kërkonte kanal push (Web Push, që te WebView-i nuk ekziston) ose
  // email; prandaj në telefon rrinte gjithmonë i çaktivizuar, dhe lista «për
  // kë» — që e lexon vetë filtri i njoftimeve të telefonit — s'mbushej dot.
  // Tani:
  //   · në app: kanali 'app' — pa push, pa email; rreshti ruhet te serveri
  //     për personin, pra vlen për çdo pajisje ku hyn ai;
  //   · në shfletues: mbetet push/email, por butoni nuk çaktivizohet më —
  //     nëse push-i s'është ndezur, ndizet këtu, jo te një skedë tjetër.
  const kanaliIApp = Capacitor.isNativePlatform();
  // QENDRA1 — një çelës i vetëm për «njoftime në këtë pajisje». Te shfletuesi
  // ai ndez/fik edhe push-in e shfletuesit (lajmërimet e platformës), që më
  // parë kishte buton të vetin te skeda «Platforma». Në app push-i s'ekziston,
  // ndaj çelësi prek vetëm kujtesat lokale.
  // NJOFTIME5 — ndërrimi i mënyrës «nga kush». «dega» = asgjë e ruajtur;
  // «familja» = një rresht i vetëm; «persona» = lista me «Aktivizo».
  const menyraEfektive = menyraEZgjedhur || njoftimetNgaKush.menyra;
  const vendosMenyren = async (m) => {
    setMenyraEZgjedhur(m);
    if (!currentPersonId) return;
    const rreshtat = mySubscriptions || [];
    const hiq = async (lista) => {
      for (const r of lista) {
        const { error } = await supabase.rpc("rpc_remove_subscription", { p_id: r.id, p_requester_id: currentPersonId });
        if (error) { showToast(t("toast.failed", { error: error.message })); return false; }
      }
      return true;
    };
    if (m === "persona") {
      if (!(await hiq(rreshtat.filter((x) => x.watch_type === "family")))) return;
    } else {
      const perTuHequr = rreshtat.filter((x) => (m === "familja" ? x.watch_type !== "family" : true));
      if (!(await hiq(perTuHequr))) return;
      if (m === "familja" && !rreshtat.some((x) => x.watch_type === "family")) {
        const { error } = await supabase.rpc("rpc_add_subscription", {
          p_requester_id: currentPersonId, p_channel: "app", p_contact: null, p_watch_type: "family", p_watch_person_id: currentPersonId,
        });
        if (error) { showToast(t("toast.failed", { error: error.message })); return; }
      }
      if (perTuHequr.some((x) => x.watch_type !== "family")) showToast(t("The selected list was cleared."));
    }
    setSubBasket([]);
    await fetchMySubscriptions();
  };
  const ndryshoNjoftimetEPajisjes = async (v) => {
    ruajNjoftimetPref({ ...njoftimetPref, ndezur: v });
    if (kanaliIApp || !pushEnabled || !pushIMundshem) return;
    if (v && !myPushSubscribed) await enablePushForMe();
    else if (!v && myPushSubscribed) await disablePushForMe();
  };
  const submitSubBasket = async () => {
    if (subBasket.length === 0) { showToast(t("toast.addAtLeastOnePerson")); return; }
    let kanali = kanaliIApp ? "app" : subForm.channel;
    if (kanali === "email" && !subForm.contact.trim()) { showToast(t("toast.enterEmail")); return; }
    if (kanali === "push" && !myPushSubscribed) {
      await enablePushForMe();
      // `enablePushForMe` e vendos gjendjen vetë; e rilexojmë nga shfletuesi,
      // që të mos varemi nga një render i ndërmjetëm.
      let uAbonua = false;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        uAbonua = !!(reg && (await reg.pushManager.getSubscription()));
      } catch (_e) { uAbonua = false; }
      if (!uAbonua) return; // toast-in e dha enablePushForMe
    }
    let failed = 0, firstError = "";
    for (const item of subBasket) {
      const { error } = await supabase.rpc("rpc_add_subscription", {
        p_requester_id: currentPersonId, p_channel: kanali, p_contact: kanali === "email" ? subForm.contact.trim() : null, p_watch_type: item.watchType, p_watch_person_id: item.personId,
      });
      if (error) { failed++; if (!firstError) firstError = error.message; }
    }
    setSubBasket([]);
    await fetchMySubscriptions();
    showToast(failed > 0 ? t("toast.someActivatedSomeFailed", { failed, error: firstError.slice(0, 150) }) : t("toast.allActivated"));
  };
  const removeSubscription = async (id) => {
    // Çregjistrimi që dështon në heshtje është më i keq se sa duket: personi
    // beson se s'do të marrë më njoftime, dhe vazhdon t'i marrë. Kjo lexohet
    // si mospërfillje, jo si defekt.
    const { error } = await supabase.rpc("rpc_remove_subscription", { p_id: id, p_requester_id: currentPersonId });
    if (error) { showToast(t("toast.failed", { error: error.message })); return; }
    await fetchMySubscriptions();
  };
  // SIGURIA2 (L2) — lista ngarkohet sapo njihet personi, jo vetëm kur hapet paneli.
  useEffect(() => {
    setSubsGati(false);
    setMySubscriptions([]);
    fetchMySubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPersonId]);
  useEffect(() => { if (announcementsOpen && notificationsTab === "birthdays") fetchMySubscriptions(); }, [announcementsOpen, notificationsTab, currentPersonId]);
  // FAZA 24 — ngarkon mesazhet Legacy të zbuluara (persona të ndjerë) një
  // herë, sapo të njihet personi aktual — jo për çdo klikim profili.
  useEffect(() => { if (currentPersonId) fetchUnlockedLegacy(); }, [currentPersonId]);
  // Statusi i trashëgimisë për personin e zgjedhur (vetëm nëse i ndjerë).
  useEffect(() => {
    if (selectedId && currentPersonId && people[selectedId] && people[selectedId].death_date) fetchLegacyStatusFor(selectedId);
    else setSelectedLegacyStatus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, currentPersonId]);
  // FAZA 25 — "Family Feed". `fetchFamilyHistory` më parë thirrej VETËM kur
  // hapej modali "Historia e Familjes" — tani e thërrasim edhe proaktivisht
  // këtu, sepse Feed-i e ka nevojë menjëherë, jo vetëm pas hapjes së atij
  // modali specifik.
  useEffect(() => { if (currentPersonId) fetchFamilyHistory(); }, [currentPersonId]);
  useEffect(() => { if (currentPersonId) fetchArchiveEnabled(); }, [currentPersonId]);
  // FAZA 30 — dashboard-i i desktopit ka nevojë për numër real fotosh; pa
  // këtë, archiveItems mbetej bosh derisa dikush hapte vetë modalin Archive.
  useEffect(() => { if (archiveEnabled) fetchArchiveItems(); }, [archiveEnabled]);

  // FAZA 35 — persistTreeName u hoq (s'ka më redaktim inline të titullit nga header-i).
  // FAZA 37 (rishikuar) — funksionet openFamilyIdentity/saveFamilyIdentity u
  // hoqën nga këtu; emri+slug tani editohen nga Admin Center (shih AdminApp.jsx),
  // jo nga admini i vetë familjes — slug është shumë i rrezikshëm ta lësh në
  // duart e adminit të familjes (thyen linqe pa vetëdije të plotë të pasojave).
  // Një derë e vetme për të tri zgjedhjet e pamjes. Më parë secili buton e
  // thërriste funksionin drejt dhe e shpërfillte përgjigjen — pra një ruajtje e
  // dështuar dukej krejt si një e suksesshme, derisa hyje herën tjetër.
  const applyAppearance = async (fn) => {
    const res = await fn();
    if (res && res.error) showToast(t("The appearance could not be saved: ") + (res.error.message || res.error));
  };

  const persistTheme = async (key) => {
    setThemeKey(key);
    setThemePanelOpen(false);
    if (canManage) {
      // 20 gusht 2026 — përgjigjja e RPC-së nuk kontrollohej fare: nëse ruajtja
      // dështonte (leje, `p_requester_id` bosh, rrjet), tema ndryshonte në ekran
      // dhe njoftimi thoshte "u ndryshua për të gjithë" — derisa e rifreskoje
      // faqen dhe kthehej te e vjetra, pa asnjë shpjegim. Pikërisht ankesa
      // "ndryshimet tek pamja nuk janë ruajtur".
      const { error } = await supabase.rpc("rpc_update_settings", { p_tree_name: null, p_theme_key: key, p_requester_id: currentPersonId });
      if (error) {
        setThemeKey(themeKey);   // kthehu te ajo që është vërtet e ruajtur
        showToast(t("The theme could not be saved: ") + (error.message || error));
        return;
      }
      // Edhe admini e vizaton kornizën e parë nga pajisja e vet, jo nga rrjeti.
      try { localStorage.setItem("pef.theme.last", key); } catch (_e) { /* asgjë */ }
      showToast(t("toast.themeChangedForAll", { theme: THEMES[key].name }));
    } else {
      // Më parë kjo degë vetëm e ndryshonte gjendjen në kujtesë, kurse mesazhi
      // thoshte "u ndryshua për ty" — pra premtonte ruajtje që s'ndodhte kurrë:
      // zgjedhja humbte me rifreskimin e faqes. Tani ruhet vërtet, në pajisje,
      // për këtë familje veç e veç. Tema e familjes s'preket për të tjerët.
      try {
        localStorage.setItem(`pef.theme.${activeFamilyId || "default"}`, key);
        // Kopje pa familje: te render-i i parë familja aktive ende s'dihet, dhe
        // pikërisht atëherë duhet ngjyra e duhur.
        localStorage.setItem("pef.theme.last", key);
      } catch (_e) { /* modaliteti privat */ }
      showToast(t("toast.themeChangedForYou"));
    }
  };

  // ---------- pan / zoom ----------
  const onWheel = (e) => { e.preventDefault(); userAdjustedViewRef.current = true; const delta = e.deltaY > 0 ? -0.08 : 0.08; setScale((s) => Math.min(2.2, Math.max(0.3, s + delta))); };
  const dragMoved = useRef(false);
  const startDrag = (x, y) => { dragging.current = { startX: x, startY: y, panX: pan.x, panY: pan.y }; dragMoved.current = false; };
  const moveDrag = (x, y) => {
    if (!dragging.current) return;
    const dx = x - dragging.current.startX, dy = y - dragging.current.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) { dragMoved.current = true; userAdjustedViewRef.current = true; }
    setPan({ x: dragging.current.panX + dx, y: dragging.current.panY + dy });
  };
  const endDrag = () => (dragging.current = null);

  // FIX (kërkesë e userit) — parametri opsional `centerId`: nëse jepet,
  // qendërzohet mbi ATË person specifikisht (pozicioni i tij bëhet mesi i
  // ekranit), jo mbi qendrën gjeometrike të KREJT pemës. Përndryshe dikush
  // shumë breza larg rrënjës (p.sh. brezi 7-8, kur rrënja/paraardhësi i
  // parë është gjithmonë brezi 1 lart fare) hapej jashtë ekranit — poshtë
  // fare — edhe pse "krejt pema" ishte teknikisht e dukshme e shkallëzuar.
  // Shkalla (zoom) mbetet e njëjta si më parë (llogaritet nga madhësia e
  // krejt pemës, që të përshtatet mirë në ekran) — ndryshon vetëm ku
  // qendërzohet.
  const fitView = (centerId) => {
    userAdjustedViewRef.current = false;
    const ids = Object.keys(positions);
    if (!ids.length || !containerRef.current) { setScale(0.9); setPan({ x: 60, y: 60 }); return; }
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width < 100 || rect.height < 100) { setScale(0.9); setPan({ x: 60, y: 60 }); return; }
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    ids.forEach((id) => {
      const p = positions[id];
      minX = Math.min(minX, p.x * H_SPACING); maxX = Math.max(maxX, p.x * H_SPACING + CARD_W);
      minY = Math.min(minY, p.y * V_SPACING); maxY = Math.max(maxY, p.y * V_SPACING + CARD_H);
    });
    const pad = 90;
    const treeW = Math.max(1, maxX - minX), treeH = Math.max(1, maxY - minY);
    const fitScale = Math.min(1.1, Math.max(0.3, Math.min((rect.width - pad) / treeW, (rect.height - pad) / treeH)));
    const focusPos = centerId && positions[centerId] ? positions[centerId] : null;
    // FIX (raportuar nga useri — "pak në të djathtë, sepse partneri s'llogaritet
    // fare") — nëse personi ka bashkëshort/e (kartelë e dukshme ngjitur), qendra
    // duhet të jetë NË MES të dyjave (çiftit), jo mbi vetë personin — përndryshe
    // vizualisht kartela e partnerit "del jashtë" nga njëra anë sapo hapet pema,
    // duke i dhënë përshtypjen se s'është vërtet e qendërzuar. E njëjta llogaritje
    // përdoret tashmë te butonat e zgjerimit (expandButtons, më sipër) për të
    // gjetur mesin X të një çifti. Kur s'ka partner (ose partneri s'është ende i
    // dukshëm në pemë), sillet si më parë — qendra bie direkt mbi vetë personin.
    let centerX, centerY;
    if (focusPos) {
      const coupleIds = centerId ? [centerId, ...(people[centerId]?.spouse_ids || [])].filter((id) => positions[id]) : [centerId];
      const xs = coupleIds.map((id) => positions[id].x);
      centerX = ((Math.min(...xs) + Math.max(...xs)) / 2) * H_SPACING + CARD_W / 2;
      centerY = focusPos.y * V_SPACING + CARD_H / 2;
    } else {
      centerX = (minX + maxX) / 2;
      centerY = (minY + maxY) / 2;
    }
    // Kërkesë e userit — kur qendërzohet mbi një PERSON specifik (jo mbi
    // krejt pemën), aplikohet shkallë shtesë zoom-i (treeFocusZoom, e
    // rregullueshme nga Admin Center) mbi shkallën bazë të fitView-t —
    // përndryshe teksti mbetet shumë i vogël për t'u lexuar rehat, meqë
    // shkalla bazë llogaritet për të përshtatur KREJT pemën. E kufizuar në
    // 2.2, njësoj si kufiri ekzistues i pinch-zoom, që të mos dalë jashtë
    // sjelljes normale të zoom-it të vetë pemës.
    const newScale = focusPos ? Math.min(2.2, fitScale * (treeFocusZoom.enabled ? treeFocusZoom.multiplier : 1)) : fitScale;
    if (!Number.isFinite(newScale) || newScale <= 0) { setScale(0.9); setPan({ x: 60, y: 60 }); return; }
    const npx = rect.width / 2 - centerX * newScale, npy = rect.height / 2 - centerY * newScale;
    if (!Number.isFinite(npx) || !Number.isFinite(npy)) { setScale(0.9); setPan({ x: 60, y: 60 }); return; }
    setScale(newScale); setPan({ x: npx, y: npy });
  };
  const [pendingFocusId, setPendingFocusId] = useState(null);
  const [pendingRecenterId, setPendingRecenterId] = useState(null);
  const didFitRef = useRef(false); // a u bë NDONJË fit fillestar (qoftë edhe pa person)
  // FIX (raportuar nga useri — "në Android, hapja e ftohtë, prap fokusohet
  // te rrënja — vetëm refresh e rregullon") — provova më parë me pritje me
  // kohë fikse (~1s), por në "cold start" të app-it nativ Android,
  // identifikimi (currentPersonId) mund të marrë MË SHUMË se 1s — s'ka
  // kufi të sigurt kohor për ta hamendësuar. Në vend të kësaj: lejohet një
  // RIFOKUSIM i vetëm automatik sapo currentPersonId të mbërrijë, SADO VONË
  // qoftë — `didFocusPersonRef` mbetet `false` derisa kjo të ndodhë vërtet
  // (jo thjesht "provuam"), kështu që efekti rithirret çdo herë që
  // currentPersonId ndryshon (varësia poshtë) dhe e kap menjëherë sapo
  // vlera e vërtetë mbërrin — pa pasur nevojë fare për numërim provash apo
  // kohë maksimale. Pas kësaj rifokusimi të parë real, s'përsëritet më
  // automatikisht (p.sh. kur ndryshon currentPersonId më vonë nga zgjedhësi
  // i familjeve) — atëherë përdoruesi e ka tashmë lëvizjen e vet nën kontroll.
  const didFocusPersonRef = useRef(false);
  const userAdjustedViewRef = useRef(false);
  // FIX (raportuar nga useri — "tek pema fokusi mbetet te rrënja deri sa të
  // bëj refresh") — SHKAKU I VËRTETË: elementi i vetë pemës (containerRef)
  // ekziston VETËM kur activeScreen === "tree" (shih më poshtë te render —
  // krejt blloku i pemës është brenda `{activeScreen === "tree" && (...)}`).
  // Nëse identifikimi (currentPersonId) merr më shumë se ~1s (provat e
  // rrjetit poshtë), DHE useri ende s'ka shkuar te "Pema" brenda asaj kohe
  // (krejt normale — zakonisht shikohet "Ballina" fillimisht), efekti
  // dorëzohej me pamje fallback (pa person) sepse containerRef.current
  // ishte null — dhe `activeScreen` s'ishte fare pjesë e varësive, kështu
  // që KALIMI i mëvonshëm te "Pema" s'e rithërriste më efektin fare. Refresh
  // funksiononte "rastësisht" vetëm kur identifikimi ndodhte mjaftueshëm
  // shpejt (session i ruajtur në cache) që të mbaronte PARA se useri të
  // arrinte te tab-i i pemës. `activeScreen` tani është te varësitë poshtë.
  useEffect(() => {
    if (activeScreen !== "tree") return; // containeri s'ekziston fare jashtë ekranit "Pema" — s'ka kuptim të provojmë
    if (loading || Object.keys(positions).length === 0 || didFocusPersonRef.current) return;
    // Nëse currentPersonId tashmë e njohim (dhe ka pozicion në pemë),
    // qendërzohu direkt mbi të — kjo është rrjedha normale, dhe MBULON edhe
    // rastin kur fit-i i parë (fallback, pa person) ishte bërë më herët:
    // sapo currentPersonId të ndryshojë nga null në një vlerë reale, ky
    // useEffect rithirret vetë (është te varësitë) dhe e kap këtu.
    if (currentPersonId && positions[currentPersonId] && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width > 100 && rect.height > 100) {
        didFitRef.current = true;
        didFocusPersonRef.current = true;
        fitView(currentPersonId);
        return;
      }
    }
    // FIX (raportuar nga useri — "prap fokusohet te rrënja, edhe pas
    // rregullimeve të mëparshme, në sesion krejt të ri") — problemi këtu
    // s'ishte KOHA (currentPersonId mund të jetë tashmë gati), por që
    // POZICIONI yt mund të MOS EKZISTOJË FARE në pamjen fillestare
    // parazgjedhje ("Trungu im" — trunk), nëse s'je pjesë e zinxhirit të
    // paraardhësve të shfaqur si parazgjedhje.
    //
    // FIX #2 — rruga e mëparshme provonte, kur viewMode==="trunk", të
    // ndryshonte VETËM `focusPersonId` (duke shpresuar që zinxhiri i ri i
    // "trungut" të përfshinte pozicionin tënd). Kjo varet nga logjika e
    // ndërlikuar e ndërtimit të zinxhirit "trunk" (disa gjendje të tjera
    // ndërmjetëse), dhe në praktikë s'garantonte gjithmonë sukses — pikërisht
    // rasti i raportuar. Tani përdoret GJITHMONË rruga e garantuar: kalim te
    // "shiko gjithçka" (viewMode "full", të gjitha degët të dukshme), ku ti
    // je GARANTUESHT i përfshirë, pavarësisht ku ndodhesh në pemë. E njëjta
    // rrugë që përdor `focusOn` për kërkimin (kur viewMode s'është "trunk").
    if (currentPersonId && !positions[currentPersonId] && people[currentPersonId]) {
      didFocusPersonRef.current = true; // s'duhet të riprovojmë automatikisht — pendingFocusId merr përsipër
      setViewMode("full");
      setShowCore(true); setShowMarriedIn(true); setShowCloseCousin(true); setShowMarriedOut(true); setShowExtended(true);
      setPendingFocusId(currentPersonId);
      return;
    }
    if (didFitRef.current) return; // fit fallback tashmë u bë, presim vetëm currentPersonId (varësia poshtë e rithërret vetë)
    let cancelled = false, attempts = 0;
    const tryFit = () => {
      if (cancelled || didFitRef.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      attempts++;
      if (rect && rect.width > 100 && rect.height > 100) {
        didFitRef.current = true;
        // currentPersonId ende s'është gati — fit fallback (pa person),
        // JO i "kyçur" përgjithmonë: sapo currentPersonId të mbërrijë,
        // dega e sipërme e këtij useEffect-i do ta rifokusojë vetë.
        fitView();
      } else if (attempts < 20) {
        setTimeout(tryFit, 50);
      } else {
        didFitRef.current = true;
        setScale(0.9); setPan({ x: 60, y: 60 });
      }
    };
    tryFit();
    return () => { cancelled = true; };
    // FIX (raportuar nga useri — "prap fokusohet te rrënja/krejt pema në
    // sesion krejt të ri; vetëm butoni fit ose refresh e rregullon") —
    // `people` mungonte nga varësitë, edhe pse lexohet brenda efektit
    // (`people[currentPersonId]`). Nëse `people` përditësohej PAS që
    // currentPersonId të vendosej (krejt normale në sesion të ri, ku
    // identifikimi bën disa hapa rrjeti njëri pas tjetrit), efekti xhironte
    // me `people` të vjetruar/bosh nga mbyllja e tij, "people[currentPersonId]"
    // dilte bosh, dhe efekti s'e kapte dot asnjërën nga dy rrugët e sipërme
    // — mbetej "bllokuar" pa fokus, PËRGJITHMONË (asnjë varësi tjetër s'do
    // ta rithirrte më). Butoni "fit" funksiononte sepse ai lexon `people`
    // direkt në kohën e klikimit (jo nga një closure i vjetëruar).
  }, [loading, positions, currentPersonId, people, viewMode, activeScreen]);

  // The viewport/available space can keep shifting for a few seconds AFTER
  // the first fit — mobile browser chrome (address bar) settling, or the
  // custom display font swapping in and changing the header's height. Not
  // every browser fires a resize/visualViewport event for these, so on top
  // of listening for those, actively re-check the container size for a
  // while after load and self-correct if it changed — but never once the
  // person has manually zoomed or panned, so this can't fight their input.
  //
  // FIX (raportuar nga useri — "pasi lëviz pemën me gisht, rrotullimi
  // pastaj çorienton") — `userAdjustedViewRef.current` më parë e bllokonte
  // KREJT këtë efekt (edhe vetë ruajtjen e fokusit gjatë rrotullimit),
  // sepse ishte shtuar për të mbrojtur nga fitView() e vjetër, e cila
  // GJITHMONË rikthente te "krejt pema" — diçka që s'duhej t'i ndodhte
  // dikujt që sapo kishte lëvizur vetë me dorë. Tani që maybeRefit() VETËM
  // ruan pikën ekzistuese qendrore (nuk "fit" nga e para), s'ka më nevojë
  // për këtë mbrojtje — përkundrazi, DUHET të vazhdojë të funksionojë EDHE
  // pas lëvizjes manuale, përndryshe pikërisht kjo "ndalesë" e lë pemën të
  // pa-përshtatur pas rrotullimeve të mëvonshme (defekti i raportuar).
  useEffect(() => {
    if (loading || Object.keys(positions).length === 0) return;
    let cancelled = false;
    let lastRect = null;
    const maybeRefit = () => {
      if (cancelled || !didFitRef.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width < 100 || rect.height < 100) return;
      const changed = lastRect && (Math.round(rect.width) !== Math.round(lastRect.width) || Math.round(rect.height) !== Math.round(lastRect.height));
      if (changed) {
        // FIX (raportuar nga useri — "kur rrotulloj telefonin, pema
        // çorientohet, s'e ruan fokusin ku ishte") — më parë këtu thirrej
        // fitView() e thjeshtë, e cila GJITHMONË e rikthente pamjen te
        // "krejt pema", duke fshirë ç'do fokus/zoom ku ndodhej (qoftë
        // fokusi automatik mbi personin, qoftë vendi ku kishte lëvizur
        // vetë me dorë). Tani, në vend të "fit nga e para", RUHET pika e
        // pemës që ishte SAKTËSISHT në qendër të ekranit PARA ndryshimit
        // të madhësisë (rrotullim, ose "settle" i shiritit të shfletuesit),
        // dhe pan-i rillogaritet vetëm aq sa duhet që ajo pikë e njëjtë të
        // mbetet në qendër të ekranit TË RI — zoom-i mbetet krejt i
        // pandryshuar. `scaleRef` (jo `scale` direkt) sepse ky closure
        // mund të mos jetë rifreskuar që kur useri ka zmadhuar/zvogëluar
        // vetë me dorë (pinch) mes dy ndryshimeve të madhësisë.
        const oldRect = lastRect;
        setPan((prevPan) => {
          const s = scaleRef.current || 1;
          const treeCenterX = (oldRect.width / 2 - prevPan.x) / s;
          const treeCenterY = (oldRect.height / 2 - prevPan.y) / s;
          return { x: rect.width / 2 - treeCenterX * s, y: rect.height / 2 - treeCenterY * s };
        });
      }
      lastRect = { width: rect.width, height: rect.height };
    };
    const pollTimer = setInterval(maybeRefit, 300);
    const pollStop = setTimeout(() => clearInterval(pollTimer), 4000);
    const onResize = () => setTimeout(maybeRefit, 150);
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    document.fonts?.ready?.then(() => { if (!cancelled) maybeRefit(); });
    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      clearTimeout(pollStop);
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [loading, positions]);

  const focusOn = (id) => {
    if (!positions[id]) {
      if (viewMode === "trunk" && people[id]) {
        // make this person the new anchor of the trunk view — their own ancestor
        // chain becomes what's shown, exactly like "Trungu im" but centered on them
        setFocusPersonId(id);
        setExpandedIds(new Set());
        setChainStopId(null);
        setPendingFocusId(id);
        setSearchOpen(false); setSearch("");
        return;
      }
      // other view modes: fall back to showing everything, then focus once positioned
      setViewMode("full");
      setShowCore(true); setShowMarriedIn(true); setShowCloseCousin(true); setShowMarriedOut(true); setShowExtended(true);
      setPendingFocusId(id);
      setSearchOpen(false); setSearch("");
      return;
    }
    // FIX — ripërdor fitView(id) (zoom shtesë + qendërzim mbi çiftin, jo
    // vetëm mbi vetë personin) në vend të llogaritjes së vjetër, të
    // thjeshtë, që s'i kishte këto përmirësime. Njëjtësohet sjellja
    // kudo që dikush qendërzohet mbi një person specifik.
    fitView(id);
    setSelectedId(id); setSearchOpen(false); setSearch("");
  };

  // ══ TURNEU I UDHËHEQUR ═══════════════════════════════════════════ TURNEU1
  //
  // Aplikacioni tregon vetë historinë: kursori udhëton nëpër pemë, zgjedh
  // persona me radhë, hap profilin e secilit, dhe poshtë rri një rresht që
  // thotë çfarë dimë për të.
  //
  // PSE RRI PIKËRISHT KËTU, PAS `focusOn`
  //
  // Sepse turneu NUK është ekran i dytë dhe nuk ka rrugë të vetën. Ai thërret
  // `focusOn(id)` — saktësisht të njëjtën derë që e thërret gishti i njeriut
  // kur prek një kartelë. Kjo do të thotë se qendërzimi, zoom-i mbi çiftin,
  // hapja e profilit dhe rasti "personi s'është ende në pamje" janë tashmë të
  // zgjidhura, dhe do të mbeten të zgjidhura edhe kur ato të ndryshojnë nesër.
  //
  // Nëse turneu do të lëvizte vetë pan-in dhe do të "klikonte" mbi pikselat,
  // do të prishej me çdo ndryshim pamjeje — dhe pamjen po e ndryshojmë
  // vazhdimisht (guaskat, temat). Këtu turneu është LISTË GJENDJESH, jo listë
  // klikimesh; kursori i lëvizshëm është zbukurim mbi të, jo drejtuesi i saj.
  const [turneCfg, setTurneCfg] = useState(null);      // null = nuk shfaqet
  const [turneAktiv, setTurneAktiv] = useState(false);
  // FUSHA E RRËFIMIT (27 gusht) — «fillo nga ky paraardhës, ndalu te ky».
  //
  // Rri te gjendja e kësaj pamjeje, JO te baza: kush shikon zgjedh çfarë do të
  // dëgjojë, dhe të nesërmen fillon prapë nga e para. Parazgjedhja për një
  // familje (ekrani i muzeut që rri gjithë ditën i ndezur) vjen nga serveri me
  // TREGIMI2; kjo gjendje e mbivendos atë vetëm për këtë shikim.
  const [turneFusha, setTurneFusha] = useState({ lloj: "gjithe", nga: null, deri: null });
  const [turneFushaHapur, setTurneFushaHapur] = useState(false);
  const [turneIndeks, setTurneIndeks] = useState(0);
  // Çdo person merr DY rrahje: fillimisht pema lëviz drejt tij me profilin e
  // mbyllur (kjo është pjesa që duhet parë — udhëtimi), pastaj hapet profili.
  // Pa këtë ndarje, te telefoni turneu do të ishte një varg fletësh profili
  // njëra mbi tjetrën: profili aty është `fixed inset-0`, pra e mbulon pemën
  // krejt, dhe lëvizja që e bën turne nuk do të shihej kurrë.
  const [turneFaza, setTurneFaza] = useState(0);       // 0 = pema, 1 = profili
  const [turnePauza, setTurnePauza] = useState(false);
  // Cili hap e mbaroi leximin. Kjo është zemra e ndryshimit të 26 gushtit:
  // turneu nuk numëron më sekonda, po **pret sa flitet**. Kërkesë e pronarit —
  // *"jo kaq sekonda për person, po ta lexojë gjithçka për atë person"*.
  const [lexiMbaroi, setLexiMbaroi] = useState(-1);
  // Cila rrahje është zbatuar tashmë. Pa këtë, çdo rirenderim (p.sh. kur
  // vizitori prek ekranin dhe turneu ndalon) do ta rikthente pamjen me forcë
  // te personi — pra "merr timonin" do të ishte premtim i thyer brenda çastit.
  const turneRrahjaRef = useRef("");

  useEffect(() => {
    if (!activeFamilyId) { setTurneCfg(null); return; }
    let gjallë = true;
    supabase.rpc("rpc_get_turne", { p_family_id: activeFamilyId }).then(({ data, error }) => {
      if (!gjallë) return;
      const r = Array.isArray(data) ? data[0] : data;
      // Pa TURNEU1 kjo derë s'ekziston → `error` → turneu thjesht nuk shfaqet.
      // Mungesa e një veçorie prototip s'guxon ta prishë ekranin e askujt.
      if (error || !r || !r.ndezur) { setTurneCfg(null); return; }
      // `zeri_ndezur` mungon te bazat pa ZERI1 — atëherë `undefined` bëhet
      // `false` dhe turneu sillet si më parë. Një veçori e re s'guxon ta prishë
      // atë që punonte para saj.
      setTurneCfg({
        sekonda: Number(r.sekonda) || 8,
        hapa: Number(r.hapa) || 12,
        zeriNdezur: !!r.zeri_ndezur,
        zeri: r.zeri || "sq-AL-AnilaNeural",
        // Pa TREGIMI1 kolona s'ekziston → `undefined` → `klasik`. Një veçori e
        // re nuk guxon ta ndryshojë sjelljen e atyre që s'e kanë xhiruar ende.
        stil: r.stil === "rrefim" ? "rrefim" : "klasik",
      });
      // Ngjarjet familjare ngarkohen vetëm kur hapen Kujtimet. Turneu ka nevojë
      // për to që në fillim — hyrja e tij është ngjarja më e vjetër — dhe kush
      // shkon drejt te Pema s'i ka prekur ato ende.
      if (!familyHistoryEvents.length) fetchFamilyHistory();
    }).catch(() => { if (gjallë) setTurneCfg(null); });
    return () => { gjallë = false; };
  }, [activeFamilyId, liveTick]);

  // RRËFIMI — nga faktet, dhe vetëm nga faktet.
  //
  // Asnjë fjali këtu nuk shpjegon PSE. "Emigroi sepse…", "vendosi të…" — ato
  // i shkruan njeriu ose askush. Kjo funksion vetëm i bashkon ato që dimë.
  //
  // Kur s'ka veçse emrin, e thotë me zë: "për këtë person nuk kemi më shumë".
  // Kërkesë e pronarit, dhe ka të drejtë — një rrëfim që e pranon boshllëkun
  // besohet më shumë se një që e mbulon, dhe bëhet ftesë për ta plotësuar.
  //
  // KUFI I NDERSHËM: gjendja "përafërsisht 1930" NUK ekziston, sepse baza sot
  // s'e di dot — `birth_date` është datë dhe asgjë s'thotë nëse është e saktë
  // apo e hamendësuar. Pra dy gjendje: e dimë, ose nuk e dimë. E treta kërkon
  // një kolonë saktësie te personi; kur të vendoset si mblidhet, shtohet këtu.
  // Sa i gjatë guxon të jetë një hap. Kufiri s'është shije: audioja paguhet me
  // shenjë, dhe fjalia që dëgjohet duhet të jetë PIKËRISHT ajo që lexohet me sy
  // — ndryshe kthehemi te dy të vërteta. Prandaj pritet një herë, dhe pritja
  // vlen njësoj për titrën dhe për zërin.
  const TURNE_GJATESIA = 520;
  const preji = (teksti) => {
    const t0 = String(teksti || "").trim();
    if (t0.length <= TURNE_GJATESIA) return { teksti: t0, u_pre: false };
    // Pritet te fundi i fjalisë së fundit që hyn — jo në mes të një fjale.
    const copa = t0.slice(0, TURNE_GJATESIA);
    const fundi = Math.max(copa.lastIndexOf("."), copa.lastIndexOf("!"), copa.lastIndexOf("?"));
    return { teksti: (fundi > 160 ? copa.slice(0, fundi + 1) : copa.trim() + "…"), u_pre: true };
  };

  const turneRrëfimi = useCallback((p) => {
    const vitL = p.birth_date ? String(p.birth_date).slice(0, 4) : null;
    const vitV = p.death_date ? String(p.death_date).slice(0, 4) : null;
    const jeta = vitL && vitV ? `${vitL}–${vitV}` : vitL ? t("b. {{viti}}", { viti: vitL }) : null;
    const copat = [];
    // MOS E THUAJ DY HERË (26 gusht, pasi historitë e vërteta hynë brenda).
    //
    // Pa këtë, Naimi dëgjohej: *"Lindur në Vermosh, Kelmend. … Naimi lindi në
    // Vermosh, në malësinë e Kelmendit…"* — e njëjta gjë dy herë brenda dhjetë
    // sekondash. Faktet janë aty për ato që historia s'i thotë; kur historia e
    // thotë vetë, fakti hiqet.
    //
    // Krahasohet rrënja e emrit të vendit, jo vargu i plotë: "Gjakovë" te
    // fusha dhe "Gjakova" te fjalia janë i njëjti vend, po jo i njëjti varg.
    // Heuristikë, dhe e pranoj si të tillë: një heqje e tepërt kushton një
    // fakt të pathënë; një përsëritje kushton besueshmërinë e rrëfimit.
    const bioTxt = String(p.bio || "").toLowerCase();
    const vendi = String(p.birth_place || "").split(",")[0].trim();
    const rrënja = vendi.toLowerCase().slice(0, 5);
    const vendiThënë = rrënja.length >= 4 && bioTxt.includes(rrënja);
    if (p.birth_place && !vendiThënë) copat.push(t("Born in {{vendi}}", { vendi: p.birth_place }));
    const prindërit = (p.parent_ids || []).map((id) => (people[id] ? displayName(people[id]) : null)).filter(Boolean);
    // Presje, jo `&`: shenja shkon te SSML-ja dhe lexohet keq nga zëri.
    if (prindërit.length) copat.push(t("Child of {{emrat}}", { emrat: prindërit.join(", ") }));
    const bashkëshortët = (p.spouse_ids || []).map((id) => (people[id] ? displayName(people[id]) : null)).filter(Boolean);
    if (bashkëshortët.length) copat.push(t("Married to {{emrat}}", { emrat: bashkëshortët.join(", ") }));
    const fëmijë = Object.values(people || {}).filter((x) => x && !x.deleted_at && (x.parent_ids || []).includes(p.id)).length;
    if (fëmijë) copat.push(t("The branch continued with {{sa}}", { sa: fëmijë }));

    // HISTORIA E SHKRUAR — `bio` është pikërisht fusha që vetë aplikacioni e
    // quan "Historia" te forma e personit. Këto janë fjalët e familjes, jo të
    // sistemit; prandaj lexohen ashtu siç janë shkruar, pa u prekur.
    //
    // Ngjarjet e Kujtimeve NUK hyjnë këtu: ato janë të familjes, jo të personit
    // (`family_history_events` s'ka lidhje me një person). T'i vishnim njërit
    // do të ishte hamendësim — dhe hamendësimi është e vetmja gjë e ndaluar.
    const e_prera = preji(p.bio);
    const historia = e_prera.teksti;

    return {
      emri: displayName(p),
      jeta,
      teksti: copat.length ? copat.join("  ·  ") : t("We have no more information about this person."),
      historia,
      u_pre: e_prera.u_pre,
      // Dy heshtje të ndryshme, dy fjali të ndryshme: "s'dimë asgjë" nuk është
      // e njëjta gjë me "dimë datat, po askush s'ka shkruar ende historinë".
      bosh: copat.length === 0,
      paHistori: !historia,
    };
  }, [people, t]);

  // NJË VARG PËR VESHIN, I NDËRTUAR NGA I NJËJTI RRËFIM QË SHKON TE SYRI.
  // "·" është shenjë për syrin; veshi do pika. Përveç kësaj, asnjë ndryshim —
  // po të lexohej diçka tjetër nga ç'shkruhet, do të kishim sërish dy të
  // vërteta, vetëm se njëra do të ishte e padukshme.
  const turneFjalia = useCallback((hap) => hap.fjalite
    // STILI «RRËFIM»: vargu ndërtohet nga vetë rrëfimtari, që veshi të dëgjojë
    // pikërisht atë që sytë lexojnë. `hap.emri` NUK i ngjitet përpara — te një
    // rrëfim emri është brenda fjalisë ("Gjithçka që dimë nis me Naimin…"),
    // dhe po t'i vihej edhe përpara, çdo hap do të niste me emrin dy herë.
    ? [hap.fjalite.map((x) => t(x.celes, x.vlerat || {})).join(" "), hap.historia]
        .filter(Boolean).join(" ")
    : [
    hap.emri,
    hap.jeta,
    hap.teksti.replace(/\s*·\s*/g, ". "),
    // `hap.id` mungon vetëm te hyrja/fundi i familjes — atje "nuk ka histori
    // të shkruar" s'ka kuptim: ngjarja VETË është historia.
    hap.historia || (hap.bosh || !hap.id ? "" : t("There is no written story for this person yet.")),
  ].filter(Boolean).join(". "), [t]);

  // RADHA ËSHTË E LIBRIT — sipas datëlindjes, kush s'ka datë shkon i fundit.
  // E njëjta rregull si te `familyBook.js`, me qëllim: dy renditje të ndryshme
  // për të njëjtën familje do të thoshin dy histori të ndryshme.
  //
  // Preferohen personat që NDODHEN në pamjen e tanishme, që kursori të ketë ku
  // të shkojë pa e rrëzuar pamjen; nëse janë shumë pak, merren të gjithë dhe
  // `focusOn` e zgjeron vetë pamjen — punë që e bën tashmë për kërkimin.
  // NJË TURNE KA FILLIM DHE FUND, JO VETËM MES.
  //
  // Deri sot turneu niste te personi më i vjetër dhe mbaronte te i fundit —
  // listë, jo rrëfim. Origjina e familjes ("nga erdhën, ku shkuan") jetonte te
  // ngjarjet familjare dhe nuk dëgjohej kurrë.
  //
  // Tani ngjarja më e vjetër e hap turneun mbi PEMËN E TËRË — një pamje e gjerë,
  // pa profil — dhe ajo më e reja e mbyll. Personat rrinë në mes, si më parë.
  //
  // Familjet pa ngjarje nuk marrin as hyrje as fund: nuk shpikim kornizë për
  // një rrëfim që s'e ka.
  const turneNgjarjet = useMemo(() => {
    return (familyHistoryEvents || [])
      .filter((e) => e && (e.title || e.description))
      .slice()
      .sort((a, b) => String(a.event_date || "9999").localeCompare(String(b.event_date || "9999")));
  }, [familyHistoryEvents]);

  // ══ STILI «RRËFIM» ═══════════════════════════════════════════════ TREGIMI1
  //
  // Klasiku i tregon personat me radhë. Rrëfimi tregon MOMENTET: ato që u
  // shkruan nga familja, rrënjën, zhvendosjet, brezat e rinj dhe ata për të
  // cilët ekziston një histori e shkruar.
  //
  // Zgjedhja bëhet te `tregimi.js`, jo këtu — një skedar i pastër, i provuar me
  // Node, që do t'i shërbejë njësoj edhe Librit të Familjes. Këtu vetëm
  // përkthehet rezultati te i njëjti hap që di ta vizatojë shiriti.
  // ── NDIHMËSIT E ZGJEDHËSIT TË FUSHËS ─────────────────────────────────────
  //
  // Etiketa mban emrin DHE vitet, sepse te familja e provës ka tre "Kaltrina"
  // dhe dy "Jeta": pa vitet, kërkimi do t'i jepte dikujt historinë e gjyshes.
  // I njëjti mësim që u shkrua te KELMENDI1 kur historitë u lidhën me `id`.
  const turnePersonat = useMemo(() => {
    return Object.values(people || {})
      .filter((p) => p && !p.deleted_at)
      .map((p) => {
        const l = p.birth_date ? String(p.birth_date).slice(0, 4) : null;
        const v = p.death_date ? String(p.death_date).slice(0, 4) : null;
        const vitet = l && v ? `${l}–${v}` : l ? l : null;
        return { id: p.id, emri: displayName(p), etiketa: vitet ? `${displayName(p)} (${vitet})` : displayName(p) };
      })
      .sort((a, b) => a.etiketa.localeCompare(b.etiketa));
  }, [people]);

  const turneGjejPersonin = useCallback((teksti) => {
    const q = String(teksti || "").trim();
    if (!q) return null;
    const saktë = turnePersonat.find((p) => p.etiketa === q);
    if (saktë) return saktë.id;
    // Kur dikush shkruan vetëm emrin dhe ka më shumë se një me atë emër, NUK
    // zgjidhet i pari: do të ishte hamendësim i heshtur. Rrëfimi mbetet ku
    // ishte derisa zgjedhja të jetë e vetme.
    const njesoj = turnePersonat.filter((p) => p.emri.toLowerCase() === q.toLowerCase());
    return njesoj.length === 1 ? njesoj[0].id : null;
  }, [turnePersonat]);

  const turneEmriI = useCallback(
    (id) => (id ? (turnePersonat.find((p) => p.id === id)?.etiketa || "") : ""),
    [turnePersonat]);

  const turneShkurtimiIFushes = useMemo(() => {
    const emri = turnePersonat.find((p) => p.id === turneFusha.nga)?.emri;
    if (!emri) return t("Whose story?");
    if (turneFusha.lloj === "person") return emri;
    if (turneFusha.lloj === "pasardhesit") return `${emri} ↓`;
    if (turneFusha.lloj === "paraardhesit") return `${emri} ↑`;
    const deri = turnePersonat.find((p) => p.id === turneFusha.deri)?.emri;
    return deri ? `${emri} → ${deri}` : emri;
  }, [turneFusha, turnePersonat, t]);

  // Sa veta e ka rrëfimi që do të dilte — thuhet PARA se të nisë, jo pasi
  // dikush pret dy minuta dhe kupton se zgjodhi një degë me dy njerëz.
  const turnePershkrimiIFushes = useMemo(() => {
    if (turneFusha.lloj === "gjithe") return t("The story covers the whole family.");
    if (!turneFusha.nga) return t("Pick a person to begin.");
    if (turneFusha.lloj === "vija" && !turneFusha.deri) return t("Pick where the line should stop.");
    const provë = ndertoTregimin({ people, ngjarjet: [], fusha: turneFusha, maks: 1 });
    if (provë.fusha.bosh) {
      return provë.fusha.arsyeja === "nuk_lidhen"
        ? t("These two are not on the same line — nobody links them in this tree.")
        : t("That person is no longer in this family.");
    }
    return t("{{sa}} people, {{breza}} generations.", {
      sa: provë.saPersona, breza: provë.brezat.length,
    });
  }, [turneFusha, people, t]);

  // FJALITË E RRËFIMTARIT
  //
  // `rrefimi.js` kthen `{celes, vlerat}`; përkthimi bëhet këtu, sepse `t()` i
  // takon ndërfaqes. Kështu shqipja dhe anglishtja rrinë te `locales/`, dhe
  // rrëfimtari mbetet i provueshëm me Node.
  const rrPerkthe = useCallback((celes, vlerat) => t(celes, vlerat || {}), [t]);

  const turneRrefim = useMemo(() => {
    if (!turneCfg || turneCfg.stil !== "rrefim") return null;
    const tr = ndertoTregimin({
      people, ngjarjet: familyHistoryEvents || [], maks: turneCfg.hapa,
      fusha: turneFusha,
    });
    const rr = ndertoRrefimin({
      tregimi: tr, people, familja: treeName, maksTeksti: TURNE_GJATESIA,
    });

    // Fushë e pamundur — një hap i vetëm që e thotë pse. Kjo NUK është gabim
    // programi, është zgjedhje që pema s'e mban; prandaj shfaqet si fjali, jo
    // si mesazh i kuq, dhe turneu mbetet i hapur që zgjedhja të ndryshohet.
    if (rr.bosh) {
      return [{
        id: null, emri: t("The story cannot be told"), jeta: null,
        teksti: rr.hyrja.map((x) => rrPerkthe(x.celes, x.vlerat)).join(" "),
        historia: "", u_pre: false, bosh: true, paHistori: false, kap: null,
        fjalite: rr.hyrja,
      }];
    }

    // HYRJA DHE MBYLLJA JANË HAPA TË VËRTETË, jo dekor.
    //
    // Një rrëfim që nis me "Naim Kelmendi, 1942" e le dëgjuesin të hamendësojë
    // se çfarë po dëgjon. Një që nis me "kjo është historia e familjes X, nga
    // 1942 deri më 2021" nuk e le. E njëjta gjë te fundi.
    const anesore = (fjalite, emri) => ({
      id: null, emri, jeta: null,
      teksti: fjalite.map((x) => rrPerkthe(x.celes, x.vlerat)).join(" "),
      historia: "", u_pre: false, bosh: false, paHistori: false, kap: null,
      fjalite,
    });

    const hapat = rr.hapat.map((h) => {
      // Korniza + lidhësi + fakti i vetëm — pikërisht ajo që dëgjohet, dhe
      // pikërisht ajo që shkruhet. Dy të vërteta për të njëjtin hap do të
      // ishin një gënjeshtër e padukshme.
      const rreshti = [h.lidhesi, h.fjalia, h.shtese]
        .filter(Boolean).map((x) => rrPerkthe(x.celes, x.vlerat)).join(" ");
      return {
        id: h.id,
        emri: h.emri,
        jeta: h.vit ? String(h.vit) : null,
        teksti: rreshti,
        historia: h.teksti,
        u_pre: h.u_pre,
        bosh: false,
        // Rreshti «nuk ka histori të shkruar» del vetëm kur rrëfimtari nuk e
        // ka thënë vetë — përndryshe do të thuhej dy herë me dy fjalë.
        paHistori: !h.teksti && h.shtese?.celes !== "rr.shtese.heshtje" && !!h.id,
        kap: h.kap ? `${rrPerkthe(h.kap.celes, h.kap.vlerat)} · ${h.kap.vlerat.nga}–${h.kap.vlerat.deri}` : null,
        // Zëri e lexon pikërisht këtë varg fjalish.
        fjalite: [h.lidhesi, h.fjalia, h.shtese].filter(Boolean),
      };
    });

    return [
      anesore(rr.hyrja, treeName ? t("family.label", { surname: treeName }) : t("The family")),
      ...hapat,
      anesore(rr.mbyllja, t("And the story goes on")),
    ];
  }, [turneCfg, people, familyHistoryEvents, turneFusha, treeName, rrPerkthe, t, TURNE_GJATESIA]);

  const turneHapat = useMemo(() => {
    if (!turneCfg) return [];
    if (turneRrefim) return turneRrefim;
    const gjithë = Object.values(people || {}).filter((p) => p && !p.deleted_at);
    const radha = gjithë.slice().sort((a, b) =>
      String(a.birth_date || "9999").localeCompare(String(b.birth_date || "9999")));
    // Vetëm personat që NDODHEN në pamjen e tanishme. Turneu tregon atë që
    // pema ka vizatuar — jo më shumë. Kështu kursori ka gjithmonë ku të
    // shkojë, dhe turneu s'e rrëzon kurrë pamjen që zgjodhi vetë përdoruesi.
    const personat = radha.filter((p) => positions[p.id])
      .slice(0, turneCfg.hapa)
      .map((p) => ({ id: p.id, ...turneRrëfimi(p) }));

    // Një hap familjeje ka të njëjtat fusha si një hap personi — vetëm pa `id`.
    // Kështu titra dhe zëri punojnë pa asnjë ndryshim: e vetmja gjë që e dallon
    // është se pema nuk qendërzohet mbi askënd dhe profili nuk hapet.
    const ngjarjaSiHap = (ev) => ({
      emri: ev.title || t("From the family history"),
      jeta: ev.event_date ? String(ev.event_date).slice(0, 4) : null,
      teksti: String(ev.description || "").trim() || (ev.title || ""),
      historia: "",
      u_pre: false, bosh: false, paHistori: false,
    });

    if (!personat.length || turneNgjarjet.length === 0) return personat;
    const hyrja = ngjarjaSiHap(turneNgjarjet[0]);
    const fundi = turneNgjarjet.length > 1 ? ngjarjaSiHap(turneNgjarjet[turneNgjarjet.length - 1]) : null;
    return [hyrja, ...personat, ...(fundi ? [fundi] : [])];
  }, [turneCfg, people, positions, turneRrëfimi, turneNgjarjet, turneRrefim, t]);

  // NGJYRAT E BUTONAVE — raportuar 26 gusht: "kontrasti i ngjyrave s'po lexoj
  // dot". Kisha marrë `bgDeep` (sfondi i thellë i pemës — vishnje e errët te
  // pamja klasike) si sfond butoni, dhe `ink` (kafe e errët) si tekst mbi të.
  // Dy të errëta mbi njëra-tjetrën.
  //
  // Gabimi i vërtetë s'ishte zgjedhja e ngjyrës — ishte se e zgjodha pa e parë:
  // shiriti rri mbi `panel` (krem), pra butonat i takojnë familjes së panelit
  // (`panelSoft` + `ink`), jo asaj të sfondit. E njëjta çift përdoret te butoni
  // i kthimit brenda profilit, dhe lexohet në çdo temë sepse dizajni i ruan të
  // dyja së bashku.
  const turneBtn = {
    padding: "6px 11px", borderRadius: 9, fontSize: 12, fontWeight: 600,
    background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink,
  };
  // Butoni kryesor merr arin me tekst mbi sfondin e thellë — çifti që përdoret
  // kudo te veprimi kryesor, prandaj lexohet edhe kur ndërrohet tema.
  const turneBtnKryesor = {
    ...turneBtn, background: COLORS.gold, color: COLORS.bgDeep, border: "none",
  };
  // ══ ZËRI — provë e lirë, dhe e ndershme kur s'ka ═══════════════════════════
  //
  // Përdor `speechSynthesis`, zërin e vetë pajisjes: pa server, pa kosto për
  // shikim, pa skedar audio për t'u ruajtur. Prandaj vlen të provohet i pari —
  // nëse mjafton, e gjithë pjesa e shtrenjtë s'ka pse ekziston.
  //
  // POR: shumica e telefonave nuk kanë zë SHQIP të instaluar. Dhe një zë
  // anglisht që lexon shqip nuk është "më mirë se asgjë" — është më keq: bën
  // të qeshë aty ku duhej të prekte. Prandaj nuk lexohet kurrë me zë tjetër.
  // Kur s'ka zë shqip, butoni rri i fikur dhe e thotë vetë pse; turneu vazhdon
  // me titra, ashtu si më parë.
  //
  // Zgjedhja rri te PAJISJA (`localStorage`), jo te platforma: ekrani i muzeut
  // e do zërin gjithmonë, telefoni në xhep gati kurrë. E njëjta logjikë si te
  // guaska.
  const [turneZëri, setTurneZëri] = useState(() => {
    try { return localStorage.getItem("pef_turne_zeri") === "1"; } catch { return false; }
  });
  const [zëriGati, setZëriGati] = useState(null);   // null = ende s'e dimë pajisja
  // NJË element i vetëm audio, i krijuar me KLIKIMIN që nis turneun.
  //
  // Kjo s'është kursim kujtese. Shfletuesit — iOS-i më i rrepti — e lejojnë
  // audion vetëm si pasojë e drejtpërdrejtë e një veprimi njerëzor. Nëse do ta
  // krijonim elementin pasi kthehet përgjigjja e serverit, veprimi do të kishte
  // "skaduar" dhe zëri do të heshtte pa asnjë gabim. Krijohet te klikimi, dhe
  // pastaj vetëm i ndërrohet burimi.
  const audioRef = useRef(null);
  // PSE EKZISTON KY VARG (26 gusht, pas raportimit "butoni punon por zë nuk ka")
  //
  // Zinxhirin e zërit e ndërtova që të bjerë NË HESHTJE — dhe kjo është e saktë
  // për një familje: askush s'duhet të shohë gabim serveri mbi historinë e
  // gjyshit. Po për atë që po e provon, heshtja është e padobishme: nuk dallon
  // dot "s'u dorëzua funksioni" nga "mbaroi kuota" nga "e ndaloi shfletuesi".
  //
  // Prandaj arsyeja shfaqet, po VETËM kur zëri është i ndezur me dorë. E fikur,
  // turneu hesht si më parë dhe nuk e sheh kurrë këtë rresht.
  const [zeriGabimi, setZeriGabimi] = useState("");

  // GABIM I RAPORTUAR (26 gusht): "po thotë s'ka zë shqip edhe kur e ndërroj
  // gjuhën në anglisht". E kisha lidhur kërkimin fort te `sq`, sikur turneu të
  // fliste vetëm shqip. Por titrat e turneut janë në gjuhën e ndërfaqes — pra
  // zëri duhet të ndjekë ATË, jo një gjuhë të shkruar te kodi.
  //
  // Gjuha lexohet nga `document.documentElement.lang`, të cilin e vendos vetë
  // `i18n.js` me çdo ndërrim — pra pa importuar asgjë të re. `MutationObserver`
  // e kap ndërrimin edhe kur ndodh me turneun hapur.
  const [uiGjuha, setUiGjuha] = useState(() => {
    try { return (document.documentElement.lang || "sq").slice(0, 2).toLowerCase(); }
    catch { return "sq"; }
  });
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    const ob = new MutationObserver(() => {
      const g = (document.documentElement.lang || "sq").slice(0, 2).toLowerCase();
      setUiGjuha((x) => (x === g ? x : g));
    });
    ob.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
    return () => ob.disconnect();
  }, []);

  const gjejZërin = useCallback(() => {
    const ss = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (!ss) return null;
    const zërat = ss.getVoices() || [];
    return zërat.find((z) => String(z.lang || "").toLowerCase().startsWith(uiGjuha)) || null;
  }, [uiGjuha]);

  // Emri i gjuhës me fjalë, jo kodi: "shqip" lexohet, "sq" jo. `Intl` mungon
  // te disa WebView të vjetra — atëherë bie te kodi me shkronja të mëdha.
  const emriIGjuhës = useMemo(() => {
    try { return new Intl.DisplayNames([uiGjuha], { type: "language" }).of(uiGjuha) || uiGjuha.toUpperCase(); }
    catch { return uiGjuha.toUpperCase(); }
  }, [uiGjuha]);

  useEffect(() => {
    // ⚠ 28 GUSHT 2026 — PROVË NDARËSE, jo zgjidhje e mbaruar.
    //
    // Te logu i iOS-it, çdo rrëzim i WebView-it paraprihet nga rreshti
    // `xpc_user_sessions_get_foreground_uid() failed`. Atë rresht e lëshon
    // nënsistemi i medias/zërit i WebKit-it — dhe e vetmja gjë që e prek atë
    // gjatë nisjes sonë është pikërisht kjo: `speechSynthesis.getVoices()`.
    //
    // Te shfletuesi kjo është e padëmshme. Brenda një WKWebView-i të aplikacionit
    // ajo ndez shërbimin e zërit të sistemit qysh në sekondën e parë, pa ia
    // kërkuar askush.
    //
    // Prandaj këtu, te telefoni, nuk e prekim fare derisa njeriu të shtypë
    // «lexo». Zëri mbetet i fikur në telefon sa zgjat prova; te webi asgjë
    // s'ndryshon. Nëse pas kësaj aplikacioni rri gjallë, e dimë fajtorin dhe
    // e rregullojmë si duhet: ta thërrasim vetëm te prekja e butonit.
    if (Capacitor.isNativePlatform()) { setZëriGati(false); return; }
    const ss = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (!ss) { setZëriGati(false); return; }
    // Lista e zërave mbërrin me vonesë te disa shfletues — prandaj pyetet dy
    // herë: tani, dhe kur vetë shfletuesi thotë se e mbushi.
    const kontrollo = () => setZëriGati(!!gjejZërin());
    kontrollo();
    ss.addEventListener?.("voiceschanged", kontrollo);
    return () => ss.removeEventListener?.("voiceschanged", kontrollo);
  }, [gjejZërin]);

  // Kodet që kthen funksioni janë për kodin, jo për njeriun. Këtu bëhen fjali.
  // Ato që s'i njohim kalojnë ashtu siç janë — një gabim i papërkthyer është
  // shumë më i dobishëm se një "diçka shkoi keq".
  const turneArsyeja = useCallback((kodi) => {
    const k = String(kodi || "");
    if (k.includes("settings_read")) return t("The server could not read the settings: {{arsyeja}}", { arsyeja: k.replace(/^.*settings_read:\s*/, "") });
    if (k.includes("settings_missing")) return t("There is no settings row yet — open Admin Center → Tree bar & navigation once and save.");
    if (k.includes("needs_zeri2")) return t("This needs ZERI2 — run ZERI2_dera_e_cilesimeve.sql in Supabase.");
    if (k.includes("settings_stale")) return t("The server is still on an old schema — run `notify pgrst, 'reload schema';` in Supabase, then try again.");
    if (k.includes("voice_off")) return t("Turn on \"Read aloud\" in Admin Center → Guided tour, then Save.");
    if (k.includes("rate_limited")) return t("Daily limit reached — the tour continues with captions.");
    if (k.includes("quota_month")) return t("The monthly free quota is used up — the tour continues with captions.");
    if (k.includes("voice_lang_mismatch")) return t("Anila and Ilir speak Albanian; the interface is in another language.");
    if (/AZURE_SPEECH/i.test(k)) return t("The Azure secrets are not set in Supabase.");
    if (/Failed to send a request|non-2xx|not found|404/i.test(k)) return t("The `turne-zeri` function is not deployed yet.");
    return k;
  }, [t]);

  // A ka fare zë për të ndezur, dhe nga ku. Butoni s'guxon të premtojë zë kur
  // s'ka asnjë burim — dhe s'guxon të rrijë i fikur kur serveri e ka.
  const zeriBurimi = turneCfg?.zeriNdezur ? "server" : (zëriGati ? "pajisje" : null);

  const turneZeriToggle = () => {
    if (!zeriBurimi) return;
    const v = !turneZëri;
    setTurneZëri(v);
    try { localStorage.setItem("pef_turne_zeri", v ? "1" : "0"); } catch { /* e padëmshme */ }
    if (!v) { setZeriGabimi(""); try { window.speechSynthesis?.cancel(); } catch { /* nuk prish gjë */ } }
  };

  // ZËRI — tri shkallë, dhe secila bie e qetë te tjetra:
  //
  //   1. Zëri i Azure-it (Anila/Iliri), i prodhuar një herë dhe i ruajtur.
  //   2. Zëri i pajisjes, nëse ka për gjuhën e ndërfaqes.
  //   3. Heshtje me titra — që punonte para se zëri të ekzistonte.
  //
  // Asnjë hallkë s'e ndal turneun kur bie. Rrjeti mungon, sekreti s'është
  // vendosur, kuota mbaroi — te të treja rezultati është i njëjti: titrat
  // vazhdojnë dhe askush s'sheh gabim.
  //
  // ---------------------------------------------------------------------------
  // GABIMI I 26 GUSHTIT: "vetëm emrin Naim Kelmendi e thotë dhe asgjë më shumë"
  //
  // Ky efekt kishte `turneFaza` te varësitë DHE te kushti i heshtjes. Zëri niste
  // te rrahja e parë (udhëtimi te pema); pas ~2,6 sekondash fillonte rrahja e
  // dytë (hapja e profilit), efekti rixhirohej, shihte `turneFaza !== 0` dhe e
  // NDALONTE audion. Dy sekonda e gjysmë janë saktësisht sa një emër.
  //
  // Rrahja është ngjarje pamjeje. Zëri i takon HAPIT, jo rrahjes — dhe kalimi
  // nga pema te profili nuk është arsye për të heshtur. Tani efekti dëgjon
  // vetëm indeksin e hapit, dhe `turneLexuarRef` siguron një lexim të vetëm
  // për hap, sado herë të rirenderohet ekrani.
  const turneLexuarRef = useRef("");
  useEffect(() => {
    const ss = typeof window !== "undefined" ? window.speechSynthesis : null;
    const hesht = () => {
      try { ss?.cancel(); } catch { /* nuk prish gjë */ }
      try { if (audioRef.current) audioRef.current.pause(); } catch { /* njëjtë */ }
    };
    // Vetëm ndalimi, pauza ose fikja e zërit heshtin. Jo rrahja.
    if (!turneAktiv || !turneZëri || turnePauza) { hesht(); turneLexuarRef.current = ""; return; }
    const hap = turneHapat[turneIndeks];
    if (!hap) return;
    if (turneLexuarRef.current === String(turneIndeks)) return;
    turneLexuarRef.current = String(turneIndeks);

    const fjalia = turneFjalia(hap);
    let anuluar = false;

    // Arsyeja e serverit mbahet LOKALISHT, jo te gjendja: `zeriGabimi` brenda
    // kësaj mbylljeje është vlera e renderimit të kaluar, dhe pikërisht ajo
    // vjetërsi bëri që arsyeja e vërtetë të mbulohej nga ajo e pajisjes.
    let arsyejaServeri = "";

    (async () => {
      if (turneCfg?.zeriNdezur) {
        try {
          const { data, error } = await supabase.functions.invoke("turne-zeri", {
            body: { teksti: fjalia, gjuha: uiGjuha },
          });
          if (!anuluar && !error && data?.url && audioRef.current) {
            setZeriGabimi("");
            const indeksi = turneIndeks;
            audioRef.current.onended = () => setLexiMbaroi(indeksi);
            audioRef.current.src = data.url;
            audioRef.current.play().catch((e) => {
              setZeriGabimi(t("The browser blocked the sound: {{arsyeja}}", { arsyeja: String(e?.message || e) }));
            });
            return;
          }
          if (!anuluar) {
            arsyejaServeri = turneArsyeja(await extractFunctionErrorMessage(error, data));
            setZeriGabimi(arsyejaServeri);
          }
        } catch (e) {
          if (!anuluar) {
            arsyejaServeri = turneArsyeja(String(e?.message || e));
            setZeriGabimi(arsyejaServeri);
          }
        }
      }
      if (anuluar || !ss) return;
      const zëri = gjejZërin();
      if (!zëri) {
        if (turneCfg?.zeriNdezur && !arsyejaServeri) {
          setZeriGabimi(t("This device has no voice for {{gjuha}} — the tour stays silent, with captions.", { gjuha: emriIGjuhës }));
        }
        return;
      }
      const u = new SpeechSynthesisUtterance(fjalia);
      u.voice = zëri; u.lang = zëri.lang; u.rate = 0.95;
      const indeksi = turneIndeks;
      u.onend = () => setLexiMbaroi(indeksi);
      ss.cancel(); ss.speak(u);
    })();

    // Vetëm kërkesa në fluturim anulohet. Audioja që tashmë po luan NUK ndalet
    // këtu — ky ishte pikërisht defekti.
    return () => { anuluar = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turneAktiv, turneZëri, turnePauza, turneIndeks, turneCfg, uiGjuha, gjejZërin, turneFjalia]);

  // ── E RIKTHYER PAS 26 GUSHTIT ─────────────────────────────────────────────
  // Ky bllok u fshi pa dashje kur u rishkrua efekti i zërit: zëvendësimi kapte
  // nga fillimi i efektit deri te ky koment, dhe midis tyre rrinin nisja,
  // ndalja, vazhdimi, llogaritja e kohës dhe efekti i rrahjeve. Aplikacioni ra
  // me `ReferenceError: nisTurneun is not defined` — dhe esbuild-i s'e kapi
  // dot, sepse ai kontrollon SINTAKSËN, jo referencat. Një emër i papërcaktuar
  // është JavaScript krejt i ligjshëm derisa të xhirohet.
  const nisTurneun = () => {
    if (turneHapat.length < 3) return;
    if (!audioRef.current) {
      try { audioRef.current = new Audio(); audioRef.current.preload = "auto"; }
      catch { audioRef.current = null; }
    }
    setActiveScreen("tree");
    turneRrahjaRef.current = "";
    turneLexuarRef.current = "";
    setTurneIndeks(0); setTurneFaza(0); setTurnePauza(false); setTurneAktiv(true);
  };
  // ── KIOSKA: laku, timoni, dhe ringarkimi ────────────────────────────────
  //
  // MUZEU5. Te një ekran salle turneu nuk «mbaron» — ai është gjendja e qetë e
  // aparatit. Prandaj fundi i listës nuk e fik: e rikthen te fillimi.
  //
  // Ringarkimi pas N lakesh nuk është kujdes i tepërt: dhjetë orë lak
  // grumbullojnë kujtesë te çdo shfletues, dhe një ekran që ngec në mesditë e
  // ka humbur ditën. Bëhet PAS një laku, kurrë në mes — askush s'po shikon
  // atëherë.
  const lakuRef = useRef(0);
  const mbaroLaku = useCallback(() => {
    turneRrahjaRef.current = "";
    setTurneIndeks(0);
    setTurneFaza(0);
    setSelectedId(null);
    if (!kiosku) { setTurneAktiv(false); return; }
    lakuRef.current += 1;
    if (lakuRef.current >= Math.max(1, Number(llojiHapesires.kiosk_lake) || 12)) {
      try { window.location.reload(); } catch (_e) { lakuRef.current = 0; }
      return;
    }
    // Mbetet aktiv: `turneIndeks = 0` e nis lakun e radhës vetvetiu.
  }, [kiosku, llojiHapesires.kiosk_lake]);

  const ndalTurneun = () => {
    turneRrahjaRef.current = "";
    turneLexuarRef.current = "";
    try { window.speechSynthesis?.cancel(); } catch { /* nuk prish gjë */ }
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute("src"); } } catch { /* njëjtë */ }
    setTurneAktiv(false); setTurnePauza(false); setTurneFaza(0); setSelectedId(null);
  };
  // "Vazhdo" e rikthen pamjen te personi dhe e rinis fjalinë — përndryshe pas
  // një endjeje do të numëronte mbi një ekran që s'ka lidhje me tekstin poshtë.
  const vazhdoTurneun = () => {
    turneRrahjaRef.current = "";
    turneLexuarRef.current = "";
    setTurnePauza(false);
  };

  // SA ZGJAT NJË HAP
  //
  // Koha del nga vetë teksti (~2,4 fjalë në sekondë, ritmi i një zëri të qetë),
  // dhe cilësimi i Admin Center-it është KUFI I POSHTËM, jo i prerë. Kur zëri
  // lexon vërtet, ai vendos: hapi mbaron kur mbaron fjalia, dhe kohëmatësi
  // mbetet vetëm rrjetë sigurie nëse zëri s'e njofton kurrë fundin.
  const turneKoha = useCallback((hap) => {
    const minSek = turneCfg?.sekonda || 8;
    const fjalë = turneFjalia(hap).split(/\s+/).filter(Boolean).length;
    return Math.max(minSek * 1000, Math.round((fjalë / 2.4) * 1000) + 1400);
  }, [turneCfg, turneFjalia]);

  useEffect(() => {
    if (!turneAktiv || !turneHapat.length) return;
    const hap = turneHapat[Math.min(turneIndeks, turneHapat.length - 1)];
    if (!hap) return;
    // Hapi i familjes (hyrja/fundi) s'ka person: pamje e gjerë mbi krejt pemën,
    // pa profil, dhe një rrahje e vetme.
    const eshteFamilje = !hap.id;
    const rrahja = `${turneIndeks}:${turneFaza}`;
    if (turneRrahjaRef.current !== rrahja) {
      turneRrahjaRef.current = rrahja;
      // `fitView` dhe jo `focusOn`: na duhet VETËM qendërzimi te rrahja e parë.
      // `focusOn` e hap edhe profilin — punë e saktë për gishtin e njeriut, e
      // gabuar këtu, ku profili ka rrahjen e vet.
      if (eshteFamilje) { fitView(); setSelectedId(null); }
      else if (turneFaza === 0) { fitView(hap.id); setSelectedId(null); }
      else setSelectedId(hap.id);
    }
    if (turnePauza) return;

    const gjithsej = turneKoha(hap);
    const udhetimi = Math.min(2600, Math.max(1200, Math.round(gjithsej * 0.3)));
    // Kur zëri po lexon vërtet, kohëmatësi s'guxon ta presë fjalinë: bëhet
    // tavan i gjerë, dhe fundin e vërtetë e thotë vetë zëri (`lexiMbaroi`).
    const meZë = turneZëri && !!zeriBurimi;
    const leximi = Math.max(1500, (meZë ? gjithsej * 3 : gjithsej) - udhetimi);

    const koha = setTimeout(() => {
      if (!eshteFamilje && turneFaza === 0) { setTurneFaza(1); return; }
      setSelectedId(null);
      setTurneFaza(0);
      if (turneIndeks + 1 < turneHapat.length) setTurneIndeks(turneIndeks + 1);
      else { mbaroLaku(); }
    }, eshteFamilje ? Math.max(2500, meZë ? gjithsej * 3 : gjithsej) : (turneFaza === 0 ? udhetimi : leximi));
    return () => clearTimeout(koha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turneAktiv, turnePauza, turneIndeks, turneFaza, turneHapat.length, turneKoha, turneZëri, zeriBurimi]);

  // KUR MBARON LEXIMI, MBARON HAPI.
  //
  // Po jo para se profili të jetë parë: nëse fjalia mbaron ende gjatë udhëtimit,
  // pritet derisa profili të hapet, plus një frymë. Përndryshe kartela do të
  // pulsonte për një çast dhe do të dukej si defekt.
  useEffect(() => {
    if (!turneAktiv || turnePauza) return;
    const hapAktual = turneHapat[turneIndeks];
    const eshteFamilje = hapAktual && !hapAktual.id;
    if (lexiMbaroi !== turneIndeks) return;
    if (!eshteFamilje && turneFaza !== 1) return;
    const koha = setTimeout(() => {
      setLexiMbaroi(-1);
      setSelectedId(null);
      setTurneFaza(0);
      if (turneIndeks + 1 < turneHapat.length) setTurneIndeks(turneIndeks + 1);
      else { mbaroLaku(); }
    }, 1400);
    return () => clearTimeout(koha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lexiMbaroi, turneIndeks, turneFaza, turneAktiv, turnePauza, turneHapat.length]);

  // VIZITORI MERR TIMONIN. Çdo prekje a rrotullim jashtë shiritit të turneut e
  // ndal numërimin. Një turne që nuk lëshohet është video me hapa shtesë.
  useEffect(() => {
    if (!turneAktiv || turnePauza) return;
    const dora = (e) => {
      if (e?.target?.closest?.("[data-turne-kontroll]")) return;
      setTurnePauza(true);
    };
    window.addEventListener("pointerdown", dora, true);
    window.addEventListener("wheel", dora, true);
    return () => {
      window.removeEventListener("pointerdown", dora, true);
      window.removeEventListener("wheel", dora, true);
    };
  }, [turneAktiv, turnePauza]);
  // VIZITORI E LËSHON TIMONIN. Gjysma e dytë e rregullit të gushtit: prekja e
  // ndal turneun (më sipër), dhe pas qetësisë ai e merr sërish.
  //
  // Pa këtë, ekrani i sallës do të mbetej i ngrirë aty ku e la vizitori i fundit
  // — dhe i pari që vjen nesër do të gjente një profil të hapur pa kontekst.
  // Numëratori rinisi me çdo prekje: `turnePauza` bëhet e vërtetë sërish, dhe
  // efekti ri-xhirohet.
  useEffect(() => {
    if (!kiosku || !turneAktiv || !turnePauza) return;
    const sek = Math.max(5, Number(llojiHapesires.kiosk_pauza) || 40);
    let ora = null;
    const rinis = () => {
      if (ora) clearTimeout(ora);
      ora = setTimeout(() => setTurnePauza(false), sek * 1000);
    };
    rinis();
    window.addEventListener("pointerdown", rinis, true);
    window.addEventListener("wheel", rinis, true);
    window.addEventListener("keydown", rinis, true);
    return () => {
      if (ora) clearTimeout(ora);
      window.removeEventListener("pointerdown", rinis, true);
      window.removeEventListener("wheel", rinis, true);
      window.removeEventListener("keydown", rinis, true);
    };
  }, [kiosku, turneAktiv, turnePauza, llojiHapesires.kiosk_pauza]);

  // Te kioska turneu nis vetë. Jo me çdo render — vetëm një herë, kur hapat janë
  // gati: një ekran salle që pret dikë të shtypë «Nis» është një ekran i fikur.
  const kioskaUNisRef = useRef(false);
  useEffect(() => {
    if (!kiosku || kioskaUNisRef.current) return;
    if (!turneHapat.length) return;
    kioskaUNisRef.current = true;
    setTurneIndeks(0);
    setTurneFaza(0);
    setTurnePauza(false);
    setTurneAktiv(true);
  }, [kiosku, turneHapat.length]);

  useEffect(() => {
    if (!pendingFocusId || !positions[pendingFocusId] || !containerRef.current) return;
    // FIX — njëjtë si te focusOn sipër, ripërdor fitView(id) në vend të
    // llogaritjes së vjetër të thjeshtë.
    fitView(pendingFocusId);
    setSelectedId(pendingFocusId);
    setPendingFocusId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFocusId, positions]);
  useEffect(() => {
    if (!pendingRecenterId || !positions[pendingRecenterId] || !containerRef.current) return;
    const pos = positions[pendingRecenterId];
    const rect = containerRef.current.getBoundingClientRect();
    setPan((prev) => ({ x: rect.width / 2 - pos.x * H_SPACING, y: rect.height / 2 - pos.y * V_SPACING }));
    setPendingRecenterId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRecenterId, positions]);
  // Një herë për krejt pemën, jo një herë për rresht. Përdoret nga çdo listë ku
  // njeriu ZGJEDH një person: kërkimi te pema, anëtarët, posteri, etiketat e
  // fotove, fleta e shtimit.
  const etiketatDalluese = useMemo(() => ndertoEtiketatDalluese(people), [people]);
  const searchMatches = useMemo(() => {
    if (!search.trim()) return [];
    const l = search.toLowerCase();
    return Object.values(people).filter((p) => displayName(p).toLowerCase().includes(l)
      || emriDallues(p, etiketatDalluese).toLowerCase().includes(l)).slice(0, 8);
  }, [search, people, etiketatDalluese]);
  const visibleCount = Object.keys(visiblePeople).length;

  const coupleCenterX = (personId) => {
    const person = visiblePeople[personId];
    const pos = positions[personId];
    if (!pos) return 0;
    if (!person) return pos.x;
    const spouseXs = (person.spouse_ids || []).filter((s) => positions[s]).map((s) => positions[s].x);
    if (spouseXs.length === 0) return pos.x;
    const xs = [pos.x, ...spouseXs];
    return (Math.min(...xs) + Math.max(...xs)) / 2;
  };

  // FIX (raportuar nga useri, me foto krahasuese) — REDIZAJNIM i plotë,
  // sipas shembullit të dhënë: viza horizontale mes bashkëshortëve duhet
  // të kalojë EKZAKTËSISHT në lartësinë e fotove (jo poshtë tekstit),
  // zemra saktësisht NË MES të asaj vije, dhe vija vertikale drejt
  // fëmijëve NISET pikërisht nga zemra. `photoCenterY` = e njëjta formulë
  // e përdorur nga clip-path-i i vetë fotos (shih më poshtë, "10 + 6 +
  // rrezja") — kështu linja/zemra binë GJITHMONË saktësisht mbi qendrën e
  // fotos, pavarësisht madhësisë. Butoni "+/-" MBETET te ROW_CONTENT_H
  // (më poshtë, afër fundit të kartelës) — natyrshëm larg nga
  // photoCenterY (afër majës), pa u mbivendosur më me zemrën.
  const photoCenterY = 10 + 6 + Math.max(14, Math.min(30, CARD_W * 0.21));
  // Te «heirloom» fotoja rri gjithmonë lart, prandaj pika e bashkimit është
  // qendra e fotos pavarësisht topologjisë — me `CARD_H / 2` vija e çiftit u
  // vizatonte mbi emrat, jo mes fytyrave.
  const coupleJunctionY = (rowY) => rowY * V_SPACING + ((treeLineTopology === "parent_child" || pemaHeirloom()) ? photoCenterY : CARD_H / 2) + (treeAdvLayout.spouse_line_offset_y || 0);
  // v2.1 — FIX (raportuar nga useri: "tre fëmijë solo nga dy prindër të
  // ndryshëm — vijat horizontale janë në të njëjtin nivel dhe preken").
  // Çdo prind me fëmijë solo merr KORSINË e vet vertikale: hapësira më e
  // gjatë horizontale shkon më lart (korsia 0), më e shkurtra më poshtë —
  // pra vijat "futen" njëra brenda tjetrës pa u prekur, në vend që të
  // shtrihen të gjitha në të njëjtën lartësi.
  // v2.5 — një burim i VETËM i së vërtetës për "ku është pika e bashkimit të
  // një çifti": përdoret NJËSOJ nga vijat drejt fëmijëve TË PËRBASHKËT dhe
  // nga ikona e statusit (zemra). Kështu vija e një fëmije niset gjithmonë
  // pikërisht nga zemra e marrëdhënies prej së cilës ai e ka prejardhjen —
  // jo nga lidhja kryesore e prindit. Kthen NJËSI RRJETE (jo piksel).
  const coupleAnchorX = (idA, idB) => {
    if (!positions[idA] || !positions[idB]) return null;
    const multiA = ((people[idA]?.spouse_ids) || []).length > 1;
    const multiB = ((people[idB]?.spouse_ids) || []).length > 1;
    const anchorId = multiA && !multiB ? idA : multiB && !multiA ? idB : null;
    const fromId = anchorId ? (anchorId === idA ? idB : idA) : idA;
    const toId = anchorId || idB;
    const fromX = positions[fromId].x, toX = positions[toId].x, rowY = positions[fromId].y;
    const dir = toX >= fromX ? 1 : -1;
    let nearestX = toX;
    Object.entries(positions).forEach(([oid, op]) => {
      if (oid === fromId || op.y !== rowY) return;
      const d = (op.x - fromX) * dir;
      if (d > 0 && d < (nearestX - fromX) * dir) nearestX = op.x;
    });
    return (fromX + nearestX) / 2;
  };
  const soloBus = new Map();
  {
    // ======================================================================
    // v2.6 — VIJAT E FËMIJËVE SOLO: forma "V" me rregullim zinxhir
    // ----------------------------------------------------------------------
    // Prioriteti, siç u kërkua:
    //  1. Grupi = një prind + TË GJITHË fëmijët e tij solo (vëllezërit kurrë
    //     s'ndahen në dy lartësi).
    //  2. Renditja nis nga grupet MË TË JASHTME të secilës anë dhe ecën drejt
    //     qendrës; i jashtmi merr nivelin më të LARTË, pastaj zbritet — pra
    //     "V", jo piramidë. Të dyja anët nisin nga E NJËJTA lartësi bazë.
    //  3. Para se të fiksohet një nivel, kontrollohet nëse vija do të prekte
    //     ndonjë objekt (kartelë, ikonë statusi, buton +/-). Nëse po, ulet
    //     derisa të ketë distancë të mjaftueshme.
    //  4. Çdo ulje TRANSMETOHET te vijat pasuese drejt qendrës — ato nisin
    //     nga niveli i ri, jo nga ai fillestar, që rendi dhe distancat mes
    //     tyre të ruhen gjithmonë.
    // Asgjë nuk varet nga emra apo nga persona konkretë.
    // ======================================================================
    // v3.0 — ALGORITMI ËSHTË I VETËMJAFTUESHËM.
    // Lartësitë llogariten tërësisht nga vetë struktura e pemës (grupet, anët,
    // mbivendosjet, objektet pengesë) — nuk varen më nga asnjë vlerë e Admin
    // Center. Kjo do të thotë: as vlerat e vjetra të ruajtura, as ndryshimet
    // atje nuk mund ta prishin formën "V". Cilësimet e Admin Center që prekin
    // gjeometrinë e vijave lihen qëllimisht JASHTË kësaj llogaritjeje; ato që
    // mbeten aktive janë vetëm ato thjesht pamore (ngjyra, trashësi, stil).
    const CLEAR = 14; // distanca minimale nga çdo objekt
    const STEP = 15;  // hapi maksimal mes niveleve (zvogëlohet vetë sipas vendit)

    // --- objektet që një vijë horizontale s'duhet t'i prekë ---
    const obstacles = [];
    const photoRAll = Math.max(14, Math.min(30, CARD_W * 0.21));
    const cardVisualH = pemaHeirloom()
      ? lartesiaEMedaljonit()
      : Math.min(ROW_CONTENT_H, photoCenterY + photoRAll + 36);
    Object.entries(positions).forEach(([oid, pos]) => {
      obstacles.push({ id: oid, x1: pos.x * H_SPACING, x2: pos.x * H_SPACING + CARD_W,
                       top: pos.y * V_SPACING, bottom: pos.y * V_SPACING + cardVisualH });
    });
    expandButtons.forEach((b) => {
      obstacles.push({ small: true, x1: b.x - 13, x2: b.x + 13, top: b.y - 13, bottom: b.y + 13 });
    });
    {
      const seenPair = new Set();
      Object.values(visiblePeople).forEach((p) => {
        (p.spouse_ids || []).forEach((sid) => {
          const key = [p.id, sid].sort().join("-");
          if (seenPair.has(key)) return;
          if (!positions[p.id] || !positions[sid] || positions[p.id].y !== positions[sid].y) return;
          seenPair.add(key);
          const ax = coupleAnchorX(p.id, sid);
          if (ax == null) return;
          const cx = ax * H_SPACING + CARD_W / 2, cy = coupleJunctionY(positions[p.id].y);
          obstacles.push({ small: true, x1: cx - 12, x2: cx + 12, top: cy - 12, bottom: cy + 12 });
        });
      });
    }
    // Kartelat e VETË prindërve të një grupi nuk numërohen si pengesë — vija
    // e tyre nis pikërisht aty, s'ka kuptim ta shmangë vetveten.
    const hits = (x1, x2, y, skip) => obstacles.some((o) =>
      !(o.id && skip && skip.has(o.id)) &&
      x2 > o.x1 - CLEAR && x1 < o.x2 + CLEAR && y > o.top - CLEAR && y < o.bottom + CLEAR);

    // --- grupet familjare (komponentë të lidhur në grafin e martesave) ---
    const clusterKeyOf = new Map(), clusterMembers = new Map();
    {
      const seen = new Set();
      Object.keys(positions).forEach((id) => {
        if (seen.has(id)) return;
        const stack = [id], members = [];
        while (stack.length) {
          const cur = stack.pop();
          if (seen.has(cur) || !positions[cur]) continue;
          seen.add(cur); members.push(cur);
          ((people[cur]?.spouse_ids) || []).forEach((sp) => { if (positions[sp] && !seen.has(sp)) stack.push(sp); });
        }
        const k = members.slice().sort()[0];
        clusterMembers.set(k, members);
        members.forEach((m) => clusterKeyOf.set(m, k));
      });
    }
    const centerOfCluster = (k) => {
      const m = clusterMembers.get(k) || [];
      if (!m.length) return 0;
      let maxN = -1;
      m.forEach((x) => { maxN = Math.max(maxN, ((people[x]?.spouse_ids) || []).length); });
      const top = m.filter((x) => ((people[x]?.spouse_ids) || []).length === maxN);
      const xs = (maxN > 1 ? top : m).map((x) => positions[x].x);
      return (Math.min(...xs) + Math.max(...xs)) / 2;
    };

    // --- një grup = TË GJITHË vëllezërit që kanë TË NJËJTIN set prindërish.
    // Mbulon si fëmijët solo (1 prind) ashtu edhe ata të përbashkët (2
    // prindër) — të dyja llojet duhet të ndjekin të njëjtën formë "V".
    // Pika e nisjes së grupit: prindi vetë (solo) ose ZEMRA e asaj
    // marrëdhënieje specifike (të përbashkët) — e njëjta ankorë si ikona.
    const groups = new Map();
    Object.values(visiblePeople).forEach((p) => {
      const par = (p.parent_ids || []).filter((x) => positions[x]);
      if (!par.length || !positions[p.id]) return;
      const key = par.slice().sort().join("|");
      const pPos = positions[par[0]];
      if (par.length === 2 && positions[par[1]] && positions[par[1]].y !== pPos.y) return;
      let originX = pPos.x;
      if (par.length === 2) {
        const a = coupleAnchorX(par[0], par[1]);
        if (a != null) originX = a;
      }
      const cPos = positions[p.id];
      const g = groups.get(key) || { pid: key, y: pPos.y, minX: originX, maxX: originX,
                                     own: new Set(par), cluster: clusterKeyOf.get(par[0]) || par[0] };
      g.minX = Math.min(g.minX, cPos.x);
      g.maxX = Math.max(g.maxX, cPos.x);
      groups.set(key, g);
    });

    const sets = {};
    groups.forEach((g) => {
      const k = `${g.y}|${g.cluster}`;
      if (!sets[k]) sets[k] = [];
      sets[k].push(g);
    });

    Object.values(sets).forEach((arr) => {
      const centerX = centerOfCluster(arr[0].cluster);
      const rowY = arr[0].y;
      const photoR = photoRAll;
      arr.forEach((g) => {
        g.outer = Math.max(Math.abs(g.minX - centerX), Math.abs(g.maxX - centerX));
        g.side = ((g.minX + g.maxX) / 2) >= centerX ? 1 : -1;
        g.px1 = Math.min(g.minX, g.maxX) * H_SPACING + CARD_W / 2;
        g.px2 = Math.max(g.minX, g.maxX) * H_SPACING + CARD_W / 2;
      });
      // Kufiri i sipërm: pika e parë poshtë kartelave ku një vijë që
      // përshkon krejt gjerësinë e këtij grupi NUK prek asnjë objekt —
      // kartelë, ikonë statusi apo buton +/-. Kjo e mban `hits` në përdorim
      // (më parë ishte bërë kod i vdekur) dhe garanton që as butoni minus
      // të mos preket, pa e ngushtuar brezin siç bënte ulja për çdo grup.
      // Kufiri i poshtëm ABSOLUT: mbi fotot e fëmijëve. Asnjë vijë s'guxon
      // ta kalojë kurrë këtë — përndryshe do të dilte NËN rreshtin e
      // fëmijëve dhe personat do të dukeshin sipër saj.
      const limitY = coupleJunctionY(rowY + 1) - photoR - CLEAR;
      let winTop = Math.min(limitY, rowY * V_SPACING + cardVisualH + CLEAR);
      {
        // Kërkimi shikon VETËM objektet e rreshtit të prindërve (kartela,
        // ikona statusi, buton +/-). Më parë përfshiheshin edhe kartelat e
        // fëmijëve, ndaj kërkimi s'ndalej kurrë dhe e çonte vijën shumë
        // poshtë tyre — pikërisht defekti i raportuar.
        const rowBottom = (rowY + 1) * V_SPACING;
        // VETËM kartelat. Objektet e vogla (butoni +/-, ikonat e statusit)
        // qëllimisht nuk hyjnë këtu: ato janë në një pikë të vetme, dhe po
        // t'i lejonim të bllokonin krejt gjerësinë, do ta shtynin nivelin 0
        // poshtë tyre dhe do të mbetej fare pak vend për nivelet e tjera —
        // pikërisht arsyeja pse forma "V" dukej e sheshtë. Ato shmangen më
        // poshtë, individualisht, vetëm nga grupet që u kalojnë sipër.
        const rowObs = obstacles.filter((o) => !o.small && o.bottom <= rowBottom);
        const rx1 = Math.min(...arr.map((g) => g.px1));
        const rx2 = Math.max(...arr.map((g) => g.px2));
        const hitsRow = (y) => rowObs.some((o) =>
          rx2 > o.x1 - CLEAR && rx1 < o.x2 + CLEAR && y > o.top - CLEAR && y < o.bottom + CLEAR);
        let guard = 0;
        while (guard++ < 200 && winTop < limitY && hitsRow(winTop)) winTop += 2;
        winTop = Math.min(winTop, limitY);
      }
      const maxY = limitY;
      // ── NIVELET SIPAS MBIVENDOSJES, JO SIPAS RENDIT ──────────────────
      // Brezi i lirë mes dy rreshtave është i ngushtë, ndaj s'ka kuptim t'i
      // japim nivel të veçantë ÇDO grupi. Dy grupe që s'mbivendosen
      // horizontalisht mund të rrinë fare mirë në TË NJËJTIN nivel — syri
      // s'i ngatërron dot, sepse ndodhen në pjesë krejt të ndryshme të
      // rreshtit. Kështu numri i niveleve bie ndjeshëm dhe secili merr
      // hapësirë reale për t'u dalluar.
      // Renditja mbetet nga jashtë brenda: grupet më të jashtme (të dyja
      // anët) zënë nivelin 0, dhe vetëm ata që PËRPLASEN me to zbresin.
      const ordered = arr.slice().sort((x, y) =>
        (y.outer - x.outer) || String(x.pid).localeCompare(String(y.pid)));
      const levelSpans = [];
      const MARGIN = CARD_W * 0.35;
      ordered.forEach((g) => {
        let lv = 0;
        while (levelSpans[lv] && levelSpans[lv].some((sp) =>
          g.px2 > sp[0] - MARGIN && g.px1 < sp[1] + MARGIN)) lv++;
        if (!levelSpans[lv]) levelSpans[lv] = [];
        levelSpans[lv].push([g.px1, g.px2]);
        g.level = lv;
      });
      const topLevel = Math.max(0, ...ordered.map((g) => g.level));
      // Niveli 0 nis DREJTPËRDREJT te kufiri i sipërm. Më parë ai ulej me
      // hapa derisa të pastronte çdo objekt — por meqë vijat e gjata
      // përshkojnë krejt rreshtin, ulja e çonte deri në fund dhe nuk mbetej
      // hapësirë për nivelet e tjera: të gjitha ngjesheshin bashkë. Meqë
      // `winTop` është tashmë poshtë kartelave dhe butonit +/-, ajo ulje
      // ishte e panevojshme.
      const startY = winTop;
      const band = Math.max(0, maxY - startY);
      const step = topLevel > 0 ? Math.min(STEP * 3, band / topLevel) : 0;
      // Offset-i i Admin Center vepron si zhvendosje UNIFORME e të gjitha
      // niveleve (vlerë negative = më lart). Nuk garon më me asnjë kufi, ndaj
      // sillet gradualisht dhe nuk e prish kurrë formën "V".
      // DY kontrolle të ndara, siç u kërkua:
      //  • "Offset për fëmijët solo (px)"  → grupet me NJË prind
      //  • "Zhvendosja e vijës për anëtarët normalë" → grupet me DY prindër
      // E dyta jepet si përqindje (−0.3…0.3) dhe kthehet në piksela mbi
      // distancën mes brezave. Më parë ajo lidhej te `buildConnectorPath`, ku
      // vepronte VETËM kur lartësia s'ishte caktuar shprehimisht — dhe meqë
      // tani caktohet gjithmonë, kishte mbetur pa asnjë efekt.
      // NJË kontroll i vetëm global për lartësinë e të gjitha vijave
      // horizontale në krejt pemën. Hapësirat mes niveleve (solo kundrejt të
      // tjerëve, nga jashtë drejt qendrës) i vendos VETËM algoritmi — asnjë
      // cilësim nuk i prek ato, ndaj forma "V" mbetet gjithmonë e njëjtë,
      // thjesht e ngritur ose e ulur si tërësi.
      const nudgeAll = Number(treeAdvLayout.solo_bend_offset) || 0;
      // sa lart mund të ngjiten me offset negativ — kurrë mbi fotot e
      // prindërve, dhe kurrë nën kufirin e poshtëm
      const lo = Math.min(maxY, rowY * V_SPACING + photoCenterY + photoR + 6);
      ordered.forEach((g) => {
        // Asnjë shmangie individuale këtu: më parë një vijë që kalonte mbi
        // butonin +/- shtyhej vetëm ajo poshtë, dhe kur ngriheshin të gjitha
        // të tjerat, ajo mbetej në vend — dukej sikur cilësimi s'e prek. Tani
        // të gjitha lëvizin njësoj; butoni vizatohet sipër vijave, me sfond
        // të vetin, ndaj vija duket sikur kalon prapa tij.
        g.busY = Math.max(lo, Math.min(maxY, startY + g.level * step + nudgeAll));
      });
      arr.forEach((g) => soloBus.set(g.pid, { busY: g.busY, level: g.level, top: topLevel }));
    });
  }
  const lines = [];
  // SHTESË (kërkesë e userit) — topologji e re "parent_child": (a) linja
  // synon FËMIJËN E GJAKUT (cPos.x), jo mesin e çiftit të tij; (b) NJË
  // linjë e vetme për çift prindëror (jo e dyfishtë kur prindërit janë
  // bashkëshortë të njëri-tjetrit — dedup me çelës "prind1-prind2-fëmijë").
  // Origjinali ("original") mbetet EKZAKTËSISHT si ishte, i paprekur.
  const seenParentChildLines = new Set();
  // SHTESË (kërkesë e userit, me shigjetë krahasuese) — kur babai ka 3+
  // nuse (rasti i vendosur poshtë, shih multiWifeTiers), babai DHE nëna
  // NUK janë më në të njëjtin rresht — mesatarizimi i pozicionit të tyre
  // (posA.x+posB.x)/2 prodhonte një pikë krejt të gabuar (mes dy brezash
  // të ndryshëm), duke ia dërguar vijën diku krejt tjetër. Këto lookup-e
  // identifikojnë kur njëri prind është "babai i tiered", që ta trajtojmë
  // ndryshe (poshtë).
  const tieredWifeIds = new Set();
  Object.values(multiWifeTiers).forEach((wIds) => wIds.forEach((wId) => tieredWifeIds.add(wId)));
  Object.values(visiblePeople).forEach((p) => {
    (p.parent_ids || []).forEach((pid) => {
      const cPos = positions[p.id], pPos = positions[pid];
      if (!cPos || !pPos) return;
      // FIX (raportuar nga useri, me shigjetë) — nëse `pid` është "babai i
      // tiered" DHE prindi tjetër i `p`-së është njëra nga nuset e TIJ të
      // regjistruara, ky është pikërisht ai fëmijë-i-nipit që s'duhet
      // marrë vijë TË DREJTPËRDREJTË nga babai (babai lidhet VETËM me
      // nusen, jo me nipin/mbesën — ajo lidhje tashmë vizatohet nga
      // "nusja→fëmija" te iterimi tjetër, kur pid=nusja). Pa këtë kontroll,
      // dilnin DY vija: një e saktë (nusja→fëmija) dhe një krejt e gabuar
      // (babai→fëmija, mesatarizuar mes dy brezash) — pikërisht ajo që
      // shënoi useri me shigjetë.
      const otherParentId = (p.parent_ids || []).find((x) => x !== pid);
      if (multiWifeTiers[pid] && otherParentId && multiWifeTiers[pid].includes(otherParentId)) return;
      // FIX (raportuar nga useri — "kur personi ka disa martesa/divorc, s'
      // kuptohet fare kush ka nënë nga vijat") — `coupleCenterX(pid)`
      // llogariste mesataren e POZICIONIT TË TË GJITHË bashkëshortëve të
      // `pid`-it (aktualë, të divorcuar, të ndjerë — të gjithë përzier
      // bashkë), jo VETËM prindin tjetër SPECIFIK të `p`-së. Kur dikush
      // ka disa marrëdhënie, kjo prodhonte një pikë "mesatare të
      // përzier" pa kuptim (as te njëri bashkëshort, as te tjetri). Tani,
      // te "parent_child" me saktësisht 2 prindër të njohur, përdoret
      // mesi i ATYRE DY specifikëve (çifti i VËRTETË prindëror i p-së),
      // jo çdo bashkëshort tjetër i parëndësishëm i pid-it.
      let startX;
      let soloOfTieredHusband = false;
      let quickSidewaysBend = false;
      if (treeLineTopology === "parent_child" && tieredWifeIds.has(pid)) {
        // Nusja e "tiered" — vetë pozicioni i saj, PA mesatarizim me
        // babain (janë në breza krejt të ndryshëm tani).
        startX = pPos.x;
        // FIX (raportuar nga useri, me shigjetë — "rasti 3+, fëmija solo i
        // nuses: vija duhet të kthehet MENJËHERË djathtas, jo të zbresë
        // poshtë nenës fillimisht") — kjo ndodh VETËM kur fëmija ka 1
        // prind (solo, i zhvendosur djathtas nga `wifeAnchorOf`) — fëmijët
        // E PËRBASHKËT (2 prindër) mbeten siç ishin, s'e kanë nevojën
        // (janë tashmë pranë pozicionit të nuses, s'ka zhvendosje).
        if ((p.parent_ids || []).length === 1) quickSidewaysBend = true;
      } else if (treeLineTopology === "parent_child" && (p.parent_ids || []).length === 1) {
        // FIX (raportuar nga useri — "kur ka një prind shkon nga prindi,
        // nuk lyp mes" — rregull i PËRGJITHSHËM, jo vetëm për babain me
        // disa marrëdhënie) — çdo fëmijë me VETËM 1 prind të njohur (baba
        // "solo", OSE nënë "solo" — kur nusja ka fëmijë të vetat, jo të
        // përbashkët) lidhet DREJTPËRDREJT me pozicionin E VETË ATIJ
        // prindi — kurrë s'mesatarizohet me asnjë marrëdhënie tjetër.
        startX = pPos.x;
        soloOfTieredHusband = !!multiWifeTiers[pid];} else if (treeLineTopology === "parent_child" && (p.parent_ids || []).length === 2) {
        const [pa, pb] = p.parent_ids;
        const posA = positions[pa], posB = positions[pb];
        // v2.5 — pikërisht te zemra e ASAJ marrëdhënieje (jo mesi i verbër i
        // dy kartelave, që mund të bjerë mbi një bashkëshort tjetër në mes)
        const anchored = coupleAnchorX(pa, pb);
        startX = anchored != null ? anchored : (posA && posB) ? (posA.x + posB.x) / 2 : coupleCenterX(pid);
      } else {
        startX = coupleCenterX(pid);
      }
      // FIX (raportuar nga useri — "viza nga prindërit tek fëmijët/çift
      // s'e prek njëra tjetrën, duket si deformim") — më parë y1/y2
      // synonin buzën E SIPËRME/E POSHTME të vetë kartelës (CARD_H / 0),
      // JO lartësinë e vijës horizontale të ÇIFTIT (CARD_H/2, shih
      // spouseLines poshtë). Kur prindi/fëmija ishte pjesë e një çifti,
      // viza horizontale e çiftit ishte në një lartësi TJETËR nga ku
      // ndalej viza vertikale — "kontakti fizik" mes tyre varej rastësisht
      // nga a e mbulonte kartela hendekun mes tyre, jo nga vetë gjeometria.
      // Tani që dyja synojnë SAKTËSISHT CARD_H/2, lidhen fizikisht, kudo.
      const y1 = coupleJunctionY(pPos.y);
      const y2 = coupleJunctionY(cPos.y);
      const x1 = startX * H_SPACING + CARD_W / 2;
      let x2, dedupeKey;
      if (treeLineTopology === "parent_child") {
        // (a) synon direkt fëmijën e gjakut, jo mesin e çiftit të tij.
        x2 = cPos.x * H_SPACING + CARD_W / 2;
        // (b) dedup — nëse dy prindër (p.sh. mami+babi) janë bashkëshortë
        // të njëri-tjetrit, coupleCenterX(mami) === coupleCenterX(babi),
        // pra do prodhonin DY linja identike të mbivendosura; çelësi
        // (startX+endX+y1+y2) i bën ato "të njëjtën" linjë, e dyta anashkalohet.
        dedupeKey = `${startX}-${cPos.x}-${y1}-${y2}`;
        if (seenParentChildLines.has(dedupeKey)) return;
        seenParentChildLines.add(dedupeKey);
      } else {
        x2 = coupleCenterX(p.id) * H_SPACING + CARD_W / 2;
      }
      let forceMidY;
      if (soloOfTieredHusband) {
        forceMidY = coupleJunctionY(pPos.y + 0.5); // bashkimi babai→nuse (siç ishte)
      } else if (!quickSidewaysBend && treeLineTopology === "parent_child" && (p.parent_ids || []).length === 1 && x1 !== x2) {
        // FIX (raportuar nga useri — "kthesa e shpejtë ishte ndërtuar
        // VETËM për rastin 3+ (tiered); rasti me 2 nuse s'e kishte fare
        // këtë trajtim, prandaj përdorej kthesa standarde 50%") — kusht i
        // PËRGJITHSHËM: ÇDO fëmijë solo (baba OSE nënë, në ÇDO rast —
        // 2 nuse OSE 3+) që është ZHVENDOSUR anash (x1≠x2) e merr këtë
        // trajtim, jo vetëm rasti "tiered".
        quickSidewaysBend = true;
      }
      if (quickSidewaysBend) {
        // FIX (kërkesë e userit — pozicion i saktë, jo përqindje arbitrare)
        // — kthesa fillon PIKËRISHT te fundi i kartelës së nënës (aty ku
        // është data e lindjes, ROW_CONTENT_H — e njëjta lartësi që
        // përdor butoni "+/-"), jo më në 20% të distancës. Kjo është pika
        // e saktë "mes datës së lindjes së nënës dhe rreshtit ku lidhet
        // fëmija" — e përcaktuar qartë, jo e llogaritur si përqindje.
        // FIX (raportuar nga useri, herë e dytë — "e bëre në anën e
        // gabuar, jo poshtë, lart, më afër nënës") — ROW_CONTENT_H (fundi
        // i kartelës) doli SHUMË poshtë. Tani përdoret pika saktë NËN
        // FOTON e nënës (photoCenterY + rrezja e fotos — e njëjta formulë
        // e përdorur nga clip-path-i i vetë fotos) — sa më afër saj që të
        // jetë e mundur, menjëherë sapo mbaron fotoja, PARA emrit/datës.
        const photoR = Math.max(14, Math.min(30, CARD_W * 0.21));
        forceMidY = pPos.y * V_SPACING + photoCenterY + photoR;
      }
      // v2.7 — lartësia e llogaritur me formën "V" (nga jashtë drejt qendrës,
      // me shmangie objektesh dhe rregullim zinxhir) zbatohet për ÇDO vijë
      // prind→fëmijë të asaj familjeje: si për fëmijët solo, ashtu edhe për
      // ata të përbashkët. Kjo ishte pjesa që mungonte — më parë preknin
      // vetëm solo-fëmijët, ndaj vijat e fëmijëve të përbashkët mbeteshin te
      // thyerja standarde dhe forma dilte piramidë.
      if (!soloOfTieredHusband) {
        const gKey = (p.parent_ids || []).filter((x) => positions[x]).slice().sort().join("|");
        const bus = soloBus.get(gKey);
        if (bus) forceMidY = bus.busY;
      }

      lines.push(<path key={`${pid}-${p.id}`} d={buildConnectorPath(x1, y1, x2, y2, treeLineStyle, forceMidY)} fill="none" stroke={ngjyraEVijes()} strokeWidth={treeAdvLayout.line_thickness ?? 1.6} strokeDasharray={treeAdvLayout.line_dash === "dashed" ? "6,4" : treeAdvLayout.line_dash === "dotted" ? "1.5,4" : undefined} strokeLinejoin="round" strokeLinecap="round" />);
    });
  });
  // SHTESË (kërkesë e userit, me shembull vizual) — lidhja babai→nëna kur
  // ka 3+ nëna (të vendosura një brez poshtë babait nga computeLayout,
  // shih multiWifeTiers) — vizatohet SI DEGËZIM I ZAKONSHËM (trung + degë,
  // njësoj si prind→fëmijë), JO si vijë çifti/zemër (ato tashmë s'kanë
  // kuptim këtu — nënat s'janë më "ngjitur" pranë babait).
  Object.entries(multiWifeTiers).forEach(([husbandId, wifeIds]) => {
    const hPos = positions[husbandId];
    if (!hPos) return;
    const y1 = coupleJunctionY(hPos.y);
    const x1 = hPos.x * H_SPACING + CARD_W / 2;
    wifeIds.forEach((wId) => {
      const wPos = positions[wId];
      if (!wPos) return;
      const y2 = coupleJunctionY(wPos.y);
      const x2 = wPos.x * H_SPACING + CARD_W / 2;
      lines.push(<path key={`mw-${husbandId}-${wId}`} d={buildConnectorPath(x1, y1, x2, y2, treeLineStyle)} fill="none" stroke={ngjyraEVijes()} strokeWidth={treeAdvLayout.line_thickness ?? 1.6} strokeDasharray={treeAdvLayout.line_dash === "dashed" ? "6,4" : treeAdvLayout.line_dash === "dotted" ? "1.5,4" : undefined} strokeLinejoin="round" strokeLinecap="round" />);
    });
  });
  const spouseLines = [];
  const seenPairs = new Set();
  Object.values(visiblePeople).forEach((p) => {
    (p.spouse_ids || []).forEach((sId) => {
      const key = [p.id, sId].sort().join("-");
      if (seenPairs.has(key) || !positions[p.id] || !positions[sId]) return;
      seenPairs.add(key);
      const a = positions[p.id], b = positions[sId];
      if (a.y !== b.y) return;
      // FIX (ide e userit — "vija të nisë nga mesi i kutisë, meqë
      // gjithsesi është në background, kjo jep siguri nëse ndryshon
      // madhësia e kornizës së fotos në të ardhmen") — MË E FORTË se
      // llogaritja e mëparshme (bazuar te rrezja e fotos, që mund të
      // ndryshojë me çdo redizajnim stili). Tani: vija shtrihet THJESHT
      // QENDËR-ME-QENDËR (CARD_W/2 te secili person) — GJITHMONË gjatësi
      // pozitive e saktë (a.x ndryshon nga b.x gjithmonë, s'ka rrezik
      // përplasjeje). Vija vizatohet PRAPA kartelave (shih më poshtë,
      // {spouseLines} u zhvendos para {Object.values(visiblePeople).map}
      // sërish) — pjesa "nën" çdo foto mbulohet natyrshëm nga vetë
      // fotoja, mbetet e dukshme VETËM hendeku real mes dy fotove,
      // pavarësisht sa e madhe/vogël është fotoja apo si ndryshon në të
      // ardhmen.
      const sx1 = a.x * H_SPACING + CARD_W / 2, sx2 = b.x * H_SPACING + CARD_W / 2;
      const sy = coupleJunctionY(a.y);
      // FIX (kërkesë e userit — "bëj të gjitha vijat të njëjtën ngjyrë,
      // vijat mes partnerëve ngjyrë tjetër nga prind-fëmijë") — hoqa
      // ngjyrosjen "një ngjyrë për çdo marrëdhënie" (provë e mëparshme) —
      // tani ripozicionimi (majtas/djathtas ose poshtë-babait, shih
      // computeLayout) e zgjidh dallimin VIZUALISHT, ngjyra mbetet e
      // thjeshtë/konsistente kudo, siç kërkohet.
      spouseLines.push(<line key={key} x1={sx1} y1={sy} x2={sx2} y2={sy} stroke={ngjyraEVijesSeCiftit()} strokeWidth={treeAdvLayout.line_thickness ?? 1.6} strokeLinecap="round" />);
    });
  });
  // SHTESË (posteri dy-familjesh "krah-për-krah") — dy pemët janë tashmë
  // grafe TË PALIDHUR (asnjë dedup), ndaj pika e bashkimit tregohet QARTË:
  // vijë e ndërprerë mes DY KOPJEVE të personit urë (i njëjti njeri, një
  // herë te secila pemë) + etiketë. Kjo zëvendëson "ngjitjen" e vjetër të
  // rastësishme që e bënte të paqartë se kush lidhej me kë.
  if (twoFamilyActive && twoFamLayout === "sidebyside" && twoFamBridge) {
    // FIX (id-të e urës mund të jenë të vdekura): nëse person_a/person_b
    // s'kanë pozicion, vija vizatohet mes ÇFARËDO çifti kopjesh ndër-familjare
    // (synced_from) ku të dyja anët janë të dukshme — i njëjti njeri në të
    // dy pemët, preferohet vetë personi urë (i shënuar _twofam_first).
    let idA = positions[twoFamBridge.person_a] ? twoFamBridge.person_a : null;
    let idB = positions[twoFamBridge.person_b] ? twoFamBridge.person_b : null;
    if (!idA || !idB) {
      const cand = Object.values(visiblePeople)
        .filter((p) => p.synced_from_person_id && visiblePeople[p.synced_from_person_id]
          && visiblePeople[p.synced_from_person_id].family_id !== p.family_id
          && positions[p.id] && positions[p.synced_from_person_id])
        .sort((a, b) => (b._twofam_first ? 1 : 0) - (a._twofam_first ? 1 : 0))[0];
      if (cand) {
        const other = cand.synced_from_person_id;
        // ana A = kopja në familjen aktive → majtas
        if (visiblePeople[cand.id].family_id === activeFamilyId) { idA = cand.id; idB = other; }
        else { idA = other; idB = cand.id; }
      }
    }
    const ba = idA && positions[idA], bb = idB && positions[idB];
    if (ba && bb) {
      const bx1 = ba.x * H_SPACING + CARD_W / 2, by1 = ba.y * V_SPACING + CARD_H / 2;
      const bx2 = bb.x * H_SPACING + CARD_W / 2, by2 = bb.y * V_SPACING + CARD_H / 2;
      const bmx = (bx1 + bx2) / 2;
      const btop = Math.min(by1, by2) - 70;
      spouseLines.push(
        <g key="twofam-bridge">
          <path d={`M ${bx1} ${by1} C ${bmx} ${btop}, ${bmx} ${btop}, ${bx2} ${by2}`} fill="none" stroke={COLORS.gold} strokeWidth="2.2" strokeDasharray="8 6" strokeOpacity="0.8" strokeLinecap="round" />
          <text x={bmx} y={btop - 8} textAnchor="middle" style={{ fontFamily: FONTS.body, fontSize: 13, fill: COLORS.gold, fontWeight: 600 }}>{t("Same person in both trees")}</text>
        </g>
      );
    }
  }

  // SHTESË (kërkesë e userit) — ikonë statusi (❤️ aktiv / 💔 divorcuar /
  // 🕊️ i-e ve) te pika ku bashkohet çifti, VETËM te topologjia e re
  // "parent_child" (ku trungu tashmë nis pikërisht nga ajo pikë). "I-e ve"
  // përdor 🕊️ — të NJËJTËN ikonë që aplikacioni përdor tashmë KUDO tjetër
  // për "i/e ndjerë", për qëndrueshmëri vizuale, jo simbol i ri i
  // panjohur, siç kërkoi vetë useri ("nëse ke sugjerim më të mirë"), dhe
  // e ndarë QARTË nga 💔 (divorc) — dy gjendje krejt të ndryshme, siç u
  // theksua shprehimisht. Një ikonë E VETME për çift, edhe nëse ka disa
  // fëmijë (dedup sipas çiftit të VETË 2 prindërve, jo për fëmijë).
  // v2.1 — FIX (raportuar nga useri: "ikona te Test2 është gabim në ngarkimin
  // e parë, por rregullohet sapo shtypet +"). SHKAKU: pozicioni varej nga
  // `multiMarriageParents`, i cili llogaritet nga FËMIJËT E DUKSHËM — kur
  // fëmijët janë ende të mbyllur, ai Set është BOSH, ndaj kodi binte te
  // "mesi mes dy bashkëshortëve", që bie pikërisht mbi kartelën e
  // bashkëshortes në mes. Tani ankora përcaktohet nga VETË TË DHËNAT (kush
  // ka disa marrëdhënie), pa asnjë varësi nga çfarë është e dukshme — ndaj
  // rezultati është i njëjtë në renderimin e parë dhe pas çdo zgjerimi.
  const coupleIconX = (idA, idB) => {
    const v = coupleAnchorX(idA, idB);
    return v == null ? null : v * H_SPACING + CARD_W / 2;
  };
  const coupleStatusIcons = [];
  // SHTESË (kërkesë e userit) — vendos statusin/ikonën e saktë mes 5
  // gjendjeve (❤️ Martuar/💔 Shkurorëzuar/🖤 Partner i ndjerë/🤝
  // Bashkëjetesë/💍 Fejuar), duke respektuar edhe dukshmërinë e zgjedhur
  // nga admini (nëse është fikur, kthen null — s'shfaqet fare, por statusi
  // vetë MBETET i ruajtur në të dhëna, thjesht s'shfaqet si ikonë).
  // Prioriteti (kur ka disa flamuj njëkohësisht — rrallë, por e mundur):
  // divorcuar > i/e ndjerë > bashkëjetesë > fejuar > martuar (parazgjedhje).
  // ── PAMJA «HEIRLOOM»: SHENJA E ÇIFTIT ───────────────────────────────────
  // 4 shtator 2026. Pronari, para pemës: «plusi edhe zemra e dominojnë krejt
  // pemën», dhe më herët: «i buk zëvendësimi i zemrave me gjëra ma jo brutale».
  //
  // Kishte të drejtë. Një zemër e kuqe mbi vijën që lidh dy njerëz bërtet më
  // fort se vetë njerëzit — dhe te një pemë familjeje njerëzit janë përmbajtja.
  // Te mockup-i i tij nuk ka asnjë zemër: ka dy unaza të ndërthurura për të
  // martuarit dhe një unazë me gur për të fejuarit, të holla, në ar, mbi një
  // disk në ngjyrën e sfondit.
  //
  // Vizatohet me elemente SVG të thjeshta e jo me komponentët e `ikonatHeirloom`,
  // sepse këtu jemi BRENDA një `<svg>`: një `<svg>` i mbivendosur do të punonte
  // te shfletuesi, po do të sillte kornizë e shkallë të vetën pikërisht aty ku
  // gjithçka tjetër matet në koordinatat e pemës.
  const shenjaECiftitHeirloom = (key, cx, cy, icon, title) => {
    const ari = PALETA_TIPI.bronze || COLORS.gold;
    // 4 shtator 2026 — FJALORI, jo emoji. Shih shënimin te `ikonatHeirloom.jsx`:
    // kuptimi qëndron te largësia mes dy unazave. Fejesa e ka unazën me gur.
    // Divorci dhe partneri i ndjerë mbeten të zbehta — ndarja dhe humbja nuk
    // duan një simbol që bërtet, duan të njëjtin fjalor më të qetë.
    const eZbehte = icon === "\ud83d\udc94" || icon === "\ud83d\udda4";
    const F = icon === "\ud83d\udc8d" ? UnazaMeGur
            : icon === "\ud83e\udd1d" ? UnazatQePreken
            : icon === "\ud83d\udc94" ? UnazatENdara
            : icon === "\ud83d\udda4" ? UnazatMePellumb
            : UnazatNderthurura;
    const P = F === UnazaMeGur ? 15 : 19;
    return (
      /* 4 shtator 2026 — «ikona është më e errët se ç'duhet, po e dominon
         pemën, po merr shumë vëmendje». Një shenjë statusi duhet gjetur kur e
         kërkon, jo parë e para: prandaj 0.62 në vend të 1, dhe 0.42 për ato
         që tashmë ishin të zbehta. Vija e çiftit vetë rri te ~0.55 — shenja
         tani është pak më e fortë se vija, jo dyfish. */
      <g key={key} data-status-icon="1" transform={`translate(${cx},${cy})`} opacity={eZbehte ? 0.42 : 0.62}>
        <title>{title}</title>
        {/* 4 shtator 2026 — «nuk dua të jenë në një sfond të bardhë ose të
            hijezuar, vetëm simboli». Disku ishte mënyra ime për ta ndarë
            shenjën nga vija; po ai krijonte edhe dy pamje të ndryshme për të
            njëjtën ikonë, dhe pikërisht kjo e prishte standardin: «është
            problem nëse në pemë nuk ka një standard».
            Tani vija ndërpritet vetë — një segment i shkurtër me ngjyrën e
            sfondit, pa kufi — dhe mbi të rri vetëm simboli. */}
        <F x={-P / 2} y={-P / 2} size={P} color={ari} />
      </g>
    );
  };

  const resolveCoupleStatus = (personA, personB, idA, idB) => {
    // Dukshmëria e ikonave vjen tani nga rule_sets (Rregullat & Ngjyrat), jo nga
    // cilësimet e vjetra. `!== false` → parazgjedhja shfaq (nëse s'është vënë).
    const _si = ACTIVE_RULES.status_icons || {};
    const isDivorced = (personA?.divorced_from_ids || []).includes(idB) || (personB?.divorced_from_ids || []).includes(idA);
    if (isDivorced) return _si.show_divorced !== false ? { icon: "💔", title: t("Divorced") } : null;
    const isWidowed = !!personA?.death_date || !!personB?.death_date;
    if (isWidowed) return _si.show_widowed !== false ? { icon: "🖤", title: t("Late partner") } : null;
    const isCohabiting = (personA?.cohabiting_ids || []).includes(idB) || (personB?.cohabiting_ids || []).includes(idA);
    if (isCohabiting) return _si.show_cohabiting !== false ? { icon: "🤝", title: t("Cohabitation") } : null;
    const isEngaged = (personA?.engaged_ids || []).includes(idB) || (personB?.engaged_ids || []).includes(idA);
    if (isEngaged) return _si.show_engaged !== false ? { icon: "💍", title: t("Engaged") } : null;
    return _si.show_married !== false ? { icon: "❤️", title: t("Married") } : null;
  };
  if (treeLineTopology === "parent_child") {
    const seenCouples = new Set();
    Object.values(visiblePeople).forEach((p) => {
      if ((p.parent_ids || []).length !== 2) return;
      const [pid1, pid2] = p.parent_ids;
      const key = [pid1, pid2].sort().join("-");
      if (seenCouples.has(key)) return;
      const pos1 = positions[pid1], pos2 = positions[pid2];
      if (!pos1 || !pos2 || pos1.y !== pos2.y) return;
      seenCouples.add(key);
      const person1 = people[pid1], person2 = people[pid2];
      const isDivorced = (person1?.divorced_from_ids || []).includes(pid2) || (person2?.divorced_from_ids || []).includes(pid1);
      const isWidowed = !isDivorced && (!!person1?.death_date || !!person2?.death_date);
      // FIX (kërkesë e userit — "hiqim krejt zemrat te marrëdhëniet joaktive
      // majtas") — te martesa e shumëfishtë, statusi i divorcuar/i ndjerë
      // JASHTË zemrës është tashmë i dukshëm (ikona te vetë foto e
      // personit + vetë pozicioni majtas) — zemra këtu hiqet fare për ta,
      // mbetet VETËM për marrëdhënien aktive, për të shmangur pozicionim
      // piksel-për-piksel të pasigurt.
      // v2: heqja e dikurshme e ikonës te martesa e shumëfishtë nuk nevojitet
      // më — layout-i i ri garanton hapësirë të mjaftueshme mes ÇDO dy
      // bashkëshortëve fqinjë (gjerësia e kartelës + vendi i ikonës), ndaj
      // ikona s'ka më si të mbivendoset. Tani çdo status shfaqet.
      void isDivorced; void isWidowed;
      const status = resolveCoupleStatus(person1, person2, pid1, pid2);
      if (!status) return; // ikona e këtij statusi është fikur nga admini
      const { icon, title } = status;
      // FIX (raportuar nga useri — "pse s'shfaqen zemrat te test/testo
      // test") — te martesa e shumëfishtë, nëse njëri prind është "babai"
      // (multiMarriageParents), zemra vendoset PRANË VETË NUSES specifike
      // (jo në mes të dyve) — kështu s'ka rrezik të "kapërcejë" mbi ndonjë
      // nuse tjetër që ndodhet fizikisht mes tyre (rasti normal — martesë
      // e vetme — mbetet i paprekur, ende në mes, siç ka qenë gjithmonë).
      // FIX (kërkesë e userit — "llogaritja duhet bërë gjithmonë nga
      // personi që bart ikonën deri te personi më i afërt, sipas
      // SIMETRISË, jo sipas vetë lidhjes") — në vend të një offseti fiks
      // (-22px, që supozonte gjithmonë babai është menjëherë ngjitur),
      // gjendet FQINJI I VËRTETË më i afërt i nuses (në drejtim të babait
      // — kushdo qoftë fizikisht atje, jo domosdoshmërisht vetë babai),
      // dhe ikona vendoset PIKËRISHT NË MES të nuses dhe atij fqinji —
      // simetrike, e saktë pavarësisht rendit real në rresht.
      const cx = coupleIconX(pid1, pid2);
      if (cx == null) return;
      // Zemra tani ndodhet SAKTËSISHT te pika e bashkimit (lartësia e
      // fotove) — e njëjta pikë prej nga niset vija vertikale drejt
      // fëmijëve (y1 te "lines" më sipër). Butoni "+/-" mbetet larg,
      // afër fundit të kartelës (ROW_CONTENT_H) — pa mbivendosje më,
      // sepse photoCenterY (afër majës) dhe ROW_CONTENT_H (afër fundit)
      // janë natyrshëm larg njëra-tjetrës.
      const cy = coupleJunctionY(pos1.y);
      coupleStatusIcons.push(
        shenjatHeirloom() ? shenjaECiftitHeirloom(key, cx, cy, icon, title) : (
        <g key={key} data-status-icon="1" transform={`translate(${cx},${cy})`}>
          <circle r="9" fill={COLORS.bgDeep} stroke={COLORS.gold} strokeWidth="1.2" />
          <text x="0" y="4" textAnchor="middle" style={{ fontSize: 10 }} title={title}>{icon}</text>
        </g>)
      );
    });
    // FIX (raportuar nga useri — "testo test është lidhje aktuale, duhet ta
    // ketë zemrën e pathyer, në sinkron me nuset e tjera") — dega më sipër
    // e llogarit zemrën VETËM nga një fëmijë (parent_ids.length===2) — një
    // çift PA fëmijë (ende) s'kishte asnjë mënyrë të merrte zemër fare. Kjo
    // shtesë mbulon ÇDO çift bashkëshortor të dukshëm, direkt nga
    // `spouse_ids`, pavarësisht nëse kanë fëmijë — e njëjta llogaritje
    // statusi (aktiv/divorcuar/i-e ve), thjesht burim tjetër të dhënash.
    Object.values(visiblePeople).forEach((p) => {
      (p.spouse_ids || []).forEach((sId) => {
        const key = [p.id, sId].sort().join("-");
        if (seenCouples.has(key) || !positions[p.id] || !positions[sId]) return;
        const pos1 = positions[p.id], pos2 = positions[sId];
        if (pos1.y !== pos2.y) return;
        // FIX (raportuar nga useri, dy herë — "zemra u vend gabim", pastaj
        // "pse s'shfaqen zemrat te test/testo test") — provë e mëparshme
        // (mesi mes dy personave) shkaktonte kapërcim mbi kë ndodhej mes
        // tyre; PROVA E DYTË (anulim krejt kur ndodhej dikush mes) ishte
        // shumë rigoroze, bllokonte zemrën për ÇDO nuse jo-ngjitur direkt
        // me babain (rasti normal te martesa e shumëfishtë). Tani: zemra
        // vendoset PRANË VETË NUSES specifike (jo në mes) kur njëri prej
        // të dyve është "babai" (multiMarriageParents) — s'ka më rrezik
        // kapërcimi, ndaj s'nevojitet fare kontroll/anulim.
        seenCouples.add(key);
        const person1 = p, person2 = people[sId];
        const isDivorced = (person1.divorced_from_ids || []).includes(sId) || (person2?.divorced_from_ids || []).includes(p.id);
        const isWidowed = !isDivorced && (!!person1.death_date || !!person2?.death_date);
        // FIX (kërkesë e userit — "hiqim krejt zemrat te marrëdhëniet
        // joaktive majtas") — njësoj si dega tjetër më sipër.
        // v2 — shih shënimin te dega më sipër.
        void isDivorced; void isWidowed;
        const status = resolveCoupleStatus(person1, person2, p.id, sId);
        if (!status) return; // ikona e këtij statusi është fikur nga admini
        const { icon, title } = status;
        // FIX (i njëjti rregullim simetrie si dega tjetër më sipër).
        const cx = coupleIconX(p.id, sId);
        if (cx == null) return;
        const cy = coupleJunctionY(pos1.y);
        coupleStatusIcons.push(
          shenjatHeirloom() ? shenjaECiftitHeirloom(key, cx, cy, icon, title) : (
          <g key={key} data-status-icon="1" transform={`translate(${cx},${cy})`}>
            <circle r="9" fill={COLORS.bgDeep} stroke={COLORS.gold} strokeWidth="1.2" />
            <text x="0" y="4" textAnchor="middle" style={{ fontSize: 10 }} title={title}>{icon}</text>
          </g>)
        );
      });
    });
  }

  // ==========================================================================
  // FAZA 43 — forma e identifikimit e nxjerrë si funksion i ripërdorshëm:
  // përdoret njësoj brenda Modal-it (siç ishte) DHE brenda ekranit të ri
  // "locked" (poshtë) — një burim i vetëm i së vërtetës, jo dy kopje që mund
  // të dalin nga sinkronizimi.
  // ==========================================================================
  // Parametri `AC` (auth colors) — lejon që E NJËJTA formë të marrë paletën e
  // variantit të ekranit të hyrjes, pa u dyfishuar kodi. Pa argument sillet
  // saktësisht si më parë (ngjyrat e aplikacionit).
  const renderAuthFormBody = (AC = COLORS) => {
    // Hapet vetvetiu kur ka diçka për të parë brenda: gabim, regjistrim i ri,
    // link i dërguar, ose një email tashmë i shkruar.
    const emailShown = emailOpen || signupEmailOnly || !!authError || resetSent || magicLinkSent || !!authForm.email;
    // DYQANET1 — rreshti «Merre në telefon». Android sheh vetëm Play, iPhone/iPad
    // vetëm App Store, desktopi të dy. Distinktivi zyrtar vjen nga
    // /dyqani/<emri>.svg (skedar statik); kur mungon, del një buton teksti.
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const eshteAndroid = /Android/i.test(ua), eshteIOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);
    const dyqaniPlay = dyqanet.play_url && !eshteIOS ? dyqanet.play_url : null;
    const dyqaniApple = dyqanet.appstore_url && !eshteAndroid ? dyqanet.appstore_url : null;
    const rreshtiIDyqaneve = !isNativeApp && (dyqaniPlay || dyqaniApple) ? (
      <div style={{ marginTop: 6, paddingTop: 10, borderTop: `1px solid ${AC.goldSoft}`, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 12, color: AC.inkLight, letterSpacing: ".02em" }}>{t("auth.getTheApp")}</span>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          {dyqaniPlay && <Distinktivi href={dyqaniPlay} skedari="google-play.svg" teksti={t("auth.onGooglePlay")} />}
          {dyqaniApple && <Distinktivi href={dyqaniApple} skedari="app-store.svg" teksti={t("auth.onAppStore")} />}
        </div>
      </div>
    ) : null;
    const inputStyleA = AC === COLORS ? inputStyle : {
      ...inputStyle, background: AC.panelSoft || AC.card, color: AC.ink,
      border: `1px solid ${AC.cardBorder || AC.goldSoft}`,
    };
    return (
    needsEmailConfirm ? (
      /* FAZA 3: Supabase kërkoi konfirmim email-i para se sesioni të aktivizohej */
      <div className="space-y-3">
        <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.ink }}>
          {t("auth.checkEmailTitle", { email: authForm.email })}
        </p>
        <button onClick={() => { setAuthOpen(false); resetAuthForm(); }} className="w-full py-2.5 rounded-md text-sm font-medium" style={{ background: AC.gold, color: AC.bgDeep }}>{t("auth.close")}</button>
      </div>
    ) : authSessionLoading ? (
      <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight }}>{t("auth.checkingSession")}</p>
    ) : authSession && !linkedPersonChecked && !paFamilje ? (
      /* FIX (raportuar: "pas login-it, para se ngarkohet pema, RISHFAQET
         ekrani i Google-it dhe zhduket" — sa më shumë persona, aq më gjatë).
         Shkaku: ka sesion Auth të vlefshëm, por zgjidhja e personit të lidhur
         (rpc_my_linked_people → fetchPeople → fetchRoles → families) ende s'ka
         mbaruar, kështu që `currentPersonId` është null → `locked` bëhet true →
         kjo formë binte te dega e fundit dhe SHFAQTE sërish butonat "Vazhdo me
         Google". Kohëzgjatja rritet me numrin e personave (fetchPeople).
         Zgjidhja: derisa kontrolli i lidhjes të përfundojë, shfaq VETËM një
         tregues "duke identifikuar…", KURRË formën e hyrjes — s'ka më rishfaqje
         mashtruese të Google-it, pavarësisht sa gjatë zgjat ngarkimi. */
      <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight }}>{t("auth.checkingSession")}</p>
    ) : authSession && (notLinkedYet || paFamilje) ? (
      /* `paFamilje` shtohet këtu bashkë me `notLinkedYet` për një arsye të
         thjeshtë: nëse porta mbyllet pa u ngritur flamuri, kjo formë duhet ta
         dijë gjithashtu — përndryshe do të mbetej te dega e parë («duke
         identifikuar…») dhe njeriu do të shihte një ekran që ngarkohet përjetë. */
      /* FAZA 3: ka sesion Auth të vlefshëm, por asnjë person i kësaj peme
         s'është lidhur me të ende — anëtarësimi mbetet vetëm me ftesë
         (shih DECISIONS.md), kështu që këtu vetëm shpjegojmë, s'ofrojmë
         t("request to join"). */
      <div className="space-y-3">
        <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.ink }}>
          {t("auth.notLinkedYet", { email: authSession.user.email })}
        </p>
        {/* HEQUR më 31 gusht 2026 — rreshti i tri butonave «Bashkohu me familje
            tjetër (Kod) · Krijo familje të re · Dil» qëndronte pikërisht këtu.
            Ai u shtua si «rrugëdalje, që përdoruesi të mos mbetet i ngecur»,
            pa vënë re se poshtë, brenda së njëjtës kartelë, ekzistonin tashmë
            të tria: «Kam një Kod Familjeje», «Krijo familjen tënde» dhe
            «Dilni dhe provoni një llogari tjetër».
            Rrjedhoja që raportoi pronari: çdo veprim shfaqej DY HERË.
            Mbeti grupi i poshtëm — ai e shpjegon edhe ftesën, dhe butonat e tij
            janë të plotë, jo të ngjeshur në një rresht. */}
        {/* VETË-BASHKIM (pa ftesë) — familjet ku email-i yt përputhet me një
            person ende të palidhur. E shfaqim SI TABELË: emri/mbiemri që kemi
            për ty në atë familje + emri i familjes, dhe një buton "Bashkohu".
            Vetëm Google/Apple e mbushin këtë (backend-i e filtron). */}
        {matchableFamilies.length > 0 && (
          <div className="space-y-2" style={{ marginBottom: 6 }}>
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: AC.ink }}>
              {t("auth.selfJoinIntro", { defaultValue: t("These families have your email and data. Choose where you want to join:") })}
            </p>
            {matchableFamilies.map((f) => (
              <div key={f.person_id} className="flex items-center justify-between gap-2 py-2 px-3 rounded-md" style={{ background: AC.panelSoft, border: `1px solid ${AC.goldSoft}` }}>
                <div style={{ minWidth: 0 }}>
                  <div className="truncate" style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.ink, fontWeight: 600 }}>{f.family_name}</div>
                  <div className="truncate" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: AC.inkLight }}>{f.first_name} {f.last_name}</div>
                </div>
                <button
                  onClick={() => handleSelfJoin(f.person_id)}
                  disabled={selfJoinBusy === f.person_id}
                  className="py-1.5 px-3 rounded-md text-sm font-medium shrink-0"
                  style={{ background: AC.gold, color: AC.bgDeep, opacity: selfJoinBusy === f.person_id ? 0.6 : 1 }}
                >
                  {selfJoinBusy === f.person_id ? t("auth.pleaseWait") : t("auth.selfJoinButton", { defaultValue: "Bashkohu" })}
                </button>
              </div>
            ))}
          </div>
        )}
        {authError ? (
          <p style={{ color: AC.danger, fontSize: 13 }}>{authError}</p>
        ) : myLinkedFamilies.length > 0 ? (
          /* FAZA 12 — zgjedhësi i familjeve: llogaria ËSHTË e lidhur,
             thjesht jo me këtë instancë specifike (familje tjetër nga
             ajo që po shikohet tani, p.sh. dikush që i përket trungut
             të vet DHE familjes ku ka hyrë me martesë). Tregoji si
             linqe të drejtpërdrejta, në vend të "kërko ftesë". */
          <div className="space-y-2">
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: AC.inkLight }}>{t("auth.belongsToOtherFamilies")}</p>
            {myLinkedFamilies.map((f) => (
              <a
                key={f.person_id}
                href={`/f/${f.family_slug}`}
                onClick={(e) => { e.preventDefault(); shkoTeFamilja(f.family_slug); }}
                className="block w-full py-2 px-3 rounded-md text-sm text-left"
                style={{ background: AC.panelSoft, color: AC.ink, textDecoration: "none" }}
              >
                {f.family_name} <span style={{ color: AC.inkLight, fontSize: 11.5 }}>({f.first_name} {f.last_name})</span>
              </a>
            ))}
            <a
              href="/join"
              className="block w-full py-2 px-3 rounded-md text-sm text-center"
              style={{ background: "transparent", color: AC.ink, textDecoration: "none", border: `1px solid ${AC.goldSoft}` }}
            >
              {t("Join another family (Code)")}
            </a>
          </div>
        ) : (
          <div className="space-y-2">
            {/* DY DYER ME PESHË TË BARABARTË (1 shtator 2026).

                Më parë: «Krijo familjen tënde» ishte i mbushur me `AC.gold`,
                kurse «Kam një Kod Familjeje» kishte `AC.panelSoft` — pra TË
                NJËJTIN sfond si vetë kartela — me një kufi të zbehtë. Dhe
                butoni i daljes poshtë përdorte po atë sfond. Rezultati: kodi
                dukej pjesë e mobiljes, jo derë, dhe krijimi e dominonte
                dritaren.

                Por këta të dy nuk janë kryesor dhe dytësor. Janë dy dyer për
                dy njerëz të ndryshëm — dhe hierarkia ishte e PËRMBYSUR ndaj
                realitetit: çdo familje ka NJË themelues dhe dhjetëra anëtarë
                të ftuar. Veprimi më i rrallë bërtiste; ai më i shpeshti
                fshihej.

                Tani dallohen nga FJALËT dhe IKONA, jo nga zhurma. Ari ruhet
                për veprimin brenda secilës rrugë («Krijo familjen» te /start),
                jo për zgjedhjen mes tyre — sepse ne nuk e dimë cila i duhet
                këtij vizitori, dhe fjalët e bëjnë drejtimin më mirë se ngjyra. */}
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.ink, marginBottom: 2 }}>
              {t("auth.chooseDoor", { defaultValue: "Si dëshiron të vazhdosh?" })}
            </p>

            <a
              href="/join"
              className="block w-full rounded-md"
              style={{ background: AC.panelSoft, color: AC.ink, textDecoration: "none",
                       border: `1px solid ${AC.goldSoft}`, padding: "12px 14px",
                       display: "flex", alignItems: "flex-start", gap: 11 }}
            >
              <KeyRound size={18} color={AC.ink} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, color: AC.ink }}>
                  {t("I have a Family Code")}
                </span>
                <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 12.5, color: AC.inkLight, lineHeight: 1.45, marginTop: 2 }}>
                  {t("auth.doorCodeDesc", { defaultValue: "Dikush nga familja të ka dërguar një kod ose një ftesë." })}
                </span>
              </span>
            </a>
            {/* HISTORIKU I KËTIJ KUSHTI, dhe pse u hoq më 1 shtator 2026.

                Fillimisht «Krijo familjen tënde» nuk ekzistonte fare këtu.
                U shtua, por vetëm kur adresa NUK ishte `/f/<slug>` — logjika:
                kush hap adresën e një familjeje të caktuar po përpiqet të hyjë
                atje, ndaj oferta për të krijuar familje do ta ngatërronte.
                (Më parë kushti ishte `!familySlug`, çka e fshihte edhe nga
                kujtesa e pajisjes — ndreqje e 31 gushtit.)

                Por pasoja ishte RRUGË PA KRYE: një person te `/f/haliti` që
                në të vërtetë do të niste pemën e vet kishte vetëm «kod» ose
                «dil». Useri e hasi vetë duke testuar.

                Dhe frika që e shkaktoi fshehjen ishte frikë nga NGATËRRIMI —
                e cila ekzistonte sepse butoni ishte dy fjalë të arta pa asnjë
                shpjegim. Tani secila derë e thotë vetë se kujt i takon
                («Nis një pemë të re nga fillimi — ti bëhesh personi i parë»),
                ndaj askush nuk e shtyp duke menduar se po bashkohet.

                Kundërmasa u hoq sepse problemi që e shkaktoi u zgjidh ndryshe:
                me fjalë, jo me fshehje. */}
            <a
                href="/start"
                className="block w-full rounded-md"
                style={{ background: AC.panelSoft, color: AC.ink, textDecoration: "none",
                         border: `1px solid ${AC.goldSoft}`, padding: "12px 14px",
                         display: "flex", alignItems: "flex-start", gap: 11 }}
              >
                <PlusCircle size={18} color={AC.ink} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, color: AC.ink }}>
                    {t("Create your family")}
                  </span>
                  <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 12.5, color: AC.inkLight, lineHeight: 1.45, marginTop: 2 }}>
                    {t("auth.doorCreateDesc", { defaultValue: "Nis një pemë të re nga fillimi — ti bëhesh personi i parë." })}
                  </span>
                </span>
              </a>
            {/* Shpjegimi i ftesës zbriti këtu, poshtë dyerve: është
                informacion për atë që nuk ka ende as kod as ftesë, jo një
                hyrje e tretë. Më parë rrinte SIPËR dhe e kornizonte kodin si
                zgjidhje për atë që dështoi — kur ai është rruga normale. */}
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: AC.inkLight, lineHeight: 1.5, marginTop: 4 }}>
              {t("auth.askForInvite")}
            </p>
          </div>
        )}
        {/* Dalja mban peshën më të vogël nga të gjitha — më parë kishte të
            njëjtin sfond si butoni i kodit, çka i bënte të dukeshin njësoj. */}
        <button onClick={handleSignOut} className="w-full py-2.5 rounded-md text-sm font-medium" style={{ background: "transparent", color: AC.inkLight, border: `1px solid ${AC.goldSoft}` }}>{t("auth.signOutTryAnother")}</button>
      </div>
    ) : (
      /* Kartela e identifikimit — e ndërtuar sipas mockup-it: etiketa e
         vogël "VAZHDO ME", dy butona të bardhë, ndarësi "ose", fushat me
         ikona brenda, rreshti "më mbaj të kyçur / harrove fjalëkalimin",
         butoni i kuq, dhe poshtë vijës rruga drejt llogarisë së re. */
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ textAlign: "center", fontSize: 12.5, fontWeight: 600, letterSpacing: ".1em", color: AC.inkLight, fontFamily: FONTS.body }}>
          {t("auth.continueWith", { defaultValue: "VAZHDO ME" })}
        </div>

        <button onClick={handleGoogleSignIn} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#fff", color: "#1f1f1f", border: "1.5px solid rgba(0,0,0,.10)", fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: "pointer" }}>
          <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M45 24.5c0-1.6-.1-2.7-.4-3.9H24v7.1h12c-.2 1.9-1.5 4.7-4.4 6.6l6.7 5.2c4-3.7 6.7-9.1 6.7-15z"/><path fill="#34A853" d="M24 46c5.9 0 10.8-1.9 14.4-5.3l-6.9-5.3c-1.8 1.3-4.3 2.2-7.5 2.2-5.7 0-10.6-3.8-12.3-9l-7.1 5.5C8.2 41.1 15.5 46 24 46z"/><path fill="#FBBC05" d="M11.7 28.6c-.4-1.3-.7-2.7-.7-4.1s.3-2.8.7-4.1l-7.1-5.5C3.6 17.6 3 20.7 3 24s.6 6.4 1.6 9.1z"/><path fill="#EA4335" d="M24 10.6c4 0 6.7 1.7 8.3 3.2l6.1-5.9C34.8 4.4 29.9 2 24 2 15.5 2 8.2 6.9 4.6 14.9l7.1 5.5c1.7-5.2 6.6-9.8 12.3-9.8z"/></svg>
          {t("auth.google")}
        </button>

        <button onClick={handleAppleSignIn} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#fff", color: "#1f1f1f", border: "1.5px solid rgba(0,0,0,.10)", fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: "pointer" }}>
          <svg width="16" height="18" viewBox="0 0 384 512" fill="#000" aria-hidden="true"><path d="M318.7 268c-.2-37 16.6-64.9 50.6-85.5-19-27.2-47.7-42.2-85.6-45.1-35.9-2.8-75.1 21-89.5 21-15.2 0-49.9-20-77.2-20C61.2 139.1 0 184.6 0 277.2c0 27.3 5 55.5 15 84.5 13.3 38.2 61.3 132 111.4 130.4 26.2-.6 44.7-18.6 78.8-18.6 33.1 0 50.2 18.6 79.4 18.6 50.5-.7 93.9-86 106.6-124.3-67.7-31.9-72.5-93.5-72.5-99.8zM256.4 82.6c22.4-26.6 20.4-50.8 19.7-59.5-19.8 1.1-42.7 13.4-55.8 28.6-14.4 16.3-22.9 36.5-21.1 58.9 21.4 1.6 41-9.4 57.2-28z"/></svg>
          {t("auth.apple")}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "2px 0" }}>
          <span style={{ flex: 1, height: 1, background: AC.goldSoft }} />
          <span style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight }}>{t("auth.or")}</span>
          <span style={{ flex: 1, height: 1, background: AC.goldSoft }} />
        </div>

        {!emailShown ? (
          <>
          <button onClick={() => setEmailOpen(true)}
            style={{ width: "100%", padding: "11px", borderRadius: 10, background: AC.gold, color: AC.bgDeep, border: "none", fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            {t("auth.continueWithEmail", { defaultValue: "Vazhdo me Email" })}
          </button>
          {rreshtiIDyqaneve}
          </>
        ) : (
        <>
        <div>
          <div style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: ".08em", color: AC.inkLight, marginBottom: 4 }}>{t("auth.email").toUpperCase()}</div>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", display: "flex", color: AC.inkLight }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="M3.5 7l8.5 6 8.5-6"/></svg>
            </span>
            <input type="email" autoComplete="email" placeholder={t("auth.emailPlaceholder", { defaultValue: "email@shembull.com" })}
              className="w-full rounded-md outline-none" style={{ ...inputStyleA, padding: "10px 12px 10px 36px", borderRadius: 10 }}
              value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} />
          </div>
        </div>

        {!signupEmailOnly && (
          <div>
            <div style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: ".08em", color: AC.inkLight, marginBottom: 4 }}>{t("auth.password").toUpperCase()}</div>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", display: "flex", color: AC.inkLight }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="4.5" y="10.5" width="15" height="9.5" rx="2"/><path d="M8.2 10.5V8a3.8 3.8 0 0 1 7.6 0v2.5"/></svg>
              </span>
              <input type={showPw ? "text" : "password"} autoComplete="current-password" placeholder={t("auth.passwordPlaceholder", { defaultValue: "Fjalëkalimi juaj" })}
                className="w-full rounded-md outline-none" style={{ ...inputStyleA, padding: "10px 38px 10px 36px", borderRadius: 10 }}
                value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} />
              <button type="button" onClick={() => setShowPw((v) => !v)} title={showPw ? t("Hide") : t("Show")}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: AC.inkLight, display: "flex", padding: 2 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/>
                  {showPw && <path d="M4 20 20 4"/>}
                </svg>
              </button>
            </div>
          </div>
        )}

        {!signupEmailOnly && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 1 }}>
            {/* Në aplikacionin e telefonit çelësi fshihet: aty s'ka dritare që
                mbyllet, pra premtimi i tij s'do të thoshte asgjë. Hapësira i
                mbetet linkut të fjalëkalimit. */}
            {isNativeApp ? <span /> : (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", fontFamily: FONTS.body, fontSize: 13.5, color: AC.inkLight }}>
              <input type="checkbox" checked={rememberMe} onChange={() => setRememberMe((v) => !v)} style={{ accentColor: AC.gold, width: 14, height: 14 }} />
              {t("auth.rememberMe", { defaultValue: "Më mbaj të kyçur" })}
            </label>
            )}
            {resetSent ? (
              <span style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight }}>{t("We sent you a link. Open it and choose a new password.")}</span>
            ) : (
              <button disabled={authBusy} onClick={sendPasswordReset} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: FONTS.body, fontSize: 13.5, color: AC.gold, padding: 0 }}>
                {t("Forgot your password?")}
              </button>
            )}
          </div>
        )}

        {authError && <p style={{ color: AC.danger, fontSize: 12.5, fontFamily: FONTS.body }}>{authError}</p>}

        {signupEmailOnly ? (
          <>
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight, lineHeight: 1.5 }}>{t("We send you a confirmation link. You choose your password right after.")}</p>
            <button disabled={authBusy} onClick={handleSignUpEmailOnly} style={{ width: "100%", padding: "11px", borderRadius: 10, background: AC.gold, color: AC.bgDeep, border: "none", fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: authBusy ? .6 : 1 }}>
              {t("auth.createAccount", { defaultValue: "Krijo llogarinë" })}
            </button>
            <button onClick={() => { setSignupEmailOnly(false); setAuthError(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: FONTS.body, fontSize: 13.5, color: AC.inkLight, textDecoration: "underline" }}>
              {t("auth.backToSignIn", { defaultValue: "Kthehu te hyrja" })}
            </button>
          </>
        ) : (
          <button disabled={authBusy} onClick={handleEmailAuth} style={{ width: "100%", padding: "11px", borderRadius: 10, background: AC.gold, color: AC.bgDeep, border: "none", fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: authBusy ? .6 : 1, marginTop: 2 }}>
            {t("auth.signIn", { defaultValue: "Hyr" })}
          </button>
        )}
        </>
        )}

        {!signupEmailOnly && (
          <>
            <div style={{ height: 1, background: AC.goldSoft, margin: "4px 0 0" }} />
            <p style={{ textAlign: "center", fontFamily: FONTS.body, fontSize: 12.5, color: AC.inkLight, margin: 0 }}>
              {t("auth.noAccount", { defaultValue: "Nuk keni llogari?" })}{" "}
              <button onClick={() => { setSignupEmailOnly(true); setAuthError(""); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: FONTS.body, fontSize: 12.5, color: AC.gold }}>
                {t("auth.createOne", { defaultValue: "Krijo një llogari" })}
              </button>
            </p>
          </>
        )}

        {/* Rrugë të dorës së dytë — të pranishme, por të qeta, që të mos e
            ngarkojnë kartelën: link magjik dhe kontakti i mbështetjes. */}
        {/* Rrugët e dorës së dytë mbahen të pranishme por gati të tejdukshme —
            që të mos i zënë sytë ilustrimit. Dalin plotësisht kur i afrohesh. */}
        {emailShown && (
        <div style={{ textAlign: "center", marginTop: 2, opacity: 0.55, transition: "opacity .2s" }}
             onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
             onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.55)}>
          {magicLinkSent ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight, margin: 0 }}>{t("auth.magicLinkSent", { email: authForm.email })}</p>
          ) : (
            <button disabled={magicLinkBusy} onClick={handleMagicLink} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight, textDecoration: "underline" }}>
              {magicLinkBusy ? t("auth.pleaseWait") : t("auth.sendMagicLink")}
            </button>
          )}
          {/* NDËRTUAR (19 gusht 2026) — "S'mund të hyj?" ekzistonte i plotë si
              kod (`handleRequestPasswordHelp` + `rpc_request_password_help`)
              por asnjë buton s'e thërriste. Ndryshon nga rivendosja vetjake:
              këtu kërkesa i shkon adminit të platformës, çka është e vetmja
              rrugë për dikë që s'e merr dot email-in fare. RPC-ja hesht njësoj
              edhe kur email-i s'ekziston, prandaj mesazhi është gjithmonë i
              njëjti — s'zbulohet dot kush është i regjistruar. */}
          {helpRequestSent ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight, marginTop: 6, marginBottom: 0 }}>{t("auth.helpRequestSent")}</p>
          ) : (
            <p style={{ marginTop: 6, marginBottom: 0 }}>
              <button disabled={helpRequestBusy} onClick={handleRequestPasswordHelp} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight, textDecoration: "underline" }}>
                {helpRequestBusy ? t("auth.pleaseWait") : t("Can't sign in?")}
              </button>
            </p>
          )}
          <p style={{ fontFamily: FONTS.body, fontSize: 13, color: AC.inkLight, marginTop: 6, marginBottom: 0, opacity: .8 }}>
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "inherit", textDecoration: "underline" }}>{SUPPORT_EMAIL}</a>
          </p>
        </div>
        )}
        {emailShown && rreshtiIDyqaneve}
      </div>
    )
    );
  };

  // FAZA 43 — 3 slides që ndryshojnë vetvetiu mbi/pranë formës së
  // identifikimit, thjesht atmosferë/prezantim (jo hapa detyrues si në
  // onboarding klasik) — ashtu siç u kërkua.
  // ==========================================================================
  // EKRANI I HYRJES — pamja vjen nga baza (rpc_get_login_settings), publike
  // sepse lexohet PARA identifikimit. Karuseli i vjetër 3-slide u zëvendësua
  // nga LoginScreen; hiqet edhe intervali i tij, që s'kishte më kuptim të
  // rifreskonte komponentin çdo 4.5 sekonda.
  // ==========================================================================
  const [showPw, setShowPw] = useState(false);          // syri te fusha e fjalëkalimit
  // Hyrja me email nis e mbledhur: kartela mban vetëm Google/Apple dhe një
  // buton. Fushat shpalosen me klikim. Kështu ekrani i hyrjes mbetet i qetë
  // dhe ilustrimi duket, pa i hequr asgjë kujt i duhet email-i.
  const [emailOpen, setEmailOpen] = useState(false);
  // "Më mbaj të kyçur" — kur është i fikur, sesioni vlen vetëm derisa mbyllet
  // shfletuesi: shënjuesi rri te sessionStorage, dhe në hapjen e radhës (kur ai
  // shënjues mungon, por preferenca thotë "mos më mbaj") dilet automatikisht.
  // NË TELEFON KJO S'KA KUPTIM, dhe deri më 20 gusht bënte dëm.
  //
  // Ideja e "sesionit sa një vizitë" është e shfletuesit: mban një shenjë te
  // `sessionStorage`, dhe kur ajo mungon në hapjen e radhës do të thotë se
  // dritarja u mbyll. Në telefon nuk ka dritare për t'u mbyllur — sistemi e vret
  // procesin sa herë i duhet memoria, dhe me të vdes `sessionStorage`. Pra me
  // çelësin e fikur, ÇDO hapje e aplikacionit ishte një dalje e detyruar.
  //
  // Vetë preferenca rrinte te `localStorage`, pikërisht ruajtja që Capacitor-i
  // paralajmëron se sistemi e pastron periodikisht — arsyeja pse sesioni u
  // zhvendos prej saj te `@capacitor/preferences`.
  //
  // Në web mbetet ashtu siç ishte: aty kompjuteri ndahet me të tjerë dhe
  // "mos më mbaj të kyçur" është kërkesë e ligjshme.
  const isNativeApp = Capacitor.isNativePlatform();
  const [rememberMe, setRememberMe] = useState(() => isNativeApp || localStorage.getItem("pef_remember") !== "0");
  useEffect(() => {
    if (isNativeApp) return;
    localStorage.setItem("pef_remember", rememberMe ? "1" : "0");
  }, [rememberMe, isNativeApp]);
  useEffect(() => {
    if (isNativeApp) return;
    // MBËRRITJE ME TOKEN → MOS DIL (1 shtator 2026).
    //
    // Ky ishte shkaku i vërtetë i «impersonimi nuk punon», dhe u kuptua vetëm
    // kur useri dërgoi adresën: `https://pemaefamiljes.com/#` — hash bosh, dhe
    // ekrani i hyrjes. Sesioni ishte krijuar nga magic-link-u, dhe pastaj u shua
    // nga vetë ky bllok, brenda së njëjtës ngarkim.
    //
    // Pse: një SKEDË E RE ka `sessionStorage` krejt të zbrazët, ndaj
    // `pef_session_alive` mungon GJITHMONË aty. Me «më mbaj të kyçur» të fikur,
    // çdo skedë e re dilte vetvetiu — përfshirë atë që sapo ishte kyçur nga
    // impersonimi.
    //
    // Dhe meqë ai signOut ishte GLOBAL, ai shuante njëkohësisht edhe sesionin e
    // Admin Center-it. Të dyja ankesat — impersonimi që s'punon dhe dalja që të
    // nxjerr nga të dyja aplikacionet — ishin ky rresht i vetëm.
    //
    // `KA_TOKEN_NE_URL` lexohet te ngarkimi i modulit, PARA se supabase-js ta
    // pastrojë fragmentin — nëse do ta lexonim këtu, do të ishte tepër vonë dhe
    // do të shihnim gjithmonë `#` bosh, pikërisht siç e pa useri.
    if (
      !KA_TOKEN_NE_URL &&
      localStorage.getItem("pef_remember") === "0" &&
      !sessionStorage.getItem("pef_session_alive")
    ) {
      // `scope: "local"` — «mos më mbaj të kyçur» është premtim për KËTË
      // shfletues, jo urdhër për të gjitha pajisjet. Parazgjedhja e Supabase-it
      // (`global`) shfuqizon çdo sesion të llogarisë kudo.
      supabase.auth.signOut({ scope: "local" }).catch(() => {});
    }
    sessionStorage.setItem("pef_session_alive", "1");
  }, [isNativeApp]);
  // PAMJA E HYRJES MBAHET MEND — i njëjti mësim si te tema, 20 gusht 2026.
  //
  // Deri sot: `useState(null)` → derisa të përgjigjej baza, pikturohej varianti i
  // integruar. Pra çdo rifreskim shfaqte për një çast një ekran që admini nuk e
  // kishte zgjedhur kurrë. Pronari e pa dhe pyeti ku ruhej — dhe përgjigjja ishte
  // "askund", që është pikërisht problemi.
  //
  // Zgjidhja është ajo që funksionoi te temat: ruaje atë që u zgjidh vërtet, dhe
  // nisu prej saj herën tjetër. Rrjeti pastaj e përditëson pa u vënë re, sepse
  // zakonisht s'ka ndryshuar asgjë.
  //
  // `localStorage` mjafton këtu ndryshe nga sesioni: Android-i mund ta pastrojë,
  // dhe kostoja është një pulsim i vetëm, një herë — jo një dalje nga llogaria.
  const LOGIN_CACHE = "pef.login.last";
  // DYQANET1 (12 shtator 2026) — lidhjet e Google Play / App Store për
  // ekranin e hyrjes. Publike (para identifikimit), nga Admin Center →
  // Ekrani i hyrjes → «Dyqanet». Distinktivi del vetëm kur adresa është e
  // mbushur; në app-in native s'del fare (aty je tashmë brenda).
  const [dyqanet, setDyqanet] = useState({});
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    supabase.rpc("rpc_publik_dyqanet").then(({ data }) => { if (data && typeof data === "object") setDyqanet(data); }, () => {});
  }, []);
  const [loginSettings, setLoginSettings] = useState(() => {
    try { const raw = localStorage.getItem(LOGIN_CACHE); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  useEffect(() => {
    let alive = true;
    supabase.rpc("rpc_get_login_settings").then(({ data, error }) => {
      if (!alive || error || !data) return;   // pa përgjigje → mbetet ajo e ruajtur
      setLoginSettings(data);
      try { localStorage.setItem(LOGIN_CACHE, JSON.stringify(data)); } catch { /* kuota / modaliteti privat */ }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Paleta e variantit, e përkthyer në emrat që përdor forma e identifikimit.
  // Shpërndajmë COLORS-in i pari, që çdo çelës i paprekur të mbetet i vlefshëm.
  const loginAuthColors = useMemo(() => {
    const tk = loginSettings?.tokens || FALLBACK_LOGIN.tokens;
    if (!tk) return COLORS;
    return {
      ...COLORS,
      gold: tk.accent, bgDeep: tk.accentInk,
      ink: tk.ink, inkLight: tk.inkSoft,
      panel: tk.card, panelSoft: tk.bg2,
      goldSoft: tk.cardBorder, cardBorder: tk.cardBorder,
    };
  }, [loginSettings, COLORS]);

  // FIX (SIGURI) — "shkoj backward, më shfaqet pema/familja e vjetër edhe
  // pse aksesi m'u hoq". Shkak i pafajshëm por real: browser-at (bfcache —
  // "back/forward cache") e ruajnë krejt gjendjen e JS-it në kujtesë kur
  // largohesh nga faqja, dhe e RIKTHEJNË TË PANDRYSHUAR kur shtypet "prapa"
  // — PA asnjë kërkesë të re rrjeti, pra pa asnjë rikontroll real aksesi.
  // Kjo do të thotë: nëse dikush ta ka hequr aksesin ndërkohë, "prapa" do
  // të shfaqte ende pemën/të dhënat e vjetra të ngrira në kujtesë, edhe pse
  // teknikisht databaza tashmë do ta refuzonte një kërkesë të re. Zgjidhja
  // standarde: kur ngjarja 'pageshow' tregon se faqja u rikthye NGA bfcache
  // (event.persisted === true), detyro rifreskim TË PLOTË — kjo e bën çdo
  // "prapa" të bëjë sërish kërkesat reale te serveri, respektuar rregullat
  // aktuale të RLS-së, jo gjendjen e ngrirë.
  useEffect(() => {
    // ⚠ 28 GUSHT 2026 — VETËM WEB. Brenda app-it native ky rresht ishte motori
    // i ciklit të pafund të rifreskimit.
    //
    // Te WKWebView-i i iOS-it, çdo kthim brenda historisë e rikthen faqen nga
    // bfcache-i, pra `event.persisted` del `true` — dhe ne aty thërrisnim
    // `reload()`. Efekti më poshtë («histori me THELLËSI») thërret
    // `history.back()` sa herë mbyllet një dritare. Të dy bashkë mbyllin një
    // rreth: back → pageshow(persisted) → reload → efekti xhiron prapë → back.
    // Aplikacioni nisej, rifreskohej, dhe asnjë prekje s'kapte dot radhën.
    //
    // Në shfletues arsyeja mbetet e vlefshme: bfcache-i i shfletuesit e kthen
    // pemën e ngrirë pa asnjë kërkesë të re, pra pa rikontroll aksesi. Brenda
    // app-it native s'ka «prapa» shfletuesi fare — pra s'ka çfarë të mbrojmë.
    if (Capacitor.isNativePlatform()) return;
    function onPageShow(event) {
      if (event.persisted) window.location.reload();
    }
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Ref e përbashkët mes të dy efekteve më poshtë — mban gjendjen "a kemi
  // shtuar aktualisht një rresht historie për overlay-in e hapur".
  const overlayHistoryArmedRef = useRef(false);
  // Model me THELLËSI: mbajmë NJË hyrje historie për SECILËN dritare të hapur,
  // që back/swipe të kthejë VETËM një hap (dritaren e sipërme), jo t'i mbyllë
  // të gjitha. `histDepthRef` = sa hyrje kemi shtyrë; `suppressPopRef` = sa
  // ngjarje popstate t'i shpërfillim (nga vetë-pastrimi ynë me history.back()).
  const histDepthRef = useRef(0);
  const suppressPopRef = useRef(0);
  // "Shtyp/rrëshqit prapa edhe një herë për të dalë" — te rrënja e app-it (pa
  // asnjë dritare të hapur), swipe/back i PARË s'e mbyll app-in: shfaq një njoftim
  // të shkurtër. Vetëm swipe/back i DYTË brenda ~2s e minimizon (del jashtë).
  const lastBackRef = useRef(0);

  // FIX v2 (raportuar: "back mbyll faqen, por kur s'ka asgjë hapur duhen 2
  // shtypje back/swipe për me mbyll app-in"). Shkaku i versionit të parë:
  // `overlayHistoryArmedRef` përpiqej të "rebalanconte" historinë e browser-it
  // EDHE kur mbyllej diçka me buton "X" (jo back) — duke thirrur vetë
  // history.back() si "pastrim". Ai `history.back()` është ASINKRON; nëse
  // përdoruesi hapte diçka tjetër SHPEJT para se pastrimi i vjetër të
  // përfundonte, `popstate`-i i vonuar e "gjente" ref-in në gjendje të
  // gabuar (dukej "e kurdisur" edhe kur s'ishte asgjë hapur) — pikërisht
  // simptoma e raportuar (nevojiten 2 shtypje te ekrani rrënjë).
  //
  // Rregullimi: hiqet çdo "rebalancim" aktiv i historisë. Tani:
  //   • ANDROID (backButton) kontrollon GJENDJEN REALE drejtpërdrejt (jo një
  //     ref të ndarë) dhe e mbyll SINKRON, direkt — pa kaluar fare nëpër
  //     history.back()/popstate. Kjo e bën Android-in të parashikueshëm,
  //     pa vonesë, pa race — asnjë varësi nga historia e browser-it fare.
  //   • iOS (s'ka 'backButton' fare) dhe browser normal vazhdojnë të
  //     mbështeten te historia (gjesti i WKWebView-it/butoni "prapa" vetë
  //     prodhojnë 'popstate') — shtohet nga NJË rresht historie sa herë hapet
  //     diçka E RE, POR s'hiqet manualisht kur mbyllet me "X" — rreshtat
  //     "orfanë" janë të padëmshëm (closeTopOverlay është idempotente, no-op
  //     nëse s'ka asgjë hapur), thjesht kërkojnë ndonjëherë NJË shtypje shtesë
  //     "të padëmshme" back te browser/iOS (jo Android, atje s'ka fare këtë
  //     kosto) — kompromis i qëllimshëm, siguri mbi përsosmëri.
  // FIX (THJESHTIM) — më parë kishte DY menu të veçanta ("menuOpen" — Rreth/
  // Kontakto/Njoftime/Postë/Gjuhë, dhe "avatarMenuOpen" — Profil/Siguri/
  // Administrimi/Dil) — dy vende të ndryshme, dy koncepte "menu kryesore",
  // konfuze për t'u gjetur. Tani është VETËM "avatarMenuOpen"; kjo funksion
  // e përbashkët e lë PËRMBAJTJEN e menusë të përcaktuar një herë të vetme
  // (jo 3 kopje), thirrur nga të tria pikat e hapjes (ikona e avatarit,
  // "Më shumë" i sidebar-it, "Më shumë" i bar-it poshtë në telefon) — çdo
  // pikë e ruan POZICIONIMIN e vet (absolute, pranë vetë butonit që u shtyp),
  // thjesht përmbajtja brenda është identike kudo.
  // GJUHA — një rresht, i përdorur nga të dy shtigjet e menysë së avatarit.
  //
  // 29 gusht 2026, pronari: «edhe dropdown-i i gjuhëve s'po më pëlqen si
  // dropdown, po e dua faqe si krejt të tjerat, që të ruajmë standardin».
  //
  // Kishte të drejtë edhe për fjalën e fundit. Kishte DY lista rënëse gjuhësh,
  // jo një — kjo, dhe ajo e shtegut kur llogaria s'është lidhur ende me person.
  // Kisha ndrequr vetëm të parën. Një standard që vlen vetëm te ekranet e
  // shpeshta nuk është standard; është zakon.
  //
  // Tani të dyja janë i njëjti rresht, dhe hapin të njëjtën faqe.
  function rreshtiIGjuhes() {
    return (
      <div className="pt-1" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
        {/* PA SHIGJETË, DHE PA E MBYLLUR MENYNË — dy vërejtje të pronarit, të
            dyja të drejta.
            1. Asnjë rresht tjetër i kësaj menyje s'ka shigjetë. E shtova sepse
               ky hap faqe, po ashtu bëjnë edhe «Profili im», «Siguria»,
               «Pamja» — pra shigjeta nuk thoshte asgjë të re; thoshte vetëm se
               kishte ardhur nga një meny tjetër. Emri i gjuhës në të djathtë e
               bën punën: tregon gjendjen, dhe se rreshti është i prekshëm.
            2. E kisha mbyllur menynë para se të hapja faqen. Prandaj kthimi
               nga gjuhët të nxirrte jashtë krejt — s'kishte ku të kthehej.
               Tani faqja hapet MBI menynë; kthimi e mbyll vetëm atë, dhe
               menyja është aty ku e le. */}
        <button
          onClick={() => setGjuhaPanelOpen(true)}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"
        >
          <span style={{ fontFamily: FONTS.body, fontSize: 13 }}>🌐</span>
          <span style={{ flex: 1, fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Language")}</span>
          <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{emriIGjuhes(langChoice)}</span>
        </button>
      </div>
    );
  }

  function renderAccountMenuItems() {
    // FIX (raportuar nga useri — "jam në një vrimë të zezë, s'kam asnjë
    // mundësi"): kur llogaria është e kyçur POR ende e palidhur me ndonjë
    // person, menyja e familjes s'ka kuptim (dhe pjesa dërrmuese e saj do të
    // rrëzohej te `people[currentPersonId]`). Në vend që të mos shfaqet FARE
    // — që e linte përdoruesin pa asnjë rrugëdalje — shfaqet një meny e
    // shkurtër me pikërisht atë që i duhet: bashkohu, krijo, gjuha, siguria,
    // fshirja e llogarisë, dalja.
    if (!currentPersonId) {
      // MUZEU1 — te një muze kjo gjendje është NORMALE, jo e papërfunduar.
      // Askush aty nuk lidhet me figurë: llogaritë janë kujdestarë, dhe roli
      // do të thotë «çfarë mund të bësh», jo «kush je».
      //
      // Pa këtë degë, çdo kurator a vizitor muzeu do të shihte «llogaria jote
      // nuk është lidhur ende me askënd» dhe do të ftohej të krijonte familje.
      // Ky ishte kurthi më i afërt i gjithë idesë — dhe do të dilte i pari.
      const nukJeFigure = eshteMuze;
      return (
        <>
          <p className="px-2 pt-1 pb-2" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, lineHeight: 1.45 }}>
            {nukJeFigure ? t("You are here as staff — you are not part of the exhibition.") : t("Your account is not linked to anyone in a family yet.")}
          </p>
          {!nukJeFigure && (
            <>
              <button onClick={() => leaveToPage("/join", "")} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5">
                <NovaIcon role="join" Icon={KeyRound} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Join another family (Code)")}</span>
              </button>
              <button onClick={() => leaveToPage("/start", "")} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5">
                <NovaIcon role="create" Icon={PlusCircle} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Create a new family")}</span>
              </button>
            </>
          )}
          <button onClick={() => { openSecurityPanel(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5">
            <NovaIcon role="security" Icon={Shield} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Account security")}</span>
          </button>
          {/* I njëjti rresht si te menyja e plotë. Ky është shtegu kur llogaria
              s'është lidhur ende me një person — pak i vizituar, prandaj mbeti
              me listë rënëse edhe pasi tjetri u bë faqe. Një standard që vlen
              vetëm te ekranet e shpeshta nuk është standard. */}
          {rreshtiIGjuhes()}
          <button onClick={openDeleteAccount} className="w-full flex items-center gap-2 px-2 py-2 mt-1 rounded-md text-left hover:bg-black/5" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            <Trash2 size={14} color="#A33" /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: "#A33" }}>{t("Delete my account")}</span>
          </button>
          <button onClick={() => { setAvatarMenuOpen(false); handleSignOut(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5">
            <LogOut size={14} color={COLORS.ink} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("nav.signOut")}</span>
          </button>
        </>
      );
    }
    return (
      <>
        {/* Llogaria ime */}
        <button onClick={() => { openSelfEdit(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="profile" Icon={UserCircle2} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("My profile")}</span></button>
        <button onClick={() => { openSecurityPanel(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="security" Icon={Shield} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Account security")}</span></button>
        {!locked && (
          <button onClick={() => { setThemePanelOpen(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="appearance" Icon={Palette} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Appearance")}</span></button>
        )}
        {/* Riemërtuar "Njoftimet" → "Preferencat e njoftimeve": zilja pranë
            avatarit tashmë e hap listën; ky zë mban preferencat (ditëlindje,
            kanalet). Hap të njëjtin modal. */}
        <button onClick={() => { openAnnouncements(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="bell" Icon={Bell} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Notification preferences")}{unreadAnnouncementCount > 0 ? ` (${unreadAnnouncementCount})` : ""}</span></button>

        {/* Informacion & ndihmë */}
        <div className="pt-1 mt-1" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
          <button onClick={() => { setContactAdminOpen(true); setSupportView("list"); loadMyTickets(); loadFaq().then(() => {}, () => {}); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="help" Icon={HelpCircle} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Help Center")}</span>{(() => { const tot = (myTickets || []).reduce((a, tk) => a + (Number(tk.unread) || 0), 0); return tot > 0 ? <span style={{ marginLeft: "auto", minWidth: 17, height: 17, padding: "0 5px", borderRadius: 999, background: "#D14343", color: "#fff", fontFamily: FONTS.body, fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>{tot > 99 ? "99+" : tot}</span> : null; })()}</button>
          <button onClick={() => { setAboutOpen(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="info" Icon={Info} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("About the platform")}</span></button>
        </div>

        {/* Komunikim (admin) */}
        {canManage && !locked && (
          <div className="pt-1" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            <p className="px-2 pt-1 pb-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("Communication")}</p>
            <button onClick={() => { fetchAllAnnouncements(); setAnnounceManageOpen(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="announce" Icon={Megaphone} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Manage Announcements")}</span></button>
            {platformFeatures.family_mail_enabled && (
              <button onClick={() => { openEmailReplies(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="mail" Icon={Mail} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Family Mail")}{unreadReplyCount > 0 ? ` (${unreadReplyCount})` : ""}</span></button>
            )}
          </div>
        )}

        {/* Administrimi */}
        {canManage && !locked && (
          <div className="pt-1" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            <p className="px-2 pt-1 pb-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("ADMINISTRATION")}</p>
            <button onClick={() => { setRolesPanelOpen(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="members" Icon={Shield} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("nav.members")}</span></button>
            <button onClick={() => { fetchDeletedPeople(); fetchDeletedHistory(); setTrashOpen(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="trash" Icon={Trash2} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Trash (deleted)")}</span></button>
            <button onClick={() => { setLogPanelOpen(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5"><NovaIcon role="log" Icon={ScrollText} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("The log")}</span></button>
          </div>
        )}
        {/* Hub-i "Familjet" — një zë i vetëm në meny. Klikimi hap një faqe ku
            i rregullon të gjitha bashkë: lista e familjeve (ndërro/★/fshih/dil),
            të fshehura, dhe veprimet (sinkronizo/sill familjen/bashkohu/krijo).
            Më parë kjo ishte një listë e gjatë inline që e ngarkonte menynë. */}
        <div className="pt-1" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
          <button onClick={() => { setFamiliesHubOpen(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5">
            <NovaIcon role="family" Icon={Home} size={14} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Family Hub")}</span>
          </button>
        </div>
        {/* GJUHA — faqe, jo listë rënëse. Kërkesë e pronarit, 29 gusht 2026:
            «ta bëjmë edhe faqen e gjuhëve që hapet si krejt faqet e tjera».
            Ishte një `<select>` i sistemit: te iPhone-i hapet si rrotë poshtë
            ekranit, te Androidi si dialog, te desktopi si listë — tri pamje të
            ndryshme për një veprim, dhe asnjëra si pjesa tjetër e app-it. Me
            njëzet gjuhë, ajo rrotë kërkon rrotullim të gjatë pa parë se ku je.
            Tani është dritare si çdo tjetër, dhe hapet nga i njëjti vend. */}
        {rreshtiIGjuhes()}
        {authSession && (
          <button onClick={openDeleteAccount} className="w-full flex items-center gap-2 px-2 py-2 mt-1 rounded-md text-left hover:bg-black/5" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            <Trash2 size={14} color="#A33" /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: "#A33" }}>{t("Delete my account")}</span>
          </button>
        )}
        <button onClick={() => { setAvatarMenuOpen(false); handleSignOut(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5">
          <LogOut size={14} color={COLORS.ink} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("nav.signOut")}</span>
        </button>
        {/* VULA E NDËRTIMIT — data dhe ora kur u ndërtua ky paketim, e vendosur
            nga `vite.config.js`. Rri këtu sepse është vendi ku çdo aplikacion
            e mban numrin e versionit, dhe sepse duhen dy prekje për ta parë.
            E vogël dhe e zbehtë: familja s'ka pse ta vërë re, ne e gjejmë
            menjëherë kur pyetja është «a po xhiron telefoni kodin e ri?». */}
        <p className="px-2 pt-2 mt-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, opacity: 0.85, borderTop: `1px solid ${COLORS.goldSoft}` }}>
          {VULA_E_NDERTIMIT}
        </p>
      </>
    );
  }

  function computeAnyOverlayOpen() {
    return !!(
      addSheet || focusPickerOpen ||
      etiketaPer || historiaPersoniHapur || shtoHistoriOpen || shtoSkedarOpen || kerkimiOpen ||
      photoLightbox || archiveLightbox || personModal || spouseModalFor || docHistoryOpenType ||
      docModalOpen || myLegacyOpen ||
      emailRepliesOpen || announceManageOpen || announcementsOpen || simpleMourningOpen ||
      contactAdminOpen || aboutOpen || logPanelOpen || rolesPanelOpen || themePanelOpen ||
      viewPanelOpen || searchOpen || avatarMenuOpen || gjuhaPanelOpen ||
      securityPanelOpen || trashOpen ||
      syncModalFor || importOpen || familiesHubOpen || switchFamOpen ||
      occasionAI || impModalOpen || posterModalOpen || treeSettingsOpen || legacyConfirm || treeBarPanel || deleteAccountOpen || guestAccessFor ||
      authOpen || legacyReqOpen || chatOpen || fabOpen || treeDrawerOpen || treeFabOpen ||
      (selectedId && people[selectedId]) ||
      // 6 shtator 2026 — ekrani «Shto» mbyllej me back, po nuk numërohej si
      // dritare: në Android back-u i parë thoshte «rrëshqit sërish për të
      // dalë» dhe i dyti e minimizonte app-in; në web back-u dilte nga faqja.
      activeScreen === "shto"
    );
  }
  // Sa dritare janë hapur (thellësia e pirgut). Përdoret që të mbajmë po aq
  // hyrje historie sa dritare — çdo back kthen një hap.
  function computeOverlayDepth() {
    let n = 0;
    [focusPickerOpen, syncModalFor, importOpen, familiesHubOpen, switchFamOpen,
      occasionAI, impModalOpen, posterModalOpen, treeSettingsOpen, legacyConfirm, treeBarPanel, deleteAccountOpen, guestAccessFor,
      authOpen, legacyReqOpen, chatOpen, fabOpen, treeDrawerOpen, treeFabOpen,
      (selectedId && people[selectedId]) ? 1 : 0,
      addSheet, etiketaPer, historiaPersoniHapur, shtoHistoriOpen, shtoSkedarOpen, kerkimiOpen, photoLightbox, archiveLightbox,
      personModal, spouseModalFor, docHistoryOpenType, docModalOpen,
      myLegacyOpen, emailRepliesOpen, announceManageOpen, announcementsOpen,
      simpleMourningOpen, contactAdminOpen, aboutOpen, securityPanelOpen, logPanelOpen, trashOpen,
      rolesPanelOpen, themePanelOpen, viewPanelOpen, searchOpen, avatarMenuOpen, gjuhaPanelOpen,
      activeScreen === "shto",
    ].forEach((v) => { if (v) n++; });
    return n;
  }
  function closeTopOverlay() {
    // Renditur nga "më sipër" (lightbox, mbi çdo gjë tjetër) te "më poshtë"
    // (menu/panele anësore) — mbyll VETËM njërin, jo të gjithë njëherësh.
    // SHTESË (auditim): dritaret që mungonin krejt nga sistemi i back-ut —
    // asistenti (z-9000, mbi gjithçka), kyçja, kërkesa e trashëgimisë, paneli
    // i profilit, dhe tri shtresat e shiritit. `termsGateOpen` MBETET
    // qëllimisht jashtë: është portë ligjore, s'guxon të anashkalohet me back.
    if (chatOpen) { setChatOpen(false); return; }
    if (focusPickerOpen) { setFocusPickerOpen(false); return; } // editori i prerjes (mbi modalin e personit)
    if (authOpen) { setAuthOpen(false); return; }
    if (legacyReqOpen) { setLegacyReqOpen(false); return; }
    // Modalet e reja (familje) + occasionAI/impModal — të gjitha mbyllen me back/swipe.
    if (syncModalFor) { setSyncModalFor(null); return; }
    if (importOpen) { setImportOpen(false); return; }
    if (familiesHubOpen) { setFamiliesHubOpen(false); return; }
    if (switchFamOpen) { setSwitchFamOpen(false); return; }
    if (occasionAI) { setOccasionAI(null); return; }
    if (impModalOpen) { setImpModalOpen(false); return; }
    if (posterModalOpen) { closePosterModal(); return; }
    if (deleteAccountOpen) { setDeleteAccountOpen(false); return; }
    if (guestAccessFor) { setGuestAccessFor(null); return; }
    if (legacyConfirm) { setLegacyConfirm(null); return; }   // konfirmimi i trashëgimisë (mbi Legacy)
    if (treeSettingsOpen) { setTreeSettingsOpen(false); return; } // ⚙️ cilësimet e pamjes
    if (treeBarPanel) { setTreeBarPanel(null); return; }          // paneli i shiritit (kërkim/pamje)
    // «Shto» është EKRAN tani, jo dritare — pra back-i nuk mbyll asgjë, të
    // kthen aty ku ishe. Rri para faqeve të shtimit për të njëjtën arsye si
    // më parë: ato hapen NGA kjo listë, pra duhet të mbyllen para saj.
    if (activeScreen === "shto") { setActiveScreen(ekraniPara.current || "home"); return; }
    // BALLINA8 — faqet e shtimit rrinë NËN listën «çfarë do të shtosh», sepse
    // ajo hapet mbi to; por MBI Historinë, që një back të mbyllë faqen dhe jo
    // ekranin poshtë saj.
    if (shtoHistoriOpen) { setShtoHistoriOpen(false); return; }
    if (shtoSkedarOpen) { setShtoSkedarOpen(false); return; }
    if (kerkimiOpen) { setKerkimiOpen(false); return; }
    // Fleta «Shto person»: back-u i sistemit bën NJË hap prapa (formular →
    // lista), si butoni ← i saj; vetëm kur s'ka ku të kthehet, e mbyll.
    if (addSheet) { if (addSheetBackRef.current && addSheetBackRef.current()) return; setAddSheet(null); return; }
    if (photoLightbox) { setPhotoLightbox(null); return; }
    if (archiveLightbox) { setArchiveLightbox(null); return; }
    // BALLINA6 — kutitë e Historisë. Pa këto dy rreshta, butoni i kthimit te
    // Androidi do ta mbyllte ekranin nën to dhe kutia do të mbetej varur mbi
    // një faqe tjetër — pikërisht defekti i faqes së gjuhëve.
    if (etiketaPer) { setEtiketaPer(null); return; }
    if (historiaPersoniHapur) { setHistoriaPersoniHapur(false); return; }
    if (personModal) { setPersonModal(null); return; }
    if (spouseModalFor) { setSpouseModalFor(null); return; }
    if (docHistoryOpenType) { setDocHistoryOpenType(null); return; }
    if (docModalOpen) { setDocModalOpen(null); return; }
    if (myLegacyOpen) { setMyLegacyOpen(false); return; }
    if (emailRepliesOpen) { setEmailRepliesOpen(false); return; }
    if (announceManageOpen) { setAnnounceManageOpen(false); return; }
    if (announcementsOpen) { setAnnouncementsOpen(false); return; }
    if (simpleMourningOpen) { setSimpleMourningOpen(false); return; }
    if (contactAdminOpen) { setContactAdminOpen(false); return; }
    if (aboutOpen) { setAboutOpen(false); return; }
    if (securityPanelOpen) { setSecurityPanelOpen(false); return; }
    if (logPanelOpen) { setLogPanelOpen(false); return; }
    if (trashOpen) { setTrashOpen(false); return; }
    if (rolesPanelOpen) { setRolesPanelOpen(false); return; }
    if (themePanelOpen) { setThemePanelOpen(false); return; }
    if (viewPanelOpen) { setViewPanelOpen(false); return; }
    if (searchOpen) { setSearchOpen(false); return; }
    // PSE KJO S'MJAFTONTE VETË — 29 gusht 2026, pronari: «e ndreqe vetëm për
    // iOS; edhe shigjeta edhe step-back-u a janë ndrequr për Android?».
    //
    // Kishte të drejtë, dhe shkaku ishte i padukshëm nga ky rresht. Butoni fizik
    // i Android-it dëgjohet nga një `useEffect` më poshtë, dhe ai efekt mban një
    // MBYLLJE mbi gjendjen e çastit kur u lidh dëgjuesi. Nëse `gjuhaPanelOpen`
    // s'është te lista e varësive, efekti nuk rilidhet kur faqja hapet — dhe
    // dëgjuesi vazhdon të lexojë një «false» të vjetruar. Rezultati: Androidi
    // mendon se s'ka asnjë dritare të hapur dhe e minimizon aplikacionin.
    //
    // iOS-i nuk e ka fare atë dëgjues (mbyllja bëhet me gjest dhe me histori),
    // prandaj rregullimi dukej i plotë atje dhe i munguar këtu. E njëjta gjendje
    // duhet shtuar në TRE vende: te numëruesi, dhe te dy listat e varësive.
    if (gjuhaPanelOpen) { setGjuhaPanelOpen(false); return; }  // faqja e gjuhëve mbyllet e para, si çdo dritare
    if (avatarMenuOpen) { setAvatarMenuOpen(false); return; }
    if (selectedId && people[selectedId]) { setSelectedId(null); return; }  // paneli i profilit (full-screen në telefon)
    if (treeFabOpen) { setTreeFabOpen(false); return; }
    if (fabOpen) { setFabOpen(false); return; }
    if (treeDrawerOpen) { setTreeDrawerOpen(false); return; }
  }

  // iOS + browser: histori me THELLËSI — një hyrje për secilën dritare.
  //  • Kur hapet një dritare (thellësia rritet) → shtyj hyrje historie.
  //  • Kur mbyllet me buton ← / swipe brenda modalit (thellësia zvogëlohet PA
  //    popstate) → konsumo hyrjet e tepërta me history.back() (të shpërfillura
  //    nga suppressPopRef, që të mos mbyllin dritare tjetër).
  //  • popstate nga useri (back i telefonit/browser-it) → mbyll VETËM dritaren e
  //    sipërme dhe ul thellësinë me 1. Kështu çdo back kthen NJË hap.
  // Kthim nga një faqe e jashtme (/join, /start) — rihap dritaren ku ishe.
  useEffect(() => {
    let reopen = null;
    try { reopen = sessionStorage.getItem("pef_reopen"); sessionStorage.removeItem("pef_reopen"); } catch (_e) { /* asgjë */ }
    if (reopen === "familiesHub") setFamiliesHubOpen(true);
  }, []);

  useEffect(() => {
    // ⚠ 28 GUSHT 2026 — VETËM WEB. Ky model e imiton butonin «prapa» të
    // shfletuesit duke shtuar e hequr hyrje historie. Brenda app-it native ai
    // nuk mbron asgjë dhe prish shumë:
    //   · Androidi e ka butonin e vet fizik, të trajtuar më poshtë me
    //     `CapacitorApp.addListener("backButton")` — pa histori fare;
    //   · iOS-i s'ka buton «prapa», dhe gjesti i rrëshqitjes te WKView-i i
    //     Capacitor-it është i fikur.
    // Kurse `history.back()` këtu, bashkë me `pageshow(persisted)` më sipër,
    // ishte gjysma e dytë e ciklit të rifreskimit.
    if (Capacitor.isNativePlatform()) return;
    const depth = computeOverlayDepth();
    if (depth > histDepthRef.current) {
      for (let i = histDepthRef.current; i < depth; i++) window.history.pushState({ pefOverlay: i + 1 }, "");
      histDepthRef.current = depth;
    } else if (depth < histDepthRef.current) {
      const toPop = histDepthRef.current - depth;
      histDepthRef.current = depth;
      suppressPopRef.current += toPop;
      for (let i = 0; i < toPop; i++) window.history.back();
    }
    function onPopState() {
      if (suppressPopRef.current > 0) { suppressPopRef.current -= 1; return; } // pastrimi ynë
      if (computeAnyOverlayOpen()) {
        histDepthRef.current = Math.max(0, histDepthRef.current - 1);
        closeTopOverlay();
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [
    addSheet, focusPickerOpen,
    etiketaPer, historiaPersoniHapur, shtoHistoriOpen, shtoSkedarOpen, kerkimiOpen,
    photoLightbox, archiveLightbox, personModal, spouseModalFor, docHistoryOpenType,
    docModalOpen, myLegacyOpen,
    emailRepliesOpen, announceManageOpen, announcementsOpen, simpleMourningOpen,
    contactAdminOpen, aboutOpen, logPanelOpen, rolesPanelOpen, themePanelOpen,
    viewPanelOpen, searchOpen, avatarMenuOpen, gjuhaPanelOpen,
    securityPanelOpen, trashOpen,
    syncModalFor, importOpen, familiesHubOpen, switchFamOpen,
    occasionAI, impModalOpen, posterModalOpen, treeSettingsOpen, legacyConfirm, treeBarPanel,
    authOpen, legacyReqOpen, chatOpen, fabOpen, treeDrawerOpen, treeFabOpen, selectedId, deleteAccountOpen, guestAccessFor,
    activeScreen,
  ]);

  // Android: kontrollon gjendjen REALE drejtpërdrejt, mbyll SINKRON — pa
  // asnjë varësi nga historia e browser-it, pa vonesë, pa race.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // SIGURIA2 (U5) — pastrimi pret premtimin: nëse efekti rinisej para se
    // `addListener` të kthente dorezën, dëgjuesi i vjetër mbetej dhe «prapa»
    // ekzekutohej dy herë (mbyllte dy ekrane, ose minimizonte app-in).
    const premtimi = CapacitorApp.addListener("backButton", () => {
      if (computeAnyOverlayOpen()) {
        closeTopOverlay();
      } else {
        // Te rrënja: swipe/back i parë s'del jashtë — shfaq njoftim. I dyti
        // brenda ~2s minimizon app-in. (Norma e Android-it "shtyp prapa përsëri".)
        const now = Date.now();
        if (now - lastBackRef.current < 2000) {
          CapacitorApp.minimizeApp();
        } else {
          lastBackRef.current = now;
          showToast(t("Swipe back again to exit"));
        }
      }
    });
    return () => { Promise.resolve(premtimi).then((h) => h?.remove?.()).catch(() => {}); };
  }, [
    addSheet, focusPickerOpen,
    etiketaPer, historiaPersoniHapur, shtoHistoriOpen, shtoSkedarOpen, kerkimiOpen,
    photoLightbox, archiveLightbox, personModal, spouseModalFor, docHistoryOpenType,
    docModalOpen, myLegacyOpen,
    emailRepliesOpen, announceManageOpen, announcementsOpen, simpleMourningOpen,
    contactAdminOpen, aboutOpen, logPanelOpen, rolesPanelOpen, themePanelOpen,
    viewPanelOpen, searchOpen, avatarMenuOpen, gjuhaPanelOpen,
    securityPanelOpen, trashOpen,
    syncModalFor, importOpen, familiesHubOpen, switchFamOpen,
    occasionAI, impModalOpen, posterModalOpen, treeSettingsOpen, legacyConfirm, treeBarPanel,
    authOpen, legacyReqOpen, chatOpen, fabOpen, treeDrawerOpen, treeFabOpen, selectedId, deleteAccountOpen, guestAccessFor,
    activeScreen,
  ]);

  // FIX (raportuar nga useri) — dëgjuesi që MUNGONTE dhe që shkaktonte "hyrja
  // me Google në telefon më lë të bllokuar te Chrome". OS-i e rihap app-in
  // (jo Chrome-in) sapo Google të përfundojë hyrjen, sepse tani e dërgojmë
  // te skema com.pemaefamiljes.app://auth-callback (shih oauthRedirectUrl
  // sipër) — por app-i duhet EDHE ta "kapë" atë thirrje kthimi dhe t'ia
  // japë Supabase-it, përndryshe rihapet bosh, pa sesion. Kjo e bën
  // pikërisht atë: "appUrlOpen" xhiron sapo OS-i e rihap app-in me një URL
  // të kësaj skeme.
  //
  // Mbështet TË DYJA format e mundshme të URL-së së kthimit, sipas
  // konfigurimit të projektit Supabase (PKCE, parazgjedhja aktuale, JEP
  // "?code=..."; flow-i i vjetër "implicit" jep "#access_token=...") — kështu
  // funksionon pa marrë parasysh cilin ka Gazmendi të konfiguruar.
  // NOTCH-I — historia e plotë, sepse u rregullua tri herë dhe dy prej tyre
  // ishin të gabuara për arsye që s'dukeshin atëherë.
  //
  // Rregullimi i vjetër mbështetej te `StatusBar.setOverlaysWebView({ overlay:
  // false })`: i thoshte sistemit "mos e mbulo WebView-in me shiritin e
  // statusit". Funksiononte. Pastaj u shtua edhe `env(safe-area-inset-top)` te
  // header-i, dhe hapësira doli e dyfishtë — prandaj u zëvendësua me një
  // zbritje "- 24px", që rezervonte vetëm diferencën mbi lartësinë standarde.
  //
  // I gjithë ai ndërtim varej nga një gjë: që sistemi ta pranonte çkyçjen nga
  // edge-to-edge. Android 15 nisi ta shpërfillte; **Android 16 (targetSdk 36,
  // ai që ndërtojmë sot) e ka hequr fare**. Pra prej atij përditësimi:
  //   · `overlay: false` nuk bën asgjë — WebView-i vizatohet gjithmonë nën
  //     shiritin e statusit;
  //   · dhe "- 24px" zbret hapësirë që askush s'e ka rezervuar, duke e futur
  //     përmbajtjen 24px NËN prerje.
  //
  // Kjo është arsyeja pse pas logout-it stema e pemës hipte nën notch: te pema
  // shiriti i errët i header-it e mbulonte gabimin me trupin e vet, kurse te
  // ekrani i hyrjes ai shirit fshihet, dhe stema mbetet elementi i parë.
  //
  // ZGJIDHJA E TANISHME — një burim i vetëm, `SAFE_TOP`/`SAFE_BOTTOM`:
  // `--safe-area-inset-*` i injekton vetë Capacitor 8.3+ nga ana native, sepse
  // WebView-i i Android-it nën versionin 140 e kthen gabim `env()` (shpesh 0 —
  // pikërisht defekti që na dukej si "notch-i s'respektohet"). `env()` mbetet
  // si rezervë për iOS dhe për WebView-t e rinj, dhe `0px` si rezervë e fundit
  // për shfletuesin në desktop.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // 20 gusht 2026 — `overlay: false` u hoq. Android 16 (targetSdk 36) e ka
    // hequr mundësinë e çkyçjes nga edge-to-edge, pra thirrja ishte bërë pa
    // efekt në Android dhe linte iOS-in të sillej ndryshe nga Androidi. Tani
    // të dyja vizatojnë nën shiritat, dhe hapësirën e mban CSS-ja, me
    // `--safe-area-inset-*` (i injekton Capacitor 8.3+) dhe `env()` si rezervë.
    StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
    // 29 gusht 2026 — këtu qëndronte `Style.Light` me komentin «ikona/tekst i
    // çelët». Komenti ishte i gabuar dhe kodi bënte të kundërtën e tij: te
    // Capacitor `Light` do të thotë TEKST I ERRËT. Mbi header-in vishnje, ora
    // dhe bateria dilnin të zeza mbi të errët — të padukshme.
    // Tani stilin e llogarit `sinkronizoShiritinESistemit()` nga ndriçimi i
    // ngjyrës që ndodhet vërtet nën shirit, pra vlen për të shtatë temat dhe
    // për çdo dizajn të ardhshëm.
    sinkronizoShiritinESistemit();
  }, []);

  // Shiriti duhet rillogaritur edhe kur tema/dizajni ndryshon pa u rimontuar
  // aplikacioni. `designTick` rritet nga `onDesignChange`; `themeKey` ndryshon
  // kur përdoruesi zgjedh një temë te «Pamja».
  useEffect(() => {
    sinkronizoShiritinESistemit();
  }, [designTick, themeKey]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // OAUTH1 — përpunimi i adresës së kthimit jeton te oauth.js (i njëjti për
    // /start, /join, /invite): mbyll fletën e shfletuesit, lexon parametrat me
    // emër (dështimi para kodit), implicit ose PKCE. Këtu vetëm gabimi.
    const hiq = degjoKthiminEOAuth((gabimi) => {
      setAuthError(gabimi === "no_data" ? t("The sign-in came back with no data at all.") : gabimi);
    });
    return () => { hiq(); };
  }, []);

  // FAZA 3 (2FA OPSIONALE) — i njëjti API si Admin Center (supabase.auth.mfa),
  // thjesht KËTU është vetë-shërbyer dhe OPSIONAL, jo i detyrueshëm te login.
  async function openSecurityPanel() {
    setSecurityPanelOpen(true);
    setMfaError(""); setMfaEnrollData(null); setMfaCode("");
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) { setMfaError(error.message); setMfaFactors([]); return; }
    setMfaFactors((data?.totp || []).filter((f) => f.status === "verified"));
  }
  async function startMfaEnroll() {
    setMfaBusy(true); setMfaError("");
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
    setMfaBusy(false);
    if (error) { setMfaError(error.message); return; }
    setMfaEnrollData({ factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  }
  async function verifyMfaEnroll() {
    if (!mfaEnrollData || !mfaCode.trim()) return;
    setMfaBusy(true); setMfaError("");
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId: mfaEnrollData.factorId });
    if (chErr) { setMfaBusy(false); setMfaError(chErr.message); return; }
    const { error: verErr } = await supabase.auth.mfa.verify({ factorId: mfaEnrollData.factorId, challengeId: challenge.id, code: mfaCode.trim() });
    setMfaBusy(false);
    if (verErr) { setMfaError(verErr.message); return; }
    setMfaEnrollData(null); setMfaCode("");
    await openSecurityPanel(); // rifresko listën — tani duhet të shfaqet si aktive
  }
  async function unenrollMfa(factorId) {
    if (!window.confirm(t("Disable two-step verification? Next time you'll sign in only with password/Google/Apple."))) return;
    setMfaBusy(true); setMfaError("");
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    setMfaBusy(false);
    if (error) { setMfaError(error.message); return; }
    await openSecurityPanel();
  }

  // BALLINA1 — nëse skeda e zgjedhur nuk është më në listë (cilësimi ndryshoi,
  // ose arkivi u fik), kthehu te e para. Përndryshe ballina mbetet bosh pa e
  // ditur askush pse.
  //
  // RRI MBI ÇDO KTHIM TË HERSHËM TË `App` (mirëmbajtja, nisja, prova), DHE KJO
  // KA RËNDËSI. Nëse një rendje del para një hook-u dhe një tjetër e thërret,
  // numri i hook-ave ndryshon dhe React-i e ndal aplikacionin me gabimin #310.
  useEffect(() => {
    // MUZEU4 — edhe këtu duhet lloji: pa të, te një muze këta dy efekte do të
    // vazhdonin ta pranonin `legacy` ose `oda` si skedë të vlefshme, dhe do ta
    // rikthenin një skedë që lista e vërtetë e ka hequr. Një rregull i zbatuar
    // në dy vende nga tri, nuk është rregull.
    const cilat = skedatENjohura(homeTabsCfg, archiveEnabled, eshteMuze);
    if (!cilat.includes(homeTab)) setHomeTab(cilat[0]);
  }, [homeTabsCfg, archiveEnabled, homeTab, eshteMuze]);

  // KOKA1 — sa e lartë është koka PA foton. Matet nga kolona e majtë (ikona +
  // emri i familjes), sepse ajo është ana që e cakton lartësinë kur fotoja nuk
  // e rrit. `ResizeObserver` sepse emri i familjes, fonti dhe gjuha ndryshojnë
  // — një numër i ngulitur do të ishte i saktë sot dhe i gabuar javën tjetër.
  useEffect(() => {
    const el = kokaMajtasRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ob = new ResizeObserver(() => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) setKokaKufi(h);
    });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  // Nëse dikush rri te skeda e skedarëve te Historia pikërisht kur arkivi
  // zhvendoset te ballina, mos e lër përpara një ekrani bosh.
  //
  // BALLINA6 — arkivi nuk shkon më te ballina, pra ky efekt s'ka më çfarë të
  // zgjidhë. Mbetet vetëm rregulli që `memoriesTab` të mos ngecë te një vlerë
  // që s'ka më ekran; skeda «files» u hoq bashkë me `renderArkiven`.
  useEffect(() => {
    if (memoriesTab === "files") setMemoriesTab("events");
  }, [memoriesTab]);

  // ==========================================================================
  // RENDER. Header bar → SVG tree canvas (cards, connector lines, expand
  // buttons) → ~25 modals (auth, add/edit person, stats, members, log,
  // notifications, documents…), each gated by its own `xOpen` boolean state.
  // ==========================================================================
  // Maintenance Mode — bllokim i plotë për këdo që s'është në listën e lejimit.
  if (maintenanceBlocked) {
    return (
      <div className="w-full flex flex-col items-center justify-center text-center" style={{ minHeight: "100vh", background: COLORS.bgDeep, padding: 24, fontFamily: FONTS.body }}>
        <div style={{ fontSize: 46, marginBottom: 12 }}>🛠️</div>
        <h1 style={{ fontFamily: FONTS.display, fontSize: 24, color: COLORS.onBg, margin: 0 }}>{t("We're doing some maintenance")}</h1>
        {maintStatus && maintStatus.reason && <p style={{ fontFamily: FONTS.body, fontSize: 15, color: COLORS.gold, marginTop: 12 }}>{maintStatus.reason}</p>}
        {maintStatus && maintStatus.info && <p style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.onBg, opacity: 0.8, marginTop: 8, maxWidth: 440, lineHeight: 1.55 }}>{maintStatus.info}</p>}
        <MaintCountdown until={maintStatus && maintStatus.until} labelPrefix={t("Back in")} onDone={refreshMaint} />
        <button onClick={() => setMaintPeek(true)} style={{ marginTop: 26, background: "transparent", border: `1px solid ${COLORS.goldSoft}`, color: COLORS.onBg, opacity: 0.75, borderRadius: 8, padding: "8px 16px", fontFamily: FONTS.body, fontSize: 13, cursor: "pointer" }}>
          {t("Sign in (allowed users)")}
        </button>
      </div>
    );
  }

  // NISJA — pse kishte pulsim.
  //
  // `locked` llogaritet nga tri sinjale që NUK mbërrijnë njëkohësisht:
  // sesioni, personi i lidhur, dhe a ka pema ndonjë anëtar. Gjatë ngarkimit
  // secili prej tyre ka një vlerë të përkohshme, dhe `locked` ndërron vlerë me
  // to. Prandaj shiheshin dy pulsime të kundërta:
  //
  //   · në hapje të ftohtë — `treeHasAnyPeople` është ende null, pra kushti i
  //     dytë s'plotësohet, `locked` del false dhe aplikacioni vizatohet sikur
  //     të ishe brenda; pastaj mbërrijnë të dhënat, `locked` bëhet true, dhe
  //     ekrani i hyrjes e zë vendin.
  //   · në rifreskim brenda aplikacionit — e kundërta: `treeHasAnyPeople`
  //     mbërrin para `currentPersonId`, `locked` bëhet true për një çast, dhe
  //     ekrani i hyrjes pulson mbi pemën.
  //
  // Zgjidhja s'është të përshpejtohet ngarkimi, por të mos merret vendim para
  // kohe. Derisa dihen të dyja — a ka sesion, dhe nëse po, cili person është —
  // nuk vizatohet as njëra as tjetra, vetëm sfondi. Asnjë ndryshim ngjyre, asnjë
  // mbivendosje: ekrani rri i qetë dhe pastaj shfaqet ajo që duhet, njëherë.
  // Pritja tani është sa lexohet sesioni nga ruajtja vendore — zakonisht nën një
  // të dhjetën e sekondës, pra praktikisht e padukshme. Më parë priste edhe
  // zgjidhjen e personit, dhe ajo zgjat sa të ngarkohet pema: rezultati ishte një
  // ekran i plotë me ngjyrën e temës, i dukshëm sa për të bezdisur. Ai ishte
  // ilaç i gabuar — pulsimi nuk vinte nga shpejtësia, por nga kushti i mësipërm.
  // Pa sesion nuk pritet fare për temën: ekrani i hyrjes ka paletën e vet, të
  // pavarur nga cilësimet e familjes. Pritja vlen vetëm për atë që hyn brenda.
  const bootSettled = bootGraceOver || (!authSessionLoading && (themeReady || !authSession));
  if (!bootSettled) {
    // TRANSPARENT, jo `COLORS.bgDeep` — dhe kjo ishte "dritarja e kuqe".
    // `COLORS` nis si tema klasike (e kuqja) dhe merr vlerat e dizajnit vetëm te
    // `syncDesignColors()`, që xhiron pas vizatimit të parë. Pra pikërisht ky
    // element e pikturonte të kuqen mbi një faqe që tashmë kishte ngjyrën e
    // duhur. Sfondi i `html/body` vjen nga `--bgDeep`, i vendosur sinkron nga
    // cache-i para se React-i të nisë — ndaj mjafton të mos e mbulojmë.
    return <div className="app-shell w-full" style={{ background: "transparent" }} aria-busy="true" />;
  }

  // ─── GUASKAT ─────────────────────────────────────────────────────────────
  //
  // Çdo hyrje tregon te diçka që EKZISTON tashmë. Asnjë ekran i ri.
  //
  //   Pema       → activeScreen "tree"
  //   Kujtimet   → activeScreen "memories", skeda "files"
  //   Historia   → activeScreen "memories", skeda "events"   (i njëjti ekran)
  //   Familja    → activeScreen "home"
  //   Shërbimet  → activeScreen "services"
  //   Profili    → menuja ekzistuese e llogarisë
  //   Statistika → activeScreen "stats"
  //
  // Vër re Kujtimet dhe Historinë: një ekran, dy dyer. Kjo është e gjithë ideja.
  // `NAV_SHELLS` (pesë shabllone të ngulitura) u hoq më 29 gusht 2026 — shih
  // shënimin te «PAMJA E APLIKACIONIT — E HEQUR». Katalogu i hyrjeve mbetet
  // këtu poshtë; radha vjen nga cilësimi i Admin Center.
  const NAV_HYRJET = {
    tree:     { etiketa: t("The Tree"),  Ikona: PemaSkeda,   aktive: () => activeScreen === "tree",
                shko: () => setActiveScreen("tree") },
    // BALLINA6 — arkivi nuk është më vend më vete. Skedarët janë pjesë e
    // Historisë; ndryshimi është vetëm cili filtër ndizet kur mbërrin.
    // Hyrja mbetet e regjistruar (ka shirita të ruajtur që e përmendin) dhe të
    // çon te Historia me filtrin «Foto» të ndezur — jo te një ekran që s'ka më.
    arkiva:   { etiketa: t("Archive"),   Ikona: Camera,
                aktive: () => activeScreen === "memories" && historiaFiltri !== "gjitha",
                shko: () => { setHistoriaFiltri("foto"); openMemories("events"); } },
    history:  { etiketa: t("History"),   Ikona: BookOpen,    aktive: () => activeScreen === "memories" && historiaFiltri === "gjitha",
                shko: () => { setHistoriaFiltri("gjitha"); openMemories("events"); } },
    home:     { etiketa: t("Family"),    Ikona: Home,        aktive: () => activeScreen === "home",
                shko: () => setActiveScreen("home") },
    services: { etiketa: t("Services"),  Ikona: ShoppingBag, aktive: () => activeScreen === "services",
                shko: () => setActiveScreen("services") },
    stats:    { etiketa: t("Statistics"),Ikona: BarChart3,   aktive: () => activeScreen === "stats",
                shko: () => setActiveScreen("stats") },
    profile:  { etiketa: t("Profile"),   Ikona: UserCircle2, aktive: () => avatarMenuOpen,
                shko: () => setAvatarMenuOpen(true) },
    chat:     { etiketa: t("Assistant"), Ikona: MessageCircle, aktive: () => chatOpen,
                shko: () => setChatOpen(true) },
    // BALLINA4 — «Shto» nuk është ekran; është veprim. Prandaj `aktive` mbetet
    // gjithmonë false: asnjë skedë nuk ndizet, sepse askund nuk shkohet.
    // Vizatohet ndryshe nga të tjerat — shih `renderShellBar`.
    shto:     { etiketa: t("Add"), Ikona: Plus, aktive: () => activeScreen === "shto",
                shko: () => hapShtimin(), veprim: true },
  };
  // Alias: `memories` te shiriti ka nënkuptuar gjithmonë arkivin.
  NAV_HYRJET.memories = NAV_HYRJET.arkiva;

  // Hyrjet e shiritit: cilësimi fiton; pa cilësim, shabllonet e vjetra. Filtrimi
  // ndaj `NAV_HYRJET` nuk është dekor — një emër i panjohur do të hidhte
  // `undefined.Ikona` dhe do ta linte aplikacionin me ekran të bardhë.
  function hyrjetEShiritit() {
    const ngaCilesimi = (navEntries || []).filter((k) => NAV_HYRJET[k]).slice(0, 6);
    if (ngaCilesimi.length > 0) return ngaCilesimi;
    // Pa cilësim → `null` = shiriti klasik i vjetër, i pandryshuar. Ky ishte
    // gjithnjë kuptimi i «classic»; tani është i vetmi rrugëtim mbrapa.
    return null;
  }

  // Skedat e ballinës — po ai parim: cilësimi fiton, pa cilësim tri skedat e
  // vjetra. `arkiva` shfaqet vetëm nëse arkivi është i ndezur, përndryshe do të
  // ishte një skedë që hap një zbrazëti.
  function skedatEBallines() {
    return skedatENjohura(homeTabsCfg, archiveEnabled, eshteMuze).map((k) => [k, HOME_TAB_ETIKETA(t)[k]]);
  }

  // ARKIVI JETON NË NJË VEND TË VETËM (kërkesë e pronarit, 28 gusht 2026):
  // «arkiva siç është tani te Historia zhvendoset aty» — te ballina.
  //
  // «Zhvendoset» do të thotë hiqet nga vendi i vjetër. Deri tani ishte në të dy
  // vendet: skeda «Arkiva» te ballina DHE skeda «Të gjithë skedarët» te Historia.
  // Dy dyer për të njëjtën dhomë janë pikërisht ajo që e nisi këtë bisedë —
  // «Kujtimet» te menyja që hapte arkivin.
  //
  // BALLINA6 — pyetja «ku jeton arkivi» mbaroi: jeton te Historia, gjithmonë,
  // si filtër i një lumi të vetëm. Ndryshorja `arkiviTeBallina` u hoq bashkë me
  // përgjigjen që ndryshonte sipas cilësimeve.

  // ── HISTORIA: NJË LUMË ─────────────────────────────────────────────────────
  //
  // Ngjarjet e familjes dhe skedarët e arkivës ishin dy lista në dy skeda. Por
  // një njeri që kërkon «kur zbritëm nga Kelmendi» nuk e di paraprakisht nëse
  // përgjigjja u shkrua si ngjarje apo u ngarkua si dokument — dhe e humb duke
  // kërkuar te skeda e gabuar. Prandaj një listë e vetme, e renditur nga sot
  // drejt fillimit, dhe filtrat e ngushtojnë pa e lëvizur nga vendi.
  //
  // Llogaritet te trupi, pa `useMemo`: qindra rreshta, një renditje. Një hook i
  // ri këtu do të ishte hook i katërmbëdhjetë me varësi që duhen mbajtur mend,
  // dhe pikërisht ata na kanë dhënë ekranet e bardha këtë javë.
  const historiaRrjedha = (() => {
    const rreshtat = [];
    (familyHistoryEvents || []).forEach((ev) => {
      rreshtat.push({ id: `ev-${ev.id}`, tipi: "ngjarje", ev,
        koha: kohaEHistorise(ev.event_date), viti: vitiEHistorise(ev.event_date) });
    });
    if (archiveEnabled) {
      (archiveItems || []).forEach((it) => {
        const tipi = (it.file_type === "photo" || it.file_type === "video") ? "foto"
          : it.file_type === "audio" ? "zera" : "dokumente";
        rreshtat.push({ id: `ar-${it.id}`, tipi, it,
          koha: kohaEHistorise(it.created_at), viti: vitiEHistorise(it.created_at) });
      });
    }
    return rreshtat.sort((a, b) => b.koha - a.koha);
  })();

  const historiaERreshtuar = historiaRrjedha.filter((r) => {
    if (historiaFiltri === "gjitha") return true;
    if (historiaFiltri === "person") {
      return !!historiaPersoni && Array.isArray(r.it?.person_ids) && r.it.person_ids.includes(historiaPersoni);
    }
    return r.tipi === historiaFiltri;
  });

  const historiaNumrat = historiaRrjedha.reduce((acc, r) => { acc[r.tipi] = (acc[r.tipi] || 0) + 1; return acc; }, {});
  const historiaFiltrat = [
    ["gjitha", t("All"), historiaRrjedha.length],
    ["ngjarje", t("Events"), historiaNumrat.ngjarje || 0],
    ...(archiveEnabled ? [
      ["foto", t("Photos"), historiaNumrat.foto || 0],
      ["zera", t("Voices"), historiaNumrat.zera || 0],
      ["dokumente", t("Documents"), historiaNumrat.dokumente || 0],
      ["person", historiaPersoni ? displayName(people[historiaPersoni]) : t("A person…"), null],
    ] : []),
  ];


  // BALLINA6 — `renderArkiven` u hoq. Ishte një ekran i dytë për të njëjtat
  // skedarë, dhe pikërisht dy vende për një gjë ishte problemi që po zgjidhim.
  // Skedarët tani rrjedhin te Historia; ngarkimi, matësi dhe etiketat rrinë aty
  // ku shihen. Ajo që mbeti prej saj — kuti e zmadhimit — u ngjit te guaska,
  // sepse një mbivendosje `fixed` brenda një ekrani që rrëshqet nuk është
  // mbivendosje (mësimi i PEMA3).

  // BALLINA7 — STATISTIKAT SI SKEDË E BALLINËS.
  //
  // Kërkesë e pronarit (29 gusht 2026): «ma bëj të mundur në Admin Center ta
  // vendos statistikat si tab aty, dhe ndoshta ta heq nga menyja në të ardhmen».
  //
  // Prandaj NUK u kopjua ekrani. Trupi u nxor këtu një herë dhe lexohet nga të
  // dy vendet — shiriti dhe skeda. Rregulli i GUASKA1: dy kopje brenda një muaji
  // tregojnë dy të vërteta, dhe atëherë askush s'e di cila është e vërteta.
  //
  // Titulli shfaqet vetëm te ekrani i vet: te ballina skeda e ka thënë tashmë,
  // dhe një titull që përsërit skedën e sapo prekur është zhurmë.
  // ARKIVA3 — MATËSI I HAPËSIRËS, NJË ZBATIM.
  //
  // Pronari e do shiritin të dukshëm; unë e kisha lënë vetëm te faqja e
  // ngarkimit, dy prekje larg. Tani shfaqet edhe te Historia — por nga i njëjti
  // funksion, jo nga dy kopje. Ky ishte kundërshtimi im i parë («i njëjti numër
  // i vizatuar dy herë»), dhe ai vlen vetëm kur ka dy VIZATIME. Me një zbatim,
  // numri s'ka nga t'i ndahet vetes.
  //
  // Pa ARKIVA2 `arkivaGjendja` mbetet null dhe këtu nuk shfaqet asgjë — asnjë
  // gjysmë-shirit me kufij të trilluar.
  // ══════════════════════════════════════════════════════════════════════
  // HAPËSIRA — SA E DUKSHME, AQ SA URGJENTE (3 shtator 2026)
  //
  // Vërejtja e pronarit: «aty ku është po zë shumë vend; ai shirit nuk duhet
  // të bjerë në sy më shumë se çdo gjë tjetër.» E drejtë, dhe shkaku nuk ishte
  // vendi — ishte se shiriti fliste GJITHMONË ME TË NJËJTIN ZË. «0.3% e
  // përdorur» merrte një kartelë të plotë mbi përmbajtjen që njeriu erdhi ta
  // shohë, me të njëjtën peshë me të cilën do të thoshte «hapësira mbaroi».
  //
  // Një numër që thotë «gjithçka është në rregull» nuk është lajm.
  //
  // Prandaj tani dukshmëria ndjek urgjencën:
  //
  //   nën 90%   →  asgjë. Për çdo familje që ekziston sot, shiriti thjesht
  //                zhduket nga Historia dhe nga fleta e ngarkimit.
  //   90–99%    →  një vijë e hollë me një fjali. Pa kartelë, pa kufi, pa panel.
  //   100%      →  kartelë e vërtetë — atëherë PO duhet të bjerë në sy, sepse
  //                ngarkimi i radhës do të refuzohet.
  //
  // Pamja e plotë (tre numrat, shiriti, «Zgjero») jeton te SHËRBIMET — aty ku
  // hapësira edhe shitet, dhe ku njeriu shkon kur do ta rrisë.
  //
  // `vendi`: "sherbimet" = gjithmonë e plotë · "mbi"/"nen" = sipas pragut.
  // ══════════════════════════════════════════════════════════════════════
  const PRAGU_I_HAPESIRES = 90;

  // Numrat e përbashkët, një herë. `null` kur s'dimë ende asgjë.
  const hapesiraNumrat = () => {
    const G = arkivaGjendja;
    if (!G) return null;
    const ka3 = G.total_bajte !== undefined && G.total_bajte !== null;
    const eNdare = ka3 && G.menyra === "ndare";
    const bajteArkiv = Number(ka3 ? G.arkiv_bajte : G.bajte) || 0;
    const bajtePlat  = Number(ka3 ? G.platforme_bajte : 0) || 0;
    const bajteTot   = Number(ka3 ? G.total_bajte : G.bajte) || 0;
    const saGjithsej = Number((ka3 ? G.total_sa : G.sa) || 0);
    const paLimit  = !!G.pa_limit;
    const limArkiv = paLimit ? null : Number((ka3 ? G.limit_arkiv_mb : G.limit_mb) || 0);
    const limPlat  = paLimit ? null : Number(G.limit_platforme_mb || 0);
    const limTot   = paLimit ? null : Number((ka3 ? G.limit_total_mb : G.limit_mb) || 0);
    const mb = (b) => b / 1048576;
    const pj = (u, l) => (l ? Math.min(100, (u / l) * 100) : 0);
    // Te mënyra e ndarë vlen ajo prej të dyjave që është më afër kufirit të vet:
    // ajo është e para që do të ndalojë dikë, pra ajo është e vërteta që numëron.
    const perqindja = eNdare
      ? Math.max(pj(mb(bajteArkiv), limArkiv), pj(mb(bajtePlat), limPlat))
      : pj(mb(bajteTot), limTot);
    return { ka3, eNdare, bajteArkiv, bajtePlat, bajteTot, saGjithsej,
             paLimit, limArkiv, limPlat, limTot, mb, pj, perqindja };
  };

  const sipasH = (n) => (n < 1024 ? `${n.toFixed(n < 10 ? 1 : 0)} MB` : `${(n / 1024).toFixed(2)} GB`);

  function renderMatesinEArkives(vendi) {
    const N = hapesiraNumrat();
    if (!N) return null;
    // Te Historia dhe te fleta e ngarkimit lidhja me arkivin ka kuptim; te
    // Shërbimet jo — aty hapësira është produkt, jo veçori e arkivit.
    if (vendi !== "sherbimet" && !archiveEnabled) return null;
    if (N.paLimit && vendi !== "sherbimet") return null;
    const plot = N.perqindja >= 100;
    const afer = N.perqindja >= PRAGU_I_HAPESIRES;
    if (vendi !== "sherbimet" && !afer) return null;   // heshtja e parazgjedhur

    const NGJ_ARKIV = COLORS.gold;
    const NGJ_PLAT  = _rgba(COLORS.ink, 0.34);
    const NGJ_ALARM = "#C2410C";

    const pike = (ngjyre) => (
      <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: ngjyre, marginRight: 5, verticalAlign: "middle" }} />
    );
    const butoniZgjero = (
      <button onClick={() => { setShtoSkedarOpen(false); setActiveScreen("services"); }}
              style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: COLORS.ink, background: "transparent", border: "none", cursor: "pointer", whiteSpace: "nowrap" }}>
        {t("Expand space")} →
      </button>
    );
    const segmentet = (limitMb, aferX) => {
      const a = limitMb ? Math.min(100, (N.mb(N.bajteArkiv) / limitMb) * 100) : 0;
      const p = limitMb ? Math.min(100 - a, (N.mb(N.bajtePlat) / limitMb) * 100) : 0;
      return (
        <div className="mt-2 rounded-full" style={{ height: 6, background: COLORS.accentTint, overflow: "hidden", display: "flex" }}>
          <div style={{ width: `${a}%`, height: "100%", background: aferX ? NGJ_ALARM : NGJ_ARKIV, transition: "width .25s" }} />
          <div style={{ width: `${p}%`, height: "100%", background: aferX ? NGJ_ALARM : NGJ_PLAT, transition: "width .25s" }} />
        </div>
      );
    };

    // ── 90–99%: NJË VIJË, JO NJË KARTELË ─────────────────────────────────
    // Pa sfond, pa kufi, pa panel. Zë një rresht dhe një fije 3px — sa duhet
    // për ta parë, jo aq sa për ta ndalur syrin.
    if (vendi !== "sherbimet" && !plot) {
      return (
        <div className={vendi === "mbi" ? "mt-4" : "mb-4"}>
          <div className="flex items-baseline justify-between gap-2">
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: NGJ_ALARM, margin: 0 }}>
              {t("Space is running out — {{perdorur}} of {{limit}}.", {
                perdorur: sipasH(N.mb(N.bajteTot)),
                limit: sipasH(N.eNdare ? (N.limArkiv + N.limPlat) : N.limTot),
              })}
            </p>
            {butoniZgjero}
          </div>
          <div className="mt-1 rounded-full" style={{ height: 3, background: COLORS.accentTint, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, N.perqindja)}%`, height: "100%", background: NGJ_ALARM }} />
          </div>
        </div>
      );
    }

    // ── 100% te Historia/fleta, dhe GJITHMONË te Shërbimet ───────────────
    const korniza = (femijet) => (
      <div className={vendi === "mbi" ? "rounded-xl mt-4" : "rounded-xl mb-4"}
           style={{ background: vendi === "mbi" ? COLORS.panelSoft : COLORS.panel,
                    border: `1px solid ${plot && vendi !== "sherbimet" ? NGJ_ALARM : COLORS.goldSoft}`,
                    padding: "12px 14px" }}>
        {femijet}
      </div>
    );

    if (N.eNdare) {
      const pA = N.pj(N.mb(N.bajteArkiv), N.limArkiv), pP = N.pj(N.mb(N.bajtePlat), N.limPlat);
      const aA = pA >= PRAGU_I_HAPESIRES, aP = pP >= PRAGU_I_HAPESIRES;
      return korniza(<>
        <div className="flex items-baseline justify-between gap-2">
          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: 0 }}>{t("Space")}</p>
          <p style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: (aA || aP) ? NGJ_ALARM : COLORS.ink, opacity: (aA || aP) ? 1 : 0.78, margin: 0, fontVariantNumeric: "tabular-nums" }}>
            {N.paLimit ? t("no limit") : `${sipasH(N.mb(N.bajteTot))} ${t("in total")}`}
          </p>
        </div>
        {[["arkiv", N.bajteArkiv, N.limArkiv, pA, aA, NGJ_ARKIV, t("Archive")],
          ["plat",  N.bajtePlat,  N.limPlat,  pP, aP, NGJ_PLAT,  t("Everything else")]].map(([k, b, lim, pc, af, ngj, et]) => (
          <div key={k} style={{ marginTop: 10 }}>
            <div className="flex items-baseline justify-between gap-2">
              <p style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight, margin: 0 }}>{pike(ngj)}{et}</p>
              <p style={{ fontFamily: FONTS.body, fontSize: 12, color: af ? NGJ_ALARM : COLORS.ink, opacity: af ? 1 : 0.78, margin: 0, fontVariantNumeric: "tabular-nums" }}>
                {lim ? `${sipasH(N.mb(b))} / ${sipasH(lim)}` : sipasH(N.mb(b))}
              </p>
            </div>
            {lim ? (
              <div className="mt-2 rounded-full" style={{ height: 6, background: COLORS.accentTint, overflow: "hidden" }}>
                <div style={{ width: `${pc}%`, height: "100%", background: af ? NGJ_ALARM : ngj, transition: "width .25s" }} />
              </div>
            ) : null}
          </div>
        ))}
        <div className="flex items-center justify-between gap-2 mt-2">
          <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, margin: 0 }}>{t("{{sa}} files", { sa: N.saGjithsej })}</p>
          {(N.limArkiv || N.limPlat) ? butoniZgjero : null}
        </div>
      </>);
    }

    return korniza(<>
      <div className="flex items-baseline justify-between gap-2">
        <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: 0 }}>
          {N.ka3 ? t("Space") : t("Archive space")}
        </p>
        <p style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: afer ? NGJ_ALARM : COLORS.ink, opacity: afer ? 1 : 0.78, margin: 0, fontVariantNumeric: "tabular-nums" }}>
          {N.limTot ? `${sipasH(N.mb(N.bajteTot))} / ${sipasH(N.limTot)}` : t("no limit")}
        </p>
      </div>
      {N.limTot ? segmentet(N.limTot, afer) : null}
      {N.ka3 ? (
        <div className="flex items-center gap-3 mt-2" style={{ flexWrap: "wrap" }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>
            {pike(NGJ_ARKIV)}{t("Archive")} {sipasH(N.mb(N.bajteArkiv))}
          </span>
          <span style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>
            {pike(NGJ_PLAT)}{t("Everything else")} {sipasH(N.mb(N.bajtePlat))}
          </span>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 mt-2">
        <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, margin: 0 }}>
          {N.limTot ? t("{{p}}% used · {{sa}} files", { p: Math.round(N.perqindja), sa: N.saGjithsej })
                    : t("{{sa}} files", { sa: N.saGjithsej })}
        </p>
        {N.limTot ? butoniZgjero : null}
      </div>
    </>);
  }

  // RRIPI KUR HAPËSIRA MBARON — nën header, në çdo ekran.
  //
  // I vetmi vend ku hapësira e ndjek njeriun kudo, dhe vetëm në gjendjen ku çdo
  // ngarkim i radhës do të refuzohet. Deri atëherë s'ekziston fare. Ndjek të
  // njëjtën gjuhë vizuale si shiriti i mirëmbajtjes: i hollë, nën kokë, jo modal.
  function renderRripinEHapesires() {
    const N = hapesiraNumrat();
    if (!N || N.paLimit || N.perqindja < 100) return null;
    return (
      <div style={{ background: "#C2410C", color: "#FFF7ED", display: "flex", alignItems: "center",
                    gap: 8, padding: "6px 10px", fontFamily: FONTS.body, fontSize: 12,
                    borderBottom: "1px solid rgba(0,0,0,.15)" }}>
        <span style={{ flexShrink: 0 }}>⚠</span>
        <span style={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>{t("space.full")}</span>
        <button onClick={() => setActiveScreen("services")}
                style={{ flexShrink: 0, background: "transparent", border: "1px solid rgba(255,255,255,.5)",
                         color: "inherit", borderRadius: 6, padding: "2px 8px", fontSize: 11.5, cursor: "pointer" }}>
          {t("Expand space")}
        </button>
      </div>
    );
  }

  function renderStatistikat(meTitull) {
    // STATISTIKA3 — një pamje e vetme; gjithçka jeton te `EkraniIStatistikave`.
    return (
      /* ⚠ 5 shtator 2026 — «si ka mundësi që unë vetë jam kushëriri i largët?»
         Sepse statistikat lexonin kolonën e vjetër `p.branch`, kurse pema lexon
         `effectiveBranch(p)`. Ajo kolonë është, me fjalët e komentit te
         `relationMap`, «fusha e vjetër MANUALE, hequr nga formulari i zakonshëm»
         — mbahet vetëm si mbajtëse e tri rasteve të veçanta. Motori automatik i
         llogarit degët nga farefisnia, dhe ai vlen kudo veç këtu. Pra një vlerë
         e mbetur nga një redaktim a import i vjetër nuk dukej askund tjetër, po
         te statistikat po. Tani të dyja lexojnë të njëjtin burim.
         `degaVersioni` ekziston që memo-t të rillogariten kur ndryshon pika e
         referimit — farefisnia matet nga dikush, ndaj degët varen prej tij. */
      <EkraniIStatistikave
        people={people} marriages={marriages} ngjarjeHistorie={(familyHistoryEvents || []).length}
        cilesimet={statistikaCilesimet} KART={KART} FONTS={FONTS} t={t} lang={i18n.language || "sq"}
        meTitull={meTitull} generationsCount={generationsCount}
        degaEfektive={effectiveBranch}
        degaFamiljare={degaFamiljare}
        emriIFamiljes={familyDisplayName || ""}
        personiIReferimit={focusPersonId || myPersonId || null}
        degaVersioni={String(focusPersonId || myPersonId || "") + "|" + String(rrenjaEFamiljes || "")}
        onHapPerson={(id) => { setActiveScreen("tree"); setSelectedId(id); }} />
    );
  }

  function renderShellBar() {
    const hyrjet = hyrjetEShiritit();
    if (!hyrjet) return null;
    const conf = { hyrjet };
    return (
      <div className="flex items-center shrink-0" style={{ position: "relative" }}>
        {conf.hyrjet.map((k) => {
          const h = NAV_HYRJET[k];
          if (!h) return null;
          // Shërbimet fshihen kur s'ka asgjë për të shitur — ndryshe hyrja
          // çon te një dhomë bosh, dhe muajin e parë vitrina rri e mbyllur.
          if (k === "services" && servicesCount === 0) return null;
          const on = h.aktive();
          // BALLINA5 — «Shto» është hyrje VEPRIMI, jo destinacion. E pata bërë
          // një rreth të artë të mbushur, që të dallohej — dhe pronari e pa
          // menjëherë: «nuk po e ndjek harmoninë, ngjyrat e butonave të tjerë».
          // Kishte të drejtë. Një hyrje mund të thotë «unë jam ndryshe» me formë,
          // pa dalë nga paleta e shiritit.
          //
          // Prandaj tani parazgjedhja është `njesoj` — saktësisht si të tjerat,
          // me `navIconColor` dhe të njëjtin opacitet. Dallimi, kur e do, bëhet
          // me një unazë të hollë ose me rrethin e mbushur, të zgjedhur te
          // Admin Center. Asnjë prej tyre nuk shpik ngjyrë të re.
          if (h.veprim) {
            const st = navAddStyle || "njesoj";
            const ngjyra = navIconColor(k, false);
            return (
              <button key={k} onClick={h.shko}
                className="flex-1 flex flex-col items-center gap-0.5 py-2"
                aria-label={h.etiketa} title={h.etiketa}>
                {st === "ngritur" ? (
                  <span className="flex items-center justify-center rounded-full"
                    style={{ width: 34, height: 34, background: COLORS.gold, color: COLORS.bgDeep,
                             boxShadow: "0 4px 12px rgba(0,0,0,.28)", marginTop: -2 }}>
                    <h.Ikona size={19} />
                  </span>
                ) : st === "unaze" ? (
                  <span className="flex items-center justify-center rounded-full"
                    style={{ width: 26, height: 26, border: `1.5px solid ${ngjyra}`,
                             opacity: COLORS.vividIcons ? 1 : 0.75 }}>
                    <h.Ikona size={15} color={ngjyra} />
                  </span>
                ) : (
                  <h.Ikona size={19} color={ngjyra}
                    style={{ opacity: COLORS.vividIcons ? 1 : 0.55 }} />
                )}
                <span style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.01em",
                               color: st === "ngritur" ? COLORS.accentText : COLORS.onBg,
                               opacity: st === "ngritur" ? 1 : 0.7 }}>{h.etiketa}</span>
              </button>
            );
          }
          return (
            <button key={k} onClick={h.shko}
              className="flex-1 flex flex-col items-center gap-0.5 py-2"
              aria-current={on ? "page" : undefined}>
              <h.Ikona size={19} color={navIconColor(k, on)}
                style={{ opacity: COLORS.vividIcons ? 1 : (on ? 1 : 0.55) }} />
              {/* SISTEMI VIZUAL — 9.5px mono ishte nën çdo dysheme. Etiketa e
                  shiritit është përjashtimi i vetëm nga 12.5: pesë fjalë në 360px
                  nuk zënë vend më të madhe. 11.5 me peshë 500 lexohet; opacity
                  0.7 në vend të 0.55, që teksti joaktiv të mos zhduket. */}
              <span style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.01em",
                             color: on ? COLORS.accentText : COLORS.onBg,
                             opacity: on ? 1 : 0.7 }}>{h.etiketa}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // ── PROVA 2 — NDARJA TE KUFIRI I IDENTIFIKIMIT ────────────────────────────
  //
  //   ⚠ E PËRKOHSHME. Fiket duke vënë `PROVA = 0` te `prova.jsx`; ky bllok dhe
  //     importi lart hiqen bashkë me atë skedar para lansimit.
  //
  // Të gjitha hook-et e `App`-it janë thirrur tashmë më sipër — pra efektet
  // punojnë, të dhënat merren, dëgjuesit ndizen, fotografitë nisin të vijnë.
  // Ndryshimi i vetëm është se PAMJA nuk vizatohet: asnjë komponent fëmijë
  // nuk montohet, as pema, as tab-et. Ekrani i hyrjes mbetet i paprekur, sepse
  // aty rrëzimi s'ka ndodhur kurrë — ndarja nis pikërisht kur `locked` bie.
  //
  // Numri vazhdon → gjysma e efekteve dhe e të dhënave është e pastër.
  // Numri kthehet te 0 → fajtori është aty, dhe pemën s'kemi pse e prekim.
  if (!locked && prova(2)) return <EkraniProves etiketa="pas hyrjes" />;

  return (
    <div className="app-shell w-full flex flex-col" style={{ background: "var(--bg, #1F2A1E)", fontFamily: FONTS.body }}>
      {/* FAZA 40 — indikatori i pull-to-refresh, shfaqet vetëm kur po tërheq */}
      {pullDistance > 0 && (
        <div className="flex items-center justify-center lg:hidden" style={{ height: Math.min(pullDistance, 48), overflow: "hidden", background: COLORS.bgDeep, transition: pullDistance === 0 ? "height 0.2s" : "none" }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 500, color: COLORS.gold, opacity: pullDistance > PULL_THRESHOLD ? 1 : 0.6 }}>
            {pullDistance > PULL_THRESHOLD ? t("↓ Release to refresh") : t("↓ Pull to refresh")}
          </span>
        </div>
      )}
      <div
        ref={headerBarRef}
        className="relative"
        style={{
          // Te ekrani i hyrjes shiriti fshihet krejt: aty s'ka çka të tregojë
          // (emri i familjes shfaqet vetëm pas identifikimit) dhe i hante
          // ilustrimit rreth 60px lartësi. Hapësira e notch-it merret përsipër
          // nga vetë LoginScreen, me env(safe-area-inset-top).
          display: locked ? "none" : undefined,
          background: COLORS.bgDeep, borderBottom: `1px solid ${COLORS.goldSoft}`,
          // RISHKRIM (20 gusht 2026) — këtu kishte një zbritje "- 24px", e
          // ndërtuar mbi supozimin se StatusBar.setOverlaysWebView({overlay:
          // false}) i rezervonte vetë ato 24px. Ai supozim nuk vlen më: me
          // targetSdk 36 Android-i e vizaton gjithmonë WebView-in nën shiritin
          // e statusit dhe s'e lejon më çkyçjen. Pra asgjë s'rezervon 24px, dhe
          // zbritja thjesht e ngjiste header-in 24px nën prerje.
          // Tani hapësira merret e plotë, nga i vetmi burim që e di sa është.
          paddingTop: hapesira(SAFE_TOP, headerPad("padTop")),
        }}
      >
        {showEmblem("eagle", themeKey) && (
          <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
            {/* Këtu korniza është vetë e vogël (110px) — pra shqiponja e mbush atë, jo ekranin. */}
            <div style={{ position: "absolute", right: 8, top: -30, width: 110, height: 110 }}><EagleWatermark opacity={0.16} madhesia="100%" /></div>
          </div>
        )}
        {showEmblem("kosovo", themeKey) && (
          <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
            <div style={{ position: "absolute", right: 8, top: -30, width: 110, height: 110 }}><KosovoWatermark opacity={0.2} /></div>
          </div>
        )}
        {designHoliday() && HOLIDAY_MOTIF[designHoliday()] && (
          <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }} aria-hidden>
            <div style={{ position: "absolute", right: 52, top: 8, fontSize: 22, opacity: 0.55 }}>{HOLIDAY_MOTIF[designHoliday()]}</div>
          </div>
        )}
        <div className="flex items-center gap-2 px-3 sm:px-6 pt-3 flex-nowrap justify-between" /* KOKA2 — poshtë sa lart. `pt-3` është 12px; çdo vlerë tjetër e zhvendos
             foton nga qendra, dhe pikërisht ai zhvendosje u pa si «poshtë shumë
             më e madhe se lart». */
          style={{ paddingBottom: 12 }}>
        {/* KOKA2 (29 gusht 2026) — NËN-RRESHTI HYN BRENDA KOLONËS SË MAJTË.
            Ishte rresht i vetin, i gjerë sa krejt koka. Majtas e mbushte teksti;
            djathtas, nën foton, mbetej bosh — pra pak hapësirë sipër fotos dhe
            shumë poshtë saj (12px kundër 29px, e matur). Pronari e pa saktë.
            Tani koka është një rresht me dy anë: majtas emri me mesazhin,
            djathtas fotoja, të qendërzuara ndaj njëra-tjetrës. */}
        <div className="min-w-0 flex-1">
        {/* KOKA3 (29 gusht 2026) — KUFIRI I FOTOS MATET NGA RRESHTI I EMRIT.
            E kisha matur nga krejt kolona (emri + mesazhi), dhe ishte gabim:
            atëherë kufiri del ~59px, pra fotoja 50px hyn brenda tij dhe merr
            gjithë atë gjerësi — teksti mbetet me më pak, këputet në dy rreshta,
            dhe RRESHTI I DYTË e rrit kokën. Pronari e tha saktë: «me rrit foton
            shumë bëhet rreshti i dytë».
            Fotoja nuk e rrit kokën drejtpërdrejt; e rrit përmes tekstit. Prandaj
            kufiri duhet të jetë lartësia e rreshtit të emrit (~28px): fotoja
            merr aq vend, dhe del jashtë tij — lart, poshtë dhe anash. */}
        <div ref={kokaMajtasRef} className="flex items-center gap-2 min-w-0">
          {/* BALLINA1 (28 gusht 2026, kërkesë e pronarit) — ndërruesi i familjes
              rri PARA emrit, jo i fshehur te menyja. Kush jeton në dy familje e
              ndërron disa herë në ditë; kush jeton në një s'e sheh fare, sepse
              butoni shfaqet vetëm kur ka ku të shkosh.
              Klikim = ndërrim i shpejtë · shtypje e gjatë = zgjidh nga lista. */}
          {/* Te pamja «heirloom» ky buton nuk del: ndërrimi ka hyrë BRENDA
              stemës (shih më poshtë), pra dy shenja për të njëjtën gjë do të
              ishin dyfishim. Kudo tjetër mbetet ashtu siç ka qenë. */}
          {authSession && !pamja("kryet", "heirloom") && myLinkedFamilies.filter((x) => x.person_id).length >= 2 && (
            <button
              {...switchFamPressProps()}
              title={t("Tap to switch · hold to choose")}
              aria-label={t("Switch family")}
              className="shrink-0 flex items-center justify-center rounded-full"
              style={{ width: 28, height: 28, background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}`, cursor: "pointer" }}
            >
              <ArrowLeftRight size={15} color={COLORS.gold} />
            </button>
          )}
          {/* Ikona para emrit — ndizet/fiket te Design Studio (tokens.header.familyIcon). */}
          {headerToken("familyIcon") !== false && (pamja("kryet", "heirloom") ? (() => {
            /* Te mockup-i, para emrit të familjes nuk rri një ikonë njerëzish
               por STEMA: pema në ar, brenda dy unazave të holla mbi krem.
               4 shtator 2026, propozim i pronarit: kur personi është në DISA
               familje, ajo unazë bëhet unaza e ndërrimit — dy harqe me shigjeta
               rreth së njëjtës stemë. Kur është në një familje të vetme, mbetet
               stema e qetë, pa asnjë kontroll. */
            const ari = PALETA_TIPI.bronze || COLORS.gold;
            const shume = authSession && myLinkedFamilies.filter((x) => x.person_id).length >= 2;
            if (shume) {
              return (
                <button
                  {...switchFamPressProps()}
                  title={t("Tap to switch · hold to choose")}
                  aria-label={t("Switch family")}
                  className="shrink-0"
                  style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", lineHeight: 0 }}
                >
                  <StemaMeNderrim size={34} color={ari} sfondi={PALETA_TIPI.surface || PALETA_TIPI.bg || COLORS.card || "#fffdf8"} />
                </button>
              );
            }
            return (
              <span style={{
                width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                border: `1.5px solid ${PALETA_TIPI.spouseLine || COLORS.gold}`,
                background: PALETA_TIPI.surface || COLORS.panel,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `inset 0 0 0 2.5px ${PALETA_TIPI.surface || COLORS.panel}, inset 0 0 0 3.5px ${PALETA_TIPI.accentBorder || COLORS.goldSoft}`,
              }}>
                <StemaHeirloom size={26} color={ari} />
              </span>
            );
          })() : <Users size={20} color={COLORS.gold} />)}
          {/* FAZA 35 — u hoq redaktimi inline i titullit (klik-për-ndryshim);
              emri i familjes tani është vetëm për shfaqje këtu, i vendosur
              njëherë e mirë (nga emri i vetë familjes), jo diçka që admini ndryshon
              rastësisht duke klikuar header-in. */}
          {/* FIX (SIGURI, raportuar nga useri — "rrjedhje informacioni:
              emri i familjes shfaqet edhe para login-it") —
              familyDisplayName vjen nga rpc_resolve_family_by_slug, lexuar
              PUBLIKISHT (duhet të funksionojë PARA identifikimit, që app-i
              të dijë cilën familje po ngarkon). Dikush që thjesht e gjen/
              hamendëson URL-në e një familjeje (p.sh. /f/haliti-4821) do ta
              shihte emrin e saj REAL, edhe pa u identifikuar fare — rrjedhje
              e vërtetë. Header-i tani e shfaq emrin REAL vetëm PASI
              authSession të ekzistojë (llogari e vërtetë Google/Apple/
              Email — s'kërkohet domosdo lidhje me KËTË familje specifike,
              mjafton identifikim real). Para kësaj, GJITHKUJT i shfaqet
              saktësisht e njëjta gjë — emri i vetë platformës. */}
          <h1 className="truncate" style={{ fontFamily: FONTS.display, color: COLORS.onBgDeep, fontSize: headerNum("familyNameSize", 14, 34), fontWeight: 600 }}>{authSession ? familyDisplayName : t("The Family Tree")}</h1>
          {/* KOKA2 (29 gusht 2026, vërejtje e pronarit) — ZILJA NGJITET TE
              RRESHTI I EMRIT. Ai pyeti nëse zilja po kontribuon te këputja e
              nën-rreshtit në dy rreshta, dhe kishte të drejtë: ajo rrinte te
              grupi i djathtë, pra i hiqte ~38px gjerësi kolonës së majtë — dhe
              ato piksela i mungonin pikërisht tekstit.
              Tani rri në një lartësi me emrin e familjes, siç e kërkoi, dhe
              nën-rreshti i merr ata piksela prapa. Dy përfitime nga një lëvizje. */}
          {/* Zilja është më e gjatë se emri (18px ikonë + 12px mbushje kundër
              ~24px të tekstit), pra po ta linim ashtu do ta rriste rreshtin me
              6px — pra do ta rriste kokën, pikërisht atë që po heqim. Margjina
              negative e lë zonën e prekjes të paprekur dhe e nxjerr ndryshimin
              jashtë llogarisë së lartësisë. */}
          <span className="shrink-0 flex items-center" style={{ marginLeft: "auto", marginTop: -4, marginBottom: -4 }}>
                {/* KERKIMI1 — kërkimi merr ikonën e vet pranë ziles. Dy punë,
                    dy ikona: zilja të thotë çfarë të kërkoi ty, kërkimi të lejon
                    të kërkosh ti. Deri sot kërkimi jetonte vetëm te Pema, pra
                    kush ishte te Historia s'kishte si të kërkonte fare. */}
                {!locked && currentPersonId && (
                  <button onClick={() => { setKerkimiKudo(false); setKerkimiOpen(true); }} className="p-1.5 rounded-md" title={t("Search")}>
                    <Search size={17} color={navIconColor("bellHeader", true)} />
                  </button>
                )}
                {(announcements.length > 0 || activeNotices.length > 0) && (
                  <button onClick={openAnnouncements} className="relative p-1.5 rounded-md" title={t("Notifications")}>
                    <Bell size={18} color={navIconColor("bellHeader", true)} />
                    {unreadAnnouncementCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full"
                        style={{ minWidth: 15, height: 15, padding: "0 3px", background: COLORS.danger, color: "#fff", fontSize: 9.5, fontFamily: FONTS.body, fontWeight: 600, lineHeight: 1 }}
                      >
                        {unreadAnnouncementCount > 9 ? "9+" : unreadAnnouncementCount}
                      </span>
                    )}
                  </button>
                )}
          </span>
        </div>
            {!locked && (
              <p style={{
                    fontFamily: { body: FONTS.body, display: FONTS.display }[headerToken("subFont")] || FONTS.mono,
                    fontSize: headerNum("subSize", 8, 20),
                    // 3 shtator 2026 — «asnjëherë nuk kam kërkuar tekst të kaftë».
                    // Kishte të drejtë. Ky rresht ishte lidhur me `COLORS.gold`,
                    // dhe te `COLORS` ai çelës mban THEKSIN, jo arin: te pamja
                    // klasike theksi ishte ar mbi shirit të errët dhe dukej mirë;
                    // te një dizajn me shirit të çelët dhe theks terrakotë, i
                    // njëjti rresht bëhet tekst kafe mbi letër.
                    // Tani, kur ka dizajn, ndjek ngjyrën e tekstit dytësor —
                    // pikërisht si te mockup-i. Pa dizajn, asgjë nuk ndryshon.
                    // ⚠ 4 shtator 2026 — RREGULLIM I DYTË I TË NJËJTIT RRESHT.
                    // Herën e parë e lidha me `PALETA_TIPI.text2`, ngjyrën e
                    // tekstit dytësor. Kjo është e saktë kur shiriti i kokës rri
                    // mbi letër të çelët — si te Trashëgimia. Po te një dizajn me
                    // shirit TË ERRËT (Rrënjët: e kuqe e thellë), `text2` është
                    // shkruar për letrën, jo për atë shirit: del një kafe e
                    // shuar mbi të kuqe. Pronari: «ka marrë një ngjyrë që nuk e
                    // di nga e ka marrë» — dhe kishte të drejtë të mos e dinte.
                    // Tani zgjidhet nga NDRIÇIMI i vetë shiritit: mbi sfond të
                    // errët merret ngjyra e tekstit mbi sfond, mbi të çelët
                    // teksti dytësor. Një rregull, pa ngulitur asnjë ngjyrë.
                    color: (_luma(COLORS.bgDeep || COLORS.bg || "#fff") < 0.5
                              ? (COLORS.onBg || "#fff")
                              : (PALETA_TIPI.text2 || COLORS.gold)),
                    opacity: _luma(COLORS.bgDeep || COLORS.bg || "#fff") < 0.5 ? 0.72 : (PALETA_TIPI.text2 ? 1 : 0.85),
                    marginLeft: headerSubIndent(),
                    display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: headerNum("subLines", 1, 3), overflow: "hidden", lineHeight: 1.35,
                    letterSpacing: `${headerNum("subTracking", -1, 6)}px`,
                    // `word-spacing` është pikërisht ajo që e ndan iOS-in nga Android-i:
                    // hapësira mes fjalëve. Negative i afron fjalët pa i ngjeshur
                    // shkronjat, pra teksti mbetet i lexueshëm dhe zë më pak.
                    wordSpacing: `${headerNum("subWord", -4, 8)}px` }}>
                {(() => {
                  // Mesazhet vijnë nga Admin Center (rpc_list_header_messages). Nëse s'ka
                  // ende (p.sh. SQL i pa-aplikuar), bie te statistika e paracaktuar.
                  const msgs = headerMessages.length ? headerMessages : [{ kind: "stats", text: t("Our history continues…") }];
                  const m = msgs[headerMsgIdx % msgs.length] || msgs[0];
                  if (m.kind === "stats") {
                    // Ndarësi nuk është më pjesë e tekstit por element i vetin, që
                    // hapësira majtas e djathtas tij të jetë numër që admini e
                    // vendos — jo gjerësia e rastësishme e një hapësire monospace.
                    const pjesët = [`${generationsCount} ${t("Generations")}`, `${peopleCount} ${t("Members")}`];
                    if (m.text) pjesët.push(m.text);
                    const g = headerNum("sepGap", 0, 24);
                    // Shenja vetë, përmasa e saj dhe hapësira anash janë tri gjëra
                    // të ndryshme, dhe secila duket ndryshe te iPhone-i e te
                    // Android-i — prandaj të tria vendosen nga Admin Center.
                    // Lexohet e papërpunuar: teksti bosh KËTU do të thotë «pa
                    // ndarës fare», jo «mungon, vër parazgjedhjen» — dhe `headerToken`
                    // e trajton boshin si mungesë.
                    const rr = headerRaw("sepChar");
                    const shenja = rr === undefined ? HEADER_PARAZGJEDHJE.sepChar : String(rr);
                    const madhesia = headerNum("sepSize", 0, 24);
                    return pjesët.map((pj, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && (shenja
                          ? <span style={{ marginLeft: g, marginRight: g, opacity: 0.7,
                                           fontSize: madhesia || undefined,
                                           lineHeight: madhesia ? 1 : undefined }}>{shenja}</span>
                          : <span style={{ marginLeft: g, marginRight: g }} />)}
                        {pj}
                      </React.Fragment>
                    ));
                  }
                  // Mesazhi «ka version të ri» (VERSIONI2/4) është i shtypshëm: hap dyqanin.
                  if (m.id === ID_MESAZHI_PERDITESIM) {
                    return (
                      <span role="button" tabIndex={0}
                            onClick={hapDyqaninNgaShiriti}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); hapDyqaninNgaShiriti(); } }}
                            style={{ cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>
                        {m.text} ›
                      </span>
                    );
                  }
                  return m.text;
                })()}
              </p>
            )}
        </div>

        {authSession && crestUrl && crestVisible() && (
          <img src={crestUrl} alt={t("Crest")} className="shrink-0" style={{ height: 34, width: 34, objectFit: "contain" }} />
        )}

        <div ref={headerControlsRef} className="relative flex items-center gap-2 flex-nowrap justify-end shrink-0 w-auto">
          {/* FAZA 41 — hamburgeri (☰) u hoq si buton i veçantë këtu — bar-i
              poshtë (telefon) dhe sidebar-i (desktop) tashmë kanë "Më shumë"
              që hap të NJËJTIN dropdown. Vetë dropdown-i (poshtë) mbetet, që
              ato butona të vazhdojnë të funksionojnë. */}
          {/* TELEFON: menyja e avatarit si dritare e PLOTË (Modal) me swipe për
              mbyllje — jo dropdown anash. Desktop-i mban dropdown-in e vet poshtë. */}
          {avatarMenuOpen && (
            <div className="lg:hidden">
              <Modal title={displayName(people[currentPersonId] || {}) || t("nav.account")} onClose={() => setAvatarMenuOpen(false)}>
                <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t(ROLE_LABELS[myRole] || myRole)}</p>
                {renderAccountMenuItems()}
              </Modal>
            </div>
          )}
          {/* FAZA 32 — Kërkimi dhe zgjedhësi i pamjes u hoqën nga header-i
              global (s'kanë kuptim jashtë faqes "Pema"); tani shfaqen si
              panel pluskues në cepin e faqes Pema vetë, shih më poshtë. */}

          {!locked && (
          <>
          {themePanelOpen && (
              <Modal title={t("Appearance")} onClose={() => setThemePanelOpen(false)}>
                {/* «Pamja e aplikacionit» (pesë guaskat) u hoq më 29 gusht
                    2026 me kërkesë të pronarit. Shiriti vendoset te Admin
                    Center; klienti nuk e ndryshon. Shih shënimin te gjendja. */}

                {/* ─── KARTELA1 — KARTELAT E POSTIMEVE ──────────────────────
                    Dy zgjedhje: e bardhë, ose e errët. Zgjedhja ruhet VETËM te
                    kjo pajisje; ajo për të gjithë vendoset te Admin Center.

                    «Parazgjedhja e platformës» e shkruan edhe SE CILA është.
                    Pa atë, dy butona dukeshin identikë dhe pronari e tha:
                    «kur zgjedh default ose white, për mua duket se është e
                    njëjta». Ishte e njëjta — dhe butoni nuk e thoshte. */}
                <div className="mb-3 pb-3" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
                  <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("POST CARDS")}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[["", `${t("Platform default")} (${kartelaPlatform === "erret" ? t("Dark") : t("White")})`],
                      ["bardhe", t("White")],
                      ["erret", t("Dark")]].map(([val, label]) => {
                      const on = (kartelaImja || "") === val;
                      return (
                        <button key={val || "default"} onClick={() => zgjidhKartelen(val || null)}
                          className="px-2.5 py-1.5 rounded-md text-xs"
                          style={{ border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`,
                                   background: on ? COLORS.accentTint : COLORS.panel,
                                   color: COLORS.ink, fontWeight: on ? 700 : 500 }}>{label}</button>
                      );
                    })}
                  </div>
                  <p className="mt-1.5" style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight }}>
                    {t("The surface the cards sit on in Home, History, Statistics and Services. Applies to this device only.")}
                  </p>
                </div>

                {/* FAZA 2 — Appearance: hierarkia Design → Temë → Family Crest.
                    Përdoruesi zgjedh nga çka i lejohet; lista e temave ndryshon
                    sipas design-it. Tregohet vetëm nëse hierarkia është aktive
                    (SQL i ekzekutuar); përndryshe bie te zgjedhësi i vjetër poshtë. */}
                {(() => {
                  const designs = getDesigns();
                  const themes = getThemes();
                  const ap = getAppearance();
                  if (!designs.length) return null;
                  return (
                    <div style={{ marginBottom: 10 }}>
                      {designs.length > 1 && (
                        <>
                          <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("DESIGN")}</p>
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {designs.map((d) => (
                              <button key={d.id} onClick={() => applyAppearance(() => setUserDesign(d.id))} className="px-2.5 py-1.5 rounded-md text-xs" style={{ border: `1px solid ${ap.designId === d.id ? COLORS.gold : COLORS.goldSoft}`, background: ap.designId === d.id ? COLORS.accentTint : COLORS.panel, color: COLORS.ink, fontWeight: ap.designId === d.id ? 700 : 500 }}>{tDyn(d.name)}</button>
                            ))}
                          </div>
                        </>
                      )}
                      {themes.length > 0 && (
                        <>
                          <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("APPEARANCE")}</p>
                          <div className="flex flex-col gap-1 mb-3">
                            {themes.map((th) => (
                              <button key={th.key} onClick={() => applyAppearance(() => setUserTheme(th.key))} className="flex items-center justify-between px-2.5 py-2 rounded-md text-sm" style={{ border: `1px solid ${ap.themeKey === th.key ? COLORS.gold : COLORS.goldSoft}`, background: ap.themeKey === th.key ? COLORS.accentTint : COLORS.panel, color: COLORS.ink }}>
                                <span>{tDyn(th.name)}</span>
                                {ap.themeKey === th.key && <span style={{ color: COLORS.ink }}>✓</span>}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      {crestUrl && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: COLORS.ink }}>
                          <input type="checkbox" checked={ap.crestOn} onChange={(e) => applyAppearance(() => setUserCrest(e.target.checked))} />
                          {t("Family Crest")}
                        </label>
                      )}
                      <div style={{ borderTop: `1px solid ${COLORS.goldSoft}`, marginTop: 10 }} />
                    </div>
                  );
                })()}
                {/* Porta ndryshoi më 20 gusht: më parë ishte `getDesigns().length === 0`.
                    Me një design të vetëm dhe pa tema aktive, rreshti i design-eve
                    fshihej (s'ka çka zgjedhësh mes njërit), lista e temave fshihej
                    (bosh), dhe zgjedhësi i vjetër fshihej gjithashtu — pra paneli
                    "Pamja" hapej krejt i zbrazët. Tani kushti pyet atë që ka vërtet
                    rëndësi: a ka ndonjë temë për të zgjedhur? */}
                {getThemes().length === 0 && (<>
                <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("THEME")}</p>
                <select
                  className="w-full rounded-md px-2 py-2 outline-none"
                  style={inputStyle}
                  value={themeKey}
                  onChange={(e) => persistTheme(e.target.value)}
                >
                  {Object.entries(THEMES).map(([key, t]) => (
                    <option key={key} value={key}>{t.name}</option>
                  ))}
                </select>
                </>)}
                {/* Zgjedhësi i MODALITETIT (vetëm admini, dhe vetëm nëse platform admin-i
                    e ka lejuar zgjedhjen). Sipas modalitetit të platformës, opsionet
                    janë ose shtete ose 3 modelet farefisnore — emri mbetet "Modalitet". */}
                {canManage && rulesCanChoose && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                    <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("MODE")}</p>
                    <select
                      className="w-full rounded-md px-2 py-2 outline-none"
                      style={inputStyle}
                      value={familyRuleSetId || ""}
                      onChange={(e) => setFamilyCountry(e.target.value)}
                    >
                      <option value="">{t("Platform default")}</option>
                      {ruleSetOptions.map((rs) => (
                        <option key={rs.id} value={rs.id}>{tDyn(rs.name)}</option>
                      ))}
                    </select>
                    <p className="mt-1" style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight }}>{t("Colors, labels and rules adapt to the selected mode.")}</p>
                  </div>
                )}
                {!canManage && <p className="mt-2 pt-2" style={{ borderTop: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight }}>{t("Only the admin changes the theme for everyone; your change applies until you refresh.")}</p>}
                {canManage && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                    <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>{t("🛡️ The family crest")}</p>
                    <p className="mt-0.5 mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight }}>{t("Upload the crest/theme image — it appears as a faint watermark.")}</p>
                    <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.inkLight, fontStyle: "italic" }}>
                      {t("Recommended: PNG with a transparent background, square, at least 500×500px.")}
                    </p>
                    {crestUrl && <img src={crestUrl} alt={t("Crest")} style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 6 }} />}
                    <div className="flex items-center gap-2 flex-wrap">
                      <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCrest(f); e.target.value = ""; }} className="text-xs" style={{ fontFamily: FONTS.body, color: COLORS.inkLight }} />
                      {crestUrl && <button onClick={removeCrest} className="px-2 py-1 rounded-md text-xs" style={{ background: COLORS.dangerSoft, color: COLORS.danger }}>{t("Remove crest")}</button>}
                    </div>
                  </div>
                )}
                {/* AUDITI 2026-08 — nga "Pamja" u hoqën tri gjëra që s'e kishin vendin këtu:
                    · çelësi i shiritit të sipërm — shiriti u hoq krejt për familjet;
                      tani e publikon vetëm platforma, për mirëmbajtje;
                    · çelësi kryesor i push-it — është vendim platforme, jo familjeje;
                    · lista e pajisjeve dhe të dy butonat e testit — vegla mbështetjeje,
                      pra i takojnë Admin Center-it, ku shihen pajisjet e një familjeje
                      të caktuar dhe dërgohet provë e drejtuar.
                    Emaili s'hyn më te testi: kanali i vetëm i njoftimeve është push-i
                    në telefon; emaili mbetet opsion që ndizet nga Admin Center. */}
                {currentPersonId && people[currentPersonId] && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                    <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("MY KINSHIP")}</p>
                    <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>{t("You are:")} <strong>{displayName(people[currentPersonId])}</strong></p>
                    {/* FIX (kërkesë e userit) — checkbox-i u hoq; ngjyrosja
                        sipas farefisnisë është tani GJITHMONË aktive. */}
                    <p className="mt-2" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, fontStyle: "italic" }}>{t("Branch colors are computed automatically, based on your kinship.")}</p>
                  </div>
                )}
              </Modal>
          )}

          {/* FAZA 41 — "Posta e Familjes" dhe ndërrimi i gjuhës u hoqën si
              ikona të veçanta këtu — tani jetojnë te "Më shumë" (shih më
              lart). Header-i mbetet vetëm me profilin/hyrjen. */}
          </>
          )}
          {(currentPersonId || authSession) ? (
            <div className="flex items-center gap-2">
              {/* Zilja shfaqet kur ka njoftim(e) AKTIV(E): announcements OSE
                  njoftim mirëmbajtjeje. NUMRI i kuq shfaqet VETËM për njoftimet
                  e adminit të palexuara (unreadAnnouncementCount). Njoftimi i
                  mirëmbajtjes e ndez ziljen "në heshtje" — pa numër — dhe kur
                  mbaron mirëmbajtja (dhe s'ka njoftime tjera) zilja fshihet. */}
              <div className="relative">
              {/* AVATARI — përmasa dhe korniza vijnë nga Design Studio
                  (tokens.header), veç për telefon dhe veç për desktop. Pa
                  kornizë hiqet edhe shigjeta: një foto e vetme, e rrumbullakët,
                  është vetë butoni — shigjeta aty do të ishte zhurmë. */}
              {(() => {
                const gjeresi = Number(headerToken(isWideScreen ? "avatar" : "avatarM"));
                const madh = Math.max(18, Math.min(64, Number.isFinite(gjeresi) ? gjeresi : 24));
                const kornize = headerToken(isWideScreen ? "frame" : "frameM") !== false;
                // KOKA1 — kufiri vjen nga MATJA e kolonës së majtë të kokës
                // (ikona + emri i familjes), jo nga një numër i shkruar me dorë.
                // Kështu, nëse emri i familjes rritet, kufiri rritet bashkë me
                // të pa e prekur askush.
                //
                // Me kornizë kjo nuk zbatohet: aty fotoja rri brenda një kutie
                // me buzë dhe emër, dhe një foto që del nga korniza e vet do të
                // dukej si defekt, jo si zgjedhje.
                const nukERrit = headerToken(isWideScreen ? "avatarNoGrow" : "avatarNoGrowM") === true;
                const vendi = (!kornize && nukERrit && kokaKufi > 0) ? Math.min(kokaKufi, madh) : madh;
                const del = madh > vendi;
                return (
              <button onClick={() => setAvatarMenuOpen((v) => !v)} className="flex items-center gap-1.5 text-sm"
                      style={{ color: COLORS.onBg,
                               border: kornize ? `1px solid ${COLORS.goldSoft}` : "none",
                               borderRadius: kornize ? 6 : 999,
                               padding: kornize ? "4px 10px 4px 4px" : 0,
                               background: "transparent" }}>
                {/* Kutia jashtë cakton VENDIN; fotoja brenda cakton PËRMASËN.
                    Kur janë të barabarta (parazgjedhja) kjo është saktësisht
                    ajo që ishte më parë, vetëm me një mbështjellës më shumë. */}
                <span className="shrink-0" style={{ position: "relative", width: vendi, height: vendi, display: "block" }}>
                  <span className="flex items-center justify-center rounded-full overflow-hidden"
                    style={{
                      position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                      width: madh, height: madh, background: COLORS.gold, color: COLORS.bgDeep,
                      fontFamily: FONTS.display, fontWeight: 700, fontSize: Math.round(madh * 0.46),
                      // Kur fotoja del nga kutia e vet, një buzë e hollë e ndan
                      // nga teksti pas së cilës kalon. Pa të, në ekrane të ngushta
                      // fotoja duket sikur është ngjitur mbi emrin e familjes.
                      boxShadow: del ? `0 0 0 2px ${COLORS.bgDeep}` : undefined,
                    }}>
                    {people[currentPersonId]?.photo_path
                      ? <img src={photoUrl(people[currentPersonId].photo_path)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : (displayName(people[currentPersonId] || {}) || "?").trim().charAt(0).toUpperCase()}
                  </span>
                </span>
                {kornize && <span className="hidden sm:inline" style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.gold, maxWidth: 140 }}>{displayName(people[currentPersonId] || {})}</span>}
                {kornize && <ChevronDown size={13} color={COLORS.gold} />}
              </button>
                );
              })()}
              {(() => { const tot = (myTickets || []).reduce((a, tk) => a + (Number(tk.unread) || 0), 0); return tot > 0 ? (
                <span title={t("{{n}} new message(s) in the Help Centre", { n: tot })} style={{ position: "absolute", top: -5, right: -5, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999, background: "#D14343", color: "#fff", fontFamily: FONTS.body, fontSize: 10.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>{tot > 99 ? "99+" : tot}</span>
              ) : null; })()}
              {avatarMenuOpen && (
                <div className="hidden lg:block absolute right-0 mt-1 w-56 rounded-md shadow-xl z-30 p-2" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, maxWidth: "calc(100vw - 24px)" }}>
                  <p className="px-2 pt-1 pb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>
                    {displayName(people[currentPersonId] || {})} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>· {t(ROLE_LABELS[myRole] || myRole)}</span>
                  </p>
                  {renderAccountMenuItems()}
                </div>
              )}
              </div>
            </div>
          ) : (
            /* FAZA 42 — u hoq butoni "Identifikohu" këtu: kur s'je i
               identifikuar, faqja gjithmonë tregon një CTA qendrore
               ("locked" ose "tree bosh") me të njëjtin buton — mbajtja e
               një kopje edhe në header ishte redondante/konfuze. */
            null
          )}
        </div>
        </div>
      </div>

      {/* HAPESIRA — rripi kur hapësira ka mbaruar. Rri i pari sepse është i
          vetmi që e ndal punën: sa kohë ai është aty, çdo ngarkim refuzohet. */}
      {renderRripinEHapesires()}

      {/* Shiriti i njoftimit të mirëmbajtjes — DIREKT NËN header (jo te notch-i).
          I hollë; teksti rrëshqet djathtas→majtas pa ndalur (marquee) kur s'ka
          vend. Mbyllja e kalon njoftimin te Njoftimet (nuk e fshin fare). */}
      {unseenNotices.length > 0 && (
        <div style={{ position: "relative", background: COLORS.gold, color: COLORS.bgDeep, display: "flex", alignItems: "center", overflow: "hidden", height: 24, borderBottom: `1px solid ${COLORS.goldSoft}` }}>
          <style>{"@keyframes pefMaintMarquee{0%{transform:translateX(0)}100%{transform:translateX(-100%)}}"}</style>
          <span style={{ fontSize: 13, flexShrink: 0, paddingLeft: 8, paddingRight: 4, zIndex: 2 }}>🛠️</span>
          <div style={{ flex: 1, overflow: "hidden", whiteSpace: "nowrap" }}>
            <div style={{ display: "inline-block", paddingLeft: "100%", animation: "pefMaintMarquee 22s linear infinite", fontFamily: FONTS.body, fontSize: 12 }}>
              {unseenNotices.map((n) => (
                <span key={n.id} style={{ marginRight: 64 }}>
                  <b>{t("Scheduled maintenance")}:</b> {n.message}
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={() => unseenNotices.forEach((n) => dismissNotice(n.id))}
            aria-label={t("Close")}
            style={{ flexShrink: 0, background: COLORS.gold, border: "none", color: COLORS.bgDeep, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 8px", height: "100%", zIndex: 2 }}
          >✕</button>
        </div>
      )}

      {/* FAZA 1g — NJOFTIMI PARA PAGESËS.
          Ndryshe nga shiriti i mirëmbajtjes më sipër, ky NUK është marquee dhe
          NUK mbyllet me ✕. Një marquee lexohet nëse e zë me sy në momentin e
          duhur; këtu njeriu duhet ta pranojë, sepse ai klikim është i vetmi
          fakt që i thotë adminit se njoftimi mbërriti vërtet. */}
      {noticeToShow && (
        <div style={{
          background: COLORS.bgDeep, color: COLORS.gold, borderBottom: `1px solid ${COLORS.goldSoft}`,
          padding: "8px 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          fontFamily: FONTS.body, fontSize: 12.5,
        }}>
          <span style={{ flexShrink: 0 }}>{noticeToShow.days_left <= 7 ? "⏳" : "ℹ️"}</span>
          <div style={{ flex: 1, minWidth: 190, lineHeight: 1.45 }}>
            <b>{noticeToShow.label}</b>{" "}
            {t("becomes a paid service on")}{" "}
            <b>{new Date(noticeToShow.effective_at).toLocaleDateString("sq")}</b>
            {" · "}
            {noticeToShow.days_left} {t("days left")}
            <div style={{ opacity: 0.82, marginTop: 2 }}>
              {noticeToShow.existing_families === "keep"
                ? t("You are using it now, so it stays free for you.")
                : noticeToShow.existing_families === "grace"
                  ? `${t("You are using it now, so you keep it")} ${noticeToShow.grace_days} ${t("days longer.")}`
                  : t("After that date it works only for families that buy it.")}
              {/* `price_text` vjen i shkruar nga serveri — "1,00 €/muaj", jo
                  "1,00 €". Pa atë prapashtesë njeriu lexon një euro dhe mendon
                  një herë: shifra e saktë, kuptimi i gabuar. */}
              {(noticeToShow.price_text || noticeToShow.price_cents != null) && noticeToShow.existing_families !== "keep"
                ? ` ${t("Price")}: ${noticeToShow.price_text || eurFmt(noticeToShow.price_cents)}.` : ""}
              {noticeToShow.note ? ` ${noticeToShow.note}` : ""}
            </div>
          </div>
          <button
            onClick={() => ackPriceNotice(noticeToShow.change_id, noticeToShow.days_left <= 7)}
            style={{
              flexShrink: 0, background: COLORS.gold, color: COLORS.bgDeep, border: "none",
              borderRadius: 6, padding: "6px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}
          >{t("Got it")}</button>
        </div>
      )}

      {/* FAZA 30 — Sidebar për desktop (lg+), i fshehur në telefon (bar
          poshtë e mbulon atje). Të njëjtat artikuj si bar-i poshtë, thjesht
          paraqitje vertikale gjithmonë e dukshme — pikërisht siç funksionon
          Admin Center, por me ngjyrat/temën e VETË familjes, jo të bardha. */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {!locked && (
          <div className="hidden lg:flex lg:flex-col shrink-0" style={{ width: 200, background: COLORS.bgDeep, borderRight: `1px solid ${COLORS.goldSoft}` }}>
            {/* «SHTO» TE DESKTOPI (29 gusht 2026).
                Mungonte krejt: te telefoni «Shto» është hyrje e shiritit poshtë,
                kurse te desktopi shiriti nuk ekziston — pra i vetmi vend ku
                shtohej diçka ishte butoni pluskues te ekrani i pemës. Kush hapte
                Ballinën te kompjuteri nuk kishte nga t'ia niste.

                Rri i PARI dhe me ngjyrën e theksit, sepse nuk është vend ku
                shkohet — është veprim. Një veprim i vizatuar si destinacion
                humbet mes destinacioneve. */}
            {/* KUSHTI U NDREQ (30 gusht 2026). Ishte `["admin","editor",
                "contributor"].includes(myRole)` — dhe kjo nuk përputhej me
                asnjë vend tjetër: te telefoni «Shto» rri te shiriti PA asnjë
                kusht roli, dhe vetë lista «Çfarë do të shtosh?» e mbyll secilin
                zë veç e veç («Vetëm administratori mund të shtojë histori»).
                Pra desktopi fshihte një derë që telefoni e mban të hapur, dhe
                një anëtar me rol `viewer` e kishte «Shto» në xhep e jo në
                kompjuter. Kushti i vërtetë është vetëm një: a ke një person në
                këtë pemë. Lejet vendosen brenda listës, dhe përfundimisht te
                RLS-ja — jo duke fshehur menunë. */}
            <button onClick={() => setActiveScreen("home")} className="flex items-center gap-2.5 px-4 py-2.5 text-left" style={{ background: activeScreen === "home" ? COLORS.accentTint : "transparent" }}>
              <Home size={18} strokeWidth={1.75} color={navIconColor("home", activeScreen === "home")} style={{ opacity: COLORS.vividIcons ? 1 : (activeScreen === "home" ? 1 : 0.6) }} />
              <span style={{ fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 500, color: activeScreen === "home" ? COLORS.accentText : COLORS.onBg, opacity: activeScreen === "home" ? 1 : 0.75 }}>{t("Family")}</span>
            </button>
            <button onClick={() => setActiveScreen("tree")} className="flex items-center gap-2.5 px-4 py-2.5 text-left" style={{ background: activeScreen === "tree" ? COLORS.accentTint : "transparent" }}>
              {/* E njëjta skedë, e njëjta ikonë — edhe te menyja e gjatë, jo
                  vetëm te shiriti. Përndryshe e njëjta gjë do të kishte dy
                  fytyra në dy vende të së njëjtës aplikacion. */}
              <PemaSkeda size={18} strokeWidth={1.75} color={navIconColor("tree", activeScreen === "tree")} style={{ opacity: COLORS.vividIcons ? 1 : (activeScreen === "tree" ? 1 : 0.6) }} />
              <span style={{ fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 500, color: activeScreen === "tree" ? COLORS.accentText : COLORS.onBg, opacity: activeScreen === "tree" ? 1 : 0.75 }}>{t("The Tree")}</span>
            </button>
            {/* BASHKIM (kërkesë e userit) — një hyrje e vetme "Kujtime", jo
                më dy artikuj të veçantë (Archive + Kujtime) që dukeshin
                njësoj nga jashtë. Skedat (Ngjarje/Skedarë) zgjidhen BRENDA
                vetë ekranit — shih activeScreen === "memories" më poshtë. */}
            <button onClick={() => openMemories("events")} className="flex items-center gap-2.5 px-4 py-2.5 text-left" style={{ background: activeScreen === "memories" ? COLORS.accentTint : "transparent" }}>
              <BookOpen size={18} strokeWidth={1.75} color={navIconColor("memories", activeScreen === "memories")} style={{ opacity: COLORS.vividIcons ? 1 : 0.6 }} />
              {/* Butoni hap Historinë; deri sot etiketa thoshte «Kujtime». Një
                  emër i dytë për një derë të vetme — i njëjti gabim që na hëngri
                  ditën. Emri i derës është emri i dhomës. */}
              <span style={{ fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 500, color: COLORS.onBg, opacity: 0.75 }}>{t("History")}</span>
            </button>
            <button onClick={() => setActiveScreen("stats")} className="flex items-center gap-2.5 px-4 py-2.5 text-left" style={{ background: activeScreen === "stats" ? COLORS.accentTint : "transparent" }}>
              <BarChart3 size={18} strokeWidth={1.75} color={navIconColor("stats", activeScreen === "stats")} style={{ opacity: COLORS.vividIcons ? 1 : 0.6 }} />
              <span style={{ fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 500, color: COLORS.onBg, opacity: 0.75 }}>{t("Statistics")}</span>
            </button>
            {/* FAZA 0 — "Shërbimet": shfaqet VETËM kur ka diçka për të blerë.
                Menuja nuk rritet kot: me regjistër bosh ky buton s'ekziston. */}
            {servicesCount > 0 && (
              <button onClick={() => setActiveScreen("services")} className="flex items-center gap-2.5 px-4 py-2.5 text-left" style={{ background: activeScreen === "services" ? "rgba(201,162,39,0.12)" : "transparent", border: "none", cursor: "pointer" }}>
                <ShoppingBag size={18} strokeWidth={1.75} color={navIconColor("services", activeScreen === "services")} style={{ opacity: COLORS.vividIcons ? 1 : 0.6 }} />
                <span style={{ fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 500, color: activeScreen === "services" ? COLORS.accentText : COLORS.onBg, opacity: activeScreen === "services" ? 1 : 0.75 }}>{t("Services")}</span>
                {/* Shenja. `ne_pritje` (pret pagesën) ka përparësi mbi `te_reja`,
                    sepse ajo kërkon veprim, kurse e reja kërkon vetëm sy. */}
                {(orderAlerts.ne_pritje > 0 || orderAlerts.te_reja > 0) && (
                  <span style={{ marginLeft: "auto", minWidth: 18, height: 18, borderRadius: 9,
                                 background: orderAlerts.ne_pritje > 0 ? "#A3331A" : COLORS.gold,
                                 color: orderAlerts.ne_pritje > 0 ? "#fff" : COLORS.bgDeep,
                                 fontSize: 11, fontWeight: 700, display: "inline-flex",
                                 alignItems: "center", justifyContent: "center", padding: "0 5px" }}>
                    {orderAlerts.ne_pritje > 0 ? orderAlerts.ne_pritje : orderAlerts.te_reja}
                  </span>
                )}
              </button>
            )}
            {/* «Ndërro familjen» u hoq nga sidebar-i (2 shtator 2026): dera e
                njëjtë rri te emri i familjes në kokë — dy dyer për një dhomë.
                «Shto» erdhi këtu, I FUNDIT dhe I NJËJTË me të tjerët: pronari
                nuk e donte rrethin e artë dhe vendin e parë — «ndryshe nga çdo
                gjë tjetër». Një hyrje veprimi nuk ka pse të bërtasë; radha e
                fundit e thotë vetë se s'është ekran. */}
            {!!currentPersonId && (
              <button onClick={() => hapShtimin()} className="flex items-center gap-2.5 px-4 py-2.5 text-left" style={{ background: "transparent" }}>
                <Plus size={18} strokeWidth={1.75} color={navIconColor("add", false)} style={{ opacity: COLORS.vividIcons ? 1 : 0.6 }} />
                <span style={{ fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 500, color: COLORS.onBg, opacity: 0.75 }}>{t("Add")}</span>
              </button>
            )}
            {/* Asistenti u hoq nga sidebar-i — te desktopi jeton te butoni rrethor
                (chat) poshtë-djathtas; te telefoni te hamburgeri i tab-it. */}
            {/* THJESHTIM — butoni "Më shumë" i sidebar-it u hoq (ishte
                identik me menunë e avatarit sipër, hiqte fare qëllimin —
                mbahet vetëm NJË vend për menunë e llogarisë). */}
          </div>
        )}
        <div className="flex-1 flex flex-col overflow-hidden">

      {/* FAZA 43 — Ekrani i ri, i BASHKUAR, i identifikimit: shfaqet
          GJITHMONË kur `locked` është true, pavarësisht cilin activeScreen
          ka ruajtur sesioni (rregullim i bug-ut: më parë Ballina/etj s'e
          kontrollonin fare `locked`, kështu që aplikacioni shfaqej "bosh" pa
          asnjë mënyrë identifikimi). Karuseli 3-slide sipër është thjesht
          atmosferë; forma poshtë është EXACT e njëjta që përdorej te Modal-i
          (shih renderAuthFormBody()). */}
      {/* Ekrani i identifikimit — korniza vjen nga LoginScreen (varianti,
          tekstet dhe numrat i cakton admini te Admin Center → Ekrani i hyrjes),
          ndërsa forma brenda mbetet E NJËJTA `renderAuthFormBody`, tani me
          paletën e variantit. */}
      {locked && (
        <LoginScreen settings={loginSettings} tr={tDyn}>
          {renderAuthFormBody(loginAuthColors)}
        </LoginScreen>
      )}

      {/* ── PROVA 3 — GUASKA E NDËRFAQES PA ASNJË EKRAN ────────────────────
          ⚠ E PËRKOHSHME (shih `prova.jsx`). Të pesë ekranet më poshtë janë
          fikur; mbetet vetëm korniza: shiriti i sipërm, sidebar-i, shiriti i
          tab-eve dhe dritaret. Numri këtu tregon nëse ajo kornizë rron. */}
      {!locked && provaNga(3) && <EkraniProves brenda flamujt={{
        // ⚠ I PËRKOHSHËM (shih `prova.jsx`). Të gjithë flamujt që hapin një
        // dritare a mbivendosje, të mbledhur në një vend. Dritaret vetë rrinë
        // të fikura; kjo vetëm RAPORTON cili prej tyre është i hapur — sepse
        // "shumica presin një flamur të mbyllur" ishte supozim, jo matje.
        authOpen, personModal, spouseModalFor, myLegacyOpen, legacyConfirm,
        needsPasswordSetup, guestAccessFor, deleteAccountOpen, legacyReqOpen,
        announcementsOpen, announceManageOpen, emailRepliesOpen,
        simpleMourningOpen, aboutOpen, securityPanelOpen, contactAdminOpen,
        docModalOpen, docHistoryOpenType, termsGateOpen, logPanelOpen,
        rolesPanelOpen, trashOpen, syncModalFor, familiesHubOpen, importOpen,
        treeSettingsOpen, posterModalOpen, previewZoom, photoLightbox, toast,
      }} />}
      {!locked && !provaNga(3) && activeScreen === "tree" && (
      <div ref={containerRef} className="relative flex-1 overflow-hidden touch-none select-none" style={{ cursor: dragging.current ? "grabbing" : "grab", background: `radial-gradient(circle at 20% 10%, ${COLORS.bgDeep}, ${COLORS.bg})`, minHeight: 320 }}
        onWheel={onWheel}
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)} onMouseMove={(e) => moveDrag(e.clientX, e.clientY)} onMouseUp={endDrag} onMouseLeave={endDrag}
        onTouchStart={(e) => {
          if (e.touches?.length === 2) {
            // Pinch me ANKORIM: ruajmë distancën, shkallën, pan-in dhe MESIN e
            // gishtave (në koordinata të kontejnerit) në fillim të gjestit. Kështu
            // gjatë zoom-it mund ta mbajmë pikën-botë nën gishta të palëvizur.
            const rect = containerRef.current?.getBoundingClientRect();
            const rL = rect?.left || 0, rT = rect?.top || 0;
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            pinch.current = {
              startDist: Math.hypot(dx, dy), startScale: scale,
              startPanX: pan.x, startPanY: pan.y,
              startMidX: (e.touches[0].clientX + e.touches[1].clientX) / 2 - rL,
              startMidY: (e.touches[0].clientY + e.touches[1].clientY) / 2 - rT,
              rectLeft: rL, rectTop: rT,
            };
            dragging.current = null;
          } else if (e.touches?.length === 1) {
            pinch.current = null;
            startDrag(e.touches[0].clientX, e.touches[0].clientY);
          }
        }}
        onTouchMove={(e) => {
          if (e.touches?.length === 2 && pinch.current) {
            const p = pinch.current;
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            const next = Math.min(2.2, Math.max(0.3, p.startScale * (dist / p.startDist)));
            // Pika-botë që ishte nën mesin fillestar të gishtave.
            const worldX = (p.startMidX - p.startPanX) / p.startScale;
            const worldY = (p.startMidY - p.startPanY) / p.startScale;
            // Mesi AKTUAL i gishtave (ndjek edhe zhvendosjen me dy gishta).
            const curMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - p.rectLeft;
            const curMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - p.rectTop;
            userAdjustedViewRef.current = true;
            setScale(next);
            // screen = world*scale + pan  →  pan = mes − world*scaleZ (mbaje pikën nën gishta)
            setPan({ x: curMidX - worldX * next, y: curMidY - worldY * next });
          } else if (e.touches?.length === 1 && !pinch.current) {
            moveDrag(e.touches[0].clientX, e.touches[0].clientY);
          }
        }}
        onTouchEnd={(e) => {
          if (!e.touches || e.touches.length < 2) pinch.current = null;
          if (!e.touches || e.touches.length === 0) endDrag();
        }}>
        {showEmblem("eagle", themeKey) && <EagleWatermark />}
        {showEmblem("kosovo", themeKey) && <KosovoWatermark />}
        {crestUrl && crestVisible() && <CrestWatermark crestUrl={crestUrl} />}

        {/* FAZA 32 — Kërkimi + "Shiko: Trungu im" tani jetojnë vetëm këtu,
            mbi faqen Pema, në cepin lart-djathtas — njësoj si kontrollet e
            zoom-it poshtë-djathtas. S'kanë më vend në header-in global.
            FIX (kërkesë e userit — "në desktop u zhduken +/- dhe të tjerat")
            — bar-i alternativ (sheet/fab/contextual/drawer/hybrid) është
            `lg:hidden` (VETËM celular) — kur variant ≠ "classic", ky bllok
            fshihej KOMPLET (edhe në desktop), duke lënë desktopin PA ASNJË
            mënyrë për të kërkuar/ndryshuar pamjen. Tani: në celular vazhdon
            të fshihet siç ishte (mobile-i e ka bar-in e ri poshtë); në
            desktop (lg+) MBETET GJITHMONË i dukshëm, pavarësisht variantit
            — variantet e reja janë koncept VETËM mobil (desktopi ka
            gjithmonë sidebar-in fiks, s'i duhen fare). */}
        {!locked && peopleCount > 0 && !shiritiIPemesKa("search", "view", "settings") && (
          <div ref={treeControlsRef} className="hidden lg:flex absolute right-4 top-4 z-20 items-center gap-1.5">
            <div className="relative">
              {searchExpanded ? (
                <div className="flex items-center rounded-md px-2 py-1.5" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}>
                  <Search size={14} color={COLORS.gold} />
                  <input
                    ref={searchInputRef}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
                    onFocus={() => setSearchOpen(true)}
                    onBlur={() => { if (!search.trim()) setSearchExpanded(false); }}
                    placeholder={t("Search someone")}
                    className="bg-transparent outline-none px-2 text-sm w-28 sm:w-40"
                    style={{ color: COLORS.ink, fontFamily: FONTS.body }}
                  />
                  <button type="button" onClick={() => { setSearch(""); setSearchOpen(false); setSearchExpanded(false); }} aria-label={t("Close")} className="rounded shrink-0 flex items-center justify-center" style={{ width: ZONA_PREKJES, height: ZONA_PREKJES, margin: "-12px -10px -12px 0", background: "transparent", border: "none", padding: 0 }}><X size={13} color={COLORS.gold} /></button>
                </div>
              ) : (
                <button onClick={() => { setSearchExpanded(true); setTimeout(() => searchInputRef.current?.focus(), 0); }} className="p-2 rounded-md" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }} title={t("Search someone")}>
                  <Search size={15} color={COLORS.ink} />
                </button>
              )}
              {searchOpen && searchMatches.length > 0 && (
                <div className="absolute right-0 mt-1 w-56 rounded-md shadow-xl overflow-hidden z-30" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}>
                  {searchMatches.map((m) => (
                    <button key={m.id} onClick={() => focusOn(m.id)} className="block w-full text-left px-3 py-2 hover:bg-black/5" style={{ color: COLORS.ink, fontSize: 14 }}>
                      {emriDallues(m, etiketatDalluese)} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{lifespan(m)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button onClick={() => setViewPanelOpen((v) => !v)} className="flex items-center gap-1 px-2.5 sm:px-3 py-2 rounded-md text-sm" style={{ color: COLORS.ink, background: viewMode !== "full" ? COLORS.accentTint : COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}>
                <span className="sm:hidden">🌳 ▾</span>
                <span className="hidden sm:inline">{t("View:")} {viewMode === "trunk" ? t("My trunk") : viewMode === "blood_lisi" ? t("The Blood Line") : viewMode === "full" ? t("Full tree") : viewMode === "close" ? t("Close family") : t("Descendants")}</span>
              </button>
              {/* PEMA1 — e njëjta dritare si te shiriti i telefonit. Më parë
                  kjo ishte listë rënëse e ngjitur nën buton, pra e njëjta
                  përmbajtje kishte dy paraqitje dhe dy grupe rregullash. */}
              {/* Edhe kjo u zhvendos te niveli i guaskës, për të njëjtën arsye:
                  rrinte brenda shiritit të mjeteve të pemës, jo te vendi ku
                  jetojnë dritaret e tjera. Shih poshtë. */}
            </div>
            {/* Ikonat e ndara (kërkesë e userit): 🖼️ posteri dhe ⚙️ cilësimet
                e pamjes — direkt në shiritin e pemës, pa kaluar nga "Shiko". */}
            {posterVisible && (
              <button onClick={() => { openPoster(); setViewPanelOpen(false); }} className="p-2 rounded-md" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }} title={featureGated("poster_pro") ? t("Poster for printing") + " 🔒" : t("Poster for printing")}><span style={{ fontSize: 14, opacity: featureGated("poster_pro") ? 0.55 : 1 }}>🖼️</span></button>
            )}
            <button onClick={() => { setTreeSettingsOpen(true); setViewPanelOpen(false); }} className="p-2 rounded-md" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }} title={t("Tree view settings")}><span style={{ fontSize: 14 }}>⚙️</span></button>
          </div>
        )}
        {slugFamilyStatus === "not_found" ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <Users size={36} color={COLORS.gold} />
            <p style={{ fontFamily: FONTS.display, color: COLORS.onBg, fontSize: 20 }}>{t("emptyTree.notFoundTitle")}</p>
            <p style={{ fontFamily: FONTS.body, color: COLORS.gold, fontSize: 13, maxWidth: 320 }}>
              {t("emptyTree.notFoundBodyPrefix")} <a href="/start" style={{ textDecoration: "underline" }}>{t("emptyTree.createYourFamily")}</a>.
            </p>
          </div>
        ) : loading ? (
          <div className="w-full h-full flex items-center justify-center" style={{ color: COLORS.gold }}>{t("emptyTree.loadingTree")}</div>
        ) : locked ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <Users size={36} color={COLORS.gold} />
            <p style={{ fontFamily: FONTS.display, color: COLORS.onBg, fontSize: 20 }}>{authSession && slugFamilyName ? t("emptyTree.signInToViewNamed", { name: slugFamilyName }) : t("emptyTree.signInToView")}</p>
            <p style={{ fontFamily: FONTS.body, color: COLORS.gold, fontSize: 13, maxWidth: 320 }}>{t("emptyTree.privacyNote")}</p>
            <button onClick={() => { setAuthOpen(true); resetAuthForm(); }} className="mt-1 flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>
              <LogIn size={15} /> {t("emptyTree.signIn")}
            </button>
          </div>
        ) : treeHasAnyPeople === false && !authSession ? (
          /* FAZA 3: bootstrap-i i pemës bosh tani kërkon sesion Auth i vërtetë
             PARA se ta shtosh personin e parë — pa këtë, admini i parë do të
             mbetej i palidhur me asnjë llogari (dhe do të kyçej jashtë në
             ringarkimin e ardhshëm, pa mënyrë për t'u rikyçur, sepse s'ka
             ende asnjë admin që të mund ta ftojë). Shih rpc_bootstrap_admin
             (FAZA 3) te schema.sql — tani lidh person_account_links automatikisht. */
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <Users size={36} color={COLORS.gold} />
            <p style={{ fontFamily: FONTS.display, color: COLORS.onBg, fontSize: 20 }}>{t("emptyTree.nobodyAddedYet")}</p>
            <p style={{ fontFamily: FONTS.body, color: COLORS.gold, fontSize: 13, maxWidth: 320 }}>{t("emptyTree.signInThenAddFirst")}</p>
            <button onClick={() => { setAuthOpen(true); resetAuthForm(); }} className="mt-1 flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>
              <LogIn size={15} /> {t("emptyTree.signIn")}
            </button>
          </div>
        ) : treeHasAnyPeople === false ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <Users size={36} color={COLORS.gold} />
            <p style={{ fontFamily: FONTS.display, color: COLORS.onBg, fontSize: 20 }}>{t("emptyTree.nobodyAddedYet")}</p>
            <p style={{ fontFamily: FONTS.body, color: COLORS.gold, fontSize: 13, maxWidth: 320 }}>{t("emptyTree.addFirstPersonBecomeAdmin")}</p>
            <button onClick={() => openAdd()} className="mt-1 flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>
              <Plus size={15} /> {t("emptyTree.addFirstPerson")}
            </button>
          </div>
        ) : (
          <svg id="pef-tree-svg" width="100%" height="100%" style={{ display: "block" }}>
            <defs>
              {/* FIX (kërkesë e userit) — 3 skemat e reja (elegant/modern3d/
                  story) janë "foto lart" njësoj si "vertical", ndaj e
                  ndajnë të njëjtin pozicionim rrethi për clip-path. "elegant"
                  përdor ELIPS (jo rreth) — kërkesë e userit: "vetëm foto
                  ovale + emri poshtë, si te modern". */}
              {Object.values(visiblePeople).filter((p) => p.photo_path).map((p) => {
                const clipPhotoR = Math.max(14, Math.min(30, CARD_W * 0.21));
                const clipPhotoCY = 10 + 6 + clipPhotoR;
                const isTopPhoto = ["vertical", "elegant", "elegantoriginal", "modern3d", "modernphoto", "story", "glass", "heirloom"].includes(treeCardLayoutEf);
                return (
                  <clipPath id={`clip-${p.id}`} key={p.id}>
                    {treeCardLayoutEf === "elegant" ? (
                      <ellipse cx={CARD_W / 2} cy={clipPhotoCY}
                        rx={clipPhotoR * 1.05 + (paNgjyra() ? Math.max(photoRingGap, 2) : 0)}
                        ry={clipPhotoR * 1.35 + (paNgjyra() ? Math.max(photoRingGap, 2) : 0)} />
                    ) : isTopPhoto ? (
                      /* 4 shtator 2026 — propozim i pronarit, dhe i drejtë:
                         «kur zgjidhet no colors, fotografia të rritet deri te
                         kufijtë e jashtëm të rrethit edhe ta mbulojë atë».
                         Kjo e zgjidh njëherësh edhe unazën edhe hijëzimin që
                         mbetej rreth saj — sepse asgjë nuk ngjyroset më;
                         thjesht nuk ka çfarë të ngjyroset. Prerja duhet të
                         ndjekë të njëjtën rreze, përndryshe fotoja rritet po
                         mbetet e prerë te e vjetra. */
                      <circle cx={CARD_W / 2} cy={clipPhotoCY}
                        r={paNgjyra() ? clipPhotoR + Math.max(photoRingGap, 2) : clipPhotoR} />
                    ) : (
                      <circle cx="33" cy="29" r="18" />
                    )}
                  </clipPath>
                );
              })}
              {/* SHTESË (kërkesë e userit) — hije e butë SVG për skemën
                  "Modern (3D)". `feDropShadow` prek VETËM formën vetë
                  (rect/circle), jo ç'ka PRAPA saj (ndryshe nga CSS
                  box-shadow, por mjafton për efektin e kërkuar këtu). */}
              <filter id="cardShadow3d" x="-40%" y="-40%" width="180%" height="180%">
                <feDropShadow dx="0" dy="2.5" stdDeviation="3" floodColor="#000000" floodOpacity="0.28" />
              </filter>
              {/* SHTESË (kërkesë e userit) — "Glass (Premium)", PROVË siç u
                  kërkua. KUFIZIM I RËNDËSISHËM: "blur-i i vërtetë" i
                  glassmorphism (shiko përmes qelqit çfarë ka PRAPA
                  kartelës — degët, vijat) përdor `backdrop-filter` në CSS,
                  diçka që SVG s'e mbështet (inputet e vjetra `BackgroundImage`
                  /`BackgroundAlpha` të SVG 1.1 janë hequr prej kohësh nga
                  të gjithë browser-at, për arsye performance). Kjo këtu
                  është PËRAFRIMI më i mirë i mundshëm brenda SVG: kartelë
                  gjysmë-transparente + "shkëlqim" (gradient i lehtë sipër)
                  + hije e butë — jep ndjesinë e qelqit, POR pa e "shikuar
                  përmes" saj cilindo element real pas saj. */}
              <linearGradient id="glassSheen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
            </defs>
            <g transform={`translate(${pan.x},${pan.y}) scale(${scale})`}>
              {sallaBands}
              {generationBands}
              {/* ⚠ 4 shtator 2026 — «kur disa vija shkojnë nga i njëjti person,
                  për çdo vijë mbi tjetrën ngjyra e vijave bëhet më e ndezur».
                  Vëzhgim i saktë, dhe shkaku është i thjeshtë: secila vijë
                  vizatohej me 55% tejdukshmëri VETË. Dy vija gjysmë-të-tejdukshme
                  të mbivendosura nuk japin 55% — japin 80%, tri japin 91%, dhe
                  aty ku dega ndahet në pesë fëmijë ngjyra bëhet e plotë. Pra sa
                  më e degëzuar familja, aq më e errët vija: pikërisht e kundërta
                  e asaj që duhet.
                  Tejdukshmëria kalon te GRUPI: krejt vijat vizatohen njëherë të
                  plota, dhe pastaj i tërë vizatimi zbutet një herë të vetme.
                  I njëjti pamje kudo, sa herë të mbivendosen. */}
              <g opacity="0.55">{lines}</g>
              {spouseLines}
              {Object.values(visiblePeople).map((p) => {
                const pos = positions[p.id];
                if (!pos) return null;
                const x = pos.x * H_SPACING, y = pos.y * V_SPACING;
                const isSelected = selectedId === p.id;
                // SHTESË (kërkesë e userit) — meqë CARD_W/CARD_H tani janë
                // të rregullueshme lirisht nga Admin Center (jo më vetëm dy
                // madhësi të fiksuara), pozicionet e elementeve BRENDA
                // kartelës vertikale llogariten PROPORCIONALISHT me
                // CARD_W/CARD_H, jo më me numra absolutë të "gozhduar" —
                // përndryshe një kartelë shumë e ngushtë/e gjatë do të
                // dukej "e ngjeshur" ose me hapësirë boshe të tepërt.
                const vStripH = 10;
                const vPhotoR = Math.max(14, Math.min(30, CARD_W * 0.21));
                const vPhotoCY = vStripH + 6 + vPhotoR;
                // ⚠ 4 shtator 2026 — E RIKTHYER, TANI PËR TË DY DIZAJNET.
                // E kisha bërë vetëm te Trashëgimia dhe e ktheva mbrapsht kur
                // pronari tha «mos u merr me temat e tjera». Tani mbeten vetëm
                // dy dizajne dhe të dyja e duan: «unaza që duhet mbuluar nga
                // fotoja nuk po mbulohet te Rrënjët».
                // ⚠ 4 shtator 2026 — «pa ngjyra prapë shfaqet një rreth, sikur
                // fotoja nuk rritet mjaftueshëm».
                //
                // Nuk ishte fotoja: nën fotografi rri një DISK i sipërfaqes
                // (`vPhotoR + 2`, me hije) që e krijon pamjen «foto pezull».
                // Fotoja rritej sa hendeku i unazës — dhe kur ai hendek është
                // 0 ose 1, disku mbetej më i gjerë se fotoja dhe dilte si një
                // rreth i çelët rreth saj. Unaza ishte fikur; rrethi që shihte
                // syri ishte disku.
                //
                // Tani rritja është të paktën 2 — pikërisht sa disku — dhe vetë
                // disku nuk vizatohet fare kur ka fotografi.
                const rFotoEf = paNgjyra() ? vPhotoR + Math.max(photoRingGap, 2) : vPhotoR;
                const vNameY = vPhotoCY + vPhotoR + 14;
                const vLastNameY = vNameY + 13;
                const vYearY = vLastNameY + 14;
                const vBadgeW = 22, vBadgeH = 18;
                return (
                  <g key={p.id} transform={`translate(${x},${y})`}
                    data-pid={p.id} data-deceased={p.death_date ? "1" : "0"}
                    onClick={(e) => { e.stopPropagation(); if (dragMoved.current) return; setSelectedId(p.id); if (freeSpiritMode && viewMode === "trunk" && p.id !== (focusPersonId || myPersonId)) { setFocusPersonId(p.id); setExpandedIds(new Set()); setChainStopId(null); setPendingRecenterId(p.id); } }}
                    onMouseEnter={(e) => { const r = containerRef.current?.getBoundingClientRect(); if (r) setHoverPos({ x: e.clientX - r.left, y: e.clientY - r.top }); setHoverId(p.id); }}
                    onMouseMove={(e) => { const r = containerRef.current?.getBoundingClientRect(); if (r) setHoverPos({ x: e.clientX - r.left, y: e.clientY - r.top }); }}
                    onMouseLeave={() => setHoverId(null)}
                    style={{ cursor: "pointer" }}>
                    {/* Kërkesë e userit — skemë e re vertikale (foto lart,
                        emri nën foto, mbiemri, viti i lindjes) — më e
                        ngushtë (CARD_W_VERTICAL=96 vs 132), që zgjerimet
                        ("+") të mos përhapen aq shumë horizontalisht. Të
                        dyja skemat ruhen — zgjidhet nga Admin Center →
                        Cilësimet, origjinali mbetet parazgjedhja.

                        SHTESË (kërkesë e userit) — 3 skema shtesë, të
                        frymëzuara nga shembuj (Bridgerton family chart, etj.):
                        "elegant" (kapsulë/ovale, ngjyra neutrale), "modern3d"
                        (unazë ngjyre + hije SVG), "story" (rresht shtesë me
                        bio/vendlindje të shkurtuar + ikona ngjarjesh —
                        💍 e martuar/martuar, 🌍 martuar jashtë fisit, 🕊️ e
                        ndjerë). "Glass/Premium" u hoq me qëllim — blur-i i
                        vërtetë "shiko përmes qelqit" s'realizohet në SVG si
                        në CSS (backdrop-filter). */}
                    {treeCardLayoutEf === "heirloom" ? (
                      /* ══ MEDALJONI «HEIRLOOM» ═══════════════════════════════
                         Nga mockup-i i pronarit: fotoja në një medaljon të
                         rrumbullakët me unazë ari, emri i plotë poshtë me
                         serif, vitet nën të me ar dhe shifra me gjerësi të
                         barabartë, dhe — vetëm te të ndjerët — pëllumbi te
                         këndi.
                         Asnjë kuti, asnjë shirit ngjyre sipër: te ky dizajn
                         degën e tregon vija, jo korniza. */
                      <>
                        {/* 4 shtator 2026 — hapësira e bardhë mes rrethit dhe
                            fotos vjen tani nga Admin Center (`photo_ring_gap`),
                            jo nga një «3» e ngulitur. Ishte pyetje e pronarit:
                            «te aparencat nuk duket sikur administron madhësinë
                            e rrathëve rreth fotos, hapësirën e bardhë mes
                            rrethit dhe fotos». Nuk administronte — ky ekran i
                            kishte të fiksuara. */}
                        {/* Rrezja e fotos: normalisht e brendshmja, po nën «pa
                            ngjyra» ajo e jashtme — pra fotoja zë edhe hapësirën
                            ku rrinte unaza dhe hijëzimi. */}
                        {(() => {
                          const rFoto = paNgjyra() ? vPhotoR + photoRingGap : vPhotoR;
                          return (
                            <>
                              {!paNgjyra() && (
                                <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR + photoRingGap} fill={PALETA_TIPI.surface2 || COLORS.panelSoft} />
                              )}
                              {p.photo_path ? (
                                <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - rFoto, vPhotoCY - rFoto, rFoto * 2, rFoto * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                              ) : (
                                /* Pa fotografi s'ka çfarë ta mbulojë unazën —
                                   prandaj këtu mbetet një rreth i thjeshtë, dhe
                                   bashkë me të mbetet edhe e vetmja gjë që unaza
                                   mbante vërtet: vija e ndërprerë e profilit të
                                   pakompletuar. */
                                <circle cx={CARD_W / 2} cy={vPhotoCY} r={rFoto} fill={paNgjyra() ? (COLORS.panel || "#fff") : (PALETA_TIPI.bronzeSoft || "#f4e9d4")} />
                              )}
                            </>
                          );
                        })()}
                        {/* Unaza — e vetmja gjë që e mban personin bashkë.
                            E NDËRPRERË = PROFIL I PAKOMPLETUAR. Rregull i
                            konfirmuar nga pronari më 4 shtator 2026: «kështu
                            identifikohen profilat e pakompletuar».
                            Sot «i pakompletuar» do të thotë: as foto, as
                            datëlindje — pra vetëm një emër. Është pragu më i
                            ashpër me qëllim: po të merrej përkufizimi i plotë
                            i statistikave (datëlindje + foto + vendlindje +
                            të dy prindërit), pothuajse çdo unazë do të dilte e
                            ndërprerë dhe shenja s'do të thoshte më asgjë.
                            Nëse duhet zgjeruar, kushti është ky rresht i vetëm. */}
                        {/* Nën «pa ngjyra» me fotografi, unaza NUK vizatohet
                            fare — fotoja e ka zënë vendin e saj. Pa fotografi
                            mbetet një fije neutrale, sepse aty ajo është e
                            vetmja gjë që e mban rrethin dhe shenjën e profilit
                            të pakompletuar. I zgjedhuri e ka gjithmonë. */}
                        {(isSelected || !(paNgjyra() && p.photo_path)) && (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR + photoRingGap} fill="none"
                            stroke={isSelected ? COLORS.moss : (paNgjyra() ? toniNeutral() : ngjyratENdjekinDizajnin() ? (PALETA_TIPI.spouseLine || COLORS.gold) : ringColorFor(p))}
                            strokeWidth={isSelected ? 3.5 : (paNgjyra() ? ringW(1) : ringW(2.5))}
                            strokeDasharray={p.photo_path || p.birth_date ? undefined : "3 3"} />
                        )}
                        {/* ⚠ 4 shtator 2026 — «emrat mbivendosen mbi njëri-tjetrin».
                            E kisha vënë emrin e plotë në një rresht, sepse ashtu
                            është te mockup-i. Po te mockup-i emrat janë «Ardit
                            Demo» dhe nyja është 130px e gjerë; te pema e vërtetë
                            emrat janë «Mehmet Krasniqi», «Xhemajl Krasniqi», dhe
                            kartela është 96px. Një rresht i vetëm s'ka ku të
                            hyjë, dhe dy bashkëshortë rrinë vetëm ~134px larg
                            qendër-me-qendër — pra teksti i njërit hyn te tjetri.
                            Tani emri ndahet në dy rreshta, si te skema klasike:
                            asnjë emër i vërtetë nuk e kalon gjysmën e kartelës. */}
                        {/* SKEMA1 (6 shtator 2026): «emri dominon» — madhësitë, rreshtat
                            dhe vajzëria vijnë tani nga Admin Center → The tree → Heirloom. */}
                        {emriNeKartele({ p, x: CARD_W / 2, yEmri: vNameY, yMbiemri: vNameY + 13, yVitet: vNameY + 27, gjeresia: CARD_W - 4, ngjesh: true,
                          emri: { fontFamily: FONTS.display, fontSize: 13, fontWeight: 600, fill: COLORS.ink },
                          mbiemri: { fontFamily: FONTS.display, fontSize: 11.5, fontWeight: 600, fill: COLORS.ink },
                          vitet: { fontFamily: FONTS.body, fontSize: 10.5, fontWeight: 500, fill: PALETA_TIPI.bronze || COLORS.gold, fontVariantNumeric: "tabular-nums" },
                          maxEmri: 11, maxMbiemri: 12 })}
                        {p.death_date && (() => {
                          // ⚠ 4 shtator 2026 — «për të vdekurit do të doja vetëm
                          // pëllumbin, jo gjethen; edhe po merr shumë hapësirë,
                          // bëje më të vogël edhe pa atë rrethin kufizues».
                          //
                          // Rrethi ishte gabim i imi dhe e kuptoj tani pse: ai
                          // e kthen shenjën në NJOFTIM — një disk i bardhë me
                          // kufi ari mbi një fytyrë tërheq syrin më shumë se
                          // vetë fytyra. Pëllumbi nuk ka nevojë për kornizë; ai
                          // është siluetë, dhe një siluetë lexohet mbi çdo
                          // sfond pa u rrethuar. (Gjethja s'ka ekzistuar kurrë
                          // te kodi ynë — është shenja e dytë e mockup-it, dhe
                          // s'do të vijë.)
                          const bx = CARD_W / 2 + (vPhotoR + photoRingGap) * 0.76;
                          const by = vPhotoCY - (vPhotoR + photoRingGap) * 0.76;
                          const ari = PALETA_TIPI.bronze || COLORS.gold;
                          return (
                            <g data-status-icon="1">
                              <title>{t("Deceased")}</title>
                              {/* Pëllumbi, i njëjti vizatim si te `ikonatHeirloom`,
                                  i shkallëzuar këtu sepse jemi brenda një `<svg>`. */}
                              {/* 4 shtator 2026 — VIZATIMI NUK ËSHTË MË I IMI.
                                  Pronari dërgoi vetë SVG-në e mockup-it dhe kërkoi
                                  ta krahasoja me timen. E krahasova: e imja kishte
                                  një krah të gjatë dhe bisht të pirunjtë — pra
                                  dallëndyshe, jo pëllumb. Këtu tani rri shtegu i
                                  tij, fjalë për fjalë, nga `ikonatHeirloom.jsx`.
                                  18px, jo 12: cepat e pendëve — e vetmja gjë që e
                                  bën pëllumb — mbushen dhe zhduken nën ~16px. */}
                              {/* 4 shtator 2026 — «po bie shumë në sy». 18 → 14:
                                  shenja e kujtimit duhet vënë re, jo lexuar e para. */}
                              <svg x={bx - 7} y={by - 7} width="14" height="14" viewBox="0 0 200 200">
                                <path d={SHTEG_PELLUMBI_PLOT} fill={ari} fillRule="evenodd" />
                              </svg>
                            </g>
                          );
                        })()}
                      </>
                    ) : treeCardLayoutEf === "vertical" ? (
                      <>
                        <rect width={CARD_W} height={CARD_H} rx="12" fill={ngjyraEDegesSoft(effectiveBranch(p))} stroke={isSelected ? COLORS.moss : ngjyraEDeges(effectiveBranch(p))} strokeWidth={isSelected ? 3.5 : 2.5} />
                        <rect x="0" y="0" width={CARD_W} height={vStripH} rx="10" fill={mbushjaEDeges(effectiveBranch(p))} />
                        <rect x="0" y={vStripH / 2} width={CARD_W} height={vStripH / 2} fill={mbushjaEDeges(effectiveBranch(p))} />
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - rFotoEf, vPhotoCY - rFotoEf, rFotoEf * 2, rFotoEf * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR} fill="#fff" />
                        )}
                        {/* SKEMA1: emri, mbiemri, vitet — stilet e skemës mbeten këtu, madhësitë/rreshtat i vendos Admin Center */}
                        {emriNeKartele({ p, x: CARD_W / 2, yEmri: vNameY, yMbiemri: vLastNameY, yVitet: vYearY, gjeresia: CARD_W - 8,
                          emri: { fontFamily: FONTS.display, fontSize: 12.5, fontWeight: 600, fill: COLORS.ink }, mbiemri: { fontFamily: FONTS.display, fontSize: 10.5, fill: COLORS.inkLight }, vitet: { fontFamily: FONTS.mono, fontSize: 9.5, fill: COLORS.gold },
                          maxEmri: 10, maxMbiemri: 12 })}
                        {p.death_date && (
                          <>
                            <circle data-status-icon="1" cx="10" cy="10" r="9" fill={COLORS.bgDeep} stroke={COLORS.onBg} strokeWidth="1.5" />
                            {shenjaEEndjeres(10, 14, 11)}
                          </>
                        )}
                        <rect data-status-icon="1" x={CARD_W - vBadgeW - 4} y={CARD_H - vBadgeH - 4} width={vBadgeW} height={vBadgeH} rx="6" fill={ngjyraEDegesSoft(effectiveBranch(p))} />
                        <g data-status-icon="1" transform={`translate(${CARD_W - vBadgeW + 2},${CARD_H - vBadgeH - 1})`}><BranchIcon branch={effectiveBranch(p)} gender={p.gender} size={14} color={ngjyraEDeges(effectiveBranch(p))} /></g>
                      </>
                    ) : treeCardLayoutEf === "elegant" ? (
                      /* "Elegant (Oval)" — FIX (kërkesë e userit): "vetëm
                         foto ovale + emri poshtë, si te modern" — hequr
                         krejt kutia/kapsula e dukshme, njësoj strukturë si
                         "modernphoto" (foto pezull mbi kanavacë, hije e
                         butë), POR me foto të VËRTETË OVALE (elips, jo
                         rreth — clip-path "elegant" në <defs>) dhe unazë
                         gjithashtu ovale rreth saj. Ngjyra neutrale (krem)
                         për unazën, njësoj si më parë. */
                      <>
                        {!(paNgjyra() && p.photo_path) && (
                          <ellipse cx={CARD_W / 2} cy={vPhotoCY} rx={(vPhotoR * 1.05) + 2} ry={(vPhotoR * 1.35) + 2} fill={COLORS.panel} filter="url(#cardShadow3d)" />
                        )}
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - (vPhotoR * 1.05 + (paNgjyra() ? Math.max(photoRingGap, 2) : 0)), vPhotoCY - (vPhotoR * 1.35 + (paNgjyra() ? Math.max(photoRingGap, 2) : 0)), (vPhotoR * 1.05 + (paNgjyra() ? Math.max(photoRingGap, 2) : 0)) * 2, (vPhotoR * 1.35 + (paNgjyra() ? Math.max(photoRingGap, 2) : 0)) * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <ellipse cx={CARD_W / 2} cy={vPhotoCY} rx={vPhotoR * 1.05} ry={vPhotoR * 1.35} fill="#fff" />
                        )}
                        {/* FIX (kërkesë e userit) — unaza ishte e fiksuar
                            gjithmonë krem (COLORS.goldSoft), pa lidhje me
                            degën. Tani përdor `ringColorFor(p)` — të njëjtin
                            cilësim si te Modern/Ring Photo (Admin Center →
                            Ngjyra e unazës): "branch" e bën si degën, "none"
                            e mban EKZAKTËSISHT si më parë (krem neutrale). */}
                        {(isSelected || ringW(1.75) > 0) && (isSelected || !(paNgjyra() && p.photo_path)) && (
                          <ellipse cx={CARD_W / 2} cy={vPhotoCY} rx={(vPhotoR * 1.05) + photoRingGap} ry={(vPhotoR * 1.35) + photoRingGap} fill="none" stroke={isSelected ? COLORS.moss : ringColorFor(p)} strokeWidth={isSelected ? 3.5 : ringW(1.75)} />
                        )}
                        {(() => {
                          const elNameSize = Math.max(8, Math.min(11.5, CARD_W * 0.105));
                          const elYearSize = Math.max(7, Math.min(9, CARD_W * 0.075));
                          const elPhotoBottom = vPhotoCY + vPhotoR * 1.35 + 2;
                          const elMaxChars = Math.max(8, Math.floor(CARD_W / (elNameSize * 0.62)));
                          // FIX (kërkesë e userit — "kur ka dy mbiemra, dy
                          // rreshta, jo '...'") — `wrapNameTwoLines` kthen 1
                          // ose 2 rreshta; kur janë 2, gjithçka poshtë
                          // (viti) zhvendoset me lartësinë e rreshtit shtesë.
                          const elNameY = elPhotoBottom + elNameSize + 6;
                          const elLastY = elNameY + (elNameSize + 2);
                          const elYearY = elLastY + elYearSize + 3;
                          // SKEMA1: emri/mbiemri/vitet përmes një vendi të vetëm — skema jep stilin, admini madhësitë.
                          return (
                            <>
                              {emriNeKartele({ p, x: CARD_W / 2, yEmri: elNameY, yMbiemri: elLastY, yVitet: elYearY, gjeresia: CARD_W - 6, ngjesh: true,
                                emri: { fontFamily: FONTS.display, fontSize: elNameSize, fontWeight: 600, fill: COLORS.onBg, letterSpacing: 0.2 }, mbiemri: { fontFamily: FONTS.display, fontSize: elNameSize, fontWeight: 600, fill: COLORS.onBg, letterSpacing: 0.2 }, vitet: { fontFamily: FONTS.mono, fontSize: elYearSize, fill: COLORS.onBg, opacity: 0.8 },
                                maxEmri: elMaxChars, maxMbiemri: elMaxChars + 1 })}
                            </>
                          );
                        })()}
                        {p.death_date && (
                          shenjaEEndjeres(CARD_W / 2 + vPhotoR - 2, vPhotoCY - vPhotoR * 1.35 + 8, 12)
                        )}
                      </>
                    ) : treeCardLayoutEf === "modern3d" ? (
                      /* "Modern (3D)" — ORIGJINALI (rikthyer siç ishte —
                         kërkesë e userit: "s'doja ta ndryshoje, doja ta
                         shtoje si të re"). Hije e butë SVG (filter
                         "cardShadow3d"), unazë ngjyre rreth fotos sipas
                         degës/brezit, kënde shumë të rrumbullakosura, kuti
                         kartele e dukshme (jo "foto pezull"). */
                      <>
                        <rect width={CARD_W} height={CARD_H} rx="16" fill={ngjyraEDegesSoft(effectiveBranch(p))} filter="url(#cardShadow3d)" />
                        <rect x="1" y="1" width={CARD_W - 2} height={CARD_H - 2} rx="15" fill="none" stroke={isSelected ? COLORS.moss : ngjyraEDeges(effectiveBranch(p))} strokeWidth={isSelected ? 3 : 1.5} opacity={isSelected ? 1 : 0.55} />
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - rFotoEf, vPhotoCY - rFotoEf, rFotoEf * 2, rFotoEf * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR} fill="#fff" />
                        )}
                        {ringW(2.5) > 0 && (isSelected || !(paNgjyra() && p.photo_path)) && (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR + photoRingGap} fill="none" stroke={ringColorFor(p)} strokeWidth={ringW(2.5)} />
                        )}
                        {/* SKEMA1: emri, mbiemri, vitet — stilet e skemës mbeten këtu, madhësitë/rreshtat i vendos Admin Center */}
                        {emriNeKartele({ p, x: CARD_W / 2, yEmri: vNameY, yMbiemri: vLastNameY, yVitet: vYearY, gjeresia: CARD_W - 8,
                          emri: { fontFamily: FONTS.display, fontSize: 12.5, fontWeight: 700, fill: COLORS.ink }, mbiemri: { fontFamily: FONTS.display, fontSize: 10, fill: COLORS.inkLight }, vitet: { fontFamily: FONTS.mono, fontSize: 9, fill: ngjyraEDeges(effectiveBranch(p)) },
                          maxEmri: 10, maxMbiemri: 12 })}
                        {p.death_date && (
                          <>
                            <circle data-status-icon="1" cx="12" cy="12" r="9" fill={COLORS.bgDeep} stroke="#fff" strokeWidth="1.5" />
                            {shenjaEEndjeres(12, 16, 11)}
                          </>
                        )}
                      </>
                    ) : treeCardLayoutEf === "modernphoto" ? (
                      /* "Modern (Foto Pezull)" — SHTESË E RE (jo zëvendësim
                         i "modern3d") — versioni i thjeshtuar sipas foto-s
                         reference të userit: vetëm foto rrethore me unazë
                         ngjyre + hije, emri i plotë + viti lindje-vdekje
                         poshtë, PA "kuti" kartele të dukshme.
                         FIX (kërkesë e userit) — emrat dilnin shumë të
                         mëdhenj (mbivendoseshin mes dy kartelash fqinje,
                         p.sh. çift) DHE viti (lifespan) ishte i ngjyrosur
                         sipas degës — tani: (1) madhësia e shkronjave është
                         PROPORCIONALE me CARD_W, jo fikse; (2) e GJITHË
                         shkrimi (emri + viti) përdor NJË ngjyrë të vetme,
                         të lexueshme (COLORS.panel — krem, kontrast i mirë
                         mbi çdo temë, sepse kartela s'ka fare sfond të vetin
                         këtu), jo më ngjyrë dege. */
                      <>
                        {!(paNgjyra() && p.photo_path) && (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR + 2} fill={COLORS.panel} filter="url(#cardShadow3d)" />
                        )}
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - rFotoEf, vPhotoCY - rFotoEf, rFotoEf * 2, rFotoEf * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR} fill="#fff" />
                        )}
                        {(isSelected || ringW(3) > 0) && (isSelected || !(paNgjyra() && p.photo_path)) && (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR + photoRingGap} fill="none" stroke={isSelected ? COLORS.moss : ringColorFor(p)} strokeWidth={isSelected ? 4 : ringW(3)} />
                        )}
                        {(() => {
                          const mpNameSize = Math.max(8, Math.min(11.5, CARD_W * 0.105));
                          const mpYearSize = Math.max(7, Math.min(9, CARD_W * 0.075));
                          const mpMaxChars = Math.max(8, Math.floor(CARD_W / (mpNameSize * 0.62)));
                          const mpNameY = vPhotoCY + vPhotoR + mpNameSize + 6;
                          const mpLastY = mpNameY + (mpNameSize + 2);
                          const mpYearY = mpLastY + mpYearSize + 3;
                          // SKEMA1: emri/mbiemri/vitet përmes një vendi të vetëm — skema jep stilin, admini madhësitë.
                          return (
                            <>
                              {emriNeKartele({ p, x: CARD_W / 2, yEmri: mpNameY, yMbiemri: mpLastY, yVitet: mpYearY, gjeresia: CARD_W - 6, ngjesh: true,
                                emri: { fontFamily: FONTS.display, fontSize: mpNameSize, fontWeight: 700, fill: COLORS.onBg }, mbiemri: { fontFamily: FONTS.display, fontSize: mpNameSize, fontWeight: 700, fill: COLORS.onBg }, vitet: { fontFamily: FONTS.mono, fontSize: mpYearSize, fill: COLORS.onBg, opacity: 0.8 },
                                maxEmri: mpMaxChars, maxMbiemri: mpMaxChars + 1 })}
                            </>
                          );
                        })()}
                        {p.death_date && (
                          shenjaEEndjeres(CARD_W / 2 + vPhotoR - 3, vPhotoCY - vPhotoR + 6, 12)
                        )}
                      </>
                    ) : treeCardLayoutEf === "elegantoriginal" ? (
                      /* "Elegant (Kapsulë — origjinali)" — STIL I RI, ruan
                         dizajnin E PARË të "Elegant (Oval)" (para se ta
                         ndryshoja në foto-pezull ovale, kërkesë e userit:
                         "nuk dua me ndryshu ekzistueset, krijo të reja").
                         Formë kapsulë (rx = gjysma e lartësisë), foto
                         RRETHORE brenda saj, ngjyra neutrale krem. */
                      <>
                        <rect width={CARD_W} height={CARD_H} rx={CARD_H / 2} fill={COLORS.panel} stroke={isSelected ? COLORS.moss : COLORS.goldSoft} strokeWidth={isSelected ? 3 : 1.5} />
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - rFotoEf, vPhotoCY - rFotoEf, rFotoEf * 2, rFotoEf * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR} fill="#fff" />
                        )}
                        {ringW(1.5) > 0 && (isSelected || !(paNgjyra() && p.photo_path)) && (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR + (photoRingGap - 2)} fill="none" stroke={ringColorFor(p)} strokeWidth={ringW(1.5)} />
                        )}
                        {/* SKEMA1: emri, mbiemri, vitet — stilet e skemës mbeten këtu, madhësitë/rreshtat i vendos Admin Center */}
                        {emriNeKartele({ p, x: CARD_W / 2, yEmri: vNameY, yMbiemri: vLastNameY, yVitet: vYearY, gjeresia: CARD_W - 8,
                          emri: { fontFamily: FONTS.display, fontSize: 12, fontWeight: 600, fill: COLORS.ink, letterSpacing: 0.2 }, mbiemri: { fontFamily: FONTS.display, fontSize: 9, fill: COLORS.inkLight, letterSpacing: 0.6 }, vitet: { fontFamily: FONTS.mono, fontSize: 8.5, fill: COLORS.gold },
                          maxEmri: 10, maxMbiemri: 12, mbiemriTransform: (s) => s.toUpperCase() })}
                        {p.death_date && (
                          shenjaEEndjeres(CARD_W - 16, 16, 10)
                        )}
                      </>
                    ) : treeCardLayoutEf === "story" ? (
                      /* "Story (Editorial)" — njësoj foto-lart, PLUS një
                         rresht shtesë me bio/vendlindje të shkurtuar, PLUS
                         një rresht ikonash ngjarjesh, të nxjerra nga fusha
                         EKZISTUESE (jo një regjistër i ri ngjarjesh, ende s'e
                         ka skema): 💍 i/e martuar, 🌍 martuar jashtë fisit
                         (branch "married_out"), 🕊️ i/e ndjerë. */
                      <>
                        <rect width={CARD_W} height={CARD_H} rx="8" fill={COLORS.panel} stroke={isSelected ? COLORS.moss : ngjyraEDeges(effectiveBranch(p))} strokeWidth={isSelected ? 3 : 1.5} />
                        <rect x="0" y="0" width={CARD_W} height="4" fill={mbushjaEDeges(effectiveBranch(p))} />
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - rFotoEf, vPhotoCY - rFotoEf, rFotoEf * 2, rFotoEf * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR} fill="#fff" />
                        )}
                        {/* SKEMA1: emri, mbiemri, vitet — stilet e skemës mbeten këtu, madhësitë/rreshtat i vendos Admin Center */}
                        {emriNeKartele({ p, x: CARD_W / 2, yEmri: vNameY, yMbiemri: vLastNameY, yVitet: vYearY, gjeresia: CARD_W - 8,
                          emri: { fontFamily: FONTS.display, fontSize: 12, fontWeight: 600, fill: COLORS.ink }, mbiemri: { fontFamily: FONTS.display, fontSize: 10, fill: COLORS.inkLight }, vitet: { fontFamily: FONTS.mono, fontSize: 8.5, fill: COLORS.gold },
                          maxEmri: 10, maxMbiemri: 12 })}
                        <text x={CARD_W / 2} y={vYearY + 12} textAnchor="middle" style={{ fontFamily: FONTS.body, fontSize: 8, fill: COLORS.inkLight, fontStyle: "italic" }}>
                          {truncateCard(p.bio || p.birth_place || "", 22)}
                        </text>
                        <text data-status-icon="1" x="8" y={CARD_H - 6} style={{ fontSize: 10 }}>
                          {[(p.spouse_ids || []).length > 0 ? "💍" : "", effectiveBranch(p) === "married_out" ? "🌍" : "", p.death_date ? "🕊️" : ""].filter(Boolean).join(" ")}
                        </text>
                      </>
                    ) : treeCardLayoutEf === "glass" ? (
                      /* "Glass (Premium)" — PROVË, siç u kërkua shprehimisht
                         edhe pas paralajmërimit të kufizimit (shih komentin
                         te "glassSheen" në <defs>). Kartelë gjysmë-
                         transparente + "shkëlqim" gradient sipër + hije e
                         butë + kufi i hollë i bardhë ("buzë qelqi”). */
                      <>
                        <rect width={CARD_W} height={CARD_H} rx="18" fill="rgba(255,255,255,0.16)" filter="url(#cardShadow3d)" />
                        <rect width={CARD_W} height={CARD_H} rx="18" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" />
                        <rect width={CARD_W} height={CARD_H / 2} rx="18" fill="url(#glassSheen)" />
                        <rect width={CARD_W} height={CARD_H} rx="18" fill="none" stroke={isSelected ? COLORS.moss : "transparent"} strokeWidth={isSelected ? 3 : 0} />
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, CARD_W / 2 - rFotoEf, vPhotoCY - rFotoEf, rFotoEf * 2, rFotoEf * 2)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR} fill="rgba(255,255,255,0.85)" />
                        )}
                        <circle cx={CARD_W / 2} cy={vPhotoCY} r={vPhotoR} fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" />
                        {/* SKEMA1: emri, mbiemri, vitet — stilet e skemës mbeten këtu, madhësitë/rreshtat i vendos Admin Center */}
                        {emriNeKartele({ p, x: CARD_W / 2, yEmri: vNameY, yMbiemri: vLastNameY, yVitet: vYearY, gjeresia: CARD_W - 8,
                          emri: { fontFamily: FONTS.display, fontSize: 12, fontWeight: 600, fill: COLORS.ink }, mbiemri: { fontFamily: FONTS.display, fontSize: 9.5, fill: COLORS.inkLight }, vitet: { fontFamily: FONTS.mono, fontSize: 8.5, fill: COLORS.ink, opacity: 0.75 },
                          maxEmri: 10, maxMbiemri: 12 })}
                        {p.death_date && (
                          shenjaEEndjeres(CARD_W - 14, 16, 10)
                        )}
                      </>
                    ) : (
                      <>
                        <rect width={CARD_W} height={CARD_H} rx="10" fill={ngjyraEDegesSoft(effectiveBranch(p))} stroke={isSelected ? COLORS.moss : ngjyraEDeges(effectiveBranch(p))} strokeWidth={isSelected ? 3.5 : 2.5} />
                        <rect x="0" y="0" width="10" height={CARD_H} rx="10" fill={mbushjaEDeges(effectiveBranch(p))} />
                        <rect x="5" y="0" width="5" height={CARD_H} fill={mbushjaEDeges(effectiveBranch(p))} />
                        {p.photo_path ? (
                          <image href={photoUrl(p.photo_path)} {...coverImageProps(p, 15, 11, 36, 36)} clipPath={`url(#clip-${p.id})`} preserveAspectRatio="none" />
                        ) : (
                          <circle cx="33" cy="29" r="18" fill="#fff" />
                        )}
                        {/* SKEMA1: emri, mbiemri, vitet — majtas, si gjithmonë; madhësitë i vendos Admin Center */}
                        {emriNeKartele({ p, x: 62, anchor: "start", yEmri: 24, yMbiemri: 39, yVitet: 51, gjeresia: CARD_W - 62 - 26,
                          emri: { fontFamily: FONTS.display, fontSize: 13, fontWeight: 600, fill: COLORS.ink },
                          mbiemri: { fontFamily: FONTS.display, fontSize: 11, fill: COLORS.inkLight },
                          vitet: { fontFamily: FONTS.mono, fontSize: 9, fill: COLORS.gold },
                          maxEmri: 12, maxMbiemri: 14 })}
                        {p.death_date && (
                          <>
                            <circle data-status-icon="1" cx="10" cy="47" r="9" fill={COLORS.bgDeep} stroke={COLORS.onBg} strokeWidth="1.5" />
                            {shenjaEEndjeres(10, 51, 11)}
                          </>
                        )}
                        <rect data-status-icon="1" x={CARD_W - 26} y={CARD_H - 22} width="26" height="22" rx="6" fill={ngjyraEDegesSoft(effectiveBranch(p))} />
                        <g data-status-icon="1" transform={`translate(${CARD_W - 19},${CARD_H - 19})`}><BranchIcon branch={effectiveBranch(p)} gender={p.gender} size={14} color={ngjyraEDeges(effectiveBranch(p))} /></g>
                      </>
                    )}
                    {eshteMysafir(p) && (
                      <g data-mysafir="1" transform="translate(0, -25)">
                        <rect width={CARD_W} height="20" rx="7" fill={COLORS.gold} stroke={COLORS.onBg} strokeWidth="1" />
                        <text x={CARD_W / 2} y="14" textAnchor="middle" style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, fill: COLORS.bgDeep }}>★ {t("Guest")}</text>
                      </g>
                    )}
                  </g>
                );
              })}
              {/* FIX (raportuar nga useri — "mungon viza mes bashkëshortëve
                  aktivë") — spouseLines/coupleStatusIcons u zhvendosën KËTU
                  (pas kartelave, jo para) — më parë vizatoheshin PARA
                  kartelave, dhe te stilet "foto pezull" (foto rrethore të
                  mëdha, pa kuti) fotoja i mbulonte krejt vijën e shkurtër
                  mes dy kartelave fqinje kur hendeku (couple_spacing) ishte
                  i vogël — viza EKZISTONTE në të dhëna/kod, thjesht ishte
                  fizikisht e padukshme pas fotos. Tani mbeten gjithmonë
                  mbi çdo stil kartele. */}
              {coupleStatusIcons}
              {expandButtons.map((btn) => (
                <g
                  key={btn.key}
                  transform={`translate(${btn.x},${btn.y})`}
                  onClick={(e) => {
                    e.stopPropagation();
                    // FIX (kërkesë e userit — "kur shtyp plus, pema nuk
                    // guxon të lëvizë, vetëm personat e rinj duhet të
                    // shfaqen") — `setPendingRecenterId` më parë e
                    // RIQENDËRZONTE krejt panin (pan.x/pan.y) mbi personin
                    // e zgjeruar në çdo klikim "+" — kombinuar me
                    // zoom-out-in e degës, kjo dukej si "pema lëviz djathtas
                    // / fëmijët dalin jashtë ekranit poshtë". Hequr krejt —
                    // mbetet VETËM zoom-out-i (shkallëzimi, nëse aktiv më
                    // poshtë), asnjë ndryshim i pan.x/pan.y kur zgjerohet.
                    // Kërkesë e userit — zoom-out i lehtë VETËM kur ZGJEROHET
                    // (jo kur mbyllet) një degë. "isCollapsing" përputhet me
                    // kushtet e njëjta që vetë logjika poshtë përdor për të
                    // vendosur "mbyll apo hap": chain → state "revealed"/
                    // "flowing" do të thotë tashmë e hapur (do mbyllet);
                    // normal/parallel → btn.isExpanded.
                    const isCollapsing = btn.kind === "chain"
                      ? (btn.state === "revealed" || btn.state === "flowing")
                      : btn.isExpanded;
                    // Kërkesë e userit — kur MBYLLET një degë, zoom-in i
                    // lehtë, i KUNDËRT me faktorin e zoom-out-it të
                    // zgjerimit (shih Admin Center → Cilësimet) — një
                    // faktor i vetëm për të dyja drejtimet, jo dy cilësime
                    // të veçanta për t'u mbajtur mend.
                    if (expandZoomOut.enabled) {
                      if (!isCollapsing) {
                        setScale((s) => Math.max(0.3, s * expandZoomOut.factor));
                      } else {
                        setScale((s) => Math.min(2.2, s / expandZoomOut.factor));
                      }
                    }
                    if (btn.kind === "chain") {
                      if (btn.state === "stopped") {
                        setChainStopId(null); // resume: show the direct line again
                      } else if (btn.state === "single") {
                        setExpandedIds((prev) => { const next = new Set(prev); btn.coupleIds.forEach((id) => next.add(id)); return next; }); // reveal siblings too
                      } else {
                        // "revealed" or plain "flowing" — collapse everything below back to nothing
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          const toRemove = new Set(btn.coupleIds);
                          const stack = [...btn.coupleIds];
                          while (stack.length) {
                            const id = stack.pop();
                            (baseChildrenMap[id] || []).forEach((cid) => { if (!toRemove.has(cid)) { toRemove.add(cid); stack.push(cid); } });
                          }
                          toRemove.forEach((id) => next.delete(id));
                          return next;
                        });
                        // FIX (kërkesë e userit — "çdo degë e pavarur; mbyllja
                        // e njërës nuk duhet të prekë 4 të tjerat"): chainStopId
                        // është një PRERJE GLOBALE e trungut — fsheh gjithçka
                        // poshtë asaj pike, pra prek edhe degët e tjera. Në
                        // modalitetin "disa degë njëkohësisht" nuk vendoset më:
                        // mbyllja heq VETËM nën-pemën e vet.
                        if (!parallelBranches && !btn.isLastInChain) setChainStopId(btn.chainId);
                      }
                      return;
                    }
                    // kind === "normal": non-chain branches (siblings/cousins already revealed)
                    if (!parallelBranches) {
                      if (btn.isExpanded) {
                        setExpandedIds(new Set());
                        return;
                      }
                      const path = [];
                      let cur = btn.coupleIds[0];
                      const seen = new Set();
                      while (cur && people[cur] && !seen.has(cur)) {
                        seen.add(cur);
                        path.push(cur);
                        if (defaultChainSet.has(cur)) break;
                        // Ngjitja ndjek anën e GJAKUT (jo babain by-default) —
                        // i njëjti rregull si te defaultChainArray, që i ardhuri
                        // me martesë të mos e presë trungun (shih climbBloodParent).
                        cur = climbBloodParent(cur);
                      }
                      const newExpanded = new Set();
                      path.forEach((id) => {
                        newExpanded.add(id);
                        (people[id]?.spouse_ids || []).forEach((s) => newExpanded.add(s));
                      });
                      setExpandedIds(newExpanded);
                      return;
                    }
                    setExpandedIds((prev) => {
                      const next = new Set(prev);
                      if (btn.isExpanded) {
                        const toRemove = new Set(btn.coupleIds);
                        const stack = [...btn.coupleIds];
                        while (stack.length) {
                          const id = stack.pop();
                          (baseChildrenMap[id] || []).forEach((cid) => {
                            if (!toRemove.has(cid)) {
                              toRemove.add(cid);
                              stack.push(cid);
                            }
                          });
                        }
                        toRemove.forEach((id) => next.delete(id));
                      } else {
                        btn.coupleIds.forEach((id) => next.add(id));
                      }
                      return next;
                    });
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {/* ⚠ ZONA E PREKJES — 4 shtator 2026.
                      Pronari: «është bërë shumë bukur, por kostoja është që
                      është shumë e vështirë me prekë plusin — duhet klikuar
                      5 herë derisa të hapet».
                      Kjo është e vërtetë dhe e parashikueshme: e zvogëlova
                      diskun nga 12 në ~8.4, pra sipërfaqja e prekjes u bë
                      gati gjysma — dhe në ekran me gisht ~17px është nën
                      çdo prag të arsyeshëm.
                      Zgjidhja nuk është ta rrisim përsëri butonin (atëherë
                      kthehet ankesa e parë), po ta ndajmë ATË QË SHIHET nga
                      ATË QË PREKET. Ky rreth është i padukshëm — pa mbushje,
                      pa vijë — po e kap klikimin në gjithë sipërfaqen e vet.
                      Rri i pari, që pjesët e dukshme të vizatohen mbi të.
                      `pointerEvents="all"` është i domosdoshëm: pa të, një
                      formë pa mbushje nuk merr as klikim as prekje. */}
                  <circle cx="0" cy="0"
                    r={Math.max(expandBtnSize * 2, 15)}
                    fill="none" pointerEvents="all" />
                  {/* «plusi dominon krejt pemën» — dhe e dominonte vërtet:
                      disku mbushej me THEKSIN (te Heirloom terrakotë e errët)
                      dhe rrethohej me ngjyrën e tekstit mbi sfond, pra dilte
                      një pikë e errët e trashë mbi një pemë të qetë. Te
                      mockup-i i njëjti buton është ar i lehtë me buzë kremi
                      dhe pak më i vogël. Vetëm te pamja «heirloom»; kudo
                      tjetër mbetet ashtu siç ka qenë. */}
                  {pemaHeirloom() ? (
                    /* 4 shtator 2026 — «plusi nuk dua të jetë i rrethuar me një
                       rreth të bardhë» dhe «po duket shumë i madh».
                       Buza e kremtë ishte imja, jo e mockup-it. Dhe madhësia:
                       disku ishte r=11 po shenja brenda vizatohej me font
                       `expandBtnSize * 1.33` = 16px — pra glifi ishte më i
                       gjerë se vetë disku dhe e bënte të dukej i fryrë.
                       Tani shenja vizatohet me dy vija të matura ndaj diskut,
                       jo me font — dhe madhësia vjen vetëm nga cilësimi. */
                    <circle cx="0" cy="0" r={expandBtnSize}
                      fill={PALETA_TIPI.spouseLine || COLORS.gold} stroke="none" />
                  ) : (
                    /* 4 shtator 2026 — «plusit te klasikja me ja hjek rrethin
                       e bardhë». E njëjta arsye si te heirloom: buza e bën
                       butonin të duket i ngjitur mbi pemë. Mbetet disku. */
                    <circle cx="0" cy="0" r={expandBtnSize} fill={COLORS.gold} stroke="none" />
                  )}
                  {/* SHTESË (kërkesë e userit — "buton më modern") — 3
                      stile: "classic" (tekst +/- si më parë), "chevron"
                      (shigjetë poshtë kur mbyllur / sipër kur hapur),
                      "outline" (vija gjeometrike +/-, jo font). */}
                  {pemaHeirloom() ? (() => {
                    // Te heirloom disku është më i vogël (0.7 e madhësisë së
                    // caktuar), prandaj çdo stil rivizatohet ndaj TIJ — jo
                    // ndaj `expandBtnSize` të plotë, siç ishte, ku glifi dilte
                    // më i gjerë se disku. Të tre stilet e Admin Center
                    // funksionojnë; vetëm shkalla ndryshon.
                    // 4 shtator 2026 — shumëzuesi 0.7 u hoq: i njëjti numër
                    // duhet të japë të njëjtën madhësi në çdo dizajn, përndryshe
                    // rrëshqitësi «punon» te njëri e jo te tjetri. Shih PEMA2.
                    const rH = expandBtnSize;
                    const ngj = PALETA_TIPI.surface || COLORS.panel;
                    if (expandBtnStyle === "chevron") {
                      return (
                        <g transform={btn.icon === "\u2212" ? "rotate(180)" : "rotate(0)"}>
                          <path d={`M ${-rH * 0.5} ${-rH * 0.2} L 0 ${rH * 0.3} L ${rH * 0.5} ${-rH * 0.2}`}
                                fill="none" stroke={ngj} strokeWidth={Math.max(1.3, rH * 0.24)}
                                strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                      );
                    }
                    return (
                      <g stroke={ngj} strokeWidth={Math.max(1.3, rH * 0.24)} strokeLinecap="round">
                        <line x1={-rH * 0.46} y1="0" x2={rH * 0.46} y2="0" />
                        {btn.icon === "+" && <line x1="0" y1={-rH * 0.46} x2="0" y2={rH * 0.46} />}
                      </g>
                    );
                  })() : expandBtnStyle === "chevron" ? (
                    <g transform={btn.icon === "−" ? "rotate(180)" : "rotate(0)"}>
                      <ChevronDown size={expandBtnSize * 1.1} color={COLORS.bgDeep} strokeWidth={2.5} style={{ transform: `translate(${-expandBtnSize * 0.55}px, ${-expandBtnSize * 0.55}px)` }} />
                    </g>
                  ) : expandBtnStyle === "outline" ? (
                    <g stroke={COLORS.bgDeep} strokeWidth={Math.max(1.5, expandBtnSize * 0.16)} strokeLinecap="round">
                      <line x1={-expandBtnSize * 0.45} y1="0" x2={expandBtnSize * 0.45} y2="0" />
                      {btn.icon === "+" && <line x1="0" y1={-expandBtnSize * 0.45} x2="0" y2={expandBtnSize * 0.45} />}
                    </g>
                  ) : (
                    <text x="0" y={expandBtnSize * 0.42} textAnchor="middle" style={{ fontFamily: FONTS.mono, fontSize: expandBtnSize * 1.33, fontWeight: 700, fill: pemaHeirloom() ? (PALETA_TIPI.surface || COLORS.panel) : COLORS.bgDeep }}>{btn.icon}</text>
                  )}
                </g>
              ))}
            </g>
          </svg>
        )}

        {hoverId && visiblePeople[hoverId] && hoverId !== selectedId && (
          <div className="absolute z-20 pointer-events-none rounded-lg shadow-xl px-3 py-2.5" style={{ left: Math.min(hoverPos.x + 16, (containerRef.current?.clientWidth || 999) - 220), top: Math.max(hoverPos.y - 10, 8), width: 200, background: COLORS.panel, border: `1px solid ${ngjyraEDeges(effectiveBranch(visiblePeople[hoverId]))}` }}>
            <p style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 15, color: COLORS.ink }}>{displayName(visiblePeople[hoverId])}</p>
            <p style={{ fontFamily: FONTS.mono, fontSize: 11, color: COLORS.ink, marginTop: 1 }}>{lifespan(visiblePeople[hoverId])}</p>
            {(visiblePeople[hoverId].birth_city || visiblePeople[hoverId].birth_country) && <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, marginTop: 2 }}>📍 {[visiblePeople[hoverId].birth_city, displayCountry(visiblePeople[hoverId].birth_country, i18n.language)].filter(Boolean).join(", ")}</p>}
          </div>
        )}

        {/* FIX (raportuar nga useri — "në telefon me notch, butoni '+' s'shfaqet
            fare") — i njëjti model defekti si te header-i sipër (dyfishim i
            env(safe-area-inset-*)). Vetë kontejneri i pemës (containerRef)
            është TASHMË i pozicionuar sigurt sipër bar-it të navigimit
            poshtë — ai vetë e rezervon hapësirën (paddingBottom:
            var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), shih bar-in poshtë) përmes rrjedhës
            normale flex, jo pozicionim "fixed" mbivendosje. Shtimi i
            var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) SËRISH këtu (te vetë butonat, brenda
            atij kontejneri tashmë të sigurt) e shtynte butonin "+" tepër
            lart — në disa telefona (kryesisht ata me "home indicator" poshtë,
            shpesh të çiftëzuar me notch sipër) kjo vlerë e dytë del aq e
            madhe sa butoni del jashtë zonës së dukshme të kontejnerit
            (overflow-hidden e "pret"). Hequr — 1rem i vetëm mjafton, sepse
            konteksti është TASHMË brenda zonës së sigurt. */}
        {/* FIX (kërkesë e userit) — Zoom In/Out mbeten floating në variantet
            "classic"/"fab"/"contextual" (përdoren shpesh me gjeste/klikime
            të njëpasnjëshme, s'ka kuptim t'i fusësh brenda menusë). Vetëm
            te "sheet" zhvendosen brenda panelit (shih poshtë), pasi ai
            variant konsolidon LITERALISHT gjithçka. Butoni "Fit" (qendrim)
            mbetet këtu VETËM te "classic" — te fab/contextual/sheet e ka
            zëvendësin e vet brenda bar-it/menusë së re.
            FIX (kërkesë e userit — "në desktop u zhduken +/- dhe të
            tjerat") — i njëjti problem si më sipër: në celular vazhdojnë
            të fshihen siç ishte (bar-i i ri i mobilit i zëvendëson), por në
            desktop (lg+) MBETEN GJITHMONË të dukshme, sido që të jetë
            varianti — desktopi s'e ka fare bar-in alternativ (lg:hidden). */}
        {/* ─── TURNEU I UDHËHEQUR (TURNEU1) ──────────────────────────────
            Butoni shfaqet VETËM kur platforma e ka ndezur DHE kjo familje e
            sheh (sot: vetëm sandbox). Pra për çdo familje tjetër ky bllok
            është sikur të mos ekzistonte — pa buton, pa vend të zënë.

            `position: fixed` me qëllim: shiriti duhet të rrijë mbi pemë pa
            varur nga struktura e saj, dhe të jetë aty edhe në telefon. Blloku
            i mëposhtëm (zoom-i) është `hidden lg:flex` — pra desktop-only; një
            turne që shihet vetëm në desktop s'do të provohej kurrë atje ku do
            të përdoret, te ekrani me prekje.

            KURSORI I LËVIZSHËM S'ËSHTË KËTU, ME VETËDIJE. `focusOn` e sjell
            personin GJITHMONË në qendër, pra një pikë drite do të ishte
            gjithmonë në të njëjtin vend — zbukurim që s'thotë asgjë. Vjen kur
            të shohim lëvizjen në telefon dhe të dimë ç'i mungon. */}
        {/* ─── ZGJEDHËSI I FUSHËS (RRËFIMI1) ──────────────────────────────
            Kërkesë e pronarit: *"fillo tregimin nga ky paraardhës edhe ndalu
            te ky … pastaj tregim vetëm për x person … plus pasardhësit"*.

            Rri pranë butonit që nis turneun, jo te Admin Center, sepse kjo NUK
            është cilësim platforme: është pyetja "çfarë dua të dëgjoj tani".
            Ndryshimi ndodh menjëherë — `turneFusha` është varësi e
            `turneRrefim`, pra rrëfimi rindërtohet vetë.

            `datalist` dhe jo një listë e shkruar me dorë: te një familje me
            treqind veta, një `select` do të ishte i pashfrytëzueshëm, dhe një
            ndërtues i ri kërkuesish do të ishte ekran i dytë — pikërisht ajo
            që ky projekt nuk e lejon. Kërkimi vendas punon edhe me prekje. */}
        {!turneAktiv && turneHapat.length >= 3 && turneCfg?.stil === "rrefim" && turneFushaHapur && (
          <div data-turne-kontroll
            style={{
              position: "fixed", left: 10, right: 10, bottom: 150, zIndex: 61,
              maxWidth: 460, margin: "0 auto",
              background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`,
              borderRadius: 16, padding: "12px 13px",
              boxShadow: "0 10px 34px rgba(0,0,0,.22)",
            }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: COLORS.ink, marginBottom: 8 }}>
              {t("Whose story?")}
            </div>
            <datalist id="turne-personat">
              {turnePersonat.map((p) => (
                <option key={p.id} value={p.etiketa} />
              ))}
            </datalist>

            <select
              value={turneFusha.lloj}
              onChange={(e) => setTurneFusha((f) => ({ ...f, lloj: e.target.value }))}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 9, fontSize: 12.5,
                border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft,
                color: COLORS.ink, fontFamily: "inherit",
              }}>
              <option value="gjithe">{t("The whole family")}</option>
              <option value="person">{t("One person only")}</option>
              <option value="pasardhesit">{t("A person and their descendants")}</option>
              <option value="paraardhesit">{t("A person and their ancestors")}</option>
              <option value="vija">{t("From one ancestor down to one descendant")}</option>
            </select>

            {turneFusha.lloj !== "gjithe" && (
              <input
                list="turne-personat"
                defaultValue={turneEmriI(turneFusha.nga)}
                placeholder={turneFusha.lloj === "vija" ? t("Start from…") : t("Which person?")}
                onChange={(e) => setTurneFusha((f) => ({ ...f, nga: turneGjejPersonin(e.target.value) }))}
                style={{
                  width: "100%", marginTop: 7, padding: "8px 10px", borderRadius: 9, fontSize: 12.5,
                  border: `1px solid ${COLORS.goldSoft}`, background: "transparent",
                  color: COLORS.ink, fontFamily: "inherit",
                }} />
            )}
            {turneFusha.lloj === "vija" && (
              <input
                list="turne-personat"
                defaultValue={turneEmriI(turneFusha.deri)}
                placeholder={t("…down to")}
                onChange={(e) => setTurneFusha((f) => ({ ...f, deri: turneGjejPersonin(e.target.value) }))}
                style={{
                  width: "100%", marginTop: 7, padding: "8px 10px", borderRadius: 9, fontSize: 12.5,
                  border: `1px solid ${COLORS.goldSoft}`, background: "transparent",
                  color: COLORS.ink, fontFamily: "inherit",
                }} />
            )}

            <div style={{ fontSize: 11, color: COLORS.inkLight, marginTop: 8, lineHeight: 1.45 }}>
              {turnePershkrimiIFushes}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 9 }}>
              <button data-turne-kontroll
                onClick={() => { setTurneFusha({ lloj: "gjithe", nga: null, deri: null }); }}
                style={turneBtn}>{t("Reset")}</button>
              <button data-turne-kontroll onClick={() => setTurneFushaHapur(false)}
                style={{ ...turneBtnKryesor, marginLeft: "auto" }}>{t("Done")}</button>
            </div>
          </div>
        )}

        {!turneAktiv && turneHapat.length >= 3 && (
          <div style={{
            position: "fixed", left: 14, bottom: 96, zIndex: 60,
            display: "flex", alignItems: "center", gap: 7,
          }}>
            <button data-turne-kontroll onClick={nisTurneun}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "9px 14px", borderRadius: 999,
                background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`,
                color: COLORS.ink, fontSize: 12.5, fontWeight: 600,
                boxShadow: "0 6px 20px rgba(0,0,0,.16)",
              }}>
              <span style={{ color: COLORS.ink }}>▶</span>
              {t("Tell me the story")}
            </button>
            {/* Butoni i fushës del vetëm te stili «rrëfim». Te klasiku turneu i
                merr personat nga pamja, jo nga një prerje e pemës — një
                zgjedhës aty do të premtonte diçka që ai stil s'e mban. */}
            {turneCfg?.stil === "rrefim" && (
              <button data-turne-kontroll onClick={() => setTurneFushaHapur((v) => !v)}
                title={t("Whose story?")}
                style={{
                  padding: "9px 12px", borderRadius: 999,
                  background: turneFusha.lloj === "gjithe" ? COLORS.panel : COLORS.gold,
                  border: `1px solid ${COLORS.goldSoft}`,
                  color: turneFusha.lloj === "gjithe" ? COLORS.ink : COLORS.bgDeep,
                  fontSize: 12, fontWeight: 600,
                  boxShadow: "0 6px 20px rgba(0,0,0,.16)", maxWidth: 190,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                {turneFusha.lloj === "gjithe" ? "⚙" : turneShkurtimiIFushes}
              </button>
            )}
          </div>
        )}

        {turneAktiv && turneHapat[turneIndeks] && (
          <div data-turne-kontroll
            style={{
              position: "fixed", left: 10, right: 10, bottom: 88, zIndex: 60,
              maxWidth: 620, margin: "0 auto",
              background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`,
              borderRadius: 16, padding: "11px 13px",
              boxShadow: "0 10px 34px rgba(0,0,0,.22)",
            }}>
            {/* Etiketa e kapitullit del vetëm te stili «rrëfim», dhe vetëm te
                hapi i parë i secilit kapitull — aty ku historia merr kthesë. */}
            {turneHapat[turneIndeks].kap && (
              <div style={{
                fontSize: 10, letterSpacing: ".14em",
                color: COLORS.ink, marginBottom: 5, fontWeight: 700,
              }}>{turneHapat[turneIndeks].kap}</div>
            )}
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <b style={{ fontSize: 14.5, color: COLORS.ink }}>{turneHapat[turneIndeks].emri}</b>
              {turneHapat[turneIndeks].jeta && (
                <span style={{ fontSize: 11.5, color: COLORS.ink }}>{turneHapat[turneIndeks].jeta}</span>
              )}
              <span style={{ marginLeft: "auto", fontSize: 11, color: COLORS.inkLight }}>
                {turneIndeks + 1}/{turneHapat.length}
              </span>
            </div>
            {/* Boshllëku shkruhet ndryshe nga faktet — me shkronja të pjerrëta
                dhe të zbehta — që të mos lexohet si fakt. */}
            <div style={{
              fontSize: 12.5, lineHeight: 1.5, marginTop: 4,
              color: turneHapat[turneIndeks].bosh ? COLORS.inkLight : COLORS.ink,
              fontStyle: turneHapat[turneIndeks].bosh ? "italic" : "normal",
            }}>
              {turneHapat[turneIndeks].teksti}
            </div>
            {/* HISTORIA E SHKRUAR — fjalët e familjes, jo të sistemit. Prandaj
                rri veçmas nga faktet dhe me shkronjë tjetër: aty ku dikush ka
                shkruar diçka, ajo duhet të duket se e ka shkruar një njeri.

                Kur s'ka, e thotë — kërkesë e pronarit: *"nëse nuk ka, thuaj
                se nuk ka më shumë histori për personin"*. Një boshllëk i thënë
                është ftesë; një boshllëk i heshtur është mungesë. */}
            {turneHapat[turneIndeks].historia ? (
              <div style={{
                fontSize: 12.5, lineHeight: 1.55, marginTop: 7,
                paddingTop: 7, borderTop: `1px solid ${COLORS.goldSoft}`,
                color: COLORS.ink, whiteSpace: "pre-wrap",
              }}>
                {turneHapat[turneIndeks].historia}
                {turneHapat[turneIndeks].u_pre && (
                  <span style={{ color: COLORS.inkLight }}> {t("(the story continues in the profile)")}</span>
                )}
              </div>
            ) : turneHapat[turneIndeks].paHistori && !turneHapat[turneIndeks].bosh && turneHapat[turneIndeks].id ? (
              <div style={{ fontSize: 11.5, marginTop: 7, color: COLORS.inkLight, fontStyle: "italic" }}>
                {t("There is no written story for this person yet.")}
              </div>
            ) : null}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 9 }}>
              <button data-turne-kontroll
                onClick={() => { turneRrahjaRef.current = ""; setTurneFaza(0); setTurneIndeks((i) => Math.max(0, i - 1)); }}
                disabled={turneIndeks === 0}
                style={{ ...turneBtn, opacity: turneIndeks === 0 ? .4 : 1 }}>‹</button>
              <button data-turne-kontroll
                onClick={() => (turnePauza ? vazhdoTurneun() : setTurnePauza(true))}
                style={{ ...turneBtnKryesor, minWidth: 84 }}>
                {turnePauza ? "▶ " + t("Continue") : "⏸ " + t("Pause")}
              </button>
              <button data-turne-kontroll
                onClick={() => { turneRrahjaRef.current = ""; setTurneFaza(0); setTurneIndeks((i) => Math.min(turneHapat.length - 1, i + 1)); }}
                disabled={turneIndeks + 1 >= turneHapat.length}
                style={{ ...turneBtn, opacity: turneIndeks + 1 >= turneHapat.length ? .4 : 1 }}>›</button>
              <button data-turne-kontroll onClick={turneZeriToggle}
                disabled={!zeriBurimi}
                title={!zeriBurimi
                  ? t("This device has no voice for {{gjuha}} — the tour stays silent, with captions.", { gjuha: emriIGjuhës })
                  : zeriBurimi === "server"
                    ? t("Voice: {{zeri}}", { zeri: String(turneCfg?.zeri || "").replace("sq-AL-", "").replace("Neural", "") })
                    : ""}
                style={{ ...turneBtn, opacity: zeriBurimi ? 1 : 0.45, cursor: zeriBurimi ? "pointer" : "help" }}>
                {turneZëri && zeriBurimi ? "🔊" : "🔇"}
              </button>
              <button data-turne-kontroll onClick={ndalTurneun}
                style={{ ...turneBtn, marginLeft: "auto", background: "transparent", color: COLORS.inkLight }}>
                ✕ {t("Exit")}
              </button>
            </div>
            {turneZëri && zeriGabimi && (
              <div style={{ fontSize: 10.5, color: COLORS.danger, marginTop: 6, lineHeight: 1.45 }}>
                🔇 {zeriGabimi}
              </div>
            )}
            {turnePauza && (
              <div style={{ fontSize: 10.5, color: COLORS.inkLight, marginTop: 6 }}>
                {t("You took the wheel — the tour waits.")}
              </div>
            )}
          </div>
        )}

        {!locked && peopleCount > 0 && !shiritiIPemesKa("zoomin", "zoomout", "fit") && (
          <div className="hidden lg:flex absolute right-4 flex-col gap-1.5" style={{ bottom: "1rem" }}>
            <button onClick={() => { userAdjustedViewRef.current = true; setScale((s) => Math.min(2.2, s + 0.15)); }} className="p-2 rounded-md" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}><ZoomIn size={15} color={COLORS.ink} /></button>
            <button onClick={() => { userAdjustedViewRef.current = true; setScale((s) => Math.max(0.3, s - 0.15)); }} className="p-2 rounded-md" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}><ZoomOut size={15} color={COLORS.ink} /></button>
            <button onClick={() => fitView(currentPersonId)} title={t("Center on me")} className="p-2 rounded-md" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}><Crosshair size={15} color={COLORS.ink} /></button>
          </div>
        )}
        {/* FIX (raportuar nga useri — "shto person s'shfaqet për një
            përdorues me rolin Redaktor") — ky buton ishte i kufizuar te
            `canManage` (VETËM admin), edhe pse vetë modeli i lejeve në
            backend (rpc_upsert_person) i lejon EDHE redaktorit (brenda
            degës së vet) EDHE kontribuesit (vetëm si prind i drejtpërdrejtë)
            të shtojnë persona. Butoni tani shfaqet për të tria rolet me
            leje shtimi — vetë RPC-ja e zbaton kufirin e saktë (dega/prindi)
            kur klikohet "Shto në pemë", jo UI-ja këtu.
            FIX (kërkesë e userit) — te sheet/fab/contextual/drawer/hybrid,
            t("Add person") jeton brenda bar-it/menusë së re poshtë NË
            CELULAR (jo si buton lundrues i veçantë), por në DESKTOP (lg+,
            pa bar alternativ fare) mbetet gjithmonë i dukshëm këtu. */}
        {/* HEQUR — 4 shtator 2026. Pronari: «hiq edhe butonin Shto, sepse
            nuk duhet kurrë me ai buton në sipërfaqen e pemës — është shumë e
            vjetër si ide». «Shto» rri te menyja dhe te shiriti, ku rrinë
            hyrjet e tjera; mbi kanavacën e pemës nuk rri asgjë që s'është pemë. */}
        {/* FIX (kërkesë e userit) — variant "drawer": "shumë telefonë kanë
            sidebar që shfaqet kur e tërheq" — "gjuhëz" (tab) e vogël fikse
            në skajin djathtas, vertikalisht në mes të ekranit të pemës. Prek
            (ose tërhiq majtas) → rrëshqet drejtë-në-majtas një panel me
            gjithçka (Kërko, Shiko I PLOTË me opsionet e avancuara, Zoom/
            Fit, Shto Person). Tërhiq djathtas brenda panelit → mbyllet. */}
        {/* Bar STATIK i "classic" (kërkesë e userit — "bar statik mbi menynë
            kryesore, me të gjitha ikonat ekzistuese"): gjithmonë i dukshëm në
            telefon; ikonat, renditja, orientimi dhe pozicioni vijnë nga Admin
            Center (treeBarConfig). Desktopi mban shiritin lart si më parë. */}
        {!locked && peopleCount > 0 && treeNavVariant === "classic" && (() => {
          const pos = treeBarConfig.position || "bottom";
          const vert = (treeBarConfig.orientation || (pos === "left" || pos === "right" ? "vertical" : "horizontal")) === "vertical";
          // FORMA e shiritit (kërkesë e userit — "me mujt me ndryshu formën"):
          //   "bar"   — shirit i plotë anë-më-anë, ngjitur me skajin, si rreshti
          //             i contextual-it ("perfekt për klasikun" — parazgjedhja);
          //   "pill"  — pil i rrumbullakosur pluskues;
          //   "ghost" — vetëm ikonat, pa sfond fare.
          const shape = treeBarConfig.shape || "bar";
          // NJË linjë e vetme (kërkesë e userit): asnjë wrap — ikonat kompakte
          // ndajnë hapësirën në mënyrë të barabartë, si te Swap.
          const st = { position: "absolute", zIndex: 25, display: "flex", flexDirection: vert ? "column" : "row", gap: 4, alignItems: "center", justifyContent: !vert ? "space-around" : "center", flexWrap: "nowrap" };
          if (shape === "bar") {
            st.background = COLORS.bgDeep;
            st.padding = "6px 4px";
            if (pos === "top") { st.top = 0; st.left = 0; st.right = 0; st.borderBottom = `1px solid ${COLORS.goldSoft}`; }
            else if (pos === "left") { st.left = 0; st.top = 0; st.bottom = 0; st.borderRight = `1px solid ${COLORS.goldSoft}`; st.overflowY = "auto"; st.justifyContent = "center"; }
            else if (pos === "right") { st.right = 0; st.top = 0; st.bottom = 0; st.borderLeft = `1px solid ${COLORS.goldSoft}`; st.overflowY = "auto"; st.justifyContent = "center"; }
            else { st.bottom = 0; st.left = 0; st.right = 0; st.borderTop = `1px solid ${COLORS.goldSoft}`; }
          } else {
            st.padding = 6;
            st.borderRadius = 18;
            if (shape === "pill") { st.background = COLORS.panel; st.border = `1px solid ${COLORS.goldSoft}`; st.boxShadow = "0 6px 18px rgba(0,0,0,.18)"; }
            if (pos === "top") { st.top = 8; st.left = "50%"; st.transform = "translateX(-50%)"; st.maxWidth = "calc(100% - 16px)"; }
            else if (pos === "left") { st.left = 8; st.top = "50%"; st.transform = "translateY(-50%)"; st.maxHeight = "80%"; st.overflowY = "auto"; }
            else if (pos === "right") { st.right = 8; st.top = "50%"; st.transform = "translateY(-50%)"; st.maxHeight = "80%"; st.overflowY = "auto"; }
            else { st.bottom = 8; st.left = "50%"; st.transform = "translateX(-50%)"; st.maxWidth = "calc(100% - 16px)"; }
          }
          // Kur desktopi e zgjedh vetë `classic`, ai `lg:hidden` do ta fshihte
          // pikërisht atë që sapo u kërkua. Klasa mbetet vetëm për telefonin.
          return <div className={isWideScreen ? "" : "lg:hidden"} style={st}>{renderTreeBarIcons(undefined, !vert)}</div>;
        })()}
        {/* PANELET U ZHVENDOSËN JASHTË KËSAJ ENE — 29 gusht 2026.
            Pyetja e pronarit e zgjidhi rastin: «pse dritaret e tjera janë në
            rregull kurse kjo jo?». Sepse kjo e vetmja renderohej BRENDA
            kontejnerit të pemës (`containerRef`), i cili ka `overflow:hidden`,
            `touch-action:none` dhe merr gjeste zmadhimi e zhvendosjeje. Të
            gjitha të tjerat — cilësimet e pemës, menyja e avatarit, qendra e
            familjeve — renderohen te niveli i guaskës.
            Prandaj asnjë ndryshim te vetë `Modal` nuk e prekte dot: dritarja
            ishte e njëjta, vendi ku jetonte ishte tjetër. Shih më poshtë, te
            `renderTreeBarPanels()` pranë dritareve të tjera. */}
        {/* E njëjta shprehje si te vijat lidhëse (shih `lines.push` më poshtë):
            dizajni aktiv → COLORS.gold; përndryshe cilësimi i admin-it. */}
        {!locked && peopleCount > 0 && treeNavVariant === "drawer" && (
          <div ref={treeDrawerRef}>
            {/* FIX (kërkesë e userit): (a) gjuhëza merr NGJYRËN E VIJAVE që
                lidhin personat në pemë — nëse vijat janë jeshile, edhe ajo
                jeshile; (b) punon me KLIK dhe me SWIPE; (c) swipe-i mbi të
                NUK bën kurrë "back" — vetëm hap/mbyll gjuhëzën, dhe gjesti
                ndalet këtu (stopPropagation), pa prekur asnjë cilësim. */}
            <button
              onClick={() => setTreeDrawerOpen((v) => !v)}
              onTouchStart={(e) => { e.stopPropagation(); handleTouchX.current = e.touches[0].clientX; }}
              onTouchMove={(e) => { e.stopPropagation(); }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                if (handleTouchX.current == null) return;
                const dx = e.changedTouches[0].clientX - handleTouchX.current;
                handleTouchX.current = null;
                if (dx < -25) setTreeDrawerOpen(true);        // tërhiq majtas → hap
                else if (dx > 25) setTreeDrawerOpen(false);   // shtyj djathtas → mbyll (jo back)
              }}
              className="absolute z-20 flex items-center justify-center shadow-lg"
              style={{ right: treeDrawerOpen ? 64 : 0, top: "50%", transform: "translateY(-50%)", width: 22, height: 60, background: treeHandleColor, border: `1px solid ${treeHandleColor}`, borderRight: "none", borderRadius: "10px 0 0 10px", transition: "right 0.25s ease", touchAction: "none" }}
              title={t("Tree Tools")}
            >
              <ChevronLeft size={16} color={COLORS.bgDeep} style={{ transform: treeDrawerOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </button>
            {/* FIX (kërkesë e userit — "kur tërheq, s'duhet të dalë faqe e
                madhe, vetëm ikonat; pastaj kur klikon një ikonë specifike,
                ATËHERË mund të shfaqet ajo e madhja") — kolonë e NGUSHTË
                (64px) me VETËM ikona, njësoj si rreshti i "fab"-it — çdo
                ikonë hap VETËM interaksionin e vet (kërkim brenda linje,
                panel i vogël "Shiko"), jo më një panel të gjatë të vetëm
                me eticketa gjithmonë të hapura. */}
            {treeDrawerOpen && (
              <div
                className="absolute right-0 top-0 bottom-0 z-20 shadow-2xl py-4 flex flex-col items-center gap-3 overflow-y-auto"
                style={{ width: 64, background: COLORS.panel, borderLeft: `1px solid ${COLORS.goldSoft}` }}
                onTouchStart={(e) => { drawerTouchStartX.current = e.touches[0].clientX; }}
                onTouchMove={(e) => {
                  if (drawerTouchStartX.current == null) return;
                  if (e.touches[0].clientX - drawerTouchStartX.current > 60) { setTreeDrawerOpen(false); drawerTouchStartX.current = null; }
                }}
                onTouchEnd={() => { drawerTouchStartX.current = null; }}
              >
                {/* Ikonat e konfigurueshme — i njëjti burim si çdo bar tjetër.
                    Kërkimi/Pamja hapen si panele FIKSE (renderTreeBarPanels),
                    kurrë brenda kësaj kolone me overflow (fix i clipping-ut). */}
                {renderTreeBarIcons()}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {!locked && !provaNga(3) && activeScreen === "shto" && (() => {
      // «eh» = «e ka Heirloom-in». Një emër i shkurtër sepse përsëritet shtatë
      // herë brenda kartelës; një `pamja(...)` te secili do ta bënte rreshtin
      // të palexueshëm.
      const ehShto = pamja("shto", "heirloom");
      return (
        <div className="flex-1 overflow-y-auto px-4 py-4"
             style={{ background: `radial-gradient(circle at 20% 10%, ${KART.bgDeep}, ${KART.bg})` }}>
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
          {/* Titulli i faqes — jo titull dritareje. Vija poshtë tij është ajo
              e mockup-it, dhe njëkohësisht ajo që e bën këtë të duket faqe:
              një dritare ka shirit me X, një faqe ka kokë. */}
          <h2 style={{
            ...tip("faqja", { fontFamily: FONTS.display, fontSize: 26, fontWeight: 600, lineHeight: 1.1, color: COLORS.ink }),
            // Mbi sfondin e faqes (bg/bgDeep) boja është gjithmonë `onBg` — roli
            // «faqja» i dizajnit sjell `text`, që është për panel të çelët.
            color: KART.onBg,
            margin: "2px 0 12px", paddingBottom: 12,
            borderBottom: `1px solid ${PALETA_TIPI.accentBorder || COLORS.goldSoft}`,
          }}>{t("What would you like to add?")}</h2>
          <div className="flex flex-col" style={{ gap: ehShto ? 11 : 8 }}>
            {[
              {
                çelësi: "person",
                Ikona: Users,
                titulli: t("Add person"),
                nën: t("A new member of the family — name, dates, place, and who they belong to."),
                shko: () => { mbyllShtimin(); openAdd(); },
              },
              {
                çelësi: "histori",
                Ikona: BookOpen,
                titulli: t("Add a story"),
                // Kur s'ka leje, nën-rreshti nuk përsërit se ç'është historia —
                // thotë pse s'hapet. Ajo është e vetmja gjë që i duhet aty.
                nën: canManage
                  ? t("An event with a date, a title and photos — it appears in History.")
                  : t("Only the family admin can add a story."),
                kyçur: !canManage,
                shko: () => { mbyllShtimin(); setFamilyHistoryForm({ id: null, eventDate: sotSiTekst(), title: "", description: "", saktesia: "dite" }); setFamilyHistoryPhotoFiles([]); setFamilyHistoryPhotoPreviews([]); setShtoHistoriOpen(true); },
              },
              // «Çdo gjë që shtohet, shtohet nga këtu» (pronari, 29 gusht 2026).
              // Skedarët e arkivës kishin mbetur jashtë: fusha e ngarkimit rri
              // te Historia, dhe kush s'e dinte, s'e gjente kurrë. Dera nuk e
              // zhvendos formularin — të çon te ai dhe ta vë kursorin aty, si te
              // «Shto histori». Një derë, dhe secili send te vendi i vet.
              ...(archiveEnabled ? [{
                çelësi: "arkiva",
                Ikona: Camera,
                titulli: t("Add a photo, a voice or a document"),
                nën: ["admin", "editor", "contributor"].includes(myRole)
                  ? t("It goes into the family history, with the date and the people in it.")
                  : t("Only members with permission can add files."),
                kyçur: !["admin", "editor", "contributor"].includes(myRole),
                shko: () => { mbyllShtimin(); setArchiveCaptionDraft(""); setShtoSkedarOpen(true); },
              }] : []),
              // Trashëgimia ishte buton i lirë te ballina — pra një gjë që
              // shtohet, e shtuar nga një vend tjetër. Këtu ka edhe një dallim
              // që vlen të thuhet me zë: kjo nuk i shtohet familjes sot, por
              // dikujt nesër. Prandaj nën-rreshti nuk e përsërit titullin.
              // «Çdo gjë që shtohet, shtohet nga këtu» vlen edhe për datën.
              // Deri sot ajo hyhej vetëm nga një buton i vogël te «Së shpejti»
              // — pra kush s'e kishte hapur atë skedë, s'e dinte se ekziston.
              ...(currentPersonId ? [{
                çelësi: "data",
                Ikona: Bell,
                titulli: t("Add an important date"),
                nën: t("A day that comes back every year — it appears in «Upcoming», for you."),
                shko: () => { mbyllShtimin(); setImpKujt("une"); setImpModalOpen(true); },
              }] : []),
              ...(currentPersonId ? [{
                çelësi: "trashegimi",
                Ikona: Heart,
                titulli: t("Your Legacy"),
                nën: t("A message you leave behind — it is opened at the time you choose."),
                shko: () => { mbyllShtimin(); openMyLegacy(currentPersonId); },
              }] : []),
            ].map(({ çelësi, Ikona, titulli, nën, shko, kyçur }) => (
              <button
                key={çelësi}
                onClick={kyçur ? undefined : shko}
                disabled={!!kyçur}
                aria-disabled={!!kyçur}
                className="flex items-start gap-3 text-left w-full"
                style={ehShto
                  /* Kartela e mockup-it: e bardhë mbi krem, kufi ari flokëhollë,
                     hije shumë e butë — jo sfond kremi me kufi të fortë. */
                  ? { background: PALETA_TIPI.surface || COLORS.panel,
                      border: `1px solid ${PALETA_TIPI.accentBorder || COLORS.goldSoft}`,
                      borderRadius: 15, padding: 15,
                      boxShadow: "0 8px 20px -18px rgba(44,38,37,.3)",
                      cursor: kyçur ? "default" : "pointer", opacity: kyçur ? 0.6 : 1 }
                  : { background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`,
                      borderRadius: 12, padding: 14,
                      cursor: kyçur ? "default" : "pointer", opacity: kyçur ? 0.6 : 1 }}
              >
                <span
                  className="shrink-0 flex items-center justify-center rounded-full"
                  style={ehShto
                    ? { width: 46, height: 46, background: PALETA_TIPI.surface2 || COLORS.accentTint }
                    : { width: 38, height: 38, background: COLORS.accentTint }}
                >
                  <Ikona size={ehShto ? 21 : 18} color={ehShto ? (PALETA_TIPI.bronze || COLORS.gold) : COLORS.gold} />
                </span>
                <span className="min-w-0" style={{ flex: 1 }}>
                  <span className="block" style={ehShto
                    ? { fontFamily: FONTS.display, fontSize: 19, fontWeight: 600, lineHeight: 1.1, color: COLORS.ink }
                    : { fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 600, color: COLORS.ink }}>{titulli}</span>
                  <span className="block" style={ehShto
                    ? { fontFamily: FONTS.body, fontSize: 12.5, color: PALETA_TIPI.text2 || COLORS.inkLight, lineHeight: 1.4, marginTop: 3 }
                    : { fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight, lineHeight: 1.45, marginTop: 2 }}>{nën}</span>
                </span>
                {kyçur && <span className="shrink-0" style={{ fontSize: 13, color: COLORS.ink }}>🔒</span>}
              </button>
            ))}
          </div>
          </div>
        </div>
      ); })()}

      {!locked && !provaNga(3) && activeScreen === "home" && (
        <div className="flex-1 overflow-y-auto px-4 py-4" style={{ background: `radial-gradient(circle at 20% 10%, ${KART.bgDeep}, ${KART.bg})` }}>
          {/* FAZA 30 — Dashboard për desktop (lg+): karta statistikash +
              pamje e shpejtë e pemës, siç u kërkua ("si Admin Center", por
              me temën e familjes). Në telefon, e njëjta të dhëna paraqiten
              si Feed (poshtë, lg:hidden) — më e përshtatshme për ekran të
              vogël, ku lista vertikale funksionon më mirë se karta të gjera. */}
          {/* Home i riorganizuar: FEED-FIRST me shirit anësor. Rrjet 2-kolonësh
              në desktop: feed-i (lg:order-1) majtas; statistikat/"së shpejti"/
              pema te shiriti anësor djathtas (lg:order-2). Në telefon, shiriti
              fshihet dhe mbetet vetëm feed-i. */}
          <div className="lg:flex lg:gap-6 lg:items-start" style={{ maxWidth: 1120, margin: "0 auto" }}>
          <div className="hidden lg:block lg:order-2 lg:w-80 lg:shrink-0">
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="rounded-xl p-4" style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}` }}>
                <p style={{ fontFamily: FONTS.display, fontSize: 26, fontWeight: 600, color: KART.ink }}>{peopleCount}</p>
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: KART.inkLight }}>{t("Members")}</p>
              </div>
              <div className="rounded-xl p-4" style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}` }}>
                <p style={{ fontFamily: FONTS.display, fontSize: 26, fontWeight: 600, color: KART.ink }}>{generationsCount}</p>
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: KART.inkLight }}>{t("Generations")}</p>
              </div>
              <div className="rounded-xl p-4" style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}` }}>
                <p style={{ fontFamily: FONTS.display, fontSize: 26, fontWeight: 600, color: KART.ink }}>{archiveItems.filter((i) => i.file_type === "photo").length + familyHistoryEvents.reduce((sum, ev) => sum + (ev.photo_path ? 1 : 0) + (ev.photo_paths || []).length, 0)}</p>
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: KART.inkLight }}>{t("Photo")}</p>
              </div>
              <div className="rounded-xl p-4" style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}` }}>
                <p style={{ fontFamily: FONTS.display, fontSize: 26, fontWeight: 600, color: KART.ink }}>{familyHistoryEvents.length + unlockedLegacyMessages.length}</p>
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: KART.inkLight }}>{t("History")}</p>
              </div>
            </div>
            {(feedUpcoming.length > 0 || ["admin", "editor"].includes(myRole)) && (
              <div className="mb-6">
                <div className="mb-2 flex items-center justify-between">
                  <p style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.onBg, opacity: 0.8 }}>{t("Coming soon")}</p>
                  {["admin", "editor"].includes(myRole) && (
                    <button onClick={() => setImpModalOpen(true)} style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: KART.gold, background: "transparent", border: "none", cursor: "pointer" }}>＋ {t("Important date")}</button>
                  )}
                </div>
                {feedUpcoming.length > 0 && (
                  <div className="grid grid-cols-1 gap-2">
                    {feedUpcoming.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-md px-3 py-2" style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}` }}>
                        <span onClick={() => item.personId && setSelectedId(item.personId)} style={{ cursor: "pointer", display: "inline-flex" }}>
                          <IkonaP size={18} color={KART.inkLight}
                            Lucide={item.type === "birthday" ? Cake : item.type === "important" ? CalendarDays : item.type === "wedding" ? Heart : Flame}
                            emoji={item.type === "birthday" ? "🎂" : item.type === "important" ? "📅" : item.type === "wedding" ? "💍" : "🕯️"} />
                        </span>
                        <span onClick={() => item.personId && setSelectedId(item.personId)} style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.ink, flex: 1, cursor: "pointer" }}>{item.label}</span>
                        <span style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: KART.inkLight, fontVariantNumeric: "tabular-nums" }}>{item.daysUntil === 0 ? t("today") : item.daysUntil === 1 ? t("tomorrow") : `${item.daysUntil}d`}</span>
                        <button onClick={() => suggestForOccasion(item)} title={t("Suggest a message")} style={{ fontSize: 14, lineHeight: 1, padding: "2px 4px", background: "transparent", border: "none", cursor: "pointer" }}>✨</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}` }}>
              <div>
                <p style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 600, color: KART.ink }}>{t("The Family Tree")}</p>
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: KART.inkLight }}>{t("See the full structure, search someone, or add a new member.")}</p>
              </div>
              <button onClick={() => setActiveScreen("tree")} className="px-4 py-2 rounded-md text-sm font-medium shrink-0" style={{ background: KART.gold, color: KART.bgDeep }}>{t("Open the Tree")}</button>
            </div>
          </div>

          <div className="lg:order-1 lg:flex-1 min-w-0">
          {/* Kolona kryesore — feed-i (tani i dukshëm edhe në desktop). */}

          {/* BALLINA1 (28 gusht 2026) — një buton veprimi para skedave. Deri
              sot ballina tregonte çka kishin bërë të tjerët dhe nuk të linte të
              bëje asgjë vetë.

              «+ Shto» hap listën e vetme të shtimit (shih `hapShtimin`), jo
              drejt formularin e personit. Dhe «Shto histori» prej saj nuk hap
              një formular të dytë — hap TË NJËJTIN ekran të ngjarjeve. Një
              formular i dytë do të thoshte dy vende ku shkruhet e njëjta
              ngjarje, dhe brenda një muaji dy të vërteta. */}
          {/* BALLINA4 — pilula ndizet/fiket te Admin Center, DHE fiket vetvetiu
              kur «Shto» rri te shiriti poshtë: atëherë do të ishin dy dyer për
              të njëjtën listë, njëra mbi tjetrën në të njëjtin ekran. */}
          {homeAddButton && !(hyrjetEShiritit() || []).includes("shto")
            && ["admin", "editor", "contributor"].includes(myRole) && peopleCount > 0 && (
            <div className="flex items-center gap-2 mb-3 px-1 pt-1 flex-wrap">
              <button onClick={() => hapShtimin()} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium" style={{ background: KART.gold, color: KART.bgDeep }}>
                <Plus size={15} /> {t("Add")}
              </button>
            </div>
          )}

          {/* FAZA 33 — Ndërrues Feed / Kujtime / Legacy, zëvendëson listën e
              vetme të bashkuar — tani mund të filtrosh çka sheh.
              BALLINA1 — lista tani vjen nga cilësimi (Admin Center), me
              parazgjedhje pikërisht të tri skedat e vjetra. Rrëshqet anash, që
              një skedë e katërt të mos e thyejë rreshtin në telefon. */}
          {(() => {
            // BALLINA4 — katër renditje për të njëjtat skeda. Ndryshon vetëm
            // forma; lista, radha dhe përmbajtja janë të njëjtat te të katërta.
            //
            //   pilula      pilula në një rresht që rrëshqet (si sot)
            //   nje_rresht  drejtkëndësha me gjerësi të barabartë, pa rrëshqitje
            //   dy_rreshta  drejtkëndësha, të ndarë saktësisht në dy rreshta
            //   ikona       drejtkëndësha me ikonë mbi emër
            const lista = skedatEBallines();
            const st = homeTabsStyle || "pilula";
            const zgjedhur = (key) => homeTab === key;

            // SKEDA E PAZGJEDHUR — ngjyra e kokës, jo një e bardhë e holluar.
            //
            // 29 gusht 2026. Pronari: «kjo e kuqja e tabs nuk po më pëlqen, se
            // më shumë është lejla se e kuqe — të mund ta përafroj me ngjyrën e
            // hederit».
            //
            // Kishte të drejtë, dhe shkaku ishte matematikë, jo shije: sfondi
            // ishte `rgba(255,255,255,0.06)` — E BARDHË mbi të kuqen. E bardha i
            // ngre TË TRIA kanalet me të njëjtën sasi, dhe mbi një të kuqe të
            // errët (110,20,35) kjo do të thotë se e gjelbra dhe e kaltra
            // rriten PROPORCIONALISHT shumë më tepër se e kuqja. Rezultati
            // rrëshqet drejt vjollcës — pikërisht «lejla».
            //
            // Zgjidhja nuk është një e bardhë më e dobët; është të mos përzihet
            // fare një ngjyrë e huaj. `bgDeep` është vetë ngjyra e kokës, pra
            // skeda e pazgjedhur duket si vazhdim i saj dhe hue-ja mbetet e
            // familjes, jo e llogaritjes.
            // SISTEMI VIZUAL — skeda joaktive rri mbi `bgDeep`, ndaj teksti i saj
            // ndjek shiritin (`onBgDeep`), jo dyshemenë (`onBg`). Te klasikja janë
            // e njëjta ngjyrë; te «Letra» dyshemeja është letër dhe shiriti
            // burgundy, dhe pa këtë skeda joaktive do të kishte tekst të errët
            // mbi burgundy. Pesha 500 në vend të 400: 14px joaktiv me 400 mbi
            // sfond të errët humbet.
            // Te pamja «heirloom» skedat janë pilula të bardha me kufi ari dhe
            // shkrim të errët; e zgjedhura mbushet me terrakotë. Ndryshimi nuk
            // është vetëm ngjyra: te klasikja skeda e pazgjedhur rri mbi
            // `bgDeep` (vazhdim i kokës), kurse këtu rri mbi letër dhe duhet
            // sipërfaqe e vetën, përndryshe zhduket.
            const ehFeed = pamja("feed", "heirloom");
            const stiliBaze = (key) => (ehFeed ? {
              background: zgjedhur(key) ? (PALETA_TIPI.accent || KART.gold) : (PALETA_TIPI.surface || KART.panel),
              color: zgjedhur(key) ? (PALETA_TIPI.bg || "#FAF4E9") : (PALETA_TIPI.text || KART.ink),
              border: `1px solid ${zgjedhur(key) ? (PALETA_TIPI.accent || KART.gold) : (PALETA_TIPI.accentBorder || KART.goldSoft)}`,
              fontFamily: FONTS.body,
              fontWeight: zgjedhur(key) ? 600 : 500,
              opacity: 1,
            } : {
              background: zgjedhur(key) ? KART.gold : KART.bgDeep,
              color: zgjedhur(key) ? KART.bgDeep : (COLORS.onBgDeep || KART.onBg),
              border: `1px solid ${zgjedhur(key) ? KART.gold : KART.goldSoft}`,
              fontFamily: FONTS.body,
              fontWeight: zgjedhur(key) ? 600 : 500,
              opacity: zgjedhur(key) ? 1 : 0.92,
            });

            if (st === "pilula") {
              return (
                <div className="flex items-center gap-2 mb-4 px-1 pt-1 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                  {lista.map(([key, label]) => (
                    <button key={key} onClick={() => setHomeTab(key)}
                      className="px-3.5 py-1.5 rounded-full text-sm shrink-0"
                      style={stiliBaze(key)}>{label}</button>
                  ))}
                </div>
              );
            }

            // Sa për rresht. «dy_rreshta» e ndan listën në gjysmë duke rrumbullakosur
            // lart — katër skeda dalin 2+2, pesë dalin 3+2. Kështu rreshti i parë
            // nuk mbetet kurrë më i shkurtër se i dyti, gjë që duket si gabim.
            const perRresht = st === "dy_rreshta" ? Math.ceil(lista.length / 2) : lista.length;

            return (
              <div className="mb-4 px-1 pt-1"
                style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(1, perRresht)}, minmax(0, 1fr))`, gap: 6 }}>
                {lista.map(([key, label]) => {
                  const Ikona = HOME_TAB_IKONA[key];
                  return (
                    <button key={key} onClick={() => setHomeTab(key)}
                      className="rounded-lg"
                      style={{
                        ...stiliBaze(key),
                        display: "flex", flexDirection: st === "ikona" ? "column" : "row",
                        alignItems: "center", justifyContent: "center", gap: st === "ikona" ? 3 : 0,
                        padding: st === "ikona" ? "9px 6px" : "10px 8px",
                        fontSize: st === "ikona" ? 12.5 : 14,
                        lineHeight: 1.15, minWidth: 0,
                      }}>
                      {st === "ikona" && Ikona ? <Ikona size={17} /> : null}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* BALLINA2 — kartela hyrëse. Nga dizajni i pronarit (28 gusht).
              Ballina hapej me një listë ngjarjesh; tani hapet me atë që është
              vërtet aplikacioni — rrëfimi i familjes — dhe me derën për ta
              dëgjuar.

              KARTELA SHFAQET VETËM KUR TURNEU ËSHTË GATI (së paku tri hapa).
              Provova një variant që, kur turneu s'ishte gati, e kthente butonin
              te «Hap Pemën» — dhe ishte e gabuar: një kartelë e madhe që
              premton një rrëfim dhe pastaj hap thjesht pemën, mbi një kartelë
              tjetër pak më poshtë që bën saktësisht të njëjtën gjë. Kur s'ka
              rrëfim për të treguar, më mirë asgjë. */}
          {homeTab === "feed" && turneHapat.length >= 3 && (
            <div className="mb-4" style={{
              background: `linear-gradient(140deg, ${KART.panel}, ${KART.panelSoft})`,
              border: `1px solid ${KART.goldSoft}`, padding: "20px 20px 18px", borderRadius: 12, boxShadow: KARTELA_HIJE,
            }}>
              {/* KONTRASTI: sipërfaqja e kësaj kartele nis me `panel` (#F6F2E7).
                  `onBg` te tema klasike është PIKËRISHT ai kod ngjyre — pra
                  teksti nuk ishte i zbehtë, ishte i padukshëm. Bojë `ink`. */}
              <p style={{ ...kartelaEtikete(KART.inkLight), margin: 0 }}>{t("The family's story")}</p>
              <p style={{ fontFamily: FONTS.display, fontSize: 26, fontWeight: 600, color: KART.ink, margin: "8px 0 0", lineHeight: 1.15 }}>
                {t("Our story goes on…")}
              </p>
              <p style={{ fontFamily: FONTS.body, fontSize: 15.5, color: KART.ink, opacity: 0.85, lineHeight: 1.55, margin: "8px 0 0" }}>
                {t("A story built from the people, the places, the memories and the moments that the {{family}} family has kept over the years.", { family: familyDisplayName || t("The Family") })}
              </p>
              <button
                onClick={nisTurneun}
                className="w-full flex items-center justify-center gap-2 rounded-xl mt-4"
                style={{ background: KART.gold, color: KART.bgDeep, border: "none", padding: "11px 16px", fontFamily: FONTS.body, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >
                ▶ {t("Present the story")}
              </button>
            </div>
          )}

          {(() => {
            // BALLINA1 — anekdota/fjalë e urtë/barcoletë dalin nga feed-i VETËM
            // kur ekziston Oda ku të shkojnë. Ndryshe do të zhdukeshin fare për
            // këdo që s'e ka konfiguruar ende ballinën, dhe një veçori që
            // zhduket në heshtje lexohet si defekt.
            const odaAktive = skedatEBallines().some(([k]) => k === "oda");
            const urtesiaKetu = homeTab === "oda" || (homeTab === "feed" && !odaAktive);
            // ODA1 — te faqja e vet hapur, te feed-i mbledhur. Shih shënimin te
            // `urtesiaEPrekur`.
            const urtesiaHapetVete = homeTab === "oda";
            // Hyrja e Odës: thotë çfarë është kjo faqe dhe se rinovohet çdo ditë.
            // Pa të, tri karta pa kontekst duken si mbetje e feed-it; me të,
            // faqja ka premtim — dhe një premtim i përditshëm është arsye kthimi.
            // NGJYRAT — rregulluar më 29 gusht 2026, pas «jo vetëm që nuk është
            // shkruar drejt, por edhe është e palexueshme».
            //
            // E kisha bërë sfondin `linear-gradient(panel → accentTint)` dhe
            // tekstin `onBg`. Ato dy nuk shkojnë bashkë: `onBg` do të thotë
            // «teksti që rri mbi sfondin e thellë», pra te kjo temë është i
            // çelët — kurse `panel` është sipërfaqe e çelët. Në gjysmën e parë
            // të gradientit dilte i çelët mbi të çelët, domethënë asgjë.
            //
            // Rregulli që e shmang këtë përgjithmonë: teksti merret nga i njëjti
            // çift me sipërfaqen poshtë tij. Mbi `panel` → `ink`. Mbi sfondin e
            // thellë ose mbi `accentTint` → `onBg`.
            //
            // Këtu zgjidhja e drejtë ishte e dukshme: kjo kartelë prezanton tri
            // kartelat menjëherë poshtë saj, dhe ATO përdorin `accentTint` me
            // `onBg`. Pra e njëjta sipërfaqe — lexohet, dhe duket si familje me
            // atë që prezanton, jo si copë e ngjitur sipër.
            // MINIMALE — kërkesë e pronarit, 29 gusht 2026: «baneri të jetë pak
            // më minimalist».
            //
            // E kisha nisur si kartelë me titull të madh, përshkrim dhe tri
            // pilula me ikona. Por hyrja e një faqeje nuk garon me përmbajtjen e
            // saj: ajo thotë çfarë je duke parë dhe hiqet nga mesi. Tri pilulat
            // që thoshin «Një anekdotë · Një fjalë e urtë · Një barcoletë»
            // përsërisnin fjalë për fjalë etiketat e tri kartelave menjëherë
            // poshtë tyre — pra zinin vend duke mos shtuar asgjë.
            //
            // Mbeti një rresht. Ai mban të gjithë kuptimin që kërkoi pronari:
            // çfarë postohet, dhe se nesër ndryshon.
            const hyrjaEOdes = (
              <p style={{
                fontFamily: FONTS.body, fontSize: 14, color: KART.onBg, opacity: 0.8,
                lineHeight: 1.5, margin: "0 2px 12px",
              }}>
                {t("Each day in the Oda: one anecdote, one proverb and one joke — tomorrow, three more.")}
              </p>
            );
            const isEmptyOverall = feedUpcoming.length === 0 && feedRecent.length === 0;

            // BALLINA7 — po i njëjti trup si te ekrani «Statistika». Pa titull,
            // sepse skeda e sapo prekur e ka thënë emrin.
            if (homeTab === "stats") return renderStatistikat(false);

            if (homeTab === "oda" && feedWisdom.length === 0) {
              // Edhe kur s'ka ende asgjë, hyrja mbetet: ajo e shpjegon boshllëkun.
              // Pa të, faqja e zbrazët duket e prishur; me të, duket e re.
              return (
                <div className="max-w-xl mx-auto">
                  {hyrjaEOdes}
                  <div className="flex flex-col items-center justify-center text-center px-6 py-8">
                    <p style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.onBg, opacity: 0.7 }}>{t("The Oda is quiet today. Anecdotes, proverbs and jokes appear here.")}</p>
                  </div>
                </div>
              );
            }

            if (homeTab === "feed" && isEmptyOverall && (odaAktive || feedWisdom.length === 0)) {
              return (
                <div className="flex flex-col items-center justify-center text-center px-6 py-10">
                  <p style={{ fontFamily: FONTS.display, fontSize: 17, color: KART.onBg, marginBottom: 6 }}>{t("Welcome")}</p>
                  <p style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.onBg, opacity: 0.7 }}>{t("Nothing new here yet — when the family adds memories, photos, or Legacy, you'll see them right here.")}</p>
                </div>
              );
            }

            // SESHPEJTI1 — kthimi i hershëm «nuk ka asgjë» u hoq. Ai e ndalonte
            // faqen para kutizave dhe para butonit «＋ Datë e rëndësishme» —
            // pra pikërisht kur s'kishte asgjë, largohej edhe e vetmja mënyrë
            // për të shtuar diçka. Boshllëku thuhet brenda listës, jo në vend
            // të saj.

            return (
            <div className="space-y-4 max-w-xl mx-auto">
              {homeTab === "oda" && feedWisdom.length > 0 && hyrjaEOdes}
              {urtesiaKetu && feedWisdom.length > 0 && (
                <div className="space-y-2">
                  {feedWisdom.map((w) => {
                    // PERKTHIMI2 — etiketa vjen e gatshme nga dera, në gjuhën e
                    // kërkuar. Më parë zgjidhej këtu mes `label_sq` dhe
                    // `label_en`, dhe kjo do të thoshte se një lexues gjerman i
                    // shihte «Anekdota / Fjalë e urtë / Barcoletë» SHQIP, edhe
                    // pse gjithçka rreth tyre ishte gjermanisht.
                    const label = w.out_label || "";
                    // Baza fiton; harta e vjetër është rezervë për rastin kur FJALET1
                    // s'është xhiruar ende; «✨» për çdo rubrikë që s'ka zgjedhur ikonë.
                    // SISTEMI VIZUAL (2 shtator 2026) — tri rubrikat e njohura
                    // marrin ikona Lucide, jo emoji. Emoji vizatohen ndryshe në
                    // çdo sistem, kanë ngjyra që s'i kontrollojmë, dhe lexohen si
                    // «chat», jo si arkiv. Ikona e admin-it (`urtesiaIkonat`) mbetet
                    // vetëm për rubrika të reja, që s'kanë ende ikonë të vetën.
                    // IKONAT1 — në modalitetin Emoji del emoji-ja që ka zgjedhur
                    // admini te rubrika (ose parazgjedhja); në Lucide, ikona e
                    // vijës. Rubrikat e reja pa ikonë Lucide mbeten me emoji-n e tyre.
                    const IkonaRubrike = ({ anecdote: MessageSquareQuote, proverb: ScrollText, joke: Smile })[w.out_key] || null;
                    const emojiRubrike = urtesiaIkonat[w.out_key] || ({ anecdote: "🗣️", proverb: "📜", joke: "😄" })[w.out_key] || "✨";
                    const icon = <IkonaP Lucide={IkonaRubrike} emoji={emojiRubrike} size={16} color={KART.inkLight} />;
                    // Titulli ndahet nga trupi VETËM kur ka më shumë se një rresht
                    // dhe i pari nuk është replikë: një fjalë e urtë me një rresht
                    // të vetëm është trup, jo titull.
                    const odaRreshtat = odaMeRreshta(w.out_text).split("\n");
                    const odaKaTitull = odaRreshtat.length > 1 && !/^[—–]/.test(odaRreshtat[0]);
                    const odaTitulli = odaKaTitull ? odaRreshtat[0] : null;
                    const odaTrupi = (odaKaTitull ? odaRreshtat.slice(1) : odaRreshtat).join("\n");
                    // «E prekur» do të thotë e kthyer nga parazgjedhja e vendit,
                    // jo «e hapur» — prandaj i njëjti klikim funksionon te të dyja.
                    const open = urtesiaEPrekur.has(w.out_key) ? !urtesiaHapetVete : urtesiaHapetVete;
                    // PERKTHIMI2 — «EN» pranë emrit të rubrikës, siç e kërkoi
                    // pronari. Një prekje i përkthen të TRIA njëherësh, sepse
                    // funksioni i merr të gjitha rubrikat e ditës që i mungojnë
                    // asaj gjuhe — pra edhe butonat e dy kartelave të tjera
                    // zhduken bashkë me këtë.
                    const odaMungon = !!(w.out_gjuha && w.out_gjuha !== gjuhaIme);
                    return (
                      // KARTELA1 — kartelat e Odës ishin të vetmet e shkruara
                      // drejtpërdrejt me `accentTint`/`onBg`. Tani e marrin
                      // çiftin nga paleta, si të gjitha të tjerat.
                      <div key={w.out_key} style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}`, borderRadius: 12, boxShadow: KARTELA_HIJE, overflow: "hidden" }}>
                        {/* Rreshti i kokës NUK është më një buton i vetëm: një
                            buton brenda një butoni nuk është HTML i vlefshëm,
                            dhe shfletuesit e zgjidhin secili si t'u teket.
                            Emri majtas, «EN» pas tij, dhe pjesa e mbetur djathtas
                            mbetet zona që hap e mbyll. */}
                        <div className="w-full flex items-center gap-2" style={{ padding: "14px 20px 8px" }}>
                          {/* Etiketa: Work Sans 12.5 / 600 / shkronja të mëdha — jo mono.
                              Mono në 11px ishte shenja e dashboard-it. */}
                          <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: KART.inkLight, display: "flex", alignItems: "center", gap: 7 }}>{icon} {label}</span>
                          {odaMungon && (
                            odaGabim ? (
                              <span title={odaGabim} style={{ fontFamily: FONTS.body, fontSize: 12.5, color: KART.inkLight, cursor: "help" }}>⚠</span>
                            ) : (
                              <button
                                onClick={perktheOden}
                                disabled={odaDuke}
                                title={t("Translate this post")}
                                className="shrink-0 px-1.5 rounded"
                                style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", lineHeight: "18px",
                                         color: KART.etiketa, background: "transparent", border: `1px solid ${COLORS.goldSoft}`,
                                         cursor: odaDuke ? "default" : "pointer", opacity: odaDuke ? 0.5 : 1 }}>
                                {odaDuke ? "…" : gjuhaIme.slice(0, 2).toUpperCase()}
                              </button>
                            )
                          )}
                          <button
                            onClick={() => toggleWisdom(w.out_key)}
                            aria-expanded={open}
                            className="flex-1 flex items-center justify-end"
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px 0" }}
                          >
                            <ChevronDown size={18} color={KART.inkLight} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flexShrink: 0 }} />
                          </button>
                        </div>
                        {open && (
                          // Hierarkia: rubrika (e vogël, e qetë) → titulli (serif,
                          // 18) → trupi (16/1.6, elementi më i madh i kartelës).
                          // Përmbajtja fiton jo sepse është e trashë, por sepse
                          // është e madhe dhe e qetë.
                          <div style={{ padding: "0 20px 18px", display: "flex", flexDirection: "column", gap: 6 }}>
                            {odaTitulli && (
                              <p style={{ ...tip("titulli", { fontFamily: FONTS.display, fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: KART.ink }), margin: 0 }}>{odaTitulli}</p>
                            )}
                            <p style={{ fontFamily: FONTS.body, fontSize: 16, color: KART.ink, lineHeight: 1.6, whiteSpace: "pre-wrap", margin: 0 }}>{odaTrupi}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {homeTab === "upcoming" && (() => {
                /* SESHPEJTI1 — KUTIZAT.
                   Kërkesë e pronarit: secili zgjedh vetë çfarë do të shohë.
                   Numri pranë secilës nuk është dekor — pa të, «Përkujtime 0»
                   duket si kutizë e prishur, jo si javë pa përvjetorë. */
                const LLOJET = [
                  ["birthday", t("Birthdays"), "🎂"],
                  ["wedding", t("Wedding anniversaries"), "💍"],
                  ["anniversary", t("Remembrances"), "🕯️"],
                  ["important", t("Other dates"), "📅"],
                ];
                const numrat = feedUpcoming.reduce((acc, x) => { acc[x.type] = (acc[x.type] || 0) + 1; return acc; }, {});
                const dukshem = feedUpcoming.filter((x) => seShpejtiFiltrat[x.type] !== false);
                const teGjitha = LLOJET.every(([k]) => seShpejtiFiltrat[k] !== false);
                return (
                <div>
                  {/* Titulli «SË SHPEJTI» u hoq: skeda mbi të e thotë tashmë atë
                      fjalë, dhe një etiketë që përsërit skedën e sapo prekur zë
                      rreshtin e parë pa shtuar asgjë. Mbeten dy butona të një
                      forme — një për të parë më pak, një për të shtuar. */}
                  <div className="px-1 mb-3 flex flex-wrap items-center gap-2">
                    {/* FILTRI, i mbledhur. E thotë gjendjen kur ajo NUK është
                        «të gjitha» — përndryshe dikush do të fikte një lloj, do
                        ta harronte, dhe javën tjetër do të raportonte se
                        ditëlindjet «u zhdukën». Një filtër i fshehur duhet ta
                        shpallë veten. */}
                    <button
                      onClick={() => setFiltratEHapur((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ background: teGjitha ? "transparent" : KART.accentTint, color: KART.onBg,
                               border: `1px solid ${teGjitha ? KART.goldSoft : KART.gold}`, cursor: "pointer" }}>
                      <span>⌕ {teGjitha ? t("Filter") : LLOJET.filter(([k]) => seShpejtiFiltrat[k] !== false).map(([, e]) => e).join(" · ")}</span>
                      <ChevronDown size={13} color={KART.onBg} style={{ transform: filtratEHapur ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                    </button>
                    {/* Parazgjedhja është «vetëm për mua» EDHE për adminin: një
                        datë e familjes u shfaqet tetëdhjetë vetave dhe nuk hiqet
                        dot prej tyre. Zgjedhje e vetëdijshme, jo gjendje. */}
                    {currentPersonId && (
                      <button
                        onClick={() => { setImpKujt("une"); setImpModalOpen(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                        style={{ background: "transparent", color: KART.onBg, border: `1px solid ${KART.goldSoft}`, cursor: "pointer" }}>
                        ＋ {t("Add a date")}
                      </button>
                    )}
                  </div>

                  {filtratEHapur && (
                    <div className="flex flex-wrap gap-1.5 mb-3 px-1">
                      <button
                        onClick={() => { const v = { birthday: true, wedding: true, anniversary: true, important: true }; setSeShpejtiFiltrat(v); try { localStorage.setItem("pef_seshpejti", JSON.stringify(v)); } catch (_e) { /* privat */ } setFiltratEHapur(false); }}
                        className="px-3 py-1.5 rounded-full text-xs font-medium"
                        style={{ background: teGjitha ? KART.gold : "transparent", color: teGjitha ? KART.bgDeep : KART.onBg, border: `1px solid ${teGjitha ? KART.gold : KART.goldSoft}`, cursor: "pointer" }}>
                        {t("All")}
                      </button>
                      {LLOJET.map(([k, etiketa, ikona]) => {
                        const on = seShpejtiFiltrat[k] !== false;
                        return (
                          <button key={k} onClick={() => { ndryshoFiltrinESeShpejtit(k); setFiltratEHapur(false); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                            style={{ background: on ? KART.accentTint : "transparent", color: KART.onBg, border: `1px solid ${on ? KART.gold : KART.goldSoft}`, opacity: on ? 1 : 0.6, cursor: "pointer" }}>
                            <span style={{ fontSize: 11, width: 10, color: KART.gold }}>{on ? "✓" : ""}</span>
                            <span>{ikona} {etiketa}</span>
                            <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 500, opacity: 0.75, fontVariantNumeric: "tabular-nums" }}>{numrat[k] || 0}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {dukshem.length === 0 && (
                    <p className="px-1" style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.onBg, opacity: 0.7 }}>
                      {feedUpcoming.length === 0
                        ? t("Nothing coming up in the next weeks — birthdays and anniversaries appear here.")
                        : t("Nothing under these filters — turn one back on, or wait for the calendar.")}
                    </p>
                  )}

                  {/* KARTELA1 — RRESHTAT E «SË SHPEJTI» JANË KARTELA.
                      Ishin shkruar me `accentTint` dhe `onBg` drejtpërdrejt, pra
                      nuk e ndiqnin fare zgjedhjen: me pamjen e bardhë ndezur,
                      kutitë e statistikave pranë tyre dilnin të bardha kurse
                      këto mbeteshin kafe. Pronari e pa dy herë, dhe herën e dytë
                      e tha shkurt: «upcoming prap nuk po e ndjek temën».
                      Tani sipërfaqja dhe boja vijnë nga çifti, si kudo. */}
                  <div className="space-y-1.5">
                    {dukshem.map((item, i) => (
                      <div
                        key={i}
                        className="w-full flex items-center gap-2 rounded-md px-3 py-2"
                        style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}` }}
                      >
                        <span onClick={() => item.personId && setSelectedId(item.personId)} style={{ cursor: "pointer", display: "inline-flex" }}>
                          <IkonaP size={18} color={KART.inkLight}
                            Lucide={item.type === "birthday" ? Cake : item.type === "important" ? CalendarDays : item.type === "wedding" ? Heart : Flame}
                            emoji={item.type === "birthday" ? "🎂" : item.type === "important" ? "📅" : item.type === "wedding" ? "💍" : "🕯️"} />
                        </span>
                        <span onClick={() => item.personId && setSelectedId(item.personId)} style={{ fontFamily: FONTS.body, fontSize: 15, color: KART.ink, flex: 1, cursor: "pointer" }}>
                          {item.label}
                          {/* Kush e vuri këtë datë. Pa këtë shenjë, data ime dhe
                              data e familjes duken njësoj — dhe atëherë askush
                              s'e di pse fqinji nuk e sheh të tijën. */}
                          {item.imja && <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: KART.inkLight, marginLeft: 8 }}>{t("mine")}</span>}
                        </span>
                        <span style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: KART.inkLight, fontVariantNumeric: "tabular-nums" }}>
                          {item.daysUntil === 0 ? t("today") : item.daysUntil === 1 ? t("tomorrow") : t("in {{count}} days", { count: item.daysUntil })}
                        </span>
                        <button onClick={() => suggestForOccasion(item)} title={t("Suggest a message")} style={{ fontSize: 14, lineHeight: 1, padding: "2px 4px", background: "transparent", border: "none", cursor: "pointer" }}>✨</button>
                      </div>
                    ))}
                  </div>
                </div>
                );
              })()}
              {/* Butoni «Shkruaj trashëgiminë» u hoq nga këtu dhe hyri te dera
                  «Shto» — bashkë me personin, historinë dhe skedarët. Ishte i
                  vetmi veprim shtimi që jetonte i lirë mbi një listë leximi. */}
              {(() => {
                const fullList = homeTab === "feed" ? feedRecent : [];
                if (fullList.length === 0) return null;
                // VENDIM (19 gusht 2026) — kujtimet e postuara NUK fshihen dhe
                // NUK fshehen kurrë. Më parë çdo artikull më i vjetër se
                // `retention_days` (7 ditë) zhdukej nga lista: kujtime, mesazhe
                // trashëgimie, lindje, vdekje, martesa. Emri "mbajtje" i jepte
                // gjithashtu përshtypjen se fshiheshin nga baza — s'fshiheshin
                // kurrë, ishte vetëm filtër pamjeje.
                //
                // Tani lista thjesht ndahet në faqe: shfaqen të parat, dhe
                // butoni i hap të gjitha. Asgjë nuk zhduket me kalimin e kohës.
                // Kujtesat që përsëriten (ditëlindje, përvjetorë) jetojnë te
                // lista e ngjarjeve më sipër, e cila kufizohet vetë nga dritarja
                // e ditëve — pra ato skadojnë vetvetiu, siç duhet.
                const PAGE = 12;
                const list = showOlderFeed ? fullList : fullList.slice(0, PAGE);
                const hiddenOlder = fullList.length - list.length;
                // FEED1 — kutiza e zgjedhjes rri PËRBRI kartelës, jo brenda saj:
                // kartelat janë butona që hapin profilin, dhe një buton brenda
                // një butoni nuk klikohet dot.
                const mbeshtjellaAdmin = (postId, nyja) => {
                  if (!canManage || !postId || !feedSelectMode) return nyja;
                  const on = feedSelect.has(postId);
                  return (
                    <div key={`sel-${postId}`} className="flex items-start gap-2">
                      <button type="button" aria-label={t("Select")}
                        onClick={() => setFeedSelect((prev) => { const n = new Set(prev); if (n.has(postId)) n.delete(postId); else n.add(postId); return n; })}
                        style={{ width: 22, height: 22, marginTop: 10, borderRadius: 6, flex: "none", cursor: "pointer",
                                 border: `2px solid ${on ? KART.gold : KART.goldSoft}`, background: on ? KART.gold : "transparent",
                                 color: KART.bgDeep, fontSize: 13, lineHeight: 1, padding: 0 }}>{on ? "✓" : ""}</button>
                      <div style={{ flex: 1, minWidth: 0 }}>{nyja}</div>
                    </div>
                  );
                };
                const idetEZgjedhshme = fullList.map((x) => x.postId).filter(Boolean);
                return (
                <div>
                  {/* FEED1 — veglat e adminit rrinë TË FSHEHURA pas vetë titullit.
                      Pronari: «do doja që ky opsion të mos jetë çdo herë në
                      ekran». Titulli bëhet çelës vetëm për adminin dhe vetëm kur
                      ka çfarë të zgjidhet; për këdo tjetër mbetet tekst i thjeshtë,
                      pa asnjë ndryshim pamjeje. */}
                  {homeTab === "feed" && (
                    (canManage && idetEZgjedhshme.length > 0) ? (
                      <button type="button"
                        onClick={() => { const v = !feedToolsOpen; setFeedToolsOpen(v); if (!v) { setFeedSelectMode(false); setFeedSelect(new Set()); } }}
                        className="mb-2 px-1 flex items-center gap-1.5"
                        style={{ ...SEKSION_TITULL(KART, FONTS), background: "transparent", border: "none", cursor: "pointer" }}>
                        {t("What's new in the family")}
                        <ChevronDown size={13} style={{ transform: feedToolsOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                      </button>
                    ) : (
                      <p className="mb-2 px-1" style={SEKSION_TITULL(KART, FONTS)}>{t("What's new in the family")}</p>
                    )
                  )}
                  {/* Sipërfaqja këtu është SFONDI i rrjedhës (`bg`/`bgDeep`), jo
                      një panel — ndaj boja është `onBg`, kurse `ink`/`inkLight`
                      do të ishin të padukshme mbi të. Rregulli i kontrastit,
                      29 gusht: teksti dhe sipërfaqja vijnë nga i njëjti çift. */}
                  {homeTab === "feed" && canManage && feedToolsOpen && idetEZgjedhshme.length > 0 && (
                    <div className="flex items-center gap-2 mb-2 px-1 flex-wrap">
                      <button type="button" onClick={() => { setFeedSelectMode((v) => !v); setFeedSelect(new Set()); }}
                        className="px-2.5 py-1 rounded-md text-xs"
                        style={{ border: `1px solid ${KART.goldSoft}`, background: feedSelectMode ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)", color: KART.onBg, cursor: "pointer" }}>
                        {feedSelectMode ? t("Cancel") : t("Select posts")}
                      </button>
                      {feedSelectMode && (
                        <>
                          <button type="button" onClick={() => setFeedSelect(new Set(idetEZgjedhshme))}
                            className="px-2.5 py-1 rounded-md text-xs"
                            style={{ border: `1px solid ${KART.goldSoft}`, background: "rgba(255,255,255,0.06)", color: KART.onBg, cursor: "pointer" }}>
                            {t("Select all")}
                          </button>
                          <button type="button" disabled={feedSelect.size === 0} onClick={() => fshihPostimet(feedSelect, true)}
                            className="px-2.5 py-1 rounded-md text-xs"
                            style={{ border: "none", background: KART.gold, color: KART.bgDeep, fontWeight: 600, cursor: "pointer", opacity: feedSelect.size === 0 ? 0.45 : 1 }}>
                            {t("Remove from the feed ({{count}})", { count: feedSelect.size })}
                          </button>
                        </>
                      )}
                      <button type="button" onClick={hapTeFshehurat}
                        className="px-2.5 py-1 rounded-md text-xs ml-auto"
                        style={{ border: `1px solid ${KART.goldSoft}`, background: "rgba(255,255,255,0.06)", color: KART.onBg, cursor: "pointer" }}>
                        {t("Removed posts")}
                      </button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {list.slice(0, 300).map((item) => {
                      if (item.type === "memory") {
                        return (
                          <div key={item.id} style={kartelaGuacka(KART)}>
                            {/* BALLINA2 — rreshti i sipërm tregon ÇFARË është,
                                KUR ndodhi dhe KUSH e shtoi. Më parë thoshte
                                vetëm «Kujtim i ri», i ngulitur shqip: një
                                kujtim familjeje pa vit dhe pa emër, dhe teksti
                                s'përkthehej kurrë. */}
                            {(() => {
                              const autori = item.createdBy && people[item.createdBy] ? displayName(people[item.createdBy]) : null;
                              const copat = [
                                item.photoPaths.length > 1
                                  ? t("Memory · {{count}} photos", { count: item.photoPaths.length })
                                  : t("Memory"),
                                item.eventDate || null,
                                autori ? t("by {{name}}", { name: autori }) : null,
                              ].filter(Boolean);
                              return (
                                <p style={{ ...kartelaEtikete(KART.inkLight) }}>
                                  <IkonaP Lucide={Camera} emoji="📷" size={14} color={KART.inkLight} /> {copat.join(" · ")}
                                </p>
                              );
                            })()}
                            {item.photoPaths.length > 0 && (
                              <div className="flex gap-1.5 mb-2" style={{ overflowX: "auto" }}>
                                {item.photoPaths.map((p, i) => (
                                  // Klikimi hap foton e plotë — të njëjtën dritare
                                  // që përdor Arkivi. Deri sot një foto e vogël
                                  // 150px ishte gjithçka që shihte njeriu.
                                  <img key={i} src={photoUrl(p)} alt=""
                                    onClick={() => setArchiveLightbox(p)}
                                    className="rounded-md shrink-0"
                                    style={{ height: 150, width: item.photoPaths.length === 1 ? "100%" : "auto", objectFit: "cover", cursor: "zoom-in" }} />
                                ))}
                              </div>
                            )}
                            {/* PERKTHIMI1 — po ai filtër si te Historia. Kujtimi
                                te ballina dhe ngjarja te Historia janë i njëjti
                                rresht i bazës, prandaj përkthimi është i njëjti
                                dhe nuk paguhet dy herë. */}
                            {(() => {
                              const fushat = { title: item.title, description: item.description };
                              const btn = butoniIPerkthimit(item.id, fushat, KART.etiketa);
                              return (<>
                                <p style={{ ...tip("titulli", { fontFamily: FONTS.display, fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: KART.ink }), margin: 0 }}>
                                  {tekstiIDukshem(item.id, "title", item.title).teksti}
                                  {btn && <span style={{ marginLeft: 7, verticalAlign: "middle", display: "inline-flex" }}>{btn}</span>}
                                </p>
                                {item.description && <p className="mt-1.5" style={{ fontFamily: FONTS.body, fontSize: 15.5, color: KART.ink, lineHeight: 1.6 }}>{tekstiIDukshem(item.id, "description", item.description).teksti}</p>}
                                {kaPerkthim(item.id, fushat) && rreshtiIPerkthimit(item.id, KART.etiketa)}
                              </>);
                            })()}
                            <ReactionBar eventId={item.id} reactions={feedReactions[item.id]} myAccountId={myAccountId} onToggle={toggleReaction} colors={KART}
                              raporto={{ etiketa: t("Report"), titulli: t("Report this content to the platform"), hap: () => hapRaportimin("kujtim", item.id, item.title) }} />
                          </div>
                        );
                      }
                      if (item.type === "new_baby") {
                        const p = people[item.personId];
                        if (!p) return null;
                        return (
                          <button key={item.id} onClick={() => item.personId && setSelectedId(item.personId)} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-left" style={njoftimiKutia(KART)}>
                            <IkonaP Lucide={Baby} emoji="👶" size={17} color={KART.inkLight} />
                            <span style={njoftimiTeksti(KART)}>{t("Welcome to the family: {{name}}", { name: displayName(p) })}</span>
                          </button>
                        );
                      }
                      if (item.type === "legacy") {
                        return (
                          <button key={item.id} onClick={() => item.personId && setSelectedId(item.personId)} className="w-full text-left" style={kartelaGuacka(KART)}>
                            <p style={{ ...kartelaEtikete(KART.inkLight) }}><IkonaP Lucide={Mail} emoji="💌" size={14} color={KART.inkLight} /> {t("Legacy from {{name}}", { name: item.personName })}</p>
                            <p className="whitespace-pre-wrap" style={{ fontFamily: FONTS.body, fontSize: 16, color: KART.ink, lineHeight: 1.6 }}>{item.messageText}</p>
                          </button>
                        );
                      }
                      // Degëzimi `item.type === "addition"` u hoq (19 gusht 2026):
                      // asnjë prodhues s'e krijon këtë tip — `feedRecent` jep vetëm
                      // memory/legacy/new_baby/death/branch_joined/marriage/new_member.
                      // Rolin e tij e zuri `new_member`.
                      if (item.type === "sot") {
                        // Postimi i ditës — po ai rresht si ngjarjet e tjera të
                        // shkurtra (lindja, ndarja): ikonë, një fjali, klikimi hap personin.
                        const p = item.personId ? people[item.personId] : null;
                        const mosha = p && item.lloji === "birthday" ? birthdayNextAge(p.birth_date) : null;
                        const teksti = item.lloji === "birthday" && p
                          ? (p.death_date
                              ? t("Today is {{name}}'s birthday — {{age}} years since birth", { name: displayName(p), age: mosha })
                              : t("Today {{name}} turns {{age}}", { name: displayName(p), age: mosha }))
                          : t("Today: {{label}}", { label: item.label });
                        // ⚠ 5 shtator 2026 — «te së shpejti e kemi ai për ditëlindjet,
                        // super, po kur ditëlindja del te së fundi nuk është ai».
                        // Kishte të drejtë: ✨ jetonte vetëm te lista «Së shpejti».
                        // Sapo dita vinte, rreshti kalonte te rrjedha dhe butoni
                        // zhdukej — pikërisht ditën kur urimi shkruhet.
                        //
                        // Rreshti u kthye nga `<button>` te `<div>`: një buton
                        // brenda një butoni nuk klikohet dot. I njëjti mësim si te
                        // kutiza e zgjedhjes së postimeve.
                        //
                        // `suggestForOccasion` pret `type` = birthday/anniversary/
                        // wedding/important, kurse artikujt e ditës e mbajnë atë te
                        // `lloji` dhe `type` e kanë "sot" — prandaj përkthehet këtu.
                        // Pa këtë, çdo klikim do të kthente 400 nga edge-i.
                        //
                        // Përjashtimi: ditëlindja e një personi të ndjerë. Rreshti
                        // aty është përkujtim («84 vjet nga lindja»), jo urim —
                        // dhe një sugjerim me AI do të shkruante urim. E njëjta
                        // arsye pse njoftimi nuk dërgohet më për ta.
                        const aiMundshem = item.personId
                          && ["birthday", "anniversary", "wedding", "important"].includes(item.lloji)
                          && !(item.lloji === "birthday" && p && p.death_date);
                        return (
                          <div key={item.id} className="w-full flex items-center gap-2 rounded-md px-3 py-2" style={njoftimiKutia(KART)}>
                            <IkonaP size={17} color={KART.inkLight}
                              Lucide={item.lloji === "birthday" ? Cake : item.lloji === "important" ? CalendarDays : item.lloji === "wedding" ? Heart : Flame}
                              emoji={item.lloji === "birthday" ? "🎂" : item.lloji === "important" ? "📅" : item.lloji === "wedding" ? "💍" : "🕯️"} />
                            <span onClick={() => item.personId && setSelectedId(item.personId)}
                              style={{ ...njoftimiTeksti(KART), flex: 1, cursor: item.personId ? "pointer" : "default", textAlign: "left" }}>{teksti}</span>
                            {aiMundshem && (
                              <button onClick={() => suggestForOccasion({ ...item, type: item.lloji })} title={t("Suggest a message")}
                                style={{ fontSize: 14, lineHeight: 1, padding: "2px 4px", background: "transparent", border: "none", cursor: "pointer", flexShrink: 0 }}>✨</button>
                            )}
                          </div>
                        );
                      }
                      if (item.type === "death") {
                        const p = people[item.personId];
                        if (!p) return null;
                        return (
                          <button key={item.id} onClick={() => item.personId && setSelectedId(item.personId)} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-left" style={njoftimiKutia(KART)}>
                            <IkonaP Lucide={Feather} emoji="🕊️" size={17} color={KART.inkLight} />
                            <span style={njoftimiTeksti(KART)}>{t("With sorrow: {{name}} is no longer among us", { name: displayName(p) })}</span>
                          </button>
                        );
                      }
                      if (item.type === "branch_joined") {
                        const head = people[item.headPersonId];
                        const names = (item.memberIds || []).map((id) => people[id]).filter(Boolean);
                        return mbeshtjellaAdmin(item.postId, (
                          <div key={item.id} style={kartelaGuacka(KART)}>
                            <p style={{ ...kartelaEtikete(KART.inkLight) }}><IkonaP Lucide={Users} emoji="🌳" size={14} color={KART.inkLight} /> {t("A new branch joined the tree")}</p>
                            <p style={{ fontFamily: FONTS.body, fontSize: 15.5, color: KART.ink, lineHeight: 1.5, margin: 0 }}>{t("The family of {{name}} joined the tree.", { name: head ? displayName(head) : "—" })}</p>
                            {names.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {names.map((p) => (
                                  <button key={p.id} onClick={() => setSelectedId(p.id)} className="px-2 py-0.5 rounded-full text-xs" style={{ background: KART.accentTint, border: `1px solid ${KART.goldSoft}`, color: KART.ink }}>{displayName(p)}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        ));
                      }
                      if (item.type === "marriage") {
                        // NJË PESHË PËR NJË NGJARJE (2 shtator 2026). Pronari: ndarja
                        // ishte një rresht i qetë «Me trishtim…», kurse martesa një
                        // kartelë me kryetitull «NJË LIDHJE E RE» — «bombastik si lajm».
                        // Lindja, ndarja, martesa, anëtari i ri, dita e sotme: të
                        // pesta janë ngjarje të një fjalie, dhe marrin të njëjtin
                        // rresht. Kartela me kryetitull mbetet për përmbajtjen
                        // (kujtimi, trashëgimia, dega e re me anëtarët e saj).
                        // Dhe pa dy emra nuk ka çfarë të thuhet — rreshti nuk del.
                        const names = (item.memberIds || []).map((id) => people[id]).filter(Boolean);
                        if (names.length < 2) return null;
                        return mbeshtjellaAdmin(item.postId, (
                          <button key={item.id} onClick={() => setSelectedId(names[0].id)} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-left" style={njoftimiKutia(KART)}>
                            <IkonaP Lucide={Heart} emoji="💍" size={17} color={KART.inkLight} />
                            <span style={njoftimiTeksti(KART)}>{t("{{a}} and {{b}} are now a couple.", { a: displayName(names[0]), b: displayName(names[1]) })}</span>
                          </button>
                        ));
                      }
                      if (item.type === "new_member") {
                        const p = people[item.personId];
                        if (!p) return null;
                        return mbeshtjellaAdmin(item.postId, (
                          <button key={item.id} onClick={() => setSelectedId(item.personId)} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-left" style={njoftimiKutia(KART)}>
                            <IkonaP Lucide={Hand} emoji="👋" size={17} color={KART.inkLight} />
                            <span style={njoftimiTeksti(KART)}>{t("{{name}} joined the family tree", { name: displayName(p) })}</span>
                          </button>
                        ));
                      }
                      return null;
                    })}
                  </div>
                  {(hiddenOlder > 0 || showOlderFeed) && (
                    <button onClick={() => setShowOlderFeed((v) => !v)} className="mt-3 w-full text-center py-2.5 rounded-md" style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: KART.onBg, background: "rgba(255,255,255,0.06)", border: `1px solid ${KART.goldSoft}` }}>
                      {showOlderFeed ? t("Show less") : t("Show older ({{count}})", { count: hiddenOlder })}
                    </button>
                  )}
                </div>
                );
              })()}
            </div>
            );
          })()}
          </div>
          </div>
        </div>
      )}

      {/* BASHKIM (kërkesë e userit) — "Archive" dhe "Kujtime" (Historia e
          Familjes) tani janë NJË ekran i vetëm, "memories", me 2 skeda.
          TË DHËNAT/RPC-të mbeten krejt të pandryshuara — bashkohet vetëm
          paraqitja, që dukeshin njësoj nga jashtë (të dyja "vend ku shoh
          foto"), edhe pse struktura e brendshme (ngjarje me datë+tregim vs
          magazinë e sheshtë skedarësh) është e ndryshme. Skeda "Skedarë"
          shfaqet vetëm nëse admini e ka aktivizuar Archive-n (archiveEnabled)
          — njësoj si më parë, kur ishte artikull i veçantë në navigim. */}
      {!locked && !provaNga(3) && activeScreen === "memories" && (
        <div className="flex-1 overflow-y-auto px-4 py-4" style={{ background: `radial-gradient(circle at 20% 10%, ${KART.bgDeep}, ${KART.bg})` }}>
        <div className="max-w-3xl mx-auto">
          {(() => {
            const kohet = historiaRrjedha.map((r) => r.viti).filter(Boolean);
            const meVjetri = kohet.length ? kohet[kohet.length - 1] : null;
            return (
              <div className="mb-3">
                <p style={{ fontFamily: FONTS.display, fontSize: 22, fontWeight: 600, color: KART.onBg, margin: 0 }}>{t("History")}</p>
                <p style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.onBg, opacity: 0.7, margin: "3px 0 0" }}>
                  {meVjetri ? `${meVjetri} — ${t("today")} · ` : ""}{t("{{sa}} things", { sa: historiaRrjedha.length })}
                </p>
              </div>
            );
          })()}

          {/* «Ma trego historinë» — i njëjti turne që niste nga ballina, por
              këtu ka kuptimin e vet: je te historia, dhe dikush ta lexon. */}
          {turneHapat.length >= 3 && (
            <button
              onClick={nisTurneun}
              className="w-full flex items-center gap-3 rounded-2xl mb-4 text-left"
              style={{ background: `linear-gradient(140deg, ${KART.panel}, ${KART.panelSoft})`, border: `1px solid ${KART.goldSoft}`, padding: "13px 15px", cursor: "pointer" }}
            >
              <span style={{ fontSize: 17, color: KART.etiketa }}>▶</span>
              <span style={{ display: "block" }}>
                <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 14.5, fontWeight: 600, color: KART.ink }}>{t("Tell me the story")}</span>
                <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 12, color: KART.inkLight, marginTop: 2 }}>
                  {t("{{sa}} stops — from the oldest name to today", { sa: turneHapat.length })}
                </span>
              </span>
            </button>
          )}

          {/* FILTRAT. Numri pranë çdo filtri nuk është dekor: pa të, «Zëra» me
              zero regjistrime duket si filtër i prishur, jo si arkivë bosh. */}
          <div className="flex flex-wrap gap-2 mb-4">
            {historiaFiltrat.map(([kodi, etiketa, sa]) => {
              const on = historiaFiltri === kodi;
              return (
                <button
                  key={kodi}
                  onClick={() => { if (kodi === "person") { setHistoriaPersoniHapur(true); } else { setHistoriaFiltri(kodi); setHistoriaPersoni(null); } }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: on ? KART.gold : "transparent", color: on ? KART.bgDeep : KART.onBg, border: `1px solid ${on ? KART.gold : KART.goldSoft}`, cursor: "pointer" }}
                >
                  {etiketa}{sa == null ? "" : ` ${sa}`}
                </button>
              );
            })}
          </div>

          {renderMatesinEArkives("nen")}

          {/* KOSHI — një rresht i qetë, i hapur me klikim. Rri pas filtrave dhe
              para lumit: është administrim, jo përmbajtje, dhe s'duhet ta ndajë
              syrin nga historia derisa dikush ta kërkojë. Shfaqet vetëm për ata
              që mund të fshijnë, dhe vetëm kur ka diçka brenda. */}
          {archiveEnabled && (myRole === "admin" || myRole === "editor") && (
            <div className="mb-4">
              <button
                onClick={() => { const v = !koshiOpen; setKoshiOpen(v); if (v && koshiItems === null) ngarkoKoshin(); }}
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0,
                         fontFamily: FONTS.body, fontSize: 12.5, color: KART.inkLight }}
              >
                {koshiOpen ? "▾ " : "▸ "}{t("Trash")}
                {Array.isArray(koshiItems) && koshiItems.length > 0 ? ` (${koshiItems.length})` : ""}
              </button>

              {koshiOpen && (
                <div className="mt-2 rounded-xl" style={{ background: KART.panel, border: `1px solid ${KART.goldSoft}`, padding: "10px 12px" }}>
                  {koshiItems === null ? (
                    <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: KART.inkLight, margin: 0 }}>…</p>
                  ) : koshiItems.length === 0 ? (
                    <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: KART.inkLight, margin: 0 }}>{t("space.trashEmpty")}</p>
                  ) : (<>
                    <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: KART.inkLight, margin: "0 0 8px", lineHeight: 1.5 }}>
                      {t("space.trashIntro")}
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {koshiItems.map((it) => (
                        <div key={it.id} className="flex items-center justify-between gap-3">
                          <span style={{ minWidth: 0, flex: 1 }}>
                            <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 13, color: KART.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {it.caption || (it.file_path || "").split("/").pop()}
                            </span>
                            <span style={{ display: "block", fontFamily: FONTS.body, fontSize: 11, color: KART.inkLight, fontVariantNumeric: "tabular-nums" }}>
                              {sipasH(Number(it.size_bytes || 0) / 1048576)}
                              {" · "}
                              {t("space.trashDays", { n: Number(it.dite_te_mbetura || 0) })}
                            </span>
                          </span>
                          <button
                            disabled={koshiBusy === it.id}
                            onClick={() => ktheNgaKoshi(it.id)}
                            style={{ flexShrink: 0, background: "transparent", border: `1px solid ${KART.goldSoft}`,
                                     color: KART.ink, borderRadius: 8, padding: "4px 10px",
                                     fontFamily: FONTS.body, fontSize: 12.5, cursor: "pointer",
                                     opacity: koshiBusy === it.id ? 0.6 : 1 }}
                          >
                            {koshiBusy === it.id ? "…" : t("Restore")}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>)}
                </div>
              )}
            </div>
          )}

          {/* Kuota e muajit. E vetmja gjendje e përkthimit që i thuhet
              lexuesit: ajo zgjat javë, dhe pa të «pse gjysma është shqip» s'ka
              përgjigje. Gjithçka tjetër hesht — teksti origjinal është aty. */}
          {perkthimiGabim === "quota_month" && (
            <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 12, color: KART.onBg, opacity: 0.7 }}>
              {t("Translation has reached this month's limit — the originals are shown.")}
            </p>
          )}

          {/* BALLINA8 — formularët dolën nga këtu. Historia lexohet; shtimi ka
              dy faqet e veta, të hapura nga «Shto». Redaktimi i një ngjarjeje
              (✏️ poshtë) hap po atë faqe si shtimi. */}

          <div style={{ background: KART.panel, borderRadius: 12, padding: "18px 20px", boxShadow: KARTELA_HIJE }}>
          {historiaERreshtuar.length === 0 ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: KART.inkLight }}>
              {historiaFiltri === "gjitha" ? t("history.noEventsYet") : t("Nothing under this filter — try another one.")}
            </p>
          ) : (
            <div className="space-y-4">
              {historiaERreshtuar.map((r, idx) => {
                const vitiParaardhes = idx > 0 ? historiaERreshtuar[idx - 1].viti : null;
                const trego = r.viti && r.viti !== vitiParaardhes;
                const eFundit = idx === historiaERreshtuar.length - 1;
                const ev = r.ev; const it = r.it;
                const evPhotos = ev ? [...(ev.photo_path ? [ev.photo_path] : []), ...(ev.photo_paths || [])] : [];
                const etiketuarit = (it && Array.isArray(it.person_ids) ? it.person_ids : []).map((pid) => people[pid]).filter(Boolean);
                const imKryesor = it && (it.uploaded_by === currentPersonId || myRole === "admin");
                // SISTEMI VIZUAL (2 shtator 2026) — ikonë Lucide, jo emoji.
                const IkonaLloji = r.tipi === "ngjarje" ? BookOpen : r.tipi === "zera" ? Mic : r.tipi === "dokumente" ? FileText : Camera;
                const emojiLloji = r.tipi === "ngjarje" ? "📖" : r.tipi === "zera" ? "🎙️" : r.tipi === "dokumente" ? "📄" : "📷";
                const kokaELlojit = r.tipi === "ngjarje" ? t("Events")
                  : r.tipi === "zera" ? t("Voices")
                  : r.tipi === "dokumente" ? t("Documents")
                  : t("Photos");
                return (
                <div key={r.id} className="flex gap-3">
                  {/* Shiriti i kohës majtas: viti shfaqet vetëm kur ndryshon —
                      përndryshe dhjetë foto të një dite do të shkruanin dhjetë
                      herë të njëjtin numër dhe rrjedha do të dukej listë. */}
                  <div className="flex flex-col items-center" style={{ width: 56, flexShrink: 0 }}>
                    {/* PAMJA «HEIRLOOM» — viti si numër serif në margjinë, pika
                        ari me unazë në ngjyrën e faqes (që të mos ngjitet me
                        shinën) dhe shina flokëhollë. Te mockup-i pikërisht kjo
                        kolonë e bën Historinë të duket kronikë, jo listë. */}
                    <span style={pamja("histori", "heirloom")
                      ? { fontFamily: FONTS.display, fontSize: 18, color: PALETA_TIPI.text || KART.ink, fontWeight: 600, minHeight: 20, fontVariantNumeric: "tabular-nums" }
                      : { fontFamily: FONTS.body, fontSize: 13, color: KART.ink, fontWeight: 600, minHeight: 16, fontVariantNumeric: "tabular-nums" }}>
                      {trego ? r.viti : ""}
                    </span>
                    {pamja("histori", "heirloom") ? (
                      <span className="rounded-full mt-1" style={{ width: 11, height: 11, flexShrink: 0,
                        background: trego ? (PALETA_TIPI.spouseLine || KART.gold) : (PALETA_TIPI.accentBorder || KART.goldSoft),
                        boxShadow: `0 0 0 3px ${PALETA_TIPI.bg || KART.bg}` }} />
                    ) : (
                      <span className="rounded-full mt-1" style={{ width: 9, height: 9, background: trego ? KART.gold : KART.goldSoft, flexShrink: 0 }} />
                    )}
                    {!eFundit && <span style={{ width: pamja("histori", "heirloom") ? 1.5 : 2, flex: 1, background: pamja("histori", "heirloom") ? (PALETA_TIPI.spouseLine || KART.goldSoft) : KART.goldSoft, marginTop: 4 }} />}
                  </div>
                  <div className="flex-1 pb-2">
                    <p style={{ ...kartelaEtikete(KART.inkLight), margin: "0 0 4px" }}>
                      <IkonaP Lucide={IkonaLloji} emoji={emojiLloji} size={14} color={KART.inkLight} />{kokaELlojit}{ev?.event_date ? ` · ${dataELexueshme(ev.event_date, ev.date_precision || "dite", i18n.language, t)}` : ""}
                    </p>

                    {/* ── NGJARJE ── */}
                    {r.tipi === "ngjarje" && (() => {
                      // PERKTHIMI1 — titulli dhe rrëfimi kalojnë nga i njëjti
                      // filtër. Nëse gjuha e lexuesit është e njëjta me atë të
                      // familjes, `tekstiIDukshem` kthen origjinalin pa prekur
                      // asgjë — pra ky degëzim nuk ekziston fare për shumicën.
                      const tTit = tekstiIDukshem(ev.id, "title", ev.title);
                      const tPer = ev.description ? tekstiIDukshem(ev.id, "description", ev.description) : null;
                      const fushat = { title: ev.title, description: ev.description };
                      const trego = kaPerkthim(ev.id, fushat);
                      const btnPerk = butoniIPerkthimit(ev.id, fushat, KART.etiketa);
                      return (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <p style={{ ...tip("titulli", { fontFamily: FONTS.display, fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: KART.ink }), margin: 0 }}>
                            {tTit.teksti}
                            {/* «EN» rri BRENDA titullit, jo pranë tij: kështu
                                shkon te fundi i rreshtit të fundit dhe nuk e
                                shtyn tekstin kur titulli mbulon dy rreshta. */}
                            {btnPerk && <span style={{ marginLeft: 7, verticalAlign: "middle", display: "inline-flex" }}>{btnPerk}</span>}
                          </p>
                          {canManage && (
                            <div className="flex items-center gap-2 shrink-0">
                              <button onClick={() => editFamilyHistoryEvent(ev)} style={{ fontSize: 12, color: KART.inkLight }}>✏️</button>
                              <button onClick={() => deleteFamilyHistoryEvent(ev)} style={{ fontSize: 12, color: KART.inkLight }}>🗑️</button>
                            </div>
                          )}
                        </div>
                        {ev.description && <p className="mt-1.5" style={{ ...tip("trupi", { fontFamily: FONTS.body, fontSize: 16, lineHeight: 1.6, color: KART.ink }), whiteSpace: "pre-wrap" }}>{tPer.teksti}</p>}
                        {trego && rreshtiIPerkthimit(ev.id, KART.etiketa)}
                        {evPhotos.length > 0 && (
                          <div className="flex gap-1.5 mt-2" style={{ overflowX: "auto" }}>
                            {evPhotos.map((photoPath, pi) => (
                              <img
                                key={pi}
                                src={photoUrl(photoPath)}
                                alt=""
                                onClick={() => setPhotoLightbox(photoPath)}
                                className="rounded-md cursor-pointer shrink-0"
                                style={{ width: evPhotos.length === 1 ? 260 : 130, maxWidth: "100%", height: 180, objectFit: "cover", background: KART.panelSoft }}
                                title={t("history.enlargeTitle")}
                              />
                            ))}
                          </div>
                        )}
                        <ReactionBar eventId={ev.id} reactions={feedReactions[ev.id]} myAccountId={myAccountId} onToggle={toggleReaction} colors={KART}
                          raporto={{ etiketa: t("Report"), titulli: t("Report this content to the platform"), hap: () => hapRaportimin("kujtim", ev.id, ev.title) }} />
                      </>
                      );
                    })()}

                    {/* ── SKEDAR ── */}
                    {r.tipi !== "ngjarje" && (
                      <>
                        {it.caption && <p style={{ fontFamily: FONTS.body, fontSize: 15.5, lineHeight: 1.5, color: KART.ink, margin: "0 0 6px" }}>{it.caption}</p>}
                        {it.file_type === "photo" && (
                          <img src={photoUrl(it.file_path)} alt="" onClick={() => setArchiveLightbox(it.file_path)}
                            className="rounded-md cursor-pointer" style={{ width: 260, maxWidth: "100%", height: 180, objectFit: "cover", background: KART.panelSoft }} />
                        )}
                        {it.file_type === "video" && (
                          <video src={photoUrl(it.file_path)} controls className="rounded-md" style={{ width: 260, maxWidth: "100%", background: KART.panelSoft }} />
                        )}
                        {/* Zëri nuk ka pamje — dhe pikërisht prandaj i duhet një
                            rresht që thotë se është zë. Një player pa etiketë
                            duket si skedar i prishur. */}
                        {it.file_type === "audio" && (
                          <audio src={photoUrl(it.file_path)} controls style={{ width: "100%", maxWidth: 320 }} />
                        )}
                        {it.file_type === "document" && (
                          <a href={photoUrl(it.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md px-3 py-2"
                            style={{ background: KART.accentTint, border: `1px solid ${KART.goldSoft}`, textDecoration: "none" }}>
                            {/* Brenda kartelës, jo mbi sfond — pra `etiketa`.
                                Ari këtu rri mbi një ar të hollë mbi të bardhë. */}
                            <ScrollText size={16} color={KART.etiketa} />
                            <span style={{ fontFamily: FONTS.mono, fontSize: 12.5, color: KART.ink, wordBreak: "break-all" }}>{it.file_path.split("/").pop()}</span>
                          </a>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {etiketuarit.map((p) => (
                            <button key={p.id} onClick={() => { setHistoriaPersoni(p.id); setHistoriaFiltri("person"); }}
                              className="rounded-full px-2 py-0.5"
                              style={{ background: KART.accentTint, border: `1px solid ${KART.goldSoft}`, fontFamily: FONTS.body, fontSize: 11, color: KART.ink, cursor: "pointer" }}>
                              {emriDallues(p, etiketatDalluese)}
                            </button>
                          ))}
                          {["admin", "editor", "contributor"].includes(myRole) && (
                            <button onClick={() => hapEtiketat(it)} className="rounded-full px-2 py-0.5"
                              style={{ background: "transparent", border: `1px dashed ${KART.goldSoft}`, fontFamily: FONTS.body, fontSize: 11, color: KART.inkLight, cursor: "pointer" }}>
                              ＋ {t("Who is in it?")}
                            </button>
                          )}
                          {imKryesor && (
                            <button onClick={() => deleteArchiveItem(it.id)} style={{ fontSize: 12, color: KART.inkLight, background: "transparent", border: "none", cursor: "pointer" }}>🗑️</button>
                          )}
                          {/* RAPORTIMI1 — JASHTË `imKryesor` me qëllim. Fotoja e
                              ngarkuar nga dikush tjetër është pikërisht ajo që
                              anëtari i thjeshtë s'mund ta fshijë dhe prandaj i
                              duhet një derë. */}
                          <button onClick={() => hapRaportimin("skedar", it.id, it.caption || (it.file_path || "").split("/").pop())}
                            title={t("Report this content to the platform")}
                            style={{ marginLeft: "auto", fontSize: 11.5, color: KART.inkLight, background: "transparent", border: "none", cursor: "pointer" }}>
                            ⚑ {t("Report")}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
        </div>
      )}


      {/* KARTELA1 — Shërbimet marrin paletën e kartelave, jo `COLORS`. Asnjë
          rresht i `ServicesScreen.jsx` nuk ndryshon: aty çifti sipërfaqe+bojë
          ishte tashmë i saktë, dhe këtu ndërrohet kuptimi i të dyve njëherësh.
          (Komenti rri MBI kushtin: një koment JSX si fëmija i parë brenda
          `{kusht && ( … )}` nuk është shprehje — dhe kjo është hera e tretë që
          më kushton një ndërtim të dështuar.) */}
      {!locked && !provaNga(3) && activeScreen === "services" && (
        <ServicesScreen familyId={activeFamilyId} C={KART} F={FONTS} t={t}
          // HAPESIRA — shtëpia e vërtetë e numrave. Kalohet si funksion e jo si
          // të dhëna: kështu `ServicesScreen.jsx` s'ka nevojë të dijë asgjë për
          // kuotat, ngjyrat apo mënyrat — vetëm ku ta vendosë atë që i jepet.
          renderHapesiren={() => renderMatesinEArkives("sherbimet")}
          // E njëjta listë që ushqen zgjedhësin e turneut — një burim, jo dy.
          onListPeople={() => turnePersonat}
          onGenerateBook={async (stil, fusha) => {
            // DY BOTIME NGA I NJËJTI BUTON (26 gusht 2026)
            //
            //   rrefim     Libri i Familjes — kapituj e momente. Parazgjedhja.
            //   regjistri  Regjistri i Familjes — lista sipas datëlindjes,
            //              botimi i gushtit, i paprekur.
            //
            // Import dinamik: pdf-lib (~200kb) hyn në bundle vetëm kur dikush e
            // gjeneron vërtet librin, jo në çdo nisje.
            const botimi = stil === "regjistri" ? "regjistri" : "rrefim";
            const { generateFamilyBook } = await import("./familyBook.js");
            const memories = (familyHistoryEvents || []).map((ev) => ({
              title: ev.title, date: ev.event_date || "", text: ev.description || "",
            }));
            const bytes = await generateFamilyBook({
              familyName: treeName, people, memories,
              // Ngjarjet e papërpunuara i duhen motorit të rrëfimit: `memories`
              // është pamja e gatshme për faqen «Kujtime», ndërsa `tregimi.js`
              // e do datën për ta vendosur ngjarjen te kapitulli i vet.
              ngjarjet: familyHistoryEvents || [],
              stil: botimi,
              // Fusha vjen nga kartela e Shërbimeve dhe vlen vetëm për rrëfimin;
              // regjistri e injoron sepse duhet të mbetet i plotë.
              fusha: botimi === "rrefim" ? (fusha || null) : null,
              imageUrlFor: (p) => (p.photo_path ? photoUrl(p.photo_path) : null),
            });
            // Dorëzimi ndahet sipas platformës te `saveFile.js`: në web
            // shkarkim, në APK shkrim te Cache-i + Share-Sheet. `<a download>`
            // me blob është i heshtur në WebView-në e Android-it.
            const { saveAndShare } = await import("./saveFile.js");
            const emri = botimi === "regjistri" ? "Regjistri-i-Familjes" : "Libri-i-Familjes";
            // Dy libra të së njëjtës familje — njëri i plotë, tjetri i një dege
            // — do të kishin të njëjtin emër dhe i dyti do ta mbishkruante të
            // parin te dosja e shkarkimeve. Emri i personit e ndan.
            const sufiks = botimi === "rrefim" && fusha?.lloj && fusha.lloj !== "gjithe"
              ? "-" + String(turnePersonat.find((x) => x.id === fusha.nga)?.emri || "")
                  .replace(/[^\w]+/g, "-").replace(/^-|-$/g, "")
              : "";
            const res = await saveAndShare({
              bytes,
              filename: `${emri}-${(treeName || "").replace(/[^\w]+/g, "-")}${sufiks}.pdf`,
              title: botimi === "regjistri" ? t("Family Register") : t("Family Book"),
            });
            if (!res.ok) throw new Error(res.error || t("Saving failed"));
            if (res.how === "saved") {
              return botimi === "regjistri"
                ? t("The register was saved on the device.")
                : t("The book was saved on the device.");
            }
            return "";
          }}
        />
      )}

      {!locked && !provaNga(3) && activeScreen === "stats" && (
        <div className="flex-1 overflow-y-auto px-4 py-4" style={{ background: `radial-gradient(circle at 20% 10%, ${COLORS.bgDeep}, ${COLORS.bg})` }}>
        {renderStatistikat(true)}
        </div>
      )}



      </div>
        </div>

{selectedId && people[selectedId] && (
        <div
          onClick={(e) => e.stopPropagation()}
          onTouchStart={onProfileTouchStart}
          onTouchMove={onProfileTouchMove}
          onTouchEnd={onProfileTouchEnd}
          onTouchCancel={onProfileTouchEnd}
          className="fixed inset-0 sm:inset-auto sm:top-0 sm:right-0 sm:h-full w-full sm:w-80 sm:max-w-xs shadow-2xl z-40 overflow-y-auto"
          style={{
            background: COLORS.panel, borderLeft: `1px solid ${COLORS.goldSoft}`,
            transform: profileDragX ? `translateX(${profileDragX}px)` : undefined,
            opacity: profileDragX ? Math.max(0.35, 1 - profileDragX / 280) : 1,
            transition: profileDragging ? "none" : "transform 0.22s ease, opacity 0.22s ease",
          }}
        >
          <div className="p-5" style={{ paddingTop: `max(1.25rem, ${SAFE_TOP})` }}>
            {/* Shigjeta mbrapa — VETËM telefon; X — VETËM desktop (njësoj si Modal) */}
            <ButonIkone onClick={() => setSelectedId(null)} className="flex sm:hidden mb-1 -ml-2.5" rrethi={{ background: COLORS.panelSoft }}><ChevronLeft size={20} color={COLORS.ink} /></ButonIkone>
            <ButonIkone onClick={() => setSelectedId(null)} className="hidden sm:flex mb-1 -ml-2.5"><X size={18} color={COLORS.inkLight} /></ButonIkone>
            <div
              onClick={() => people[selectedId].photo_path && setPhotoLightbox(people[selectedId].photo_path)}
              className="rounded-xl overflow-hidden mb-4 flex items-center justify-center"
              style={{ width: 96, height: 96, background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`, cursor: people[selectedId].photo_path ? "zoom-in" : "default" }}
            >
              {people[selectedId].photo_path ? <CroppedImg src={photoUrl(people[selectedId].photo_path)} focusX={people[selectedId].photo_focus_x ?? 0.5} focusY={people[selectedId].photo_focus_y ?? 0.5} zoom={people[selectedId].photo_zoom ?? 1} size={96} downloadable={canDownloadPhoto(people[selectedId])} style={{ borderRadius: 12 }} /> : <UserCircle2 size={40} color={COLORS.inkLight} />}
            </div>
            <p
              onClick={() => people[selectedId].photo_path && setPhotoLightbox(people[selectedId].photo_path)}
              style={{ fontFamily: FONTS.display, fontSize: 24, fontWeight: 600, color: COLORS.ink, cursor: people[selectedId].photo_path ? "zoom-in" : "default" }}
            >
              {displayName(people[selectedId])}{" "}
              {people[selectedId].death_date && (
                <span title={t("Deceased")} className="inline-flex items-center justify-center rounded-full" style={{ width: 22, height: 22, background: COLORS.moss, fontSize: 13 }}>
                  🕊️
                </span>
              )}
            </p>
            {people[selectedId].maiden_name && <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontStyle: "italic", color: COLORS.inkLight, marginTop: 1 }}>{t("Maiden name: {{name}}", { name: people[selectedId].maiden_name })}</p>}
            {!currentPersonId ? (
              <p className="mt-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("Sign in to see more.")}</p>
            ) : (
              <>
                <p style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 500, color: COLORS.inkLight, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{lifespan(people[selectedId])}</p>
                {(people[selectedId].birth_city || people[selectedId].birth_country) && <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 2 }}><IkonaP Lucide={_MapPin} emoji="📍" size={13} color={COLORS.inkLight} style={{ verticalAlign: "-2px", marginRight: 4 }} />{[people[selectedId].birth_city, displayCountry(people[selectedId].birth_country, i18n.language) || people[selectedId].birth_country].filter(Boolean).join(", ")}</p>}
                {(people[selectedId].residence_city || people[selectedId].residence_country) && <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 2 }}><IkonaP Lucide={_Home} emoji="🏠" size={13} color={COLORS.inkLight} style={{ verticalAlign: "-2px", marginRight: 4 }} />{t("Lives in {{place}}", { place: [people[selectedId].residence_city, displayCountry(people[selectedId].residence_country, i18n.language) || people[selectedId].residence_country].filter(Boolean).join(", ") })}</p>}
                {people[selectedId].external_tree_url && (
                  <a href={people[selectedId].external_tree_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.branchNearCousin, textDecoration: "underline" }}>{t("🔗 The other family tree")}</a>
                )}
                <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", background: ngjyraEDegesSoft(effectiveBranch(people[selectedId])), color: ngjyraEDeges(effectiveBranch(people[selectedId])) }}>
                  {branchLabel(effectiveBranch(people[selectedId]))}
                  {relationMap && relationMap[selectedId] && <span title={t("Calculated automatically")}>🧭</span>}
                </span>
                {(() => {
                  const bloodSeen = new Set();
                  const stack = [...(baseChildrenMap[selectedId] || [])];
                  while (stack.length) {
                    const id = stack.pop();
                    if (bloodSeen.has(id)) continue;
                    bloodSeen.add(id);
                    (baseChildrenMap[id] || []).forEach((c) => stack.push(c));
                  }
                  const marriedIn = new Set();
                  bloodSeen.forEach((id) => {
                    (people[id]?.spouse_ids || []).forEach((sid) => {
                      if (people[sid] && !bloodSeen.has(sid)) marriedIn.add(sid);
                    });
                  });
                  if (bloodSeen.size === 0) return null;
                  return (
                    <div className="mt-2">
                      <p style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.inkLight }}>🌳 {bloodSeen.size} {t("blood descendants")}</p>
                      {marriedIn.size > 0 && (
                        <p style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.branchMarriedIn }}>💍 {marriedIn.size} {t("members who married in")}</p>
                      )}
                    </div>
                  );
                })()}
                {(people[selectedId].unregistered_note || canManage) && (
                  <div className="mt-3 p-2.5 rounded-md" style={{ background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}` }}>
                    {noteEditing ? (
                      <div className="space-y-2">
                        <textarea rows={2} className="w-full rounded-md px-2 py-1.5 outline-none text-sm" style={inputStyle} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder={t("E.g. The line continues, but the people aren't registered.")} />
                        <div className="flex gap-2">
                          <button onClick={saveUnregisteredNote} className="px-3 py-1 rounded-md text-xs" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("Save")}</button>
                          <button onClick={() => setNoteEditing(false)} className="px-3 py-1 rounded-md text-xs" style={{ color: COLORS.inkLight }}>{t("Cancel")}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>
                          📝 {people[selectedId].unregistered_note || (canManage ? t("Add note (e.g. unregistered descendants)") : "")}
                        </p>
                        {canManage && (
                          <button onClick={() => { setNoteDraft(people[selectedId].unregistered_note || ""); setNoteEditing(true); }} style={{ fontSize: 11, color: COLORS.inkLight, textDecoration: "underline", whiteSpace: "nowrap" }}>
                            {t("Edit")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {people[selectedId].bio && <p className="mt-3 whitespace-pre-wrap" style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.inkLight, lineHeight: 1.5 }}>{people[selectedId].bio}</p>}
                {/* FAZA 24 — "Legacy". Person i ndjerë me mesazhe të zbuluara: shfaqen
                    direkt këtu, në profilin e tij — pikërisht momenti i "zbulimit"
                    që e bën këtë veçori të veçantë. Person i gjallë (veten tënde,
                    ose çdo profil nëse je admin): buton për të menaxhuar mesazhin
                    tënd të vulosur. */}
                {people[selectedId].death_date && unlockedLegacyMessages.filter((m) => m.person_id === selectedId).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {unlockedLegacyMessages.filter((m) => m.person_id === selectedId).map((m) => (
                      <div key={m.id} className="rounded-md p-3" style={{ background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}` }}>
                        <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, marginBottom: 6 }}>💌 {t("Legacy")}{m.title ? ` — ${m.title}` : ""}</p>
                        <p className="whitespace-pre-wrap" style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink, lineHeight: 1.5 }}>{m.message_text}</p>
                      </div>
                    ))}
                  </div>
                )}
                {/* Zbulimi me verifikim: kontrollet e kujdestarit + kërkesa. */}
                {people[selectedId].death_date && selectedLegacyStatus && selectedLegacyStatus.message_count > 0 && (() => {
                  const st = selectedLegacyStatus;
                  const visibleToMe = unlockedLegacyMessages.filter((m) => m.person_id === selectedId).length > 0;
                  const scopeBtn = (val, label, primary) => (
                    <button onClick={() => setLegacyVisibility(val)} className="px-3 py-1.5 rounded-md text-xs font-medium"
                      style={st.scope === val
                        ? { background: COLORS.gold, color: COLORS.bgDeep }
                        : { background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>{label}</button>
                  );
                  return (
                    <div className="mt-3">
                      {st.am_i_unlocked_viewer && (
                        <div className="rounded-md p-3" style={{ background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}` }}>
                          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink, marginBottom: 8 }}>{t("You are a guardian — decide who sees these messages:")}</p>
                          <div className="flex flex-wrap gap-2">
                            {scopeBtn("gatekeeper", t("List only"))}
                            {scopeBtn("close", t("Close circle"))}
                            {scopeBtn("descendants", t("Descendants"))}
                            {scopeBtn("all", t("The whole family"))}
                          </div>
                          <div className="mt-3">
                            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, marginBottom: 6 }}>{t("SPECIFIC PEOPLE WHO SEE THEM")}</p>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {legacyViewers.map((v) => (
                                <span key={v.viewer_person_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}`, fontSize: 11.5, color: COLORS.ink }}>
                                  {v.viewer_name || t("(no name)")}
                                  {v.source === "shared" && <button onClick={() => removeLegacyViewer(v.viewer_person_id)} style={{ color: COLORS.inkLight, fontWeight: 700 }}>×</button>}
                                </span>
                              ))}
                              {legacyViewers.length === 0 && <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Nobody yet.")}</span>}
                            </div>
                            <SearchSelect people={people} value="" excludeId={selectedId} placeholder={t("Add a person…")} onChange={(id) => addLegacyViewer(id)} />
                          </div>
                        </div>
                      )}
                      {!visibleToMe && (
                        <div className="rounded-md p-3" style={{ background: COLORS.accentTint, border: `1px dashed ${COLORS.goldSoft}` }}>
                          <p style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.ink, marginBottom: 6 }}>💌 {t("{{sa}} legacy message(s) — sealed", { sa: st.message_count })}</p>
                          {st.my_request_status === "pending" ? (
                            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("Your request has been sent — awaiting approval from the platform.")}</p>
                          ) : st.my_request_status === "needs_info" ? (
                            <>
                              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginBottom: 8 }}>{t("The platform asked for more information")}{st.my_request_note ? `: “${st.my_request_note}”` : ""}. {t("Fill in or re-upload the request.")}</p>
                              <button onClick={() => { setLegacyReqNote(""); setLegacyReqFile(null); setLegacyReqOpen(true); }} className="px-3 py-1.5 rounded-md text-xs font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("Complete the request")}</button>
                            </>
                          ) : currentPersonId ? (
                            <>
                              {(st.am_i_close_circle || canManage) ? (
                                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginBottom: 8 }}>{t("To see them, make a request with the death certificate. Each close-circle member can request separately; after approval, they read them and decide who else sees them.")}</p>
                              ) : (
                                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink, marginBottom: 8 }}>{t("⚠ You are not in the close circle (spouse or child) of this person. You can request anyway — but it is marked as")} <b>{t("third party")}</b> {t("and the platform reviews it with extra care; the guardian decides whether to share them.")}</p>
                              )}
                              <button onClick={() => { setLegacyReqNote(""); setLegacyReqFile(null); setLegacyReqOpen(true); }} className="px-3 py-1.5 rounded-md text-xs font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("Request to see the messages")}</button>
                            </>
                          ) : (
                            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("These messages open only upon a family member's request, with a death certificate and approval.")}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {!people[selectedId].death_date && (selectedId === currentPersonId || canManage) && (
                  <button onClick={() => openMyLegacy(selectedId)} className="mt-3 flex items-center gap-1.5 text-xs" style={{ color: COLORS.inkLight, textDecoration: "underline" }}>
                    {legacyTier && legacyTier.i_hapur === false ? "🔒 " : "💌 "}{selectedId === currentPersonId ? t("My legacy — a message for future generations") : t("Manage Legacy")}
                  </button>
                )}
                <div className="mt-4 space-y-1">
                  {(people[selectedId].parent_ids || []).length > 0 && <p style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.inkLight }}>{t("Parents:")} {(people[selectedId].parent_ids || []).map((id) => displayName(people[id] || {})).join(", ")}</p>}
                  {/* FIX (kërkesë e userit — "martesa të shumta/divorc, dhe
                      mundësia të fshihet dikush nga pema PERSONALE") — lista
                      e thjeshtë e mëparshme (tekst i vetëm, pa veprime) u
                      zëvendësua me rreshta të veçantë PËR SECILIN
                      bashkëshort/e, me: (1) "Shkurorëzo" — heq lidhjen
                      t("spouse") (rpc_set_spouses, p_link=false — RPC-ja
                      e mbështeste tashmë), të dy mbeten në pemë; (2) "Fshihe
                      për mua"/"Shfaqe" — krejt personale, çdokush fsheh
                      kë të dojë VETËM nga pamja e vet, pa prekur çfarë
                      shohin të tjerët (as vetë personi tjetër). */}
                  {(people[selectedId].spouse_ids || []).length > 0 && (
                    <div>
                      <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, marginBottom: 6 }}>{t("Spouse:")}</p>
                      {(people[selectedId].spouse_ids || []).map((sid) => (
                        <div key={sid}>
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>{displayName(people[sid] || {})}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            {(() => {
                              const isDiv = (people[selectedId].divorced_from_ids || []).includes(sid);
                              const isCohab = (people[selectedId].cohabiting_ids || []).includes(sid);
                              const isEng = (people[selectedId].engaged_ids || []).includes(sid);
                              const canRel = canLinkSpouseFor(selectedId) || selectedId === currentPersonId;
                              const canHide = currentPersonId && (canHideForMe(currentPersonId, sid) || myHiddenPeople.has(sid));
                              const isHidden = myHiddenPeople.has(sid);
                              const deceased = people[selectedId].death_date || people[sid]?.death_date;
                              const opts = [];
                              if (canRel) {
                                if (isDiv) opts.push(["undivorce", t("💔 Cancel the divorce")]);
                                else if (!deceased) opts.push(["divorce", t("💔 Divorce")]);
                                if (!isDiv) {
                                  opts.push(isCohab ? ["uncohabit", t("🤝 Cancel cohabitation")] : ["cohabit", t("🤝 Cohabitation")]);
                                  opts.push(isEng ? ["unengage", t("💍 Cancel engagement")] : ["engage", t("💍 Engaged")]);
                                }
                              }
                              if (canManage) opts.push(["unlink", t("✕ Remove the link (mistake)")]);
                              if (canHide) opts.push(isHidden ? ["unhide", t("👁 Show in my tree")] : ["hide", t("🙈 Hide for me")]);
                              if (opts.length === 0) return null;
                              const statusLabel = isDiv ? t("Divorced") : isCohab ? t("Cohabitation") : isEng ? t("Engaged") : t("Married");
                              return (
                                <select value="" onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) handleSpouseAction(selectedId, sid, v); }} style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.ink, background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`, borderRadius: 6, padding: "3px 6px", cursor: "pointer" }} title={t("Status / actions")}>
                                  <option value="">⚙ {statusLabel} ▾</option>
                                  {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                              );
                            })()}
                          </div>
                        </div>
                        {/* DATA E MARTESËS — një rresht për ÇIFT, jo për person.
                            Futet te profili i njërit dhe duket te të dy, sepse
                            tabela `marriages` e ruan çiftin një herë të vetme
                            (`person_a < person_b`). Data e ardhjes së
                            bashkëshortit/es në familje NUK është data e martesës,
                            prandaj feed-i mbështetet vetëm te kjo fushë.
                            Kutia "Shfaqe në feed" e heq përvjetorin nga feed-i;
                            mjafton njëri ta heqë, se ngjarja u takon të dyve.
                            E fshehur kur çifti është i divorcuar ose kur njëri
                            ka ndërruar jetë — atëherë përvjetori s'ka vend. */}
                        {(() => {
                          const isDiv = (people[selectedId].divorced_from_ids || []).includes(sid);
                          const deceased = people[selectedId].death_date || people[sid]?.death_date;
                          if (isDiv || deceased) return null;
                          // I njëjti kufi si te serveri: admin/redaktor, ose vetë njëri nga të dy.
                          const mayEdit = canLinkSpouseFor(selectedId) || selectedId === currentPersonId || sid === currentPersonId;
                          const key = pairKey(selectedId, sid);
                          const row = marriages[key];
                          if (!mayEdit && !row?.married_on) return null;
                          const busy = marriageSaving === key;
                          return (
                            <div className="flex items-center gap-2 flex-wrap pl-1 pb-1.5" style={{ opacity: busy ? 0.6 : 1 }}>
                              <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("Wedding date")}</span>
                              {mayEdit ? (
                                <input
                                  type="date" value={row?.married_on || ""} disabled={busy}
                                  onChange={(e) => saveMarriage(selectedId, sid, e.target.value, row?.show_in_feed !== false)}
                                  style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`, borderRadius: 6, padding: "2px 6px" }}
                                />
                              ) : (
                                <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{row.married_on}</span>
                              )}
                              {mayEdit && row?.married_on && (
                                <label className="flex items-center gap-1" style={{ cursor: busy ? "default" : "pointer" }}>
                                  <input
                                    type="checkbox" checked={row.show_in_feed !== false} disabled={busy}
                                    onChange={(e) => saveMarriage(selectedId, sid, row.married_on, e.target.checked)}
                                  />
                                  <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("Show in the feed")}</span>
                                </label>
                              )}
                            </div>
                          );
                        })()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(canManage || selectedId === currentPersonId) && selectedContact && (
                  <div className="mt-3 p-2.5 rounded-md" style={{ background: COLORS.panelSoft }}>
                    <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{selectedId === currentPersonId ? t("person.myContact") : t("🔒 CONTACT (ADMINS ONLY)")}</p>
                    <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink, marginTop: 3 }}>{(selectedContact.email && !emailIFshehurNgaApple(selectedContact.email) ? selectedContact.email : "") || "— pa email —"}</p>
                    <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>{selectedContact.phone || "— pa telefon —"}</p>
                  </div>
                )}
                {/* NDËRTUAR (19 gusht 2026) — më parë ky ishte i vetmi buton që
                    hapte `openMourning`, dhe rrinte pas `canManage`. Rrjedhoja:
                    dega jo-admin brenda vetë funksionit dhe i gjithë modali
                    `simpleMourningOpen` s'arriheshin KURRË, edhe pse serveri e
                    mbështeste plotësisht skenarin (`fn_is_close_relative`).
                    Veçori e ndërtuar dhe e mbyllur pa dashje. Tani butoni u
                    shfaqet edhe të afërmve të ngushtë — ata marrin kompozuesin e
                    thjeshtë, jo Postën e Familjes, pra s'shohin korrespondencën
                    e familjes. */}
                {people[selectedId].death_date && (canManage || isCloseRelativeOf(currentPersonId, selectedId)) && (
                  <button onClick={() => openMourning(people[selectedId])} className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.dangerSoft, color: COLORS.danger }}>
                    {t("🕊️ Notify the family")}
                  </button>
                )}
              </>
            )}
            {(canAddChildFor(selectedId) || canEditFor(selectedId) || selectedId === currentPersonId) && (
              <div className="mt-6 flex flex-col gap-2">
                {canEditFor(selectedId) && <button onClick={() => openEdit(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><Edit2 size={14} /> {t("Edit the details")}</button>}
                {/* FAZA 5 — gjenero kodin e bashkimit për këtë MYSAFIR (H-…), vetëm admin. */}
                {/* REDAKTOR1 (6 shtator 2026) — kod personal NGA KARTELA: admini për
                    të gjithë, redaktori (+) brenda fushëveprimit, kontribuesi për
                    fëmijët dhe bashkëshortin/en e vet. Serveri e refuzon nëse personi
                    ka tashmë llogari — mesazhi del si toast. */}
                {kodPersonalILejuar(selectedId) && !llogaritEAnetareve[selectedId] && <button onClick={() => kopjoKodinPersonal(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><KeyRound size={14} /> {t("Copy a personal join code")}</button>}
                {canManage && eshteMysafir(people[selectedId]) && <button onClick={() => generateHonoraryCode(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}>🔗 {t("Generate guest join code")}</button>}
                {/* Kush mund ta shohë çfarë — vendoset nga ADMINI I FAMILJES,
                    këtu te profili i mysafirit (jo vetëm nga Admin Center). */}
                {canManage && eshteMysafir(people[selectedId]) && <button onClick={() => openGuestAccess(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}>👁️ {t("What this guest can see")}</button>}
                {!canEditFor(selectedId) && selectedId === currentPersonId && (
                  <button onClick={() => openSelfEdit()} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><Camera size={14} /> {t("Edit my photo/contact")}</button>
                )}
                {canAddChildFor(selectedId) && <button onClick={() => openAdd({ parent1: selectedId })} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><Plus size={14} /> {t("Add child")}</button>}
                {/* SHTO PRIND (ndërtim poshtë-lart) — vetëm ADMIN. Shfaqet vetëm
                    nëse personi ka ende vend për prind (< 2 prindër). */}
                {canManage && (people[selectedId].parent_ids || []).length < 2 && <button onClick={() => openAdd({ childOf: selectedId })} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><Plus size={14} /> {t("Add parent")}</button>}
                {/* FIX (kërkesë e userit) — "Shto/Lidh bashkëshort/e" tani
                    shfaqet VETËM nëse s'ka ende partner AKTIV (jo të
                    divorcuar) — nëse ka, menaxhimi bëhet te rreshtat e
                    bashkëshortëve më sipër (Shkurorëzo/Anulo/Fshihe për
                    mua), jo nga një buton i përgjithshëm "shto" konfuz
                    ngjitur me listën ekzistuese. */}
                {(() => {
                  const hasActiveSpouse = (people[selectedId].spouse_ids || []).some((sid) => !(people[selectedId].divorced_from_ids || []).includes(sid));
                  if (hasActiveSpouse) return null;
                  return (canLinkSpouseFor(selectedId) || selectedId === currentPersonId) && (
                    <button onClick={() => setSpouseModalFor(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><Heart size={14} /> {t("Add/Link spouse")}</button>
                  );
                })()}
                {/* FIX (kërkesë e userit — "asnjë person tjetër, veç
                    partnerit/ish-partnerit, s'duhet ta ketë këtë opsion")
                    — më parë shfaqej për KËDO (fëmijë, vëllezër, etj.);
                    tani VETËM kur `canHideForMe` e lejon (divorc mes
                    dyve, ose ish i ndjerë nga bashkëshorti/ja aktual/e). */}
                {currentPersonId && selectedId !== currentPersonId && (canHideForMe(currentPersonId, selectedId) || myHiddenPeople.has(selectedId)) && (
                  myHiddenPeople.has(selectedId) ? (
                    <button onClick={() => unhidePersonForMe(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><EyeOff size={14} /> {t("Show in my tree")}</button>
                  ) : (
                    <button onClick={() => hidePersonForMe(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}><EyeOff size={14} /> {t("Hide from my tree")}</button>
                  )
                )}
                {/* I fundit s'mund të hiqet — familja do të mbetej bosh (kur ka
                    vetëm 1 anëtar). Admini i VETËM i familjes fshihet vetëm nga
                    Super Admini i platformës. Të dyja edhe server-side. */}
                {/* LLOGARIA1 — shkëputja e llogarisë rri E PARAFUNDIT, ngjitur me
                    «Fshi personin»: veprimet e rrezikshme bashkë, larg butonave të
                    përditshëm — që të mos shtypen gabimisht (pronari, 13 shtator). */}
                {kodPersonalILejuar(selectedId) && llogaritEAnetareve[selectedId] && roles[selectedId] !== "admin" && <button onClick={() => shkeputLlogarine(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm mt-2" style={{ background: COLORS.dangerSoft, color: COLORS.danger }}><AlertTriangle size={14} /> {t("members.unlinkAccount")}</button>}
                {canDeletePerson(selectedId) && <button onClick={() => deletePerson(selectedId)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm mt-2" style={{ background: COLORS.dangerSoft, color: COLORS.danger }}><Trash2 size={14} /> {t("Remove the person")}</button>}
              </div>
            )}
            {/* RAPORTIMI1 — bllok I VETI, jashtë portës së lejeve sipër. Nëse do
                të hynte brenda saj, butonin do ta shihte vetëm ai që mund ta
                ndreqte vetë kartelën — pra kurrë ai që ka nevojë ta raportojë.
                Nuk shfaqet te kartela jote: raportimi i vetvetes s'ka kuptim. */}
            {selectedId !== currentPersonId && (
              <div className="mt-6 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                <button
                  onClick={() => hapRaportimin("person", selectedId, displayName(people[selectedId]))}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm"
                  style={{ background: "transparent", color: COLORS.inkLight, border: `1px solid ${COLORS.goldSoft}` }}
                >
                  ⚑ {t("Report this profile")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RAPORTIMI1 · dritarja e raportimit ────────────────────────────
          Rri JASHTË bllokut `!provaNga(5)`: një dritare raportimi që zhduket
          bashkë me dritaret e tjera gjatë një prove do të ishte pikërisht ajo
          që recensuesi i Apple-it s'do ta gjente. */}
      {raportoObj && (
        <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center"
             style={{ background: "rgba(0,0,0,.45)" }}
             onClick={() => !raportoDuke && setRaportoObj(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5"
               style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, maxHeight: "90vh", overflowY: "auto", overflowX: "hidden" }}>
            <p style={{ fontFamily: FONTS.display, fontSize: 17, color: COLORS.ink, margin: 0 }}>
              {t("Report content")}
            </p>
            {raportoObj.titulli && (
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, margin: "4px 0 0" }}>
                {raportoObj.titulli}
              </p>
            )}
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, margin: "10px 0 6px" }}>
              {t("Why are you reporting this?")}
            </p>
            <div className="flex flex-col gap-2">
              {[
                ["spam", t("Spam or advertising")],
                ["abuzim", t("Abuse or harassment")],
                ["e_papershtatshme", t("Inappropriate content")],
                ["e_imja", t("This is mine, posted without my permission")],
              ].map(([vlera, etiketa]) => (
                <button key={vlera} onClick={() => setRaportoArsye(vlera)}
                  className="text-left px-3 py-2 rounded-md text-sm"
                  style={{
                    fontFamily: FONTS.body,
                    background: raportoArsye === vlera ? COLORS.accentTint : "transparent",
                    border: `1px solid ${raportoArsye === vlera ? COLORS.gold : COLORS.goldSoft}`,
                    color: COLORS.ink,
                  }}>
                  {etiketa}
                </button>
              ))}
            </div>
            <textarea
              value={raportoShenim}
              onChange={(e) => setRaportoShenim(e.target.value)}
              placeholder={t("Anything else we should know? (optional)")}
              rows={3}
              className="w-full mt-3 px-3 py-2 rounded-md text-sm"
              style={{ fontFamily: FONTS.body, background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, resize: "vertical" }}
            />
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, margin: "8px 0 0", lineHeight: 1.5 }}>
              {t("The report goes to the platform team, not to the family. You will find it under Help, and we answer you there.")}
            </p>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={() => setRaportoObj(null)} disabled={raportoDuke}
                className="px-3 py-2 rounded-md text-sm"
                style={{ fontFamily: FONTS.body, background: "transparent", border: `1px solid ${COLORS.goldSoft}`, color: COLORS.inkLight }}>
                {t("Cancel")}
              </button>
              <button onClick={dergoRaportimin} disabled={!raportoArsye || raportoDuke}
                className="px-3 py-2 rounded-md text-sm ml-auto"
                style={{
                  fontFamily: FONTS.body, border: "none",
                  background: (!raportoArsye || raportoDuke) ? COLORS.panelSoft : COLORS.gold,
                  color: (!raportoArsye || raportoDuke) ? COLORS.inkLight : "#fff",
                  cursor: (!raportoArsye || raportoDuke) ? "default" : "pointer",
                }}>
                {raportoDuke ? "…" : t("Send the report")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PROVA 5 — TË GJITHA DRITARET DHE MBIVENDOSJET, TË FIKURA ───────
          ⚠ E PËRKOHSHME (shih `prova.jsx`). Poshtë kësaj rrjete rrinë rreth
          2.600 rreshta dritaresh, panelesh, konfirmimesh dhe mbivendosjesh.
          Shumica presin një flamur të mbyllur, por "shumica" nuk është provë.
          Kjo i heq të gjitha përnjëherë, që të mbetet vetëm shiriti i sipërm,
          njoftimet dhe sidebar-i. */}
      {/* Brava e aplikacionit. Vetëdrejtuar: lexon vetë gjendjen nga serveri, e
          mban zgjedhjen per pajisje, dhe kthen null kur s'ka çfarë të mbrojë.
          Kur admini s'e ka ndezur, ky komponent nuk vizaton asgjë fare. */}
      {/* ── PROVA 7 ─────────────────────────────────────────────────────────
          ⚠ E PËRKOHSHME (shih `prova.jsx`). `<AppLock>` rrinte brenda bllokut
          të dritareve, dhe ishte E VETMJA gjë atje që vizatohet PA KUSHT — të
          gjitha të tjerat presin një flamur, dhe asnjë flamur nuk u hap. Pra
          ai është i vetmi ndryshim mes PROVËS 4 (u thye) dhe PROVËS 5 (rroi).
          E nxora nga blloku që të mund ta rindez vetëm atë. */}
      {(!provaNga(5) || provaNga(7)) && (
        <AppLock session={authSession} C={COLORS} F={FONTS} t={t} />
      )}
      {/* VERSIONI1 — vetëdrejtuar: s'vizaton asgjë te shfletuesi, ose kur versioni është i përditësuar. */}
      {(!provaNga(5) || provaNga(7)) && (
        <VersioniIRi C={COLORS} F={FONTS} t={t} />
      )}

      {!provaNga(5) && (<>
      {authOpen && (
        <Modal title={t("auth.signIn")} onClose={() => { setAuthOpen(false); resetAuthForm(); }}>
          {renderAuthFormBody()}
        </Modal>
      )}

      {personModal && (() => {
        // Kërkesë e userit — "fokusimi i fotografive". Llogaritur një herë
        // këtu (jo brenda JSX-it të përsëritur), ripërdorur nga foto e
        // vogël rrumbullakët, butoni "Rregullo fokusin" dhe vetë dritarja e
        // fokusit poshtë.
        const currentPhotoSrc = photoPreview || (((personModal.mode === "edit" || personModal.mode === "self") && people[personModal.id]?.photo_path && !photoRemoved) ? photoUrl(people[personModal.id].photo_path) : null);
        // `handleFocusPointer` u hoq (19 gusht 2026) — asnjë element s'e lidhte;
        // fokusimi i fotos bëhet nga `PhotoFocusPicker`, që e ka logjikën e vet.
        return (
      <Modal title={personModal.mode === "add" ? t("person.addPerson") : personModal.mode === "self" ? t("person.myProfile") : t("person.editDetails")} onClose={() => { setPersonModal(null); resetPhotoState(); }} wide>
          <div className="flex items-center gap-4 mb-4">
            <div className="rounded-full overflow-hidden flex items-center justify-center shrink-0" style={{ width: 72, height: 72, background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
              {currentPhotoSrc ? <CroppedImg src={currentPhotoSrc} focusX={photoFocus.x} focusY={photoFocus.y} zoom={photoZoom} size={72} round /> : <UserCircle2 size={34} color={COLORS.inkLight} />}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm cursor-pointer w-fit" style={{ background: COLORS.panelSoft, color: COLORS.ink }}>
                <Camera size={14} /> {photoFile || ((personModal.mode === "edit" || personModal.mode === "self") && people[personModal.id]?.photo_path && !photoRemoved) ? t("person.changePhoto") : t("person.addPhoto")}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
              </label>
              {currentPhotoSrc && (
                <button type="button" onClick={() => setFocusPickerOpen(true)} style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.ink, textAlign: "left" }}>
                  🎯 {t("person.adjustFocus")}
                </button>
              )}
              {currentPhotoSrc && (
                <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); setPhotoRemoved(true); }} style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.danger, textAlign: "left" }}>{t("person.removePhoto")}</button>
              )}
            </div>
          </div>

          {/* SHTESË (kërkesë e userit) — dritarja interaktive e fokusit:
              foto e PLOTË (object-fit: contain, s'pritet fare këtu — që
              useri ta shohë krejt kornizën), me një pikë të tërheqshme
              (klikohet ose zvarritet) që shënon "qendrën e interesit"
              (zakonisht fytyra). Përditësohet LIVE një paraparje e
              rrumbullakët poshtë, saktësisht si do të dukej te kartela. */}
          {focusPickerOpen && currentPhotoSrc && (
            <Modal title={t("person.adjustFocusTitle")} onClose={() => setFocusPickerOpen(false)}>
              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, marginBottom: 10 }}>
                {t("person.adjustFocusHint")}
              </p>
              <CropEditor
                src={currentPhotoSrc}
                focusX={photoFocus.x}
                focusY={photoFocus.y}
                zoom={photoZoom}
                onChange={({ x, y, zoom }) => { setPhotoFocus({ x, y }); setPhotoZoom(zoom); }}
              />
              <div className="flex flex-col items-center gap-1.5 mt-4">
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("person.focusPreview")}</p>
                <CroppedImg src={currentPhotoSrc} focusX={photoFocus.x} focusY={photoFocus.y} zoom={photoZoom} size={56} round style={{ border: `2px solid ${COLORS.gold}` }} />
              </div>
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={() => { setPhotoFocus({ x: 0.5, y: 0.5 }); setPhotoZoom(1); }} className="flex-1 py-2 rounded-md text-sm" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>{t("person.resetFocus")}</button>
                <button type="button" onClick={() => setFocusPickerOpen(false)} className="flex-1 py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("person.doneAdding")}</button>
              </div>
            </Modal>
          )}

          {personModal.mode === "self" ? (
            <>
              {/* PROFILI1 — kartela ime: emri, gjinia, datëlindja, vendlindja, banimi, bio */}
              <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, lineHeight: 1.45 }}>{t("profile.completeHint")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div><FieldLabel>{t("person.firstName")}</FieldLabel><input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.firstName || ""} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></div>
                <div><FieldLabel>{t("person.lastName")}</FieldLabel><input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.lastName || ""} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></div>
                <div><FieldLabel>{t("person.maidenName")}</FieldLabel><input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.maidenName || ""} onChange={(e) => setForm({ ...form, maidenName: e.target.value })} /></div>
                <div><FieldLabel>{t("person.birthDate")}</FieldLabel><DateSelect value={form.birthDate || ""} onChange={(v) => setForm({ ...form, birthDate: v })} /></div>
              </div>
              <div className="mt-3">
                <FieldLabel>{t("person.genderLabel")}</FieldLabel>
                {/* 13 shtator 2026 — lista rënëse, si te «Shto person»: një kontroll për gjininë në gjithë app-in */}
                <select className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value=""></option>
                  <option value="F">{t("person.female")}</option>
                  <option value="M">{t("person.male")}</option>
                </select>
              </div>
              <p className="mt-3 mb-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", color: COLORS.inkLight }}>{t("Birthplace")}</p>
              <div className="grid grid-cols-2 gap-3">
                <BirthPlaceFields form={form} setForm={setForm} people={people} countries={countries} canManage={canManage} addCountry={addCountry} lang={i18n.language} />
              </div>
              <p className="mt-3 mb-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", color: COLORS.inkLight }}>{t("Current residence (optional)")}</p>
              <div className="grid grid-cols-2 gap-3">
                <BirthPlaceFields form={form} setForm={setForm} people={people} countries={countries} canManage={canManage} addCountry={addCountry} lang={i18n.language}
                  celesat={{ village: null, city: "residenceCity", country: "residenceCountry" }} meFshat={false} />
              </div>
              <div className="mt-3">
                <FieldLabel>{t("person.theirStory")}</FieldLabel>
                <textarea rows={3} placeholder={t("person.theirStoryPlaceholder")} className="w-full rounded-md px-3 py-2 outline-none resize-none" style={inputStyle} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
              </div>
              <div className="p-3 rounded-md mt-3" style={{ background: COLORS.panelSoft }}>
                <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", color: COLORS.inkLight }}>{t("person.myContact")}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><FieldLabel>{t("person.email")}</FieldLabel><input type="email" className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} /></div>
                  <div><FieldLabel>{t("person.phone")}</FieldLabel><input type="tel" className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} /></div>
                </div>
                <p className="mt-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("person.onlyYouAndAdminsSee")}</p>
              </div>
              {/* Kërkesë e userit — "në profilin e vet, gjithsecili mund të
                  vendosë" — por vetëm nëse ka VËRTET mbiemër vajzërie të
                  regjistruar (admini e shton emrin vetë; këtu vetëm
                  vendoset SI shfaqet). */}
              {people[personModal.id]?.maiden_name && (
                <div className="mt-3 p-3 rounded-md" style={{ background: COLORS.panelSoft }}>
                  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: COLORS.ink }}>
                    <input
                      type="checkbox"
                      checked={!!form.maidenNameInDisplay}
                      onChange={(e) => setForm((f) => ({ ...f, maidenNameInDisplay: e.target.checked }))}
                    />
                    {t("person.showMaidenInDisplay", { example: `${people[personModal.id].first_name || ""} ${people[personModal.id].maiden_name} ${people[personModal.id].last_name || ""}`.replace(/\s+/g, " ").trim() })}
                  </label>
                </div>
              )}
              <button onClick={saveSelfEdit} className="w-full mt-5 py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("person.saveMyProfile")}</button>
            </>
          ) : (
            <>
              {/* Kërkesë e userit — formulari "Shto" tregon info thelbësore
                  për ta vendosur personin saktë në pemë. Tashmë (kërkesë e
                  re) përfshin edhe vendlindjen dhe kontaktin — mbetet
                  VETËM historia/bio për redaktim të mëvonshëm. */}
              <div className="grid grid-cols-2 gap-3">
                <div><FieldLabel>{t("person.firstName")}</FieldLabel><input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></div>
                <div><FieldLabel>{t("person.lastName")}</FieldLabel><input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></div>
                <div>
                  <FieldLabel>{t("person.birthDate")}</FieldLabel>
                  <DateSelect value={form.birthDate} onChange={(v) => setForm({ ...form, birthDate: v })} />
                  {/* DITA1 — «viti mund të hamendësohet, muaji dhe dita jo».
                      Shfaqet vetëm kur ka një datë: pa datë s'ka çfarë të
                      shënohet e përafërt. */}
                  {form.birthDate && (
                    <label className="flex items-start gap-2 mt-1.5" style={{ cursor: "pointer" }}>
                      <input type="checkbox" checked={!!form.birthDateApprox}
                        onChange={(e) => setForm({ ...form, birthDateApprox: e.target.checked })}
                        style={{ marginTop: 3, flexShrink: 0 }} />
                      <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, lineHeight: 1.4 }}>
                        {t("The date of birth is not exact")}
                        <span style={{ display: "block", fontSize: 11.5, opacity: 0.85 }}>{t("The year still counts; the month and day are left out of the statistics and birthday reminders.")}</span>
                      </span>
                    </label>
                  )}
                </div>
                {personModal.mode !== "add" && (
                  <div><FieldLabel>{t("person.deathDate")}</FieldLabel><DateSelect value={form.deathDate} onChange={(v) => setForm({ ...form, deathDate: v })} /></div>
                )}
              </div>
              {/* Kërkesë e userit — te "Shto Person" (thjeshtuar), data e
                  vdekjes s'kishte fare vend më. Checkbox me togfjalësh të
                  butë (t("has passed away"), jo "ka vdekur") e zbulon fushën e
                  datës VETËM kur nevojitet — vetëm në modalitetin "Shto"; te
                  redaktimi (edit) fusha mbetet gjithmonë e dukshme si më parë. */}
              {personModal.mode === "add" && (
                <div className="mt-3">
                  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: COLORS.ink }}>
                    <input
                      type="checkbox"
                      checked={markDeceased}
                      onChange={(e) => {
                        setMarkDeceased(e.target.checked);
                        if (!e.target.checked) setForm((f) => ({ ...f, deathDate: "" }));
                      }}
                    />
                    {t("person.hasPassedAway")}
                  </label>
                  {markDeceased && (
                    <div className="mt-2" style={{ maxWidth: 220 }}>
                      <FieldLabel>{t("person.deathDate")}</FieldLabel>
                      <DateSelect value={form.deathDate} onChange={(v) => setForm({ ...form, deathDate: v })} />
                    </div>
                  )}
                </div>
              )}
              {/* Kërkesë e userit — njësoj si "Ka ndërruar jetë" sipër,
                  checkbox me togfjalësh të butë (t("maiden name"), jo
                  ndonjë term tjetër) — e zbulon fushën VETËM kur nevojitet,
                  vetëm në "Shto" (te "Redakto" fusha mbetet gjithmonë e
                  dukshme, e ka pasur tashmë). Kur ka mbiemër vajzërie,
                  shfaqet EDHE checkbox-i "shfaqe në mes të emrit" —
                  traditë shqiptare: Emri MbiemriVajzërisë Mbiemri, p.sh.
                  "Merita Orlishta Krasniqi" (shih displayName() te App.jsx). */}
              <div className="mt-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: COLORS.ink }}>
                  <input
                    type="checkbox"
                    checked={hasMaidenName}
                    onChange={(e) => {
                      setHasMaidenName(e.target.checked);
                      if (!e.target.checked) setForm((f) => ({ ...f, maidenName: "", maidenNameInDisplay: false }));
                    }}
                  />
                  {t("person.hasMaidenName")}
                </label>
                {hasMaidenName && (
                  <div className="mt-2" style={{ maxWidth: 260 }}>
                    <FieldLabel>{t("person.maidenName")}</FieldLabel>
                    <input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.maidenName} onChange={(e) => setForm({ ...form, maidenName: e.target.value })} />
                    <label className="flex items-center gap-2 text-xs cursor-pointer mt-2" style={{ color: COLORS.inkLight }}>
                      <input
                        type="checkbox"
                        checked={!!form.maidenNameInDisplay}
                        onChange={(e) => setForm((f) => ({ ...f, maidenNameInDisplay: e.target.checked }))}
                      />
                      {t("person.showMaidenInDisplay", { example: `${form.firstName || t("First name")} ${form.maidenName || t("person.maidenName")} ${form.lastName || t("Last name")}` })}
                    </label>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <FieldLabel>{t("person.genderLabel")}</FieldLabel>
                {/* 13 shtator 2026 — lista rënëse, si te «Shto person»: një kontroll për gjininë në gjithë app-in */}
                <select className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value=""></option>
                  <option value="F">{t("person.female")}</option>
                  <option value="M">{t("person.male")}</option>
                </select>
              </div>
              {/* FIX (kërkesë e userit — "e heqim fushën manuale komplet,
                  motori automatik bëhet i vetmi") — "Dega e familjes" u hoq
                  KOMPLET (as për adminin) — dega llogaritet tani GJITHMONË
                  automatikisht (relationMap, bazuar te "Shpirt i Lirë"/
                  farefisnia). Mbetet VETËM ky dropdown, për 3 raste
                  PËRJASHTIMORE që algoritmi automatik s'i njeh dot vetë
                  (fëmijë i adoptuar, mik nderi jo-gjaku, prind i panjohur/
                  të dhëna jo të plota) — anulon automatikun VETËM për këtë
                  person specifik, jo si zgjedhje e rregullt e degës. */}
              {canManage && (
                <div className="mt-3">
                  <FieldLabel>{t("Special case (optional)")}</FieldLabel>
                  <select
                    value={form.branchSpecialCase || ""}
                    onChange={(e) => setForm({ ...form, branchSpecialCase: e.target.value || null })}
                    className="w-full py-1.5 px-2 rounded-md text-xs"
                    style={{ border: `1.5px solid ${COLORS.goldSoft}`, background: COLORS.field, color: COLORS.ink }}
                  >
                    <option value="">{t("— None (automatic, default) —")}</option>
                    <option value="adopted">{t("Adopted child")}</option>
                    <option value="mysafir">{t("Guest (not blood)")}</option>
                    <option value="unknown_parent">{t("Unknown parent / incomplete data")}</option>
                  </select>
                </div>
              )}
              {/* FIX (kërkesë e userit) — checkbox-i "Mos e trajto si fëmijë
                  me një prind" u zhvendos POSHTË fushave të prindërve (shih
                  poshtë, menjëherë pas Prindi 2) — logjikisht lidhet me
                  numrin e prindërve të zgjedhur, ndaj duhet të shihet PASI
                  ti i shikon/plotëson ato fusha, jo para. */}
              {/* FIX (kërkesë e userit) — hequr toggle-i "Shto si: Fëmijë/
                  Bashkëshort/e": kur dikush zgjedh VETEN (ose dikë tjetër)
                  si prind, sistemi e kupton vetë që po shtohet fëmijë — s'ka
                  nevojë për zgjedhje shtesë. Mbetet vetëm një checkbox i
                  vetëm, për rastin e kundërt: person i ardhur në familje
                  PËRMES MARTESE (pa asnjë prind në pemë). Vetëm në "Shto"
                  (jo redaktim). Listat e SearchSelect (prind1/prind2/
                  bashkëshort) përdorin `relationPeopleOptions`, jo `people`
                  të papërpunuar — redaktori/kontribuesi shohin VETËM
                  personat brenda zonës së tyre të të drejtave; admini
                  vazhdon të shohë gjithë familjen. */}
              {/* HEQJE (19 gusht 2026) — tri degëzime të paarritshme.
                  `openAdd()` e hap `personModal` VETËM kur pema është bosh
                  (bootstrap-i i të parit); çdo shtim tjetër kalon te
                  AddPersonSheet. Në atë gjendje `addRelationType` është
                  gjithmonë "child" (s'ka njeri për ta zgjedhur si childOf),
                  dhe kushti `treeHasAnyPeople !== false` është gjithmonë i
                  rremë. Pra: checkbox-i "shtoje si bashkëshort/e", blloku
                  "po shton prind" dhe zgjedhësi i bashkëshortit s'shfaqeshin
                  kurrë. Mbetet vetëm rruga e vërtetë: Prindi 1 / Prindi 2. */}
                  <div className="mt-3"><FieldLabel>{t("person.parent1")}</FieldLabel><SearchSelect people={personModal.mode === "add" ? relationPeopleOptions : people} value={form.parent1} onChange={(v) => setForm({ ...form, parent1: v })} placeholder={t("person.searchExisting")} excludeId={personModal.id} /></div>
                  {/* Prindi 2 (bashkëshorti/ja e prindit 1) mbetet me listë TË
                      PLOTË, jo të kufizuar te `relationPeopleOptions` — pjesa
                      tjetër e çiftit shpesh është pikërisht dikush "i
                      ardhur në familje" (p.sh. nëna, martuar brenda), pra
                      jashtë zonës së gjakut të redaktorit/kontribuesit;
                      backend-i (rpc_upsert_person) e lejon kaq — mjafton
                      QË NJËRI prind (Prindi 1) të jetë brenda zonës. */}
                  <div className="mt-3"><FieldLabel>{t("person.parent2")}</FieldLabel><SearchSelect people={people} value={form.parent2} onChange={(v) => setForm({ ...form, parent2: v })} placeholder={t("person.searchExisting")} excludeId={personModal.id} /></div>
              {/* FIX (kërkesë e userit — "duhet ta shohë edhe redaktori, jo
                  kontribuesi" + "checkbox-i nën prindin e dytë, jo mbi
                  prindërit") — zhvendosur këtu (poshtë Prindi 2), dhe
                  dukshmëria u zgjerua te admin + redaktor (jo më VETËM
                  admin, dhe JO kontribues). */}
              {["admin", "editor"].includes(myRole) && (
                <div className="mt-3 p-3 rounded-md" style={{ background: COLORS.panelSoft }}>
                  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: COLORS.ink }}>
                    <input
                      type="checkbox"
                      checked={!!form.hideSoloRing}
                      onChange={(e) => setForm({ ...form, hideSoloRing: e.target.checked })}
                    />
                    {t("Don't treat as a single-parent child")}
                  </label>
                </div>
              )}

              {/* Kërkesë e userit — vendlindja (qytet/shtet) dhe kontakti
                  tani gjithmonë të dukshme (edhe te "Shto"), jo më vetëm te
                  redaktimi. VETËM historia/bio mbetet për më vonë (kërkon
                  kohë/mendim, jo diçka që zakonisht plotësohet menjëherë
                  gjatë shtimit të shpejtë të disa personave njëri pas
                  tjetrit). */}
              <div className="mt-3 grid grid-cols-2 gap-3">
                <BirthPlaceFields form={form} setForm={setForm} people={people} countries={countries} canManage={canManage} addCountry={addCountry} lang={i18n.language} />
              </div>
              {/* BANIMI1 — vendbanimi aktual: shteti dhe qyteti, të dyja opsionale.
                  Poshtë vendlindjes, sepse pyetja është e njëjta («ku?») me një
                  kohë tjetër; ushqen hartën «Banimi aktual» dhe migrimin. */}
              <p className="mt-3 mb-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", color: COLORS.inkLight }}>{t("Current residence (optional)")}</p>
              <div className="grid grid-cols-2 gap-3">
                <BirthPlaceFields form={form} setForm={setForm} people={people} countries={countries} canManage={canManage} addCountry={addCountry} lang={i18n.language}
                  celesat={{ village: null, city: "residenceCity", country: "residenceCountry" }} meFshat={false} />
              </div>
              {/* ⚠ 5 shtator 2026 — «Lidhje me një pemë tjetër» U HOQ nga formulari.
                  Pronari: «nuk po e shoh leverdinë — kur personi nuk është anëtar
                  te pema tjetër, nuk mund ta shohë atë pemë. Ide e vjetruar që
                  nuk përshtatet me drejtimin që ka marrë aplikacioni.»
                  Kishte të drejtë: fusha premtonte një urë që nuk ekziston. Sot
                  familjet lidhen me `rpc_sync_person_across_families` dhe me
                  kodet e familjes — jo me një link që hapet vetëm për ata që
                  tashmë kanë qasje.
                  Kolona `external_tree_url` MBETET te baza dhe vlerat e vjetra
                  ruhen (shih `saveForm`): fshirja e të dhënave është vendim
                  tjetër nga heqja e një fushe. Linku i vjetër, po të ketë, ende
                  shfaqet te profili. */}
              {canManage && (
                <div className="mt-3 p-3 rounded-md" style={{ background: COLORS.panelSoft }}>
                  <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", color: COLORS.inkLight }}>{t("person.adminOnlyContact")}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><FieldLabel>{t("person.email")}</FieldLabel><input type="email" className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} /></div>
                    <div><FieldLabel>{t("person.phone")}</FieldLabel><input type="tel" className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} /></div>
                  </div>
                  <p className="mt-1.5" style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight }}>
                    {t("person.adminOnlyContactNote")}
                  </p>
                </div>
              )}
              {/* MUZEU3 — salla. Del vetëm te një koleksion, sepse vetëm aty ka
                  kuptim: te një familje ose te një muze-pemë, boshti është
                  lidhja, dhe një fushë e mbushur aty ku s'ka kuptim bëhet e
                  dhënë e rreme që dikush do ta besojë më vonë.
                  Ruhet menjëherë, me RPC-në e vet — jo bashkë me personin,
                  sepse salla nuk është e dhënë e personit (si datëlindja) por
                  vendi ku kuratori e vendos brenda ekspozitës. */}
              {eshteKoleksion && personModal.mode !== "add" && (
                <div className="mt-3">
                  <FieldLabel>{t("Hall")}</FieldLabel>
                  <input
                    list="pef-sallat"
                    className="w-full rounded-md px-3 py-2 outline-none"
                    style={inputStyle}
                    placeholder={t("Leave empty if not placed yet")}
                    defaultValue={people[selectedId]?.salla || ""}
                    onBlur={async (e) => {
                      const v = e.target.value;
                      const { error } = await supabase.rpc("rpc_vendos_sallen", { p_person_id: selectedId, p_salla: v });
                      if (error) { showToast(error.message || String(error)); return; }
                      await fetchPeople();
                      setLayoutVersion((n) => n + 1);
                    }}
                  />
                  <datalist id="pef-sallat">
                    {sallatEKoleksionit.filter(Boolean).map((em) => <option key={em} value={em} />)}
                  </datalist>
                </div>
              )}
              {personModal.mode !== "add" && (
                <div className="mt-3">
                  <FieldLabel>{t("person.theirStory")}</FieldLabel>
                  <textarea rows={4} placeholder={t("person.theirStoryPlaceholder")} className="w-full rounded-md px-3 py-2 outline-none resize-none" style={inputStyle} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
                </div>
              )}
              {personModal.mode === "add" && (
                <p className="mt-3" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, lineHeight: 1.4 }}>
                  {t("person.moreDetailsLaterNote")}
                </p>
              )}
              {/* Faza 11 — fushat custom nga template-i i publikuar (vetëm jo-core).
                  Filtrohen sipas visible_for kur je te "Shto" (child/parent/partner). */}
              {(() => {
                const flds = (customTemplate && Array.isArray(customTemplate.fields) ? customTemplate.fields : [])
                  .filter((f) => !f.is_core && (!f.visible_for || f.visible_for.length === 0 || personModal.mode !== "add" || f.visible_for.includes(addRelationType)));
                if (!flds.length) return null;
                return (
                  <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                    {flds.map((f) => {
                      const val = customFieldValues[f.key] ?? "";
                      const set = (v) => setCustomFieldValues((cf) => ({ ...cf, [f.key]: v }));
                      return (
                        <div key={f.key} className="mb-3">
                          <FieldLabel>{f.label}{f.required ? " *" : ""}</FieldLabel>
                          {f.type === "boolean" ? (
                            <label className="flex items-center gap-2 text-sm" style={{ color: COLORS.ink }}>
                              <input type="checkbox" checked={!!val} onChange={(e) => set(e.target.checked)} /> {f.label}
                            </label>
                          ) : f.type === "select" ? (
                            <select className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={val} onChange={(e) => set(e.target.value)}>
                              <option value="">—</option>
                              {(Array.isArray(f.options) ? f.options : []).map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={val} onChange={(e) => set(e.target.value)} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {personModal.mode === "add" ? (
                <div className="flex gap-2 mt-5">
                  <button onClick={() => saveForm(true)} className="flex-1 py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("Add")}</button>
                  <button onClick={() => saveForm(false)} className="flex-1 py-2.5 rounded-md text-sm font-medium" style={{ background: "transparent", color: COLORS.ink, border: `1px solid ${COLORS.gold}` }}>{t("Save and add another")}</button>
                </div>
              ) : (
                <button onClick={() => saveForm(true)} className="w-full mt-5 py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("person.saveChanges")}</button>
              )}
            </>
          )}
        </Modal>
      ); })()}

      {spouseModalFor && (
        <Modal title={t("person.linkSpouseTitle", { name: displayName(people[spouseModalFor]) })} onClose={() => { setSpouseModalFor(null); setSpousePick(null); }}>
          <FieldLabel>{t("person.chooseExistingPerson")}</FieldLabel>
          {/* Kërkesë e userit — njësoj si te "Shto Person": redaktori/
              kontribuesi zgjedhin VETËM brenda zonës së tyre; admini sheh
              gjithë familjen. */}
          <SearchSelect people={canManage ? people : relationPeopleOptions} value={spousePick} onChange={setSpousePick} placeholder={t("person.searchExisting")} excludeId={spouseModalFor} />
          <button onClick={linkSpouse} disabled={!spousePick} className="w-full mt-4 py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: spousePick ? 1 : 0.5 }}>{t("person.linkAsSpouses")}</button>
        </Modal>
      )}

      {myLegacyOpen && (
        <Modal title={t("Legacy")} onClose={() => setMyLegacyOpen(false)} wide>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, lineHeight: 1.5 }}>
            {myLegacyForPersonId === currentPersonId ? t("The message stays completely hidden until you pass away — then it becomes visible to the whole family, on your profile.") : t("The message stays completely hidden until this person passes away — then it becomes visible to the whole family, on their profile.")}
          </p>
          {/* ÇFARË KA KY NJERI — dhe, nëse ka më shumë për të pasur, oferta rri
              KËTU, aty ku ai po shkruan mesazhin. Jo diku te dyqani: askush nuk
              shkon te dyqani për një gjë që s'e di se ekziston. */}
          {legacyTier && (
            <div className="mb-4 rounded-md px-3 py-2" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>
                {legacyTier.lejon_video ? t("This message can hold text, voice and video.")
                  : legacyTier.lejon_ze ? t("This message can hold text and voice.")
                  : t("This message holds text.")}
              </p>
              {!legacyTier.lejon_video && legacyTier.mund_te_bleje && myLegacyForPersonId === currentPersonId && (
                <p className="mt-1" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight, lineHeight: 1.5 }}>
                  {legacyTier.lejon_ze ? t("A video message can be added — we help you record it properly.")
                                       : t("A voice or video message can be added.")}
                  {(legacyTier.cmimet || []).filter((p) => (p.variant === "ze" && !legacyTier.lejon_ze) || p.variant === "video").map((p) => (
                    <span key={p.price_id} className="ml-2" style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.ink }}>
                      {p.variant === "ze" ? t("voice") : t("video")} {(p.amount_cents / 100).toFixed(0)} €
                    </span>
                  ))}
                </p>
              )}
            </div>
          )}
          <div className="mb-4 pb-4" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
            <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>
              {legacyForm.id ? t("Edit the message") : t("New message")}
            </p>
            <input placeholder={t("Title (optional)")} value={legacyForm.title} onChange={(e) => setLegacyForm({ ...legacyForm, title: e.target.value })} className="w-full rounded-md px-3 py-2 mb-2 outline-none text-sm" style={inputStyle} />
            <textarea placeholder={t("If you're reading this...")} value={legacyForm.messageText} onChange={(e) => setLegacyForm({ ...legacyForm, messageText: e.target.value })} className="w-full rounded-md px-3 py-2 mb-2 outline-none text-sm" style={{ ...inputStyle, minHeight: 120, resize: "vertical" }} />
            <div className="flex gap-2">
              <button onClick={() => { if (!legacyForm.messageText.trim()) { showToast(t("toast.writeATitle")); return; } setLegacyConfirm({ action: "save", applyAll: false }); }} disabled={legacySaving} className="px-3 py-1.5 rounded-md text-xs" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{legacySaving ? t("Saving…") : t("Save")}</button>
              {legacyForm.id && <button onClick={() => setLegacyForm({ id: null, title: "", messageText: "" })} className="px-3 py-1.5 rounded-md text-xs" style={{ color: COLORS.inkLight }}>{t("Cancel editing")}</button>}
            </div>
          </div>
          <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("Sealed messages")}</p>
          {myLegacyMessages.length === 0 ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("No messages yet.")}</p>
          ) : (
            <div className="space-y-2">
              {myLegacyMessages.map((m) => (
                <div key={m.id} className="rounded-md p-3" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, color: COLORS.ink }}>{m.title || "(pa titull)"}</p>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setLegacyForm({ id: m.id, title: m.title, messageText: m.message_text })} style={{ fontSize: 11, color: COLORS.inkLight, textDecoration: "underline" }}>{t("Edit")}</button>
                      <button onClick={() => setLegacyConfirm({ action: "delete", id: m.id, applyAll: false })} style={{ fontSize: 11, color: "#A33", textDecoration: "underline" }}>{t("Delete")}</button>
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, lineHeight: 1.5 }}>{m.message_text}</p>

                  {/* ZËRI DHE VIDEOJA. Shfaqen vetëm nëse niveli i blerë i
                      lejon — dhe edhe po t'i shfaqte gabimisht, baza i refuzon
                      (TRASH1). Ekrani është lehtësi; ligji rri poshtë. */}
                  {legacyTier && myLegacyForPersonId === currentPersonId && (legacyTier.lejon_ze || legacyTier.lejon_video) && (
                    <div className="mt-2 pt-2 flex items-center gap-3 flex-wrap" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                      {m.media_kind && m.media_kind !== "asnje" ? (
                        <>
                          <span style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.ink }}>
                            {m.media_kind === "video" ? t("video attached") : t("voice attached")}
                          </span>
                          <button onClick={() => removeLegacyMedia(m.id)} style={{ fontSize: 11, color: "#A33", textDecoration: "underline" }}>{t("Remove")}</button>
                        </>
                      ) : (
                        <>
                          {legacyTier.lejon_ze && (
                            <label style={{ fontSize: 11, color: COLORS.inkLight, textDecoration: "underline", cursor: "pointer" }}>
                              {legacyMediaBusy ? t("Uploading…") : t("Add voice")}
                              <input type="file" accept="audio/*" style={{ display: "none" }}
                                onChange={(e) => attachLegacyMedia(m.id, "ze", e.target.files?.[0])} />
                            </label>
                          )}
                          {legacyTier.lejon_video && (
                            <label style={{ fontSize: 11, color: COLORS.inkLight, textDecoration: "underline", cursor: "pointer" }}>
                              {legacyMediaBusy ? t("Uploading…") : t("Add video")}
                              <input type="file" accept="video/*" style={{ display: "none" }}
                                onChange={(e) => attachLegacyMedia(m.id, "video", e.target.files?.[0])} />
                            </label>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
      {/* Konfirmimi i trashëgimisë së kontrolluar — GJITHMONË para ruajtjes
          (edhe krijim i ri) dhe fshirjes, me checkbox "edhe në familjet e
          tjera" kur llogaria është anëtare në >1 familje (LEGACY_multi_family.sql). */}
      {legacyConfirm && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-5" style={{ background: "rgba(0,0,0,.45)" }} onClick={() => setLegacyConfirm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-xl p-4 shadow-2xl" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}>
            <p style={{ fontFamily: FONTS.display, fontSize: 16, fontWeight: 600, color: COLORS.ink }}>
              {legacyConfirm.action === "delete" ? t("Delete this legacy message?") : t("Save the legacy message?")}
            </p>
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 6, lineHeight: 1.5 }}>
              {legacyConfirm.action === "delete"
                ? t("This is part of your controlled legacy — confirm before it is removed.")
                : t("Confirm before the change is saved — this is your controlled legacy.")}
            </p>
            {myLinkedFamilies.length > 1 && (
              <label className="flex items-start gap-2 mt-3 cursor-pointer" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>
                <input type="checkbox" checked={legacyConfirm.applyAll} onChange={(e) => setLegacyConfirm((c) => ({ ...c, applyAll: e.target.checked }))} style={{ marginTop: 2 }} />
                <span>{t("Apply in the other families where I am a member too")} ({myLinkedFamilies.length - 1})</span>
              </label>
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setLegacyConfirm(null)} className="flex-1 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontFamily: FONTS.body }}>{t("Cancel")}</button>
              <button onClick={confirmLegacyAction} className="flex-1 py-2 rounded-md text-sm font-medium" style={{ background: legacyConfirm.action === "delete" ? "#A33" : COLORS.gold, color: legacyConfirm.action === "delete" ? "#fff" : COLORS.bgDeep, fontFamily: FONTS.body }}>
                {legacyConfirm.action === "delete" ? t("Delete") : t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* EKRANI I DETYRUESHËM "Vendos fjalëkalimin" — pas konfirmimit të email-it.
          Pa X dhe pa mbyllje: derisa fjalëkalimi të vendoset, s'ka ku të shkohet. */}
      {needsPasswordSetup && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center p-5" style={{ background: "rgba(15,18,13,0.75)" }}>
          <div className="w-full max-w-sm rounded-xl p-5 shadow-2xl" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}` }}>
            <p style={{ fontFamily: FONTS.display, fontSize: 18, fontWeight: 600, color: COLORS.ink }}>{recoveryMode ? t("Choose a new password") : t("Set your password")}</p>
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 6, lineHeight: 1.5 }}>
              {recoveryMode ? t("Write the new password twice. It replaces the old one right away.") : t("Your email is confirmed. Choose a password — you will use it every time you sign in.")}
            </p>
            <div className="mt-3">
              <FieldLabel>{t("auth.password")}</FieldLabel>
              <input type="password" value={setPwd.a} onChange={(e) => setSetPwd({ ...setPwd, a: e.target.value })} className="w-full rounded-md px-3 py-2 outline-none text-sm" style={inputStyle} autoFocus />
            </div>
            <div className="mt-2">
              <FieldLabel>{t("Repeat the password")}</FieldLabel>
              <input type="password" value={setPwd.b} onChange={(e) => setSetPwd({ ...setPwd, b: e.target.value })} className="w-full rounded-md px-3 py-2 outline-none text-sm" style={inputStyle} onKeyDown={(e) => { if (e.key === "Enter") submitNewPassword(); }} />
            </div>
            {setPwdErr && <p style={{ color: COLORS.danger, fontSize: 12.5, marginTop: 8 }}>{setPwdErr}</p>}
            <button onClick={submitNewPassword} disabled={setPwdBusy || !setPwd.a || !setPwd.b} className="w-full mt-4 py-2.5 rounded-lg text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body, opacity: (setPwdBusy || !setPwd.a || !setPwd.b) ? 0.5 : 1 }}>
              {setPwdBusy ? t("Saving…") : t("Save the password")}
            </button>
            <button onClick={handleSignOut} className="w-full mt-2 py-2 rounded-lg text-xs" style={{ background: "transparent", color: COLORS.inkLight, fontFamily: FONTS.body }}>{t("nav.signOut")}</button>
          </div>
        </div>
      )}
      {guestAccessFor && people[guestAccessFor] && (
        <Modal title={`👁️ ${t("What this guest can see")}`} onClose={() => setGuestAccessFor(null)} wide>
          <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, lineHeight: 1.6, marginBottom: 12 }}>
            {t("Mysafiri")}: <b style={{ color: COLORS.ink }}>{displayName(people[guestAccessFor])}</b>. {t("Rules are applied on the server — the guest never receives data you have not allowed.")}
          </p>

          {/* ARSYEJA DHE DRITARJA E AKSESIT — të redaktueshme edhe pas krijimit.
              Arsyeja është e detyrueshme te serveri dhe ruhet te regjistri i
              veprimeve; datat mund të lihen bosh për akses pa afat. */}
          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("Reason and access window")}</p>
          <div className="space-y-2 mb-4">
            <textarea
              value={guestMeta.reason} disabled={guestBusy}
              onChange={(e) => setGuestMeta({ ...guestMeta, reason: e.target.value })}
              placeholder={t("Why is this guest in the tree?")}
              className="w-full rounded-md px-3 py-2 outline-none text-sm"
              style={{ ...inputStyle, minHeight: 58, resize: "vertical", fontFamily: FONTS.body }}
            />
            <div className="flex gap-2 items-center flex-wrap">
              <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 500, color: COLORS.inkLight }}>{t("From")}</span>
              <input type="date" value={guestMeta.start} disabled={guestBusy} onChange={(e) => setGuestMeta({ ...guestMeta, start: e.target.value })} className="rounded-md px-2 py-1.5 text-xs" style={inputStyle} />
              <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 500, color: COLORS.inkLight }}>{t("Until")}</span>
              <input type="date" value={guestMeta.end} disabled={guestBusy} onChange={(e) => setGuestMeta({ ...guestMeta, end: e.target.value })} className="rounded-md px-2 py-1.5 text-xs" style={inputStyle} />
              <button onClick={saveGuestMeta} disabled={guestBusy} className="px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body }}>{t("Save")}</button>
            </div>
            <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, lineHeight: 1.5 }}>
              {t("Leave the dates empty for access without an end date. The reason is required and is kept in the audit log.")}
            </p>
          </div>

          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("Rules")}</p>
          {guestRules.length === 0 ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginBottom: 10 }}>{t("No rules — the guest sees only themselves.")}</p>
          ) : (
            <div className="mb-2">
              {guestRules.map((r) => (
                <div key={r.id} className="flex items-center gap-2 py-1.5" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
                  <span style={{ fontSize: 10, fontWeight: 800, borderRadius: 99, padding: "2px 7px", background: r.effect === "allow" ? COLORS.accentTint : "#fbe4e1", color: r.effect === "allow" ? COLORS.ink : "#b23a3a", fontFamily: FONTS.body }}>
                    {r.effect === "allow" ? t("ALLOW") : t("DENY")}
                  </span>
                  <span style={{ flex: 1, fontSize: 12.5, color: COLORS.ink, fontFamily: FONTS.body }}>
                    {r.scope === "family" ? t("Everyone in the family") : <>{displayName(people[r.target_person_id] || {})} — {t((GUEST_SCOPES.find((x) => x[0] === r.scope) || [])[1] || r.scope)}</>}
                    {r.depth ? ` · ${r.depth}` : ""}
                    {r.effect === "allow" && r.include_spouses ? ` · ${t("+ spouses")}` : ""}
                  </span>
                  <button onClick={() => delGuestRule(r.id)} style={{ color: COLORS.rose, fontSize: 13 }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {guestRules.length > 0 && (
            <button onClick={guestResetRules} disabled={guestBusy} className="mb-3" style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.rose, textDecoration: "underline", padding: 0 }}>
              {t("Remove all rules")}
            </button>
          )}

          <div className="space-y-2 mb-4">
            <div className="flex gap-2">
              <select value={guestDraft.effect} onChange={(e) => setGuestDraft({ ...guestDraft, effect: e.target.value })} className="rounded-md text-xs px-2 py-1.5" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }}>
                <option value="allow">{t("ALLOW")}</option>
                <option value="deny">{t("DENY")}</option>
              </select>
              <select value={guestDraft.scope} onChange={(e) => setGuestDraft({ ...guestDraft, scope: e.target.value })} className="flex-1 rounded-md text-xs px-2 py-1.5" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }}>
                {GUEST_SCOPES.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
              </select>
              {(guestDraft.scope === "ancestors" || guestDraft.scope === "descendants" || guestDraft.scope === "subtree") && (
                <input type="number" min="1" max="20" value={guestDraft.depth} onChange={(e) => setGuestDraft({ ...guestDraft, depth: e.target.value })} placeholder={t("levels")} className="w-20 rounded-md text-xs px-2 py-1.5" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }} />
              )}
            </div>
            {guestDraft.scope !== "family" && (
              <>
                <SearchSelect people={people} value={null} placeholder={t("Search a person…")}
                  onChange={(v) => { if (!v) return; setGuestDraft((d) => ({ ...d, target_person_ids: d.target_person_ids.includes(v) ? d.target_person_ids : [...d.target_person_ids, v] })); }} />
                {guestDraft.target_person_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {guestDraft.target_person_ids.map((pid) => (
                      <span key={pid} className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: COLORS.accentTint, border: `1px solid ${COLORS.gold}`, fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.ink }}>
                        {displayName(people[pid] || {})}
                        <button onClick={() => setGuestDraft((d) => ({ ...d, target_person_ids: d.target_person_ids.filter((x) => x !== pid) }))} style={{ color: COLORS.rose, background: "transparent", border: "none", cursor: "pointer", lineHeight: 1 }}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
            {/* Bashkëshortët — i njëjti çelës si te Admin Center. Pa të, dy
                ndërfaqet krijonin rregulla që dukeshin njësoj por s'ishin. */}
            {guestDraft.effect === "allow" && (
              <label className="flex items-center gap-2" style={{ cursor: "pointer" }}>
                <input type="checkbox" checked={!!guestDraft.include_spouses} onChange={(e) => setGuestDraft({ ...guestDraft, include_spouses: e.target.checked })} />
                <span style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.ink }}>{t("Include their spouses")}</span>
              </label>
            )}
            <button onClick={addGuestRule} disabled={guestBusy} className="w-full py-2 rounded-lg text-xs font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body }}>{guestDraft.scope !== "family" && guestDraft.target_person_ids.length > 1 ? t("Add {{count}} rules", { count: guestDraft.target_person_ids.length }) : t("Add rule")}</button>
            <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, lineHeight: 1.5 }}>
              {t("DENY always wins. To stop the view at someone (they stay visible, their descendants do not), add DENY + \"Pasardhësit (poshtë)\" on them.")}
            </p>
          </div>

          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("What is shown about each person")}</p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {GUEST_FIELDS.map(([k, label]) => {
              const on = (guestFields ? guestFields.fields : GUEST_DEFAULT_FIELDS).includes(k);
              return (
                <button key={k} onClick={() => toggleGuestField(k, !on)} disabled={guestBusy} className="px-2.5 py-1.5 rounded-lg text-xs" style={{ background: on ? COLORS.accentTint : COLORS.panelSoft, color: on ? COLORS.gold : COLORS.inkLight, border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`, fontFamily: FONTS.body, fontWeight: on ? 700 : 400 }}>
                  {on ? "✓ " : ""}{t(label)}
                </button>
              );
            })}
          </div>

          {/* ZONAT E FAMILJES — Kujtimet dhe Arkivi. Këto nuk janë fusha të një
              personi: janë zona të tëra, prandaj vlejnë për krejt mysafirin.
              Pa i ndezur, mysafiri s'sheh asnjë ngjarje dhe asnjë skedar —
              edhe sikur t'i lejohet gjithë pema. */}
          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("Family sections")}</p>
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {GUEST_AREAS.map(([k, label]) => {
              const on = (guestFields ? guestFields.fields || [] : []).includes(k);
              return (
                <button key={k} onClick={() => toggleGuestField(k, !on)} disabled={guestBusy} className="px-2.5 py-1.5 rounded-lg text-xs" style={{ background: on ? COLORS.accentTint : COLORS.panelSoft, color: on ? COLORS.gold : COLORS.inkLight, border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`, fontFamily: FONTS.body, fontWeight: on ? 700 : 400 }}>
                  {on ? "✓ " : ""}{t(label)}
                </button>
              );
            })}
          </div>
          <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, lineHeight: 1.5, marginBottom: 16 }}>
            {t("Off by default: a guest sees no family events and no files until you turn these on.")}
          </p>

          {/* MBIVENDOSJE PËR NJË PERSON — i njëjti model si te Admin Center.
              Rregulli i përgjithshëm sipër vlen për të gjithë; këtu ndryshohet
              vetëm për një person. `Mesazhet e trashëgimisë` është fushë si
              çdo tjetër, por e fikur nga parazgjedhja: ta shohësh dikë te pema
              nuk do të thotë t'i lexosh mesazhet e vulosura. */}
          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("Per-person override")}</p>
          {guestPersonRules.length > 0 && (
            <div className="mb-2">
              {guestPersonRules.map((r) => (
                <button key={r.person_id} onClick={() => setGuestLegacyDraft(r.person_id)}
                        className="w-full flex items-center gap-2 py-1.5 text-left"
                        style={{ borderBottom: `1px solid ${COLORS.goldSoft}`, background: "transparent" }}>
                  <span style={{ flex: 1, fontSize: 12.5, color: COLORS.ink, fontFamily: FONTS.body }}>{displayName(people[r.person_id] || {})}</span>
                  <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{(r.fields || []).length} {t("fields")}</span>
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2 mb-4">
            <SearchSelect people={people} value={guestLegacyDraft} onChange={(v) => setGuestLegacyDraft(v)} placeholder={t("Search a person…")} />
            {guestLegacyDraft && (() => {
              const row = guestPersonRules.find((x) => x.person_id === guestLegacyDraft);
              const cur = row ? row.fields || [] : [];
              return (
                <div className="flex flex-wrap gap-1.5">
                  {GUEST_FIELDS.map(([k, label]) => {
                    const on = cur.includes(k);
                    return (
                      <button key={k} onClick={() => setGuestPersonField(guestLegacyDraft, k, !on)} disabled={guestBusy} className="px-2.5 py-1.5 rounded-lg text-xs" style={{ background: on ? COLORS.accentTint : COLORS.panelSoft, color: on ? COLORS.gold : COLORS.inkLight, border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`, fontFamily: FONTS.body, fontWeight: on ? 700 : 400 }}>
                        {on ? "✓ " : ""}{t(label)}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, lineHeight: 1.5 }}>
              {t("Leaving this empty means the general rule above applies. Legacy messages can be allowed by an administrator, by the person themselves, or by an editor who covers them.")}
            </p>
          </div>

          <div className="pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink, marginBottom: 6 }}>
              {t("This guest currently sees")}: <b>{guestPreview ? guestPreview.length : "…"}</b> {t("people")}
            </p>
            <div className="flex flex-wrap gap-1">
              {(guestPreview || []).map((p) => (
                <span key={p.id} className="px-1.5 py-0.5 rounded" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontSize: 10.5, fontFamily: FONTS.body }}>{p.full_name}</span>
              ))}
            </div>
          </div>
        </Modal>
      )}
      {deleteAccountOpen && (
        <Modal title={t("Delete my account")} onClose={() => setDeleteAccountOpen(false)}>
          <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, lineHeight: 1.6 }}>
            {t("This cannot be undone. Your sign-in, your contact details, your legacy messages and your notification settings are deleted for good.")}
          </p>
          <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, lineHeight: 1.6, marginTop: 8 }}>
            {t("Your place in the family tree stays — relatives keep seeing you in the tree, but nobody can sign in as you again.")}
          </p>
          {delInfo && Array.isArray(delInfo.blocking_families) && delInfo.blocking_families.length > 0 && (
            <div className="mt-3 p-3 rounded-md" style={{ background: "#A3331A", color: "#fff", fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 1.5 }}>
              {t("You are the last administrator of:")} <b>{delInfo.blocking_families.join(", ")}</b>. {t("Promote another administrator first — otherwise the family would be left with nobody in charge.")}
            </div>
          )}
          <div className="mt-4">
            <FieldLabel>{t('Type FSHIJ to confirm')}</FieldLabel>
            <input value={delWord} onChange={(e) => setDelWord(e.target.value)} placeholder={t("DELETE")} className="w-full rounded-md px-3 py-2 outline-none text-sm" style={inputStyle} autoCapitalize="characters" />
          </div>
          <div className="mt-3">
            <FieldLabel>{hasPasswordLogin ? t("Your password") : t("Your account email")}</FieldLabel>
            <input
              type={hasPasswordLogin ? "password" : "email"}
              value={delSecret}
              onChange={(e) => setDelSecret(e.target.value)}
              placeholder={hasPasswordLogin ? "••••••••" : (authSession?.user?.email || "")}
              className="w-full rounded-md px-3 py-2 outline-none text-sm"
              style={inputStyle}
            />
            <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, marginTop: 4 }}>
              {hasPasswordLogin ? t("We ask for it once more so nobody can delete your account from an unlocked phone.") : t("You signed in with Google/Apple, so there is no password — type your email exactly instead.")}
            </p>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setDeleteAccountOpen(false)} className="flex-1 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink, fontFamily: FONTS.body }}>{t("Cancel")}</button>
            <button
              onClick={runDeleteAccount}
              disabled={delBusy || delWord.trim().toUpperCase() !== "FSHIJ" || !delSecret.trim() || (delInfo && (delInfo.blocking_families || []).length > 0)}
              className="flex-1 py-2 rounded-md text-sm font-medium"
              style={{ background: "#A33", color: "#fff", fontFamily: FONTS.body, opacity: (delBusy || delWord.trim().toUpperCase() !== "FSHIJ" || !delSecret.trim() || (delInfo && (delInfo.blocking_families || []).length > 0)) ? 0.5 : 1 }}
            >
              {delBusy ? t("Deleting…") : t("Delete for good")}
            </button>
          </div>
        </Modal>
      )}
      {legacyReqOpen && (
        <Modal title={t("Request for legacy messages")} onClose={() => setLegacyReqOpen(false)}>
          <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, marginBottom: 12, lineHeight: 1.5 }}>
            {t("Upload a photo/scan of")} <b>{t("the death certificate")}</b>{t(". The request is reviewed by the platform; after approval, the messages open")} <b>{t("you first")}</b> {t("and you decide who else sees them. You remain the only one who reads them until you decide.")}
          </p>
          <label style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{t("Death certificate")}</label>
          <input type="file" accept="image/*,application/pdf" onChange={(e) => setLegacyReqFile(e.target.files?.[0] || null)} className="w-full text-sm mt-1 mb-3" style={{ color: COLORS.ink }} />
          {legacyReqFile && <p style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight, marginBottom: 8 }}>{t("Selected: {{name}}", { name: legacyReqFile.name })}</p>}
          <textarea placeholder={t("Note (optional) — e.g. your relationship to the deceased")} value={legacyReqNote} onChange={(e) => setLegacyReqNote(e.target.value)} className="w-full rounded-md px-3 py-2 mb-3 outline-none text-sm" style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} />
          <div className="flex gap-2">
            <button onClick={submitLegacyRequest} disabled={legacyReqBusy || !legacyReqFile} className="px-3 py-1.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: (legacyReqBusy || !legacyReqFile) ? 0.6 : 1 }}>{legacyReqBusy ? t("Sending…") : t("Send the request")}</button>
            <button onClick={() => setLegacyReqOpen(false)} className="px-3 py-1.5 rounded-md text-sm" style={{ color: COLORS.inkLight }}>{t("Cancel")}</button>
          </div>
        </Modal>
      )}
      {announcementsOpen && (
        <Modal title={t("Notifications")} onClose={() => setAnnouncementsOpen(false)} wide>
          {/* BASHKIM (kërkesë e userit) — "Njoftimet për ditëlindje", banner-i
              t("added to the tree") (më parë i ngjitur sipër faqes), dhe
              t("Platform notifications") tani janë NJË faqe e vetme, me 3
              skeda. Asnjë RPC/tabelë s'ndryshoi — bashkohet vetëm paraqitja. */}
          <div className="flex gap-2 mb-4">
            {/* «Ndryshime» dhe «Platforma» ishin shkruar shqip te kodi — pra
                një lexues anglisht i shihte shqip mes dy fjalëve angleze.
                Kontrolluesi i gjuhës nuk i kap: janë vargje te një varg, jo
                tekst te JSX-i. */}
            {[["birthdays", t("Preferences")], ["platform", t("Announcements")]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setNotificationsTab(key)}
                className="px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: notificationsTab === key ? COLORS.gold : "transparent", color: notificationsTab === key ? COLORS.bgDeep : COLORS.ink, border: `1px solid ${notificationsTab === key ? COLORS.gold : COLORS.goldSoft}` }}
              >
                {label}
              </button>
            ))}
          </div>

          {notificationsTab === "birthdays" && (
            !currentPersonId ? (
              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("birthdayNotif.signInToEnable")}</p>
            ) : (
              <>
                {/* ─── NJOFTIME2 — PREFERENCAT ────────────────────────────
                    Çelësi i madh i pari, pastaj llojet, pastaj personat. Ky
                    rend nuk është shije: kush hyn këtu për të HEQUR njoftimet
                    duhet ta gjejë menjëherë, pa lexuar as edhe një fjali. */}
                <div className="mb-4 pb-4" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div>
                      <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Notifications on this device")}</p>
                      <p style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight }}>
                        {kanaliIApp
                          ? t("Date reminders are kept by the phone itself.")
                          : (pushEnabled && pushIMundshem)
                            ? t("Includes platform announcements, even when the page is closed.")
                            : t("Turn everything off with one switch.")}
                      </p>
                    </div>
                    <Toggle on={njoftimetPref.ndezur} onChange={ndryshoNjoftimetEPajisjes} />
                  </div>

                  {njoftimetPref.ndezur && (
                    <>
                      <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>
                        {t("What you get notified about")}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {[["birthday", `🎂 ${t("Birthdays")}`],
                          ["wedding", `💍 ${t("Wedding anniversaries")}`],
                          ["anniversary", `🕯️ ${t("Remembrances")}`],
                          ["important", `📅 ${t("Other dates")}`]].map(([k, etiketa]) => {
                          const on = llojiNdezur(k);
                          return (
                            <button key={k}
                              onClick={() => ruajNjoftimetPref({ ...njoftimetPref, llojet: { ...njoftimetPref.llojet, [k]: !on } }, true)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                              style={{ background: on ? COLORS.accentTint : "transparent", color: COLORS.ink,
                                       border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`, cursor: "pointer" }}>
                              <span style={{ fontSize: 11, width: 10, color: COLORS.ink }}>{on ? "✓" : ""}</span>
                              <span>{etiketa}</span>
                            </button>
                          );
                        })}
                      </div>
                      {/* E VËRTETA, JO NJË PREMTIM. Ngjarjet e reja te Historia
                          nuk mund t'i mbajë vetë telefoni: ato nuk dihen
                          paraprakisht, dhe kërkojnë push nga serveri (FCM/APNs),
                          që ende s'është ndërtuar. Një kutizë e pesta këtu do të
                          dukej e ndezur dhe s'do të bënte asgjë. */}
                      <p className="mt-2" style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight, lineHeight: 1.45 }}>
                        {t("These are dates known in advance, kept by the phone itself. Notifications for new posts need server push, which is not built yet.")}
                      </p>
                    </>
                  )}
                </div>

                <p className="mb-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>
                  {t("Who you get notified about")}
                </p>
                <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, lineHeight: 1.45 }}>
                  {t("Applies to your account — on every device you sign in to.")}
                </p>
                {/* NJOFTIME5 — tri mënyra; parazgjedhja është dega e gjyshit
                    («dy breza lart dhe gjithë poshtë tyre»), që njoftimet të
                    jenë rrethi që njeriu e uron vërtet, jo gjithë pema. */}
                <div className="space-y-1.5 mb-3">
                  {[
                    ["dega", njoftimetNgaKush.niveli === "prindi" ? t("Parent's branch") : t("Grandfather's branch"), njoftimetNgaKush.gjyshi
                      ? t("The branch of {{name}} — {{n}} people", { name: displayName(people[njoftimetNgaKush.gjyshi] || {}), n: njoftimetNgaKush.dega.size })
                        + (njoftimetNgaKush.niveli === "prindi" ? ` · ${t("Your parent's branch already has {{n}}+ people, so the default stays one generation up.", { n: pragaEDeges })}` : "")
                      : t("Grandfather not known yet — until then, the whole family."), true],
                    ["familja", t("The whole family"), t("Everyone in this family tree."), false],
                    ["persona", t("Selected people"), t("Only the people you add below."), false],
                  ].map(([m, titulli, nen, parazgjedhje]) => {
                    const on = menyraEfektive === m;
                    return (
                      <button key={m} type="button" onClick={() => { if (!on) vendosMenyren(m); }}
                        className="w-full flex items-start gap-2.5 px-3 py-2 rounded-md text-left"
                        style={{ background: on ? COLORS.accentTint : COLORS.panelSoft, border: `1.5px solid ${on ? COLORS.gold : COLORS.goldSoft}`, cursor: "pointer" }}>
                        <span aria-hidden="true" style={{ width: 16, height: 16, borderRadius: 8, marginTop: 2, flexShrink: 0, border: `1.5px solid ${on ? COLORS.gold : COLORS.inkLight}`, background: on ? COLORS.gold : "transparent" }} />
                        <span>
                          <span style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, color: COLORS.ink }}>{titulli}{parazgjedhje ? ` ${t("(default)")}` : ""}</span>
                          <span className="block" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, lineHeight: 1.4 }}>{nen}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {menyraEfektive === "persona" && (
                <div className="space-y-3 mb-4 p-3 rounded-md" style={{ background: COLORS.panelSoft }}>
                  <div>
                    <FieldLabel>{t("birthdayNotif.addPerson")}</FieldLabel>
                    <SearchSelect people={people} value={null} onChange={(v) => addToSubBasket(v, "person")} placeholder={t("birthdayNotif.searchPerson")} />
                  </div>

                  {subBasket.length > 0 && (
                    <div className="space-y-1.5">
                      {subBasket.map((item) => (
                        <div key={item.personId} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md" style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}` }}>
                          <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{displayName(people[item.personId] || {})}</span>
                          <div className="flex items-center gap-1.5">
                            {[["person", t("birthdayNotif.onlyThem")], ["descendants", t("birthdayNotif.plusDescendants")]].map(([val, label]) => (
                              <button
                                key={val}
                                type="button"
                                onClick={() => setSubBasket((prev) => prev.map((it) => (it.personId === item.personId ? { ...it, watchType: val } : it)))}
                                className="py-1 px-2 rounded-md text-[11px]"
                                style={{ border: `1.5px solid ${item.watchType === val ? COLORS.gold : COLORS.goldSoft}`, background: item.watchType === val ? (COLORS.accentTint || _rgba(COLORS.gold, 0.16)) : (COLORS.field || COLORS.panel), color: COLORS.ink }}
                              >
                                {label}
                              </button>
                            ))}
                            <button onClick={() => removeFromSubBasket(item.personId)} style={{ color: COLORS.danger, fontSize: 11 }}>✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* NJOFTIME3 — brenda aplikacionit s'ka kanal për të zgjedhur:
                      njoftimet i mban vetë telefoni. Pyetja push/email vlen
                      vetëm te shfletuesi, ku ato dy kanale punojnë vërtet
                      (gating-uar nga Admin Center si më parë). */}
                  {!kanaliIApp && (platformFeatures.notify_channels_visible && platformFeatures.notify_push_enabled && platformFeatures.notify_email_enabled) ? (
                    <div>
                      <FieldLabel>{t("birthdayNotif.howToNotify")}</FieldLabel>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setSubForm({ ...subForm, channel: "push" })} className="flex-1 py-1.5 rounded-md text-xs" style={{ background: subForm.channel === "push" ? COLORS.gold : "#fff", color: subForm.channel === "push" ? COLORS.bgDeep : COLORS.ink, border: `1px solid ${COLORS.goldSoft}` }}>
                          {t("birthdayNotif.pushNotif")}
                        </button>
                        <button type="button" onClick={() => setSubForm({ ...subForm, channel: "email" })} className="flex-1 py-1.5 rounded-md text-xs" style={{ background: subForm.channel === "email" ? COLORS.gold : "#fff", color: subForm.channel === "email" ? COLORS.bgDeep : COLORS.ink, border: `1px solid ${COLORS.goldSoft}` }}>
                          {t("birthdayNotif.email")}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {!kanaliIApp && subForm.channel === "email" && (
                    <div>
                      <FieldLabel>{t("birthdayNotif.yourEmail")}</FieldLabel>
                      <input
                        type="email"
                        className="w-full rounded-md px-3 py-2 outline-none"
                        style={inputStyle}
                        value={subForm.contact}
                        onChange={(e) => setSubForm({ ...subForm, contact: e.target.value })}
                        placeholder="you@example.com"
                      />
                    </div>
                  )}
                  {!kanaliIApp && subForm.channel === "push" && (
                    <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }}>
                      {myPushSubscribed ? t("birthdayNotif.pushWillWork") : t("birthdayNotif.pushNeedsEnabling")}
                    </p>
                  )}
                  {/* Butoni nuk çaktivizohet më: një buton i zbehtë që s'bën asgjë
                      ishte pikërisht ankesa. Çdo pengesë thuhet me toast. */}
                  <button onClick={submitSubBasket} className="w-full py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: subBasket.length === 0 ? 0.6 : 1 }}>
                    {t("birthdayNotif.activate")} {subBasket.length > 0 ? `(${subBasket.length})` : ""}
                  </button>
                </div>
                )}
                {/* Rreshti «familja» është vetë butoni radio sipër; këtu dalin
                    vetëm personat (dhe abonimet push/email të shfletuesit). */}
                {mySubscriptions.some((x) => x.watch_type !== "family") && (<>
                <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("birthdayNotif.yourActiveNotifs")}</p>
                  <ul className="space-y-1.5">
                    {mySubscriptions.filter((x) => x.watch_type !== "family").map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md" style={{ background: COLORS.panelSoft }}>
                        <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>
                          {displayName(people[s.watch_person_id] || {})} {s.watch_type === "descendants" ? t("birthdayNotif.plusDescendants") : ""} → {s.channel === "app" ? `📱 ${t("In the app")}` : s.channel === "push" ? t("birthdayNotif.pushNotif") : `📧 ${s.contact}`}
                        </span>
                        <button onClick={() => removeSubscription(s.id)} style={{ color: COLORS.danger, fontSize: 11 }}>{t("birthdayNotif.remove")}</button>
                      </li>
                    ))}
                  </ul>
                </>)}
              </>
            )
          )}

          {notificationsTab === "platform" && (
            <>
              {activeNotices.length > 0 && (
                <ul className="space-y-3 mb-4 pb-4" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
                  {activeNotices.map((n) => (
                    <li key={n.id} className="px-3 py-2.5 rounded-md" style={{ background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}` }}>
                      <p style={{ fontFamily: FONTS.display, fontSize: 14, fontWeight: 600, color: COLORS.ink }}>🛠️ {t("Scheduled maintenance")}</p>
                      <p className="mt-1" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, whiteSpace: "pre-wrap" }}>{n.message}</p>
                      <p className="mt-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>
                        {new Date(n.starts_at).toLocaleString((i18n.language || "").startsWith("en") ? "en-GB" : "sq-AL")}{n.duration_text ? ` · ${n.duration_text}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {/* QENDRA1 — rreshti «Në këtë pajisje · Aktivizo/Çaktivizo» (push-i i
                  shfletuesit) u zhvendos: tani e ndez/fik çelësi i vetëm te
                  Preferencat. Dy çelësa me emër të njëjtë e kuptim tjetër
                  ishin ankesa e pronarit. */}
              {announcements.length === 0 ? (
                <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("announcements.noneActiveYet")}</p>
              ) : (
                <ul className="space-y-3">
                  {/* FAZA1h — etiketa. Një risi dhe një ndryshim kërkojnë dy
                      gjëra të ndryshme nga njeriu: risia thotë "shiko nëse të
                      duhet", ndryshimi thotë "vendos para kësaj date". Pa
                      etiketë të dyja lexohen si i njëjti rresht i bardhë.

                      `admin` dhe `legacy` mbeten PA etiketë me qëllim: ato janë
                      fjalori i vjetër i kësaj kolone (LEGACY_notice_flow.sql),
                      dhe u ngjisja një shenjë të re do të thoshte t'i ndryshoja
                      pamjen njoftimeve që sot duken mirë. */}
                  {announcements.map((a) => {
                    const tag = a.kind === "news"
                      ? { icon: "✨", label: t("New"), bg: COLORS.gold }
                      : a.kind === "change"
                        ? { icon: "⚠️", label: t("Change"), bg: "#A3331A" }
                        : null;
                    return (
                      <li key={a.id} className="px-3 py-2.5 rounded-md" style={{ background: COLORS.panelSoft, border: `1px solid ${a.kind === "change" ? "#A3331A55" : COLORS.goldSoft}` }}>
                        {tag && (
                          <span className="inline-block mb-1.5 px-2 py-0.5 rounded-full" style={{ background: tag.bg, color: "#fff", fontFamily: FONTS.body, fontSize: 10.5, fontWeight: 700 }}>
                            {pamja("kryet", "heirloom") || designIconMode() === "heirloom" ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "-2px" }}>
                                {a.kind === "news" ? <Shkendija size={12} color="#fff" /> : <Paralajmerimi size={12} color="#fff" />}
                                {tag.label}
                              </span>
                            ) : (<>{tag.icon} {tag.label}</>)}
                          </span>
                        )}
                        <p style={{ fontFamily: FONTS.display, fontSize: 14.5, fontWeight: 600, color: COLORS.ink }}>{a.title}</p>
                        <p className="mt-1" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, whiteSpace: "pre-wrap" }}>{a.body}</p>
                        <p className="mt-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{fmtDate(a.created_at)}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </Modal>
      )}

      {announceManageOpen && canManage && (
        <Modal title={t("announcements.manageTitle")} onClose={() => setAnnounceManageOpen(false)} wide>
          <div className="mb-4 pb-4" style={{ borderBottom: `1px solid ${COLORS.goldSoft}` }}>
            <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("announcements.newAnnouncement")}</p>
            <input className="w-full rounded-md px-3 py-2 mb-2 outline-none" style={inputStyle} placeholder={t("announcements.titlePlaceholder")} value={announceForm.title} onChange={(e) => setAnnounceForm({ ...announceForm, title: e.target.value })} />
            <textarea className="w-full rounded-md px-3 py-2 mb-2 outline-none" style={{ ...inputStyle, minHeight: 90, resize: "vertical" }} placeholder={t("announcements.bodyPlaceholder")} value={announceForm.body} onChange={(e) => setAnnounceForm({ ...announceForm, body: e.target.value })} />
            <div className="mb-2">
              <FieldLabel>{t("announcements.startsAt")}</FieldLabel>
              <input type="datetime-local" className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={announceForm.startsAt} onChange={(e) => setAnnounceForm({ ...announceForm, startsAt: e.target.value })} />
            </div>
            <FieldLabel>{t("announcements.duration")}</FieldLabel>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {[
                ["manual", t("announcements.untilDisabled")],
                ["1w", t("announcements.oneWeek")],
                ["1m", t("announcements.oneMonth")],
                ["6m", t("announcements.sixMonths")],
                ["1y", t("announcements.oneYear")],
                ["custom", t("announcements.specificDate")],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setAnnounceForm({ ...announceForm, durationPreset: key })} className="py-1.5 rounded-md text-xs" style={{ background: announceForm.durationPreset === key ? COLORS.gold : COLORS.onBgSoft, color: announceForm.durationPreset === key ? COLORS.bgDeep : COLORS.ink }}>
                  {label}
                </button>
              ))}
            </div>
            {announceForm.durationPreset === "custom" && (
              <div className="mb-2">
                <FieldLabel>{t("announcements.endsAt")}</FieldLabel>
                <input type="datetime-local" className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={announceForm.endsAt} onChange={(e) => setAnnounceForm({ ...announceForm, endsAt: e.target.value })} />
              </div>
            )}
            <FieldLabel>{t("announcements.shownTo")}</FieldLabel>
            <select className="w-full rounded-md px-3 py-2 mb-2 outline-none" style={inputStyle} value={announceForm.targetGroup} onChange={(e) => setAnnounceForm({ ...announceForm, targetGroup: e.target.value })}>
              <option value="all">{t("announcements.everyone")}</option>
              <option value="admin">{t("announcements.adminsOnly")}</option>
              <option value="editor">{t("announcements.editorsOnly")}</option>
              <option value="contributor">{t("announcements.contributorsOnly")}</option>
              <option value="viewer">{t("announcements.viewersOnly")}</option>
            </select>
            <button onClick={createAnnouncement} className="w-full py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("announcements.publish")}</button>
          </div>
          <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("announcements.allAnnouncements")}</p>
          {allAnnouncements.length === 0 ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("announcements.noneYet")}</p>
          ) : (
            <ul className="space-y-2">
              {allAnnouncements.map((a) => {
                const now = Date.now();
                const active = new Date(a.starts_at).getTime() <= now && (!a.ends_at || new Date(a.ends_at).getTime() >= now);
                return (
                  <li key={a.id} className="px-3 py-2 rounded-md flex items-start justify-between gap-2" style={{ background: COLORS.panelSoft }}>
                    <div>
                      <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{a.title} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: active ? COLORS.accentText : COLORS.inkLight }}>{active ? t("announcements.active") : t("announcements.inactive")}</span></p>
                      <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>
                        {a.target_group === "all" ? t("announcements.everyone") : a.target_group} · {fmtDate(a.starts_at)}{a.ends_at ? ` → ${fmtDate(a.ends_at)}` : ""}
                      </p>
                    </div>
                    <button onClick={() => deleteAnnouncement(a.id)} className="px-2 py-1 rounded-md text-xs shrink-0" style={{ background: COLORS.dangerSoft, color: COLORS.danger }}>{t("announcements.delete")}</button>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal>
      )}

      {emailRepliesOpen && canManage && (() => {
        const snippet = (t) => (t || "").replace(/\s+/g, " ").trim().slice(0, 90);
        const threadsMap = {};
        emailReplies.forEach((r) => {
          if (!threadsMap[r.thread_key]) threadsMap[r.thread_key] = [];
          threadsMap[r.thread_key].push(r);
        });
        const inboxThreads = Object.entries(threadsMap).map(([key, msgs]) => {
          const sorted = [...msgs].sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
          return { key, msgs: sorted, latest: sorted[0], unread: msgs.some((m) => !m.is_read), starred: msgs.some((m) => m.starred) };
        }).sort((a, b) => new Date(b.latest.received_at) - new Date(a.latest.received_at));

        const sidebarBtn = (key, icon, label, badge) => (
          <button
            onClick={() => { setMailFolder(key); setMailView("list"); setSelectedMailId(null); }}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left mb-1"
            style={{ background: mailFolder === key && mailView !== "compose" ? COLORS.panelSoft : "transparent" }}
          >
            <span style={{ fontSize: 14 }}>{icon}</span>
            <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{label}</span>
            {badge > 0 && <span className="ml-auto flex items-center justify-center rounded-full" style={{ minWidth: 16, height: 16, padding: "0 3px", background: COLORS.danger, color: "#fff", fontSize: 10, fontFamily: FONTS.body, fontWeight: 700 }}>{badge}</span>}
          </button>
        );

        const StarButton = ({ table, id, starred }) => (
          <button onClick={(e) => { e.stopPropagation(); toggleStar(table, id, !starred); }} className="shrink-0" title={starred ? t("Remove from favorites") : t("Add to favorites")}>
            <span style={{ fontSize: 14, color: starred ? COLORS.gold : COLORS.inkLight, opacity: starred ? 1 : 0.4 }}>{starred ? "★" : "☆"}</span>
          </button>
        );

        const openThread = (key) => {
          setSelectedMailId({ type: "inbox", key });
          setMailView("detail");
          const thread = threadsMap[key] || [];
          thread.forEach((m) => { if (!m.is_read) markReplyRead(m.id); });
        };
        const openSent = (id) => { setSelectedMailId({ type: "sent", id }); setMailView("detail"); };

        const favoriteRows = [
          ...inboxThreads.filter((t) => t.starred).map((t) => ({ kind: "inbox", key: t.key, date: t.latest.received_at, title: t.latest.original_subject, from: t.latest.from_name || t.latest.from_email, preview: snippet(t.latest.body_text), starred: true })),
          ...sentEmails.filter((s) => s.starred).map((s) => ({ kind: "sent", id: s.id, date: s.sent_at, title: s.subject, from: t("To {{count}} recipients", { count: (s.recipient_emails || []).length }), preview: snippet(s.body), starred: true })),
        ].sort((a, b) => new Date(b.date) - new Date(a.date));

        const folderRows = !selectedFolderId ? [] : [
          ...inboxThreads.filter((t) => t.msgs.some((m) => m.folder_id === selectedFolderId)).map((t) => ({ kind: "inbox", key: t.key, date: t.latest.received_at, title: t.latest.original_subject, from: t.latest.from_name || t.latest.from_email, preview: snippet(t.latest.body_text) })),
          ...sentEmails.filter((s) => s.folder_id === selectedFolderId).map((s) => ({ kind: "sent", id: s.id, date: s.sent_at, title: s.subject, from: t("To {{count}} recipients", { count: (s.recipient_emails || []).length }), preview: snippet(s.body) })),
        ].sort((a, b) => new Date(b.date) - new Date(a.date));

        const FolderMenu = ({ table, id }) => (
          <select
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { e.stopPropagation(); moveToFolder(table, id, e.target.value || null); }}
            defaultValue=""
            className="text-xs rounded px-1 py-0.5 outline-none"
            style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.inkLight, background: COLORS.field }}
            title={t("Move to folder")}
          >
            <option value="">{t("📁 Folder…")}</option>
            {mailFolders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            <option value="">{t("— remove from folder —")}</option>
          </select>
        );

        return (
          <Modal title={t("mail.title")} onClose={() => setEmailRepliesOpen(false)} wide>
            <div className="flex gap-4" style={{ minHeight: 380 }}>
              {/* ---------- left sidebar (Outlook-style folders) ---------- */}
              <div className="w-24 sm:w-36 shrink-0" style={{ borderRight: `1px solid ${COLORS.goldSoft}`, paddingRight: 12 }}>
                <button
                  onClick={() => openFamilyNotify()}
                  className="w-full mb-3 py-2 rounded-md text-xs font-medium"
                  style={{ background: COLORS.gold, color: COLORS.bgDeep }}
                >
                  ✏️ <span className="hidden sm:inline">{t("mail.compose")}</span>
                </button>
                {sidebarBtn("inbox", "📥", t("mail.inbox"), unreadReplyCount)}
                {sidebarBtn("sent", "📤", t("mail.sent"), 0)}
                {sidebarBtn("favorites", "⭐", t("mail.favorites"), 0)}
                {hasSavedDraft && (
                  <button
                    onClick={restoreMailDraft}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left mb-1"
                    style={{ background: mailView === "compose" ? COLORS.gold + "22" : "transparent" }}
                    title={t("mail.hasSavedDraftTitle")}
                  >
                    <span style={{ fontSize: 13 }}>📝</span>
                    <span className="hidden sm:inline" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>{t("mail.draft")}</span>
                  </button>
                )}
                <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                  <p className="mb-1 hidden sm:block" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{t("mail.folders")}</p>
                  {mailFolders.map((f) => (
                    <div key={f.id} className="flex items-center gap-1 mb-1">
                      <button
                        onClick={() => { setMailFolder("folder"); setSelectedFolderId(f.id); setMailView("list"); setSelectedMailId(null); }}
                        className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left"
                        style={{ background: mailFolder === "folder" && selectedFolderId === f.id ? COLORS.panelSoft : "transparent" }}
                      >
                        <span style={{ fontSize: 12 }}>📁</span>
                        <span className="truncate hidden sm:inline" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.ink }}>{f.name}</span>
                      </button>
                      <button onClick={() => deleteMailFolder(f.id)} className="shrink-0 hidden sm:block" style={{ color: COLORS.inkLight, fontSize: 11 }} title={t("mail.deleteFolder")}>✕</button>
                    </div>
                  ))}
                  <div className="flex items-center gap-1 mt-1">
                    <input
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && createMailFolder()}
                      placeholder={t("mail.newFolderPlaceholder")}
                      className="w-full min-w-0 rounded-md px-1.5 py-1 text-xs outline-none"
                      style={{ border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body, color: COLORS.ink }}
                    />
                  </div>
                </div>
              </div>

              {/* ---------- main pane ---------- */}
              <div className="flex-1 min-w-0">
                {mailView === "compose" && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p style={{ fontFamily: FONTS.display, fontSize: 15, fontWeight: 600, color: COLORS.ink }}>{t("mail.newMessage")}</p>
                      <div className="flex items-center gap-3">
                        {hasSavedDraft && (
                          <button onClick={() => { clearMailDraft(); setNotifySubject(""); setNotifyBody(""); setMailView("list"); }} style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>{t("mail.removeDraft")}</button>
                        )}
                        <button onClick={() => setMailView("list")} style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>{t("mail.cancel")}</button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t("mail.senderShownAs")}</FieldLabel>
                      <div className="flex gap-2">
                        {/* ⚠ 5 shtator 2026 — këtu rrinin dy emra TË NGULITUR të
                            familjes së parë. Te një platformë shumë-familjare kjo
                            do të thoshte se çdo familje dërgonte postë nën emrin e
                            një tjetre. Tani emri vjen nga vetë familja që po
                            shkruan. */}
                        {[
                          t("The {{emri}} family", { emri: (effTreeName || "").trim() || t("Family Tree") }),
                          t("The {{emri}} family tree", { emri: (effTreeName || "").trim() || t("Family Tree") }),
                        ].map((opt) => (
                          <button key={opt} onClick={() => setNotifySenderName(opt)} className="flex-1 py-2 rounded-md text-sm" style={{ background: notifySenderName === opt ? COLORS.gold : COLORS.onBgSoft, color: notifySenderName === opt ? COLORS.bgDeep : COLORS.ink }}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t("mail.subject")}</FieldLabel>
                      <input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={notifySubject} onChange={(e) => setNotifySubject(e.target.value)} placeholder={t("mail.subjectPlaceholder")} />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t("mail.messageBody")}</FieldLabel>
                      <textarea className="w-full rounded-md px-3 py-2 outline-none" style={{ ...inputStyle, minHeight: 130, resize: "vertical" }} value={notifyBody} onChange={(e) => setNotifyBody(e.target.value)} placeholder={t("mail.messageBodyPlaceholder")} />
                      <p className="mt-1" style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight }}>
                        {t("mail.autoSignature", { name: displayName(people[currentPersonId] || {}) })}
                      </p>
                    </div>
                    {mourningForId && (
                      <div className="mb-3 grid grid-cols-2 gap-2">
                        <div>
                          <FieldLabel>{t("mail.funeralDate")}</FieldLabel>
                          <DateField className="w-full" inputStyle={{ ...inputStyle, padding: "8px 12px" }} value={funeralDate} onChange={setFuneralDate} />
                        </div>
                        <div>
                          <FieldLabel>{t("mail.place")}</FieldLabel>
                          <input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={funeralPlace} onChange={(e) => setFuneralPlace(e.target.value)} placeholder={t("mail.placePlaceholder")} />
                        </div>
                      </div>
                    )}
                    <div className="mb-3">
                      <FieldLabel>{t("mail.recipients")}</FieldLabel>
                      <div className="flex gap-2 mb-2">
                        <button onClick={() => setNotifyRecipientMode("all")} className="flex-1 py-1.5 rounded-md text-xs" style={{ background: notifyRecipientMode === "all" ? COLORS.gold : COLORS.onBgSoft, color: notifyRecipientMode === "all" ? COLORS.bgDeep : COLORS.ink }}>
                          {t("mail.allWithEmail", { count: allContacts.length })}
                        </button>
                        <button onClick={() => setNotifyRecipientMode("branch")} className="flex-1 py-1.5 rounded-md text-xs" style={{ background: notifyRecipientMode === "branch" ? COLORS.gold : COLORS.onBgSoft, color: notifyRecipientMode === "branch" ? COLORS.bgDeep : COLORS.ink }}>
                          {t("mail.branch")}
                        </button>
                        <button onClick={() => { setNotifyRecipientMode("selected"); setNotifyPicked(new Set()); }} className="flex-1 py-1.5 rounded-md text-xs" style={{ background: notifyRecipientMode === "selected" ? COLORS.gold : COLORS.onBgSoft, color: notifyRecipientMode === "selected" ? COLORS.bgDeep : COLORS.ink }}>
                          {t("mail.onlySelected")}
                        </button>
                      </div>
                      {notifyRecipientMode === "branch" && (
                        <div>
                          <SearchSelect people={people} value={notifyBranchAnchorId} onChange={setNotifyBranchAnchorId} placeholder={t("mail.chooseBranchAnchor")} />
                          {notifyBranchAnchorId && (
                            <p className="mt-2" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>
                              {notifyBranchRecipients.length === 0
                                ? t("mail.noOneInBranchHasEmail")
                                : t("mail.willGoToBranch", { count: notifyBranchRecipients.length, name: displayName(people[notifyBranchAnchorId]) })}
                            </p>
                          )}
                        </div>
                      )}
                      {notifyRecipientMode === "selected" && (
                        allContacts.length === 0 ? (
                          <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("mail.noOneHasEmailYet")}</p>
                        ) : (
                          <ContactMultiSelect contacts={allContacts} selectedIds={notifyPicked} onChange={setNotifyPicked} placeholder={t("mail.searchPerson")} />
                        )
                      )}
                    </div>
                    <button onClick={sendFamilyNotify} disabled={allContacts.length === 0 || notifySending} className="w-full py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: (allContacts.length === 0 || notifySending) ? 0.5 : 1 }}>
                      {notifySending ? t("mail.sending") : t("mail.sendFromPlatform")}
                    </button>
                    <button onClick={sendFamilyNotifyViaDevice} disabled={allContacts.length === 0} className="w-full mt-2 py-2 rounded-md text-sm" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, opacity: allContacts.length === 0 ? 0.5 : 1 }}>
                      {t("mail.sendFromMyEmail")}
                    </button>
                  </div>
                )}

                {mailView === "list" && mailFolder === "inbox" && (
                  inboxThreads.length === 0 ? (
                    <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("mail.noMessagesYet")}</p>
                  ) : (
                    <ul className="divide-y" style={{ borderColor: COLORS.goldSoft }}>
                      {inboxThreads.map((t2) => (
                        <li key={t2.key} onClick={() => openThread(t2.key)} className="py-2.5 px-1 cursor-pointer flex items-start gap-2 hover:bg-black/5 rounded-md">
                          <StarButton table="email_replies" id={t2.latest.id} starred={t2.starred} />
                          <FolderMenu table="email_replies" id={t2.latest.id} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, fontWeight: t2.unread ? 700 : 400 }} className="truncate">
                                {t2.latest.from_name || t2.latest.from_email} {t2.msgs.length > 1 && <span style={{ color: COLORS.inkLight, fontWeight: 400 }}>({t2.msgs.length})</span>}
                              </p>
                              <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtDate(t2.latest.received_at)}</span>
                            </div>
                            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink, fontWeight: t2.unread ? 600 : 400 }} className="truncate">{t2.latest.original_subject || t("mail.noSubject")}</p>
                            <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }} className="truncate">{snippet(t2.latest.body_text)}</p>
                          </div>
                          {t2.unread && <span style={{ width: 7, height: 7, borderRadius: 999, background: COLORS.gold, flexShrink: 0, marginTop: 5 }} />}
                        </li>
                      ))}
                    </ul>
                  )
                )}

                {mailView === "list" && mailFolder === "sent" && (
                  sentEmails.length === 0 ? (
                    <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("mail.noEmailSentYet")}</p>
                  ) : (
                    <ul className="divide-y" style={{ borderColor: COLORS.goldSoft }}>
                      {sentEmails.map((s) => (
                        <li key={s.id} onClick={() => openSent(s.id)} className="py-2.5 px-1 cursor-pointer flex items-start gap-2 hover:bg-black/5 rounded-md">
                          <StarButton table="sent_emails" id={s.id} starred={s.starred} />
                          <FolderMenu table="sent_emails" id={s.id} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }} className="truncate">{t("mail.toRecipients", { count: (s.recipient_emails || []).length })}</p>
                              <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtDate(s.sent_at)}</span>
                            </div>
                            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }} className="truncate">{s.subject || t("mail.noSubject")}</p>
                            <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }} className="truncate">{snippet(s.body)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                )}

                {mailView === "list" && mailFolder === "favorites" && (
                  favoriteRows.length === 0 ? (
                    <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("mail.noFavoritesYet")}</p>
                  ) : (
                    <ul className="divide-y" style={{ borderColor: COLORS.goldSoft }}>
                      {favoriteRows.map((f) => (
                        <li key={f.kind + f.key + f.id} onClick={() => (f.kind === "inbox" ? openThread(f.key) : openSent(f.id))} className="py-2.5 px-1 cursor-pointer flex items-start gap-2 hover:bg-black/5 rounded-md">
                          <StarButton table={f.kind === "inbox" ? "email_replies" : "sent_emails"} id={f.kind === "inbox" ? threadsMap[f.key][0].id : f.id} starred />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }} className="truncate">{f.from} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{f.kind === "inbox" ? "📥" : "📤"}</span></p>
                              <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtDate(f.date)}</span>
                            </div>
                            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }} className="truncate">{f.title || t("mail.noSubject")}</p>
                            <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }} className="truncate">{f.preview}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                )}

                {mailView === "detail" && selectedMailId?.type === "inbox" && (() => {
                  const thread = (threadsMap[selectedMailId.key] || []).slice().sort((a, b) => new Date(a.received_at) - new Date(b.received_at));
                  if (thread.length === 0) return null;
                  const last = thread[thread.length - 1];
                  return (
                    <div>
                      <button onClick={() => setMailView("list")} className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>{t("mail.backToList")}</button>
                      <p className="mb-3" style={{ fontFamily: FONTS.display, fontSize: 15.5, fontWeight: 600, color: COLORS.ink }}>{last.original_subject || t("mail.noSubject")}</p>
                      <div className="space-y-3 mb-3">
                        {thread.map((m) => (
                          <div key={m.id} className="px-3 py-2.5 rounded-md" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
                            <div className="flex items-center justify-between gap-2">
                              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>
                                <strong>{m.from_name || m.from_email}</strong> <span style={{ color: COLORS.inkLight, fontFamily: FONTS.body, fontSize: 12.5 }}>({m.from_email})</span>
                              </p>
                              <div className="flex items-center gap-2 shrink-0">
                                <StarButton table="email_replies" id={m.id} starred={m.starred} />
                                <button onClick={() => deleteEmail("email_replies", m.id)} title={t("mail.delete")} style={{ fontSize: 13, color: COLORS.inkLight }}>🗑️</button>
                              </div>
                            </div>
                            <p className="mt-1" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, whiteSpace: "pre-wrap" }}>{m.body_text}</p>
                            <p className="mt-1.5" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{fmtDateTime(m.received_at)}</p>
                          </div>
                        ))}
                      </div>
                      {replyDraftFor === last.id ? (
                        <div>
                          <textarea autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t("mail.replyToPlaceholder", { name: last.from_name || last.from_email })} className="w-full rounded-md px-2 py-1.5 text-sm outline-none" style={{ ...inputStyle, minHeight: 90, resize: "vertical" }} />
                          <div className="flex gap-2 mt-1.5">
                            <button onClick={() => sendReplyTo(last)} disabled={replySending} className="px-3 py-1.5 rounded-md text-xs font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: replySending ? 0.6 : 1 }}>
                              {replySending ? t("mail.sending") : t("mail.sendReply")}
                            </button>
                            <button onClick={() => { setReplyDraftFor(null); setReplyText(""); }} className="px-3 py-1.5 rounded-md text-xs" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>{t("common.cancel")}</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setReplyDraftFor(last.id); setReplyText(""); }} className="px-3 py-1.5 rounded-md text-xs" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>{t("mail.reply")}</button>
                      )}
                    </div>
                  );
                })()}

                {mailView === "detail" && selectedMailId?.type === "sent" && (() => {
                  const s = sentEmails.find((x) => x.id === selectedMailId.id);
                  if (!s) return null;
                  return (
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <button onClick={() => setMailView("list")} style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>{t("mail.backToList")}</button>
                        <button onClick={() => deleteEmail("sent_emails", s.id)} title={t("mail.delete")} style={{ fontSize: 13, color: COLORS.inkLight }}>🗑️</button>
                      </div>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p style={{ fontFamily: FONTS.display, fontSize: 15.5, fontWeight: 600, color: COLORS.ink }}>{s.subject || t("mail.noSubject")}</p>
                        <StarButton table="sent_emails" id={s.id} starred={s.starred} />
                      </div>
                      <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>
                        {t("mail.fromTo", { actor: s.actor_name || "Admin", recipients: (s.recipient_emails || []).join(", ") })}
                      </p>
                      <p style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink, whiteSpace: "pre-wrap" }}>{s.body}</p>
                      <p className="mt-3" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{fmtDateTime(s.sent_at)}</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </Modal>
        );
      })()}

      {simpleMourningOpen && !canManage && (
        <Modal title={t("🕊️ Notify the family")} onClose={() => setSimpleMourningOpen(false)}>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>
            {t("Sent directly from the platform to relatives with a saved email.")}
          </p>
          <div className="mb-3">
            <FieldLabel>{t("Subject")}</FieldLabel>
            <input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={notifySubject} onChange={(e) => setNotifySubject(e.target.value)} />
          </div>
          <div className="mb-3">
            <FieldLabel>{t("Message")}</FieldLabel>
            <textarea className="w-full rounded-md px-3 py-2 outline-none" style={{ ...inputStyle, minHeight: 110, resize: "vertical" }} value={notifyBody} onChange={(e) => setNotifyBody(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <FieldLabel>{t("Burial (date)")}</FieldLabel>
              <DateField className="w-full" inputStyle={{ ...inputStyle, padding: "8px 12px" }} value={funeralDate} onChange={setFuneralDate} />
            </div>
            <div>
              <FieldLabel>{t("Place")}</FieldLabel>
              <input className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} value={funeralPlace} onChange={(e) => setFuneralPlace(e.target.value)} placeholder={t("E.g. Fushë Kosovë")} />
            </div>
          </div>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight }}>{t("It goes to every family member with a saved email ({{sa}}).", { sa: allContacts.length })}</p>
          <button onClick={sendFamilyNotify} disabled={allContacts.length === 0 || notifySending} className="w-full py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: (allContacts.length === 0 || notifySending) ? 0.5 : 1 }}>
            {notifySending ? t("Sending…") : t("Send")}
          </button>
        </Modal>
      )}

      {aboutOpen && (
        <Modal title={t("About the platform")} onClose={() => setAboutOpen(false)} wide>
          <div className="space-y-3">
            {/* PASTRIM (25 gusht 2026) — vërejtja e pronarit: *"kemi thënë që
                te Rreth platformës të fshihet gjithçka hard-coded, por po e
                shoh akoma një info të vjetër"*. Tre paragrafët hyrës dhe
                "KRIJUAR NGA" ishin pikërisht ai tekst i ngulitur — dokument
                që s'e mirëmbante njeri. Tani kjo dritare tregon VETËM
                dokumentet e publikuara nga Admin Center (Platforma → "Rreth
                Platformës") — një burim i vetëm, i përditësueshëm pa deploy. */}
            {/* NDËRTUAR (19 gusht 2026) — më parë këtu kishte një buton të vetëm
                që hapte gjithmonë `info`. Platforma mban gjashtë lloje
                dokumentesh dhe Admin Center i ngarkon të gjitha, por katër prej
                tyre s'kishin asnjë rrugë drejt syrit të përdoruesit. Tani
                shfaqet çdo dokument që (a) ka një version të ngarkuar dhe
                (b) nuk është fikur te Admin Center. */}
            <div className="flex flex-col gap-1.5">
              {Object.keys(DOC_LABELS).filter((k) => docs[k] && docVisibility[k] !== false).map((k) => (
                <button key={k} onClick={() => { setAboutOpen(false); setDocModalOpen(k); }} className="w-full py-2 rounded-md text-sm font-medium" style={{ background: COLORS.panelSoft, color: COLORS.ink }}>
                  📄 {t(DOC_LABELS[k])}
                </button>
              ))}
              {/* Pa asnjë dokument të publikuar nuk shfaqet asgjë për t'u lexuar.
                  Më parë këtu binte një tekst i ngulitur në kod — dokument i
                  dytë që s'e mirëmbante njeri. */}
              {!Object.keys(DOC_LABELS).some((k) => docs[k] && docVisibility[k] !== false) && (
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("No document has been published yet.")}</p>
              )}
            </div>
            {/* FIX (RISTRUKTURIM) — "ADMINISTRIMI I DOKUMENTEVE" u hoq
                krejtësisht që këtej — menaxhohet tani VETËM nga Admin Center
                (Platforma → "Rreth Platformës"), jo më nga admini i
                familjes. Vetë leximi (docs/docHistory, butoni sipër) mbetet
                i pandryshuar, thjesht burimi tani është qendror. */}
          </div>
        </Modal>
      )}

      {securityPanelOpen && (
        <Modal title={t("Account security")} onClose={() => setSecurityPanelOpen(false)}>
          <div className="space-y-3">
            <AppLockSetting C={COLORS} F={FONTS} t={t} />
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, lineHeight: 1.6 }}>
              {t("Two-step verification (2FA) adds an extra step when you sign in — besides the password/Google/Apple, a code from an app such as Google Authenticator is also required. Entirely optional.")}
            </p>
            {mfaError && (
              <p className="px-2.5 py-2 rounded-md" style={{ background: "#FBEAEA", color: "#8A2E2E", fontFamily: FONTS.body, fontSize: 12.5 }}>{mfaError}</p>
            )}
            {mfaFactors === null ? (
              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Loading…")}</p>
            ) : mfaFactors.length > 0 ? (
              <div className="px-2.5 py-2.5 rounded-md" style={{ background: COLORS.panelSoft }}>
                <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("✓ Two-step verification is")} <strong>{t("on")}</strong>.</p>
                <button disabled={mfaBusy} onClick={() => unenrollMfa(mfaFactors[0].id)} className="mt-2 px-3 py-1.5 rounded-md text-xs font-medium" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>
                  {t("Disable")}
                </button>
              </div>
            ) : !mfaEnrollData ? (
              <button disabled={mfaBusy} onClick={startMfaEnroll} className="w-full py-2 rounded-md text-sm font-medium" style={{ background: COLORS.panelSoft, color: COLORS.ink }}>
                {t("Enable two-step verification")}
              </button>
            ) : (
              <div className="space-y-2">
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
                  {t("Scan this QR code with Google Authenticator (or any 2FA app), or enter the code manually:")} <code style={{ fontFamily: FONTS.mono, fontSize: 13 }}>{mfaEnrollData.secret}</code>
                </p>
                {mfaEnrollData.qr && <img src={mfaEnrollData.qr} alt={t("2FA QR code")} className="mx-auto" style={{ width: 160, height: 160 }} />}
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder={t("6-digit code")}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="w-full rounded-md px-3 py-2 outline-none text-center"
                  style={{ border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.mono, fontSize: 16, letterSpacing: 2 }}
                />
                <button disabled={mfaBusy || mfaCode.trim().length < 6} onClick={verifyMfaEnroll} className="w-full py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: "#fff" }}>
                  {t("Confirm")}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {contactAdminOpen && (
        <Modal title={t("Help Center")} onClose={() => setContactAdminOpen(false)} wide={supportView === "thread"}>
          {/* FAQ1 — dy skeda: pyetjet e shpeshta · tiketat. Skeda e pyetjeve del
              vetëm kur admini i platformës ka shënuar të paktën një pyetje «faq». */}
          {(supportView === "list" || supportView === "faq") && (
            <div className="flex gap-1 mb-3 p-1 rounded-lg" style={{ background: COLORS.onBgSoft }}>
              {[["list", t("help.tickets")], ["faq", t("help.faq")]].map(([v, label]) => (
                <button key={v} onClick={() => setSupportView(v)} className="flex-1 py-1.5 rounded-md text-sm" style={{ background: supportView === v ? COLORS.gold : "transparent", color: supportView === v ? COLORS.bgDeep : COLORS.ink, fontFamily: FONTS.body, fontWeight: supportView === v ? 700 : 500 }}>{label}</button>
              ))}
            </div>
          )}
          {supportView === "faq" && (
            <div className="space-y-2">
              {/* Pyetjet e ngulitura vijnë nga gjuha (faq.N.q / faq.N.a — redaktohen te
                  përkthimet); pas tyre ato që admini i platformës i shënon «faq»
                  te baza e njohurive. Kështu faqja s'është kurrë bosh. */}
              {[...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ id: "n" + n, question: t(`faq.${n}.q`), answer: t(`faq.${n}.a`), ngulitur: true })), ...(faq || [])].map((q) => (
                <div key={q.id} className="rounded-md" style={{ background: COLORS.panelSoft }}>
                  <button onClick={() => setFaqHapur(faqHapur === q.id ? null : q.id)} className="w-full text-left px-3 py-2.5 flex items-start gap-2" style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>
                    <span style={{ flex: 1 }}>{q.ngulitur ? q.question : tDyn(q.question)}</span>
                    <span aria-hidden="true" style={{ color: COLORS.inkLight, transform: faqHapur === q.id ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</span>
                  </button>
                  {faqHapur === q.id && (
                    <div className="px-3 pb-3" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{q.ngulitur ? q.answer : tDyn(q.answer)}</div>
                  )}
                </div>
              ))}
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 8 }}>{t("help.faqHint")}</p>
            </div>
          )}
          {supportView === "list" && (
            <div className="space-y-3">
              {/* ADMINET1 (12 shtator 2026) — pronari: «familjarët duan ta kontaktojnë
                  një admin, po s'e dinë kush është». Emrat vijnë nga `roles` që
                  klienti i ka tashmë; s'ka derë të re. */}
              {(() => {
                const adminet = Object.keys(roles).filter((id) => roles[id] === "admin" && people[id] && !people[id].death_date).map((id) => displayName(people[id]));
                if (!adminet.length) return null;
                return (
                  <div className="px-3 py-2.5 rounded-md" style={{ background: COLORS.panelSoft }}>
                    <div style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("help.familyAdmins")}</div>
                    <div style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.ink, marginTop: 3 }}>{adminet.join(" · ")}</div>
                    <div style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 3 }}>{t("help.familyAdminsHint")}</div>
                  </div>
                );
              })()}
              <button onClick={() => setSupportView("new")} className="w-full py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>
                {t("+ New ticket")}
              </button>
              {myTickets.length === 0 ? (
                <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, textAlign: "center", padding: "20px 0" }}>
                  {t("You don't have any ticket yet. Click \"New ticket\" to start.")}
                </p>
              ) : (
                <div className="space-y-2">
                  {myTickets.map((tk) => (
                    <button key={tk.id} onClick={() => openTicketThread(tk.id)} className="w-full text-left px-3 py-2.5 rounded-md" style={{ background: COLORS.panelSoft }}>
                      <div className="flex items-center justify-between gap-2">
                        <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, fontWeight: 600 }}>
                          {tk.ref && <span style={{ fontFamily: FONTS.mono, fontSize: 12.5, color: COLORS.ink, marginRight: 6 }}>{tk.ref}</span>}
                          {tk.category === "raportim" ? t("Content report") : tk.category === "legacy_unlock" ? t("Legacy request") : tk.category === "bug" ? t("Technical error") : tk.category === "question" ? t("Question / Request") : tk.category === "account_change" ? t("Change of data/roles") : tk.category === "problem" ? t("Access/account problem") : t("Other")}
                        </span>
                        <span style={{
                          fontFamily: FONTS.body, fontSize: 12.5,
                          color: tk.status === "resolved" ? "#3D7A4A" : tk.status === "pending" ? "#B8862F" : tk.status === "in_progress" ? "#3D6B9E" : COLORS.inkLight,
                        }}>
                          {tk.status === "open" ? t("Open") : tk.status === "in_progress" ? t("In progress") : tk.status === "pending" ? t("Pending") : tk.status === "resolved" ? t("Resolved") : t("Closed")}
                        </span>
                      </div>
                      <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{t("{{count}} message(s)", { count: tk.message_count })} · {timeAgo(tk.last_message_at || tk.created_at)}</span>
                        {Number(tk.unread) > 0 ? <span style={{ minWidth: 16, height: 16, padding: "0 5px", borderRadius: 999, background: "#D14343", color: "#fff", fontFamily: FONTS.body, fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{t("{{sa}} new", { sa: tk.unread })}</span> : null}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {supportView === "new" && (
            <div className="space-y-3">
              <button onClick={() => setSupportView("list")} className="text-xs" style={{ fontFamily: FONTS.body, color: COLORS.inkLight }}>{t("← Back")}</button>
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
                {t("For a forgotten password use the \"Can't sign in?\" button on the sign-in screen — this is for everything else: errors, change requests, or questions.")}
              </p>
              <div>
                <FieldLabel>{t("Category")}</FieldLabel>
                <select value={contactAdminCategory} onChange={(e) => setContactAdminCategory(e.target.value)} className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle}>
                  <option value="bug">{t("Technical error (Incident)")}</option>
                  <option value="question">{t("Question / Request")}</option>
                  <option value="account_change">{t("Change of data/roles")}</option>
                  <option value="problem">{t("Access/account problem")}</option>
                  <option value="other">{t("Other")}</option>
                </select>
              </div>
              <div>
                <FieldLabel>{t("Message")}</FieldLabel>
                <textarea rows={4} value={contactAdminMessage} onChange={(e) => setContactAdminMessage(e.target.value)} className="w-full rounded-md px-3 py-2 outline-none" style={inputStyle} placeholder={t("Write here…")} />
              </div>
              <button disabled={contactAdminBusy || !contactAdminMessage.trim()} onClick={handleSubmitTicket} className="w-full py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: (contactAdminBusy || !contactAdminMessage.trim()) ? 0.6 : 1 }}>
                {contactAdminBusy ? t("Sending…") : t("Send")}
              </button>
            </div>
          )}

          {supportView === "thread" && (() => {
            const activeTk = (myTickets || []).find((t) => t.id === activeTicketId) || {};
            const stLabel = activeTk.status === "open" ? t("Open") : activeTk.status === "in_progress" ? t("In progress") : activeTk.status === "pending" ? t("Pending") : activeTk.status === "resolved" ? t("Resolved") : activeTk.status === "closed" ? t("Closed") : "";
            const catLabel = activeTk.category === "legacy_unlock" ? t("Legacy request") : activeTk.category === "bug" ? t("Technical error") : activeTk.category === "question" ? t("Question / Request") : activeTk.category === "account_change" ? t("Change of data/roles") : activeTk.category === "problem" ? t("Access/account problem") : activeTk.category === "other" ? t("Other") : "";
            return (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button onClick={() => { setSupportView("list"); loadMyTickets(); }} className="text-xs" style={{ fontFamily: FONTS.body, color: COLORS.inkLight }}>{t("← Back to tickets")}</button>
                <div className="flex items-center gap-2">
                  <button onClick={downloadTicketTranscript} disabled={ticketDownloadBusy || !(ticketThread && ticketThread.length)} className="text-xs px-2.5 py-1 rounded-md" style={{ fontFamily: FONTS.body, background: COLORS.panelSoft, color: COLORS.ink, opacity: (ticketDownloadBusy || !(ticketThread && ticketThread.length)) ? 0.5 : 1 }} title={t("Download the conversation as proof (standalone HTML)")}>{ticketDownloadBusy ? t("Preparing…") : t("⬇ Download the conversation")}</button>
                  <button onClick={hideTicketFromView} className="text-xs px-2.5 py-1 rounded-md" style={{ fontFamily: FONTS.body, background: "transparent", color: COLORS.inkLight, border: `1px solid ${COLORS.goldSoft}` }} title={t("Delete the conversation from your view (kept on the platform)")}>{t("🗑 Delete the conversation")}</button>
                </div>
              </div>
              {activeTk.ref ? (
                <div className="flex items-center gap-2 flex-wrap" style={{ borderBottom: `1px solid ${COLORS.goldSoft}`, paddingBottom: 8 }}>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 12.5, fontWeight: 700, color: COLORS.ink }}>{activeTk.ref}</span>
                  {catLabel ? <span style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.ink, background: COLORS.panelSoft, borderRadius: 999, padding: "1px 8px" }}>{catLabel}</span> : null}
                  <span style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>{stLabel}</span>
                </div>
              ) : null}
              {ticketThreadError ? (
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: "#B23A3A" }}>{t("The conversation did not load")}: {ticketThreadError}. {t("Make sure the new schema has been run in Supabase.")}</p>
              ) : null}
              {!ticketThread ? (
                <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Loading…")}</p>
              ) : ticketThread.length === 0 && !ticketThreadError ? (
                <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("No messages in this conversation yet.")}</p>
              ) : (
                <>
                  <div className="space-y-2" style={{ maxHeight: "55vh", overflowY: "auto", overflowX: "hidden" }}>
                    {ticketThread.map((m) => (
                      <div key={m.id} style={{
                        marginLeft: m.sender_role === "user" ? "auto" : 0,
                        marginRight: m.sender_role === "admin" ? "auto" : 0,
                        maxWidth: "80%",
                        padding: "8px 12px",
                        borderRadius: 10,
                        background: m.sender_role === "user" ? COLORS.gold : COLORS.onBgSoft,
                      }}>
                        {/* PËRKTHIMI I BISEDËS (31 gusht 2026) — i njëjti tub si postimet.
                            Deri sot trupi kalonte nga `tDyn(m.body, false)`, që përkthen
                            VETËM nëse ekziston një mbivendosje e shkruar nga admini — dhe
                            për tekstin që shkruan një njeri ajo s'ekziston kurrë. Pra ishte
                            një derë e mbyllur që dukej e hapur.
                            Tani vlen po ai rregull i PERKTHIMI1: një prekje, një mesazh;
                            përkthimi ruhet përgjithmonë; dhe origjinali rri një prekje larg,
                            sepse fjalët e dikujt të rishkruara nga një makinë dhe të
                            paraqitura si të tijat do të ishin gënjeshtër e heshtur. */}
                        <p style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONTS.body, fontSize: 12.5, color: m.sender_role === "user" ? COLORS.bgDeep : COLORS.inkLight, opacity: 0.8, marginBottom: 2 }}>
                          <span>{m.sender_role === "user" ? t("You") : (tDyn(m.sender_label) || t("Support"))} · {timeAgo(m.created_at)}</span>
                          {m.body ? butoniIPerkthimit(m.id, { body: m.body }, m.sender_role === "user" ? COLORS.bgDeep : COLORS.inkLight, t("Translate this message"), "tiketa") : null}
                        </p>
                        {m.body ? <p style={{ fontFamily: FONTS.body, fontSize: 13, color: m.sender_role === "user" ? COLORS.bgDeep : COLORS.ink, whiteSpace: "pre-wrap" }}>{tekstiIDukshem(m.id, "body", tDyn(m.body, false)).teksti}</p> : null}
                        {m.body && kaPerkthim(m.id, { body: m.body }) && rreshtiIPerkthimit(m.id, m.sender_role === "user" ? COLORS.bgDeep : COLORS.inkLight)}
                        {(m.attachments || []).map((p) => ticketAttUrls[p]
                          ? <a key={p} href={ticketAttUrls[p]} target="_blank" rel="noreferrer"><img src={ticketAttUrls[p]} alt={t("attachment")} style={{ maxWidth: "100%", maxHeight: 260, borderRadius: 8, marginTop: 6, display: "block" }} /></a>
                          : <div key={p} style={{ fontFamily: FONTS.body, fontSize: 12.5, opacity: 0.75, marginTop: 4 }}>{t("📎 image…")}</div>)}
                      </div>
                    ))}
                  </div>
                  {ticketPendingAtts.length > 0 && (
                    <div style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }}>
                      📎 {t("{{sa}} image(s) ready to send", { sa: ticketPendingAtts.length })}
                      <button onClick={() => setTicketPendingAtts([])} className="ml-2 underline" style={{ color: COLORS.inkLight }}>{t("Remove")}</button>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 items-center" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                    <label className="px-2 py-2 rounded-md cursor-pointer" title={t("Attach image")} style={{ background: COLORS.panelSoft }}>
                      📎<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadTicketAtt(f); e.target.value = ""; }} />
                    </label>
                    <input
                      type="text"
                      value={ticketReplyText}
                      onChange={(e) => setTicketReplyText(e.target.value)}
                      onPaste={onTicketReplyPaste}
                      onKeyDown={(e) => { if (e.key === "Enter" && !ticketReplyBusy) handleSendTicketReply(); }}
                      placeholder={t("Write the message (or paste a screenshot)…")}
                      className="flex-1 rounded-md px-3 py-2 outline-none"
                      style={inputStyle}
                    />
                    <button onClick={handleSendTicketReply} disabled={ticketReplyBusy || (!ticketReplyText.trim() && ticketPendingAtts.length === 0)} className="px-4 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: (ticketReplyBusy || (!ticketReplyText.trim() && ticketPendingAtts.length === 0)) ? 0.6 : 1 }}>
                      {t("Send")}
                    </button>
                  </div>
                </>
              )}
            </div>
            );
          })()}
        </Modal>
      )}

      {docModalOpen && (
        <Modal title={t(DOC_LABELS[docModalOpen] || "Full documentation")} onClose={() => setDocModalOpen(null)} wide fullBleed>
          {/* NDËRTUAR (19 gusht 2026) — `docHistory` mbushej me TË GJITHA
              versionet që në nisje, dhe `docHistoryOpenType` ekzistonte si
              gjendje për ta hapur historinë, por asnjë buton s'e vendoste dhe
              asnjë JSX s'e lexonte: të dhënat merreshin dhe hidheshin. Tani
              versionet e mëparshme janë të arritshme, sepse kur ndryshojnë
              kushtet ka rëndësi të shihet edhe ç'ishte më parë. */}
          {/* DOKUMENTET1 — kur përkthimi mungon, personi e mëson këtë para se
              të lexojë, jo pasi ta ketë pranuar. */}
          {docFallback[docModalOpen] && (
            <div className="px-4 pt-3">
              <p style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight, background: "rgba(255,255,255,0.04)", border: `1px solid ${COLORS.gold}33`, borderRadius: 8, padding: "8px 10px", margin: 0 }}>
                {t("This document is not yet available in your language. It is shown in Albanian, the original.")}
              </p>
            </div>
          )}
          {(docHistory[docModalOpen] || []).length > 1 && (
            <div className="px-4 pt-3">
              <button onClick={() => setDocHistoryOpenType(docModalOpen)} style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, color: COLORS.ink, background: "transparent", border: "none", cursor: "pointer", padding: 0 }}>
                {t("Earlier versions ({{count}})", { count: (docHistory[docModalOpen] || []).length - 1 })}
              </button>
            </div>
          )}
          {docs[docModalOpen] ? (
            <DocHtmlContent row={docs[docModalOpen]} docUrl={documentUrl(docs[docModalOpen])} />
          ) : (
            <p className="p-4" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("This document has not been published yet.")}</p>
          )}
        </Modal>
      )}

      {/* Historiku i versioneve — lista e plotë, me lidhje drejt secilit. */}
      {docHistoryOpenType && (
        <Modal title={t("Earlier versions")} onClose={() => setDocHistoryOpenType(null)}>
          {((docHistory[docHistoryOpenType] || []).length === 0) ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("No earlier version.")}</p>
          ) : (
            <div className="space-y-1">
              {(docHistory[docHistoryOpenType] || []).map((row) => {
                const isCurrent = docs[docHistoryOpenType] && row.version === docs[docHistoryOpenType].version;
                return (
                  <a key={row.id || row.version} href={documentUrl(row)} target="_blank" rel="noreferrer"
                     className="flex items-center justify-between gap-2 px-2 py-2 rounded-md"
                     style={{ textDecoration: "none", background: isCurrent ? COLORS.accentTint : "transparent", border: `1px solid ${COLORS.goldSoft}` }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>
                      {t("Version {{v}}", { v: row.version })}{isCurrent ? ` — ${t("current")}` : ""}
                    </span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : ""}
                    </span>
                  </a>
                );
              })}
            </div>
          )}
        </Modal>
      )}

      {termsGateOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(15,18,13,0.7)" }}>
          <div className="w-full max-w-lg rounded-xl shadow-2xl flex flex-col" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, maxHeight: "88vh" }}>
            <div className="px-5 py-4 shrink-0 sticky top-0" style={{ borderBottom: `1px solid ${COLORS.goldSoft}`, background: COLORS.panel }}>
              <h3 style={{ fontFamily: FONTS.display, color: COLORS.ink, fontSize: 19, fontWeight: 600 }}>{t("Terms of Use and Privacy Policy")}</h3>
              <p style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight, marginTop: 2 }}>{t("You must read and accept them to continue.")}</p>
            </div>
            <div className="p-5" style={{ overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch" }}>
              <TermsContent termsDoc={docs.terms ? { row: docs.terms, publicUrl: documentUrl(docs.terms) } : null} />
            </div>
            <div className="p-5 shrink-0" style={{ borderTop: `1px solid ${COLORS.goldSoft}`, background: COLORS.panel }}>
              <label className="flex items-start gap-2 mb-3 cursor-pointer">
                <input type="checkbox" checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)} className="mt-0.5" />
                <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>
                  {t("I have read, understood and accepted the Terms of Use and the Privacy Policy, and I accept using the platform under these terms.")}
                </span>
              </label>
              <div className="flex gap-2">
                <button onClick={refuseTerms} className="flex-1 py-2 rounded-md text-sm" style={{ color: COLORS.inkLight, border: `1px solid ${COLORS.goldSoft}` }}>{t("Reject")}</button>
                <button onClick={acceptTerms} className="flex-1 py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, opacity: termsChecked ? 1 : 0.6 }}>{t("Accept")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {logPanelOpen && canManage && (
        <Modal title={t("The log")} onClose={() => setLogPanelOpen(false)} wide>
          <div className="flex gap-2 mb-4">
            <button onClick={() => setLogTab("changes")} className="flex-1 py-1.5 rounded-md text-sm" style={{ background: logTab === "changes" ? COLORS.gold : COLORS.onBgSoft, color: logTab === "changes" ? COLORS.bgDeep : COLORS.ink }}>{t("Changes")}</button>
            <button onClick={() => setLogTab("logins")} className="flex-1 py-1.5 rounded-md text-sm" style={{ background: logTab === "logins" ? COLORS.gold : COLORS.onBgSoft, color: logTab === "logins" ? COLORS.bgDeep : COLORS.ink }}>{t("Sign-ins")}</button>
            <button onClick={() => setLogTab("checks")} className="flex-1 py-1.5 rounded-md text-sm" style={{ background: logTab === "checks" ? COLORS.gold : COLORS.onBgSoft, color: logTab === "checks" ? COLORS.bgDeep : COLORS.ink }}>{t("Checks")}</button>
          </div>

          {logTab === "changes" && (() => {
            // 5 shtator 2026 — pesë prej këtyre etiketave ishin shqip të
            // ngulitura («shtoi», «redaktoi», «fshiu», «bllokoi», «zhbllokoi»),
            // pra dilnin shqip edhe brenda versionit anglisht, mes fjalëve të
            // përkthyera. Të njëjtin defekt e kishte «Dikush».
            const actionLabel = {
              add: t("added"), edit: t("edited"), delete: t("deleted"), set_role: t("changed the role of"),
              link_spouse: t("linked as spouses"), block: t("blocked"), unblock: t("unblocked"),
              upload_document: t("uploaded a document"), send_notification: t("sent a notification"),
              export: t("exported a backup"), export_poster: t("downloaded a poster"),
              sync_profile: t("synced the profile"), import_family: t("brought the family"),
              leave_family: t("left the family"), join_code: t("joined with a code"),
              legacy_grant: t("was granted legacy access"),
              // LIDHJA5 — çdo bashkim (kod, ftesë, lidhje nga admini) + shkëputja (LLOGARIA1)
              account_link: t("joined the family"), unlink_account: t("unlinked the account of"),
            };
            // 6 shtator 2026 — «kur ka disa persona me emër të njëjtë, edhe te
            // historiku duhet të dihet për cilin bëhet fjalë». Emri vjen me
            // dalluesin (prindi / viti i lindjes) kur personi është ende në pemë;
            // për të fshirët mbetet emri i ruajtur te regjistri.
            const emriNeRegjister = (id, emriIRuajtur) => (id && people[id] ? emriDallues(people[id], etiketatDalluese) : emriIRuajtur);
            const nonLogin = activityLog
              .filter((l) => l.action !== "login")
              .map((l) => ({ ...l, actor_name: emriNeRegjister(l.actor_id, l.actor_name), target_name: emriNeRegjister(l.target_id, l.target_name) }));
            const q = logSearch.trim().toLowerCase();
            const filtered = q
              ? nonLogin.filter((l) => [l.actor_name, l.target_name, l.detail, actionLabel[l.action] || l.action].filter(Boolean).some((s) => s.toLowerCase().includes(q)))
              : nonLogin;
            const downloadLog = () => {
              const lines = nonLogin.map((l) => {
                const label = actionLabel[l.action] || l.action;
                const when = fmtDateTime(l.created_at);
                return `${when} — ${l.actor_name || t("Someone")} ${label} ${l.target_name || ""}${l.detail ? ` (${l.detail})` : ""}`.trim();
              });
              const blob = new Blob([lines.join("\n")], { type: "text/plain" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `regjistri-ndryshimeve-${stampForFilename()}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            };
            return (
              <div>
                <div className="flex gap-2 mb-3">
                  <input
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    placeholder={t("Search by person, action or detail…")}
                    className="flex-1 rounded-md px-3 py-2 outline-none text-sm"
                    style={inputStyle}
                  />
                  <button onClick={downloadLog} disabled={nonLogin.length === 0} className="px-3 py-2 rounded-md text-sm shrink-0" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, opacity: nonLogin.length === 0 ? 0.5 : 1 }}>
                    {t("⬇️ Download")}
                  </button>
                </div>
                {filtered.length === 0 ? (
                  <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>
                    {nonLogin.length === 0 ? t("No change recorded yet.") : t("No results for this search.")}
                  </p>
                ) : (
                  <>
                  <ul className="space-y-1.5 max-h-96 overflow-y-auto">
                    {filtered.map((l) => (
                      <li key={l.id} className="px-2.5 py-2 rounded-md" style={{ background: COLORS.panelSoft }}>
                        <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>
                          <strong>{l.actor_name || t("Someone")}</strong> {actionLabel[l.action] || l.action} <strong>{l.action === "account_link" ? "" : (l.target_name || "")}</strong>
                          {l.action === "set_role" && l.detail ? ` → ${t(ROLE_LABELS[l.detail] || l.detail)}` : ""}
                        </p>
                        {["edit", "delete", "block", "unblock", "upload_document", "send_notification", "account_link", "unlink_account", "join_code"].includes(l.action) && l.detail && l.detail !== "ruajti pa ndryshime të dukshme" && (
                          <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, marginTop: 2 }}>{l.detail}</p>
                        )}
                        <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{fmtDateTime(l.created_at)}</p>
                      </li>
                    ))}
                  </ul>
                  {/* REGJISTRI4 — sa kohë serveri e mbush kufirin, ka edhe më.
                      Pa këtë buton, njeriu s'e di nëse regjistri mbaroi apo
                      thjesht u pre. */}
                  {activityLog.length >= logLimit && (
                    <button
                      onClick={() => { const n = logLimit + 300; setLogLimit(n); fetchActivityLog(n); }}
                      className="w-full mt-2 py-2 rounded-md text-sm"
                      style={{ border: `1px solid ${COLORS.goldSoft}`, background: "transparent", color: COLORS.ink, fontFamily: FONTS.body, cursor: "pointer" }}>
                      {t("Show more")}
                    </button>
                  )}
                  </>
                )}
              </div>
            );
          })()}

          {logTab === "logins" && (
            <div>
              <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.inkLight }}>
                {t("{{a}} of {{b}} members have signed in at least once.", { a: loginActivity.filter((l) => l.login_count > 0).length, b: loginActivity.length })}
              </p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {[
                  ["all", t("Everyone")],
                  ["logged_in", "Identifikuar"],
                  ["never", t("Never")],
                  ["active_15m", t("Active (15 min)")],
                  ["active_1h", t("Active (1 hour)")],
                  ["active_24h", t("Active (24 hours)")],
                ].map(([val, label]) => (
                  <button key={val} onClick={() => setLoginFilter(val)} className="py-1 px-2.5 rounded-md text-xs" style={{ border: `1.5px solid ${loginFilter === val ? COLORS.gold : COLORS.goldSoft}`, background: loginFilter === val ? (COLORS.accentTint || _rgba(COLORS.gold, 0.16)) : (COLORS.field || COLORS.panel), color: COLORS.ink }}>
                    {label}
                  </button>
                ))}
              </div>
              {(() => {
                const now = Date.now();
                const WINDOW_MS = { active_15m: 15 * 60 * 1000, active_1h: 60 * 60 * 1000, active_24h: 24 * 60 * 60 * 1000 };
                const filtered = loginActivity.filter((l) => {
                  if (loginFilter === "logged_in") return l.login_count > 0;
                  if (loginFilter === "never") return l.login_count === 0 || !l.login_count;
                  if (WINDOW_MS[loginFilter]) return l.last_login && now - new Date(l.last_login).getTime() < WINDOW_MS[loginFilter];
                  return true;
                });
                if (filtered.length === 0) return <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Nobody matches this filter.")}</p>;
                return (
                  <ul className="space-y-1.5 max-h-96 overflow-y-auto">
                    {filtered.map((l) => (
                      <li key={l.person_id} className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-md" style={{ background: l.login_count > 0 ? COLORS.panelSoft : "transparent", border: l.login_count > 0 ? "none" : `1px dashed ${COLORS.goldSoft}` }}>
                        <div>
                          <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{l.full_name} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>({t(ROLE_LABELS[l.role] || l.role)})</span></p>
                          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
                            {l.login_count > 0 ? t("{{count}}× signed in · last: {{when}}", { count: l.login_count, when: fmtDateTime(l.last_login) }) : t("Never signed in yet")}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          )}


          {logTab === "checks" && (() => {
            const all = Object.values(people);
            const dupGroups = {};
            all.forEach((p) => {
              const key = `${(p.first_name || "").trim().toLowerCase()}|${(p.last_name || "").trim().toLowerCase()}|${p.birth_date || ""}`;
              if (!dupGroups[key]) dupGroups[key] = [];
              dupGroups[key].push(p);
            });
            const duplicates = Object.values(dupGroups).filter((g) => g.length > 1);
            const orphans = all.filter((p) => !(p.parent_ids || []).length);
            const noParentNoSpouse = all.filter((p) => !(p.parent_ids || []).length && !(p.spouse_ids || []).length);
            const noParentNoDescendants = all.filter((p) => !(p.parent_ids || []).length && !(baseChildrenMap[p.id] || []).length);
            return (
              <div className="space-y-2.5">
                {(() => {
                  const isOpen = (k) => checksOpen.has(k);
                  const toggle = (k) => setChecksOpen((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
                  const personRow = (p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-md" style={{ background: COLORS.panelSoft }}>
                      <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{emriDallues(p, etiketatDalluese)} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>({lifespan(p)})</span></span>
                      <button onClick={() => { setLogPanelOpen(false); setSelectedId(p.id); }} className="px-2 py-1 rounded-md text-xs shrink-0" style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>{t("View the profile")}</button>
                    </li>
                  );
                  const section = (k, emoji, title, count, desc, empty, listJsx) => (
                    <div className="rounded-xl" style={{ border: `1px solid ${COLORS.goldSoft}`, overflow: "hidden" }}>
                      <button onClick={() => toggle(k)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-black/5">
                        <span style={{ fontSize: 15 }}>{emoji}</span>
                        <span className="flex-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>{title}</span>
                        <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: count > 0 ? COLORS.accentText : COLORS.inkLight, background: COLORS.panelSoft, borderRadius: 999, padding: "1px 8px" }}>{count}</span>
                        <ChevronDown size={16} color={COLORS.gold} style={{ transform: isOpen(k) ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                      </button>
                      {isOpen(k) && (
                        <div className="px-3 pb-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                          {desc && <p className="mt-2 mb-2" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }}>{desc}</p>}
                          {count === 0 ? <p className="mt-2" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{empty}</p> : listJsx}
                        </div>
                      )}
                    </div>
                  );
                  return (
                    <>
                      {section("dup", "🔁", t("POSSIBLY RECORDED TWICE"), duplicates.length, null, t("No possible duplicate found."), (
                        <ul className="space-y-2 max-h-80 overflow-y-auto">
                          {duplicates.map((group, gi) => (
                            <li key={gi} className="px-2.5 py-2 rounded-md" style={{ background: COLORS.accentTint, border: `1px solid ${COLORS.goldSoft}` }}>
                              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{displayName(group[0])} <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>({lifespan(group[0])})</span> — {t("{{sa}} records", { sa: group.length })}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {group.map((p) => (
                                  <button key={p.id} onClick={() => { setLogPanelOpen(false); setSelectedId(p.id); }} className="px-2 py-1 rounded-md text-xs" style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>{t("View the profile")}</button>
                                ))}
                              </div>
                            </li>
                          ))}
                        </ul>
                      ))}
                      {section("orphan", "🧩", t("WITH NO PARENT RECORDED"), orphans.length, t("Normally only one person (the founding ancestor of the tree) should be here. If you see more, someone is likely missing the link to their own parent."), t("Nobody."), (
                        <ul className="space-y-1.5 max-h-80 overflow-y-auto">{orphans.map(personRow)}</ul>
                      ))}
                      {section("noSpouse", "💔", t("WITH NO PARENTS AND NO MARRIAGE"), noParentNoSpouse.length, t("People completely unconnected to the rest of the tree — they are likely missing a link."), t("Nobody."), (
                        <ul className="space-y-1.5 max-h-80 overflow-y-auto">{noParentNoSpouse.map(personRow)}</ul>
                      ))}
                      {section("noDesc", "🍃", t("WITH NO PARENTS AND NO DESCENDANTS"), noParentNoDescendants.length, t("People who connect to no generation above or below — they may be dead branches or registration mistakes."), t("Nobody."), (
                        <ul className="space-y-1.5 max-h-80 overflow-y-auto">{noParentNoDescendants.map(personRow)}</ul>
                      ))}
                    </>
                  );
                })()}
              </div>
            );
          })()}
        </Modal>
      )}

      {rolesPanelOpen && canManage && (() => {
        const filteredMembers = Object.values(people).filter((p) => {
          if (membersRoleFilter !== "all" && (roles[p.id] || "viewer") !== membersRoleFilter) return false;
          if (membersSearch.trim() && !`${displayName(p)} ${emriDallues(p, etiketatDalluese)}`.toLowerCase().includes(membersSearch.trim().toLowerCase())) return false;
          return true;
        });
        return (
        <Modal title={t("members.manageMembers")} onClose={() => setRolesPanelOpen(false)} wide>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("members.roleExplainer")}</p>

          {/* "Kodi i Familjes" — mekanizmi i tretë i bashkimit (shih schema.sql).
              Admini gjeneron një kod 6-shifror, e shpërndan lirisht (WhatsApp,
              letër, gojarisht), dhe kushdo me kodin bashkohet vetë te /join. */}
          <div className="mb-4 p-3 rounded-md" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}` }}>
            <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, color: COLORS.ink }}>{t("members.joinCodeTitle")}</p>

            {/* Jetëgjatësia zgjidhet PARA çdo gjenerimi (i pari ose rigjenerim
                — rpc_set_family_join_code revokon gjithmonë kodin e vjetër
                automatikisht, pavarësisht kohëzgjatjes së zgjedhur këtu). */}
            <div className="flex items-center gap-2 mb-2">
              <span style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight }}>{t("members.joinCodeDuration")}</span>
              <select
                value={joinCodeDurationHours}
                onChange={(e) => setJoinCodeDurationHours(Number(e.target.value))}
                className="rounded-md px-2 py-1 text-xs"
                style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.field, color: COLORS.ink }}
              >
                <option value={1}>{t("members.joinCodeDuration1h")}</option>
                <option value={24}>{t("members.joinCodeDuration1d")}</option>
                <option value={168}>{t("members.joinCodeDuration1w")}</option>
                <option value={720}>{t("members.joinCodeDuration1m")}</option>
              </select>
            </div>

            {familyJoinCode ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ fontFamily: FONTS.mono, fontSize: 22, letterSpacing: 4, color: COLORS.ink }}>{familyJoinCode.code}</span>
                  <button onClick={copyJoinCodeLink} title={t("members.joinCodeCopyLink")} className="p-1 rounded-md" style={{ background: COLORS.field }}>
                    <Link2 size={14} color={COLORS.ink} />
                  </button>
                </div>
                <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight }}>
                  {t("members.joinCodeExpires", { date: fmtDate(familyJoinCode.expires_at) })} · {t("members.joinCodeUses", { count: familyJoinCode.use_count || 0 })}
                </p>
                <div className="flex gap-2">
                  <button onClick={generateFamilyJoinCode} disabled={joinCodeBusy} className="px-2 py-1 rounded-md text-xs" style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink }}>{t("members.joinCodeRegenerate")}</button>
                  <button onClick={revokeFamilyJoinCode} disabled={joinCodeBusy} className="px-2 py-1 rounded-md text-xs" style={{ background: COLORS.dangerSoft, color: COLORS.danger }}>{t("members.joinCodeRevoke")}</button>
                </div>
              </>
            ) : (
              <button onClick={generateFamilyJoinCode} disabled={joinCodeBusy} className="px-3 py-1.5 rounded-md text-xs" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>
                {joinCodeBusy ? "…" : t("members.joinCodeGenerate")}
              </button>
            )}

            {joinCodeClaims.length > 0 && (
              <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
                <p className="mb-1.5" style={{ fontFamily: FONTS.body, fontSize: 11, fontWeight: 600, color: COLORS.inkLight }}>
                  {t("members.joinCodeRecentJoins")}{unseenJoinCodeClaimsCount > 0 ? ` (${unseenJoinCodeClaimsCount} ${t("members.joinCodeNew")})` : ""}
                </p>
                <ul className="space-y-1 max-h-32 overflow-y-auto">
                  {joinCodeClaims.slice(0, 10).map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-2" style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.ink }}>
                      <span>{!c.seen_at && <span style={{ color: COLORS.ink }}>● </span>}{c.first_name} {c.last_name} {c.kind === "new_branch" ? `(${t("members.joinCodeNewBranch")})` : ""}</span>
                      <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, fontVariantNumeric: "tabular-nums" }}>{fmtDate(c.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <input
            value={membersSearch}
            onChange={(e) => setMembersSearch(e.target.value)}
            placeholder={t("members.searchByName")}
            className="w-full rounded-md px-3 py-2 mb-2 outline-none text-sm"
            style={inputStyle}
          />
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[["all", t("members.all")], ["admin", t("members.admin")], ["editor", t("members.editor")], ["contributor", t("members.contributor")], ["viewer", t("members.viewer")]].map(([val, label]) => (
              <button key={val} onClick={() => setMembersRoleFilter(val)} className="py-1 px-2.5 rounded-md text-xs" style={{ border: `1.5px solid ${membersRoleFilter === val ? COLORS.gold : COLORS.goldSoft}`, background: membersRoleFilter === val ? (COLORS.accentTint || _rgba(COLORS.gold, 0.16)) : (COLORS.field || COLORS.panel), color: COLORS.ink }}>
                {label}
              </button>
            ))}
          </div>
          <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("members.countOf", { shown: filteredMembers.length, total: Object.keys(people).length })}</p>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {filteredMembers.length === 0 ? (
              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("members.noResults")}</p>
            ) : (
            filteredMembers.map((p) => (
              <div key={p.id} className="px-2 py-1.5 rounded-md" style={{ background: p.blocked ? COLORS.dangerSoft : COLORS.panelSoft }}>
                <div className="flex items-center justify-between gap-2">
                  <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>
                    {emriDallues(p, etiketatDalluese)} {p.blocked && <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.danger }}>{t("members.blocked")}</span>}
                  </span>
                  {/* REDAKTOR1: «Redaktor +» = roli editor me shkallën 2 (gjyshërit); serveri e njeh `editor_plus`. */}
                  <select value={roles[p.id] === "editor" && rolesShkalla[p.id] === 2 ? "editor_plus" : (roles[p.id] || "viewer")} onChange={(e) => setRole(p.id, e.target.value)} className="rounded-md px-2 py-1 text-xs" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.field, color: COLORS.ink }}>
                    <option value="admin">{t("members.admin")}</option><option value="editor">{t("members.editor")}</option><option value="editor_plus">{t("members.editor_plus")}</option><option value="contributor">{t("members.contributor")}</option><option value="viewer">{t("members.viewer")}</option>
                  </select>
                  {/* ROLI1 — «me dorë» + ↺ te automatiku; adminët s'kanë rol automatik */}
                  {roles[p.id] !== "admin" && rolesMeDore[p.id] && (
                    <button onClick={() => riktheRolinAutomatik(p.id)} title={t("members.roleAutoTitle")} className="p-1 rounded-md shrink-0" style={{ background: COLORS.panelSoft }}>
                      <RefreshCw size={13} color={COLORS.ink} />
                    </button>
                  )}
                  {/* FAZA 2: krijon + dërgon një ftesë reale (email) që ky person të lidhë një llogari Google/Apple/Email — shih sendInviteTo më lart */}
                  <button onClick={() => sendInviteTo(p.id)} title={t("members.sendInviteTitle")} className="p-1 rounded-md shrink-0" style={{ background: COLORS.panelSoft }}>
                    <Mail size={14} color={COLORS.ink} />
                  </button>
                  {/* HEQUR më 2 shtator 2026 — butoni «Kopjo lidhjen e ftesës»
                      rrinte këtu. Ai prodhonte një link pa adresë, që hynte te
                      pema për këdo që e mbante. Zëvendësohet nga butoni ngjitur
                      me kodin personal, që e di se për kë është. Shpjegimi i
                      plotë te vendi ku ishte `copyInviteLink`. */}
                  {/* KODI2 — kod personal vetëm për këtë person. Ndryshe nga
                      ftesa, nuk kërkon email; dhe ndryshe nga kodi i familjes,
                      personi nuk kërkon veten me emër — kodi e di kush është.
                      Serveri e refuzon nëse personi ka tashmë llogari. */}
                  {llogaritEAnetareve[p.id] ? (
                    /* LLOGARIA1 — i lidhur: në vend të çelësit, trekëndëshi «Shkëput llogarinë» (jo për veten / adminët) */
                    p.id !== currentPersonId && roles[p.id] !== "admin" ? (
                      <button onClick={() => shkeputLlogarine(p.id)} title={t("members.unlinkAccountTitle")}
                        className="p-1 rounded-md shrink-0" style={{ background: COLORS.dangerSoft }}>
                        <AlertTriangle size={14} color={COLORS.danger} />
                      </button>
                    ) : null
                  ) : (
                  <button onClick={() => kopjoKodinPersonal(p.id)}
                    title={kodetPersonale[p.id] ? t("members.personCodeAgainTitle") : t("members.personCodeTitle")}
                    className="p-1 rounded-md shrink-0" style={{ background: kodetPersonale[p.id] ? COLORS.gold : COLORS.panelSoft }}>
                    <KeyRound size={14} color={kodetPersonale[p.id] ? COLORS.bgDeep : COLORS.ink} />
                  </button>
                  )}
                </div>
                {/* KODI7 — kodi personal aktiv: kush pret ende, deri kur, dhe revokimi.
                    Del vetëm sa ka kod; kodi digjet vetë kur përdoret, prandaj
                    «aktiv» do të thotë «i pashfrytëzuar». */}
                {kodetPersonale[p.id] && (
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
                      {t("members.personCodeActive", { date: new Date(kodetPersonale[p.id].expires_at).toLocaleDateString(i18n.language || "sq") })}
                    </span>
                    <button onClick={() => revokoKodinPersonal(p.id)} className="px-2 py-0.5 rounded-md text-xs shrink-0"
                      style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, background: COLORS.dangerSoft, color: COLORS.danger, border: "none", cursor: "pointer" }}>
                      {t("members.personCodeRevoke")}
                    </button>
                  </div>
                )}
                {/* LLOGARIA1 — llogaria e lidhur e këtij personi dhe shkëputja e saj.
                    Del vetëm kur ka lidhje. Vetja dhe adminët e tjerë s'kanë buton:
                    serveri i refuzon gjithsesi, po më mirë të mos premtohet. */}
                {llogaritEAnetareve[p.id] && (
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
                      {t("members.linkedAccount", { email: llogaritEAnetareve[p.id].email_masked || "—", date: fmtDate(llogaritEAnetareve[p.id].linked_at) })}
                      {llogaritEAnetareve[p.id].last_seen_at ? ` · ${t("members.linkedLastSeen", { date: fmtRelative(llogaritEAnetareve[p.id].last_seen_at) })}` : ""}
                    </span>
                  </div>
                )}
                <div className="mt-1.5">
                  {p.blocked ? (
                    <div className="flex items-center justify-between gap-2">
                      <span style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight }}>{t("members.blockReason", { reason: p.blocked_reason || "—" })}</span>
                      <button onClick={() => setBlocked(p.id, false, null)} className="px-2 py-1 rounded-md text-xs shrink-0" style={{ background: COLORS.gold, color: COLORS.bgDeep }}>{t("members.unblock")}</button>
                    </div>
                  ) : roles[p.id] === "admin" ? (
                    <p style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight }}>{t("members.removeAdminRoleFirst")}</p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input placeholder={t("members.blockReasonPlaceholder")} value={blockReasonDrafts[p.id] || ""} onChange={(e) => setBlockReasonDrafts({ ...blockReasonDrafts, [p.id]: e.target.value })} className="flex-1 rounded-md px-2 py-1 text-xs outline-none" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.field, color: COLORS.ink }} />
                      <button onClick={async () => { const r = (blockReasonDrafts[p.id] || "").trim(); if (!r) { showToast(t("members.reasonRequired")); return; } await setBlocked(p.id, true, r); setBlockReasonDrafts({ ...blockReasonDrafts, [p.id]: "" }); }} className="px-2 py-1 rounded-md text-xs shrink-0" style={{ background: COLORS.dangerSoft, color: COLORS.danger }}>{t("members.block")}</button>
                    </div>
                  )}
                </div>
              </div>
            ))
            )}
          </div>
        </Modal>
        );
      })()}

      {trashOpen && canManage && (
        <Modal title={t("Trash")} onClose={() => setTrashOpen(false)} wide>
          {/* HISTORIA2 — KOSHI KA DY SEKSIONE.
              Deri sot titulli thoshte «personat e fshirë», dhe ai ishte i saktë:
              vetëm personat vinin këtu. Ngjarjet e historisë fshiheshin vërtet.
              Tani të dyja bien te i njëjti vend, sepse për atë që i ka fshirë
              janë e njëjta pyetje: «ku shkoi ajo që hoqa pa dashur?» */}
          <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>
            {t("People")}
          </p>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>
            {t("Deleted people are kept here and you can restore them. After 30 days (if they are not restored) they are deleted automatically — the date is shown on each one.")}
          </p>
          {deletedPeople.length === 0 ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Trash is empty — no deleted person.")}</p>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {deletedPeople.map((p) => {
                const nm = `${p.first_name || ""} ${p.last_name || ""}`.trim() || t("(no name)");
                let when = "";
                try { when = p.deleted_at ? fmtDate(p.deleted_at) : ""; } catch { when = ""; }
                let purge = "";
                try { purge = p.purge_after ? fmtDate(p.purge_after) : ""; } catch { purge = ""; }
                return (
                  <div key={p.id} className="flex items-center gap-2 px-2 py-2 rounded-md" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.field }}>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.ink }}>{nm}</p>
                      <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
                        {t("Deleted")}{when ? " " + t("on {{date}}", { date: when }) : ""}{p.deleted_by_name ? " " + t("by {{name}}", { name: p.deleted_by_name }) : ""}
                      </p>
                      {purge && <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.danger }}>{t("Deleted automatically on {{date}} unless restored", { date: purge })}</p>}
                    </div>
                    <button onClick={() => restorePerson(p.id)} className="px-3 py-1.5 rounded-md text-sm shrink-0 flex items-center gap-1.5" style={{ background: COLORS.gold, color: "#fff" }}>
                      <Undo2 size={14} /> {t("Restore")}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* NGJARJET E HISTORISË.
              Data e fshirjes përfundimtare NUK shkruhet këtu, edhe pse ruhet te
              baza: sot asnjë punë e planifikuar nuk i fshin këto rreshta, dhe
              një datë në ndërfaqe që s'ndodh kurrë është më keq se asnjë datë. */}
          <p className="mt-5 mb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>
            {t("History")}
          </p>
          {deletedHistory.length === 0 ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("No deleted event.")}</p>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {deletedHistory.map((e) => {
                let when = "";
                try { when = e.deleted_at ? fmtDate(e.deleted_at) : ""; } catch { when = ""; }
                return (
                  <div key={e.id} className="flex items-center gap-2 px-2 py-2 rounded-md" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.field }}>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.ink }}>{e.title || "—"}</p>
                      <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>
                        {t("Deleted")}{when ? " " + t("on {{date}}", { date: when }) : ""}{e.deleted_by_name ? " " + t("by {{name}}", { name: e.deleted_by_name }) : ""}
                      </p>
                    </div>
                    <button onClick={() => restoreHistoryEvent(e.id)} className="px-3 py-1.5 rounded-md text-sm shrink-0 flex items-center gap-1.5" style={{ background: COLORS.gold, color: "#fff" }}>
                      <Undo2 size={14} /> {t("Restore")}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}

      {syncModalFor && (
        <Modal title={t("Sync the profile between families")} onClose={() => setSyncModalFor(null)}>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>
            {t("Choose the source family and the destination(s). The selected fields are copied FROM the source and overwrite the destinations.")}
          </p>

          {/* Burimi */}
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.inkLight, marginBottom: 4 }}>{t("From (source)")}</label>
          <select value={syncSource || ""} onChange={(e) => { const v = e.target.value; setSyncSource(v); setSyncTargets((prev) => { const n = new Set(prev); n.delete(v); return n; }); }} className="w-full rounded-md px-3 py-2 mb-3 outline-none" style={inputStyle}>
            {myLinkedFamilies.map((f) => <option key={f.person_id} value={f.person_id}>{f.family_name}</option>)}
          </select>

          {/* Destinacioni */}
          <div className="flex items-center justify-between mb-1">
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.inkLight }}>{t("To (destinations)")}</label>
            <button onClick={() => setSyncTargets(new Set(myLinkedFamilies.filter((f) => f.person_id !== syncSource && f.person_id).map((f) => f.person_id)))} style={{ fontSize: 11, color: COLORS.ink, background: "transparent", border: "none", cursor: "pointer" }}>{t("Select all")}</button>
          </div>
          <div className="space-y-1 mb-3" style={{ maxHeight: 150, overflowY: "auto", overflowX: "hidden" }}>
            {myLinkedFamilies.filter((f) => f.person_id !== syncSource).map((f) => (
              <label key={f.person_id} className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>
                <input type="checkbox" checked={syncTargets.has(f.person_id)} onChange={(e) => setSyncTargets((prev) => { const n = new Set(prev); if (e.target.checked) n.add(f.person_id); else n.delete(f.person_id); return n; })} />
                {f.family_name}
              </label>
            ))}
            {myLinkedFamilies.filter((f) => f.person_id !== syncSource).length === 0 && <p style={{ fontSize: 12, color: COLORS.inkLight }}>{t("No other families.")}</p>}
          </div>

          {/* Fushat */}
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.inkLight, marginBottom: 4 }}>{t("Fields")}</label>
          <div className="space-y-2 mb-4">
            {[["photo", t("Photo")], ["contact", "Kontakti (email/telefon)"], ["birth", t("Birth date / birthplace / gender")], ["bio", "Historia (bio)"], ["legacy", t("Legacy (messages)")]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>
                <input type="checkbox" checked={!!syncFields[k]} onChange={(e) => setSyncFields((prev) => ({ ...prev, [k]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>

          <div className="flex gap-2">
            <button onClick={runSync} disabled={syncBusy} className="flex-1 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.gold, color: "#fff", opacity: syncBusy ? 0.6 : 1 }}>{syncBusy ? t("Syncing…") : t("Sync the selected")}</button>
            <button onClick={() => setSyncModalFor(null)} className="px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}>{t("Cancel")}</button>
          </div>
        </Modal>
      )}

      {/* HUB-i "Familjet" — një faqe ku i rregullon të gjitha: lista e familjeve
          (ndërro/★/fshih/dil), të fshehura, dhe veprimet poshtë. */}
      {familiesHubOpen && (
        <Modal title={t("Family Hub")} onClose={() => setFamiliesHubOpen(false)}>
          <p className="px-1 pb-2" style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight }}>{t("auth.myFamilies")}</p>
          <div className="space-y-0.5" style={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}>
            {myLinkedFamilies.filter((f) => !hiddenFamilies.has(f.family_id)).map((f) => (
              <div key={f.person_id}>
                <div className="flex items-center rounded-md" style={{ background: f.family_id === activeFamilyId ? "rgba(63,111,82,0.08)" : "transparent" }}>
                  <a href={`/f/${f.family_slug}`} onClick={(e) => { e.preventDefault(); shkoTeFamilja(f.family_slug); }} className="flex-1 flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-black/5" style={{ textDecoration: "none" }}>
                    <Link2 size={14} color={COLORS.ink} />
                    <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink, fontWeight: f.family_id === activeFamilyId ? 700 : 400 }}>
                      {f.family_name} {f.family_id === activeFamilyId ? t("auth.currentFamilyTag") : ""}
                    </span>
                  </a>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavoriteFamily(f.family_id); }}
                    title={favoriteFamilyId === f.family_id ? t("Default family (opens on login)") : t("Set as default family")}
                    className="px-2 py-2 rounded-md hover:bg-black/5 shrink-0"
                    style={{ color: favoriteFamilyId === f.family_id ? COLORS.gold : COLORS.inkLight, fontSize: 15, lineHeight: 1 }}
                    aria-pressed={favoriteFamilyId === f.family_id}
                  >
                    {favoriteFamilyId === f.family_id ? "★" : "☆"}
                  </button>
                  {f.family_id !== activeFamilyId && currentPersonId && (
                    <button
                      onClick={() => setFamActionsOpenFor((cur) => (cur === f.person_id ? null : f.person_id))}
                      title={t("Actions (sync / bring the family)")}
                      className="px-2 py-2 rounded-md hover:bg-black/5 shrink-0"
                      aria-expanded={famActionsOpenFor === f.person_id}
                    >
                      <ChevronDown size={15} color={COLORS.gold} style={{ transform: famActionsOpenFor === f.person_id ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                    </button>
                  )}
                </div>
                {f.family_id !== activeFamilyId && currentPersonId && famActionsOpenFor === f.person_id && (
                  <div className="flex flex-wrap gap-1 px-2 pb-1">
                    <button onClick={() => { setFamActionsOpenFor(null); toggleHiddenFamily(f.family_id); }} title={t("Hide from my list — reversible; only from your view")} className="px-2 py-1 rounded-md hover:bg-black/5" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>
                      {t("🙈 Hide from my list")}
                    </button>
                    <button onClick={() => leaveFamily(f)} title={t("Leave the family — removes only you")} className="px-2 py-1 rounded-md hover:bg-black/5" style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: COLORS.danger }}>
                      {t("🚪 Leave the family")}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {/* Familjet e fshehura (localStorage) — të kthyeshme me "Shfaq". */}
            {(() => {
              const hidden = myLinkedFamilies.filter((f) => hiddenFamilies.has(f.family_id));
              if (hidden.length === 0) return null;
              return (
                <div>
                  <button onClick={() => setShowHiddenFamilies((v) => !v)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-black/5" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>
                    <ChevronDown size={13} color={COLORS.inkLight} style={{ transform: showHiddenFamilies ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                    {t("Hidden ({{count}})", { count: hidden.length })}
                  </button>
                  {showHiddenFamilies && hidden.map((f) => (
                    <div key={f.person_id} className="flex items-center px-2 py-1.5" style={{ opacity: 0.7 }}>
                      <span className="flex-1" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.ink }}>{f.family_name}</span>
                      <button onClick={() => toggleHiddenFamily(f.family_id)} className="px-2 py-1 rounded-md hover:bg-black/5" style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: COLORS.ink }}>
                        {t("👁 Show")}
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Veprimet — sinkronizo / sill familjen / bashkohu / krijo */}
          <div className="grid grid-cols-2 gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            {currentPersonId && myLinkedFamilies.filter((f) => f.person_id).length >= 2 && (
              <button onClick={() => openSyncModal()} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-left hover:bg-black/5" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft }}>
                <NovaIcon role="sync" Icon={RefreshCw} size={16} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Sync the profile")}</span>
              </button>
            )}
            {["admin", "editor", "contributor"].includes(myRole) && currentPersonId && myLinkedFamilies.filter((f) => f.person_id).length >= 2 && (
              <button onClick={() => openImportModal()} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-left hover:bg-black/5" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft }}>
                <NovaIcon role="bring" Icon={Users} size={16} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Bring your family")}</span>
              </button>
            )}
            <button onClick={() => leaveToPage("/join", "familiesHub")} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-left hover:bg-black/5" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft }}>
              <NovaIcon role="join" Icon={KeyRound} size={16} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Join another family (Code)")}</span>
            </button>
            <button onClick={() => leaveToPage("/start", "familiesHub")} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-left hover:bg-black/5" style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft }}>
              <NovaIcon role="create" Icon={PlusCircle} size={16} /><span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>{t("Create a new family")}</span>
            </button>
          </div>
        </Modal>
      )}

      {/* "Sill familjen tënde" — model dropdown si sinkronizimi: zgjidh burimin
          (familja që ka bashkëshortin+fëmijët) → destinacione VETËM ato ku ende
          s'ekziston (preview>0), për të mos krijuar dyfishime. */}
      {importOpen && (
        <Modal title={t("Bring your family")} onClose={() => setImportOpen(false)}>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>
            {t("Choose the family that has your spouse and children, then the destinations. Only families where they don't exist yet are shown — no duplicates.")}
          </p>

          {/* Burimi */}
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.inkLight, marginBottom: 4 }}>{t("From (source)")}</label>
          <select value={importSource || ""} onChange={(e) => { setImportSource(e.target.value); setImportTargets(new Set()); }} className="w-full rounded-md px-3 py-2 mb-3 outline-none" style={inputStyle}>
            {myLinkedFamilies.filter((f) => f.person_id).map((f) => <option key={f.person_id} value={f.person_id}>{f.family_name}</option>)}
          </select>

          {/* Destinacioni */}
          <div className="flex items-center justify-between mb-1">
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.inkLight }}>{t("To (destinations)")}</label>
            {(() => {
              const eligible = myLinkedFamilies.filter((f) => f.person_id && f.person_id !== importSource && (importPreview[f.person_id] || 0) > 0);
              if (eligible.length === 0) return null;
              return <button onClick={() => setImportTargets(new Set(eligible.map((f) => f.person_id)))} style={{ fontSize: 11, color: COLORS.ink, background: "transparent", border: "none", cursor: "pointer" }}>{t("Select all")}</button>;
            })()}
          </div>
          <div className="space-y-1 mb-4" style={{ maxHeight: 180, overflowY: "auto", overflowX: "hidden" }}>
            {importPreviewLoading && <p style={{ fontSize: 12.5, color: COLORS.inkLight }}>{t("Checking…")}</p>}
            {!importPreviewLoading && (() => {
              const eligible = myLinkedFamilies.filter((f) => f.person_id && f.person_id !== importSource && (importPreview[f.person_id] || 0) > 0);
              if (eligible.length === 0) return <p style={{ fontSize: 12.5, color: COLORS.inkLight }}>{t("No families to bring into — your family already exists everywhere else.")}</p>;
              return eligible.map((f) => (
                <label key={f.person_id} className="flex items-center gap-2 cursor-pointer px-1 py-1" style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>
                  <input type="checkbox" checked={importTargets.has(f.person_id)} onChange={(e) => setImportTargets((prev) => { const n = new Set(prev); if (e.target.checked) n.add(f.person_id); else n.delete(f.person_id); return n; })} />
                  <span className="flex-1">{f.family_name}</span>
                  <span style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.ink, background: COLORS.panelSoft, fontVariantNumeric: "tabular-nums", borderRadius: 999, padding: "1px 7px" }}>+{importPreview[f.person_id]}</span>
                </label>
              ));
            })()}
          </div>

          <div className="flex gap-2">
            <button onClick={runImport} disabled={importBusy || importPreviewLoading} className="flex-1 px-3 py-2 rounded-md text-sm" style={{ background: COLORS.gold, color: "#fff", opacity: (importBusy || importPreviewLoading) ? 0.6 : 1 }}>{importBusy ? t("Bringing…") : t("Bring the family")}</button>
            <button onClick={() => setImportOpen(false)} className="px-3 py-2 rounded-md text-sm" style={{ background: COLORS.panelSoft, color: COLORS.ink }}>{t("Cancel")}</button>
          </div>
        </Modal>
      )}

      {/* Dritarja "⚙️ Cilësimet e pamjes së pemës" — dritare e vet (Modal,
          full-screen në telefon) me TË GJITHA cilësimet e avancuara. Zgjidh
          edhe defektin "Settings hapet në background": më parë ky material
          hapej si popover BRENDA kolonës me overflow të drawer-it dhe pritej
          nga CSS — Modal-i del gjithmonë sipër gjithçkaje. */}
      {treeSettingsOpen && (
        <Modal title={`⚙️ ${t("Tree view settings")}`} onClose={() => setTreeSettingsOpen(false)}>
          {renderTreeSettingsContent()}
        </Modal>
      )}

      {/* «Kërko» dhe «Shiko» — tani këtu, bashkë me motrat e tyre.
          Ky është i njëjti vend nga ku hapen cilësimet e pemës, menyja e
          avatarit dhe qendra e familjeve — pra pikërisht ato dritare që
          pronari i quajti «në rregull». */}
      {renderTreeBarPanels()}
      {viewPanelOpen && (
        <Modal title={t("View")} onClose={() => setViewPanelOpen(false)}>
          {renderShikoPanelContent(() => setViewPanelOpen(false))}
        </Modal>
      )}

      {/* Faqja e gjuhëve — e njëjta formë si «Shiko»: një grup, rreshta sa
          gishti, shenjë te ajo që është zgjedhur. «Sipas telefonit» rri veç e
          para, sepse nuk është gjuhë; është një rregull. */}
      {/* ── KËRKIMI ─────────────────────────────────────────────────────────
          Nis te faqja ku je (parazgjedhje), me «Kudo» një prekje larg. Kur
          faqja ku je nuk ka çfarë të kërkojë (Shërbimet, Statistika), nis vetë
          te «Kudo» — një kërkim që hapet i kyçur mbi një fushë boshe do të
          dukej i prishur. */}
      {kerkimiOpen && (() => {
        const fushaEFaqes = activeScreen === "tree" ? "persona"
          : activeScreen === "memories" ? "historia"
          : activeScreen === "home" ? "historia" : null;
        const kudo = kerkimiKudo || !fushaEFaqes;
        const emriIFaqes = activeScreen === "tree" ? t("The Tree")
          : activeScreen === "memories" ? t("History")
          : activeScreen === "home" ? t("Family") : t("This page");
        const q = kerkimiTeksti.trim().toLowerCase();
        const perputh = (x) => !!x && String(x).toLowerCase().includes(q);
        const meNjerez = kudo || fushaEFaqes === "persona" || fushaEFaqes === "historia";
        const meRreshta = kudo || fushaEFaqes === "historia";
        const njerezit = (q && meNjerez)
          ? Object.values(people).filter((pp) => perputh(displayName(pp)) || perputh(pp.birth_place)).slice(0, 20) : [];
        const rreshtat = (q && meRreshta)
          ? historiaRrjedha.filter((r) => perputh(r.ev?.title) || perputh(r.ev?.description)
              || perputh(r.it?.caption) || perputh(r.it?.file_path) || perputh(r.ev?.event_date)).slice(0, 30) : [];
        const asgje = q && njerezit.length === 0 && rreshtat.length === 0;
        return (
          <Modal title={t("Search")} onClose={() => setKerkimiOpen(false)}>
            <input
              autoFocus
              value={kerkimiTeksti}
              onChange={(e) => setKerkimiTeksti(e.target.value)}
              placeholder={t("A name, a story, a year…")}
              className="w-full rounded-md px-3 py-2.5 outline-none text-sm mb-2"
              style={inputStyle}
            />
            <div className="flex flex-wrap gap-1.5 mb-3">
              {[[false, emriIFaqes], [true, t("Everywhere")]].map(([vlera, etiketa]) => {
                const on = kudo === vlera;
                const cAktiv = !fushaEFaqes && vlera === false;
                return (
                  <button key={String(vlera)} disabled={cAktiv} onClick={() => setKerkimiKudo(vlera)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium"
                    style={{ background: on ? COLORS.gold : "transparent", color: on ? COLORS.bgDeep : COLORS.ink,
                             border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`,
                             opacity: cAktiv ? 0.4 : 1, cursor: cAktiv ? "default" : "pointer" }}>
                    {etiketa}
                  </button>
                );
              })}
            </div>

            {!q && (
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, lineHeight: 1.5 }}>
                {kudo
                  ? t("Searches people, stories and files across the whole family.")
                  : t("Searches only what is on this page. Switch to «Everywhere» to look across the family.")}
              </p>
            )}
            {asgje && (
              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Nothing found.")}</p>
            )}

            {njerezit.length > 0 && (
              <div className="mb-3">
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 0 6px" }}>{t("People")}</p>
                {njerezit.map((pp) => (
                  <button key={pp.id}
                    onClick={() => {
                      setKerkimiOpen(false); setKerkimiTeksti("");
                      // Te Historia, zgjedhja e një personi NUK të nxjerr nga
                      // faqja — ndez filtrin që ekziston. Kjo ishte vetë kërkesa:
                      // «kërkon histori por në të njëjtën kohë kërkon edhe person».
                      if (activeScreen === "memories" && !kudo) { setHistoriaPersoni(pp.id); setHistoriaFiltri("person"); }
                      else focusOn(pp.id);
                    }}
                    className="w-full text-left px-3 py-2 rounded-md mb-1"
                    style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`, cursor: "pointer" }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>{displayName(pp)}</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, marginLeft: 6 }}>{lifespan(pp)}</span>
                  </button>
                ))}
              </div>
            )}

            {rreshtat.length > 0 && (
              <div>
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 0 6px" }}>{t("History")}</p>
                {rreshtat.map((r) => {
                  const titulli = r.ev ? r.ev.title : (r.it.caption || r.it.file_path.split("/").pop());
                  const koka = r.tipi === "ngjarje" ? "📖" : r.tipi === "zera" ? "🎙️" : r.tipi === "dokumente" ? "📄" : "📷";
                  return (
                    <button key={r.id}
                      onClick={() => {
                        setKerkimiOpen(false); setKerkimiTeksti("");
                        setHistoriaPersoni(null); setHistoriaFiltri(r.tipi === "ngjarje" ? "ngjarje" : r.tipi);
                        openMemories("events");
                      }}
                      className="w-full text-left px-3 py-2 rounded-md mb-1"
                      style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.goldSoft}`, cursor: "pointer" }}>
                      <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>{koka} {titulli}</span>
                      {r.viti && <span style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: COLORS.ink, marginLeft: 6 }}>{r.viti}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </Modal>
        );
      })()}

      {/* ── FAQJA «SHTO HISTORI» ────────────────────────────────────────────
          Ngjarje: datë, titull, rrëfim, foto. Shkon te `family_history_events`.
          E njëjta faqe hapet edhe nga ✏️ te Historia — një formular për një
          ngjarje, jo dy. */}
      {shtoHistoriOpen && canManage && (
        <Modal
          title={familyHistoryForm.id ? t("history.editEvent") : t("history.newEvent")}
          onClose={() => { setShtoHistoriOpen(false); setFamilyHistoryForm({ id: null, eventDate: sotSiTekst(), title: "", description: "", saktesia: "dite" }); setFamilyHistoryPhotoFiles([]); setFamilyHistoryPhotoPreviews([]); }}
        >
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, lineHeight: 1.5 }}>
            {t("An event with a date, a title and photos — it appears in History.")}
          </p>
          {/* SA E DI VETEN DATA. Zgjedhja e ngushton edhe vetë fushën: kush
              zgjedh «Vit» sheh «1969», jo një kalendar që i kërkon një ditë që
              s'e di. Parazgjedhja është sot me saktësi dite — pra një postim i
              shpejtë nuk ka çfarë të plotësojë këtu. */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {SAKTESITE_E_DATES.map((sak) => {
              const on = (familyHistoryForm.saktesia || "dite") === sak;
              const etiketa = sak === "dite" ? t("date.day") : sak === "muaj" ? t("date.month") : sak === "vit" ? t("date.year") : t("date.decade");
              return (
                <button key={sak} onClick={() => setFamilyHistoryForm({ ...familyHistoryForm, saktesia: sak })}
                  className="px-2.5 py-1 rounded-full text-xs"
                  style={{ background: on ? COLORS.gold : "transparent", color: on ? COLORS.bgDeep : COLORS.ink, border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`, cursor: "pointer" }}>
                  {etiketa}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-1">
            <input
              autoFocus
              placeholder={(familyHistoryForm.saktesia || "dite") === "dite" ? "2026-08-29"
                : familyHistoryForm.saktesia === "muaj" ? "1969-06"
                : familyHistoryForm.saktesia === "vit" ? "1969" : "1960"}
              value={familyHistoryForm.eventDate}
              onChange={(e) => setFamilyHistoryForm({ ...familyHistoryForm, eventDate: e.target.value })}
              className="rounded-md px-3 py-2 outline-none text-sm" style={inputStyle} />
            <input placeholder={t("history.titlePlaceholder")} value={familyHistoryForm.title} onChange={(e) => setFamilyHistoryForm({ ...familyHistoryForm, title: e.target.value })} className="rounded-md px-3 py-2 outline-none text-sm" style={inputStyle} />
          </div>
          <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight, margin: "0 0 8px" }}>
            {familyHistoryForm.eventDate
              ? dataELexueshme(familyHistoryForm.eventDate, familyHistoryForm.saktesia || "dite", i18n.language, t)
              : t("Write when it happened — the date is what gives it a place in History.")}
          </p>
          <textarea placeholder={t("history.descriptionPlaceholder")} value={familyHistoryForm.description} onChange={(e) => setFamilyHistoryForm({ ...familyHistoryForm, description: e.target.value })} className="w-full rounded-md px-3 py-2 mb-2 outline-none text-sm" style={{ ...inputStyle, minHeight: 110, resize: "vertical" }} />
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {familyHistoryPhotoPreviews.map((pp, i) => (
              <div key={i} className="relative">
                <img src={pp.url} alt="" style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, background: COLORS.panelSoft }} />
                <button onClick={() => removeFamilyHistoryPreview(i)} className="absolute -top-1.5 -right-1.5 rounded-full flex items-center justify-center" style={{ width: 17, height: 17, background: COLORS.ink, color: "#fff", fontSize: 10, lineHeight: 1 }}>✕</button>
              </div>
            ))}
            <input type="file" accept="image/*" multiple onChange={(e) => { if (e.target.files?.length) onFamilyHistoryPhotoChange(e.target.files); e.target.value = ""; }} className="text-xs" style={{ fontFamily: FONTS.body, color: COLORS.inkLight }} />
          </div>
          <button onClick={saveFamilyHistoryEvent} disabled={familyHistorySaving} className="w-full py-2.5 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, border: "none", cursor: "pointer", opacity: familyHistorySaving ? 0.6 : 1 }}>
            {familyHistorySaving ? t("history.saving") : familyHistoryForm.id ? t("history.saveChanges") : t("history.addEvent")}
          </button>
        </Modal>
      )}

      {/* ── FAQJA «SHTO FOTO, ZË OSE DOKUMENT» ──────────────────────────────
          Skedar: lloj, madhësi, persona. Shkon te `archive_items`. Ndryshe nga
          ngjarja, këtu data vjen vetë nga ngarkimi dhe rrëfimi është një rresht
          — prandaj është faqe tjetër, jo e njëjta me fusha të fshehura. */}
      {shtoSkedarOpen && archiveEnabled && ["admin", "editor", "contributor"].includes(myRole) && (
        <Modal title={t("Add a photo, a voice or a document")} onClose={() => setShtoSkedarOpen(false)}>
          <p className="mb-3" style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, lineHeight: 1.5 }}>
            {t("It goes into the family history, with the date and the people in it.")}
          </p>
          <input autoFocus placeholder={t("Description (optional, applies to all files you upload now)")} value={archiveCaptionDraft} onChange={(e) => setArchiveCaptionDraft(e.target.value)} className="w-full rounded-md px-3 py-2 mb-3 outline-none text-sm" style={inputStyle} />
          <input
            type="file"
            accept="image/*,video/*,audio/*,application/pdf,.doc,.docx"
            multiple
            disabled={archiveUploading}
            onChange={(e) => { if (e.target.files?.length) uploadArchiveFiles(e.target.files); e.target.value = ""; }}
            className="text-xs w-full"
            style={{ fontFamily: FONTS.body, color: COLORS.inkLight }}
          />
          {archiveUploading && <p className="mt-2" style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: COLORS.ink }}>{t("Loading…")}</p>}

          {renderMatesinEArkives("mbi")}

          <button onClick={() => { setShtoSkedarOpen(false); setHistoriaFiltri("gjitha"); openMemories("events"); }} className="w-full mt-4 py-2.5 rounded-md text-sm font-medium" style={{ background: "transparent", border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, cursor: "pointer" }}>
            {t("See them in History")} →
          </button>
        </Modal>
      )}

      {gjuhaPanelOpen && (
        <Modal title={t("Language")} onClose={() => setGjuhaPanelOpen(false)}>
          {(() => {
            const opsionet = getSwitcherOptions();
            const sistemi = opsionet.find((o) => o.value === "system");
            const gjithe = opsionet.filter((o) => o.value !== "system");
            // Ndarja: të preferuarat e adminit sipër, të tjerat pas një kliku.
            // Gjuha e zgjedhur tani rri GJITHMONË sipër, edhe kur s'është e
            // preferuar — përndryshe dikush që ka zgjedhur norvegjishten hap
            // dritaren dhe nuk sheh asnjë shenjë se ku ndodhet.
            const tePreferuarat = gjithe.filter((o) => o.iPreferuar !== false || o.value === langChoice);
            const teTjerat = gjithe.filter((o) => o.iPreferuar === false && o.value !== langChoice);
            const gjuhet = tePreferuarat;
            // Zgjedhja e mbyll edhe menynë: puna mbaroi, s'ke pse kthehesh atje.
            // Kthimi mbrapa është tjetër gjë — ai të lë pikërisht ku ishe.
            const zgjidh = (v) => {
              setLangChoice(v); setLanguage(v);
              setGjuhaPanelOpen(false); setAvatarMenuOpen(false);
            };
            const rresht = (v, etiketa, i, gjith) => (
              <button key={v} onClick={() => zgjidh(v)}
                className="w-full flex items-center gap-3 text-left"
                style={{
                  padding: "12px 14px", fontFamily: FONTS.body,
                  background: langChoice === v ? COLORS.accentTint : "transparent",
                  borderTop: i ? `1px solid ${COLORS.goldSoft}` : "none",
                }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: langChoice === v ? 700 : 500, color: COLORS.ink }}>{etiketa}</span>
                {langChoice === v && <span style={{ color: COLORS.ink, fontWeight: 700, fontSize: 16 }}>✓</span>}
              </button>
            );
            return (
              <>
                {/* Emri i gjuhës së zbuluar shkruhet PRANË «Sistemit». Pa të,
                    rreshti është një premtim i heshtur: kur zbulimi punon nuk e
                    merr vesh, dhe kur nuk punon — as atëherë. Me të, «Sistemi ·
                    Norsk» e thotë vetë se telefoni u lexua saktë; dhe kur emri
                    mungon, mungesa është përgjigjja. */}
                {sistemi && (
                  <div style={{ ...grupiIPamjes, marginBottom: 18 }}>
                    {rresht("system",
                      sistemi.sistemiLabel
                        ? `${t("System (phone language)")} · ${sistemi.sistemiLabel}`
                        : t("System (phone language)"),
                      0, 1)}
                  </div>
                )}
                <span style={etiketaESeksionit}>{t("Language")}</span>
                <div style={grupiIPamjes}>
                  {gjuhet.map((o, i) => rresht(o.value, o.label, i, gjuhet.length))}
                </div>

                {/* Të tjerat. Nuk janë «më pak të vlefshme» — thjesht nuk i ka
                    promovuar askush. Numri shkruhet te butoni, që njeriu ta dijë
                    se çfarë e pret para se ta hapë. */}
                {teTjerat.length > 0 && (
                  <>
                    <button onClick={() => setGjuhetETjeraHapur((v) => !v)}
                      className="w-full text-left"
                      style={{
                        padding: "12px 14px", marginTop: 10, fontFamily: FONTS.body,
                        fontSize: 14, fontWeight: 600, color: COLORS.ink,
                        background: "transparent", border: `1px solid ${COLORS.goldSoft}`,
                        borderRadius: 10, cursor: "pointer",
                      }}>
                      {gjuhetETjeraHapur
                        ? t("Hide the other languages")
                        : `${t("More languages")} (${teTjerat.length})`}
                    </button>
                    {gjuhetETjeraHapur && (
                      <div style={{ ...grupiIPamjes, marginTop: 10 }}>
                        {teTjerat.map((o, i) => rresht(o.value, o.label, i, teTjerat.length))}
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </Modal>
      )}

      {/* Dritarja "Poster për printim" — e ndarë nga "Pamja", full-screen në
          telefon (Modal). Përmban dizajnin, opsionet, formatin dhe shkarkimin. */}
      {posterModalOpen && featureShown("poster_pro", posterExportAllowed) && (
        <Modal title={`🖼️ ${t("Poster for printing")}`} onClose={closePosterModal}>
          {/* Dizajnet e posterit — koleksion i shitshëm më vete. I kyçur, lista
              shihet e tëra (me bravë): përdoruesi e di ç'po blen para se ta blejë.
              Zgjedhja e njërit e çon te vitrina, jo te një mesazh refuzimi.
              I FSHEHUR nga admini, bie i tëri — titulli bashkë me listën, që të
              mos mbetet një kokë seksioni pa asgjë poshtë. */}
          {featureShown("poster_designs", true) && (<>
          <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("Design")}</p>
          <select value={posterDesignId} onChange={(e) => {
              const v = e.target.value;
              // I NJËJTI GABIM SI TE POSTERI, dy rreshta më lart — dhe do të
              // shfaqej sapo `poster_designs` të kalonte te "Me pagesë": zgjedhja
              // e një dizajni të kyçur e nxirrte njeriun nga dritarja dhe ia
              // humbte pemën që sapo rregulloi. Tani mbetet aty; dizajni thjesht
              // nuk zgjidhet, dhe i thuhet pse.
              if (v && featureGated("poster_designs")) {
                showToast(t("This design collection is bought separately — order it under Services."));
                return;
              }
              setPosterDesignId(v);
              if (v) { try { supabase.rpc("rpc_poster_design_selected", { p_id: v }); } catch { /* metrike jo-kritike */ } }
            }} className="w-full rounded-lg text-sm px-3 py-2.5 mb-1" style={{ background: COLORS.panel, color: COLORS.ink, border: `1px solid ${COLORS.goldSoft}`, fontFamily: FONTS.body }}>
            <option value="">{t("No design (simple)")}</option>
            {posterDesigns.map((d) => (<option key={d.id} value={d.id}>{featureGated("poster_designs") ? "🔒 " : ""}{tDyn(d.name)}{d.kind === "frame" ? " " + t("(frame)") : ""}</option>))}
          </select>
          {featureGated("poster_designs") ? (
            <button onClick={() => { closePosterModal(); setActiveScreen("services"); }}
              className="w-full mb-3 px-3 py-2 rounded-lg text-xs"
              style={{ background: "transparent", border: `1px solid ${COLORS.gold}`, color: COLORS.ink, fontFamily: FONTS.body, fontWeight: 600 }}>
              {t("Unlock the design collection")}
            </button>
          ) : <div className="mb-3" />}
          </>)}
          {renderPosterOptions()}
          <div className="mt-1 pt-3" style={{ borderTop: `1px solid ${COLORS.goldSoft}` }}>
            <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, margin: "0 2px 6px" }}>{t("Format")}</p>
            {/* POROSIA QË PRET VEPRËN. Kur është e zgjedhur, formati NUK zgjidhet
                më — ai u pagua. Zgjedhësi rri i dukshëm por i fikur, që të shihet
                se cili format është, jo të hamendësohet. */}
            {artworkTasks.length > 0 && (
              <div className="mb-3 p-3 rounded-xl" style={{ background: COLORS.panelSoft, border: `1px solid ${COLORS.gold}` }}>
                <p style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 700, color: COLORS.ink, marginBottom: 6 }}>
                  {t("Ordered and waiting for the poster")}
                </p>
                {artworkTasks.map((k) => {
                  const picked = artworkTask?.item_id === k.item_id;
                  return (
                    <button key={k.item_id}
                      onClick={() => {
                        if (picked) { setArtworkTask(null); return; }
                        setArtworkTask(k);
                        if (k.variant) setPosterSize(k.variant);
                      }}
                      className="w-full text-left px-3 py-2 rounded-lg mb-1"
                      style={{ background: picked ? COLORS.gold : "transparent",
                               color: picked ? COLORS.bgDeep : COLORS.ink,
                               border: `1px solid ${picked ? COLORS.gold : COLORS.goldSoft}`,
                               fontFamily: FONTS.body, fontSize: 12.5, cursor: "pointer" }}>
                      {picked ? "✓ " : ""}{k.order_no} — {tDyn(k.service_name)}{k.variant ? ` · ${k.variant}` : ""}
                    </button>
                  );
                })}
                <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, lineHeight: 1.5, marginTop: 4 }}>
                  {artworkTask
                    ? t("The format is the one you paid for. Choose the design, then attach it — it goes straight to print.")
                    : t("Pick an order to attach this poster to it, or leave it and just download.")}
                </p>
              </div>
            )}
            <div className="mb-3" style={{ opacity: artworkTask ? 0.55 : 1, pointerEvents: artworkTask ? "none" : "auto" }}>
              <Segmented value={posterSize} onChange={setPosterSize} options={["A2", "A1", "A0", "2A0", "4A0"].map((sz) => [sz, sz])} />
              {/* SA I MADH DEL EMRI. Jo "sa persona ke" — atë e di vetë. Numri që
                  s'e di dot është milimetri, dhe ai është i vetmi që vendos nëse
                  posteri lexohet apo varet në mur si njollë. */}
              {/* MURALI — vetëm kur veçoria është e ndezur për këtë familje.
                  Rri poshtë formatit sepse është vazhdim i tij: kur asnjë fletë
                  s'mjafton, kjo është rruga tjetër, jo një veçori e palidhur. */}
              {/* Murali fshihet kur posteri s'është i blerë: butoni do të binte
                  te roja e `exportTreeSVG` sidoqoftë, po një buton që gjithmonë
                  refuzon është premtim i thyer me pamje. */}
              {featureShown("poster_mural") && !artworkTask
                && featureAllowed("poster_pro", posterExportAllowed) && (
                <div style={{ marginTop: 9, padding: "9px 11px", borderRadius: 10,
                              border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft }}>
                  <button onClick={() => setMuralOpen((v) => !v)}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                             fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 700, color: COLORS.ink }}>
                    {muralOpen ? "▾" : "▸"} {t("Mural — cut into panels")}
                  </button>
                  {muralOpen && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                        {[["cols", t("columns")], ["rows", t("rows")], ["overlapMm", t("overlap mm")]].map(([k, lab]) => (
                          <label key={k} style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight }}>
                            {lab}
                            <input type="number" min={k === "overlapMm" ? 0 : 1} max={k === "overlapMm" ? 30 : 12}
                              value={mural[k]}
                              onChange={(e) => setMural((m) => ({ ...m, [k]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                              style={{ display: "block", width: 70, marginTop: 3, padding: "5px 7px", borderRadius: 7,
                                       border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panel,
                                       color: COLORS.ink, fontFamily: FONTS.body, fontSize: 12.5 }} />
                          </label>
                        ))}
                      </div>
                      {/* Numri që vendos: sa del NJË panel. Nëse ai s'hyn te ruli
                          i shtypshkronjës, gjithçka tjetër është e kotë. */}
                      {(() => {
                        const [pS, pL] = paperMm(posterSize);
                        const cw2 = Math.max(pS, pL), ch2 = Math.min(pS, pL);
                        const w = cw2 / Math.max(1, mural.cols), h = ch2 / Math.max(1, mural.rows);
                        const rul = Math.min(w, h) <= 1500;
                        return (
                          <div style={{ fontFamily: FONTS.body, fontSize: 11.5, lineHeight: 1.55, marginTop: 7,
                                        color: rul ? COLORS.inkLight : "#A3331A" }}>
                            {mural.cols * mural.rows} {t("panels")} · {Math.round(w)} × {Math.round(h)} mm {t("each")}
                            {" · "}{t("wall")} {(cw2 / 1000).toFixed(2).replace(".", ",")} × {(ch2 / 1000).toFixed(2).replace(".", ",")} m
                            {!rul && ` — ${t("a panel wider than 1.5 m does not fit a print roll")}`}
                          </div>
                        );
                      })()}
                      {/* Pa degë "shiko mostrën" këtu, dhe kjo është e qëllimshme:
                          i gjithë blloku i muralit rri brenda
                          `featureAllowed("poster_pro", …)` te rreshti ~16003. Një
                          `if` i dytë brenda një `if`-i që tashmë e mban të njëjtin
                          kusht s'mbron asgjë — vetëm e bën kodin të duket sikur
                          mbron, dhe atëherë kërkimi i defektit të vërtetë fillon
                          nga vendi i gabuar. (E shkrova një herë ashtu; e hoqa.) */}
                      <button onClick={() => exportTreeSVG(posterSize, posterDesignId, null, false, mural)}
                        style={{ marginTop: 9, padding: "7px 14px", borderRadius: 8, cursor: "pointer",
                                 border: "none", background: COLORS.gold, color: COLORS.bgDeep,
                                 fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 700 }}>
                        ⬇ {t("Download the panels")}
                      </button>
                      <div style={{ fontFamily: FONTS.body, fontSize: 10.5, color: COLORS.inkLight, marginTop: 6, lineHeight: 1.5 }}>
                        {t("Each panel carries alignment crosses and its number. The overlap is cut on the wall — without it a one-millimetre error leaves a white line the whole way.")}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {sizeAdvice && !artworkTask && (() => {
                const cur = sizeAdvice.rows.find((r) => r.key === posterSize);
                const mm = (x) => x.mm.toFixed(1).replace(".", ",");
                const tone = !cur ? COLORS.inkLight
                  : cur.ok ? COLORS.gold : cur.kufi ? COLORS.ink : "#A3331A";
                return (
                  <div style={{ marginTop: 7, fontFamily: FONTS.body, fontSize: 11.5, lineHeight: 1.55 }}>
                    <span style={{ color: tone, fontWeight: 700 }}>
                      {posterSize}: {t("names come out")} {cur ? mm(cur) : "—"} mm
                      {cur && (cur.ok ? ` — ${t("reads comfortably")}`
                        : cur.kufi ? ` — ${t("borderline")}`
                        : ` — ${t("too small")}`)}
                    </span>
                    {!cur?.ok && (
                      <span style={{ color: COLORS.inkLight }}>
                        {" · "}
                        {sizeAdvice.arrihet ? (
                          <>
                            {t("recommended")}:{" "}
                            <button onClick={() => setPosterSize(sizeAdvice.best.key)}
                              style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                                       color: COLORS.ink, fontWeight: 700, fontFamily: FONTS.body, fontSize: 11.5,
                                       textDecoration: "underline" }}>
                              {sizeAdvice.best.key} ({mm(sizeAdvice.best)} mm)
                            </button>
                          </>
                        ) : sizeAdvice.nevoja.brendaRulit ? (
                          <>
                            {t("no A-size is enough — it needs about")}{" "}
                            <b style={{ color: COLORS.ink }}>
                              {sizeAdvice.nevoja.gjate.toFixed(1).replace(".", ",")} ×{" "}
                              {sizeAdvice.nevoja.shkurt.toFixed(2).replace(".", ",")} m
                            </b>
                            {" — "}{t("printed on a roll. Ask us and we will price it.")}
                          </>
                        ) : (
                          // Mos e lër në "s'bëhet". Vegla ekziston tashmë te ky
                          // modal — "Vetëm këta" mban një paraardhës me gjithë
                          // pasardhësit e tij. Një njeri që lexon "shumë e madhe"
                          // e mbyll modalin; një njeri që lexon "zgjidh një degë"
                          // shkon dy rreshta më poshtë dhe e bën.
                          t("too wide even for a roll — pick a branch under “Only these”, or take the family book")
                        )}
                      </span>
                    )}
                    <div style={{ color: COLORS.inkLight, marginTop: 2 }}>
                      {sizeAdvice.persons} {t("people on the sheet")}
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* NGA SHKARKIM NË POROSI (Faza 1e). Posteri kompozohet këtu dhe
                porositet këtu; skedarin e merr admini dhe ia dërgon personit.
                Prandaj butoni i vjetër i shkarkimit s'ekziston më për këdo që
                s'e ka blerë — në vend të tij, dy zgjedhje me çmimet e tyre. */}
            {artworkTask ? (
              <button onClick={() => exportTreeSVG(artworkTask.variant || posterSize, posterDesignId, artworkTask)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm"
                style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body, fontWeight: 700 }}>
                <Download size={16} /> {t("Attach to order {{no}}", { no: artworkTask.order_no })}
              </button>
            ) : (posterShop) ? (
              <div>
                {/* MOSTRA PARA POROSISË. Askush s'duhet të paguajë për diçka që
                    s'e ka parë — dhe askush s'duhet ta marrë atë që s'e ka paguar.
                    Prandaj: e njëjta pemë, i njëjti dizajn, i njëjti format, por
                    e vogël dhe e vulosur. Skedari i vërtetë vektorial me foto
                    full-res nuk i shkon kurrë shfletuesit para pagesës. */}
                <div className="mb-3">
                  {previewUrl ? (
                    <>
                      <button onClick={() => setPreviewZoom(true)}
                        className="w-full rounded-xl overflow-hidden"
                        style={{ border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft,
                                 padding: 0, cursor: "zoom-in" }}>
                        <img src={previewUrl} alt={t("Sample")} draggable={false}
                          onContextMenu={(e) => e.preventDefault()}
                          style={{ width: "100%", display: "block", pointerEvents: "none" }} />
                      </button>
                      <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, marginTop: 5, lineHeight: 1.5 }}>
                        {t("Sample — small and watermarked. The print-quality file comes after payment.")}
                      </p>
                    </>
                  ) : (
                    <button onClick={async () => {
                        setPreviewBusy(true);
                        const u = await exportTreeSVG(
                          orderPick ? ((posterOffers.find((o) => o.service_key === orderPick.service_key)?.prices || [])
                            .find((p) => p.price_id === orderPick.price_id)?.variant || posterSize) : posterSize,
                          posterDesignId, null, true);
                        setPreviewBusy(false);
                        if (u) setPreviewUrl(u); else showToast(t("The sample could not be prepared."));
                      }}
                      disabled={previewBusy}
                      className="w-full py-2.5 rounded-xl text-sm"
                      style={{ background: "transparent", border: `1px solid ${COLORS.goldSoft}`,
                               color: COLORS.ink, fontFamily: FONTS.body, fontWeight: 600,
                               cursor: previewBusy ? "wait" : "pointer", opacity: previewBusy ? 0.6 : 1 }}>
                      {previewBusy ? t("Preparing the sample…") : `👁 ${t("See the sample")}`}
                    </button>
                  )}
                </div>
                {posterOffers.map((o) => {
                  const ps = o.prices || [];
                  if (ps.length === 0) return null;
                  const chosen = orderPick?.service_key === o.service_key;
                  const cur = ps.find((p) => p.price_id === orderPick?.price_id) || ps[0];
                  return (
                    <div key={o.service_key} className="mb-2 p-3 rounded-xl"
                      style={{ border: `1px solid ${chosen ? COLORS.gold : COLORS.goldSoft}`,
                               background: chosen ? COLORS.panelSoft : "transparent" }}>
                      <button onClick={() => setOrderPick(chosen ? null : { service_key: o.service_key, price_id: ps[0].price_id })}
                        className="w-full flex items-start gap-3 text-left"
                        style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>
                            {chosen ? "✓ " : ""}{tDyn(o.name)}
                          </p>
                          {o.description && (
                            <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, lineHeight: 1.5, marginTop: 2 }}>
                              {tDyn(o.description)}
                            </p>
                          )}
                        </div>
                        <span style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, color: COLORS.ink, whiteSpace: "nowrap" }}>
                          {ps.length > 1 && !chosen
                            ? `${eurFmt(Math.min(...ps.map((x) => x.amount_cents)))}+`
                            : eurFmt(cur.amount_cents)}
                        </span>
                      </button>

                      {/* Formatet e këtij shërbimi — çmimi ndryshon me secilin,
                          sepse formati ËSHTË çmimi. */}
                      {chosen && ps.length > 1 && (
                        <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {ps.map((p) => {
                            const on = p.price_id === orderPick?.price_id;
                            return (
                              <button key={p.price_id}
                                onClick={() => { setOrderPick({ service_key: o.service_key, price_id: p.price_id }); if (p.variant && p.variant !== "default") setPosterSize(p.variant); }}
                                style={{ padding: "6px 11px", borderRadius: 9, cursor: "pointer",
                                         border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`,
                                         background: on ? COLORS.gold : "transparent",
                                         color: on ? COLORS.bgDeep : COLORS.ink,
                                         fontFamily: FONTS.body, fontSize: 12, fontWeight: on ? 700 : 400 }}>
                                {p.variant === "default" ? t("Standard") : p.variant}
                                <span style={{ opacity: 0.7, marginLeft: 5 }}>{eurFmt(p.amount_cents)}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Adresa dhe telefoni — vetëm për atë që dërgohet. */}
                      {chosen && (o.fields || []).map((f) => (
                        <div key={f.key} className="mt-2">
                          <label style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, display: "block", marginBottom: 3 }}>
                            {tDyn(f.label || f.key)}{f.required ? " *" : ""}
                          </label>
                          {f.type === "textarea" ? (
                            <textarea rows={2} value={orderFields[f.key] ?? ""} placeholder={f.placeholder || ""}
                              onChange={(e) => setOrderFields((v) => ({ ...v, [f.key]: e.target.value }))}
                              style={{ width: "100%", padding: "7px 9px", borderRadius: 8, border: `1px solid ${COLORS.goldSoft}`,
                                       background: "transparent", color: COLORS.ink, fontFamily: FONTS.body, fontSize: 12.5, resize: "vertical" }} />
                          ) : (
                            <input type={f.type === "number" ? "number" : "text"} value={orderFields[f.key] ?? ""}
                              placeholder={f.placeholder || ""}
                              onChange={(e) => setOrderFields((v) => ({ ...v, [f.key]: e.target.value }))}
                              style={{ width: "100%", padding: "7px 9px", borderRadius: 8, border: `1px solid ${COLORS.goldSoft}`,
                                       background: "transparent", color: COLORS.ink, fontFamily: FONTS.body, fontSize: 12.5 }} />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {orderErr && (
                  <p className="mb-2" style={{ fontFamily: FONTS.body, fontSize: 12, color: "#8A2E2E" }}>{orderErr}</p>
                )}
                <button onClick={placePosterOrder} disabled={!orderPick || orderBusy}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm"
                  style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body, fontWeight: 700,
                           opacity: !orderPick || orderBusy ? 0.5 : 1, border: "none",
                           cursor: !orderPick || orderBusy ? "not-allowed" : "pointer" }}>
                  {orderBusy ? t("Sending…") : t("Order this poster")}
                </button>
                <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, marginTop: 8, lineHeight: 1.5 }}>
                  {t("No payment now — we contact you with the payment details, then we send you the poster exactly as you composed it here.")}
                </p>
              </div>
            ) : featureAllowed("poster_pro", posterExportAllowed) ? (
              /* SHKARKIMI — kthyer më 23 gusht 2026.
                 FAZA1e e zëvendësoi butonin e vjetër me vitrinën, dhe ky ishte
                 vendimi i duhur për një veçori ME PAGESË. Po dega tjetër mbeti
                 vetëm një fjali: "posteri nuk është ende në shitje". Pra kur
                 posteri ishte FALAS — pra i zotëruar nga të gjithë — s'kishte as
                 vitrinë as shkarkim: një dritare që hapej dhe s'bënte asgjë.
                 Rregullimi i parë im preku vetëm kushtin; dega ku binte mbeti po
                 aq bosh. Kushti pa degën s'është rregullim. */
              <>
                <button onClick={() => exportTreeSVG(posterSize, posterDesignId)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm"
                  style={{ background: COLORS.gold, color: COLORS.bgDeep, fontFamily: FONTS.body, fontWeight: 700, border: "none", cursor: "pointer" }}>
                  <Download size={16} /> {t("Download the poster")}
                </button>
                <p style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, marginTop: 8, lineHeight: 1.5 }}>
                  {t("Vector file (SVG) — open it at any print shop, at any size.")}
                </p>
              </>
            ) : (
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, lineHeight: 1.5 }}>
                {t("The poster is not for sale yet.")}
              </p>
            )}
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.inkLight, marginTop: 8, lineHeight: 1.4 }}>{t("Choose the style + format. Vector (SVG) — the same quality at any size; photos are embedded at full resolution. A0/2A0/4A0 = large print (wall).")}</div>
          </div>
        </Modal>
      )}

      {previewZoom && previewUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6"
          style={{ background: "rgba(10,8,6,0.9)" }} onClick={() => setPreviewZoom(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "96vw", maxHeight: "88vh" }}>
            <ZoomableImage src={previewUrl} restricted />
            <p className="text-center mt-3" style={{ fontFamily: FONTS.body, fontSize: 12, color: "#E7CD82" }}>
              {t("Sample — small and watermarked. The print-quality file comes after payment.")}
            </p>
          </div>
        </div>
      )}

      {photoLightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: "rgba(10,8,6,0.85)" }} onClick={() => setPhotoLightbox(null)}>
          <ZoomableImage src={photoUrl(photoLightbox)} restricted={isProfilePhotoRestricted(photoLightbox)} />
          <ButonIkone onClick={() => setPhotoLightbox(null)} className="flex absolute top-2 right-2" rrethi={{ width: 38, height: 38, background: "rgba(255,255,255,0.15)" }}>
            <X size={22} color="#fff" />
          </ButonIkone>
        </div>
      )}

      {/* FEED1 — postimet e hequra nga rrjedha. Asgjë nuk fshihet nga baza:
          `deleted_at` mbushet dhe zbrazet, ndaj kthimi është gjithmonë i mundur.
          E njëjta kolonë përdoret edhe nga heqja pas një raportimi (RAPORTIMI1),
          prandaj kjo listë lexohet me RPC admin-i, jo me lexim të drejtpërdrejtë:
          moderimi s'ka pse t'i shkojë çdo telefoni në familje. */}
      {feedHidden && (
        <Modal title={t("Removed posts")} onClose={() => setFeedHidden(null)}>
          {feedHidden.length === 0 ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Nothing has been removed from the feed.")}</p>
          ) : (
            <ul className="space-y-1.5 max-h-96 overflow-y-auto">
              {feedHidden.map((fp) => {
                const emrat = (fp.member_ids || []).map((id) => people[id]).filter(Boolean).map((p) => displayName(p));
                const kryet = fp.head_person_id && people[fp.head_person_id] ? displayName(people[fp.head_person_id]) : null;
                const lloji = fp.kind === "branch_joined" ? t("A new branch joined the tree")
                            : fp.kind === "marriage" ? t("Marriage")
                            : t("New member");
                return (
                  <li key={fp.id} className="flex items-start justify-between gap-2 px-2.5 py-2 rounded-md" style={{ background: COLORS.panelSoft }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink, margin: 0 }}>{lloji}{kryet ? ` — ${kryet}` : ""}</p>
                      {emrat.length > 0 && (
                        <p style={{ fontFamily: FONTS.body, fontSize: 11.5, color: COLORS.inkLight, marginTop: 2 }}>{emrat.join(" · ")}</p>
                      )}
                      <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{fmtDateTime(fp.deleted_at)}</p>
                    </div>
                    <button onClick={() => kthePostimin(fp.id)} className="px-2 py-1 rounded-md text-xs shrink-0"
                      style={{ background: COLORS.field, border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, cursor: "pointer" }}>
                      {t("Restore")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal>
      )}

      {/* Zmadhimi i skedarit të arkivës. Rri te guaska, jo brenda Historisë:
          PEMA3 na e mësoi që `position: fixed` brenda një enë që rrëshqet nuk
          sillet si mbivendosje e faqes. */}
      {archiveLightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: "rgba(10,8,6,0.85)" }} onClick={() => setArchiveLightbox(null)}>
          <ZoomableImage src={photoUrl(archiveLightbox)} maxW="92vw" maxH="92vh" />
          <ButonIkone onClick={() => setArchiveLightbox(null)} className="flex absolute top-2 right-2" rrethi={{ width: 38, height: 38, background: "rgba(255,255,255,0.15)" }}>
            <X size={22} color="#fff" />
          </ButonIkone>
        </div>
      )}

      {/* KUSH ËSHTË TE KJO FOTO. Pa këtë, `person_ids` mbetet bosh përgjithmonë
          dhe filtri «Një person…» është një premtim që s'e mban kurrë askush. */}
      {etiketaPer && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" style={{ background: "rgba(10,8,6,0.6)" }} onClick={() => setEtiketaPer(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
            <p className="mb-2" style={{ fontFamily: FONTS.display, fontSize: 16, fontWeight: 600, color: COLORS.ink, margin: 0 }}>{t("Who is in it?")}</p>
            <div className="flex-1 overflow-y-auto -mx-1 px-1 mt-2">
              {Object.values(people).sort((a, b) => displayName(a).localeCompare(displayName(b))).map((p) => {
                const on = etiketaZgjedhur.includes(p.id);
                return (
                  <button key={p.id}
                    onClick={() => setEtiketaZgjedhur((v) => on ? v.filter((x) => x !== p.id) : [...v, p.id])}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left mb-1"
                    style={{ background: on ? COLORS.accentTint : "transparent", border: `1px solid ${on ? COLORS.gold : "transparent"}`, cursor: "pointer" }}>
                    <span style={{ fontSize: 13, color: COLORS.ink, width: 14 }}>{on ? "✓" : ""}</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>{emriDallues(p, etiketatDalluese)}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={ruajEtiketat} className="flex-1 py-2 rounded-md text-sm font-medium" style={{ background: COLORS.gold, color: COLORS.bgDeep, border: "none", cursor: "pointer" }}>{t("history.saveChanges")}</button>
              <button onClick={() => setEtiketaPer(null)} className="px-3 py-2 rounded-md text-sm" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, background: "transparent", cursor: "pointer" }}>{t("history.cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Zgjedhësi i personit për filtrin e Historisë. */}
      {historiaPersoniHapur && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" style={{ background: "rgba(10,8,6,0.6)" }} onClick={() => setHistoriaPersoniHapur(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4" style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
            <p style={{ fontFamily: FONTS.display, fontSize: 16, fontWeight: 600, color: COLORS.ink, margin: 0 }}>{t("A person…")}</p>
            <div className="flex-1 overflow-y-auto -mx-1 px-1 mt-2">
              {Object.values(people).sort((a, b) => displayName(a).localeCompare(displayName(b))).map((p) => (
                <button key={p.id}
                  onClick={() => { setHistoriaPersoni(p.id); setHistoriaFiltri("person"); setHistoriaPersoniHapur(false); }}
                  className="w-full text-left px-3 py-2 rounded-md mb-1"
                  style={{ background: historiaPersoni === p.id ? COLORS.accentTint : "transparent", border: "1px solid transparent", cursor: "pointer" }}>
                  <span style={{ fontFamily: FONTS.body, fontSize: 13.5, color: COLORS.ink }}>{emriDallues(p, etiketatDalluese)}</span>
                </button>
              ))}
            </div>
            <button onClick={() => { setHistoriaPersoni(null); setHistoriaFiltri("gjitha"); setHistoriaPersoniHapur(false); }} className="mt-3 py-2 rounded-md text-sm" style={{ border: `1px solid ${COLORS.goldSoft}`, color: COLORS.ink, background: "transparent", cursor: "pointer" }}>
              {t("All")}
            </button>
          </div>
        </div>
      )}

      {/* z-[200] — mbi ÇDO mbivendosje. Me z-50 toast-i zhdukej pas modaleve
          (Legacy z-[95]/[96], konfirmimet z-[70], lightbox-i z-[60]), pra
          mesazhi dilte "pas faqes" dhe përdoruesi s'e shihte kurrë. */}
      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full shadow-xl z-[200]" style={{ background: COLORS.panel, color: COLORS.ink, fontSize: 13 }}>{toast}</div>}
      {/* APPLE-RELAY — një herë për llogari: email-i është i fshehur nga Apple */}
      {njoftimiRelay && activeFamilyId && (
        <div className="fixed left-1/2 -translate-x-1/2 rounded-xl shadow-xl z-[200]" role="status"
             style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)", background: COLORS.panel, color: COLORS.ink, fontSize: 13, fontFamily: FONTS.body, border: `1px solid ${COLORS.goldSoft}`, width: "min(92vw, 420px)", padding: "12px 14px" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("relay.title")}</div>
          <div style={{ color: COLORS.inkLight, lineHeight: 1.45 }}>{t("relay.body")}</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
            <button onClick={mbyllNjoftiminRelay} style={{ background: "transparent", border: `1px solid ${COLORS.goldSoft}`, color: COLORS.inkLight, borderRadius: 8, padding: "6px 12px", fontFamily: FONTS.body, fontSize: 13, cursor: "pointer" }}>{t("relay.later")}</button>
            <button onClick={() => { mbyllNjoftiminRelay(); openSelfEdit(); }} style={{ background: COLORS.gold, border: "none", color: COLORS.bgDeep, borderRadius: 8, padding: "6px 12px", fontFamily: FONTS.body, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t("relay.addEmail")}</button>
          </div>
        </div>
      )}
      {/* UDHEZIME1 — kartela e udhëzimit (pas relay-t, kurrë bashkë me të) */}
      {udhezimi !== null && udhezimetAktive && !njoftimiRelay && !termsGateOpen && activeFamilyId && (
        <div className="fixed left-1/2 -translate-x-1/2 rounded-xl shadow-xl z-[200]" role="status"
             style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)", background: COLORS.panel, color: COLORS.ink, fontSize: 13, fontFamily: FONTS.body, border: `1px solid ${COLORS.goldSoft}`, width: "min(92vw, 420px)", padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 700 }}>{t(udhezimi === "familje2" ? "udhezim.familje2.title" : typeof udhezimi === "string" ? `udhezim.themelues.${Number(udhezimi.slice(10)) + 1}.title` : `udhezim.${udhezimi + 1}.title`)}</span>
            {udhezimi !== "familje2" && <span style={{ marginLeft: "auto", fontSize: 11.5, color: COLORS.inkLight }}>{(typeof udhezimi === "string" ? Number(udhezimi.slice(10)) : udhezimi) + 1}/3</span>}
          </div>
          <div style={{ color: COLORS.inkLight, lineHeight: 1.45 }}>{t(udhezimi === "familje2" ? "udhezim.familje2.body" : typeof udhezimi === "string" ? `udhezim.themelues.${Number(udhezimi.slice(10)) + 1}.body` : `udhezim.${udhezimi + 1}.body`)}</div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button onClick={udhezimiTjeter} style={{ background: COLORS.gold, border: "none", color: COLORS.bgDeep, borderRadius: 8, padding: "6px 14px", fontFamily: FONTS.body, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {udhezimi === "familje2" || udhezimi === 2 || udhezimi === "themelues:2" ? t("udhezim.ok") : t("udhezim.next")}
            </button>
          </div>
        </div>
      )}
      {/* CACHE1 — shiriti «pa lidhje»: app-i lexohet nga kujtesa e telefonit, shkrimi pret internetin */}
      {jeOffline && activeFamilyId && (
        <div className="fixed left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full shadow-xl z-[200]" role="status"
             style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)", background: COLORS.panel, color: COLORS.ink, fontSize: 12, fontFamily: FONTS.body, border: `1px solid ${COLORS.goldSoft}`, maxWidth: "92vw", textAlign: "center" }}>
          {t("offline.banner")}
        </div>
      )}
      </>)}

      {/* FAZA 28 — Navigimi i ri poshtë. REDIZAJNUAR (kërkesë e userit, pas
          prove reale me të gjashtë variantet) — parimi i ri, i përbashkët
          për TË GJITHA variantet përveç "classic": bar-i klasik (4 tab-e)
          MBETET GJITHMONË baza — asnjë variant s'e fsheh/zëvendëson më
          përgjithmonë; VETËM disa shtojnë një shtresë të dytë mbi/rreth
          tij (badge "+", gjuhëz anësore, buton qendror) që hap
          përkohësisht "rreshtin e mjeteve të Pemës" (Kërko/Shiko/Shto/Fit
          + buton kthimi shumë i dukshëm — shigjetë e lakuar Undo2, sfond
          ari). Kujtime/Statistika NUK zhduken kurrë pa dashjen tënde. */}
      {/* ⚠ `!provaNga(4)` — i përkohshëm, shih `prova.jsx`. */}
      {!locked && !provaNga(4) && (
        <div ref={treeBottomBarRef} className="relative shrink-0 lg:hidden" style={{ background: COLORS.bgDeep, borderTop: `1px solid ${COLORS.goldSoft}`, paddingBottom: hapesira(SAFE_BOTTOM, headerPad("padBottom")) }}>

          {/* ---- "drawer" — gjuhëza anësore jeton mbi kanavacën e pemës
              (shih më lart, brenda kontejnerit të pemës, tani me rresht
              ikonash kompakt, jo faqe e madhe). Bar-i poshtë këtu ËSHTË
              GJITHMONË klasik — asgjë s'ndryshon këtu për këtë variant. ---- */}

          {/* ---- "swap" (ish-"sheet") — buton QENDROR (mbi bar, në mes),
              ikona e zgjedhur nga Admin Center. Prek → ndërron mes bar-it
              klasik dhe rreshtit të mjeteve. ---- */}
          {activeScreen === "tree" && treeNavVariant === "sheet" && (
            <button
              onClick={() => setTreeSwapOpen((v) => !v)}
              className="absolute left-1/2 -translate-x-1/2 shadow-lg flex items-center justify-center"
              /* FIX (kërkesë e userit — "po mbulon 50-60% të Kujtimet; e dua
                 ovale/horizontale, të mbulojë 10-20%, dhe të mos ngrihet
                 shumë lart"): nga rreth 44×44 i ngritur 22px → pil oval
                 56×26 i ngritur 16px. Tani zbret vetëm ~10px brenda bar-it
                 (≈18% e lartësisë së tab-it) dhe rri më afër menusë. */
              style={{ top: -16, width: 56, height: 26, borderRadius: 999, background: COLORS.gold, border: `2px solid ${COLORS.bgDeep}`, zIndex: 10 }}
              title={t("Toggle the menu")}
            >
              {treeSwapIcon === "refresh" ? <RefreshCw size={16} color={COLORS.bgDeep} /> : treeSwapIcon === "arrows" ? <ArrowLeftRight size={16} color={COLORS.bgDeep} /> : treeSwapIcon === "shuffle" ? <Shuffle size={16} color={COLORS.bgDeep} /> : <Repeat size={16} color={COLORS.bgDeep} />}
            </button>
          )}

          {/* FIX (kërkesë e userit) — "fab": butoni "+" u zhvendos nga
              ikona "Pema" te QENDRA-LART e bar-it (e njëjta pozicion/stil
              si "swap" më sipër), me ikonë shigjetë (ChevronUp/Down —
              rrotullohet kur hapet), jo më "+". */}
          {activeScreen === "tree" && treeNavVariant === "fab" && (
            <button
              onClick={() => setTreeFabOpen((v) => !v)}
              className="absolute left-1/2 -translate-x-1/2 shadow-lg flex items-center justify-center"
              /* FIX (kërkesë e userit — "po mbulon 50-60% të Kujtimet; e dua
                 ovale/horizontale, të mbulojë 10-20%, dhe të mos ngrihet
                 shumë lart"): nga rreth 44×44 i ngritur 22px → pil oval
                 56×26 i ngritur 16px. Tani zbret vetëm ~10px brenda bar-it
                 (≈18% e lartësisë së tab-it) dhe rri më afër menusë. */
              style={{ top: -16, width: 56, height: 26, borderRadius: 999, background: COLORS.gold, border: `2px solid ${COLORS.bgDeep}`, zIndex: 10 }}
              title={t("Tree Tools")}
            >
              <ChevronUp size={17} color={COLORS.bgDeep} style={{ transform: treeFabOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </button>
          )}

          {activeScreen === "tree" && treeNavVariant === "sheet" && treeSwapOpen ? (
            renderToolsRow(() => setTreeSwapOpen(false), true)
          ) : activeScreen === "tree" && treeNavVariant === "hybrid" && treeHybridOpen ? (
            renderToolsRow(() => setTreeHybridOpen(false))
          ) : activeScreen === "tree" && treeNavVariant === "contextual" && !treeContextualCollapsed ? (
            renderToolsRow(() => setTreeContextualCollapsed(true))
          ) : /* BALLINA1 — një listë e zgjedhur te Admin Center e mbivendos edhe
                 `classic`-un. Përndryshe zgjedhja do të ruhej, do të dukej e
                 ruajtur, dhe s'do të ndodhte asgjë — pikërisht ajo që na hëngri
                 një ditë të tërë me headerin.

                 Pyetja bëhet te lista E ZGJIDHUR, jo te ajo e ruajtur. Nëse një
                 telefon me version të vjetër merr një hyrje që ende s'e njeh,
                 lista e tij zbrazet pas filtrimit — dhe atëherë duhet të bjerë
                 te shiriti klasik, jo të mbetet PA shirit fare. */
            hyrjetEShiritit() ? (
            renderShellBar()
          ) : (
            <div className="flex items-center shrink-0" style={{ position: "relative" }}>
              <button onClick={() => setActiveScreen("home")} className="flex-1 flex flex-col items-center gap-0.5 py-2">
                <Home size={19} color={navIconColor("home", activeScreen === "home")} style={{ opacity: COLORS.vividIcons ? 1 : (activeScreen === "home" ? 1 : 0.55) }} />
                <span style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.01em", color: activeScreen === "home" ? COLORS.accentText : COLORS.onBg, opacity: activeScreen === "home" ? 1 : 0.7 }}>{t("Family")}</span>
              </button>
              {/* ---- "hybrid"/"contextual" (kolapsuar) — badge "+" mbi
                  ikonën "Pema". "fab" tani e ka butonin e vet në qendër-
                  lart (shih më sipër), jo më badge këtu. ---- */}
              <button onClick={() => setActiveScreen("tree")} className="flex-1 flex flex-col items-center gap-0.5 py-2 relative">
                <div className="relative">
                  <Users size={19} color={navIconColor("tree", activeScreen === "tree")} style={{ opacity: COLORS.vividIcons ? 1 : (activeScreen === "tree" ? 1 : 0.55) }} />
                  {activeScreen === "tree" && treeNavVariant === "hybrid" && renderPemaBadge(() => setTreeHybridOpen(true), false)}
                  {activeScreen === "tree" && treeNavVariant === "contextual" && treeContextualCollapsed && renderPemaBadge(() => setTreeContextualCollapsed(false), false)}
                </div>
                <span style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.01em", color: activeScreen === "tree" ? COLORS.accentText : COLORS.onBg, opacity: activeScreen === "tree" ? 1 : 0.7 }}>{t("The Tree")}</span>
              </button>
              <button onClick={() => openMemories("events")} className="flex-1 flex flex-col items-center gap-0.5 py-2">
                <BookOpen size={19} color={navIconColor("memories", activeScreen === "memories")} style={{ opacity: COLORS.vividIcons ? 1 : (activeScreen === "memories" ? 1 : 0.55) }} />
                <span style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.01em", color: activeScreen === "memories" ? COLORS.accentText : COLORS.onBg, opacity: activeScreen === "memories" ? 1 : 0.7 }}>{t("History")}</span>
              </button>
              <button onClick={() => setActiveScreen("stats")} className="flex-1 flex flex-col items-center gap-0.5 py-2">
                <BarChart3 size={19} color={navIconColor("stats", activeScreen === "stats")} style={{ opacity: COLORS.vividIcons ? 1 : (activeScreen === "stats" ? 1 : 0.55) }} />
                <span style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.01em", color: activeScreen === "stats" ? COLORS.accentText : COLORS.onBg, opacity: activeScreen === "stats" ? 1 : 0.7 }}>{t("Statistics")}</span>
              </button>
              {/* Hamburgeri zëvendëson "Assistant" — hap menynë (Ndërro familjen /
                  Asistenti / Posta). Asistenti tani jeton brenda kësaj menyje. */}
              <button onClick={() => setFabOpen(true)} className="flex-1 flex flex-col items-center gap-0.5 py-2">
                <Menu size={19} color={navIconColor("menu", fabOpen)} style={{ opacity: COLORS.vividIcons ? 1 : (fabOpen ? 1 : 0.55) }} />
                <span style={{ fontFamily: FONTS.body, fontSize: 11.5, fontWeight: 500, letterSpacing: "0.01em", color: fabOpen ? COLORS.accentText : COLORS.onBg, opacity: fabOpen ? 1 : 0.7 }}>{t("Menu")}</span>
              </button>

              {/* ---- "fab" — speed-dial-i vetë, si SHTRESË E DYTË mbi
                  bar-in klasik (jo zëvendësim) — shfaqet vetëm kur
                  treeFabOpen, ngjitur pas Pema-s. ---- */}
              {activeScreen === "tree" && treeNavVariant === "fab" && treeFabOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center flex-wrap gap-2" style={{ bottom: "100%", marginBottom: 12, maxWidth: "calc(100vw - 24px)" }}>
                  {renderTreeBarIcons(() => setTreeFabOpen(false))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {currentPersonId && !provaNga(4) && <SupportChat open={chatOpen} setOpen={setChatOpen} showLauncher={false} />}

      {/* ─── Butoni pluskues (FAB) — Ndërro familjen / Asistenti / Posta ───
          Hapet nga butoni rrethor (desktop) OSE nga hamburgeri te tab-i i
          poshtëm (telefon). Butoni rrethor fshihet në telefon që të mos
          dyfishohet me hamburgerin. */}
      {currentPersonId && !locked && !provaNga(4) && (
        <>
        {fabOpen && <div onClick={() => setFabOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 59 }} />}
        <div style={{ position: "fixed", right: 16, bottom: `calc(84px + ${SAFE_BOTTOM})`, zIndex: 60, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
          {fabOpen && (
            <>
              {servicesCount > 0 && (
                <button onClick={() => { setFabOpen(false); setActiveScreen("services"); }} className="flex items-center gap-2" style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                  <span style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, borderRadius: 8, padding: "5px 9px", fontSize: 12.5, fontFamily: FONTS.body, color: COLORS.ink }}>{t("Services")}</span>
                  <span style={{ width: 40, height: 40, borderRadius: 999, background: COLORS.panel, border: `1px solid ${COLORS.gold}`, display: "flex", alignItems: "center", justifyContent: "center" }}><ShoppingBag size={18} color={COLORS.gold} /></span>
                </button>
              )}
              <button {...switchFamPressProps(() => setFabOpen(false))} title={t("Tap to switch · hold to choose")} className="flex items-center gap-2" style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                <span style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, borderRadius: 8, padding: "5px 9px", fontSize: 12.5, color: COLORS.ink, boxShadow: "0 4px 12px rgba(0,0,0,.18)", fontFamily: FONTS.body }}>{t("Switch family")}</span>
                <span style={{ width: 40, height: 40, borderRadius: 999, background: COLORS.panel, border: `1px solid ${COLORS.gold}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,0,0,.25)" }}><ArrowLeftRight size={17} color={COLORS.gold} /></span>
              </button>
              {assistantEnabled && (
              <button onClick={() => { setFabOpen(false); setChatOpen(true); }} className="flex items-center gap-2" style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                <span style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, borderRadius: 8, padding: "5px 9px", fontSize: 12.5, color: COLORS.ink, boxShadow: "0 4px 12px rgba(0,0,0,.18)", fontFamily: FONTS.body }}>{t("Assistant")}</span>
                <span style={{ width: 40, height: 40, borderRadius: 999, background: COLORS.panel, border: `1px solid ${COLORS.gold}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,0,0,.25)" }}><MessageCircle size={17} color={COLORS.gold} /></span>
              </button>
              )}
              {canManage && platformFeatures.family_mail_enabled && (
                <button onClick={() => { setFabOpen(false); openEmailReplies(); }} className="flex items-center gap-2" style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                  <span style={{ background: COLORS.panel, border: `1px solid ${COLORS.goldSoft}`, borderRadius: 8, padding: "5px 9px", fontSize: 12.5, color: COLORS.ink, boxShadow: "0 4px 12px rgba(0,0,0,.18)", fontFamily: FONTS.body }}>{t("Family Mail")}</span>
                  <span style={{ width: 40, height: 40, borderRadius: 999, background: COLORS.panel, border: `1px solid ${COLORS.gold}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,0,0,.25)" }}><Mail size={17} color={COLORS.gold} /></span>
                </button>
              )}
            </>
          )}
        </div>
        {/* Desktop: butoni rrethor është VETËM chat-i (Asistenti) — pa hamburger.
            "Ndërro familjen" te desktopi jeton te sidebar-i. Në telefon menyja
            hapet nga hamburgeri i tab-it të poshtëm.
            FIX (kërkesë e userit — "ikona e chatit po mbulon zoom in/out"):
            zhvendosur MAJTAS kolonës së zoom-it (right: 62) dhe ULUR në të
            njëjtën lartësi me butonin "Shto person" (bottom ~1rem) — s'mbulon
            më asnjë kontroll. Kontejner i veçantë fiks, që pozicioni i
            menusë së telefonit (fabOpen, sipër tab-it) të mos preket fare. */}
        {/* BALLINA2 — ASISTENTI PLUSKUES, TANI EDHE NË TELEFON.
            Deri sot kjo kutizë ishte `hidden lg:block`: në telefon asistenti
            hyhej VETËM nga hamburgeri i shiritit klasik, dhe me çdo shirit
            tjetër ai nuk kishte asnjë derë. Nuk ishte rastësi — ishte një
            veçori e varur nga një layout i vetëm.

            Vendi zgjidhet te Admin Center. Por rregulli i fundit nuk është i
            tijëve: nëse zgjidhet «vetëm te menyja» DHE «chat» nuk është ndër
            hyrjet e shiritit, butoni shfaqet gjithsesi. Një cilësim s'duhet ta
            bëjë kurrë një veçori të paarritshme pa e thënë askush. */}
        {(() => {
          if (!assistantEnabled) return null;
          const chatNeShirit = (hyrjetEShiritit() || []).includes("chat");
          const duhetPluskuesi = chatLauncher === "pluskues" || chatLauncher === "te_dyja"
            || (chatLauncher === "meny" && !chatNeShirit);
          if (!duhetPluskuesi) return null;
          // Kur biseda është e hapur në telefon ajo mbulon krejt ekranin —
          // butoni poshtë saj do të ishte vetëm një pikë e artë mbi tekst.
          if (chatOpen) return null;
          // Sjellja jeton te `ChatFab.jsx`, me hook-at e vet. Këtu vendoset
          // vetëm NËSE shfaqet — jo si sillet.
          return (
            <ChatFab
              sjellja={chatFabStyle}
              onOpen={() => setChatOpen(true)}
              colors={COLORS}
              etiketa={t("Assistant")}
              ikona={<MessageCircle size={22} />}
              // Sa lart të rrijë — e di vetëm ky vend, sepse vetëm ai di çfarë
              // ka poshtë. Te telefoni shiriti i aplikacionit; te desktopi
              // shiriti i pemës, POR vetëm kur klasiku është aktiv, është te
              // fundi, dhe je te Pema. Përndryshe desktopi s'ka asgjë poshtë.
              poshte={(() => {
                if (!isWideScreen) return 80;
                const shiritiEshtePoshte = treeNavVariant === "classic"
                  && (treeBarConfig.position || "bottom") === "bottom"
                  && activeScreen === "tree";
                return shiritiEshtePoshte ? 76 : 16;
              })()}
            />
          );
        })()}
        </>
      )}

      {/* Paneli "Ndërro familjen" — Modal (mbulim i plotë + shigjetë back + swipe). */}
      {switchFamOpen && (
        <Modal title={t("Switch family")} onClose={() => setSwitchFamOpen(false)}>
          <div className="-mx-4 sm:-mx-5">
            {myLinkedFamilies.map((f) => (
              <a key={f.person_id} href={`/f/${f.family_slug}`} onClick={(e) => { e.preventDefault(); shkoTeFamilja(f.family_slug); }} className="flex items-center gap-3 px-4 py-3 hover:bg-black/5" style={{ textDecoration: "none", background: f.family_id === activeFamilyId ? COLORS.accentTint : "transparent" }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: f.family_id === activeFamilyId ? COLORS.gold : COLORS.inkLight, flexShrink: 0 }} />
                <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.ink, fontWeight: f.family_id === activeFamilyId ? 700 : 400 }}>{f.family_name} {f.family_id === activeFamilyId ? t("auth.currentFamilyTag") : ""}</span>
              </a>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, paddingTop: 12, marginTop: 12, borderTop: `1px solid ${COLORS.goldSoft}` }}>
            <a href="/join" style={{ flex: 1, textAlign: "center", textDecoration: "none", fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: COLORS.ink, padding: "9px 8px", border: `1px solid ${COLORS.goldSoft}`, borderRadius: 8 }}>{t("Join another family (Code)")}</a>
            <a href="/start" style={{ flex: 1, textAlign: "center", textDecoration: "none", fontFamily: FONTS.body, fontSize: 13.5, fontWeight: 500, color: COLORS.ink, padding: "9px 8px", border: `1px solid ${COLORS.goldSoft}`, borderRadius: 8 }}>{t("Create a new family")}</a>
          </div>
        </Modal>
      )}
      {/* «Çfarë do të shtosh?» — dera e vetme për gjithçka që shtohet.
          Shih `hapShtimin()` për të dy përjashtimet. */}
      {addSheet && (
        // Lista e zgjedhjes së prindit/fëmijës/partnerit KUFIZOHET si te rrjedha
        // e vjetër (personModal/spouseModal). Më parë këtu kalonte `people` i
        // plotë: redaktori ose kontribuesi zgjidhte një person jashtë zonës së
        // vet, plotësonte gjithë formularin, dhe refuzimin e merrte vetëm në
        // fund nga serveri. Për adminin `relationPeopleOptions` është vetë
        // `people`, pra asgjë s'ndryshon. Serveri mbetet kufiri i vërtetë; kjo
        // thjesht nuk premton më diçka që s'lejohet.
        <AddPersonSheet
          backRef={addSheetBackRef}
          people={relationPeopleOptions}
          currentPersonId={currentPersonId}
          isAdmin={canManage}
          treeIsEmpty={treeHasAnyPeople === false}
          rrenjaELejuar={canManage && Object.keys(people).length === 1 && !Object.values(people).some((p) => p.is_root)}
          initial={addSheet.initial}
          customTemplate={customTemplate}
          options={personTypeOptions}
          canPostToFeed={ruleOn("new_member")}
          branchOf={(p) => t(branchLabel(effectiveBranch(p)))}
          // 5 shtator 2026 — vendlindja e strukturuar + banimi aktual edhe te
          // shtimi. Komponenti kalon si prop sepse `App.jsx` e importon vetë
          // `AddPersonSheet`; një import i kundërt do të bënte cikël. `people`
          // këtu është pema E PLOTË (jo `relationPeopleOptions`), sepse prej
          // saj dalin qytetet që familja i ka përdorur tashmë.
          FushatEVendit={BirthPlaceFields}
          peopleAll={people}
          countries={countries}
          canManage={canManage}
          addCountry={addCountry}
          emriShtetit={(kodi) => displayCountry(kodi, i18n.language) || kodi}
          emriDallues={(p) => emriDallues(p, etiketatDalluese)}
          onClose={() => setAddSheet(null)}
          onSubmit={handleAddSheetSubmit}
        />
      )}

      {/* FAZA 9 — sugjerimi AI për një rast — Modal (mbulim i plotë + back + swipe). */}
      {occasionAI && (
        <Modal title={`✨ ${t("Suggested message")}`} onClose={() => setOccasionAI(null)}>
          {occasionAI.busy ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.inkLight }}>{t("Writing…")}</p>
          ) : occasionAI.error ? (
            <p style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.danger }}>{occasionAI.error}</p>
          ) : (
            <>
              <p style={{ fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.6, color: COLORS.ink, whiteSpace: "pre-wrap" }}>{occasionAI.text}</p>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button onClick={async () => { try { await navigator.clipboard.writeText(occasionAI.text); showToast(t("Copied")); } catch (e) { showToast(occasionAI.text); } }} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "none", background: COLORS.gold, color: COLORS.bgDeep, fontWeight: 700, cursor: "pointer" }}>{t("Copy")}</button>
                {occasionAI.item && <button onClick={() => suggestForOccasion(occasionAI.item)} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${COLORS.goldSoft}`, background: "transparent", color: COLORS.ink, cursor: "pointer" }}>{t("Regenerate")}</button>}
              </div>
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight, marginTop: 10 }}>{t("AI suggestion — review before sending.")}</p>
            </>
          )}
        </Modal>
      )}

      {/* FAZA 9 — datat e rëndësishme — Modal (mbulim i plotë + back + swipe). */}
      {impModalOpen && (
        <Modal title={`📅 ${t("Important dates")}`} onClose={() => setImpModalOpen(false)}>
          {/* PËR KË ËSHTË KJO DATË. Vetëm admini e ka zgjedhjen; kushdo tjetër
              — redaktori përfshirë — shton vetëm për vete, dhe atëherë ky
              rresht as nuk shfaqet: një zgjedhje me një mundësi është zhurmë.
              «Vetëm për mua» rri e para dhe është e ndezur që në hapje. */}
          {myRole === "admin" && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {[["une", t("Only for me")], ["familja", t("For the whole family")]].map(([v, etiketa]) => {
                const on = impKujt === v;
                return (
                  <button key={v} onClick={() => setImpKujt(v)}
                    style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12,
                             background: on ? COLORS.gold : "transparent", color: on ? COLORS.bgDeep : COLORS.ink,
                             border: `1px solid ${on ? COLORS.gold : COLORS.goldSoft}`, cursor: "pointer" }}>
                    {etiketa}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            <input value={impLabel} onChange={(e) => setImpLabel(e.target.value)} placeholder={t("Name (e.g. Independence Day)")} style={{ flex: "1 1 100%", padding: "9px 11px", borderRadius: 9, border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft, color: COLORS.ink }} />
            <input value={impDay} onChange={(e) => setImpDay(e.target.value.replace(/\D/g, "").slice(0, 2))} placeholder={t("Day")} inputMode="numeric" style={{ width: 70, padding: "9px 11px", borderRadius: 9, border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft, color: COLORS.ink }} />
            <input value={impMonth} onChange={(e) => setImpMonth(e.target.value.replace(/\D/g, "").slice(0, 2))} placeholder={t("Month")} inputMode="numeric" style={{ width: 80, padding: "9px 11px", borderRadius: 9, border: `1px solid ${COLORS.goldSoft}`, background: COLORS.panelSoft, color: COLORS.ink }} />
            <button onClick={addImportantDate} disabled={impBusy} style={{ padding: "9px 14px", borderRadius: 9, border: "none", background: COLORS.gold, color: COLORS.bgDeep, fontWeight: 700, cursor: "pointer" }}>{impBusy ? t("Saving…") : t("Add")}</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(importantDates || []).length === 0 ? (
              <p style={{ fontFamily: FONTS.body, fontSize: 12.5, color: COLORS.inkLight }}>{t("No important dates yet.")}</p>
            ) : (importantDates || []).map((r) => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 9, background: COLORS.panelSoft }}>
                <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.ink }}>
                  {r.label}{" "}
                  <span style={{ color: COLORS.inkLight, fontFamily: FONTS.body, fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{String(r.event_day).padStart(2, "0")}/{String(r.event_month).padStart(2, "0")}</span>
                  {r.person_id && <span style={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.inkLight, marginLeft: 8 }}>{t("mine")}</span>}
                </span>
                <button onClick={() => deleteImportantDate(r.id)} title={t("Remove")} style={{ background: "transparent", border: "none", cursor: "pointer", color: COLORS.danger, fontSize: 13 }}>🗑</button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}